import Vue from 'vue';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

/**
  * vue-class-component v7.2.6
  * (c) 2015-present Evan You
  * @license MIT
  */

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

// The rational behind the verbose Reflect-feature check below is the fact that there are polyfills
// which add an implementation for Reflect.defineMetadata but not for Reflect.getOwnMetadataKeys.
// Without this check consumers will encounter hard to track down runtime errors.
function reflectionIsSupported() {
  return typeof Reflect !== 'undefined' && Reflect.defineMetadata && Reflect.getOwnMetadataKeys;
}
function copyReflectionMetadata(to, from) {
  forwardMetadata(to, from);
  Object.getOwnPropertyNames(from.prototype).forEach(function (key) {
    forwardMetadata(to.prototype, from.prototype, key);
  });
  Object.getOwnPropertyNames(from).forEach(function (key) {
    forwardMetadata(to, from, key);
  });
}

function forwardMetadata(to, from, propertyKey) {
  var metaKeys = propertyKey ? Reflect.getOwnMetadataKeys(from, propertyKey) : Reflect.getOwnMetadataKeys(from);
  metaKeys.forEach(function (metaKey) {
    var metadata = propertyKey ? Reflect.getOwnMetadata(metaKey, from, propertyKey) : Reflect.getOwnMetadata(metaKey, from);

    if (propertyKey) {
      Reflect.defineMetadata(metaKey, metadata, to, propertyKey);
    } else {
      Reflect.defineMetadata(metaKey, metadata, to);
    }
  });
}

var fakeArray = {
  __proto__: []
};
var hasProto = fakeArray instanceof Array;
function createDecorator(factory) {
  return function (target, key, index) {
    var Ctor = typeof target === 'function' ? target : target.constructor;

    if (!Ctor.__decorators__) {
      Ctor.__decorators__ = [];
    }

    if (typeof index !== 'number') {
      index = undefined;
    }

    Ctor.__decorators__.push(function (options) {
      return factory(options, key, index);
    });
  };
}
function isPrimitive(value) {
  var type = _typeof(value);

  return value == null || type !== 'object' && type !== 'function';
}
function warn(message) {
  if (typeof console !== 'undefined') {
    console.warn('[vue-class-component] ' + message);
  }
}

function collectDataFromConstructor(vm, Component) {
  // override _init to prevent to init as Vue instance
  var originalInit = Component.prototype._init;

  Component.prototype._init = function () {
    var _this = this;

    // proxy to actual vm
    var keys = Object.getOwnPropertyNames(vm); // 2.2.0 compat (props are no longer exposed as self properties)

    if (vm.$options.props) {
      for (var key in vm.$options.props) {
        if (!vm.hasOwnProperty(key)) {
          keys.push(key);
        }
      }
    }

    keys.forEach(function (key) {
      Object.defineProperty(_this, key, {
        get: function get() {
          return vm[key];
        },
        set: function set(value) {
          vm[key] = value;
        },
        configurable: true
      });
    });
  }; // should be acquired class property values


  var data = new Component(); // restore original _init to avoid memory leak (#209)

  Component.prototype._init = originalInit; // create plain data object

  var plainData = {};
  Object.keys(data).forEach(function (key) {
    if (data[key] !== undefined) {
      plainData[key] = data[key];
    }
  });

  if (process.env.NODE_ENV !== 'production') {
    if (!(Component.prototype instanceof Vue) && Object.keys(plainData).length > 0) {
      warn('Component class must inherit Vue or its descendant class ' + 'when class property is used.');
    }
  }

  return plainData;
}

var $internalHooks = ['data', 'beforeCreate', 'created', 'beforeMount', 'mounted', 'beforeDestroy', 'destroyed', 'beforeUpdate', 'updated', 'activated', 'deactivated', 'render', 'errorCaptured', 'serverPrefetch' // 2.6
];
function componentFactory(Component) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  options.name = options.name || Component._componentTag || Component.name; // prototype props.

  var proto = Component.prototype;
  Object.getOwnPropertyNames(proto).forEach(function (key) {
    if (key === 'constructor') {
      return;
    } // hooks


    if ($internalHooks.indexOf(key) > -1) {
      options[key] = proto[key];
      return;
    }

    var descriptor = Object.getOwnPropertyDescriptor(proto, key);

    if (descriptor.value !== void 0) {
      // methods
      if (typeof descriptor.value === 'function') {
        (options.methods || (options.methods = {}))[key] = descriptor.value;
      } else {
        // typescript decorated data
        (options.mixins || (options.mixins = [])).push({
          data: function data() {
            return _defineProperty({}, key, descriptor.value);
          }
        });
      }
    } else if (descriptor.get || descriptor.set) {
      // computed properties
      (options.computed || (options.computed = {}))[key] = {
        get: descriptor.get,
        set: descriptor.set
      };
    }
  });
  (options.mixins || (options.mixins = [])).push({
    data: function data() {
      return collectDataFromConstructor(this, Component);
    }
  }); // decorate options

  var decorators = Component.__decorators__;

  if (decorators) {
    decorators.forEach(function (fn) {
      return fn(options);
    });
    delete Component.__decorators__;
  } // find super


  var superProto = Object.getPrototypeOf(Component.prototype);
  var Super = superProto instanceof Vue ? superProto.constructor : Vue;
  var Extended = Super.extend(options);
  forwardStaticMembers(Extended, Component, Super);

  if (reflectionIsSupported()) {
    copyReflectionMetadata(Extended, Component);
  }

  return Extended;
}
var reservedPropertyNames = [// Unique id
'cid', // Super Vue constructor
'super', // Component options that will be used by the component
'options', 'superOptions', 'extendOptions', 'sealedOptions', // Private assets
'component', 'directive', 'filter'];
var shouldIgnore = {
  prototype: true,
  arguments: true,
  callee: true,
  caller: true
};

function forwardStaticMembers(Extended, Original, Super) {
  // We have to use getOwnPropertyNames since Babel registers methods as non-enumerable
  Object.getOwnPropertyNames(Original).forEach(function (key) {
    // Skip the properties that should not be overwritten
    if (shouldIgnore[key]) {
      return;
    } // Some browsers does not allow reconfigure built-in properties


    var extendedDescriptor = Object.getOwnPropertyDescriptor(Extended, key);

    if (extendedDescriptor && !extendedDescriptor.configurable) {
      return;
    }

    var descriptor = Object.getOwnPropertyDescriptor(Original, key); // If the user agent does not support `__proto__` or its family (IE <= 10),
    // the sub class properties may be inherited properties from the super class in TypeScript.
    // We need to exclude such properties to prevent to overwrite
    // the component options object which stored on the extended constructor (See #192).
    // If the value is a referenced value (object or function),
    // we can check equality of them and exclude it if they have the same reference.
    // If it is a primitive value, it will be forwarded for safety.

    if (!hasProto) {
      // Only `cid` is explicitly exluded from property forwarding
      // because we cannot detect whether it is a inherited property or not
      // on the no `__proto__` environment even though the property is reserved.
      if (key === 'cid') {
        return;
      }

      var superDescriptor = Object.getOwnPropertyDescriptor(Super, key);

      if (!isPrimitive(descriptor.value) && superDescriptor && superDescriptor.value === descriptor.value) {
        return;
      }
    } // Warn if the users manually declare reserved properties


    if (process.env.NODE_ENV !== 'production' && reservedPropertyNames.indexOf(key) >= 0) {
      warn("Static property name '".concat(key, "' declared on class '").concat(Original.name, "' ") + 'conflicts with reserved property name of Vue internal. ' + 'It may cause unexpected behavior of the component. Consider renaming the property.');
    }

    Object.defineProperty(Extended, key, descriptor);
  });
}

function Component(options) {
  if (typeof options === 'function') {
    return componentFactory(options);
  }

  return function (Component) {
    return componentFactory(Component, options);
  };
}

Component.registerHooks = function registerHooks(keys) {
  $internalHooks.push.apply($internalHooks, _toConsumableArray(keys));
};

(undefined && undefined.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};

/** @see {@link https://github.com/vuejs/vue-class-component/blob/master/src/reflect.ts} */
var reflectMetadataIsSupported = typeof Reflect !== 'undefined' && typeof Reflect.getMetadata !== 'undefined';
function applyMetadata(options, target, key) {
    if (reflectMetadataIsSupported) {
        if (!Array.isArray(options) &&
            typeof options !== 'function' &&
            !options.hasOwnProperty('type') &&
            typeof options.type === 'undefined') {
            var type = Reflect.getMetadata('design:type', target, key);
            if (type !== Object) {
                options.type = type;
            }
        }
    }
}

/**
 * decorator of model
 * @param  event event name
 * @param options options
 * @return PropertyDecorator
 */
function Model(event, options) {
    if (options === void 0) { options = {}; }
    return function (target, key) {
        applyMetadata(options, target, key);
        createDecorator(function (componentOptions, k) {
            (componentOptions.props || (componentOptions.props = {}))[k] = options;
            componentOptions.model = { prop: k, event: event || k };
        })(target, key);
    };
}

/**
 * decorator of a prop
 * @param  options the options for the prop
 * @return PropertyDecorator | void
 */
function Prop(options) {
    if (options === void 0) { options = {}; }
    return function (target, key) {
        applyMetadata(options, target, key);
        createDecorator(function (componentOptions, k) {
            (componentOptions.props || (componentOptions.props = {}))[k] = options;
        })(target, key);
    };
}

/**
 * decorator of a synced prop
 * @param propName the name to interface with from outside, must be different from decorated property
 * @param options the options for the synced prop
 * @return PropertyDecorator | void
 */
function PropSync(propName, options) {
    if (options === void 0) { options = {}; }
    return function (target, key) {
        applyMetadata(options, target, key);
        createDecorator(function (componentOptions, k) {
            (componentOptions.props || (componentOptions.props = {}))[propName] = options;
            (componentOptions.computed || (componentOptions.computed = {}))[k] = {
                get: function () {
                    return this[propName];
                },
                set: function (value) {
                    this.$emit("update:" + propName, value);
                },
            };
        })(target, key);
    };
}

/**
 * decorator of a ref prop
 * @param refKey the ref key defined in template
 */
function Ref(refKey) {
    return createDecorator(function (options, key) {
        options.computed = options.computed || {};
        options.computed[key] = {
            cache: false,
            get: function () {
                return this.$refs[refKey || key];
            },
        };
    });
}

/**
 * decorator of a watch function
 * @param  path the path or the expression to observe
 * @param  WatchOption
 * @return MethodDecorator
 */
function Watch(path, options) {
    if (options === void 0) { options = {}; }
    var _a = options.deep, deep = _a === void 0 ? false : _a, _b = options.immediate, immediate = _b === void 0 ? false : _b;
    return createDecorator(function (componentOptions, handler) {
        if (typeof componentOptions.watch !== 'object') {
            componentOptions.watch = Object.create(null);
        }
        var watch = componentOptions.watch;
        if (typeof watch[path] === 'object' && !Array.isArray(watch[path])) {
            watch[path] = [watch[path]];
        }
        else if (typeof watch[path] === 'undefined') {
            watch[path] = [];
        }
        watch[path].push({ handler: handler, deep: deep, immediate: immediate });
    });
}

function Action(target, key, descriptor) {
    var vuexModule = target.constructor;
    if (!vuexModule.__actions) {
        vuexModule.__actions = {};
    }
    if (descriptor.value) {
        vuexModule.__actions[key] = descriptor.value;
    }
}

var VuexModule = /** @class */ (function () {
    function VuexModule(options) {
        this.__options = options;
    }
    VuexModule.prototype.$watch = function (fn, callback, options) {
        return function () { };
    };
    VuexModule.__useHotUpdate = false;
    return VuexModule;
}());

var __assign = (undefined && undefined.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var VuexClassModuleFactory = /** @class */ (function () {
    function VuexClassModuleFactory(classModule, instance, moduleOptions) {
        this.definition = {
            state: {},
            moduleRefs: {},
            getters: {},
            mutations: {},
            actions: {},
            localFunctions: {}
        };
        this.moduleOptions = moduleOptions;
        this.instance = instance;
        this.registerOptions = instance.__options;
        this.init(classModule);
    }
    VuexClassModuleFactory.prototype.init = function (classModule) {
        // state
        for (var _i = 0, _a = Object.keys(this.instance); _i < _a.length; _i++) {
            var key = _a[_i];
            var val = this.instance[key];
            if (key !== "__options" && this.instance.hasOwnProperty(key)) {
                if (val instanceof VuexModule) {
                    this.definition.moduleRefs[key] = val;
                }
                else {
                    this.definition.state[key] = this.instance[key];
                }
            }
        }
        var actionKeys = Object.keys(classModule.__actions || {});
        var mutationKeys = Object.keys(classModule.__mutations || {});
        var isAction = function (key) { return actionKeys.indexOf(key) !== -1; };
        var isMutation = function (key) { return mutationKeys.indexOf(key) !== -1; };
        for (var _b = 0, _c = getModulePrototypes(classModule); _b < _c.length; _b++) {
            var module_1 = _c[_b];
            for (var _d = 0, _e = Object.getOwnPropertyNames(module_1.prototype); _d < _e.length; _d++) {
                var key = _e[_d];
                var descriptor = Object.getOwnPropertyDescriptor(module_1.prototype, key);
                var isGetter = !!descriptor.get;
                if (isGetter && !(key in this.definition.getters)) {
                    this.definition.getters[key] = descriptor.get;
                }
                if (isAction(key) && !(key in this.definition.actions) && descriptor.value) {
                    this.definition.actions[key] = module_1.prototype[key];
                }
                if (isMutation(key) && !(key in this.definition.mutations) && descriptor.value) {
                    this.definition.mutations[key] = module_1.prototype[key];
                }
                var isHelperFunction = descriptor.value &&
                    typeof module_1.prototype[key] === "function" &&
                    !isAction(key) &&
                    !isMutation(key) &&
                    key !== "constructor";
                if (isHelperFunction && !(key in this.definition.localFunctions)) {
                    this.definition.localFunctions[key] = module_1.prototype[key];
                }
            }
        }
    };
    VuexClassModuleFactory.prototype.registerVuexModule = function () {
        var _a;
        var _this = this;
        var vuexModule = {
            state: this.definition.state,
            getters: {},
            mutations: {},
            actions: {},
            namespaced: true
        };
        // getters
        mapValues(vuexModule.getters, this.definition.getters, function (getter) {
            return function (state, getters) {
                var thisObj = _this.buildThisProxy({ state: state, getters: getters });
                return getter.call(thisObj);
            };
        });
        // mutations
        mapValues(vuexModule.mutations, this.definition.mutations, function (mutation) {
            return function (state, payload) {
                var thisObj = _this.buildThisProxy({
                    state: state,
                    stateSetter: function (stateField, val) {
                        state[stateField] = val;
                    }
                });
                mutation.call(thisObj, payload);
            };
        });
        if (this.moduleOptions.generateMutationSetters) {
            var _loop_1 = function (stateKey) {
                var mutation = function (state, payload) {
                    state[stateKey] = payload;
                };
                vuexModule.mutations[this_1.getMutationSetterName(stateKey)] = mutation;
            };
            var this_1 = this;
            for (var _i = 0, _b = Object.keys(this.definition.state); _i < _b.length; _i++) {
                var stateKey = _b[_i];
                _loop_1(stateKey);
            }
        }
        // actions
        mapValues(vuexModule.actions, this.definition.actions, function (action) {
            return function (context, payload) {
                var proxyDefinition = __assign(__assign({}, context), { stateSetter: _this.moduleOptions.generateMutationSetters
                        ? function (field, val) {
                            context.commit(_this.getMutationSetterName(field), val);
                        }
                        : undefined });
                var thisObj = _this.buildThisProxy(proxyDefinition);
                return action.call(thisObj, payload);
            };
        });
        // register module
        var _c = this.registerOptions, store = _c.store, name = _c.name;
        if (store.state[name]) {
            if (VuexModule.__useHotUpdate || (typeof module !== "undefined" && module.hot)) {
                store.hotUpdate({
                    modules: (_a = {},
                        _a[name] = vuexModule,
                        _a)
                });
            }
            else {
                throw Error("[vuex-class-module]: A module with name '" + name + "' already exists.");
            }
        }
        else {
            store.registerModule(this.registerOptions.name, vuexModule);
        }
    };
    VuexClassModuleFactory.prototype.buildAccessor = function () {
        var _this = this;
        var _a = this.registerOptions, store = _a.store, name = _a.name;
        var stateSetter = this.moduleOptions.generateMutationSetters
            ? function (field, val) {
                store.commit(name + "/" + _this.getMutationSetterName(field), val);
            }
            : undefined;
        var accessorModule = this.buildThisProxy(__assign(__assign({}, store), { state: store.state[name], stateSetter: stateSetter, useNamespaceKey: true, excludeModuleRefs: true, excludeLocalFunctions: true }));
        // watch API
        accessorModule.$watch = function (fn, callback, options) {
            return store.watch(function (state, getters) {
                return fn(_this.buildThisProxy({
                    state: state[name],
                    getters: getters,
                    useNamespaceKey: true
                }));
            }, callback, options);
        };
        Object.setPrototypeOf(accessorModule, Object.getPrototypeOf(this.instance));
        Object.freeze(accessorModule);
        return accessorModule;
    };
    VuexClassModuleFactory.prototype.buildThisProxy = function (proxyDefinition) {
        var obj = {};
        if (proxyDefinition.state) {
            mapValuesToProperty(obj, this.definition.state, function (key) { return proxyDefinition.state[key]; }, proxyDefinition.stateSetter
                ? function (key, val) { return proxyDefinition.stateSetter(key, val); }
                : function () {
                    throw Error("[vuex-class-module]: Cannot modify state outside mutations.");
                });
        }
        if (!proxyDefinition.excludeModuleRefs) {
            mapValues(obj, this.definition.moduleRefs, function (val) { return val; });
        }
        var namespaceKey = proxyDefinition.useNamespaceKey ? this.registerOptions.name + "/" : "";
        if (proxyDefinition.getters) {
            mapValuesToProperty(obj, this.definition.getters, function (key) { return proxyDefinition.getters["" + namespaceKey + key]; });
        }
        if (proxyDefinition.commit) {
            mapValues(obj, this.definition.mutations, function (mutation, key) {
                return function (payload) { return proxyDefinition.commit("" + namespaceKey + key, payload); };
            });
        }
        if (proxyDefinition.dispatch) {
            mapValues(obj, this.definition.actions, function (action, key) {
                return function (payload) { return proxyDefinition.dispatch("" + namespaceKey + key, payload); };
            });
        }
        if (!proxyDefinition.excludeLocalFunctions) {
            mapValues(obj, this.definition.localFunctions, function (localFunction) {
                return function () {
                    var args = [];
                    for (var _i = 0; _i < arguments.length; _i++) {
                        args[_i] = arguments[_i];
                    }
                    return localFunction.apply(obj, args);
                };
            });
        }
        return obj;
    };
    VuexClassModuleFactory.prototype.getMutationSetterName = function (stateKey) {
        return "set__" + stateKey;
    };
    return VuexClassModuleFactory;
}());
function mapValues(target, source, mapFunc) {
    for (var _i = 0, _a = Object.keys(source); _i < _a.length; _i++) {
        var key = _a[_i];
        target[key] = mapFunc(source[key], key);
    }
}
function mapValuesToProperty(target, source, get, set) {
    var _loop_2 = function (key) {
        Object.defineProperty(target, key, {
            get: function () { return get(key); },
            set: set ? function (val) { return set(key, val); } : undefined
        });
    };
    for (var _i = 0, _a = Object.keys(source); _i < _a.length; _i++) {
        var key = _a[_i];
        _loop_2(key);
    }
}
function getModulePrototypes(module) {
    var prototypes = [];
    for (var prototype = module; prototype && prototype !== VuexModule; prototype = Object.getPrototypeOf(prototype)) {
        prototypes.push(prototype);
    }
    return prototypes;
}

var __spreadArrays = (undefined && undefined.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
function Module(arg) {
    if (typeof arg === "function") {
        return moduleDecoratorFactory()(arg);
    }
    else {
        return moduleDecoratorFactory(arg);
    }
}
function moduleDecoratorFactory(moduleOptions) {
    return function (constructor) {
        var accessor = function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var instance = new ((_a = constructor.prototype.constructor).bind.apply(_a, __spreadArrays([void 0], args)))();
            Object.setPrototypeOf(instance, accessor.prototype);
            var factory = new VuexClassModuleFactory(constructor, instance, moduleOptions || {});
            factory.registerVuexModule();
            return factory.buildAccessor();
        };
        accessor.prototype = Object.create(constructor.prototype);
        accessor.prototype.constructor = accessor;
        return accessor;
    };
}

let globalDataModule = null;
/**
 * Register the module in the parent application existing store
 *
 * @param store Parent application vuex store instance
 * @return GlobalDataModule The global data vuex module instance
 */
function registerModule(store) {
    globalDataModule = new GlobalDataModule({ store: store, name: 'globalData' });
    return globalDataModule;
}
/**
 * Retrieve the registered global data vuex module
 * @return GlobalDataModule The global data vuex module instance
 */
function globalDataVuex() {
    if (!globalDataModule) {
        throw Error('@trimoz/forms-library - Vuex GlobalDataModule not registered');
    }
    return globalDataModule;
}
let GlobalDataModule = class GlobalDataModule extends VuexModule {
    constructor(options) {
        super(options);
        this.formAppData = {};
        this.lastEmittedEvent = { event: '', data: undefined };
        this.isEmptyMemoryAidForm = true;
    }
    /**
     * Vuex Getter
     *
     * Retrieve the app data for the form
     * Data related to the theme, display, acl, see FormAppData interface
     */
    get getFormAppData() {
        return this.formAppData;
    }
    /**
     * Vuex Getter
     *
     * Return the last emitted event
     * Form components can watch this value and emit events in a more modular way
     */
    get getLastEmittedEvent() {
        return this.lastEmittedEvent;
    }
    /**
     * Vuex Action
     *
     * Set the app data for the form
     * Data related to the theme, display, acl, see FormAppData interface
     *
     * @param appData
     */
    setFormAppData(appData) {
        this.formAppData = appData;
    }
    /**
     * Vuex Action
     *
     * Set the last emitted event
     * Form components can watch the value from the getter and emit events in a more modular way
     *
     * @param event
     */
    emitEvent(event) {
        this.lastEmittedEvent = event;
    }
    setEmptyMemoryAidForm(isEmpty) {
        this.isEmptyMemoryAidForm = isEmpty;
    }
    get getEmptyMemoryAidForm() {
        return this.isEmptyMemoryAidForm;
    }
};
__decorate([
    Action
], GlobalDataModule.prototype, "setFormAppData", null);
__decorate([
    Action
], GlobalDataModule.prototype, "emitEvent", null);
__decorate([
    Action
], GlobalDataModule.prototype, "setEmptyMemoryAidForm", null);
GlobalDataModule = __decorate([
    Module({ generateMutationSetters: true })
], GlobalDataModule);

let HeaderToolbar$7 = class HeaderToolbar extends Vue {
    get i18n() {
        return this.$i18n.t('influenza.general');
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get hasTransmitAccess() {
        return globalDataVuex().getFormAppData.hasTransmitAccess;
    }
    get hasExportAccess() {
        return globalDataVuex().getFormAppData.hasExportAccess;
    }
    get lockForm() {
        return globalDataVuex().getFormAppData.lockForm;
    }
    get exportLoading() {
        return globalDataVuex().getFormAppData.exportLoading;
    }
    get saveLoading() {
        return globalDataVuex().getFormAppData.saveLoading;
    }
    get isTransmit() {
        let status = this.item.status;
        return status.includes('_transmitted');
    }
    closeModalForm() {
        globalDataVuex().emitEvent({ event: 'close', data: true });
    }
    submitTransmitForm(oldStatus) {
        let newStatus;
        if (this.isTransmit) {
            newStatus = oldStatus.replace('_transmitted', '');
        }
        else {
            newStatus = oldStatus + '_transmitted';
        }
        globalDataVuex().emitEvent({ event: 'submit-transmit-form', data: newStatus });
    }
    exportToPdf() {
        globalDataVuex().emitEvent({ event: 'export-to-pdf', data: true });
    }
    saveTemporaryForm() {
        globalDataVuex().emitEvent({ event: 'save-temporary-form', data: true });
    }
};
__decorate([
    Prop()
], HeaderToolbar$7.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], HeaderToolbar$7.prototype, "item", void 0);
HeaderToolbar$7 = __decorate([
    Component
], HeaderToolbar$7);
var script$11 = HeaderToolbar$7;

/* script */
            const __vue_script__$11 = script$11;
            
/* template */
var __vue_render__$11 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-toolbar",
    { attrs: { card: "", dark: "", color: "primary" } },
    [
      _c(
        "v-btn",
        { attrs: { icon: "", dark: "" }, on: { click: _vm.closeModalForm } },
        [_c("v-icon", [_vm._v("close")])],
        1
      ),
      _vm._v(" "),
      _c("v-toolbar-title", [_vm._v(_vm._s(_vm.i18n.title))]),
      _vm._v(" "),
      _c("v-spacer"),
      _vm._v(" "),
      _c(
        "v-toolbar-items",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: !_vm.vuetifyBreakpoint.xsOnly,
              expression: "!vuetifyBreakpoint.xsOnly"
            }
          ]
        },
        [
          _vm.hasTransmitAccess
            ? _c(
                "v-btn",
                {
                  attrs: { dark: "", flat: "" },
                  on: {
                    click: function($event) {
                      return _vm.submitTransmitForm(_vm.item.status)
                    }
                  }
                },
                [
                  !_vm.isTransmit
                    ? _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsTransmitted))
                      ])
                    : _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsNotTransmitted))
                      ])
                ]
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.hasExportAccess
            ? _c(
                "v-btn",
                {
                  attrs: { loading: _vm.exportLoading, dark: "", flat: "" },
                  on: { click: _vm.exportToPdf }
                },
                [_vm._v("\n      " + _vm._s(_vm.i18n.exportToPdf) + "\n    ")]
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              attrs: {
                dark: "",
                flat: "",
                disabled: _vm.lockForm,
                loading: _vm.saveLoading
              },
              on: { click: _vm.saveTemporaryForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.save) + "\n    ")]
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$11 = [];
__vue_render__$11._withStripped = true;

  /* style */
  const __vue_inject_styles__$11 = undefined;
  /* scoped */
  const __vue_scope_id__$11 = undefined;
  /* functional template */
  const __vue_is_functional_template__$11 = false;
  /* component normalizer */
  function __vue_normalize__$11(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/HeaderToolbar.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var HeaderToolbar$6 = __vue_normalize__$11(
    { render: __vue_render__$11, staticRenderFns: __vue_staticRenderFns__$11 },
    __vue_inject_styles__$11,
    __vue_script__$11,
    __vue_scope_id__$11,
    __vue_is_functional_template__$11);

let FormRules = class FormRules extends Vue {
    constructor() {
        super(...arguments);
        this.nam = (v) => /^[A-Z]{4}\d{6}[A-HJ-NP-Z1-9]\d$/g.test(v) || this.$i18n.t('influenza.base.errors.invalidNam');
        this.validDate = (v) => {
            if (!v)
                return true;
            const regex = v.length === 8 ? /(\d{4})(\d{2})(\d{2})/ : /(\d{4})[-](\d{2})[-](\d{2})/;
            const parts = regex.exec(v);
            if (parts) {
                const date = new Date((+parts[1]), (+parts[2]) - 1, (+parts[3]));
                if ((date.getDate() === parseInt(parts[3])) &&
                    (date.getMonth() === parseInt(parts[2]) - 1) &&
                    (date.getFullYear() === parseInt(parts[1]))) {
                    return true;
                }
            }
            // return '';
            return this.$i18n.t('influenza.base.errors.invalidDate');
        };
        this.validTime = (v) => {
            // hh:mm
            if (!v)
                return true;
            const regex = /(\d{2}):(\d{2})/;
            const parts = regex.exec(v);
            if (parts) {
                if (parseInt(parts[1]) <= 24 && parseInt(parts[2]) <= 59) {
                    return true;
                }
            }
            return this.$i18n.t('influenza.base.errors.invalidTime');
        };
        this.namExpirationDate = (v) => {
            const regex = /^(\d{4})(\d{2})$/;
            const parts = regex.exec(v);
            if (parts) {
                if (parseInt(parts[2]) >= 1 && parseInt(parts[2]) <= 12) {
                    return true;
                }
            }
            return this.$i18n.t('influenza.base.errors.invalidMonth');
        };
        this.postalCode = (v) => /^([abceghjklmnprstvxy][0-9][abceghjklmnprstvwxyz][0-9][abceghjklmnprstvwxyz][0-9])$/i.test(v) || /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(v) || this.$i18n.t('influenza.base.errors.invalidPostalCode');
        // Telephone validation (Required)
        this.telephone2 = (v) => /^\d{3}\d{3}\d{4}$/.test(v) || this.$i18n.t('influenza.base.errors.phone2short');
        this.shortTelephone = (v) => /^\d{3}\d{4}$/.test(v) || this.$i18n.t('influenza.base.errors.phone2short');
        this.regInd = (v) => /^\d{3}$/.test(v) || this.$i18n.t('influenza.base.errors.phone2short');
        this.email = (v) => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(v) || this.$i18n.t('influenza.base.errors.invalidEmail');
        this.iclscNumber = (v) => /^[0-9]{8}$/.test(v) || this.$i18n.t('influenza.base.errors.iclscNumber');
    }
    /* requiredField(errorMessage: any): (v: string) => boolean | string | TranslateResult  {
        return (v: string): boolean | string => (v && !!v && !!v.toString().trim()) || errorMessage;
    } */
    requiredField() {
        return (v) => (v && !!v && !!v.toString().trim()) || this.$i18n.t('base.errors.requiredField');
    }
};
FormRules = __decorate([
    Component
], FormRules);
let EventModifier = class EventModifier extends Vue {
    clickEmitter(event) {
        this.$emit('clicked', event);
    }
};
EventModifier = __decorate([
    Component
], EventModifier);
let VersionnedTranslation = class VersionnedTranslation extends Vue {
    i18nVersionned(path, formName, configVersionNumber) {
        const versionPath = configVersionNumber > 1 ? 'v' + configVersionNumber + '.' : '';
        // We want to change "smokingCessation.section.section_1..." into "smokingCessation.v2.section.section_1..."
        const relativePath = path.replace(/^([^.]+)\./, (match, p1) => {
            return p1 + '.' + versionPath;
        });
        return this.$i18n.t(`${formName}.${relativePath}`);
    }
};
VersionnedTranslation = __decorate([
    Component
], VersionnedTranslation);

// import toUpper from 'lodash/toUpper';
// eslint-disable-next-line @typescript-eslint/no-var-requires
let JsBarcode$2 = require('jsbarcode');
// eslint-disable-next-line @typescript-eslint/no-var-requires
let toUpper$3 = require('lodash/toUpper');
let TitleAndPatient$7 = class TitleAndPatient extends Vue {
    constructor() {
        super(...arguments);
        this.showNamBarCode = false;
    }
    get i18n() {
        return this.$i18n.t('influenza.titleAndPatient');
    }
    get hasPatientSectionAccess() {
        return globalDataVuex().getFormAppData.hasPatientSectionAccess;
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    mounted() {
        if (this.item.nam) {
            this.setNamBarCode();
        }
        if (!this.item.age && this.item.birthday) {
            this.getAge();
        }
    }
    getAge() {
        let patientBirthday = this.item.birthday;
        if (patientBirthday.length === 10) {
            patientBirthday = patientBirthday.replace(/-/g, '');
        }
        if (patientBirthday.length === 8) {
            let d = patientBirthday.match(/^(\d{4})(\d{2})(\d{2})$/);
            if (d) {
                patientBirthday = d[1] + '-' + d[2] + '-' + d[3];
                let today = new Date();
                let birthDate = new Date(patientBirthday);
                let age = today.getFullYear() - birthDate.getFullYear();
                let m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
                if (!isNaN(age) && age >= 0) {
                    this.item = {
                        ...this.item,
                        age: age,
                    };
                }
            }
        }
    }
    setNamBarCode() {
        const nam = this.item.nam;
        if (nam.length === 12) {
            JsBarcode$2('#code128', nam, {
                format: 'CODE128',
                width: 2,
                height: 35,
            });
            this.item.namCodeBar = this.namBarCodeToBase64();
            this.showNamBarCode = true;
        }
        else {
            this.item.namCodeBar = '';
            this.showNamBarCode = false;
        }
    }
    namBarCodeToBase64() {
        const canvas = document.createElement('canvas');
        JsBarcode$2(canvas, this.item.nam, { format: 'CODE128' });
        return canvas.toDataURL('image/png');
    }
    toUpper(postalCode) {
        this.item.postalCode = toUpper$3(postalCode);
    }
};
__decorate([
    Prop()
], TitleAndPatient$7.prototype, "data", void 0);
__decorate([
    Prop()
], TitleAndPatient$7.prototype, "modePharma", void 0);
__decorate([
    Prop()
], TitleAndPatient$7.prototype, "modeMsss", void 0);
__decorate([
    Prop()
], TitleAndPatient$7.prototype, "establishmentId", void 0);
__decorate([
    Prop()
], TitleAndPatient$7.prototype, "appointmentNumber", void 0);
__decorate([
    PropSync('data', { type: Object })
], TitleAndPatient$7.prototype, "item", void 0);
TitleAndPatient$7 = __decorate([
    Component({
        mixins: [FormRules],
    })
], TitleAndPatient$7);
var script$10 = TitleAndPatient$7;

/* script */
            const __vue_script__$10 = script$10;
            
/* template */
var __vue_render__$10 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        { attrs: { sm6: "", xs12: "" } },
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "", "fill-height": "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs6: "" } },
                [
                  _c("v-img", {
                    staticClass: "logoAH650",
                    attrs: {
                      src:
                        "https://static.trimoz.com/clicsante/images/forms/ah650/logo-AH-650.png",
                      alt: "logo-AH-650"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs6: "" } },
                [
                  _c("v-img", {
                    staticClass: "barcodeAH650",
                    attrs: { src: _vm.i18n.barcodeUrl, alt: "barcode-AH-650" }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.showNamBarCode,
                      expression: "showNamBarCode"
                    }
                  ],
                  attrs: { xs12: "" }
                },
                [_c("svg", { attrs: { id: "code128" } })]
              ),
              _vm._v(" "),
              _vm.modePharma
                ? _c(
                    "v-flex",
                    { attrs: { xs12: "", "align-self-end": "" } },
                    [
                      _c(
                        "v-layout",
                        { attrs: { row: "", wrap: "" } },
                        [
                          _c("v-flex", { attrs: { xs12: "" } }, [
                            _vm._v("nom pharma")
                          ]),
                          _vm._v(" "),
                          _c("v-flex", { attrs: { xs12: "" } }, [
                            _vm._v("address pharma")
                          ])
                        ],
                        1
                      )
                    ],
                    1
                  )
                : _vm._e()
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-flex",
        { attrs: { sm6: "", xs12: "" } },
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.name,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50",
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.name,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "name", $$v);
                      },
                      expression: "item.name"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.motherName,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50",
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.motherName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "motherName", $$v);
                      },
                      expression: "item.motherName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.fatherName,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.fatherName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "fatherName", $$v);
                      },
                      expression: "item.fatherName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs4: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.birthday,
                      placeholder: _vm.i18n.placeholder.birthday,
                      "hide-details": "",
                      mask: "####-##-##",
                      "return-masked-value": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "10",
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.validDate(v)
                        }
                      ]
                    },
                    on: { input: _vm.getAge },
                    model: {
                      value: _vm.item.birthday,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "birthday", $$v);
                      },
                      expression: "item.birthday"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c("v-flex", {
                staticClass: "border-bottom",
                attrs: { xs4: "" }
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "text-xs-center border-left border-bottom",
                  attrs: { xs4: "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: "",
                        "hide-details": "",
                        disabled: !_vm.hasPatientSectionAccess
                      },
                      model: {
                        value: _vm.item.sex,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "sex", $$v);
                        },
                        expression: "item.sex"
                      }
                    },
                    [
                      _c("v-radio", {
                        key: "M",
                        attrs: { label: "M", value: "M" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        key: "F",
                        attrs: { label: "F", value: "F" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.nam,
                      mask: "AAAA #### ##N#",
                      "hide-details": "",
                      placeholder: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      box: "",
                      rules: [
                        function(v) {
                          return !v || _vm.nam(v)
                        }
                      ]
                    },
                    on: { input: _vm.setNamBarCode },
                    model: {
                      value: _vm.item.nam,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "nam", $$v);
                      },
                      expression: "item.nam"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.expiryDate,
                      placeholder: _vm.i18n.placeholder.expiryDate,
                      disabled: !_vm.hasPatientSectionAccess,
                      "hide-details": "",
                      mask: "####-##",
                      box: "",
                      rules: [
                        function(v) {
                          return !v || _vm.namExpirationDate(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.expiryDate,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "expiryDate", $$v);
                      },
                      expression: "item.expiryDate"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.address,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: _vm.i18n.placeholder.address,
                      "hide-details": "",
                      box: "",
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.address,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "address", $$v);
                      },
                      expression: "item.address"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.city,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: "Québec",
                      "hide-details": "",
                      box: "",
                      maxlength: "40"
                    },
                    model: {
                      value: _vm.item.city,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "city", $$v);
                      },
                      expression: "item.city"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.postalCode,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: "A1A1A1",
                      "hide-details": "",
                      box: "",
                      maxlength: "6",
                      rules: [
                        function(v) {
                          return !v || _vm.postalCode(v)
                        }
                      ]
                    },
                    on: {
                      input: function($event) {
                        return _vm.toUpper(_vm.item.postalCode)
                      }
                    },
                    model: {
                      value: _vm.item.postalCode,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "postalCode", $$v);
                      },
                      expression: "item.postalCode"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.applicationNumber,
                      disabled: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "10"
                    },
                    model: {
                      value: _vm.establishmentId,
                      callback: function($$v) {
                        _vm.establishmentId = $$v;
                      },
                      expression: "establishmentId"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.appointmentNumber,
                      disabled: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "10"
                    },
                    model: {
                      value: _vm.appointmentNumber,
                      callback: function($$v) {
                        _vm.appointmentNumber = $$v;
                      },
                      expression: "appointmentNumber"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$10 = [];
__vue_render__$10._withStripped = true;

  /* style */
  const __vue_inject_styles__$10 = function (inject) {
    if (!inject) return
    inject("data-v-4da3b858_0", { source: "\n.logoAH650[data-v-4da3b858] {\n  width: 200px;\n}\n.barcodeAH650[data-v-4da3b858] {\n  margin-top: 25px;\n  width: 175px;\n}\n", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/TitleAndPatient.vue"],"names":[],"mappings":";AAiUA;EACA,YAAA;AACA;AAEA;EACA,gBAAA;EACA,YAAA;AACA","file":"TitleAndPatient.vue","sourcesContent":["<template>\n  <v-layout row wrap>\n\n    <!-- LOGO & CODEBAR -->\n    <v-flex sm6 xs12 >\n      <v-layout row wrap fill-height>\n        <v-flex xs6>\n          <v-img\n            src=\"https://static.trimoz.com/clicsante/images/forms/ah650/logo-AH-650.png\"\n            alt=\"logo-AH-650\"\n            class=\"logoAH650\"/>\n        </v-flex>\n        <v-flex xs6>\n          <v-img\n              :src=\"i18n.barcodeUrl\"\n              alt=\"barcode-AH-650\" \n              class=\"barcodeAH650\" \n          />\n        </v-flex>\n        <v-flex xs12 v-show=\"showNamBarCode\">\n          <svg id=\"code128\"></svg>\n        </v-flex>\n\n        <v-flex v-if=\"modePharma\" xs12 align-self-end>\n          <v-layout row wrap>\n            <v-flex xs12>nom pharma</v-flex>\n            <v-flex xs12>address pharma</v-flex>\n          </v-layout>\n        </v-flex>\n      </v-layout>\n    </v-flex>\n\n    <v-flex sm6 xs12>\n\n      <!-- USER'S INFORMATION -->\n      <v-layout row wrap class=\"border-all\">\n        <v-flex xs12>\n          <v-text-field\n              v-model=\"item.name\"\n              :label=\"'* ' + i18n.name\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n              :rules=\"[\n                !appliedRequiredField || requiredField()\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs12>\n          <v-text-field\n              :label=\"'* ' + i18n.motherName\"\n              v-model=\"item.motherName\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n              :rules=\"[\n                !appliedRequiredField || requiredField()\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs12>\n          <v-text-field\n              :label=\"i18n.fatherName\"\n              v-model=\"item.fatherName\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n          />\n        </v-flex>\n        <v-flex xs4 class=\"\">\n          <v-text-field\n              v-model=\"item.birthday\"\n              :label=\"'* ' + i18n.birthday\"\n              :placeholder=\"i18n.placeholder.birthday\"\n              hide-details\n              mask=\"####-##-##\"\n              return-masked-value\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"10\"\n              @input=\"getAge\"\n              :rules=\"[\n                !appliedRequiredField || requiredField(),\n                v => !v || validDate(v)\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs4 class=\"border-bottom\">\n        </v-flex>\n        <v-flex xs4 class=\"text-xs-center border-left border-bottom\">\n          <v-radio-group\n              row\n              v-model=\"item.sex\"\n              hide-details\n              :disabled=\"!hasPatientSectionAccess\"\n          >\n            <v-radio\n                key=\"M\"\n                label=\"M\"\n                value=\"M\"\n            />\n            <v-radio\n                key=\"F\"\n                label=\"F\"\n                value=\"F\"\n            />\n          </v-radio-group>\n        </v-flex>\n\n        <v-flex xs7 :class=\"vuetifyBreakpoint.smAndDown ? '' : 'border-right'\">\n          <v-text-field\n              :label=\"i18n.nam\"\n              mask=\"AAAA #### ##N#\"\n              v-model=\"item.nam\"\n              hide-details\n              placeholder=\"\"\n              :disabled=\"!hasPatientSectionAccess\"\n              @input=\"setNamBarCode\"\n              box\n              :rules=\"[\n                v => !v || nam(v)\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs5>\n          <v-text-field\n              :label=\"i18n.expiryDate\"\n              v-model=\"item.expiryDate\"\n              :placeholder=\"i18n.placeholder.expiryDate\"\n              :disabled=\"!hasPatientSectionAccess\"\n              hide-details\n              mask=\"####-##\"\n              box\n              :rules=\"[\n                v => !v || namExpirationDate(v)\n              ]\"\n          />\n        </v-flex>\n\n        <v-flex xs12>\n          <v-text-field\n              :label=\"i18n.address\"\n              v-model=\"item.address\"\n              :disabled=\"!hasPatientSectionAccess\"\n              :placeholder=\"i18n.placeholder.address\"\n              hide-details\n              box\n              maxlength=\"50\"\n          />\n        </v-flex>\n\n        <v-flex xs7 :class=\"vuetifyBreakpoint.smAndDown ? '' : 'border-right'\">\n          <v-text-field\n              :label=\"i18n.city\"\n              v-model=\"item.city\"\n              :disabled=\"!hasPatientSectionAccess\"\n              placeholder=\"Québec\"\n              hide-details\n              box\n              maxlength=\"40\"\n          />\n        </v-flex>\n        <v-flex xs5>\n          <v-text-field\n              :label=\"i18n.postalCode\"\n              v-model=\"item.postalCode\"\n              :disabled=\"!hasPatientSectionAccess\"\n              placeholder=\"A1A1A1\"\n              hide-details\n              box\n              maxlength=\"6\"\n              @input=\"toUpper(item.postalCode)\"\n              :rules=\"[\n                v => !v || postalCode(v)\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs7 :class=\"vuetifyBreakpoint.smAndDown ? '' : 'border-right'\">\n          <v-text-field\n              :label=\"i18n.applicationNumber\"\n              v-model=\"establishmentId\"\n              disabled\n              hide-details\n              box\n              maxlength=\"10\"\n          />\n        </v-flex>\n        <v-flex xs5>\n          <v-text-field\n              :label=\"i18n.appointmentNumber\"\n              v-model=\"appointmentNumber\"\n              disabled\n              hide-details\n              box\n              maxlength=\"10\"\n          />\n        </v-flex>\n      </v-layout>\n    </v-flex>\n  </v-layout>\n</template>\n\n<script lang=\"ts\">\nimport { Component, Prop, PropSync, Vue } from 'vue-property-decorator';\nimport { FormRules } from '@/form/mixins/mixins';\nimport { globalDataVuex } from '@/store';\nimport { LocaleMessages } from 'vue-i18n';\n// import toUpper from 'lodash/toUpper';\n\n// eslint-disable-next-line @typescript-eslint/no-var-requires\nlet JsBarcode = require('jsbarcode');\n// eslint-disable-next-line @typescript-eslint/no-var-requires\nlet toUpper = require('lodash/toUpper');\n\n@Component({\n  mixins: [ FormRules ],\n})\nexport default class TitleAndPatient extends Vue {\n  @Prop() readonly data!: Record<string, unknown>;\n  @Prop() readonly modePharma!: boolean;\n  @Prop() readonly modeMsss!: boolean;\n  @Prop() readonly establishmentId!: string;\n  @Prop() readonly appointmentNumber!: string;\n  @PropSync('data', { type: Object }) item!: Record<string, unknown>;\n\n  showNamBarCode = false;\n\n  get i18n(): LocaleMessages {\n    return this.$i18n.t('influenza.titleAndPatient') as LocaleMessages;\n  }\n\n  get hasPatientSectionAccess() {\n    return globalDataVuex().getFormAppData.hasPatientSectionAccess;\n  }\n\n  get vuetifyBreakpoint() {\n    return globalDataVuex().getFormAppData.vuetifyBreakpoint;\n  }\n\n  get appliedRequiredField() {\n    return globalDataVuex().getFormAppData.appliedRequiredField;\n  }\n\n  mounted() {\n    if (this.item.nam) {\n      this.setNamBarCode();\n    }\n\n    if (!this.item.age && this.item.birthday) {\n      this.getAge();\n    }\n  }\n\n  getAge() {\n    let patientBirthday: string = this.item.birthday as string;\n\n    if (patientBirthday.length === 10) {\n      patientBirthday = patientBirthday.replace(/-/g, '');\n    }\n\n    if (patientBirthday.length === 8) {\n      let d = patientBirthday.match(/^(\\d{4})(\\d{2})(\\d{2})$/);\n      if (d) {\n        patientBirthday = d[1] + '-' + d[2] + '-' + d[3];\n\n        let today = new Date();\n        let birthDate = new Date(patientBirthday);\n        let age = today.getFullYear() - birthDate.getFullYear();\n        let m = today.getMonth() - birthDate.getMonth();\n\n        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {\n          age--;\n        }\n\n        if (!isNaN(age) && age >= 0) {\n          this.item = {\n            ...this.item,\n            age: age,\n          };\n        }\n      }\n    }\n  }\n\n  setNamBarCode() {\n    const nam: string = this.item.nam as string;\n\n    if (nam.length === 12) {\n      JsBarcode('#code128', nam, {\n        format: 'CODE128',\n        width: 2,\n        height: 35,\n      });\n\n      this.item.namCodeBar = this.namBarCodeToBase64();\n      this.showNamBarCode = true;\n    }\n    else {\n      this.item.namCodeBar = '';\n      this.showNamBarCode = false;\n    }\n  }\n\n  namBarCodeToBase64(){\n    const canvas = document.createElement('canvas');\n    JsBarcode(canvas, this.item.nam, { format: 'CODE128' });\n    return canvas.toDataURL('image/png');\n  }\n\n  toUpper(postalCode: string) {\n    this.item.postalCode = toUpper(postalCode);\n  }\n}\n</script>\n<style scoped>\n.logoAH650 {\n  width: 200px;\n}\n\n.barcodeAH650 {\n  margin-top: 25px;\n  width: 175px;\n}\n</style>\n\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$10 = "data-v-4da3b858";
  /* module identifier */
  const __vue_module_identifier__$l = undefined;
  /* functional template */
  const __vue_is_functional_template__$10 = false;
  /* component normalizer */
  function __vue_normalize__$10(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/TitleAndPatient.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$l() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$l.styles || (__vue_create_injector__$l.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var TitleAndPatient$6 = __vue_normalize__$10(
    { render: __vue_render__$10, staticRenderFns: __vue_staticRenderFns__$10 },
    __vue_inject_styles__$10,
    __vue_script__$10,
    __vue_scope_id__$10,
    __vue_is_functional_template__$10,
    __vue_module_identifier__$l,
    __vue_create_injector__$l);

let GeneralInformation$7 = class GeneralInformation extends Vue {
    get i18n() {
        return this.$i18n.t('influenza.generalInformation');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasGeneralSectionAccess() {
        return this.globalData.getFormAppData.hasGeneralSectionAccess;
    }
};
__decorate([
    Prop()
], GeneralInformation$7.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], GeneralInformation$7.prototype, "item", void 0);
GeneralInformation$7 = __decorate([
    Component({
        mixins: [FormRules],
    })
], GeneralInformation$7);
var script$$ = GeneralInformation$7;

/* script */
            const __vue_script__$$ = script$$;
            
/* template */
var __vue_render__$$ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c("v-flex", { staticClass: "black px-1", attrs: { xs12: "" } }, [
                _c("span", { staticClass: "section-title white--text" }, [
                  _vm._v(_vm._s(_vm.i18n.title))
                ])
              ]),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 px-1 font-weight-bold border-bottom section-subtitle-age",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s(_vm.i18n.user14YearsOrOlder) +
                      "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.phone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.homePhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "homePhone", $$v);
                      },
                      expression: "item.homePhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm4: "", xs8: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.otherPhone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15"
                    },
                    model: {
                      value: _vm.item.otherPhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "otherPhone", $$v);
                      },
                      expression: "item.otherPhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom user-phone", attrs: { xs4: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        "hide-details": "",
                        disabled: !_vm.hasGeneralSectionAccess
                      },
                      model: {
                        value: _vm.item.otherPhoneType,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "otherPhoneType", $$v);
                        },
                        expression: "item.otherPhoneType"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.cell, value: "cell" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.work, value: "work" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.email,
                      disabled: !_vm.hasGeneralSectionAccess,
                      type: "email",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "75",
                      rules: [
                        function(v) {
                          return !v || _vm.email(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.email,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "email", $$v);
                      },
                      expression: "item.email"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 px-1 font-weight-bold border-bottom user-below-age",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.userUnder14) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm6: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.authorizedPerson,
                      placeholder: _vm.i18n.placeholder.authorizedPerson,
                      disabled: !_vm.hasGeneralSectionAccess,
                      "hide-details": "",
                      box: "",
                      maxlength: "70"
                    },
                    model: {
                      value: _vm.item.authorizedPersonName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonName", $$v);
                      },
                      expression: "item.authorizedPersonName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm6: "", xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.email,
                      disabled: !_vm.hasGeneralSectionAccess,
                      type: "email",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "75",
                      rules: [
                        function(v) {
                          return !v || _vm.email(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonEmail,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonEmail", $$v);
                      },
                      expression: "item.authorizedPersonEmail"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom pl-1 pb-2", attrs: { xs12: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasGeneralSectionAccess,
                        "hide-details": ""
                      },
                      model: {
                        value: _vm.item.authorizedPersonLegalRepresentative,
                        callback: function($$v) {
                          _vm.$set(
                            _vm.item,
                            "authorizedPersonLegalRepresentative",
                            $$v
                          );
                        },
                        expression: "item.authorizedPersonLegalRepresentative"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.mandatary,
                          value: "mandatary"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.guardian,
                          value: "guardian"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.curator,
                          value: "curator"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.publicCurator,
                          value: "publicCurator"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.spouse,
                          value: "spouse"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.closeRelative,
                          value: "closeRelative"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.personInterest,
                          value: "personInterest"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.parentalAuthority,
                          value: "parentalAuthority"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.phone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonHomePhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonHomePhone", $$v);
                      },
                      expression: "item.authorizedPersonHomePhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm4: "", xs8: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.otherPhone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonOtherPhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonOtherPhone", $$v);
                      },
                      expression: "item.authorizedPersonOtherPhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom user-phone", attrs: { xs4: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        disabled: !_vm.hasGeneralSectionAccess,
                        "hide-details": ""
                      },
                      model: {
                        value: _vm.item.authorizedPersonOtherPhoneType,
                        callback: function($$v) {
                          _vm.$set(
                            _vm.item,
                            "authorizedPersonOtherPhoneType",
                            $$v
                          );
                        },
                        expression: "item.authorizedPersonOtherPhoneType"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.cell, value: "cell" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.work, value: "work" }
                      })
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$$ = [];
__vue_render__$$._withStripped = true;

  /* style */
  const __vue_inject_styles__$$ = undefined;
  /* scoped */
  const __vue_scope_id__$$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$$ = false;
  /* component normalizer */
  function __vue_normalize__$$(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/GeneralInformation/GeneralInformation.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var GeneralInformation$6 = __vue_normalize__$$(
    { render: __vue_render__$$, staticRenderFns: __vue_staticRenderFns__$$ },
    __vue_inject_styles__$$,
    __vue_script__$$,
    __vue_scope_id__$$,
    __vue_is_functional_template__$$);

let PreImmunization$5 = class PreImmunization extends Vue {
    constructor() {
        super(...arguments);
        // Number of questions
        this.questions = 6;
    }
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('influenza.preImmunization');
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasPreImmunisationSectionAccess() {
        return this.globalData.getFormAppData.hasPreImmunisationSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
};
__decorate([
    Prop()
], PreImmunization$5.prototype, "data", void 0);
__decorate([
    Prop()
], PreImmunization$5.prototype, "modePharma", void 0);
__decorate([
    Prop()
], PreImmunization$5.prototype, "modeMsss", void 0);
__decorate([
    PropSync('data', { type: Object })
], PreImmunization$5.prototype, "item", void 0);
PreImmunization$5 = __decorate([
    Component({
        mixins: [FormRules],
    })
], PreImmunization$5);
var script$_ = PreImmunization$5;

/* script */
            const __vue_script__$_ = script$_;
            
/* template */
var __vue_render__$_ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            {
              staticClass: "border-top border-left border-right",
              attrs: { row: "", wrap: "" }
            },
            [
              _c("v-flex", { staticClass: "black px-1", attrs: { xs12: "" } }, [
                _c("span", { staticClass: "section-title white--text" }, [
                  _vm._v(_vm._s("* " + _vm.i18n.title))
                ])
              ]),
              _vm._v(" "),
              _c("v-flex", {
                staticClass:
                  "font-weight-bold hidden-xs-only border-bottom border-right grey lighten-2",
                attrs: { xs1: "" }
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "font-weight-bold hidden-xs-only border-bottom border-right grey lighten-2 px-1 header-elements",
                  attrs: { md6: "", xs5: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.header.elements) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "font-weight-bold hidden-xs-only border-bottom border-right grey lighten-2 px-1",
                  attrs: { md2: "", xs3: "" }
                },
                [
                  _c("span", { staticClass: "header-question header-yes" }, [
                    _vm._v(_vm._s(_vm.i18n.header.yes))
                  ]),
                  _vm._v(" "),
                  _c("span", { staticClass: "header-question header-no" }, [
                    _vm._v(_vm._s(_vm.i18n.header.no))
                  ]),
                  _vm._v(" "),
                  _c("span", { staticClass: "header-question header-NA" }, [
                    _vm._v(_vm._s(_vm.i18n.header.NA))
                  ])
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "font-weight-bold hidden-xs-only border-bottom grey lighten-2 px-1 header-details",
                  attrs: { md3: "", xs3: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.header.details) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _vm._l(_vm.questions, function(i, index) {
                return [
                  _c(
                    "v-flex",
                    {
                      key: "col1-" + index,
                      staticClass: "text-xs-center font-weight-bold",
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      attrs: { xs1: "" }
                    },
                    [_vm._v("\n          " + _vm._s(i) + ".\n        ")]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col2-" + index,
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      attrs: { md6: "", sm5: "", xs12: "", "pr-3": "" }
                    },
                    [
                      _c("strong", { class: "question-title-" + i }, [
                        _vm._v(_vm._s("* " + _vm.i18n["title" + i]))
                      ]),
                      _c("br"),
                      _vm._v(" "),
                      _c("span", { class: "question-" + i }, [
                        _vm._v(_vm._s(_vm.i18n["question" + i]))
                      ])
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col3-" + index,
                      staticClass: "font-weight-bold",
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      attrs: { md2: "", sm3: "", xs12: "" }
                    },
                    [
                      _c(
                        "v-radio-group",
                        {
                          attrs: {
                            row: "",
                            disabled: !_vm.hasPreImmunisationSectionAccess,
                            rules: [
                              function(v) {
                                return (
                                  !_vm.appliedRequiredField ||
                                  v === 0 ||
                                  v === 1 ||
                                  v === -1 ||
                                  _vm.$t("base.errors.requiredField")
                                )
                              }
                            ]
                          },
                          model: {
                            value: _vm.item["preImmunization" + i],
                            callback: function($$v) {
                              _vm.$set(_vm.item, "preImmunization" + i, $$v);
                            },
                            expression: "item['preImmunization' + i]"
                          }
                        },
                        [
                          _c("v-radio", {
                            attrs: {
                              "aria-label": _vm.i18n.header.yes,
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.yes
                                : "",
                              value: 1
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              "aria-label": _vm.i18n.header.no,
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.no
                                : "",
                              value: 0
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              "aria-label": _vm.i18n.header.NA,
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.NA
                                : "",
                              value: -1
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col4-" + index,
                      staticClass: "border-bottom",
                      attrs: { md3: "", sm3: "", xs12: "" }
                    },
                    [
                      _c("v-text-field", {
                        class: _vm.vuetifyBreakpoint.xsOnly ? "ml-4" : "",
                        attrs: {
                          label: _vm.i18n.precision,
                          disabled: !_vm.hasPreImmunisationSectionAccess,
                          placeholder: "",
                          "hide-details": "",
                          box: "",
                          maxlength: "255"
                        },
                        model: {
                          value: _vm.item["preImmunizationPrecision" + i],
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "preImmunizationPrecision" + i,
                              $$v
                            );
                          },
                          expression: "item['preImmunizationPrecision' + i]"
                        }
                      })
                    ],
                    1
                  )
                ]
              })
            ],
            2
          ),
          _vm._v(" "),
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { staticClass: "px-1 caption", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.importantNote) + "\n      "
                  )
                ]
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$_ = [];
__vue_render__$_._withStripped = true;

  /* style */
  const __vue_inject_styles__$_ = undefined;
  /* scoped */
  const __vue_scope_id__$_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$_ = false;
  /* component normalizer */
  function __vue_normalize__$_(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/PreImmunization/PreImmunization.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var PreImmunization$4 = __vue_normalize__$_(
    { render: __vue_render__$_, staticRenderFns: __vue_staticRenderFns__$_ },
    __vue_inject_styles__$_,
    __vue_script__$_,
    __vue_scope_id__$_,
    __vue_is_functional_template__$_);

let AdministrativeReason$5 = class AdministrativeReason extends Vue {
    get i18n() {
        return this.$i18n.t('influenza.administration');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasReasonSectionAccess() {
        return this.globalData.getFormAppData.hasReasonSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
};
__decorate([
    Prop()
], AdministrativeReason$5.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], AdministrativeReason$5.prototype, "item", void 0);
AdministrativeReason$5 = __decorate([
    Component({
        mixins: [FormRules],
    })
], AdministrativeReason$5);
var script$Z = AdministrativeReason$5;

/* script */
            const __vue_script__$Z = script$Z;
            
/* template */
var __vue_render__$Z = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c("v-flex", { staticClass: "black px-1", attrs: { xs12: "" } }, [
                _c("span", { staticClass: "section-title white--text" }, [
                  _vm._v(_vm._s("* " + _vm.i18n.title))
                ])
              ]),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "pb-2", attrs: { xs12: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        "aria-label": "administration-reason",
                        disabled: !_vm.hasReasonSectionAccess,
                        "hide-details": "",
                        rules: [
                          !_vm.appliedRequiredField ||
                            _vm.requiredField(
                              _vm.$i18n.t("influenza.base.errors.requireField")
                            )
                        ]
                      },
                      model: {
                        value: _vm.item.administrationReason,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "administrationReason", $$v);
                        },
                        expression: "item.administrationReason"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.reason["1"],
                          value: "07 - Influenza"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.reason["2"],
                          value: "08 - Influenza"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.reason["3"],
                          value: "09 - Influenza"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.reason["4"],
                          value: "10 - Influenza"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.reason["5"],
                          value: "11 - Influenza"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.reason["6"],
                          value: "12 - Influenza"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.reason["7"],
                          value: "Out of program"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$Z = [];
__vue_render__$Z._withStripped = true;

  /* style */
  const __vue_inject_styles__$Z = undefined;
  /* scoped */
  const __vue_scope_id__$Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$Z = false;
  /* component normalizer */
  function __vue_normalize__$Z(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/AdministrativeReason/AdministrativeReason.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var AdministrativeReason$4 = __vue_normalize__$Z(
    { render: __vue_render__$Z, staticRenderFns: __vue_staticRenderFns__$Z },
    __vue_inject_styles__$Z,
    __vue_script__$Z,
    __vue_scope_id__$Z,
    __vue_is_functional_template__$Z);

let ConsentAndDecision$5 = class ConsentAndDecision extends Vue {
    constructor() {
        super(...arguments);
        // Number of consents
        this.consents = 3;
    }
    get i18n() {
        return this.$i18n.t('influenza.consentAndDecision');
    }
    get globalData() {
        return globalDataVuex();
    }
    get hasConsentSectionAccess() {
        return this.globalData.getFormAppData.hasConsentSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
};
__decorate([
    Prop()
], ConsentAndDecision$5.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], ConsentAndDecision$5.prototype, "item", void 0);
ConsentAndDecision$5 = __decorate([
    Component({
        mixins: [FormRules],
    })
], ConsentAndDecision$5);
var script$Y = ConsentAndDecision$5;

/* script */
            const __vue_script__$Y = script$Y;
            
/* template */
var __vue_render__$Y = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c("v-flex", { staticClass: "black px-1", attrs: { xs12: "" } }, [
                _c("span", { staticClass: "section-title white--text" }, [
                  _vm._v(_vm._s("* " + _vm.i18n.title))
                ])
              ]),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "", "pb-2": "" } },
                _vm._l(_vm.consents, function(i, index) {
                  return _c("v-checkbox", {
                    key: index,
                    attrs: {
                      label: _vm.i18n[i],
                      disabled: !_vm.hasConsentSectionAccess,
                      "hide-details": "",
                      rules: [
                        !_vm.appliedRequiredField ||
                          _vm.requiredField(
                            _vm.$i18n.t("influenza.base.errors.requireField")
                          )
                      ]
                    },
                    model: {
                      value: _vm.item["consentDecision" + i],
                      callback: function($$v) {
                        _vm.$set(_vm.item, "consentDecision" + i, $$v);
                      },
                      expression: "item['consentDecision' + i]"
                    }
                  })
                }),
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "px-1 font-weight-bold border-bottom border-top grey lighten-2 decision-subtitle",
                  attrs: { xs12: "" }
                },
                [_vm._v("\n        " + _vm._s(_vm.i18n.decision) + "\n      ")]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "checkbox-group-title-1",
                  attrs: {
                    xs12: "",
                    sm6: "",
                    "pl-1": "",
                    "pb-2": "",
                    "pt-1": ""
                  }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s("* " + _vm.i18n.patient) +
                      "\n        "
                  ),
                  _c(
                    "v-flex",
                    { attrs: { "ml-4": "" } },
                    [
                      _c("v-checkbox", {
                        attrs: {
                          label: _vm.i18n.patientConsentInfluenza,
                          disabled:
                            !_vm.hasConsentSectionAccess ||
                            _vm.item.refuseInfluenza,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              _vm.item.refuseInfluenza ||
                              _vm.item.consentPneumococcal ||
                              _vm.requiredField(
                                _vm.$i18n.t(
                                  "influenza.base.errors.requireField"
                                )
                              )
                          ]
                        },
                        model: {
                          value: _vm.item.consentInfluenza,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "consentInfluenza", $$v);
                          },
                          expression: "item.consentInfluenza"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-checkbox", {
                        attrs: {
                          label: _vm.i18n.patientRefuseInfluenza,
                          disabled:
                            !_vm.hasConsentSectionAccess ||
                            _vm.item.consentInfluenza,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              _vm.item.consentInfluenza ||
                              _vm.item.consentPneumococcal ||
                              _vm.requiredField(
                                _vm.$i18n.t(
                                  "influenza.base.errors.requireField"
                                )
                              )
                          ]
                        },
                        model: {
                          value: _vm.item.refuseInfluenza,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "refuseInfluenza", $$v);
                          },
                          expression: "item.refuseInfluenza"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "", sm6: "", "pt-1": "" } },
                [
                  _c("br"),
                  _vm._v(" "),
                  _c("v-checkbox", {
                    attrs: {
                      label: _vm.i18n.patientConsentPneumococcal,
                      disabled:
                        !_vm.hasConsentSectionAccess ||
                        _vm.item.refusePneumococcal,
                      "hide-details": ""
                    },
                    model: {
                      value: _vm.item.consentPneumococcal,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "consentPneumococcal", $$v);
                      },
                      expression: "item.consentPneumococcal"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-checkbox", {
                    attrs: {
                      label: _vm.i18n.patientRefusePneumococcal,
                      disabled:
                        !_vm.hasConsentSectionAccess ||
                        _vm.item.consentPneumococcal,
                      "hide-details": ""
                    },
                    model: {
                      value: _vm.item.refusePneumococcal,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "refusePneumococcal", $$v);
                      },
                      expression: "item.refusePneumococcal"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "employee-consent",
                  attrs: { xs12: "", "pl-1": "", "pb-2": "", "pt-1": "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.employee) + "\n        "
                  ),
                  _c(
                    "v-flex",
                    { attrs: { "ml-4": "" } },
                    [
                      _c("v-checkbox", {
                        attrs: {
                          label: _vm.i18n.employeeConsent,
                          disabled: !_vm.hasConsentSectionAccess,
                          "hide-details": ""
                        },
                        model: {
                          value: _vm.item.employeeConsent,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "employeeConsent", $$v);
                          },
                          expression: "item.employeeConsent"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "px-1 font-weight-bold border-bottom border-top grey lighten-2 consent-by",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s("* " + _vm.i18n.consentsRefuses.title) +
                      "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "", "pl-1": "", "pb-2": "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasConsentSectionAccess,
                        "hide-details": "",
                        "aria-label": "consents-refuses-obtained-by",
                        rules: [
                          !_vm.appliedRequiredField ||
                            _vm.requiredField(
                              _vm.$i18n.t("influenza.base.errors.requireField")
                            )
                        ]
                      },
                      model: {
                        value: _vm.item.consentRefuseObtainedFrom,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "consentRefuseObtainedFrom", $$v);
                        },
                        expression: "item.consentRefuseObtainedFrom"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.patient,
                          value: "patient"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.mandatary,
                          value: "mandatary"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.guardian,
                          value: "guardian"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.curator,
                          value: "curator"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.publicCurator,
                          value: "publicCurator"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.closeRelative,
                          value: "closeRelative"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.spouse,
                          value: "spouse"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.specialInterest,
                          value: "specialInterest"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.consentsRefuses.parentalAuthority,
                          value: "parentalAuthority"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$Y = [];
__vue_render__$Y._withStripped = true;

  /* style */
  const __vue_inject_styles__$Y = undefined;
  /* scoped */
  const __vue_scope_id__$Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$Y = false;
  /* component normalizer */
  function __vue_normalize__$Y(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/ConsentAndDecision/ConsentAndDecision.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var ConsentAndDecision$4 = __vue_normalize__$Y(
    { render: __vue_render__$Y, staticRenderFns: __vue_staticRenderFns__$Y },
    __vue_inject_styles__$Y,
    __vue_script__$Y,
    __vue_scope_id__$Y,
    __vue_is_functional_template__$Y);

let ProfessionalInformation$7 = class ProfessionalInformation extends Vue {
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('influenza.professionalInformation');
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get showEvaluatorBtn() {
        return this.globalData.getFormAppData.showEvaluatorBtn;
    }
    get isProfileComplete() {
        return this.globalData.getFormAppData.isProfileComplete;
    }
    get hasProfessionalSectionAccess() {
        return this.globalData.getFormAppData.hasProfessionalSectionAccess;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    openResourceForm() {
        this.globalData.emitEvent({ event: 'open-resource-form', data: true });
    }
    insertMyEvaluatorInfo() {
        this.globalData.emitEvent({ event: 'insert-my-evaluator-info', data: true });
    }
};
__decorate([
    Prop()
], ProfessionalInformation$7.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], ProfessionalInformation$7.prototype, "item", void 0);
ProfessionalInformation$7 = __decorate([
    Component({
        mixins: [FormRules],
    })
], ProfessionalInformation$7);
var script$X = ProfessionalInformation$7;

/* script */
            const __vue_script__$X = script$X;
            
/* template */
var __vue_render__$X = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { staticClass: "black px-1", attrs: { md8: "", xs12: "" } },
                [
                  _c("span", { staticClass: "white--text section-title" }, [
                    _vm._v(_vm._s(_vm.i18n.title))
                  ])
                ]
              ),
              _vm._v(" "),
              _vm.showEvaluatorBtn
                ? _c(
                    "v-flex",
                    {
                      staticClass: "black",
                      class: _vm.vuetifyBreakpoint.smAndDown
                        ? "text-xs-left"
                        : "text-xs-right",
                      attrs: { md4: "", xs12: "" }
                    },
                    [
                      _vm.isProfileComplete
                        ? _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: {
                                "aria-label": "insert-evaluator-info",
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.insertMyEvaluatorInfo }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.getMyInformations) +
                                  "\n        "
                              )
                            ]
                          )
                        : _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: {
                                "aria-label": "complete-profile",
                                disabled: _vm.lockForm,
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.openResourceForm }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.completeMyProfile) +
                                  "\n        "
                              )
                            ]
                          )
                    ],
                    1
                  )
                : _c("v-flex", {
                    staticClass: "black",
                    class: _vm.vuetifyBreakpoint.smAndDown
                      ? "text-xs-left"
                      : "text-xs-right",
                    attrs: { md4: "", xs12: "" }
                  }),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      "aria-label": "professional-name",
                      disabled: !_vm.hasProfessionalSectionAccess,
                      label: "* " + _vm.i18n.name,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "100",
                      rules: [
                        !_vm.appliedRequiredField ||
                          _vm.requiredField(
                            _vm.$i18n.t("influenza.base.errors.requireField")
                          )
                      ]
                    },
                    model: {
                      value: _vm.item.professionalName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "professionalName", $$v);
                      },
                      expression: "item.professionalName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "px-1 font-weight-bold border-bottom border-top grey lighten-2",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s("* " + _vm.i18n.profession.title) +
                      "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "border-bottom",
                  attrs: { xs12: "", "pl-1": "", "pb-2": "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        "aria-label": "professional-profession",
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasProfessionalSectionAccess,
                        "hide-details": "",
                        rules: [
                          !_vm.appliedRequiredField ||
                            _vm.requiredField(
                              _vm.$i18n.t("influenza.base.errors.requireField")
                            )
                        ]
                      },
                      model: {
                        value: _vm.item.professionalProfession,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "professionalProfession", $$v);
                        },
                        expression: "item.professionalProfession"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.profession.nurse,
                          value: "nurse"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.profession.physician,
                          value: "physician"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.profession.inhalotherapist,
                          value: "inhalotherapist"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.profession.midwife,
                          value: "midwife"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.profession.pharmacist,
                          value: "pharmacist"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm3: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      "aria-label": "professional-licence-number",
                      label: "* " + _vm.i18n.licenceNumber,
                      disabled: !_vm.hasProfessionalSectionAccess,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "25",
                      rules: [
                        !_vm.appliedRequiredField ||
                          _vm.requiredField(
                            _vm.$i18n.t("influenza.base.errors.requireField")
                          )
                      ]
                    },
                    model: {
                      value: _vm.item.professionalLicenceNumber,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "professionalLicenceNumber", $$v);
                      },
                      expression: "item.professionalLicenceNumber"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm9: "", xs12: "" } },
                [
                  !_vm.item.professionalSignature
                    ? _c("v-text-field", {
                        attrs: {
                          "aria-label": "professional-signature",
                          label: "* " + _vm.i18n.professionalSignature,
                          disabled: "",
                          placeholder: "",
                          "hide-details": "",
                          box: "",
                          maxlength: "100",
                          rules: [
                            !_vm.appliedRequiredField ||
                              _vm.requiredField(
                                _vm.$i18n.t(
                                  "influenza.base.errors.requireField"
                                )
                              )
                          ]
                        }
                      })
                    : [
                        _c(
                          "v-layout",
                          { attrs: { row: "", wrap: "" } },
                          [
                            _c("v-flex", { attrs: { xs12: "", "pl-2": "" } }, [
                              _c("span", { staticClass: "caption" }, [
                                _vm._v(_vm._s(_vm.i18n.professionalSignature))
                              ])
                            ]),
                            _vm._v(" "),
                            _c(
                              "v-flex",
                              {
                                staticStyle: {
                                  "margin-top": "-8px",
                                  "margin-bottom": "-8px"
                                }
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: _vm.item.professionalSignature,
                                    alt: "Signature"
                                  }
                                })
                              ]
                            )
                          ],
                          1
                        )
                      ]
                ],
                2
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$X = [];
__vue_render__$X._withStripped = true;

  /* style */
  const __vue_inject_styles__$X = undefined;
  /* scoped */
  const __vue_scope_id__$X = undefined;
  /* functional template */
  const __vue_is_functional_template__$X = false;
  /* component normalizer */
  function __vue_normalize__$X(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/ProfessionalInformation/ProfessionalInformation.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var ProfessionalInformation$6 = __vue_normalize__$X(
    { render: __vue_render__$X, staticRenderFns: __vue_staticRenderFns__$X },
    __vue_inject_styles__$X,
    __vue_script__$X,
    __vue_scope_id__$X,
    __vue_is_functional_template__$X);

let VaccineDetailsInfluenza$2 = class VaccineDetailsInfluenza extends Vue {
    constructor() {
        super(...arguments);
        this.vaccineType = [];
        this.administrationType = [];
        this.disabledIntramuscularChoice = false;
        this.disabledIntranasalChoice = false;
    }
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('influenza.vaccineDetails.influenza');
    }
    get prefillFields() {
        return this.globalData.getFormAppData.prefillFields;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasDetailSectionAccess() {
        return this.globalData.getFormAppData.hasDetailSectionAccess;
    }
    get hasDetailSectionVaccineNumberAccess() {
        return this.globalData.getFormAppData.hasDetailSectionVaccineNumberAccess;
    }
    get hasDetailSectionVaccineTypeAccess() {
        return this.globalData.getFormAppData.hasDetailSectionVaccineTypeAccess;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    mounted() {
        this.validateInjectionsite(false);
    }
    created() {
        this.vaccineType = [
            { id: null, name: '' },
            { id: 'afluria', name: this.i18n.type.afluria },
            { id: 'fluad', name: this.i18n.type.fluad },
            { id: 'fluadPediatric', name: this.i18n.type.fluadPediatric },
            { id: 'flucelvax', name: this.i18n.type.flucelvax },
            { id: 'flulaval', name: this.i18n.type.flulaval },
            { id: 'fluzoneQuadrivalent', name: this.i18n.type.fluzoneQuadrivalent },
            { id: 'fluzoneDose', name: this.i18n.type.fluzoneDose },
            { id: 'fluMist', name: this.i18n.type.fluMist },
            { id: 'influvacTetra', name: this.i18n.type.influvacTetra },
            { id: 'supemtek', name: this.i18n.type.supemtek },
        ];
        this.administrationType = [
            { id: 'intramuscular', name: this.i18n.administrationType.intramuscular },
            { id: 'intranasal', name: this.i18n.administrationType.intranasal },
        ];
        if (!this.item.vaccineDetailsInfluenzaBatchNumber) {
            this.item.vaccineDetailsInfluenzaBatchNumber = this.prefillFields.vaccineDetailsInfluenzaBatchNumber;
        }
        if (!this.item.vaccineDetailsInfluenzaName) {
            this.item.vaccineDetailsInfluenzaName = this.prefillFields.vaccineDetailsInfluenzaName;
        }
        if (!this.item.vaccineDetailsInfluenzaOtherName) {
            this.item.vaccineDetailsInfluenzaOtherName = this.prefillFields.vaccineDetailsInfluenzaOtherName;
        }
        if (!this.item.vaccineDetailsInfluenzaDose) {
            this.item.vaccineDetailsInfluenzaDose = this.prefillFields.vaccineDetailsInfluenzaDose;
        }
        if (!this.item.vaccineDetailsInfluenzaAdministration) {
            this.item.vaccineDetailsInfluenzaAdministration = this.prefillFields.vaccineDetailsInfluenzaAdministration;
        }
    }
    validateInjectionsite(resetInjectionSite = true) {
        this.disabledIntranasalChoice = false;
        this.disabledIntramuscularChoice = false;
        if (this.item.vaccineDetailsInfluenzaAdministration === 'intramuscular') {
            this.disabledIntranasalChoice = true;
        }
        if (this.item.vaccineDetailsInfluenzaAdministration === 'intranasal') {
            this.disabledIntramuscularChoice = true;
        }
        if (resetInjectionSite) {
            this.item.vaccineDetailsInfluenzaInjectionSite = '';
        }
    }
};
__decorate([
    Prop()
], VaccineDetailsInfluenza$2.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], VaccineDetailsInfluenza$2.prototype, "item", void 0);
__decorate([
    Prop()
], VaccineDetailsInfluenza$2.prototype, "shouldCheckValidation", void 0);
VaccineDetailsInfluenza$2 = __decorate([
    Component({
        mixins: [FormRules],
    })
], VaccineDetailsInfluenza$2);
var script$W = VaccineDetailsInfluenza$2;

/* script */
            const __vue_script__$W = script$W;
            
/* template */
var __vue_render__$W = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c("v-flex", { staticClass: "black px-1", attrs: { xs12: "" } }, [
                _c(
                  "span",
                  {
                    staticClass: "vaccine-details-title white--text",
                    attrs: { "data-testid": "vaccine-details-influenza" }
                  },
                  [
                    _vm._v(
                      "\n          " + _vm._s(_vm.i18n.title) + "\n        "
                    )
                  ]
                )
              ]),
              _vm._v(" "),
              _c("v-flex", { attrs: { xs12: "" } }, [
                _c("div", { attrs: { id: "vaccine-details" } }, [
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-date-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.date) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-time-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.hour) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-name-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.name) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-batch-number-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.batchNumber) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-dose-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.dose) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-administration-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.administration) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-injection-header px-1 body-1 border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.injectionSite) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "vaccine-date-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-text-field", {
                        attrs: {
                          "aria-label": "vaccine-date",
                          placeholder: _vm.i18n.placeholder.date,
                          disabled: !_vm.hasDetailSectionAccess,
                          "single-line": "",
                          solo: "",
                          maxlength: "10",
                          mask: "####-##-##",
                          "return-masked-value": "",
                          rules: [
                            function(v) {
                              return !v || _vm.validDate(v)
                            }
                          ]
                        },
                        model: {
                          value: _vm.item.vaccineDetailsInfluenzaDate,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsInfluenzaDate",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsInfluenzaDate"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "vaccine-time-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-text-field", {
                        attrs: {
                          "aria-label": "vaccine-time",
                          disabled: !_vm.hasDetailSectionAccess,
                          placeholder: "",
                          "single-line": "",
                          mask: "##:##",
                          "return-masked-value": "",
                          solo: "",
                          maxlength: "10"
                        },
                        model: {
                          value: _vm.item.vaccineDetailsInfluenzaTime,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsInfluenzaTime",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsInfluenzaTime"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "vaccine-name-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-autocomplete", {
                        attrs: {
                          "aria-label": "vaccine-name",
                          dense: "",
                          items: _vm.vaccineType,
                          "item-text": "name",
                          "item-value": "id",
                          disabled: !_vm.hasDetailSectionVaccineTypeAccess,
                          solo: "",
                          required:
                            _vm.hasDetailSectionVaccineTypeAccess &&
                            _vm.item.consentInfluenza,
                          rules: [
                            !_vm.hasDetailSectionVaccineTypeAccess ||
                              !_vm.item.consentInfluenza ||
                              _vm.requiredField()
                          ]
                        },
                        model: {
                          value: _vm.item.vaccineDetailsInfluenzaName,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsInfluenzaName",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsInfluenzaName"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "vaccine-batch-number-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-text-field", {
                        attrs: {
                          "aria-label": "vaccine-batch-number",
                          disabled: !_vm.hasDetailSectionAccess,
                          required:
                            _vm.hasDetailSectionAccess &&
                            _vm.shouldCheckValidation,
                          rules: [
                            !_vm.hasDetailSectionAccess ||
                              !_vm.shouldCheckValidation ||
                              _vm.requiredField()
                          ],
                          placeholder: "",
                          "single-line": "",
                          solo: "",
                          maxlength: "50"
                        },
                        model: {
                          value: _vm.item.vaccineDetailsInfluenzaBatchNumber,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsInfluenzaBatchNumber",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsInfluenzaBatchNumber"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "vaccine-dose-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-text-field", {
                        attrs: {
                          "aria-label": "vaccine-dose",
                          disabled: !_vm.hasDetailSectionAccess,
                          required:
                            _vm.hasDetailSectionAccess &&
                            _vm.shouldCheckValidation,
                          rules: [
                            !_vm.hasDetailSectionAccess ||
                              !_vm.shouldCheckValidation ||
                              _vm.requiredField()
                          ],
                          placeholder: "",
                          "single-line": "",
                          solo: "",
                          maxlength: "50"
                        },
                        model: {
                          value: _vm.item.vaccineDetailsInfluenzaDose,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsInfluenzaDose",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsInfluenzaDose"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "vaccine-administration-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-autocomplete", {
                        attrs: {
                          "aria-label": "vaccine-administration",
                          dense: "",
                          items: _vm.administrationType,
                          "item-text": "name",
                          "item-value": "id",
                          disabled: !_vm.hasDetailSectionAccess,
                          required:
                            _vm.hasDetailSectionAccess &&
                            _vm.shouldCheckValidation,
                          rules: [
                            !_vm.hasDetailSectionAccess ||
                              !_vm.shouldCheckValidation ||
                              _vm.requiredField()
                          ],
                          solo: ""
                        },
                        on: { change: _vm.validateInjectionsite },
                        model: {
                          value: _vm.item.vaccineDetailsInfluenzaAdministration,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsInfluenzaAdministration",
                              $$v
                            );
                          },
                          expression:
                            "item.vaccineDetailsInfluenzaAdministration"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticClass: "vaccine-injection-value px-2 py-2" },
                    [
                      _c(
                        "v-radio-group",
                        {
                          attrs: {
                            "aria-label": "vaccine-injection",
                            disabled: !_vm.hasDetailSectionAccess,
                            rules: [
                              !_vm.hasDetailSectionAccess ||
                                !_vm.shouldCheckValidation ||
                                _vm.requiredField()
                            ],
                            "hide-details": ""
                          },
                          model: {
                            value:
                              _vm.item.vaccineDetailsInfluenzaInjectionSite,
                            callback: function($$v) {
                              _vm.$set(
                                _vm.item,
                                "vaccineDetailsInfluenzaInjectionSite",
                                $$v
                              );
                            },
                            expression:
                              "item.vaccineDetailsInfluenzaInjectionSite"
                          }
                        },
                        [
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.rightArm,
                              value: "rightHarm",
                              disabled: _vm.disabledIntramuscularChoice
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.leftArm,
                              value: "leftHarm",
                              disabled: _vm.disabledIntramuscularChoice
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.rightThigh,
                              value: "rightThigh",
                              disabled: _vm.disabledIntramuscularChoice
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.leftThigh,
                              value: "leftThigh",
                              disabled: _vm.disabledIntramuscularChoice
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.rightNostril,
                              value: "rightNostril",
                              disabled: _vm.disabledIntranasalChoice
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.leftNostril,
                              value: "leftNostril",
                              disabled: _vm.disabledIntranasalChoice
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  )
                ])
              ])
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$W = [];
__vue_render__$W._withStripped = true;

  /* style */
  const __vue_inject_styles__$W = undefined;
  /* scoped */
  const __vue_scope_id__$W = undefined;
  /* functional template */
  const __vue_is_functional_template__$W = false;
  /* component normalizer */
  function __vue_normalize__$W(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/VaccineDetailsInfluenza/VaccineDetailsInfluenza.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var VaccineDetailsInfluenza$1 = __vue_normalize__$W(
    { render: __vue_render__$W, staticRenderFns: __vue_staticRenderFns__$W },
    __vue_inject_styles__$W,
    __vue_script__$W,
    __vue_scope_id__$W,
    __vue_is_functional_template__$W);

let VaccineDetailsPneumococcal$1 = class VaccineDetailsPneumococcal extends Vue {
    constructor() {
        super(...arguments);
        this.vaccineType = [];
        this.administrationType = [];
    }
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('influenza.vaccineDetails.pneumococcal');
    }
    get prefillFields() {
        return this.globalData.getFormAppData.prefillFields;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasDetailSectionVaccineTypeAccess() {
        return this.globalData.getFormAppData.hasDetailSectionVaccineTypeAccess;
    }
    get hasDetailSectionVaccineNumberAccess() {
        return this.globalData.getFormAppData.hasDetailSectionVaccineNumberAccess;
    }
    get hasDetailSectionAccess() {
        return this.globalData.getFormAppData.hasDetailSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    created() {
        this.vaccineType = [
            { id: null, name: '' },
            { id: 'pneumovax23', name: this.i18n.type.pneumovax23 },
            { id: 'prevnar13', name: this.i18n.type.prevnar13 },
            { id: 'prevnar20', name: this.i18n.type.prevnar20 },
            { id: 'synflorix', name: this.i18n.type.synflorix },
            { id: 'vaxneuvance', name: this.i18n.type.vaxneuvance },
        ];
        this.administrationType = [
            { id: 'intramuscular', name: this.i18n.administrationType.intramuscular },
        ];
        if (!this.item.vaccineDetailsPneumococcalBatchNumber) {
            this.item.vaccineDetailsPneumococcalBatchNumber = this.prefillFields.vaccineDetailsPneumococcalBatchNumber;
        }
        if (!this.item.vaccineDetailsPneumococcalName) {
            this.item.vaccineDetailsPneumococcalName = this.prefillFields.vaccineDetailsPneumococcalName;
        }
        if (!this.item.vaccineDetailsPneumococcalOtherName) {
            this.item.vaccineDetailsPneumococcalOtherName = this.prefillFields.vaccineDetailsPneumococcalOtherName;
        }
        if (!this.item.vaccineDetailsPneumococcalDose) {
            this.item.vaccineDetailsPneumococcalDose = this.prefillFields.vaccineDetailsPneumococcalDose;
        }
        if (!this.item.vaccineDetailsPneumococcalAdministration) {
            this.item.vaccineDetailsPneumococcalAdministration = this.prefillFields.vaccineDetailsPneumococcalAdministration;
        }
    }
};
__decorate([
    Prop()
], VaccineDetailsPneumococcal$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], VaccineDetailsPneumococcal$1.prototype, "item", void 0);
__decorate([
    Prop()
], VaccineDetailsPneumococcal$1.prototype, "shouldCheckValidation", void 0);
VaccineDetailsPneumococcal$1 = __decorate([
    Component({
        mixins: [FormRules],
    })
], VaccineDetailsPneumococcal$1);
var script$V = VaccineDetailsPneumococcal$1;

/* script */
            const __vue_script__$V = script$V;
            
/* template */
var __vue_render__$V = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c("v-flex", { staticClass: "black px-1", attrs: { xs12: "" } }, [
                _c(
                  "span",
                  { staticClass: "vaccine-details-title white--text" },
                  [
                    _vm._v(
                      "\n          " + _vm._s(_vm.i18n.title) + "\n        "
                    )
                  ]
                )
              ]),
              _vm._v(" "),
              _c("v-flex", { attrs: { xs12: "" } }, [
                _c("div", { attrs: { id: "vaccine-details" } }, [
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-date-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.date) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-time-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.hour) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-name-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.name) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-batch-number-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.batchNumber) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-dose-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.dose) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-administration-header px-1 body-1 border-right border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.administration) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "header vaccine-injection-header px-1 body-1 border-bottom"
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.injectionSite) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "vaccine-date-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-text-field", {
                        attrs: {
                          "aria-label": "vaccine-date",
                          placeholder: _vm.i18n.placeholder.date,
                          disabled: !_vm.hasDetailSectionAccess,
                          "single-line": "",
                          solo: "",
                          maxlength: "10",
                          mask: "####-##-##",
                          "return-masked-value": "",
                          required:
                            _vm.hasDetailSectionVaccineTypeAccess &&
                            _vm.shouldCheckValidation,
                          rules: [
                            function(v) {
                              return (
                                !v ||
                                _vm.validDate(v) ||
                                !_vm.shouldCheckValidation ||
                                _vm.requiredField()
                              )
                            }
                          ]
                        },
                        model: {
                          value: _vm.item.vaccineDetailsPneumococcalDate,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsPneumococcalDate",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsPneumococcalDate"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "vaccine-time-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-text-field", {
                        attrs: {
                          "aria-label": "vaccine-time",
                          disabled: !_vm.hasDetailSectionAccess,
                          placeholder: "",
                          "single-line": "",
                          mask: "##:##",
                          "return-masked-value": "",
                          solo: "",
                          required:
                            _vm.hasDetailSectionVaccineTypeAccess &&
                            _vm.shouldCheckValidation,
                          rules: [
                            !_vm.hasDetailSectionVaccineTypeAccess ||
                              !_vm.shouldCheckValidation ||
                              _vm.requiredField()
                          ]
                        },
                        model: {
                          value: _vm.item.vaccineDetailsPneumococcalTime,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsPneumococcalTime",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsPneumococcalTime"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "vaccine-name-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-autocomplete", {
                        attrs: {
                          "aria-label": "vaccine-name",
                          dense: "",
                          items: _vm.vaccineType,
                          "item-text": "name",
                          "item-value": "id",
                          disabled: !_vm.hasDetailSectionVaccineTypeAccess,
                          solo: "",
                          required:
                            _vm.hasDetailSectionVaccineTypeAccess &&
                            _vm.item.consentPneumococcal,
                          rules: [
                            !_vm.hasDetailSectionVaccineTypeAccess ||
                              !_vm.item.consentPneumococcal ||
                              _vm.requiredField()
                          ]
                        },
                        model: {
                          value: _vm.item.vaccineDetailsPneumococcalName,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsPneumococcalName",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsPneumococcalName"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "vaccine-batch-number-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-text-field", {
                        attrs: {
                          "aria-label": "vaccine-batch-number",
                          disabled: !_vm.hasDetailSectionAccess,
                          rules: [
                            !_vm.hasDetailSectionAccess ||
                              !_vm.shouldCheckValidation ||
                              _vm.requiredField()
                          ],
                          required:
                            _vm.hasDetailSectionAccess &&
                            _vm.shouldCheckValidation,
                          placeholder: "",
                          "single-line": "",
                          solo: "",
                          maxlength: "50"
                        },
                        model: {
                          value: _vm.item.vaccineDetailsPneumococcalBatchNumber,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsPneumococcalBatchNumber",
                              $$v
                            );
                          },
                          expression:
                            "item.vaccineDetailsPneumococcalBatchNumber"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass: "vaccine-dose-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-text-field", {
                        attrs: {
                          "aria-label": "vaccine-dose",
                          disabled: !_vm.hasDetailSectionAccess,
                          required:
                            _vm.hasDetailSectionAccess &&
                            _vm.shouldCheckValidation,
                          rules: [
                            !_vm.hasDetailSectionAccess ||
                              !_vm.shouldCheckValidation ||
                              _vm.requiredField()
                          ],
                          placeholder: "",
                          "single-line": "",
                          solo: "",
                          maxlength: "50"
                        },
                        model: {
                          value: _vm.item.vaccineDetailsPneumococcalDose,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsPneumococcalDose",
                              $$v
                            );
                          },
                          expression: "item.vaccineDetailsPneumococcalDose"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    {
                      staticClass:
                        "vaccine-administration-value px-2 py-2 border-right"
                    },
                    [
                      _c("v-autocomplete", {
                        attrs: {
                          "aria-label": "vaccine-administration",
                          dense: "",
                          items: _vm.administrationType,
                          "item-text": "name",
                          "item-value": "id",
                          disabled: !_vm.hasDetailSectionAccess,
                          required:
                            _vm.hasDetailSectionAccess &&
                            _vm.shouldCheckValidation,
                          rules: [
                            !_vm.hasDetailSectionAccess ||
                              !_vm.shouldCheckValidation ||
                              _vm.requiredField()
                          ],
                          solo: ""
                        },
                        model: {
                          value:
                            _vm.item.vaccineDetailsPneumococcalAdministration,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "vaccineDetailsPneumococcalAdministration",
                              $$v
                            );
                          },
                          expression:
                            "item.vaccineDetailsPneumococcalAdministration"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "div",
                    { staticClass: "vaccine-injection-value px-2 py-2" },
                    [
                      _c(
                        "v-radio-group",
                        {
                          attrs: {
                            "aria-label": "vaccine-injection",
                            disabled: !_vm.hasDetailSectionAccess,
                            rules: [
                              !_vm.hasDetailSectionAccess ||
                                !_vm.shouldCheckValidation ||
                                _vm.requiredField()
                            ],
                            "hide-details": ""
                          },
                          model: {
                            value:
                              _vm.item.vaccineDetailsPneumococcalInjectionSite,
                            callback: function($$v) {
                              _vm.$set(
                                _vm.item,
                                "vaccineDetailsPneumococcalInjectionSite",
                                $$v
                              );
                            },
                            expression:
                              "item.vaccineDetailsPneumococcalInjectionSite"
                          }
                        },
                        [
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.rightArm,
                              value: "rightHarm"
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.leftArm,
                              value: "leftHarm"
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.rightThigh,
                              value: "rightThigh"
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.leftThigh,
                              value: "leftThigh"
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  )
                ])
              ])
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$V = [];
__vue_render__$V._withStripped = true;

  /* style */
  const __vue_inject_styles__$V = undefined;
  /* scoped */
  const __vue_scope_id__$V = undefined;
  /* functional template */
  const __vue_is_functional_template__$V = false;
  /* component normalizer */
  function __vue_normalize__$V(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/VaccineDetailsPneumococcal/VaccineDetailsPneumococcal.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var VaccineDetailsPneumococcal = __vue_normalize__$V(
    { render: __vue_render__$V, staticRenderFns: __vue_staticRenderFns__$V },
    __vue_inject_styles__$V,
    __vue_script__$V,
    __vue_scope_id__$V,
    __vue_is_functional_template__$V);

let ImmunizationProvider$1 = class ImmunizationProvider extends Vue {
    get i18n() {
        return this.$i18n.t('influenza.immunization');
    }
    get prefillFields() {
        return globalDataVuex().getFormAppData.prefillFields;
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    get hasInjectorSectionAccess() {
        return globalDataVuex().getFormAppData.hasInjectorSectionAccess;
    }
    get hasInjectorRole() {
        return globalDataVuex().getFormAppData.hasInjectorRole;
    }
    get showEvaluatorBtn() {
        return globalDataVuex().getFormAppData.showEvaluatorBtn;
    }
    get isProfileComplete() {
        return globalDataVuex().getFormAppData.isProfileComplete;
    }
    get showInjectorBtn() {
        return globalDataVuex().getFormAppData.showInjectorBtn;
    }
    get showVaccinatorBtn() {
        return globalDataVuex().getFormAppData.showVaccinatorBtn;
    }
    get hasVaccinatorSectionAccess() {
        return globalDataVuex().getFormAppData.hasVaccinatorSectionAccess;
    }
    get hasVaccinatorSectionSignatureAccess() {
        return globalDataVuex().getFormAppData.hasVaccinatorSectionSignatureAccess;
    }
    get lockForm() {
        return globalDataVuex().getFormAppData.lockForm;
    }
    openResourceForm() {
        globalDataVuex().emitEvent({ event: 'open-resource-form', data: true });
    }
    insertMyVaccinatorInfo() {
        globalDataVuex().emitEvent({ event: 'insert-my-vaccinator-info', data: true });
    }
    insertMyInjectorInfo() {
        globalDataVuex().emitEvent({ event: 'insert-my-injector-info', data: true });
    }
    created() {
        if (!this.item.immunizationProviderName) {
            this.item.immunizationProviderName = this.prefillFields.immunizationProviderName;
        }
        if (!this.item.immunizationProviderLicenceNumber) {
            this.item.immunizationProviderLicenceNumber = this.prefillFields.immunizationProviderLicenceNumber;
        }
        if (!this.item.immunizationProviderProfession) {
            this.item.immunizationProviderProfession = this.prefillFields.immunizationProviderProfession;
        }
    }
};
__decorate([
    Prop()
], ImmunizationProvider$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], ImmunizationProvider$1.prototype, "item", void 0);
ImmunizationProvider$1 = __decorate([
    Component({
        mixins: [FormRules],
    })
], ImmunizationProvider$1);
var script$U = ImmunizationProvider$1;

/* script */
            const __vue_script__$U = script$U;
            
/* template */
var __vue_render__$U = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            {
              staticClass: "border-left border-bottom border-right",
              attrs: { row: "", wrap: "" }
            },
            [
              _c(
                "v-flex",
                {
                  staticClass:
                    "px-1 font-weight-bold border-bottom border-top grey lighten-2",
                  attrs: { md8: "", xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.provider.title) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _vm.showVaccinatorBtn && !_vm.hasInjectorRole
                ? _c(
                    "v-flex",
                    {
                      staticClass: "border-bottom border-top grey lighten-2",
                      class: _vm.vuetifyBreakpoint.smAndDown
                        ? "text-xs-left"
                        : "text-xs-right",
                      attrs: {
                        md4: "",
                        xs12: "",
                        "data-testid": "vaccinator-button-wrapper"
                      }
                    },
                    [
                      _vm.isProfileComplete
                        ? _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: { dark: "", flat: "", small: "" },
                              on: { click: _vm.insertMyVaccinatorInfo }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.getMyInformations) +
                                  "\n        "
                              )
                            ]
                          )
                        : _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: {
                                disabled: _vm.lockForm,
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.openResourceForm }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.completeMyProfile) +
                                  "\n        "
                              )
                            ]
                          )
                    ],
                    1
                  )
                : _c("v-flex", {
                    staticClass: "border-bottom border-top grey lighten-2",
                    class: _vm.vuetifyBreakpoint.smAndDown
                      ? "text-xs-left"
                      : "text-xs-right",
                    attrs: { md4: "", xs12: "" }
                  }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { md2: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label:
                        (_vm.hasVaccinatorSectionAccess ? "* " : "") +
                        _vm.i18n.provider.name,
                      disabled: !_vm.hasVaccinatorSectionAccess,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "40",
                      rules: [
                        !_vm.appliedRequiredField ||
                          !_vm.hasVaccinatorSectionAccess ||
                          _vm.requiredField()
                      ]
                    },
                    model: {
                      value: _vm.item.immunizationProviderName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "immunizationProviderName", $$v);
                      },
                      expression: "item.immunizationProviderName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "border-bottom border-right vaccinator-profession",
                  attrs: { md10: "", xs12: "", "pl-2": "" }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s(
                        (_vm.hasVaccinatorSectionAccess ? "* " : "") +
                          _vm.i18n.provider.profession.title
                      ) +
                      ":"
                  ),
                  _c("br"),
                  _vm._v(" "),
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasVaccinatorSectionAccess,
                        "hide-details": "",
                        rules: [
                          !_vm.appliedRequiredField ||
                            !_vm.hasVaccinatorSectionAccess ||
                            _vm.requiredField()
                        ]
                      },
                      model: {
                        value: _vm.item.immunizationProviderProfession,
                        callback: function($$v) {
                          _vm.$set(
                            _vm.item,
                            "immunizationProviderProfession",
                            $$v
                          );
                        },
                        expression: "item.immunizationProviderProfession"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.provider.profession.nurse,
                          value: "nurse"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.provider.profession.physician,
                          value: "physician"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.provider.profession.inhalotherapist,
                          value: "inhalotherapist"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.provider.profession.midwife,
                          value: "midwife"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.provider.profession.pharmacist,
                          value: "pharmacist"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label:
                        (_vm.hasVaccinatorSectionAccess ? "* " : "") +
                        _vm.i18n.provider.licenceNumber,
                      disabled: !_vm.hasVaccinatorSectionAccess,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "20",
                      rules: [
                        !_vm.appliedRequiredField ||
                          !_vm.hasVaccinatorSectionAccess ||
                          _vm.requiredField()
                      ]
                    },
                    model: {
                      value: _vm.item.immunizationProviderLicenceNumber,
                      callback: function($$v) {
                        _vm.$set(
                          _vm.item,
                          "immunizationProviderLicenceNumber",
                          $$v
                        );
                      },
                      expression: "item.immunizationProviderLicenceNumber"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label:
                        (_vm.hasVaccinatorSectionAccess ? "* " : "") +
                        _vm.i18n.provider.vaccinationSite,
                      disabled: !_vm.hasVaccinatorSectionAccess,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "40",
                      rules: [
                        !_vm.appliedRequiredField ||
                          _vm.hasVaccinatorSectionAccess ||
                          _vm.requiredField()
                      ]
                    },
                    model: {
                      value: _vm.item.immunizationProviderSite,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "immunizationProviderSite", $$v);
                      },
                      expression: "item.immunizationProviderSite"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm4: "", xs12: "" } },
                [
                  !_vm.item.immunizationProviderSignature
                    ? _c("v-text-field", {
                        attrs: {
                          label:
                            (_vm.hasVaccinatorSectionSignatureAccess
                              ? "* "
                              : "") + _vm.i18n.provider.vaccinatorSignature,
                          disabled: "",
                          placeholder: "",
                          "hide-details": "",
                          box: "",
                          maxlength: "100",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.hasVaccinatorSectionSignatureAccess ||
                              _vm.requiredField()
                          ]
                        }
                      })
                    : [
                        _c(
                          "v-layout",
                          { attrs: { row: "", wrap: "" } },
                          [
                            _c("v-flex", { attrs: { xs12: "", "pl-2": "" } }, [
                              _c("span", { staticClass: "caption" }, [
                                _vm._v(
                                  _vm._s(_vm.i18n.provider.vaccinatorSignature)
                                )
                              ])
                            ]),
                            _vm._v(" "),
                            _c(
                              "v-flex",
                              {
                                staticStyle: {
                                  "margin-top": "-8px",
                                  "margin-bottom": "-8px"
                                }
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: _vm.item.immunizationProviderSignature,
                                    alt: "Signature"
                                  }
                                })
                              ]
                            )
                          ],
                          1
                        )
                      ]
                ],
                2
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { md8: "", xs12: "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "px-1 font-weight-bold border-top grey lighten-2",
                      attrs: { xs12: "" }
                    },
                    [
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n.professionInformation.title) +
                          "\n        "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "px-1 border-bottom grey lighten-2",
                      attrs: { xs12: "" }
                    },
                    [
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n.professionInformation.subtitle) +
                          "\n        "
                      )
                    ]
                  )
                ],
                1
              ),
              _vm._v(" "),
              _vm.showInjectorBtn
                ? _c(
                    "v-flex",
                    {
                      staticClass: "border-bottom border-top grey lighten-2",
                      class: _vm.vuetifyBreakpoint.smAndDown
                        ? "text-xs-left"
                        : "text-xs-right",
                      attrs: { md4: "", xs12: "", "pt-1": "" }
                    },
                    [
                      _vm.isProfileComplete
                        ? _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: {
                                disabled: !_vm.hasInjectorRole || _vm.lockForm,
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.insertMyInjectorInfo }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.getMyInformations) +
                                  "\n        "
                              )
                            ]
                          )
                        : _vm.hasInjectorRole
                        ? _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: {
                                disabled: _vm.lockForm,
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.openResourceForm }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.completeMyProfile) +
                                  "\n        "
                              )
                            ]
                          )
                        : _vm._e()
                    ],
                    1
                  )
                : _c("v-flex", {
                    staticClass: "border-bottom border-top grey lighten-2",
                    class: _vm.vuetifyBreakpoint.smAndDown
                      ? "text-xs-left"
                      : "text-xs-right",
                    attrs: { md4: "", xs12: "" }
                  }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { md5: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label:
                        (_vm.hasInjectorSectionAccess ? "* " : "") +
                        _vm.i18n.professionInformation.name,
                      disabled: !_vm.hasInjectorSectionAccess,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "40",
                      rules: [
                        !_vm.appliedRequiredField ||
                          !_vm.hasInjectorSectionAccess ||
                          _vm.requiredField()
                      ]
                    },
                    model: {
                      value: _vm.item.vaccinatorName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "vaccinatorName", $$v);
                      },
                      expression: "item.vaccinatorName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "professional-profession",
                  attrs: { md3: "", sm6: "", xs12: "", "pl-2": "" }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s(
                        (_vm.hasInjectorSectionAccess ? "* " : "") +
                          _vm.i18n.professionInformation.profession.title
                      ) +
                      ":"
                  ),
                  _c("br"),
                  _vm._v(" "),
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasInjectorSectionAccess,
                        "hide-details": "",
                        rules: [
                          !_vm.appliedRequiredField ||
                            !_vm.hasInjectorSectionAccess ||
                            _vm.requiredField()
                        ]
                      },
                      model: {
                        value: _vm.item.vaccinatorProfession,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "vaccinatorProfession", $$v);
                        },
                        expression: "item.vaccinatorProfession"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label:
                            _vm.i18n.professionInformation.profession.nurse,
                          value: "nurse"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label:
                            _vm.i18n.professionInformation.profession.other,
                          value: "other"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { md2: "", sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.professionInformation.profession.specify,
                      disabled: !_vm.hasInjectorSectionAccess,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.vaccinatorOtherSpecify,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "vaccinatorOtherSpecify", $$v);
                      },
                      expression: "item.vaccinatorOtherSpecify"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm2: "", xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label:
                        (_vm.hasInjectorSectionAccess ? "* " : "") +
                        _vm.i18n.professionInformation.licenceNumber,
                      disabled: !_vm.hasInjectorSectionAccess,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "14",
                      rules: [
                        !_vm.appliedRequiredField ||
                          !_vm.hasInjectorSectionAccess ||
                          _vm.requiredField()
                      ]
                    },
                    model: {
                      value: _vm.item.vaccinatorLicenceNumber,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "vaccinatorLicenceNumber", $$v);
                      },
                      expression: "item.vaccinatorLicenceNumber"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$U = [];
__vue_render__$U._withStripped = true;

  /* style */
  const __vue_inject_styles__$U = undefined;
  /* scoped */
  const __vue_scope_id__$U = undefined;
  /* functional template */
  const __vue_is_functional_template__$U = false;
  /* component normalizer */
  function __vue_normalize__$U(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/ImmunizationProvider/ImmunizationProvider.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var ImmunizationProvider = __vue_normalize__$U(
    { render: __vue_render__$U, staticRenderFns: __vue_staticRenderFns__$U },
    __vue_inject_styles__$U,
    __vue_script__$U,
    __vue_scope_id__$U,
    __vue_is_functional_template__$U);

let DialogFormError$1 = class DialogFormError extends Vue {
    /**
     * Closes the dialog using v-model
     */
    closeDialog() {
        this.$emit('change', false);
    }
};
__decorate([
    Model('change', { type: Boolean })
], DialogFormError$1.prototype, "showDialog", void 0);
DialogFormError$1 = __decorate([
    Component
], DialogFormError$1);
var script$T = DialogFormError$1;

/* script */
            const __vue_script__$T = script$T;
            
/* template */
var __vue_render__$T = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-dialog",
    {
      attrs: { persistent: "", width: "50%" },
      model: {
        value: _vm.showDialog,
        callback: function($$v) {
          _vm.showDialog = $$v;
        },
        expression: "showDialog"
      }
    },
    [
      _c(
        "v-card",
        [
          _c(
            "v-card-title",
            { staticClass: "error white--text" },
            [
              _c(
                "v-layout",
                { attrs: { row: "", wrap: "", "align-start": "" } },
                [
                  _c("v-flex", {
                    staticClass: "error-title",
                    attrs: { xs11: "", "text-xs-left": "" },
                    domProps: {
                      textContent: _vm._s(_vm.$t("phxCovid.error.title"))
                    }
                  }),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    { attrs: { xs1: "", "text-xs-right": "" } },
                    [
                      _c(
                        "v-btn",
                        {
                          staticClass: "header-close-btn ma-0",
                          attrs: { icon: "", small: "" },
                          on: { click: _vm.closeDialog }
                        },
                        [
                          _c("v-icon", {
                            attrs: { small: "", color: "white" },
                            domProps: { textContent: _vm._s("fas fa-times") }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-card-text",
            [
              _c(
                "v-layout",
                [
                  _c(
                    "v-flex",
                    { attrs: { "pr-4": "", shrink: "" } },
                    [
                      _c("v-icon", { attrs: { color: "error" } }, [
                        _vm._v("fas fa-exclamation-triangle")
                      ])
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("v-flex", {
                    staticClass: "error-message",
                    domProps: {
                      textContent: _vm._s(_vm.$t("phxCovid.error.message"))
                    }
                  })
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-card-actions",
            [
              _c("v-spacer"),
              _vm._v(" "),
              _c("v-btn", {
                staticClass: "error close-btn",
                domProps: {
                  textContent: _vm._s(_vm.$t("phxCovid.actions.ok"))
                },
                on: { click: _vm.closeDialog }
              })
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$T = [];
__vue_render__$T._withStripped = true;

  /* style */
  const __vue_inject_styles__$T = undefined;
  /* scoped */
  const __vue_scope_id__$T = undefined;
  /* functional template */
  const __vue_is_functional_template__$T = false;
  /* component normalizer */
  function __vue_normalize__$T(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/common/dialogs/DialogFormError.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var DialogFormError = __vue_normalize__$T(
    { render: __vue_render__$T, staticRenderFns: __vue_staticRenderFns__$T },
    __vue_inject_styles__$T,
    __vue_script__$T,
    __vue_scope_id__$T,
    __vue_is_functional_template__$T);

let DialogFormEditWarning$1 = class DialogFormEditWarning extends Vue {
    /**
     * Closes the dialog using v-model
     */
    closeDialog() {
        this.$emit('change', false);
    }
    /**
     * Emit event to allow form edition
     * & Close dialog
     */
    allowEditForm() {
        this.$emit('allow-edit-form', true);
        this.closeDialog();
    }
};
__decorate([
    Model('change', { type: Boolean })
], DialogFormEditWarning$1.prototype, "showDialog", void 0);
DialogFormEditWarning$1 = __decorate([
    Component
], DialogFormEditWarning$1);
var script$S = DialogFormEditWarning$1;

/* script */
            const __vue_script__$S = script$S;
            
/* template */
var __vue_render__$S = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-dialog",
    {
      attrs: { persistent: "", width: "50%" },
      model: {
        value: _vm.showDialog,
        callback: function($$v) {
          _vm.showDialog = $$v;
        },
        expression: "showDialog"
      }
    },
    [
      _c(
        "v-card",
        [
          _c(
            "v-card-title",
            { staticClass: "warning white--text" },
            [
              _c(
                "v-layout",
                { attrs: { row: "", wrap: "", "align-start": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass: "warning-title",
                      attrs: { xs11: "", "text-xs-left": "" }
                    },
                    [_vm._v(_vm._s(_vm.$t("phxCovid.warning.title")))]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    { attrs: { xs1: "", "text-xs-right": "" } },
                    [
                      _c(
                        "v-btn",
                        {
                          staticClass: "header-close-btn ma-0",
                          attrs: { icon: "", small: "" },
                          on: { click: _vm.closeDialog }
                        },
                        [
                          _c(
                            "v-icon",
                            { attrs: { small: "", color: "white" } },
                            [_vm._v("fas fa-times")]
                          )
                        ],
                        1
                      )
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-card-text",
            [
              _c(
                "v-layout",
                [
                  _c(
                    "v-flex",
                    { attrs: { "pr-4": "", shrink: "" } },
                    [
                      _c("v-icon", { attrs: { color: "warning" } }, [
                        _vm._v("fas fa-exclamation-triangle")
                      ])
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("v-flex", { staticClass: "warning-message" }, [
                    _vm._v(
                      "\n          " +
                        _vm._s(_vm.$t("phxCovid.warning.text")) +
                        "\n          "
                    ),
                    _c("br"),
                    _c("br"),
                    _vm._v(
                      "\n          " +
                        _vm._s(_vm.$t("phxCovid.warning.question")) +
                        "\n        "
                    )
                  ])
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-card-actions",
            [
              _c("v-spacer"),
              _vm._v(" "),
              _c("v-btn", {
                staticClass: "close-btn",
                domProps: {
                  textContent: _vm._s(_vm.$t("phxCovid.actions.no"))
                },
                on: { click: _vm.closeDialog }
              }),
              _vm._v(" "),
              _c("v-spacer"),
              _vm._v(" "),
              _c("v-btn", {
                staticClass: "allow-edit-btn",
                domProps: {
                  textContent: _vm._s(_vm.$t("phxCovid.actions.yes"))
                },
                on: { click: _vm.allowEditForm }
              }),
              _vm._v(" "),
              _c("v-spacer")
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$S = [];
__vue_render__$S._withStripped = true;

  /* style */
  const __vue_inject_styles__$S = undefined;
  /* scoped */
  const __vue_scope_id__$S = undefined;
  /* functional template */
  const __vue_is_functional_template__$S = false;
  /* component normalizer */
  function __vue_normalize__$S(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/common/dialogs/DialogFormEditWarning.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var DialogFormEditWarning = __vue_normalize__$S(
    { render: __vue_render__$S, staticRenderFns: __vue_staticRenderFns__$S },
    __vue_inject_styles__$S,
    __vue_script__$S,
    __vue_scope_id__$S,
    __vue_is_functional_template__$S);

let DialogFormMemoryAidEditWarning$1 = class DialogFormMemoryAidEditWarning extends Vue {
    constructor() {
        super(...arguments);
        this.memoryAidAction = '';
    }
    get i18n() {
        return this.$i18n.t('memoryAid');
    }
    /**
     * Closes the dialog using v-model
     */
    closeDialog() {
        this.$emit('change', false);
    }
    /**
     * Emit event to Update memoryAid form
     * & Close dialog
     */
    async memoryAidIgnore() {
        await this.$emit('set-memory-form-aid-answer');
        await globalDataVuex().emitEvent({ event: this.memoryAidAction, data: true });
        this.closeDialog();
    }
    /**
     * Emit event to print memoryAid form
     * & Close dialog
     */
    async memoryAidCreateAndExport() {
        await this.$emit('set-memory-form-aid-answer');
        await globalDataVuex().emitEvent({ event: this.memoryAidAction, data: true });
        await globalDataVuex().emitEvent({ event: 'memory-aid-create-and-export', data: true });
        this.closeDialog();
    }
    btnPrintText() {
        if (this.memoryAidAction === 'export-to-pdf') {
            return this.i18n.warning.btnPrint1;
        }
        return this.i18n.warning.btnPrint;
    }
    btnSaveText() {
        if (this.memoryAidAction === 'export-to-pdf') {
            return this.i18n.warning.btnSave1;
        }
        return this.i18n.warning.btnSave;
    }
};
__decorate([
    Prop()
], DialogFormMemoryAidEditWarning$1.prototype, "memoryAidAction", void 0);
__decorate([
    Model('change', { type: Boolean })
], DialogFormMemoryAidEditWarning$1.prototype, "showDialog", void 0);
DialogFormMemoryAidEditWarning$1 = __decorate([
    Component
], DialogFormMemoryAidEditWarning$1);
var script$R = DialogFormMemoryAidEditWarning$1;

/* script */
            const __vue_script__$R = script$R;
            
/* template */
var __vue_render__$R = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-dialog",
    {
      attrs: { persistent: "", width: "780px" },
      model: {
        value: _vm.showDialog,
        callback: function($$v) {
          _vm.showDialog = $$v;
        },
        expression: "showDialog"
      }
    },
    [
      _c(
        "v-card",
        [
          _c(
            "v-card-title",
            { staticClass: "warning white--text" },
            [
              _c(
                "v-layout",
                { attrs: { row: "", wrap: "", "align-start": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass: "warning-title",
                      attrs: { xs11: "", "text-xs-left": "" }
                    },
                    [_vm._v(_vm._s(_vm.$t("memoryAid.warning.title")))]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    { attrs: { xs1: "", "text-xs-right": "" } },
                    [
                      _c(
                        "v-btn",
                        {
                          staticClass: "header-close-btn ma-0",
                          attrs: { icon: "", small: "" },
                          on: { click: _vm.closeDialog }
                        },
                        [
                          _c(
                            "v-icon",
                            { attrs: { small: "", color: "white" } },
                            [_vm._v("fas fa-times")]
                          )
                        ],
                        1
                      )
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-card-text",
            [
              _c(
                "v-layout",
                [
                  _c(
                    "v-flex",
                    { attrs: { "pr-4": "", shrink: "" } },
                    [
                      _c("v-icon", { attrs: { color: "warning" } }, [
                        _vm._v("fas fa-exclamation-triangle")
                      ])
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("v-flex", { staticClass: "warning-message" }, [
                    _vm._v(
                      "\n          " +
                        _vm._s(_vm.$t("memoryAid.warning.text")) +
                        "\n          "
                    ),
                    _c("br"),
                    _c("br"),
                    _vm._v(
                      "\n          " +
                        _vm._s(_vm.$t("memoryAid.warning.question")) +
                        "\n        "
                    )
                  ])
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-card-actions",
            [
              _c("v-spacer"),
              _vm._v(" "),
              _c("v-btn", {
                staticClass: "close-btn",
                domProps: { textContent: _vm._s(_vm.btnPrintText()) },
                on: { click: _vm.memoryAidCreateAndExport }
              }),
              _vm._v(" "),
              _c("v-spacer"),
              _vm._v(" "),
              _c("v-btn", {
                staticClass: "allow-edit-btn",
                domProps: { textContent: _vm._s(_vm.btnSaveText()) },
                on: { click: _vm.memoryAidIgnore }
              }),
              _vm._v(" "),
              _c("v-spacer")
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$R = [];
__vue_render__$R._withStripped = true;

  /* style */
  const __vue_inject_styles__$R = undefined;
  /* scoped */
  const __vue_scope_id__$R = undefined;
  /* functional template */
  const __vue_is_functional_template__$R = false;
  /* component normalizer */
  function __vue_normalize__$R(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/dialogs/DialogFormMemoryAidEditWarning.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var DialogFormMemoryAidEditWarning = __vue_normalize__$R(
    { render: __vue_render__$R, staticRenderFns: __vue_staticRenderFns__$R },
    __vue_inject_styles__$R,
    __vue_script__$R,
    __vue_scope_id__$R,
    __vue_is_functional_template__$R);

let Notes$7 = class Notes extends Vue {
    get i18n() {
        return this.$i18n.t('influenza.notes');
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    get hasNotesSectionAccess() {
        return globalDataVuex().getFormAppData.hasNotesSectionAccess;
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
};
__decorate([
    Prop()
], Notes$7.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], Notes$7.prototype, "item", void 0);
Notes$7 = __decorate([
    Component({
        mixins: [FormRules],
    })
], Notes$7);
var script$Q = Notes$7;

/* script */
            const __vue_script__$Q = script$Q;
            
/* template */
var __vue_render__$Q = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-textarea", {
                    attrs: {
                      disabled: !_vm.hasNotesSectionAccess,
                      "hide-details": "",
                      box: "",
                      label: _vm.i18n.notes,
                      maxlength: "5000"
                    },
                    model: {
                      value: _vm.item.notes,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "notes", $$v);
                      },
                      expression: "item.notes"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "font-weight-medium", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.requiredField) + "\n        "
                  ),
                  _c("br")
                ]
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$Q = [];
__vue_render__$Q._withStripped = true;

  /* style */
  const __vue_inject_styles__$Q = undefined;
  /* scoped */
  const __vue_scope_id__$Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$Q = false;
  /* component normalizer */
  function __vue_normalize__$Q(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/Notes.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var Notes$6 = __vue_normalize__$Q(
    { render: __vue_render__$Q, staticRenderFns: __vue_staticRenderFns__$Q },
    __vue_inject_styles__$Q,
    __vue_script__$Q,
    __vue_scope_id__$Q,
    __vue_is_functional_template__$Q);

let FooterToolbarActions$7 = class FooterToolbarActions extends Vue {
    get i18n() {
        return this.$i18n.t('influenza.general');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasTransmitAccess() {
        return this.globalData.getFormAppData.hasTransmitAccess;
    }
    get hasExportAccess() {
        return this.globalData.getFormAppData.hasExportAccess;
    }
    get hasCompleteFormButtonAccess() {
        return this.globalData.getFormAppData.hasCompleteFormButtonAccess;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    get saveLoading() {
        return this.globalData.getFormAppData.saveLoading;
    }
    get isTransmit() {
        let status = this.item.status;
        return status.includes('_transmitted');
    }
    submitTransmitForm(oldStatus) {
        let newStatus;
        if (this.isTransmit) {
            newStatus = oldStatus.replace('_transmitted', '');
        }
        else {
            newStatus = oldStatus + '_transmitted';
        }
        this.globalData.emitEvent({ event: 'submit-transmit-form', data: newStatus });
    }
    exportToPdf() {
        this.globalData.emitEvent({ event: 'export-to-pdf', data: true });
    }
    saveTemporaryForm() {
        this.globalData.emitEvent({ event: 'save-temporary-form', data: true });
    }
    completeButtonDispatch() {
        this.globalData.emitEvent({ event: 'complete-button-dispatch', data: true });
    }
    closeModalForm() {
        this.globalData.emitEvent({ event: 'close', data: true });
    }
};
__decorate([
    Prop()
], FooterToolbarActions$7.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], FooterToolbarActions$7.prototype, "item", void 0);
FooterToolbarActions$7 = __decorate([
    Component
], FooterToolbarActions$7);
var script$P = FooterToolbarActions$7;

/* script */
            const __vue_script__$P = script$P;
            
/* template */
var __vue_render__$P = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        {
          staticClass: "text-xs-center",
          class: this.$vuetify.breakpoint.smAndDown ? "text-xs-left" : "",
          attrs: { xs12: "", "pr-2": "", "pb-2": "" }
        },
        [
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: {
                dark: "",
                flat: "",
                disabled: _vm.lockForm || _vm.saveLoading
              },
              on: { click: _vm.saveTemporaryForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.save) + "\n    ")]
          ),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: {
                dark: "",
                flat: "",
                disabled:
                  !_vm.hasCompleteFormButtonAccess ||
                  _vm.lockForm ||
                  _vm.saveLoading,
                "data-testid": "complete-button-dispatch"
              },
              on: { click: _vm.completeButtonDispatch }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.saveAndComplete) + "\n    ")]
          ),
          _vm._v(" "),
          _vm.hasTransmitAccess
            ? _c(
                "v-btn",
                {
                  staticClass: "primary text-center",
                  attrs: { dark: "", flat: "" },
                  on: {
                    click: function($event) {
                      return _vm.submitTransmitForm(_vm.item.status)
                    }
                  }
                },
                [
                  !_vm.isTransmit
                    ? _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsTransmitted))
                      ])
                    : _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsNotTransmitted))
                      ])
                ]
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: { dark: "", flat: "" },
              on: { click: _vm.closeModalForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.close) + "\n    ")]
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$P = [];
__vue_render__$P._withStripped = true;

  /* style */
  const __vue_inject_styles__$P = undefined;
  /* scoped */
  const __vue_scope_id__$P = undefined;
  /* functional template */
  const __vue_is_functional_template__$P = false;
  /* component normalizer */
  function __vue_normalize__$P(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/form-parts/FooterToolbarActions/FooterToolbarActions.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var FooterToolbarActions$6 = __vue_normalize__$P(
    { render: __vue_render__$P, staticRenderFns: __vue_staticRenderFns__$P },
    __vue_inject_styles__$P,
    __vue_script__$P,
    __vue_scope_id__$P,
    __vue_is_functional_template__$P);

const hasSomeVaccinatorOrProviderChampsFilled = (vaccinationData) => {
    return !!(hasSomeProviderChampFilled$2(vaccinationData) ||
        vaccinationData.vaccinatorName ||
        vaccinationData.vaccinatorProfession ||
        vaccinationData.vaccinatorOtherSpecify ||
        vaccinationData.vaccinatorLicenceNumber);
};
const hasSomeProviderChampFilled$2 = (vaccinationData) => {
    return !!(vaccinationData.immunizationProviderLicenceNumber ||
        vaccinationData.immunizationProviderName ||
        vaccinationData.immunizationProviderProfession ||
        vaccinationData.immunizationProviderSignature);
};
const hasFilledAllProviderChamps$1 = (vaccinationData) => {
    return !!(vaccinationData.immunizationProviderLicenceNumber &&
        vaccinationData.immunizationProviderName &&
        vaccinationData.immunizationProviderProfession &&
        vaccinationData.immunizationProviderSignature);
};

let Ah650$1 = class Ah650 extends Vue {
    constructor() {
        super(...arguments);
        // Dialogs
        this.showFormErrorDialog = false;
        this.showFormEditWarningDialog = false;
        this.showFormMemoryAidDialog = false;
        this.memoryAidAction = '';
        this.memoryFormAidAnswer = false;
        this.establishmentId = this.data.establishmentId;
        this.appointmentNumber = this.data['appointment.appointmentNumber'];
    }
    /**
     * Vue Hook - Created
     */
    created() {
        // Set the data in the vuex store
        globalDataVuex().setFormAppData(this.appData);
    }
    /**
     * Retrieve the app data for this form
     */
    get getAppData() {
        return globalDataVuex().getFormAppData;
    }
    /**
     * Retrieve the last emitted event
     */
    get getLastEmittedEvent() {
        return globalDataVuex().getLastEmittedEvent;
    }
    get hasMemoryAidForm() {
        return globalDataVuex().getFormAppData.hasMemoryAidForm;
    }
    get hasInjectorRole() {
        return globalDataVuex().getFormAppData.hasInjectorRole;
    }
    get hasVaccinatorSectionAccess() {
        return globalDataVuex().getFormAppData.hasVaccinatorSectionAccess;
    }
    get shouldValidateInfluenzaVaccineDetailsSection() {
        const data = this.item.data;
        return data.consentInfluenza === true && hasSomeProviderChampFilled$2(data);
    }
    get shouldValidatePneumococcalVaccineDetailsSection() {
        const data = this.item.data;
        return data.consentPneumococcal === true && hasSomeProviderChampFilled$2(data);
    }
    get shouldValidateInfluenzaAndProviderForms() {
        return hasSomeVaccinatorOrProviderChampsFilled(this.item.data);
    }
    get getHasFilledAllProviderChamps() {
        return hasFilledAllProviderChamps$1(this.item.data);
    }
    get shouldMarkFormAsCompleted() {
        return (this.hasVaccinatorSectionAccess &&
            this.getHasFilledAllProviderChamps &&
            (this.influenzaForm && this.influenzaForm.validate()));
    }
    async onEventEmit(event) {
        let showDialogMemoryAid = false;
        if (this.hasMemoryAidForm && !this.memoryFormAidAnswer && !globalDataVuex().getEmptyMemoryAidForm) {
            showDialogMemoryAid = true;
        }
        switch (event.event) {
            case 'complete-button-dispatch':
                await this.$emit('update-applied-required-field', true);
                if (this.validateForm(true)) {
                    if (showDialogMemoryAid) {
                        this.memoryAidAction = event.event;
                        this.showFormMemoryAidDialog = true;
                        return;
                    }
                    await this.$emit(event.event, this.shouldMarkFormAsCompleted);
                }
                break;
            case 'export-to-pdf':
                if (showDialogMemoryAid) {
                    this.memoryAidAction = event.event;
                    this.showFormMemoryAidDialog = true;
                    return;
                }
                await this.$emit(event.event, event.data);
                break;
            case 'save-temporary-form':
                await this.$emit('update-applied-required-field', false);
                if (this.validateForm(false)) {
                    if (showDialogMemoryAid) {
                        this.memoryAidAction = event.event;
                        this.showFormMemoryAidDialog = true;
                        return;
                    }
                }
                await this.$emit(event.event, event.data);
                break;
            default:
                await this.$emit(event.event, event.data);
        }
        this.memoryFormAidAnswer = false;
    }
    onAppDataUpdate() {
        globalDataVuex().setFormAppData(this.appData);
    }
    setAllowEditForm(value) {
        this.$emit('allow-edit-form', value);
    }
    validateForm(showFormErrorDialog) {
        let isValid = this.influenzaForm && this.influenzaForm.validate();
        if (this.hasVaccinatorSectionAccess) {
            if (this.shouldValidateInfluenzaAndProviderForms || this.hasInjectorRole) {
                isValid = ((this.influenzaForm && this.influenzaForm.validate()) &&
                    (this.immunizationProviderForm && this.immunizationProviderForm.validate()));
            }
        }
        if (!isValid) {
            this.showFormErrorDialog = showFormErrorDialog;
            return false;
        }
        return true;
    }
    displayWarningIfLocked() {
        if (!globalDataVuex().getFormAppData.hasReadOnly && globalDataVuex().getFormAppData.lockForm) {
            this.showFormEditWarningDialog = true;
        }
    }
};
__decorate([
    Ref()
], Ah650$1.prototype, "influenzaForm", void 0);
__decorate([
    Ref()
], Ah650$1.prototype, "immunizationProviderForm", void 0);
__decorate([
    Prop()
], Ah650$1.prototype, "data", void 0);
__decorate([
    Prop()
], Ah650$1.prototype, "appData", void 0);
__decorate([
    PropSync('data', { type: Object })
], Ah650$1.prototype, "item", void 0);
__decorate([
    Watch('getLastEmittedEvent')
], Ah650$1.prototype, "onEventEmit", null);
__decorate([
    Watch('appData')
], Ah650$1.prototype, "onAppDataUpdate", null);
Ah650$1 = __decorate([
    Component({
        components: {
            DialogFormError,
            DialogFormEditWarning,
            HeaderToolbar: HeaderToolbar$6,
            TitleAndPatient: TitleAndPatient$6,
            GeneralInformation: GeneralInformation$6,
            PreImmunization: PreImmunization$4,
            AdministrativeReason: AdministrativeReason$4,
            ConsentAndDecision: ConsentAndDecision$4,
            ProfessionalInformation: ProfessionalInformation$6,
            VaccineDetailsInfluenza: VaccineDetailsInfluenza$1,
            VaccineDetailsPneumococcal,
            ImmunizationProvider,
            Notes: Notes$6,
            FooterToolbarActions: FooterToolbarActions$6,
            DialogFormMemoryAidEditWarning,
        },
    })
], Ah650$1);
var script$O = Ah650$1;

/* script */
            const __vue_script__$O = script$O;
            
/* template */
var __vue_render__$O = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "", "data-testid": "ah360" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-card",
            { attrs: { tile: "" } },
            [
              _c("header-toolbar", {
                attrs: { data: _vm.item },
                on: {
                  "update:data": function($event) {
                    _vm.item = $event;
                  }
                }
              }),
              _vm._v(" "),
              !_vm.getAppData.vuetifyBreakpoint.xsOnly
                ? _c(
                    "v-card-text",
                    { on: { click: _vm.displayWarningIfLocked } },
                    [
                      _c(
                        "v-form",
                        {
                          ref: "influenzaForm",
                          attrs: { id: "influenza-form", "lazy-validation": "" }
                        },
                        [
                          _c("title-and-patient", {
                            attrs: {
                              data: _vm.item.data,
                              establishmentId: _vm.establishmentId,
                              appointmentNumber: _vm.appointmentNumber
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("general-information", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("pre-immunization", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("div", [_vm._t("memoryAid")], 2),
                          _vm._v(" "),
                          _c("administrative-reason", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("consent-and-decision", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("professional-information", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("vaccine-details-influenza", {
                            attrs: {
                              data: _vm.item.data,
                              "should-check-validation":
                                _vm.shouldValidateInfluenzaVaccineDetailsSection
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("vaccine-details-pneumococcal", {
                            attrs: {
                              data: _vm.item.data,
                              "should-check-validation":
                                _vm.shouldValidatePneumococcalVaccineDetailsSection
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          })
                        ],
                        1
                      ),
                      _vm._v(" "),
                      _c(
                        "v-form",
                        {
                          ref: "immunizationProviderForm",
                          attrs: {
                            id: "immunization-provider-form",
                            "lazy-validation": ""
                          }
                        },
                        [
                          _c("immunization-provider", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("notes", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              _c(
                "v-card-actions",
                [
                  _c("footer-toolbar-actions", {
                    attrs: { data: _vm.item },
                    on: {
                      "update:data": function($event) {
                        _vm.item = $event;
                      }
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c("dialog-form-error", {
        model: {
          value: _vm.showFormErrorDialog,
          callback: function($$v) {
            _vm.showFormErrorDialog = $$v;
          },
          expression: "showFormErrorDialog"
        }
      }),
      _vm._v(" "),
      _c("dialog-form-edit-warning", {
        on: { "allow-edit-form": _vm.setAllowEditForm },
        model: {
          value: _vm.showFormEditWarningDialog,
          callback: function($$v) {
            _vm.showFormEditWarningDialog = $$v;
          },
          expression: "showFormEditWarningDialog"
        }
      }),
      _vm._v(" "),
      _c("dialog-form-memory-aid-edit-warning", {
        attrs: { memoryAidAction: _vm.memoryAidAction },
        on: {
          "set-memory-form-aid-answer": function($event) {
            _vm.memoryFormAidAnswer = true;
          }
        },
        model: {
          value: _vm.showFormMemoryAidDialog,
          callback: function($$v) {
            _vm.showFormMemoryAidDialog = $$v;
          },
          expression: "showFormMemoryAidDialog"
        }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$O = [];
__vue_render__$O._withStripped = true;

  /* style */
  const __vue_inject_styles__$O = function (inject) {
    if (!inject) return
    inject("data-v-2ea4e5e4_0", { source: "#influenza-form .border-all,\n#immunization-provider-form .border-all {\n  border: 1px black solid;\n}\n#influenza-form .border-all .v-input__slot,\n  #immunization-provider-form .border-all .v-input__slot {\n    background: none;\n}\n#influenza-form .border-top,\n#immunization-provider-form .border-top {\n  border-top: 1px solid #000000 !important;\n}\n#influenza-form .border-bottom,\n#immunization-provider-form .border-bottom {\n  border-bottom: 1px solid #000000 !important;\n}\n#influenza-form .border-left,\n#immunization-provider-form .border-left {\n  border-left: 1px solid #000000 !important;\n}\n#influenza-form .border-right,\n#immunization-provider-form .border-right {\n  border-right: 1px solid #000000 !important;\n}\n#influenza-form .header-question,\n#immunization-provider-form .header-question {\n  width: 47px;\n  display: inline-flex;\n}\n#influenza-form .v-input--checkbox,\n#influenza-form .v-input--radio-group,\n#immunization-provider-form .v-input--checkbox,\n#immunization-provider-form .v-input--radio-group {\n  margin: 0;\n}\n#influenza-form .v-radio,\n#immunization-provider-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px;\n}\n#influenza-form .phone,\n#immunization-provider-form .phone {\n  float: left;\n  width: 50%;\n}\n#influenza-form .vaccinator-profession .v-radio,\n#influenza-form .professional-profession .v-radio,\n#immunization-provider-form .vaccinator-profession .v-radio,\n#immunization-provider-form .professional-profession .v-radio {\n  margin: 0 20px 0 0;\n}\n#influenza-form .user-phone .v-radio,\n#immunization-provider-form .user-phone .v-radio {\n  margin: 0;\n}\n#influenza-form #vaccine-details,\n#immunization-provider-form #vaccine-details {\n  display: grid;\n  grid-template-columns: 14% 14% 14% 10% 10% auto auto;\n  grid-template-rows: 50px auto;\n}\n#influenza-form #vaccine-details .vaccine-date-value,\n#influenza-form #vaccine-details .vaccine-time-value,\n#influenza-form #vaccine-details .vaccine-name-value,\n#influenza-form #vaccine-details .vaccine-batch-number-value,\n#influenza-form #vaccine-details .vaccine-dose-value,\n#influenza-form #vaccine-details .vaccine-administration-value,\n#immunization-provider-form #vaccine-details .vaccine-date-value,\n#immunization-provider-form #vaccine-details .vaccine-time-value,\n#immunization-provider-form #vaccine-details .vaccine-name-value,\n#immunization-provider-form #vaccine-details .vaccine-batch-number-value,\n#immunization-provider-form #vaccine-details .vaccine-dose-value,\n#immunization-provider-form #vaccine-details .vaccine-administration-value {\n  grid-row-start: 2;\n  grid-row-end: 6;\n}\n#influenza-form #vaccine-details .header,\n#immunization-provider-form #vaccine-details .header {\n  display: flex;\n  align-items: center;\n}\n\n/*# sourceMappingURL=ah650.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/ah650.vue","ah650.vue"],"names":[],"mappings":"AAiRA;;EAGA,uBAAA;AAAA;AAHA;;IAMA,gBAAA;AAAA;AANA;;EAWA,wCAAA;AAAA;AAXA;;EAeA,2CAAA;AAAA;AAfA;;EAmBA,yCAAA;AAAA;AAnBA;;EAuBA,0CAAA;AAAA;AAvBA;;EA2BA,WAAA;EACA,oBAAA;AAAA;AA5BA;;;;EAiCA,SAAA;AAAA;AAjCA;;EAqCA,kBAAA;EACA,eAAA;AAAA;AAtCA;;EA0CA,WAAA;EACA,UAAA;AAAA;AA3CA;;;;EAgDA,kBAAA;AAAA;AAhDA;;EAoDA,SAAA;AAAA;AApDA;;EAwDA,aAAA;EACA,oDAAA;EACA,6BAAA;AAAA;AA1DA;;;;;;;;;;;;EAmEA,iBAAA;EACA,eAAA;AAAA;AApEA;;EAwEA,aAAA;EACA,mBAAA;AAAA;;AC1QA,oCAAoC","file":"ah650.vue","sourcesContent":[null,"#influenza-form .border-all,\n#immunization-provider-form .border-all {\n  border: 1px black solid; }\n  #influenza-form .border-all .v-input__slot,\n  #immunization-provider-form .border-all .v-input__slot {\n    background: none; }\n\n#influenza-form .border-top,\n#immunization-provider-form .border-top {\n  border-top: 1px solid #000000 !important; }\n\n#influenza-form .border-bottom,\n#immunization-provider-form .border-bottom {\n  border-bottom: 1px solid #000000 !important; }\n\n#influenza-form .border-left,\n#immunization-provider-form .border-left {\n  border-left: 1px solid #000000 !important; }\n\n#influenza-form .border-right,\n#immunization-provider-form .border-right {\n  border-right: 1px solid #000000 !important; }\n\n#influenza-form .header-question,\n#immunization-provider-form .header-question {\n  width: 47px;\n  display: inline-flex; }\n\n#influenza-form .v-input--checkbox,\n#influenza-form .v-input--radio-group,\n#immunization-provider-form .v-input--checkbox,\n#immunization-provider-form .v-input--radio-group {\n  margin: 0; }\n\n#influenza-form .v-radio,\n#immunization-provider-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px; }\n\n#influenza-form .phone,\n#immunization-provider-form .phone {\n  float: left;\n  width: 50%; }\n\n#influenza-form .vaccinator-profession .v-radio,\n#influenza-form .professional-profession .v-radio,\n#immunization-provider-form .vaccinator-profession .v-radio,\n#immunization-provider-form .professional-profession .v-radio {\n  margin: 0 20px 0 0; }\n\n#influenza-form .user-phone .v-radio,\n#immunization-provider-form .user-phone .v-radio {\n  margin: 0; }\n\n#influenza-form #vaccine-details,\n#immunization-provider-form #vaccine-details {\n  display: grid;\n  grid-template-columns: 14% 14% 14% 10% 10% auto auto;\n  grid-template-rows: 50px auto; }\n\n#influenza-form #vaccine-details .vaccine-date-value,\n#influenza-form #vaccine-details .vaccine-time-value,\n#influenza-form #vaccine-details .vaccine-name-value,\n#influenza-form #vaccine-details .vaccine-batch-number-value,\n#influenza-form #vaccine-details .vaccine-dose-value,\n#influenza-form #vaccine-details .vaccine-administration-value,\n#immunization-provider-form #vaccine-details .vaccine-date-value,\n#immunization-provider-form #vaccine-details .vaccine-time-value,\n#immunization-provider-form #vaccine-details .vaccine-name-value,\n#immunization-provider-form #vaccine-details .vaccine-batch-number-value,\n#immunization-provider-form #vaccine-details .vaccine-dose-value,\n#immunization-provider-form #vaccine-details .vaccine-administration-value {\n  grid-row-start: 2;\n  grid-row-end: 6; }\n\n#influenza-form #vaccine-details .header,\n#immunization-provider-form #vaccine-details .header {\n  display: flex;\n  align-items: center; }\n\n/*# sourceMappingURL=ah650.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$O = undefined;
  /* module identifier */
  const __vue_module_identifier__$k = undefined;
  /* functional template */
  const __vue_is_functional_template__$O = false;
  /* component normalizer */
  function __vue_normalize__$O(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/ah650.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$k() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$k.styles || (__vue_create_injector__$k.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var ah650 = __vue_normalize__$O(
    { render: __vue_render__$O, staticRenderFns: __vue_staticRenderFns__$O },
    __vue_inject_styles__$O,
    __vue_script__$O,
    __vue_scope_id__$O,
    __vue_is_functional_template__$O,
    __vue_module_identifier__$k,
    __vue_create_injector__$k);

let Ah650 = class Ah650 extends Vue {
    constructor() {
        super(...arguments);
        // Number of questions
        this.questionsNbr = 13;
        this.questions = {};
        // Dialogs
        this.showFormErrorDialog = false;
        this.isEmptyMemoryAidForm = true;
    }
    /**
     * Vue Hook - Created
     */
    created() {
        // Set the data in the vuex store
        globalDataVuex().setFormAppData(this.appData);
        globalDataVuex().setEmptyMemoryAidForm(true);
    }
    get i18n() {
        return this.$i18n.t('memoryAid');
    }
    /**
     * Retrieve the app data for this form
     */
    get getAppData() {
        return globalDataVuex().getFormAppData;
    }
    /**
     * Retrieve the last emitted event
     */
    get getLastEmittedEvent() {
        return globalDataVuex().getLastEmittedEvent;
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get hasPreImmunisationSectionAccess() {
        return globalDataVuex().getFormAppData.hasPreImmunisationSectionAccess && globalDataVuex().getFormAppData.hasExportAccess;
    }
    get lockForm() {
        return globalDataVuex().getFormAppData.lockForm;
    }
    get exportLoading() {
        return globalDataVuex().getFormAppData.exportLoading;
    }
    onAppDataUpdate() {
        globalDataVuex().setFormAppData(this.appData);
    }
    setAllowEditForm(value) {
        this.$emit('allow-edit-form', value);
    }
    async memoryAidUpdate() {
        this.isEmptyMemoryAidForm = false;
        globalDataVuex().setEmptyMemoryAidForm(this.isEmptyMemoryAidForm);
        await this.$emit('memory-aid-update', this.questions);
    }
    async exportToPdf() {
        await globalDataVuex().emitEvent({ event: 'memory-aid-create-and-export', data: true });
    }
};
__decorate([
    Ref()
], Ah650.prototype, "influenzaForm", void 0);
__decorate([
    Prop()
], Ah650.prototype, "data", void 0);
__decorate([
    Prop()
], Ah650.prototype, "appData", void 0);
__decorate([
    PropSync('data', { type: Object })
], Ah650.prototype, "item", void 0);
__decorate([
    Watch('appData')
], Ah650.prototype, "onAppDataUpdate", null);
Ah650 = __decorate([
    Component({
        components: {
            DialogFormError,
            DialogFormEditWarning,
        },
    })
], Ah650);
var script$N = Ah650;

/* script */
            const __vue_script__$N = script$N;
            
/* template */
var __vue_render__$N = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            {
              staticClass: "border-top border-left border-right",
              attrs: { row: "", wrap: "" }
            },
            [
              _c(
                "v-flex",
                {
                  staticClass: "black px-1 pt-1",
                  attrs: { md8: "", xs12: "" }
                },
                [
                  _c("span", { staticClass: "white--text" }, [
                    _vm._v(_vm._s(_vm.i18n.title))
                  ])
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "black px-1",
                  class: _vm.vuetifyBreakpoint.smAndDown
                    ? "text-xs-left"
                    : "text-xs-right",
                  attrs: { md4: "", xs12: "" }
                },
                [
                  _c(
                    "v-btn",
                    {
                      staticClass: "primary text-center ma-1",
                      attrs: {
                        disabled: _vm.lockForm || _vm.isEmptyMemoryAidForm,
                        dark: "",
                        flat: "",
                        small: "",
                        loading: _vm.exportLoading
                      },
                      on: { click: _vm.exportToPdf }
                    },
                    [_vm._v(_vm._s(_vm.i18n.exportToPdf) + "\n        ")]
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c("v-flex", {
                staticClass:
                  "font-weight-bold hidden-xs-only border-bottom border-right grey lighten-2",
                attrs: { xs1: "" }
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "font-weight-bold hidden-xs-only border-bottom border-right grey lighten-2 px-1",
                  attrs: { md6: "", xs5: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.header.elements) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "font-weight-bold hidden-xs-only border-bottom border-right grey lighten-2 px-1",
                  attrs: { md2: "", xs3: "" }
                },
                [
                  _c("span", { staticClass: "header-question" }, [
                    _vm._v(_vm._s(_vm.i18n.header.yes))
                  ]),
                  _vm._v(" "),
                  _c("span", { staticClass: "header-question" }, [
                    _vm._v(_vm._s(_vm.i18n.header.no))
                  ]),
                  _vm._v(" "),
                  _c("span", { staticClass: "header-question" }, [
                    _vm._v(_vm._s(_vm.i18n.header.NA))
                  ])
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "font-weight-bold hidden-xs-only border-bottom grey lighten-2 px-1",
                  attrs: { md3: "", xs3: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.header.details) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _vm._l(_vm.questionsNbr, function(i, index) {
                return [
                  _c(
                    "v-flex",
                    {
                      key: "col1-" + index,
                      staticClass: "text-xs-center font-weight-bold",
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      attrs: { xs1: "" }
                    },
                    [_vm._v("\n          " + _vm._s(i) + ".\n        ")]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col2-" + index,
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      attrs: { md6: "", sm5: "", xs12: "", "pr-3": "" }
                    },
                    [
                      _c("strong", [_vm._v(_vm._s(_vm.i18n["title" + i]))]),
                      _c("br"),
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n["question" + i]) +
                          "\n        "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col3-" + index,
                      staticClass: "font-weight-bold",
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      attrs: { md2: "", sm3: "", xs12: "" }
                    },
                    [
                      _c(
                        "v-radio-group",
                        {
                          attrs: {
                            row: "",
                            disabled: !_vm.hasPreImmunisationSectionAccess
                          },
                          on: { change: _vm.memoryAidUpdate },
                          model: {
                            value: _vm.questions["preImmunization" + i],
                            callback: function($$v) {
                              _vm.$set(
                                _vm.questions,
                                "preImmunization" + i,
                                $$v
                              );
                            },
                            expression: "questions['preImmunization' + i]"
                          }
                        },
                        [
                          _c("v-radio", {
                            attrs: {
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.yes
                                : "",
                              value: 1
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.no
                                : "",
                              value: 0
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.NA
                                : "",
                              value: -1
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col4-" + index,
                      staticClass: "border-bottom",
                      attrs: { md3: "", sm3: "", xs12: "" }
                    },
                    [
                      _c("v-text-field", {
                        class: _vm.vuetifyBreakpoint.xsOnly ? "ml-4" : "",
                        attrs: {
                          label: _vm.i18n.precision,
                          disabled: !_vm.hasPreImmunisationSectionAccess,
                          placeholder: "",
                          "hide-details": "",
                          box: "",
                          maxlength: "255"
                        },
                        on: { change: _vm.memoryAidUpdate },
                        model: {
                          value: _vm.questions["preImmunizationPrecision" + i],
                          callback: function($$v) {
                            _vm.$set(
                              _vm.questions,
                              "preImmunizationPrecision" + i,
                              $$v
                            );
                          },
                          expression:
                            "questions['preImmunizationPrecision' + i]"
                        }
                      })
                    ],
                    1
                  )
                ]
              })
            ],
            2
          ),
          _vm._v(" "),
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { staticClass: "px-1 caption", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.importantNote) + "\n      "
                  )
                ]
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$N = [];
__vue_render__$N._withStripped = true;

  /* style */
  const __vue_inject_styles__$N = function (inject) {
    if (!inject) return
    inject("data-v-0a35cb2a_0", { source: "#influenza-form .border-all {\n  border: 1px black solid;\n}\n#influenza-form .border-all .v-input__slot {\n    background: none;\n}\n#influenza-form .border-top {\n  border-top: 1px solid #000000 !important;\n}\n#influenza-form .border-bottom {\n  border-bottom: 1px solid #000000 !important;\n}\n#influenza-form .border-left {\n  border-left: 1px solid #000000 !important;\n}\n#influenza-form .border-right {\n  border-right: 1px solid #000000 !important;\n}\n#influenza-form .header-question {\n  width: 47px;\n  display: inline-flex;\n}\n#influenza-form .v-input--checkbox,\n#influenza-form .v-input--radio-group {\n  margin: 0;\n}\n#influenza-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px;\n}\n#influenza-form #memory-aid-section {\n  position: absolute;\n  top: 520px;\n  left: 10px;\n  width: 700px;\n}\n#influenza-form .title {\n  background-color: #000000 !important;\n  color: #FFFFFF !important;\n  text-transform: uppercase;\n  padding-left: 10px;\n  line-height: 150%;\n  font-size: 14px;\n  font-weight: bold;\n}\n#influenza-form #questionsList {\n  display: grid;\n  font-size: 10px;\n  grid-template-columns: 20px 310px 30px 30px 30px auto;\n  grid-template-rows: 20px minmax(auto, auto);\n}\n#influenza-form #questionsList .col-header {\n  text-transform: uppercase;\n  font-weight: bold;\n  font-size: 12px;\n  line-height: 200%;\n  background-color: #d5d5d5 !important;\n}\n#influenza-form .footer {\n  text-align: left;\n  font-size: 10px;\n}\n#influenza-form .col-precision {\n  font-size: 10px;\n}\n#influenza-form .col-number {\n  font-weight: bold;\n}\n#influenza-form .col-responses input {\n  position: absolute;\n  top: 50%;\n  transform: translateY(-50%);\n}\n#influenza-form .col-responses {\n  position: relative;\n}\n\n/*# sourceMappingURL=memoryAid.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/memoryAid.vue","memoryAid.vue"],"names":[],"mappings":"AA4LA;EAEA,uBAAA;AAAA;AAFA;IAKA,gBAAA;AAAA;AALA;EAUA,wCAAA;AAAA;AAVA;EAcA,2CAAA;AAAA;AAdA;EAkBA,yCAAA;AAAA;AAlBA;EAsBA,0CAAA;AAAA;AAtBA;EA0BA,WAAA;EACA,oBAAA;AAAA;AA3BA;;EAgCA,SAAA;AAAA;AAhCA;EAoCA,kBAAA;EACA,eAAA;AAAA;AArCA;EAyCA,kBAAA;EACA,UAAA;EACA,UAAA;EACA,YAAA;AAAA;AA5CA;EAgDA,oCAAA;EACA,yBAAA;EACA,yBAAA;EACA,kBAAA;EACA,iBAAA;EACA,eAAA;EACA,iBAAA;AAAA;AAtDA;EA0DA,aAAA;EACA,eAAA;EACA,qDAAA;EACA,2CAAA;AAAA;AA7DA;EAiEA,yBAAA;EACA,iBAAA;EACA,eAAA;EACA,iBAAA;EACA,oCAAA;AAAA;AArEA;EAyEA,gBAAA;EACA,eAAA;AAAA;AA1EA;EA8EA,eAAA;AAAA;AA9EA;EAkFA,iBAAA;AAAA;AAlFA;EAsFA,kBAAA;EACA,QAAA;EACA,2BAAA;AAAA;AAxFA;EA4FA,kBAAA;AAAA;;AC7MA,wCAAwC","file":"memoryAid.vue","sourcesContent":[null,"#influenza-form .border-all {\n  border: 1px black solid; }\n  #influenza-form .border-all .v-input__slot {\n    background: none; }\n\n#influenza-form .border-top {\n  border-top: 1px solid #000000 !important; }\n\n#influenza-form .border-bottom {\n  border-bottom: 1px solid #000000 !important; }\n\n#influenza-form .border-left {\n  border-left: 1px solid #000000 !important; }\n\n#influenza-form .border-right {\n  border-right: 1px solid #000000 !important; }\n\n#influenza-form .header-question {\n  width: 47px;\n  display: inline-flex; }\n\n#influenza-form .v-input--checkbox,\n#influenza-form .v-input--radio-group {\n  margin: 0; }\n\n#influenza-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px; }\n\n#influenza-form #memory-aid-section {\n  position: absolute;\n  top: 520px;\n  left: 10px;\n  width: 700px; }\n\n#influenza-form .title {\n  background-color: #000000 !important;\n  color: #FFFFFF !important;\n  text-transform: uppercase;\n  padding-left: 10px;\n  line-height: 150%;\n  font-size: 14px;\n  font-weight: bold; }\n\n#influenza-form #questionsList {\n  display: grid;\n  font-size: 10px;\n  grid-template-columns: 20px 310px 30px 30px 30px auto;\n  grid-template-rows: 20px minmax(auto, auto); }\n\n#influenza-form #questionsList .col-header {\n  text-transform: uppercase;\n  font-weight: bold;\n  font-size: 12px;\n  line-height: 200%;\n  background-color: #d5d5d5 !important; }\n\n#influenza-form .footer {\n  text-align: left;\n  font-size: 10px; }\n\n#influenza-form .col-precision {\n  font-size: 10px; }\n\n#influenza-form .col-number {\n  font-weight: bold; }\n\n#influenza-form .col-responses input {\n  position: absolute;\n  top: 50%;\n  transform: translateY(-50%); }\n\n#influenza-form .col-responses {\n  position: relative; }\n\n/*# sourceMappingURL=memoryAid.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$N = undefined;
  /* module identifier */
  const __vue_module_identifier__$j = undefined;
  /* functional template */
  const __vue_is_functional_template__$N = false;
  /* component normalizer */
  function __vue_normalize__$N(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/ah650/memoryAid.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$j() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$j.styles || (__vue_create_injector__$j.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var memoryAid = __vue_normalize__$N(
    { render: __vue_render__$N, staticRenderFns: __vue_staticRenderFns__$N },
    __vue_inject_styles__$N,
    __vue_script__$N,
    __vue_scope_id__$N,
    __vue_is_functional_template__$N,
    __vue_module_identifier__$j,
    __vue_create_injector__$j);

let HeaderToolbar$5 = class HeaderToolbar extends Vue {
    get i18n() {
        return this.$i18n.t('phxCovid.general');
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get hasTransmitAccess() {
        return globalDataVuex().getFormAppData.hasTransmitAccess;
    }
    get hasExportAccess() {
        return globalDataVuex().getFormAppData.hasExportAccess;
    }
    get lockForm() {
        return globalDataVuex().getFormAppData.lockForm;
    }
    get exportLoading() {
        return globalDataVuex().getFormAppData.exportLoading;
    }
    get saveLoading() {
        return globalDataVuex().getFormAppData.saveLoading;
    }
    get isTransmit() {
        let status = this.item.status;
        return status.includes('_transmitted');
    }
    closeModalForm() {
        globalDataVuex().emitEvent({ event: 'close', data: true });
    }
    submitTransmitForm(oldStatus) {
        let newStatus;
        if (this.isTransmit) {
            newStatus = oldStatus.replace('_transmitted', '');
        }
        else {
            newStatus = oldStatus + '_transmitted';
        }
        globalDataVuex().emitEvent({ event: 'submit-transmit-form', data: newStatus });
    }
    exportToPdf() {
        globalDataVuex().emitEvent({ event: 'export-to-pdf', data: true });
    }
    saveTemporaryForm() {
        globalDataVuex().emitEvent({ event: 'save-temporary-form', data: true });
    }
};
__decorate([
    Prop()
], HeaderToolbar$5.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], HeaderToolbar$5.prototype, "item", void 0);
HeaderToolbar$5 = __decorate([
    Component
], HeaderToolbar$5);
var script$M = HeaderToolbar$5;

/* script */
            const __vue_script__$M = script$M;
            
/* template */
var __vue_render__$M = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-toolbar",
    { attrs: { card: "", dark: "", color: "primary" } },
    [
      _c(
        "v-btn",
        { attrs: { icon: "", dark: "" }, on: { click: _vm.closeModalForm } },
        [_c("v-icon", [_vm._v("close")])],
        1
      ),
      _vm._v(" "),
      _c("v-toolbar-title", [_vm._v(_vm._s(_vm.i18n.title))]),
      _vm._v(" "),
      _c("v-spacer"),
      _vm._v(" "),
      _c(
        "v-toolbar-items",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: !_vm.vuetifyBreakpoint.xsOnly,
              expression: "!vuetifyBreakpoint.xsOnly"
            }
          ]
        },
        [
          _vm.hasExportAccess
            ? _c(
                "v-btn",
                {
                  attrs: { loading: _vm.exportLoading, dark: "", flat: "" },
                  on: { click: _vm.exportToPdf }
                },
                [_vm._v("\n      " + _vm._s(_vm.i18n.exportToPdf) + "\n    ")]
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              attrs: {
                dark: "",
                flat: "",
                disabled: _vm.lockForm,
                loading: _vm.saveLoading
              },
              on: { click: _vm.saveTemporaryForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.save) + "\n    ")]
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$M = [];
__vue_render__$M._withStripped = true;

  /* style */
  const __vue_inject_styles__$M = undefined;
  /* scoped */
  const __vue_scope_id__$M = undefined;
  /* functional template */
  const __vue_is_functional_template__$M = false;
  /* component normalizer */
  function __vue_normalize__$M(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/HeaderToolbar.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var HeaderToolbar$4 = __vue_normalize__$M(
    { render: __vue_render__$M, staticRenderFns: __vue_staticRenderFns__$M },
    __vue_inject_styles__$M,
    __vue_script__$M,
    __vue_scope_id__$M,
    __vue_is_functional_template__$M);

// import toUpper from 'lodash/toUpper';
// eslint-disable-next-line @typescript-eslint/no-var-requires
let toUpper$2 = require('lodash/toUpper');
let TitleAndPatient$5 = class TitleAndPatient extends Vue {
    get i18n() {
        return this.$i18n.t('phxCovid.titleAndPatient');
    }
    get rules() {
        return {
            required: (value) => !!value || 'Required.',
            email: (value) => {
                const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return pattern.test(value) || 'Invalid e-mail.';
            },
        };
    }
    get hasPatientSectionAccess() {
        return globalDataVuex().getFormAppData.hasPatientSectionAccess;
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    mounted() {
        if (!this.item.age && this.item.birthday) {
            this.getAge();
        }
    }
    getAge() {
        let patientBirthday = this.item.birthday;
        if (patientBirthday.length === 10) {
            patientBirthday = patientBirthday.replace(/-/g, '');
        }
        if (patientBirthday.length === 8) {
            let d = patientBirthday.match(/^(\d{4})(\d{2})(\d{2})$/);
            if (d) {
                patientBirthday = d[1] + '-' + d[2] + '-' + d[3];
                let today = new Date();
                let birthDate = new Date(patientBirthday);
                let age = today.getFullYear() - birthDate.getFullYear();
                let m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
                if (!isNaN(age) && age >= 0) {
                    this.item = {
                        ...this.item,
                        age: age,
                    };
                }
            }
        }
    }
    toUpper(postalCode) {
        this.item.postalCode = toUpper$2(postalCode);
    }
};
__decorate([
    Prop()
], TitleAndPatient$5.prototype, "data", void 0);
__decorate([
    Prop()
], TitleAndPatient$5.prototype, "modePharma", void 0);
__decorate([
    Prop()
], TitleAndPatient$5.prototype, "modeMsss", void 0);
__decorate([
    Prop()
], TitleAndPatient$5.prototype, "establishmentId", void 0);
__decorate([
    Prop()
], TitleAndPatient$5.prototype, "appointmentNumber", void 0);
__decorate([
    PropSync('data', { type: Object })
], TitleAndPatient$5.prototype, "item", void 0);
TitleAndPatient$5 = __decorate([
    Component({
        mixins: [FormRules, EventModifier],
    })
], TitleAndPatient$5);
var script$L = TitleAndPatient$5;

/* script */
            const __vue_script__$L = script$L;
            
/* template */
var __vue_render__$L = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        { attrs: { sm6: "", xs12: "" } },
        [
          _c(
            "v-layout",
            {
              staticClass: "logo-parent",
              attrs: { row: "", wrap: "", "fill-height": "" }
            },
            [
              _c(
                "v-flex",
                { attrs: { xs6: "" } },
                [
                  _c("v-img", {
                    attrs: {
                      src:
                        "https://static.trimoz.com/clicsante/images/forms/phx_screening/pharmaprix_logo.jpg",
                      alt: "logo-pharmaprix",
                      width: "315"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _vm.modePharma
                ? _c(
                    "v-flex",
                    { attrs: { xs12: "", "align-self-end": "" } },
                    [
                      _c(
                        "v-layout",
                        { attrs: { row: "", wrap: "" } },
                        [
                          _c("v-flex", { attrs: { xs12: "" } }, [
                            _vm._v("nom pharma")
                          ]),
                          _vm._v(" "),
                          _c("v-flex", { attrs: { xs12: "" } }, [
                            _vm._v("address pharma")
                          ])
                        ],
                        1
                      )
                    ],
                    1
                  )
                : _vm._e()
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-flex",
        { attrs: { sm6: "", xs12: "" } },
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.name,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50",
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.name,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "name", $$v);
                      },
                      expression: "item.name"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.motherName,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.motherName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "motherName", $$v);
                      },
                      expression: "item.motherName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.fatherName,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.fatherName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "fatherName", $$v);
                      },
                      expression: "item.fatherName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs4: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.birthday,
                      placeholder: _vm.i18n.placeholder.birthday,
                      "hide-details": "",
                      mask: "####-##-##",
                      "return-masked-value": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "10",
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.validDate(v, "fr")
                        }
                      ]
                    },
                    on: { input: _vm.getAge },
                    model: {
                      value: _vm.item.birthday,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "birthday", $$v);
                      },
                      expression: "item.birthday"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c("v-flex", {
                staticClass: "border-bottom",
                attrs: { xs4: "" }
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "text-xs-center border-left border-bottom",
                  attrs: { xs4: "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      directives: [
                        {
                          name: "intercept",
                          rawName: "v-intercept:click",
                          value: _vm.clickEmitter,
                          expression: "clickEmitter",
                          arg: "click"
                        }
                      ],
                      attrs: {
                        row: "",
                        "hide-details": "",
                        disabled: !_vm.hasPatientSectionAccess
                      },
                      model: {
                        value: _vm.item.sex,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "sex", $$v);
                        },
                        expression: "item.sex"
                      }
                    },
                    [
                      _c("v-radio", {
                        key: "M",
                        attrs: { label: "M", value: "M" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        key: "F",
                        attrs: { label: "F", value: "F" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.nam,
                      mask: "AAAA #### ##N#",
                      "hide-details": "",
                      placeholder: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      box: "",
                      rules: [
                        function(v) {
                          return !v || _vm.nam(v)
                        }
                      ]
                    },
                    on: { input: _vm.setNamBarCode },
                    model: {
                      value: _vm.item.nam,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "nam", $$v);
                      },
                      expression: "item.nam"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.expiryDate,
                      placeholder: _vm.i18n.placeholder.expiryDate,
                      disabled: !_vm.hasPatientSectionAccess,
                      "hide-details": "",
                      mask: "####-##",
                      box: "",
                      rules: [
                        function(v) {
                          return !v || _vm.namExpirationDate(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.expiryDate,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "expiryDate", $$v);
                      },
                      expression: "item.expiryDate"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.address,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: _vm.i18n.placeholder.address,
                      "hide-details": "",
                      box: "",
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.address,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "address", $$v);
                      },
                      expression: "item.address"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.city,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: "Québec",
                      "hide-details": "",
                      box: "",
                      maxlength: "40"
                    },
                    model: {
                      value: _vm.item.city,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "city", $$v);
                      },
                      expression: "item.city"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.postalCode,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: "A1A1A1",
                      "hide-details": "",
                      box: "",
                      maxlength: "6",
                      rules: [
                        function(v) {
                          return !v || _vm.postalCode(v)
                        }
                      ]
                    },
                    on: {
                      input: function($event) {
                        return _vm.toUpper(_vm.item.postalCode)
                      }
                    },
                    model: {
                      value: _vm.item.postalCode,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "postalCode", $$v);
                      },
                      expression: "item.postalCode"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.applicationNumber,
                      disabled: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "10"
                    },
                    model: {
                      value: _vm.establishmentId,
                      callback: function($$v) {
                        _vm.establishmentId = $$v;
                      },
                      expression: "establishmentId"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.appointmentNumber,
                      disabled: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "10"
                    },
                    model: {
                      value: _vm.appointmentNumber,
                      callback: function($$v) {
                        _vm.appointmentNumber = $$v;
                      },
                      expression: "appointmentNumber"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$L = [];
__vue_render__$L._withStripped = true;

  /* style */
  const __vue_inject_styles__$L = function (inject) {
    if (!inject) return
    inject("data-v-954c4a44_0", { source: ".logo-parent[data-v-954c4a44] {\n  align-items: center;\n  justify-content: center;\n}\n\n/*# sourceMappingURL=TitleAndPatient.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/TitleAndPatient.vue","TitleAndPatient.vue"],"names":[],"mappings":"AAuRA;EACA,mBAAA;EACA,uBAAA;AAAA;;ACrRA,8CAA8C","file":"TitleAndPatient.vue","sourcesContent":[null,".logo-parent {\n  align-items: center;\n  justify-content: center; }\n\n/*# sourceMappingURL=TitleAndPatient.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$L = "data-v-954c4a44";
  /* module identifier */
  const __vue_module_identifier__$i = undefined;
  /* functional template */
  const __vue_is_functional_template__$L = false;
  /* component normalizer */
  function __vue_normalize__$L(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/TitleAndPatient.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$i() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$i.styles || (__vue_create_injector__$i.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var TitleAndPatient$4 = __vue_normalize__$L(
    { render: __vue_render__$L, staticRenderFns: __vue_staticRenderFns__$L },
    __vue_inject_styles__$L,
    __vue_script__$L,
    __vue_scope_id__$L,
    __vue_is_functional_template__$L,
    __vue_module_identifier__$i,
    __vue_create_injector__$i);

let GeneralInformation$5 = class GeneralInformation extends Vue {
    get i18n() {
        return this.$i18n.t('phxCovid.generalInformation');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasGeneralSectionAccess() {
        return this.globalData.getFormAppData.hasGeneralSectionAccess;
    }
};
__decorate([
    Prop()
], GeneralInformation$5.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], GeneralInformation$5.prototype, "item", void 0);
GeneralInformation$5 = __decorate([
    Component({
        mixins: [FormRules, EventModifier],
    })
], GeneralInformation$5);
var script$K = GeneralInformation$5;

/* script */
            const __vue_script__$K = script$K;
            
/* template */
var __vue_render__$K = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                {
                  staticClass: "black px-1 section-title-phx-theme",
                  attrs: { xs12: "" }
                },
                [
                  _c("span", { staticClass: "section-title white--text" }, [
                    _vm._v(_vm._s(_vm.i18n.title))
                  ])
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 px-1 font-weight-bold border-bottom section-subtitle-age",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s(_vm.i18n.user14YearsOrOlder) +
                      "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.phone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.homePhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "homePhone", $$v);
                      },
                      expression: "item.homePhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm4: "", xs8: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.otherPhone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15"
                    },
                    model: {
                      value: _vm.item.otherPhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "otherPhone", $$v);
                      },
                      expression: "item.otherPhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom user-phone", attrs: { xs4: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      directives: [
                        {
                          name: "intercept",
                          rawName: "v-intercept:click",
                          value: _vm.clickEmitter,
                          expression: "clickEmitter",
                          arg: "click"
                        }
                      ],
                      attrs: {
                        "hide-details": "",
                        disabled: !_vm.hasGeneralSectionAccess
                      },
                      model: {
                        value: _vm.item.otherPhoneType,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "otherPhoneType", $$v);
                        },
                        expression: "item.otherPhoneType"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.cell, value: "cell" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.work, value: "work" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.email,
                      disabled: !_vm.hasGeneralSectionAccess,
                      type: "email",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "75",
                      rules: [
                        function(v) {
                          return !v || _vm.email(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.email,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "email", $$v);
                      },
                      expression: "item.email"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 px-1 font-weight-bold border-bottom user-below-age",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.userUnder14) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm6: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.authorizedPerson,
                      placeholder: _vm.i18n.placeholder.authorizedPerson,
                      disabled: !_vm.hasGeneralSectionAccess,
                      "hide-details": "",
                      box: "",
                      maxlength: "70"
                    },
                    model: {
                      value: _vm.item.authorizedPersonName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonName", $$v);
                      },
                      expression: "item.authorizedPersonName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm6: "", xs12: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.email,
                      disabled: !_vm.hasGeneralSectionAccess,
                      type: "email",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "75",
                      rules: [
                        function(v) {
                          return !v || _vm.email(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonEmail,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonEmail", $$v);
                      },
                      expression: "item.authorizedPersonEmail"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom pl-1 pb-2", attrs: { xs12: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      directives: [
                        {
                          name: "intercept",
                          rawName: "v-intercept:click",
                          value: _vm.clickEmitter,
                          expression: "clickEmitter",
                          arg: "click"
                        }
                      ],
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasGeneralSectionAccess,
                        "hide-details": ""
                      },
                      model: {
                        value: _vm.item.authorizedPersonLegalRepresentative,
                        callback: function($$v) {
                          _vm.$set(
                            _vm.item,
                            "authorizedPersonLegalRepresentative",
                            $$v
                          );
                        },
                        expression: "item.authorizedPersonLegalRepresentative"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.mandatary,
                          value: "mandatory"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.guardian,
                          value: "guardian"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.curator,
                          value: "curator"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.publicCurator,
                          value: "publicCurator"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.spouse,
                          value: "spouse"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.closeRelative,
                          value: "closeRelative"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.personInterest,
                          value: "personInterest"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.parentalAuthority,
                          value: "parentalAuthority"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.phone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonHomePhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonHomePhone", $$v);
                      },
                      expression: "item.authorizedPersonHomePhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm4: "", xs8: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: _vm.i18n.otherPhone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonOtherPhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonOtherPhone", $$v);
                      },
                      expression: "item.authorizedPersonOtherPhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom user-phone", attrs: { xs4: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      directives: [
                        {
                          name: "intercept",
                          rawName: "v-intercept:click",
                          value: _vm.clickEmitter,
                          expression: "clickEmitter",
                          arg: "click"
                        }
                      ],
                      attrs: {
                        disabled: !_vm.hasGeneralSectionAccess,
                        "hide-details": ""
                      },
                      model: {
                        value: _vm.item.authorizedPersonOtherPhoneType,
                        callback: function($$v) {
                          _vm.$set(
                            _vm.item,
                            "authorizedPersonOtherPhoneType",
                            $$v
                          );
                        },
                        expression: "item.authorizedPersonOtherPhoneType"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.cell, value: "cell" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.work, value: "work" }
                      })
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$K = [];
__vue_render__$K._withStripped = true;

  /* style */
  const __vue_inject_styles__$K = undefined;
  /* scoped */
  const __vue_scope_id__$K = undefined;
  /* functional template */
  const __vue_is_functional_template__$K = false;
  /* component normalizer */
  function __vue_normalize__$K(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/GeneralInformation/GeneralInformation.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var GeneralInformation$4 = __vue_normalize__$K(
    { render: __vue_render__$K, staticRenderFns: __vue_staticRenderFns__$K },
    __vue_inject_styles__$K,
    __vue_script__$K,
    __vue_scope_id__$K,
    __vue_is_functional_template__$K);

let Notes$5 = class Notes extends Vue {
    get i18n() {
        return this.$i18n.t('phxCovid.notes');
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    get hasNotesSectionAccess() {
        return globalDataVuex().getFormAppData.hasNotesSectionAccess;
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
};
__decorate([
    Prop()
], Notes$5.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], Notes$5.prototype, "item", void 0);
Notes$5 = __decorate([
    Component({
        mixins: [FormRules, EventModifier],
    })
], Notes$5);
var script$J = Notes$5;

/* script */
            const __vue_script__$J = script$J;
            
/* template */
var __vue_render__$J = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-textarea", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      disabled: !_vm.hasNotesSectionAccess,
                      "hide-details": "",
                      box: "",
                      label: _vm.i18n.notes,
                      maxlength: "5000"
                    },
                    model: {
                      value: _vm.item.notes,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "notes", $$v);
                      },
                      expression: "item.notes"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "font-weight-medium", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.requiredField) + "\n        "
                  ),
                  _c("br")
                ]
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$J = [];
__vue_render__$J._withStripped = true;

  /* style */
  const __vue_inject_styles__$J = undefined;
  /* scoped */
  const __vue_scope_id__$J = undefined;
  /* functional template */
  const __vue_is_functional_template__$J = false;
  /* component normalizer */
  function __vue_normalize__$J(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/Notes.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var Notes$4 = __vue_normalize__$J(
    { render: __vue_render__$J, staticRenderFns: __vue_staticRenderFns__$J },
    __vue_inject_styles__$J,
    __vue_script__$J,
    __vue_scope_id__$J,
    __vue_is_functional_template__$J);

let FooterToolbarActions$5 = class FooterToolbarActions extends Vue {
    get i18n() {
        return this.$i18n.t('phxCovid.general');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasTransmitAccess() {
        return this.globalData.getFormAppData.hasTransmitAccess;
    }
    get hasExportAccess() {
        return this.globalData.getFormAppData.hasExportAccess;
    }
    get hasCompleteFormButtonAccess() {
        return this.globalData.getFormAppData.hasCompleteFormButtonAccess;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    get saveLoading() {
        return this.globalData.getFormAppData.saveLoading;
    }
    get isTransmit() {
        let status = this.item.status;
        return status.includes('_transmitted');
    }
    submitTransmitForm(oldStatus) {
        let newStatus;
        if (this.isTransmit) {
            newStatus = oldStatus.replace('_transmitted', '');
        }
        else {
            newStatus = oldStatus + '_transmitted';
        }
        this.globalData.emitEvent({ event: 'submit-transmit-form', data: newStatus });
    }
    exportToPdf() {
        this.globalData.emitEvent({ event: 'export-to-pdf', data: true });
    }
    saveTemporaryForm() {
        this.globalData.emitEvent({ event: 'save-temporary-form', data: true });
    }
    completeButtonDispatch() {
        this.globalData.emitEvent({ event: 'complete-button-dispatch', data: true });
    }
    closeModalForm() {
        this.globalData.emitEvent({ event: 'close', data: true });
    }
};
__decorate([
    Prop()
], FooterToolbarActions$5.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], FooterToolbarActions$5.prototype, "item", void 0);
FooterToolbarActions$5 = __decorate([
    Component
], FooterToolbarActions$5);
var script$I = FooterToolbarActions$5;

/* script */
            const __vue_script__$I = script$I;
            
/* template */
var __vue_render__$I = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        {
          staticClass: "text-xs-center",
          class: this.$vuetify.breakpoint.smAndDown ? "text-xs-left" : "",
          attrs: { xs12: "", "pr-2": "", "pb-2": "" }
        },
        [
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: {
                dark: "",
                flat: "",
                disabled: _vm.lockForm || _vm.saveLoading
              },
              on: { click: _vm.saveTemporaryForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.save) + "\n    ")]
          ),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: {
                dark: "",
                flat: "",
                disabled:
                  !_vm.hasCompleteFormButtonAccess ||
                  _vm.lockForm ||
                  _vm.saveLoading
              },
              on: { click: _vm.completeButtonDispatch }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.saveAndComplete) + "\n    ")]
          ),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: { dark: "", flat: "" },
              on: { click: _vm.closeModalForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.close) + "\n    ")]
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$I = [];
__vue_render__$I._withStripped = true;

  /* style */
  const __vue_inject_styles__$I = undefined;
  /* scoped */
  const __vue_scope_id__$I = undefined;
  /* functional template */
  const __vue_is_functional_template__$I = false;
  /* component normalizer */
  function __vue_normalize__$I(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/FooterToolbarActions/FooterToolbarActions.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var FooterToolbarActions$4 = __vue_normalize__$I(
    { render: __vue_render__$I, staticRenderFns: __vue_staticRenderFns__$I },
    __vue_inject_styles__$I,
    __vue_script__$I,
    __vue_scope_id__$I,
    __vue_is_functional_template__$I);

let EvaluationQuestions$1 = class EvaluationQuestions extends Vue {
    constructor() {
        super(...arguments);
        // Number of questions
        this.questions = [
            'phxCovidExperiencingSymptoms',
            'phxCovidTravelledAndAdvisedToQuarantine',
            'phxCovidReceivedPositivePcrTest',
            'phxCovidReceivedPositiveExposureNotification',
            'phxCovidAskedToGetAScreeningTest',
        ];
    }
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('phxCovid.evaluationQuestions');
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasPreImmunisationSectionAccess() {
        return this.globalData.getFormAppData.hasPreImmunisationSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
};
__decorate([
    Prop()
], EvaluationQuestions$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], EvaluationQuestions$1.prototype, "item", void 0);
EvaluationQuestions$1 = __decorate([
    Component({
        mixins: [FormRules, EventModifier],
    })
], EvaluationQuestions$1);
var script$H = EvaluationQuestions$1;

/* script */
            const __vue_script__$H = script$H;
            
/* template */
var __vue_render__$H = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            {
              staticClass: "border-top border-left border-right",
              attrs: { row: "", wrap: "" }
            },
            [
              _c(
                "v-flex",
                {
                  staticClass: "black px-1 section-title-phx-theme",
                  attrs: { xs12: "" }
                },
                [
                  _c("span", { staticClass: "section-title white--text" }, [
                    _vm._v(_vm._s("* " + _vm.i18n.title))
                  ])
                ]
              ),
              _vm._v(" "),
              _c("v-flex", {
                staticClass:
                  "\n          font-weight-bold\n          hidden-xs-only\n          border-bottom border-right\n          grey\n          lighten-2\n        ",
                attrs: { xs1: "" }
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "\n          font-weight-bold\n          hidden-xs-only\n          border-bottom border-right\n          grey\n          lighten-2\n          px-1\n          header-elements\n        ",
                  attrs: { md6: "", xs5: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.header.elements) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "\n          font-weight-bold\n          hidden-xs-only\n          border-bottom border-right\n          grey\n          lighten-2\n          px-1\n        ",
                  attrs: { md2: "", xs3: "" }
                },
                [
                  _c("span", { staticClass: "header-question header-yes" }, [
                    _vm._v(_vm._s(_vm.i18n.header.yes))
                  ]),
                  _vm._v(" "),
                  _c("span", { staticClass: "header-question header-no" }, [
                    _vm._v(_vm._s(_vm.i18n.header.no))
                  ]),
                  _vm._v(" "),
                  _c("span", { staticClass: "header-question header-NA" }, [
                    _vm._v(_vm._s(_vm.i18n.header.NA))
                  ])
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "\n          font-weight-bold\n          hidden-xs-only\n          border-bottom\n          grey\n          lighten-2\n          px-1\n          header-details\n        ",
                  attrs: { md3: "", xs3: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.header.details) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _vm._l(_vm.questions, function(i, index) {
                return [
                  _c(
                    "v-flex",
                    {
                      key: "col1-" + index,
                      staticClass: "text-xs-center font-weight-bold",
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      attrs: { xs1: "" }
                    },
                    [_vm._v("\n          " + _vm._s(index + 1) + ".\n        ")]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col2-" + index,
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      attrs: { md6: "", sm5: "", xs12: "", "pr-3": "" }
                    },
                    [
                      _c("strong", {
                        class: "question-title-" + i,
                        attrs: { "data-testid": "question-title-" + i },
                        domProps: {
                          innerHTML: _vm._s(_vm.i18n[i + "Question1"])
                        }
                      }),
                      _c("br"),
                      _vm._v(" "),
                      _c("span", { class: "question-" + i }, [
                        _vm._v(_vm._s(_vm.i18n[i + "Question2"]))
                      ])
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col3-" + index,
                      staticClass: "font-weight-bold",
                      class: _vm.vuetifyBreakpoint.xsOnly
                        ? "ml-4 mb-1"
                        : "border-bottom border-right px-1",
                      staticStyle: { position: "relative" },
                      attrs: { md2: "", sm3: "", xs12: "" }
                    },
                    [
                      _c(
                        "v-radio-group",
                        {
                          directives: [
                            {
                              name: "intercept",
                              rawName: "v-intercept:click",
                              value: _vm.clickEmitter,
                              expression: "clickEmitter",
                              arg: "click"
                            }
                          ],
                          staticClass: "mb-3",
                          attrs: {
                            row: "",
                            disabled: !_vm.hasPreImmunisationSectionAccess,
                            rules: [
                              function(v) {
                                return (
                                  !_vm.appliedRequiredField ||
                                  v === 0 ||
                                  v === "0" ||
                                  v === 1 ||
                                  v === -1 ||
                                  _vm.$t("base.errors.requiredField")
                                )
                              }
                            ]
                          },
                          model: {
                            value: _vm.item[i],
                            callback: function($$v) {
                              _vm.$set(_vm.item, i, $$v);
                            },
                            expression: "item[i]"
                          }
                        },
                        [
                          _c("v-radio", {
                            attrs: {
                              "aria-label": _vm.i18n.header.yes,
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.yes
                                : "",
                              value: 1
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              "aria-label": _vm.i18n.header.no,
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.no
                                : "",
                              value: 0
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              "aria-label": _vm.i18n.header.NA,
                              label: _vm.vuetifyBreakpoint.xsOnly
                                ? _vm.i18n.header.NA
                                : "",
                              value: -1
                            }
                          })
                        ],
                        1
                      ),
                      _vm._v(" "),
                      _c(
                        "div",
                        {
                          staticClass:
                            "yes-hint font-weight-regular caption align-baseline",
                          class: "yes-hint-" + i
                        },
                        [
                          _vm._v(
                            "\n            " +
                              _vm._s(_vm.i18n[i + "YesHint"]) +
                              "\n          "
                          )
                        ]
                      )
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      key: "col4-" + index,
                      staticClass: "border-bottom",
                      attrs: { md3: "", sm3: "", xs12: "" }
                    },
                    [
                      _c("v-text-field", {
                        directives: [
                          {
                            name: "intercept",
                            rawName: "v-intercept:click",
                            value: _vm.clickEmitter,
                            expression: "clickEmitter",
                            arg: "click"
                          }
                        ],
                        class: _vm.vuetifyBreakpoint.xsOnly ? "ml-4" : "",
                        attrs: {
                          label: _vm.i18n.precision,
                          disabled: !_vm.hasPreImmunisationSectionAccess,
                          placeholder: "",
                          "hide-details": "",
                          box: "",
                          maxlength: "255"
                        },
                        model: {
                          value: _vm.item[i + "Precision"],
                          callback: function($$v) {
                            _vm.$set(_vm.item, i + "Precision", $$v);
                          },
                          expression: "item[i + 'Precision']"
                        }
                      })
                    ],
                    1
                  )
                ]
              })
            ],
            2
          ),
          _vm._v(" "),
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { staticClass: "px-1 caption", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.importantNote) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "px-1 caption", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s(_vm.i18n.importantNote2) +
                      "\n        "
                  ),
                  _c(
                    "a",
                    {
                      attrs: {
                        href: _vm.i18n.importantNoteUrl,
                        target: "_blank"
                      }
                    },
                    [_vm._v(_vm._s(_vm.i18n.importantNoteUrl))]
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "px-1 caption", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.importantNote3) + "\n      "
                  )
                ]
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$H = [];
__vue_render__$H._withStripped = true;

  /* style */
  const __vue_inject_styles__$H = function (inject) {
    if (!inject) return
    inject("data-v-378d3ec6_0", { source: ".yes-hint[data-v-378d3ec6] {\n  line-height: 120%;\n  position: absolute;\n  bottom: 0;\n  margin: 4px;\n}\n\n/*# sourceMappingURL=EvaluationQuestions.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/EvaluationQuestions/EvaluationQuestions.vue","EvaluationQuestions.vue"],"names":[],"mappings":"AAoOA;EACA,iBAAA;EACA,kBAAA;EACA,SAAA;EACA,WAAA;AAAA;;AClOA,kDAAkD","file":"EvaluationQuestions.vue","sourcesContent":[null,".yes-hint {\n  line-height: 120%;\n  position: absolute;\n  bottom: 0;\n  margin: 4px; }\n\n/*# sourceMappingURL=EvaluationQuestions.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$H = "data-v-378d3ec6";
  /* module identifier */
  const __vue_module_identifier__$h = undefined;
  /* functional template */
  const __vue_is_functional_template__$H = false;
  /* component normalizer */
  function __vue_normalize__$H(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/EvaluationQuestions/EvaluationQuestions.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$h() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$h.styles || (__vue_create_injector__$h.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var EvaluationQuestions = __vue_normalize__$H(
    { render: __vue_render__$H, staticRenderFns: __vue_staticRenderFns__$H },
    __vue_inject_styles__$H,
    __vue_script__$H,
    __vue_scope_id__$H,
    __vue_is_functional_template__$H,
    __vue_module_identifier__$h,
    __vue_create_injector__$h);

let ScreeningTestConsent$1 = class ScreeningTestConsent extends Vue {
    get globalData() {
        return globalDataVuex();
    }
    get listItem2() {
        return this.$i18n.t('phxCovid.screeningConsent.listItem2', { consentDomain: 'https://www.quebec.ca/sante/problemes-de-sante/a-z/coronavirus-2019/tests-de-depistage/faire-test-de-depistage' });
    }
    get listItem5() {
        return this.$i18n.t('phxCovid.screeningConsent.listItem5', { consentDomain: 'https://www.quebec.ca/sante/problemes-de-sante/a-z/coronavirus-2019/tests-de-depistage/utilisation-tests-rapides-maison#c127293' });
    }
    get i18n() {
        return this.$i18n.t('phxCovid.screeningConsent');
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasPreImmunisationSectionAccess() {
        return this.globalData.getFormAppData.hasPreImmunisationSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
};
__decorate([
    Prop()
], ScreeningTestConsent$1.prototype, "data", void 0);
__decorate([
    Prop()
], ScreeningTestConsent$1.prototype, "modePharma", void 0);
__decorate([
    Prop()
], ScreeningTestConsent$1.prototype, "modeMsss", void 0);
__decorate([
    PropSync('data', { type: Object })
], ScreeningTestConsent$1.prototype, "item", void 0);
ScreeningTestConsent$1 = __decorate([
    Component({
        mixins: [FormRules, EventModifier],
    })
], ScreeningTestConsent$1);
var script$G = ScreeningTestConsent$1;

/* script */
            const __vue_script__$G = script$G;
            
/* template */
var __vue_render__$G = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            {
              staticClass: "border-top border-left border-right border-bottom",
              attrs: { row: "", wrap: "" }
            },
            [
              _c(
                "v-flex",
                {
                  staticClass:
                    "black px-1 importantTitle section-title-phx-theme",
                  attrs: { xs12: "" }
                },
                [
                  _c("span", { staticClass: "section-title white--text" }, [
                    _vm._v(_vm._s("* " + _vm.i18n.title))
                  ])
                ]
              ),
              _vm._v(" "),
              _c("div", { staticClass: "p-2" }, [
                _c("span", {
                  staticClass: "hint",
                  domProps: { innerHTML: _vm._s(_vm.i18n.hint) }
                }),
                _vm._v(" "),
                _c("ol", { staticClass: "list-items" }, [
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem1) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.listItem2) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem3) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem4) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.listItem5) }
                    })
                  ])
                ])
              ]),
              _vm._v(" "),
              _c(
                "v-layout",
                {
                  staticClass: "border-top border-bottom",
                  attrs: { row: "", wrap: "" }
                },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass: "p-2 border-right grey lighten-2",
                      attrs: { xs2: "" }
                    },
                    [
                      _c("span", {
                        staticClass: "hint",
                        domProps: { innerHTML: _vm._s(_vm.i18n.decisionText) }
                      })
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    { staticClass: "p-2 border-right", attrs: { xs5: "" } },
                    [
                      _c("v-checkbox", {
                        directives: [
                          {
                            name: "intercept",
                            rawName: "v-intercept:click",
                            value: _vm.clickEmitter,
                            expression: "clickEmitter",
                            arg: "click"
                          }
                        ],
                        attrs: {
                          label: _vm.i18n.decisionYes,
                          "true-value": 1,
                          "false-value": "0",
                          "data-testid": "decisionYes",
                          disabled: _vm.lockForm
                        },
                        model: {
                          value: _vm.item.phxCovidScreeningDetailsConsent,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "phxCovidScreeningDetailsConsent",
                              $$v
                            );
                          },
                          expression: "item.phxCovidScreeningDetailsConsent"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    { staticClass: "p-2", attrs: { xs5: "" } },
                    [
                      _c("v-checkbox", {
                        directives: [
                          {
                            name: "intercept",
                            rawName: "v-intercept:click",
                            value: _vm.clickEmitter,
                            expression: "clickEmitter",
                            arg: "click"
                          }
                        ],
                        attrs: {
                          label: _vm.i18n.decisionNo,
                          "true-value": "0",
                          "false-value": 1,
                          "data-testid": "decisionNo",
                          disabled: _vm.lockForm
                        },
                        model: {
                          value: _vm.item.phxCovidScreeningDetailsConsent,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "phxCovidScreeningDetailsConsent",
                              $$v
                            );
                          },
                          expression: "item.phxCovidScreeningDetailsConsent"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-layout",
                { attrs: { wrap: "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass: "border-bottom",
                      attrs: { xs12: "", "pl-1": "", "pb-2": "" }
                    },
                    [
                      _c("div", {
                        staticClass: "hint pt-2",
                        domProps: { textContent: _vm._s(_vm.i18n.reasonLabel) }
                      }),
                      _vm._v(" "),
                      _c(
                        "v-radio-group",
                        {
                          directives: [
                            {
                              name: "intercept",
                              rawName: "v-intercept:click",
                              value: _vm.clickEmitter,
                              expression: "clickEmitter",
                              arg: "click"
                            }
                          ],
                          attrs: {
                            row: !_vm.vuetifyBreakpoint.xsOnly,
                            rules: [
                              function(v) {
                                return (
                                  !_vm.appliedRequiredField ||
                                  v === "travel" ||
                                  v === "precaution" ||
                                  v === "other_reason" ||
                                  _vm.$t("base.errors.requiredField")
                                )
                              }
                            ],
                            disabled: _vm.lockForm
                          },
                          model: {
                            value: _vm.item.phxCovidScreeningReason,
                            callback: function($$v) {
                              _vm.$set(_vm.item, "phxCovidScreeningReason", $$v);
                            },
                            expression: "item.phxCovidScreeningReason"
                          }
                        },
                        [
                          _c("v-radio", {
                            attrs: { label: _vm.i18n.reason1, value: "travel" }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.reason2,
                              value: "precaution"
                            }
                          }),
                          _vm._v(" "),
                          _c("v-radio", {
                            attrs: {
                              label: _vm.i18n.reason3,
                              value: "other_reason"
                            }
                          }),
                          _vm._v(" "),
                          _c("v-text-field", {
                            attrs: { box: "", "hide-details": "" },
                            model: {
                              value: _vm.item.phxCovidScreeningReasonPrecision,
                              callback: function($$v) {
                                _vm.$set(
                                  _vm.item,
                                  "phxCovidScreeningReasonPrecision",
                                  $$v
                                );
                              },
                              expression:
                                "item.phxCovidScreeningReasonPrecision"
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("div", { staticClass: "p-2" }, [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.reasonHint) }
                    })
                  ])
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$G = [];
__vue_render__$G._withStripped = true;

  /* style */
  const __vue_inject_styles__$G = function (inject) {
    if (!inject) return
    inject("data-v-0e448a5a_0", { source: "\n.hint[data-v-0e448a5a] {\n  font-size: 14px;\n}\n.list-items[data-v-0e448a5a] {\n  margin-left: 20px;\n  list-style-type: decimal;\n}\n", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/ScreeningTestConsent/ScreeningTestConsent.vue"],"names":[],"mappings":";AAuIA;EACA,eAAA;AACA;AAEA;EACA,iBAAA;EACA,wBAAA;AACA","file":"ScreeningTestConsent.vue","sourcesContent":["<template>\n  <v-layout mt-2>\n    <v-flex>\n      <v-layout\n        row\n        wrap\n        class=\"border-top border-left border-right border-bottom\"\n      >\n        <v-flex xs12 class=\"black px-1 importantTitle section-title-phx-theme\">\n          <span class=\"section-title white--text\">{{ \"* \" + i18n.title }}</span>\n        </v-flex>\n        <div class=\"p-2\">\n          <span v-html=\"i18n.hint\" class=\"hint\" />\n          <ol class=\"list-items\">\n            <li><span v-html=\"i18n.listItem1\" class=\"hint\" /></li>\n            <li><span v-html=\"listItem2\" class=\"hint\" /></li>\n            <li><span v-html=\"i18n.listItem3\" class=\"hint\" /></li>\n            <li><span v-html=\"i18n.listItem4\" class=\"hint\" /></li>\n            <li><span v-html=\"listItem5\" class=\"hint\" /></li>\n          </ol>\n        </div>\n        <v-layout class=\"border-top border-bottom\" row wrap>\n          <v-flex class=\"p-2 border-right grey lighten-2\" xs2>\n            <span v-html=\"i18n.decisionText\" class=\"hint\" />\n          </v-flex>\n          <v-flex class=\"p-2 border-right\" xs5>\n            <v-checkbox\n              :label=\"i18n.decisionYes\"\n              v-model=\"item.phxCovidScreeningDetailsConsent\"\n              :true-value=\"1\"\n              :false-value=\"'0'\"\n              data-testid=\"decisionYes\"\n              :disabled=\"lockForm\"\n              v-intercept:click=\"clickEmitter\"\n            />\n          </v-flex>\n          <v-flex class=\"p-2\" xs5>\n            <v-checkbox\n              :label=\"i18n.decisionNo\"\n              v-model=\"item.phxCovidScreeningDetailsConsent\"\n              :true-value=\"'0'\"\n              :false-value=\"1\"\n              data-testid=\"decisionNo\"\n              :disabled=\"lockForm\"\n              v-intercept:click=\"clickEmitter\"\n            />\n          </v-flex>\n        </v-layout>\n        <v-layout wrap>\n          <v-flex xs12 class=\"border-bottom\" pl-1 pb-2>\n            <div class=\"hint pt-2\" v-text=\"i18n.reasonLabel\"></div>\n            <v-radio-group\n              :row=\"!vuetifyBreakpoint.xsOnly\"\n              v-model=\"item.phxCovidScreeningReason\"\n              :rules=\"[\n                (v) =>\n                  !appliedRequiredField ||\n                  v === 'travel' ||\n                  v === 'precaution' ||\n                  v === 'other_reason' ||\n                  $t('base.errors.requiredField'),\n              ]\"\n              :disabled=\"lockForm\"\n              v-intercept:click=\"clickEmitter\"\n            >\n              <v-radio :label=\"i18n.reason1\" value=\"travel\" />\n              <v-radio :label=\"i18n.reason2\" value=\"precaution\" />\n              <v-radio :label=\"i18n.reason3\" value=\"other_reason\" />\n              <v-text-field\n                box\n                hide-details\n                v-model=\"item.phxCovidScreeningReasonPrecision\"\n              ></v-text-field>\n            </v-radio-group>\n          </v-flex>\n\n          <div class=\"p-2\">\n            <span v-html=\"i18n.reasonHint\" class=\"hint\" />\n          </div>\n        </v-layout>\n      </v-layout>\n    </v-flex>\n  </v-layout>\n</template>\n\n<script lang=\"ts\">\nimport { Component, Prop, PropSync, Vue } from 'vue-property-decorator';\nimport { FormRules, EventModifier } from '@/form/mixins/mixins';\nimport { globalDataVuex } from '@/store';\nimport { LocaleMessages } from 'vue-i18n';\n\n@Component({\n  mixins: [FormRules, EventModifier],\n})\nexport default class ScreeningTestConsent extends Vue {\n  @Prop() readonly data!: Record<string, unknown>;\n  @Prop() readonly modePharma!: boolean;\n  @Prop() readonly modeMsss!: boolean;\n  @PropSync('data', { type: Object }) item!: Record<string, unknown>;\n\n  get globalData() {\n    return globalDataVuex();\n  }\n\n  get listItem2() {\n    return this.$i18n.t('phxCovid.screeningConsent.listItem2', { consentDomain: 'https://www.quebec.ca/sante/problemes-de-sante/a-z/coronavirus-2019/tests-de-depistage/faire-test-de-depistage' });\n  }\n\n    get listItem5() {\n    return this.$i18n.t('phxCovid.screeningConsent.listItem5', { consentDomain: 'https://www.quebec.ca/sante/problemes-de-sante/a-z/coronavirus-2019/tests-de-depistage/utilisation-tests-rapides-maison#c127293' });\n  }\n\n  get i18n(): LocaleMessages {\n    return this.$i18n.t('phxCovid.screeningConsent') as LocaleMessages;\n  }\n\n  get vuetifyBreakpoint() {\n    return this.globalData.getFormAppData.vuetifyBreakpoint;\n  }\n\n  get hasPreImmunisationSectionAccess() {\n    return this.globalData.getFormAppData.hasPreImmunisationSectionAccess;\n  }\n\n  get appliedRequiredField() {\n    return this.globalData.getFormAppData.appliedRequiredField;\n  }\n\n  get lockForm() {\n    return this.globalData.getFormAppData.lockForm;\n  }\n}\n</script>\n\n<style scoped>\n.hint {\n  font-size: 14px;\n}\n\n.list-items {\n  margin-left: 20px;\n  list-style-type: decimal;\n}\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$G = "data-v-0e448a5a";
  /* module identifier */
  const __vue_module_identifier__$g = undefined;
  /* functional template */
  const __vue_is_functional_template__$G = false;
  /* component normalizer */
  function __vue_normalize__$G(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/ScreeningTestConsent/ScreeningTestConsent.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$g() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$g.styles || (__vue_create_injector__$g.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var ScreeningTestConsent = __vue_normalize__$G(
    { render: __vue_render__$G, staticRenderFns: __vue_staticRenderFns__$G },
    __vue_inject_styles__$G,
    __vue_script__$G,
    __vue_scope_id__$G,
    __vue_is_functional_template__$G,
    __vue_module_identifier__$g,
    __vue_create_injector__$g);

let AcknowledgementConsent$1 = class AcknowledgementConsent extends Vue {
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('phxCovid.acknowledgementConsent');
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
};
__decorate([
    Prop()
], AcknowledgementConsent$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], AcknowledgementConsent$1.prototype, "item", void 0);
AcknowledgementConsent$1 = __decorate([
    Component({
        mixins: [FormRules, EventModifier],
    })
], AcknowledgementConsent$1);
var script$F = AcknowledgementConsent$1;

/* script */
            const __vue_script__$F = script$F;
            
/* template */
var __vue_render__$F = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "", column: "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            {
              staticClass: "border-top border-left border-right border-bottom",
              attrs: { row: "", wrap: "" }
            },
            [
              _c(
                "v-flex",
                {
                  staticClass: "black px-1 section-title-phx-theme",
                  attrs: { xs12: "" }
                },
                [
                  _c("span", { staticClass: "section-title white--text" }, [
                    _vm._v(_vm._s("* " + _vm.i18n.title))
                  ])
                ]
              ),
              _vm._v(" "),
              _c("div", { staticClass: "p-2" }, [
                _c("span", {
                  staticClass: "hint",
                  domProps: { innerHTML: _vm._s(_vm.i18n.hint) }
                }),
                _vm._v(" "),
                _c("ol", { staticClass: "list-items" }, [
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem1) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem2) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem3) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem4) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem5) }
                    })
                  ]),
                  _vm._v(" "),
                  _c("li", [
                    _c("span", {
                      staticClass: "hint",
                      domProps: { innerHTML: _vm._s(_vm.i18n.listItem6) }
                    })
                  ])
                ])
              ]),
              _vm._v(" "),
              _c(
                "v-layout",
                { staticClass: "border-top", attrs: { row: "", wrap: "" } },
                [
                  _c(
                    "v-flex",
                    { staticClass: "p-2 border-right", attrs: { xs6: "" } },
                    [
                      _c("v-checkbox", {
                        directives: [
                          {
                            name: "intercept",
                            rawName: "v-intercept:click",
                            value: _vm.clickEmitter,
                            expression: "clickEmitter",
                            arg: "click"
                          }
                        ],
                        attrs: {
                          label: _vm.i18n.consent1,
                          "true-value": 1,
                          "false-value": "0",
                          "data-testid": "decisionYes",
                          disabled: _vm.lockForm
                        },
                        model: {
                          value: _vm.item.phxCovidScreeningConsent,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "phxCovidScreeningConsent", $$v);
                          },
                          expression: "item.phxCovidScreeningConsent"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    { staticClass: "p-2", attrs: { xs6: "" } },
                    [
                      _c("v-checkbox", {
                        directives: [
                          {
                            name: "intercept",
                            rawName: "v-intercept:click",
                            value: _vm.clickEmitter,
                            expression: "clickEmitter",
                            arg: "click"
                          }
                        ],
                        attrs: {
                          label: _vm.i18n.refuse1,
                          "true-value": "0",
                          "false-value": 1,
                          "data-testid": "decisionNo",
                          disabled: _vm.lockForm
                        },
                        model: {
                          value: _vm.item.phxCovidScreeningConsent,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "phxCovidScreeningConsent", $$v);
                          },
                          expression: "item.phxCovidScreeningConsent"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "p-2 border-right border-top",
                      attrs: { xs6: "" }
                    },
                    [
                      _c("v-checkbox", {
                        directives: [
                          {
                            name: "intercept",
                            rawName: "v-intercept:click",
                            value: _vm.clickEmitter,
                            expression: "clickEmitter",
                            arg: "click"
                          }
                        ],
                        attrs: {
                          label: _vm.i18n.consent2,
                          "true-value": 1,
                          "false-value": "0",
                          "data-testid": "decisionYes",
                          disabled: _vm.lockForm
                        },
                        model: {
                          value: _vm.item.phxCovidAcceptRisk,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "phxCovidAcceptRisk", $$v);
                          },
                          expression: "item.phxCovidAcceptRisk"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    { staticClass: "p-2 border-top", attrs: { xs6: "" } },
                    [
                      _c("v-checkbox", {
                        directives: [
                          {
                            name: "intercept",
                            rawName: "v-intercept:click",
                            value: _vm.clickEmitter,
                            expression: "clickEmitter",
                            arg: "click"
                          }
                        ],
                        attrs: {
                          label: _vm.i18n.refuse2,
                          "true-value": "0",
                          "false-value": 1,
                          "data-testid": "decisionNo",
                          disabled: _vm.lockForm
                        },
                        model: {
                          value: _vm.item.phxCovidAcceptRisk,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "phxCovidAcceptRisk", $$v);
                          },
                          expression: "item.phxCovidAcceptRisk"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-flex",
        {
          staticClass:
            "grey lighten-2 px-1 font-weight-bold border-bottom border-left border-right user-below-age",
          attrs: { xs12: "" }
        },
        [
          _vm._v(
            "\n    " + _vm._s(_vm.i18n.consentsObtainedFrom.title) + "\n  "
          )
        ]
      ),
      _vm._v(" "),
      _c(
        "v-flex",
        {
          staticClass: "border-bottom pl-1 pb-2 border-left border-right",
          attrs: { xs12: "" }
        },
        [
          _c(
            "v-radio-group",
            {
              directives: [
                {
                  name: "intercept",
                  rawName: "v-intercept:click",
                  value: _vm.clickEmitter,
                  expression: "clickEmitter",
                  arg: "click"
                }
              ],
              attrs: {
                row: !_vm.vuetifyBreakpoint.xsOnly,
                "hide-details": "",
                disabled: _vm.lockForm
              },
              model: {
                value: _vm.item.phxConsentObtainedFrom,
                callback: function($$v) {
                  _vm.$set(_vm.item, "phxConsentObtainedFrom", $$v);
                },
                expression: "item.phxConsentObtainedFrom"
              }
            },
            [
              _c("v-radio", {
                attrs: {
                  label: _vm.i18n.legalRepresentative.mandatary,
                  value: "mandatory"
                }
              }),
              _vm._v(" "),
              _c("v-radio", {
                attrs: {
                  label: _vm.i18n.legalRepresentative.guardian,
                  value: "guardian"
                }
              }),
              _vm._v(" "),
              _c("v-radio", {
                attrs: {
                  label: _vm.i18n.legalRepresentative.curator,
                  value: "curator"
                }
              }),
              _vm._v(" "),
              _c("v-radio", {
                attrs: {
                  label: _vm.i18n.legalRepresentative.publicCurator,
                  value: "publicCurator"
                }
              }),
              _vm._v(" "),
              _c("v-radio", {
                attrs: {
                  label: _vm.i18n.legalRepresentative.spouse,
                  value: "spouse"
                }
              }),
              _vm._v(" "),
              _c("v-radio", {
                attrs: {
                  label: _vm.i18n.legalRepresentative.closeRelative,
                  value: "closeRelative"
                }
              }),
              _vm._v(" "),
              _c("v-radio", {
                attrs: {
                  label: _vm.i18n.legalRepresentative.personInterest,
                  value: "personInterest"
                }
              }),
              _vm._v(" "),
              _c("v-radio", {
                attrs: {
                  label: _vm.i18n.legalRepresentative.parentalAuthority,
                  value: "parentalAuthority"
                }
              })
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$F = [];
__vue_render__$F._withStripped = true;

  /* style */
  const __vue_inject_styles__$F = function (inject) {
    if (!inject) return
    inject("data-v-7d33df82_0", { source: "\n.hint[data-v-7d33df82] {\n  font-size: 14px;\n}\n.flex[data-v-7d33df82] {\n  display: flex;\n}\n.list-items[data-v-7d33df82] {\n  margin-left: 20px;\n  list-style-type: decimal;\n}\n", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/AcknowledgementConsent/AcknowledgementConsent.vue"],"names":[],"mappings":";AA2JA;EACA,eAAA;AACA;AACA;EACA,aAAA;AACA;AACA;EACA,iBAAA;EACA,wBAAA;AACA","file":"AcknowledgementConsent.vue","sourcesContent":["<template>\n  <v-layout mt-2 column>\n    <v-flex>\n      <v-layout\n        row\n        wrap\n        class=\"border-top border-left border-right border-bottom\"\n      >\n        <v-flex xs12 class=\"black px-1 section-title-phx-theme\">\n          <span class=\"section-title white--text\">{{ \"* \" + i18n.title }}</span>\n        </v-flex>\n        <div class=\"p-2\">\n          <span v-html=\"i18n.hint\" class=\"hint\" />\n          <ol class=\"list-items\">\n            <li><span v-html=\"i18n.listItem1\" class=\"hint\" /></li>\n            <li><span v-html=\"i18n.listItem2\" class=\"hint\" /></li>\n            <li><span v-html=\"i18n.listItem3\" class=\"hint\" /></li>\n            <li><span v-html=\"i18n.listItem4\" class=\"hint\" /></li>\n            <li><span v-html=\"i18n.listItem5\" class=\"hint\" /></li>\n            <li><span v-html=\"i18n.listItem6\" class=\"hint\" /></li>\n          </ol>\n        </div>\n        <v-layout class=\"border-top\" row wrap>\n          <v-flex class=\"p-2 border-right\" xs6>\n            <v-checkbox\n              :label=\"i18n.consent1\"\n              v-model=\"item.phxCovidScreeningConsent\"\n              :true-value=\"1\"\n              :false-value=\"'0'\"\n              data-testid=\"decisionYes\"\n              :disabled=\"lockForm\"\n              v-intercept:click=\"clickEmitter\"\n            />\n          </v-flex>\n          <v-flex class=\"p-2\" xs6>\n            <v-checkbox\n              :label=\"i18n.refuse1\"\n              v-model=\"item.phxCovidScreeningConsent\"\n              :true-value=\"'0'\"\n              :false-value=\"1\"\n              data-testid=\"decisionNo\"\n              :disabled=\"lockForm\"\n              v-intercept:click=\"clickEmitter\"\n            />\n          </v-flex>\n          <v-flex class=\"p-2 border-right border-top\" xs6>\n            <v-checkbox\n              :label=\"i18n.consent2\"\n              v-model=\"item.phxCovidAcceptRisk\"\n              :true-value=\"1\"\n              :false-value=\"'0'\"\n              data-testid=\"decisionYes\"\n              :disabled=\"lockForm\"\n              v-intercept:click=\"clickEmitter\"\n            />\n          </v-flex>\n          <v-flex class=\"p-2 border-top\" xs6>\n            <v-checkbox\n              :label=\"i18n.refuse2\"\n              v-model=\"item.phxCovidAcceptRisk\"\n              :true-value=\"'0'\"\n              :false-value=\"1\"\n              data-testid=\"decisionNo\"\n              :disabled=\"lockForm\"\n              v-intercept:click=\"clickEmitter\"\n            />\n          </v-flex>\n        </v-layout>\n\n  </v-layout>\n    </v-flex>\n    <v-flex xs12 class=\"grey lighten-2 px-1 font-weight-bold border-bottom border-left border-right user-below-age\">\n      {{ i18n.consentsObtainedFrom.title }}\n    </v-flex>\n    <v-flex xs12 class=\"border-bottom pl-1 pb-2 border-left border-right\">\n      <v-radio-group\n        :row=\"!vuetifyBreakpoint.xsOnly\"\n        v-model=\"item.phxConsentObtainedFrom\"\n        hide-details\n        :disabled=\"lockForm\"\n        v-intercept:click=\"clickEmitter\"\n      >\n        <v-radio\n          :label=\"i18n.legalRepresentative.mandatary\"\n          value=\"mandatory\"\n        />\n        <v-radio\n          :label=\"i18n.legalRepresentative.guardian\"\n          value=\"guardian\"\n        />\n        <v-radio\n          :label=\"i18n.legalRepresentative.curator\"\n          value=\"curator\"\n        />\n        <v-radio\n          :label=\"i18n.legalRepresentative.publicCurator\"\n          value=\"publicCurator\"\n        />\n        <v-radio\n          :label=\"i18n.legalRepresentative.spouse\"\n          value=\"spouse\"\n        />\n        <v-radio\n          :label=\"i18n.legalRepresentative.closeRelative\"\n          value=\"closeRelative\"\n        />\n        <v-radio\n          :label=\"i18n.legalRepresentative.personInterest\"\n          value=\"personInterest\"\n        />\n        <v-radio\n          :label=\"i18n.legalRepresentative.parentalAuthority\"\n          value=\"parentalAuthority\"\n        />\n      </v-radio-group>\n    </v-flex>\n  </v-layout>\n</template>\n\n<script lang=\"ts\">\nimport { Component, Prop, PropSync, Vue } from 'vue-property-decorator';\nimport { FormRules, EventModifier } from '@/form/mixins/mixins';\nimport { globalDataVuex } from '@/store';\nimport { LocaleMessages } from 'vue-i18n';\n\n@Component({\n  mixins: [FormRules, EventModifier],\n})\nexport default class AcknowledgementConsent extends Vue {\n  @Prop() readonly data!: Record<string, unknown>;\n  @PropSync('data', { type: Object }) item!: Record<string, unknown>;\n\n  get globalData() {\n    return globalDataVuex();\n  }\n\n  get i18n(): LocaleMessages {\n    return this.$i18n.t('phxCovid.acknowledgementConsent') as LocaleMessages;\n  }\n\n  get vuetifyBreakpoint() {\n    return this.globalData.getFormAppData.vuetifyBreakpoint;\n  }\n\n  get appliedRequiredField() {\n    return this.globalData.getFormAppData.appliedRequiredField;\n  }\n\n  get lockForm() {\n    return this.globalData.getFormAppData.lockForm;\n  }\n}\n</script>\n\n<style scoped>\n.hint {\n  font-size: 14px;\n}\n.flex {\n  display: flex;\n}\n.list-items {\n  margin-left: 20px;\n  list-style-type: decimal;\n}\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$F = "data-v-7d33df82";
  /* module identifier */
  const __vue_module_identifier__$f = undefined;
  /* functional template */
  const __vue_is_functional_template__$F = false;
  /* component normalizer */
  function __vue_normalize__$F(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/AcknowledgementConsent/AcknowledgementConsent.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$f() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$f.styles || (__vue_create_injector__$f.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var AcknowledgementConsent = __vue_normalize__$F(
    { render: __vue_render__$F, staticRenderFns: __vue_staticRenderFns__$F },
    __vue_inject_styles__$F,
    __vue_script__$F,
    __vue_scope_id__$F,
    __vue_is_functional_template__$F,
    __vue_module_identifier__$f,
    __vue_create_injector__$f);

let ProfessionalInformation$5 = class ProfessionalInformation extends Vue {
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('phxCovid.professionalInformation');
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get showEvaluatorBtn() {
        return this.globalData.getFormAppData.showEvaluatorBtn;
    }
    get isProfileComplete() {
        return this.globalData.getFormAppData.isProfileComplete;
    }
    get hasProfessionalSectionAccess() {
        return this.globalData.getFormAppData.hasProfessionalSectionAccess;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    openResourceForm() {
        this.globalData.emitEvent({ event: 'open-resource-form', data: true });
    }
    insertMyEvaluatorInfo() {
        this.globalData.emitEvent({ event: 'insert-my-evaluator-info', data: true });
    }
};
__decorate([
    Prop()
], ProfessionalInformation$5.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], ProfessionalInformation$5.prototype, "item", void 0);
ProfessionalInformation$5 = __decorate([
    Component({
        mixins: [FormRules, EventModifier],
    })
], ProfessionalInformation$5);
var script$E = ProfessionalInformation$5;

/* script */
            const __vue_script__$E = script$E;
            
/* template */
var __vue_render__$E = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                {
                  staticClass: "black px-1 section-title-phx-theme",
                  attrs: { md8: "", xs12: "" }
                },
                [
                  _c("span", { staticClass: "white--text section-title" }, [
                    _vm._v(_vm._s(_vm.i18n.title))
                  ])
                ]
              ),
              _vm._v(" "),
              _vm.showEvaluatorBtn
                ? _c(
                    "v-flex",
                    {
                      staticClass: "black",
                      class: _vm.vuetifyBreakpoint.smAndDown
                        ? "text-xs-left section-title-phx-theme"
                        : "text-xs-right section-title-phx-theme",
                      attrs: { md4: "", xs12: "" }
                    },
                    [
                      _vm.isProfileComplete
                        ? _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: {
                                "aria-label": "insert-evaluator-info",
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.insertMyEvaluatorInfo }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.getMyInformations) +
                                  "\n        "
                              )
                            ]
                          )
                        : _c(
                            "v-btn",
                            {
                              directives: [
                                {
                                  name: "intercept",
                                  rawName: "v-intercept:click",
                                  value: _vm.clickEmitter,
                                  expression: "clickEmitter",
                                  arg: "click"
                                }
                              ],
                              staticClass: "primary text-center",
                              attrs: {
                                "aria-label": "complete-profile",
                                disabled: _vm.lockForm,
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.openResourceForm }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.completeMyProfile) +
                                  "\n        "
                              )
                            ]
                          )
                    ],
                    1
                  )
                : _c("v-flex", {
                    staticClass: "black",
                    class: _vm.vuetifyBreakpoint.smAndDown
                      ? "text-xs-left"
                      : "text-xs-right",
                    attrs: { md4: "", xs12: "" }
                  }),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      "aria-label": "professional-name",
                      disabled: !_vm.hasProfessionalSectionAccess,
                      label: "* " + _vm.i18n.name,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "100",
                      rules: [
                        !_vm.appliedRequiredField ||
                          _vm.requiredField(
                            _vm.$i18n.t("phxCovid.base.errors.requireField")
                          )
                      ]
                    },
                    model: {
                      value: _vm.item.professionalName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "professionalName", $$v);
                      },
                      expression: "item.professionalName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "px-1 font-weight-bold border-bottom border-top grey lighten-2",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s("* " + _vm.i18n.profession.title) +
                      "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "border-bottom",
                  attrs: { xs12: "", "pl-1": "", "pb-2": "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      directives: [
                        {
                          name: "intercept",
                          rawName: "v-intercept:click",
                          value: _vm.clickEmitter,
                          expression: "clickEmitter",
                          arg: "click"
                        }
                      ],
                      attrs: {
                        "aria-label": "professional-profession",
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasProfessionalSectionAccess,
                        "hide-details": "",
                        rules: [
                          !_vm.appliedRequiredField ||
                            _vm.requiredField(
                              _vm.$i18n.t("phxCovid.base.errors.requireField")
                            )
                        ]
                      },
                      model: {
                        value: _vm.item.professionalProfession,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "professionalProfession", $$v);
                        },
                        expression: "item.professionalProfession"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.profession.atp, value: "atp" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.profession.pharmacist,
                          value: "pharmacist"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.profession.nurse,
                          value: "nurse"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.profession.other,
                          value: "other"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-text-field", {
                        staticClass: "professional-profession-other",
                        attrs: { box: "", "hide-details": "" },
                        model: {
                          value: _vm.item.professionalProfessionOther,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "professionalProfessionOther",
                              $$v
                            );
                          },
                          expression: "item.professionalProfessionOther"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm3: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      "aria-label": "professional-licence-number",
                      label: _vm.i18n.licenceNumber,
                      disabled: !_vm.hasProfessionalSectionAccess,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "25"
                    },
                    model: {
                      value: _vm.item.professionalLicenceNumber,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "professionalLicenceNumber", $$v);
                      },
                      expression: "item.professionalLicenceNumber"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm9: "", xs12: "" } },
                [
                  !_vm.item.professionalSignature
                    ? _c("v-text-field", {
                        attrs: {
                          "aria-label": "professional-signature",
                          label: "* " + _vm.i18n.professionalSignature,
                          disabled: "",
                          placeholder: "",
                          "hide-details": "",
                          box: "",
                          maxlength: "100",
                          rules: [
                            !_vm.appliedRequiredField ||
                              _vm.requiredField(
                                _vm.$i18n.t("phxCovid.base.errors.requireField")
                              )
                          ]
                        }
                      })
                    : [
                        _c(
                          "v-layout",
                          { attrs: { row: "", wrap: "" } },
                          [
                            _c("v-flex", { attrs: { xs12: "", "pl-2": "" } }, [
                              _c("span", { staticClass: "caption" }, [
                                _vm._v(_vm._s(_vm.i18n.professionalSignature))
                              ])
                            ]),
                            _vm._v(" "),
                            _c(
                              "v-flex",
                              {
                                staticStyle: {
                                  "margin-top": "-8px",
                                  "margin-bottom": "-8px"
                                }
                              },
                              [
                                _c("img", {
                                  attrs: {
                                    src: _vm.item.professionalSignature,
                                    alt: "Signature"
                                  }
                                })
                              ]
                            )
                          ],
                          1
                        )
                      ]
                ],
                2
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$E = [];
__vue_render__$E._withStripped = true;

  /* style */
  const __vue_inject_styles__$E = function (inject) {
    if (!inject) return
    inject("data-v-6c84432e_0", { source: ".professional-profession-other {\n  background: rgba(0, 0, 0, 0.06);\n}\n\n/*# sourceMappingURL=ProfessionalInformation.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/ProfessionalInformation/ProfessionalInformation.vue","ProfessionalInformation.vue"],"names":[],"mappings":"AAqMA;EACA,+BAAA;AAAA;;ACnMA,sDAAsD","file":"ProfessionalInformation.vue","sourcesContent":[null,".professional-profession-other {\n  background: rgba(0, 0, 0, 0.06); }\n\n/*# sourceMappingURL=ProfessionalInformation.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$E = undefined;
  /* module identifier */
  const __vue_module_identifier__$e = undefined;
  /* functional template */
  const __vue_is_functional_template__$E = false;
  /* component normalizer */
  function __vue_normalize__$E(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/ProfessionalInformation/ProfessionalInformation.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$e() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$e.styles || (__vue_create_injector__$e.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var ProfessionalInformation$4 = __vue_normalize__$E(
    { render: __vue_render__$E, staticRenderFns: __vue_staticRenderFns__$E },
    __vue_inject_styles__$E,
    __vue_script__$E,
    __vue_scope_id__$E,
    __vue_is_functional_template__$E,
    __vue_module_identifier__$e,
    __vue_create_injector__$e);

let Results$1 = class Results extends Vue {
    get i18n() {
        return this.$i18n.t('phxCovid.results');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasConsent() {
        return this.item.phxCovidScreeningConsent == 1 && this.item.phxCovidAcceptRisk == 1;
    }
    get prefillFields() {
        return this.globalData.getFormAppData.prefillFields;
    }
    get exportLoading() {
        return this.globalData.getFormAppData.exportLoading;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    created() {
        if (!this.item.typeOfCovidTestAdministered) {
            this.item.typeOfCovidTestAdministered = this.prefillFields.typeOfCovidTestAdministered;
        }
        if (!this.item.lot) {
            this.item.lot = this.prefillFields.lot;
        }
        if (!this.item.lotExpiryDate) {
            this.item.lotExpiryDate = this.prefillFields.lotExpiryDate;
        }
        if (!this.item.pharmacistName) {
            this.item.pharmacistName = this.prefillFields.pharmacistName;
        }
        if (!this.item.pharmacistLicenseNumber) {
            this.item.pharmacistLicenseNumber = this.prefillFields.pharmacistLicenseNumber;
        }
        if (!this.item.pharmacistTitle) {
            const title = this.prefillFields.pharmacistTitle;
            this.item.pharmacistTitle = this.i18n.professionalTitle[title];
        }
    }
    exportToPdf() {
        globalDataVuex().emitEvent({ event: 'export-to-pdf', data: { configurations: { onlyCertificate: true } } });
    }
};
__decorate([
    Prop()
], Results$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], Results$1.prototype, "item", void 0);
Results$1 = __decorate([
    Component({
        mixins: [FormRules, EventModifier],
    })
], Results$1);
var script$D = Results$1;

/* script */
            const __vue_script__$D = script$D;
            
/* template */
var __vue_render__$D = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                {
                  staticClass: "black px-1 section-title-phx-theme header",
                  attrs: { xs12: "" }
                },
                [
                  _c(
                    "span",
                    { staticClass: "section-title white--text header-title" },
                    [_vm._v(_vm._s(_vm.i18n.title))]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-btn",
                    {
                      staticClass: "primary text-center",
                      attrs: {
                        "aria-label": "insert-evaluator-info",
                        dark: "",
                        flat: "",
                        small: "",
                        loading: _vm.exportLoading
                      },
                      on: { click: _vm.exportToPdf }
                    },
                    [_vm._v(_vm._s(_vm.i18n.onlyCertificate) + "\n        ")]
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.typeOfCovidTest,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasConsent || _vm.lockForm,
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.typeOfCovidTestAdministered,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "typeOfCovidTestAdministered", $$v);
                      },
                      expression: "item.typeOfCovidTestAdministered"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.lot,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      disabled: !_vm.hasConsent || _vm.lockForm,
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.lot,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "lot", $$v);
                      },
                      expression: "item.lot"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm8: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.expiryDate,
                      placeholder: _vm.i18n.placeholder.date,
                      "hide-details": "",
                      box: "",
                      maxlength: "10",
                      mask: "####-##-##",
                      "return-masked-value": "",
                      disabled: !_vm.hasConsent || _vm.lockForm,
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.validDate(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.lotExpiryDate,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "lotExpiryDate", $$v);
                      },
                      expression: "item.lotExpiryDate"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm6: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.dateOfSpecimenCollection,
                      placeholder: _vm.i18n.placeholder.date,
                      "hide-details": "",
                      box: "",
                      maxlength: "10",
                      mask: "####-##-##",
                      "return-masked-value": "",
                      disabled: !_vm.hasConsent || _vm.lockForm,
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.validDate(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.dateOfSpecimenCollection,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "dateOfSpecimenCollection", $$v);
                      },
                      expression: "item.dateOfSpecimenCollection"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm6: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.timeOfSpecimenCollection,
                      placeholder: "",
                      mask: "##:##",
                      "return-masked-value": "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasConsent || _vm.lockForm,
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.validTime(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.timeOfSpecimenCollection,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "timeOfSpecimenCollection", $$v);
                      },
                      expression: "item.timeOfSpecimenCollection"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.nameOfPharmacist,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasConsent || _vm.lockForm,
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.pharmacistName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "pharmacistName", $$v);
                      },
                      expression: "item.pharmacistName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.pharmacistLicenseNumber,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasConsent || _vm.lockForm,
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.pharmacistLicenseNumber,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "pharmacistLicenseNumber", $$v);
                      },
                      expression: "item.pharmacistLicenseNumber"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    directives: [
                      {
                        name: "intercept",
                        rawName: "v-intercept:click",
                        value: _vm.clickEmitter,
                        expression: "clickEmitter",
                        arg: "click"
                      }
                    ],
                    attrs: {
                      label: "* " + _vm.i18n.pharmacistTitle,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasConsent || _vm.lockForm,
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.pharmacistTitle,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "pharmacistTitle", $$v);
                      },
                      expression: "item.pharmacistTitle"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 px-1 font-weight-bold border-bottom section-subtitle-age",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.testResult) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "border-bottom",
                  attrs: { xs12: "", "pl-1": "", "pb-2": "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      directives: [
                        {
                          name: "intercept",
                          rawName: "v-intercept:click",
                          value: _vm.clickEmitter,
                          expression: "clickEmitter",
                          arg: "click"
                        }
                      ],
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        "hide-details": "",
                        disabled: !_vm.hasConsent || _vm.lockForm
                      },
                      model: {
                        value: _vm.item.covidTestResult,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "covidTestResult", $$v);
                        },
                        expression: "item.covidTestResult"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.testResultNegative,
                          value: "negative"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.testResultPreliminaryPositive,
                          value: "preliminary-positive"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.testResultInconclusive,
                          value: "inconclusive"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 px-1 font-weight-bold border-bottom section-subtitle-age",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.testResult2) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "border-bottom",
                  attrs: { xs12: "", "pl-1": "", "pb-2": "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      directives: [
                        {
                          name: "intercept",
                          rawName: "v-intercept:click",
                          value: _vm.clickEmitter,
                          expression: "clickEmitter",
                          arg: "click"
                        }
                      ],
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        "hide-details": "",
                        disabled: !_vm.hasConsent || _vm.lockForm
                      },
                      model: {
                        value: _vm.item.secondCovidTestResult,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "secondCovidTestResult", $$v);
                        },
                        expression: "item.secondCovidTestResult"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.testResultNegative,
                          value: "negative"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.testResultPreliminaryPositive,
                          value: "preliminary-positive"
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.testResultInconclusive,
                          value: "inconclusive"
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$D = [];
__vue_render__$D._withStripped = true;

  /* style */
  const __vue_inject_styles__$D = function (inject) {
    if (!inject) return
    inject("data-v-5c43e22f_0", { source: ".header[data-v-5c43e22f] {\n  display: flex;\n}\n.header-title[data-v-5c43e22f] {\n  flex-grow: 1;\n}\n\n/*# sourceMappingURL=Results.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/Results/Results.vue","Results.vue"],"names":[],"mappings":"AAmRA;EACA,aAAA;AAAA;AAEA;EACA,YAAA;AAAA;;ACjRA,sCAAsC","file":"Results.vue","sourcesContent":[null,".header {\n  display: flex; }\n\n.header-title {\n  flex-grow: 1; }\n\n/*# sourceMappingURL=Results.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$D = "data-v-5c43e22f";
  /* module identifier */
  const __vue_module_identifier__$d = undefined;
  /* functional template */
  const __vue_is_functional_template__$D = false;
  /* component normalizer */
  function __vue_normalize__$D(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/form-parts/Results/Results.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$d() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$d.styles || (__vue_create_injector__$d.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var Results = __vue_normalize__$D(
    { render: __vue_render__$D, staticRenderFns: __vue_staticRenderFns__$D },
    __vue_inject_styles__$D,
    __vue_script__$D,
    __vue_scope_id__$D,
    __vue_is_functional_template__$D,
    __vue_module_identifier__$d,
    __vue_create_injector__$d);

Vue.directive('intercept', {
    bind(el, binding) {
        // @ts-ignore
        el.addEventListener(binding.arg, binding.value);
    },
    unbind(el, binding) {
        // @ts-ignore
        el.removeEventListener(binding.arg, binding.value);
    },
});
let Phx = class Phx extends Vue {
    constructor() {
        super(...arguments);
        // Dialogs
        this.showFormErrorDialog = false;
        this.showFormEditWarningDialog = false;
        this.establishmentId = this.data.establishmentId;
        this.appointmentNumber = this.data['appointment.appointmentNumber'];
    }
    /**
     * Vue Hook - Created
     */
    created() {
        // Set the data in the vuex store
        globalDataVuex().setFormAppData(this.appData);
    }
    /**
     * Retrieve the app data for this form
     */
    get getAppData() {
        return globalDataVuex().getFormAppData;
    }
    /**
     * Retrieve the last emitted event
     */
    get getLastEmittedEvent() {
        return globalDataVuex().getLastEmittedEvent;
    }
    get consentToScreeningToCompleteForm() {
        const item = this.item.data;
        if (item.phxCovidScreeningConsent === 1 && item.phxCovidAcceptRisk === 1) {
            return true;
        }
        return false;
    }
    async onEventEmit(event) {
        switch (event.event) {
            case 'complete-button-dispatch':
                await this.$emit('update-applied-required-field', true);
                if (this.validateForm(true)) {
                    await this.$emit(event.event, this.consentToScreeningToCompleteForm);
                }
                break;
            case 'export-to-pdf':
                await this.$emit(event.event, event.data);
                break;
            case 'save-temporary-form':
                await this.$emit('update-applied-required-field', false);
                if (this.validateForm(false)) {
                    await this.$emit(event.event, event.data);
                }
                break;
            default:
                await this.$emit(event.event, event.data);
        }
    }
    onAppDataUpdate() {
        globalDataVuex().setFormAppData(this.appData);
    }
    setAllowEditForm(value) {
        this.$emit('allow-edit-form', value);
    }
    validateForm(showFormErrorDialog) {
        if (!(this.influenzaForm && this.influenzaForm.validate())) {
            this.showFormErrorDialog = showFormErrorDialog;
            return false;
        }
        return true;
    }
    clicked(event) {
        if (!globalDataVuex().getFormAppData.hasReadOnly && globalDataVuex().getFormAppData.lockForm) {
            event.preventDefault();
            this.showFormEditWarningDialog = true;
        }
    }
};
__decorate([
    Ref()
], Phx.prototype, "influenzaForm", void 0);
__decorate([
    Prop()
], Phx.prototype, "data", void 0);
__decorate([
    Prop()
], Phx.prototype, "appData", void 0);
__decorate([
    PropSync('data', { type: Object })
], Phx.prototype, "item", void 0);
__decorate([
    Watch('getLastEmittedEvent')
], Phx.prototype, "onEventEmit", null);
__decorate([
    Watch('appData')
], Phx.prototype, "onAppDataUpdate", null);
Phx = __decorate([
    Component({
        components: {
            DialogFormError,
            DialogFormEditWarning,
            HeaderToolbar: HeaderToolbar$4,
            TitleAndPatient: TitleAndPatient$4,
            GeneralInformation: GeneralInformation$4,
            EvaluationQuestions,
            Notes: Notes$4,
            FooterToolbarActions: FooterToolbarActions$4,
            ScreeningTestConsent,
            AcknowledgementConsent,
            ProfessionalInformation: ProfessionalInformation$4,
            Results,
        },
    })
], Phx);
var script$C = Phx;

/* script */
            const __vue_script__$C = script$C;
            
/* template */
var __vue_render__$C = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-card",
            { attrs: { tile: "" } },
            [
              _c("header-toolbar", {
                attrs: { data: _vm.item },
                on: {
                  "update:data": function($event) {
                    _vm.item = $event;
                  }
                }
              }),
              _vm._v(" "),
              !_vm.getAppData.vuetifyBreakpoint.xsOnly
                ? _c(
                    "v-card-text",
                    [
                      _c(
                        "v-form",
                        {
                          ref: "influenzaForm",
                          attrs: { id: "phx-covid-form", "lazy-validation": "" }
                        },
                        [
                          _c("title-and-patient", {
                            attrs: {
                              data: _vm.item.data,
                              establishmentId: _vm.establishmentId,
                              appointmentNumber: _vm.appointmentNumber
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              },
                              clicked: _vm.clicked
                            }
                          }),
                          _vm._v(" "),
                          _c("general-information", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              },
                              clicked: _vm.clicked
                            }
                          }),
                          _vm._v(" "),
                          _c("evaluation-questions", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              },
                              clicked: _vm.clicked
                            }
                          }),
                          _vm._v(" "),
                          _c("screening-test-consent", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              },
                              clicked: _vm.clicked
                            }
                          }),
                          _vm._v(" "),
                          _c("acknowledgement-consent", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              },
                              clicked: _vm.clicked
                            }
                          }),
                          _vm._v(" "),
                          _c("professional-information", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              },
                              clicked: _vm.clicked
                            }
                          }),
                          _vm._v(" "),
                          _c("results", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              },
                              clicked: _vm.clicked
                            }
                          }),
                          _vm._v(" "),
                          _c("notes", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              },
                              clicked: _vm.clicked
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              _c(
                "v-card-actions",
                [
                  _c("footer-toolbar-actions", {
                    attrs: { data: _vm.item },
                    on: {
                      "update:data": function($event) {
                        _vm.item = $event;
                      }
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c("dialog-form-error", {
        model: {
          value: _vm.showFormErrorDialog,
          callback: function($$v) {
            _vm.showFormErrorDialog = $$v;
          },
          expression: "showFormErrorDialog"
        }
      }),
      _vm._v(" "),
      _c("dialog-form-edit-warning", {
        on: { "allow-edit-form": _vm.setAllowEditForm },
        model: {
          value: _vm.showFormEditWarningDialog,
          callback: function($$v) {
            _vm.showFormEditWarningDialog = $$v;
          },
          expression: "showFormEditWarningDialog"
        }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$C = [];
__vue_render__$C._withStripped = true;

  /* style */
  const __vue_inject_styles__$C = function (inject) {
    if (!inject) return
    inject("data-v-60d2956c_0", { source: "#phx-covid-form .v-input--is-disabled:not(.v-input--is-readonly) {\n  pointer-events: auto;\n}\n#phx-covid-form .border-all {\n  border: 1px black solid;\n}\n#phx-covid-form .border-all .v-input__slot {\n    background: none;\n}\n#phx-covid-form .border-top {\n  border-top: 1px solid #000000 !important;\n}\n#phx-covid-form .border-bottom {\n  border-bottom: 1px solid #000000 !important;\n}\n#phx-covid-form .border-left {\n  border-left: 1px solid #000000 !important;\n}\n#phx-covid-form .border-right {\n  border-right: 1px solid #000000 !important;\n}\n#phx-covid-form .importantTitle {\n  background-color: #FF0000 !important;\n  color: #FFFFFF !important;\n  text-transform: uppercase;\n  padding-left: 10px;\n  line-height: 180%;\n  font-size: 14px;\n  font-weight: bold;\n}\n#phx-covid-form .section-title-phx-theme {\n  background-color: #e3332b !important;\n}\n#phx-covid-form .header-question {\n  width: 47px;\n  display: inline-flex;\n}\n#phx-covid-form .v-input--checkbox,\n#phx-covid-form .v-input--radio-group {\n  margin: 0;\n}\n#phx-covid-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px;\n}\n#phx-covid-form .phone {\n  float: left;\n  width: 50%;\n}\n#phx-covid-form .vaccinator-profession .v-radio,\n#phx-covid-form .professional-profession .v-radio {\n  margin: 0 20px 0 0;\n}\n#phx-covid-form .user-phone .v-radio {\n  margin: 0;\n}\n#phx-covid-form .p-2 {\n  padding: 10px;\n}\n\n/*# sourceMappingURL=phx.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/phx.vue","phx.vue"],"names":[],"mappings":"AA2LA;EAEA,oBAAA;AAAA;AAFA;EAMA,uBAAA;AAAA;AANA;IASA,gBAAA;AAAA;AATA;EAcA,wCAAA;AAAA;AAdA;EAkBA,2CAAA;AAAA;AAlBA;EAsBA,yCAAA;AAAA;AAtBA;EA0BA,0CAAA;AAAA;AA1BA;EA8BA,oCAAA;EACA,yBAAA;EACA,yBAAA;EACA,kBAAA;EACA,iBAAA;EACA,eAAA;EACA,iBAAA;AAAA;AApCA;EAwCA,oCAAA;AAAA;AAxCA;EA2CA,WAAA;EACA,oBAAA;AAAA;AA5CA;;EAiDA,SAAA;AAAA;AAjDA;EAqDA,kBAAA;EACA,eAAA;AAAA;AAtDA;EA0DA,WAAA;EACA,UAAA;AAAA;AA3DA;;EAgEA,kBAAA;AAAA;AAhEA;EAoEA,SAAA;AAAA;AApEA;EAuEA,aAAA;AAAA;;ACxMA,kCAAkC","file":"phx.vue","sourcesContent":[null,"#phx-covid-form .v-input--is-disabled:not(.v-input--is-readonly) {\n  pointer-events: auto; }\n\n#phx-covid-form .border-all {\n  border: 1px black solid; }\n  #phx-covid-form .border-all .v-input__slot {\n    background: none; }\n\n#phx-covid-form .border-top {\n  border-top: 1px solid #000000 !important; }\n\n#phx-covid-form .border-bottom {\n  border-bottom: 1px solid #000000 !important; }\n\n#phx-covid-form .border-left {\n  border-left: 1px solid #000000 !important; }\n\n#phx-covid-form .border-right {\n  border-right: 1px solid #000000 !important; }\n\n#phx-covid-form .importantTitle {\n  background-color: #FF0000 !important;\n  color: #FFFFFF !important;\n  text-transform: uppercase;\n  padding-left: 10px;\n  line-height: 180%;\n  font-size: 14px;\n  font-weight: bold; }\n\n#phx-covid-form .section-title-phx-theme {\n  background-color: #e3332b !important; }\n\n#phx-covid-form .header-question {\n  width: 47px;\n  display: inline-flex; }\n\n#phx-covid-form .v-input--checkbox,\n#phx-covid-form .v-input--radio-group {\n  margin: 0; }\n\n#phx-covid-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px; }\n\n#phx-covid-form .phone {\n  float: left;\n  width: 50%; }\n\n#phx-covid-form .vaccinator-profession .v-radio,\n#phx-covid-form .professional-profession .v-radio {\n  margin: 0 20px 0 0; }\n\n#phx-covid-form .user-phone .v-radio {\n  margin: 0; }\n\n#phx-covid-form .p-2 {\n  padding: 10px; }\n\n/*# sourceMappingURL=phx.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$C = undefined;
  /* module identifier */
  const __vue_module_identifier__$c = undefined;
  /* functional template */
  const __vue_is_functional_template__$C = false;
  /* component normalizer */
  function __vue_normalize__$C(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/phx/phx.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$c() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$c.styles || (__vue_create_injector__$c.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var phx = __vue_normalize__$C(
    { render: __vue_render__$C, staticRenderFns: __vue_staticRenderFns__$C },
    __vue_inject_styles__$C,
    __vue_script__$C,
    __vue_scope_id__$C,
    __vue_is_functional_template__$C,
    __vue_module_identifier__$c,
    __vue_create_injector__$c);

let HeaderToolbar$3 = class HeaderToolbar extends Vue {
    get i18n() {
        return this.$i18n.t('as624.general');
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get hasTransmitAccess() {
        return globalDataVuex().getFormAppData.hasTransmitAccess;
    }
    get hasExportAccess() {
        return globalDataVuex().getFormAppData.hasExportAccess;
    }
    get lockForm() {
        return globalDataVuex().getFormAppData.lockForm;
    }
    get exportLoading() {
        return globalDataVuex().getFormAppData.exportLoading;
    }
    get saveLoading() {
        return globalDataVuex().getFormAppData.saveLoading;
    }
    get isTransmit() {
        let status = this.item.status;
        return status.includes('_transmitted');
    }
    closeModalForm() {
        globalDataVuex().emitEvent({ event: 'close', data: true });
    }
    submitTransmitForm(oldStatus) {
        let newStatus;
        if (this.isTransmit) {
            newStatus = oldStatus.replace('_transmitted', '');
        }
        else {
            newStatus = oldStatus + '_transmitted';
        }
        globalDataVuex().emitEvent({ event: 'submit-transmit-form', data: newStatus });
    }
    exportToPdf() {
        globalDataVuex().emitEvent({ event: 'export-to-pdf', data: true });
    }
    saveTemporaryForm() {
        globalDataVuex().emitEvent({ event: 'save-temporary-form', data: true });
    }
};
__decorate([
    Prop()
], HeaderToolbar$3.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], HeaderToolbar$3.prototype, "item", void 0);
HeaderToolbar$3 = __decorate([
    Component
], HeaderToolbar$3);
var script$B = HeaderToolbar$3;

/* script */
            const __vue_script__$B = script$B;
            
/* template */
var __vue_render__$B = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-toolbar",
    { attrs: { card: "", dark: "", color: "primary" } },
    [
      _c(
        "v-btn",
        { attrs: { icon: "", dark: "" }, on: { click: _vm.closeModalForm } },
        [_c("v-icon", [_vm._v("close")])],
        1
      ),
      _vm._v(" "),
      _c("v-toolbar-title", [_vm._v(_vm._s(_vm.i18n.title))]),
      _vm._v(" "),
      _c("v-spacer"),
      _vm._v(" "),
      _c(
        "v-toolbar-items",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: !_vm.vuetifyBreakpoint.xsOnly,
              expression: "!vuetifyBreakpoint.xsOnly"
            }
          ]
        },
        [
          _vm.hasTransmitAccess
            ? _c(
                "v-btn",
                {
                  attrs: { dark: "", flat: "" },
                  on: {
                    click: function($event) {
                      return _vm.submitTransmitForm(_vm.item.status)
                    }
                  }
                },
                [
                  !_vm.isTransmit
                    ? _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsTransmitted))
                      ])
                    : _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsNotTransmitted))
                      ])
                ]
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.hasExportAccess
            ? _c(
                "v-btn",
                {
                  attrs: { loading: _vm.exportLoading, dark: "", flat: "" },
                  on: { click: _vm.exportToPdf }
                },
                [_vm._v("\n      " + _vm._s(_vm.i18n.exportToPdf) + "\n    ")]
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              attrs: {
                dark: "",
                flat: "",
                disabled: _vm.lockForm,
                loading: _vm.saveLoading
              },
              on: { click: _vm.saveTemporaryForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.save) + "\n    ")]
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$B = [];
__vue_render__$B._withStripped = true;

  /* style */
  const __vue_inject_styles__$B = undefined;
  /* scoped */
  const __vue_scope_id__$B = undefined;
  /* functional template */
  const __vue_is_functional_template__$B = false;
  /* component normalizer */
  function __vue_normalize__$B(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/HeaderToolbar.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var HeaderToolbar$2 = __vue_normalize__$B(
    { render: __vue_render__$B, staticRenderFns: __vue_staticRenderFns__$B },
    __vue_inject_styles__$B,
    __vue_script__$B,
    __vue_scope_id__$B,
    __vue_is_functional_template__$B);

// import toUpper from 'lodash/toUpper';
// eslint-disable-next-line @typescript-eslint/no-var-requires
let JsBarcode$1 = require('jsbarcode');
// eslint-disable-next-line @typescript-eslint/no-var-requires
let toUpper$1 = require('lodash/toUpper');
let TitleAndPatient$3 = class TitleAndPatient extends Vue {
    constructor() {
        super(...arguments);
        this.showNamBarCode = false;
    }
    get i18n() {
        return this.$i18n.t('as624.titleAndPatient');
    }
    get hasPatientSectionAccess() {
        return globalDataVuex().getFormAppData.hasPatientSectionAccess;
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    mounted() {
        if (this.item.nam) {
            this.setNamBarCode();
        }
        if (!this.item.age && this.item.birthday) {
            this.getAge();
        }
    }
    getAge() {
        let patientBirthday = this.item.birthday;
        if (patientBirthday.length === 10) {
            patientBirthday = patientBirthday.replace(/-/g, '');
        }
        if (patientBirthday.length === 8) {
            let d = patientBirthday.match(/^(\d{4})(\d{2})(\d{2})$/);
            if (d) {
                patientBirthday = d[1] + '-' + d[2] + '-' + d[3];
                let today = new Date();
                let birthDate = new Date(patientBirthday);
                let age = today.getFullYear() - birthDate.getFullYear();
                let m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
                if (!isNaN(age) && age >= 0) {
                    this.item = {
                        ...this.item,
                        age: age,
                    };
                }
            }
        }
    }
    setNamBarCode() {
        const nam = this.item.nam;
        if (nam.length === 12) {
            JsBarcode$1('#code128', nam, {
                format: 'CODE128',
                width: 2,
                height: 35,
            });
            this.item.namCodeBar = this.namBarCodeToBase64();
            this.showNamBarCode = true;
        }
        else {
            this.item.namCodeBar = '';
            this.showNamBarCode = false;
        }
    }
    namBarCodeToBase64() {
        const canvas = document.createElement('canvas');
        JsBarcode$1(canvas, this.item.nam, { format: 'CODE128' });
        return canvas.toDataURL('image/png');
    }
    toUpper(postalCode) {
        this.item.postalCode = toUpper$1(postalCode);
    }
};
__decorate([
    Prop()
], TitleAndPatient$3.prototype, "data", void 0);
__decorate([
    Prop()
], TitleAndPatient$3.prototype, "establishmentId", void 0);
__decorate([
    Prop()
], TitleAndPatient$3.prototype, "appointmentNumber", void 0);
__decorate([
    PropSync('data', { type: Object })
], TitleAndPatient$3.prototype, "item", void 0);
TitleAndPatient$3 = __decorate([
    Component({
        mixins: [FormRules],
    })
], TitleAndPatient$3);
var script$A = TitleAndPatient$3;

/* script */
            const __vue_script__$A = script$A;
            
/* template */
var __vue_render__$A = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        { attrs: { sm6: "", xs12: "" } },
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "", "fill-height": "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs6: "" } },
                [
                  _c("v-img", {
                    staticClass: "logoAS624",
                    attrs: {
                      src:
                        "https://static.trimoz.com/clicsante/images/forms/as624/logo1-AS-624.png",
                      alt: "logo-AS-624"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs6: "" } },
                [
                  _c("v-img", {
                    staticClass: "barcodeAS624",
                    attrs: { src: _vm.i18n.barcodeUrl, alt: "barcode-AS-624" }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.showNamBarCode,
                      expression: "showNamBarCode"
                    }
                  ],
                  attrs: { xs12: "" }
                },
                [_c("svg", { attrs: { id: "code128" } })]
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-flex",
        { attrs: { sm6: "", xs12: "" } },
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.fileNo,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.fileNo,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "fileNo", $$v);
                      },
                      expression: "item.fileNo"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.name,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50",
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.name,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "name", $$v);
                      },
                      expression: "item.name"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.motherName,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50",
                      rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                    },
                    model: {
                      value: _vm.item.motherName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "motherName", $$v);
                      },
                      expression: "item.motherName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.fatherName,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.fatherName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "fatherName", $$v);
                      },
                      expression: "item.fatherName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs4: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.birthday,
                      placeholder: _vm.i18n.placeholder.birthday,
                      "hide-details": "",
                      mask: "####-##-##",
                      "return-masked-value": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "10",
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.validDate(v)
                        }
                      ]
                    },
                    on: { input: _vm.getAge },
                    model: {
                      value: _vm.item.birthday,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "birthday", $$v);
                      },
                      expression: "item.birthday"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c("v-flex", {
                staticClass: "border-bottom",
                attrs: { xs4: "" }
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "text-xs-center border-left border-bottom",
                  attrs: { xs4: "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: "",
                        "hide-details": "",
                        disabled: !_vm.hasPatientSectionAccess
                      },
                      model: {
                        value: _vm.item.sex,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "sex", $$v);
                        },
                        expression: "item.sex"
                      }
                    },
                    [
                      _c("v-radio", {
                        key: "M",
                        attrs: { label: "M", value: "M" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        key: "F",
                        attrs: { label: "F", value: "F" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.nam,
                      mask: "AAAA #### ##N#",
                      "hide-details": "",
                      placeholder: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      box: "",
                      rules: [
                        function(v) {
                          return !v || _vm.nam(v)
                        }
                      ]
                    },
                    on: { input: _vm.setNamBarCode },
                    model: {
                      value: _vm.item.nam,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "nam", $$v);
                      },
                      expression: "item.nam"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.expiryDate,
                      placeholder: _vm.i18n.placeholder.expiryDate,
                      disabled: !_vm.hasPatientSectionAccess,
                      "hide-details": "",
                      mask: "####-##",
                      box: "",
                      rules: [
                        function(v) {
                          return !v || _vm.namExpirationDate(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.expiryDate,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "expiryDate", $$v);
                      },
                      expression: "item.expiryDate"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.address,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: _vm.i18n.placeholder.address,
                      "hide-details": "",
                      box: "",
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.address,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "address", $$v);
                      },
                      expression: "item.address"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.city,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: "Québec",
                      "hide-details": "",
                      box: "",
                      maxlength: "40"
                    },
                    model: {
                      value: _vm.item.city,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "city", $$v);
                      },
                      expression: "item.city"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.postalCode,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: "A1A1A1",
                      "hide-details": "",
                      box: "",
                      maxlength: "6",
                      rules: [
                        function(v) {
                          return !v || _vm.postalCode(v)
                        }
                      ]
                    },
                    on: {
                      input: function($event) {
                        return _vm.toUpper(_vm.item.postalCode)
                      }
                    },
                    model: {
                      value: _vm.item.postalCode,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "postalCode", $$v);
                      },
                      expression: "item.postalCode"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.applicationNumber,
                      disabled: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "10"
                    },
                    model: {
                      value: _vm.establishmentId,
                      callback: function($$v) {
                        _vm.establishmentId = $$v;
                      },
                      expression: "establishmentId"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.appointmentNumber,
                      disabled: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "10"
                    },
                    model: {
                      value: _vm.appointmentNumber,
                      callback: function($$v) {
                        _vm.appointmentNumber = $$v;
                      },
                      expression: "appointmentNumber"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$A = [];
__vue_render__$A._withStripped = true;

  /* style */
  const __vue_inject_styles__$A = function (inject) {
    if (!inject) return
    inject("data-v-18caa55a_0", { source: "\n.logoAS624[data-v-18caa55a] {\n  width: 200px;\n}\n.barcodeAS624[data-v-18caa55a] {\n  margin-top: 25px;\n  width: 175px;\n}\n", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/TitleAndPatient.vue"],"names":[],"mappings":";AAqUA;EACA,YAAA;AACA;AAEA;EACA,gBAAA;EACA,YAAA;AACA","file":"TitleAndPatient.vue","sourcesContent":["<template>\n  <v-layout row wrap>\n\n    <!-- LOGO & CODEBAR -->\n    <v-flex sm6 xs12 >\n      <v-layout row wrap fill-height>\n        <v-flex xs6>\n          <v-img\n            src=\"https://static.trimoz.com/clicsante/images/forms/as624/logo1-AS-624.png\"\n            alt=\"logo-AS-624\"\n            class=\"logoAS624\"/>\n        </v-flex>\n        <v-flex xs6>\n          <v-img\n              :src=\"i18n.barcodeUrl\"\n              alt=\"barcode-AS-624\"\n              class=\"barcodeAS624\"\n          />\n        </v-flex>\n        <v-flex xs12 v-show=\"showNamBarCode\">\n          <svg id=\"code128\"></svg>\n        </v-flex>\n      </v-layout>\n    </v-flex>\n\n    <v-flex sm6 xs12>\n\n      <!-- USER'S INFORMATION -->\n      <v-layout row wrap class=\"border-all\">\n        <v-flex xs12>\n          <v-text-field\n            v-model=\"item.fileNo\"\n            :label=\"i18n.fileNo\"\n            placeholder=\"\"\n            hide-details\n            box\n            :disabled=\"!hasPatientSectionAccess\"\n            maxlength=\"50\"\n          />\n        </v-flex>\n        <v-flex xs12>\n          <v-text-field\n              v-model=\"item.name\"\n              :label=\"'* ' + i18n.name\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n              :rules=\"[\n                !appliedRequiredField || requiredField()\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs12>\n          <v-text-field\n              :label=\"'* ' + i18n.motherName\"\n              v-model=\"item.motherName\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n              :rules=\"[\n                !appliedRequiredField || requiredField()\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs12>\n          <v-text-field\n              :label=\"i18n.fatherName\"\n              v-model=\"item.fatherName\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n          />\n        </v-flex>\n        <v-flex xs4 class=\"\">\n          <v-text-field\n              v-model=\"item.birthday\"\n              :label=\"'* ' + i18n.birthday\"\n              :placeholder=\"i18n.placeholder.birthday\"\n              hide-details\n              mask=\"####-##-##\"\n              return-masked-value\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"10\"\n              @input=\"getAge\"\n              :rules=\"[\n                !appliedRequiredField || requiredField(),\n                v => !v || validDate(v)\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs4 class=\"border-bottom\">\n        </v-flex>\n        <v-flex xs4 class=\"text-xs-center border-left border-bottom\">\n          <v-radio-group\n              row\n              v-model=\"item.sex\"\n              hide-details\n              :disabled=\"!hasPatientSectionAccess\"\n          >\n            <v-radio\n                key=\"M\"\n                label=\"M\"\n                value=\"M\"\n            />\n            <v-radio\n                key=\"F\"\n                label=\"F\"\n                value=\"F\"\n            />\n          </v-radio-group>\n        </v-flex>\n\n        <v-flex xs7 :class=\"vuetifyBreakpoint.smAndDown ? '' : 'border-right'\">\n          <v-text-field\n              :label=\"i18n.nam\"\n              mask=\"AAAA #### ##N#\"\n              v-model=\"item.nam\"\n              hide-details\n              placeholder=\"\"\n              :disabled=\"!hasPatientSectionAccess\"\n              @input=\"setNamBarCode\"\n              box\n              :rules=\"[\n                v => !v || nam(v)\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs5>\n          <v-text-field\n              :label=\"i18n.expiryDate\"\n              v-model=\"item.expiryDate\"\n              :placeholder=\"i18n.placeholder.expiryDate\"\n              :disabled=\"!hasPatientSectionAccess\"\n              hide-details\n              mask=\"####-##\"\n              box\n              :rules=\"[\n                v => !v || namExpirationDate(v)\n              ]\"\n          />\n        </v-flex>\n\n        <v-flex xs12>\n          <v-text-field\n              :label=\"i18n.address\"\n              v-model=\"item.address\"\n              :disabled=\"!hasPatientSectionAccess\"\n              :placeholder=\"i18n.placeholder.address\"\n              hide-details\n              box\n              maxlength=\"50\"\n          />\n        </v-flex>\n\n        <v-flex xs7 :class=\"vuetifyBreakpoint.smAndDown ? '' : 'border-right'\">\n          <v-text-field\n              :label=\"i18n.city\"\n              v-model=\"item.city\"\n              :disabled=\"!hasPatientSectionAccess\"\n              placeholder=\"Québec\"\n              hide-details\n              box\n              maxlength=\"40\"\n          />\n        </v-flex>\n        <v-flex xs5>\n          <v-text-field\n              :label=\"i18n.postalCode\"\n              v-model=\"item.postalCode\"\n              :disabled=\"!hasPatientSectionAccess\"\n              placeholder=\"A1A1A1\"\n              hide-details\n              box\n              maxlength=\"6\"\n              @input=\"toUpper(item.postalCode)\"\n              :rules=\"[\n                v => !v || postalCode(v)\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs7 :class=\"vuetifyBreakpoint.smAndDown ? '' : 'border-right'\">\n          <v-text-field\n              :label=\"i18n.applicationNumber\"\n              v-model=\"establishmentId\"\n              disabled\n              hide-details\n              box\n              maxlength=\"10\"\n          />\n        </v-flex>\n        <v-flex xs5>\n          <v-text-field\n              :label=\"i18n.appointmentNumber\"\n              v-model=\"appointmentNumber\"\n              disabled\n              hide-details\n              box\n              maxlength=\"10\"\n          />\n        </v-flex>\n      </v-layout>\n    </v-flex>\n  </v-layout>\n</template>\n\n<script lang=\"ts\">\nimport { Component, Prop, PropSync, Vue } from 'vue-property-decorator';\nimport { FormRules } from '@/form/mixins/mixins';\nimport { globalDataVuex } from '@/store';\nimport { LocaleMessages } from 'vue-i18n';\nimport { As624FormData } from '@/form/components/as624-2023/interfaces/as624.form-data';\n// import toUpper from 'lodash/toUpper';\n\n// eslint-disable-next-line @typescript-eslint/no-var-requires\nlet JsBarcode = require('jsbarcode');\n// eslint-disable-next-line @typescript-eslint/no-var-requires\nlet toUpper = require('lodash/toUpper');\n\n@Component({\n  mixins: [ FormRules ],\n})\nexport default class TitleAndPatient extends Vue {\n  @Prop() readonly data!: As624FormData;\n  @Prop() readonly establishmentId!: string;\n  @Prop() readonly appointmentNumber!: string;\n  @PropSync('data', { type: Object }) item!: Record<string, unknown>;\n\n  showNamBarCode = false;\n\n  get i18n(): LocaleMessages {\n    return this.$i18n.t('as624.titleAndPatient') as LocaleMessages;\n  }\n\n  get hasPatientSectionAccess() {\n    return globalDataVuex().getFormAppData.hasPatientSectionAccess;\n  }\n\n  get vuetifyBreakpoint() {\n    return globalDataVuex().getFormAppData.vuetifyBreakpoint;\n  }\n\n  get appliedRequiredField() {\n    return globalDataVuex().getFormAppData.appliedRequiredField;\n  }\n\n  mounted() {\n    if (this.item.nam) {\n      this.setNamBarCode();\n    }\n\n    if (!this.item.age && this.item.birthday) {\n      this.getAge();\n    }\n  }\n\n  getAge() {\n    let patientBirthday: string = this.item.birthday as string;\n\n    if (patientBirthday.length === 10) {\n      patientBirthday = patientBirthday.replace(/-/g, '');\n    }\n\n    if (patientBirthday.length === 8) {\n      let d = patientBirthday.match(/^(\\d{4})(\\d{2})(\\d{2})$/);\n      if (d) {\n        patientBirthday = d[1] + '-' + d[2] + '-' + d[3];\n\n        let today = new Date();\n        let birthDate = new Date(patientBirthday);\n        let age = today.getFullYear() - birthDate.getFullYear();\n        let m = today.getMonth() - birthDate.getMonth();\n\n        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {\n          age--;\n        }\n\n        if (!isNaN(age) && age >= 0) {\n          this.item = {\n            ...this.item,\n            age: age,\n          };\n        }\n      }\n    }\n  }\n\n  setNamBarCode() {\n    const nam: string = this.item.nam as string;\n\n    if (nam.length === 12) {\n      JsBarcode('#code128', nam, {\n        format: 'CODE128',\n        width: 2,\n        height: 35,\n      });\n\n      this.item.namCodeBar = this.namBarCodeToBase64();\n      this.showNamBarCode = true;\n    }\n    else {\n      this.item.namCodeBar = '';\n      this.showNamBarCode = false;\n    }\n  }\n\n  namBarCodeToBase64(){\n    const canvas = document.createElement('canvas');\n    JsBarcode(canvas, this.item.nam, { format: 'CODE128' });\n    return canvas.toDataURL('image/png');\n  }\n\n  toUpper(postalCode: string) {\n    this.item.postalCode = toUpper(postalCode);\n  }\n}\n</script>\n\n<style scoped>\n  .logoAS624 {\n    width: 200px;\n  }\n\n  .barcodeAS624 {\n    margin-top: 25px;\n    width: 175px;\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$A = "data-v-18caa55a";
  /* module identifier */
  const __vue_module_identifier__$b = undefined;
  /* functional template */
  const __vue_is_functional_template__$A = false;
  /* component normalizer */
  function __vue_normalize__$A(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/TitleAndPatient.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$b() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$b.styles || (__vue_create_injector__$b.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var TitleAndPatient$2 = __vue_normalize__$A(
    { render: __vue_render__$A, staticRenderFns: __vue_staticRenderFns__$A },
    __vue_inject_styles__$A,
    __vue_script__$A,
    __vue_scope_id__$A,
    __vue_is_functional_template__$A,
    __vue_module_identifier__$b,
    __vue_create_injector__$b);

var PhoneTypeEnum$1;
(function (PhoneTypeEnum) {
    PhoneTypeEnum["CELL"] = "cell";
    PhoneTypeEnum["WORK"] = "work";
})(PhoneTypeEnum$1 || (PhoneTypeEnum$1 = {}));
var PersonLegalRepresentativeEnum$1;
(function (PersonLegalRepresentativeEnum) {
    PersonLegalRepresentativeEnum["PARENTAL_AUTHORITY"] = "parentalAuthority";
    PersonLegalRepresentativeEnum["PATIENT"] = "patient";
    PersonLegalRepresentativeEnum["MANDATARY"] = "mandatary";
    PersonLegalRepresentativeEnum["GUARDIAN"] = "guardian";
    PersonLegalRepresentativeEnum["CURATOR"] = "curator";
    PersonLegalRepresentativeEnum["PUBLIC_CURATOR"] = "publicCurator";
    PersonLegalRepresentativeEnum["SPOUSE"] = "spouse";
    PersonLegalRepresentativeEnum["CLOSE_RELATIVE"] = "closeRelative";
    PersonLegalRepresentativeEnum["PERSON_INTEREST"] = "personInterest";
    PersonLegalRepresentativeEnum["SPECIAL_INTEREST"] = "specialInterest";
})(PersonLegalRepresentativeEnum$1 || (PersonLegalRepresentativeEnum$1 = {}));
var AdministrationReasonInfluenzaEnum$1;
(function (AdministrationReasonInfluenzaEnum) {
    AdministrationReasonInfluenzaEnum["RESIDENT_CHSLD"] = "Resident in a CHSLD";
    AdministrationReasonInfluenzaEnum["RESIDENT_RPA"] = "Resident in a RPA";
    AdministrationReasonInfluenzaEnum["PREGNANT_WOMAN"] = "Pregnant woman";
    AdministrationReasonInfluenzaEnum["HEALTH_WORKER"] = "Health worker";
    AdministrationReasonInfluenzaEnum["CHRONIC_ILLNESS"] = "Chronic illness";
    AdministrationReasonInfluenzaEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonInfluenzaEnum$1 || (AdministrationReasonInfluenzaEnum$1 = {}));
var AdministrationReasonShinglesEnum$1;
(function (AdministrationReasonShinglesEnum) {
    AdministrationReasonShinglesEnum["IMMUNOCOMPROMISED"] = "Immunocompromised";
    AdministrationReasonShinglesEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonShinglesEnum$1 || (AdministrationReasonShinglesEnum$1 = {}));
var AdministrationReasonDcatEnum$1;
(function (AdministrationReasonDcatEnum) {
    AdministrationReasonDcatEnum["PREGNANT_WOMAN"] = "Pregnant woman";
    AdministrationReasonDcatEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonDcatEnum$1 || (AdministrationReasonDcatEnum$1 = {}));
var AdministrationReasonCovidEnum$1;
(function (AdministrationReasonCovidEnum) {
    AdministrationReasonCovidEnum["RESIDENT_CHSLD"] = "Resident in a CHSLD";
    AdministrationReasonCovidEnum["RESIDENT_RPA"] = "Resident in a RPA";
    AdministrationReasonCovidEnum["PREGNANT_WOMAN"] = "Pregnant woman";
    AdministrationReasonCovidEnum["HEALTH_WORKER"] = "Health worker";
    AdministrationReasonCovidEnum["CHRONIC_ILLNESS"] = "Chronic illness";
    AdministrationReasonCovidEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonCovidEnum$1 || (AdministrationReasonCovidEnum$1 = {}));
var AdministrationReasonMpoxEnum$1;
(function (AdministrationReasonMpoxEnum) {
    AdministrationReasonMpoxEnum["PRE_EXPOSURE_IMMUNOCOMPROMISED"] = "Pre-exposure - immunocompromised";
    AdministrationReasonMpoxEnum["PRE_EXPOSURE_NON_IMMUNOCOMPROMISED"] = "Pre-exposure - non-immunocompromised";
    AdministrationReasonMpoxEnum["POST_EXPOSURE_IMMUNOCOMPROMISED"] = "Post-exposure - immunocompromised";
    AdministrationReasonMpoxEnum["POST_EXPOSURE_NON_IMMUNOCOMPROMISED"] = "Post-exposure - non-immunocompromised";
    AdministrationReasonMpoxEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonMpoxEnum$1 || (AdministrationReasonMpoxEnum$1 = {}));
var ProfessionEnum$1;
(function (ProfessionEnum) {
    ProfessionEnum["NURSE"] = "nurse";
    ProfessionEnum["PHYSICIAN"] = "physician";
    ProfessionEnum["INHALOTHERAPIST"] = "inhalotherapist";
    ProfessionEnum["MIDWIFE"] = "midwife";
    ProfessionEnum["PHARMACIST"] = "pharmacist";
    ProfessionEnum["OTHER"] = "other";
})(ProfessionEnum$1 || (ProfessionEnum$1 = {}));
var InjectionSiteEnum$1;
(function (InjectionSiteEnum) {
    InjectionSiteEnum["RIGHT_ARM"] = "rightArm";
    InjectionSiteEnum["LEFT_ARM"] = "leftArm";
    InjectionSiteEnum["RIGHT_THIGH"] = "rightThigh";
    InjectionSiteEnum["LEFT_THIGH"] = "leftThigh";
    InjectionSiteEnum["RIGHT_NOSTRIL"] = "rightNostril";
    InjectionSiteEnum["LEFT_NOSTRIL"] = "leftNostril";
})(InjectionSiteEnum$1 || (InjectionSiteEnum$1 = {}));
var ClosedQuestionsEnum$1;
(function (ClosedQuestionsEnum) {
    ClosedQuestionsEnum[ClosedQuestionsEnum["NO"] = 0] = "NO";
    ClosedQuestionsEnum[ClosedQuestionsEnum["YES"] = 1] = "YES";
    ClosedQuestionsEnum[ClosedQuestionsEnum["NA"] = -1] = "NA";
})(ClosedQuestionsEnum$1 || (ClosedQuestionsEnum$1 = {}));
var SexEnum$1;
(function (SexEnum) {
    SexEnum["M"] = "M";
    SexEnum["F"] = "F";
})(SexEnum$1 || (SexEnum$1 = {}));
var VaccineCategoryIdsEnum$1;
(function (VaccineCategoryIdsEnum) {
    VaccineCategoryIdsEnum["ZONA"] = "zona";
    VaccineCategoryIdsEnum["FLU"] = "flu";
    VaccineCategoryIdsEnum["COVID"] = "covid";
    VaccineCategoryIdsEnum["MPOX"] = "mpox";
    VaccineCategoryIdsEnum["DCAT"] = "dcat";
    VaccineCategoryIdsEnum["HEPATITIS"] = "hepatitis";
    VaccineCategoryIdsEnum["MENINGITIS"] = "meningitis";
    VaccineCategoryIdsEnum["PNEUMOCOCCAL"] = "pneumococcal";
    VaccineCategoryIdsEnum["RABIES"] = "rabies";
    VaccineCategoryIdsEnum["ROTAVIRUS"] = "rotavirus";
    VaccineCategoryIdsEnum["RRO"] = "rro";
    VaccineCategoryIdsEnum["TUBERCULOSIS"] = "tuberculosis";
    VaccineCategoryIdsEnum["VPH"] = "vph";
    VaccineCategoryIdsEnum["TRAVEL"] = "travel";
    VaccineCategoryIdsEnum["VRS"] = "vrs";
    VaccineCategoryIdsEnum["VRS_BABY"] = "vrs_baby";
    VaccineCategoryIdsEnum["TYPHOID"] = "typhoid";
    VaccineCategoryIdsEnum["AVIAN_INFLUENZA"] = "avian_influenza";
})(VaccineCategoryIdsEnum$1 || (VaccineCategoryIdsEnum$1 = {}));
var AdministrationEnum$1;
(function (AdministrationEnum) {
    AdministrationEnum["INTRAMUSCULAR"] = "intramuscular";
    AdministrationEnum["SUBCUTANEOUS"] = "subcutaneous";
    AdministrationEnum["ORAL"] = "oral";
    AdministrationEnum["INTRANASAL"] = "intranasal";
    AdministrationEnum["INTRADERMIC"] = "intradermic";
})(AdministrationEnum$1 || (AdministrationEnum$1 = {}));
var VaccineIdDcatEnum$1;
(function (VaccineIdDcatEnum) {
    VaccineIdDcatEnum["ACT_HIB"] = "actHib";
    VaccineIdDcatEnum["ADACEL"] = "adacel";
    VaccineIdDcatEnum["ADACEL_POLIO"] = "adacelPolio";
    VaccineIdDcatEnum["BOOSTRIX"] = "boostrix";
    VaccineIdDcatEnum["BOOSTRIX_POLIO"] = "boostrixPolio";
    VaccineIdDcatEnum["HIBERIX"] = "hiberix";
    VaccineIdDcatEnum["IMOVAX_POLIO"] = "imovaxPolio";
    VaccineIdDcatEnum["INFANRIX_HEXA"] = "infanrixHexa";
    VaccineIdDcatEnum["INFANRIX_IPV_HIB"] = "infanrixIPVHib";
    VaccineIdDcatEnum["PEDIACEL"] = "pediacel";
    VaccineIdDcatEnum["TD"] = "td";
    VaccineIdDcatEnum["PENTACEL"] = "pentacel";
})(VaccineIdDcatEnum$1 || (VaccineIdDcatEnum$1 = {}));
const DCatVaccinesRequiredForAdministrativeReason$1 = [
    'adacel',
    'adacelPolio',
    'boostrix',
    'boostrixPolio',
    'infanrixHexa',
    'infanrixIPVHib',
    'pediacel',
    'pentacel',
];

let GeneralInformation$3 = class GeneralInformation extends Vue {
    get i18n() {
        return this.$i18n.t('as624.generalInformation');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasGeneralSectionAccess() {
        return this.globalData.getFormAppData.hasGeneralSectionAccess;
    }
};
__decorate([
    Prop()
], GeneralInformation$3.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], GeneralInformation$3.prototype, "item", void 0);
GeneralInformation$3 = __decorate([
    Component({
        computed: {
            PersonLegalRepresentativeEnum() {
                return PersonLegalRepresentativeEnum$1;
            },
        },
        mixins: [FormRules],
    })
], GeneralInformation$3);
var script$z = GeneralInformation$3;

/* script */
            const __vue_script__$z = script$z;
            
/* template */
var __vue_render__$z = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c("v-flex", { staticClass: "black px-1", attrs: { xs12: "" } }, [
                _c("span", { staticClass: "section-title white--text" }, [
                  _vm._v(_vm._s(_vm.i18n.title))
                ])
              ]),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 px-1 font-weight-bold border-bottom section-subtitle-age",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " +
                      _vm._s(_vm.i18n.user14YearsOrOlder) +
                      "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.phone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        !_vm.appliedRequiredField || _vm.requiredField(),
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.homePhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "homePhone", $$v);
                      },
                      expression: "item.homePhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm4: "", xs8: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.otherPhone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15"
                    },
                    model: {
                      value: _vm.item.otherPhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "otherPhone", $$v);
                      },
                      expression: "item.otherPhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom user-phone", attrs: { xs4: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        "hide-details": "",
                        disabled: !_vm.hasGeneralSectionAccess
                      },
                      model: {
                        value: _vm.item.otherPhoneType,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "otherPhoneType", $$v);
                        },
                        expression: "item.otherPhoneType"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.cell, value: "cell" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.work, value: "work" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.email,
                      disabled: !_vm.hasGeneralSectionAccess,
                      type: "email",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "75",
                      rules: [
                        function(v) {
                          return !v || _vm.email(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.email,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "email", $$v);
                      },
                      expression: "item.email"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 px-1 font-weight-bold border-bottom user-below-age",
                  attrs: { xs12: "" }
                },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.userUnder14) + "\n      "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm6: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.authorizedPerson,
                      placeholder: _vm.i18n.placeholder.authorizedPerson,
                      disabled: !_vm.hasGeneralSectionAccess,
                      "hide-details": "",
                      box: "",
                      maxlength: "70"
                    },
                    model: {
                      value: _vm.item.authorizedPersonName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonName", $$v);
                      },
                      expression: "item.authorizedPersonName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm6: "", xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.email,
                      disabled: !_vm.hasGeneralSectionAccess,
                      type: "email",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "75",
                      rules: [
                        function(v) {
                          return !v || _vm.email(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonEmail,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonEmail", $$v);
                      },
                      expression: "item.authorizedPersonEmail"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom pl-1 pb-2", attrs: { xs12: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasGeneralSectionAccess,
                        "hide-details": ""
                      },
                      model: {
                        value: _vm.item.authorizedPersonLegalRepresentative,
                        callback: function($$v) {
                          _vm.$set(
                            _vm.item,
                            "authorizedPersonLegalRepresentative",
                            $$v
                          );
                        },
                        expression: "item.authorizedPersonLegalRepresentative"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.parentalAuthority,
                          value:
                            _vm.PersonLegalRepresentativeEnum.PARENTAL_AUTHORITY
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.spouse,
                          value: _vm.PersonLegalRepresentativeEnum.SPOUSE
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.mandatary,
                          value: _vm.PersonLegalRepresentativeEnum.MANDATARY
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.personInterest,
                          value:
                            _vm.PersonLegalRepresentativeEnum.PERSON_INTEREST
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.closeRelative,
                          value:
                            _vm.PersonLegalRepresentativeEnum.CLOSE_RELATIVE
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.guardian,
                          value: _vm.PersonLegalRepresentativeEnum.GUARDIAN
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { sm4: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.phone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonHomePhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonHomePhone", $$v);
                      },
                      expression: "item.authorizedPersonHomePhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { sm4: "", xs8: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.otherPhone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      placeholder: "",
                      mask: "phone",
                      "hide-details": "",
                      box: "",
                      maxlength: "15",
                      rules: [
                        function(v) {
                          return !v || _vm.telephone2(v)
                        }
                      ]
                    },
                    model: {
                      value: _vm.item.authorizedPersonOtherPhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonOtherPhone", $$v);
                      },
                      expression: "item.authorizedPersonOtherPhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom user-phone", attrs: { xs4: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        disabled: !_vm.hasGeneralSectionAccess,
                        "hide-details": ""
                      },
                      model: {
                        value: _vm.item.authorizedPersonOtherPhoneType,
                        callback: function($$v) {
                          _vm.$set(
                            _vm.item,
                            "authorizedPersonOtherPhoneType",
                            $$v
                          );
                        },
                        expression: "item.authorizedPersonOtherPhoneType"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.cell, value: "cell" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.work, value: "work" }
                      })
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$z = [];
__vue_render__$z._withStripped = true;

  /* style */
  const __vue_inject_styles__$z = undefined;
  /* scoped */
  const __vue_scope_id__$z = undefined;
  /* functional template */
  const __vue_is_functional_template__$z = false;
  /* component normalizer */
  function __vue_normalize__$z(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/GeneralInformation/GeneralInformation.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var GeneralInformation$2 = __vue_normalize__$z(
    { render: __vue_render__$z, staticRenderFns: __vue_staticRenderFns__$z },
    __vue_inject_styles__$z,
    __vue_script__$z,
    __vue_scope_id__$z,
    __vue_is_functional_template__$z);

let PreImmunization$3 = class PreImmunization extends Vue {
    constructor() {
        super(...arguments);
        // Number of questions
        this.questions = 7;
    }
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('as624.preImmunization');
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasPreImmunisationSectionAccess() {
        return this.globalData.getFormAppData.hasPreImmunisationSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
};
__decorate([
    Prop()
], PreImmunization$3.prototype, "data", void 0);
__decorate([
    Prop()
], PreImmunization$3.prototype, "modePharma", void 0);
__decorate([
    Prop()
], PreImmunization$3.prototype, "modeMsss", void 0);
__decorate([
    PropSync('data', { type: Object })
], PreImmunization$3.prototype, "item", void 0);
PreImmunization$3 = __decorate([
    Component({
        mixins: [FormRules],
    })
], PreImmunization$3);
var script$y = PreImmunization$3;

/* script */
            const __vue_script__$y = script$y;
            
/* template */
var __vue_render__$y = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    { staticClass: "px-2 py-0 mt-2 border-all", attrs: { fluid: "" } },
    [
      _c(
        "v-layout",
        [
          _c(
            "v-flex",
            { staticClass: "black px-1 py-0", attrs: { xs12: "" } },
            [
              _c("span", { staticClass: "section-title white--text" }, [
                _vm._v(_vm._s("* " + _vm.i18n.title))
              ])
            ]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass:
                "grey lighten-2 grey--text text--lighten-2 text-xs-center font-weight-bold border-bottom border-right px-2",
              attrs: { shrink: "" }
            },
            [_vm._v("0.")]
          ),
          _vm._v(" "),
          _c("v-flex", {
            staticClass:
              "font-weight-bold hidden-xs-only border-bottom border-right grey lighten-2 px-1 header-elements",
            attrs: { md6: "", xs5: "" },
            domProps: { innerHTML: _vm._s(_vm.i18n.header.elements) }
          }),
          _vm._v(" "),
          _c(
            "v-flex",
            {
              staticClass:
                "font-weight-bold hidden-xs-only border-bottom border-right grey lighten-2 px-1",
              attrs: { shrink: "" }
            },
            [
              _c("span", { staticClass: "header-question header-yes" }, [
                _vm._v(_vm._s(_vm.i18n.header.yes))
              ]),
              _vm._v(" "),
              _c("span", { staticClass: "header-question header-no" }, [
                _vm._v(_vm._s(_vm.i18n.header.no))
              ]),
              _vm._v(" "),
              _c("span", {
                staticClass: "header-question header-NA",
                domProps: { innerHTML: _vm._s(_vm.i18n.header.NA) }
              })
            ]
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            {
              staticClass:
                "font-weight-bold hidden-xs-only border-bottom grey lighten-2 px-1 header-details",
              attrs: { grow: "" }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.header.details) + "\n    ")]
          )
        ],
        1
      ),
      _vm._v(" "),
      _vm._l(_vm.questions, function(i, index) {
        return _c(
          "v-layout",
          { key: index, attrs: { row: "" } },
          [
            _c(
              "v-flex",
              {
                key: "col1-" + index,
                staticClass:
                  "text-xs-center font-weight-bold border-bottom border-right px-2",
                attrs: { shrink: "" }
              },
              [_vm._v("\n      " + _vm._s(i) + ".\n    ")]
            ),
            _vm._v(" "),
            _c(
              "v-flex",
              {
                key: "col2-" + index,
                class: _vm.vuetifyBreakpoint.xsOnly
                  ? "ml-4 mb-1"
                  : "border-bottom border-right px-1",
                attrs: { md6: "", sm5: "", xs12: "", "pr-3": "" }
              },
              [
                _c("strong", { class: "question-title-" + i }, [
                  _vm._v(_vm._s(_vm.i18n["title" + i]))
                ]),
                _vm._v(" "),
                _vm.i18n["title" + i] ? _c("br") : _vm._e(),
                _vm._v(" "),
                _c("span", {
                  class: "question-" + i,
                  domProps: { innerHTML: _vm._s(_vm.i18n["question" + i]) }
                })
              ]
            ),
            _vm._v(" "),
            _c(
              "v-flex",
              {
                key: "col3-" + index,
                staticClass: "font-weight-bold",
                class: _vm.vuetifyBreakpoint.xsOnly
                  ? "ml-4 mb-1"
                  : "border-bottom border-right px-1",
                attrs: { shrink: "" }
              },
              [
                _c(
                  "v-radio-group",
                  {
                    attrs: {
                      row: "",
                      disabled: !_vm.hasPreImmunisationSectionAccess,
                      rules: [
                        function(v) {
                          return (
                            !_vm.appliedRequiredField ||
                            v === 0 ||
                            v === 1 ||
                            v === -1 ||
                            _vm.$t("base.errors.requiredField")
                          )
                        }
                      ]
                    },
                    model: {
                      value: _vm.item["preImmunization" + i],
                      callback: function($$v) {
                        _vm.$set(_vm.item, "preImmunization" + i, $$v);
                      },
                      expression: "item['preImmunization' + i]"
                    }
                  },
                  [
                    _c("v-radio", {
                      attrs: {
                        "aria-label": _vm.i18n.header.yes,
                        label: _vm.vuetifyBreakpoint.xsOnly
                          ? _vm.i18n.header.yes
                          : "",
                        value: 1
                      }
                    }),
                    _vm._v(" "),
                    _c("v-radio", {
                      attrs: {
                        "aria-label": _vm.i18n.header.no,
                        label: _vm.vuetifyBreakpoint.xsOnly
                          ? _vm.i18n.header.no
                          : "",
                        value: 0
                      }
                    }),
                    _vm._v(" "),
                    _c("v-radio", {
                      attrs: {
                        "aria-label": _vm.i18n.header.NA,
                        label: _vm.vuetifyBreakpoint.xsOnly
                          ? _vm.i18n.header.NA
                          : "",
                        value: -1
                      }
                    })
                  ],
                  1
                )
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "v-flex",
              {
                key: "col4-" + index,
                staticClass: "border-bottom",
                attrs: { grow: "" }
              },
              [
                _c("v-text-field", {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "ml-4" : "",
                  attrs: {
                    label: _vm.i18n.precision,
                    disabled: !_vm.hasPreImmunisationSectionAccess,
                    placeholder: "",
                    "hide-details": "",
                    box: "",
                    maxlength: "255"
                  },
                  model: {
                    value: _vm.item["preImmunizationPrecision" + i],
                    callback: function($$v) {
                      _vm.$set(_vm.item, "preImmunizationPrecision" + i, $$v);
                    },
                    expression: "item['preImmunizationPrecision' + i]"
                  }
                })
              ],
              1
            )
          ],
          1
        )
      }),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c("v-flex", { staticClass: "px-1 caption", attrs: { xs12: "" } }, [
            _vm._v("\n      " + _vm._s(_vm.i18n.importantNote) + "\n    ")
          ])
        ],
        1
      )
    ],
    2
  )
};
var __vue_staticRenderFns__$y = [];
__vue_render__$y._withStripped = true;

  /* style */
  const __vue_inject_styles__$y = undefined;
  /* scoped */
  const __vue_scope_id__$y = undefined;
  /* functional template */
  const __vue_is_functional_template__$y = false;
  /* component normalizer */
  function __vue_normalize__$y(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/PreImmunization/PreImmunization.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var PreImmunization$2 = __vue_normalize__$y(
    { render: __vue_render__$y, staticRenderFns: __vue_staticRenderFns__$y },
    __vue_inject_styles__$y,
    __vue_script__$y,
    __vue_scope_id__$y,
    __vue_is_functional_template__$y);

let AdministrativeReason$3 = class AdministrativeReason extends Vue {
    get i18n() {
        return this.$i18n.t('as624.administration');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasReasonSectionAccess() {
        return this.globalData.getFormAppData.hasReasonSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get isReasonsRequired() {
        let result = {
            'zona': false,
            'flu': false,
            'covid': false,
            'mpox': false,
            'dcat': false,
            'hepatitis': false,
            'meningitis': false,
            'pneumococcal': false,
            'rabies': false,
            'rotavirus': false,
            'rro': false,
            'tuberculosis': false,
            'vph': false,
            'travel': false,
            'vrs': false,
            'vrs_baby': false,
            'typhoid': false,
            'avian_influenza': false,
        };
        const vaccineCategoryIds = Object.values(VaccineCategoryIdsEnum$1);
        for (let i = 1; i <= this.numberOfLineToDisplay; i++) {
            const prop = this.item[`vaccineDetailsCategory${i}`];
            const vaccineName = this.item[`vaccineDetailsName${i}`];
            if (vaccineCategoryIds.includes(prop)) {
                if ((prop === VaccineCategoryIdsEnum$1.DCAT && !DCatVaccinesRequiredForAdministrativeReason$1.includes(vaccineName)) || prop === VaccineCategoryIdsEnum$1.AVIAN_INFLUENZA) {
                    result[prop] = false;
                }
                else {
                    result[prop] = true;
                }
            }
        }
        return result;
    }
};
__decorate([
    Prop()
], AdministrativeReason$3.prototype, "data", void 0);
__decorate([
    Prop()
], AdministrativeReason$3.prototype, "numberOfLineToDisplay", void 0);
__decorate([
    PropSync('data', { type: Object })
], AdministrativeReason$3.prototype, "item", void 0);
AdministrativeReason$3 = __decorate([
    Component({
        computed: {
            VaccineCategoryIdsEnum() {
                return VaccineCategoryIdsEnum$1;
            },
            AdministrationReasonInfluenzaEnum() {
                return AdministrationReasonInfluenzaEnum$1;
            },
            AdministrationReasonMpoxEnum() {
                return AdministrationReasonMpoxEnum$1;
            },
            AdministrationReasonCovidEnum() {
                return AdministrationReasonCovidEnum$1;
            },
            AdministrationReasonDcatEnum() {
                return AdministrationReasonDcatEnum$1;
            },
            AdministrationReasonShinglesEnum() {
                return AdministrationReasonShinglesEnum$1;
            },
            AdministrationReasonInfluenzaArray() {
                return Object.keys(AdministrationReasonInfluenzaEnum$1);
            },
            AdministrationReasonMpoxArray() {
                return Object.keys(AdministrationReasonMpoxEnum$1);
            },
            AdministrationReasonCovidArray() {
                return Object.keys(AdministrationReasonCovidEnum$1);
            },
            AdministrationReasonShinglesArray() {
                return Object.keys(AdministrationReasonShinglesEnum$1);
            },
            AdministrationReasonDcatArray() {
                return Object.keys(AdministrationReasonDcatEnum$1);
            },
        },
        mixins: [FormRules],
    })
], AdministrativeReason$3);
var script$x = AdministrativeReason$3;

/* script */
            const __vue_script__$x = script$x;
            
/* template */
var __vue_render__$x = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    { staticClass: "px-2 py-0 mt-2 border-all", attrs: { fluid: "" } },
    [
      _c(
        "v-layout",
        [
          _c(
            "v-flex",
            { staticClass: "black px-1 py-0", attrs: { xs12: "" } },
            [
              _c("span", { staticClass: "section-title white--text" }, [
                _vm._v(_vm._s(_vm.i18n.title))
              ])
            ]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "", wrap: "", "fill-height": "" } },
        [
          _c(
            "v-flex",
            { staticClass: "pa-0" },
            [
              _c(
                "v-layout",
                { attrs: { "fill-height": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "administrative-reason-vaccine grey lighten-2 text-xs-center font-weight-bold px-0",
                      attrs: { shrink: "" }
                    },
                    [
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n.reason.shingles.title) +
                          "\n        "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "text-xs-left border-left px-2",
                      attrs: { grow: "" }
                    },
                    _vm._l(_vm.AdministrationReasonShinglesArray, function(
                      reason,
                      index
                    ) {
                      return _c("v-checkbox", {
                        key: reason,
                        attrs: {
                          label: _vm.i18n.reason.shingles["" + (index + 1)],
                          "aria-label":
                            "administration-reason-shingles-" +
                            _vm.AdministrationReasonShinglesEnum[reason],
                          value:
                            "" + _vm.AdministrationReasonShinglesEnum[reason],
                          disabled: !_vm.hasReasonSectionAccess,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.isReasonsRequired[
                                _vm.VaccineCategoryIdsEnum.ZONA
                              ] ||
                              _vm.requiredField()
                          ]
                        },
                        model: {
                          value: _vm.item.administrationReasonShingles,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "administrationReasonShingles",
                              $$v
                            );
                          },
                          expression: "item.administrationReasonShingles"
                        }
                      })
                    }),
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "pa-0" },
            [
              _c(
                "v-layout",
                { attrs: { "fill-height": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "administrative-reason-vaccine grey lighten-2 text-xs-center font-weight-bold border-right px-0",
                      attrs: { shrink: "" }
                    },
                    [
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n.reason.influenza.title) +
                          "\n        "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "text-xs-left border-right border-left px-2",
                      attrs: { grow: "" }
                    },
                    _vm._l(_vm.AdministrationReasonInfluenzaArray, function(
                      reason,
                      index
                    ) {
                      return _c("v-checkbox", {
                        key: reason,
                        attrs: {
                          label: _vm.i18n.reason.influenza["" + (index + 1)],
                          "aria-label":
                            "administration-reason-influenza-" +
                            _vm.AdministrationReasonInfluenzaEnum[reason],
                          value:
                            "" + _vm.AdministrationReasonInfluenzaEnum[reason],
                          disabled: !_vm.hasReasonSectionAccess,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.isReasonsRequired[
                                _vm.VaccineCategoryIdsEnum.FLU
                              ] ||
                              _vm.requiredField()
                          ]
                        },
                        model: {
                          value: _vm.item.administrationReasonInfluenza,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "administrationReasonInfluenza",
                              $$v
                            );
                          },
                          expression: "item.administrationReasonInfluenza"
                        }
                      })
                    }),
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "pa-0" },
            [
              _c(
                "v-layout",
                { attrs: { "fill-height": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "administrative-reason-vaccine grey lighten-2 text-xs-center font-weight-bold px-0",
                      attrs: { shrink: "" }
                    },
                    [
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n.reason.dcat.title) +
                          "\n        "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "text-xs-left border-left px-2",
                      attrs: { grow: "" }
                    },
                    _vm._l(_vm.AdministrationReasonDcatArray, function(
                      reason,
                      index
                    ) {
                      return _c("v-checkbox", {
                        key: reason,
                        attrs: {
                          label: _vm.i18n.reason.dcat["" + (index + 1)],
                          "aria-label":
                            "administration-reason-dcat-" +
                            _vm.AdministrationReasonDcatEnum[reason],
                          value: "" + _vm.AdministrationReasonDcatEnum[reason],
                          disabled: !_vm.hasReasonSectionAccess,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.isReasonsRequired[
                                _vm.VaccineCategoryIdsEnum.DCAT
                              ] ||
                              _vm.requiredField()
                          ]
                        },
                        model: {
                          value: _vm.item.administrationReasonDcat,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "administrationReasonDcat", $$v);
                          },
                          expression: "item.administrationReasonDcat"
                        }
                      })
                    }),
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "pa-0" },
            [
              _c(
                "v-layout",
                { attrs: { "fill-height": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "administrative-reason-vaccine grey lighten-2 text-xs-center font-weight-bold border-right px-0",
                      attrs: { shrink: "" }
                    },
                    [
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n.reason.covid.title) +
                          "\n        "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "text-xs-left border-left px-2",
                      attrs: { grow: "" }
                    },
                    _vm._l(_vm.AdministrationReasonCovidArray, function(
                      reason,
                      index
                    ) {
                      return _c("v-checkbox", {
                        key: reason,
                        attrs: {
                          label: _vm.i18n.reason.covid["" + (index + 1)],
                          "aria-label":
                            "administration-reason-covid-" +
                            _vm.AdministrationReasonCovidEnum[reason],
                          value: "" + _vm.AdministrationReasonCovidEnum[reason],
                          disabled: !_vm.hasReasonSectionAccess,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.isReasonsRequired[
                                _vm.VaccineCategoryIdsEnum.COVID
                              ] ||
                              _vm.requiredField()
                          ]
                        },
                        model: {
                          value: _vm.item.administrationReasonCovid,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "administrationReasonCovid", $$v);
                          },
                          expression: "item.administrationReasonCovid"
                        }
                      })
                    }),
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "pa-0" },
            [
              _c(
                "v-layout",
                { attrs: { "fill-height": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "administrative-reason-vaccine grey lighten-2 text-xs-center font-weight-bold border-right px-0",
                      attrs: { shrink: "" }
                    },
                    [
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n.reason.mpox.title) +
                          "\n        "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "text-xs-left border-right border-left px-2",
                      attrs: { grow: "" }
                    },
                    _vm._l(_vm.AdministrationReasonMpoxArray, function(
                      reason,
                      index
                    ) {
                      return _c("v-checkbox", {
                        key: reason,
                        attrs: {
                          label: _vm.i18n.reason.mpox["" + (index + 1)],
                          "aria-label":
                            "administration-reason-mpox-" +
                            _vm.AdministrationReasonMpoxEnum[reason],
                          value: "" + _vm.AdministrationReasonMpoxEnum[reason],
                          disabled: !_vm.hasReasonSectionAccess,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.isReasonsRequired[
                                _vm.VaccineCategoryIdsEnum.MPOX
                              ] ||
                              _vm.requiredField()
                          ]
                        },
                        model: {
                          value: _vm.item.administrationReasonMpox,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "administrationReasonMpox", $$v);
                          },
                          expression: "item.administrationReasonMpox"
                        }
                      })
                    }),
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$x = [];
__vue_render__$x._withStripped = true;

  /* style */
  const __vue_inject_styles__$x = undefined;
  /* scoped */
  const __vue_scope_id__$x = undefined;
  /* functional template */
  const __vue_is_functional_template__$x = false;
  /* component normalizer */
  function __vue_normalize__$x(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/AdministrativeReason/AdministrativeReason.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var AdministrativeReason$2 = __vue_normalize__$x(
    { render: __vue_render__$x, staticRenderFns: __vue_staticRenderFns__$x },
    __vue_inject_styles__$x,
    __vue_script__$x,
    __vue_scope_id__$x,
    __vue_is_functional_template__$x);

let VaccineCategoryPrefillInput$3 = class VaccineCategoryPrefillInput extends Vue {
};
__decorate([
    Prop()
], VaccineCategoryPrefillInput$3.prototype, "index", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$3.prototype, "value", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$3.prototype, "item", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$3.prototype, "vaccineCategoryList", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$3.prototype, "appliedRequiredField", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$3.prototype, "isVaccineCategoryRequired", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$3.prototype, "disabled", void 0);
VaccineCategoryPrefillInput$3 = __decorate([
    Component({
        mixins: [FormRules],
    })
], VaccineCategoryPrefillInput$3);
var script$w = VaccineCategoryPrefillInput$3;

/* script */
            const __vue_script__$w = script$w;
            
/* template */
var __vue_render__$w = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("v-autocomplete", {
    attrs: {
      "hide-details": "",
      "aria-label": "vaccine-category",
      dense: "",
      items: _vm.vaccineCategoryList,
      "item-text": "name",
      "item-value": "id",
      disabled: _vm.disabled,
      box: "",
      rules: [
        !_vm.appliedRequiredField ||
          !_vm.isVaccineCategoryRequired[_vm.index] ||
          _vm.requiredField()
      ]
    },
    on: {
      change: function($event) {
        return _vm.$emit("change", $event)
      }
    },
    model: {
      value: _vm.item["vaccineDetailsCategory" + _vm.index],
      callback: function($$v) {
        _vm.$set(_vm.item, "vaccineDetailsCategory" + _vm.index, $$v);
      },
      expression: "item[`vaccineDetailsCategory${index}`]"
    }
  })
};
var __vue_staticRenderFns__$w = [];
__vue_render__$w._withStripped = true;

  /* style */
  const __vue_inject_styles__$w = undefined;
  /* scoped */
  const __vue_scope_id__$w = undefined;
  /* functional template */
  const __vue_is_functional_template__$w = false;
  /* component normalizer */
  function __vue_normalize__$w(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/VaccineDetails/VaccineCategoryPrefillInput.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var VaccineCategoryPrefillInput$2 = __vue_normalize__$w(
    { render: __vue_render__$w, staticRenderFns: __vue_staticRenderFns__$w },
    __vue_inject_styles__$w,
    __vue_script__$w,
    __vue_scope_id__$w,
    __vue_is_functional_template__$w);

const as624French = {
    as624: {
        general: {
            title: 'FORMULAIRE D\'IMMUNISATION',
            save: 'Enregistrer',
            markFormAsTransmitted: 'Marquer comme transmis',
            markFormAsNotTransmitted: 'Marquer comme non transmis',
            exportToPdf: 'Imprimer en pdf',
        },
        footer: {
            save: 'Enregistrer',
            saveAndComplete: 'Enregistrer et Terminer',
            markFormAsTransmitted: 'Marquer comme transmis',
            markFormAsNotTransmitted: 'Marquer comme non transmis',
            close: 'Fermer',
        },
        titleAndPatient: {
            pageTitle: 'FORMULAIRE D\'IMMUNISATION',
            barcodeUrl: 'https://static.trimoz.com/clicsante/images/forms/as624/barcode-AS-624-fr-1.png',
            fileNo: 'Numéro de dossier',
            name: 'Nom et prénom de l’usager',
            motherName: 'Nom et prénom de la mère',
            fatherName: 'Nom et prénom du père (optionnel)',
            birthday: 'Date de naissance',
            birthday1: 'Date de',
            birthday2: 'naissance',
            year: 'Année',
            month: 'Mois',
            day: 'Jour',
            sex: 'Sexe',
            gender: 'Sexe',
            nam: 'No d’assurance maladie',
            expiryDate: 'Expiration',
            address: 'Adresse (No, rue)',
            city: 'Ville',
            postalCode: 'Code postal',
            applicationNumber: 'ClicSanté No. application',
            appointmentNumber: 'No. de coupon',
            placeholder: {
                birthday: 'AAAA-MM-JJ',
                address: 'No, rue',
                expiryDate: 'AAAA-MM',
            },
        },
        generalInformation: {
            title: 'INFORMATIONS GÉNÉRALES',
            user14YearsOrOlder: 'Usager apte de 14 ans ou plus',
            userUnder14: 'Usager de moins de 14 ans ou usager majeur inapte',
            phone: 'Nº de téléphone résidence',
            otherPhone: 'Nº téléphone (Autre)',
            cell: 'Cellulaire',
            work: 'Travail',
            email: 'Adresse courriel',
            authorizedPerson: 'Personne autorisée selon sa déclaration',
            legalRepresentative: {
                mandatary: 'Mandataire',
                guardian: 'Tuteur',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                closeRelative: 'Proche parent du majeur inapte',
                personInterest: 'Personne qui démontre pour le majeur un intérêt particulier',
                parentalAuthority: 'Autorité parentale',
            },
            placeholder: {
                authorizedPerson: 'Inscrire le nom et le prénom',
            },
        },
        preImmunization: {
            title: 'QUESTIONNAIRE PRÉ IMMUNISATION',
            header: {
                elements: 'ÉLÉMENTS À VALIDER PAR LE PROFESSIONNEL<br/>HABILITÉ À PROCÉDER À LA VACCINATION',
                yes: 'OUI',
                no: 'NON',
                NA: 'S.O. ou NSP',
                details: 'PRÉCISION',
                export: {
                    element1: 'ÉLÉMENTS À VALIDER PAR LE PROFESSIONNEL',
                    element2: 'HABILITÉ À PROCÉDER À LA VACCINATION',
                },
            },
            title1: '* Problèmes de santé',
            question1: 'L’usager présente-t-il actuellement un changement dans son état de santé (par rapport à son état habituel)?',
            title2: '* Immunodépression',
            question2: 'L’usager présente-t-il un affaiblissement du système immunitaire en raison d’une maladie \n' +
                '(ex. : leucémie) ou d’un médicament pris actuellement (ex. : chimiothérapie)?',
            title3: '* Réactions antérieures',
            question3: 'L’usager a-t-il déjà eu une réaction allergique grave qui a nécessité des soins médicaux d’urgence \n' +
                ' <div style="margin-left: 20px;">ou</div> a-t-il déjà eu, après l’administration d’un vaccin, une réaction assez grave qui a nécessité une consultation médicale?',
            title4: '* Troubles de la coagulation',
            question4: 'L’usager présente-t-il des troubles de la coagulation nécessitant un suivi médical ou une prise d’anticoagulants?',
            title5: '* Produit immunisant ou sanguin',
            question5: 'L’usager a-t-il reçu une transfusion sanguine ou une injection d’immunoglobulines au cours des 11 derniers mois?',
            title6: '* Grossesse',
            question6: 'Si l’usager est une femme, est-elle présentement enceinte?',
            title7: '',
            question7: '* Si l’usager est un enfant âgé de moins de six mois, la mère a-t-elle pris, pendant \n' +
                'la grossesse, un agent biologique (ex. : Enbrel, Remicade, Humira) pour le traitement d’une\n' +
                'maladie auto-immune (ex. : maladie de Crohn, polyarthrite rhumatoïde, lupus ou psoriasis)?',
            precision: 'Précision',
            importantNote: 'Légende: S.O. : Sans objet, NSP: Ne s\'applique pas',
        },
        administration: {
            title: 'RAISON D’ADMINISTRATION',
            reason: {
                shingles: {
                    title: 'Zona',
                    1: 'Immunodéprimé',
                    2: 'Autres raisons',
                },
                influenza: {
                    title: 'Influenza',
                    1: 'Résident en CHSLD',
                    2: 'Résident en RPA',
                    3: 'Femme enceinte',
                    4: 'Travailleur de la santé',
                    5: 'Maladie chronique',
                    6: 'Autres raisons',
                },
                dcat: {
                    title: 'dcaT',
                    1: 'Femme enceinte(coqueluche)',
                    2: 'Autres raisons',
                },
                covid: {
                    title: 'COVID-19',
                    1: 'Résident en CHSLD',
                    2: 'Résident en RPA',
                    3: 'Femme enceinte',
                    4: 'Travailleur de la santé',
                    5: 'Maladie chronique',
                    6: 'Autres raisons',
                },
                mpox: {
                    title: 'Mpox',
                    1: 'Préexposition - immunodéprimé',
                    2: 'Préexposition - non immunodéprimé',
                    3: 'Postexposition - immunodéprimé',
                    4: 'Postexposition - non immunodéprimé',
                    5: 'Autres raisons',
                },
            },
        },
        consentAndDecision: {
            title: 'CONSENTEMENT/DÉCISION',
            decision1: 'Les renseignements sur les avantages et les risques de la vaccination, sur les réactions possibles et sur la conduite à tenir après la vaccination ont été donnés à l’usager ou à son représentant légal.',
            decision2: 'Un temps de surveillance de 15 minutes sera observé après la vaccination',
            decision3: 'Un temps de surveillance de 30 minutes sera observé après la vaccination.',
            decision: 'DÉCISION',
            patient: 'L’usager ou son représentant légal :',
            decisionConsentVaccination: 'Consent à la vaccination (ajoutez un commentaire si nécessaire) :',
            decisionConsentVaccinationNote: 'decisionConsentVaccinationNote',
            decisionRefuseVaccination: 'Refuse le ou les vaccins suivants :',
            decisionRefuseVaccinationNote: 'decisionRefuseVaccinationNote',
            decisionCounterIndication: 'A la contre-indication suivante :',
            decisionCounterIndicationNote: 'decisionCounterIndicationNote',
            decisionNotApplicable: 'Ne s’applique pas',
            recommendation: 'LA VACCINATION DÉCOULE D’UNE RECOMMANDATION DU BUREAU DE SANTÉ',
            employee: 'Dans le cas d’un employé d’un établissement de santé :',
            employeeConsent: 'Consent à ce que ces informations soient acheminées au bureau de santé',
            consentsRefuses: {
                title: 'CONSENTEMENT/REFUS OBTENU AUPRÈS DE :',
                patient: 'Usager',
                parentalAuthority: 'Autorité parentale',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                mandatary: 'Mandataire',
                specialInterest: 'Personne qui démontre pour le majeur inapte un intérêt particulier',
                closeRelative: 'Proche parent du majeur inapte',
                guardian: 'Tuteur',
            },
        },
        professionalInformation: {
            title: 'INFORMATION SUR LE PROFESSIONNEL QUI OBTIENT LE CONSENTEMENT',
            clearMyInformations: 'Retirer mes informations',
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            name: 'Nom du professionnel',
            profession: {
                title: 'PROFESSION',
                nurse: 'Infirmière',
                physician: 'Médecin',
                inhalotherapist: 'Inhalothérapeute',
                midwife: 'Sage‐femme',
                pharmacist: 'Pharmacien',
            },
            licenceNumber: 'No de permis :',
            professionalSignature: 'Signature :',
        },
        phoneConsent: {
            title: 'CONSENTEMENT TÉLÉPHONIQUE',
            subtitle: '(Compléter cette section uniquement si le consentement est obtenu par téléphone)',
            clearMyInformations: 'Retirer mes informations',
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            professionalName: 'Signature du professionnel',
            professionalDate: 'Date',
            witnessName: 'Nom du témoin :',
            witnessDate: 'Date',
            profession: {
                title: 'Profession',
                nurse: 'Infirmière',
                physician: 'Médecin',
                inhalotherapist: 'Inhalothérapeute',
                midwife: 'Sage‐femme',
                pharmacist: 'Pharmacien',
            },
        },
        vaccineDetails: {
            title: 'DÉTAIL DES PRODUITS IMMUNISANTS ADMINISTRÉS',
            getMyInformations: 'Inscrire mes informations',
            clearMyInformations: 'Retirer mes informations',
            completeMyProfile: 'Compléter mon profil',
            category: 'Catégorie de vaccin',
            date: 'Date de vaccination',
            dateSubText: '(année, mois, jour)',
            hour: 'Heure (00:00) de vaccination',
            name: 'Nom du produit immunisant (vaccin)',
            batchNumber: 'No de lot',
            dose: 'Quantité / unité',
            administration: 'Voie d’administration',
            injectionSite: 'Site d’administration',
            vaccinePlace: 'Lieu de vaccination :',
            placeholder: {
                date: 'AAAA-MM-JJ',
            },
            injectionSiteType: {
                leftArm: 'Bras gauche',
                rightArm: 'Bras droit',
                leftThigh: 'Cuisse gauche',
                rightThigh: 'Cuisse droite',
                other: 'Autre',
            },
            administrationType: {
                intramuscular: 'Intra-musculaire',
                intranasal: 'Intra-nasal',
                intradermic: 'Intra-dermique',
                subcutaneous: 'Sous-cutanée',
                oral: 'Orale',
            },
            professionInformation: {
                title: 'INFORMATION SUR L’INTERVENANT QUI A ADMINISTRÉ LE VACCIN',
                name: 'Nom :',
                profession: 'Profession :',
                signature: 'Signature :',
                licenceNumber: 'No de permis :',
            },
            zona: {
                shingrix: 'Shingrix',
            },
            flu: {
                afluria: 'Afluria',
                fluad: 'Fluad',
                fluadPediatric: 'Fluad pédiatrique',
                flucelvax: 'Flucelvax quad',
                flulaval: 'Flulaval tetra',
                fluMist: 'FluMist quadrivalent',
                fluzoneQuadrivalent: 'Fluzone quadrivalent',
                fluzoneDose: 'Fluzone HD quadrivalent',
                influvacTetra: 'Influvac tetra',
                supemtek: 'Supemtek',
                other: 'Autre',
            },
            covid: {
                'Moderna': 'Spikevax (Moderna)',
                'Pfizer-BioNTech': 'Comirnaty (Pfizer-BioNTech)',
            },
            dcat: {
                actHib: 'Act-Hib',
                adacel: 'Adacel',
                adacelPolio: 'Adacel-Polio',
                boostrix: 'Boostrix',
                boostrixPolio: 'Boostrix-Polio',
                imovaxPolio: 'Imovax Polio',
                infanrixHexa: 'Infanrix hexa',
                infanrixIPVHib: 'Infanrix-IPV/Hib',
                pediacel: 'Pediacel',
                td: 'Td Adsorbées',
                pentacel: 'Pentacel',
            },
            mpox: {
                imvamune: 'Imvamune',
            },
            hepatitis: {
                avaxim: 'Avaxim',
                engerixB: 'Engerix-B',
                havrix: 'Havrix',
                recombivaxHb: 'Recombivax HB',
                twinrix: 'Twinrix',
                vaqta: 'Vaqta',
                prehevbrio: 'PREHEVBRIO',
            },
            meningitis: {
                bexsero: 'Bexsero',
                menactra: 'Menactra',
                menjugate: 'Menjugate',
                menveo: 'Menveo',
                neisvacC: 'Neisvac-C',
                nimenrix: 'Nimenrix',
                trumenba: 'Trumenba',
            },
            pneumococcal: {
                pneumovax23: 'Pneumovax 23',
                prevnar13: 'Prevnar 13',
                prevnar20: 'Prevnar 20',
                synflorix: 'Synflorix',
                vaxneuvance: 'Vaxneuvance',
            },
            rabies: {
                imovaxRage: 'Imovax Rage',
                rabAvert: 'RabAvert',
            },
            rotavirus: {
                rotarix: 'Rotarix',
                rotateq: 'RotaTeq',
            },
            rro: {
                mmrII: 'M-M-R II',
                priorix: 'Priorix',
                priorixTetra: 'Priorix Tetra',
                proQuad: 'ProQuad',
                varilrix: 'Varilrix',
                varivaxIII: 'Varivax III',
            },
            tuberculosis: {
                bcg: 'BCG lyophilisé avec glutamate',
                tubersol: 'Tubersol',
            },
            vph: {
                cervarix: 'Cervarix',
                gardasil9: 'Gardasil 9',
            },
            travel: {
                dukoral: 'Dukoral',
                ixiaro: 'Ixiaro',
                yfVax: 'Yf Vax',
                vaxchora: 'Vaxchora',
            },
            vrs: {
                arexvy: 'Arexvy',
                abrysvo: 'Abrysvo',
            },
            vrs_baby: {
                nirsevimab: 'Nirsevimab',
            },
            typhoid: {
                typhimVi: 'Typhim Vi',
                vivotif: 'Vivotif',
            },
            avian_influenza: {
                arepanrix: 'Arepanrix H5N1',
            },
            vaccineCategories: {
                zona: 'Zona',
                flu: 'Influenza',
                covid: 'COVID-19',
                mpox: 'MPOX',
                dcat: 'Diphtérie, coqueluche, tétanos, hépatite B, poliomyélite et H. influenzæ de type b',
                hepatitis: 'Hépatites',
                meningitis: 'Méningocoque',
                pneumococcal: 'Pneumocoque',
                rabies: 'Rage',
                rotavirus: 'Rotavirus',
                rro: 'Rougeole, rubéole, oreillons et varicelle',
                tuberculosis: 'Tuberculose',
                vph: 'Virus du papillome humain',
                travel: 'Voyageurs',
                vrs: 'Virus Respiratoire Syncytial (VRS)',
                vrs_baby: 'Virus Respiratoire Syncytial (VRS) Bébé',
                typhoid: 'Vaccin contre la typhoïde',
                avian_influenza: 'Grippe aviaire A (H5N1)',
            },
            vaccineCategoriesPdf: {
                zona: 'Zona',
                flu: 'Influenza',
                covid: 'COVID-19',
                mpox: 'MPOX',
                dcat: 'Dcat',
                hepatitis: 'Hépatites',
                meningitis: 'Méningocoque',
                pneumococcal: 'Pneumocoque',
                rabies: 'Rage',
                rotavirus: 'Rotavirus',
                rro: 'Rougeole, rubéole, oreillons et varicelle',
                tuberculosis: 'Tuberculose',
                vph: 'Virus du papillome humain',
                travel: 'Voyageurs',
                vrs: 'Virus Respiratoire Syncytial (VRS)',
                vrs_baby: 'Virus Respiratoire Syncytial (VRS) Bébé',
                typhoid: 'Vaccin contre la typhoïde',
                avian_influenza: 'Grippe aviaire A (H5N1)',
            },
            professionList: {
                nurse: 'Infirmière',
                physician: 'Médecin',
                inhalotherapist: 'Inhalothérapeute',
                midwife: 'Sage‐femme',
                pharmacist: 'Pharmacien',
                other: 'Autre',
            },
        },
        oldVaccineDetails: {
            flu: {
                agriflu: 'Agriflu',
                fluviral: 'Fluviral',
                influvac: 'Influvac',
            },
            covid: {
                'Spikevax-XBB-1-5': 'Spikevax XBB.1.5',
                'Comirnaty-XBB15-6m-4y': 'Comirnaty XBB.1.5 - 6 mois-4 ans',
                'Comirnaty-XBB15-5-11': 'Comirnaty XBB.1.5 - 5-11 ans',
                'Comirnaty-XBB15-12': 'Comirnaty XBB.1.5 - 12 ans +',
                'Spikevax-Bivalent-BA4-BA5': 'Spikevax Bivalent BA4-BA5 (Moderna)',
                'Moderna-Bivalent': 'Spikevax Bivalent BA1 (Moderna)',
                'Moderna-Kids': 'Spikevax (Moderna) 6 mois - 5 ans',
                'Pfizer-BioNTech-Kids': 'Comirnaty (Pfizer-BioNTech) 5-11 ans',
                'Covishield': 'Covishield',
                'Nuvaxovid': 'Nuvaxovid (Novavax)',
                'Novavax': 'Nuvaxovid (Novavax)',
                'AstraZeneca': 'Vaxzevria (AstraZeneca)',
                'Comirnaty-Bivalent-BA4-BA5': 'Comirnaty Bivalent BA4-BA5',
                'Pfizer-BioNTech-Kids-6m-4y': 'Comirnaty (Pfizer-BioNTech) 6 mois - 4 ans',
                'Comirnaty-Bivalent-BA4-BA5-Kids': 'Comirnaty Bivalent BA4-BA5 5-11 ans',
                'Jcovden': 'Jcovden',
                'Covifenz': 'Covifenz',
            },
            dcat: {
                hiberix: 'Hiberix',
            },
        },
        notes: {
            notes: 'Notes',
            requiredField: '* Indique un champ requis',
        },
        headerPage: {
            patientName: 'Nom et prénom de l\'usager',
            recordNumber: 'NO de dossier',
        },
        footerPage: {
            title: 'FORMULAIRE D\'IMMUNISATION',
            formRevision: 'AS-624 DT9225 (rév. 2023-03)',
            patientFile: 'DOSSIER DE L\'USAGER',
        },
        error: {
            title: 'Le formulaire contient des erreurs',
            message: 'Des champs requis sont manquants ou contiennent des erreurs...',
        },
        warning: {
            title: 'Êtes-vous certain de vouloir modifier le contenu?',
            text: 'Le formulaire est terminé et signé par un Vaccinateur. Une fois le changement appliqué, vous devrez signer et assumer la responsabilité de l\'entièreté du document.',
            question: 'Désirez-vous poursuivre les modifications?',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Annuler',
            no: 'Non',
            yes: 'Oui',
        },
        base: {
            errors: {
                requireField: 'Le champ est requis',
                invalidDate: 'Cette date est invalide',
                invalidTime: 'Cette heure est invalide',
                invalidNam: 'Le numéro d\'assurance maladie est invalide',
                invalidMonth: 'Ce mois est invalide',
                invalidPostalCode: 'Le code postal est invalide',
                phone2short: 'Le téléphone est incorrect',
                invalidEmail: 'Le format du courriel est invalide',
                iclscNumber: 'Le format du numéro I-CLSC est invalide',
            },
        },
    },
};

var ServiceTemplateEnum;
(function (ServiceTemplateEnum) {
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_COVID_19"] = 204] = "VACCINATION_COVID_19";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_4_17_ANS"] = 290] = "VACCINATION_4_17_ANS";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_0_5_ANS"] = 2095] = "VACCINATION_0_5_ANS";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_DIPHTERIE_COQUELUCHE_TETANOS_POLIO"] = 342] = "VACCINATION_DIPHTERIE_COQUELUCHE_TETANOS_POLIO";
    ServiceTemplateEnum[ServiceTemplateEnum["FLU_VACCIN"] = 195] = "FLU_VACCIN";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_HEPATITE_B"] = 621] = "VACCINATION_HEPATITE_B";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_HEPATITE_A"] = 618] = "VACCINATION_HEPATITE_A";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_HEPATITE_A_ET_B"] = 619] = "VACCINATION_HEPATITE_A_ET_B";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_SANTE_SEXUELLE"] = 354] = "VACCINATION_SANTE_SEXUELLE";
    ServiceTemplateEnum[ServiceTemplateEnum["MEN_VACCINATION_AGAINST_MENINGITIS"] = 283] = "MEN_VACCINATION_AGAINST_MENINGITIS";
    ServiceTemplateEnum[ServiceTemplateEnum["PNEU_VACCINATION_PNEUMOCOCCUS"] = 199] = "PNEU_VACCINATION_PNEUMOCOCCUS";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_RAGE"] = 663] = "VACCINATION_RAGE";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_ROTAVIRUS"] = 2176] = "VACCINATION_ROTAVIRUS";
    ServiceTemplateEnum[ServiceTemplateEnum["RRO"] = 1000] = "RRO";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_TUBECULOSE"] = 2030] = "VACCINATION_TUBECULOSE";
    ServiceTemplateEnum[ServiceTemplateEnum["MPOX_VACCINE"] = 698] = "MPOX_VACCINE";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_VPH"] = 200] = "VACCINATION_VPH";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_FIEVRE_JAUNE"] = 520] = "VACCINATION_FIEVRE_JAUNE";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_CHOLERA_DIARRHEE_ECOLI"] = 1256] = "VACCINATION_CHOLERA_DIARRHEE_ECOLI";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_ENCEPHALITE_JAPONAIS"] = 661] = "VACCINATION_ENCEPHALITE_JAPONAIS";
    ServiceTemplateEnum[ServiceTemplateEnum["CONSULTATION_SANTE_VOYAGE"] = 310] = "CONSULTATION_SANTE_VOYAGE";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_ZONA"] = 275] = "VACCINATION_ZONA";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_GENERA"] = 2965] = "VACCINATION_GENERA";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINE_VRS"] = 3079] = "VACCINE_VRS";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINE_VRS_BEBE"] = 3157] = "VACCINE_VRS_BEBE";
    ServiceTemplateEnum[ServiceTemplateEnum["TYPHOID_VACCIN"] = 521] = "TYPHOID_VACCIN";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_FEMME_ENCEINTE"] = 2046] = "VACCINATION_FEMME_ENCEINTE";
    ServiceTemplateEnum[ServiceTemplateEnum["COADMIN_2024"] = 3161] = "COADMIN_2024";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINATION_CHIKUNGUNYA"] = 3165] = "VACCINATION_CHIKUNGUNYA";
    ServiceTemplateEnum[ServiceTemplateEnum["VACCINE_AVIAN_INFLUENZA"] = 3180] = "VACCINE_AVIAN_INFLUENZA";
})(ServiceTemplateEnum || (ServiceTemplateEnum = {}));
const COVID_SERVICES = [
    ServiceTemplateEnum.VACCINATION_COVID_19,
];
const DCAT_SERVICES = [
    ServiceTemplateEnum.VACCINATION_4_17_ANS,
    ServiceTemplateEnum.VACCINATION_0_5_ANS,
    ServiceTemplateEnum.VACCINATION_DIPHTERIE_COQUELUCHE_TETANOS_POLIO,
    ServiceTemplateEnum.VACCINATION_FEMME_ENCEINTE,
];
const FLU_SERVICES = [
    ServiceTemplateEnum.FLU_VACCIN,
];
const HEPATITIS_SERVICES = [
    ServiceTemplateEnum.VACCINATION_HEPATITE_B,
    ServiceTemplateEnum.VACCINATION_HEPATITE_A,
    ServiceTemplateEnum.VACCINATION_HEPATITE_A_ET_B,
    ServiceTemplateEnum.VACCINATION_SANTE_SEXUELLE,
];
const MENINGITIS_SERVICES = [
    ServiceTemplateEnum.MEN_VACCINATION_AGAINST_MENINGITIS,
];
const PNEUMOCOCCAL_SERVICES = [
    ServiceTemplateEnum.PNEU_VACCINATION_PNEUMOCOCCUS,
];
const RABIES_SERVICES = [
    ServiceTemplateEnum.VACCINATION_RAGE,
];
const ROTAVIRUS_SERVICES = [
    ServiceTemplateEnum.VACCINATION_ROTAVIRUS,
];
const RRO_SERVICES = [
    ServiceTemplateEnum.RRO,
];
const TUBERCULOSIS_SERVICES = [
    ServiceTemplateEnum.VACCINATION_TUBECULOSE,
];
const MPOX_SERVICES = [
    ServiceTemplateEnum.MPOX_VACCINE,
];
const VPH_SERVICES = [
    ServiceTemplateEnum.VACCINATION_VPH,
];
const TRAVEL_SERVICES = [
    ServiceTemplateEnum.VACCINATION_FIEVRE_JAUNE,
    ServiceTemplateEnum.VACCINATION_CHOLERA_DIARRHEE_ECOLI,
    ServiceTemplateEnum.VACCINATION_ENCEPHALITE_JAPONAIS,
    ServiceTemplateEnum.CONSULTATION_SANTE_VOYAGE,
    ServiceTemplateEnum.VACCINATION_CHIKUNGUNYA,
];
const SHINGLES_SERVICES = [
    ServiceTemplateEnum.VACCINATION_ZONA,
];
const VRS_SERVICES = [
    ServiceTemplateEnum.VACCINE_VRS,
];
const VRS_BABY_SERVICES = [
    ServiceTemplateEnum.VACCINE_VRS_BEBE,
];
[
    ServiceTemplateEnum.VACCINATION_0_5_ANS,
];
[
    ServiceTemplateEnum.VACCINATION_0_5_ANS,
];
[
    ServiceTemplateEnum.VACCINATION_0_5_ANS,
];
// DEPRECATED : old co-admin flu & covid
const COADMIN_FLU_COVID_SERVICES = [
    ServiceTemplateEnum.VACCINATION_0_5_ANS,
];
const TYPHOID_SERVICES = [
    ServiceTemplateEnum.TYPHOID_VACCIN,
];
const COADMIN_FLU_COVID_SERVICES_2024 = [
    ServiceTemplateEnum.COADMIN_2024,
];
const AVIAN_INFLUENZA_SERVICES = [
    ServiceTemplateEnum.VACCINE_AVIAN_INFLUENZA,
];

// eslint-disable-next-line @typescript-eslint/no-var-requires
let toLower = require('lodash/toLower');
// eslint-disable-next-line @typescript-eslint/no-var-requires
let isEmpty = require('lodash/isEmpty');
const fields$1 = [
    'vaccineDetailsCategory',
    'vaccineDetailsDate',
    'vaccineDetailsTime',
    'vaccineDetailsName',
    'vaccineDetailsBatchNumber',
    'vaccineDetailsDose',
    'vaccineDetailsAdministration',
    'vaccineDetailsInjectionSite',
    'vaccineDetailsOther',
];
let VaccineDetailsInfluenza = class VaccineDetailsInfluenza extends Vue {
    constructor() {
        super(...arguments);
        this.AdministrationEnum = AdministrationEnum$1;
        this.InjectionSiteEnum = InjectionSiteEnum$1;
        this.maximumVaccineCategoryList = 10;
        this.vaccineCategoryList = [];
        this.immunizationProviderProfessionList = [];
    }
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('as624.vaccineDetails');
    }
    get prefillFields() {
        return this.globalData.getFormAppData.prefillFields;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasDetailSectionAccess() {
        return this.globalData.getFormAppData.hasDetailSectionAccess;
    }
    get hasDetailSectionVaccineTypeAccess() {
        return this.globalData.getFormAppData.hasDetailSectionVaccineTypeAccess;
    }
    get showInjectorBtn() {
        return this.globalData.getFormAppData.showInjectorBtn ||
            this.globalData.getFormAppData.showVaccinatorBtn;
    }
    get isProfileComplete() {
        return this.globalData.getFormAppData.isProfileComplete;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get isVaccineCategoryRequired() {
        let result = [];
        for (let i = 1; i <= this.numberOfLineToDisplay; i++) {
            result[i] = (this.hasDataInRow(i));
        }
        return result;
    }
    get hasVaccinatorSectionAccess() {
        return globalDataVuex().getFormAppData.hasVaccinatorSectionAccess;
    }
    created() {
        if (this.status === 'new') {
            let vaccineCategory1 = '';
            if (SHINGLES_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.ZONA;
            }
            else if (FLU_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.FLU;
            }
            else if (COVID_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.COVID;
            }
            else if (DCAT_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.DCAT;
            }
            else if (MPOX_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.MPOX;
            }
            else if (HEPATITIS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.HEPATITIS;
            }
            else if (MENINGITIS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.MENINGITIS;
            }
            else if (PNEUMOCOCCAL_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.PNEUMOCOCCAL;
            }
            else if (RABIES_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.RABIES;
            }
            else if (ROTAVIRUS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.ROTAVIRUS;
            }
            else if (RRO_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.RRO;
            }
            else if (TUBERCULOSIS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.TUBERCULOSIS;
            }
            else if (VPH_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.VPH;
            }
            else if (VRS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.VRS;
            }
            else if (VRS_BABY_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.VRS_BABY;
            }
            else if (TYPHOID_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.TYPHOID;
            }
            else if (TRAVEL_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.TRAVEL;
            }
            else if (AVIAN_INFLUENZA_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.AVIAN_INFLUENZA;
            }
            else if (COADMIN_FLU_COVID_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum$1.COVID;
                this.item.vaccineDetailsCategory2 = VaccineCategoryIdsEnum$1.FLU;
                this.populateWithPrefillFields(VaccineCategoryIdsEnum$1.FLU, 2);
                if (!this.item.vaccineDetailsTime2) {
                    this.item.vaccineDetailsTime2 = this.item.vaccineDetailsTime1;
                }
                if (!this.item.vaccineDetailsDate2) {
                    this.item.vaccineDetailsDate2 = this.item.vaccineDetailsDate1;
                }
            }
            // prefill the first row with the prefill fields data
            this.item.vaccineDetailsCategory1 = vaccineCategory1;
            this.populateWithPrefillFields(vaccineCategory1, 1);
        }
        this.vaccineCategoryList = [{ id: null, name: '' }];
        for (const value in VaccineCategoryIdsEnum$1) {
            this.vaccineCategoryList.push({
                id: toLower(value), name: this.i18n.vaccineCategories[toLower(value)],
            });
        }
        this.immunizationProviderProfessionList = [];
        for (const value in ProfessionEnum$1) {
            this.immunizationProviderProfessionList.push({
                id: toLower(value), name: this.i18n.professionList[toLower(value)],
            });
        }
    }
    insertMyVaccinatorInfo() {
        this.globalData.emitEvent({ event: 'insert-my-vaccinator-info', data: true });
    }
    openResourceForm() {
        this.globalData.emitEvent({ event: 'open-resource-form', data: true });
    }
    hasDataInRow(index) {
        let result = false;
        fields$1.forEach(field => {
            if (this.item[`${field}${index}`]) {
                result = true;
            }
        });
        return result;
    }
    clearMyVaccinatorInfo() {
        this.item.immunizationProviderSignature = null;
        this.item.immunizationProviderName = null;
        this.item.immunizationProviderProfession = null;
        this.item.immunizationProviderLicenceNumber = null;
    }
    populateWithPrefillFields(value, index) {
        for (let i = 1; i <= this.maximumVaccineCategoryList; i++) {
            if (this.prefillFields[`vaccineDetailsCategory${i}`] === value) {
                fields$1.forEach(field => {
                    if (this.prefillFields[`${field}${i}`] && !this.item[`${field}${index}`]) {
                        this.item[`${field}${index}`] = this.prefillFields[`${field}${i}`];
                    }
                });
            }
        }
        if (isEmpty(value)) {
            fields$1.forEach(field => {
                this.item[`${field}${index}`] = null;
            });
        }
    }
    setVaccineTypeList(index) {
        const result = [];
        const vaccineCategory = this.item[`vaccineDetailsCategory${index}`];
        if (vaccineCategory) {
            // @ts-ignore
            Object.entries(as624French.as624.vaccineDetails[vaccineCategory]).forEach(([key, value]) => result.push({ id: key, name: `as624.vaccineDetails.${vaccineCategory}.${key}` }));
            // Add old vaccine if the form is using it (meaning it's an old form)
            const selectedVaccine = this.item[`vaccineDetailsName${index}`];
            if (selectedVaccine) {
                // @ts-ignore
                const oldVaccineTypes = Object.entries(as624French.as624.oldVaccineDetails[vaccineCategory] || []).map(([key]) => key);
                const selectedOldVaccine = oldVaccineTypes.find((vaccine) => vaccine === selectedVaccine);
                if (selectedOldVaccine) {
                    result.unshift({
                        id: selectedOldVaccine,
                        name: `as624.oldVaccineDetails.${vaccineCategory}.${selectedOldVaccine}`,
                    });
                }
            }
        }
        return result;
    }
};
__decorate([
    Prop()
], VaccineDetailsInfluenza.prototype, "data", void 0);
__decorate([
    Prop()
], VaccineDetailsInfluenza.prototype, "serviceTemplate", void 0);
__decorate([
    Prop()
], VaccineDetailsInfluenza.prototype, "status", void 0);
__decorate([
    Prop()
], VaccineDetailsInfluenza.prototype, "numberOfLineToDisplay", void 0);
__decorate([
    Prop()
], VaccineDetailsInfluenza.prototype, "shouldCheckValidation", void 0);
__decorate([
    PropSync('data', { type: Object })
], VaccineDetailsInfluenza.prototype, "item", void 0);
VaccineDetailsInfluenza = __decorate([
    Component({
        mixins: [FormRules],
        components: {
            VaccineCategoryPrefillInput: VaccineCategoryPrefillInput$2,
        },
    })
], VaccineDetailsInfluenza);
var script$v = VaccineDetailsInfluenza;

/* script */
            const __vue_script__$v = script$v;
            
/* template */
var __vue_render__$v = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c("v-flex", { staticClass: "black px-1", attrs: { xs12: "" } }, [
                _c(
                  "span",
                  {
                    staticClass: "vaccine-details-title white--text",
                    attrs: { "data-testid": "vaccine-details-influenza" }
                  },
                  [
                    _vm._v(
                      "\n          " + _vm._s(_vm.i18n.title) + "\n        "
                    )
                  ]
                )
              ]),
              _vm._v(" "),
              _c("v-flex", { attrs: { xs12: "" } }, [
                _c(
                  "div",
                  { attrs: { id: "vaccine-details-as624" } },
                  [
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-category-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.category) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-date-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.date) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-time-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.hour) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-name-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.name) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-batch-number-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.batchNumber) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-dose-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.dose) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-administration-header subheading justify-center border-right border-bottom font-weight-bold"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administration) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-injection-header px-1 subheading justify-center border-bottom font-weight-bold"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSite) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-intramuscular-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.intramuscular) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-subcutaneous-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.subcutaneous) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-oral-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.oral) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-intranasal-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.intranasal) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-intradermic-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.intradermic) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-leftArm-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.leftArm) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-rightArm-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.rightArm) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-leftThigh-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.leftThigh) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-rightThigh-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.rightThigh) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-other-header px-1 caption justify-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.other) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _vm._l(_vm.numberOfLineToDisplay, function(index) {
                      return [
                        _c(
                          "div",
                          {
                            key: "vaccine-category-value" + index,
                            staticClass:
                              "vaccine-category-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("vaccine-category-prefill-input", {
                              attrs: {
                                index: index,
                                item: _vm.item,
                                value:
                                  _vm.item["vaccineDetailsCategory" + index],
                                vaccineCategoryList: _vm.vaccineCategoryList,
                                appliedRequiredField: _vm.appliedRequiredField,
                                isVaccineCategoryRequired:
                                  _vm.isVaccineCategoryRequired,
                                disabled: !_vm.hasDetailSectionVaccineTypeAccess
                              },
                              on: {
                                change: function($event) {
                                  return _vm.populateWithPrefillFields(
                                    $event,
                                    index
                                  )
                                }
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-date-value" + index,
                            staticClass:
                              "vaccine-date-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": "vaccine-date",
                                placeholder: _vm.i18n.placeholder.date,
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "10",
                                mask: "####-##-##",
                                "return-masked-value": "",
                                rules: [
                                  function(v) {
                                    return !v || _vm.validDate(v)
                                  },
                                  !_vm.appliedRequiredField ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value: _vm.item["vaccineDetailsDate" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsDate" + index,
                                    $$v
                                  );
                                },
                                expression: "item[`vaccineDetailsDate${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-time-value" + index,
                            staticClass:
                              "vaccine-time-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": "vaccine-time",
                                disabled: !_vm.hasDetailSectionAccess,
                                mask: "##:##",
                                "return-masked-value": "",
                                box: "",
                                maxlength: "10"
                              },
                              model: {
                                value: _vm.item["vaccineDetailsTime" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsTime" + index,
                                    $$v
                                  );
                                },
                                expression: "item[`vaccineDetailsTime${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-name-value" + index,
                            staticClass:
                              "vaccine-name-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-autocomplete", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": "vaccine-name",
                                dense: "",
                                items: _vm.setVaccineTypeList(index),
                                "item-text": function(item) {
                                  return _vm.$i18n.t(item.name)
                                },
                                "item-value": "id",
                                disabled: !_vm.hasDetailSectionVaccineTypeAccess,
                                box: "",
                                required:
                                  _vm.hasDetailSectionAccess &&
                                  _vm.shouldCheckValidation,
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value: _vm.item["vaccineDetailsName" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsName" + index,
                                    $$v
                                  );
                                },
                                expression: "item[`vaccineDetailsName${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-batch-number" + index,
                            staticClass:
                              "vaccine-batch-number-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": "vaccine-batch-number",
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "50",
                                required:
                                  _vm.hasDetailSectionAccess &&
                                  _vm.shouldCheckValidation,
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item["vaccineDetailsBatchNumber" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsBatchNumber" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsBatchNumber${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-dose-value" + index,
                            staticClass:
                              "vaccine-dose-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": "vaccine-dose",
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "50",
                                required:
                                  _vm.hasDetailSectionAccess &&
                                  _vm.shouldCheckValidation,
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value: _vm.item["vaccineDetailsDose" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsDose" + index,
                                    $$v
                                  );
                                },
                                expression: "item[`vaccineDetailsDose${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-intramuscular-value" + index,
                            staticClass:
                              "vaccine-intramuscular-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.AdministrationEnum.INTRAMUSCULAR,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-subcutaneous-value" + index,
                            staticClass:
                              "vaccine-subcutaneous-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.AdministrationEnum.SUBCUTANEOUS,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-oral-value" + index,
                            staticClass:
                              "vaccine-oral-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.AdministrationEnum.ORAL,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-intranasal-value" + index,
                            staticClass:
                              "vaccine-intranasal-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.AdministrationEnum.INTRANASAL,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-intradermic-value" + index,
                            staticClass:
                              "vaccine-intradermic-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.AdministrationEnum.INTRADERMIC,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-leftArm-value" + index,
                            staticClass:
                              "vaccine-leftArm-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.InjectionSiteEnum.LEFT_ARM,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    !!_vm.item["vaccineDetailsOther" + index] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsInjectionSite" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInjectionSite" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInjectionSite${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-rightArm-value" + index,
                            staticClass:
                              "vaccine-rightArm-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.InjectionSiteEnum.RIGHT_ARM,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    !!_vm.item["vaccineDetailsOther" + index] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsInjectionSite" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInjectionSite" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInjectionSite${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-leftThigh-value" + index,
                            staticClass:
                              "vaccine-leftThigh-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.InjectionSiteEnum.LEFT_THIGH,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    !!_vm.item["vaccineDetailsOther" + index] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsInjectionSite" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInjectionSite" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInjectionSite${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-rightThigh-value" + index,
                            staticClass:
                              "vaccine-rightThigh-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.InjectionSiteEnum.RIGHT_THIGH,
                                "hide-details": "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasDetailSectionAccess ||
                                    !_vm.shouldCheckValidation ||
                                    !_vm.item[
                                      "vaccineDetailsCategory" + index
                                    ] ||
                                    !!_vm.item["vaccineDetailsOther" + index] ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsInjectionSite" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInjectionSite" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInjectionSite${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-other-value" + index,
                            staticClass:
                              "vaccine-other-value px-2 py-1 border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": "vaccine-dose",
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "50"
                              },
                              model: {
                                value: _vm.item["vaccineDetailsOther" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsOther" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsOther${index}`]"
                              }
                            })
                          ],
                          1
                        )
                      ]
                    }),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "vaccine-place px-2 py-3 subheading border-bottom"
                      },
                      [
                        _c(
                          "v-layout",
                          { attrs: { row: "" } },
                          [
                            _c(
                              "v-flex",
                              {
                                staticClass:
                                  "shrink mr-3 align-self-center font-weight-bold"
                              },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(_vm.i18n.vaccinePlace) +
                                    "\n              "
                                )
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "v-flex",
                              [
                                _c("v-text-field", {
                                  attrs: {
                                    "hide-details": "",
                                    "aria-label": "place-name",
                                    disabled: !_vm.hasDetailSectionAccess,
                                    box: "",
                                    maxlength: "50"
                                  },
                                  model: {
                                    value: _vm.item.immunizationProviderSite,
                                    callback: function($$v) {
                                      _vm.$set(
                                        _vm.item,
                                        "immunizationProviderSite",
                                        $$v
                                      );
                                    },
                                    expression: "item.immunizationProviderSite"
                                  }
                                })
                              ],
                              1
                            )
                          ],
                          1
                        )
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "injector-section px-2 border-bottom font-weight-bold grey lighten-2 "
                      },
                      [
                        _c(
                          "v-layout",
                          { attrs: { row: "" } },
                          [
                            _c("v-flex", { staticClass: "py-2" }, [
                              _vm._v(
                                _vm._s(_vm.i18n.professionInformation.title)
                              )
                            ]),
                            _vm._v(" "),
                            _vm.showInjectorBtn
                              ? _c(
                                  "v-flex",
                                  { staticClass: "md4 xs12 text-xs-right" },
                                  [
                                    !_vm.item.immunizationProviderSignature
                                      ? [
                                          _vm.isProfileComplete
                                            ? _c(
                                                "v-btn",
                                                {
                                                  staticClass: "primary",
                                                  attrs: {
                                                    "aria-label":
                                                      "insert-evaluator-info",
                                                    dark: "",
                                                    flat: "",
                                                    small: "",
                                                    disabled: _vm.lockForm
                                                  },
                                                  on: {
                                                    click:
                                                      _vm.insertMyVaccinatorInfo
                                                  }
                                                },
                                                [
                                                  _vm._v(
                                                    "\n                  " +
                                                      _vm._s(
                                                        _vm.i18n
                                                          .getMyInformations
                                                      ) +
                                                      "\n                "
                                                  )
                                                ]
                                              )
                                            : _c(
                                                "v-btn",
                                                {
                                                  staticClass: "primary",
                                                  attrs: {
                                                    "aria-label":
                                                      "complete-profile",
                                                    disabled: _vm.lockForm,
                                                    dark: "",
                                                    flat: "",
                                                    small: ""
                                                  },
                                                  on: {
                                                    click: _vm.openResourceForm
                                                  }
                                                },
                                                [
                                                  _vm._v(
                                                    "\n                  " +
                                                      _vm._s(
                                                        _vm.i18n
                                                          .completeMyProfile
                                                      ) +
                                                      "\n                "
                                                  )
                                                ]
                                              )
                                        ]
                                      : [
                                          _c(
                                            "v-btn",
                                            {
                                              staticClass: "primary",
                                              attrs: {
                                                disabled: _vm.lockForm,
                                                "aria-label":
                                                  "insert-evaluator-info",
                                                dark: "",
                                                flat: "",
                                                small: ""
                                              },
                                              on: {
                                                click: _vm.clearMyVaccinatorInfo
                                              }
                                            },
                                            [
                                              _vm._v(
                                                "\n                    " +
                                                  _vm._s(
                                                    _vm.i18n.clearMyInformations
                                                  ) +
                                                  "\n                  "
                                              )
                                            ]
                                          )
                                        ]
                                  ],
                                  2
                                )
                              : _vm._e()
                          ],
                          1
                        )
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "injector-name px-2 py-3 border-right" },
                      [
                        _c(
                          "v-flex",
                          { staticClass: "shrink mr-3 align-self-center" },
                          [
                            _vm._v(
                              "\n              " +
                                _vm._s(_vm.i18n.professionInformation.name) +
                                "\n            "
                            )
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "v-flex",
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": "immunization-provider-name",
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "50",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasVaccinatorSectionAccess ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value: _vm.item.immunizationProviderName,
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "immunizationProviderName",
                                    $$v
                                  );
                                },
                                expression: "item.immunizationProviderName"
                              }
                            })
                          ],
                          1
                        )
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "injector-signature px-2 py-3 border-right"
                      },
                      [
                        !_vm.item.immunizationProviderSignature
                          ? _c(
                              "v-flex",
                              [
                                _c("v-text-field", {
                                  staticClass: "imm-provider-signature",
                                  attrs: {
                                    "hide-details": "",
                                    "aria-label":
                                      "immunization-provider-signature",
                                    label:
                                      _vm.i18n.professionInformation.signature,
                                    disabled: "",
                                    box: "",
                                    rules: [
                                      !_vm.appliedRequiredField ||
                                        !_vm.hasVaccinatorSectionAccess ||
                                        _vm.requiredField()
                                    ]
                                  }
                                })
                              ],
                              1
                            )
                          : [
                              _c(
                                "v-flex",
                                {
                                  staticClass: "shrink mr-3 align-self-center"
                                },
                                [
                                  _vm._v(
                                    "\n                " +
                                      _vm._s(
                                        _vm.i18n.professionInformation.signature
                                      ) +
                                      "\n              "
                                  )
                                ]
                              ),
                              _vm._v(" "),
                              _c(
                                "v-flex",
                                {
                                  staticClass:
                                    "align-self-center imm-provider-img"
                                },
                                [
                                  _c("img", {
                                    attrs: {
                                      src:
                                        _vm.item.immunizationProviderSignature,
                                      alt: "Signature"
                                    }
                                  })
                                ]
                              )
                            ]
                      ],
                      2
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "injector-profession px-2 py-3 border-right"
                      },
                      [
                        _c(
                          "v-flex",
                          { staticClass: "shrink mr-3 align-self-center" },
                          [
                            _vm._v(
                              "\n              " +
                                _vm._s(
                                  _vm.i18n.professionInformation.profession
                                ) +
                                "\n            "
                            )
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "v-flex",
                          [
                            _c("v-autocomplete", {
                              attrs: {
                                "hide-details": "",
                                "aria-label":
                                  "immunization-provider-profession",
                                disabled: !_vm.hasDetailSectionAccess,
                                dense: "",
                                items: _vm.immunizationProviderProfessionList,
                                "item-text": "name",
                                "item-value": "id",
                                box: "",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasVaccinatorSectionAccess ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value: _vm.item.immunizationProviderProfession,
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "immunizationProviderProfession",
                                    $$v
                                  );
                                },
                                expression:
                                  "item.immunizationProviderProfession"
                              }
                            })
                          ],
                          1
                        )
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "injector-licence px-2 py-3" },
                      [
                        _c(
                          "v-flex",
                          { staticClass: "shrink mr-3 align-self-center" },
                          [
                            _vm._v(
                              "\n              " +
                                _vm._s(
                                  _vm.i18n.professionInformation.licenceNumber
                                ) +
                                "\n            "
                            )
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "v-flex",
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label":
                                  "immunization-provider-licence-number",
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "50",
                                rules: [
                                  !_vm.appliedRequiredField ||
                                    !_vm.hasVaccinatorSectionAccess ||
                                    _vm.requiredField()
                                ]
                              },
                              model: {
                                value:
                                  _vm.item.immunizationProviderLicenceNumber,
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "immunizationProviderLicenceNumber",
                                    $$v
                                  );
                                },
                                expression:
                                  "item.immunizationProviderLicenceNumber"
                              }
                            })
                          ],
                          1
                        )
                      ],
                      1
                    )
                  ],
                  2
                )
              ])
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$v = [];
__vue_render__$v._withStripped = true;

  /* style */
  const __vue_inject_styles__$v = function (inject) {
    if (!inject) return
    inject("data-v-3afabf2a_0", { source: "#vaccine-details-as624[data-v-3afabf2a] {\n  display: grid;\n  grid-template-columns: 12% 8% 7% 14% 7% 7% repeat(9, 4%) 9%;\n  grid-template-rows: 50px auto;\n}\n#vaccine-details-as624 .header[data-v-3afabf2a] {\n    display: flex;\n    align-items: center;\n}\n#vaccine-details-as624 .vaccine-category-header[data-v-3afabf2a] {\n    grid-area: 1 / 1 / 3 / 2;\n}\n#vaccine-details-as624 .vaccine-date-header[data-v-3afabf2a] {\n    grid-area: 1 / 2 / 3 / 3;\n}\n#vaccine-details-as624 .vaccine-time-header[data-v-3afabf2a] {\n    grid-area: 1 / 3 / 3 / 4;\n}\n#vaccine-details-as624 .vaccine-name-header[data-v-3afabf2a] {\n    grid-area: 1 / 4 / 3 / 5;\n}\n#vaccine-details-as624 .vaccine-batch-number-header[data-v-3afabf2a] {\n    grid-area: 1 / 5 / 3 / 6;\n}\n#vaccine-details-as624 .vaccine-dose-header[data-v-3afabf2a] {\n    grid-area: 1 / 6 / 3 / 7;\n}\n#vaccine-details-as624 .vaccine-administration-header[data-v-3afabf2a] {\n    grid-area: 1 / 7 / 2 / 12;\n    border-right-width: 3px !important;\n}\n#vaccine-details-as624 .vaccine-injection-header[data-v-3afabf2a] {\n    grid-area: 1 / 12 / 2 / 17;\n}\n#vaccine-details-as624 .vaccine-intramuscular-header[data-v-3afabf2a] {\n    grid-area: 2 / 7 / 3 / 8;\n}\n#vaccine-details-as624 .vaccine-subcutaneous-header[data-v-3afabf2a] {\n    grid-area: 2 / 8 / 3 / 9;\n}\n#vaccine-details-as624 .vaccine-oral-header[data-v-3afabf2a] {\n    grid-area: 2 / 9 / 3 / 10;\n}\n#vaccine-details-as624 .vaccine-intranasal-header[data-v-3afabf2a] {\n    grid-area: 2 / 10 / 3 / 11;\n}\n#vaccine-details-as624 .vaccine-intradermic-header[data-v-3afabf2a] {\n    grid-area: 2 / 11 / 3 / 12;\n    border-right-width: 3px !important;\n}\n#vaccine-details-as624 .vaccine-leftArm-header[data-v-3afabf2a] {\n    grid-area: 2 / 12 / 3 / 13;\n}\n#vaccine-details-as624 .vaccine-rightArm-header[data-v-3afabf2a] {\n    grid-area: 2 / 13 / 3 / 14;\n}\n#vaccine-details-as624 .vaccine-leftThigh-header[data-v-3afabf2a] {\n    grid-area: 2 / 14 / 3 / 15;\n}\n#vaccine-details-as624 .vaccine-rightThigh-header[data-v-3afabf2a] {\n    grid-area: 2 / 15 / 3 / 16;\n}\n#vaccine-details-as624 .vaccine-other-header[data-v-3afabf2a] {\n    grid-area: 2 / 16 / 3 / 17;\n}\n#vaccine-details-as624 .vaccine-intramuscular-value[data-v-3afabf2a] {\n    grid-area: auto / 7 / auto / 8;\n}\n#vaccine-details-as624 .vaccine-subcutaneous-value[data-v-3afabf2a] {\n    grid-area: auto / 8 / auto / 9;\n}\n#vaccine-details-as624 .vaccine-oral-value[data-v-3afabf2a] {\n    grid-area: auto / 9 / auto / 10;\n}\n#vaccine-details-as624 .vaccine-intranasal-value[data-v-3afabf2a] {\n    grid-area: auto / 10 / auto / 11;\n}\n#vaccine-details-as624 .vaccine-intradermic-value[data-v-3afabf2a] {\n    grid-area: auto / 11 / auto / 12;\n    border-right-width: 3px !important;\n}\n#vaccine-details-as624 .vaccine-leftArm-value[data-v-3afabf2a] {\n    grid-area: auto / 12 / auto / 13;\n}\n#vaccine-details-as624 .vaccine-rightArm-value[data-v-3afabf2a] {\n    grid-area: auto / 13 / auto / 14;\n}\n#vaccine-details-as624 .vaccine-leftThigh-value[data-v-3afabf2a] {\n    grid-area: auto / 14 / auto / 15;\n}\n#vaccine-details-as624 .vaccine-rightThigh-value[data-v-3afabf2a] {\n    grid-area: auto / 15 / auto / 16;\n}\n#vaccine-details-as624 .vaccine-other-value[data-v-3afabf2a] {\n    grid-area: auto / 16 / auto / 17;\n}\n#vaccine-details-as624 .vaccine-place[data-v-3afabf2a] {\n    grid-area: auto / 1 / auto / 17;\n}\n#vaccine-details-as624 .injector-section[data-v-3afabf2a] {\n    grid-area: auto / 1 / auto / 17;\n}\n#vaccine-details-as624 .injector-name[data-v-3afabf2a] {\n    grid-area: auto / 1 / auto / 4;\n}\n#vaccine-details-as624 .injector-signature[data-v-3afabf2a] {\n    grid-area: auto / 4 / auto / 9;\n}\n#vaccine-details-as624 .injector-profession[data-v-3afabf2a] {\n    grid-area: auto / 9 / auto / 14;\n}\n#vaccine-details-as624 .injector-licence[data-v-3afabf2a] {\n    grid-area: auto / 14 / auto / 17;\n}\n.imm-provider-signature[data-v-3afabf2a] {\n  position: relative;\n  top: 24px;\n}\n.imm-provider-signature[data-v-3afabf2a] .v-label {\n    top: -22px;\n}\n.imm-provider-img[data-v-3afabf2a] {\n  text-align: center;\n  top: 15px;\n  position: relative;\n}\n\n/*# sourceMappingURL=VaccineDetails.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/VaccineDetails/VaccineDetails.vue","VaccineDetails.vue"],"names":[],"mappings":"AAowBA;EACA,aAAA;EACA,2DAAA;EACA,6BAAA;AAAA;AAHA;IAMA,aAAA;IACA,mBAAA;AAAA;AAPA;IAUA,wBAAA;AAAA;AAVA;IAWA,wBAAA;AAAA;AAXA;IAYA,wBAAA;AAAA;AAZA;IAaA,wBAAA;AAAA;AAbA;IAcA,wBAAA;AAAA;AAdA;IAeA,wBAAA;AAAA;AAfA;IAiBA,yBAAA;IACA,kCAAA;AAAA;AAlBA;IAoBA,0BAAA;AAAA;AApBA;IAqBA,wBAAA;AAAA;AArBA;IAsBA,wBAAA;AAAA;AAtBA;IAuBA,yBAAA;AAAA;AAvBA;IAwBA,0BAAA;AAAA;AAxBA;IA0BA,0BAAA;IACA,kCAAA;AAAA;AA3BA;IA6BA,0BAAA;AAAA;AA7BA;IA8BA,0BAAA;AAAA;AA9BA;IA+BA,0BAAA;AAAA;AA/BA;IAgCA,0BAAA;AAAA;AAhCA;IAiCA,0BAAA;AAAA;AAjCA;IAmCA,8BAAA;AAAA;AAnCA;IAoCA,8BAAA;AAAA;AApCA;IAqCA,+BAAA;AAAA;AArCA;IAsCA,gCAAA;AAAA;AAtCA;IAwCA,gCAAA;IACA,kCAAA;AAAA;AAzCA;IA2CA,gCAAA;AAAA;AA3CA;IA4CA,gCAAA;AAAA;AA5CA;IA6CA,gCAAA;AAAA;AA7CA;IA8CA,gCAAA;AAAA;AA9CA;IA+CA,gCAAA;AAAA;AA/CA;IAgDA,+BAAA;AAAA;AAhDA;IAiDA,+BAAA;AAAA;AAjDA;IAkDA,8BAAA;AAAA;AAlDA;IAmDA,8BAAA;AAAA;AAnDA;IAoDA,+BAAA;AAAA;AApDA;IAqDA,gCAAA;AAAA;AAEA;EACA,kBAAA;EACA,SAAA;AAAA;AAFA;IAIA,UAAA;AAAA;AAIA;EACA,kBAAA;EACA,SAAA;EACA,kBACA;AAAA;;AC7uBA,6CAA6C","file":"VaccineDetails.vue","sourcesContent":[null,"#vaccine-details-as624 {\n  display: grid;\n  grid-template-columns: 12% 8% 7% 14% 7% 7% repeat(9, 4%) 9%;\n  grid-template-rows: 50px auto; }\n  #vaccine-details-as624 .header {\n    display: flex;\n    align-items: center; }\n  #vaccine-details-as624 .vaccine-category-header {\n    grid-area: 1 / 1 / 3 / 2; }\n  #vaccine-details-as624 .vaccine-date-header {\n    grid-area: 1 / 2 / 3 / 3; }\n  #vaccine-details-as624 .vaccine-time-header {\n    grid-area: 1 / 3 / 3 / 4; }\n  #vaccine-details-as624 .vaccine-name-header {\n    grid-area: 1 / 4 / 3 / 5; }\n  #vaccine-details-as624 .vaccine-batch-number-header {\n    grid-area: 1 / 5 / 3 / 6; }\n  #vaccine-details-as624 .vaccine-dose-header {\n    grid-area: 1 / 6 / 3 / 7; }\n  #vaccine-details-as624 .vaccine-administration-header {\n    grid-area: 1 / 7 / 2 / 12;\n    border-right-width: 3px !important; }\n  #vaccine-details-as624 .vaccine-injection-header {\n    grid-area: 1 / 12 / 2 / 17; }\n  #vaccine-details-as624 .vaccine-intramuscular-header {\n    grid-area: 2 / 7 / 3 / 8; }\n  #vaccine-details-as624 .vaccine-subcutaneous-header {\n    grid-area: 2 / 8 / 3 / 9; }\n  #vaccine-details-as624 .vaccine-oral-header {\n    grid-area: 2 / 9 / 3 / 10; }\n  #vaccine-details-as624 .vaccine-intranasal-header {\n    grid-area: 2 / 10 / 3 / 11; }\n  #vaccine-details-as624 .vaccine-intradermic-header {\n    grid-area: 2 / 11 / 3 / 12;\n    border-right-width: 3px !important; }\n  #vaccine-details-as624 .vaccine-leftArm-header {\n    grid-area: 2 / 12 / 3 / 13; }\n  #vaccine-details-as624 .vaccine-rightArm-header {\n    grid-area: 2 / 13 / 3 / 14; }\n  #vaccine-details-as624 .vaccine-leftThigh-header {\n    grid-area: 2 / 14 / 3 / 15; }\n  #vaccine-details-as624 .vaccine-rightThigh-header {\n    grid-area: 2 / 15 / 3 / 16; }\n  #vaccine-details-as624 .vaccine-other-header {\n    grid-area: 2 / 16 / 3 / 17; }\n  #vaccine-details-as624 .vaccine-intramuscular-value {\n    grid-area: auto / 7 / auto / 8; }\n  #vaccine-details-as624 .vaccine-subcutaneous-value {\n    grid-area: auto / 8 / auto / 9; }\n  #vaccine-details-as624 .vaccine-oral-value {\n    grid-area: auto / 9 / auto / 10; }\n  #vaccine-details-as624 .vaccine-intranasal-value {\n    grid-area: auto / 10 / auto / 11; }\n  #vaccine-details-as624 .vaccine-intradermic-value {\n    grid-area: auto / 11 / auto / 12;\n    border-right-width: 3px !important; }\n  #vaccine-details-as624 .vaccine-leftArm-value {\n    grid-area: auto / 12 / auto / 13; }\n  #vaccine-details-as624 .vaccine-rightArm-value {\n    grid-area: auto / 13 / auto / 14; }\n  #vaccine-details-as624 .vaccine-leftThigh-value {\n    grid-area: auto / 14 / auto / 15; }\n  #vaccine-details-as624 .vaccine-rightThigh-value {\n    grid-area: auto / 15 / auto / 16; }\n  #vaccine-details-as624 .vaccine-other-value {\n    grid-area: auto / 16 / auto / 17; }\n  #vaccine-details-as624 .vaccine-place {\n    grid-area: auto / 1 / auto / 17; }\n  #vaccine-details-as624 .injector-section {\n    grid-area: auto / 1 / auto / 17; }\n  #vaccine-details-as624 .injector-name {\n    grid-area: auto / 1 / auto / 4; }\n  #vaccine-details-as624 .injector-signature {\n    grid-area: auto / 4 / auto / 9; }\n  #vaccine-details-as624 .injector-profession {\n    grid-area: auto / 9 / auto / 14; }\n  #vaccine-details-as624 .injector-licence {\n    grid-area: auto / 14 / auto / 17; }\n\n.imm-provider-signature::v-deep {\n  position: relative;\n  top: 24px; }\n  .imm-provider-signature::v-deep .v-label {\n    top: -22px; }\n\n.imm-provider-img::v-deep {\n  text-align: center;\n  top: 15px;\n  position: relative; }\n\n/*# sourceMappingURL=VaccineDetails.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$v = "data-v-3afabf2a";
  /* module identifier */
  const __vue_module_identifier__$a = undefined;
  /* functional template */
  const __vue_is_functional_template__$v = false;
  /* component normalizer */
  function __vue_normalize__$v(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/VaccineDetails/VaccineDetails.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$a() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$a.styles || (__vue_create_injector__$a.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var VaccineDetails$2 = __vue_normalize__$v(
    { render: __vue_render__$v, staticRenderFns: __vue_staticRenderFns__$v },
    __vue_inject_styles__$v,
    __vue_script__$v,
    __vue_scope_id__$v,
    __vue_is_functional_template__$v,
    __vue_module_identifier__$a,
    __vue_create_injector__$a);

let Notes$3 = class Notes extends Vue {
    get i18n() {
        return this.$i18n.t('as624.notes');
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    get hasNotesSectionAccess() {
        return globalDataVuex().getFormAppData.hasNotesSectionAccess;
    }
};
__decorate([
    Prop()
], Notes$3.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], Notes$3.prototype, "item", void 0);
Notes$3 = __decorate([
    Component({
        mixins: [FormRules],
    })
], Notes$3);
var script$u = Notes$3;

/* script */
            const __vue_script__$u = script$u;
            
/* template */
var __vue_render__$u = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-textarea", {
                    attrs: {
                      disabled: !_vm.hasNotesSectionAccess,
                      "hide-details": "",
                      box: "",
                      label: _vm.i18n.notes,
                      maxlength: 5000
                    },
                    model: {
                      value: _vm.item.notes,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "notes", $$v);
                      },
                      expression: "item.notes"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "font-weight-medium", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.requiredField) + "\n        "
                  ),
                  _c("br")
                ]
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$u = [];
__vue_render__$u._withStripped = true;

  /* style */
  const __vue_inject_styles__$u = undefined;
  /* scoped */
  const __vue_scope_id__$u = undefined;
  /* functional template */
  const __vue_is_functional_template__$u = false;
  /* component normalizer */
  function __vue_normalize__$u(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/Notes/Notes.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var Notes$2 = __vue_normalize__$u(
    { render: __vue_render__$u, staticRenderFns: __vue_staticRenderFns__$u },
    __vue_inject_styles__$u,
    __vue_script__$u,
    __vue_scope_id__$u,
    __vue_is_functional_template__$u);

let ConsentAndDecision$3 = class ConsentAndDecision extends Vue {
    get i18n() {
        return this.$i18n.t('as624.consentAndDecision');
    }
    get globalData() {
        return globalDataVuex();
    }
    get hasConsentSectionAccess() {
        return this.globalData.getFormAppData.hasConsentSectionAccess;
    }
    get hasAtLeastOneDecision() {
        return this.item.decisionConsentVaccination ||
            this.item.decisionRefuseVaccination ||
            this.item.decisionCounterIndication ||
            this.item.decisionNotApplicable;
    }
    get disableNotApplicableOption() {
        return this.item.decisionConsentVaccination ||
            this.item.decisionRefuseVaccination ||
            this.item.decisionCounterIndication;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
};
__decorate([
    Prop()
], ConsentAndDecision$3.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], ConsentAndDecision$3.prototype, "item", void 0);
ConsentAndDecision$3 = __decorate([
    Component({
        computed: {
            PersonLegalRepresentativeEnum() {
                return PersonLegalRepresentativeEnum$1;
            },
        },
        mixins: [FormRules],
    })
], ConsentAndDecision$3);
var script$t = ConsentAndDecision$3;

/* script */
            const __vue_script__$t = script$t;
            
/* template */
var __vue_render__$t = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    { staticClass: "px-2 py-0 mt-2 border-all", attrs: { fluid: "" } },
    [
      _c(
        "v-layout",
        { attrs: { row: "", wrap: "" } },
        [
          _c(
            "v-flex",
            { staticClass: "black px-1 py-0", attrs: { xs12: "" } },
            [
              _c("span", { staticClass: "section-title white--text" }, [
                _vm._v(_vm._s(_vm.i18n.title))
              ])
            ]
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { attrs: { xs12: "", "pb-2": "" } },
            [
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.decision1,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": "",
                  rules: [!_vm.appliedRequiredField || _vm.requiredField()]
                },
                model: {
                  value: _vm.item.consentDecision1,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentDecision1", $$v);
                  },
                  expression: "item.consentDecision1"
                }
              }),
              _vm._v(" "),
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.decision2,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField ||
                      !!_vm.item.consentDecision3 ||
                      _vm.requiredField()
                  ]
                },
                model: {
                  value: _vm.item.consentDecision2,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentDecision2", $$v);
                  },
                  expression: "item.consentDecision2"
                }
              }),
              _vm._v(" "),
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.decision3,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField ||
                      !!_vm.item.consentDecision2 ||
                      _vm.requiredField()
                  ]
                },
                model: {
                  value: _vm.item.consentDecision3,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentDecision3", $$v);
                  },
                  expression: "item.consentDecision3"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass:
                "px-1 font-weight-bold border-bottom border-top grey lighten-2 decision-subtitle",
              attrs: { xs12: "" }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.decision) + "\n    ")]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass: "checkbox-group-title-1",
              attrs: { xs12: "", sm6: "", "pl-1": "", "pb-2": "", "pt-1": "" }
            },
            [_vm._v("\n      " + _vm._s("* " + _vm.i18n.patient) + "\n    ")]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { attrs: { grow: "", "pa-1": "", "pl-4": "" } },
            [
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.decisionConsentVaccination,
                  disabled:
                    !_vm.hasConsentSectionAccess ||
                    _vm.item.decisionNotApplicable,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField ||
                      _vm.hasAtLeastOneDecision ||
                      _vm.requiredField()
                  ]
                },
                model: {
                  value: _vm.item.decisionConsentVaccination,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "decisionConsentVaccination", $$v);
                  },
                  expression: "item.decisionConsentVaccination"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { attrs: { shrink: "", "pa-1": "", "pr-4": "", xs8: "" } },
            [
              _c("v-text-field", {
                staticClass: "pa-0 ma-0 consent-textfield",
                attrs: {
                  "aria-label": _vm.i18n.decisionConsentVaccinationNote,
                  disabled:
                    !_vm.hasConsentSectionAccess ||
                    _vm.item.decisionNotApplicable ||
                    !_vm.item.decisionConsentVaccination,
                  placeholder: "",
                  "hide-details": "",
                  box: "",
                  maxlength: "255"
                },
                model: {
                  value: _vm.item.decisionConsentVaccinationNote,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "decisionConsentVaccinationNote", $$v);
                  },
                  expression: "item.decisionConsentVaccinationNote"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { attrs: { grow: "", "pa-1": "", "pl-4": "" } },
            [
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.decisionRefuseVaccination,
                  disabled:
                    !_vm.hasConsentSectionAccess ||
                    _vm.item.decisionNotApplicable,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField ||
                      _vm.hasAtLeastOneDecision ||
                      _vm.requiredField()
                  ]
                },
                model: {
                  value: _vm.item.decisionRefuseVaccination,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "decisionRefuseVaccination", $$v);
                  },
                  expression: "item.decisionRefuseVaccination"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { attrs: { shrink: "", "pa-1": "", "pr-4": "", xs10: "" } },
            [
              _c("v-text-field", {
                staticClass: "pa-0 ma-0 consent-textfield",
                attrs: {
                  "aria-label": _vm.i18n.decisionRefuseVaccinationNote,
                  disabled:
                    !_vm.hasConsentSectionAccess ||
                    _vm.item.decisionNotApplicable ||
                    !_vm.item.decisionRefuseVaccination,
                  placeholder: "",
                  "hide-details": "",
                  box: "",
                  maxlength: "255",
                  rules: [
                    !_vm.appliedRequiredField ||
                      !_vm.item.decisionRefuseVaccination ||
                      _vm.requiredField()
                  ]
                },
                model: {
                  value: _vm.item.decisionRefuseVaccinationNote,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "decisionRefuseVaccinationNote", $$v);
                  },
                  expression: "item.decisionRefuseVaccinationNote"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { attrs: { grow: "", "pa-1": "", "pl-4": "" } },
            [
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.decisionCounterIndication,
                  disabled:
                    !_vm.hasConsentSectionAccess ||
                    _vm.item.decisionNotApplicable,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField ||
                      _vm.hasAtLeastOneDecision ||
                      _vm.requiredField()
                  ]
                },
                model: {
                  value: _vm.item.decisionCounterIndication,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "decisionCounterIndication", $$v);
                  },
                  expression: "item.decisionCounterIndication"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { attrs: { shrink: "", "pa-1": "", "pr-4": "", xs10: "" } },
            [
              _c("v-text-field", {
                staticClass: "pa-0 ma-0 consent-textfield",
                attrs: {
                  "aria-label": _vm.i18n.decisionCounterIndicationNote,
                  disabled:
                    !_vm.hasConsentSectionAccess ||
                    _vm.item.decisionNotApplicable ||
                    !_vm.item.decisionCounterIndication,
                  placeholder: "",
                  "hide-details": "",
                  box: "",
                  maxlength: "255",
                  rules: [
                    !_vm.appliedRequiredField ||
                      !_vm.item.decisionCounterIndication ||
                      _vm.requiredField()
                  ]
                },
                model: {
                  value: _vm.item.decisionCounterIndicationNote,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "decisionCounterIndicationNote", $$v);
                  },
                  expression: "item.decisionCounterIndicationNote"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { attrs: { grow: "", "pa-1": "", "pb-2": "", "pl-4": "" } },
            [
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.decisionNotApplicable,
                  disabled:
                    !_vm.hasConsentSectionAccess ||
                    _vm.disableNotApplicableOption,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField ||
                      _vm.hasAtLeastOneDecision ||
                      _vm.requiredField()
                  ]
                },
                model: {
                  value: _vm.item.decisionNotApplicable,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "decisionNotApplicable", $$v);
                  },
                  expression: "item.decisionNotApplicable"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass:
                "px-1 font-weight-bold border-bottom border-top grey lighten-2 decision-subtitle",
              attrs: { xs12: "" }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.recommendation) + "\n    ")]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass: "checkbox-group-title-1",
              attrs: { xs12: "", sm6: "", "pl-1": "", "pb-2": "", "pt-1": "" }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.employee) + "\n    ")]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { attrs: { grow: "", "pa-1": "", "pl-4": "" } },
            [
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.employeeConsent,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": ""
                },
                model: {
                  value: _vm.item.employeeConsent,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "employeeConsent", $$v);
                  },
                  expression: "item.employeeConsent"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass:
                "px-1 font-weight-bold border-bottom border-top grey lighten-2 decision-subtitle",
              attrs: { xs12: "" }
            },
            [
              _vm._v(
                "\n      " +
                  _vm._s("* " + _vm.i18n.consentsRefuses.title) +
                  "\n    "
              )
            ]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { attrs: { grow: "", "pa-1": "", "pl-2": "" } },
            [
              _c(
                "v-radio-group",
                {
                  attrs: {
                    row: !_vm.vuetifyBreakpoint.xsOnly,
                    disabled: !_vm.hasConsentSectionAccess,
                    "hide-details": "",
                    "aria-label": "consents-refuses-obtained-by",
                    rules: [
                      !_vm.appliedRequiredField ||
                        _vm.requiredField(
                          _vm.$i18n.t("as624.base.errors.requireField")
                        )
                    ]
                  },
                  model: {
                    value: _vm.item.consentRefuseObtainedFrom,
                    callback: function($$v) {
                      _vm.$set(_vm.item, "consentRefuseObtainedFrom", $$v);
                    },
                    expression: "item.consentRefuseObtainedFrom"
                  }
                },
                [
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.patient,
                      value: _vm.PersonLegalRepresentativeEnum.PATIENT
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.parentalAuthority,
                      value:
                        _vm.PersonLegalRepresentativeEnum.PARENTAL_AUTHORITY
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.spouse,
                      value: _vm.PersonLegalRepresentativeEnum.SPOUSE
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.mandatary,
                      value: _vm.PersonLegalRepresentativeEnum.MANDATARY
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.specialInterest,
                      value: _vm.PersonLegalRepresentativeEnum.SPECIAL_INTEREST
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.closeRelative,
                      value: _vm.PersonLegalRepresentativeEnum.CLOSE_RELATIVE
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.guardian,
                      value: _vm.PersonLegalRepresentativeEnum.GUARDIAN
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$t = [];
__vue_render__$t._withStripped = true;

  /* style */
  const __vue_inject_styles__$t = undefined;
  /* scoped */
  const __vue_scope_id__$t = undefined;
  /* functional template */
  const __vue_is_functional_template__$t = false;
  /* component normalizer */
  function __vue_normalize__$t(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/ConsentAndDecision/ConsentAndDecision.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var ConsentAndDecision$2 = __vue_normalize__$t(
    { render: __vue_render__$t, staticRenderFns: __vue_staticRenderFns__$t },
    __vue_inject_styles__$t,
    __vue_script__$t,
    __vue_scope_id__$t,
    __vue_is_functional_template__$t);

let ProfessionalInformation$3 = class ProfessionalInformation extends Vue {
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('as624.professionalInformation');
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get showEvaluatorBtn() {
        return this.globalData.getFormAppData.showEvaluatorBtn;
    }
    get isProfileComplete() {
        return this.globalData.getFormAppData.isProfileComplete;
    }
    get hasProfessionalSectionAccess() {
        return this.globalData.getFormAppData.hasProfessionalSectionAccess;
    }
    get hasPhoneConsentProfessionalProfession() {
        return !!this.item.phoneConsentProfessionalProfession;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    openResourceForm() {
        this.globalData.emitEvent({ event: 'open-resource-form', data: true });
    }
    insertMyEvaluatorInfo() {
        this.globalData.emitEvent({ event: 'insert-my-evaluator-info', data: true });
    }
    clearMyEvaluatorInfo() {
        this.item.professionalSignature = null;
        this.item.professionalProfession = null;
        this.item.professionalName = null;
        this.item.professionalLicenceNumber = null;
    }
};
__decorate([
    Prop()
], ProfessionalInformation$3.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], ProfessionalInformation$3.prototype, "item", void 0);
ProfessionalInformation$3 = __decorate([
    Component({
        mixins: [FormRules],
    })
], ProfessionalInformation$3);
var script$s = ProfessionalInformation$3;

/* script */
            const __vue_script__$s = script$s;
            
/* template */
var __vue_render__$s = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    {
      staticClass: "px-2 py-0 border-right border-left",
      attrs: { id: "professionalInformation", fluid: "" }
    },
    [
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass:
                "font-weight-bold border-bottom grey lighten-2 decision-subtitle",
              attrs: { xs8: "" }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.title) + "\n    ")]
          ),
          _vm._v(" "),
          _vm.showEvaluatorBtn
            ? _c(
                "v-flex",
                {
                  staticClass: "grey lighten-2 border-bottom py-0",
                  class: _vm.vuetifyBreakpoint.smAndDown
                    ? "text-xs-left"
                    : "text-xs-right",
                  attrs: { md4: "", xs12: "" }
                },
                [
                  !_vm.item.professionalSignature
                    ? _c(
                        "v-template",
                        [
                          _vm.isProfileComplete
                            ? _c(
                                "v-btn",
                                {
                                  staticClass: "primary text-center",
                                  attrs: {
                                    "aria-label": "insert-evaluator-info",
                                    dark: "",
                                    flat: "",
                                    small: "",
                                    disabled: _vm.lockForm
                                  },
                                  on: { click: _vm.insertMyEvaluatorInfo }
                                },
                                [
                                  _vm._v(
                                    _vm._s(_vm.i18n.getMyInformations) +
                                      "\n        "
                                  )
                                ]
                              )
                            : _c(
                                "v-btn",
                                {
                                  staticClass: "primary text-center",
                                  attrs: {
                                    "aria-label": "complete-profile",
                                    disabled: _vm.lockForm,
                                    dark: "",
                                    flat: "",
                                    small: ""
                                  },
                                  on: { click: _vm.openResourceForm }
                                },
                                [
                                  _vm._v(
                                    _vm._s(_vm.i18n.completeMyProfile) +
                                      "\n        "
                                  )
                                ]
                              )
                        ],
                        1
                      )
                    : _c(
                        "v-template",
                        [
                          _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: {
                                "aria-label": "insert-evaluator-info",
                                disabled: _vm.lockForm,
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.clearMyEvaluatorInfo }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.clearMyInformations) +
                                  "\n        "
                              )
                            ]
                          )
                        ],
                        1
                      )
                ],
                1
              )
            : _c("v-flex", {
                staticClass: "grey lighten-2 border-bottom py-0",
                class: _vm.vuetifyBreakpoint.smAndDown
                  ? "text-xs-left"
                  : "text-xs-right",
                attrs: { md4: "", xs12: "" }
              })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass: "border-bottom grey lighten-2 decision-subtitle",
              attrs: { xs12: "" }
            },
            [
              _c(
                "v-radio-group",
                {
                  attrs: {
                    "aria-label": "professional-profession",
                    row: !_vm.vuetifyBreakpoint.xsOnly,
                    disabled: !_vm.hasProfessionalSectionAccess,
                    "hide-details": "",
                    rules: [
                      _vm.hasPhoneConsentProfessionalProfession ||
                        !_vm.appliedRequiredField ||
                        _vm.requiredField(
                          _vm.$i18n.t("as624.base.errors.requireField")
                        )
                    ]
                  },
                  model: {
                    value: _vm.item.professionalProfession,
                    callback: function($$v) {
                      _vm.$set(_vm.item, "professionalProfession", $$v);
                    },
                    expression: "item.professionalProfession"
                  }
                },
                [
                  _c("v-radio", {
                    attrs: { label: _vm.i18n.profession.nurse, value: "nurse" }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.physician,
                      value: "physician"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.inhalotherapist,
                      value: "inhalotherapist"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.midwife,
                      value: "midwife"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.pharmacist,
                      value: "pharmacist"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { staticClass: "pb-0", attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass: "border-bottom decision-subtitle",
              attrs: { xs5: "" }
            },
            [
              _c("v-text-field", {
                attrs: {
                  "aria-label": "professional-name",
                  disabled: !_vm.hasProfessionalSectionAccess,
                  label: _vm.i18n.name,
                  placeholder: "",
                  "hide-details": "",
                  box: "",
                  maxlength: "100",
                  rules: [
                    !_vm.item.professionalProfession ||
                      !_vm.appliedRequiredField ||
                      _vm.requiredField(
                        _vm.$i18n.t("as624.base.errors.requireField")
                      )
                  ]
                },
                model: {
                  value: _vm.item.professionalName,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "professionalName", $$v);
                  },
                  expression: "item.professionalName"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            {
              staticClass: "border-bottom decision-subtitle",
              attrs: { xs5: "" }
            },
            [
              !_vm.item.professionalSignature
                ? _c("v-text-field", {
                    attrs: {
                      "aria-label": "professional-signature",
                      label: _vm.i18n.professionalSignature,
                      disabled: "",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "100",
                      rules: [
                        !_vm.item.professionalProfession ||
                          !_vm.appliedRequiredField ||
                          _vm.requiredField(
                            _vm.$i18n.t("as624.base.errors.requireField")
                          )
                      ]
                    }
                  })
                : [
                    _c(
                      "v-layout",
                      { attrs: { row: "", wrap: "" } },
                      [
                        _c("v-flex", { attrs: { xs12: "", "pl-2": "" } }, [
                          _c("span", { staticClass: "caption" }, [
                            _vm._v(_vm._s(_vm.i18n.professionalSignature))
                          ])
                        ]),
                        _vm._v(" "),
                        _c(
                          "v-flex",
                          {
                            staticStyle: {
                              "margin-top": "-8px",
                              "margin-bottom": "-8px"
                            }
                          },
                          [
                            _c("img", {
                              attrs: {
                                src: _vm.item.professionalSignature,
                                alt: "Signature"
                              }
                            })
                          ]
                        )
                      ],
                      1
                    )
                  ]
            ],
            2
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            {
              staticClass: "border-bottom decision-subtitle",
              attrs: { xs2: "" }
            },
            [
              _c("v-text-field", {
                attrs: {
                  "aria-label": "professional-licence-number",
                  label: _vm.i18n.licenceNumber,
                  disabled: !_vm.hasProfessionalSectionAccess,
                  placeholder: "",
                  "hide-details": "",
                  box: "",
                  maxlength: "25",
                  rules: [
                    !_vm.item.professionalProfession ||
                      !_vm.appliedRequiredField ||
                      _vm.requiredField(
                        _vm.$i18n.t("influenza.base.errors.requireField")
                      )
                  ]
                },
                model: {
                  value: _vm.item.professionalLicenceNumber,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "professionalLicenceNumber", $$v);
                  },
                  expression: "item.professionalLicenceNumber"
                }
              })
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$s = [];
__vue_render__$s._withStripped = true;

  /* style */
  const __vue_inject_styles__$s = undefined;
  /* scoped */
  const __vue_scope_id__$s = undefined;
  /* functional template */
  const __vue_is_functional_template__$s = false;
  /* component normalizer */
  function __vue_normalize__$s(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/ProfessionalInformation/ProfessionalInformation.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var ProfessionalInformation$2 = __vue_normalize__$s(
    { render: __vue_render__$s, staticRenderFns: __vue_staticRenderFns__$s },
    __vue_inject_styles__$s,
    __vue_script__$s,
    __vue_scope_id__$s,
    __vue_is_functional_template__$s);

let PhoneConsent$3 = class PhoneConsent extends Vue {
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('as624.phoneConsent');
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasProfessionalSectionAccess() {
        return this.globalData.getFormAppData.hasProfessionalSectionAccess;
    }
    get hasProfessionalProfession() {
        return !!this.item.professionalProfession;
    }
    get showEvaluatorBtn() {
        return this.globalData.getFormAppData.showEvaluatorBtn;
    }
    get isProfileComplete() {
        return this.globalData.getFormAppData.isProfileComplete;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    openResourceForm() {
        this.globalData.emitEvent({ event: 'open-resource-form', data: true });
    }
    insertMyPhoneConsentInfo() {
        this.globalData.emitEvent({ event: 'insert-my-phone-consent-info', data: true });
    }
    clearMyPhoneConsentInfo() {
        this.item.phoneConsentProfessional = null;
        this.item.phoneConsentProfessionalProfession = null;
        this.item.phoneConsentProfessionalDate = null;
        this.item.phoneConsentWitness = null;
        this.item.phoneConsentWitnessDate = null;
    }
};
__decorate([
    Prop()
], PhoneConsent$3.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], PhoneConsent$3.prototype, "item", void 0);
PhoneConsent$3 = __decorate([
    Component({
        mixins: [FormRules],
    })
], PhoneConsent$3);
var script$r = PhoneConsent$3;

/* script */
            const __vue_script__$r = script$r;
            
/* template */
var __vue_render__$r = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    { staticClass: "px-2 py-0 border-right border-left", attrs: { fluid: "" } },
    [
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass: "font-weight-bold border-bottom grey lighten-2",
              attrs: { xs8: "" }
            },
            [
              _vm._v("\n      " + _vm._s(_vm.i18n.title)),
              _c("br"),
              _vm._v("\n      " + _vm._s(_vm.i18n.subtitle) + "\n    ")
            ]
          ),
          _vm._v(" "),
          _vm.showEvaluatorBtn
            ? _c(
                "v-flex",
                {
                  staticClass: "grey lighten-2 border-bottom py-0",
                  class: _vm.vuetifyBreakpoint.smAndDown
                    ? "text-xs-left"
                    : "text-xs-right",
                  attrs: { md4: "", xs12: "" }
                },
                [
                  !_vm.item.phoneConsentProfessional
                    ? _c(
                        "v-template",
                        [
                          _vm.isProfileComplete
                            ? _c(
                                "v-btn",
                                {
                                  staticClass: "primary text-center",
                                  attrs: {
                                    "aria-label": "insert-evaluator-info",
                                    dark: "",
                                    flat: "",
                                    small: "",
                                    disabled: _vm.lockForm
                                  },
                                  on: { click: _vm.insertMyPhoneConsentInfo }
                                },
                                [
                                  _vm._v(
                                    _vm._s(_vm.i18n.getMyInformations) +
                                      "\n        "
                                  )
                                ]
                              )
                            : _c(
                                "v-btn",
                                {
                                  staticClass: "primary text-center",
                                  attrs: {
                                    "aria-label": "complete-profile",
                                    disabled: _vm.lockForm,
                                    dark: "",
                                    flat: "",
                                    small: ""
                                  },
                                  on: { click: _vm.openResourceForm }
                                },
                                [
                                  _vm._v(
                                    _vm._s(_vm.i18n.completeMyProfile) +
                                      "\n        "
                                  )
                                ]
                              )
                        ],
                        1
                      )
                    : _c(
                        "v-template",
                        [
                          _c(
                            "v-btn",
                            {
                              staticClass: "primary text-center",
                              attrs: {
                                "aria-label": "insert-evaluator-info",
                                disabled: _vm.lockForm,
                                dark: "",
                                flat: "",
                                small: ""
                              },
                              on: { click: _vm.clearMyPhoneConsentInfo }
                            },
                            [
                              _vm._v(
                                _vm._s(_vm.i18n.clearMyInformations) +
                                  "\n        "
                              )
                            ]
                          )
                        ],
                        1
                      )
                ],
                1
              )
            : _vm._e()
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass: "border-bottom grey lighten-2 decision-subtitle",
              attrs: { xs6: "" }
            },
            [
              _c(
                "v-radio-group",
                {
                  attrs: {
                    "aria-label": "professional-profession",
                    row: !_vm.vuetifyBreakpoint.xsOnly,
                    disabled: !_vm.hasProfessionalSectionAccess,
                    "hide-details": "",
                    rules: [
                      _vm.hasProfessionalProfession ||
                        !_vm.appliedRequiredField ||
                        _vm.requiredField(
                          _vm.$i18n.t("as624.base.errors.requireField")
                        )
                    ]
                  },
                  model: {
                    value: _vm.item.phoneConsentProfessionalProfession,
                    callback: function($$v) {
                      _vm.$set(
                        _vm.item,
                        "phoneConsentProfessionalProfession",
                        $$v
                      );
                    },
                    expression: "item.phoneConsentProfessionalProfession"
                  }
                },
                [
                  _c("v-radio", {
                    attrs: { label: _vm.i18n.profession.nurse, value: "nurse" }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.physician,
                      value: "physician"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.inhalotherapist,
                      value: "inhalotherapist"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.midwife,
                      value: "midwife"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.pharmacist,
                      value: "pharmacist"
                    }
                  })
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "border-bottom", attrs: { xs4: "" } },
            [
              !_vm.item.phoneConsentProfessional
                ? _c("v-text-field", {
                    attrs: {
                      "aria-label": "professional-signature",
                      label: _vm.i18n.professionalName,
                      disabled: "",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "100",
                      rules: [
                        !_vm.item.phoneConsentProfessionalProfession ||
                          !_vm.appliedRequiredField ||
                          _vm.requiredField(
                            _vm.$i18n.t("as624.base.errors.requireField")
                          )
                      ]
                    }
                  })
                : [
                    _c(
                      "v-layout",
                      { attrs: { row: "", wrap: "" } },
                      [
                        _c("v-flex", { attrs: { xs12: "", "pl-2": "" } }, [
                          _c("span", { staticClass: "caption" }, [
                            _vm._v(_vm._s(_vm.i18n.professionalSignature))
                          ])
                        ]),
                        _vm._v(" "),
                        _c(
                          "v-flex",
                          {
                            staticStyle: {
                              "margin-top": "-8px",
                              "margin-bottom": "-8px"
                            }
                          },
                          [
                            _c("img", {
                              attrs: {
                                src: _vm.item.phoneConsentProfessional,
                                alt: "Signature"
                              }
                            })
                          ]
                        )
                      ],
                      1
                    )
                  ]
            ],
            2
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "border-bottom", attrs: { xs2: "" } },
            [
              _c("v-text-field", {
                attrs: {
                  "hide-details": "",
                  "aria-label": "phone-consent-professional-signature-date",
                  placeholder: _vm.i18n.professionalDate,
                  disabled: !_vm.hasProfessionalSectionAccess,
                  "single-line": "",
                  box: "",
                  maxlength: "10",
                  mask: "####-##-##",
                  "return-masked-value": "",
                  rules: [
                    function(v) {
                      return !v || _vm.validDate(v)
                    },
                    !_vm.item.phoneConsentProfessionalProfession ||
                      !_vm.appliedRequiredField ||
                      _vm.requiredField(
                        _vm.$i18n.t("as624.base.errors.requireField")
                      )
                  ]
                },
                model: {
                  value: _vm.item.phoneConsentProfessionalDate,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "phoneConsentProfessionalDate", $$v);
                  },
                  expression: "item.phoneConsentProfessionalDate"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass:
                "font-weight-bold border-bottom grey lighten-2 decision-subtitle",
              attrs: { xs4: "" }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.witnessName) + "\n    ")]
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "border-bottom", attrs: { xs6: "" } },
            [
              _c("v-text-field", {
                attrs: {
                  "aria-label": "professional-signature",
                  disabled: !_vm.hasProfessionalSectionAccess,
                  placeholder: "",
                  "hide-details": "",
                  box: "",
                  maxlength: "100",
                  rules: [
                    !_vm.item.phoneConsentProfessionalProfession ||
                      !_vm.appliedRequiredField ||
                      _vm.requiredField(
                        _vm.$i18n.t("as624.base.errors.requireField")
                      )
                  ]
                },
                model: {
                  value: _vm.item.phoneConsentWitness,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "phoneConsentWitness", $$v);
                  },
                  expression: "item.phoneConsentWitness"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "border-bottom", attrs: { xs2: "" } },
            [
              _c("v-text-field", {
                attrs: {
                  "hide-details": "",
                  "aria-label": "phone-consent-witness-signature-date",
                  placeholder: _vm.i18n.witnessDate,
                  disabled: !_vm.hasProfessionalSectionAccess,
                  "single-line": "",
                  box: "",
                  maxlength: "10",
                  mask: "####-##-##",
                  "return-masked-value": "",
                  rules: [
                    function(v) {
                      return !v || _vm.validDate(v)
                    },
                    !_vm.item.phoneConsentProfessionalProfession ||
                      !_vm.appliedRequiredField ||
                      _vm.requiredField(
                        _vm.$i18n.t("as624.base.errors.requireField")
                      )
                  ]
                },
                model: {
                  value: _vm.item.phoneConsentWitnessDate,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "phoneConsentWitnessDate", $$v);
                  },
                  expression: "item.phoneConsentWitnessDate"
                }
              })
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$r = [];
__vue_render__$r._withStripped = true;

  /* style */
  const __vue_inject_styles__$r = undefined;
  /* scoped */
  const __vue_scope_id__$r = undefined;
  /* functional template */
  const __vue_is_functional_template__$r = false;
  /* component normalizer */
  function __vue_normalize__$r(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/PhoneConsent/PhoneConsent.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var PhoneConsent$2 = __vue_normalize__$r(
    { render: __vue_render__$r, staticRenderFns: __vue_staticRenderFns__$r },
    __vue_inject_styles__$r,
    __vue_script__$r,
    __vue_scope_id__$r,
    __vue_is_functional_template__$r);

let FooterToolbarActions$3 = class FooterToolbarActions extends Vue {
    get i18n() {
        return this.$i18n.t('as624.footer');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasTransmitAccess() {
        return this.globalData.getFormAppData.hasTransmitAccess;
    }
    get hasExportAccess() {
        return this.globalData.getFormAppData.hasExportAccess;
    }
    get hasCompleteFormButtonAccess() {
        return this.globalData.getFormAppData.hasCompleteFormButtonAccess;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    get saveLoading() {
        return this.globalData.getFormAppData.saveLoading;
    }
    get isTransmit() {
        let status = this.item.status;
        return status.includes('_transmitted');
    }
    submitTransmitForm(oldStatus) {
        let newStatus;
        if (this.isTransmit) {
            newStatus = oldStatus.replace('_transmitted', '');
        }
        else {
            newStatus = oldStatus + '_transmitted';
        }
        this.globalData.emitEvent({ event: 'submit-transmit-form', data: newStatus });
    }
    exportToPdf() {
        this.globalData.emitEvent({ event: 'export-to-pdf', data: true });
    }
    saveTemporaryForm() {
        this.globalData.emitEvent({ event: 'save-temporary-form', data: true });
    }
    completeButtonDispatch() {
        this.globalData.emitEvent({ event: 'complete-button-dispatch', data: true });
    }
    closeModalForm() {
        this.globalData.emitEvent({ event: 'close', data: true });
    }
};
__decorate([
    Prop()
], FooterToolbarActions$3.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], FooterToolbarActions$3.prototype, "item", void 0);
FooterToolbarActions$3 = __decorate([
    Component
], FooterToolbarActions$3);
var script$q = FooterToolbarActions$3;

/* script */
            const __vue_script__$q = script$q;
            
/* template */
var __vue_render__$q = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        {
          staticClass: "text-xs-center",
          class: this.$vuetify.breakpoint.smAndDown ? "text-xs-left" : "",
          attrs: { xs12: "", "pr-2": "", "pb-2": "" }
        },
        [
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: {
                dark: "",
                flat: "",
                disabled: _vm.lockForm || _vm.saveLoading,
                "aria-label": _vm.i18n.save
              },
              on: { click: _vm.saveTemporaryForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.save) + "\n    ")]
          ),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: {
                dark: "",
                flat: "",
                disabled:
                  !_vm.hasCompleteFormButtonAccess ||
                  _vm.lockForm ||
                  _vm.saveLoading,
                "aria-label": _vm.i18n.saveAndComplete
              },
              on: { click: _vm.completeButtonDispatch }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.saveAndComplete) + "\n    ")]
          ),
          _vm._v(" "),
          _vm.hasTransmitAccess
            ? _c(
                "v-btn",
                {
                  staticClass: "primary text-center",
                  attrs: { dark: "", flat: "" },
                  on: {
                    click: function($event) {
                      return _vm.submitTransmitForm(_vm.item.status)
                    }
                  }
                },
                [
                  !_vm.isTransmit
                    ? _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsTransmitted))
                      ])
                    : _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsNotTransmitted))
                      ])
                ]
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: { dark: "", flat: "" },
              on: { click: _vm.closeModalForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.close) + "\n    ")]
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$q = [];
__vue_render__$q._withStripped = true;

  /* style */
  const __vue_inject_styles__$q = undefined;
  /* scoped */
  const __vue_scope_id__$q = undefined;
  /* functional template */
  const __vue_is_functional_template__$q = false;
  /* component normalizer */
  function __vue_normalize__$q(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/form-parts/FooterToolbarActions/FooterToolbarActions.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var FooterToolbarActions$2 = __vue_normalize__$q(
    { render: __vue_render__$q, staticRenderFns: __vue_staticRenderFns__$q },
    __vue_inject_styles__$q,
    __vue_script__$q,
    __vue_scope_id__$q,
    __vue_is_functional_template__$q);

const hasSomeProviderChampFilled$1 = (vaccinationData) => {
    return !!(vaccinationData.immunizationProviderLicenceNumber ||
        vaccinationData.immunizationProviderName ||
        vaccinationData.immunizationProviderProfession ||
        vaccinationData.immunizationProviderSignature);
};

let As624 = class As624 extends Vue {
    constructor() {
        super(...arguments);
        // Dialogs
        this.showFormErrorDialog = false;
        this.showFormEditWarningDialog = false;
        this.establishmentId = this.data.establishmentId;
        this.appointmentNumber = this.data['appointment.appointmentNumber'];
        this.serviceTemplate = this.data['appointment.serviceTemplate'];
        this.status = this.data.status;
    }
    /**
     * Vue Hook - Created
     */
    created() {
        // Set the data in the vuex store
        globalDataVuex().setFormAppData(this.appData);
    }
    /**
     * Retrieve the app data for this form
     */
    get getAppData() {
        return globalDataVuex().getFormAppData;
    }
    get getLastEmittedEvent() {
        return globalDataVuex().getLastEmittedEvent;
    }
    get numberOfLineToDisplay() {
        const item = this.item.data;
        for (let i = 9; i > 0; i--) {
            if (item[`vaccineDetailsCategory${i}`]) {
                return i + 1;
            }
        }
        return 1;
    }
    get shouldValidateVaccineDetailsSection() {
        const data = this.item.data;
        return data.decisionConsentVaccination === true && hasSomeProviderChampFilled$1(data);
    }
    get getHasFilledAllProviderChamps() {
        return hasFilledAllProviderChamps$1(this.item.data);
    }
    async onEventEmit(event) {
        switch (event.event) {
            case 'complete-button-dispatch':
                await this.$emit('update-applied-required-field', true);
                if (this.validateForm(true)) {
                    await this.$emit(event.event, this.getHasFilledAllProviderChamps);
                }
                break;
            case 'export-to-pdf':
                await this.$emit(event.event, event.data);
                break;
            case 'save-temporary-form':
                await this.$emit('update-applied-required-field', false);
                if (this.validateForm(false)) {
                    await this.$emit(event.event, event.data);
                }
                break;
            default:
                await this.$emit(event.event, event.data);
        }
    }
    onAppDataUpdate() {
        globalDataVuex().setFormAppData(this.appData);
    }
    setAllowEditForm(value) {
        this.$emit('allow-edit-form', value);
    }
    validateForm(showFormErrorDialog) {
        let isValid = (this.as624Form && this.as624Form.validate());
        if (!isValid) {
            this.showFormErrorDialog = showFormErrorDialog;
            return false;
        }
        return true;
    }
    displayWarningIfLocked() {
        if (!globalDataVuex().getFormAppData.hasReadOnly && globalDataVuex().getFormAppData.lockForm) {
            this.showFormEditWarningDialog = true;
        }
    }
};
__decorate([
    Ref()
], As624.prototype, "as624Form", void 0);
__decorate([
    Ref()
], As624.prototype, "immunizationProviderForm", void 0);
__decorate([
    Prop()
], As624.prototype, "data", void 0);
__decorate([
    Prop()
], As624.prototype, "appData", void 0);
__decorate([
    PropSync('data', { type: Object })
], As624.prototype, "item", void 0);
__decorate([
    Watch('getLastEmittedEvent')
], As624.prototype, "onEventEmit", null);
__decorate([
    Watch('appData')
], As624.prototype, "onAppDataUpdate", null);
As624 = __decorate([
    Component({
        components: {
            DialogFormError, DialogFormEditWarning,
            FooterToolbarActions: FooterToolbarActions$2,
            TitleAndPatient: TitleAndPatient$2,
            HeaderToolbar: HeaderToolbar$2,
            GeneralInformation: GeneralInformation$2,
            PreImmunization: PreImmunization$2,
            AdministrativeReason: AdministrativeReason$2,
            ConsentAndDecision: ConsentAndDecision$2,
            ProfessionalInformation: ProfessionalInformation$2,
            VaccineDetails: VaccineDetails$2,
            PhoneConsent: PhoneConsent$2,
            Notes: Notes$2,
        },
    })
], As624);
var script$p = As624;

/* script */
            const __vue_script__$p = script$p;
            
/* template */
var __vue_render__$p = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-card",
            { attrs: { tile: "" } },
            [
              _c("header-toolbar", {
                attrs: { data: _vm.item },
                on: {
                  "update:data": function($event) {
                    _vm.item = $event;
                  }
                }
              }),
              _vm._v(" "),
              !_vm.getAppData.vuetifyBreakpoint.xsOnly
                ? _c(
                    "v-card-text",
                    { on: { click: _vm.displayWarningIfLocked } },
                    [
                      _c(
                        "v-form",
                        {
                          ref: "as624Form",
                          attrs: { id: "as-624-form", "lazy-validation": "" }
                        },
                        [
                          _c("title-and-patient", {
                            attrs: {
                              data: _vm.item.data,
                              establishmentId: _vm.establishmentId,
                              appointmentNumber: _vm.appointmentNumber
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("general-information", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("pre-immunization", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("administrative-reason", {
                            attrs: {
                              data: _vm.item.data,
                              numberOfLineToDisplay: _vm.numberOfLineToDisplay
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("consent-and-decision", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("professional-information", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("phone-consent", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("vaccine-details", {
                            attrs: {
                              data: _vm.item.data,
                              serviceTemplate: +_vm.serviceTemplate,
                              status: _vm.status.toString(),
                              numberOfLineToDisplay: _vm.numberOfLineToDisplay,
                              "should-check-validation":
                                _vm.shouldValidateVaccineDetailsSection
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("notes", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              _c(
                "v-card-actions",
                [
                  _c("footer-toolbar-actions", {
                    attrs: { data: _vm.item },
                    on: {
                      "update:data": function($event) {
                        _vm.item = $event;
                      }
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c("dialog-form-error", {
        model: {
          value: _vm.showFormErrorDialog,
          callback: function($$v) {
            _vm.showFormErrorDialog = $$v;
          },
          expression: "showFormErrorDialog"
        }
      }),
      _vm._v(" "),
      _c("dialog-form-edit-warning", {
        on: { "allow-edit-form": _vm.setAllowEditForm },
        model: {
          value: _vm.showFormEditWarningDialog,
          callback: function($$v) {
            _vm.showFormEditWarningDialog = $$v;
          },
          expression: "showFormEditWarningDialog"
        }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$p = [];
__vue_render__$p._withStripped = true;

  /* style */
  const __vue_inject_styles__$p = function (inject) {
    if (!inject) return
    inject("data-v-40ffb228_0", { source: "#as-624-form .border-all,\n#immunization-provider-form .border-all {\n  border: 1px black solid;\n}\n#as-624-form .v-input__slot,\n#immunization-provider-form .v-input__slot {\n  background: none;\n}\n#as-624-form .border-top,\n#immunization-provider-form .border-top {\n  border-top: 1px solid #000000 !important;\n}\n#as-624-form .border-bottom,\n#immunization-provider-form .border-bottom {\n  border-bottom: 1px solid #000000 !important;\n}\n#as-624-form .border-left,\n#immunization-provider-form .border-left {\n  border-left: 1px solid #000000 !important;\n}\n#as-624-form .border-right,\n#immunization-provider-form .border-right {\n  border-right: 1px solid #000000 !important;\n}\n#as-624-form .header-question,\n#immunization-provider-form .header-question {\n  width: 38px;\n  display: inline-flex;\n}\n#as-624-form .header-NA,\n#immunization-provider-form .header-NA {\n  width: 60px;\n  display: inline-flex;\n}\n#as-624-form .v-input--checkbox,\n#as-624-form .v-input--radio-group,\n#immunization-provider-form .v-input--checkbox,\n#immunization-provider-form .v-input--radio-group {\n  margin: 0;\n}\n#as-624-form .v-radio,\n#immunization-provider-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px;\n}\n#as-624-form .phone,\n#immunization-provider-form .phone {\n  float: left;\n  width: 50%;\n}\n#as-624-form .vaccinator-profession .v-radio,\n#as-624-form .professional-profession .v-radio,\n#immunization-provider-form .vaccinator-profession .v-radio,\n#immunization-provider-form .professional-profession .v-radio {\n  margin: 0 20px 0 0;\n}\n#as-624-form .user-phone .v-radio,\n#immunization-provider-form .user-phone .v-radio {\n  margin: 0;\n}\n#as-624-form .consent-textfield .v-input__control .v-input__slot,\n#immunization-provider-form .consent-textfield .v-input__control .v-input__slot {\n  height: 30px !important;\n  min-height: 30px !important;\n}\n#as-624-form .consent-textfield input,\n#immunization-provider-form .consent-textfield input {\n  margin-top: 0 !important;\n}\n#as-624-form .administrative-reason-vaccine,\n#immunization-provider-form .administrative-reason-vaccine {\n  writing-mode: tb-rl;\n  transform: rotate(-180deg);\n  text-transform: uppercase;\n}\n\n/*# sourceMappingURL=as624.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/as624.vue","as624.vue"],"names":[],"mappings":"AAsMA;;EAGA,uBAAA;AAAA;AAHA;;EAOA,gBAAA;AAAA;AAPA;;EAWA,wCAAA;AAAA;AAXA;;EAeA,2CAAA;AAAA;AAfA;;EAmBA,yCAAA;AAAA;AAnBA;;EAuBA,0CAAA;AAAA;AAvBA;;EA2BA,WAAA;EACA,oBAAA;AAAA;AA5BA;;EAgCA,WAAA;EACA,oBAAA;AAAA;AAjCA;;;;EAsCA,SAAA;AAAA;AAtCA;;EA0CA,kBAAA;EACA,eAAA;AAAA;AA3CA;;EA+CA,WAAA;EACA,UAAA;AAAA;AAhDA;;;;EAqDA,kBAAA;AAAA;AArDA;;EAyDA,SAAA;AAAA;AAzDA;;EA6DA,uBAAA;EACA,2BAAA;AAAA;AA9DA;;EAkEA,wBAAA;AAAA;AAlEA;;EAsEA,mBAAA;EACA,0BAAA;EACA,yBAAA;AAAA;;ACnMA,oCAAoC","file":"as624.vue","sourcesContent":[null,"#as-624-form .border-all,\n#immunization-provider-form .border-all {\n  border: 1px black solid; }\n\n#as-624-form .v-input__slot,\n#immunization-provider-form .v-input__slot {\n  background: none; }\n\n#as-624-form .border-top,\n#immunization-provider-form .border-top {\n  border-top: 1px solid #000000 !important; }\n\n#as-624-form .border-bottom,\n#immunization-provider-form .border-bottom {\n  border-bottom: 1px solid #000000 !important; }\n\n#as-624-form .border-left,\n#immunization-provider-form .border-left {\n  border-left: 1px solid #000000 !important; }\n\n#as-624-form .border-right,\n#immunization-provider-form .border-right {\n  border-right: 1px solid #000000 !important; }\n\n#as-624-form .header-question,\n#immunization-provider-form .header-question {\n  width: 38px;\n  display: inline-flex; }\n\n#as-624-form .header-NA,\n#immunization-provider-form .header-NA {\n  width: 60px;\n  display: inline-flex; }\n\n#as-624-form .v-input--checkbox,\n#as-624-form .v-input--radio-group,\n#immunization-provider-form .v-input--checkbox,\n#immunization-provider-form .v-input--radio-group {\n  margin: 0; }\n\n#as-624-form .v-radio,\n#immunization-provider-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px; }\n\n#as-624-form .phone,\n#immunization-provider-form .phone {\n  float: left;\n  width: 50%; }\n\n#as-624-form .vaccinator-profession .v-radio,\n#as-624-form .professional-profession .v-radio,\n#immunization-provider-form .vaccinator-profession .v-radio,\n#immunization-provider-form .professional-profession .v-radio {\n  margin: 0 20px 0 0; }\n\n#as-624-form .user-phone .v-radio,\n#immunization-provider-form .user-phone .v-radio {\n  margin: 0; }\n\n#as-624-form .consent-textfield .v-input__control .v-input__slot,\n#immunization-provider-form .consent-textfield .v-input__control .v-input__slot {\n  height: 30px !important;\n  min-height: 30px !important; }\n\n#as-624-form .consent-textfield input,\n#immunization-provider-form .consent-textfield input {\n  margin-top: 0 !important; }\n\n#as-624-form .administrative-reason-vaccine,\n#immunization-provider-form .administrative-reason-vaccine {\n  writing-mode: tb-rl;\n  transform: rotate(-180deg);\n  text-transform: uppercase; }\n\n/*# sourceMappingURL=as624.vue.map */"]}, media: undefined })
,inject("data-v-40ffb228_1", { source: "[data-v-40ffb228] .v-progress-circular__overlay {\n  stroke: white !important;\n}\n\n/*# sourceMappingURL=as624.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/as624.vue","as624.vue"],"names":[],"mappings":"AAoRA;EAEA,wBAAA;AAAA;;ACnRA,oCAAoC","file":"as624.vue","sourcesContent":[null,"::v-deep .v-progress-circular__overlay {\n  stroke: white !important; }\n\n/*# sourceMappingURL=as624.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$p = "data-v-40ffb228";
  /* module identifier */
  const __vue_module_identifier__$9 = undefined;
  /* functional template */
  const __vue_is_functional_template__$p = false;
  /* component normalizer */
  function __vue_normalize__$p(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2023/as624.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$9() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$9.styles || (__vue_create_injector__$9.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var as624 = __vue_normalize__$p(
    { render: __vue_render__$p, staticRenderFns: __vue_staticRenderFns__$p },
    __vue_inject_styles__$p,
    __vue_script__$p,
    __vue_scope_id__$p,
    __vue_is_functional_template__$p,
    __vue_module_identifier__$9,
    __vue_create_injector__$9);

let HeaderToolbar$1 = class HeaderToolbar extends Vue {
    get i18n() {
        return this.$i18n.t('as6242024.general');
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get hasTransmitAccess() {
        return globalDataVuex().getFormAppData.hasTransmitAccess;
    }
    get hasExportAccess() {
        return globalDataVuex().getFormAppData.hasExportAccess;
    }
    get lockForm() {
        return globalDataVuex().getFormAppData.lockForm;
    }
    get exportLoading() {
        return globalDataVuex().getFormAppData.exportLoading;
    }
    get saveLoading() {
        return globalDataVuex().getFormAppData.saveLoading;
    }
    get isTransmit() {
        let status = this.item.status;
        return status.includes('_transmitted');
    }
    closeModalForm() {
        globalDataVuex().emitEvent({ event: 'close', data: true });
    }
    submitTransmitForm(oldStatus) {
        let newStatus;
        if (this.isTransmit) {
            newStatus = oldStatus.replace('_transmitted', '');
        }
        else {
            newStatus = oldStatus + '_transmitted';
        }
        globalDataVuex().emitEvent({ event: 'submit-transmit-form', data: newStatus });
    }
    exportToPdf() {
        globalDataVuex().emitEvent({ event: 'export-to-pdf', data: true });
    }
    saveTemporaryForm() {
        globalDataVuex().emitEvent({ event: 'save-temporary-form', data: true });
    }
};
__decorate([
    Prop()
], HeaderToolbar$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], HeaderToolbar$1.prototype, "item", void 0);
HeaderToolbar$1 = __decorate([
    Component
], HeaderToolbar$1);
var script$o = HeaderToolbar$1;

/* script */
            const __vue_script__$o = script$o;
            
/* template */
var __vue_render__$o = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-toolbar",
    { attrs: { card: "", dark: "", color: "primary" } },
    [
      _c(
        "v-btn",
        { attrs: { icon: "", dark: "" }, on: { click: _vm.closeModalForm } },
        [_c("v-icon", [_vm._v("close")])],
        1
      ),
      _vm._v(" "),
      _c("v-toolbar-title", [_vm._v(_vm._s(_vm.i18n.title))]),
      _vm._v(" "),
      _c("v-spacer"),
      _vm._v(" "),
      _c(
        "v-toolbar-items",
        {
          directives: [
            {
              name: "show",
              rawName: "v-show",
              value: !_vm.vuetifyBreakpoint.xsOnly,
              expression: "!vuetifyBreakpoint.xsOnly"
            }
          ]
        },
        [
          _vm.hasTransmitAccess
            ? _c(
                "v-btn",
                {
                  attrs: { dark: "", flat: "" },
                  on: {
                    click: function($event) {
                      return _vm.submitTransmitForm(_vm.item.status)
                    }
                  }
                },
                [
                  !_vm.isTransmit
                    ? _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsTransmitted))
                      ])
                    : _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsNotTransmitted))
                      ])
                ]
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.hasExportAccess
            ? _c(
                "v-btn",
                {
                  attrs: { loading: _vm.exportLoading, dark: "", flat: "" },
                  on: { click: _vm.exportToPdf }
                },
                [_vm._v("\n      " + _vm._s(_vm.i18n.exportToPdf) + "\n    ")]
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              attrs: {
                dark: "",
                flat: "",
                disabled: _vm.lockForm,
                loading: _vm.saveLoading
              },
              on: { click: _vm.saveTemporaryForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.save) + "\n    ")]
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$o = [];
__vue_render__$o._withStripped = true;

  /* style */
  const __vue_inject_styles__$o = undefined;
  /* scoped */
  const __vue_scope_id__$o = undefined;
  /* functional template */
  const __vue_is_functional_template__$o = false;
  /* component normalizer */
  function __vue_normalize__$o(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/HeaderToolbar.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var HeaderToolbar = __vue_normalize__$o(
    { render: __vue_render__$o, staticRenderFns: __vue_staticRenderFns__$o },
    __vue_inject_styles__$o,
    __vue_script__$o,
    __vue_scope_id__$o,
    __vue_is_functional_template__$o);

// these aren't really private, but nor are they really useful to document

/**
 * @private
 */
class LuxonError extends Error {}

/**
 * @private
 */
class InvalidDateTimeError extends LuxonError {
  constructor(reason) {
    super(`Invalid DateTime: ${reason.toMessage()}`);
  }
}

/**
 * @private
 */
class InvalidIntervalError extends LuxonError {
  constructor(reason) {
    super(`Invalid Interval: ${reason.toMessage()}`);
  }
}

/**
 * @private
 */
class InvalidDurationError extends LuxonError {
  constructor(reason) {
    super(`Invalid Duration: ${reason.toMessage()}`);
  }
}

/**
 * @private
 */
class ConflictingSpecificationError extends LuxonError {}

/**
 * @private
 */
class InvalidUnitError extends LuxonError {
  constructor(unit) {
    super(`Invalid unit ${unit}`);
  }
}

/**
 * @private
 */
class InvalidArgumentError extends LuxonError {}

/**
 * @private
 */
class ZoneIsAbstractError extends LuxonError {
  constructor() {
    super("Zone is an abstract class");
  }
}

/**
 * @private
 */

const n = "numeric",
  s = "short",
  l = "long";

const DATE_SHORT = {
  year: n,
  month: n,
  day: n,
};

const DATE_MED = {
  year: n,
  month: s,
  day: n,
};

const DATE_MED_WITH_WEEKDAY = {
  year: n,
  month: s,
  day: n,
  weekday: s,
};

const DATE_FULL = {
  year: n,
  month: l,
  day: n,
};

const DATE_HUGE = {
  year: n,
  month: l,
  day: n,
  weekday: l,
};

const TIME_SIMPLE = {
  hour: n,
  minute: n,
};

const TIME_WITH_SECONDS = {
  hour: n,
  minute: n,
  second: n,
};

const TIME_WITH_SHORT_OFFSET = {
  hour: n,
  minute: n,
  second: n,
  timeZoneName: s,
};

const TIME_WITH_LONG_OFFSET = {
  hour: n,
  minute: n,
  second: n,
  timeZoneName: l,
};

const TIME_24_SIMPLE = {
  hour: n,
  minute: n,
  hourCycle: "h23",
};

const TIME_24_WITH_SECONDS = {
  hour: n,
  minute: n,
  second: n,
  hourCycle: "h23",
};

const TIME_24_WITH_SHORT_OFFSET = {
  hour: n,
  minute: n,
  second: n,
  hourCycle: "h23",
  timeZoneName: s,
};

const TIME_24_WITH_LONG_OFFSET = {
  hour: n,
  minute: n,
  second: n,
  hourCycle: "h23",
  timeZoneName: l,
};

const DATETIME_SHORT = {
  year: n,
  month: n,
  day: n,
  hour: n,
  minute: n,
};

const DATETIME_SHORT_WITH_SECONDS = {
  year: n,
  month: n,
  day: n,
  hour: n,
  minute: n,
  second: n,
};

const DATETIME_MED = {
  year: n,
  month: s,
  day: n,
  hour: n,
  minute: n,
};

const DATETIME_MED_WITH_SECONDS = {
  year: n,
  month: s,
  day: n,
  hour: n,
  minute: n,
  second: n,
};

const DATETIME_MED_WITH_WEEKDAY = {
  year: n,
  month: s,
  day: n,
  weekday: s,
  hour: n,
  minute: n,
};

const DATETIME_FULL = {
  year: n,
  month: l,
  day: n,
  hour: n,
  minute: n,
  timeZoneName: s,
};

const DATETIME_FULL_WITH_SECONDS = {
  year: n,
  month: l,
  day: n,
  hour: n,
  minute: n,
  second: n,
  timeZoneName: s,
};

const DATETIME_HUGE = {
  year: n,
  month: l,
  day: n,
  weekday: l,
  hour: n,
  minute: n,
  timeZoneName: l,
};

const DATETIME_HUGE_WITH_SECONDS = {
  year: n,
  month: l,
  day: n,
  weekday: l,
  hour: n,
  minute: n,
  second: n,
  timeZoneName: l,
};

/*
  This is just a junk drawer, containing anything used across multiple classes.
  Because Luxon is small(ish), this should stay small and we won't worry about splitting
  it up into, say, parsingUtil.js and basicUtil.js and so on. But they are divided up by feature area.
*/

/**
 * @private
 */

// TYPES

function isUndefined(o) {
  return typeof o === "undefined";
}

function isNumber(o) {
  return typeof o === "number";
}

function isInteger(o) {
  return typeof o === "number" && o % 1 === 0;
}

function isString(o) {
  return typeof o === "string";
}

function isDate$3(o) {
  return Object.prototype.toString.call(o) === "[object Date]";
}

// CAPABILITIES

function hasRelative() {
  try {
    return typeof Intl !== "undefined" && !!Intl.RelativeTimeFormat;
  } catch (e) {
    return false;
  }
}

// OBJECTS AND ARRAYS

function maybeArray(thing) {
  return Array.isArray(thing) ? thing : [thing];
}

function bestBy(arr, by, compare) {
  if (arr.length === 0) {
    return undefined;
  }
  return arr.reduce((best, next) => {
    const pair = [by(next), next];
    if (!best) {
      return pair;
    } else if (compare(best[0], pair[0]) === best[0]) {
      return best;
    } else {
      return pair;
    }
  }, null)[1];
}

function pick(obj, keys) {
  return keys.reduce((a, k) => {
    a[k] = obj[k];
    return a;
  }, {});
}

function hasOwnProperty(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

// NUMBERS AND STRINGS

function integerBetween(thing, bottom, top) {
  return isInteger(thing) && thing >= bottom && thing <= top;
}

// x % n but takes the sign of n instead of x
function floorMod(x, n) {
  return x - n * Math.floor(x / n);
}

function padStart(input, n = 2) {
  const isNeg = input < 0;
  let padded;
  if (isNeg) {
    padded = "-" + ("" + -input).padStart(n, "0");
  } else {
    padded = ("" + input).padStart(n, "0");
  }
  return padded;
}

function parseInteger(string) {
  if (isUndefined(string) || string === null || string === "") {
    return undefined;
  } else {
    return parseInt(string, 10);
  }
}

function parseFloating(string) {
  if (isUndefined(string) || string === null || string === "") {
    return undefined;
  } else {
    return parseFloat(string);
  }
}

function parseMillis(fraction) {
  // Return undefined (instead of 0) in these cases, where fraction is not set
  if (isUndefined(fraction) || fraction === null || fraction === "") {
    return undefined;
  } else {
    const f = parseFloat("0." + fraction) * 1000;
    return Math.floor(f);
  }
}

function roundTo(number, digits, towardZero = false) {
  const factor = 10 ** digits,
    rounder = towardZero ? Math.trunc : Math.round;
  return rounder(number * factor) / factor;
}

// DATE BASICS

function isLeapYear(year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInYear(year) {
  return isLeapYear(year) ? 366 : 365;
}

function daysInMonth(year, month) {
  const modMonth = floorMod(month - 1, 12) + 1,
    modYear = year + (month - modMonth) / 12;

  if (modMonth === 2) {
    return isLeapYear(modYear) ? 29 : 28;
  } else {
    return [31, null, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][modMonth - 1];
  }
}

// covert a calendar object to a local timestamp (epoch, but with the offset baked in)
function objToLocalTS(obj) {
  let d = Date.UTC(
    obj.year,
    obj.month - 1,
    obj.day,
    obj.hour,
    obj.minute,
    obj.second,
    obj.millisecond
  );

  // for legacy reasons, years between 0 and 99 are interpreted as 19XX; revert that
  if (obj.year < 100 && obj.year >= 0) {
    d = new Date(d);
    d.setUTCFullYear(d.getUTCFullYear() - 1900);
  }
  return +d;
}

function weeksInWeekYear(weekYear) {
  const p1 =
      (weekYear +
        Math.floor(weekYear / 4) -
        Math.floor(weekYear / 100) +
        Math.floor(weekYear / 400)) %
      7,
    last = weekYear - 1,
    p2 = (last + Math.floor(last / 4) - Math.floor(last / 100) + Math.floor(last / 400)) % 7;
  return p1 === 4 || p2 === 3 ? 53 : 52;
}

function untruncateYear(year) {
  if (year > 99) {
    return year;
  } else return year > 60 ? 1900 + year : 2000 + year;
}

// PARSING

function parseZoneInfo(ts, offsetFormat, locale, timeZone = null) {
  const date = new Date(ts),
    intlOpts = {
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };

  if (timeZone) {
    intlOpts.timeZone = timeZone;
  }

  const modified = { timeZoneName: offsetFormat, ...intlOpts };

  const parsed = new Intl.DateTimeFormat(locale, modified)
    .formatToParts(date)
    .find((m) => m.type.toLowerCase() === "timezonename");
  return parsed ? parsed.value : null;
}

// signedOffset('-5', '30') -> -330
function signedOffset(offHourStr, offMinuteStr) {
  let offHour = parseInt(offHourStr, 10);

  // don't || this because we want to preserve -0
  if (Number.isNaN(offHour)) {
    offHour = 0;
  }

  const offMin = parseInt(offMinuteStr, 10) || 0,
    offMinSigned = offHour < 0 || Object.is(offHour, -0) ? -offMin : offMin;
  return offHour * 60 + offMinSigned;
}

// COERCION

function asNumber(value) {
  const numericValue = Number(value);
  if (typeof value === "boolean" || value === "" || Number.isNaN(numericValue))
    throw new InvalidArgumentError(`Invalid unit value ${value}`);
  return numericValue;
}

function normalizeObject(obj, normalizer) {
  const normalized = {};
  for (const u in obj) {
    if (hasOwnProperty(obj, u)) {
      const v = obj[u];
      if (v === undefined || v === null) continue;
      normalized[normalizer(u)] = asNumber(v);
    }
  }
  return normalized;
}

function formatOffset(offset, format) {
  const hours = Math.trunc(Math.abs(offset / 60)),
    minutes = Math.trunc(Math.abs(offset % 60)),
    sign = offset >= 0 ? "+" : "-";

  switch (format) {
    case "short":
      return `${sign}${padStart(hours, 2)}:${padStart(minutes, 2)}`;
    case "narrow":
      return `${sign}${hours}${minutes > 0 ? `:${minutes}` : ""}`;
    case "techie":
      return `${sign}${padStart(hours, 2)}${padStart(minutes, 2)}`;
    default:
      throw new RangeError(`Value format ${format} is out of range for property format`);
  }
}

function timeObject(obj) {
  return pick(obj, ["hour", "minute", "second", "millisecond"]);
}

const ianaRegex =
  /[A-Za-z_+-]{1,256}(?::?\/[A-Za-z0-9_+-]{1,256}(?:\/[A-Za-z0-9_+-]{1,256})?)?/;

/**
 * @private
 */

const monthsLong = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthsShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const monthsNarrow = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

function months(length) {
  switch (length) {
    case "narrow":
      return [...monthsNarrow];
    case "short":
      return [...monthsShort];
    case "long":
      return [...monthsLong];
    case "numeric":
      return ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
    case "2-digit":
      return ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
    default:
      return null;
  }
}

const weekdaysLong = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const weekdaysShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const weekdaysNarrow = ["M", "T", "W", "T", "F", "S", "S"];

function weekdays(length) {
  switch (length) {
    case "narrow":
      return [...weekdaysNarrow];
    case "short":
      return [...weekdaysShort];
    case "long":
      return [...weekdaysLong];
    case "numeric":
      return ["1", "2", "3", "4", "5", "6", "7"];
    default:
      return null;
  }
}

const meridiems = ["AM", "PM"];

const erasLong = ["Before Christ", "Anno Domini"];

const erasShort = ["BC", "AD"];

const erasNarrow = ["B", "A"];

function eras(length) {
  switch (length) {
    case "narrow":
      return [...erasNarrow];
    case "short":
      return [...erasShort];
    case "long":
      return [...erasLong];
    default:
      return null;
  }
}

function meridiemForDateTime(dt) {
  return meridiems[dt.hour < 12 ? 0 : 1];
}

function weekdayForDateTime(dt, length) {
  return weekdays(length)[dt.weekday - 1];
}

function monthForDateTime(dt, length) {
  return months(length)[dt.month - 1];
}

function eraForDateTime(dt, length) {
  return eras(length)[dt.year < 0 ? 0 : 1];
}

function formatRelativeTime(unit, count, numeric = "always", narrow = false) {
  const units = {
    years: ["year", "yr."],
    quarters: ["quarter", "qtr."],
    months: ["month", "mo."],
    weeks: ["week", "wk."],
    days: ["day", "day", "days"],
    hours: ["hour", "hr."],
    minutes: ["minute", "min."],
    seconds: ["second", "sec."],
  };

  const lastable = ["hours", "minutes", "seconds"].indexOf(unit) === -1;

  if (numeric === "auto" && lastable) {
    const isDay = unit === "days";
    switch (count) {
      case 1:
        return isDay ? "tomorrow" : `next ${units[unit][0]}`;
      case -1:
        return isDay ? "yesterday" : `last ${units[unit][0]}`;
      case 0:
        return isDay ? "today" : `this ${units[unit][0]}`;
    }
  }

  const isInPast = Object.is(count, -0) || count < 0,
    fmtValue = Math.abs(count),
    singular = fmtValue === 1,
    lilUnits = units[unit],
    fmtUnit = narrow
      ? singular
        ? lilUnits[1]
        : lilUnits[2] || lilUnits[1]
      : singular
      ? units[unit][0]
      : unit;
  return isInPast ? `${fmtValue} ${fmtUnit} ago` : `in ${fmtValue} ${fmtUnit}`;
}

function stringifyTokens(splits, tokenToString) {
  let s = "";
  for (const token of splits) {
    if (token.literal) {
      s += token.val;
    } else {
      s += tokenToString(token.val);
    }
  }
  return s;
}

const macroTokenToFormatOpts = {
  D: DATE_SHORT,
  DD: DATE_MED,
  DDD: DATE_FULL,
  DDDD: DATE_HUGE,
  t: TIME_SIMPLE,
  tt: TIME_WITH_SECONDS,
  ttt: TIME_WITH_SHORT_OFFSET,
  tttt: TIME_WITH_LONG_OFFSET,
  T: TIME_24_SIMPLE,
  TT: TIME_24_WITH_SECONDS,
  TTT: TIME_24_WITH_SHORT_OFFSET,
  TTTT: TIME_24_WITH_LONG_OFFSET,
  f: DATETIME_SHORT,
  ff: DATETIME_MED,
  fff: DATETIME_FULL,
  ffff: DATETIME_HUGE,
  F: DATETIME_SHORT_WITH_SECONDS,
  FF: DATETIME_MED_WITH_SECONDS,
  FFF: DATETIME_FULL_WITH_SECONDS,
  FFFF: DATETIME_HUGE_WITH_SECONDS,
};

/**
 * @private
 */

class Formatter {
  static create(locale, opts = {}) {
    return new Formatter(locale, opts);
  }

  static parseFormat(fmt) {
    let current = null,
      currentFull = "",
      bracketed = false;
    const splits = [];
    for (let i = 0; i < fmt.length; i++) {
      const c = fmt.charAt(i);
      if (c === "'") {
        if (currentFull.length > 0) {
          splits.push({ literal: bracketed, val: currentFull });
        }
        current = null;
        currentFull = "";
        bracketed = !bracketed;
      } else if (bracketed) {
        currentFull += c;
      } else if (c === current) {
        currentFull += c;
      } else {
        if (currentFull.length > 0) {
          splits.push({ literal: false, val: currentFull });
        }
        currentFull = c;
        current = c;
      }
    }

    if (currentFull.length > 0) {
      splits.push({ literal: bracketed, val: currentFull });
    }

    return splits;
  }

  static macroTokenToFormatOpts(token) {
    return macroTokenToFormatOpts[token];
  }

  constructor(locale, formatOpts) {
    this.opts = formatOpts;
    this.loc = locale;
    this.systemLoc = null;
  }

  formatWithSystemDefault(dt, opts) {
    if (this.systemLoc === null) {
      this.systemLoc = this.loc.redefaultToSystem();
    }
    const df = this.systemLoc.dtFormatter(dt, { ...this.opts, ...opts });
    return df.format();
  }

  formatDateTime(dt, opts = {}) {
    const df = this.loc.dtFormatter(dt, { ...this.opts, ...opts });
    return df.format();
  }

  formatDateTimeParts(dt, opts = {}) {
    const df = this.loc.dtFormatter(dt, { ...this.opts, ...opts });
    return df.formatToParts();
  }

  resolvedOptions(dt, opts = {}) {
    const df = this.loc.dtFormatter(dt, { ...this.opts, ...opts });
    return df.resolvedOptions();
  }

  num(n, p = 0) {
    // we get some perf out of doing this here, annoyingly
    if (this.opts.forceSimple) {
      return padStart(n, p);
    }

    const opts = { ...this.opts };

    if (p > 0) {
      opts.padTo = p;
    }

    return this.loc.numberFormatter(opts).format(n);
  }

  formatDateTimeFromString(dt, fmt) {
    const knownEnglish = this.loc.listingMode() === "en",
      useDateTimeFormatter = this.loc.outputCalendar && this.loc.outputCalendar !== "gregory",
      string = (opts, extract) => this.loc.extract(dt, opts, extract),
      formatOffset = (opts) => {
        if (dt.isOffsetFixed && dt.offset === 0 && opts.allowZ) {
          return "Z";
        }

        return dt.isValid ? dt.zone.formatOffset(dt.ts, opts.format) : "";
      },
      meridiem = () =>
        knownEnglish
          ? meridiemForDateTime(dt)
          : string({ hour: "numeric", hourCycle: "h12" }, "dayperiod"),
      month = (length, standalone) =>
        knownEnglish
          ? monthForDateTime(dt, length)
          : string(standalone ? { month: length } : { month: length, day: "numeric" }, "month"),
      weekday = (length, standalone) =>
        knownEnglish
          ? weekdayForDateTime(dt, length)
          : string(
              standalone ? { weekday: length } : { weekday: length, month: "long", day: "numeric" },
              "weekday"
            ),
      maybeMacro = (token) => {
        const formatOpts = Formatter.macroTokenToFormatOpts(token);
        if (formatOpts) {
          return this.formatWithSystemDefault(dt, formatOpts);
        } else {
          return token;
        }
      },
      era = (length) =>
        knownEnglish ? eraForDateTime(dt, length) : string({ era: length }, "era"),
      tokenToString = (token) => {
        // Where possible: http://cldr.unicode.org/translation/date-time-1/date-time#TOC-Standalone-vs.-Format-Styles
        switch (token) {
          // ms
          case "S":
            return this.num(dt.millisecond);
          case "u":
          // falls through
          case "SSS":
            return this.num(dt.millisecond, 3);
          // seconds
          case "s":
            return this.num(dt.second);
          case "ss":
            return this.num(dt.second, 2);
          // fractional seconds
          case "uu":
            return this.num(Math.floor(dt.millisecond / 10), 2);
          case "uuu":
            return this.num(Math.floor(dt.millisecond / 100));
          // minutes
          case "m":
            return this.num(dt.minute);
          case "mm":
            return this.num(dt.minute, 2);
          // hours
          case "h":
            return this.num(dt.hour % 12 === 0 ? 12 : dt.hour % 12);
          case "hh":
            return this.num(dt.hour % 12 === 0 ? 12 : dt.hour % 12, 2);
          case "H":
            return this.num(dt.hour);
          case "HH":
            return this.num(dt.hour, 2);
          // offset
          case "Z":
            // like +6
            return formatOffset({ format: "narrow", allowZ: this.opts.allowZ });
          case "ZZ":
            // like +06:00
            return formatOffset({ format: "short", allowZ: this.opts.allowZ });
          case "ZZZ":
            // like +0600
            return formatOffset({ format: "techie", allowZ: this.opts.allowZ });
          case "ZZZZ":
            // like EST
            return dt.zone.offsetName(dt.ts, { format: "short", locale: this.loc.locale });
          case "ZZZZZ":
            // like Eastern Standard Time
            return dt.zone.offsetName(dt.ts, { format: "long", locale: this.loc.locale });
          // zone
          case "z":
            // like America/New_York
            return dt.zoneName;
          // meridiems
          case "a":
            return meridiem();
          // dates
          case "d":
            return useDateTimeFormatter ? string({ day: "numeric" }, "day") : this.num(dt.day);
          case "dd":
            return useDateTimeFormatter ? string({ day: "2-digit" }, "day") : this.num(dt.day, 2);
          // weekdays - standalone
          case "c":
            // like 1
            return this.num(dt.weekday);
          case "ccc":
            // like 'Tues'
            return weekday("short", true);
          case "cccc":
            // like 'Tuesday'
            return weekday("long", true);
          case "ccccc":
            // like 'T'
            return weekday("narrow", true);
          // weekdays - format
          case "E":
            // like 1
            return this.num(dt.weekday);
          case "EEE":
            // like 'Tues'
            return weekday("short", false);
          case "EEEE":
            // like 'Tuesday'
            return weekday("long", false);
          case "EEEEE":
            // like 'T'
            return weekday("narrow", false);
          // months - standalone
          case "L":
            // like 1
            return useDateTimeFormatter
              ? string({ month: "numeric", day: "numeric" }, "month")
              : this.num(dt.month);
          case "LL":
            // like 01, doesn't seem to work
            return useDateTimeFormatter
              ? string({ month: "2-digit", day: "numeric" }, "month")
              : this.num(dt.month, 2);
          case "LLL":
            // like Jan
            return month("short", true);
          case "LLLL":
            // like January
            return month("long", true);
          case "LLLLL":
            // like J
            return month("narrow", true);
          // months - format
          case "M":
            // like 1
            return useDateTimeFormatter
              ? string({ month: "numeric" }, "month")
              : this.num(dt.month);
          case "MM":
            // like 01
            return useDateTimeFormatter
              ? string({ month: "2-digit" }, "month")
              : this.num(dt.month, 2);
          case "MMM":
            // like Jan
            return month("short", false);
          case "MMMM":
            // like January
            return month("long", false);
          case "MMMMM":
            // like J
            return month("narrow", false);
          // years
          case "y":
            // like 2014
            return useDateTimeFormatter ? string({ year: "numeric" }, "year") : this.num(dt.year);
          case "yy":
            // like 14
            return useDateTimeFormatter
              ? string({ year: "2-digit" }, "year")
              : this.num(dt.year.toString().slice(-2), 2);
          case "yyyy":
            // like 0012
            return useDateTimeFormatter
              ? string({ year: "numeric" }, "year")
              : this.num(dt.year, 4);
          case "yyyyyy":
            // like 000012
            return useDateTimeFormatter
              ? string({ year: "numeric" }, "year")
              : this.num(dt.year, 6);
          // eras
          case "G":
            // like AD
            return era("short");
          case "GG":
            // like Anno Domini
            return era("long");
          case "GGGGG":
            return era("narrow");
          case "kk":
            return this.num(dt.weekYear.toString().slice(-2), 2);
          case "kkkk":
            return this.num(dt.weekYear, 4);
          case "W":
            return this.num(dt.weekNumber);
          case "WW":
            return this.num(dt.weekNumber, 2);
          case "o":
            return this.num(dt.ordinal);
          case "ooo":
            return this.num(dt.ordinal, 3);
          case "q":
            // like 1
            return this.num(dt.quarter);
          case "qq":
            // like 01
            return this.num(dt.quarter, 2);
          case "X":
            return this.num(Math.floor(dt.ts / 1000));
          case "x":
            return this.num(dt.ts);
          default:
            return maybeMacro(token);
        }
      };

    return stringifyTokens(Formatter.parseFormat(fmt), tokenToString);
  }

  formatDurationFromString(dur, fmt) {
    const tokenToField = (token) => {
        switch (token[0]) {
          case "S":
            return "millisecond";
          case "s":
            return "second";
          case "m":
            return "minute";
          case "h":
            return "hour";
          case "d":
            return "day";
          case "w":
            return "week";
          case "M":
            return "month";
          case "y":
            return "year";
          default:
            return null;
        }
      },
      tokenToString = (lildur) => (token) => {
        const mapped = tokenToField(token);
        if (mapped) {
          return this.num(lildur.get(mapped), token.length);
        } else {
          return token;
        }
      },
      tokens = Formatter.parseFormat(fmt),
      realTokens = tokens.reduce(
        (found, { literal, val }) => (literal ? found : found.concat(val)),
        []
      ),
      collapsed = dur.shiftTo(...realTokens.map(tokenToField).filter((t) => t));
    return stringifyTokens(tokens, tokenToString(collapsed));
  }
}

class Invalid {
  constructor(reason, explanation) {
    this.reason = reason;
    this.explanation = explanation;
  }

  toMessage() {
    if (this.explanation) {
      return `${this.reason}: ${this.explanation}`;
    } else {
      return this.reason;
    }
  }
}

/**
 * @interface
 */
class Zone {
  /**
   * The type of zone
   * @abstract
   * @type {string}
   */
  get type() {
    throw new ZoneIsAbstractError();
  }

  /**
   * The name of this zone.
   * @abstract
   * @type {string}
   */
  get name() {
    throw new ZoneIsAbstractError();
  }

  get ianaName() {
    return this.name;
  }

  /**
   * Returns whether the offset is known to be fixed for the whole year.
   * @abstract
   * @type {boolean}
   */
  get isUniversal() {
    throw new ZoneIsAbstractError();
  }

  /**
   * Returns the offset's common name (such as EST) at the specified timestamp
   * @abstract
   * @param {number} ts - Epoch milliseconds for which to get the name
   * @param {Object} opts - Options to affect the format
   * @param {string} opts.format - What style of offset to return. Accepts 'long' or 'short'.
   * @param {string} opts.locale - What locale to return the offset name in.
   * @return {string}
   */
  offsetName(ts, opts) {
    throw new ZoneIsAbstractError();
  }

  /**
   * Returns the offset's value as a string
   * @abstract
   * @param {number} ts - Epoch milliseconds for which to get the offset
   * @param {string} format - What style of offset to return.
   *                          Accepts 'narrow', 'short', or 'techie'. Returning '+6', '+06:00', or '+0600' respectively
   * @return {string}
   */
  formatOffset(ts, format) {
    throw new ZoneIsAbstractError();
  }

  /**
   * Return the offset in minutes for this zone at the specified timestamp.
   * @abstract
   * @param {number} ts - Epoch milliseconds for which to compute the offset
   * @return {number}
   */
  offset(ts) {
    throw new ZoneIsAbstractError();
  }

  /**
   * Return whether this Zone is equal to another zone
   * @abstract
   * @param {Zone} otherZone - the zone to compare
   * @return {boolean}
   */
  equals(otherZone) {
    throw new ZoneIsAbstractError();
  }

  /**
   * Return whether this Zone is valid.
   * @abstract
   * @type {boolean}
   */
  get isValid() {
    throw new ZoneIsAbstractError();
  }
}

let singleton$1 = null;

/**
 * Represents the local zone for this JavaScript environment.
 * @implements {Zone}
 */
class SystemZone extends Zone {
  /**
   * Get a singleton instance of the local zone
   * @return {SystemZone}
   */
  static get instance() {
    if (singleton$1 === null) {
      singleton$1 = new SystemZone();
    }
    return singleton$1;
  }

  /** @override **/
  get type() {
    return "system";
  }

  /** @override **/
  get name() {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /** @override **/
  get isUniversal() {
    return false;
  }

  /** @override **/
  offsetName(ts, { format, locale }) {
    return parseZoneInfo(ts, format, locale);
  }

  /** @override **/
  formatOffset(ts, format) {
    return formatOffset(this.offset(ts), format);
  }

  /** @override **/
  offset(ts) {
    return -new Date(ts).getTimezoneOffset();
  }

  /** @override **/
  equals(otherZone) {
    return otherZone.type === "system";
  }

  /** @override **/
  get isValid() {
    return true;
  }
}

let dtfCache = {};
function makeDTF(zone) {
  if (!dtfCache[zone]) {
    dtfCache[zone] = new Intl.DateTimeFormat("en-US", {
      hour12: false,
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      era: "short",
    });
  }
  return dtfCache[zone];
}

const typeToPos = {
  year: 0,
  month: 1,
  day: 2,
  era: 3,
  hour: 4,
  minute: 5,
  second: 6,
};

function hackyOffset(dtf, date) {
  const formatted = dtf.format(date).replace(/\u200E/g, ""),
    parsed = /(\d+)\/(\d+)\/(\d+) (AD|BC),? (\d+):(\d+):(\d+)/.exec(formatted),
    [, fMonth, fDay, fYear, fadOrBc, fHour, fMinute, fSecond] = parsed;
  return [fYear, fMonth, fDay, fadOrBc, fHour, fMinute, fSecond];
}

function partsOffset(dtf, date) {
  const formatted = dtf.formatToParts(date);
  const filled = [];
  for (let i = 0; i < formatted.length; i++) {
    const { type, value } = formatted[i];
    const pos = typeToPos[type];

    if (type === "era") {
      filled[pos] = value;
    } else if (!isUndefined(pos)) {
      filled[pos] = parseInt(value, 10);
    }
  }
  return filled;
}

let ianaZoneCache = {};
/**
 * A zone identified by an IANA identifier, like America/New_York
 * @implements {Zone}
 */
class IANAZone extends Zone {
  /**
   * @param {string} name - Zone name
   * @return {IANAZone}
   */
  static create(name) {
    if (!ianaZoneCache[name]) {
      ianaZoneCache[name] = new IANAZone(name);
    }
    return ianaZoneCache[name];
  }

  /**
   * Reset local caches. Should only be necessary in testing scenarios.
   * @return {void}
   */
  static resetCache() {
    ianaZoneCache = {};
    dtfCache = {};
  }

  /**
   * Returns whether the provided string is a valid specifier. This only checks the string's format, not that the specifier identifies a known zone; see isValidZone for that.
   * @param {string} s - The string to check validity on
   * @example IANAZone.isValidSpecifier("America/New_York") //=> true
   * @example IANAZone.isValidSpecifier("Sport~~blorp") //=> false
   * @deprecated This method returns false for some valid IANA names. Use isValidZone instead.
   * @return {boolean}
   */
  static isValidSpecifier(s) {
    return this.isValidZone(s);
  }

  /**
   * Returns whether the provided string identifies a real zone
   * @param {string} zone - The string to check
   * @example IANAZone.isValidZone("America/New_York") //=> true
   * @example IANAZone.isValidZone("Fantasia/Castle") //=> false
   * @example IANAZone.isValidZone("Sport~~blorp") //=> false
   * @return {boolean}
   */
  static isValidZone(zone) {
    if (!zone) {
      return false;
    }
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: zone }).format();
      return true;
    } catch (e) {
      return false;
    }
  }

  constructor(name) {
    super();
    /** @private **/
    this.zoneName = name;
    /** @private **/
    this.valid = IANAZone.isValidZone(name);
  }

  /** @override **/
  get type() {
    return "iana";
  }

  /** @override **/
  get name() {
    return this.zoneName;
  }

  /** @override **/
  get isUniversal() {
    return false;
  }

  /** @override **/
  offsetName(ts, { format, locale }) {
    return parseZoneInfo(ts, format, locale, this.name);
  }

  /** @override **/
  formatOffset(ts, format) {
    return formatOffset(this.offset(ts), format);
  }

  /** @override **/
  offset(ts) {
    const date = new Date(ts);

    if (isNaN(date)) return NaN;

    const dtf = makeDTF(this.name);
    let [year, month, day, adOrBc, hour, minute, second] = dtf.formatToParts
      ? partsOffset(dtf, date)
      : hackyOffset(dtf, date);

    if (adOrBc === "BC") {
      year = -Math.abs(year) + 1;
    }

    // because we're using hour12 and https://bugs.chromium.org/p/chromium/issues/detail?id=1025564&can=2&q=%2224%3A00%22%20datetimeformat
    const adjustedHour = hour === 24 ? 0 : hour;

    const asUTC = objToLocalTS({
      year,
      month,
      day,
      hour: adjustedHour,
      minute,
      second,
      millisecond: 0,
    });

    let asTS = +date;
    const over = asTS % 1000;
    asTS -= over >= 0 ? over : 1000 + over;
    return (asUTC - asTS) / (60 * 1000);
  }

  /** @override **/
  equals(otherZone) {
    return otherZone.type === "iana" && otherZone.name === this.name;
  }

  /** @override **/
  get isValid() {
    return this.valid;
  }
}

let singleton = null;

/**
 * A zone with a fixed offset (meaning no DST)
 * @implements {Zone}
 */
class FixedOffsetZone extends Zone {
  /**
   * Get a singleton instance of UTC
   * @return {FixedOffsetZone}
   */
  static get utcInstance() {
    if (singleton === null) {
      singleton = new FixedOffsetZone(0);
    }
    return singleton;
  }

  /**
   * Get an instance with a specified offset
   * @param {number} offset - The offset in minutes
   * @return {FixedOffsetZone}
   */
  static instance(offset) {
    return offset === 0 ? FixedOffsetZone.utcInstance : new FixedOffsetZone(offset);
  }

  /**
   * Get an instance of FixedOffsetZone from a UTC offset string, like "UTC+6"
   * @param {string} s - The offset string to parse
   * @example FixedOffsetZone.parseSpecifier("UTC+6")
   * @example FixedOffsetZone.parseSpecifier("UTC+06")
   * @example FixedOffsetZone.parseSpecifier("UTC-6:00")
   * @return {FixedOffsetZone}
   */
  static parseSpecifier(s) {
    if (s) {
      const r = s.match(/^utc(?:([+-]\d{1,2})(?::(\d{2}))?)?$/i);
      if (r) {
        return new FixedOffsetZone(signedOffset(r[1], r[2]));
      }
    }
    return null;
  }

  constructor(offset) {
    super();
    /** @private **/
    this.fixed = offset;
  }

  /** @override **/
  get type() {
    return "fixed";
  }

  /** @override **/
  get name() {
    return this.fixed === 0 ? "UTC" : `UTC${formatOffset(this.fixed, "narrow")}`;
  }

  get ianaName() {
    if (this.fixed === 0) {
      return "Etc/UTC";
    } else {
      return `Etc/GMT${formatOffset(-this.fixed, "narrow")}`;
    }
  }

  /** @override **/
  offsetName() {
    return this.name;
  }

  /** @override **/
  formatOffset(ts, format) {
    return formatOffset(this.fixed, format);
  }

  /** @override **/
  get isUniversal() {
    return true;
  }

  /** @override **/
  offset() {
    return this.fixed;
  }

  /** @override **/
  equals(otherZone) {
    return otherZone.type === "fixed" && otherZone.fixed === this.fixed;
  }

  /** @override **/
  get isValid() {
    return true;
  }
}

/**
 * A zone that failed to parse. You should never need to instantiate this.
 * @implements {Zone}
 */
class InvalidZone extends Zone {
  constructor(zoneName) {
    super();
    /**  @private */
    this.zoneName = zoneName;
  }

  /** @override **/
  get type() {
    return "invalid";
  }

  /** @override **/
  get name() {
    return this.zoneName;
  }

  /** @override **/
  get isUniversal() {
    return false;
  }

  /** @override **/
  offsetName() {
    return null;
  }

  /** @override **/
  formatOffset() {
    return "";
  }

  /** @override **/
  offset() {
    return NaN;
  }

  /** @override **/
  equals() {
    return false;
  }

  /** @override **/
  get isValid() {
    return false;
  }
}

/**
 * @private
 */

function normalizeZone(input, defaultZone) {
  if (isUndefined(input) || input === null) {
    return defaultZone;
  } else if (input instanceof Zone) {
    return input;
  } else if (isString(input)) {
    const lowered = input.toLowerCase();
    if (lowered === "local" || lowered === "system") return defaultZone;
    else if (lowered === "utc" || lowered === "gmt") return FixedOffsetZone.utcInstance;
    else return FixedOffsetZone.parseSpecifier(lowered) || IANAZone.create(input);
  } else if (isNumber(input)) {
    return FixedOffsetZone.instance(input);
  } else if (typeof input === "object" && input.offset && typeof input.offset === "number") {
    // This is dumb, but the instanceof check above doesn't seem to really work
    // so we're duck checking it
    return input;
  } else {
    return new InvalidZone(input);
  }
}

let now = () => Date.now(),
  defaultZone = "system",
  defaultLocale = null,
  defaultNumberingSystem = null,
  defaultOutputCalendar = null,
  throwOnInvalid;

/**
 * Settings contains static getters and setters that control Luxon's overall behavior. Luxon is a simple library with few options, but the ones it does have live here.
 */
class Settings {
  /**
   * Get the callback for returning the current timestamp.
   * @type {function}
   */
  static get now() {
    return now;
  }

  /**
   * Set the callback for returning the current timestamp.
   * The function should return a number, which will be interpreted as an Epoch millisecond count
   * @type {function}
   * @example Settings.now = () => Date.now() + 3000 // pretend it is 3 seconds in the future
   * @example Settings.now = () => 0 // always pretend it's Jan 1, 1970 at midnight in UTC time
   */
  static set now(n) {
    now = n;
  }

  /**
   * Set the default time zone to create DateTimes in. Does not affect existing instances.
   * Use the value "system" to reset this value to the system's time zone.
   * @type {string}
   */
  static set defaultZone(zone) {
    defaultZone = zone;
  }

  /**
   * Get the default time zone object currently used to create DateTimes. Does not affect existing instances.
   * The default value is the system's time zone (the one set on the machine that runs this code).
   * @type {Zone}
   */
  static get defaultZone() {
    return normalizeZone(defaultZone, SystemZone.instance);
  }

  /**
   * Get the default locale to create DateTimes with. Does not affect existing instances.
   * @type {string}
   */
  static get defaultLocale() {
    return defaultLocale;
  }

  /**
   * Set the default locale to create DateTimes with. Does not affect existing instances.
   * @type {string}
   */
  static set defaultLocale(locale) {
    defaultLocale = locale;
  }

  /**
   * Get the default numbering system to create DateTimes with. Does not affect existing instances.
   * @type {string}
   */
  static get defaultNumberingSystem() {
    return defaultNumberingSystem;
  }

  /**
   * Set the default numbering system to create DateTimes with. Does not affect existing instances.
   * @type {string}
   */
  static set defaultNumberingSystem(numberingSystem) {
    defaultNumberingSystem = numberingSystem;
  }

  /**
   * Get the default output calendar to create DateTimes with. Does not affect existing instances.
   * @type {string}
   */
  static get defaultOutputCalendar() {
    return defaultOutputCalendar;
  }

  /**
   * Set the default output calendar to create DateTimes with. Does not affect existing instances.
   * @type {string}
   */
  static set defaultOutputCalendar(outputCalendar) {
    defaultOutputCalendar = outputCalendar;
  }

  /**
   * Get whether Luxon will throw when it encounters invalid DateTimes, Durations, or Intervals
   * @type {boolean}
   */
  static get throwOnInvalid() {
    return throwOnInvalid;
  }

  /**
   * Set whether Luxon will throw when it encounters invalid DateTimes, Durations, or Intervals
   * @type {boolean}
   */
  static set throwOnInvalid(t) {
    throwOnInvalid = t;
  }

  /**
   * Reset Luxon's global caches. Should only be necessary in testing scenarios.
   * @return {void}
   */
  static resetCaches() {
    Locale.resetCache();
    IANAZone.resetCache();
  }
}

// todo - remap caching

let intlLFCache = {};
function getCachedLF(locString, opts = {}) {
  const key = JSON.stringify([locString, opts]);
  let dtf = intlLFCache[key];
  if (!dtf) {
    dtf = new Intl.ListFormat(locString, opts);
    intlLFCache[key] = dtf;
  }
  return dtf;
}

let intlDTCache = {};
function getCachedDTF(locString, opts = {}) {
  const key = JSON.stringify([locString, opts]);
  let dtf = intlDTCache[key];
  if (!dtf) {
    dtf = new Intl.DateTimeFormat(locString, opts);
    intlDTCache[key] = dtf;
  }
  return dtf;
}

let intlNumCache = {};
function getCachedINF(locString, opts = {}) {
  const key = JSON.stringify([locString, opts]);
  let inf = intlNumCache[key];
  if (!inf) {
    inf = new Intl.NumberFormat(locString, opts);
    intlNumCache[key] = inf;
  }
  return inf;
}

let intlRelCache = {};
function getCachedRTF(locString, opts = {}) {
  const { base, ...cacheKeyOpts } = opts; // exclude `base` from the options
  const key = JSON.stringify([locString, cacheKeyOpts]);
  let inf = intlRelCache[key];
  if (!inf) {
    inf = new Intl.RelativeTimeFormat(locString, opts);
    intlRelCache[key] = inf;
  }
  return inf;
}

let sysLocaleCache = null;
function systemLocale() {
  if (sysLocaleCache) {
    return sysLocaleCache;
  } else {
    sysLocaleCache = new Intl.DateTimeFormat().resolvedOptions().locale;
    return sysLocaleCache;
  }
}

function parseLocaleString(localeStr) {
  // I really want to avoid writing a BCP 47 parser
  // see, e.g. https://github.com/wooorm/bcp-47
  // Instead, we'll do this:

  // a) if the string has no -u extensions, just leave it alone
  // b) if it does, use Intl to resolve everything
  // c) if Intl fails, try again without the -u

  const uIndex = localeStr.indexOf("-u-");
  if (uIndex === -1) {
    return [localeStr];
  } else {
    let options;
    const smaller = localeStr.substring(0, uIndex);
    try {
      options = getCachedDTF(localeStr).resolvedOptions();
    } catch (e) {
      options = getCachedDTF(smaller).resolvedOptions();
    }

    const { numberingSystem, calendar } = options;
    // return the smaller one so that we can append the calendar and numbering overrides to it
    return [smaller, numberingSystem, calendar];
  }
}

function intlConfigString(localeStr, numberingSystem, outputCalendar) {
  if (outputCalendar || numberingSystem) {
    localeStr += "-u";

    if (outputCalendar) {
      localeStr += `-ca-${outputCalendar}`;
    }

    if (numberingSystem) {
      localeStr += `-nu-${numberingSystem}`;
    }
    return localeStr;
  } else {
    return localeStr;
  }
}

function mapMonths(f) {
  const ms = [];
  for (let i = 1; i <= 12; i++) {
    const dt = DateTime.utc(2016, i, 1);
    ms.push(f(dt));
  }
  return ms;
}

function mapWeekdays(f) {
  const ms = [];
  for (let i = 1; i <= 7; i++) {
    const dt = DateTime.utc(2016, 11, 13 + i);
    ms.push(f(dt));
  }
  return ms;
}

function listStuff(loc, length, defaultOK, englishFn, intlFn) {
  const mode = loc.listingMode(defaultOK);

  if (mode === "error") {
    return null;
  } else if (mode === "en") {
    return englishFn(length);
  } else {
    return intlFn(length);
  }
}

function supportsFastNumbers(loc) {
  if (loc.numberingSystem && loc.numberingSystem !== "latn") {
    return false;
  } else {
    return (
      loc.numberingSystem === "latn" ||
      !loc.locale ||
      loc.locale.startsWith("en") ||
      new Intl.DateTimeFormat(loc.intl).resolvedOptions().numberingSystem === "latn"
    );
  }
}

/**
 * @private
 */

class PolyNumberFormatter {
  constructor(intl, forceSimple, opts) {
    this.padTo = opts.padTo || 0;
    this.floor = opts.floor || false;

    const { padTo, floor, ...otherOpts } = opts;

    if (!forceSimple || Object.keys(otherOpts).length > 0) {
      const intlOpts = { useGrouping: false, ...opts };
      if (opts.padTo > 0) intlOpts.minimumIntegerDigits = opts.padTo;
      this.inf = getCachedINF(intl, intlOpts);
    }
  }

  format(i) {
    if (this.inf) {
      const fixed = this.floor ? Math.floor(i) : i;
      return this.inf.format(fixed);
    } else {
      // to match the browser's numberformatter defaults
      const fixed = this.floor ? Math.floor(i) : roundTo(i, 3);
      return padStart(fixed, this.padTo);
    }
  }
}

/**
 * @private
 */

class PolyDateFormatter {
  constructor(dt, intl, opts) {
    this.opts = opts;

    let z;
    if (dt.zone.isUniversal) {
      // UTC-8 or Etc/UTC-8 are not part of tzdata, only Etc/GMT+8 and the like.
      // That is why fixed-offset TZ is set to that unless it is:
      // 1. Representing offset 0 when UTC is used to maintain previous behavior and does not become GMT.
      // 2. Unsupported by the browser:
      //    - some do not support Etc/
      //    - < Etc/GMT-14, > Etc/GMT+12, and 30-minute or 45-minute offsets are not part of tzdata
      const gmtOffset = -1 * (dt.offset / 60);
      const offsetZ = gmtOffset >= 0 ? `Etc/GMT+${gmtOffset}` : `Etc/GMT${gmtOffset}`;
      if (dt.offset !== 0 && IANAZone.create(offsetZ).valid) {
        z = offsetZ;
        this.dt = dt;
      } else {
        // Not all fixed-offset zones like Etc/+4:30 are present in tzdata.
        // So we have to make do. Two cases:
        // 1. The format options tell us to show the zone. We can't do that, so the best
        // we can do is format the date in UTC.
        // 2. The format options don't tell us to show the zone. Then we can adjust them
        // the time and tell the formatter to show it to us in UTC, so that the time is right
        // and the bad zone doesn't show up.
        z = "UTC";
        if (opts.timeZoneName) {
          this.dt = dt;
        } else {
          this.dt = dt.offset === 0 ? dt : DateTime.fromMillis(dt.ts + dt.offset * 60 * 1000);
        }
      }
    } else if (dt.zone.type === "system") {
      this.dt = dt;
    } else {
      this.dt = dt;
      z = dt.zone.name;
    }

    const intlOpts = { ...this.opts };
    if (z) {
      intlOpts.timeZone = z;
    }
    this.dtf = getCachedDTF(intl, intlOpts);
  }

  format() {
    return this.dtf.format(this.dt.toJSDate());
  }

  formatToParts() {
    return this.dtf.formatToParts(this.dt.toJSDate());
  }

  resolvedOptions() {
    return this.dtf.resolvedOptions();
  }
}

/**
 * @private
 */
class PolyRelFormatter {
  constructor(intl, isEnglish, opts) {
    this.opts = { style: "long", ...opts };
    if (!isEnglish && hasRelative()) {
      this.rtf = getCachedRTF(intl, opts);
    }
  }

  format(count, unit) {
    if (this.rtf) {
      return this.rtf.format(count, unit);
    } else {
      return formatRelativeTime(unit, count, this.opts.numeric, this.opts.style !== "long");
    }
  }

  formatToParts(count, unit) {
    if (this.rtf) {
      return this.rtf.formatToParts(count, unit);
    } else {
      return [];
    }
  }
}

/**
 * @private
 */

class Locale {
  static fromOpts(opts) {
    return Locale.create(opts.locale, opts.numberingSystem, opts.outputCalendar, opts.defaultToEN);
  }

  static create(locale, numberingSystem, outputCalendar, defaultToEN = false) {
    const specifiedLocale = locale || Settings.defaultLocale;
    // the system locale is useful for human readable strings but annoying for parsing/formatting known formats
    const localeR = specifiedLocale || (defaultToEN ? "en-US" : systemLocale());
    const numberingSystemR = numberingSystem || Settings.defaultNumberingSystem;
    const outputCalendarR = outputCalendar || Settings.defaultOutputCalendar;
    return new Locale(localeR, numberingSystemR, outputCalendarR, specifiedLocale);
  }

  static resetCache() {
    sysLocaleCache = null;
    intlDTCache = {};
    intlNumCache = {};
    intlRelCache = {};
  }

  static fromObject({ locale, numberingSystem, outputCalendar } = {}) {
    return Locale.create(locale, numberingSystem, outputCalendar);
  }

  constructor(locale, numbering, outputCalendar, specifiedLocale) {
    const [parsedLocale, parsedNumberingSystem, parsedOutputCalendar] = parseLocaleString(locale);

    this.locale = parsedLocale;
    this.numberingSystem = numbering || parsedNumberingSystem || null;
    this.outputCalendar = outputCalendar || parsedOutputCalendar || null;
    this.intl = intlConfigString(this.locale, this.numberingSystem, this.outputCalendar);

    this.weekdaysCache = { format: {}, standalone: {} };
    this.monthsCache = { format: {}, standalone: {} };
    this.meridiemCache = null;
    this.eraCache = {};

    this.specifiedLocale = specifiedLocale;
    this.fastNumbersCached = null;
  }

  get fastNumbers() {
    if (this.fastNumbersCached == null) {
      this.fastNumbersCached = supportsFastNumbers(this);
    }

    return this.fastNumbersCached;
  }

  listingMode() {
    const isActuallyEn = this.isEnglish();
    const hasNoWeirdness =
      (this.numberingSystem === null || this.numberingSystem === "latn") &&
      (this.outputCalendar === null || this.outputCalendar === "gregory");
    return isActuallyEn && hasNoWeirdness ? "en" : "intl";
  }

  clone(alts) {
    if (!alts || Object.getOwnPropertyNames(alts).length === 0) {
      return this;
    } else {
      return Locale.create(
        alts.locale || this.specifiedLocale,
        alts.numberingSystem || this.numberingSystem,
        alts.outputCalendar || this.outputCalendar,
        alts.defaultToEN || false
      );
    }
  }

  redefaultToEN(alts = {}) {
    return this.clone({ ...alts, defaultToEN: true });
  }

  redefaultToSystem(alts = {}) {
    return this.clone({ ...alts, defaultToEN: false });
  }

  months(length, format = false, defaultOK = true) {
    return listStuff(this, length, defaultOK, months, () => {
      const intl = format ? { month: length, day: "numeric" } : { month: length },
        formatStr = format ? "format" : "standalone";
      if (!this.monthsCache[formatStr][length]) {
        this.monthsCache[formatStr][length] = mapMonths((dt) => this.extract(dt, intl, "month"));
      }
      return this.monthsCache[formatStr][length];
    });
  }

  weekdays(length, format = false, defaultOK = true) {
    return listStuff(this, length, defaultOK, weekdays, () => {
      const intl = format
          ? { weekday: length, year: "numeric", month: "long", day: "numeric" }
          : { weekday: length },
        formatStr = format ? "format" : "standalone";
      if (!this.weekdaysCache[formatStr][length]) {
        this.weekdaysCache[formatStr][length] = mapWeekdays((dt) =>
          this.extract(dt, intl, "weekday")
        );
      }
      return this.weekdaysCache[formatStr][length];
    });
  }

  meridiems(defaultOK = true) {
    return listStuff(
      this,
      undefined,
      defaultOK,
      () => meridiems,
      () => {
        // In theory there could be aribitrary day periods. We're gonna assume there are exactly two
        // for AM and PM. This is probably wrong, but it's makes parsing way easier.
        if (!this.meridiemCache) {
          const intl = { hour: "numeric", hourCycle: "h12" };
          this.meridiemCache = [DateTime.utc(2016, 11, 13, 9), DateTime.utc(2016, 11, 13, 19)].map(
            (dt) => this.extract(dt, intl, "dayperiod")
          );
        }

        return this.meridiemCache;
      }
    );
  }

  eras(length, defaultOK = true) {
    return listStuff(this, length, defaultOK, eras, () => {
      const intl = { era: length };

      // This is problematic. Different calendars are going to define eras totally differently. What I need is the minimum set of dates
      // to definitely enumerate them.
      if (!this.eraCache[length]) {
        this.eraCache[length] = [DateTime.utc(-40, 1, 1), DateTime.utc(2017, 1, 1)].map((dt) =>
          this.extract(dt, intl, "era")
        );
      }

      return this.eraCache[length];
    });
  }

  extract(dt, intlOpts, field) {
    const df = this.dtFormatter(dt, intlOpts),
      results = df.formatToParts(),
      matching = results.find((m) => m.type.toLowerCase() === field);
    return matching ? matching.value : null;
  }

  numberFormatter(opts = {}) {
    // this forcesimple option is never used (the only caller short-circuits on it, but it seems safer to leave)
    // (in contrast, the rest of the condition is used heavily)
    return new PolyNumberFormatter(this.intl, opts.forceSimple || this.fastNumbers, opts);
  }

  dtFormatter(dt, intlOpts = {}) {
    return new PolyDateFormatter(dt, this.intl, intlOpts);
  }

  relFormatter(opts = {}) {
    return new PolyRelFormatter(this.intl, this.isEnglish(), opts);
  }

  listFormatter(opts = {}) {
    return getCachedLF(this.intl, opts);
  }

  isEnglish() {
    return (
      this.locale === "en" ||
      this.locale.toLowerCase() === "en-us" ||
      new Intl.DateTimeFormat(this.intl).resolvedOptions().locale.startsWith("en-us")
    );
  }

  equals(other) {
    return (
      this.locale === other.locale &&
      this.numberingSystem === other.numberingSystem &&
      this.outputCalendar === other.outputCalendar
    );
  }
}

/*
 * This file handles parsing for well-specified formats. Here's how it works:
 * Two things go into parsing: a regex to match with and an extractor to take apart the groups in the match.
 * An extractor is just a function that takes a regex match array and returns a { year: ..., month: ... } object
 * parse() does the work of executing the regex and applying the extractor. It takes multiple regex/extractor pairs to try in sequence.
 * Extractors can take a "cursor" representing the offset in the match to look at. This makes it easy to combine extractors.
 * combineExtractors() does the work of combining them, keeping track of the cursor through multiple extractions.
 * Some extractions are super dumb and simpleParse and fromStrings help DRY them.
 */

function combineRegexes(...regexes) {
  const full = regexes.reduce((f, r) => f + r.source, "");
  return RegExp(`^${full}$`);
}

function combineExtractors(...extractors) {
  return m =>
    extractors
      .reduce(
        ([mergedVals, mergedZone, cursor], ex) => {
          const [val, zone, next] = ex(m, cursor);
          return [{ ...mergedVals, ...val }, zone || mergedZone, next];
        },
        [{}, null, 1]
      )
      .slice(0, 2);
}

function parse(s, ...patterns) {
  if (s == null) {
    return [null, null];
  }

  for (const [regex, extractor] of patterns) {
    const m = regex.exec(s);
    if (m) {
      return extractor(m);
    }
  }
  return [null, null];
}

function simpleParse(...keys) {
  return (match, cursor) => {
    const ret = {};
    let i;

    for (i = 0; i < keys.length; i++) {
      ret[keys[i]] = parseInteger(match[cursor + i]);
    }
    return [ret, null, cursor + i];
  };
}

// ISO and SQL parsing
const offsetRegex = /(?:(Z)|([+-]\d\d)(?::?(\d\d))?)/;
const isoExtendedZone = `(?:${offsetRegex.source}?(?:\\[(${ianaRegex.source})\\])?)?`;
const isoTimeBaseRegex = /(\d\d)(?::?(\d\d)(?::?(\d\d)(?:[.,](\d{1,30}))?)?)?/;
const isoTimeRegex = RegExp(`${isoTimeBaseRegex.source}${isoExtendedZone}`);
const isoTimeExtensionRegex = RegExp(`(?:T${isoTimeRegex.source})?`);
const isoYmdRegex = /([+-]\d{6}|\d{4})(?:-?(\d\d)(?:-?(\d\d))?)?/;
const isoWeekRegex = /(\d{4})-?W(\d\d)(?:-?(\d))?/;
const isoOrdinalRegex = /(\d{4})-?(\d{3})/;
const extractISOWeekData = simpleParse("weekYear", "weekNumber", "weekDay");
const extractISOOrdinalData = simpleParse("year", "ordinal");
const sqlYmdRegex = /(\d{4})-(\d\d)-(\d\d)/; // dumbed-down version of the ISO one
const sqlTimeRegex = RegExp(
  `${isoTimeBaseRegex.source} ?(?:${offsetRegex.source}|(${ianaRegex.source}))?`
);
const sqlTimeExtensionRegex = RegExp(`(?: ${sqlTimeRegex.source})?`);

function int(match, pos, fallback) {
  const m = match[pos];
  return isUndefined(m) ? fallback : parseInteger(m);
}

function extractISOYmd(match, cursor) {
  const item = {
    year: int(match, cursor),
    month: int(match, cursor + 1, 1),
    day: int(match, cursor + 2, 1)
  };

  return [item, null, cursor + 3];
}

function extractISOTime(match, cursor) {
  const item = {
    hours: int(match, cursor, 0),
    minutes: int(match, cursor + 1, 0),
    seconds: int(match, cursor + 2, 0),
    milliseconds: parseMillis(match[cursor + 3])
  };

  return [item, null, cursor + 4];
}

function extractISOOffset(match, cursor) {
  const local = !match[cursor] && !match[cursor + 1],
    fullOffset = signedOffset(match[cursor + 1], match[cursor + 2]),
    zone = local ? null : FixedOffsetZone.instance(fullOffset);
  return [{}, zone, cursor + 3];
}

function extractIANAZone(match, cursor) {
  const zone = match[cursor] ? IANAZone.create(match[cursor]) : null;
  return [{}, zone, cursor + 1];
}

// ISO time parsing

const isoTimeOnly = RegExp(`^T?${isoTimeBaseRegex.source}$`);

// ISO duration parsing

const isoDuration = /^-?P(?:(?:(-?\d{1,20}(?:\.\d{1,20})?)Y)?(?:(-?\d{1,20}(?:\.\d{1,20})?)M)?(?:(-?\d{1,20}(?:\.\d{1,20})?)W)?(?:(-?\d{1,20}(?:\.\d{1,20})?)D)?(?:T(?:(-?\d{1,20}(?:\.\d{1,20})?)H)?(?:(-?\d{1,20}(?:\.\d{1,20})?)M)?(?:(-?\d{1,20})(?:[.,](-?\d{1,20}))?S)?)?)$/;

function extractISODuration(match) {
  const [
    s,
    yearStr,
    monthStr,
    weekStr,
    dayStr,
    hourStr,
    minuteStr,
    secondStr,
    millisecondsStr
  ] = match;

  const hasNegativePrefix = s[0] === "-";
  const negativeSeconds = secondStr && secondStr[0] === "-";

  const maybeNegate = (num, force = false) =>
    num !== undefined && (force || (num && hasNegativePrefix)) ? -num : num;

  return [
    {
      years: maybeNegate(parseFloating(yearStr)),
      months: maybeNegate(parseFloating(monthStr)),
      weeks: maybeNegate(parseFloating(weekStr)),
      days: maybeNegate(parseFloating(dayStr)),
      hours: maybeNegate(parseFloating(hourStr)),
      minutes: maybeNegate(parseFloating(minuteStr)),
      seconds: maybeNegate(parseFloating(secondStr), secondStr === "-0"),
      milliseconds: maybeNegate(parseMillis(millisecondsStr), negativeSeconds)
    }
  ];
}

// These are a little braindead. EDT *should* tell us that we're in, say, America/New_York
// and not just that we're in -240 *right now*. But since I don't think these are used that often
// I'm just going to ignore that
const obsOffsets = {
  GMT: 0,
  EDT: -4 * 60,
  EST: -5 * 60,
  CDT: -5 * 60,
  CST: -6 * 60,
  MDT: -6 * 60,
  MST: -7 * 60,
  PDT: -7 * 60,
  PST: -8 * 60
};

function fromStrings(weekdayStr, yearStr, monthStr, dayStr, hourStr, minuteStr, secondStr) {
  const result = {
    year: yearStr.length === 2 ? untruncateYear(parseInteger(yearStr)) : parseInteger(yearStr),
    month: monthsShort.indexOf(monthStr) + 1,
    day: parseInteger(dayStr),
    hour: parseInteger(hourStr),
    minute: parseInteger(minuteStr)
  };

  if (secondStr) result.second = parseInteger(secondStr);
  if (weekdayStr) {
    result.weekday =
      weekdayStr.length > 3
        ? weekdaysLong.indexOf(weekdayStr) + 1
        : weekdaysShort.indexOf(weekdayStr) + 1;
  }

  return result;
}

// RFC 2822/5322
const rfc2822 = /^(?:(Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s)?(\d{1,2})\s(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s(\d{2,4})\s(\d\d):(\d\d)(?::(\d\d))?\s(?:(UT|GMT|[ECMP][SD]T)|([Zz])|(?:([+-]\d\d)(\d\d)))$/;

function extractRFC2822(match) {
  const [
      ,
      weekdayStr,
      dayStr,
      monthStr,
      yearStr,
      hourStr,
      minuteStr,
      secondStr,
      obsOffset,
      milOffset,
      offHourStr,
      offMinuteStr
    ] = match,
    result = fromStrings(weekdayStr, yearStr, monthStr, dayStr, hourStr, minuteStr, secondStr);

  let offset;
  if (obsOffset) {
    offset = obsOffsets[obsOffset];
  } else if (milOffset) {
    offset = 0;
  } else {
    offset = signedOffset(offHourStr, offMinuteStr);
  }

  return [result, new FixedOffsetZone(offset)];
}

function preprocessRFC2822(s) {
  // Remove comments and folding whitespace and replace multiple-spaces with a single space
  return s
    .replace(/\([^()]*\)|[\n\t]/g, " ")
    .replace(/(\s\s+)/g, " ")
    .trim();
}

// http date

const rfc1123 = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun), (\d\d) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{4}) (\d\d):(\d\d):(\d\d) GMT$/,
  rfc850 = /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday), (\d\d)-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d\d) (\d\d):(\d\d):(\d\d) GMT$/,
  ascii = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) ( \d|\d\d) (\d\d):(\d\d):(\d\d) (\d{4})$/;

function extractRFC1123Or850(match) {
  const [, weekdayStr, dayStr, monthStr, yearStr, hourStr, minuteStr, secondStr] = match,
    result = fromStrings(weekdayStr, yearStr, monthStr, dayStr, hourStr, minuteStr, secondStr);
  return [result, FixedOffsetZone.utcInstance];
}

function extractASCII(match) {
  const [, weekdayStr, monthStr, dayStr, hourStr, minuteStr, secondStr, yearStr] = match,
    result = fromStrings(weekdayStr, yearStr, monthStr, dayStr, hourStr, minuteStr, secondStr);
  return [result, FixedOffsetZone.utcInstance];
}

const isoYmdWithTimeExtensionRegex = combineRegexes(isoYmdRegex, isoTimeExtensionRegex);
const isoWeekWithTimeExtensionRegex = combineRegexes(isoWeekRegex, isoTimeExtensionRegex);
const isoOrdinalWithTimeExtensionRegex = combineRegexes(isoOrdinalRegex, isoTimeExtensionRegex);
const isoTimeCombinedRegex = combineRegexes(isoTimeRegex);

const extractISOYmdTimeAndOffset = combineExtractors(
  extractISOYmd,
  extractISOTime,
  extractISOOffset,
  extractIANAZone
);
const extractISOWeekTimeAndOffset = combineExtractors(
  extractISOWeekData,
  extractISOTime,
  extractISOOffset,
  extractIANAZone
);
const extractISOOrdinalDateAndTime = combineExtractors(
  extractISOOrdinalData,
  extractISOTime,
  extractISOOffset,
  extractIANAZone
);
const extractISOTimeAndOffset = combineExtractors(
  extractISOTime,
  extractISOOffset,
  extractIANAZone
);

/*
 * @private
 */

function parseISODate(s) {
  return parse(
    s,
    [isoYmdWithTimeExtensionRegex, extractISOYmdTimeAndOffset],
    [isoWeekWithTimeExtensionRegex, extractISOWeekTimeAndOffset],
    [isoOrdinalWithTimeExtensionRegex, extractISOOrdinalDateAndTime],
    [isoTimeCombinedRegex, extractISOTimeAndOffset]
  );
}

function parseRFC2822Date(s) {
  return parse(preprocessRFC2822(s), [rfc2822, extractRFC2822]);
}

function parseHTTPDate(s) {
  return parse(
    s,
    [rfc1123, extractRFC1123Or850],
    [rfc850, extractRFC1123Or850],
    [ascii, extractASCII]
  );
}

function parseISODuration(s) {
  return parse(s, [isoDuration, extractISODuration]);
}

const extractISOTimeOnly = combineExtractors(extractISOTime);

function parseISOTimeOnly(s) {
  return parse(s, [isoTimeOnly, extractISOTimeOnly]);
}

const sqlYmdWithTimeExtensionRegex = combineRegexes(sqlYmdRegex, sqlTimeExtensionRegex);
const sqlTimeCombinedRegex = combineRegexes(sqlTimeRegex);

const extractISOTimeOffsetAndIANAZone = combineExtractors(
  extractISOTime,
  extractISOOffset,
  extractIANAZone
);

function parseSQL(s) {
  return parse(
    s,
    [sqlYmdWithTimeExtensionRegex, extractISOYmdTimeAndOffset],
    [sqlTimeCombinedRegex, extractISOTimeOffsetAndIANAZone]
  );
}

const INVALID$2 = "Invalid Duration";

// unit conversion constants
const lowOrderMatrix = {
    weeks: {
      days: 7,
      hours: 7 * 24,
      minutes: 7 * 24 * 60,
      seconds: 7 * 24 * 60 * 60,
      milliseconds: 7 * 24 * 60 * 60 * 1000,
    },
    days: {
      hours: 24,
      minutes: 24 * 60,
      seconds: 24 * 60 * 60,
      milliseconds: 24 * 60 * 60 * 1000,
    },
    hours: { minutes: 60, seconds: 60 * 60, milliseconds: 60 * 60 * 1000 },
    minutes: { seconds: 60, milliseconds: 60 * 1000 },
    seconds: { milliseconds: 1000 },
  },
  casualMatrix = {
    years: {
      quarters: 4,
      months: 12,
      weeks: 52,
      days: 365,
      hours: 365 * 24,
      minutes: 365 * 24 * 60,
      seconds: 365 * 24 * 60 * 60,
      milliseconds: 365 * 24 * 60 * 60 * 1000,
    },
    quarters: {
      months: 3,
      weeks: 13,
      days: 91,
      hours: 91 * 24,
      minutes: 91 * 24 * 60,
      seconds: 91 * 24 * 60 * 60,
      milliseconds: 91 * 24 * 60 * 60 * 1000,
    },
    months: {
      weeks: 4,
      days: 30,
      hours: 30 * 24,
      minutes: 30 * 24 * 60,
      seconds: 30 * 24 * 60 * 60,
      milliseconds: 30 * 24 * 60 * 60 * 1000,
    },

    ...lowOrderMatrix,
  },
  daysInYearAccurate = 146097.0 / 400,
  daysInMonthAccurate = 146097.0 / 4800,
  accurateMatrix = {
    years: {
      quarters: 4,
      months: 12,
      weeks: daysInYearAccurate / 7,
      days: daysInYearAccurate,
      hours: daysInYearAccurate * 24,
      minutes: daysInYearAccurate * 24 * 60,
      seconds: daysInYearAccurate * 24 * 60 * 60,
      milliseconds: daysInYearAccurate * 24 * 60 * 60 * 1000,
    },
    quarters: {
      months: 3,
      weeks: daysInYearAccurate / 28,
      days: daysInYearAccurate / 4,
      hours: (daysInYearAccurate * 24) / 4,
      minutes: (daysInYearAccurate * 24 * 60) / 4,
      seconds: (daysInYearAccurate * 24 * 60 * 60) / 4,
      milliseconds: (daysInYearAccurate * 24 * 60 * 60 * 1000) / 4,
    },
    months: {
      weeks: daysInMonthAccurate / 7,
      days: daysInMonthAccurate,
      hours: daysInMonthAccurate * 24,
      minutes: daysInMonthAccurate * 24 * 60,
      seconds: daysInMonthAccurate * 24 * 60 * 60,
      milliseconds: daysInMonthAccurate * 24 * 60 * 60 * 1000,
    },
    ...lowOrderMatrix,
  };

// units ordered by size
const orderedUnits$1 = [
  "years",
  "quarters",
  "months",
  "weeks",
  "days",
  "hours",
  "minutes",
  "seconds",
  "milliseconds",
];

const reverseUnits = orderedUnits$1.slice(0).reverse();

// clone really means "create another instance just like this one, but with these changes"
function clone$1(dur, alts, clear = false) {
  // deep merge for vals
  const conf = {
    values: clear ? alts.values : { ...dur.values, ...(alts.values || {}) },
    loc: dur.loc.clone(alts.loc),
    conversionAccuracy: alts.conversionAccuracy || dur.conversionAccuracy,
  };
  return new Duration(conf);
}

function antiTrunc(n) {
  return n < 0 ? Math.floor(n) : Math.ceil(n);
}

// NB: mutates parameters
function convert(matrix, fromMap, fromUnit, toMap, toUnit) {
  const conv = matrix[toUnit][fromUnit],
    raw = fromMap[fromUnit] / conv,
    sameSign = Math.sign(raw) === Math.sign(toMap[toUnit]),
    // ok, so this is wild, but see the matrix in the tests
    added =
      !sameSign && toMap[toUnit] !== 0 && Math.abs(raw) <= 1 ? antiTrunc(raw) : Math.trunc(raw);
  toMap[toUnit] += added;
  fromMap[fromUnit] -= added * conv;
}

// NB: mutates parameters
function normalizeValues(matrix, vals) {
  reverseUnits.reduce((previous, current) => {
    if (!isUndefined(vals[current])) {
      if (previous) {
        convert(matrix, vals, previous, vals, current);
      }
      return current;
    } else {
      return previous;
    }
  }, null);
}

/**
 * A Duration object represents a period of time, like "2 months" or "1 day, 1 hour". Conceptually, it's just a map of units to their quantities, accompanied by some additional configuration and methods for creating, parsing, interrogating, transforming, and formatting them. They can be used on their own or in conjunction with other Luxon types; for example, you can use {@link DateTime#plus} to add a Duration object to a DateTime, producing another DateTime.
 *
 * Here is a brief overview of commonly used methods and getters in Duration:
 *
 * * **Creation** To create a Duration, use {@link Duration#fromMillis}, {@link Duration#fromObject}, or {@link Duration#fromISO}.
 * * **Unit values** See the {@link Duration#years}, {@link Duration#months}, {@link Duration#weeks}, {@link Duration#days}, {@link Duration#hours}, {@link Duration#minutes}, {@link Duration#seconds}, {@link Duration#milliseconds} accessors.
 * * **Configuration** See  {@link Duration#locale} and {@link Duration#numberingSystem} accessors.
 * * **Transformation** To create new Durations out of old ones use {@link Duration#plus}, {@link Duration#minus}, {@link Duration#normalize}, {@link Duration#set}, {@link Duration#reconfigure}, {@link Duration#shiftTo}, and {@link Duration#negate}.
 * * **Output** To convert the Duration into other representations, see {@link Duration#as}, {@link Duration#toISO}, {@link Duration#toFormat}, and {@link Duration#toJSON}
 *
 * There's are more methods documented below. In addition, for more information on subtler topics like internationalization and validity, see the external documentation.
 */
class Duration {
  /**
   * @private
   */
  constructor(config) {
    const accurate = config.conversionAccuracy === "longterm" || false;
    /**
     * @access private
     */
    this.values = config.values;
    /**
     * @access private
     */
    this.loc = config.loc || Locale.create();
    /**
     * @access private
     */
    this.conversionAccuracy = accurate ? "longterm" : "casual";
    /**
     * @access private
     */
    this.invalid = config.invalid || null;
    /**
     * @access private
     */
    this.matrix = accurate ? accurateMatrix : casualMatrix;
    /**
     * @access private
     */
    this.isLuxonDuration = true;
  }

  /**
   * Create Duration from a number of milliseconds.
   * @param {number} count of milliseconds
   * @param {Object} opts - options for parsing
   * @param {string} [opts.locale='en-US'] - the locale to use
   * @param {string} opts.numberingSystem - the numbering system to use
   * @param {string} [opts.conversionAccuracy='casual'] - the conversion system to use
   * @return {Duration}
   */
  static fromMillis(count, opts) {
    return Duration.fromObject({ milliseconds: count }, opts);
  }

  /**
   * Create a Duration from a JavaScript object with keys like 'years' and 'hours'.
   * If this object is empty then a zero milliseconds duration is returned.
   * @param {Object} obj - the object to create the DateTime from
   * @param {number} obj.years
   * @param {number} obj.quarters
   * @param {number} obj.months
   * @param {number} obj.weeks
   * @param {number} obj.days
   * @param {number} obj.hours
   * @param {number} obj.minutes
   * @param {number} obj.seconds
   * @param {number} obj.milliseconds
   * @param {Object} [opts=[]] - options for creating this Duration
   * @param {string} [opts.locale='en-US'] - the locale to use
   * @param {string} opts.numberingSystem - the numbering system to use
   * @param {string} [opts.conversionAccuracy='casual'] - the conversion system to use
   * @return {Duration}
   */
  static fromObject(obj, opts = {}) {
    if (obj == null || typeof obj !== "object") {
      throw new InvalidArgumentError(
        `Duration.fromObject: argument expected to be an object, got ${
          obj === null ? "null" : typeof obj
        }`
      );
    }

    return new Duration({
      values: normalizeObject(obj, Duration.normalizeUnit),
      loc: Locale.fromObject(opts),
      conversionAccuracy: opts.conversionAccuracy,
    });
  }

  /**
   * Create a Duration from DurationLike.
   *
   * @param {Object | number | Duration} durationLike
   * One of:
   * - object with keys like 'years' and 'hours'.
   * - number representing milliseconds
   * - Duration instance
   * @return {Duration}
   */
  static fromDurationLike(durationLike) {
    if (isNumber(durationLike)) {
      return Duration.fromMillis(durationLike);
    } else if (Duration.isDuration(durationLike)) {
      return durationLike;
    } else if (typeof durationLike === "object") {
      return Duration.fromObject(durationLike);
    } else {
      throw new InvalidArgumentError(
        `Unknown duration argument ${durationLike} of type ${typeof durationLike}`
      );
    }
  }

  /**
   * Create a Duration from an ISO 8601 duration string.
   * @param {string} text - text to parse
   * @param {Object} opts - options for parsing
   * @param {string} [opts.locale='en-US'] - the locale to use
   * @param {string} opts.numberingSystem - the numbering system to use
   * @param {string} [opts.conversionAccuracy='casual'] - the conversion system to use
   * @see https://en.wikipedia.org/wiki/ISO_8601#Durations
   * @example Duration.fromISO('P3Y6M1W4DT12H30M5S').toObject() //=> { years: 3, months: 6, weeks: 1, days: 4, hours: 12, minutes: 30, seconds: 5 }
   * @example Duration.fromISO('PT23H').toObject() //=> { hours: 23 }
   * @example Duration.fromISO('P5Y3M').toObject() //=> { years: 5, months: 3 }
   * @return {Duration}
   */
  static fromISO(text, opts) {
    const [parsed] = parseISODuration(text);
    if (parsed) {
      return Duration.fromObject(parsed, opts);
    } else {
      return Duration.invalid("unparsable", `the input "${text}" can't be parsed as ISO 8601`);
    }
  }

  /**
   * Create a Duration from an ISO 8601 time string.
   * @param {string} text - text to parse
   * @param {Object} opts - options for parsing
   * @param {string} [opts.locale='en-US'] - the locale to use
   * @param {string} opts.numberingSystem - the numbering system to use
   * @param {string} [opts.conversionAccuracy='casual'] - the conversion system to use
   * @see https://en.wikipedia.org/wiki/ISO_8601#Times
   * @example Duration.fromISOTime('11:22:33.444').toObject() //=> { hours: 11, minutes: 22, seconds: 33, milliseconds: 444 }
   * @example Duration.fromISOTime('11:00').toObject() //=> { hours: 11, minutes: 0, seconds: 0 }
   * @example Duration.fromISOTime('T11:00').toObject() //=> { hours: 11, minutes: 0, seconds: 0 }
   * @example Duration.fromISOTime('1100').toObject() //=> { hours: 11, minutes: 0, seconds: 0 }
   * @example Duration.fromISOTime('T1100').toObject() //=> { hours: 11, minutes: 0, seconds: 0 }
   * @return {Duration}
   */
  static fromISOTime(text, opts) {
    const [parsed] = parseISOTimeOnly(text);
    if (parsed) {
      return Duration.fromObject(parsed, opts);
    } else {
      return Duration.invalid("unparsable", `the input "${text}" can't be parsed as ISO 8601`);
    }
  }

  /**
   * Create an invalid Duration.
   * @param {string} reason - simple string of why this datetime is invalid. Should not contain parameters or anything else data-dependent
   * @param {string} [explanation=null] - longer explanation, may include parameters and other useful debugging information
   * @return {Duration}
   */
  static invalid(reason, explanation = null) {
    if (!reason) {
      throw new InvalidArgumentError("need to specify a reason the Duration is invalid");
    }

    const invalid = reason instanceof Invalid ? reason : new Invalid(reason, explanation);

    if (Settings.throwOnInvalid) {
      throw new InvalidDurationError(invalid);
    } else {
      return new Duration({ invalid });
    }
  }

  /**
   * @private
   */
  static normalizeUnit(unit) {
    const normalized = {
      year: "years",
      years: "years",
      quarter: "quarters",
      quarters: "quarters",
      month: "months",
      months: "months",
      week: "weeks",
      weeks: "weeks",
      day: "days",
      days: "days",
      hour: "hours",
      hours: "hours",
      minute: "minutes",
      minutes: "minutes",
      second: "seconds",
      seconds: "seconds",
      millisecond: "milliseconds",
      milliseconds: "milliseconds",
    }[unit ? unit.toLowerCase() : unit];

    if (!normalized) throw new InvalidUnitError(unit);

    return normalized;
  }

  /**
   * Check if an object is a Duration. Works across context boundaries
   * @param {object} o
   * @return {boolean}
   */
  static isDuration(o) {
    return (o && o.isLuxonDuration) || false;
  }

  /**
   * Get  the locale of a Duration, such 'en-GB'
   * @type {string}
   */
  get locale() {
    return this.isValid ? this.loc.locale : null;
  }

  /**
   * Get the numbering system of a Duration, such 'beng'. The numbering system is used when formatting the Duration
   *
   * @type {string}
   */
  get numberingSystem() {
    return this.isValid ? this.loc.numberingSystem : null;
  }

  /**
   * Returns a string representation of this Duration formatted according to the specified format string. You may use these tokens:
   * * `S` for milliseconds
   * * `s` for seconds
   * * `m` for minutes
   * * `h` for hours
   * * `d` for days
   * * `w` for weeks
   * * `M` for months
   * * `y` for years
   * Notes:
   * * Add padding by repeating the token, e.g. "yy" pads the years to two digits, "hhhh" pads the hours out to four digits
   * * Tokens can be escaped by wrapping with single quotes.
   * * The duration will be converted to the set of units in the format string using {@link Duration#shiftTo} and the Durations's conversion accuracy setting.
   * @param {string} fmt - the format string
   * @param {Object} opts - options
   * @param {boolean} [opts.floor=true] - floor numerical values
   * @example Duration.fromObject({ years: 1, days: 6, seconds: 2 }).toFormat("y d s") //=> "1 6 2"
   * @example Duration.fromObject({ years: 1, days: 6, seconds: 2 }).toFormat("yy dd sss") //=> "01 06 002"
   * @example Duration.fromObject({ years: 1, days: 6, seconds: 2 }).toFormat("M S") //=> "12 518402000"
   * @return {string}
   */
  toFormat(fmt, opts = {}) {
    // reverse-compat since 1.2; we always round down now, never up, and we do it by default
    const fmtOpts = {
      ...opts,
      floor: opts.round !== false && opts.floor !== false,
    };
    return this.isValid
      ? Formatter.create(this.loc, fmtOpts).formatDurationFromString(this, fmt)
      : INVALID$2;
  }

  /**
   * Returns a string representation of a Duration with all units included.
   * To modify its behavior use the `listStyle` and any Intl.NumberFormat option, though `unitDisplay` is especially relevant.
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
   * @param opts - On option object to override the formatting. Accepts the same keys as the options parameter of the native `Int.NumberFormat` constructor, as well as `listStyle`.
   * @example
   * ```js
   * var dur = Duration.fromObject({ days: 1, hours: 5, minutes: 6 })
   * dur.toHuman() //=> '1 day, 5 hours, 6 minutes'
   * dur.toHuman({ listStyle: "long" }) //=> '1 day, 5 hours, and 6 minutes'
   * dur.toHuman({ unitDisplay: "short" }) //=> '1 day, 5 hr, 6 min'
   * ```
   */
  toHuman(opts = {}) {
    const l = orderedUnits$1
      .map((unit) => {
        const val = this.values[unit];
        if (isUndefined(val)) {
          return null;
        }
        return this.loc
          .numberFormatter({ style: "unit", unitDisplay: "long", ...opts, unit: unit.slice(0, -1) })
          .format(val);
      })
      .filter((n) => n);

    return this.loc
      .listFormatter({ type: "conjunction", style: opts.listStyle || "narrow", ...opts })
      .format(l);
  }

  /**
   * Returns a JavaScript object with this Duration's values.
   * @example Duration.fromObject({ years: 1, days: 6, seconds: 2 }).toObject() //=> { years: 1, days: 6, seconds: 2 }
   * @return {Object}
   */
  toObject() {
    if (!this.isValid) return {};
    return { ...this.values };
  }

  /**
   * Returns an ISO 8601-compliant string representation of this Duration.
   * @see https://en.wikipedia.org/wiki/ISO_8601#Durations
   * @example Duration.fromObject({ years: 3, seconds: 45 }).toISO() //=> 'P3YT45S'
   * @example Duration.fromObject({ months: 4, seconds: 45 }).toISO() //=> 'P4MT45S'
   * @example Duration.fromObject({ months: 5 }).toISO() //=> 'P5M'
   * @example Duration.fromObject({ minutes: 5 }).toISO() //=> 'PT5M'
   * @example Duration.fromObject({ milliseconds: 6 }).toISO() //=> 'PT0.006S'
   * @return {string}
   */
  toISO() {
    // we could use the formatter, but this is an easier way to get the minimum string
    if (!this.isValid) return null;

    let s = "P";
    if (this.years !== 0) s += this.years + "Y";
    if (this.months !== 0 || this.quarters !== 0) s += this.months + this.quarters * 3 + "M";
    if (this.weeks !== 0) s += this.weeks + "W";
    if (this.days !== 0) s += this.days + "D";
    if (this.hours !== 0 || this.minutes !== 0 || this.seconds !== 0 || this.milliseconds !== 0)
      s += "T";
    if (this.hours !== 0) s += this.hours + "H";
    if (this.minutes !== 0) s += this.minutes + "M";
    if (this.seconds !== 0 || this.milliseconds !== 0)
      // this will handle "floating point madness" by removing extra decimal places
      // https://stackoverflow.com/questions/588004/is-floating-point-math-broken
      s += roundTo(this.seconds + this.milliseconds / 1000, 3) + "S";
    if (s === "P") s += "T0S";
    return s;
  }

  /**
   * Returns an ISO 8601-compliant string representation of this Duration, formatted as a time of day.
   * Note that this will return null if the duration is invalid, negative, or equal to or greater than 24 hours.
   * @see https://en.wikipedia.org/wiki/ISO_8601#Times
   * @param {Object} opts - options
   * @param {boolean} [opts.suppressMilliseconds=false] - exclude milliseconds from the format if they're 0
   * @param {boolean} [opts.suppressSeconds=false] - exclude seconds from the format if they're 0
   * @param {boolean} [opts.includePrefix=false] - include the `T` prefix
   * @param {string} [opts.format='extended'] - choose between the basic and extended format
   * @example Duration.fromObject({ hours: 11 }).toISOTime() //=> '11:00:00.000'
   * @example Duration.fromObject({ hours: 11 }).toISOTime({ suppressMilliseconds: true }) //=> '11:00:00'
   * @example Duration.fromObject({ hours: 11 }).toISOTime({ suppressSeconds: true }) //=> '11:00'
   * @example Duration.fromObject({ hours: 11 }).toISOTime({ includePrefix: true }) //=> 'T11:00:00.000'
   * @example Duration.fromObject({ hours: 11 }).toISOTime({ format: 'basic' }) //=> '110000.000'
   * @return {string}
   */
  toISOTime(opts = {}) {
    if (!this.isValid) return null;

    const millis = this.toMillis();
    if (millis < 0 || millis >= 86400000) return null;

    opts = {
      suppressMilliseconds: false,
      suppressSeconds: false,
      includePrefix: false,
      format: "extended",
      ...opts,
    };

    const value = this.shiftTo("hours", "minutes", "seconds", "milliseconds");

    let fmt = opts.format === "basic" ? "hhmm" : "hh:mm";

    if (!opts.suppressSeconds || value.seconds !== 0 || value.milliseconds !== 0) {
      fmt += opts.format === "basic" ? "ss" : ":ss";
      if (!opts.suppressMilliseconds || value.milliseconds !== 0) {
        fmt += ".SSS";
      }
    }

    let str = value.toFormat(fmt);

    if (opts.includePrefix) {
      str = "T" + str;
    }

    return str;
  }

  /**
   * Returns an ISO 8601 representation of this Duration appropriate for use in JSON.
   * @return {string}
   */
  toJSON() {
    return this.toISO();
  }

  /**
   * Returns an ISO 8601 representation of this Duration appropriate for use in debugging.
   * @return {string}
   */
  toString() {
    return this.toISO();
  }

  /**
   * Returns an milliseconds value of this Duration.
   * @return {number}
   */
  toMillis() {
    return this.as("milliseconds");
  }

  /**
   * Returns an milliseconds value of this Duration. Alias of {@link toMillis}
   * @return {number}
   */
  valueOf() {
    return this.toMillis();
  }

  /**
   * Make this Duration longer by the specified amount. Return a newly-constructed Duration.
   * @param {Duration|Object|number} duration - The amount to add. Either a Luxon Duration, a number of milliseconds, the object argument to Duration.fromObject()
   * @return {Duration}
   */
  plus(duration) {
    if (!this.isValid) return this;

    const dur = Duration.fromDurationLike(duration),
      result = {};

    for (const k of orderedUnits$1) {
      if (hasOwnProperty(dur.values, k) || hasOwnProperty(this.values, k)) {
        result[k] = dur.get(k) + this.get(k);
      }
    }

    return clone$1(this, { values: result }, true);
  }

  /**
   * Make this Duration shorter by the specified amount. Return a newly-constructed Duration.
   * @param {Duration|Object|number} duration - The amount to subtract. Either a Luxon Duration, a number of milliseconds, the object argument to Duration.fromObject()
   * @return {Duration}
   */
  minus(duration) {
    if (!this.isValid) return this;

    const dur = Duration.fromDurationLike(duration);
    return this.plus(dur.negate());
  }

  /**
   * Scale this Duration by the specified amount. Return a newly-constructed Duration.
   * @param {function} fn - The function to apply to each unit. Arity is 1 or 2: the value of the unit and, optionally, the unit name. Must return a number.
   * @example Duration.fromObject({ hours: 1, minutes: 30 }).mapUnits(x => x * 2) //=> { hours: 2, minutes: 60 }
   * @example Duration.fromObject({ hours: 1, minutes: 30 }).mapUnits((x, u) => u === "hour" ? x * 2 : x) //=> { hours: 2, minutes: 30 }
   * @return {Duration}
   */
  mapUnits(fn) {
    if (!this.isValid) return this;
    const result = {};
    for (const k of Object.keys(this.values)) {
      result[k] = asNumber(fn(this.values[k], k));
    }
    return clone$1(this, { values: result }, true);
  }

  /**
   * Get the value of unit.
   * @param {string} unit - a unit such as 'minute' or 'day'
   * @example Duration.fromObject({years: 2, days: 3}).get('years') //=> 2
   * @example Duration.fromObject({years: 2, days: 3}).get('months') //=> 0
   * @example Duration.fromObject({years: 2, days: 3}).get('days') //=> 3
   * @return {number}
   */
  get(unit) {
    return this[Duration.normalizeUnit(unit)];
  }

  /**
   * "Set" the values of specified units. Return a newly-constructed Duration.
   * @param {Object} values - a mapping of units to numbers
   * @example dur.set({ years: 2017 })
   * @example dur.set({ hours: 8, minutes: 30 })
   * @return {Duration}
   */
  set(values) {
    if (!this.isValid) return this;

    const mixed = { ...this.values, ...normalizeObject(values, Duration.normalizeUnit) };
    return clone$1(this, { values: mixed });
  }

  /**
   * "Set" the locale and/or numberingSystem.  Returns a newly-constructed Duration.
   * @example dur.reconfigure({ locale: 'en-GB' })
   * @return {Duration}
   */
  reconfigure({ locale, numberingSystem, conversionAccuracy } = {}) {
    const loc = this.loc.clone({ locale, numberingSystem }),
      opts = { loc };

    if (conversionAccuracy) {
      opts.conversionAccuracy = conversionAccuracy;
    }

    return clone$1(this, opts);
  }

  /**
   * Return the length of the duration in the specified unit.
   * @param {string} unit - a unit such as 'minutes' or 'days'
   * @example Duration.fromObject({years: 1}).as('days') //=> 365
   * @example Duration.fromObject({years: 1}).as('months') //=> 12
   * @example Duration.fromObject({hours: 60}).as('days') //=> 2.5
   * @return {number}
   */
  as(unit) {
    return this.isValid ? this.shiftTo(unit).get(unit) : NaN;
  }

  /**
   * Reduce this Duration to its canonical representation in its current units.
   * @example Duration.fromObject({ years: 2, days: 5000 }).normalize().toObject() //=> { years: 15, days: 255 }
   * @example Duration.fromObject({ hours: 12, minutes: -45 }).normalize().toObject() //=> { hours: 11, minutes: 15 }
   * @return {Duration}
   */
  normalize() {
    if (!this.isValid) return this;
    const vals = this.toObject();
    normalizeValues(this.matrix, vals);
    return clone$1(this, { values: vals }, true);
  }

  /**
   * Convert this Duration into its representation in a different set of units.
   * @example Duration.fromObject({ hours: 1, seconds: 30 }).shiftTo('minutes', 'milliseconds').toObject() //=> { minutes: 60, milliseconds: 30000 }
   * @return {Duration}
   */
  shiftTo(...units) {
    if (!this.isValid) return this;

    if (units.length === 0) {
      return this;
    }

    units = units.map((u) => Duration.normalizeUnit(u));

    const built = {},
      accumulated = {},
      vals = this.toObject();
    let lastUnit;

    for (const k of orderedUnits$1) {
      if (units.indexOf(k) >= 0) {
        lastUnit = k;

        let own = 0;

        // anything we haven't boiled down yet should get boiled to this unit
        for (const ak in accumulated) {
          own += this.matrix[ak][k] * accumulated[ak];
          accumulated[ak] = 0;
        }

        // plus anything that's already in this unit
        if (isNumber(vals[k])) {
          own += vals[k];
        }

        const i = Math.trunc(own);
        built[k] = i;
        accumulated[k] = (own * 1000 - i * 1000) / 1000;

        // plus anything further down the chain that should be rolled up in to this
        for (const down in vals) {
          if (orderedUnits$1.indexOf(down) > orderedUnits$1.indexOf(k)) {
            convert(this.matrix, vals, down, built, k);
          }
        }
        // otherwise, keep it in the wings to boil it later
      } else if (isNumber(vals[k])) {
        accumulated[k] = vals[k];
      }
    }

    // anything leftover becomes the decimal for the last unit
    // lastUnit must be defined since units is not empty
    for (const key in accumulated) {
      if (accumulated[key] !== 0) {
        built[lastUnit] +=
          key === lastUnit ? accumulated[key] : accumulated[key] / this.matrix[lastUnit][key];
      }
    }

    return clone$1(this, { values: built }, true).normalize();
  }

  /**
   * Return the negative of this Duration.
   * @example Duration.fromObject({ hours: 1, seconds: 30 }).negate().toObject() //=> { hours: -1, seconds: -30 }
   * @return {Duration}
   */
  negate() {
    if (!this.isValid) return this;
    const negated = {};
    for (const k of Object.keys(this.values)) {
      negated[k] = this.values[k] === 0 ? 0 : -this.values[k];
    }
    return clone$1(this, { values: negated }, true);
  }

  /**
   * Get the years.
   * @type {number}
   */
  get years() {
    return this.isValid ? this.values.years || 0 : NaN;
  }

  /**
   * Get the quarters.
   * @type {number}
   */
  get quarters() {
    return this.isValid ? this.values.quarters || 0 : NaN;
  }

  /**
   * Get the months.
   * @type {number}
   */
  get months() {
    return this.isValid ? this.values.months || 0 : NaN;
  }

  /**
   * Get the weeks
   * @type {number}
   */
  get weeks() {
    return this.isValid ? this.values.weeks || 0 : NaN;
  }

  /**
   * Get the days.
   * @type {number}
   */
  get days() {
    return this.isValid ? this.values.days || 0 : NaN;
  }

  /**
   * Get the hours.
   * @type {number}
   */
  get hours() {
    return this.isValid ? this.values.hours || 0 : NaN;
  }

  /**
   * Get the minutes.
   * @type {number}
   */
  get minutes() {
    return this.isValid ? this.values.minutes || 0 : NaN;
  }

  /**
   * Get the seconds.
   * @return {number}
   */
  get seconds() {
    return this.isValid ? this.values.seconds || 0 : NaN;
  }

  /**
   * Get the milliseconds.
   * @return {number}
   */
  get milliseconds() {
    return this.isValid ? this.values.milliseconds || 0 : NaN;
  }

  /**
   * Returns whether the Duration is invalid. Invalid durations are returned by diff operations
   * on invalid DateTimes or Intervals.
   * @return {boolean}
   */
  get isValid() {
    return this.invalid === null;
  }

  /**
   * Returns an error code if this Duration became invalid, or null if the Duration is valid
   * @return {string}
   */
  get invalidReason() {
    return this.invalid ? this.invalid.reason : null;
  }

  /**
   * Returns an explanation of why this Duration became invalid, or null if the Duration is valid
   * @type {string}
   */
  get invalidExplanation() {
    return this.invalid ? this.invalid.explanation : null;
  }

  /**
   * Equality check
   * Two Durations are equal iff they have the same units and the same values for each unit.
   * @param {Duration} other
   * @return {boolean}
   */
  equals(other) {
    if (!this.isValid || !other.isValid) {
      return false;
    }

    if (!this.loc.equals(other.loc)) {
      return false;
    }

    function eq(v1, v2) {
      // Consider 0 and undefined as equal
      if (v1 === undefined || v1 === 0) return v2 === undefined || v2 === 0;
      return v1 === v2;
    }

    for (const u of orderedUnits$1) {
      if (!eq(this.values[u], other.values[u])) {
        return false;
      }
    }
    return true;
  }
}

const INVALID$1 = "Invalid Interval";

// checks if the start is equal to or before the end
function validateStartEnd(start, end) {
  if (!start || !start.isValid) {
    return Interval.invalid("missing or invalid start");
  } else if (!end || !end.isValid) {
    return Interval.invalid("missing or invalid end");
  } else if (end < start) {
    return Interval.invalid(
      "end before start",
      `The end of an interval must be after its start, but you had start=${start.toISO()} and end=${end.toISO()}`
    );
  } else {
    return null;
  }
}

/**
 * An Interval object represents a half-open interval of time, where each endpoint is a {@link DateTime}. Conceptually, it's a container for those two endpoints, accompanied by methods for creating, parsing, interrogating, comparing, transforming, and formatting them.
 *
 * Here is a brief overview of the most commonly used methods and getters in Interval:
 *
 * * **Creation** To create an Interval, use {@link Interval#fromDateTimes}, {@link Interval#after}, {@link Interval#before}, or {@link Interval#fromISO}.
 * * **Accessors** Use {@link Interval#start} and {@link Interval#end} to get the start and end.
 * * **Interrogation** To analyze the Interval, use {@link Interval#count}, {@link Interval#length}, {@link Interval#hasSame}, {@link Interval#contains}, {@link Interval#isAfter}, or {@link Interval#isBefore}.
 * * **Transformation** To create other Intervals out of this one, use {@link Interval#set}, {@link Interval#splitAt}, {@link Interval#splitBy}, {@link Interval#divideEqually}, {@link Interval#merge}, {@link Interval#xor}, {@link Interval#union}, {@link Interval#intersection}, or {@link Interval#difference}.
 * * **Comparison** To compare this Interval to another one, use {@link Interval#equals}, {@link Interval#overlaps}, {@link Interval#abutsStart}, {@link Interval#abutsEnd}, {@link Interval#engulfs}
 * * **Output** To convert the Interval into other representations, see {@link Interval#toString}, {@link Interval#toISO}, {@link Interval#toISODate}, {@link Interval#toISOTime}, {@link Interval#toFormat}, and {@link Interval#toDuration}.
 */
class Interval {
  /**
   * @private
   */
  constructor(config) {
    /**
     * @access private
     */
    this.s = config.start;
    /**
     * @access private
     */
    this.e = config.end;
    /**
     * @access private
     */
    this.invalid = config.invalid || null;
    /**
     * @access private
     */
    this.isLuxonInterval = true;
  }

  /**
   * Create an invalid Interval.
   * @param {string} reason - simple string of why this Interval is invalid. Should not contain parameters or anything else data-dependent
   * @param {string} [explanation=null] - longer explanation, may include parameters and other useful debugging information
   * @return {Interval}
   */
  static invalid(reason, explanation = null) {
    if (!reason) {
      throw new InvalidArgumentError("need to specify a reason the Interval is invalid");
    }

    const invalid = reason instanceof Invalid ? reason : new Invalid(reason, explanation);

    if (Settings.throwOnInvalid) {
      throw new InvalidIntervalError(invalid);
    } else {
      return new Interval({ invalid });
    }
  }

  /**
   * Create an Interval from a start DateTime and an end DateTime. Inclusive of the start but not the end.
   * @param {DateTime|Date|Object} start
   * @param {DateTime|Date|Object} end
   * @return {Interval}
   */
  static fromDateTimes(start, end) {
    const builtStart = friendlyDateTime(start),
      builtEnd = friendlyDateTime(end);

    const validateError = validateStartEnd(builtStart, builtEnd);

    if (validateError == null) {
      return new Interval({
        start: builtStart,
        end: builtEnd,
      });
    } else {
      return validateError;
    }
  }

  /**
   * Create an Interval from a start DateTime and a Duration to extend to.
   * @param {DateTime|Date|Object} start
   * @param {Duration|Object|number} duration - the length of the Interval.
   * @return {Interval}
   */
  static after(start, duration) {
    const dur = Duration.fromDurationLike(duration),
      dt = friendlyDateTime(start);
    return Interval.fromDateTimes(dt, dt.plus(dur));
  }

  /**
   * Create an Interval from an end DateTime and a Duration to extend backwards to.
   * @param {DateTime|Date|Object} end
   * @param {Duration|Object|number} duration - the length of the Interval.
   * @return {Interval}
   */
  static before(end, duration) {
    const dur = Duration.fromDurationLike(duration),
      dt = friendlyDateTime(end);
    return Interval.fromDateTimes(dt.minus(dur), dt);
  }

  /**
   * Create an Interval from an ISO 8601 string.
   * Accepts `<start>/<end>`, `<start>/<duration>`, and `<duration>/<end>` formats.
   * @param {string} text - the ISO string to parse
   * @param {Object} [opts] - options to pass {@link DateTime#fromISO} and optionally {@link Duration#fromISO}
   * @see https://en.wikipedia.org/wiki/ISO_8601#Time_intervals
   * @return {Interval}
   */
  static fromISO(text, opts) {
    const [s, e] = (text || "").split("/", 2);
    if (s && e) {
      let start, startIsValid;
      try {
        start = DateTime.fromISO(s, opts);
        startIsValid = start.isValid;
      } catch (e) {
        startIsValid = false;
      }

      let end, endIsValid;
      try {
        end = DateTime.fromISO(e, opts);
        endIsValid = end.isValid;
      } catch (e) {
        endIsValid = false;
      }

      if (startIsValid && endIsValid) {
        return Interval.fromDateTimes(start, end);
      }

      if (startIsValid) {
        const dur = Duration.fromISO(e, opts);
        if (dur.isValid) {
          return Interval.after(start, dur);
        }
      } else if (endIsValid) {
        const dur = Duration.fromISO(s, opts);
        if (dur.isValid) {
          return Interval.before(end, dur);
        }
      }
    }
    return Interval.invalid("unparsable", `the input "${text}" can't be parsed as ISO 8601`);
  }

  /**
   * Check if an object is an Interval. Works across context boundaries
   * @param {object} o
   * @return {boolean}
   */
  static isInterval(o) {
    return (o && o.isLuxonInterval) || false;
  }

  /**
   * Returns the start of the Interval
   * @type {DateTime}
   */
  get start() {
    return this.isValid ? this.s : null;
  }

  /**
   * Returns the end of the Interval
   * @type {DateTime}
   */
  get end() {
    return this.isValid ? this.e : null;
  }

  /**
   * Returns whether this Interval's end is at least its start, meaning that the Interval isn't 'backwards'.
   * @type {boolean}
   */
  get isValid() {
    return this.invalidReason === null;
  }

  /**
   * Returns an error code if this Interval is invalid, or null if the Interval is valid
   * @type {string}
   */
  get invalidReason() {
    return this.invalid ? this.invalid.reason : null;
  }

  /**
   * Returns an explanation of why this Interval became invalid, or null if the Interval is valid
   * @type {string}
   */
  get invalidExplanation() {
    return this.invalid ? this.invalid.explanation : null;
  }

  /**
   * Returns the length of the Interval in the specified unit.
   * @param {string} unit - the unit (such as 'hours' or 'days') to return the length in.
   * @return {number}
   */
  length(unit = "milliseconds") {
    return this.isValid ? this.toDuration(...[unit]).get(unit) : NaN;
  }

  /**
   * Returns the count of minutes, hours, days, months, or years included in the Interval, even in part.
   * Unlike {@link Interval#length} this counts sections of the calendar, not periods of time, e.g. specifying 'day'
   * asks 'what dates are included in this interval?', not 'how many days long is this interval?'
   * @param {string} [unit='milliseconds'] - the unit of time to count.
   * @return {number}
   */
  count(unit = "milliseconds") {
    if (!this.isValid) return NaN;
    const start = this.start.startOf(unit),
      end = this.end.startOf(unit);
    return Math.floor(end.diff(start, unit).get(unit)) + 1;
  }

  /**
   * Returns whether this Interval's start and end are both in the same unit of time
   * @param {string} unit - the unit of time to check sameness on
   * @return {boolean}
   */
  hasSame(unit) {
    return this.isValid ? this.isEmpty() || this.e.minus(1).hasSame(this.s, unit) : false;
  }

  /**
   * Return whether this Interval has the same start and end DateTimes.
   * @return {boolean}
   */
  isEmpty() {
    return this.s.valueOf() === this.e.valueOf();
  }

  /**
   * Return whether this Interval's start is after the specified DateTime.
   * @param {DateTime} dateTime
   * @return {boolean}
   */
  isAfter(dateTime) {
    if (!this.isValid) return false;
    return this.s > dateTime;
  }

  /**
   * Return whether this Interval's end is before the specified DateTime.
   * @param {DateTime} dateTime
   * @return {boolean}
   */
  isBefore(dateTime) {
    if (!this.isValid) return false;
    return this.e <= dateTime;
  }

  /**
   * Return whether this Interval contains the specified DateTime.
   * @param {DateTime} dateTime
   * @return {boolean}
   */
  contains(dateTime) {
    if (!this.isValid) return false;
    return this.s <= dateTime && this.e > dateTime;
  }

  /**
   * "Sets" the start and/or end dates. Returns a newly-constructed Interval.
   * @param {Object} values - the values to set
   * @param {DateTime} values.start - the starting DateTime
   * @param {DateTime} values.end - the ending DateTime
   * @return {Interval}
   */
  set({ start, end } = {}) {
    if (!this.isValid) return this;
    return Interval.fromDateTimes(start || this.s, end || this.e);
  }

  /**
   * Split this Interval at each of the specified DateTimes
   * @param {...DateTime} dateTimes - the unit of time to count.
   * @return {Array}
   */
  splitAt(...dateTimes) {
    if (!this.isValid) return [];
    const sorted = dateTimes
        .map(friendlyDateTime)
        .filter((d) => this.contains(d))
        .sort(),
      results = [];
    let { s } = this,
      i = 0;

    while (s < this.e) {
      const added = sorted[i] || this.e,
        next = +added > +this.e ? this.e : added;
      results.push(Interval.fromDateTimes(s, next));
      s = next;
      i += 1;
    }

    return results;
  }

  /**
   * Split this Interval into smaller Intervals, each of the specified length.
   * Left over time is grouped into a smaller interval
   * @param {Duration|Object|number} duration - The length of each resulting interval.
   * @return {Array}
   */
  splitBy(duration) {
    const dur = Duration.fromDurationLike(duration);

    if (!this.isValid || !dur.isValid || dur.as("milliseconds") === 0) {
      return [];
    }

    let { s } = this,
      idx = 1,
      next;

    const results = [];
    while (s < this.e) {
      const added = this.start.plus(dur.mapUnits((x) => x * idx));
      next = +added > +this.e ? this.e : added;
      results.push(Interval.fromDateTimes(s, next));
      s = next;
      idx += 1;
    }

    return results;
  }

  /**
   * Split this Interval into the specified number of smaller intervals.
   * @param {number} numberOfParts - The number of Intervals to divide the Interval into.
   * @return {Array}
   */
  divideEqually(numberOfParts) {
    if (!this.isValid) return [];
    return this.splitBy(this.length() / numberOfParts).slice(0, numberOfParts);
  }

  /**
   * Return whether this Interval overlaps with the specified Interval
   * @param {Interval} other
   * @return {boolean}
   */
  overlaps(other) {
    return this.e > other.s && this.s < other.e;
  }

  /**
   * Return whether this Interval's end is adjacent to the specified Interval's start.
   * @param {Interval} other
   * @return {boolean}
   */
  abutsStart(other) {
    if (!this.isValid) return false;
    return +this.e === +other.s;
  }

  /**
   * Return whether this Interval's start is adjacent to the specified Interval's end.
   * @param {Interval} other
   * @return {boolean}
   */
  abutsEnd(other) {
    if (!this.isValid) return false;
    return +other.e === +this.s;
  }

  /**
   * Return whether this Interval engulfs the start and end of the specified Interval.
   * @param {Interval} other
   * @return {boolean}
   */
  engulfs(other) {
    if (!this.isValid) return false;
    return this.s <= other.s && this.e >= other.e;
  }

  /**
   * Return whether this Interval has the same start and end as the specified Interval.
   * @param {Interval} other
   * @return {boolean}
   */
  equals(other) {
    if (!this.isValid || !other.isValid) {
      return false;
    }

    return this.s.equals(other.s) && this.e.equals(other.e);
  }

  /**
   * Return an Interval representing the intersection of this Interval and the specified Interval.
   * Specifically, the resulting Interval has the maximum start time and the minimum end time of the two Intervals.
   * Returns null if the intersection is empty, meaning, the intervals don't intersect.
   * @param {Interval} other
   * @return {Interval}
   */
  intersection(other) {
    if (!this.isValid) return this;
    const s = this.s > other.s ? this.s : other.s,
      e = this.e < other.e ? this.e : other.e;

    if (s >= e) {
      return null;
    } else {
      return Interval.fromDateTimes(s, e);
    }
  }

  /**
   * Return an Interval representing the union of this Interval and the specified Interval.
   * Specifically, the resulting Interval has the minimum start time and the maximum end time of the two Intervals.
   * @param {Interval} other
   * @return {Interval}
   */
  union(other) {
    if (!this.isValid) return this;
    const s = this.s < other.s ? this.s : other.s,
      e = this.e > other.e ? this.e : other.e;
    return Interval.fromDateTimes(s, e);
  }

  /**
   * Merge an array of Intervals into a equivalent minimal set of Intervals.
   * Combines overlapping and adjacent Intervals.
   * @param {Array} intervals
   * @return {Array}
   */
  static merge(intervals) {
    const [found, final] = intervals
      .sort((a, b) => a.s - b.s)
      .reduce(
        ([sofar, current], item) => {
          if (!current) {
            return [sofar, item];
          } else if (current.overlaps(item) || current.abutsStart(item)) {
            return [sofar, current.union(item)];
          } else {
            return [sofar.concat([current]), item];
          }
        },
        [[], null]
      );
    if (final) {
      found.push(final);
    }
    return found;
  }

  /**
   * Return an array of Intervals representing the spans of time that only appear in one of the specified Intervals.
   * @param {Array} intervals
   * @return {Array}
   */
  static xor(intervals) {
    let start = null,
      currentCount = 0;
    const results = [],
      ends = intervals.map((i) => [
        { time: i.s, type: "s" },
        { time: i.e, type: "e" },
      ]),
      flattened = Array.prototype.concat(...ends),
      arr = flattened.sort((a, b) => a.time - b.time);

    for (const i of arr) {
      currentCount += i.type === "s" ? 1 : -1;

      if (currentCount === 1) {
        start = i.time;
      } else {
        if (start && +start !== +i.time) {
          results.push(Interval.fromDateTimes(start, i.time));
        }

        start = null;
      }
    }

    return Interval.merge(results);
  }

  /**
   * Return an Interval representing the span of time in this Interval that doesn't overlap with any of the specified Intervals.
   * @param {...Interval} intervals
   * @return {Array}
   */
  difference(...intervals) {
    return Interval.xor([this].concat(intervals))
      .map((i) => this.intersection(i))
      .filter((i) => i && !i.isEmpty());
  }

  /**
   * Returns a string representation of this Interval appropriate for debugging.
   * @return {string}
   */
  toString() {
    if (!this.isValid) return INVALID$1;
    return `[${this.s.toISO()} – ${this.e.toISO()})`;
  }

  /**
   * Returns an ISO 8601-compliant string representation of this Interval.
   * @see https://en.wikipedia.org/wiki/ISO_8601#Time_intervals
   * @param {Object} opts - The same options as {@link DateTime#toISO}
   * @return {string}
   */
  toISO(opts) {
    if (!this.isValid) return INVALID$1;
    return `${this.s.toISO(opts)}/${this.e.toISO(opts)}`;
  }

  /**
   * Returns an ISO 8601-compliant string representation of date of this Interval.
   * The time components are ignored.
   * @see https://en.wikipedia.org/wiki/ISO_8601#Time_intervals
   * @return {string}
   */
  toISODate() {
    if (!this.isValid) return INVALID$1;
    return `${this.s.toISODate()}/${this.e.toISODate()}`;
  }

  /**
   * Returns an ISO 8601-compliant string representation of time of this Interval.
   * The date components are ignored.
   * @see https://en.wikipedia.org/wiki/ISO_8601#Time_intervals
   * @param {Object} opts - The same options as {@link DateTime#toISO}
   * @return {string}
   */
  toISOTime(opts) {
    if (!this.isValid) return INVALID$1;
    return `${this.s.toISOTime(opts)}/${this.e.toISOTime(opts)}`;
  }

  /**
   * Returns a string representation of this Interval formatted according to the specified format string.
   * @param {string} dateFormat - the format string. This string formats the start and end time. See {@link DateTime#toFormat} for details.
   * @param {Object} opts - options
   * @param {string} [opts.separator =  ' – '] - a separator to place between the start and end representations
   * @return {string}
   */
  toFormat(dateFormat, { separator = " – " } = {}) {
    if (!this.isValid) return INVALID$1;
    return `${this.s.toFormat(dateFormat)}${separator}${this.e.toFormat(dateFormat)}`;
  }

  /**
   * Return a Duration representing the time spanned by this interval.
   * @param {string|string[]} [unit=['milliseconds']] - the unit or units (such as 'hours' or 'days') to include in the duration.
   * @param {Object} opts - options that affect the creation of the Duration
   * @param {string} [opts.conversionAccuracy='casual'] - the conversion system to use
   * @example Interval.fromDateTimes(dt1, dt2).toDuration().toObject() //=> { milliseconds: 88489257 }
   * @example Interval.fromDateTimes(dt1, dt2).toDuration('days').toObject() //=> { days: 1.0241812152777778 }
   * @example Interval.fromDateTimes(dt1, dt2).toDuration(['hours', 'minutes']).toObject() //=> { hours: 24, minutes: 34.82095 }
   * @example Interval.fromDateTimes(dt1, dt2).toDuration(['hours', 'minutes', 'seconds']).toObject() //=> { hours: 24, minutes: 34, seconds: 49.257 }
   * @example Interval.fromDateTimes(dt1, dt2).toDuration('seconds').toObject() //=> { seconds: 88489.257 }
   * @return {Duration}
   */
  toDuration(unit, opts) {
    if (!this.isValid) {
      return Duration.invalid(this.invalidReason);
    }
    return this.e.diff(this.s, unit, opts);
  }

  /**
   * Run mapFn on the interval start and end, returning a new Interval from the resulting DateTimes
   * @param {function} mapFn
   * @return {Interval}
   * @example Interval.fromDateTimes(dt1, dt2).mapEndpoints(endpoint => endpoint.toUTC())
   * @example Interval.fromDateTimes(dt1, dt2).mapEndpoints(endpoint => endpoint.plus({ hours: 2 }))
   */
  mapEndpoints(mapFn) {
    return Interval.fromDateTimes(mapFn(this.s), mapFn(this.e));
  }
}

/**
 * The Info class contains static methods for retrieving general time and date related data. For example, it has methods for finding out if a time zone has a DST, for listing the months in any supported locale, and for discovering which of Luxon features are available in the current environment.
 */
class Info {
  /**
   * Return whether the specified zone contains a DST.
   * @param {string|Zone} [zone='local'] - Zone to check. Defaults to the environment's local zone.
   * @return {boolean}
   */
  static hasDST(zone = Settings.defaultZone) {
    const proto = DateTime.now().setZone(zone).set({ month: 12 });

    return !zone.isUniversal && proto.offset !== proto.set({ month: 6 }).offset;
  }

  /**
   * Return whether the specified zone is a valid IANA specifier.
   * @param {string} zone - Zone to check
   * @return {boolean}
   */
  static isValidIANAZone(zone) {
    return IANAZone.isValidZone(zone);
  }

  /**
   * Converts the input into a {@link Zone} instance.
   *
   * * If `input` is already a Zone instance, it is returned unchanged.
   * * If `input` is a string containing a valid time zone name, a Zone instance
   *   with that name is returned.
   * * If `input` is a string that doesn't refer to a known time zone, a Zone
   *   instance with {@link Zone#isValid} == false is returned.
   * * If `input is a number, a Zone instance with the specified fixed offset
   *   in minutes is returned.
   * * If `input` is `null` or `undefined`, the default zone is returned.
   * @param {string|Zone|number} [input] - the value to be converted
   * @return {Zone}
   */
  static normalizeZone(input) {
    return normalizeZone(input, Settings.defaultZone);
  }

  /**
   * Return an array of standalone month names.
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
   * @param {string} [length='long'] - the length of the month representation, such as "numeric", "2-digit", "narrow", "short", "long"
   * @param {Object} opts - options
   * @param {string} [opts.locale] - the locale code
   * @param {string} [opts.numberingSystem=null] - the numbering system
   * @param {string} [opts.locObj=null] - an existing locale object to use
   * @param {string} [opts.outputCalendar='gregory'] - the calendar
   * @example Info.months()[0] //=> 'January'
   * @example Info.months('short')[0] //=> 'Jan'
   * @example Info.months('numeric')[0] //=> '1'
   * @example Info.months('short', { locale: 'fr-CA' } )[0] //=> 'janv.'
   * @example Info.months('numeric', { locale: 'ar' })[0] //=> '١'
   * @example Info.months('long', { outputCalendar: 'islamic' })[0] //=> 'Rabiʻ I'
   * @return {Array}
   */
  static months(
    length = "long",
    { locale = null, numberingSystem = null, locObj = null, outputCalendar = "gregory" } = {}
  ) {
    return (locObj || Locale.create(locale, numberingSystem, outputCalendar)).months(length);
  }

  /**
   * Return an array of format month names.
   * Format months differ from standalone months in that they're meant to appear next to the day of the month. In some languages, that
   * changes the string.
   * See {@link Info#months}
   * @param {string} [length='long'] - the length of the month representation, such as "numeric", "2-digit", "narrow", "short", "long"
   * @param {Object} opts - options
   * @param {string} [opts.locale] - the locale code
   * @param {string} [opts.numberingSystem=null] - the numbering system
   * @param {string} [opts.locObj=null] - an existing locale object to use
   * @param {string} [opts.outputCalendar='gregory'] - the calendar
   * @return {Array}
   */
  static monthsFormat(
    length = "long",
    { locale = null, numberingSystem = null, locObj = null, outputCalendar = "gregory" } = {}
  ) {
    return (locObj || Locale.create(locale, numberingSystem, outputCalendar)).months(length, true);
  }

  /**
   * Return an array of standalone week names.
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
   * @param {string} [length='long'] - the length of the weekday representation, such as "narrow", "short", "long".
   * @param {Object} opts - options
   * @param {string} [opts.locale] - the locale code
   * @param {string} [opts.numberingSystem=null] - the numbering system
   * @param {string} [opts.locObj=null] - an existing locale object to use
   * @example Info.weekdays()[0] //=> 'Monday'
   * @example Info.weekdays('short')[0] //=> 'Mon'
   * @example Info.weekdays('short', { locale: 'fr-CA' })[0] //=> 'lun.'
   * @example Info.weekdays('short', { locale: 'ar' })[0] //=> 'الاثنين'
   * @return {Array}
   */
  static weekdays(length = "long", { locale = null, numberingSystem = null, locObj = null } = {}) {
    return (locObj || Locale.create(locale, numberingSystem, null)).weekdays(length);
  }

  /**
   * Return an array of format week names.
   * Format weekdays differ from standalone weekdays in that they're meant to appear next to more date information. In some languages, that
   * changes the string.
   * See {@link Info#weekdays}
   * @param {string} [length='long'] - the length of the month representation, such as "narrow", "short", "long".
   * @param {Object} opts - options
   * @param {string} [opts.locale=null] - the locale code
   * @param {string} [opts.numberingSystem=null] - the numbering system
   * @param {string} [opts.locObj=null] - an existing locale object to use
   * @return {Array}
   */
  static weekdaysFormat(
    length = "long",
    { locale = null, numberingSystem = null, locObj = null } = {}
  ) {
    return (locObj || Locale.create(locale, numberingSystem, null)).weekdays(length, true);
  }

  /**
   * Return an array of meridiems.
   * @param {Object} opts - options
   * @param {string} [opts.locale] - the locale code
   * @example Info.meridiems() //=> [ 'AM', 'PM' ]
   * @example Info.meridiems({ locale: 'my' }) //=> [ 'နံနက်', 'ညနေ' ]
   * @return {Array}
   */
  static meridiems({ locale = null } = {}) {
    return Locale.create(locale).meridiems();
  }

  /**
   * Return an array of eras, such as ['BC', 'AD']. The locale can be specified, but the calendar system is always Gregorian.
   * @param {string} [length='short'] - the length of the era representation, such as "short" or "long".
   * @param {Object} opts - options
   * @param {string} [opts.locale] - the locale code
   * @example Info.eras() //=> [ 'BC', 'AD' ]
   * @example Info.eras('long') //=> [ 'Before Christ', 'Anno Domini' ]
   * @example Info.eras('long', { locale: 'fr' }) //=> [ 'avant Jésus-Christ', 'après Jésus-Christ' ]
   * @return {Array}
   */
  static eras(length = "short", { locale = null } = {}) {
    return Locale.create(locale, null, "gregory").eras(length);
  }

  /**
   * Return the set of available features in this environment.
   * Some features of Luxon are not available in all environments. For example, on older browsers, relative time formatting support is not available. Use this function to figure out if that's the case.
   * Keys:
   * * `relative`: whether this environment supports relative time formatting
   * @example Info.features() //=> { relative: false }
   * @return {Object}
   */
  static features() {
    return { relative: hasRelative() };
  }
}

function dayDiff(earlier, later) {
  const utcDayStart = (dt) => dt.toUTC(0, { keepLocalTime: true }).startOf("day").valueOf(),
    ms = utcDayStart(later) - utcDayStart(earlier);
  return Math.floor(Duration.fromMillis(ms).as("days"));
}

function highOrderDiffs(cursor, later, units) {
  const differs = [
    ["years", (a, b) => b.year - a.year],
    ["quarters", (a, b) => b.quarter - a.quarter],
    ["months", (a, b) => b.month - a.month + (b.year - a.year) * 12],
    [
      "weeks",
      (a, b) => {
        const days = dayDiff(a, b);
        return (days - (days % 7)) / 7;
      },
    ],
    ["days", dayDiff],
  ];

  const results = {};
  let lowestOrder, highWater;

  for (const [unit, differ] of differs) {
    if (units.indexOf(unit) >= 0) {
      lowestOrder = unit;

      let delta = differ(cursor, later);
      highWater = cursor.plus({ [unit]: delta });

      if (highWater > later) {
        cursor = cursor.plus({ [unit]: delta - 1 });
        delta -= 1;
      } else {
        cursor = highWater;
      }

      results[unit] = delta;
    }
  }

  return [cursor, results, highWater, lowestOrder];
}

function diff (earlier, later, units, opts) {
  let [cursor, results, highWater, lowestOrder] = highOrderDiffs(earlier, later, units);

  const remainingMillis = later - cursor;

  const lowerOrderUnits = units.filter(
    (u) => ["hours", "minutes", "seconds", "milliseconds"].indexOf(u) >= 0
  );

  if (lowerOrderUnits.length === 0) {
    if (highWater < later) {
      highWater = cursor.plus({ [lowestOrder]: 1 });
    }

    if (highWater !== cursor) {
      results[lowestOrder] = (results[lowestOrder] || 0) + remainingMillis / (highWater - cursor);
    }
  }

  const duration = Duration.fromObject(results, opts);

  if (lowerOrderUnits.length > 0) {
    return Duration.fromMillis(remainingMillis, opts)
      .shiftTo(...lowerOrderUnits)
      .plus(duration);
  } else {
    return duration;
  }
}

const numberingSystems = {
  arab: "[\u0660-\u0669]",
  arabext: "[\u06F0-\u06F9]",
  bali: "[\u1B50-\u1B59]",
  beng: "[\u09E6-\u09EF]",
  deva: "[\u0966-\u096F]",
  fullwide: "[\uFF10-\uFF19]",
  gujr: "[\u0AE6-\u0AEF]",
  hanidec: "[〇|一|二|三|四|五|六|七|八|九]",
  khmr: "[\u17E0-\u17E9]",
  knda: "[\u0CE6-\u0CEF]",
  laoo: "[\u0ED0-\u0ED9]",
  limb: "[\u1946-\u194F]",
  mlym: "[\u0D66-\u0D6F]",
  mong: "[\u1810-\u1819]",
  mymr: "[\u1040-\u1049]",
  orya: "[\u0B66-\u0B6F]",
  tamldec: "[\u0BE6-\u0BEF]",
  telu: "[\u0C66-\u0C6F]",
  thai: "[\u0E50-\u0E59]",
  tibt: "[\u0F20-\u0F29]",
  latn: "\\d",
};

const numberingSystemsUTF16 = {
  arab: [1632, 1641],
  arabext: [1776, 1785],
  bali: [6992, 7001],
  beng: [2534, 2543],
  deva: [2406, 2415],
  fullwide: [65296, 65303],
  gujr: [2790, 2799],
  khmr: [6112, 6121],
  knda: [3302, 3311],
  laoo: [3792, 3801],
  limb: [6470, 6479],
  mlym: [3430, 3439],
  mong: [6160, 6169],
  mymr: [4160, 4169],
  orya: [2918, 2927],
  tamldec: [3046, 3055],
  telu: [3174, 3183],
  thai: [3664, 3673],
  tibt: [3872, 3881],
};

const hanidecChars = numberingSystems.hanidec.replace(/[\[|\]]/g, "").split("");

function parseDigits(str) {
  let value = parseInt(str, 10);
  if (isNaN(value)) {
    value = "";
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);

      if (str[i].search(numberingSystems.hanidec) !== -1) {
        value += hanidecChars.indexOf(str[i]);
      } else {
        for (const key in numberingSystemsUTF16) {
          const [min, max] = numberingSystemsUTF16[key];
          if (code >= min && code <= max) {
            value += code - min;
          }
        }
      }
    }
    return parseInt(value, 10);
  } else {
    return value;
  }
}

function digitRegex({ numberingSystem }, append = "") {
  return new RegExp(`${numberingSystems[numberingSystem || "latn"]}${append}`);
}

const MISSING_FTP = "missing Intl.DateTimeFormat.formatToParts support";

function intUnit(regex, post = (i) => i) {
  return { regex, deser: ([s]) => post(parseDigits(s)) };
}

const NBSP = String.fromCharCode(160);
const spaceOrNBSP = `[ ${NBSP}]`;
const spaceOrNBSPRegExp = new RegExp(spaceOrNBSP, "g");

function fixListRegex(s) {
  // make dots optional and also make them literal
  // make space and non breakable space characters interchangeable
  return s.replace(/\./g, "\\.?").replace(spaceOrNBSPRegExp, spaceOrNBSP);
}

function stripInsensitivities(s) {
  return s
    .replace(/\./g, "") // ignore dots that were made optional
    .replace(spaceOrNBSPRegExp, " ") // interchange space and nbsp
    .toLowerCase();
}

function oneOf(strings, startIndex) {
  if (strings === null) {
    return null;
  } else {
    return {
      regex: RegExp(strings.map(fixListRegex).join("|")),
      deser: ([s]) =>
        strings.findIndex((i) => stripInsensitivities(s) === stripInsensitivities(i)) + startIndex,
    };
  }
}

function offset(regex, groups) {
  return { regex, deser: ([, h, m]) => signedOffset(h, m), groups };
}

function simple(regex) {
  return { regex, deser: ([s]) => s };
}

function escapeToken(value) {
  return value.replace(/[\-\[\]{}()*+?.,\\\^$|#\s]/g, "\\$&");
}

function unitForToken(token, loc) {
  const one = digitRegex(loc),
    two = digitRegex(loc, "{2}"),
    three = digitRegex(loc, "{3}"),
    four = digitRegex(loc, "{4}"),
    six = digitRegex(loc, "{6}"),
    oneOrTwo = digitRegex(loc, "{1,2}"),
    oneToThree = digitRegex(loc, "{1,3}"),
    oneToSix = digitRegex(loc, "{1,6}"),
    oneToNine = digitRegex(loc, "{1,9}"),
    twoToFour = digitRegex(loc, "{2,4}"),
    fourToSix = digitRegex(loc, "{4,6}"),
    literal = (t) => ({ regex: RegExp(escapeToken(t.val)), deser: ([s]) => s, literal: true }),
    unitate = (t) => {
      if (token.literal) {
        return literal(t);
      }
      switch (t.val) {
        // era
        case "G":
          return oneOf(loc.eras("short", false), 0);
        case "GG":
          return oneOf(loc.eras("long", false), 0);
        // years
        case "y":
          return intUnit(oneToSix);
        case "yy":
          return intUnit(twoToFour, untruncateYear);
        case "yyyy":
          return intUnit(four);
        case "yyyyy":
          return intUnit(fourToSix);
        case "yyyyyy":
          return intUnit(six);
        // months
        case "M":
          return intUnit(oneOrTwo);
        case "MM":
          return intUnit(two);
        case "MMM":
          return oneOf(loc.months("short", true, false), 1);
        case "MMMM":
          return oneOf(loc.months("long", true, false), 1);
        case "L":
          return intUnit(oneOrTwo);
        case "LL":
          return intUnit(two);
        case "LLL":
          return oneOf(loc.months("short", false, false), 1);
        case "LLLL":
          return oneOf(loc.months("long", false, false), 1);
        // dates
        case "d":
          return intUnit(oneOrTwo);
        case "dd":
          return intUnit(two);
        // ordinals
        case "o":
          return intUnit(oneToThree);
        case "ooo":
          return intUnit(three);
        // time
        case "HH":
          return intUnit(two);
        case "H":
          return intUnit(oneOrTwo);
        case "hh":
          return intUnit(two);
        case "h":
          return intUnit(oneOrTwo);
        case "mm":
          return intUnit(two);
        case "m":
          return intUnit(oneOrTwo);
        case "q":
          return intUnit(oneOrTwo);
        case "qq":
          return intUnit(two);
        case "s":
          return intUnit(oneOrTwo);
        case "ss":
          return intUnit(two);
        case "S":
          return intUnit(oneToThree);
        case "SSS":
          return intUnit(three);
        case "u":
          return simple(oneToNine);
        case "uu":
          return simple(oneOrTwo);
        case "uuu":
          return intUnit(one);
        // meridiem
        case "a":
          return oneOf(loc.meridiems(), 0);
        // weekYear (k)
        case "kkkk":
          return intUnit(four);
        case "kk":
          return intUnit(twoToFour, untruncateYear);
        // weekNumber (W)
        case "W":
          return intUnit(oneOrTwo);
        case "WW":
          return intUnit(two);
        // weekdays
        case "E":
        case "c":
          return intUnit(one);
        case "EEE":
          return oneOf(loc.weekdays("short", false, false), 1);
        case "EEEE":
          return oneOf(loc.weekdays("long", false, false), 1);
        case "ccc":
          return oneOf(loc.weekdays("short", true, false), 1);
        case "cccc":
          return oneOf(loc.weekdays("long", true, false), 1);
        // offset/zone
        case "Z":
        case "ZZ":
          return offset(new RegExp(`([+-]${oneOrTwo.source})(?::(${two.source}))?`), 2);
        case "ZZZ":
          return offset(new RegExp(`([+-]${oneOrTwo.source})(${two.source})?`), 2);
        // we don't support ZZZZ (PST) or ZZZZZ (Pacific Standard Time) in parsing
        // because we don't have any way to figure out what they are
        case "z":
          return simple(/[a-z_+-/]{1,256}?/i);
        default:
          return literal(t);
      }
    };

  const unit = unitate(token) || {
    invalidReason: MISSING_FTP,
  };

  unit.token = token;

  return unit;
}

const partTypeStyleToTokenVal = {
  year: {
    "2-digit": "yy",
    numeric: "yyyyy",
  },
  month: {
    numeric: "M",
    "2-digit": "MM",
    short: "MMM",
    long: "MMMM",
  },
  day: {
    numeric: "d",
    "2-digit": "dd",
  },
  weekday: {
    short: "EEE",
    long: "EEEE",
  },
  dayperiod: "a",
  dayPeriod: "a",
  hour: {
    numeric: "h",
    "2-digit": "hh",
  },
  minute: {
    numeric: "m",
    "2-digit": "mm",
  },
  second: {
    numeric: "s",
    "2-digit": "ss",
  },
};

function tokenForPart(part, locale, formatOpts) {
  const { type, value } = part;

  if (type === "literal") {
    return {
      literal: true,
      val: value,
    };
  }

  const style = formatOpts[type];

  let val = partTypeStyleToTokenVal[type];
  if (typeof val === "object") {
    val = val[style];
  }

  if (val) {
    return {
      literal: false,
      val,
    };
  }

  return undefined;
}

function buildRegex(units) {
  const re = units.map((u) => u.regex).reduce((f, r) => `${f}(${r.source})`, "");
  return [`^${re}$`, units];
}

function match(input, regex, handlers) {
  const matches = input.match(regex);

  if (matches) {
    const all = {};
    let matchIndex = 1;
    for (const i in handlers) {
      if (hasOwnProperty(handlers, i)) {
        const h = handlers[i],
          groups = h.groups ? h.groups + 1 : 1;
        if (!h.literal && h.token) {
          all[h.token.val[0]] = h.deser(matches.slice(matchIndex, matchIndex + groups));
        }
        matchIndex += groups;
      }
    }
    return [matches, all];
  } else {
    return [matches, {}];
  }
}

function dateTimeFromMatches(matches) {
  const toField = (token) => {
    switch (token) {
      case "S":
        return "millisecond";
      case "s":
        return "second";
      case "m":
        return "minute";
      case "h":
      case "H":
        return "hour";
      case "d":
        return "day";
      case "o":
        return "ordinal";
      case "L":
      case "M":
        return "month";
      case "y":
        return "year";
      case "E":
      case "c":
        return "weekday";
      case "W":
        return "weekNumber";
      case "k":
        return "weekYear";
      case "q":
        return "quarter";
      default:
        return null;
    }
  };

  let zone = null;
  let specificOffset;
  if (!isUndefined(matches.z)) {
    zone = IANAZone.create(matches.z);
  }

  if (!isUndefined(matches.Z)) {
    if (!zone) {
      zone = new FixedOffsetZone(matches.Z);
    }
    specificOffset = matches.Z;
  }

  if (!isUndefined(matches.q)) {
    matches.M = (matches.q - 1) * 3 + 1;
  }

  if (!isUndefined(matches.h)) {
    if (matches.h < 12 && matches.a === 1) {
      matches.h += 12;
    } else if (matches.h === 12 && matches.a === 0) {
      matches.h = 0;
    }
  }

  if (matches.G === 0 && matches.y) {
    matches.y = -matches.y;
  }

  if (!isUndefined(matches.u)) {
    matches.S = parseMillis(matches.u);
  }

  const vals = Object.keys(matches).reduce((r, k) => {
    const f = toField(k);
    if (f) {
      r[f] = matches[k];
    }

    return r;
  }, {});

  return [vals, zone, specificOffset];
}

let dummyDateTimeCache = null;

function getDummyDateTime() {
  if (!dummyDateTimeCache) {
    dummyDateTimeCache = DateTime.fromMillis(1555555555555);
  }

  return dummyDateTimeCache;
}

function maybeExpandMacroToken(token, locale) {
  if (token.literal) {
    return token;
  }

  const formatOpts = Formatter.macroTokenToFormatOpts(token.val);

  if (!formatOpts) {
    return token;
  }

  const formatter = Formatter.create(locale, formatOpts);
  const parts = formatter.formatDateTimeParts(getDummyDateTime());

  const tokens = parts.map((p) => tokenForPart(p, locale, formatOpts));

  if (tokens.includes(undefined)) {
    return token;
  }

  return tokens;
}

function expandMacroTokens(tokens, locale) {
  return Array.prototype.concat(...tokens.map((t) => maybeExpandMacroToken(t, locale)));
}

/**
 * @private
 */

function explainFromTokens(locale, input, format) {
  const tokens = expandMacroTokens(Formatter.parseFormat(format), locale),
    units = tokens.map((t) => unitForToken(t, locale)),
    disqualifyingUnit = units.find((t) => t.invalidReason);

  if (disqualifyingUnit) {
    return { input, tokens, invalidReason: disqualifyingUnit.invalidReason };
  } else {
    const [regexString, handlers] = buildRegex(units),
      regex = RegExp(regexString, "i"),
      [rawMatches, matches] = match(input, regex, handlers),
      [result, zone, specificOffset] = matches
        ? dateTimeFromMatches(matches)
        : [null, null, undefined];
    if (hasOwnProperty(matches, "a") && hasOwnProperty(matches, "H")) {
      throw new ConflictingSpecificationError(
        "Can't include meridiem when specifying 24-hour format"
      );
    }
    return { input, tokens, regex, rawMatches, matches, result, zone, specificOffset };
  }
}

function parseFromTokens(locale, input, format) {
  const { result, zone, specificOffset, invalidReason } = explainFromTokens(locale, input, format);
  return [result, zone, specificOffset, invalidReason];
}

const nonLeapLadder = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334],
  leapLadder = [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];

function unitOutOfRange(unit, value) {
  return new Invalid(
    "unit out of range",
    `you specified ${value} (of type ${typeof value}) as a ${unit}, which is invalid`
  );
}

function dayOfWeek(year, month, day) {
  const d = new Date(Date.UTC(year, month - 1, day));

  if (year < 100 && year >= 0) {
    d.setUTCFullYear(d.getUTCFullYear() - 1900);
  }

  const js = d.getUTCDay();

  return js === 0 ? 7 : js;
}

function computeOrdinal(year, month, day) {
  return day + (isLeapYear(year) ? leapLadder : nonLeapLadder)[month - 1];
}

function uncomputeOrdinal(year, ordinal) {
  const table = isLeapYear(year) ? leapLadder : nonLeapLadder,
    month0 = table.findIndex((i) => i < ordinal),
    day = ordinal - table[month0];
  return { month: month0 + 1, day };
}

/**
 * @private
 */

function gregorianToWeek(gregObj) {
  const { year, month, day } = gregObj,
    ordinal = computeOrdinal(year, month, day),
    weekday = dayOfWeek(year, month, day);

  let weekNumber = Math.floor((ordinal - weekday + 10) / 7),
    weekYear;

  if (weekNumber < 1) {
    weekYear = year - 1;
    weekNumber = weeksInWeekYear(weekYear);
  } else if (weekNumber > weeksInWeekYear(year)) {
    weekYear = year + 1;
    weekNumber = 1;
  } else {
    weekYear = year;
  }

  return { weekYear, weekNumber, weekday, ...timeObject(gregObj) };
}

function weekToGregorian(weekData) {
  const { weekYear, weekNumber, weekday } = weekData,
    weekdayOfJan4 = dayOfWeek(weekYear, 1, 4),
    yearInDays = daysInYear(weekYear);

  let ordinal = weekNumber * 7 + weekday - weekdayOfJan4 - 3,
    year;

  if (ordinal < 1) {
    year = weekYear - 1;
    ordinal += daysInYear(year);
  } else if (ordinal > yearInDays) {
    year = weekYear + 1;
    ordinal -= daysInYear(weekYear);
  } else {
    year = weekYear;
  }

  const { month, day } = uncomputeOrdinal(year, ordinal);
  return { year, month, day, ...timeObject(weekData) };
}

function gregorianToOrdinal(gregData) {
  const { year, month, day } = gregData;
  const ordinal = computeOrdinal(year, month, day);
  return { year, ordinal, ...timeObject(gregData) };
}

function ordinalToGregorian(ordinalData) {
  const { year, ordinal } = ordinalData;
  const { month, day } = uncomputeOrdinal(year, ordinal);
  return { year, month, day, ...timeObject(ordinalData) };
}

function hasInvalidWeekData(obj) {
  const validYear = isInteger(obj.weekYear),
    validWeek = integerBetween(obj.weekNumber, 1, weeksInWeekYear(obj.weekYear)),
    validWeekday = integerBetween(obj.weekday, 1, 7);

  if (!validYear) {
    return unitOutOfRange("weekYear", obj.weekYear);
  } else if (!validWeek) {
    return unitOutOfRange("week", obj.week);
  } else if (!validWeekday) {
    return unitOutOfRange("weekday", obj.weekday);
  } else return false;
}

function hasInvalidOrdinalData(obj) {
  const validYear = isInteger(obj.year),
    validOrdinal = integerBetween(obj.ordinal, 1, daysInYear(obj.year));

  if (!validYear) {
    return unitOutOfRange("year", obj.year);
  } else if (!validOrdinal) {
    return unitOutOfRange("ordinal", obj.ordinal);
  } else return false;
}

function hasInvalidGregorianData(obj) {
  const validYear = isInteger(obj.year),
    validMonth = integerBetween(obj.month, 1, 12),
    validDay = integerBetween(obj.day, 1, daysInMonth(obj.year, obj.month));

  if (!validYear) {
    return unitOutOfRange("year", obj.year);
  } else if (!validMonth) {
    return unitOutOfRange("month", obj.month);
  } else if (!validDay) {
    return unitOutOfRange("day", obj.day);
  } else return false;
}

function hasInvalidTimeData(obj) {
  const { hour, minute, second, millisecond } = obj;
  const validHour =
      integerBetween(hour, 0, 23) ||
      (hour === 24 && minute === 0 && second === 0 && millisecond === 0),
    validMinute = integerBetween(minute, 0, 59),
    validSecond = integerBetween(second, 0, 59),
    validMillisecond = integerBetween(millisecond, 0, 999);

  if (!validHour) {
    return unitOutOfRange("hour", hour);
  } else if (!validMinute) {
    return unitOutOfRange("minute", minute);
  } else if (!validSecond) {
    return unitOutOfRange("second", second);
  } else if (!validMillisecond) {
    return unitOutOfRange("millisecond", millisecond);
  } else return false;
}

const INVALID = "Invalid DateTime";
const MAX_DATE = 8.64e15;

function unsupportedZone(zone) {
  return new Invalid("unsupported zone", `the zone "${zone.name}" is not supported`);
}

// we cache week data on the DT object and this intermediates the cache
function possiblyCachedWeekData(dt) {
  if (dt.weekData === null) {
    dt.weekData = gregorianToWeek(dt.c);
  }
  return dt.weekData;
}

// clone really means, "make a new object with these modifications". all "setters" really use this
// to create a new object while only changing some of the properties
function clone(inst, alts) {
  const current = {
    ts: inst.ts,
    zone: inst.zone,
    c: inst.c,
    o: inst.o,
    loc: inst.loc,
    invalid: inst.invalid,
  };
  return new DateTime({ ...current, ...alts, old: current });
}

// find the right offset a given local time. The o input is our guess, which determines which
// offset we'll pick in ambiguous cases (e.g. there are two 3 AMs b/c Fallback DST)
function fixOffset(localTS, o, tz) {
  // Our UTC time is just a guess because our offset is just a guess
  let utcGuess = localTS - o * 60 * 1000;

  // Test whether the zone matches the offset for this ts
  const o2 = tz.offset(utcGuess);

  // If so, offset didn't change and we're done
  if (o === o2) {
    return [utcGuess, o];
  }

  // If not, change the ts by the difference in the offset
  utcGuess -= (o2 - o) * 60 * 1000;

  // If that gives us the local time we want, we're done
  const o3 = tz.offset(utcGuess);
  if (o2 === o3) {
    return [utcGuess, o2];
  }

  // If it's different, we're in a hole time. The offset has changed, but the we don't adjust the time
  return [localTS - Math.min(o2, o3) * 60 * 1000, Math.max(o2, o3)];
}

// convert an epoch timestamp into a calendar object with the given offset
function tsToObj(ts, offset) {
  ts += offset * 60 * 1000;

  const d = new Date(ts);

  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
    millisecond: d.getUTCMilliseconds(),
  };
}

// convert a calendar object to a epoch timestamp
function objToTS(obj, offset, zone) {
  return fixOffset(objToLocalTS(obj), offset, zone);
}

// create a new DT instance by adding a duration, adjusting for DSTs
function adjustTime(inst, dur) {
  const oPre = inst.o,
    year = inst.c.year + Math.trunc(dur.years),
    month = inst.c.month + Math.trunc(dur.months) + Math.trunc(dur.quarters) * 3,
    c = {
      ...inst.c,
      year,
      month,
      day:
        Math.min(inst.c.day, daysInMonth(year, month)) +
        Math.trunc(dur.days) +
        Math.trunc(dur.weeks) * 7,
    },
    millisToAdd = Duration.fromObject({
      years: dur.years - Math.trunc(dur.years),
      quarters: dur.quarters - Math.trunc(dur.quarters),
      months: dur.months - Math.trunc(dur.months),
      weeks: dur.weeks - Math.trunc(dur.weeks),
      days: dur.days - Math.trunc(dur.days),
      hours: dur.hours,
      minutes: dur.minutes,
      seconds: dur.seconds,
      milliseconds: dur.milliseconds,
    }).as("milliseconds"),
    localTS = objToLocalTS(c);

  let [ts, o] = fixOffset(localTS, oPre, inst.zone);

  if (millisToAdd !== 0) {
    ts += millisToAdd;
    // that could have changed the offset by going over a DST, but we want to keep the ts the same
    o = inst.zone.offset(ts);
  }

  return { ts, o };
}

// helper useful in turning the results of parsing into real dates
// by handling the zone options
function parseDataToDateTime(parsed, parsedZone, opts, format, text, specificOffset) {
  const { setZone, zone } = opts;
  if (parsed && Object.keys(parsed).length !== 0) {
    const interpretationZone = parsedZone || zone,
      inst = DateTime.fromObject(parsed, {
        ...opts,
        zone: interpretationZone,
        specificOffset,
      });
    return setZone ? inst : inst.setZone(zone);
  } else {
    return DateTime.invalid(
      new Invalid("unparsable", `the input "${text}" can't be parsed as ${format}`)
    );
  }
}

// if you want to output a technical format (e.g. RFC 2822), this helper
// helps handle the details
function toTechFormat(dt, format, allowZ = true) {
  return dt.isValid
    ? Formatter.create(Locale.create("en-US"), {
        allowZ,
        forceSimple: true,
      }).formatDateTimeFromString(dt, format)
    : null;
}

function toISODate(o, extended) {
  const longFormat = o.c.year > 9999 || o.c.year < 0;
  let c = "";
  if (longFormat && o.c.year >= 0) c += "+";
  c += padStart(o.c.year, longFormat ? 6 : 4);

  if (extended) {
    c += "-";
    c += padStart(o.c.month);
    c += "-";
    c += padStart(o.c.day);
  } else {
    c += padStart(o.c.month);
    c += padStart(o.c.day);
  }
  return c;
}

function toISOTime(
  o,
  extended,
  suppressSeconds,
  suppressMilliseconds,
  includeOffset,
  extendedZone
) {
  let c = padStart(o.c.hour);
  if (extended) {
    c += ":";
    c += padStart(o.c.minute);
    if (o.c.second !== 0 || !suppressSeconds) {
      c += ":";
    }
  } else {
    c += padStart(o.c.minute);
  }

  if (o.c.second !== 0 || !suppressSeconds) {
    c += padStart(o.c.second);

    if (o.c.millisecond !== 0 || !suppressMilliseconds) {
      c += ".";
      c += padStart(o.c.millisecond, 3);
    }
  }

  if (includeOffset) {
    if (o.isOffsetFixed && o.offset === 0 && !extendedZone) {
      c += "Z";
    } else if (o.o < 0) {
      c += "-";
      c += padStart(Math.trunc(-o.o / 60));
      c += ":";
      c += padStart(Math.trunc(-o.o % 60));
    } else {
      c += "+";
      c += padStart(Math.trunc(o.o / 60));
      c += ":";
      c += padStart(Math.trunc(o.o % 60));
    }
  }

  if (extendedZone) {
    c += "[" + o.zone.ianaName + "]";
  }
  return c;
}

// defaults for unspecified units in the supported calendars
const defaultUnitValues = {
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  },
  defaultWeekUnitValues = {
    weekNumber: 1,
    weekday: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  },
  defaultOrdinalUnitValues = {
    ordinal: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  };

// Units in the supported calendars, sorted by bigness
const orderedUnits = ["year", "month", "day", "hour", "minute", "second", "millisecond"],
  orderedWeekUnits = [
    "weekYear",
    "weekNumber",
    "weekday",
    "hour",
    "minute",
    "second",
    "millisecond",
  ],
  orderedOrdinalUnits = ["year", "ordinal", "hour", "minute", "second", "millisecond"];

// standardize case and plurality in units
function normalizeUnit(unit) {
  const normalized = {
    year: "year",
    years: "year",
    month: "month",
    months: "month",
    day: "day",
    days: "day",
    hour: "hour",
    hours: "hour",
    minute: "minute",
    minutes: "minute",
    quarter: "quarter",
    quarters: "quarter",
    second: "second",
    seconds: "second",
    millisecond: "millisecond",
    milliseconds: "millisecond",
    weekday: "weekday",
    weekdays: "weekday",
    weeknumber: "weekNumber",
    weeksnumber: "weekNumber",
    weeknumbers: "weekNumber",
    weekyear: "weekYear",
    weekyears: "weekYear",
    ordinal: "ordinal",
  }[unit.toLowerCase()];

  if (!normalized) throw new InvalidUnitError(unit);

  return normalized;
}

// this is a dumbed down version of fromObject() that runs about 60% faster
// but doesn't do any validation, makes a bunch of assumptions about what units
// are present, and so on.
function quickDT(obj, opts) {
  const zone = normalizeZone(opts.zone, Settings.defaultZone),
    loc = Locale.fromObject(opts),
    tsNow = Settings.now();

  let ts, o;

  // assume we have the higher-order units
  if (!isUndefined(obj.year)) {
    for (const u of orderedUnits) {
      if (isUndefined(obj[u])) {
        obj[u] = defaultUnitValues[u];
      }
    }

    const invalid = hasInvalidGregorianData(obj) || hasInvalidTimeData(obj);
    if (invalid) {
      return DateTime.invalid(invalid);
    }

    const offsetProvis = zone.offset(tsNow);
    [ts, o] = objToTS(obj, offsetProvis, zone);
  } else {
    ts = tsNow;
  }

  return new DateTime({ ts, zone, loc, o });
}

function diffRelative(start, end, opts) {
  const round = isUndefined(opts.round) ? true : opts.round,
    format = (c, unit) => {
      c = roundTo(c, round || opts.calendary ? 0 : 2, true);
      const formatter = end.loc.clone(opts).relFormatter(opts);
      return formatter.format(c, unit);
    },
    differ = (unit) => {
      if (opts.calendary) {
        if (!end.hasSame(start, unit)) {
          return end.startOf(unit).diff(start.startOf(unit), unit).get(unit);
        } else return 0;
      } else {
        return end.diff(start, unit).get(unit);
      }
    };

  if (opts.unit) {
    return format(differ(opts.unit), opts.unit);
  }

  for (const unit of opts.units) {
    const count = differ(unit);
    if (Math.abs(count) >= 1) {
      return format(count, unit);
    }
  }
  return format(start > end ? -0 : 0, opts.units[opts.units.length - 1]);
}

function lastOpts(argList) {
  let opts = {},
    args;
  if (argList.length > 0 && typeof argList[argList.length - 1] === "object") {
    opts = argList[argList.length - 1];
    args = Array.from(argList).slice(0, argList.length - 1);
  } else {
    args = Array.from(argList);
  }
  return [opts, args];
}

/**
 * A DateTime is an immutable data structure representing a specific date and time and accompanying methods. It contains class and instance methods for creating, parsing, interrogating, transforming, and formatting them.
 *
 * A DateTime comprises of:
 * * A timestamp. Each DateTime instance refers to a specific millisecond of the Unix epoch.
 * * A time zone. Each instance is considered in the context of a specific zone (by default the local system's zone).
 * * Configuration properties that effect how output strings are formatted, such as `locale`, `numberingSystem`, and `outputCalendar`.
 *
 * Here is a brief overview of the most commonly used functionality it provides:
 *
 * * **Creation**: To create a DateTime from its components, use one of its factory class methods: {@link DateTime#local}, {@link DateTime#utc}, and (most flexibly) {@link DateTime#fromObject}. To create one from a standard string format, use {@link DateTime#fromISO}, {@link DateTime#fromHTTP}, and {@link DateTime#fromRFC2822}. To create one from a custom string format, use {@link DateTime#fromFormat}. To create one from a native JS date, use {@link DateTime#fromJSDate}.
 * * **Gregorian calendar and time**: To examine the Gregorian properties of a DateTime individually (i.e as opposed to collectively through {@link DateTime#toObject}), use the {@link DateTime#year}, {@link DateTime#month},
 * {@link DateTime#day}, {@link DateTime#hour}, {@link DateTime#minute}, {@link DateTime#second}, {@link DateTime#millisecond} accessors.
 * * **Week calendar**: For ISO week calendar attributes, see the {@link DateTime#weekYear}, {@link DateTime#weekNumber}, and {@link DateTime#weekday} accessors.
 * * **Configuration** See the {@link DateTime#locale} and {@link DateTime#numberingSystem} accessors.
 * * **Transformation**: To transform the DateTime into other DateTimes, use {@link DateTime#set}, {@link DateTime#reconfigure}, {@link DateTime#setZone}, {@link DateTime#setLocale}, {@link DateTime.plus}, {@link DateTime#minus}, {@link DateTime#endOf}, {@link DateTime#startOf}, {@link DateTime#toUTC}, and {@link DateTime#toLocal}.
 * * **Output**: To convert the DateTime to other representations, use the {@link DateTime#toRelative}, {@link DateTime#toRelativeCalendar}, {@link DateTime#toJSON}, {@link DateTime#toISO}, {@link DateTime#toHTTP}, {@link DateTime#toObject}, {@link DateTime#toRFC2822}, {@link DateTime#toString}, {@link DateTime#toLocaleString}, {@link DateTime#toFormat}, {@link DateTime#toMillis} and {@link DateTime#toJSDate}.
 *
 * There's plenty others documented below. In addition, for more information on subtler topics like internationalization, time zones, alternative calendars, validity, and so on, see the external documentation.
 */
class DateTime {
  /**
   * @access private
   */
  constructor(config) {
    const zone = config.zone || Settings.defaultZone;

    let invalid =
      config.invalid ||
      (Number.isNaN(config.ts) ? new Invalid("invalid input") : null) ||
      (!zone.isValid ? unsupportedZone(zone) : null);
    /**
     * @access private
     */
    this.ts = isUndefined(config.ts) ? Settings.now() : config.ts;

    let c = null,
      o = null;
    if (!invalid) {
      const unchanged = config.old && config.old.ts === this.ts && config.old.zone.equals(zone);

      if (unchanged) {
        [c, o] = [config.old.c, config.old.o];
      } else {
        const ot = zone.offset(this.ts);
        c = tsToObj(this.ts, ot);
        invalid = Number.isNaN(c.year) ? new Invalid("invalid input") : null;
        c = invalid ? null : c;
        o = invalid ? null : ot;
      }
    }

    /**
     * @access private
     */
    this._zone = zone;
    /**
     * @access private
     */
    this.loc = config.loc || Locale.create();
    /**
     * @access private
     */
    this.invalid = invalid;
    /**
     * @access private
     */
    this.weekData = null;
    /**
     * @access private
     */
    this.c = c;
    /**
     * @access private
     */
    this.o = o;
    /**
     * @access private
     */
    this.isLuxonDateTime = true;
  }

  // CONSTRUCT

  /**
   * Create a DateTime for the current instant, in the system's time zone.
   *
   * Use Settings to override these default values if needed.
   * @example DateTime.now().toISO() //~> now in the ISO format
   * @return {DateTime}
   */
  static now() {
    return new DateTime({});
  }

  /**
   * Create a local DateTime
   * @param {number} [year] - The calendar year. If omitted (as in, call `local()` with no arguments), the current time will be used
   * @param {number} [month=1] - The month, 1-indexed
   * @param {number} [day=1] - The day of the month, 1-indexed
   * @param {number} [hour=0] - The hour of the day, in 24-hour time
   * @param {number} [minute=0] - The minute of the hour, meaning a number between 0 and 59
   * @param {number} [second=0] - The second of the minute, meaning a number between 0 and 59
   * @param {number} [millisecond=0] - The millisecond of the second, meaning a number between 0 and 999
   * @example DateTime.local()                                  //~> now
   * @example DateTime.local({ zone: "America/New_York" })      //~> now, in US east coast time
   * @example DateTime.local(2017)                              //~> 2017-01-01T00:00:00
   * @example DateTime.local(2017, 3)                           //~> 2017-03-01T00:00:00
   * @example DateTime.local(2017, 3, 12, { locale: "fr" })     //~> 2017-03-12T00:00:00, with a French locale
   * @example DateTime.local(2017, 3, 12, 5)                    //~> 2017-03-12T05:00:00
   * @example DateTime.local(2017, 3, 12, 5, { zone: "utc" })   //~> 2017-03-12T05:00:00, in UTC
   * @example DateTime.local(2017, 3, 12, 5, 45)                //~> 2017-03-12T05:45:00
   * @example DateTime.local(2017, 3, 12, 5, 45, 10)            //~> 2017-03-12T05:45:10
   * @example DateTime.local(2017, 3, 12, 5, 45, 10, 765)       //~> 2017-03-12T05:45:10.765
   * @return {DateTime}
   */
  static local() {
    const [opts, args] = lastOpts(arguments),
      [year, month, day, hour, minute, second, millisecond] = args;
    return quickDT({ year, month, day, hour, minute, second, millisecond }, opts);
  }

  /**
   * Create a DateTime in UTC
   * @param {number} [year] - The calendar year. If omitted (as in, call `utc()` with no arguments), the current time will be used
   * @param {number} [month=1] - The month, 1-indexed
   * @param {number} [day=1] - The day of the month
   * @param {number} [hour=0] - The hour of the day, in 24-hour time
   * @param {number} [minute=0] - The minute of the hour, meaning a number between 0 and 59
   * @param {number} [second=0] - The second of the minute, meaning a number between 0 and 59
   * @param {number} [millisecond=0] - The millisecond of the second, meaning a number between 0 and 999
   * @param {Object} options - configuration options for the DateTime
   * @param {string} [options.locale] - a locale to set on the resulting DateTime instance
   * @param {string} [options.outputCalendar] - the output calendar to set on the resulting DateTime instance
   * @param {string} [options.numberingSystem] - the numbering system to set on the resulting DateTime instance
   * @example DateTime.utc()                                              //~> now
   * @example DateTime.utc(2017)                                          //~> 2017-01-01T00:00:00Z
   * @example DateTime.utc(2017, 3)                                       //~> 2017-03-01T00:00:00Z
   * @example DateTime.utc(2017, 3, 12)                                   //~> 2017-03-12T00:00:00Z
   * @example DateTime.utc(2017, 3, 12, 5)                                //~> 2017-03-12T05:00:00Z
   * @example DateTime.utc(2017, 3, 12, 5, 45)                            //~> 2017-03-12T05:45:00Z
   * @example DateTime.utc(2017, 3, 12, 5, 45, { locale: "fr" })          //~> 2017-03-12T05:45:00Z with a French locale
   * @example DateTime.utc(2017, 3, 12, 5, 45, 10)                        //~> 2017-03-12T05:45:10Z
   * @example DateTime.utc(2017, 3, 12, 5, 45, 10, 765, { locale: "fr" }) //~> 2017-03-12T05:45:10.765Z with a French locale
   * @return {DateTime}
   */
  static utc() {
    const [opts, args] = lastOpts(arguments),
      [year, month, day, hour, minute, second, millisecond] = args;

    opts.zone = FixedOffsetZone.utcInstance;
    return quickDT({ year, month, day, hour, minute, second, millisecond }, opts);
  }

  /**
   * Create a DateTime from a JavaScript Date object. Uses the default zone.
   * @param {Date} date - a JavaScript Date object
   * @param {Object} options - configuration options for the DateTime
   * @param {string|Zone} [options.zone='local'] - the zone to place the DateTime into
   * @return {DateTime}
   */
  static fromJSDate(date, options = {}) {
    const ts = isDate$3(date) ? date.valueOf() : NaN;
    if (Number.isNaN(ts)) {
      return DateTime.invalid("invalid input");
    }

    const zoneToUse = normalizeZone(options.zone, Settings.defaultZone);
    if (!zoneToUse.isValid) {
      return DateTime.invalid(unsupportedZone(zoneToUse));
    }

    return new DateTime({
      ts: ts,
      zone: zoneToUse,
      loc: Locale.fromObject(options),
    });
  }

  /**
   * Create a DateTime from a number of milliseconds since the epoch (meaning since 1 January 1970 00:00:00 UTC). Uses the default zone.
   * @param {number} milliseconds - a number of milliseconds since 1970 UTC
   * @param {Object} options - configuration options for the DateTime
   * @param {string|Zone} [options.zone='local'] - the zone to place the DateTime into
   * @param {string} [options.locale] - a locale to set on the resulting DateTime instance
   * @param {string} options.outputCalendar - the output calendar to set on the resulting DateTime instance
   * @param {string} options.numberingSystem - the numbering system to set on the resulting DateTime instance
   * @return {DateTime}
   */
  static fromMillis(milliseconds, options = {}) {
    if (!isNumber(milliseconds)) {
      throw new InvalidArgumentError(
        `fromMillis requires a numerical input, but received a ${typeof milliseconds} with value ${milliseconds}`
      );
    } else if (milliseconds < -MAX_DATE || milliseconds > MAX_DATE) {
      // this isn't perfect because because we can still end up out of range because of additional shifting, but it's a start
      return DateTime.invalid("Timestamp out of range");
    } else {
      return new DateTime({
        ts: milliseconds,
        zone: normalizeZone(options.zone, Settings.defaultZone),
        loc: Locale.fromObject(options),
      });
    }
  }

  /**
   * Create a DateTime from a number of seconds since the epoch (meaning since 1 January 1970 00:00:00 UTC). Uses the default zone.
   * @param {number} seconds - a number of seconds since 1970 UTC
   * @param {Object} options - configuration options for the DateTime
   * @param {string|Zone} [options.zone='local'] - the zone to place the DateTime into
   * @param {string} [options.locale] - a locale to set on the resulting DateTime instance
   * @param {string} options.outputCalendar - the output calendar to set on the resulting DateTime instance
   * @param {string} options.numberingSystem - the numbering system to set on the resulting DateTime instance
   * @return {DateTime}
   */
  static fromSeconds(seconds, options = {}) {
    if (!isNumber(seconds)) {
      throw new InvalidArgumentError("fromSeconds requires a numerical input");
    } else {
      return new DateTime({
        ts: seconds * 1000,
        zone: normalizeZone(options.zone, Settings.defaultZone),
        loc: Locale.fromObject(options),
      });
    }
  }

  /**
   * Create a DateTime from a JavaScript object with keys like 'year' and 'hour' with reasonable defaults.
   * @param {Object} obj - the object to create the DateTime from
   * @param {number} obj.year - a year, such as 1987
   * @param {number} obj.month - a month, 1-12
   * @param {number} obj.day - a day of the month, 1-31, depending on the month
   * @param {number} obj.ordinal - day of the year, 1-365 or 366
   * @param {number} obj.weekYear - an ISO week year
   * @param {number} obj.weekNumber - an ISO week number, between 1 and 52 or 53, depending on the year
   * @param {number} obj.weekday - an ISO weekday, 1-7, where 1 is Monday and 7 is Sunday
   * @param {number} obj.hour - hour of the day, 0-23
   * @param {number} obj.minute - minute of the hour, 0-59
   * @param {number} obj.second - second of the minute, 0-59
   * @param {number} obj.millisecond - millisecond of the second, 0-999
   * @param {Object} opts - options for creating this DateTime
   * @param {string|Zone} [opts.zone='local'] - interpret the numbers in the context of a particular zone. Can take any value taken as the first argument to setZone()
   * @param {string} [opts.locale='system's locale'] - a locale to set on the resulting DateTime instance
   * @param {string} opts.outputCalendar - the output calendar to set on the resulting DateTime instance
   * @param {string} opts.numberingSystem - the numbering system to set on the resulting DateTime instance
   * @example DateTime.fromObject({ year: 1982, month: 5, day: 25}).toISODate() //=> '1982-05-25'
   * @example DateTime.fromObject({ year: 1982 }).toISODate() //=> '1982-01-01'
   * @example DateTime.fromObject({ hour: 10, minute: 26, second: 6 }) //~> today at 10:26:06
   * @example DateTime.fromObject({ hour: 10, minute: 26, second: 6 }, { zone: 'utc' }),
   * @example DateTime.fromObject({ hour: 10, minute: 26, second: 6 }, { zone: 'local' })
   * @example DateTime.fromObject({ hour: 10, minute: 26, second: 6 }, { zone: 'America/New_York' })
   * @example DateTime.fromObject({ weekYear: 2016, weekNumber: 2, weekday: 3 }).toISODate() //=> '2016-01-13'
   * @return {DateTime}
   */
  static fromObject(obj, opts = {}) {
    obj = obj || {};
    const zoneToUse = normalizeZone(opts.zone, Settings.defaultZone);
    if (!zoneToUse.isValid) {
      return DateTime.invalid(unsupportedZone(zoneToUse));
    }

    const tsNow = Settings.now(),
      offsetProvis = !isUndefined(opts.specificOffset)
        ? opts.specificOffset
        : zoneToUse.offset(tsNow),
      normalized = normalizeObject(obj, normalizeUnit),
      containsOrdinal = !isUndefined(normalized.ordinal),
      containsGregorYear = !isUndefined(normalized.year),
      containsGregorMD = !isUndefined(normalized.month) || !isUndefined(normalized.day),
      containsGregor = containsGregorYear || containsGregorMD,
      definiteWeekDef = normalized.weekYear || normalized.weekNumber,
      loc = Locale.fromObject(opts);

    // cases:
    // just a weekday -> this week's instance of that weekday, no worries
    // (gregorian data or ordinal) + (weekYear or weekNumber) -> error
    // (gregorian month or day) + ordinal -> error
    // otherwise just use weeks or ordinals or gregorian, depending on what's specified

    if ((containsGregor || containsOrdinal) && definiteWeekDef) {
      throw new ConflictingSpecificationError(
        "Can't mix weekYear/weekNumber units with year/month/day or ordinals"
      );
    }

    if (containsGregorMD && containsOrdinal) {
      throw new ConflictingSpecificationError("Can't mix ordinal dates with month/day");
    }

    const useWeekData = definiteWeekDef || (normalized.weekday && !containsGregor);

    // configure ourselves to deal with gregorian dates or week stuff
    let units,
      defaultValues,
      objNow = tsToObj(tsNow, offsetProvis);
    if (useWeekData) {
      units = orderedWeekUnits;
      defaultValues = defaultWeekUnitValues;
      objNow = gregorianToWeek(objNow);
    } else if (containsOrdinal) {
      units = orderedOrdinalUnits;
      defaultValues = defaultOrdinalUnitValues;
      objNow = gregorianToOrdinal(objNow);
    } else {
      units = orderedUnits;
      defaultValues = defaultUnitValues;
    }

    // set default values for missing stuff
    let foundFirst = false;
    for (const u of units) {
      const v = normalized[u];
      if (!isUndefined(v)) {
        foundFirst = true;
      } else if (foundFirst) {
        normalized[u] = defaultValues[u];
      } else {
        normalized[u] = objNow[u];
      }
    }

    // make sure the values we have are in range
    const higherOrderInvalid = useWeekData
        ? hasInvalidWeekData(normalized)
        : containsOrdinal
        ? hasInvalidOrdinalData(normalized)
        : hasInvalidGregorianData(normalized),
      invalid = higherOrderInvalid || hasInvalidTimeData(normalized);

    if (invalid) {
      return DateTime.invalid(invalid);
    }

    // compute the actual time
    const gregorian = useWeekData
        ? weekToGregorian(normalized)
        : containsOrdinal
        ? ordinalToGregorian(normalized)
        : normalized,
      [tsFinal, offsetFinal] = objToTS(gregorian, offsetProvis, zoneToUse),
      inst = new DateTime({
        ts: tsFinal,
        zone: zoneToUse,
        o: offsetFinal,
        loc,
      });

    // gregorian data + weekday serves only to validate
    if (normalized.weekday && containsGregor && obj.weekday !== inst.weekday) {
      return DateTime.invalid(
        "mismatched weekday",
        `you can't specify both a weekday of ${normalized.weekday} and a date of ${inst.toISO()}`
      );
    }

    return inst;
  }

  /**
   * Create a DateTime from an ISO 8601 string
   * @param {string} text - the ISO string
   * @param {Object} opts - options to affect the creation
   * @param {string|Zone} [opts.zone='local'] - use this zone if no offset is specified in the input string itself. Will also convert the time to this zone
   * @param {boolean} [opts.setZone=false] - override the zone with a fixed-offset zone specified in the string itself, if it specifies one
   * @param {string} [opts.locale='system's locale'] - a locale to set on the resulting DateTime instance
   * @param {string} [opts.outputCalendar] - the output calendar to set on the resulting DateTime instance
   * @param {string} [opts.numberingSystem] - the numbering system to set on the resulting DateTime instance
   * @example DateTime.fromISO('2016-05-25T09:08:34.123')
   * @example DateTime.fromISO('2016-05-25T09:08:34.123+06:00')
   * @example DateTime.fromISO('2016-05-25T09:08:34.123+06:00', {setZone: true})
   * @example DateTime.fromISO('2016-05-25T09:08:34.123', {zone: 'utc'})
   * @example DateTime.fromISO('2016-W05-4')
   * @return {DateTime}
   */
  static fromISO(text, opts = {}) {
    const [vals, parsedZone] = parseISODate(text);
    return parseDataToDateTime(vals, parsedZone, opts, "ISO 8601", text);
  }

  /**
   * Create a DateTime from an RFC 2822 string
   * @param {string} text - the RFC 2822 string
   * @param {Object} opts - options to affect the creation
   * @param {string|Zone} [opts.zone='local'] - convert the time to this zone. Since the offset is always specified in the string itself, this has no effect on the interpretation of string, merely the zone the resulting DateTime is expressed in.
   * @param {boolean} [opts.setZone=false] - override the zone with a fixed-offset zone specified in the string itself, if it specifies one
   * @param {string} [opts.locale='system's locale'] - a locale to set on the resulting DateTime instance
   * @param {string} opts.outputCalendar - the output calendar to set on the resulting DateTime instance
   * @param {string} opts.numberingSystem - the numbering system to set on the resulting DateTime instance
   * @example DateTime.fromRFC2822('25 Nov 2016 13:23:12 GMT')
   * @example DateTime.fromRFC2822('Fri, 25 Nov 2016 13:23:12 +0600')
   * @example DateTime.fromRFC2822('25 Nov 2016 13:23 Z')
   * @return {DateTime}
   */
  static fromRFC2822(text, opts = {}) {
    const [vals, parsedZone] = parseRFC2822Date(text);
    return parseDataToDateTime(vals, parsedZone, opts, "RFC 2822", text);
  }

  /**
   * Create a DateTime from an HTTP header date
   * @see https://www.w3.org/Protocols/rfc2616/rfc2616-sec3.html#sec3.3.1
   * @param {string} text - the HTTP header date
   * @param {Object} opts - options to affect the creation
   * @param {string|Zone} [opts.zone='local'] - convert the time to this zone. Since HTTP dates are always in UTC, this has no effect on the interpretation of string, merely the zone the resulting DateTime is expressed in.
   * @param {boolean} [opts.setZone=false] - override the zone with the fixed-offset zone specified in the string. For HTTP dates, this is always UTC, so this option is equivalent to setting the `zone` option to 'utc', but this option is included for consistency with similar methods.
   * @param {string} [opts.locale='system's locale'] - a locale to set on the resulting DateTime instance
   * @param {string} opts.outputCalendar - the output calendar to set on the resulting DateTime instance
   * @param {string} opts.numberingSystem - the numbering system to set on the resulting DateTime instance
   * @example DateTime.fromHTTP('Sun, 06 Nov 1994 08:49:37 GMT')
   * @example DateTime.fromHTTP('Sunday, 06-Nov-94 08:49:37 GMT')
   * @example DateTime.fromHTTP('Sun Nov  6 08:49:37 1994')
   * @return {DateTime}
   */
  static fromHTTP(text, opts = {}) {
    const [vals, parsedZone] = parseHTTPDate(text);
    return parseDataToDateTime(vals, parsedZone, opts, "HTTP", opts);
  }

  /**
   * Create a DateTime from an input string and format string.
   * Defaults to en-US if no locale has been specified, regardless of the system's locale. For a table of tokens and their interpretations, see [here](https://moment.github.io/luxon/#/parsing?id=table-of-tokens).
   * @param {string} text - the string to parse
   * @param {string} fmt - the format the string is expected to be in (see the link below for the formats)
   * @param {Object} opts - options to affect the creation
   * @param {string|Zone} [opts.zone='local'] - use this zone if no offset is specified in the input string itself. Will also convert the DateTime to this zone
   * @param {boolean} [opts.setZone=false] - override the zone with a zone specified in the string itself, if it specifies one
   * @param {string} [opts.locale='en-US'] - a locale string to use when parsing. Will also set the DateTime to this locale
   * @param {string} opts.numberingSystem - the numbering system to use when parsing. Will also set the resulting DateTime to this numbering system
   * @param {string} opts.outputCalendar - the output calendar to set on the resulting DateTime instance
   * @return {DateTime}
   */
  static fromFormat(text, fmt, opts = {}) {
    if (isUndefined(text) || isUndefined(fmt)) {
      throw new InvalidArgumentError("fromFormat requires an input string and a format");
    }

    const { locale = null, numberingSystem = null } = opts,
      localeToUse = Locale.fromOpts({
        locale,
        numberingSystem,
        defaultToEN: true,
      }),
      [vals, parsedZone, specificOffset, invalid] = parseFromTokens(localeToUse, text, fmt);
    if (invalid) {
      return DateTime.invalid(invalid);
    } else {
      return parseDataToDateTime(vals, parsedZone, opts, `format ${fmt}`, text, specificOffset);
    }
  }

  /**
   * @deprecated use fromFormat instead
   */
  static fromString(text, fmt, opts = {}) {
    return DateTime.fromFormat(text, fmt, opts);
  }

  /**
   * Create a DateTime from a SQL date, time, or datetime
   * Defaults to en-US if no locale has been specified, regardless of the system's locale
   * @param {string} text - the string to parse
   * @param {Object} opts - options to affect the creation
   * @param {string|Zone} [opts.zone='local'] - use this zone if no offset is specified in the input string itself. Will also convert the DateTime to this zone
   * @param {boolean} [opts.setZone=false] - override the zone with a zone specified in the string itself, if it specifies one
   * @param {string} [opts.locale='en-US'] - a locale string to use when parsing. Will also set the DateTime to this locale
   * @param {string} opts.numberingSystem - the numbering system to use when parsing. Will also set the resulting DateTime to this numbering system
   * @param {string} opts.outputCalendar - the output calendar to set on the resulting DateTime instance
   * @example DateTime.fromSQL('2017-05-15')
   * @example DateTime.fromSQL('2017-05-15 09:12:34')
   * @example DateTime.fromSQL('2017-05-15 09:12:34.342')
   * @example DateTime.fromSQL('2017-05-15 09:12:34.342+06:00')
   * @example DateTime.fromSQL('2017-05-15 09:12:34.342 America/Los_Angeles')
   * @example DateTime.fromSQL('2017-05-15 09:12:34.342 America/Los_Angeles', { setZone: true })
   * @example DateTime.fromSQL('2017-05-15 09:12:34.342', { zone: 'America/Los_Angeles' })
   * @example DateTime.fromSQL('09:12:34.342')
   * @return {DateTime}
   */
  static fromSQL(text, opts = {}) {
    const [vals, parsedZone] = parseSQL(text);
    return parseDataToDateTime(vals, parsedZone, opts, "SQL", text);
  }

  /**
   * Create an invalid DateTime.
   * @param {string} reason - simple string of why this DateTime is invalid. Should not contain parameters or anything else data-dependent
   * @param {string} [explanation=null] - longer explanation, may include parameters and other useful debugging information
   * @return {DateTime}
   */
  static invalid(reason, explanation = null) {
    if (!reason) {
      throw new InvalidArgumentError("need to specify a reason the DateTime is invalid");
    }

    const invalid = reason instanceof Invalid ? reason : new Invalid(reason, explanation);

    if (Settings.throwOnInvalid) {
      throw new InvalidDateTimeError(invalid);
    } else {
      return new DateTime({ invalid });
    }
  }

  /**
   * Check if an object is an instance of DateTime. Works across context boundaries
   * @param {object} o
   * @return {boolean}
   */
  static isDateTime(o) {
    return (o && o.isLuxonDateTime) || false;
  }

  // INFO

  /**
   * Get the value of unit.
   * @param {string} unit - a unit such as 'minute' or 'day'
   * @example DateTime.local(2017, 7, 4).get('month'); //=> 7
   * @example DateTime.local(2017, 7, 4).get('day'); //=> 4
   * @return {number}
   */
  get(unit) {
    return this[unit];
  }

  /**
   * Returns whether the DateTime is valid. Invalid DateTimes occur when:
   * * The DateTime was created from invalid calendar information, such as the 13th month or February 30
   * * The DateTime was created by an operation on another invalid date
   * @type {boolean}
   */
  get isValid() {
    return this.invalid === null;
  }

  /**
   * Returns an error code if this DateTime is invalid, or null if the DateTime is valid
   * @type {string}
   */
  get invalidReason() {
    return this.invalid ? this.invalid.reason : null;
  }

  /**
   * Returns an explanation of why this DateTime became invalid, or null if the DateTime is valid
   * @type {string}
   */
  get invalidExplanation() {
    return this.invalid ? this.invalid.explanation : null;
  }

  /**
   * Get the locale of a DateTime, such 'en-GB'. The locale is used when formatting the DateTime
   *
   * @type {string}
   */
  get locale() {
    return this.isValid ? this.loc.locale : null;
  }

  /**
   * Get the numbering system of a DateTime, such 'beng'. The numbering system is used when formatting the DateTime
   *
   * @type {string}
   */
  get numberingSystem() {
    return this.isValid ? this.loc.numberingSystem : null;
  }

  /**
   * Get the output calendar of a DateTime, such 'islamic'. The output calendar is used when formatting the DateTime
   *
   * @type {string}
   */
  get outputCalendar() {
    return this.isValid ? this.loc.outputCalendar : null;
  }

  /**
   * Get the time zone associated with this DateTime.
   * @type {Zone}
   */
  get zone() {
    return this._zone;
  }

  /**
   * Get the name of the time zone.
   * @type {string}
   */
  get zoneName() {
    return this.isValid ? this.zone.name : null;
  }

  /**
   * Get the year
   * @example DateTime.local(2017, 5, 25).year //=> 2017
   * @type {number}
   */
  get year() {
    return this.isValid ? this.c.year : NaN;
  }

  /**
   * Get the quarter
   * @example DateTime.local(2017, 5, 25).quarter //=> 2
   * @type {number}
   */
  get quarter() {
    return this.isValid ? Math.ceil(this.c.month / 3) : NaN;
  }

  /**
   * Get the month (1-12).
   * @example DateTime.local(2017, 5, 25).month //=> 5
   * @type {number}
   */
  get month() {
    return this.isValid ? this.c.month : NaN;
  }

  /**
   * Get the day of the month (1-30ish).
   * @example DateTime.local(2017, 5, 25).day //=> 25
   * @type {number}
   */
  get day() {
    return this.isValid ? this.c.day : NaN;
  }

  /**
   * Get the hour of the day (0-23).
   * @example DateTime.local(2017, 5, 25, 9).hour //=> 9
   * @type {number}
   */
  get hour() {
    return this.isValid ? this.c.hour : NaN;
  }

  /**
   * Get the minute of the hour (0-59).
   * @example DateTime.local(2017, 5, 25, 9, 30).minute //=> 30
   * @type {number}
   */
  get minute() {
    return this.isValid ? this.c.minute : NaN;
  }

  /**
   * Get the second of the minute (0-59).
   * @example DateTime.local(2017, 5, 25, 9, 30, 52).second //=> 52
   * @type {number}
   */
  get second() {
    return this.isValid ? this.c.second : NaN;
  }

  /**
   * Get the millisecond of the second (0-999).
   * @example DateTime.local(2017, 5, 25, 9, 30, 52, 654).millisecond //=> 654
   * @type {number}
   */
  get millisecond() {
    return this.isValid ? this.c.millisecond : NaN;
  }

  /**
   * Get the week year
   * @see https://en.wikipedia.org/wiki/ISO_week_date
   * @example DateTime.local(2014, 12, 31).weekYear //=> 2015
   * @type {number}
   */
  get weekYear() {
    return this.isValid ? possiblyCachedWeekData(this).weekYear : NaN;
  }

  /**
   * Get the week number of the week year (1-52ish).
   * @see https://en.wikipedia.org/wiki/ISO_week_date
   * @example DateTime.local(2017, 5, 25).weekNumber //=> 21
   * @type {number}
   */
  get weekNumber() {
    return this.isValid ? possiblyCachedWeekData(this).weekNumber : NaN;
  }

  /**
   * Get the day of the week.
   * 1 is Monday and 7 is Sunday
   * @see https://en.wikipedia.org/wiki/ISO_week_date
   * @example DateTime.local(2014, 11, 31).weekday //=> 4
   * @type {number}
   */
  get weekday() {
    return this.isValid ? possiblyCachedWeekData(this).weekday : NaN;
  }

  /**
   * Get the ordinal (meaning the day of the year)
   * @example DateTime.local(2017, 5, 25).ordinal //=> 145
   * @type {number|DateTime}
   */
  get ordinal() {
    return this.isValid ? gregorianToOrdinal(this.c).ordinal : NaN;
  }

  /**
   * Get the human readable short month name, such as 'Oct'.
   * Defaults to the system's locale if no locale has been specified
   * @example DateTime.local(2017, 10, 30).monthShort //=> Oct
   * @type {string}
   */
  get monthShort() {
    return this.isValid ? Info.months("short", { locObj: this.loc })[this.month - 1] : null;
  }

  /**
   * Get the human readable long month name, such as 'October'.
   * Defaults to the system's locale if no locale has been specified
   * @example DateTime.local(2017, 10, 30).monthLong //=> October
   * @type {string}
   */
  get monthLong() {
    return this.isValid ? Info.months("long", { locObj: this.loc })[this.month - 1] : null;
  }

  /**
   * Get the human readable short weekday, such as 'Mon'.
   * Defaults to the system's locale if no locale has been specified
   * @example DateTime.local(2017, 10, 30).weekdayShort //=> Mon
   * @type {string}
   */
  get weekdayShort() {
    return this.isValid ? Info.weekdays("short", { locObj: this.loc })[this.weekday - 1] : null;
  }

  /**
   * Get the human readable long weekday, such as 'Monday'.
   * Defaults to the system's locale if no locale has been specified
   * @example DateTime.local(2017, 10, 30).weekdayLong //=> Monday
   * @type {string}
   */
  get weekdayLong() {
    return this.isValid ? Info.weekdays("long", { locObj: this.loc })[this.weekday - 1] : null;
  }

  /**
   * Get the UTC offset of this DateTime in minutes
   * @example DateTime.now().offset //=> -240
   * @example DateTime.utc().offset //=> 0
   * @type {number}
   */
  get offset() {
    return this.isValid ? +this.o : NaN;
  }

  /**
   * Get the short human name for the zone's current offset, for example "EST" or "EDT".
   * Defaults to the system's locale if no locale has been specified
   * @type {string}
   */
  get offsetNameShort() {
    if (this.isValid) {
      return this.zone.offsetName(this.ts, {
        format: "short",
        locale: this.locale,
      });
    } else {
      return null;
    }
  }

  /**
   * Get the long human name for the zone's current offset, for example "Eastern Standard Time" or "Eastern Daylight Time".
   * Defaults to the system's locale if no locale has been specified
   * @type {string}
   */
  get offsetNameLong() {
    if (this.isValid) {
      return this.zone.offsetName(this.ts, {
        format: "long",
        locale: this.locale,
      });
    } else {
      return null;
    }
  }

  /**
   * Get whether this zone's offset ever changes, as in a DST.
   * @type {boolean}
   */
  get isOffsetFixed() {
    return this.isValid ? this.zone.isUniversal : null;
  }

  /**
   * Get whether the DateTime is in a DST.
   * @type {boolean}
   */
  get isInDST() {
    if (this.isOffsetFixed) {
      return false;
    } else {
      return (
        this.offset > this.set({ month: 1, day: 1 }).offset ||
        this.offset > this.set({ month: 5 }).offset
      );
    }
  }

  /**
   * Returns true if this DateTime is in a leap year, false otherwise
   * @example DateTime.local(2016).isInLeapYear //=> true
   * @example DateTime.local(2013).isInLeapYear //=> false
   * @type {boolean}
   */
  get isInLeapYear() {
    return isLeapYear(this.year);
  }

  /**
   * Returns the number of days in this DateTime's month
   * @example DateTime.local(2016, 2).daysInMonth //=> 29
   * @example DateTime.local(2016, 3).daysInMonth //=> 31
   * @type {number}
   */
  get daysInMonth() {
    return daysInMonth(this.year, this.month);
  }

  /**
   * Returns the number of days in this DateTime's year
   * @example DateTime.local(2016).daysInYear //=> 366
   * @example DateTime.local(2013).daysInYear //=> 365
   * @type {number}
   */
  get daysInYear() {
    return this.isValid ? daysInYear(this.year) : NaN;
  }

  /**
   * Returns the number of weeks in this DateTime's year
   * @see https://en.wikipedia.org/wiki/ISO_week_date
   * @example DateTime.local(2004).weeksInWeekYear //=> 53
   * @example DateTime.local(2013).weeksInWeekYear //=> 52
   * @type {number}
   */
  get weeksInWeekYear() {
    return this.isValid ? weeksInWeekYear(this.weekYear) : NaN;
  }

  /**
   * Returns the resolved Intl options for this DateTime.
   * This is useful in understanding the behavior of formatting methods
   * @param {Object} opts - the same options as toLocaleString
   * @return {Object}
   */
  resolvedLocaleOptions(opts = {}) {
    const { locale, numberingSystem, calendar } = Formatter.create(
      this.loc.clone(opts),
      opts
    ).resolvedOptions(this);
    return { locale, numberingSystem, outputCalendar: calendar };
  }

  // TRANSFORM

  /**
   * "Set" the DateTime's zone to UTC. Returns a newly-constructed DateTime.
   *
   * Equivalent to {@link DateTime#setZone}('utc')
   * @param {number} [offset=0] - optionally, an offset from UTC in minutes
   * @param {Object} [opts={}] - options to pass to `setZone()`
   * @return {DateTime}
   */
  toUTC(offset = 0, opts = {}) {
    return this.setZone(FixedOffsetZone.instance(offset), opts);
  }

  /**
   * "Set" the DateTime's zone to the host's local zone. Returns a newly-constructed DateTime.
   *
   * Equivalent to `setZone('local')`
   * @return {DateTime}
   */
  toLocal() {
    return this.setZone(Settings.defaultZone);
  }

  /**
   * "Set" the DateTime's zone to specified zone. Returns a newly-constructed DateTime.
   *
   * By default, the setter keeps the underlying time the same (as in, the same timestamp), but the new instance will report different local times and consider DSTs when making computations, as with {@link DateTime#plus}. You may wish to use {@link DateTime#toLocal} and {@link DateTime#toUTC} which provide simple convenience wrappers for commonly used zones.
   * @param {string|Zone} [zone='local'] - a zone identifier. As a string, that can be any IANA zone supported by the host environment, or a fixed-offset name of the form 'UTC+3', or the strings 'local' or 'utc'. You may also supply an instance of a {@link DateTime#Zone} class.
   * @param {Object} opts - options
   * @param {boolean} [opts.keepLocalTime=false] - If true, adjust the underlying time so that the local time stays the same, but in the target zone. You should rarely need this.
   * @return {DateTime}
   */
  setZone(zone, { keepLocalTime = false, keepCalendarTime = false } = {}) {
    zone = normalizeZone(zone, Settings.defaultZone);
    if (zone.equals(this.zone)) {
      return this;
    } else if (!zone.isValid) {
      return DateTime.invalid(unsupportedZone(zone));
    } else {
      let newTS = this.ts;
      if (keepLocalTime || keepCalendarTime) {
        const offsetGuess = zone.offset(this.ts);
        const asObj = this.toObject();
        [newTS] = objToTS(asObj, offsetGuess, zone);
      }
      return clone(this, { ts: newTS, zone });
    }
  }

  /**
   * "Set" the locale, numberingSystem, or outputCalendar. Returns a newly-constructed DateTime.
   * @param {Object} properties - the properties to set
   * @example DateTime.local(2017, 5, 25).reconfigure({ locale: 'en-GB' })
   * @return {DateTime}
   */
  reconfigure({ locale, numberingSystem, outputCalendar } = {}) {
    const loc = this.loc.clone({ locale, numberingSystem, outputCalendar });
    return clone(this, { loc });
  }

  /**
   * "Set" the locale. Returns a newly-constructed DateTime.
   * Just a convenient alias for reconfigure({ locale })
   * @example DateTime.local(2017, 5, 25).setLocale('en-GB')
   * @return {DateTime}
   */
  setLocale(locale) {
    return this.reconfigure({ locale });
  }

  /**
   * "Set" the values of specified units. Returns a newly-constructed DateTime.
   * You can only set units with this method; for "setting" metadata, see {@link DateTime#reconfigure} and {@link DateTime#setZone}.
   * @param {Object} values - a mapping of units to numbers
   * @example dt.set({ year: 2017 })
   * @example dt.set({ hour: 8, minute: 30 })
   * @example dt.set({ weekday: 5 })
   * @example dt.set({ year: 2005, ordinal: 234 })
   * @return {DateTime}
   */
  set(values) {
    if (!this.isValid) return this;

    const normalized = normalizeObject(values, normalizeUnit),
      settingWeekStuff =
        !isUndefined(normalized.weekYear) ||
        !isUndefined(normalized.weekNumber) ||
        !isUndefined(normalized.weekday),
      containsOrdinal = !isUndefined(normalized.ordinal),
      containsGregorYear = !isUndefined(normalized.year),
      containsGregorMD = !isUndefined(normalized.month) || !isUndefined(normalized.day),
      containsGregor = containsGregorYear || containsGregorMD,
      definiteWeekDef = normalized.weekYear || normalized.weekNumber;

    if ((containsGregor || containsOrdinal) && definiteWeekDef) {
      throw new ConflictingSpecificationError(
        "Can't mix weekYear/weekNumber units with year/month/day or ordinals"
      );
    }

    if (containsGregorMD && containsOrdinal) {
      throw new ConflictingSpecificationError("Can't mix ordinal dates with month/day");
    }

    let mixed;
    if (settingWeekStuff) {
      mixed = weekToGregorian({ ...gregorianToWeek(this.c), ...normalized });
    } else if (!isUndefined(normalized.ordinal)) {
      mixed = ordinalToGregorian({ ...gregorianToOrdinal(this.c), ...normalized });
    } else {
      mixed = { ...this.toObject(), ...normalized };

      // if we didn't set the day but we ended up on an overflow date,
      // use the last day of the right month
      if (isUndefined(normalized.day)) {
        mixed.day = Math.min(daysInMonth(mixed.year, mixed.month), mixed.day);
      }
    }

    const [ts, o] = objToTS(mixed, this.o, this.zone);
    return clone(this, { ts, o });
  }

  /**
   * Add a period of time to this DateTime and return the resulting DateTime
   *
   * Adding hours, minutes, seconds, or milliseconds increases the timestamp by the right number of milliseconds. Adding days, months, or years shifts the calendar, accounting for DSTs and leap years along the way. Thus, `dt.plus({ hours: 24 })` may result in a different time than `dt.plus({ days: 1 })` if there's a DST shift in between.
   * @param {Duration|Object|number} duration - The amount to add. Either a Luxon Duration, a number of milliseconds, the object argument to Duration.fromObject()
   * @example DateTime.now().plus(123) //~> in 123 milliseconds
   * @example DateTime.now().plus({ minutes: 15 }) //~> in 15 minutes
   * @example DateTime.now().plus({ days: 1 }) //~> this time tomorrow
   * @example DateTime.now().plus({ days: -1 }) //~> this time yesterday
   * @example DateTime.now().plus({ hours: 3, minutes: 13 }) //~> in 3 hr, 13 min
   * @example DateTime.now().plus(Duration.fromObject({ hours: 3, minutes: 13 })) //~> in 3 hr, 13 min
   * @return {DateTime}
   */
  plus(duration) {
    if (!this.isValid) return this;
    const dur = Duration.fromDurationLike(duration);
    return clone(this, adjustTime(this, dur));
  }

  /**
   * Subtract a period of time to this DateTime and return the resulting DateTime
   * See {@link DateTime#plus}
   * @param {Duration|Object|number} duration - The amount to subtract. Either a Luxon Duration, a number of milliseconds, the object argument to Duration.fromObject()
   @return {DateTime}
   */
  minus(duration) {
    if (!this.isValid) return this;
    const dur = Duration.fromDurationLike(duration).negate();
    return clone(this, adjustTime(this, dur));
  }

  /**
   * "Set" this DateTime to the beginning of a unit of time.
   * @param {string} unit - The unit to go to the beginning of. Can be 'year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second', or 'millisecond'.
   * @example DateTime.local(2014, 3, 3).startOf('month').toISODate(); //=> '2014-03-01'
   * @example DateTime.local(2014, 3, 3).startOf('year').toISODate(); //=> '2014-01-01'
   * @example DateTime.local(2014, 3, 3).startOf('week').toISODate(); //=> '2014-03-03', weeks always start on Mondays
   * @example DateTime.local(2014, 3, 3, 5, 30).startOf('day').toISOTime(); //=> '00:00.000-05:00'
   * @example DateTime.local(2014, 3, 3, 5, 30).startOf('hour').toISOTime(); //=> '05:00:00.000-05:00'
   * @return {DateTime}
   */
  startOf(unit) {
    if (!this.isValid) return this;
    const o = {},
      normalizedUnit = Duration.normalizeUnit(unit);
    switch (normalizedUnit) {
      case "years":
        o.month = 1;
      // falls through
      case "quarters":
      case "months":
        o.day = 1;
      // falls through
      case "weeks":
      case "days":
        o.hour = 0;
      // falls through
      case "hours":
        o.minute = 0;
      // falls through
      case "minutes":
        o.second = 0;
      // falls through
      case "seconds":
        o.millisecond = 0;
        break;
      // no default, invalid units throw in normalizeUnit()
    }

    if (normalizedUnit === "weeks") {
      o.weekday = 1;
    }

    if (normalizedUnit === "quarters") {
      const q = Math.ceil(this.month / 3);
      o.month = (q - 1) * 3 + 1;
    }

    return this.set(o);
  }

  /**
   * "Set" this DateTime to the end (meaning the last millisecond) of a unit of time
   * @param {string} unit - The unit to go to the end of. Can be 'year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second', or 'millisecond'.
   * @example DateTime.local(2014, 3, 3).endOf('month').toISO(); //=> '2014-03-31T23:59:59.999-05:00'
   * @example DateTime.local(2014, 3, 3).endOf('year').toISO(); //=> '2014-12-31T23:59:59.999-05:00'
   * @example DateTime.local(2014, 3, 3).endOf('week').toISO(); // => '2014-03-09T23:59:59.999-05:00', weeks start on Mondays
   * @example DateTime.local(2014, 3, 3, 5, 30).endOf('day').toISO(); //=> '2014-03-03T23:59:59.999-05:00'
   * @example DateTime.local(2014, 3, 3, 5, 30).endOf('hour').toISO(); //=> '2014-03-03T05:59:59.999-05:00'
   * @return {DateTime}
   */
  endOf(unit) {
    return this.isValid
      ? this.plus({ [unit]: 1 })
          .startOf(unit)
          .minus(1)
      : this;
  }

  // OUTPUT

  /**
   * Returns a string representation of this DateTime formatted according to the specified format string.
   * **You may not want this.** See {@link DateTime#toLocaleString} for a more flexible formatting tool. For a table of tokens and their interpretations, see [here](https://moment.github.io/luxon/#/formatting?id=table-of-tokens).
   * Defaults to en-US if no locale has been specified, regardless of the system's locale.
   * @param {string} fmt - the format string
   * @param {Object} opts - opts to override the configuration options on this DateTime
   * @example DateTime.now().toFormat('yyyy LLL dd') //=> '2017 Apr 22'
   * @example DateTime.now().setLocale('fr').toFormat('yyyy LLL dd') //=> '2017 avr. 22'
   * @example DateTime.now().toFormat('yyyy LLL dd', { locale: "fr" }) //=> '2017 avr. 22'
   * @example DateTime.now().toFormat("HH 'hours and' mm 'minutes'") //=> '20 hours and 55 minutes'
   * @return {string}
   */
  toFormat(fmt, opts = {}) {
    return this.isValid
      ? Formatter.create(this.loc.redefaultToEN(opts)).formatDateTimeFromString(this, fmt)
      : INVALID;
  }

  /**
   * Returns a localized string representing this date. Accepts the same options as the Intl.DateTimeFormat constructor and any presets defined by Luxon, such as `DateTime.DATE_FULL` or `DateTime.TIME_SIMPLE`.
   * The exact behavior of this method is browser-specific, but in general it will return an appropriate representation
   * of the DateTime in the assigned locale.
   * Defaults to the system's locale if no locale has been specified
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
   * @param formatOpts {Object} - Intl.DateTimeFormat constructor options and configuration options
   * @param {Object} opts - opts to override the configuration options on this DateTime
   * @example DateTime.now().toLocaleString(); //=> 4/20/2017
   * @example DateTime.now().setLocale('en-gb').toLocaleString(); //=> '20/04/2017'
   * @example DateTime.now().toLocaleString({ locale: 'en-gb' }); //=> '20/04/2017'
   * @example DateTime.now().toLocaleString(DateTime.DATE_FULL); //=> 'April 20, 2017'
   * @example DateTime.now().toLocaleString(DateTime.TIME_SIMPLE); //=> '11:32 AM'
   * @example DateTime.now().toLocaleString(DateTime.DATETIME_SHORT); //=> '4/20/2017, 11:32 AM'
   * @example DateTime.now().toLocaleString({ weekday: 'long', month: 'long', day: '2-digit' }); //=> 'Thursday, April 20'
   * @example DateTime.now().toLocaleString({ weekday: 'short', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }); //=> 'Thu, Apr 20, 11:27 AM'
   * @example DateTime.now().toLocaleString({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }); //=> '11:32'
   * @return {string}
   */
  toLocaleString(formatOpts = DATE_SHORT, opts = {}) {
    return this.isValid
      ? Formatter.create(this.loc.clone(opts), formatOpts).formatDateTime(this)
      : INVALID;
  }

  /**
   * Returns an array of format "parts", meaning individual tokens along with metadata. This is allows callers to post-process individual sections of the formatted output.
   * Defaults to the system's locale if no locale has been specified
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat/formatToParts
   * @param opts {Object} - Intl.DateTimeFormat constructor options, same as `toLocaleString`.
   * @example DateTime.now().toLocaleParts(); //=> [
   *                                   //=>   { type: 'day', value: '25' },
   *                                   //=>   { type: 'literal', value: '/' },
   *                                   //=>   { type: 'month', value: '05' },
   *                                   //=>   { type: 'literal', value: '/' },
   *                                   //=>   { type: 'year', value: '1982' }
   *                                   //=> ]
   */
  toLocaleParts(opts = {}) {
    return this.isValid
      ? Formatter.create(this.loc.clone(opts), opts).formatDateTimeParts(this)
      : [];
  }

  /**
   * Returns an ISO 8601-compliant string representation of this DateTime
   * @param {Object} opts - options
   * @param {boolean} [opts.suppressMilliseconds=false] - exclude milliseconds from the format if they're 0
   * @param {boolean} [opts.suppressSeconds=false] - exclude seconds from the format if they're 0
   * @param {boolean} [opts.includeOffset=true] - include the offset, such as 'Z' or '-04:00'
   * @param {boolean} [opts.extendedZone=true] - add the time zone format extension
   * @param {string} [opts.format='extended'] - choose between the basic and extended format
   * @example DateTime.utc(1983, 5, 25).toISO() //=> '1982-05-25T00:00:00.000Z'
   * @example DateTime.now().toISO() //=> '2017-04-22T20:47:05.335-04:00'
   * @example DateTime.now().toISO({ includeOffset: false }) //=> '2017-04-22T20:47:05.335'
   * @example DateTime.now().toISO({ format: 'basic' }) //=> '20170422T204705.335-0400'
   * @return {string}
   */
  toISO({
    format = "extended",
    suppressSeconds = false,
    suppressMilliseconds = false,
    includeOffset = true,
    extendedZone = false,
  } = {}) {
    if (!this.isValid) {
      return null;
    }

    const ext = format === "extended";

    let c = toISODate(this, ext);
    c += "T";
    c += toISOTime(this, ext, suppressSeconds, suppressMilliseconds, includeOffset, extendedZone);
    return c;
  }

  /**
   * Returns an ISO 8601-compliant string representation of this DateTime's date component
   * @param {Object} opts - options
   * @param {string} [opts.format='extended'] - choose between the basic and extended format
   * @example DateTime.utc(1982, 5, 25).toISODate() //=> '1982-05-25'
   * @example DateTime.utc(1982, 5, 25).toISODate({ format: 'basic' }) //=> '19820525'
   * @return {string}
   */
  toISODate({ format = "extended" } = {}) {
    if (!this.isValid) {
      return null;
    }

    return toISODate(this, format === "extended");
  }

  /**
   * Returns an ISO 8601-compliant string representation of this DateTime's week date
   * @example DateTime.utc(1982, 5, 25).toISOWeekDate() //=> '1982-W21-2'
   * @return {string}
   */
  toISOWeekDate() {
    return toTechFormat(this, "kkkk-'W'WW-c");
  }

  /**
   * Returns an ISO 8601-compliant string representation of this DateTime's time component
   * @param {Object} opts - options
   * @param {boolean} [opts.suppressMilliseconds=false] - exclude milliseconds from the format if they're 0
   * @param {boolean} [opts.suppressSeconds=false] - exclude seconds from the format if they're 0
   * @param {boolean} [opts.includeOffset=true] - include the offset, such as 'Z' or '-04:00'
   * @param {boolean} [opts.extendedZone=true] - add the time zone format extension
   * @param {boolean} [opts.includePrefix=false] - include the `T` prefix
   * @param {string} [opts.format='extended'] - choose between the basic and extended format
   * @example DateTime.utc().set({ hour: 7, minute: 34 }).toISOTime() //=> '07:34:19.361Z'
   * @example DateTime.utc().set({ hour: 7, minute: 34, seconds: 0, milliseconds: 0 }).toISOTime({ suppressSeconds: true }) //=> '07:34Z'
   * @example DateTime.utc().set({ hour: 7, minute: 34 }).toISOTime({ format: 'basic' }) //=> '073419.361Z'
   * @example DateTime.utc().set({ hour: 7, minute: 34 }).toISOTime({ includePrefix: true }) //=> 'T07:34:19.361Z'
   * @return {string}
   */
  toISOTime({
    suppressMilliseconds = false,
    suppressSeconds = false,
    includeOffset = true,
    includePrefix = false,
    extendedZone = false,
    format = "extended",
  } = {}) {
    if (!this.isValid) {
      return null;
    }

    let c = includePrefix ? "T" : "";
    return (
      c +
      toISOTime(
        this,
        format === "extended",
        suppressSeconds,
        suppressMilliseconds,
        includeOffset,
        extendedZone
      )
    );
  }

  /**
   * Returns an RFC 2822-compatible string representation of this DateTime
   * @example DateTime.utc(2014, 7, 13).toRFC2822() //=> 'Sun, 13 Jul 2014 00:00:00 +0000'
   * @example DateTime.local(2014, 7, 13).toRFC2822() //=> 'Sun, 13 Jul 2014 00:00:00 -0400'
   * @return {string}
   */
  toRFC2822() {
    return toTechFormat(this, "EEE, dd LLL yyyy HH:mm:ss ZZZ", false);
  }

  /**
   * Returns a string representation of this DateTime appropriate for use in HTTP headers. The output is always expressed in GMT.
   * Specifically, the string conforms to RFC 1123.
   * @see https://www.w3.org/Protocols/rfc2616/rfc2616-sec3.html#sec3.3.1
   * @example DateTime.utc(2014, 7, 13).toHTTP() //=> 'Sun, 13 Jul 2014 00:00:00 GMT'
   * @example DateTime.utc(2014, 7, 13, 19).toHTTP() //=> 'Sun, 13 Jul 2014 19:00:00 GMT'
   * @return {string}
   */
  toHTTP() {
    return toTechFormat(this.toUTC(), "EEE, dd LLL yyyy HH:mm:ss 'GMT'");
  }

  /**
   * Returns a string representation of this DateTime appropriate for use in SQL Date
   * @example DateTime.utc(2014, 7, 13).toSQLDate() //=> '2014-07-13'
   * @return {string}
   */
  toSQLDate() {
    if (!this.isValid) {
      return null;
    }
    return toISODate(this, true);
  }

  /**
   * Returns a string representation of this DateTime appropriate for use in SQL Time
   * @param {Object} opts - options
   * @param {boolean} [opts.includeZone=false] - include the zone, such as 'America/New_York'. Overrides includeOffset.
   * @param {boolean} [opts.includeOffset=true] - include the offset, such as 'Z' or '-04:00'
   * @param {boolean} [opts.includeOffsetSpace=true] - include the space between the time and the offset, such as '05:15:16.345 -04:00'
   * @example DateTime.utc().toSQL() //=> '05:15:16.345'
   * @example DateTime.now().toSQL() //=> '05:15:16.345 -04:00'
   * @example DateTime.now().toSQL({ includeOffset: false }) //=> '05:15:16.345'
   * @example DateTime.now().toSQL({ includeZone: false }) //=> '05:15:16.345 America/New_York'
   * @return {string}
   */
  toSQLTime({ includeOffset = true, includeZone = false, includeOffsetSpace = true } = {}) {
    let fmt = "HH:mm:ss.SSS";

    if (includeZone || includeOffset) {
      if (includeOffsetSpace) {
        fmt += " ";
      }
      if (includeZone) {
        fmt += "z";
      } else if (includeOffset) {
        fmt += "ZZ";
      }
    }

    return toTechFormat(this, fmt, true);
  }

  /**
   * Returns a string representation of this DateTime appropriate for use in SQL DateTime
   * @param {Object} opts - options
   * @param {boolean} [opts.includeZone=false] - include the zone, such as 'America/New_York'. Overrides includeOffset.
   * @param {boolean} [opts.includeOffset=true] - include the offset, such as 'Z' or '-04:00'
   * @param {boolean} [opts.includeOffsetSpace=true] - include the space between the time and the offset, such as '05:15:16.345 -04:00'
   * @example DateTime.utc(2014, 7, 13).toSQL() //=> '2014-07-13 00:00:00.000 Z'
   * @example DateTime.local(2014, 7, 13).toSQL() //=> '2014-07-13 00:00:00.000 -04:00'
   * @example DateTime.local(2014, 7, 13).toSQL({ includeOffset: false }) //=> '2014-07-13 00:00:00.000'
   * @example DateTime.local(2014, 7, 13).toSQL({ includeZone: true }) //=> '2014-07-13 00:00:00.000 America/New_York'
   * @return {string}
   */
  toSQL(opts = {}) {
    if (!this.isValid) {
      return null;
    }

    return `${this.toSQLDate()} ${this.toSQLTime(opts)}`;
  }

  /**
   * Returns a string representation of this DateTime appropriate for debugging
   * @return {string}
   */
  toString() {
    return this.isValid ? this.toISO() : INVALID;
  }

  /**
   * Returns the epoch milliseconds of this DateTime. Alias of {@link DateTime#toMillis}
   * @return {number}
   */
  valueOf() {
    return this.toMillis();
  }

  /**
   * Returns the epoch milliseconds of this DateTime.
   * @return {number}
   */
  toMillis() {
    return this.isValid ? this.ts : NaN;
  }

  /**
   * Returns the epoch seconds of this DateTime.
   * @return {number}
   */
  toSeconds() {
    return this.isValid ? this.ts / 1000 : NaN;
  }

  /**
   * Returns the epoch seconds (as a whole number) of this DateTime.
   * @return {number}
   */
  toUnixInteger() {
    return this.isValid ? Math.floor(this.ts / 1000) : NaN;
  }

  /**
   * Returns an ISO 8601 representation of this DateTime appropriate for use in JSON.
   * @return {string}
   */
  toJSON() {
    return this.toISO();
  }

  /**
   * Returns a BSON serializable equivalent to this DateTime.
   * @return {Date}
   */
  toBSON() {
    return this.toJSDate();
  }

  /**
   * Returns a JavaScript object with this DateTime's year, month, day, and so on.
   * @param opts - options for generating the object
   * @param {boolean} [opts.includeConfig=false] - include configuration attributes in the output
   * @example DateTime.now().toObject() //=> { year: 2017, month: 4, day: 22, hour: 20, minute: 49, second: 42, millisecond: 268 }
   * @return {Object}
   */
  toObject(opts = {}) {
    if (!this.isValid) return {};

    const base = { ...this.c };

    if (opts.includeConfig) {
      base.outputCalendar = this.outputCalendar;
      base.numberingSystem = this.loc.numberingSystem;
      base.locale = this.loc.locale;
    }
    return base;
  }

  /**
   * Returns a JavaScript Date equivalent to this DateTime.
   * @return {Date}
   */
  toJSDate() {
    return new Date(this.isValid ? this.ts : NaN);
  }

  // COMPARE

  /**
   * Return the difference between two DateTimes as a Duration.
   * @param {DateTime} otherDateTime - the DateTime to compare this one to
   * @param {string|string[]} [unit=['milliseconds']] - the unit or array of units (such as 'hours' or 'days') to include in the duration.
   * @param {Object} opts - options that affect the creation of the Duration
   * @param {string} [opts.conversionAccuracy='casual'] - the conversion system to use
   * @example
   * var i1 = DateTime.fromISO('1982-05-25T09:45'),
   *     i2 = DateTime.fromISO('1983-10-14T10:30');
   * i2.diff(i1).toObject() //=> { milliseconds: 43807500000 }
   * i2.diff(i1, 'hours').toObject() //=> { hours: 12168.75 }
   * i2.diff(i1, ['months', 'days']).toObject() //=> { months: 16, days: 19.03125 }
   * i2.diff(i1, ['months', 'days', 'hours']).toObject() //=> { months: 16, days: 19, hours: 0.75 }
   * @return {Duration}
   */
  diff(otherDateTime, unit = "milliseconds", opts = {}) {
    if (!this.isValid || !otherDateTime.isValid) {
      return Duration.invalid("created by diffing an invalid DateTime");
    }

    const durOpts = { locale: this.locale, numberingSystem: this.numberingSystem, ...opts };

    const units = maybeArray(unit).map(Duration.normalizeUnit),
      otherIsLater = otherDateTime.valueOf() > this.valueOf(),
      earlier = otherIsLater ? this : otherDateTime,
      later = otherIsLater ? otherDateTime : this,
      diffed = diff(earlier, later, units, durOpts);

    return otherIsLater ? diffed.negate() : diffed;
  }

  /**
   * Return the difference between this DateTime and right now.
   * See {@link DateTime#diff}
   * @param {string|string[]} [unit=['milliseconds']] - the unit or units units (such as 'hours' or 'days') to include in the duration
   * @param {Object} opts - options that affect the creation of the Duration
   * @param {string} [opts.conversionAccuracy='casual'] - the conversion system to use
   * @return {Duration}
   */
  diffNow(unit = "milliseconds", opts = {}) {
    return this.diff(DateTime.now(), unit, opts);
  }

  /**
   * Return an Interval spanning between this DateTime and another DateTime
   * @param {DateTime} otherDateTime - the other end point of the Interval
   * @return {Interval}
   */
  until(otherDateTime) {
    return this.isValid ? Interval.fromDateTimes(this, otherDateTime) : this;
  }

  /**
   * Return whether this DateTime is in the same unit of time as another DateTime.
   * Higher-order units must also be identical for this function to return `true`.
   * Note that time zones are **ignored** in this comparison, which compares the **local** calendar time. Use {@link DateTime#setZone} to convert one of the dates if needed.
   * @param {DateTime} otherDateTime - the other DateTime
   * @param {string} unit - the unit of time to check sameness on
   * @example DateTime.now().hasSame(otherDT, 'day'); //~> true if otherDT is in the same current calendar day
   * @return {boolean}
   */
  hasSame(otherDateTime, unit) {
    if (!this.isValid) return false;

    const inputMs = otherDateTime.valueOf();
    const adjustedToZone = this.setZone(otherDateTime.zone, { keepLocalTime: true });
    return adjustedToZone.startOf(unit) <= inputMs && inputMs <= adjustedToZone.endOf(unit);
  }

  /**
   * Equality check
   * Two DateTimes are equal iff they represent the same millisecond, have the same zone and location, and are both valid.
   * To compare just the millisecond values, use `+dt1 === +dt2`.
   * @param {DateTime} other - the other DateTime
   * @return {boolean}
   */
  equals(other) {
    return (
      this.isValid &&
      other.isValid &&
      this.valueOf() === other.valueOf() &&
      this.zone.equals(other.zone) &&
      this.loc.equals(other.loc)
    );
  }

  /**
   * Returns a string representation of a this time relative to now, such as "in two days". Can only internationalize if your
   * platform supports Intl.RelativeTimeFormat. Rounds down by default.
   * @param {Object} options - options that affect the output
   * @param {DateTime} [options.base=DateTime.now()] - the DateTime to use as the basis to which this time is compared. Defaults to now.
   * @param {string} [options.style="long"] - the style of units, must be "long", "short", or "narrow"
   * @param {string|string[]} options.unit - use a specific unit or array of units; if omitted, or an array, the method will pick the best unit. Use an array or one of "years", "quarters", "months", "weeks", "days", "hours", "minutes", or "seconds"
   * @param {boolean} [options.round=true] - whether to round the numbers in the output.
   * @param {number} [options.padding=0] - padding in milliseconds. This allows you to round up the result if it fits inside the threshold. Don't use in combination with {round: false} because the decimal output will include the padding.
   * @param {string} options.locale - override the locale of this DateTime
   * @param {string} options.numberingSystem - override the numberingSystem of this DateTime. The Intl system may choose not to honor this
   * @example DateTime.now().plus({ days: 1 }).toRelative() //=> "in 1 day"
   * @example DateTime.now().setLocale("es").toRelative({ days: 1 }) //=> "dentro de 1 día"
   * @example DateTime.now().plus({ days: 1 }).toRelative({ locale: "fr" }) //=> "dans 23 heures"
   * @example DateTime.now().minus({ days: 2 }).toRelative() //=> "2 days ago"
   * @example DateTime.now().minus({ days: 2 }).toRelative({ unit: "hours" }) //=> "48 hours ago"
   * @example DateTime.now().minus({ hours: 36 }).toRelative({ round: false }) //=> "1.5 days ago"
   */
  toRelative(options = {}) {
    if (!this.isValid) return null;
    const base = options.base || DateTime.fromObject({}, { zone: this.zone }),
      padding = options.padding ? (this < base ? -options.padding : options.padding) : 0;
    let units = ["years", "months", "days", "hours", "minutes", "seconds"];
    let unit = options.unit;
    if (Array.isArray(options.unit)) {
      units = options.unit;
      unit = undefined;
    }
    return diffRelative(base, this.plus(padding), {
      ...options,
      numeric: "always",
      units,
      unit,
    });
  }

  /**
   * Returns a string representation of this date relative to today, such as "yesterday" or "next month".
   * Only internationalizes on platforms that supports Intl.RelativeTimeFormat.
   * @param {Object} options - options that affect the output
   * @param {DateTime} [options.base=DateTime.now()] - the DateTime to use as the basis to which this time is compared. Defaults to now.
   * @param {string} options.locale - override the locale of this DateTime
   * @param {string} options.unit - use a specific unit; if omitted, the method will pick the unit. Use one of "years", "quarters", "months", "weeks", or "days"
   * @param {string} options.numberingSystem - override the numberingSystem of this DateTime. The Intl system may choose not to honor this
   * @example DateTime.now().plus({ days: 1 }).toRelativeCalendar() //=> "tomorrow"
   * @example DateTime.now().setLocale("es").plus({ days: 1 }).toRelative() //=> ""mañana"
   * @example DateTime.now().plus({ days: 1 }).toRelativeCalendar({ locale: "fr" }) //=> "demain"
   * @example DateTime.now().minus({ days: 2 }).toRelativeCalendar() //=> "2 days ago"
   */
  toRelativeCalendar(options = {}) {
    if (!this.isValid) return null;

    return diffRelative(options.base || DateTime.fromObject({}, { zone: this.zone }), this, {
      ...options,
      numeric: "auto",
      units: ["years", "months", "days"],
      calendary: true,
    });
  }

  /**
   * Return the min of several date times
   * @param {...DateTime} dateTimes - the DateTimes from which to choose the minimum
   * @return {DateTime} the min DateTime, or undefined if called with no argument
   */
  static min(...dateTimes) {
    if (!dateTimes.every(DateTime.isDateTime)) {
      throw new InvalidArgumentError("min requires all arguments be DateTimes");
    }
    return bestBy(dateTimes, (i) => i.valueOf(), Math.min);
  }

  /**
   * Return the max of several date times
   * @param {...DateTime} dateTimes - the DateTimes from which to choose the maximum
   * @return {DateTime} the max DateTime, or undefined if called with no argument
   */
  static max(...dateTimes) {
    if (!dateTimes.every(DateTime.isDateTime)) {
      throw new InvalidArgumentError("max requires all arguments be DateTimes");
    }
    return bestBy(dateTimes, (i) => i.valueOf(), Math.max);
  }

  // MISC

  /**
   * Explain how a string would be parsed by fromFormat()
   * @param {string} text - the string to parse
   * @param {string} fmt - the format the string is expected to be in (see description)
   * @param {Object} options - options taken by fromFormat()
   * @return {Object}
   */
  static fromFormatExplain(text, fmt, options = {}) {
    const { locale = null, numberingSystem = null } = options,
      localeToUse = Locale.fromOpts({
        locale,
        numberingSystem,
        defaultToEN: true,
      });
    return explainFromTokens(localeToUse, text, fmt);
  }

  /**
   * @deprecated use fromFormatExplain instead
   */
  static fromStringExplain(text, fmt, options = {}) {
    return DateTime.fromFormatExplain(text, fmt, options);
  }

  // FORMAT PRESETS

  /**
   * {@link DateTime#toLocaleString} format like 10/14/1983
   * @type {Object}
   */
  static get DATE_SHORT() {
    return DATE_SHORT;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'Oct 14, 1983'
   * @type {Object}
   */
  static get DATE_MED() {
    return DATE_MED;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'Fri, Oct 14, 1983'
   * @type {Object}
   */
  static get DATE_MED_WITH_WEEKDAY() {
    return DATE_MED_WITH_WEEKDAY;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'October 14, 1983'
   * @type {Object}
   */
  static get DATE_FULL() {
    return DATE_FULL;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'Tuesday, October 14, 1983'
   * @type {Object}
   */
  static get DATE_HUGE() {
    return DATE_HUGE;
  }

  /**
   * {@link DateTime#toLocaleString} format like '09:30 AM'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get TIME_SIMPLE() {
    return TIME_SIMPLE;
  }

  /**
   * {@link DateTime#toLocaleString} format like '09:30:23 AM'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get TIME_WITH_SECONDS() {
    return TIME_WITH_SECONDS;
  }

  /**
   * {@link DateTime#toLocaleString} format like '09:30:23 AM EDT'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get TIME_WITH_SHORT_OFFSET() {
    return TIME_WITH_SHORT_OFFSET;
  }

  /**
   * {@link DateTime#toLocaleString} format like '09:30:23 AM Eastern Daylight Time'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get TIME_WITH_LONG_OFFSET() {
    return TIME_WITH_LONG_OFFSET;
  }

  /**
   * {@link DateTime#toLocaleString} format like '09:30', always 24-hour.
   * @type {Object}
   */
  static get TIME_24_SIMPLE() {
    return TIME_24_SIMPLE;
  }

  /**
   * {@link DateTime#toLocaleString} format like '09:30:23', always 24-hour.
   * @type {Object}
   */
  static get TIME_24_WITH_SECONDS() {
    return TIME_24_WITH_SECONDS;
  }

  /**
   * {@link DateTime#toLocaleString} format like '09:30:23 EDT', always 24-hour.
   * @type {Object}
   */
  static get TIME_24_WITH_SHORT_OFFSET() {
    return TIME_24_WITH_SHORT_OFFSET;
  }

  /**
   * {@link DateTime#toLocaleString} format like '09:30:23 Eastern Daylight Time', always 24-hour.
   * @type {Object}
   */
  static get TIME_24_WITH_LONG_OFFSET() {
    return TIME_24_WITH_LONG_OFFSET;
  }

  /**
   * {@link DateTime#toLocaleString} format like '10/14/1983, 9:30 AM'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_SHORT() {
    return DATETIME_SHORT;
  }

  /**
   * {@link DateTime#toLocaleString} format like '10/14/1983, 9:30:33 AM'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_SHORT_WITH_SECONDS() {
    return DATETIME_SHORT_WITH_SECONDS;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'Oct 14, 1983, 9:30 AM'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_MED() {
    return DATETIME_MED;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'Oct 14, 1983, 9:30:33 AM'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_MED_WITH_SECONDS() {
    return DATETIME_MED_WITH_SECONDS;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'Fri, 14 Oct 1983, 9:30 AM'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_MED_WITH_WEEKDAY() {
    return DATETIME_MED_WITH_WEEKDAY;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'October 14, 1983, 9:30 AM EDT'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_FULL() {
    return DATETIME_FULL;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'October 14, 1983, 9:30:33 AM EDT'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_FULL_WITH_SECONDS() {
    return DATETIME_FULL_WITH_SECONDS;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'Friday, October 14, 1983, 9:30 AM Eastern Daylight Time'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_HUGE() {
    return DATETIME_HUGE;
  }

  /**
   * {@link DateTime#toLocaleString} format like 'Friday, October 14, 1983, 9:30:33 AM Eastern Daylight Time'. Only 12-hour if the locale is.
   * @type {Object}
   */
  static get DATETIME_HUGE_WITH_SECONDS() {
    return DATETIME_HUGE_WITH_SECONDS;
  }
}

/**
 * @private
 */
function friendlyDateTime(dateTimeish) {
  if (DateTime.isDateTime(dateTimeish)) {
    return dateTimeish;
  } else if (dateTimeish && dateTimeish.valueOf && isNumber(dateTimeish.valueOf())) {
    return DateTime.fromJSDate(dateTimeish);
  } else if (dateTimeish && typeof dateTimeish === "object") {
    return DateTime.fromObject(dateTimeish);
  } else {
    throw new InvalidArgumentError(
      `Unknown datetime argument: ${dateTimeish}, of type ${typeof dateTimeish}`
    );
  }
}

const useFormRules = () => {
    const isDate = (v) => !v || !isNaN(Date.parse(v));
    const pastDate = (v) => {
        return isPastDate(v) || 'Invalid Past Date';
    };
    const isPastDate = (v) => {
        const dateTime = DateTime.fromISO(v);
        return dateTime.isValid && dateTime < DateTime.now();
    };
    const requiredField = (v) => {
        return (!!v && !!v.toString().trim());
    };
    const requiredNumberField = (v) => {
        return (!!v && !!Number(v));
    };
    const regInd = (v) => {
        return (!v || /^\d{3}$/.test(v));
    };
    const shortTelephone = (v) => {
        return (!v || /^\d{3}\d{4}$/.test(v));
    };
    const email = (v) => {
        return (!v || /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(v));
    };
    const nam = (v) => {
        return (!v || /^[A-Z]{4}\d{6}[A-HJ-NP-Z1-9]\d$/g.test(v));
    };
    const namExpirationDate = (v) => {
        if (!v) {
            return true;
        }
        const regex = /^(\d{4})(\d{2})$/;
        const parts = regex.exec(v);
        if (parts) {
            if (parseInt(parts[2]) >= 1 && parseInt(parts[2]) <= 12) {
                return true;
            }
        }
        return false;
    };
    const postalCode = (v) => {
        return !v || /^([abceghjklmnprstvxy][0-9][abceghjklmnprstvwxyz][0-9][abceghjklmnprstvwxyz][0-9])$/i.test(v) || /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(v);
    };
    const formatDate = (date, defaultFormat = 'dd-MM-yyyy') => {
        if (date) {
            const dateTime = DateTime.fromISO(date);
            return dateTime.toFormat(defaultFormat);
        }
        return '';
    };
    const formatNam = (nam) => {
        if (nam) {
            const n = nam.match(/^([A-Z]{4})(\d{4})(\d\d[A-Z0-9]\d)$/);
            if (n) {
                return `${n[1]} ${n[2]} ${n[3]}`;
            }
        }
        return '';
    };
    return {
        isDate,
        pastDate,
        isPastDate,
        requiredField,
        regInd,
        shortTelephone,
        email,
        nam,
        namExpirationDate,
        postalCode,
        formatDate,
        formatNam,
        requiredNumberField,
    };
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var lodash = {exports: {}};

/**
 * @license
 * Lodash <https://lodash.com/>
 * Copyright OpenJS Foundation and other contributors <https://openjsf.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */
lodash.exports;

(function (module, exports) {
(function() {

	  /** Used as a safe reference for `undefined` in pre-ES5 environments. */
	  var undefined$1;

	  /** Used as the semantic version number. */
	  var VERSION = '4.17.21';

	  /** Used as the size to enable large array optimizations. */
	  var LARGE_ARRAY_SIZE = 200;

	  /** Error message constants. */
	  var CORE_ERROR_TEXT = 'Unsupported core-js use. Try https://npms.io/search?q=ponyfill.',
	      FUNC_ERROR_TEXT = 'Expected a function',
	      INVALID_TEMPL_VAR_ERROR_TEXT = 'Invalid `variable` option passed into `_.template`';

	  /** Used to stand-in for `undefined` hash values. */
	  var HASH_UNDEFINED = '__lodash_hash_undefined__';

	  /** Used as the maximum memoize cache size. */
	  var MAX_MEMOIZE_SIZE = 500;

	  /** Used as the internal argument placeholder. */
	  var PLACEHOLDER = '__lodash_placeholder__';

	  /** Used to compose bitmasks for cloning. */
	  var CLONE_DEEP_FLAG = 1,
	      CLONE_FLAT_FLAG = 2,
	      CLONE_SYMBOLS_FLAG = 4;

	  /** Used to compose bitmasks for value comparisons. */
	  var COMPARE_PARTIAL_FLAG = 1,
	      COMPARE_UNORDERED_FLAG = 2;

	  /** Used to compose bitmasks for function metadata. */
	  var WRAP_BIND_FLAG = 1,
	      WRAP_BIND_KEY_FLAG = 2,
	      WRAP_CURRY_BOUND_FLAG = 4,
	      WRAP_CURRY_FLAG = 8,
	      WRAP_CURRY_RIGHT_FLAG = 16,
	      WRAP_PARTIAL_FLAG = 32,
	      WRAP_PARTIAL_RIGHT_FLAG = 64,
	      WRAP_ARY_FLAG = 128,
	      WRAP_REARG_FLAG = 256,
	      WRAP_FLIP_FLAG = 512;

	  /** Used as default options for `_.truncate`. */
	  var DEFAULT_TRUNC_LENGTH = 30,
	      DEFAULT_TRUNC_OMISSION = '...';

	  /** Used to detect hot functions by number of calls within a span of milliseconds. */
	  var HOT_COUNT = 800,
	      HOT_SPAN = 16;

	  /** Used to indicate the type of lazy iteratees. */
	  var LAZY_FILTER_FLAG = 1,
	      LAZY_MAP_FLAG = 2,
	      LAZY_WHILE_FLAG = 3;

	  /** Used as references for various `Number` constants. */
	  var INFINITY = 1 / 0,
	      MAX_SAFE_INTEGER = 9007199254740991,
	      MAX_INTEGER = 1.7976931348623157e+308,
	      NAN = 0 / 0;

	  /** Used as references for the maximum length and index of an array. */
	  var MAX_ARRAY_LENGTH = 4294967295,
	      MAX_ARRAY_INDEX = MAX_ARRAY_LENGTH - 1,
	      HALF_MAX_ARRAY_LENGTH = MAX_ARRAY_LENGTH >>> 1;

	  /** Used to associate wrap methods with their bit flags. */
	  var wrapFlags = [
	    ['ary', WRAP_ARY_FLAG],
	    ['bind', WRAP_BIND_FLAG],
	    ['bindKey', WRAP_BIND_KEY_FLAG],
	    ['curry', WRAP_CURRY_FLAG],
	    ['curryRight', WRAP_CURRY_RIGHT_FLAG],
	    ['flip', WRAP_FLIP_FLAG],
	    ['partial', WRAP_PARTIAL_FLAG],
	    ['partialRight', WRAP_PARTIAL_RIGHT_FLAG],
	    ['rearg', WRAP_REARG_FLAG]
	  ];

	  /** `Object#toString` result references. */
	  var argsTag = '[object Arguments]',
	      arrayTag = '[object Array]',
	      asyncTag = '[object AsyncFunction]',
	      boolTag = '[object Boolean]',
	      dateTag = '[object Date]',
	      domExcTag = '[object DOMException]',
	      errorTag = '[object Error]',
	      funcTag = '[object Function]',
	      genTag = '[object GeneratorFunction]',
	      mapTag = '[object Map]',
	      numberTag = '[object Number]',
	      nullTag = '[object Null]',
	      objectTag = '[object Object]',
	      promiseTag = '[object Promise]',
	      proxyTag = '[object Proxy]',
	      regexpTag = '[object RegExp]',
	      setTag = '[object Set]',
	      stringTag = '[object String]',
	      symbolTag = '[object Symbol]',
	      undefinedTag = '[object Undefined]',
	      weakMapTag = '[object WeakMap]',
	      weakSetTag = '[object WeakSet]';

	  var arrayBufferTag = '[object ArrayBuffer]',
	      dataViewTag = '[object DataView]',
	      float32Tag = '[object Float32Array]',
	      float64Tag = '[object Float64Array]',
	      int8Tag = '[object Int8Array]',
	      int16Tag = '[object Int16Array]',
	      int32Tag = '[object Int32Array]',
	      uint8Tag = '[object Uint8Array]',
	      uint8ClampedTag = '[object Uint8ClampedArray]',
	      uint16Tag = '[object Uint16Array]',
	      uint32Tag = '[object Uint32Array]';

	  /** Used to match empty string literals in compiled template source. */
	  var reEmptyStringLeading = /\b__p \+= '';/g,
	      reEmptyStringMiddle = /\b(__p \+=) '' \+/g,
	      reEmptyStringTrailing = /(__e\(.*?\)|\b__t\)) \+\n'';/g;

	  /** Used to match HTML entities and HTML characters. */
	  var reEscapedHtml = /&(?:amp|lt|gt|quot|#39);/g,
	      reUnescapedHtml = /[&<>"']/g,
	      reHasEscapedHtml = RegExp(reEscapedHtml.source),
	      reHasUnescapedHtml = RegExp(reUnescapedHtml.source);

	  /** Used to match template delimiters. */
	  var reEscape = /<%-([\s\S]+?)%>/g,
	      reEvaluate = /<%([\s\S]+?)%>/g,
	      reInterpolate = /<%=([\s\S]+?)%>/g;

	  /** Used to match property names within property paths. */
	  var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
	      reIsPlainProp = /^\w*$/,
	      rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

	  /**
	   * Used to match `RegExp`
	   * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
	   */
	  var reRegExpChar = /[\\^$.*+?()[\]{}|]/g,
	      reHasRegExpChar = RegExp(reRegExpChar.source);

	  /** Used to match leading whitespace. */
	  var reTrimStart = /^\s+/;

	  /** Used to match a single whitespace character. */
	  var reWhitespace = /\s/;

	  /** Used to match wrap detail comments. */
	  var reWrapComment = /\{(?:\n\/\* \[wrapped with .+\] \*\/)?\n?/,
	      reWrapDetails = /\{\n\/\* \[wrapped with (.+)\] \*/,
	      reSplitDetails = /,? & /;

	  /** Used to match words composed of alphanumeric characters. */
	  var reAsciiWord = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;

	  /**
	   * Used to validate the `validate` option in `_.template` variable.
	   *
	   * Forbids characters which could potentially change the meaning of the function argument definition:
	   * - "()," (modification of function parameters)
	   * - "=" (default value)
	   * - "[]{}" (destructuring of function parameters)
	   * - "/" (beginning of a comment)
	   * - whitespace
	   */
	  var reForbiddenIdentifierChars = /[()=,{}\[\]\/\s]/;

	  /** Used to match backslashes in property paths. */
	  var reEscapeChar = /\\(\\)?/g;

	  /**
	   * Used to match
	   * [ES template delimiters](http://ecma-international.org/ecma-262/7.0/#sec-template-literal-lexical-components).
	   */
	  var reEsTemplate = /\$\{([^\\}]*(?:\\.[^\\}]*)*)\}/g;

	  /** Used to match `RegExp` flags from their coerced string values. */
	  var reFlags = /\w*$/;

	  /** Used to detect bad signed hexadecimal string values. */
	  var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

	  /** Used to detect binary string values. */
	  var reIsBinary = /^0b[01]+$/i;

	  /** Used to detect host constructors (Safari). */
	  var reIsHostCtor = /^\[object .+?Constructor\]$/;

	  /** Used to detect octal string values. */
	  var reIsOctal = /^0o[0-7]+$/i;

	  /** Used to detect unsigned integer values. */
	  var reIsUint = /^(?:0|[1-9]\d*)$/;

	  /** Used to match Latin Unicode letters (excluding mathematical operators). */
	  var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

	  /** Used to ensure capturing order of template delimiters. */
	  var reNoMatch = /($^)/;

	  /** Used to match unescaped characters in compiled string literals. */
	  var reUnescapedString = /['\n\r\u2028\u2029\\]/g;

	  /** Used to compose unicode character classes. */
	  var rsAstralRange = '\\ud800-\\udfff',
	      rsComboMarksRange = '\\u0300-\\u036f',
	      reComboHalfMarksRange = '\\ufe20-\\ufe2f',
	      rsComboSymbolsRange = '\\u20d0-\\u20ff',
	      rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
	      rsDingbatRange = '\\u2700-\\u27bf',
	      rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
	      rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
	      rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
	      rsPunctuationRange = '\\u2000-\\u206f',
	      rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
	      rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
	      rsVarRange = '\\ufe0e\\ufe0f',
	      rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;

	  /** Used to compose unicode capture groups. */
	  var rsApos = "['\u2019]",
	      rsAstral = '[' + rsAstralRange + ']',
	      rsBreak = '[' + rsBreakRange + ']',
	      rsCombo = '[' + rsComboRange + ']',
	      rsDigits = '\\d+',
	      rsDingbat = '[' + rsDingbatRange + ']',
	      rsLower = '[' + rsLowerRange + ']',
	      rsMisc = '[^' + rsAstralRange + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
	      rsFitz = '\\ud83c[\\udffb-\\udfff]',
	      rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
	      rsNonAstral = '[^' + rsAstralRange + ']',
	      rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
	      rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
	      rsUpper = '[' + rsUpperRange + ']',
	      rsZWJ = '\\u200d';

	  /** Used to compose unicode regexes. */
	  var rsMiscLower = '(?:' + rsLower + '|' + rsMisc + ')',
	      rsMiscUpper = '(?:' + rsUpper + '|' + rsMisc + ')',
	      rsOptContrLower = '(?:' + rsApos + '(?:d|ll|m|re|s|t|ve))?',
	      rsOptContrUpper = '(?:' + rsApos + '(?:D|LL|M|RE|S|T|VE))?',
	      reOptMod = rsModifier + '?',
	      rsOptVar = '[' + rsVarRange + ']?',
	      rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
	      rsOrdLower = '\\d*(?:1st|2nd|3rd|(?![123])\\dth)(?=\\b|[A-Z_])',
	      rsOrdUpper = '\\d*(?:1ST|2ND|3RD|(?![123])\\dTH)(?=\\b|[a-z_])',
	      rsSeq = rsOptVar + reOptMod + rsOptJoin,
	      rsEmoji = '(?:' + [rsDingbat, rsRegional, rsSurrPair].join('|') + ')' + rsSeq,
	      rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

	  /** Used to match apostrophes. */
	  var reApos = RegExp(rsApos, 'g');

	  /**
	   * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
	   * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
	   */
	  var reComboMark = RegExp(rsCombo, 'g');

	  /** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
	  var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

	  /** Used to match complex or compound words. */
	  var reUnicodeWord = RegExp([
	    rsUpper + '?' + rsLower + '+' + rsOptContrLower + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')',
	    rsMiscUpper + '+' + rsOptContrUpper + '(?=' + [rsBreak, rsUpper + rsMiscLower, '$'].join('|') + ')',
	    rsUpper + '?' + rsMiscLower + '+' + rsOptContrLower,
	    rsUpper + '+' + rsOptContrUpper,
	    rsOrdUpper,
	    rsOrdLower,
	    rsDigits,
	    rsEmoji
	  ].join('|'), 'g');

	  /** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
	  var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboRange + rsVarRange + ']');

	  /** Used to detect strings that need a more robust regexp to match words. */
	  var reHasUnicodeWord = /[a-z][A-Z]|[A-Z]{2}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

	  /** Used to assign default `context` object properties. */
	  var contextProps = [
	    'Array', 'Buffer', 'DataView', 'Date', 'Error', 'Float32Array', 'Float64Array',
	    'Function', 'Int8Array', 'Int16Array', 'Int32Array', 'Map', 'Math', 'Object',
	    'Promise', 'RegExp', 'Set', 'String', 'Symbol', 'TypeError', 'Uint8Array',
	    'Uint8ClampedArray', 'Uint16Array', 'Uint32Array', 'WeakMap',
	    '_', 'clearTimeout', 'isFinite', 'parseInt', 'setTimeout'
	  ];

	  /** Used to make template sourceURLs easier to identify. */
	  var templateCounter = -1;

	  /** Used to identify `toStringTag` values of typed arrays. */
	  var typedArrayTags = {};
	  typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
	  typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
	  typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
	  typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
	  typedArrayTags[uint32Tag] = true;
	  typedArrayTags[argsTag] = typedArrayTags[arrayTag] =
	  typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
	  typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
	  typedArrayTags[errorTag] = typedArrayTags[funcTag] =
	  typedArrayTags[mapTag] = typedArrayTags[numberTag] =
	  typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
	  typedArrayTags[setTag] = typedArrayTags[stringTag] =
	  typedArrayTags[weakMapTag] = false;

	  /** Used to identify `toStringTag` values supported by `_.clone`. */
	  var cloneableTags = {};
	  cloneableTags[argsTag] = cloneableTags[arrayTag] =
	  cloneableTags[arrayBufferTag] = cloneableTags[dataViewTag] =
	  cloneableTags[boolTag] = cloneableTags[dateTag] =
	  cloneableTags[float32Tag] = cloneableTags[float64Tag] =
	  cloneableTags[int8Tag] = cloneableTags[int16Tag] =
	  cloneableTags[int32Tag] = cloneableTags[mapTag] =
	  cloneableTags[numberTag] = cloneableTags[objectTag] =
	  cloneableTags[regexpTag] = cloneableTags[setTag] =
	  cloneableTags[stringTag] = cloneableTags[symbolTag] =
	  cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] =
	  cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
	  cloneableTags[errorTag] = cloneableTags[funcTag] =
	  cloneableTags[weakMapTag] = false;

	  /** Used to map Latin Unicode letters to basic Latin letters. */
	  var deburredLetters = {
	    // Latin-1 Supplement block.
	    '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
	    '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
	    '\xc7': 'C',  '\xe7': 'c',
	    '\xd0': 'D',  '\xf0': 'd',
	    '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
	    '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
	    '\xcc': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
	    '\xec': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
	    '\xd1': 'N',  '\xf1': 'n',
	    '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
	    '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
	    '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
	    '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
	    '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
	    '\xc6': 'Ae', '\xe6': 'ae',
	    '\xde': 'Th', '\xfe': 'th',
	    '\xdf': 'ss',
	    // Latin Extended-A block.
	    '\u0100': 'A',  '\u0102': 'A', '\u0104': 'A',
	    '\u0101': 'a',  '\u0103': 'a', '\u0105': 'a',
	    '\u0106': 'C',  '\u0108': 'C', '\u010a': 'C', '\u010c': 'C',
	    '\u0107': 'c',  '\u0109': 'c', '\u010b': 'c', '\u010d': 'c',
	    '\u010e': 'D',  '\u0110': 'D', '\u010f': 'd', '\u0111': 'd',
	    '\u0112': 'E',  '\u0114': 'E', '\u0116': 'E', '\u0118': 'E', '\u011a': 'E',
	    '\u0113': 'e',  '\u0115': 'e', '\u0117': 'e', '\u0119': 'e', '\u011b': 'e',
	    '\u011c': 'G',  '\u011e': 'G', '\u0120': 'G', '\u0122': 'G',
	    '\u011d': 'g',  '\u011f': 'g', '\u0121': 'g', '\u0123': 'g',
	    '\u0124': 'H',  '\u0126': 'H', '\u0125': 'h', '\u0127': 'h',
	    '\u0128': 'I',  '\u012a': 'I', '\u012c': 'I', '\u012e': 'I', '\u0130': 'I',
	    '\u0129': 'i',  '\u012b': 'i', '\u012d': 'i', '\u012f': 'i', '\u0131': 'i',
	    '\u0134': 'J',  '\u0135': 'j',
	    '\u0136': 'K',  '\u0137': 'k', '\u0138': 'k',
	    '\u0139': 'L',  '\u013b': 'L', '\u013d': 'L', '\u013f': 'L', '\u0141': 'L',
	    '\u013a': 'l',  '\u013c': 'l', '\u013e': 'l', '\u0140': 'l', '\u0142': 'l',
	    '\u0143': 'N',  '\u0145': 'N', '\u0147': 'N', '\u014a': 'N',
	    '\u0144': 'n',  '\u0146': 'n', '\u0148': 'n', '\u014b': 'n',
	    '\u014c': 'O',  '\u014e': 'O', '\u0150': 'O',
	    '\u014d': 'o',  '\u014f': 'o', '\u0151': 'o',
	    '\u0154': 'R',  '\u0156': 'R', '\u0158': 'R',
	    '\u0155': 'r',  '\u0157': 'r', '\u0159': 'r',
	    '\u015a': 'S',  '\u015c': 'S', '\u015e': 'S', '\u0160': 'S',
	    '\u015b': 's',  '\u015d': 's', '\u015f': 's', '\u0161': 's',
	    '\u0162': 'T',  '\u0164': 'T', '\u0166': 'T',
	    '\u0163': 't',  '\u0165': 't', '\u0167': 't',
	    '\u0168': 'U',  '\u016a': 'U', '\u016c': 'U', '\u016e': 'U', '\u0170': 'U', '\u0172': 'U',
	    '\u0169': 'u',  '\u016b': 'u', '\u016d': 'u', '\u016f': 'u', '\u0171': 'u', '\u0173': 'u',
	    '\u0174': 'W',  '\u0175': 'w',
	    '\u0176': 'Y',  '\u0177': 'y', '\u0178': 'Y',
	    '\u0179': 'Z',  '\u017b': 'Z', '\u017d': 'Z',
	    '\u017a': 'z',  '\u017c': 'z', '\u017e': 'z',
	    '\u0132': 'IJ', '\u0133': 'ij',
	    '\u0152': 'Oe', '\u0153': 'oe',
	    '\u0149': "'n", '\u017f': 's'
	  };

	  /** Used to map characters to HTML entities. */
	  var htmlEscapes = {
	    '&': '&amp;',
	    '<': '&lt;',
	    '>': '&gt;',
	    '"': '&quot;',
	    "'": '&#39;'
	  };

	  /** Used to map HTML entities to characters. */
	  var htmlUnescapes = {
	    '&amp;': '&',
	    '&lt;': '<',
	    '&gt;': '>',
	    '&quot;': '"',
	    '&#39;': "'"
	  };

	  /** Used to escape characters for inclusion in compiled string literals. */
	  var stringEscapes = {
	    '\\': '\\',
	    "'": "'",
	    '\n': 'n',
	    '\r': 'r',
	    '\u2028': 'u2028',
	    '\u2029': 'u2029'
	  };

	  /** Built-in method references without a dependency on `root`. */
	  var freeParseFloat = parseFloat,
	      freeParseInt = parseInt;

	  /** Detect free variable `global` from Node.js. */
	  var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

	  /** Detect free variable `self`. */
	  var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

	  /** Used as a reference to the global object. */
	  var root = freeGlobal || freeSelf || Function('return this')();

	  /** Detect free variable `exports`. */
	  var freeExports = exports && !exports.nodeType && exports;

	  /** Detect free variable `module`. */
	  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

	  /** Detect the popular CommonJS extension `module.exports`. */
	  var moduleExports = freeModule && freeModule.exports === freeExports;

	  /** Detect free variable `process` from Node.js. */
	  var freeProcess = moduleExports && freeGlobal.process;

	  /** Used to access faster Node.js helpers. */
	  var nodeUtil = (function() {
	    try {
	      // Use `util.types` for Node.js 10+.
	      var types = freeModule && freeModule.require && freeModule.require('util').types;

	      if (types) {
	        return types;
	      }

	      // Legacy `process.binding('util')` for Node.js < 10.
	      return freeProcess && freeProcess.binding && freeProcess.binding('util');
	    } catch (e) {}
	  }());

	  /* Node.js helper references. */
	  var nodeIsArrayBuffer = nodeUtil && nodeUtil.isArrayBuffer,
	      nodeIsDate = nodeUtil && nodeUtil.isDate,
	      nodeIsMap = nodeUtil && nodeUtil.isMap,
	      nodeIsRegExp = nodeUtil && nodeUtil.isRegExp,
	      nodeIsSet = nodeUtil && nodeUtil.isSet,
	      nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

	  /*--------------------------------------------------------------------------*/

	  /**
	   * A faster alternative to `Function#apply`, this function invokes `func`
	   * with the `this` binding of `thisArg` and the arguments of `args`.
	   *
	   * @private
	   * @param {Function} func The function to invoke.
	   * @param {*} thisArg The `this` binding of `func`.
	   * @param {Array} args The arguments to invoke `func` with.
	   * @returns {*} Returns the result of `func`.
	   */
	  function apply(func, thisArg, args) {
	    switch (args.length) {
	      case 0: return func.call(thisArg);
	      case 1: return func.call(thisArg, args[0]);
	      case 2: return func.call(thisArg, args[0], args[1]);
	      case 3: return func.call(thisArg, args[0], args[1], args[2]);
	    }
	    return func.apply(thisArg, args);
	  }

	  /**
	   * A specialized version of `baseAggregator` for arrays.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} setter The function to set `accumulator` values.
	   * @param {Function} iteratee The iteratee to transform keys.
	   * @param {Object} accumulator The initial aggregated object.
	   * @returns {Function} Returns `accumulator`.
	   */
	  function arrayAggregator(array, setter, iteratee, accumulator) {
	    var index = -1,
	        length = array == null ? 0 : array.length;

	    while (++index < length) {
	      var value = array[index];
	      setter(accumulator, value, iteratee(value), array);
	    }
	    return accumulator;
	  }

	  /**
	   * A specialized version of `_.forEach` for arrays without support for
	   * iteratee shorthands.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @returns {Array} Returns `array`.
	   */
	  function arrayEach(array, iteratee) {
	    var index = -1,
	        length = array == null ? 0 : array.length;

	    while (++index < length) {
	      if (iteratee(array[index], index, array) === false) {
	        break;
	      }
	    }
	    return array;
	  }

	  /**
	   * A specialized version of `_.forEachRight` for arrays without support for
	   * iteratee shorthands.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @returns {Array} Returns `array`.
	   */
	  function arrayEachRight(array, iteratee) {
	    var length = array == null ? 0 : array.length;

	    while (length--) {
	      if (iteratee(array[length], length, array) === false) {
	        break;
	      }
	    }
	    return array;
	  }

	  /**
	   * A specialized version of `_.every` for arrays without support for
	   * iteratee shorthands.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} predicate The function invoked per iteration.
	   * @returns {boolean} Returns `true` if all elements pass the predicate check,
	   *  else `false`.
	   */
	  function arrayEvery(array, predicate) {
	    var index = -1,
	        length = array == null ? 0 : array.length;

	    while (++index < length) {
	      if (!predicate(array[index], index, array)) {
	        return false;
	      }
	    }
	    return true;
	  }

	  /**
	   * A specialized version of `_.filter` for arrays without support for
	   * iteratee shorthands.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} predicate The function invoked per iteration.
	   * @returns {Array} Returns the new filtered array.
	   */
	  function arrayFilter(array, predicate) {
	    var index = -1,
	        length = array == null ? 0 : array.length,
	        resIndex = 0,
	        result = [];

	    while (++index < length) {
	      var value = array[index];
	      if (predicate(value, index, array)) {
	        result[resIndex++] = value;
	      }
	    }
	    return result;
	  }

	  /**
	   * A specialized version of `_.includes` for arrays without support for
	   * specifying an index to search from.
	   *
	   * @private
	   * @param {Array} [array] The array to inspect.
	   * @param {*} target The value to search for.
	   * @returns {boolean} Returns `true` if `target` is found, else `false`.
	   */
	  function arrayIncludes(array, value) {
	    var length = array == null ? 0 : array.length;
	    return !!length && baseIndexOf(array, value, 0) > -1;
	  }

	  /**
	   * This function is like `arrayIncludes` except that it accepts a comparator.
	   *
	   * @private
	   * @param {Array} [array] The array to inspect.
	   * @param {*} target The value to search for.
	   * @param {Function} comparator The comparator invoked per element.
	   * @returns {boolean} Returns `true` if `target` is found, else `false`.
	   */
	  function arrayIncludesWith(array, value, comparator) {
	    var index = -1,
	        length = array == null ? 0 : array.length;

	    while (++index < length) {
	      if (comparator(value, array[index])) {
	        return true;
	      }
	    }
	    return false;
	  }

	  /**
	   * A specialized version of `_.map` for arrays without support for iteratee
	   * shorthands.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @returns {Array} Returns the new mapped array.
	   */
	  function arrayMap(array, iteratee) {
	    var index = -1,
	        length = array == null ? 0 : array.length,
	        result = Array(length);

	    while (++index < length) {
	      result[index] = iteratee(array[index], index, array);
	    }
	    return result;
	  }

	  /**
	   * Appends the elements of `values` to `array`.
	   *
	   * @private
	   * @param {Array} array The array to modify.
	   * @param {Array} values The values to append.
	   * @returns {Array} Returns `array`.
	   */
	  function arrayPush(array, values) {
	    var index = -1,
	        length = values.length,
	        offset = array.length;

	    while (++index < length) {
	      array[offset + index] = values[index];
	    }
	    return array;
	  }

	  /**
	   * A specialized version of `_.reduce` for arrays without support for
	   * iteratee shorthands.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @param {*} [accumulator] The initial value.
	   * @param {boolean} [initAccum] Specify using the first element of `array` as
	   *  the initial value.
	   * @returns {*} Returns the accumulated value.
	   */
	  function arrayReduce(array, iteratee, accumulator, initAccum) {
	    var index = -1,
	        length = array == null ? 0 : array.length;

	    if (initAccum && length) {
	      accumulator = array[++index];
	    }
	    while (++index < length) {
	      accumulator = iteratee(accumulator, array[index], index, array);
	    }
	    return accumulator;
	  }

	  /**
	   * A specialized version of `_.reduceRight` for arrays without support for
	   * iteratee shorthands.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @param {*} [accumulator] The initial value.
	   * @param {boolean} [initAccum] Specify using the last element of `array` as
	   *  the initial value.
	   * @returns {*} Returns the accumulated value.
	   */
	  function arrayReduceRight(array, iteratee, accumulator, initAccum) {
	    var length = array == null ? 0 : array.length;
	    if (initAccum && length) {
	      accumulator = array[--length];
	    }
	    while (length--) {
	      accumulator = iteratee(accumulator, array[length], length, array);
	    }
	    return accumulator;
	  }

	  /**
	   * A specialized version of `_.some` for arrays without support for iteratee
	   * shorthands.
	   *
	   * @private
	   * @param {Array} [array] The array to iterate over.
	   * @param {Function} predicate The function invoked per iteration.
	   * @returns {boolean} Returns `true` if any element passes the predicate check,
	   *  else `false`.
	   */
	  function arraySome(array, predicate) {
	    var index = -1,
	        length = array == null ? 0 : array.length;

	    while (++index < length) {
	      if (predicate(array[index], index, array)) {
	        return true;
	      }
	    }
	    return false;
	  }

	  /**
	   * Gets the size of an ASCII `string`.
	   *
	   * @private
	   * @param {string} string The string inspect.
	   * @returns {number} Returns the string size.
	   */
	  var asciiSize = baseProperty('length');

	  /**
	   * Converts an ASCII `string` to an array.
	   *
	   * @private
	   * @param {string} string The string to convert.
	   * @returns {Array} Returns the converted array.
	   */
	  function asciiToArray(string) {
	    return string.split('');
	  }

	  /**
	   * Splits an ASCII `string` into an array of its words.
	   *
	   * @private
	   * @param {string} The string to inspect.
	   * @returns {Array} Returns the words of `string`.
	   */
	  function asciiWords(string) {
	    return string.match(reAsciiWord) || [];
	  }

	  /**
	   * The base implementation of methods like `_.findKey` and `_.findLastKey`,
	   * without support for iteratee shorthands, which iterates over `collection`
	   * using `eachFunc`.
	   *
	   * @private
	   * @param {Array|Object} collection The collection to inspect.
	   * @param {Function} predicate The function invoked per iteration.
	   * @param {Function} eachFunc The function to iterate over `collection`.
	   * @returns {*} Returns the found element or its key, else `undefined`.
	   */
	  function baseFindKey(collection, predicate, eachFunc) {
	    var result;
	    eachFunc(collection, function(value, key, collection) {
	      if (predicate(value, key, collection)) {
	        result = key;
	        return false;
	      }
	    });
	    return result;
	  }

	  /**
	   * The base implementation of `_.findIndex` and `_.findLastIndex` without
	   * support for iteratee shorthands.
	   *
	   * @private
	   * @param {Array} array The array to inspect.
	   * @param {Function} predicate The function invoked per iteration.
	   * @param {number} fromIndex The index to search from.
	   * @param {boolean} [fromRight] Specify iterating from right to left.
	   * @returns {number} Returns the index of the matched value, else `-1`.
	   */
	  function baseFindIndex(array, predicate, fromIndex, fromRight) {
	    var length = array.length,
	        index = fromIndex + (fromRight ? 1 : -1);

	    while ((fromRight ? index-- : ++index < length)) {
	      if (predicate(array[index], index, array)) {
	        return index;
	      }
	    }
	    return -1;
	  }

	  /**
	   * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
	   *
	   * @private
	   * @param {Array} array The array to inspect.
	   * @param {*} value The value to search for.
	   * @param {number} fromIndex The index to search from.
	   * @returns {number} Returns the index of the matched value, else `-1`.
	   */
	  function baseIndexOf(array, value, fromIndex) {
	    return value === value
	      ? strictIndexOf(array, value, fromIndex)
	      : baseFindIndex(array, baseIsNaN, fromIndex);
	  }

	  /**
	   * This function is like `baseIndexOf` except that it accepts a comparator.
	   *
	   * @private
	   * @param {Array} array The array to inspect.
	   * @param {*} value The value to search for.
	   * @param {number} fromIndex The index to search from.
	   * @param {Function} comparator The comparator invoked per element.
	   * @returns {number} Returns the index of the matched value, else `-1`.
	   */
	  function baseIndexOfWith(array, value, fromIndex, comparator) {
	    var index = fromIndex - 1,
	        length = array.length;

	    while (++index < length) {
	      if (comparator(array[index], value)) {
	        return index;
	      }
	    }
	    return -1;
	  }

	  /**
	   * The base implementation of `_.isNaN` without support for number objects.
	   *
	   * @private
	   * @param {*} value The value to check.
	   * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
	   */
	  function baseIsNaN(value) {
	    return value !== value;
	  }

	  /**
	   * The base implementation of `_.mean` and `_.meanBy` without support for
	   * iteratee shorthands.
	   *
	   * @private
	   * @param {Array} array The array to iterate over.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @returns {number} Returns the mean.
	   */
	  function baseMean(array, iteratee) {
	    var length = array == null ? 0 : array.length;
	    return length ? (baseSum(array, iteratee) / length) : NAN;
	  }

	  /**
	   * The base implementation of `_.property` without support for deep paths.
	   *
	   * @private
	   * @param {string} key The key of the property to get.
	   * @returns {Function} Returns the new accessor function.
	   */
	  function baseProperty(key) {
	    return function(object) {
	      return object == null ? undefined$1 : object[key];
	    };
	  }

	  /**
	   * The base implementation of `_.propertyOf` without support for deep paths.
	   *
	   * @private
	   * @param {Object} object The object to query.
	   * @returns {Function} Returns the new accessor function.
	   */
	  function basePropertyOf(object) {
	    return function(key) {
	      return object == null ? undefined$1 : object[key];
	    };
	  }

	  /**
	   * The base implementation of `_.reduce` and `_.reduceRight`, without support
	   * for iteratee shorthands, which iterates over `collection` using `eachFunc`.
	   *
	   * @private
	   * @param {Array|Object} collection The collection to iterate over.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @param {*} accumulator The initial value.
	   * @param {boolean} initAccum Specify using the first or last element of
	   *  `collection` as the initial value.
	   * @param {Function} eachFunc The function to iterate over `collection`.
	   * @returns {*} Returns the accumulated value.
	   */
	  function baseReduce(collection, iteratee, accumulator, initAccum, eachFunc) {
	    eachFunc(collection, function(value, index, collection) {
	      accumulator = initAccum
	        ? (initAccum = false, value)
	        : iteratee(accumulator, value, index, collection);
	    });
	    return accumulator;
	  }

	  /**
	   * The base implementation of `_.sortBy` which uses `comparer` to define the
	   * sort order of `array` and replaces criteria objects with their corresponding
	   * values.
	   *
	   * @private
	   * @param {Array} array The array to sort.
	   * @param {Function} comparer The function to define sort order.
	   * @returns {Array} Returns `array`.
	   */
	  function baseSortBy(array, comparer) {
	    var length = array.length;

	    array.sort(comparer);
	    while (length--) {
	      array[length] = array[length].value;
	    }
	    return array;
	  }

	  /**
	   * The base implementation of `_.sum` and `_.sumBy` without support for
	   * iteratee shorthands.
	   *
	   * @private
	   * @param {Array} array The array to iterate over.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @returns {number} Returns the sum.
	   */
	  function baseSum(array, iteratee) {
	    var result,
	        index = -1,
	        length = array.length;

	    while (++index < length) {
	      var current = iteratee(array[index]);
	      if (current !== undefined$1) {
	        result = result === undefined$1 ? current : (result + current);
	      }
	    }
	    return result;
	  }

	  /**
	   * The base implementation of `_.times` without support for iteratee shorthands
	   * or max array length checks.
	   *
	   * @private
	   * @param {number} n The number of times to invoke `iteratee`.
	   * @param {Function} iteratee The function invoked per iteration.
	   * @returns {Array} Returns the array of results.
	   */
	  function baseTimes(n, iteratee) {
	    var index = -1,
	        result = Array(n);

	    while (++index < n) {
	      result[index] = iteratee(index);
	    }
	    return result;
	  }

	  /**
	   * The base implementation of `_.toPairs` and `_.toPairsIn` which creates an array
	   * of key-value pairs for `object` corresponding to the property names of `props`.
	   *
	   * @private
	   * @param {Object} object The object to query.
	   * @param {Array} props The property names to get values for.
	   * @returns {Object} Returns the key-value pairs.
	   */
	  function baseToPairs(object, props) {
	    return arrayMap(props, function(key) {
	      return [key, object[key]];
	    });
	  }

	  /**
	   * The base implementation of `_.trim`.
	   *
	   * @private
	   * @param {string} string The string to trim.
	   * @returns {string} Returns the trimmed string.
	   */
	  function baseTrim(string) {
	    return string
	      ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
	      : string;
	  }

	  /**
	   * The base implementation of `_.unary` without support for storing metadata.
	   *
	   * @private
	   * @param {Function} func The function to cap arguments for.
	   * @returns {Function} Returns the new capped function.
	   */
	  function baseUnary(func) {
	    return function(value) {
	      return func(value);
	    };
	  }

	  /**
	   * The base implementation of `_.values` and `_.valuesIn` which creates an
	   * array of `object` property values corresponding to the property names
	   * of `props`.
	   *
	   * @private
	   * @param {Object} object The object to query.
	   * @param {Array} props The property names to get values for.
	   * @returns {Object} Returns the array of property values.
	   */
	  function baseValues(object, props) {
	    return arrayMap(props, function(key) {
	      return object[key];
	    });
	  }

	  /**
	   * Checks if a `cache` value for `key` exists.
	   *
	   * @private
	   * @param {Object} cache The cache to query.
	   * @param {string} key The key of the entry to check.
	   * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
	   */
	  function cacheHas(cache, key) {
	    return cache.has(key);
	  }

	  /**
	   * Used by `_.trim` and `_.trimStart` to get the index of the first string symbol
	   * that is not found in the character symbols.
	   *
	   * @private
	   * @param {Array} strSymbols The string symbols to inspect.
	   * @param {Array} chrSymbols The character symbols to find.
	   * @returns {number} Returns the index of the first unmatched string symbol.
	   */
	  function charsStartIndex(strSymbols, chrSymbols) {
	    var index = -1,
	        length = strSymbols.length;

	    while (++index < length && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
	    return index;
	  }

	  /**
	   * Used by `_.trim` and `_.trimEnd` to get the index of the last string symbol
	   * that is not found in the character symbols.
	   *
	   * @private
	   * @param {Array} strSymbols The string symbols to inspect.
	   * @param {Array} chrSymbols The character symbols to find.
	   * @returns {number} Returns the index of the last unmatched string symbol.
	   */
	  function charsEndIndex(strSymbols, chrSymbols) {
	    var index = strSymbols.length;

	    while (index-- && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
	    return index;
	  }

	  /**
	   * Gets the number of `placeholder` occurrences in `array`.
	   *
	   * @private
	   * @param {Array} array The array to inspect.
	   * @param {*} placeholder The placeholder to search for.
	   * @returns {number} Returns the placeholder count.
	   */
	  function countHolders(array, placeholder) {
	    var length = array.length,
	        result = 0;

	    while (length--) {
	      if (array[length] === placeholder) {
	        ++result;
	      }
	    }
	    return result;
	  }

	  /**
	   * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
	   * letters to basic Latin letters.
	   *
	   * @private
	   * @param {string} letter The matched letter to deburr.
	   * @returns {string} Returns the deburred letter.
	   */
	  var deburrLetter = basePropertyOf(deburredLetters);

	  /**
	   * Used by `_.escape` to convert characters to HTML entities.
	   *
	   * @private
	   * @param {string} chr The matched character to escape.
	   * @returns {string} Returns the escaped character.
	   */
	  var escapeHtmlChar = basePropertyOf(htmlEscapes);

	  /**
	   * Used by `_.template` to escape characters for inclusion in compiled string literals.
	   *
	   * @private
	   * @param {string} chr The matched character to escape.
	   * @returns {string} Returns the escaped character.
	   */
	  function escapeStringChar(chr) {
	    return '\\' + stringEscapes[chr];
	  }

	  /**
	   * Gets the value at `key` of `object`.
	   *
	   * @private
	   * @param {Object} [object] The object to query.
	   * @param {string} key The key of the property to get.
	   * @returns {*} Returns the property value.
	   */
	  function getValue(object, key) {
	    return object == null ? undefined$1 : object[key];
	  }

	  /**
	   * Checks if `string` contains Unicode symbols.
	   *
	   * @private
	   * @param {string} string The string to inspect.
	   * @returns {boolean} Returns `true` if a symbol is found, else `false`.
	   */
	  function hasUnicode(string) {
	    return reHasUnicode.test(string);
	  }

	  /**
	   * Checks if `string` contains a word composed of Unicode symbols.
	   *
	   * @private
	   * @param {string} string The string to inspect.
	   * @returns {boolean} Returns `true` if a word is found, else `false`.
	   */
	  function hasUnicodeWord(string) {
	    return reHasUnicodeWord.test(string);
	  }

	  /**
	   * Converts `iterator` to an array.
	   *
	   * @private
	   * @param {Object} iterator The iterator to convert.
	   * @returns {Array} Returns the converted array.
	   */
	  function iteratorToArray(iterator) {
	    var data,
	        result = [];

	    while (!(data = iterator.next()).done) {
	      result.push(data.value);
	    }
	    return result;
	  }

	  /**
	   * Converts `map` to its key-value pairs.
	   *
	   * @private
	   * @param {Object} map The map to convert.
	   * @returns {Array} Returns the key-value pairs.
	   */
	  function mapToArray(map) {
	    var index = -1,
	        result = Array(map.size);

	    map.forEach(function(value, key) {
	      result[++index] = [key, value];
	    });
	    return result;
	  }

	  /**
	   * Creates a unary function that invokes `func` with its argument transformed.
	   *
	   * @private
	   * @param {Function} func The function to wrap.
	   * @param {Function} transform The argument transform.
	   * @returns {Function} Returns the new function.
	   */
	  function overArg(func, transform) {
	    return function(arg) {
	      return func(transform(arg));
	    };
	  }

	  /**
	   * Replaces all `placeholder` elements in `array` with an internal placeholder
	   * and returns an array of their indexes.
	   *
	   * @private
	   * @param {Array} array The array to modify.
	   * @param {*} placeholder The placeholder to replace.
	   * @returns {Array} Returns the new array of placeholder indexes.
	   */
	  function replaceHolders(array, placeholder) {
	    var index = -1,
	        length = array.length,
	        resIndex = 0,
	        result = [];

	    while (++index < length) {
	      var value = array[index];
	      if (value === placeholder || value === PLACEHOLDER) {
	        array[index] = PLACEHOLDER;
	        result[resIndex++] = index;
	      }
	    }
	    return result;
	  }

	  /**
	   * Converts `set` to an array of its values.
	   *
	   * @private
	   * @param {Object} set The set to convert.
	   * @returns {Array} Returns the values.
	   */
	  function setToArray(set) {
	    var index = -1,
	        result = Array(set.size);

	    set.forEach(function(value) {
	      result[++index] = value;
	    });
	    return result;
	  }

	  /**
	   * Converts `set` to its value-value pairs.
	   *
	   * @private
	   * @param {Object} set The set to convert.
	   * @returns {Array} Returns the value-value pairs.
	   */
	  function setToPairs(set) {
	    var index = -1,
	        result = Array(set.size);

	    set.forEach(function(value) {
	      result[++index] = [value, value];
	    });
	    return result;
	  }

	  /**
	   * A specialized version of `_.indexOf` which performs strict equality
	   * comparisons of values, i.e. `===`.
	   *
	   * @private
	   * @param {Array} array The array to inspect.
	   * @param {*} value The value to search for.
	   * @param {number} fromIndex The index to search from.
	   * @returns {number} Returns the index of the matched value, else `-1`.
	   */
	  function strictIndexOf(array, value, fromIndex) {
	    var index = fromIndex - 1,
	        length = array.length;

	    while (++index < length) {
	      if (array[index] === value) {
	        return index;
	      }
	    }
	    return -1;
	  }

	  /**
	   * A specialized version of `_.lastIndexOf` which performs strict equality
	   * comparisons of values, i.e. `===`.
	   *
	   * @private
	   * @param {Array} array The array to inspect.
	   * @param {*} value The value to search for.
	   * @param {number} fromIndex The index to search from.
	   * @returns {number} Returns the index of the matched value, else `-1`.
	   */
	  function strictLastIndexOf(array, value, fromIndex) {
	    var index = fromIndex + 1;
	    while (index--) {
	      if (array[index] === value) {
	        return index;
	      }
	    }
	    return index;
	  }

	  /**
	   * Gets the number of symbols in `string`.
	   *
	   * @private
	   * @param {string} string The string to inspect.
	   * @returns {number} Returns the string size.
	   */
	  function stringSize(string) {
	    return hasUnicode(string)
	      ? unicodeSize(string)
	      : asciiSize(string);
	  }

	  /**
	   * Converts `string` to an array.
	   *
	   * @private
	   * @param {string} string The string to convert.
	   * @returns {Array} Returns the converted array.
	   */
	  function stringToArray(string) {
	    return hasUnicode(string)
	      ? unicodeToArray(string)
	      : asciiToArray(string);
	  }

	  /**
	   * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
	   * character of `string`.
	   *
	   * @private
	   * @param {string} string The string to inspect.
	   * @returns {number} Returns the index of the last non-whitespace character.
	   */
	  function trimmedEndIndex(string) {
	    var index = string.length;

	    while (index-- && reWhitespace.test(string.charAt(index))) {}
	    return index;
	  }

	  /**
	   * Used by `_.unescape` to convert HTML entities to characters.
	   *
	   * @private
	   * @param {string} chr The matched character to unescape.
	   * @returns {string} Returns the unescaped character.
	   */
	  var unescapeHtmlChar = basePropertyOf(htmlUnescapes);

	  /**
	   * Gets the size of a Unicode `string`.
	   *
	   * @private
	   * @param {string} string The string inspect.
	   * @returns {number} Returns the string size.
	   */
	  function unicodeSize(string) {
	    var result = reUnicode.lastIndex = 0;
	    while (reUnicode.test(string)) {
	      ++result;
	    }
	    return result;
	  }

	  /**
	   * Converts a Unicode `string` to an array.
	   *
	   * @private
	   * @param {string} string The string to convert.
	   * @returns {Array} Returns the converted array.
	   */
	  function unicodeToArray(string) {
	    return string.match(reUnicode) || [];
	  }

	  /**
	   * Splits a Unicode `string` into an array of its words.
	   *
	   * @private
	   * @param {string} The string to inspect.
	   * @returns {Array} Returns the words of `string`.
	   */
	  function unicodeWords(string) {
	    return string.match(reUnicodeWord) || [];
	  }

	  /*--------------------------------------------------------------------------*/

	  /**
	   * Create a new pristine `lodash` function using the `context` object.
	   *
	   * @static
	   * @memberOf _
	   * @since 1.1.0
	   * @category Util
	   * @param {Object} [context=root] The context object.
	   * @returns {Function} Returns a new `lodash` function.
	   * @example
	   *
	   * _.mixin({ 'foo': _.constant('foo') });
	   *
	   * var lodash = _.runInContext();
	   * lodash.mixin({ 'bar': lodash.constant('bar') });
	   *
	   * _.isFunction(_.foo);
	   * // => true
	   * _.isFunction(_.bar);
	   * // => false
	   *
	   * lodash.isFunction(lodash.foo);
	   * // => false
	   * lodash.isFunction(lodash.bar);
	   * // => true
	   *
	   * // Create a suped-up `defer` in Node.js.
	   * var defer = _.runInContext({ 'setTimeout': setImmediate }).defer;
	   */
	  var runInContext = (function runInContext(context) {
	    context = context == null ? root : _.defaults(root.Object(), context, _.pick(root, contextProps));

	    /** Built-in constructor references. */
	    var Array = context.Array,
	        Date = context.Date,
	        Error = context.Error,
	        Function = context.Function,
	        Math = context.Math,
	        Object = context.Object,
	        RegExp = context.RegExp,
	        String = context.String,
	        TypeError = context.TypeError;

	    /** Used for built-in method references. */
	    var arrayProto = Array.prototype,
	        funcProto = Function.prototype,
	        objectProto = Object.prototype;

	    /** Used to detect overreaching core-js shims. */
	    var coreJsData = context['__core-js_shared__'];

	    /** Used to resolve the decompiled source of functions. */
	    var funcToString = funcProto.toString;

	    /** Used to check objects for own properties. */
	    var hasOwnProperty = objectProto.hasOwnProperty;

	    /** Used to generate unique IDs. */
	    var idCounter = 0;

	    /** Used to detect methods masquerading as native. */
	    var maskSrcKey = (function() {
	      var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
	      return uid ? ('Symbol(src)_1.' + uid) : '';
	    }());

	    /**
	     * Used to resolve the
	     * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
	     * of values.
	     */
	    var nativeObjectToString = objectProto.toString;

	    /** Used to infer the `Object` constructor. */
	    var objectCtorString = funcToString.call(Object);

	    /** Used to restore the original `_` reference in `_.noConflict`. */
	    var oldDash = root._;

	    /** Used to detect if a method is native. */
	    var reIsNative = RegExp('^' +
	      funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&')
	      .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
	    );

	    /** Built-in value references. */
	    var Buffer = moduleExports ? context.Buffer : undefined$1,
	        Symbol = context.Symbol,
	        Uint8Array = context.Uint8Array,
	        allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined$1,
	        getPrototype = overArg(Object.getPrototypeOf, Object),
	        objectCreate = Object.create,
	        propertyIsEnumerable = objectProto.propertyIsEnumerable,
	        splice = arrayProto.splice,
	        spreadableSymbol = Symbol ? Symbol.isConcatSpreadable : undefined$1,
	        symIterator = Symbol ? Symbol.iterator : undefined$1,
	        symToStringTag = Symbol ? Symbol.toStringTag : undefined$1;

	    var defineProperty = (function() {
	      try {
	        var func = getNative(Object, 'defineProperty');
	        func({}, '', {});
	        return func;
	      } catch (e) {}
	    }());

	    /** Mocked built-ins. */
	    var ctxClearTimeout = context.clearTimeout !== root.clearTimeout && context.clearTimeout,
	        ctxNow = Date && Date.now !== root.Date.now && Date.now,
	        ctxSetTimeout = context.setTimeout !== root.setTimeout && context.setTimeout;

	    /* Built-in method references for those with the same name as other `lodash` methods. */
	    var nativeCeil = Math.ceil,
	        nativeFloor = Math.floor,
	        nativeGetSymbols = Object.getOwnPropertySymbols,
	        nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined$1,
	        nativeIsFinite = context.isFinite,
	        nativeJoin = arrayProto.join,
	        nativeKeys = overArg(Object.keys, Object),
	        nativeMax = Math.max,
	        nativeMin = Math.min,
	        nativeNow = Date.now,
	        nativeParseInt = context.parseInt,
	        nativeRandom = Math.random,
	        nativeReverse = arrayProto.reverse;

	    /* Built-in method references that are verified to be native. */
	    var DataView = getNative(context, 'DataView'),
	        Map = getNative(context, 'Map'),
	        Promise = getNative(context, 'Promise'),
	        Set = getNative(context, 'Set'),
	        WeakMap = getNative(context, 'WeakMap'),
	        nativeCreate = getNative(Object, 'create');

	    /** Used to store function metadata. */
	    var metaMap = WeakMap && new WeakMap;

	    /** Used to lookup unminified function names. */
	    var realNames = {};

	    /** Used to detect maps, sets, and weakmaps. */
	    var dataViewCtorString = toSource(DataView),
	        mapCtorString = toSource(Map),
	        promiseCtorString = toSource(Promise),
	        setCtorString = toSource(Set),
	        weakMapCtorString = toSource(WeakMap);

	    /** Used to convert symbols to primitives and strings. */
	    var symbolProto = Symbol ? Symbol.prototype : undefined$1,
	        symbolValueOf = symbolProto ? symbolProto.valueOf : undefined$1,
	        symbolToString = symbolProto ? symbolProto.toString : undefined$1;

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates a `lodash` object which wraps `value` to enable implicit method
	     * chain sequences. Methods that operate on and return arrays, collections,
	     * and functions can be chained together. Methods that retrieve a single value
	     * or may return a primitive value will automatically end the chain sequence
	     * and return the unwrapped value. Otherwise, the value must be unwrapped
	     * with `_#value`.
	     *
	     * Explicit chain sequences, which must be unwrapped with `_#value`, may be
	     * enabled using `_.chain`.
	     *
	     * The execution of chained methods is lazy, that is, it's deferred until
	     * `_#value` is implicitly or explicitly called.
	     *
	     * Lazy evaluation allows several methods to support shortcut fusion.
	     * Shortcut fusion is an optimization to merge iteratee calls; this avoids
	     * the creation of intermediate arrays and can greatly reduce the number of
	     * iteratee executions. Sections of a chain sequence qualify for shortcut
	     * fusion if the section is applied to an array and iteratees accept only
	     * one argument. The heuristic for whether a section qualifies for shortcut
	     * fusion is subject to change.
	     *
	     * Chaining is supported in custom builds as long as the `_#value` method is
	     * directly or indirectly included in the build.
	     *
	     * In addition to lodash methods, wrappers have `Array` and `String` methods.
	     *
	     * The wrapper `Array` methods are:
	     * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
	     *
	     * The wrapper `String` methods are:
	     * `replace` and `split`
	     *
	     * The wrapper methods that support shortcut fusion are:
	     * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
	     * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
	     * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
	     *
	     * The chainable wrapper methods are:
	     * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
	     * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
	     * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
	     * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
	     * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
	     * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
	     * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
	     * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
	     * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
	     * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
	     * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
	     * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
	     * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
	     * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
	     * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
	     * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
	     * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
	     * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
	     * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
	     * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
	     * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
	     * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
	     * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
	     * `zipObject`, `zipObjectDeep`, and `zipWith`
	     *
	     * The wrapper methods that are **not** chainable by default are:
	     * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
	     * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `conformsTo`, `deburr`,
	     * `defaultTo`, `divide`, `each`, `eachRight`, `endsWith`, `eq`, `escape`,
	     * `escapeRegExp`, `every`, `find`, `findIndex`, `findKey`, `findLast`,
	     * `findLastIndex`, `findLastKey`, `first`, `floor`, `forEach`, `forEachRight`,
	     * `forIn`, `forInRight`, `forOwn`, `forOwnRight`, `get`, `gt`, `gte`, `has`,
	     * `hasIn`, `head`, `identity`, `includes`, `indexOf`, `inRange`, `invoke`,
	     * `isArguments`, `isArray`, `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`,
	     * `isBoolean`, `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`,
	     * `isEqualWith`, `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`,
	     * `isMap`, `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`,
	     * `isNumber`, `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`,
	     * `isSafeInteger`, `isSet`, `isString`, `isUndefined`, `isTypedArray`,
	     * `isWeakMap`, `isWeakSet`, `join`, `kebabCase`, `last`, `lastIndexOf`,
	     * `lowerCase`, `lowerFirst`, `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`,
	     * `min`, `minBy`, `multiply`, `noConflict`, `noop`, `now`, `nth`, `pad`,
	     * `padEnd`, `padStart`, `parseInt`, `pop`, `random`, `reduce`, `reduceRight`,
	     * `repeat`, `result`, `round`, `runInContext`, `sample`, `shift`, `size`,
	     * `snakeCase`, `some`, `sortedIndex`, `sortedIndexBy`, `sortedLastIndex`,
	     * `sortedLastIndexBy`, `startCase`, `startsWith`, `stubArray`, `stubFalse`,
	     * `stubObject`, `stubString`, `stubTrue`, `subtract`, `sum`, `sumBy`,
	     * `template`, `times`, `toFinite`, `toInteger`, `toJSON`, `toLength`,
	     * `toLower`, `toNumber`, `toSafeInteger`, `toString`, `toUpper`, `trim`,
	     * `trimEnd`, `trimStart`, `truncate`, `unescape`, `uniqueId`, `upperCase`,
	     * `upperFirst`, `value`, and `words`
	     *
	     * @name _
	     * @constructor
	     * @category Seq
	     * @param {*} value The value to wrap in a `lodash` instance.
	     * @returns {Object} Returns the new `lodash` wrapper instance.
	     * @example
	     *
	     * function square(n) {
	     *   return n * n;
	     * }
	     *
	     * var wrapped = _([1, 2, 3]);
	     *
	     * // Returns an unwrapped value.
	     * wrapped.reduce(_.add);
	     * // => 6
	     *
	     * // Returns a wrapped value.
	     * var squares = wrapped.map(square);
	     *
	     * _.isArray(squares);
	     * // => false
	     *
	     * _.isArray(squares.value());
	     * // => true
	     */
	    function lodash(value) {
	      if (isObjectLike(value) && !isArray(value) && !(value instanceof LazyWrapper)) {
	        if (value instanceof LodashWrapper) {
	          return value;
	        }
	        if (hasOwnProperty.call(value, '__wrapped__')) {
	          return wrapperClone(value);
	        }
	      }
	      return new LodashWrapper(value);
	    }

	    /**
	     * The base implementation of `_.create` without support for assigning
	     * properties to the created object.
	     *
	     * @private
	     * @param {Object} proto The object to inherit from.
	     * @returns {Object} Returns the new object.
	     */
	    var baseCreate = (function() {
	      function object() {}
	      return function(proto) {
	        if (!isObject(proto)) {
	          return {};
	        }
	        if (objectCreate) {
	          return objectCreate(proto);
	        }
	        object.prototype = proto;
	        var result = new object;
	        object.prototype = undefined$1;
	        return result;
	      };
	    }());

	    /**
	     * The function whose prototype chain sequence wrappers inherit from.
	     *
	     * @private
	     */
	    function baseLodash() {
	      // No operation performed.
	    }

	    /**
	     * The base constructor for creating `lodash` wrapper objects.
	     *
	     * @private
	     * @param {*} value The value to wrap.
	     * @param {boolean} [chainAll] Enable explicit method chain sequences.
	     */
	    function LodashWrapper(value, chainAll) {
	      this.__wrapped__ = value;
	      this.__actions__ = [];
	      this.__chain__ = !!chainAll;
	      this.__index__ = 0;
	      this.__values__ = undefined$1;
	    }

	    /**
	     * By default, the template delimiters used by lodash are like those in
	     * embedded Ruby (ERB) as well as ES2015 template strings. Change the
	     * following template settings to use alternative delimiters.
	     *
	     * @static
	     * @memberOf _
	     * @type {Object}
	     */
	    lodash.templateSettings = {

	      /**
	       * Used to detect `data` property values to be HTML-escaped.
	       *
	       * @memberOf _.templateSettings
	       * @type {RegExp}
	       */
	      'escape': reEscape,

	      /**
	       * Used to detect code to be evaluated.
	       *
	       * @memberOf _.templateSettings
	       * @type {RegExp}
	       */
	      'evaluate': reEvaluate,

	      /**
	       * Used to detect `data` property values to inject.
	       *
	       * @memberOf _.templateSettings
	       * @type {RegExp}
	       */
	      'interpolate': reInterpolate,

	      /**
	       * Used to reference the data object in the template text.
	       *
	       * @memberOf _.templateSettings
	       * @type {string}
	       */
	      'variable': '',

	      /**
	       * Used to import variables into the compiled template.
	       *
	       * @memberOf _.templateSettings
	       * @type {Object}
	       */
	      'imports': {

	        /**
	         * A reference to the `lodash` function.
	         *
	         * @memberOf _.templateSettings.imports
	         * @type {Function}
	         */
	        '_': lodash
	      }
	    };

	    // Ensure wrappers are instances of `baseLodash`.
	    lodash.prototype = baseLodash.prototype;
	    lodash.prototype.constructor = lodash;

	    LodashWrapper.prototype = baseCreate(baseLodash.prototype);
	    LodashWrapper.prototype.constructor = LodashWrapper;

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
	     *
	     * @private
	     * @constructor
	     * @param {*} value The value to wrap.
	     */
	    function LazyWrapper(value) {
	      this.__wrapped__ = value;
	      this.__actions__ = [];
	      this.__dir__ = 1;
	      this.__filtered__ = false;
	      this.__iteratees__ = [];
	      this.__takeCount__ = MAX_ARRAY_LENGTH;
	      this.__views__ = [];
	    }

	    /**
	     * Creates a clone of the lazy wrapper object.
	     *
	     * @private
	     * @name clone
	     * @memberOf LazyWrapper
	     * @returns {Object} Returns the cloned `LazyWrapper` object.
	     */
	    function lazyClone() {
	      var result = new LazyWrapper(this.__wrapped__);
	      result.__actions__ = copyArray(this.__actions__);
	      result.__dir__ = this.__dir__;
	      result.__filtered__ = this.__filtered__;
	      result.__iteratees__ = copyArray(this.__iteratees__);
	      result.__takeCount__ = this.__takeCount__;
	      result.__views__ = copyArray(this.__views__);
	      return result;
	    }

	    /**
	     * Reverses the direction of lazy iteration.
	     *
	     * @private
	     * @name reverse
	     * @memberOf LazyWrapper
	     * @returns {Object} Returns the new reversed `LazyWrapper` object.
	     */
	    function lazyReverse() {
	      if (this.__filtered__) {
	        var result = new LazyWrapper(this);
	        result.__dir__ = -1;
	        result.__filtered__ = true;
	      } else {
	        result = this.clone();
	        result.__dir__ *= -1;
	      }
	      return result;
	    }

	    /**
	     * Extracts the unwrapped value from its lazy wrapper.
	     *
	     * @private
	     * @name value
	     * @memberOf LazyWrapper
	     * @returns {*} Returns the unwrapped value.
	     */
	    function lazyValue() {
	      var array = this.__wrapped__.value(),
	          dir = this.__dir__,
	          isArr = isArray(array),
	          isRight = dir < 0,
	          arrLength = isArr ? array.length : 0,
	          view = getView(0, arrLength, this.__views__),
	          start = view.start,
	          end = view.end,
	          length = end - start,
	          index = isRight ? end : (start - 1),
	          iteratees = this.__iteratees__,
	          iterLength = iteratees.length,
	          resIndex = 0,
	          takeCount = nativeMin(length, this.__takeCount__);

	      if (!isArr || (!isRight && arrLength == length && takeCount == length)) {
	        return baseWrapperValue(array, this.__actions__);
	      }
	      var result = [];

	      outer:
	      while (length-- && resIndex < takeCount) {
	        index += dir;

	        var iterIndex = -1,
	            value = array[index];

	        while (++iterIndex < iterLength) {
	          var data = iteratees[iterIndex],
	              iteratee = data.iteratee,
	              type = data.type,
	              computed = iteratee(value);

	          if (type == LAZY_MAP_FLAG) {
	            value = computed;
	          } else if (!computed) {
	            if (type == LAZY_FILTER_FLAG) {
	              continue outer;
	            } else {
	              break outer;
	            }
	          }
	        }
	        result[resIndex++] = value;
	      }
	      return result;
	    }

	    // Ensure `LazyWrapper` is an instance of `baseLodash`.
	    LazyWrapper.prototype = baseCreate(baseLodash.prototype);
	    LazyWrapper.prototype.constructor = LazyWrapper;

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates a hash object.
	     *
	     * @private
	     * @constructor
	     * @param {Array} [entries] The key-value pairs to cache.
	     */
	    function Hash(entries) {
	      var index = -1,
	          length = entries == null ? 0 : entries.length;

	      this.clear();
	      while (++index < length) {
	        var entry = entries[index];
	        this.set(entry[0], entry[1]);
	      }
	    }

	    /**
	     * Removes all key-value entries from the hash.
	     *
	     * @private
	     * @name clear
	     * @memberOf Hash
	     */
	    function hashClear() {
	      this.__data__ = nativeCreate ? nativeCreate(null) : {};
	      this.size = 0;
	    }

	    /**
	     * Removes `key` and its value from the hash.
	     *
	     * @private
	     * @name delete
	     * @memberOf Hash
	     * @param {Object} hash The hash to modify.
	     * @param {string} key The key of the value to remove.
	     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
	     */
	    function hashDelete(key) {
	      var result = this.has(key) && delete this.__data__[key];
	      this.size -= result ? 1 : 0;
	      return result;
	    }

	    /**
	     * Gets the hash value for `key`.
	     *
	     * @private
	     * @name get
	     * @memberOf Hash
	     * @param {string} key The key of the value to get.
	     * @returns {*} Returns the entry value.
	     */
	    function hashGet(key) {
	      var data = this.__data__;
	      if (nativeCreate) {
	        var result = data[key];
	        return result === HASH_UNDEFINED ? undefined$1 : result;
	      }
	      return hasOwnProperty.call(data, key) ? data[key] : undefined$1;
	    }

	    /**
	     * Checks if a hash value for `key` exists.
	     *
	     * @private
	     * @name has
	     * @memberOf Hash
	     * @param {string} key The key of the entry to check.
	     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
	     */
	    function hashHas(key) {
	      var data = this.__data__;
	      return nativeCreate ? (data[key] !== undefined$1) : hasOwnProperty.call(data, key);
	    }

	    /**
	     * Sets the hash `key` to `value`.
	     *
	     * @private
	     * @name set
	     * @memberOf Hash
	     * @param {string} key The key of the value to set.
	     * @param {*} value The value to set.
	     * @returns {Object} Returns the hash instance.
	     */
	    function hashSet(key, value) {
	      var data = this.__data__;
	      this.size += this.has(key) ? 0 : 1;
	      data[key] = (nativeCreate && value === undefined$1) ? HASH_UNDEFINED : value;
	      return this;
	    }

	    // Add methods to `Hash`.
	    Hash.prototype.clear = hashClear;
	    Hash.prototype['delete'] = hashDelete;
	    Hash.prototype.get = hashGet;
	    Hash.prototype.has = hashHas;
	    Hash.prototype.set = hashSet;

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates an list cache object.
	     *
	     * @private
	     * @constructor
	     * @param {Array} [entries] The key-value pairs to cache.
	     */
	    function ListCache(entries) {
	      var index = -1,
	          length = entries == null ? 0 : entries.length;

	      this.clear();
	      while (++index < length) {
	        var entry = entries[index];
	        this.set(entry[0], entry[1]);
	      }
	    }

	    /**
	     * Removes all key-value entries from the list cache.
	     *
	     * @private
	     * @name clear
	     * @memberOf ListCache
	     */
	    function listCacheClear() {
	      this.__data__ = [];
	      this.size = 0;
	    }

	    /**
	     * Removes `key` and its value from the list cache.
	     *
	     * @private
	     * @name delete
	     * @memberOf ListCache
	     * @param {string} key The key of the value to remove.
	     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
	     */
	    function listCacheDelete(key) {
	      var data = this.__data__,
	          index = assocIndexOf(data, key);

	      if (index < 0) {
	        return false;
	      }
	      var lastIndex = data.length - 1;
	      if (index == lastIndex) {
	        data.pop();
	      } else {
	        splice.call(data, index, 1);
	      }
	      --this.size;
	      return true;
	    }

	    /**
	     * Gets the list cache value for `key`.
	     *
	     * @private
	     * @name get
	     * @memberOf ListCache
	     * @param {string} key The key of the value to get.
	     * @returns {*} Returns the entry value.
	     */
	    function listCacheGet(key) {
	      var data = this.__data__,
	          index = assocIndexOf(data, key);

	      return index < 0 ? undefined$1 : data[index][1];
	    }

	    /**
	     * Checks if a list cache value for `key` exists.
	     *
	     * @private
	     * @name has
	     * @memberOf ListCache
	     * @param {string} key The key of the entry to check.
	     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
	     */
	    function listCacheHas(key) {
	      return assocIndexOf(this.__data__, key) > -1;
	    }

	    /**
	     * Sets the list cache `key` to `value`.
	     *
	     * @private
	     * @name set
	     * @memberOf ListCache
	     * @param {string} key The key of the value to set.
	     * @param {*} value The value to set.
	     * @returns {Object} Returns the list cache instance.
	     */
	    function listCacheSet(key, value) {
	      var data = this.__data__,
	          index = assocIndexOf(data, key);

	      if (index < 0) {
	        ++this.size;
	        data.push([key, value]);
	      } else {
	        data[index][1] = value;
	      }
	      return this;
	    }

	    // Add methods to `ListCache`.
	    ListCache.prototype.clear = listCacheClear;
	    ListCache.prototype['delete'] = listCacheDelete;
	    ListCache.prototype.get = listCacheGet;
	    ListCache.prototype.has = listCacheHas;
	    ListCache.prototype.set = listCacheSet;

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates a map cache object to store key-value pairs.
	     *
	     * @private
	     * @constructor
	     * @param {Array} [entries] The key-value pairs to cache.
	     */
	    function MapCache(entries) {
	      var index = -1,
	          length = entries == null ? 0 : entries.length;

	      this.clear();
	      while (++index < length) {
	        var entry = entries[index];
	        this.set(entry[0], entry[1]);
	      }
	    }

	    /**
	     * Removes all key-value entries from the map.
	     *
	     * @private
	     * @name clear
	     * @memberOf MapCache
	     */
	    function mapCacheClear() {
	      this.size = 0;
	      this.__data__ = {
	        'hash': new Hash,
	        'map': new (Map || ListCache),
	        'string': new Hash
	      };
	    }

	    /**
	     * Removes `key` and its value from the map.
	     *
	     * @private
	     * @name delete
	     * @memberOf MapCache
	     * @param {string} key The key of the value to remove.
	     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
	     */
	    function mapCacheDelete(key) {
	      var result = getMapData(this, key)['delete'](key);
	      this.size -= result ? 1 : 0;
	      return result;
	    }

	    /**
	     * Gets the map value for `key`.
	     *
	     * @private
	     * @name get
	     * @memberOf MapCache
	     * @param {string} key The key of the value to get.
	     * @returns {*} Returns the entry value.
	     */
	    function mapCacheGet(key) {
	      return getMapData(this, key).get(key);
	    }

	    /**
	     * Checks if a map value for `key` exists.
	     *
	     * @private
	     * @name has
	     * @memberOf MapCache
	     * @param {string} key The key of the entry to check.
	     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
	     */
	    function mapCacheHas(key) {
	      return getMapData(this, key).has(key);
	    }

	    /**
	     * Sets the map `key` to `value`.
	     *
	     * @private
	     * @name set
	     * @memberOf MapCache
	     * @param {string} key The key of the value to set.
	     * @param {*} value The value to set.
	     * @returns {Object} Returns the map cache instance.
	     */
	    function mapCacheSet(key, value) {
	      var data = getMapData(this, key),
	          size = data.size;

	      data.set(key, value);
	      this.size += data.size == size ? 0 : 1;
	      return this;
	    }

	    // Add methods to `MapCache`.
	    MapCache.prototype.clear = mapCacheClear;
	    MapCache.prototype['delete'] = mapCacheDelete;
	    MapCache.prototype.get = mapCacheGet;
	    MapCache.prototype.has = mapCacheHas;
	    MapCache.prototype.set = mapCacheSet;

	    /*------------------------------------------------------------------------*/

	    /**
	     *
	     * Creates an array cache object to store unique values.
	     *
	     * @private
	     * @constructor
	     * @param {Array} [values] The values to cache.
	     */
	    function SetCache(values) {
	      var index = -1,
	          length = values == null ? 0 : values.length;

	      this.__data__ = new MapCache;
	      while (++index < length) {
	        this.add(values[index]);
	      }
	    }

	    /**
	     * Adds `value` to the array cache.
	     *
	     * @private
	     * @name add
	     * @memberOf SetCache
	     * @alias push
	     * @param {*} value The value to cache.
	     * @returns {Object} Returns the cache instance.
	     */
	    function setCacheAdd(value) {
	      this.__data__.set(value, HASH_UNDEFINED);
	      return this;
	    }

	    /**
	     * Checks if `value` is in the array cache.
	     *
	     * @private
	     * @name has
	     * @memberOf SetCache
	     * @param {*} value The value to search for.
	     * @returns {number} Returns `true` if `value` is found, else `false`.
	     */
	    function setCacheHas(value) {
	      return this.__data__.has(value);
	    }

	    // Add methods to `SetCache`.
	    SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
	    SetCache.prototype.has = setCacheHas;

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates a stack cache object to store key-value pairs.
	     *
	     * @private
	     * @constructor
	     * @param {Array} [entries] The key-value pairs to cache.
	     */
	    function Stack(entries) {
	      var data = this.__data__ = new ListCache(entries);
	      this.size = data.size;
	    }

	    /**
	     * Removes all key-value entries from the stack.
	     *
	     * @private
	     * @name clear
	     * @memberOf Stack
	     */
	    function stackClear() {
	      this.__data__ = new ListCache;
	      this.size = 0;
	    }

	    /**
	     * Removes `key` and its value from the stack.
	     *
	     * @private
	     * @name delete
	     * @memberOf Stack
	     * @param {string} key The key of the value to remove.
	     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
	     */
	    function stackDelete(key) {
	      var data = this.__data__,
	          result = data['delete'](key);

	      this.size = data.size;
	      return result;
	    }

	    /**
	     * Gets the stack value for `key`.
	     *
	     * @private
	     * @name get
	     * @memberOf Stack
	     * @param {string} key The key of the value to get.
	     * @returns {*} Returns the entry value.
	     */
	    function stackGet(key) {
	      return this.__data__.get(key);
	    }

	    /**
	     * Checks if a stack value for `key` exists.
	     *
	     * @private
	     * @name has
	     * @memberOf Stack
	     * @param {string} key The key of the entry to check.
	     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
	     */
	    function stackHas(key) {
	      return this.__data__.has(key);
	    }

	    /**
	     * Sets the stack `key` to `value`.
	     *
	     * @private
	     * @name set
	     * @memberOf Stack
	     * @param {string} key The key of the value to set.
	     * @param {*} value The value to set.
	     * @returns {Object} Returns the stack cache instance.
	     */
	    function stackSet(key, value) {
	      var data = this.__data__;
	      if (data instanceof ListCache) {
	        var pairs = data.__data__;
	        if (!Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
	          pairs.push([key, value]);
	          this.size = ++data.size;
	          return this;
	        }
	        data = this.__data__ = new MapCache(pairs);
	      }
	      data.set(key, value);
	      this.size = data.size;
	      return this;
	    }

	    // Add methods to `Stack`.
	    Stack.prototype.clear = stackClear;
	    Stack.prototype['delete'] = stackDelete;
	    Stack.prototype.get = stackGet;
	    Stack.prototype.has = stackHas;
	    Stack.prototype.set = stackSet;

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates an array of the enumerable property names of the array-like `value`.
	     *
	     * @private
	     * @param {*} value The value to query.
	     * @param {boolean} inherited Specify returning inherited property names.
	     * @returns {Array} Returns the array of property names.
	     */
	    function arrayLikeKeys(value, inherited) {
	      var isArr = isArray(value),
	          isArg = !isArr && isArguments(value),
	          isBuff = !isArr && !isArg && isBuffer(value),
	          isType = !isArr && !isArg && !isBuff && isTypedArray(value),
	          skipIndexes = isArr || isArg || isBuff || isType,
	          result = skipIndexes ? baseTimes(value.length, String) : [],
	          length = result.length;

	      for (var key in value) {
	        if ((inherited || hasOwnProperty.call(value, key)) &&
	            !(skipIndexes && (
	               // Safari 9 has enumerable `arguments.length` in strict mode.
	               key == 'length' ||
	               // Node.js 0.10 has enumerable non-index properties on buffers.
	               (isBuff && (key == 'offset' || key == 'parent')) ||
	               // PhantomJS 2 has enumerable non-index properties on typed arrays.
	               (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
	               // Skip index properties.
	               isIndex(key, length)
	            ))) {
	          result.push(key);
	        }
	      }
	      return result;
	    }

	    /**
	     * A specialized version of `_.sample` for arrays.
	     *
	     * @private
	     * @param {Array} array The array to sample.
	     * @returns {*} Returns the random element.
	     */
	    function arraySample(array) {
	      var length = array.length;
	      return length ? array[baseRandom(0, length - 1)] : undefined$1;
	    }

	    /**
	     * A specialized version of `_.sampleSize` for arrays.
	     *
	     * @private
	     * @param {Array} array The array to sample.
	     * @param {number} n The number of elements to sample.
	     * @returns {Array} Returns the random elements.
	     */
	    function arraySampleSize(array, n) {
	      return shuffleSelf(copyArray(array), baseClamp(n, 0, array.length));
	    }

	    /**
	     * A specialized version of `_.shuffle` for arrays.
	     *
	     * @private
	     * @param {Array} array The array to shuffle.
	     * @returns {Array} Returns the new shuffled array.
	     */
	    function arrayShuffle(array) {
	      return shuffleSelf(copyArray(array));
	    }

	    /**
	     * This function is like `assignValue` except that it doesn't assign
	     * `undefined` values.
	     *
	     * @private
	     * @param {Object} object The object to modify.
	     * @param {string} key The key of the property to assign.
	     * @param {*} value The value to assign.
	     */
	    function assignMergeValue(object, key, value) {
	      if ((value !== undefined$1 && !eq(object[key], value)) ||
	          (value === undefined$1 && !(key in object))) {
	        baseAssignValue(object, key, value);
	      }
	    }

	    /**
	     * Assigns `value` to `key` of `object` if the existing value is not equivalent
	     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * for equality comparisons.
	     *
	     * @private
	     * @param {Object} object The object to modify.
	     * @param {string} key The key of the property to assign.
	     * @param {*} value The value to assign.
	     */
	    function assignValue(object, key, value) {
	      var objValue = object[key];
	      if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
	          (value === undefined$1 && !(key in object))) {
	        baseAssignValue(object, key, value);
	      }
	    }

	    /**
	     * Gets the index at which the `key` is found in `array` of key-value pairs.
	     *
	     * @private
	     * @param {Array} array The array to inspect.
	     * @param {*} key The key to search for.
	     * @returns {number} Returns the index of the matched value, else `-1`.
	     */
	    function assocIndexOf(array, key) {
	      var length = array.length;
	      while (length--) {
	        if (eq(array[length][0], key)) {
	          return length;
	        }
	      }
	      return -1;
	    }

	    /**
	     * Aggregates elements of `collection` on `accumulator` with keys transformed
	     * by `iteratee` and values set by `setter`.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} setter The function to set `accumulator` values.
	     * @param {Function} iteratee The iteratee to transform keys.
	     * @param {Object} accumulator The initial aggregated object.
	     * @returns {Function} Returns `accumulator`.
	     */
	    function baseAggregator(collection, setter, iteratee, accumulator) {
	      baseEach(collection, function(value, key, collection) {
	        setter(accumulator, value, iteratee(value), collection);
	      });
	      return accumulator;
	    }

	    /**
	     * The base implementation of `_.assign` without support for multiple sources
	     * or `customizer` functions.
	     *
	     * @private
	     * @param {Object} object The destination object.
	     * @param {Object} source The source object.
	     * @returns {Object} Returns `object`.
	     */
	    function baseAssign(object, source) {
	      return object && copyObject(source, keys(source), object);
	    }

	    /**
	     * The base implementation of `_.assignIn` without support for multiple sources
	     * or `customizer` functions.
	     *
	     * @private
	     * @param {Object} object The destination object.
	     * @param {Object} source The source object.
	     * @returns {Object} Returns `object`.
	     */
	    function baseAssignIn(object, source) {
	      return object && copyObject(source, keysIn(source), object);
	    }

	    /**
	     * The base implementation of `assignValue` and `assignMergeValue` without
	     * value checks.
	     *
	     * @private
	     * @param {Object} object The object to modify.
	     * @param {string} key The key of the property to assign.
	     * @param {*} value The value to assign.
	     */
	    function baseAssignValue(object, key, value) {
	      if (key == '__proto__' && defineProperty) {
	        defineProperty(object, key, {
	          'configurable': true,
	          'enumerable': true,
	          'value': value,
	          'writable': true
	        });
	      } else {
	        object[key] = value;
	      }
	    }

	    /**
	     * The base implementation of `_.at` without support for individual paths.
	     *
	     * @private
	     * @param {Object} object The object to iterate over.
	     * @param {string[]} paths The property paths to pick.
	     * @returns {Array} Returns the picked elements.
	     */
	    function baseAt(object, paths) {
	      var index = -1,
	          length = paths.length,
	          result = Array(length),
	          skip = object == null;

	      while (++index < length) {
	        result[index] = skip ? undefined$1 : get(object, paths[index]);
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.clamp` which doesn't coerce arguments.
	     *
	     * @private
	     * @param {number} number The number to clamp.
	     * @param {number} [lower] The lower bound.
	     * @param {number} upper The upper bound.
	     * @returns {number} Returns the clamped number.
	     */
	    function baseClamp(number, lower, upper) {
	      if (number === number) {
	        if (upper !== undefined$1) {
	          number = number <= upper ? number : upper;
	        }
	        if (lower !== undefined$1) {
	          number = number >= lower ? number : lower;
	        }
	      }
	      return number;
	    }

	    /**
	     * The base implementation of `_.clone` and `_.cloneDeep` which tracks
	     * traversed objects.
	     *
	     * @private
	     * @param {*} value The value to clone.
	     * @param {boolean} bitmask The bitmask flags.
	     *  1 - Deep clone
	     *  2 - Flatten inherited properties
	     *  4 - Clone symbols
	     * @param {Function} [customizer] The function to customize cloning.
	     * @param {string} [key] The key of `value`.
	     * @param {Object} [object] The parent object of `value`.
	     * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
	     * @returns {*} Returns the cloned value.
	     */
	    function baseClone(value, bitmask, customizer, key, object, stack) {
	      var result,
	          isDeep = bitmask & CLONE_DEEP_FLAG,
	          isFlat = bitmask & CLONE_FLAT_FLAG,
	          isFull = bitmask & CLONE_SYMBOLS_FLAG;

	      if (customizer) {
	        result = object ? customizer(value, key, object, stack) : customizer(value);
	      }
	      if (result !== undefined$1) {
	        return result;
	      }
	      if (!isObject(value)) {
	        return value;
	      }
	      var isArr = isArray(value);
	      if (isArr) {
	        result = initCloneArray(value);
	        if (!isDeep) {
	          return copyArray(value, result);
	        }
	      } else {
	        var tag = getTag(value),
	            isFunc = tag == funcTag || tag == genTag;

	        if (isBuffer(value)) {
	          return cloneBuffer(value, isDeep);
	        }
	        if (tag == objectTag || tag == argsTag || (isFunc && !object)) {
	          result = (isFlat || isFunc) ? {} : initCloneObject(value);
	          if (!isDeep) {
	            return isFlat
	              ? copySymbolsIn(value, baseAssignIn(result, value))
	              : copySymbols(value, baseAssign(result, value));
	          }
	        } else {
	          if (!cloneableTags[tag]) {
	            return object ? value : {};
	          }
	          result = initCloneByTag(value, tag, isDeep);
	        }
	      }
	      // Check for circular references and return its corresponding clone.
	      stack || (stack = new Stack);
	      var stacked = stack.get(value);
	      if (stacked) {
	        return stacked;
	      }
	      stack.set(value, result);

	      if (isSet(value)) {
	        value.forEach(function(subValue) {
	          result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
	        });
	      } else if (isMap(value)) {
	        value.forEach(function(subValue, key) {
	          result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
	        });
	      }

	      var keysFunc = isFull
	        ? (isFlat ? getAllKeysIn : getAllKeys)
	        : (isFlat ? keysIn : keys);

	      var props = isArr ? undefined$1 : keysFunc(value);
	      arrayEach(props || value, function(subValue, key) {
	        if (props) {
	          key = subValue;
	          subValue = value[key];
	        }
	        // Recursively populate clone (susceptible to call stack limits).
	        assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
	      });
	      return result;
	    }

	    /**
	     * The base implementation of `_.conforms` which doesn't clone `source`.
	     *
	     * @private
	     * @param {Object} source The object of property predicates to conform to.
	     * @returns {Function} Returns the new spec function.
	     */
	    function baseConforms(source) {
	      var props = keys(source);
	      return function(object) {
	        return baseConformsTo(object, source, props);
	      };
	    }

	    /**
	     * The base implementation of `_.conformsTo` which accepts `props` to check.
	     *
	     * @private
	     * @param {Object} object The object to inspect.
	     * @param {Object} source The object of property predicates to conform to.
	     * @returns {boolean} Returns `true` if `object` conforms, else `false`.
	     */
	    function baseConformsTo(object, source, props) {
	      var length = props.length;
	      if (object == null) {
	        return !length;
	      }
	      object = Object(object);
	      while (length--) {
	        var key = props[length],
	            predicate = source[key],
	            value = object[key];

	        if ((value === undefined$1 && !(key in object)) || !predicate(value)) {
	          return false;
	        }
	      }
	      return true;
	    }

	    /**
	     * The base implementation of `_.delay` and `_.defer` which accepts `args`
	     * to provide to `func`.
	     *
	     * @private
	     * @param {Function} func The function to delay.
	     * @param {number} wait The number of milliseconds to delay invocation.
	     * @param {Array} args The arguments to provide to `func`.
	     * @returns {number|Object} Returns the timer id or timeout object.
	     */
	    function baseDelay(func, wait, args) {
	      if (typeof func != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      return setTimeout(function() { func.apply(undefined$1, args); }, wait);
	    }

	    /**
	     * The base implementation of methods like `_.difference` without support
	     * for excluding multiple arrays or iteratee shorthands.
	     *
	     * @private
	     * @param {Array} array The array to inspect.
	     * @param {Array} values The values to exclude.
	     * @param {Function} [iteratee] The iteratee invoked per element.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new array of filtered values.
	     */
	    function baseDifference(array, values, iteratee, comparator) {
	      var index = -1,
	          includes = arrayIncludes,
	          isCommon = true,
	          length = array.length,
	          result = [],
	          valuesLength = values.length;

	      if (!length) {
	        return result;
	      }
	      if (iteratee) {
	        values = arrayMap(values, baseUnary(iteratee));
	      }
	      if (comparator) {
	        includes = arrayIncludesWith;
	        isCommon = false;
	      }
	      else if (values.length >= LARGE_ARRAY_SIZE) {
	        includes = cacheHas;
	        isCommon = false;
	        values = new SetCache(values);
	      }
	      outer:
	      while (++index < length) {
	        var value = array[index],
	            computed = iteratee == null ? value : iteratee(value);

	        value = (comparator || value !== 0) ? value : 0;
	        if (isCommon && computed === computed) {
	          var valuesIndex = valuesLength;
	          while (valuesIndex--) {
	            if (values[valuesIndex] === computed) {
	              continue outer;
	            }
	          }
	          result.push(value);
	        }
	        else if (!includes(values, computed, comparator)) {
	          result.push(value);
	        }
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.forEach` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} iteratee The function invoked per iteration.
	     * @returns {Array|Object} Returns `collection`.
	     */
	    var baseEach = createBaseEach(baseForOwn);

	    /**
	     * The base implementation of `_.forEachRight` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} iteratee The function invoked per iteration.
	     * @returns {Array|Object} Returns `collection`.
	     */
	    var baseEachRight = createBaseEach(baseForOwnRight, true);

	    /**
	     * The base implementation of `_.every` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} predicate The function invoked per iteration.
	     * @returns {boolean} Returns `true` if all elements pass the predicate check,
	     *  else `false`
	     */
	    function baseEvery(collection, predicate) {
	      var result = true;
	      baseEach(collection, function(value, index, collection) {
	        result = !!predicate(value, index, collection);
	        return result;
	      });
	      return result;
	    }

	    /**
	     * The base implementation of methods like `_.max` and `_.min` which accepts a
	     * `comparator` to determine the extremum value.
	     *
	     * @private
	     * @param {Array} array The array to iterate over.
	     * @param {Function} iteratee The iteratee invoked per iteration.
	     * @param {Function} comparator The comparator used to compare values.
	     * @returns {*} Returns the extremum value.
	     */
	    function baseExtremum(array, iteratee, comparator) {
	      var index = -1,
	          length = array.length;

	      while (++index < length) {
	        var value = array[index],
	            current = iteratee(value);

	        if (current != null && (computed === undefined$1
	              ? (current === current && !isSymbol(current))
	              : comparator(current, computed)
	            )) {
	          var computed = current,
	              result = value;
	        }
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.fill` without an iteratee call guard.
	     *
	     * @private
	     * @param {Array} array The array to fill.
	     * @param {*} value The value to fill `array` with.
	     * @param {number} [start=0] The start position.
	     * @param {number} [end=array.length] The end position.
	     * @returns {Array} Returns `array`.
	     */
	    function baseFill(array, value, start, end) {
	      var length = array.length;

	      start = toInteger(start);
	      if (start < 0) {
	        start = -start > length ? 0 : (length + start);
	      }
	      end = (end === undefined$1 || end > length) ? length : toInteger(end);
	      if (end < 0) {
	        end += length;
	      }
	      end = start > end ? 0 : toLength(end);
	      while (start < end) {
	        array[start++] = value;
	      }
	      return array;
	    }

	    /**
	     * The base implementation of `_.filter` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} predicate The function invoked per iteration.
	     * @returns {Array} Returns the new filtered array.
	     */
	    function baseFilter(collection, predicate) {
	      var result = [];
	      baseEach(collection, function(value, index, collection) {
	        if (predicate(value, index, collection)) {
	          result.push(value);
	        }
	      });
	      return result;
	    }

	    /**
	     * The base implementation of `_.flatten` with support for restricting flattening.
	     *
	     * @private
	     * @param {Array} array The array to flatten.
	     * @param {number} depth The maximum recursion depth.
	     * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
	     * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
	     * @param {Array} [result=[]] The initial result value.
	     * @returns {Array} Returns the new flattened array.
	     */
	    function baseFlatten(array, depth, predicate, isStrict, result) {
	      var index = -1,
	          length = array.length;

	      predicate || (predicate = isFlattenable);
	      result || (result = []);

	      while (++index < length) {
	        var value = array[index];
	        if (depth > 0 && predicate(value)) {
	          if (depth > 1) {
	            // Recursively flatten arrays (susceptible to call stack limits).
	            baseFlatten(value, depth - 1, predicate, isStrict, result);
	          } else {
	            arrayPush(result, value);
	          }
	        } else if (!isStrict) {
	          result[result.length] = value;
	        }
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `baseForOwn` which iterates over `object`
	     * properties returned by `keysFunc` and invokes `iteratee` for each property.
	     * Iteratee functions may exit iteration early by explicitly returning `false`.
	     *
	     * @private
	     * @param {Object} object The object to iterate over.
	     * @param {Function} iteratee The function invoked per iteration.
	     * @param {Function} keysFunc The function to get the keys of `object`.
	     * @returns {Object} Returns `object`.
	     */
	    var baseFor = createBaseFor();

	    /**
	     * This function is like `baseFor` except that it iterates over properties
	     * in the opposite order.
	     *
	     * @private
	     * @param {Object} object The object to iterate over.
	     * @param {Function} iteratee The function invoked per iteration.
	     * @param {Function} keysFunc The function to get the keys of `object`.
	     * @returns {Object} Returns `object`.
	     */
	    var baseForRight = createBaseFor(true);

	    /**
	     * The base implementation of `_.forOwn` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Object} object The object to iterate over.
	     * @param {Function} iteratee The function invoked per iteration.
	     * @returns {Object} Returns `object`.
	     */
	    function baseForOwn(object, iteratee) {
	      return object && baseFor(object, iteratee, keys);
	    }

	    /**
	     * The base implementation of `_.forOwnRight` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Object} object The object to iterate over.
	     * @param {Function} iteratee The function invoked per iteration.
	     * @returns {Object} Returns `object`.
	     */
	    function baseForOwnRight(object, iteratee) {
	      return object && baseForRight(object, iteratee, keys);
	    }

	    /**
	     * The base implementation of `_.functions` which creates an array of
	     * `object` function property names filtered from `props`.
	     *
	     * @private
	     * @param {Object} object The object to inspect.
	     * @param {Array} props The property names to filter.
	     * @returns {Array} Returns the function names.
	     */
	    function baseFunctions(object, props) {
	      return arrayFilter(props, function(key) {
	        return isFunction(object[key]);
	      });
	    }

	    /**
	     * The base implementation of `_.get` without support for default values.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @param {Array|string} path The path of the property to get.
	     * @returns {*} Returns the resolved value.
	     */
	    function baseGet(object, path) {
	      path = castPath(path, object);

	      var index = 0,
	          length = path.length;

	      while (object != null && index < length) {
	        object = object[toKey(path[index++])];
	      }
	      return (index && index == length) ? object : undefined$1;
	    }

	    /**
	     * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
	     * `keysFunc` and `symbolsFunc` to get the enumerable property names and
	     * symbols of `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @param {Function} keysFunc The function to get the keys of `object`.
	     * @param {Function} symbolsFunc The function to get the symbols of `object`.
	     * @returns {Array} Returns the array of property names and symbols.
	     */
	    function baseGetAllKeys(object, keysFunc, symbolsFunc) {
	      var result = keysFunc(object);
	      return isArray(object) ? result : arrayPush(result, symbolsFunc(object));
	    }

	    /**
	     * The base implementation of `getTag` without fallbacks for buggy environments.
	     *
	     * @private
	     * @param {*} value The value to query.
	     * @returns {string} Returns the `toStringTag`.
	     */
	    function baseGetTag(value) {
	      if (value == null) {
	        return value === undefined$1 ? undefinedTag : nullTag;
	      }
	      return (symToStringTag && symToStringTag in Object(value))
	        ? getRawTag(value)
	        : objectToString(value);
	    }

	    /**
	     * The base implementation of `_.gt` which doesn't coerce arguments.
	     *
	     * @private
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {boolean} Returns `true` if `value` is greater than `other`,
	     *  else `false`.
	     */
	    function baseGt(value, other) {
	      return value > other;
	    }

	    /**
	     * The base implementation of `_.has` without support for deep paths.
	     *
	     * @private
	     * @param {Object} [object] The object to query.
	     * @param {Array|string} key The key to check.
	     * @returns {boolean} Returns `true` if `key` exists, else `false`.
	     */
	    function baseHas(object, key) {
	      return object != null && hasOwnProperty.call(object, key);
	    }

	    /**
	     * The base implementation of `_.hasIn` without support for deep paths.
	     *
	     * @private
	     * @param {Object} [object] The object to query.
	     * @param {Array|string} key The key to check.
	     * @returns {boolean} Returns `true` if `key` exists, else `false`.
	     */
	    function baseHasIn(object, key) {
	      return object != null && key in Object(object);
	    }

	    /**
	     * The base implementation of `_.inRange` which doesn't coerce arguments.
	     *
	     * @private
	     * @param {number} number The number to check.
	     * @param {number} start The start of the range.
	     * @param {number} end The end of the range.
	     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
	     */
	    function baseInRange(number, start, end) {
	      return number >= nativeMin(start, end) && number < nativeMax(start, end);
	    }

	    /**
	     * The base implementation of methods like `_.intersection`, without support
	     * for iteratee shorthands, that accepts an array of arrays to inspect.
	     *
	     * @private
	     * @param {Array} arrays The arrays to inspect.
	     * @param {Function} [iteratee] The iteratee invoked per element.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new array of shared values.
	     */
	    function baseIntersection(arrays, iteratee, comparator) {
	      var includes = comparator ? arrayIncludesWith : arrayIncludes,
	          length = arrays[0].length,
	          othLength = arrays.length,
	          othIndex = othLength,
	          caches = Array(othLength),
	          maxLength = Infinity,
	          result = [];

	      while (othIndex--) {
	        var array = arrays[othIndex];
	        if (othIndex && iteratee) {
	          array = arrayMap(array, baseUnary(iteratee));
	        }
	        maxLength = nativeMin(array.length, maxLength);
	        caches[othIndex] = !comparator && (iteratee || (length >= 120 && array.length >= 120))
	          ? new SetCache(othIndex && array)
	          : undefined$1;
	      }
	      array = arrays[0];

	      var index = -1,
	          seen = caches[0];

	      outer:
	      while (++index < length && result.length < maxLength) {
	        var value = array[index],
	            computed = iteratee ? iteratee(value) : value;

	        value = (comparator || value !== 0) ? value : 0;
	        if (!(seen
	              ? cacheHas(seen, computed)
	              : includes(result, computed, comparator)
	            )) {
	          othIndex = othLength;
	          while (--othIndex) {
	            var cache = caches[othIndex];
	            if (!(cache
	                  ? cacheHas(cache, computed)
	                  : includes(arrays[othIndex], computed, comparator))
	                ) {
	              continue outer;
	            }
	          }
	          if (seen) {
	            seen.push(computed);
	          }
	          result.push(value);
	        }
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.invert` and `_.invertBy` which inverts
	     * `object` with values transformed by `iteratee` and set by `setter`.
	     *
	     * @private
	     * @param {Object} object The object to iterate over.
	     * @param {Function} setter The function to set `accumulator` values.
	     * @param {Function} iteratee The iteratee to transform values.
	     * @param {Object} accumulator The initial inverted object.
	     * @returns {Function} Returns `accumulator`.
	     */
	    function baseInverter(object, setter, iteratee, accumulator) {
	      baseForOwn(object, function(value, key, object) {
	        setter(accumulator, iteratee(value), key, object);
	      });
	      return accumulator;
	    }

	    /**
	     * The base implementation of `_.invoke` without support for individual
	     * method arguments.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @param {Array|string} path The path of the method to invoke.
	     * @param {Array} args The arguments to invoke the method with.
	     * @returns {*} Returns the result of the invoked method.
	     */
	    function baseInvoke(object, path, args) {
	      path = castPath(path, object);
	      object = parent(object, path);
	      var func = object == null ? object : object[toKey(last(path))];
	      return func == null ? undefined$1 : apply(func, object, args);
	    }

	    /**
	     * The base implementation of `_.isArguments`.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
	     */
	    function baseIsArguments(value) {
	      return isObjectLike(value) && baseGetTag(value) == argsTag;
	    }

	    /**
	     * The base implementation of `_.isArrayBuffer` without Node.js optimizations.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an array buffer, else `false`.
	     */
	    function baseIsArrayBuffer(value) {
	      return isObjectLike(value) && baseGetTag(value) == arrayBufferTag;
	    }

	    /**
	     * The base implementation of `_.isDate` without Node.js optimizations.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a date object, else `false`.
	     */
	    function baseIsDate(value) {
	      return isObjectLike(value) && baseGetTag(value) == dateTag;
	    }

	    /**
	     * The base implementation of `_.isEqual` which supports partial comparisons
	     * and tracks traversed objects.
	     *
	     * @private
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @param {boolean} bitmask The bitmask flags.
	     *  1 - Unordered comparison
	     *  2 - Partial comparison
	     * @param {Function} [customizer] The function to customize comparisons.
	     * @param {Object} [stack] Tracks traversed `value` and `other` objects.
	     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
	     */
	    function baseIsEqual(value, other, bitmask, customizer, stack) {
	      if (value === other) {
	        return true;
	      }
	      if (value == null || other == null || (!isObjectLike(value) && !isObjectLike(other))) {
	        return value !== value && other !== other;
	      }
	      return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
	    }

	    /**
	     * A specialized version of `baseIsEqual` for arrays and objects which performs
	     * deep comparisons and tracks traversed objects enabling objects with circular
	     * references to be compared.
	     *
	     * @private
	     * @param {Object} object The object to compare.
	     * @param {Object} other The other object to compare.
	     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
	     * @param {Function} customizer The function to customize comparisons.
	     * @param {Function} equalFunc The function to determine equivalents of values.
	     * @param {Object} [stack] Tracks traversed `object` and `other` objects.
	     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
	     */
	    function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
	      var objIsArr = isArray(object),
	          othIsArr = isArray(other),
	          objTag = objIsArr ? arrayTag : getTag(object),
	          othTag = othIsArr ? arrayTag : getTag(other);

	      objTag = objTag == argsTag ? objectTag : objTag;
	      othTag = othTag == argsTag ? objectTag : othTag;

	      var objIsObj = objTag == objectTag,
	          othIsObj = othTag == objectTag,
	          isSameTag = objTag == othTag;

	      if (isSameTag && isBuffer(object)) {
	        if (!isBuffer(other)) {
	          return false;
	        }
	        objIsArr = true;
	        objIsObj = false;
	      }
	      if (isSameTag && !objIsObj) {
	        stack || (stack = new Stack);
	        return (objIsArr || isTypedArray(object))
	          ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
	          : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
	      }
	      if (!(bitmask & COMPARE_PARTIAL_FLAG)) {
	        var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
	            othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

	        if (objIsWrapped || othIsWrapped) {
	          var objUnwrapped = objIsWrapped ? object.value() : object,
	              othUnwrapped = othIsWrapped ? other.value() : other;

	          stack || (stack = new Stack);
	          return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
	        }
	      }
	      if (!isSameTag) {
	        return false;
	      }
	      stack || (stack = new Stack);
	      return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
	    }

	    /**
	     * The base implementation of `_.isMap` without Node.js optimizations.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a map, else `false`.
	     */
	    function baseIsMap(value) {
	      return isObjectLike(value) && getTag(value) == mapTag;
	    }

	    /**
	     * The base implementation of `_.isMatch` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Object} object The object to inspect.
	     * @param {Object} source The object of property values to match.
	     * @param {Array} matchData The property names, values, and compare flags to match.
	     * @param {Function} [customizer] The function to customize comparisons.
	     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
	     */
	    function baseIsMatch(object, source, matchData, customizer) {
	      var index = matchData.length,
	          length = index,
	          noCustomizer = !customizer;

	      if (object == null) {
	        return !length;
	      }
	      object = Object(object);
	      while (index--) {
	        var data = matchData[index];
	        if ((noCustomizer && data[2])
	              ? data[1] !== object[data[0]]
	              : !(data[0] in object)
	            ) {
	          return false;
	        }
	      }
	      while (++index < length) {
	        data = matchData[index];
	        var key = data[0],
	            objValue = object[key],
	            srcValue = data[1];

	        if (noCustomizer && data[2]) {
	          if (objValue === undefined$1 && !(key in object)) {
	            return false;
	          }
	        } else {
	          var stack = new Stack;
	          if (customizer) {
	            var result = customizer(objValue, srcValue, key, object, source, stack);
	          }
	          if (!(result === undefined$1
	                ? baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG, customizer, stack)
	                : result
	              )) {
	            return false;
	          }
	        }
	      }
	      return true;
	    }

	    /**
	     * The base implementation of `_.isNative` without bad shim checks.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a native function,
	     *  else `false`.
	     */
	    function baseIsNative(value) {
	      if (!isObject(value) || isMasked(value)) {
	        return false;
	      }
	      var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
	      return pattern.test(toSource(value));
	    }

	    /**
	     * The base implementation of `_.isRegExp` without Node.js optimizations.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
	     */
	    function baseIsRegExp(value) {
	      return isObjectLike(value) && baseGetTag(value) == regexpTag;
	    }

	    /**
	     * The base implementation of `_.isSet` without Node.js optimizations.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a set, else `false`.
	     */
	    function baseIsSet(value) {
	      return isObjectLike(value) && getTag(value) == setTag;
	    }

	    /**
	     * The base implementation of `_.isTypedArray` without Node.js optimizations.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
	     */
	    function baseIsTypedArray(value) {
	      return isObjectLike(value) &&
	        isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
	    }

	    /**
	     * The base implementation of `_.iteratee`.
	     *
	     * @private
	     * @param {*} [value=_.identity] The value to convert to an iteratee.
	     * @returns {Function} Returns the iteratee.
	     */
	    function baseIteratee(value) {
	      // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
	      // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
	      if (typeof value == 'function') {
	        return value;
	      }
	      if (value == null) {
	        return identity;
	      }
	      if (typeof value == 'object') {
	        return isArray(value)
	          ? baseMatchesProperty(value[0], value[1])
	          : baseMatches(value);
	      }
	      return property(value);
	    }

	    /**
	     * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property names.
	     */
	    function baseKeys(object) {
	      if (!isPrototype(object)) {
	        return nativeKeys(object);
	      }
	      var result = [];
	      for (var key in Object(object)) {
	        if (hasOwnProperty.call(object, key) && key != 'constructor') {
	          result.push(key);
	        }
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property names.
	     */
	    function baseKeysIn(object) {
	      if (!isObject(object)) {
	        return nativeKeysIn(object);
	      }
	      var isProto = isPrototype(object),
	          result = [];

	      for (var key in object) {
	        if (!(key == 'constructor' && (isProto || !hasOwnProperty.call(object, key)))) {
	          result.push(key);
	        }
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.lt` which doesn't coerce arguments.
	     *
	     * @private
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {boolean} Returns `true` if `value` is less than `other`,
	     *  else `false`.
	     */
	    function baseLt(value, other) {
	      return value < other;
	    }

	    /**
	     * The base implementation of `_.map` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} iteratee The function invoked per iteration.
	     * @returns {Array} Returns the new mapped array.
	     */
	    function baseMap(collection, iteratee) {
	      var index = -1,
	          result = isArrayLike(collection) ? Array(collection.length) : [];

	      baseEach(collection, function(value, key, collection) {
	        result[++index] = iteratee(value, key, collection);
	      });
	      return result;
	    }

	    /**
	     * The base implementation of `_.matches` which doesn't clone `source`.
	     *
	     * @private
	     * @param {Object} source The object of property values to match.
	     * @returns {Function} Returns the new spec function.
	     */
	    function baseMatches(source) {
	      var matchData = getMatchData(source);
	      if (matchData.length == 1 && matchData[0][2]) {
	        return matchesStrictComparable(matchData[0][0], matchData[0][1]);
	      }
	      return function(object) {
	        return object === source || baseIsMatch(object, source, matchData);
	      };
	    }

	    /**
	     * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
	     *
	     * @private
	     * @param {string} path The path of the property to get.
	     * @param {*} srcValue The value to match.
	     * @returns {Function} Returns the new spec function.
	     */
	    function baseMatchesProperty(path, srcValue) {
	      if (isKey(path) && isStrictComparable(srcValue)) {
	        return matchesStrictComparable(toKey(path), srcValue);
	      }
	      return function(object) {
	        var objValue = get(object, path);
	        return (objValue === undefined$1 && objValue === srcValue)
	          ? hasIn(object, path)
	          : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
	      };
	    }

	    /**
	     * The base implementation of `_.merge` without support for multiple sources.
	     *
	     * @private
	     * @param {Object} object The destination object.
	     * @param {Object} source The source object.
	     * @param {number} srcIndex The index of `source`.
	     * @param {Function} [customizer] The function to customize merged values.
	     * @param {Object} [stack] Tracks traversed source values and their merged
	     *  counterparts.
	     */
	    function baseMerge(object, source, srcIndex, customizer, stack) {
	      if (object === source) {
	        return;
	      }
	      baseFor(source, function(srcValue, key) {
	        stack || (stack = new Stack);
	        if (isObject(srcValue)) {
	          baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
	        }
	        else {
	          var newValue = customizer
	            ? customizer(safeGet(object, key), srcValue, (key + ''), object, source, stack)
	            : undefined$1;

	          if (newValue === undefined$1) {
	            newValue = srcValue;
	          }
	          assignMergeValue(object, key, newValue);
	        }
	      }, keysIn);
	    }

	    /**
	     * A specialized version of `baseMerge` for arrays and objects which performs
	     * deep merges and tracks traversed objects enabling objects with circular
	     * references to be merged.
	     *
	     * @private
	     * @param {Object} object The destination object.
	     * @param {Object} source The source object.
	     * @param {string} key The key of the value to merge.
	     * @param {number} srcIndex The index of `source`.
	     * @param {Function} mergeFunc The function to merge values.
	     * @param {Function} [customizer] The function to customize assigned values.
	     * @param {Object} [stack] Tracks traversed source values and their merged
	     *  counterparts.
	     */
	    function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
	      var objValue = safeGet(object, key),
	          srcValue = safeGet(source, key),
	          stacked = stack.get(srcValue);

	      if (stacked) {
	        assignMergeValue(object, key, stacked);
	        return;
	      }
	      var newValue = customizer
	        ? customizer(objValue, srcValue, (key + ''), object, source, stack)
	        : undefined$1;

	      var isCommon = newValue === undefined$1;

	      if (isCommon) {
	        var isArr = isArray(srcValue),
	            isBuff = !isArr && isBuffer(srcValue),
	            isTyped = !isArr && !isBuff && isTypedArray(srcValue);

	        newValue = srcValue;
	        if (isArr || isBuff || isTyped) {
	          if (isArray(objValue)) {
	            newValue = objValue;
	          }
	          else if (isArrayLikeObject(objValue)) {
	            newValue = copyArray(objValue);
	          }
	          else if (isBuff) {
	            isCommon = false;
	            newValue = cloneBuffer(srcValue, true);
	          }
	          else if (isTyped) {
	            isCommon = false;
	            newValue = cloneTypedArray(srcValue, true);
	          }
	          else {
	            newValue = [];
	          }
	        }
	        else if (isPlainObject(srcValue) || isArguments(srcValue)) {
	          newValue = objValue;
	          if (isArguments(objValue)) {
	            newValue = toPlainObject(objValue);
	          }
	          else if (!isObject(objValue) || isFunction(objValue)) {
	            newValue = initCloneObject(srcValue);
	          }
	        }
	        else {
	          isCommon = false;
	        }
	      }
	      if (isCommon) {
	        // Recursively merge objects and arrays (susceptible to call stack limits).
	        stack.set(srcValue, newValue);
	        mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
	        stack['delete'](srcValue);
	      }
	      assignMergeValue(object, key, newValue);
	    }

	    /**
	     * The base implementation of `_.nth` which doesn't coerce arguments.
	     *
	     * @private
	     * @param {Array} array The array to query.
	     * @param {number} n The index of the element to return.
	     * @returns {*} Returns the nth element of `array`.
	     */
	    function baseNth(array, n) {
	      var length = array.length;
	      if (!length) {
	        return;
	      }
	      n += n < 0 ? length : 0;
	      return isIndex(n, length) ? array[n] : undefined$1;
	    }

	    /**
	     * The base implementation of `_.orderBy` without param guards.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
	     * @param {string[]} orders The sort orders of `iteratees`.
	     * @returns {Array} Returns the new sorted array.
	     */
	    function baseOrderBy(collection, iteratees, orders) {
	      if (iteratees.length) {
	        iteratees = arrayMap(iteratees, function(iteratee) {
	          if (isArray(iteratee)) {
	            return function(value) {
	              return baseGet(value, iteratee.length === 1 ? iteratee[0] : iteratee);
	            }
	          }
	          return iteratee;
	        });
	      } else {
	        iteratees = [identity];
	      }

	      var index = -1;
	      iteratees = arrayMap(iteratees, baseUnary(getIteratee()));

	      var result = baseMap(collection, function(value, key, collection) {
	        var criteria = arrayMap(iteratees, function(iteratee) {
	          return iteratee(value);
	        });
	        return { 'criteria': criteria, 'index': ++index, 'value': value };
	      });

	      return baseSortBy(result, function(object, other) {
	        return compareMultiple(object, other, orders);
	      });
	    }

	    /**
	     * The base implementation of `_.pick` without support for individual
	     * property identifiers.
	     *
	     * @private
	     * @param {Object} object The source object.
	     * @param {string[]} paths The property paths to pick.
	     * @returns {Object} Returns the new object.
	     */
	    function basePick(object, paths) {
	      return basePickBy(object, paths, function(value, path) {
	        return hasIn(object, path);
	      });
	    }

	    /**
	     * The base implementation of  `_.pickBy` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Object} object The source object.
	     * @param {string[]} paths The property paths to pick.
	     * @param {Function} predicate The function invoked per property.
	     * @returns {Object} Returns the new object.
	     */
	    function basePickBy(object, paths, predicate) {
	      var index = -1,
	          length = paths.length,
	          result = {};

	      while (++index < length) {
	        var path = paths[index],
	            value = baseGet(object, path);

	        if (predicate(value, path)) {
	          baseSet(result, castPath(path, object), value);
	        }
	      }
	      return result;
	    }

	    /**
	     * A specialized version of `baseProperty` which supports deep paths.
	     *
	     * @private
	     * @param {Array|string} path The path of the property to get.
	     * @returns {Function} Returns the new accessor function.
	     */
	    function basePropertyDeep(path) {
	      return function(object) {
	        return baseGet(object, path);
	      };
	    }

	    /**
	     * The base implementation of `_.pullAllBy` without support for iteratee
	     * shorthands.
	     *
	     * @private
	     * @param {Array} array The array to modify.
	     * @param {Array} values The values to remove.
	     * @param {Function} [iteratee] The iteratee invoked per element.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns `array`.
	     */
	    function basePullAll(array, values, iteratee, comparator) {
	      var indexOf = comparator ? baseIndexOfWith : baseIndexOf,
	          index = -1,
	          length = values.length,
	          seen = array;

	      if (array === values) {
	        values = copyArray(values);
	      }
	      if (iteratee) {
	        seen = arrayMap(array, baseUnary(iteratee));
	      }
	      while (++index < length) {
	        var fromIndex = 0,
	            value = values[index],
	            computed = iteratee ? iteratee(value) : value;

	        while ((fromIndex = indexOf(seen, computed, fromIndex, comparator)) > -1) {
	          if (seen !== array) {
	            splice.call(seen, fromIndex, 1);
	          }
	          splice.call(array, fromIndex, 1);
	        }
	      }
	      return array;
	    }

	    /**
	     * The base implementation of `_.pullAt` without support for individual
	     * indexes or capturing the removed elements.
	     *
	     * @private
	     * @param {Array} array The array to modify.
	     * @param {number[]} indexes The indexes of elements to remove.
	     * @returns {Array} Returns `array`.
	     */
	    function basePullAt(array, indexes) {
	      var length = array ? indexes.length : 0,
	          lastIndex = length - 1;

	      while (length--) {
	        var index = indexes[length];
	        if (length == lastIndex || index !== previous) {
	          var previous = index;
	          if (isIndex(index)) {
	            splice.call(array, index, 1);
	          } else {
	            baseUnset(array, index);
	          }
	        }
	      }
	      return array;
	    }

	    /**
	     * The base implementation of `_.random` without support for returning
	     * floating-point numbers.
	     *
	     * @private
	     * @param {number} lower The lower bound.
	     * @param {number} upper The upper bound.
	     * @returns {number} Returns the random number.
	     */
	    function baseRandom(lower, upper) {
	      return lower + nativeFloor(nativeRandom() * (upper - lower + 1));
	    }

	    /**
	     * The base implementation of `_.range` and `_.rangeRight` which doesn't
	     * coerce arguments.
	     *
	     * @private
	     * @param {number} start The start of the range.
	     * @param {number} end The end of the range.
	     * @param {number} step The value to increment or decrement by.
	     * @param {boolean} [fromRight] Specify iterating from right to left.
	     * @returns {Array} Returns the range of numbers.
	     */
	    function baseRange(start, end, step, fromRight) {
	      var index = -1,
	          length = nativeMax(nativeCeil((end - start) / (step || 1)), 0),
	          result = Array(length);

	      while (length--) {
	        result[fromRight ? length : ++index] = start;
	        start += step;
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.repeat` which doesn't coerce arguments.
	     *
	     * @private
	     * @param {string} string The string to repeat.
	     * @param {number} n The number of times to repeat the string.
	     * @returns {string} Returns the repeated string.
	     */
	    function baseRepeat(string, n) {
	      var result = '';
	      if (!string || n < 1 || n > MAX_SAFE_INTEGER) {
	        return result;
	      }
	      // Leverage the exponentiation by squaring algorithm for a faster repeat.
	      // See https://en.wikipedia.org/wiki/Exponentiation_by_squaring for more details.
	      do {
	        if (n % 2) {
	          result += string;
	        }
	        n = nativeFloor(n / 2);
	        if (n) {
	          string += string;
	        }
	      } while (n);

	      return result;
	    }

	    /**
	     * The base implementation of `_.rest` which doesn't validate or coerce arguments.
	     *
	     * @private
	     * @param {Function} func The function to apply a rest parameter to.
	     * @param {number} [start=func.length-1] The start position of the rest parameter.
	     * @returns {Function} Returns the new function.
	     */
	    function baseRest(func, start) {
	      return setToString(overRest(func, start, identity), func + '');
	    }

	    /**
	     * The base implementation of `_.sample`.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to sample.
	     * @returns {*} Returns the random element.
	     */
	    function baseSample(collection) {
	      return arraySample(values(collection));
	    }

	    /**
	     * The base implementation of `_.sampleSize` without param guards.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to sample.
	     * @param {number} n The number of elements to sample.
	     * @returns {Array} Returns the random elements.
	     */
	    function baseSampleSize(collection, n) {
	      var array = values(collection);
	      return shuffleSelf(array, baseClamp(n, 0, array.length));
	    }

	    /**
	     * The base implementation of `_.set`.
	     *
	     * @private
	     * @param {Object} object The object to modify.
	     * @param {Array|string} path The path of the property to set.
	     * @param {*} value The value to set.
	     * @param {Function} [customizer] The function to customize path creation.
	     * @returns {Object} Returns `object`.
	     */
	    function baseSet(object, path, value, customizer) {
	      if (!isObject(object)) {
	        return object;
	      }
	      path = castPath(path, object);

	      var index = -1,
	          length = path.length,
	          lastIndex = length - 1,
	          nested = object;

	      while (nested != null && ++index < length) {
	        var key = toKey(path[index]),
	            newValue = value;

	        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
	          return object;
	        }

	        if (index != lastIndex) {
	          var objValue = nested[key];
	          newValue = customizer ? customizer(objValue, key, nested) : undefined$1;
	          if (newValue === undefined$1) {
	            newValue = isObject(objValue)
	              ? objValue
	              : (isIndex(path[index + 1]) ? [] : {});
	          }
	        }
	        assignValue(nested, key, newValue);
	        nested = nested[key];
	      }
	      return object;
	    }

	    /**
	     * The base implementation of `setData` without support for hot loop shorting.
	     *
	     * @private
	     * @param {Function} func The function to associate metadata with.
	     * @param {*} data The metadata.
	     * @returns {Function} Returns `func`.
	     */
	    var baseSetData = !metaMap ? identity : function(func, data) {
	      metaMap.set(func, data);
	      return func;
	    };

	    /**
	     * The base implementation of `setToString` without support for hot loop shorting.
	     *
	     * @private
	     * @param {Function} func The function to modify.
	     * @param {Function} string The `toString` result.
	     * @returns {Function} Returns `func`.
	     */
	    var baseSetToString = !defineProperty ? identity : function(func, string) {
	      return defineProperty(func, 'toString', {
	        'configurable': true,
	        'enumerable': false,
	        'value': constant(string),
	        'writable': true
	      });
	    };

	    /**
	     * The base implementation of `_.shuffle`.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to shuffle.
	     * @returns {Array} Returns the new shuffled array.
	     */
	    function baseShuffle(collection) {
	      return shuffleSelf(values(collection));
	    }

	    /**
	     * The base implementation of `_.slice` without an iteratee call guard.
	     *
	     * @private
	     * @param {Array} array The array to slice.
	     * @param {number} [start=0] The start position.
	     * @param {number} [end=array.length] The end position.
	     * @returns {Array} Returns the slice of `array`.
	     */
	    function baseSlice(array, start, end) {
	      var index = -1,
	          length = array.length;

	      if (start < 0) {
	        start = -start > length ? 0 : (length + start);
	      }
	      end = end > length ? length : end;
	      if (end < 0) {
	        end += length;
	      }
	      length = start > end ? 0 : ((end - start) >>> 0);
	      start >>>= 0;

	      var result = Array(length);
	      while (++index < length) {
	        result[index] = array[index + start];
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.some` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} predicate The function invoked per iteration.
	     * @returns {boolean} Returns `true` if any element passes the predicate check,
	     *  else `false`.
	     */
	    function baseSome(collection, predicate) {
	      var result;

	      baseEach(collection, function(value, index, collection) {
	        result = predicate(value, index, collection);
	        return !result;
	      });
	      return !!result;
	    }

	    /**
	     * The base implementation of `_.sortedIndex` and `_.sortedLastIndex` which
	     * performs a binary search of `array` to determine the index at which `value`
	     * should be inserted into `array` in order to maintain its sort order.
	     *
	     * @private
	     * @param {Array} array The sorted array to inspect.
	     * @param {*} value The value to evaluate.
	     * @param {boolean} [retHighest] Specify returning the highest qualified index.
	     * @returns {number} Returns the index at which `value` should be inserted
	     *  into `array`.
	     */
	    function baseSortedIndex(array, value, retHighest) {
	      var low = 0,
	          high = array == null ? low : array.length;

	      if (typeof value == 'number' && value === value && high <= HALF_MAX_ARRAY_LENGTH) {
	        while (low < high) {
	          var mid = (low + high) >>> 1,
	              computed = array[mid];

	          if (computed !== null && !isSymbol(computed) &&
	              (retHighest ? (computed <= value) : (computed < value))) {
	            low = mid + 1;
	          } else {
	            high = mid;
	          }
	        }
	        return high;
	      }
	      return baseSortedIndexBy(array, value, identity, retHighest);
	    }

	    /**
	     * The base implementation of `_.sortedIndexBy` and `_.sortedLastIndexBy`
	     * which invokes `iteratee` for `value` and each element of `array` to compute
	     * their sort ranking. The iteratee is invoked with one argument; (value).
	     *
	     * @private
	     * @param {Array} array The sorted array to inspect.
	     * @param {*} value The value to evaluate.
	     * @param {Function} iteratee The iteratee invoked per element.
	     * @param {boolean} [retHighest] Specify returning the highest qualified index.
	     * @returns {number} Returns the index at which `value` should be inserted
	     *  into `array`.
	     */
	    function baseSortedIndexBy(array, value, iteratee, retHighest) {
	      var low = 0,
	          high = array == null ? 0 : array.length;
	      if (high === 0) {
	        return 0;
	      }

	      value = iteratee(value);
	      var valIsNaN = value !== value,
	          valIsNull = value === null,
	          valIsSymbol = isSymbol(value),
	          valIsUndefined = value === undefined$1;

	      while (low < high) {
	        var mid = nativeFloor((low + high) / 2),
	            computed = iteratee(array[mid]),
	            othIsDefined = computed !== undefined$1,
	            othIsNull = computed === null,
	            othIsReflexive = computed === computed,
	            othIsSymbol = isSymbol(computed);

	        if (valIsNaN) {
	          var setLow = retHighest || othIsReflexive;
	        } else if (valIsUndefined) {
	          setLow = othIsReflexive && (retHighest || othIsDefined);
	        } else if (valIsNull) {
	          setLow = othIsReflexive && othIsDefined && (retHighest || !othIsNull);
	        } else if (valIsSymbol) {
	          setLow = othIsReflexive && othIsDefined && !othIsNull && (retHighest || !othIsSymbol);
	        } else if (othIsNull || othIsSymbol) {
	          setLow = false;
	        } else {
	          setLow = retHighest ? (computed <= value) : (computed < value);
	        }
	        if (setLow) {
	          low = mid + 1;
	        } else {
	          high = mid;
	        }
	      }
	      return nativeMin(high, MAX_ARRAY_INDEX);
	    }

	    /**
	     * The base implementation of `_.sortedUniq` and `_.sortedUniqBy` without
	     * support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array} array The array to inspect.
	     * @param {Function} [iteratee] The iteratee invoked per element.
	     * @returns {Array} Returns the new duplicate free array.
	     */
	    function baseSortedUniq(array, iteratee) {
	      var index = -1,
	          length = array.length,
	          resIndex = 0,
	          result = [];

	      while (++index < length) {
	        var value = array[index],
	            computed = iteratee ? iteratee(value) : value;

	        if (!index || !eq(computed, seen)) {
	          var seen = computed;
	          result[resIndex++] = value === 0 ? 0 : value;
	        }
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.toNumber` which doesn't ensure correct
	     * conversions of binary, hexadecimal, or octal string values.
	     *
	     * @private
	     * @param {*} value The value to process.
	     * @returns {number} Returns the number.
	     */
	    function baseToNumber(value) {
	      if (typeof value == 'number') {
	        return value;
	      }
	      if (isSymbol(value)) {
	        return NAN;
	      }
	      return +value;
	    }

	    /**
	     * The base implementation of `_.toString` which doesn't convert nullish
	     * values to empty strings.
	     *
	     * @private
	     * @param {*} value The value to process.
	     * @returns {string} Returns the string.
	     */
	    function baseToString(value) {
	      // Exit early for strings to avoid a performance hit in some environments.
	      if (typeof value == 'string') {
	        return value;
	      }
	      if (isArray(value)) {
	        // Recursively convert values (susceptible to call stack limits).
	        return arrayMap(value, baseToString) + '';
	      }
	      if (isSymbol(value)) {
	        return symbolToString ? symbolToString.call(value) : '';
	      }
	      var result = (value + '');
	      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
	    }

	    /**
	     * The base implementation of `_.uniqBy` without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array} array The array to inspect.
	     * @param {Function} [iteratee] The iteratee invoked per element.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new duplicate free array.
	     */
	    function baseUniq(array, iteratee, comparator) {
	      var index = -1,
	          includes = arrayIncludes,
	          length = array.length,
	          isCommon = true,
	          result = [],
	          seen = result;

	      if (comparator) {
	        isCommon = false;
	        includes = arrayIncludesWith;
	      }
	      else if (length >= LARGE_ARRAY_SIZE) {
	        var set = iteratee ? null : createSet(array);
	        if (set) {
	          return setToArray(set);
	        }
	        isCommon = false;
	        includes = cacheHas;
	        seen = new SetCache;
	      }
	      else {
	        seen = iteratee ? [] : result;
	      }
	      outer:
	      while (++index < length) {
	        var value = array[index],
	            computed = iteratee ? iteratee(value) : value;

	        value = (comparator || value !== 0) ? value : 0;
	        if (isCommon && computed === computed) {
	          var seenIndex = seen.length;
	          while (seenIndex--) {
	            if (seen[seenIndex] === computed) {
	              continue outer;
	            }
	          }
	          if (iteratee) {
	            seen.push(computed);
	          }
	          result.push(value);
	        }
	        else if (!includes(seen, computed, comparator)) {
	          if (seen !== result) {
	            seen.push(computed);
	          }
	          result.push(value);
	        }
	      }
	      return result;
	    }

	    /**
	     * The base implementation of `_.unset`.
	     *
	     * @private
	     * @param {Object} object The object to modify.
	     * @param {Array|string} path The property path to unset.
	     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
	     */
	    function baseUnset(object, path) {
	      path = castPath(path, object);
	      object = parent(object, path);
	      return object == null || delete object[toKey(last(path))];
	    }

	    /**
	     * The base implementation of `_.update`.
	     *
	     * @private
	     * @param {Object} object The object to modify.
	     * @param {Array|string} path The path of the property to update.
	     * @param {Function} updater The function to produce the updated value.
	     * @param {Function} [customizer] The function to customize path creation.
	     * @returns {Object} Returns `object`.
	     */
	    function baseUpdate(object, path, updater, customizer) {
	      return baseSet(object, path, updater(baseGet(object, path)), customizer);
	    }

	    /**
	     * The base implementation of methods like `_.dropWhile` and `_.takeWhile`
	     * without support for iteratee shorthands.
	     *
	     * @private
	     * @param {Array} array The array to query.
	     * @param {Function} predicate The function invoked per iteration.
	     * @param {boolean} [isDrop] Specify dropping elements instead of taking them.
	     * @param {boolean} [fromRight] Specify iterating from right to left.
	     * @returns {Array} Returns the slice of `array`.
	     */
	    function baseWhile(array, predicate, isDrop, fromRight) {
	      var length = array.length,
	          index = fromRight ? length : -1;

	      while ((fromRight ? index-- : ++index < length) &&
	        predicate(array[index], index, array)) {}

	      return isDrop
	        ? baseSlice(array, (fromRight ? 0 : index), (fromRight ? index + 1 : length))
	        : baseSlice(array, (fromRight ? index + 1 : 0), (fromRight ? length : index));
	    }

	    /**
	     * The base implementation of `wrapperValue` which returns the result of
	     * performing a sequence of actions on the unwrapped `value`, where each
	     * successive action is supplied the return value of the previous.
	     *
	     * @private
	     * @param {*} value The unwrapped value.
	     * @param {Array} actions Actions to perform to resolve the unwrapped value.
	     * @returns {*} Returns the resolved value.
	     */
	    function baseWrapperValue(value, actions) {
	      var result = value;
	      if (result instanceof LazyWrapper) {
	        result = result.value();
	      }
	      return arrayReduce(actions, function(result, action) {
	        return action.func.apply(action.thisArg, arrayPush([result], action.args));
	      }, result);
	    }

	    /**
	     * The base implementation of methods like `_.xor`, without support for
	     * iteratee shorthands, that accepts an array of arrays to inspect.
	     *
	     * @private
	     * @param {Array} arrays The arrays to inspect.
	     * @param {Function} [iteratee] The iteratee invoked per element.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new array of values.
	     */
	    function baseXor(arrays, iteratee, comparator) {
	      var length = arrays.length;
	      if (length < 2) {
	        return length ? baseUniq(arrays[0]) : [];
	      }
	      var index = -1,
	          result = Array(length);

	      while (++index < length) {
	        var array = arrays[index],
	            othIndex = -1;

	        while (++othIndex < length) {
	          if (othIndex != index) {
	            result[index] = baseDifference(result[index] || array, arrays[othIndex], iteratee, comparator);
	          }
	        }
	      }
	      return baseUniq(baseFlatten(result, 1), iteratee, comparator);
	    }

	    /**
	     * This base implementation of `_.zipObject` which assigns values using `assignFunc`.
	     *
	     * @private
	     * @param {Array} props The property identifiers.
	     * @param {Array} values The property values.
	     * @param {Function} assignFunc The function to assign values.
	     * @returns {Object} Returns the new object.
	     */
	    function baseZipObject(props, values, assignFunc) {
	      var index = -1,
	          length = props.length,
	          valsLength = values.length,
	          result = {};

	      while (++index < length) {
	        var value = index < valsLength ? values[index] : undefined$1;
	        assignFunc(result, props[index], value);
	      }
	      return result;
	    }

	    /**
	     * Casts `value` to an empty array if it's not an array like object.
	     *
	     * @private
	     * @param {*} value The value to inspect.
	     * @returns {Array|Object} Returns the cast array-like object.
	     */
	    function castArrayLikeObject(value) {
	      return isArrayLikeObject(value) ? value : [];
	    }

	    /**
	     * Casts `value` to `identity` if it's not a function.
	     *
	     * @private
	     * @param {*} value The value to inspect.
	     * @returns {Function} Returns cast function.
	     */
	    function castFunction(value) {
	      return typeof value == 'function' ? value : identity;
	    }

	    /**
	     * Casts `value` to a path array if it's not one.
	     *
	     * @private
	     * @param {*} value The value to inspect.
	     * @param {Object} [object] The object to query keys on.
	     * @returns {Array} Returns the cast property path array.
	     */
	    function castPath(value, object) {
	      if (isArray(value)) {
	        return value;
	      }
	      return isKey(value, object) ? [value] : stringToPath(toString(value));
	    }

	    /**
	     * A `baseRest` alias which can be replaced with `identity` by module
	     * replacement plugins.
	     *
	     * @private
	     * @type {Function}
	     * @param {Function} func The function to apply a rest parameter to.
	     * @returns {Function} Returns the new function.
	     */
	    var castRest = baseRest;

	    /**
	     * Casts `array` to a slice if it's needed.
	     *
	     * @private
	     * @param {Array} array The array to inspect.
	     * @param {number} start The start position.
	     * @param {number} [end=array.length] The end position.
	     * @returns {Array} Returns the cast slice.
	     */
	    function castSlice(array, start, end) {
	      var length = array.length;
	      end = end === undefined$1 ? length : end;
	      return (!start && end >= length) ? array : baseSlice(array, start, end);
	    }

	    /**
	     * A simple wrapper around the global [`clearTimeout`](https://mdn.io/clearTimeout).
	     *
	     * @private
	     * @param {number|Object} id The timer id or timeout object of the timer to clear.
	     */
	    var clearTimeout = ctxClearTimeout || function(id) {
	      return root.clearTimeout(id);
	    };

	    /**
	     * Creates a clone of  `buffer`.
	     *
	     * @private
	     * @param {Buffer} buffer The buffer to clone.
	     * @param {boolean} [isDeep] Specify a deep clone.
	     * @returns {Buffer} Returns the cloned buffer.
	     */
	    function cloneBuffer(buffer, isDeep) {
	      if (isDeep) {
	        return buffer.slice();
	      }
	      var length = buffer.length,
	          result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

	      buffer.copy(result);
	      return result;
	    }

	    /**
	     * Creates a clone of `arrayBuffer`.
	     *
	     * @private
	     * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
	     * @returns {ArrayBuffer} Returns the cloned array buffer.
	     */
	    function cloneArrayBuffer(arrayBuffer) {
	      var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
	      new Uint8Array(result).set(new Uint8Array(arrayBuffer));
	      return result;
	    }

	    /**
	     * Creates a clone of `dataView`.
	     *
	     * @private
	     * @param {Object} dataView The data view to clone.
	     * @param {boolean} [isDeep] Specify a deep clone.
	     * @returns {Object} Returns the cloned data view.
	     */
	    function cloneDataView(dataView, isDeep) {
	      var buffer = isDeep ? cloneArrayBuffer(dataView.buffer) : dataView.buffer;
	      return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
	    }

	    /**
	     * Creates a clone of `regexp`.
	     *
	     * @private
	     * @param {Object} regexp The regexp to clone.
	     * @returns {Object} Returns the cloned regexp.
	     */
	    function cloneRegExp(regexp) {
	      var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
	      result.lastIndex = regexp.lastIndex;
	      return result;
	    }

	    /**
	     * Creates a clone of the `symbol` object.
	     *
	     * @private
	     * @param {Object} symbol The symbol object to clone.
	     * @returns {Object} Returns the cloned symbol object.
	     */
	    function cloneSymbol(symbol) {
	      return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
	    }

	    /**
	     * Creates a clone of `typedArray`.
	     *
	     * @private
	     * @param {Object} typedArray The typed array to clone.
	     * @param {boolean} [isDeep] Specify a deep clone.
	     * @returns {Object} Returns the cloned typed array.
	     */
	    function cloneTypedArray(typedArray, isDeep) {
	      var buffer = isDeep ? cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
	      return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
	    }

	    /**
	     * Compares values to sort them in ascending order.
	     *
	     * @private
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {number} Returns the sort order indicator for `value`.
	     */
	    function compareAscending(value, other) {
	      if (value !== other) {
	        var valIsDefined = value !== undefined$1,
	            valIsNull = value === null,
	            valIsReflexive = value === value,
	            valIsSymbol = isSymbol(value);

	        var othIsDefined = other !== undefined$1,
	            othIsNull = other === null,
	            othIsReflexive = other === other,
	            othIsSymbol = isSymbol(other);

	        if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
	            (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
	            (valIsNull && othIsDefined && othIsReflexive) ||
	            (!valIsDefined && othIsReflexive) ||
	            !valIsReflexive) {
	          return 1;
	        }
	        if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
	            (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
	            (othIsNull && valIsDefined && valIsReflexive) ||
	            (!othIsDefined && valIsReflexive) ||
	            !othIsReflexive) {
	          return -1;
	        }
	      }
	      return 0;
	    }

	    /**
	     * Used by `_.orderBy` to compare multiple properties of a value to another
	     * and stable sort them.
	     *
	     * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
	     * specify an order of "desc" for descending or "asc" for ascending sort order
	     * of corresponding values.
	     *
	     * @private
	     * @param {Object} object The object to compare.
	     * @param {Object} other The other object to compare.
	     * @param {boolean[]|string[]} orders The order to sort by for each property.
	     * @returns {number} Returns the sort order indicator for `object`.
	     */
	    function compareMultiple(object, other, orders) {
	      var index = -1,
	          objCriteria = object.criteria,
	          othCriteria = other.criteria,
	          length = objCriteria.length,
	          ordersLength = orders.length;

	      while (++index < length) {
	        var result = compareAscending(objCriteria[index], othCriteria[index]);
	        if (result) {
	          if (index >= ordersLength) {
	            return result;
	          }
	          var order = orders[index];
	          return result * (order == 'desc' ? -1 : 1);
	        }
	      }
	      // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
	      // that causes it, under certain circumstances, to provide the same value for
	      // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
	      // for more details.
	      //
	      // This also ensures a stable sort in V8 and other engines.
	      // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
	      return object.index - other.index;
	    }

	    /**
	     * Creates an array that is the composition of partially applied arguments,
	     * placeholders, and provided arguments into a single array of arguments.
	     *
	     * @private
	     * @param {Array} args The provided arguments.
	     * @param {Array} partials The arguments to prepend to those provided.
	     * @param {Array} holders The `partials` placeholder indexes.
	     * @params {boolean} [isCurried] Specify composing for a curried function.
	     * @returns {Array} Returns the new array of composed arguments.
	     */
	    function composeArgs(args, partials, holders, isCurried) {
	      var argsIndex = -1,
	          argsLength = args.length,
	          holdersLength = holders.length,
	          leftIndex = -1,
	          leftLength = partials.length,
	          rangeLength = nativeMax(argsLength - holdersLength, 0),
	          result = Array(leftLength + rangeLength),
	          isUncurried = !isCurried;

	      while (++leftIndex < leftLength) {
	        result[leftIndex] = partials[leftIndex];
	      }
	      while (++argsIndex < holdersLength) {
	        if (isUncurried || argsIndex < argsLength) {
	          result[holders[argsIndex]] = args[argsIndex];
	        }
	      }
	      while (rangeLength--) {
	        result[leftIndex++] = args[argsIndex++];
	      }
	      return result;
	    }

	    /**
	     * This function is like `composeArgs` except that the arguments composition
	     * is tailored for `_.partialRight`.
	     *
	     * @private
	     * @param {Array} args The provided arguments.
	     * @param {Array} partials The arguments to append to those provided.
	     * @param {Array} holders The `partials` placeholder indexes.
	     * @params {boolean} [isCurried] Specify composing for a curried function.
	     * @returns {Array} Returns the new array of composed arguments.
	     */
	    function composeArgsRight(args, partials, holders, isCurried) {
	      var argsIndex = -1,
	          argsLength = args.length,
	          holdersIndex = -1,
	          holdersLength = holders.length,
	          rightIndex = -1,
	          rightLength = partials.length,
	          rangeLength = nativeMax(argsLength - holdersLength, 0),
	          result = Array(rangeLength + rightLength),
	          isUncurried = !isCurried;

	      while (++argsIndex < rangeLength) {
	        result[argsIndex] = args[argsIndex];
	      }
	      var offset = argsIndex;
	      while (++rightIndex < rightLength) {
	        result[offset + rightIndex] = partials[rightIndex];
	      }
	      while (++holdersIndex < holdersLength) {
	        if (isUncurried || argsIndex < argsLength) {
	          result[offset + holders[holdersIndex]] = args[argsIndex++];
	        }
	      }
	      return result;
	    }

	    /**
	     * Copies the values of `source` to `array`.
	     *
	     * @private
	     * @param {Array} source The array to copy values from.
	     * @param {Array} [array=[]] The array to copy values to.
	     * @returns {Array} Returns `array`.
	     */
	    function copyArray(source, array) {
	      var index = -1,
	          length = source.length;

	      array || (array = Array(length));
	      while (++index < length) {
	        array[index] = source[index];
	      }
	      return array;
	    }

	    /**
	     * Copies properties of `source` to `object`.
	     *
	     * @private
	     * @param {Object} source The object to copy properties from.
	     * @param {Array} props The property identifiers to copy.
	     * @param {Object} [object={}] The object to copy properties to.
	     * @param {Function} [customizer] The function to customize copied values.
	     * @returns {Object} Returns `object`.
	     */
	    function copyObject(source, props, object, customizer) {
	      var isNew = !object;
	      object || (object = {});

	      var index = -1,
	          length = props.length;

	      while (++index < length) {
	        var key = props[index];

	        var newValue = customizer
	          ? customizer(object[key], source[key], key, object, source)
	          : undefined$1;

	        if (newValue === undefined$1) {
	          newValue = source[key];
	        }
	        if (isNew) {
	          baseAssignValue(object, key, newValue);
	        } else {
	          assignValue(object, key, newValue);
	        }
	      }
	      return object;
	    }

	    /**
	     * Copies own symbols of `source` to `object`.
	     *
	     * @private
	     * @param {Object} source The object to copy symbols from.
	     * @param {Object} [object={}] The object to copy symbols to.
	     * @returns {Object} Returns `object`.
	     */
	    function copySymbols(source, object) {
	      return copyObject(source, getSymbols(source), object);
	    }

	    /**
	     * Copies own and inherited symbols of `source` to `object`.
	     *
	     * @private
	     * @param {Object} source The object to copy symbols from.
	     * @param {Object} [object={}] The object to copy symbols to.
	     * @returns {Object} Returns `object`.
	     */
	    function copySymbolsIn(source, object) {
	      return copyObject(source, getSymbolsIn(source), object);
	    }

	    /**
	     * Creates a function like `_.groupBy`.
	     *
	     * @private
	     * @param {Function} setter The function to set accumulator values.
	     * @param {Function} [initializer] The accumulator object initializer.
	     * @returns {Function} Returns the new aggregator function.
	     */
	    function createAggregator(setter, initializer) {
	      return function(collection, iteratee) {
	        var func = isArray(collection) ? arrayAggregator : baseAggregator,
	            accumulator = initializer ? initializer() : {};

	        return func(collection, setter, getIteratee(iteratee, 2), accumulator);
	      };
	    }

	    /**
	     * Creates a function like `_.assign`.
	     *
	     * @private
	     * @param {Function} assigner The function to assign values.
	     * @returns {Function} Returns the new assigner function.
	     */
	    function createAssigner(assigner) {
	      return baseRest(function(object, sources) {
	        var index = -1,
	            length = sources.length,
	            customizer = length > 1 ? sources[length - 1] : undefined$1,
	            guard = length > 2 ? sources[2] : undefined$1;

	        customizer = (assigner.length > 3 && typeof customizer == 'function')
	          ? (length--, customizer)
	          : undefined$1;

	        if (guard && isIterateeCall(sources[0], sources[1], guard)) {
	          customizer = length < 3 ? undefined$1 : customizer;
	          length = 1;
	        }
	        object = Object(object);
	        while (++index < length) {
	          var source = sources[index];
	          if (source) {
	            assigner(object, source, index, customizer);
	          }
	        }
	        return object;
	      });
	    }

	    /**
	     * Creates a `baseEach` or `baseEachRight` function.
	     *
	     * @private
	     * @param {Function} eachFunc The function to iterate over a collection.
	     * @param {boolean} [fromRight] Specify iterating from right to left.
	     * @returns {Function} Returns the new base function.
	     */
	    function createBaseEach(eachFunc, fromRight) {
	      return function(collection, iteratee) {
	        if (collection == null) {
	          return collection;
	        }
	        if (!isArrayLike(collection)) {
	          return eachFunc(collection, iteratee);
	        }
	        var length = collection.length,
	            index = fromRight ? length : -1,
	            iterable = Object(collection);

	        while ((fromRight ? index-- : ++index < length)) {
	          if (iteratee(iterable[index], index, iterable) === false) {
	            break;
	          }
	        }
	        return collection;
	      };
	    }

	    /**
	     * Creates a base function for methods like `_.forIn` and `_.forOwn`.
	     *
	     * @private
	     * @param {boolean} [fromRight] Specify iterating from right to left.
	     * @returns {Function} Returns the new base function.
	     */
	    function createBaseFor(fromRight) {
	      return function(object, iteratee, keysFunc) {
	        var index = -1,
	            iterable = Object(object),
	            props = keysFunc(object),
	            length = props.length;

	        while (length--) {
	          var key = props[fromRight ? length : ++index];
	          if (iteratee(iterable[key], key, iterable) === false) {
	            break;
	          }
	        }
	        return object;
	      };
	    }

	    /**
	     * Creates a function that wraps `func` to invoke it with the optional `this`
	     * binding of `thisArg`.
	     *
	     * @private
	     * @param {Function} func The function to wrap.
	     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
	     * @param {*} [thisArg] The `this` binding of `func`.
	     * @returns {Function} Returns the new wrapped function.
	     */
	    function createBind(func, bitmask, thisArg) {
	      var isBind = bitmask & WRAP_BIND_FLAG,
	          Ctor = createCtor(func);

	      function wrapper() {
	        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
	        return fn.apply(isBind ? thisArg : this, arguments);
	      }
	      return wrapper;
	    }

	    /**
	     * Creates a function like `_.lowerFirst`.
	     *
	     * @private
	     * @param {string} methodName The name of the `String` case method to use.
	     * @returns {Function} Returns the new case function.
	     */
	    function createCaseFirst(methodName) {
	      return function(string) {
	        string = toString(string);

	        var strSymbols = hasUnicode(string)
	          ? stringToArray(string)
	          : undefined$1;

	        var chr = strSymbols
	          ? strSymbols[0]
	          : string.charAt(0);

	        var trailing = strSymbols
	          ? castSlice(strSymbols, 1).join('')
	          : string.slice(1);

	        return chr[methodName]() + trailing;
	      };
	    }

	    /**
	     * Creates a function like `_.camelCase`.
	     *
	     * @private
	     * @param {Function} callback The function to combine each word.
	     * @returns {Function} Returns the new compounder function.
	     */
	    function createCompounder(callback) {
	      return function(string) {
	        return arrayReduce(words(deburr(string).replace(reApos, '')), callback, '');
	      };
	    }

	    /**
	     * Creates a function that produces an instance of `Ctor` regardless of
	     * whether it was invoked as part of a `new` expression or by `call` or `apply`.
	     *
	     * @private
	     * @param {Function} Ctor The constructor to wrap.
	     * @returns {Function} Returns the new wrapped function.
	     */
	    function createCtor(Ctor) {
	      return function() {
	        // Use a `switch` statement to work with class constructors. See
	        // http://ecma-international.org/ecma-262/7.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
	        // for more details.
	        var args = arguments;
	        switch (args.length) {
	          case 0: return new Ctor;
	          case 1: return new Ctor(args[0]);
	          case 2: return new Ctor(args[0], args[1]);
	          case 3: return new Ctor(args[0], args[1], args[2]);
	          case 4: return new Ctor(args[0], args[1], args[2], args[3]);
	          case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
	          case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
	          case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
	        }
	        var thisBinding = baseCreate(Ctor.prototype),
	            result = Ctor.apply(thisBinding, args);

	        // Mimic the constructor's `return` behavior.
	        // See https://es5.github.io/#x13.2.2 for more details.
	        return isObject(result) ? result : thisBinding;
	      };
	    }

	    /**
	     * Creates a function that wraps `func` to enable currying.
	     *
	     * @private
	     * @param {Function} func The function to wrap.
	     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
	     * @param {number} arity The arity of `func`.
	     * @returns {Function} Returns the new wrapped function.
	     */
	    function createCurry(func, bitmask, arity) {
	      var Ctor = createCtor(func);

	      function wrapper() {
	        var length = arguments.length,
	            args = Array(length),
	            index = length,
	            placeholder = getHolder(wrapper);

	        while (index--) {
	          args[index] = arguments[index];
	        }
	        var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
	          ? []
	          : replaceHolders(args, placeholder);

	        length -= holders.length;
	        if (length < arity) {
	          return createRecurry(
	            func, bitmask, createHybrid, wrapper.placeholder, undefined$1,
	            args, holders, undefined$1, undefined$1, arity - length);
	        }
	        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
	        return apply(fn, this, args);
	      }
	      return wrapper;
	    }

	    /**
	     * Creates a `_.find` or `_.findLast` function.
	     *
	     * @private
	     * @param {Function} findIndexFunc The function to find the collection index.
	     * @returns {Function} Returns the new find function.
	     */
	    function createFind(findIndexFunc) {
	      return function(collection, predicate, fromIndex) {
	        var iterable = Object(collection);
	        if (!isArrayLike(collection)) {
	          var iteratee = getIteratee(predicate, 3);
	          collection = keys(collection);
	          predicate = function(key) { return iteratee(iterable[key], key, iterable); };
	        }
	        var index = findIndexFunc(collection, predicate, fromIndex);
	        return index > -1 ? iterable[iteratee ? collection[index] : index] : undefined$1;
	      };
	    }

	    /**
	     * Creates a `_.flow` or `_.flowRight` function.
	     *
	     * @private
	     * @param {boolean} [fromRight] Specify iterating from right to left.
	     * @returns {Function} Returns the new flow function.
	     */
	    function createFlow(fromRight) {
	      return flatRest(function(funcs) {
	        var length = funcs.length,
	            index = length,
	            prereq = LodashWrapper.prototype.thru;

	        if (fromRight) {
	          funcs.reverse();
	        }
	        while (index--) {
	          var func = funcs[index];
	          if (typeof func != 'function') {
	            throw new TypeError(FUNC_ERROR_TEXT);
	          }
	          if (prereq && !wrapper && getFuncName(func) == 'wrapper') {
	            var wrapper = new LodashWrapper([], true);
	          }
	        }
	        index = wrapper ? index : length;
	        while (++index < length) {
	          func = funcs[index];

	          var funcName = getFuncName(func),
	              data = funcName == 'wrapper' ? getData(func) : undefined$1;

	          if (data && isLaziable(data[0]) &&
	                data[1] == (WRAP_ARY_FLAG | WRAP_CURRY_FLAG | WRAP_PARTIAL_FLAG | WRAP_REARG_FLAG) &&
	                !data[4].length && data[9] == 1
	              ) {
	            wrapper = wrapper[getFuncName(data[0])].apply(wrapper, data[3]);
	          } else {
	            wrapper = (func.length == 1 && isLaziable(func))
	              ? wrapper[funcName]()
	              : wrapper.thru(func);
	          }
	        }
	        return function() {
	          var args = arguments,
	              value = args[0];

	          if (wrapper && args.length == 1 && isArray(value)) {
	            return wrapper.plant(value).value();
	          }
	          var index = 0,
	              result = length ? funcs[index].apply(this, args) : value;

	          while (++index < length) {
	            result = funcs[index].call(this, result);
	          }
	          return result;
	        };
	      });
	    }

	    /**
	     * Creates a function that wraps `func` to invoke it with optional `this`
	     * binding of `thisArg`, partial application, and currying.
	     *
	     * @private
	     * @param {Function|string} func The function or method name to wrap.
	     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
	     * @param {*} [thisArg] The `this` binding of `func`.
	     * @param {Array} [partials] The arguments to prepend to those provided to
	     *  the new function.
	     * @param {Array} [holders] The `partials` placeholder indexes.
	     * @param {Array} [partialsRight] The arguments to append to those provided
	     *  to the new function.
	     * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
	     * @param {Array} [argPos] The argument positions of the new function.
	     * @param {number} [ary] The arity cap of `func`.
	     * @param {number} [arity] The arity of `func`.
	     * @returns {Function} Returns the new wrapped function.
	     */
	    function createHybrid(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
	      var isAry = bitmask & WRAP_ARY_FLAG,
	          isBind = bitmask & WRAP_BIND_FLAG,
	          isBindKey = bitmask & WRAP_BIND_KEY_FLAG,
	          isCurried = bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG),
	          isFlip = bitmask & WRAP_FLIP_FLAG,
	          Ctor = isBindKey ? undefined$1 : createCtor(func);

	      function wrapper() {
	        var length = arguments.length,
	            args = Array(length),
	            index = length;

	        while (index--) {
	          args[index] = arguments[index];
	        }
	        if (isCurried) {
	          var placeholder = getHolder(wrapper),
	              holdersCount = countHolders(args, placeholder);
	        }
	        if (partials) {
	          args = composeArgs(args, partials, holders, isCurried);
	        }
	        if (partialsRight) {
	          args = composeArgsRight(args, partialsRight, holdersRight, isCurried);
	        }
	        length -= holdersCount;
	        if (isCurried && length < arity) {
	          var newHolders = replaceHolders(args, placeholder);
	          return createRecurry(
	            func, bitmask, createHybrid, wrapper.placeholder, thisArg,
	            args, newHolders, argPos, ary, arity - length
	          );
	        }
	        var thisBinding = isBind ? thisArg : this,
	            fn = isBindKey ? thisBinding[func] : func;

	        length = args.length;
	        if (argPos) {
	          args = reorder(args, argPos);
	        } else if (isFlip && length > 1) {
	          args.reverse();
	        }
	        if (isAry && ary < length) {
	          args.length = ary;
	        }
	        if (this && this !== root && this instanceof wrapper) {
	          fn = Ctor || createCtor(fn);
	        }
	        return fn.apply(thisBinding, args);
	      }
	      return wrapper;
	    }

	    /**
	     * Creates a function like `_.invertBy`.
	     *
	     * @private
	     * @param {Function} setter The function to set accumulator values.
	     * @param {Function} toIteratee The function to resolve iteratees.
	     * @returns {Function} Returns the new inverter function.
	     */
	    function createInverter(setter, toIteratee) {
	      return function(object, iteratee) {
	        return baseInverter(object, setter, toIteratee(iteratee), {});
	      };
	    }

	    /**
	     * Creates a function that performs a mathematical operation on two values.
	     *
	     * @private
	     * @param {Function} operator The function to perform the operation.
	     * @param {number} [defaultValue] The value used for `undefined` arguments.
	     * @returns {Function} Returns the new mathematical operation function.
	     */
	    function createMathOperation(operator, defaultValue) {
	      return function(value, other) {
	        var result;
	        if (value === undefined$1 && other === undefined$1) {
	          return defaultValue;
	        }
	        if (value !== undefined$1) {
	          result = value;
	        }
	        if (other !== undefined$1) {
	          if (result === undefined$1) {
	            return other;
	          }
	          if (typeof value == 'string' || typeof other == 'string') {
	            value = baseToString(value);
	            other = baseToString(other);
	          } else {
	            value = baseToNumber(value);
	            other = baseToNumber(other);
	          }
	          result = operator(value, other);
	        }
	        return result;
	      };
	    }

	    /**
	     * Creates a function like `_.over`.
	     *
	     * @private
	     * @param {Function} arrayFunc The function to iterate over iteratees.
	     * @returns {Function} Returns the new over function.
	     */
	    function createOver(arrayFunc) {
	      return flatRest(function(iteratees) {
	        iteratees = arrayMap(iteratees, baseUnary(getIteratee()));
	        return baseRest(function(args) {
	          var thisArg = this;
	          return arrayFunc(iteratees, function(iteratee) {
	            return apply(iteratee, thisArg, args);
	          });
	        });
	      });
	    }

	    /**
	     * Creates the padding for `string` based on `length`. The `chars` string
	     * is truncated if the number of characters exceeds `length`.
	     *
	     * @private
	     * @param {number} length The padding length.
	     * @param {string} [chars=' '] The string used as padding.
	     * @returns {string} Returns the padding for `string`.
	     */
	    function createPadding(length, chars) {
	      chars = chars === undefined$1 ? ' ' : baseToString(chars);

	      var charsLength = chars.length;
	      if (charsLength < 2) {
	        return charsLength ? baseRepeat(chars, length) : chars;
	      }
	      var result = baseRepeat(chars, nativeCeil(length / stringSize(chars)));
	      return hasUnicode(chars)
	        ? castSlice(stringToArray(result), 0, length).join('')
	        : result.slice(0, length);
	    }

	    /**
	     * Creates a function that wraps `func` to invoke it with the `this` binding
	     * of `thisArg` and `partials` prepended to the arguments it receives.
	     *
	     * @private
	     * @param {Function} func The function to wrap.
	     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
	     * @param {*} thisArg The `this` binding of `func`.
	     * @param {Array} partials The arguments to prepend to those provided to
	     *  the new function.
	     * @returns {Function} Returns the new wrapped function.
	     */
	    function createPartial(func, bitmask, thisArg, partials) {
	      var isBind = bitmask & WRAP_BIND_FLAG,
	          Ctor = createCtor(func);

	      function wrapper() {
	        var argsIndex = -1,
	            argsLength = arguments.length,
	            leftIndex = -1,
	            leftLength = partials.length,
	            args = Array(leftLength + argsLength),
	            fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;

	        while (++leftIndex < leftLength) {
	          args[leftIndex] = partials[leftIndex];
	        }
	        while (argsLength--) {
	          args[leftIndex++] = arguments[++argsIndex];
	        }
	        return apply(fn, isBind ? thisArg : this, args);
	      }
	      return wrapper;
	    }

	    /**
	     * Creates a `_.range` or `_.rangeRight` function.
	     *
	     * @private
	     * @param {boolean} [fromRight] Specify iterating from right to left.
	     * @returns {Function} Returns the new range function.
	     */
	    function createRange(fromRight) {
	      return function(start, end, step) {
	        if (step && typeof step != 'number' && isIterateeCall(start, end, step)) {
	          end = step = undefined$1;
	        }
	        // Ensure the sign of `-0` is preserved.
	        start = toFinite(start);
	        if (end === undefined$1) {
	          end = start;
	          start = 0;
	        } else {
	          end = toFinite(end);
	        }
	        step = step === undefined$1 ? (start < end ? 1 : -1) : toFinite(step);
	        return baseRange(start, end, step, fromRight);
	      };
	    }

	    /**
	     * Creates a function that performs a relational operation on two values.
	     *
	     * @private
	     * @param {Function} operator The function to perform the operation.
	     * @returns {Function} Returns the new relational operation function.
	     */
	    function createRelationalOperation(operator) {
	      return function(value, other) {
	        if (!(typeof value == 'string' && typeof other == 'string')) {
	          value = toNumber(value);
	          other = toNumber(other);
	        }
	        return operator(value, other);
	      };
	    }

	    /**
	     * Creates a function that wraps `func` to continue currying.
	     *
	     * @private
	     * @param {Function} func The function to wrap.
	     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
	     * @param {Function} wrapFunc The function to create the `func` wrapper.
	     * @param {*} placeholder The placeholder value.
	     * @param {*} [thisArg] The `this` binding of `func`.
	     * @param {Array} [partials] The arguments to prepend to those provided to
	     *  the new function.
	     * @param {Array} [holders] The `partials` placeholder indexes.
	     * @param {Array} [argPos] The argument positions of the new function.
	     * @param {number} [ary] The arity cap of `func`.
	     * @param {number} [arity] The arity of `func`.
	     * @returns {Function} Returns the new wrapped function.
	     */
	    function createRecurry(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
	      var isCurry = bitmask & WRAP_CURRY_FLAG,
	          newHolders = isCurry ? holders : undefined$1,
	          newHoldersRight = isCurry ? undefined$1 : holders,
	          newPartials = isCurry ? partials : undefined$1,
	          newPartialsRight = isCurry ? undefined$1 : partials;

	      bitmask |= (isCurry ? WRAP_PARTIAL_FLAG : WRAP_PARTIAL_RIGHT_FLAG);
	      bitmask &= ~(isCurry ? WRAP_PARTIAL_RIGHT_FLAG : WRAP_PARTIAL_FLAG);

	      if (!(bitmask & WRAP_CURRY_BOUND_FLAG)) {
	        bitmask &= ~(WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG);
	      }
	      var newData = [
	        func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
	        newHoldersRight, argPos, ary, arity
	      ];

	      var result = wrapFunc.apply(undefined$1, newData);
	      if (isLaziable(func)) {
	        setData(result, newData);
	      }
	      result.placeholder = placeholder;
	      return setWrapToString(result, func, bitmask);
	    }

	    /**
	     * Creates a function like `_.round`.
	     *
	     * @private
	     * @param {string} methodName The name of the `Math` method to use when rounding.
	     * @returns {Function} Returns the new round function.
	     */
	    function createRound(methodName) {
	      var func = Math[methodName];
	      return function(number, precision) {
	        number = toNumber(number);
	        precision = precision == null ? 0 : nativeMin(toInteger(precision), 292);
	        if (precision && nativeIsFinite(number)) {
	          // Shift with exponential notation to avoid floating-point issues.
	          // See [MDN](https://mdn.io/round#Examples) for more details.
	          var pair = (toString(number) + 'e').split('e'),
	              value = func(pair[0] + 'e' + (+pair[1] + precision));

	          pair = (toString(value) + 'e').split('e');
	          return +(pair[0] + 'e' + (+pair[1] - precision));
	        }
	        return func(number);
	      };
	    }

	    /**
	     * Creates a set object of `values`.
	     *
	     * @private
	     * @param {Array} values The values to add to the set.
	     * @returns {Object} Returns the new set.
	     */
	    var createSet = !(Set && (1 / setToArray(new Set([,-0]))[1]) == INFINITY) ? noop : function(values) {
	      return new Set(values);
	    };

	    /**
	     * Creates a `_.toPairs` or `_.toPairsIn` function.
	     *
	     * @private
	     * @param {Function} keysFunc The function to get the keys of a given object.
	     * @returns {Function} Returns the new pairs function.
	     */
	    function createToPairs(keysFunc) {
	      return function(object) {
	        var tag = getTag(object);
	        if (tag == mapTag) {
	          return mapToArray(object);
	        }
	        if (tag == setTag) {
	          return setToPairs(object);
	        }
	        return baseToPairs(object, keysFunc(object));
	      };
	    }

	    /**
	     * Creates a function that either curries or invokes `func` with optional
	     * `this` binding and partially applied arguments.
	     *
	     * @private
	     * @param {Function|string} func The function or method name to wrap.
	     * @param {number} bitmask The bitmask flags.
	     *    1 - `_.bind`
	     *    2 - `_.bindKey`
	     *    4 - `_.curry` or `_.curryRight` of a bound function
	     *    8 - `_.curry`
	     *   16 - `_.curryRight`
	     *   32 - `_.partial`
	     *   64 - `_.partialRight`
	     *  128 - `_.rearg`
	     *  256 - `_.ary`
	     *  512 - `_.flip`
	     * @param {*} [thisArg] The `this` binding of `func`.
	     * @param {Array} [partials] The arguments to be partially applied.
	     * @param {Array} [holders] The `partials` placeholder indexes.
	     * @param {Array} [argPos] The argument positions of the new function.
	     * @param {number} [ary] The arity cap of `func`.
	     * @param {number} [arity] The arity of `func`.
	     * @returns {Function} Returns the new wrapped function.
	     */
	    function createWrap(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
	      var isBindKey = bitmask & WRAP_BIND_KEY_FLAG;
	      if (!isBindKey && typeof func != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      var length = partials ? partials.length : 0;
	      if (!length) {
	        bitmask &= ~(WRAP_PARTIAL_FLAG | WRAP_PARTIAL_RIGHT_FLAG);
	        partials = holders = undefined$1;
	      }
	      ary = ary === undefined$1 ? ary : nativeMax(toInteger(ary), 0);
	      arity = arity === undefined$1 ? arity : toInteger(arity);
	      length -= holders ? holders.length : 0;

	      if (bitmask & WRAP_PARTIAL_RIGHT_FLAG) {
	        var partialsRight = partials,
	            holdersRight = holders;

	        partials = holders = undefined$1;
	      }
	      var data = isBindKey ? undefined$1 : getData(func);

	      var newData = [
	        func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
	        argPos, ary, arity
	      ];

	      if (data) {
	        mergeData(newData, data);
	      }
	      func = newData[0];
	      bitmask = newData[1];
	      thisArg = newData[2];
	      partials = newData[3];
	      holders = newData[4];
	      arity = newData[9] = newData[9] === undefined$1
	        ? (isBindKey ? 0 : func.length)
	        : nativeMax(newData[9] - length, 0);

	      if (!arity && bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG)) {
	        bitmask &= ~(WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG);
	      }
	      if (!bitmask || bitmask == WRAP_BIND_FLAG) {
	        var result = createBind(func, bitmask, thisArg);
	      } else if (bitmask == WRAP_CURRY_FLAG || bitmask == WRAP_CURRY_RIGHT_FLAG) {
	        result = createCurry(func, bitmask, arity);
	      } else if ((bitmask == WRAP_PARTIAL_FLAG || bitmask == (WRAP_BIND_FLAG | WRAP_PARTIAL_FLAG)) && !holders.length) {
	        result = createPartial(func, bitmask, thisArg, partials);
	      } else {
	        result = createHybrid.apply(undefined$1, newData);
	      }
	      var setter = data ? baseSetData : setData;
	      return setWrapToString(setter(result, newData), func, bitmask);
	    }

	    /**
	     * Used by `_.defaults` to customize its `_.assignIn` use to assign properties
	     * of source objects to the destination object for all destination properties
	     * that resolve to `undefined`.
	     *
	     * @private
	     * @param {*} objValue The destination value.
	     * @param {*} srcValue The source value.
	     * @param {string} key The key of the property to assign.
	     * @param {Object} object The parent object of `objValue`.
	     * @returns {*} Returns the value to assign.
	     */
	    function customDefaultsAssignIn(objValue, srcValue, key, object) {
	      if (objValue === undefined$1 ||
	          (eq(objValue, objectProto[key]) && !hasOwnProperty.call(object, key))) {
	        return srcValue;
	      }
	      return objValue;
	    }

	    /**
	     * Used by `_.defaultsDeep` to customize its `_.merge` use to merge source
	     * objects into destination objects that are passed thru.
	     *
	     * @private
	     * @param {*} objValue The destination value.
	     * @param {*} srcValue The source value.
	     * @param {string} key The key of the property to merge.
	     * @param {Object} object The parent object of `objValue`.
	     * @param {Object} source The parent object of `srcValue`.
	     * @param {Object} [stack] Tracks traversed source values and their merged
	     *  counterparts.
	     * @returns {*} Returns the value to assign.
	     */
	    function customDefaultsMerge(objValue, srcValue, key, object, source, stack) {
	      if (isObject(objValue) && isObject(srcValue)) {
	        // Recursively merge objects and arrays (susceptible to call stack limits).
	        stack.set(srcValue, objValue);
	        baseMerge(objValue, srcValue, undefined$1, customDefaultsMerge, stack);
	        stack['delete'](srcValue);
	      }
	      return objValue;
	    }

	    /**
	     * Used by `_.omit` to customize its `_.cloneDeep` use to only clone plain
	     * objects.
	     *
	     * @private
	     * @param {*} value The value to inspect.
	     * @param {string} key The key of the property to inspect.
	     * @returns {*} Returns the uncloned value or `undefined` to defer cloning to `_.cloneDeep`.
	     */
	    function customOmitClone(value) {
	      return isPlainObject(value) ? undefined$1 : value;
	    }

	    /**
	     * A specialized version of `baseIsEqualDeep` for arrays with support for
	     * partial deep comparisons.
	     *
	     * @private
	     * @param {Array} array The array to compare.
	     * @param {Array} other The other array to compare.
	     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
	     * @param {Function} customizer The function to customize comparisons.
	     * @param {Function} equalFunc The function to determine equivalents of values.
	     * @param {Object} stack Tracks traversed `array` and `other` objects.
	     * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
	     */
	    function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
	      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
	          arrLength = array.length,
	          othLength = other.length;

	      if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
	        return false;
	      }
	      // Check that cyclic values are equal.
	      var arrStacked = stack.get(array);
	      var othStacked = stack.get(other);
	      if (arrStacked && othStacked) {
	        return arrStacked == other && othStacked == array;
	      }
	      var index = -1,
	          result = true,
	          seen = (bitmask & COMPARE_UNORDERED_FLAG) ? new SetCache : undefined$1;

	      stack.set(array, other);
	      stack.set(other, array);

	      // Ignore non-index properties.
	      while (++index < arrLength) {
	        var arrValue = array[index],
	            othValue = other[index];

	        if (customizer) {
	          var compared = isPartial
	            ? customizer(othValue, arrValue, index, other, array, stack)
	            : customizer(arrValue, othValue, index, array, other, stack);
	        }
	        if (compared !== undefined$1) {
	          if (compared) {
	            continue;
	          }
	          result = false;
	          break;
	        }
	        // Recursively compare arrays (susceptible to call stack limits).
	        if (seen) {
	          if (!arraySome(other, function(othValue, othIndex) {
	                if (!cacheHas(seen, othIndex) &&
	                    (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
	                  return seen.push(othIndex);
	                }
	              })) {
	            result = false;
	            break;
	          }
	        } else if (!(
	              arrValue === othValue ||
	                equalFunc(arrValue, othValue, bitmask, customizer, stack)
	            )) {
	          result = false;
	          break;
	        }
	      }
	      stack['delete'](array);
	      stack['delete'](other);
	      return result;
	    }

	    /**
	     * A specialized version of `baseIsEqualDeep` for comparing objects of
	     * the same `toStringTag`.
	     *
	     * **Note:** This function only supports comparing values with tags of
	     * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
	     *
	     * @private
	     * @param {Object} object The object to compare.
	     * @param {Object} other The other object to compare.
	     * @param {string} tag The `toStringTag` of the objects to compare.
	     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
	     * @param {Function} customizer The function to customize comparisons.
	     * @param {Function} equalFunc The function to determine equivalents of values.
	     * @param {Object} stack Tracks traversed `object` and `other` objects.
	     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
	     */
	    function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
	      switch (tag) {
	        case dataViewTag:
	          if ((object.byteLength != other.byteLength) ||
	              (object.byteOffset != other.byteOffset)) {
	            return false;
	          }
	          object = object.buffer;
	          other = other.buffer;

	        case arrayBufferTag:
	          if ((object.byteLength != other.byteLength) ||
	              !equalFunc(new Uint8Array(object), new Uint8Array(other))) {
	            return false;
	          }
	          return true;

	        case boolTag:
	        case dateTag:
	        case numberTag:
	          // Coerce booleans to `1` or `0` and dates to milliseconds.
	          // Invalid dates are coerced to `NaN`.
	          return eq(+object, +other);

	        case errorTag:
	          return object.name == other.name && object.message == other.message;

	        case regexpTag:
	        case stringTag:
	          // Coerce regexes to strings and treat strings, primitives and objects,
	          // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
	          // for more details.
	          return object == (other + '');

	        case mapTag:
	          var convert = mapToArray;

	        case setTag:
	          var isPartial = bitmask & COMPARE_PARTIAL_FLAG;
	          convert || (convert = setToArray);

	          if (object.size != other.size && !isPartial) {
	            return false;
	          }
	          // Assume cyclic values are equal.
	          var stacked = stack.get(object);
	          if (stacked) {
	            return stacked == other;
	          }
	          bitmask |= COMPARE_UNORDERED_FLAG;

	          // Recursively compare objects (susceptible to call stack limits).
	          stack.set(object, other);
	          var result = equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
	          stack['delete'](object);
	          return result;

	        case symbolTag:
	          if (symbolValueOf) {
	            return symbolValueOf.call(object) == symbolValueOf.call(other);
	          }
	      }
	      return false;
	    }

	    /**
	     * A specialized version of `baseIsEqualDeep` for objects with support for
	     * partial deep comparisons.
	     *
	     * @private
	     * @param {Object} object The object to compare.
	     * @param {Object} other The other object to compare.
	     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
	     * @param {Function} customizer The function to customize comparisons.
	     * @param {Function} equalFunc The function to determine equivalents of values.
	     * @param {Object} stack Tracks traversed `object` and `other` objects.
	     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
	     */
	    function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
	      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
	          objProps = getAllKeys(object),
	          objLength = objProps.length,
	          othProps = getAllKeys(other),
	          othLength = othProps.length;

	      if (objLength != othLength && !isPartial) {
	        return false;
	      }
	      var index = objLength;
	      while (index--) {
	        var key = objProps[index];
	        if (!(isPartial ? key in other : hasOwnProperty.call(other, key))) {
	          return false;
	        }
	      }
	      // Check that cyclic values are equal.
	      var objStacked = stack.get(object);
	      var othStacked = stack.get(other);
	      if (objStacked && othStacked) {
	        return objStacked == other && othStacked == object;
	      }
	      var result = true;
	      stack.set(object, other);
	      stack.set(other, object);

	      var skipCtor = isPartial;
	      while (++index < objLength) {
	        key = objProps[index];
	        var objValue = object[key],
	            othValue = other[key];

	        if (customizer) {
	          var compared = isPartial
	            ? customizer(othValue, objValue, key, other, object, stack)
	            : customizer(objValue, othValue, key, object, other, stack);
	        }
	        // Recursively compare objects (susceptible to call stack limits).
	        if (!(compared === undefined$1
	              ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
	              : compared
	            )) {
	          result = false;
	          break;
	        }
	        skipCtor || (skipCtor = key == 'constructor');
	      }
	      if (result && !skipCtor) {
	        var objCtor = object.constructor,
	            othCtor = other.constructor;

	        // Non `Object` object instances with different constructors are not equal.
	        if (objCtor != othCtor &&
	            ('constructor' in object && 'constructor' in other) &&
	            !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
	              typeof othCtor == 'function' && othCtor instanceof othCtor)) {
	          result = false;
	        }
	      }
	      stack['delete'](object);
	      stack['delete'](other);
	      return result;
	    }

	    /**
	     * A specialized version of `baseRest` which flattens the rest array.
	     *
	     * @private
	     * @param {Function} func The function to apply a rest parameter to.
	     * @returns {Function} Returns the new function.
	     */
	    function flatRest(func) {
	      return setToString(overRest(func, undefined$1, flatten), func + '');
	    }

	    /**
	     * Creates an array of own enumerable property names and symbols of `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property names and symbols.
	     */
	    function getAllKeys(object) {
	      return baseGetAllKeys(object, keys, getSymbols);
	    }

	    /**
	     * Creates an array of own and inherited enumerable property names and
	     * symbols of `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property names and symbols.
	     */
	    function getAllKeysIn(object) {
	      return baseGetAllKeys(object, keysIn, getSymbolsIn);
	    }

	    /**
	     * Gets metadata for `func`.
	     *
	     * @private
	     * @param {Function} func The function to query.
	     * @returns {*} Returns the metadata for `func`.
	     */
	    var getData = !metaMap ? noop : function(func) {
	      return metaMap.get(func);
	    };

	    /**
	     * Gets the name of `func`.
	     *
	     * @private
	     * @param {Function} func The function to query.
	     * @returns {string} Returns the function name.
	     */
	    function getFuncName(func) {
	      var result = (func.name + ''),
	          array = realNames[result],
	          length = hasOwnProperty.call(realNames, result) ? array.length : 0;

	      while (length--) {
	        var data = array[length],
	            otherFunc = data.func;
	        if (otherFunc == null || otherFunc == func) {
	          return data.name;
	        }
	      }
	      return result;
	    }

	    /**
	     * Gets the argument placeholder value for `func`.
	     *
	     * @private
	     * @param {Function} func The function to inspect.
	     * @returns {*} Returns the placeholder value.
	     */
	    function getHolder(func) {
	      var object = hasOwnProperty.call(lodash, 'placeholder') ? lodash : func;
	      return object.placeholder;
	    }

	    /**
	     * Gets the appropriate "iteratee" function. If `_.iteratee` is customized,
	     * this function returns the custom method, otherwise it returns `baseIteratee`.
	     * If arguments are provided, the chosen function is invoked with them and
	     * its result is returned.
	     *
	     * @private
	     * @param {*} [value] The value to convert to an iteratee.
	     * @param {number} [arity] The arity of the created iteratee.
	     * @returns {Function} Returns the chosen function or its result.
	     */
	    function getIteratee() {
	      var result = lodash.iteratee || iteratee;
	      result = result === iteratee ? baseIteratee : result;
	      return arguments.length ? result(arguments[0], arguments[1]) : result;
	    }

	    /**
	     * Gets the data for `map`.
	     *
	     * @private
	     * @param {Object} map The map to query.
	     * @param {string} key The reference key.
	     * @returns {*} Returns the map data.
	     */
	    function getMapData(map, key) {
	      var data = map.__data__;
	      return isKeyable(key)
	        ? data[typeof key == 'string' ? 'string' : 'hash']
	        : data.map;
	    }

	    /**
	     * Gets the property names, values, and compare flags of `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the match data of `object`.
	     */
	    function getMatchData(object) {
	      var result = keys(object),
	          length = result.length;

	      while (length--) {
	        var key = result[length],
	            value = object[key];

	        result[length] = [key, value, isStrictComparable(value)];
	      }
	      return result;
	    }

	    /**
	     * Gets the native function at `key` of `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @param {string} key The key of the method to get.
	     * @returns {*} Returns the function if it's native, else `undefined`.
	     */
	    function getNative(object, key) {
	      var value = getValue(object, key);
	      return baseIsNative(value) ? value : undefined$1;
	    }

	    /**
	     * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
	     *
	     * @private
	     * @param {*} value The value to query.
	     * @returns {string} Returns the raw `toStringTag`.
	     */
	    function getRawTag(value) {
	      var isOwn = hasOwnProperty.call(value, symToStringTag),
	          tag = value[symToStringTag];

	      try {
	        value[symToStringTag] = undefined$1;
	        var unmasked = true;
	      } catch (e) {}

	      var result = nativeObjectToString.call(value);
	      if (unmasked) {
	        if (isOwn) {
	          value[symToStringTag] = tag;
	        } else {
	          delete value[symToStringTag];
	        }
	      }
	      return result;
	    }

	    /**
	     * Creates an array of the own enumerable symbols of `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of symbols.
	     */
	    var getSymbols = !nativeGetSymbols ? stubArray : function(object) {
	      if (object == null) {
	        return [];
	      }
	      object = Object(object);
	      return arrayFilter(nativeGetSymbols(object), function(symbol) {
	        return propertyIsEnumerable.call(object, symbol);
	      });
	    };

	    /**
	     * Creates an array of the own and inherited enumerable symbols of `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of symbols.
	     */
	    var getSymbolsIn = !nativeGetSymbols ? stubArray : function(object) {
	      var result = [];
	      while (object) {
	        arrayPush(result, getSymbols(object));
	        object = getPrototype(object);
	      }
	      return result;
	    };

	    /**
	     * Gets the `toStringTag` of `value`.
	     *
	     * @private
	     * @param {*} value The value to query.
	     * @returns {string} Returns the `toStringTag`.
	     */
	    var getTag = baseGetTag;

	    // Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
	    if ((DataView && getTag(new DataView(new ArrayBuffer(1))) != dataViewTag) ||
	        (Map && getTag(new Map) != mapTag) ||
	        (Promise && getTag(Promise.resolve()) != promiseTag) ||
	        (Set && getTag(new Set) != setTag) ||
	        (WeakMap && getTag(new WeakMap) != weakMapTag)) {
	      getTag = function(value) {
	        var result = baseGetTag(value),
	            Ctor = result == objectTag ? value.constructor : undefined$1,
	            ctorString = Ctor ? toSource(Ctor) : '';

	        if (ctorString) {
	          switch (ctorString) {
	            case dataViewCtorString: return dataViewTag;
	            case mapCtorString: return mapTag;
	            case promiseCtorString: return promiseTag;
	            case setCtorString: return setTag;
	            case weakMapCtorString: return weakMapTag;
	          }
	        }
	        return result;
	      };
	    }

	    /**
	     * Gets the view, applying any `transforms` to the `start` and `end` positions.
	     *
	     * @private
	     * @param {number} start The start of the view.
	     * @param {number} end The end of the view.
	     * @param {Array} transforms The transformations to apply to the view.
	     * @returns {Object} Returns an object containing the `start` and `end`
	     *  positions of the view.
	     */
	    function getView(start, end, transforms) {
	      var index = -1,
	          length = transforms.length;

	      while (++index < length) {
	        var data = transforms[index],
	            size = data.size;

	        switch (data.type) {
	          case 'drop':      start += size; break;
	          case 'dropRight': end -= size; break;
	          case 'take':      end = nativeMin(end, start + size); break;
	          case 'takeRight': start = nativeMax(start, end - size); break;
	        }
	      }
	      return { 'start': start, 'end': end };
	    }

	    /**
	     * Extracts wrapper details from the `source` body comment.
	     *
	     * @private
	     * @param {string} source The source to inspect.
	     * @returns {Array} Returns the wrapper details.
	     */
	    function getWrapDetails(source) {
	      var match = source.match(reWrapDetails);
	      return match ? match[1].split(reSplitDetails) : [];
	    }

	    /**
	     * Checks if `path` exists on `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @param {Array|string} path The path to check.
	     * @param {Function} hasFunc The function to check properties.
	     * @returns {boolean} Returns `true` if `path` exists, else `false`.
	     */
	    function hasPath(object, path, hasFunc) {
	      path = castPath(path, object);

	      var index = -1,
	          length = path.length,
	          result = false;

	      while (++index < length) {
	        var key = toKey(path[index]);
	        if (!(result = object != null && hasFunc(object, key))) {
	          break;
	        }
	        object = object[key];
	      }
	      if (result || ++index != length) {
	        return result;
	      }
	      length = object == null ? 0 : object.length;
	      return !!length && isLength(length) && isIndex(key, length) &&
	        (isArray(object) || isArguments(object));
	    }

	    /**
	     * Initializes an array clone.
	     *
	     * @private
	     * @param {Array} array The array to clone.
	     * @returns {Array} Returns the initialized clone.
	     */
	    function initCloneArray(array) {
	      var length = array.length,
	          result = new array.constructor(length);

	      // Add properties assigned by `RegExp#exec`.
	      if (length && typeof array[0] == 'string' && hasOwnProperty.call(array, 'index')) {
	        result.index = array.index;
	        result.input = array.input;
	      }
	      return result;
	    }

	    /**
	     * Initializes an object clone.
	     *
	     * @private
	     * @param {Object} object The object to clone.
	     * @returns {Object} Returns the initialized clone.
	     */
	    function initCloneObject(object) {
	      return (typeof object.constructor == 'function' && !isPrototype(object))
	        ? baseCreate(getPrototype(object))
	        : {};
	    }

	    /**
	     * Initializes an object clone based on its `toStringTag`.
	     *
	     * **Note:** This function only supports cloning values with tags of
	     * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
	     *
	     * @private
	     * @param {Object} object The object to clone.
	     * @param {string} tag The `toStringTag` of the object to clone.
	     * @param {boolean} [isDeep] Specify a deep clone.
	     * @returns {Object} Returns the initialized clone.
	     */
	    function initCloneByTag(object, tag, isDeep) {
	      var Ctor = object.constructor;
	      switch (tag) {
	        case arrayBufferTag:
	          return cloneArrayBuffer(object);

	        case boolTag:
	        case dateTag:
	          return new Ctor(+object);

	        case dataViewTag:
	          return cloneDataView(object, isDeep);

	        case float32Tag: case float64Tag:
	        case int8Tag: case int16Tag: case int32Tag:
	        case uint8Tag: case uint8ClampedTag: case uint16Tag: case uint32Tag:
	          return cloneTypedArray(object, isDeep);

	        case mapTag:
	          return new Ctor;

	        case numberTag:
	        case stringTag:
	          return new Ctor(object);

	        case regexpTag:
	          return cloneRegExp(object);

	        case setTag:
	          return new Ctor;

	        case symbolTag:
	          return cloneSymbol(object);
	      }
	    }

	    /**
	     * Inserts wrapper `details` in a comment at the top of the `source` body.
	     *
	     * @private
	     * @param {string} source The source to modify.
	     * @returns {Array} details The details to insert.
	     * @returns {string} Returns the modified source.
	     */
	    function insertWrapDetails(source, details) {
	      var length = details.length;
	      if (!length) {
	        return source;
	      }
	      var lastIndex = length - 1;
	      details[lastIndex] = (length > 1 ? '& ' : '') + details[lastIndex];
	      details = details.join(length > 2 ? ', ' : ' ');
	      return source.replace(reWrapComment, '{\n/* [wrapped with ' + details + '] */\n');
	    }

	    /**
	     * Checks if `value` is a flattenable `arguments` object or array.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
	     */
	    function isFlattenable(value) {
	      return isArray(value) || isArguments(value) ||
	        !!(spreadableSymbol && value && value[spreadableSymbol]);
	    }

	    /**
	     * Checks if `value` is a valid array-like index.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
	     * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
	     */
	    function isIndex(value, length) {
	      var type = typeof value;
	      length = length == null ? MAX_SAFE_INTEGER : length;

	      return !!length &&
	        (type == 'number' ||
	          (type != 'symbol' && reIsUint.test(value))) &&
	            (value > -1 && value % 1 == 0 && value < length);
	    }

	    /**
	     * Checks if the given arguments are from an iteratee call.
	     *
	     * @private
	     * @param {*} value The potential iteratee value argument.
	     * @param {*} index The potential iteratee index or key argument.
	     * @param {*} object The potential iteratee object argument.
	     * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
	     *  else `false`.
	     */
	    function isIterateeCall(value, index, object) {
	      if (!isObject(object)) {
	        return false;
	      }
	      var type = typeof index;
	      if (type == 'number'
	            ? (isArrayLike(object) && isIndex(index, object.length))
	            : (type == 'string' && index in object)
	          ) {
	        return eq(object[index], value);
	      }
	      return false;
	    }

	    /**
	     * Checks if `value` is a property name and not a property path.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @param {Object} [object] The object to query keys on.
	     * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
	     */
	    function isKey(value, object) {
	      if (isArray(value)) {
	        return false;
	      }
	      var type = typeof value;
	      if (type == 'number' || type == 'symbol' || type == 'boolean' ||
	          value == null || isSymbol(value)) {
	        return true;
	      }
	      return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
	        (object != null && value in Object(object));
	    }

	    /**
	     * Checks if `value` is suitable for use as unique object key.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
	     */
	    function isKeyable(value) {
	      var type = typeof value;
	      return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
	        ? (value !== '__proto__')
	        : (value === null);
	    }

	    /**
	     * Checks if `func` has a lazy counterpart.
	     *
	     * @private
	     * @param {Function} func The function to check.
	     * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
	     *  else `false`.
	     */
	    function isLaziable(func) {
	      var funcName = getFuncName(func),
	          other = lodash[funcName];

	      if (typeof other != 'function' || !(funcName in LazyWrapper.prototype)) {
	        return false;
	      }
	      if (func === other) {
	        return true;
	      }
	      var data = getData(other);
	      return !!data && func === data[0];
	    }

	    /**
	     * Checks if `func` has its source masked.
	     *
	     * @private
	     * @param {Function} func The function to check.
	     * @returns {boolean} Returns `true` if `func` is masked, else `false`.
	     */
	    function isMasked(func) {
	      return !!maskSrcKey && (maskSrcKey in func);
	    }

	    /**
	     * Checks if `func` is capable of being masked.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `func` is maskable, else `false`.
	     */
	    var isMaskable = coreJsData ? isFunction : stubFalse;

	    /**
	     * Checks if `value` is likely a prototype object.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
	     */
	    function isPrototype(value) {
	      var Ctor = value && value.constructor,
	          proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto;

	      return value === proto;
	    }

	    /**
	     * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
	     *
	     * @private
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` if suitable for strict
	     *  equality comparisons, else `false`.
	     */
	    function isStrictComparable(value) {
	      return value === value && !isObject(value);
	    }

	    /**
	     * A specialized version of `matchesProperty` for source values suitable
	     * for strict equality comparisons, i.e. `===`.
	     *
	     * @private
	     * @param {string} key The key of the property to get.
	     * @param {*} srcValue The value to match.
	     * @returns {Function} Returns the new spec function.
	     */
	    function matchesStrictComparable(key, srcValue) {
	      return function(object) {
	        if (object == null) {
	          return false;
	        }
	        return object[key] === srcValue &&
	          (srcValue !== undefined$1 || (key in Object(object)));
	      };
	    }

	    /**
	     * A specialized version of `_.memoize` which clears the memoized function's
	     * cache when it exceeds `MAX_MEMOIZE_SIZE`.
	     *
	     * @private
	     * @param {Function} func The function to have its output memoized.
	     * @returns {Function} Returns the new memoized function.
	     */
	    function memoizeCapped(func) {
	      var result = memoize(func, function(key) {
	        if (cache.size === MAX_MEMOIZE_SIZE) {
	          cache.clear();
	        }
	        return key;
	      });

	      var cache = result.cache;
	      return result;
	    }

	    /**
	     * Merges the function metadata of `source` into `data`.
	     *
	     * Merging metadata reduces the number of wrappers used to invoke a function.
	     * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
	     * may be applied regardless of execution order. Methods like `_.ary` and
	     * `_.rearg` modify function arguments, making the order in which they are
	     * executed important, preventing the merging of metadata. However, we make
	     * an exception for a safe combined case where curried functions have `_.ary`
	     * and or `_.rearg` applied.
	     *
	     * @private
	     * @param {Array} data The destination metadata.
	     * @param {Array} source The source metadata.
	     * @returns {Array} Returns `data`.
	     */
	    function mergeData(data, source) {
	      var bitmask = data[1],
	          srcBitmask = source[1],
	          newBitmask = bitmask | srcBitmask,
	          isCommon = newBitmask < (WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG | WRAP_ARY_FLAG);

	      var isCombo =
	        ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_CURRY_FLAG)) ||
	        ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_REARG_FLAG) && (data[7].length <= source[8])) ||
	        ((srcBitmask == (WRAP_ARY_FLAG | WRAP_REARG_FLAG)) && (source[7].length <= source[8]) && (bitmask == WRAP_CURRY_FLAG));

	      // Exit early if metadata can't be merged.
	      if (!(isCommon || isCombo)) {
	        return data;
	      }
	      // Use source `thisArg` if available.
	      if (srcBitmask & WRAP_BIND_FLAG) {
	        data[2] = source[2];
	        // Set when currying a bound function.
	        newBitmask |= bitmask & WRAP_BIND_FLAG ? 0 : WRAP_CURRY_BOUND_FLAG;
	      }
	      // Compose partial arguments.
	      var value = source[3];
	      if (value) {
	        var partials = data[3];
	        data[3] = partials ? composeArgs(partials, value, source[4]) : value;
	        data[4] = partials ? replaceHolders(data[3], PLACEHOLDER) : source[4];
	      }
	      // Compose partial right arguments.
	      value = source[5];
	      if (value) {
	        partials = data[5];
	        data[5] = partials ? composeArgsRight(partials, value, source[6]) : value;
	        data[6] = partials ? replaceHolders(data[5], PLACEHOLDER) : source[6];
	      }
	      // Use source `argPos` if available.
	      value = source[7];
	      if (value) {
	        data[7] = value;
	      }
	      // Use source `ary` if it's smaller.
	      if (srcBitmask & WRAP_ARY_FLAG) {
	        data[8] = data[8] == null ? source[8] : nativeMin(data[8], source[8]);
	      }
	      // Use source `arity` if one is not provided.
	      if (data[9] == null) {
	        data[9] = source[9];
	      }
	      // Use source `func` and merge bitmasks.
	      data[0] = source[0];
	      data[1] = newBitmask;

	      return data;
	    }

	    /**
	     * This function is like
	     * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
	     * except that it includes inherited enumerable properties.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property names.
	     */
	    function nativeKeysIn(object) {
	      var result = [];
	      if (object != null) {
	        for (var key in Object(object)) {
	          result.push(key);
	        }
	      }
	      return result;
	    }

	    /**
	     * Converts `value` to a string using `Object.prototype.toString`.
	     *
	     * @private
	     * @param {*} value The value to convert.
	     * @returns {string} Returns the converted string.
	     */
	    function objectToString(value) {
	      return nativeObjectToString.call(value);
	    }

	    /**
	     * A specialized version of `baseRest` which transforms the rest array.
	     *
	     * @private
	     * @param {Function} func The function to apply a rest parameter to.
	     * @param {number} [start=func.length-1] The start position of the rest parameter.
	     * @param {Function} transform The rest array transform.
	     * @returns {Function} Returns the new function.
	     */
	    function overRest(func, start, transform) {
	      start = nativeMax(start === undefined$1 ? (func.length - 1) : start, 0);
	      return function() {
	        var args = arguments,
	            index = -1,
	            length = nativeMax(args.length - start, 0),
	            array = Array(length);

	        while (++index < length) {
	          array[index] = args[start + index];
	        }
	        index = -1;
	        var otherArgs = Array(start + 1);
	        while (++index < start) {
	          otherArgs[index] = args[index];
	        }
	        otherArgs[start] = transform(array);
	        return apply(func, this, otherArgs);
	      };
	    }

	    /**
	     * Gets the parent value at `path` of `object`.
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @param {Array} path The path to get the parent value of.
	     * @returns {*} Returns the parent value.
	     */
	    function parent(object, path) {
	      return path.length < 2 ? object : baseGet(object, baseSlice(path, 0, -1));
	    }

	    /**
	     * Reorder `array` according to the specified indexes where the element at
	     * the first index is assigned as the first element, the element at
	     * the second index is assigned as the second element, and so on.
	     *
	     * @private
	     * @param {Array} array The array to reorder.
	     * @param {Array} indexes The arranged array indexes.
	     * @returns {Array} Returns `array`.
	     */
	    function reorder(array, indexes) {
	      var arrLength = array.length,
	          length = nativeMin(indexes.length, arrLength),
	          oldArray = copyArray(array);

	      while (length--) {
	        var index = indexes[length];
	        array[length] = isIndex(index, arrLength) ? oldArray[index] : undefined$1;
	      }
	      return array;
	    }

	    /**
	     * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
	     *
	     * @private
	     * @param {Object} object The object to query.
	     * @param {string} key The key of the property to get.
	     * @returns {*} Returns the property value.
	     */
	    function safeGet(object, key) {
	      if (key === 'constructor' && typeof object[key] === 'function') {
	        return;
	      }

	      if (key == '__proto__') {
	        return;
	      }

	      return object[key];
	    }

	    /**
	     * Sets metadata for `func`.
	     *
	     * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
	     * period of time, it will trip its breaker and transition to an identity
	     * function to avoid garbage collection pauses in V8. See
	     * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
	     * for more details.
	     *
	     * @private
	     * @param {Function} func The function to associate metadata with.
	     * @param {*} data The metadata.
	     * @returns {Function} Returns `func`.
	     */
	    var setData = shortOut(baseSetData);

	    /**
	     * A simple wrapper around the global [`setTimeout`](https://mdn.io/setTimeout).
	     *
	     * @private
	     * @param {Function} func The function to delay.
	     * @param {number} wait The number of milliseconds to delay invocation.
	     * @returns {number|Object} Returns the timer id or timeout object.
	     */
	    var setTimeout = ctxSetTimeout || function(func, wait) {
	      return root.setTimeout(func, wait);
	    };

	    /**
	     * Sets the `toString` method of `func` to return `string`.
	     *
	     * @private
	     * @param {Function} func The function to modify.
	     * @param {Function} string The `toString` result.
	     * @returns {Function} Returns `func`.
	     */
	    var setToString = shortOut(baseSetToString);

	    /**
	     * Sets the `toString` method of `wrapper` to mimic the source of `reference`
	     * with wrapper details in a comment at the top of the source body.
	     *
	     * @private
	     * @param {Function} wrapper The function to modify.
	     * @param {Function} reference The reference function.
	     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
	     * @returns {Function} Returns `wrapper`.
	     */
	    function setWrapToString(wrapper, reference, bitmask) {
	      var source = (reference + '');
	      return setToString(wrapper, insertWrapDetails(source, updateWrapDetails(getWrapDetails(source), bitmask)));
	    }

	    /**
	     * Creates a function that'll short out and invoke `identity` instead
	     * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
	     * milliseconds.
	     *
	     * @private
	     * @param {Function} func The function to restrict.
	     * @returns {Function} Returns the new shortable function.
	     */
	    function shortOut(func) {
	      var count = 0,
	          lastCalled = 0;

	      return function() {
	        var stamp = nativeNow(),
	            remaining = HOT_SPAN - (stamp - lastCalled);

	        lastCalled = stamp;
	        if (remaining > 0) {
	          if (++count >= HOT_COUNT) {
	            return arguments[0];
	          }
	        } else {
	          count = 0;
	        }
	        return func.apply(undefined$1, arguments);
	      };
	    }

	    /**
	     * A specialized version of `_.shuffle` which mutates and sets the size of `array`.
	     *
	     * @private
	     * @param {Array} array The array to shuffle.
	     * @param {number} [size=array.length] The size of `array`.
	     * @returns {Array} Returns `array`.
	     */
	    function shuffleSelf(array, size) {
	      var index = -1,
	          length = array.length,
	          lastIndex = length - 1;

	      size = size === undefined$1 ? length : size;
	      while (++index < size) {
	        var rand = baseRandom(index, lastIndex),
	            value = array[rand];

	        array[rand] = array[index];
	        array[index] = value;
	      }
	      array.length = size;
	      return array;
	    }

	    /**
	     * Converts `string` to a property path array.
	     *
	     * @private
	     * @param {string} string The string to convert.
	     * @returns {Array} Returns the property path array.
	     */
	    var stringToPath = memoizeCapped(function(string) {
	      var result = [];
	      if (string.charCodeAt(0) === 46 /* . */) {
	        result.push('');
	      }
	      string.replace(rePropName, function(match, number, quote, subString) {
	        result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
	      });
	      return result;
	    });

	    /**
	     * Converts `value` to a string key if it's not a string or symbol.
	     *
	     * @private
	     * @param {*} value The value to inspect.
	     * @returns {string|symbol} Returns the key.
	     */
	    function toKey(value) {
	      if (typeof value == 'string' || isSymbol(value)) {
	        return value;
	      }
	      var result = (value + '');
	      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
	    }

	    /**
	     * Converts `func` to its source code.
	     *
	     * @private
	     * @param {Function} func The function to convert.
	     * @returns {string} Returns the source code.
	     */
	    function toSource(func) {
	      if (func != null) {
	        try {
	          return funcToString.call(func);
	        } catch (e) {}
	        try {
	          return (func + '');
	        } catch (e) {}
	      }
	      return '';
	    }

	    /**
	     * Updates wrapper `details` based on `bitmask` flags.
	     *
	     * @private
	     * @returns {Array} details The details to modify.
	     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
	     * @returns {Array} Returns `details`.
	     */
	    function updateWrapDetails(details, bitmask) {
	      arrayEach(wrapFlags, function(pair) {
	        var value = '_.' + pair[0];
	        if ((bitmask & pair[1]) && !arrayIncludes(details, value)) {
	          details.push(value);
	        }
	      });
	      return details.sort();
	    }

	    /**
	     * Creates a clone of `wrapper`.
	     *
	     * @private
	     * @param {Object} wrapper The wrapper to clone.
	     * @returns {Object} Returns the cloned wrapper.
	     */
	    function wrapperClone(wrapper) {
	      if (wrapper instanceof LazyWrapper) {
	        return wrapper.clone();
	      }
	      var result = new LodashWrapper(wrapper.__wrapped__, wrapper.__chain__);
	      result.__actions__ = copyArray(wrapper.__actions__);
	      result.__index__  = wrapper.__index__;
	      result.__values__ = wrapper.__values__;
	      return result;
	    }

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates an array of elements split into groups the length of `size`.
	     * If `array` can't be split evenly, the final chunk will be the remaining
	     * elements.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to process.
	     * @param {number} [size=1] The length of each chunk
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Array} Returns the new array of chunks.
	     * @example
	     *
	     * _.chunk(['a', 'b', 'c', 'd'], 2);
	     * // => [['a', 'b'], ['c', 'd']]
	     *
	     * _.chunk(['a', 'b', 'c', 'd'], 3);
	     * // => [['a', 'b', 'c'], ['d']]
	     */
	    function chunk(array, size, guard) {
	      if ((guard ? isIterateeCall(array, size, guard) : size === undefined$1)) {
	        size = 1;
	      } else {
	        size = nativeMax(toInteger(size), 0);
	      }
	      var length = array == null ? 0 : array.length;
	      if (!length || size < 1) {
	        return [];
	      }
	      var index = 0,
	          resIndex = 0,
	          result = Array(nativeCeil(length / size));

	      while (index < length) {
	        result[resIndex++] = baseSlice(array, index, (index += size));
	      }
	      return result;
	    }

	    /**
	     * Creates an array with all falsey values removed. The values `false`, `null`,
	     * `0`, `""`, `undefined`, and `NaN` are falsey.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to compact.
	     * @returns {Array} Returns the new array of filtered values.
	     * @example
	     *
	     * _.compact([0, 1, false, 2, '', 3]);
	     * // => [1, 2, 3]
	     */
	    function compact(array) {
	      var index = -1,
	          length = array == null ? 0 : array.length,
	          resIndex = 0,
	          result = [];

	      while (++index < length) {
	        var value = array[index];
	        if (value) {
	          result[resIndex++] = value;
	        }
	      }
	      return result;
	    }

	    /**
	     * Creates a new array concatenating `array` with any additional arrays
	     * and/or values.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to concatenate.
	     * @param {...*} [values] The values to concatenate.
	     * @returns {Array} Returns the new concatenated array.
	     * @example
	     *
	     * var array = [1];
	     * var other = _.concat(array, 2, [3], [[4]]);
	     *
	     * console.log(other);
	     * // => [1, 2, 3, [4]]
	     *
	     * console.log(array);
	     * // => [1]
	     */
	    function concat() {
	      var length = arguments.length;
	      if (!length) {
	        return [];
	      }
	      var args = Array(length - 1),
	          array = arguments[0],
	          index = length;

	      while (index--) {
	        args[index - 1] = arguments[index];
	      }
	      return arrayPush(isArray(array) ? copyArray(array) : [array], baseFlatten(args, 1));
	    }

	    /**
	     * Creates an array of `array` values not included in the other given arrays
	     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * for equality comparisons. The order and references of result values are
	     * determined by the first array.
	     *
	     * **Note:** Unlike `_.pullAll`, this method returns a new array.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {...Array} [values] The values to exclude.
	     * @returns {Array} Returns the new array of filtered values.
	     * @see _.without, _.xor
	     * @example
	     *
	     * _.difference([2, 1], [2, 3]);
	     * // => [1]
	     */
	    var difference = baseRest(function(array, values) {
	      return isArrayLikeObject(array)
	        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true))
	        : [];
	    });

	    /**
	     * This method is like `_.difference` except that it accepts `iteratee` which
	     * is invoked for each element of `array` and `values` to generate the criterion
	     * by which they're compared. The order and references of result values are
	     * determined by the first array. The iteratee is invoked with one argument:
	     * (value).
	     *
	     * **Note:** Unlike `_.pullAllBy`, this method returns a new array.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {...Array} [values] The values to exclude.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {Array} Returns the new array of filtered values.
	     * @example
	     *
	     * _.differenceBy([2.1, 1.2], [2.3, 3.4], Math.floor);
	     * // => [1.2]
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.differenceBy([{ 'x': 2 }, { 'x': 1 }], [{ 'x': 1 }], 'x');
	     * // => [{ 'x': 2 }]
	     */
	    var differenceBy = baseRest(function(array, values) {
	      var iteratee = last(values);
	      if (isArrayLikeObject(iteratee)) {
	        iteratee = undefined$1;
	      }
	      return isArrayLikeObject(array)
	        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), getIteratee(iteratee, 2))
	        : [];
	    });

	    /**
	     * This method is like `_.difference` except that it accepts `comparator`
	     * which is invoked to compare elements of `array` to `values`. The order and
	     * references of result values are determined by the first array. The comparator
	     * is invoked with two arguments: (arrVal, othVal).
	     *
	     * **Note:** Unlike `_.pullAllWith`, this method returns a new array.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {...Array} [values] The values to exclude.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new array of filtered values.
	     * @example
	     *
	     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
	     *
	     * _.differenceWith(objects, [{ 'x': 1, 'y': 2 }], _.isEqual);
	     * // => [{ 'x': 2, 'y': 1 }]
	     */
	    var differenceWith = baseRest(function(array, values) {
	      var comparator = last(values);
	      if (isArrayLikeObject(comparator)) {
	        comparator = undefined$1;
	      }
	      return isArrayLikeObject(array)
	        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), undefined$1, comparator)
	        : [];
	    });

	    /**
	     * Creates a slice of `array` with `n` elements dropped from the beginning.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.5.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {number} [n=1] The number of elements to drop.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * _.drop([1, 2, 3]);
	     * // => [2, 3]
	     *
	     * _.drop([1, 2, 3], 2);
	     * // => [3]
	     *
	     * _.drop([1, 2, 3], 5);
	     * // => []
	     *
	     * _.drop([1, 2, 3], 0);
	     * // => [1, 2, 3]
	     */
	    function drop(array, n, guard) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return [];
	      }
	      n = (guard || n === undefined$1) ? 1 : toInteger(n);
	      return baseSlice(array, n < 0 ? 0 : n, length);
	    }

	    /**
	     * Creates a slice of `array` with `n` elements dropped from the end.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {number} [n=1] The number of elements to drop.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * _.dropRight([1, 2, 3]);
	     * // => [1, 2]
	     *
	     * _.dropRight([1, 2, 3], 2);
	     * // => [1]
	     *
	     * _.dropRight([1, 2, 3], 5);
	     * // => []
	     *
	     * _.dropRight([1, 2, 3], 0);
	     * // => [1, 2, 3]
	     */
	    function dropRight(array, n, guard) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return [];
	      }
	      n = (guard || n === undefined$1) ? 1 : toInteger(n);
	      n = length - n;
	      return baseSlice(array, 0, n < 0 ? 0 : n);
	    }

	    /**
	     * Creates a slice of `array` excluding elements dropped from the end.
	     * Elements are dropped until `predicate` returns falsey. The predicate is
	     * invoked with three arguments: (value, index, array).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'active': true },
	     *   { 'user': 'fred',    'active': false },
	     *   { 'user': 'pebbles', 'active': false }
	     * ];
	     *
	     * _.dropRightWhile(users, function(o) { return !o.active; });
	     * // => objects for ['barney']
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.dropRightWhile(users, { 'user': 'pebbles', 'active': false });
	     * // => objects for ['barney', 'fred']
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.dropRightWhile(users, ['active', false]);
	     * // => objects for ['barney']
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.dropRightWhile(users, 'active');
	     * // => objects for ['barney', 'fred', 'pebbles']
	     */
	    function dropRightWhile(array, predicate) {
	      return (array && array.length)
	        ? baseWhile(array, getIteratee(predicate, 3), true, true)
	        : [];
	    }

	    /**
	     * Creates a slice of `array` excluding elements dropped from the beginning.
	     * Elements are dropped until `predicate` returns falsey. The predicate is
	     * invoked with three arguments: (value, index, array).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'active': false },
	     *   { 'user': 'fred',    'active': false },
	     *   { 'user': 'pebbles', 'active': true }
	     * ];
	     *
	     * _.dropWhile(users, function(o) { return !o.active; });
	     * // => objects for ['pebbles']
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.dropWhile(users, { 'user': 'barney', 'active': false });
	     * // => objects for ['fred', 'pebbles']
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.dropWhile(users, ['active', false]);
	     * // => objects for ['pebbles']
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.dropWhile(users, 'active');
	     * // => objects for ['barney', 'fred', 'pebbles']
	     */
	    function dropWhile(array, predicate) {
	      return (array && array.length)
	        ? baseWhile(array, getIteratee(predicate, 3), true)
	        : [];
	    }

	    /**
	     * Fills elements of `array` with `value` from `start` up to, but not
	     * including, `end`.
	     *
	     * **Note:** This method mutates `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.2.0
	     * @category Array
	     * @param {Array} array The array to fill.
	     * @param {*} value The value to fill `array` with.
	     * @param {number} [start=0] The start position.
	     * @param {number} [end=array.length] The end position.
	     * @returns {Array} Returns `array`.
	     * @example
	     *
	     * var array = [1, 2, 3];
	     *
	     * _.fill(array, 'a');
	     * console.log(array);
	     * // => ['a', 'a', 'a']
	     *
	     * _.fill(Array(3), 2);
	     * // => [2, 2, 2]
	     *
	     * _.fill([4, 6, 8, 10], '*', 1, 3);
	     * // => [4, '*', '*', 10]
	     */
	    function fill(array, value, start, end) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return [];
	      }
	      if (start && typeof start != 'number' && isIterateeCall(array, value, start)) {
	        start = 0;
	        end = length;
	      }
	      return baseFill(array, value, start, end);
	    }

	    /**
	     * This method is like `_.find` except that it returns the index of the first
	     * element `predicate` returns truthy for instead of the element itself.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.1.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @param {number} [fromIndex=0] The index to search from.
	     * @returns {number} Returns the index of the found element, else `-1`.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'active': false },
	     *   { 'user': 'fred',    'active': false },
	     *   { 'user': 'pebbles', 'active': true }
	     * ];
	     *
	     * _.findIndex(users, function(o) { return o.user == 'barney'; });
	     * // => 0
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.findIndex(users, { 'user': 'fred', 'active': false });
	     * // => 1
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.findIndex(users, ['active', false]);
	     * // => 0
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.findIndex(users, 'active');
	     * // => 2
	     */
	    function findIndex(array, predicate, fromIndex) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return -1;
	      }
	      var index = fromIndex == null ? 0 : toInteger(fromIndex);
	      if (index < 0) {
	        index = nativeMax(length + index, 0);
	      }
	      return baseFindIndex(array, getIteratee(predicate, 3), index);
	    }

	    /**
	     * This method is like `_.findIndex` except that it iterates over elements
	     * of `collection` from right to left.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @param {number} [fromIndex=array.length-1] The index to search from.
	     * @returns {number} Returns the index of the found element, else `-1`.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'active': true },
	     *   { 'user': 'fred',    'active': false },
	     *   { 'user': 'pebbles', 'active': false }
	     * ];
	     *
	     * _.findLastIndex(users, function(o) { return o.user == 'pebbles'; });
	     * // => 2
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.findLastIndex(users, { 'user': 'barney', 'active': true });
	     * // => 0
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.findLastIndex(users, ['active', false]);
	     * // => 2
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.findLastIndex(users, 'active');
	     * // => 0
	     */
	    function findLastIndex(array, predicate, fromIndex) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return -1;
	      }
	      var index = length - 1;
	      if (fromIndex !== undefined$1) {
	        index = toInteger(fromIndex);
	        index = fromIndex < 0
	          ? nativeMax(length + index, 0)
	          : nativeMin(index, length - 1);
	      }
	      return baseFindIndex(array, getIteratee(predicate, 3), index, true);
	    }

	    /**
	     * Flattens `array` a single level deep.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to flatten.
	     * @returns {Array} Returns the new flattened array.
	     * @example
	     *
	     * _.flatten([1, [2, [3, [4]], 5]]);
	     * // => [1, 2, [3, [4]], 5]
	     */
	    function flatten(array) {
	      var length = array == null ? 0 : array.length;
	      return length ? baseFlatten(array, 1) : [];
	    }

	    /**
	     * Recursively flattens `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to flatten.
	     * @returns {Array} Returns the new flattened array.
	     * @example
	     *
	     * _.flattenDeep([1, [2, [3, [4]], 5]]);
	     * // => [1, 2, 3, 4, 5]
	     */
	    function flattenDeep(array) {
	      var length = array == null ? 0 : array.length;
	      return length ? baseFlatten(array, INFINITY) : [];
	    }

	    /**
	     * Recursively flatten `array` up to `depth` times.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.4.0
	     * @category Array
	     * @param {Array} array The array to flatten.
	     * @param {number} [depth=1] The maximum recursion depth.
	     * @returns {Array} Returns the new flattened array.
	     * @example
	     *
	     * var array = [1, [2, [3, [4]], 5]];
	     *
	     * _.flattenDepth(array, 1);
	     * // => [1, 2, [3, [4]], 5]
	     *
	     * _.flattenDepth(array, 2);
	     * // => [1, 2, 3, [4], 5]
	     */
	    function flattenDepth(array, depth) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return [];
	      }
	      depth = depth === undefined$1 ? 1 : toInteger(depth);
	      return baseFlatten(array, depth);
	    }

	    /**
	     * The inverse of `_.toPairs`; this method returns an object composed
	     * from key-value `pairs`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} pairs The key-value pairs.
	     * @returns {Object} Returns the new object.
	     * @example
	     *
	     * _.fromPairs([['a', 1], ['b', 2]]);
	     * // => { 'a': 1, 'b': 2 }
	     */
	    function fromPairs(pairs) {
	      var index = -1,
	          length = pairs == null ? 0 : pairs.length,
	          result = {};

	      while (++index < length) {
	        var pair = pairs[index];
	        result[pair[0]] = pair[1];
	      }
	      return result;
	    }

	    /**
	     * Gets the first element of `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @alias first
	     * @category Array
	     * @param {Array} array The array to query.
	     * @returns {*} Returns the first element of `array`.
	     * @example
	     *
	     * _.head([1, 2, 3]);
	     * // => 1
	     *
	     * _.head([]);
	     * // => undefined
	     */
	    function head(array) {
	      return (array && array.length) ? array[0] : undefined$1;
	    }

	    /**
	     * Gets the index at which the first occurrence of `value` is found in `array`
	     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * for equality comparisons. If `fromIndex` is negative, it's used as the
	     * offset from the end of `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {*} value The value to search for.
	     * @param {number} [fromIndex=0] The index to search from.
	     * @returns {number} Returns the index of the matched value, else `-1`.
	     * @example
	     *
	     * _.indexOf([1, 2, 1, 2], 2);
	     * // => 1
	     *
	     * // Search from the `fromIndex`.
	     * _.indexOf([1, 2, 1, 2], 2, 2);
	     * // => 3
	     */
	    function indexOf(array, value, fromIndex) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return -1;
	      }
	      var index = fromIndex == null ? 0 : toInteger(fromIndex);
	      if (index < 0) {
	        index = nativeMax(length + index, 0);
	      }
	      return baseIndexOf(array, value, index);
	    }

	    /**
	     * Gets all but the last element of `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * _.initial([1, 2, 3]);
	     * // => [1, 2]
	     */
	    function initial(array) {
	      var length = array == null ? 0 : array.length;
	      return length ? baseSlice(array, 0, -1) : [];
	    }

	    /**
	     * Creates an array of unique values that are included in all given arrays
	     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * for equality comparisons. The order and references of result values are
	     * determined by the first array.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @returns {Array} Returns the new array of intersecting values.
	     * @example
	     *
	     * _.intersection([2, 1], [2, 3]);
	     * // => [2]
	     */
	    var intersection = baseRest(function(arrays) {
	      var mapped = arrayMap(arrays, castArrayLikeObject);
	      return (mapped.length && mapped[0] === arrays[0])
	        ? baseIntersection(mapped)
	        : [];
	    });

	    /**
	     * This method is like `_.intersection` except that it accepts `iteratee`
	     * which is invoked for each element of each `arrays` to generate the criterion
	     * by which they're compared. The order and references of result values are
	     * determined by the first array. The iteratee is invoked with one argument:
	     * (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {Array} Returns the new array of intersecting values.
	     * @example
	     *
	     * _.intersectionBy([2.1, 1.2], [2.3, 3.4], Math.floor);
	     * // => [2.1]
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.intersectionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
	     * // => [{ 'x': 1 }]
	     */
	    var intersectionBy = baseRest(function(arrays) {
	      var iteratee = last(arrays),
	          mapped = arrayMap(arrays, castArrayLikeObject);

	      if (iteratee === last(mapped)) {
	        iteratee = undefined$1;
	      } else {
	        mapped.pop();
	      }
	      return (mapped.length && mapped[0] === arrays[0])
	        ? baseIntersection(mapped, getIteratee(iteratee, 2))
	        : [];
	    });

	    /**
	     * This method is like `_.intersection` except that it accepts `comparator`
	     * which is invoked to compare elements of `arrays`. The order and references
	     * of result values are determined by the first array. The comparator is
	     * invoked with two arguments: (arrVal, othVal).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new array of intersecting values.
	     * @example
	     *
	     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
	     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
	     *
	     * _.intersectionWith(objects, others, _.isEqual);
	     * // => [{ 'x': 1, 'y': 2 }]
	     */
	    var intersectionWith = baseRest(function(arrays) {
	      var comparator = last(arrays),
	          mapped = arrayMap(arrays, castArrayLikeObject);

	      comparator = typeof comparator == 'function' ? comparator : undefined$1;
	      if (comparator) {
	        mapped.pop();
	      }
	      return (mapped.length && mapped[0] === arrays[0])
	        ? baseIntersection(mapped, undefined$1, comparator)
	        : [];
	    });

	    /**
	     * Converts all elements in `array` into a string separated by `separator`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to convert.
	     * @param {string} [separator=','] The element separator.
	     * @returns {string} Returns the joined string.
	     * @example
	     *
	     * _.join(['a', 'b', 'c'], '~');
	     * // => 'a~b~c'
	     */
	    function join(array, separator) {
	      return array == null ? '' : nativeJoin.call(array, separator);
	    }

	    /**
	     * Gets the last element of `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @returns {*} Returns the last element of `array`.
	     * @example
	     *
	     * _.last([1, 2, 3]);
	     * // => 3
	     */
	    function last(array) {
	      var length = array == null ? 0 : array.length;
	      return length ? array[length - 1] : undefined$1;
	    }

	    /**
	     * This method is like `_.indexOf` except that it iterates over elements of
	     * `array` from right to left.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {*} value The value to search for.
	     * @param {number} [fromIndex=array.length-1] The index to search from.
	     * @returns {number} Returns the index of the matched value, else `-1`.
	     * @example
	     *
	     * _.lastIndexOf([1, 2, 1, 2], 2);
	     * // => 3
	     *
	     * // Search from the `fromIndex`.
	     * _.lastIndexOf([1, 2, 1, 2], 2, 2);
	     * // => 1
	     */
	    function lastIndexOf(array, value, fromIndex) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return -1;
	      }
	      var index = length;
	      if (fromIndex !== undefined$1) {
	        index = toInteger(fromIndex);
	        index = index < 0 ? nativeMax(length + index, 0) : nativeMin(index, length - 1);
	      }
	      return value === value
	        ? strictLastIndexOf(array, value, index)
	        : baseFindIndex(array, baseIsNaN, index, true);
	    }

	    /**
	     * Gets the element at index `n` of `array`. If `n` is negative, the nth
	     * element from the end is returned.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.11.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {number} [n=0] The index of the element to return.
	     * @returns {*} Returns the nth element of `array`.
	     * @example
	     *
	     * var array = ['a', 'b', 'c', 'd'];
	     *
	     * _.nth(array, 1);
	     * // => 'b'
	     *
	     * _.nth(array, -2);
	     * // => 'c';
	     */
	    function nth(array, n) {
	      return (array && array.length) ? baseNth(array, toInteger(n)) : undefined$1;
	    }

	    /**
	     * Removes all given values from `array` using
	     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * for equality comparisons.
	     *
	     * **Note:** Unlike `_.without`, this method mutates `array`. Use `_.remove`
	     * to remove elements from an array by predicate.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Array
	     * @param {Array} array The array to modify.
	     * @param {...*} [values] The values to remove.
	     * @returns {Array} Returns `array`.
	     * @example
	     *
	     * var array = ['a', 'b', 'c', 'a', 'b', 'c'];
	     *
	     * _.pull(array, 'a', 'c');
	     * console.log(array);
	     * // => ['b', 'b']
	     */
	    var pull = baseRest(pullAll);

	    /**
	     * This method is like `_.pull` except that it accepts an array of values to remove.
	     *
	     * **Note:** Unlike `_.difference`, this method mutates `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to modify.
	     * @param {Array} values The values to remove.
	     * @returns {Array} Returns `array`.
	     * @example
	     *
	     * var array = ['a', 'b', 'c', 'a', 'b', 'c'];
	     *
	     * _.pullAll(array, ['a', 'c']);
	     * console.log(array);
	     * // => ['b', 'b']
	     */
	    function pullAll(array, values) {
	      return (array && array.length && values && values.length)
	        ? basePullAll(array, values)
	        : array;
	    }

	    /**
	     * This method is like `_.pullAll` except that it accepts `iteratee` which is
	     * invoked for each element of `array` and `values` to generate the criterion
	     * by which they're compared. The iteratee is invoked with one argument: (value).
	     *
	     * **Note:** Unlike `_.differenceBy`, this method mutates `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to modify.
	     * @param {Array} values The values to remove.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {Array} Returns `array`.
	     * @example
	     *
	     * var array = [{ 'x': 1 }, { 'x': 2 }, { 'x': 3 }, { 'x': 1 }];
	     *
	     * _.pullAllBy(array, [{ 'x': 1 }, { 'x': 3 }], 'x');
	     * console.log(array);
	     * // => [{ 'x': 2 }]
	     */
	    function pullAllBy(array, values, iteratee) {
	      return (array && array.length && values && values.length)
	        ? basePullAll(array, values, getIteratee(iteratee, 2))
	        : array;
	    }

	    /**
	     * This method is like `_.pullAll` except that it accepts `comparator` which
	     * is invoked to compare elements of `array` to `values`. The comparator is
	     * invoked with two arguments: (arrVal, othVal).
	     *
	     * **Note:** Unlike `_.differenceWith`, this method mutates `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.6.0
	     * @category Array
	     * @param {Array} array The array to modify.
	     * @param {Array} values The values to remove.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns `array`.
	     * @example
	     *
	     * var array = [{ 'x': 1, 'y': 2 }, { 'x': 3, 'y': 4 }, { 'x': 5, 'y': 6 }];
	     *
	     * _.pullAllWith(array, [{ 'x': 3, 'y': 4 }], _.isEqual);
	     * console.log(array);
	     * // => [{ 'x': 1, 'y': 2 }, { 'x': 5, 'y': 6 }]
	     */
	    function pullAllWith(array, values, comparator) {
	      return (array && array.length && values && values.length)
	        ? basePullAll(array, values, undefined$1, comparator)
	        : array;
	    }

	    /**
	     * Removes elements from `array` corresponding to `indexes` and returns an
	     * array of removed elements.
	     *
	     * **Note:** Unlike `_.at`, this method mutates `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to modify.
	     * @param {...(number|number[])} [indexes] The indexes of elements to remove.
	     * @returns {Array} Returns the new array of removed elements.
	     * @example
	     *
	     * var array = ['a', 'b', 'c', 'd'];
	     * var pulled = _.pullAt(array, [1, 3]);
	     *
	     * console.log(array);
	     * // => ['a', 'c']
	     *
	     * console.log(pulled);
	     * // => ['b', 'd']
	     */
	    var pullAt = flatRest(function(array, indexes) {
	      var length = array == null ? 0 : array.length,
	          result = baseAt(array, indexes);

	      basePullAt(array, arrayMap(indexes, function(index) {
	        return isIndex(index, length) ? +index : index;
	      }).sort(compareAscending));

	      return result;
	    });

	    /**
	     * Removes all elements from `array` that `predicate` returns truthy for
	     * and returns an array of the removed elements. The predicate is invoked
	     * with three arguments: (value, index, array).
	     *
	     * **Note:** Unlike `_.filter`, this method mutates `array`. Use `_.pull`
	     * to pull elements from an array by value.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Array
	     * @param {Array} array The array to modify.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the new array of removed elements.
	     * @example
	     *
	     * var array = [1, 2, 3, 4];
	     * var evens = _.remove(array, function(n) {
	     *   return n % 2 == 0;
	     * });
	     *
	     * console.log(array);
	     * // => [1, 3]
	     *
	     * console.log(evens);
	     * // => [2, 4]
	     */
	    function remove(array, predicate) {
	      var result = [];
	      if (!(array && array.length)) {
	        return result;
	      }
	      var index = -1,
	          indexes = [],
	          length = array.length;

	      predicate = getIteratee(predicate, 3);
	      while (++index < length) {
	        var value = array[index];
	        if (predicate(value, index, array)) {
	          result.push(value);
	          indexes.push(index);
	        }
	      }
	      basePullAt(array, indexes);
	      return result;
	    }

	    /**
	     * Reverses `array` so that the first element becomes the last, the second
	     * element becomes the second to last, and so on.
	     *
	     * **Note:** This method mutates `array` and is based on
	     * [`Array#reverse`](https://mdn.io/Array/reverse).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to modify.
	     * @returns {Array} Returns `array`.
	     * @example
	     *
	     * var array = [1, 2, 3];
	     *
	     * _.reverse(array);
	     * // => [3, 2, 1]
	     *
	     * console.log(array);
	     * // => [3, 2, 1]
	     */
	    function reverse(array) {
	      return array == null ? array : nativeReverse.call(array);
	    }

	    /**
	     * Creates a slice of `array` from `start` up to, but not including, `end`.
	     *
	     * **Note:** This method is used instead of
	     * [`Array#slice`](https://mdn.io/Array/slice) to ensure dense arrays are
	     * returned.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to slice.
	     * @param {number} [start=0] The start position.
	     * @param {number} [end=array.length] The end position.
	     * @returns {Array} Returns the slice of `array`.
	     */
	    function slice(array, start, end) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return [];
	      }
	      if (end && typeof end != 'number' && isIterateeCall(array, start, end)) {
	        start = 0;
	        end = length;
	      }
	      else {
	        start = start == null ? 0 : toInteger(start);
	        end = end === undefined$1 ? length : toInteger(end);
	      }
	      return baseSlice(array, start, end);
	    }

	    /**
	     * Uses a binary search to determine the lowest index at which `value`
	     * should be inserted into `array` in order to maintain its sort order.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The sorted array to inspect.
	     * @param {*} value The value to evaluate.
	     * @returns {number} Returns the index at which `value` should be inserted
	     *  into `array`.
	     * @example
	     *
	     * _.sortedIndex([30, 50], 40);
	     * // => 1
	     */
	    function sortedIndex(array, value) {
	      return baseSortedIndex(array, value);
	    }

	    /**
	     * This method is like `_.sortedIndex` except that it accepts `iteratee`
	     * which is invoked for `value` and each element of `array` to compute their
	     * sort ranking. The iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The sorted array to inspect.
	     * @param {*} value The value to evaluate.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {number} Returns the index at which `value` should be inserted
	     *  into `array`.
	     * @example
	     *
	     * var objects = [{ 'x': 4 }, { 'x': 5 }];
	     *
	     * _.sortedIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
	     * // => 0
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.sortedIndexBy(objects, { 'x': 4 }, 'x');
	     * // => 0
	     */
	    function sortedIndexBy(array, value, iteratee) {
	      return baseSortedIndexBy(array, value, getIteratee(iteratee, 2));
	    }

	    /**
	     * This method is like `_.indexOf` except that it performs a binary
	     * search on a sorted `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {*} value The value to search for.
	     * @returns {number} Returns the index of the matched value, else `-1`.
	     * @example
	     *
	     * _.sortedIndexOf([4, 5, 5, 5, 6], 5);
	     * // => 1
	     */
	    function sortedIndexOf(array, value) {
	      var length = array == null ? 0 : array.length;
	      if (length) {
	        var index = baseSortedIndex(array, value);
	        if (index < length && eq(array[index], value)) {
	          return index;
	        }
	      }
	      return -1;
	    }

	    /**
	     * This method is like `_.sortedIndex` except that it returns the highest
	     * index at which `value` should be inserted into `array` in order to
	     * maintain its sort order.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The sorted array to inspect.
	     * @param {*} value The value to evaluate.
	     * @returns {number} Returns the index at which `value` should be inserted
	     *  into `array`.
	     * @example
	     *
	     * _.sortedLastIndex([4, 5, 5, 5, 6], 5);
	     * // => 4
	     */
	    function sortedLastIndex(array, value) {
	      return baseSortedIndex(array, value, true);
	    }

	    /**
	     * This method is like `_.sortedLastIndex` except that it accepts `iteratee`
	     * which is invoked for `value` and each element of `array` to compute their
	     * sort ranking. The iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The sorted array to inspect.
	     * @param {*} value The value to evaluate.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {number} Returns the index at which `value` should be inserted
	     *  into `array`.
	     * @example
	     *
	     * var objects = [{ 'x': 4 }, { 'x': 5 }];
	     *
	     * _.sortedLastIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
	     * // => 1
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.sortedLastIndexBy(objects, { 'x': 4 }, 'x');
	     * // => 1
	     */
	    function sortedLastIndexBy(array, value, iteratee) {
	      return baseSortedIndexBy(array, value, getIteratee(iteratee, 2), true);
	    }

	    /**
	     * This method is like `_.lastIndexOf` except that it performs a binary
	     * search on a sorted `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {*} value The value to search for.
	     * @returns {number} Returns the index of the matched value, else `-1`.
	     * @example
	     *
	     * _.sortedLastIndexOf([4, 5, 5, 5, 6], 5);
	     * // => 3
	     */
	    function sortedLastIndexOf(array, value) {
	      var length = array == null ? 0 : array.length;
	      if (length) {
	        var index = baseSortedIndex(array, value, true) - 1;
	        if (eq(array[index], value)) {
	          return index;
	        }
	      }
	      return -1;
	    }

	    /**
	     * This method is like `_.uniq` except that it's designed and optimized
	     * for sorted arrays.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @returns {Array} Returns the new duplicate free array.
	     * @example
	     *
	     * _.sortedUniq([1, 1, 2]);
	     * // => [1, 2]
	     */
	    function sortedUniq(array) {
	      return (array && array.length)
	        ? baseSortedUniq(array)
	        : [];
	    }

	    /**
	     * This method is like `_.uniqBy` except that it's designed and optimized
	     * for sorted arrays.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {Function} [iteratee] The iteratee invoked per element.
	     * @returns {Array} Returns the new duplicate free array.
	     * @example
	     *
	     * _.sortedUniqBy([1.1, 1.2, 2.3, 2.4], Math.floor);
	     * // => [1.1, 2.3]
	     */
	    function sortedUniqBy(array, iteratee) {
	      return (array && array.length)
	        ? baseSortedUniq(array, getIteratee(iteratee, 2))
	        : [];
	    }

	    /**
	     * Gets all but the first element of `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * _.tail([1, 2, 3]);
	     * // => [2, 3]
	     */
	    function tail(array) {
	      var length = array == null ? 0 : array.length;
	      return length ? baseSlice(array, 1, length) : [];
	    }

	    /**
	     * Creates a slice of `array` with `n` elements taken from the beginning.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {number} [n=1] The number of elements to take.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * _.take([1, 2, 3]);
	     * // => [1]
	     *
	     * _.take([1, 2, 3], 2);
	     * // => [1, 2]
	     *
	     * _.take([1, 2, 3], 5);
	     * // => [1, 2, 3]
	     *
	     * _.take([1, 2, 3], 0);
	     * // => []
	     */
	    function take(array, n, guard) {
	      if (!(array && array.length)) {
	        return [];
	      }
	      n = (guard || n === undefined$1) ? 1 : toInteger(n);
	      return baseSlice(array, 0, n < 0 ? 0 : n);
	    }

	    /**
	     * Creates a slice of `array` with `n` elements taken from the end.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {number} [n=1] The number of elements to take.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * _.takeRight([1, 2, 3]);
	     * // => [3]
	     *
	     * _.takeRight([1, 2, 3], 2);
	     * // => [2, 3]
	     *
	     * _.takeRight([1, 2, 3], 5);
	     * // => [1, 2, 3]
	     *
	     * _.takeRight([1, 2, 3], 0);
	     * // => []
	     */
	    function takeRight(array, n, guard) {
	      var length = array == null ? 0 : array.length;
	      if (!length) {
	        return [];
	      }
	      n = (guard || n === undefined$1) ? 1 : toInteger(n);
	      n = length - n;
	      return baseSlice(array, n < 0 ? 0 : n, length);
	    }

	    /**
	     * Creates a slice of `array` with elements taken from the end. Elements are
	     * taken until `predicate` returns falsey. The predicate is invoked with
	     * three arguments: (value, index, array).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'active': true },
	     *   { 'user': 'fred',    'active': false },
	     *   { 'user': 'pebbles', 'active': false }
	     * ];
	     *
	     * _.takeRightWhile(users, function(o) { return !o.active; });
	     * // => objects for ['fred', 'pebbles']
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.takeRightWhile(users, { 'user': 'pebbles', 'active': false });
	     * // => objects for ['pebbles']
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.takeRightWhile(users, ['active', false]);
	     * // => objects for ['fred', 'pebbles']
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.takeRightWhile(users, 'active');
	     * // => []
	     */
	    function takeRightWhile(array, predicate) {
	      return (array && array.length)
	        ? baseWhile(array, getIteratee(predicate, 3), false, true)
	        : [];
	    }

	    /**
	     * Creates a slice of `array` with elements taken from the beginning. Elements
	     * are taken until `predicate` returns falsey. The predicate is invoked with
	     * three arguments: (value, index, array).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Array
	     * @param {Array} array The array to query.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the slice of `array`.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'active': false },
	     *   { 'user': 'fred',    'active': false },
	     *   { 'user': 'pebbles', 'active': true }
	     * ];
	     *
	     * _.takeWhile(users, function(o) { return !o.active; });
	     * // => objects for ['barney', 'fred']
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.takeWhile(users, { 'user': 'barney', 'active': false });
	     * // => objects for ['barney']
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.takeWhile(users, ['active', false]);
	     * // => objects for ['barney', 'fred']
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.takeWhile(users, 'active');
	     * // => []
	     */
	    function takeWhile(array, predicate) {
	      return (array && array.length)
	        ? baseWhile(array, getIteratee(predicate, 3))
	        : [];
	    }

	    /**
	     * Creates an array of unique values, in order, from all given arrays using
	     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * for equality comparisons.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @returns {Array} Returns the new array of combined values.
	     * @example
	     *
	     * _.union([2], [1, 2]);
	     * // => [2, 1]
	     */
	    var union = baseRest(function(arrays) {
	      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true));
	    });

	    /**
	     * This method is like `_.union` except that it accepts `iteratee` which is
	     * invoked for each element of each `arrays` to generate the criterion by
	     * which uniqueness is computed. Result values are chosen from the first
	     * array in which the value occurs. The iteratee is invoked with one argument:
	     * (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {Array} Returns the new array of combined values.
	     * @example
	     *
	     * _.unionBy([2.1], [1.2, 2.3], Math.floor);
	     * // => [2.1, 1.2]
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.unionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
	     * // => [{ 'x': 1 }, { 'x': 2 }]
	     */
	    var unionBy = baseRest(function(arrays) {
	      var iteratee = last(arrays);
	      if (isArrayLikeObject(iteratee)) {
	        iteratee = undefined$1;
	      }
	      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), getIteratee(iteratee, 2));
	    });

	    /**
	     * This method is like `_.union` except that it accepts `comparator` which
	     * is invoked to compare elements of `arrays`. Result values are chosen from
	     * the first array in which the value occurs. The comparator is invoked
	     * with two arguments: (arrVal, othVal).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new array of combined values.
	     * @example
	     *
	     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
	     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
	     *
	     * _.unionWith(objects, others, _.isEqual);
	     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
	     */
	    var unionWith = baseRest(function(arrays) {
	      var comparator = last(arrays);
	      comparator = typeof comparator == 'function' ? comparator : undefined$1;
	      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), undefined$1, comparator);
	    });

	    /**
	     * Creates a duplicate-free version of an array, using
	     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * for equality comparisons, in which only the first occurrence of each element
	     * is kept. The order of result values is determined by the order they occur
	     * in the array.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @returns {Array} Returns the new duplicate free array.
	     * @example
	     *
	     * _.uniq([2, 1, 2]);
	     * // => [2, 1]
	     */
	    function uniq(array) {
	      return (array && array.length) ? baseUniq(array) : [];
	    }

	    /**
	     * This method is like `_.uniq` except that it accepts `iteratee` which is
	     * invoked for each element in `array` to generate the criterion by which
	     * uniqueness is computed. The order of result values is determined by the
	     * order they occur in the array. The iteratee is invoked with one argument:
	     * (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {Array} Returns the new duplicate free array.
	     * @example
	     *
	     * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
	     * // => [2.1, 1.2]
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
	     * // => [{ 'x': 1 }, { 'x': 2 }]
	     */
	    function uniqBy(array, iteratee) {
	      return (array && array.length) ? baseUniq(array, getIteratee(iteratee, 2)) : [];
	    }

	    /**
	     * This method is like `_.uniq` except that it accepts `comparator` which
	     * is invoked to compare elements of `array`. The order of result values is
	     * determined by the order they occur in the array.The comparator is invoked
	     * with two arguments: (arrVal, othVal).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new duplicate free array.
	     * @example
	     *
	     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 2 }];
	     *
	     * _.uniqWith(objects, _.isEqual);
	     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }]
	     */
	    function uniqWith(array, comparator) {
	      comparator = typeof comparator == 'function' ? comparator : undefined$1;
	      return (array && array.length) ? baseUniq(array, undefined$1, comparator) : [];
	    }

	    /**
	     * This method is like `_.zip` except that it accepts an array of grouped
	     * elements and creates an array regrouping the elements to their pre-zip
	     * configuration.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.2.0
	     * @category Array
	     * @param {Array} array The array of grouped elements to process.
	     * @returns {Array} Returns the new array of regrouped elements.
	     * @example
	     *
	     * var zipped = _.zip(['a', 'b'], [1, 2], [true, false]);
	     * // => [['a', 1, true], ['b', 2, false]]
	     *
	     * _.unzip(zipped);
	     * // => [['a', 'b'], [1, 2], [true, false]]
	     */
	    function unzip(array) {
	      if (!(array && array.length)) {
	        return [];
	      }
	      var length = 0;
	      array = arrayFilter(array, function(group) {
	        if (isArrayLikeObject(group)) {
	          length = nativeMax(group.length, length);
	          return true;
	        }
	      });
	      return baseTimes(length, function(index) {
	        return arrayMap(array, baseProperty(index));
	      });
	    }

	    /**
	     * This method is like `_.unzip` except that it accepts `iteratee` to specify
	     * how regrouped values should be combined. The iteratee is invoked with the
	     * elements of each group: (...group).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.8.0
	     * @category Array
	     * @param {Array} array The array of grouped elements to process.
	     * @param {Function} [iteratee=_.identity] The function to combine
	     *  regrouped values.
	     * @returns {Array} Returns the new array of regrouped elements.
	     * @example
	     *
	     * var zipped = _.zip([1, 2], [10, 20], [100, 200]);
	     * // => [[1, 10, 100], [2, 20, 200]]
	     *
	     * _.unzipWith(zipped, _.add);
	     * // => [3, 30, 300]
	     */
	    function unzipWith(array, iteratee) {
	      if (!(array && array.length)) {
	        return [];
	      }
	      var result = unzip(array);
	      if (iteratee == null) {
	        return result;
	      }
	      return arrayMap(result, function(group) {
	        return apply(iteratee, undefined$1, group);
	      });
	    }

	    /**
	     * Creates an array excluding all given values using
	     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * for equality comparisons.
	     *
	     * **Note:** Unlike `_.pull`, this method returns a new array.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {Array} array The array to inspect.
	     * @param {...*} [values] The values to exclude.
	     * @returns {Array} Returns the new array of filtered values.
	     * @see _.difference, _.xor
	     * @example
	     *
	     * _.without([2, 1, 2, 3], 1, 2);
	     * // => [3]
	     */
	    var without = baseRest(function(array, values) {
	      return isArrayLikeObject(array)
	        ? baseDifference(array, values)
	        : [];
	    });

	    /**
	     * Creates an array of unique values that is the
	     * [symmetric difference](https://en.wikipedia.org/wiki/Symmetric_difference)
	     * of the given arrays. The order of result values is determined by the order
	     * they occur in the arrays.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.4.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @returns {Array} Returns the new array of filtered values.
	     * @see _.difference, _.without
	     * @example
	     *
	     * _.xor([2, 1], [2, 3]);
	     * // => [1, 3]
	     */
	    var xor = baseRest(function(arrays) {
	      return baseXor(arrayFilter(arrays, isArrayLikeObject));
	    });

	    /**
	     * This method is like `_.xor` except that it accepts `iteratee` which is
	     * invoked for each element of each `arrays` to generate the criterion by
	     * which by which they're compared. The order of result values is determined
	     * by the order they occur in the arrays. The iteratee is invoked with one
	     * argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {Array} Returns the new array of filtered values.
	     * @example
	     *
	     * _.xorBy([2.1, 1.2], [2.3, 3.4], Math.floor);
	     * // => [1.2, 3.4]
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.xorBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
	     * // => [{ 'x': 2 }]
	     */
	    var xorBy = baseRest(function(arrays) {
	      var iteratee = last(arrays);
	      if (isArrayLikeObject(iteratee)) {
	        iteratee = undefined$1;
	      }
	      return baseXor(arrayFilter(arrays, isArrayLikeObject), getIteratee(iteratee, 2));
	    });

	    /**
	     * This method is like `_.xor` except that it accepts `comparator` which is
	     * invoked to compare elements of `arrays`. The order of result values is
	     * determined by the order they occur in the arrays. The comparator is invoked
	     * with two arguments: (arrVal, othVal).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to inspect.
	     * @param {Function} [comparator] The comparator invoked per element.
	     * @returns {Array} Returns the new array of filtered values.
	     * @example
	     *
	     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
	     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
	     *
	     * _.xorWith(objects, others, _.isEqual);
	     * // => [{ 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
	     */
	    var xorWith = baseRest(function(arrays) {
	      var comparator = last(arrays);
	      comparator = typeof comparator == 'function' ? comparator : undefined$1;
	      return baseXor(arrayFilter(arrays, isArrayLikeObject), undefined$1, comparator);
	    });

	    /**
	     * Creates an array of grouped elements, the first of which contains the
	     * first elements of the given arrays, the second of which contains the
	     * second elements of the given arrays, and so on.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to process.
	     * @returns {Array} Returns the new array of grouped elements.
	     * @example
	     *
	     * _.zip(['a', 'b'], [1, 2], [true, false]);
	     * // => [['a', 1, true], ['b', 2, false]]
	     */
	    var zip = baseRest(unzip);

	    /**
	     * This method is like `_.fromPairs` except that it accepts two arrays,
	     * one of property identifiers and one of corresponding values.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.4.0
	     * @category Array
	     * @param {Array} [props=[]] The property identifiers.
	     * @param {Array} [values=[]] The property values.
	     * @returns {Object} Returns the new object.
	     * @example
	     *
	     * _.zipObject(['a', 'b'], [1, 2]);
	     * // => { 'a': 1, 'b': 2 }
	     */
	    function zipObject(props, values) {
	      return baseZipObject(props || [], values || [], assignValue);
	    }

	    /**
	     * This method is like `_.zipObject` except that it supports property paths.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.1.0
	     * @category Array
	     * @param {Array} [props=[]] The property identifiers.
	     * @param {Array} [values=[]] The property values.
	     * @returns {Object} Returns the new object.
	     * @example
	     *
	     * _.zipObjectDeep(['a.b[0].c', 'a.b[1].d'], [1, 2]);
	     * // => { 'a': { 'b': [{ 'c': 1 }, { 'd': 2 }] } }
	     */
	    function zipObjectDeep(props, values) {
	      return baseZipObject(props || [], values || [], baseSet);
	    }

	    /**
	     * This method is like `_.zip` except that it accepts `iteratee` to specify
	     * how grouped values should be combined. The iteratee is invoked with the
	     * elements of each group: (...group).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.8.0
	     * @category Array
	     * @param {...Array} [arrays] The arrays to process.
	     * @param {Function} [iteratee=_.identity] The function to combine
	     *  grouped values.
	     * @returns {Array} Returns the new array of grouped elements.
	     * @example
	     *
	     * _.zipWith([1, 2], [10, 20], [100, 200], function(a, b, c) {
	     *   return a + b + c;
	     * });
	     * // => [111, 222]
	     */
	    var zipWith = baseRest(function(arrays) {
	      var length = arrays.length,
	          iteratee = length > 1 ? arrays[length - 1] : undefined$1;

	      iteratee = typeof iteratee == 'function' ? (arrays.pop(), iteratee) : undefined$1;
	      return unzipWith(arrays, iteratee);
	    });

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates a `lodash` wrapper instance that wraps `value` with explicit method
	     * chain sequences enabled. The result of such sequences must be unwrapped
	     * with `_#value`.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.3.0
	     * @category Seq
	     * @param {*} value The value to wrap.
	     * @returns {Object} Returns the new `lodash` wrapper instance.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'age': 36 },
	     *   { 'user': 'fred',    'age': 40 },
	     *   { 'user': 'pebbles', 'age': 1 }
	     * ];
	     *
	     * var youngest = _
	     *   .chain(users)
	     *   .sortBy('age')
	     *   .map(function(o) {
	     *     return o.user + ' is ' + o.age;
	     *   })
	     *   .head()
	     *   .value();
	     * // => 'pebbles is 1'
	     */
	    function chain(value) {
	      var result = lodash(value);
	      result.__chain__ = true;
	      return result;
	    }

	    /**
	     * This method invokes `interceptor` and returns `value`. The interceptor
	     * is invoked with one argument; (value). The purpose of this method is to
	     * "tap into" a method chain sequence in order to modify intermediate results.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Seq
	     * @param {*} value The value to provide to `interceptor`.
	     * @param {Function} interceptor The function to invoke.
	     * @returns {*} Returns `value`.
	     * @example
	     *
	     * _([1, 2, 3])
	     *  .tap(function(array) {
	     *    // Mutate input array.
	     *    array.pop();
	     *  })
	     *  .reverse()
	     *  .value();
	     * // => [2, 1]
	     */
	    function tap(value, interceptor) {
	      interceptor(value);
	      return value;
	    }

	    /**
	     * This method is like `_.tap` except that it returns the result of `interceptor`.
	     * The purpose of this method is to "pass thru" values replacing intermediate
	     * results in a method chain sequence.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Seq
	     * @param {*} value The value to provide to `interceptor`.
	     * @param {Function} interceptor The function to invoke.
	     * @returns {*} Returns the result of `interceptor`.
	     * @example
	     *
	     * _('  abc  ')
	     *  .chain()
	     *  .trim()
	     *  .thru(function(value) {
	     *    return [value];
	     *  })
	     *  .value();
	     * // => ['abc']
	     */
	    function thru(value, interceptor) {
	      return interceptor(value);
	    }

	    /**
	     * This method is the wrapper version of `_.at`.
	     *
	     * @name at
	     * @memberOf _
	     * @since 1.0.0
	     * @category Seq
	     * @param {...(string|string[])} [paths] The property paths to pick.
	     * @returns {Object} Returns the new `lodash` wrapper instance.
	     * @example
	     *
	     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
	     *
	     * _(object).at(['a[0].b.c', 'a[1]']).value();
	     * // => [3, 4]
	     */
	    var wrapperAt = flatRest(function(paths) {
	      var length = paths.length,
	          start = length ? paths[0] : 0,
	          value = this.__wrapped__,
	          interceptor = function(object) { return baseAt(object, paths); };

	      if (length > 1 || this.__actions__.length ||
	          !(value instanceof LazyWrapper) || !isIndex(start)) {
	        return this.thru(interceptor);
	      }
	      value = value.slice(start, +start + (length ? 1 : 0));
	      value.__actions__.push({
	        'func': thru,
	        'args': [interceptor],
	        'thisArg': undefined$1
	      });
	      return new LodashWrapper(value, this.__chain__).thru(function(array) {
	        if (length && !array.length) {
	          array.push(undefined$1);
	        }
	        return array;
	      });
	    });

	    /**
	     * Creates a `lodash` wrapper instance with explicit method chain sequences enabled.
	     *
	     * @name chain
	     * @memberOf _
	     * @since 0.1.0
	     * @category Seq
	     * @returns {Object} Returns the new `lodash` wrapper instance.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney', 'age': 36 },
	     *   { 'user': 'fred',   'age': 40 }
	     * ];
	     *
	     * // A sequence without explicit chaining.
	     * _(users).head();
	     * // => { 'user': 'barney', 'age': 36 }
	     *
	     * // A sequence with explicit chaining.
	     * _(users)
	     *   .chain()
	     *   .head()
	     *   .pick('user')
	     *   .value();
	     * // => { 'user': 'barney' }
	     */
	    function wrapperChain() {
	      return chain(this);
	    }

	    /**
	     * Executes the chain sequence and returns the wrapped result.
	     *
	     * @name commit
	     * @memberOf _
	     * @since 3.2.0
	     * @category Seq
	     * @returns {Object} Returns the new `lodash` wrapper instance.
	     * @example
	     *
	     * var array = [1, 2];
	     * var wrapped = _(array).push(3);
	     *
	     * console.log(array);
	     * // => [1, 2]
	     *
	     * wrapped = wrapped.commit();
	     * console.log(array);
	     * // => [1, 2, 3]
	     *
	     * wrapped.last();
	     * // => 3
	     *
	     * console.log(array);
	     * // => [1, 2, 3]
	     */
	    function wrapperCommit() {
	      return new LodashWrapper(this.value(), this.__chain__);
	    }

	    /**
	     * Gets the next value on a wrapped object following the
	     * [iterator protocol](https://mdn.io/iteration_protocols#iterator).
	     *
	     * @name next
	     * @memberOf _
	     * @since 4.0.0
	     * @category Seq
	     * @returns {Object} Returns the next iterator value.
	     * @example
	     *
	     * var wrapped = _([1, 2]);
	     *
	     * wrapped.next();
	     * // => { 'done': false, 'value': 1 }
	     *
	     * wrapped.next();
	     * // => { 'done': false, 'value': 2 }
	     *
	     * wrapped.next();
	     * // => { 'done': true, 'value': undefined }
	     */
	    function wrapperNext() {
	      if (this.__values__ === undefined$1) {
	        this.__values__ = toArray(this.value());
	      }
	      var done = this.__index__ >= this.__values__.length,
	          value = done ? undefined$1 : this.__values__[this.__index__++];

	      return { 'done': done, 'value': value };
	    }

	    /**
	     * Enables the wrapper to be iterable.
	     *
	     * @name Symbol.iterator
	     * @memberOf _
	     * @since 4.0.0
	     * @category Seq
	     * @returns {Object} Returns the wrapper object.
	     * @example
	     *
	     * var wrapped = _([1, 2]);
	     *
	     * wrapped[Symbol.iterator]() === wrapped;
	     * // => true
	     *
	     * Array.from(wrapped);
	     * // => [1, 2]
	     */
	    function wrapperToIterator() {
	      return this;
	    }

	    /**
	     * Creates a clone of the chain sequence planting `value` as the wrapped value.
	     *
	     * @name plant
	     * @memberOf _
	     * @since 3.2.0
	     * @category Seq
	     * @param {*} value The value to plant.
	     * @returns {Object} Returns the new `lodash` wrapper instance.
	     * @example
	     *
	     * function square(n) {
	     *   return n * n;
	     * }
	     *
	     * var wrapped = _([1, 2]).map(square);
	     * var other = wrapped.plant([3, 4]);
	     *
	     * other.value();
	     * // => [9, 16]
	     *
	     * wrapped.value();
	     * // => [1, 4]
	     */
	    function wrapperPlant(value) {
	      var result,
	          parent = this;

	      while (parent instanceof baseLodash) {
	        var clone = wrapperClone(parent);
	        clone.__index__ = 0;
	        clone.__values__ = undefined$1;
	        if (result) {
	          previous.__wrapped__ = clone;
	        } else {
	          result = clone;
	        }
	        var previous = clone;
	        parent = parent.__wrapped__;
	      }
	      previous.__wrapped__ = value;
	      return result;
	    }

	    /**
	     * This method is the wrapper version of `_.reverse`.
	     *
	     * **Note:** This method mutates the wrapped array.
	     *
	     * @name reverse
	     * @memberOf _
	     * @since 0.1.0
	     * @category Seq
	     * @returns {Object} Returns the new `lodash` wrapper instance.
	     * @example
	     *
	     * var array = [1, 2, 3];
	     *
	     * _(array).reverse().value()
	     * // => [3, 2, 1]
	     *
	     * console.log(array);
	     * // => [3, 2, 1]
	     */
	    function wrapperReverse() {
	      var value = this.__wrapped__;
	      if (value instanceof LazyWrapper) {
	        var wrapped = value;
	        if (this.__actions__.length) {
	          wrapped = new LazyWrapper(this);
	        }
	        wrapped = wrapped.reverse();
	        wrapped.__actions__.push({
	          'func': thru,
	          'args': [reverse],
	          'thisArg': undefined$1
	        });
	        return new LodashWrapper(wrapped, this.__chain__);
	      }
	      return this.thru(reverse);
	    }

	    /**
	     * Executes the chain sequence to resolve the unwrapped value.
	     *
	     * @name value
	     * @memberOf _
	     * @since 0.1.0
	     * @alias toJSON, valueOf
	     * @category Seq
	     * @returns {*} Returns the resolved unwrapped value.
	     * @example
	     *
	     * _([1, 2, 3]).value();
	     * // => [1, 2, 3]
	     */
	    function wrapperValue() {
	      return baseWrapperValue(this.__wrapped__, this.__actions__);
	    }

	    /*------------------------------------------------------------------------*/

	    /**
	     * Creates an object composed of keys generated from the results of running
	     * each element of `collection` thru `iteratee`. The corresponding value of
	     * each key is the number of times the key was returned by `iteratee`. The
	     * iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.5.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
	     * @returns {Object} Returns the composed aggregate object.
	     * @example
	     *
	     * _.countBy([6.1, 4.2, 6.3], Math.floor);
	     * // => { '4': 1, '6': 2 }
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.countBy(['one', 'two', 'three'], 'length');
	     * // => { '3': 2, '5': 1 }
	     */
	    var countBy = createAggregator(function(result, value, key) {
	      if (hasOwnProperty.call(result, key)) {
	        ++result[key];
	      } else {
	        baseAssignValue(result, key, 1);
	      }
	    });

	    /**
	     * Checks if `predicate` returns truthy for **all** elements of `collection`.
	     * Iteration is stopped once `predicate` returns falsey. The predicate is
	     * invoked with three arguments: (value, index|key, collection).
	     *
	     * **Note:** This method returns `true` for
	     * [empty collections](https://en.wikipedia.org/wiki/Empty_set) because
	     * [everything is true](https://en.wikipedia.org/wiki/Vacuous_truth) of
	     * elements of empty collections.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {boolean} Returns `true` if all elements pass the predicate check,
	     *  else `false`.
	     * @example
	     *
	     * _.every([true, 1, null, 'yes'], Boolean);
	     * // => false
	     *
	     * var users = [
	     *   { 'user': 'barney', 'age': 36, 'active': false },
	     *   { 'user': 'fred',   'age': 40, 'active': false }
	     * ];
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.every(users, { 'user': 'barney', 'active': false });
	     * // => false
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.every(users, ['active', false]);
	     * // => true
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.every(users, 'active');
	     * // => false
	     */
	    function every(collection, predicate, guard) {
	      var func = isArray(collection) ? arrayEvery : baseEvery;
	      if (guard && isIterateeCall(collection, predicate, guard)) {
	        predicate = undefined$1;
	      }
	      return func(collection, getIteratee(predicate, 3));
	    }

	    /**
	     * Iterates over elements of `collection`, returning an array of all elements
	     * `predicate` returns truthy for. The predicate is invoked with three
	     * arguments: (value, index|key, collection).
	     *
	     * **Note:** Unlike `_.remove`, this method returns a new array.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the new filtered array.
	     * @see _.reject
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney', 'age': 36, 'active': true },
	     *   { 'user': 'fred',   'age': 40, 'active': false }
	     * ];
	     *
	     * _.filter(users, function(o) { return !o.active; });
	     * // => objects for ['fred']
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.filter(users, { 'age': 36, 'active': true });
	     * // => objects for ['barney']
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.filter(users, ['active', false]);
	     * // => objects for ['fred']
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.filter(users, 'active');
	     * // => objects for ['barney']
	     *
	     * // Combining several predicates using `_.overEvery` or `_.overSome`.
	     * _.filter(users, _.overSome([{ 'age': 36 }, ['age', 40]]));
	     * // => objects for ['fred', 'barney']
	     */
	    function filter(collection, predicate) {
	      var func = isArray(collection) ? arrayFilter : baseFilter;
	      return func(collection, getIteratee(predicate, 3));
	    }

	    /**
	     * Iterates over elements of `collection`, returning the first element
	     * `predicate` returns truthy for. The predicate is invoked with three
	     * arguments: (value, index|key, collection).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to inspect.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @param {number} [fromIndex=0] The index to search from.
	     * @returns {*} Returns the matched element, else `undefined`.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'age': 36, 'active': true },
	     *   { 'user': 'fred',    'age': 40, 'active': false },
	     *   { 'user': 'pebbles', 'age': 1,  'active': true }
	     * ];
	     *
	     * _.find(users, function(o) { return o.age < 40; });
	     * // => object for 'barney'
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.find(users, { 'age': 1, 'active': true });
	     * // => object for 'pebbles'
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.find(users, ['active', false]);
	     * // => object for 'fred'
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.find(users, 'active');
	     * // => object for 'barney'
	     */
	    var find = createFind(findIndex);

	    /**
	     * This method is like `_.find` except that it iterates over elements of
	     * `collection` from right to left.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to inspect.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @param {number} [fromIndex=collection.length-1] The index to search from.
	     * @returns {*} Returns the matched element, else `undefined`.
	     * @example
	     *
	     * _.findLast([1, 2, 3, 4], function(n) {
	     *   return n % 2 == 1;
	     * });
	     * // => 3
	     */
	    var findLast = createFind(findLastIndex);

	    /**
	     * Creates a flattened array of values by running each element in `collection`
	     * thru `iteratee` and flattening the mapped results. The iteratee is invoked
	     * with three arguments: (value, index|key, collection).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the new flattened array.
	     * @example
	     *
	     * function duplicate(n) {
	     *   return [n, n];
	     * }
	     *
	     * _.flatMap([1, 2], duplicate);
	     * // => [1, 1, 2, 2]
	     */
	    function flatMap(collection, iteratee) {
	      return baseFlatten(map(collection, iteratee), 1);
	    }

	    /**
	     * This method is like `_.flatMap` except that it recursively flattens the
	     * mapped results.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.7.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the new flattened array.
	     * @example
	     *
	     * function duplicate(n) {
	     *   return [[[n, n]]];
	     * }
	     *
	     * _.flatMapDeep([1, 2], duplicate);
	     * // => [1, 1, 2, 2]
	     */
	    function flatMapDeep(collection, iteratee) {
	      return baseFlatten(map(collection, iteratee), INFINITY);
	    }

	    /**
	     * This method is like `_.flatMap` except that it recursively flattens the
	     * mapped results up to `depth` times.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.7.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @param {number} [depth=1] The maximum recursion depth.
	     * @returns {Array} Returns the new flattened array.
	     * @example
	     *
	     * function duplicate(n) {
	     *   return [[[n, n]]];
	     * }
	     *
	     * _.flatMapDepth([1, 2], duplicate, 2);
	     * // => [[1, 1], [2, 2]]
	     */
	    function flatMapDepth(collection, iteratee, depth) {
	      depth = depth === undefined$1 ? 1 : toInteger(depth);
	      return baseFlatten(map(collection, iteratee), depth);
	    }

	    /**
	     * Iterates over elements of `collection` and invokes `iteratee` for each element.
	     * The iteratee is invoked with three arguments: (value, index|key, collection).
	     * Iteratee functions may exit iteration early by explicitly returning `false`.
	     *
	     * **Note:** As with other "Collections" methods, objects with a "length"
	     * property are iterated like arrays. To avoid this behavior use `_.forIn`
	     * or `_.forOwn` for object iteration.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @alias each
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Array|Object} Returns `collection`.
	     * @see _.forEachRight
	     * @example
	     *
	     * _.forEach([1, 2], function(value) {
	     *   console.log(value);
	     * });
	     * // => Logs `1` then `2`.
	     *
	     * _.forEach({ 'a': 1, 'b': 2 }, function(value, key) {
	     *   console.log(key);
	     * });
	     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
	     */
	    function forEach(collection, iteratee) {
	      var func = isArray(collection) ? arrayEach : baseEach;
	      return func(collection, getIteratee(iteratee, 3));
	    }

	    /**
	     * This method is like `_.forEach` except that it iterates over elements of
	     * `collection` from right to left.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @alias eachRight
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Array|Object} Returns `collection`.
	     * @see _.forEach
	     * @example
	     *
	     * _.forEachRight([1, 2], function(value) {
	     *   console.log(value);
	     * });
	     * // => Logs `2` then `1`.
	     */
	    function forEachRight(collection, iteratee) {
	      var func = isArray(collection) ? arrayEachRight : baseEachRight;
	      return func(collection, getIteratee(iteratee, 3));
	    }

	    /**
	     * Creates an object composed of keys generated from the results of running
	     * each element of `collection` thru `iteratee`. The order of grouped values
	     * is determined by the order they occur in `collection`. The corresponding
	     * value of each key is an array of elements responsible for generating the
	     * key. The iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
	     * @returns {Object} Returns the composed aggregate object.
	     * @example
	     *
	     * _.groupBy([6.1, 4.2, 6.3], Math.floor);
	     * // => { '4': [4.2], '6': [6.1, 6.3] }
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.groupBy(['one', 'two', 'three'], 'length');
	     * // => { '3': ['one', 'two'], '5': ['three'] }
	     */
	    var groupBy = createAggregator(function(result, value, key) {
	      if (hasOwnProperty.call(result, key)) {
	        result[key].push(value);
	      } else {
	        baseAssignValue(result, key, [value]);
	      }
	    });

	    /**
	     * Checks if `value` is in `collection`. If `collection` is a string, it's
	     * checked for a substring of `value`, otherwise
	     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * is used for equality comparisons. If `fromIndex` is negative, it's used as
	     * the offset from the end of `collection`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object|string} collection The collection to inspect.
	     * @param {*} value The value to search for.
	     * @param {number} [fromIndex=0] The index to search from.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
	     * @returns {boolean} Returns `true` if `value` is found, else `false`.
	     * @example
	     *
	     * _.includes([1, 2, 3], 1);
	     * // => true
	     *
	     * _.includes([1, 2, 3], 1, 2);
	     * // => false
	     *
	     * _.includes({ 'a': 1, 'b': 2 }, 1);
	     * // => true
	     *
	     * _.includes('abcd', 'bc');
	     * // => true
	     */
	    function includes(collection, value, fromIndex, guard) {
	      collection = isArrayLike(collection) ? collection : values(collection);
	      fromIndex = (fromIndex && !guard) ? toInteger(fromIndex) : 0;

	      var length = collection.length;
	      if (fromIndex < 0) {
	        fromIndex = nativeMax(length + fromIndex, 0);
	      }
	      return isString(collection)
	        ? (fromIndex <= length && collection.indexOf(value, fromIndex) > -1)
	        : (!!length && baseIndexOf(collection, value, fromIndex) > -1);
	    }

	    /**
	     * Invokes the method at `path` of each element in `collection`, returning
	     * an array of the results of each invoked method. Any additional arguments
	     * are provided to each invoked method. If `path` is a function, it's invoked
	     * for, and `this` bound to, each element in `collection`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Array|Function|string} path The path of the method to invoke or
	     *  the function invoked per iteration.
	     * @param {...*} [args] The arguments to invoke each method with.
	     * @returns {Array} Returns the array of results.
	     * @example
	     *
	     * _.invokeMap([[5, 1, 7], [3, 2, 1]], 'sort');
	     * // => [[1, 5, 7], [1, 2, 3]]
	     *
	     * _.invokeMap([123, 456], String.prototype.split, '');
	     * // => [['1', '2', '3'], ['4', '5', '6']]
	     */
	    var invokeMap = baseRest(function(collection, path, args) {
	      var index = -1,
	          isFunc = typeof path == 'function',
	          result = isArrayLike(collection) ? Array(collection.length) : [];

	      baseEach(collection, function(value) {
	        result[++index] = isFunc ? apply(path, value, args) : baseInvoke(value, path, args);
	      });
	      return result;
	    });

	    /**
	     * Creates an object composed of keys generated from the results of running
	     * each element of `collection` thru `iteratee`. The corresponding value of
	     * each key is the last element responsible for generating the key. The
	     * iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
	     * @returns {Object} Returns the composed aggregate object.
	     * @example
	     *
	     * var array = [
	     *   { 'dir': 'left', 'code': 97 },
	     *   { 'dir': 'right', 'code': 100 }
	     * ];
	     *
	     * _.keyBy(array, function(o) {
	     *   return String.fromCharCode(o.code);
	     * });
	     * // => { 'a': { 'dir': 'left', 'code': 97 }, 'd': { 'dir': 'right', 'code': 100 } }
	     *
	     * _.keyBy(array, 'dir');
	     * // => { 'left': { 'dir': 'left', 'code': 97 }, 'right': { 'dir': 'right', 'code': 100 } }
	     */
	    var keyBy = createAggregator(function(result, value, key) {
	      baseAssignValue(result, key, value);
	    });

	    /**
	     * Creates an array of values by running each element in `collection` thru
	     * `iteratee`. The iteratee is invoked with three arguments:
	     * (value, index|key, collection).
	     *
	     * Many lodash methods are guarded to work as iteratees for methods like
	     * `_.every`, `_.filter`, `_.map`, `_.mapValues`, `_.reject`, and `_.some`.
	     *
	     * The guarded methods are:
	     * `ary`, `chunk`, `curry`, `curryRight`, `drop`, `dropRight`, `every`,
	     * `fill`, `invert`, `parseInt`, `random`, `range`, `rangeRight`, `repeat`,
	     * `sampleSize`, `slice`, `some`, `sortBy`, `split`, `take`, `takeRight`,
	     * `template`, `trim`, `trimEnd`, `trimStart`, and `words`
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the new mapped array.
	     * @example
	     *
	     * function square(n) {
	     *   return n * n;
	     * }
	     *
	     * _.map([4, 8], square);
	     * // => [16, 64]
	     *
	     * _.map({ 'a': 4, 'b': 8 }, square);
	     * // => [16, 64] (iteration order is not guaranteed)
	     *
	     * var users = [
	     *   { 'user': 'barney' },
	     *   { 'user': 'fred' }
	     * ];
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.map(users, 'user');
	     * // => ['barney', 'fred']
	     */
	    function map(collection, iteratee) {
	      var func = isArray(collection) ? arrayMap : baseMap;
	      return func(collection, getIteratee(iteratee, 3));
	    }

	    /**
	     * This method is like `_.sortBy` except that it allows specifying the sort
	     * orders of the iteratees to sort by. If `orders` is unspecified, all values
	     * are sorted in ascending order. Otherwise, specify an order of "desc" for
	     * descending or "asc" for ascending sort order of corresponding values.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Array[]|Function[]|Object[]|string[]} [iteratees=[_.identity]]
	     *  The iteratees to sort by.
	     * @param {string[]} [orders] The sort orders of `iteratees`.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
	     * @returns {Array} Returns the new sorted array.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'fred',   'age': 48 },
	     *   { 'user': 'barney', 'age': 34 },
	     *   { 'user': 'fred',   'age': 40 },
	     *   { 'user': 'barney', 'age': 36 }
	     * ];
	     *
	     * // Sort by `user` in ascending order and by `age` in descending order.
	     * _.orderBy(users, ['user', 'age'], ['asc', 'desc']);
	     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 40]]
	     */
	    function orderBy(collection, iteratees, orders, guard) {
	      if (collection == null) {
	        return [];
	      }
	      if (!isArray(iteratees)) {
	        iteratees = iteratees == null ? [] : [iteratees];
	      }
	      orders = guard ? undefined$1 : orders;
	      if (!isArray(orders)) {
	        orders = orders == null ? [] : [orders];
	      }
	      return baseOrderBy(collection, iteratees, orders);
	    }

	    /**
	     * Creates an array of elements split into two groups, the first of which
	     * contains elements `predicate` returns truthy for, the second of which
	     * contains elements `predicate` returns falsey for. The predicate is
	     * invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the array of grouped elements.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney',  'age': 36, 'active': false },
	     *   { 'user': 'fred',    'age': 40, 'active': true },
	     *   { 'user': 'pebbles', 'age': 1,  'active': false }
	     * ];
	     *
	     * _.partition(users, function(o) { return o.active; });
	     * // => objects for [['fred'], ['barney', 'pebbles']]
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.partition(users, { 'age': 1, 'active': false });
	     * // => objects for [['pebbles'], ['barney', 'fred']]
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.partition(users, ['active', false]);
	     * // => objects for [['barney', 'pebbles'], ['fred']]
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.partition(users, 'active');
	     * // => objects for [['fred'], ['barney', 'pebbles']]
	     */
	    var partition = createAggregator(function(result, value, key) {
	      result[key ? 0 : 1].push(value);
	    }, function() { return [[], []]; });

	    /**
	     * Reduces `collection` to a value which is the accumulated result of running
	     * each element in `collection` thru `iteratee`, where each successive
	     * invocation is supplied the return value of the previous. If `accumulator`
	     * is not given, the first element of `collection` is used as the initial
	     * value. The iteratee is invoked with four arguments:
	     * (accumulator, value, index|key, collection).
	     *
	     * Many lodash methods are guarded to work as iteratees for methods like
	     * `_.reduce`, `_.reduceRight`, and `_.transform`.
	     *
	     * The guarded methods are:
	     * `assign`, `defaults`, `defaultsDeep`, `includes`, `merge`, `orderBy`,
	     * and `sortBy`
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @param {*} [accumulator] The initial value.
	     * @returns {*} Returns the accumulated value.
	     * @see _.reduceRight
	     * @example
	     *
	     * _.reduce([1, 2], function(sum, n) {
	     *   return sum + n;
	     * }, 0);
	     * // => 3
	     *
	     * _.reduce({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
	     *   (result[value] || (result[value] = [])).push(key);
	     *   return result;
	     * }, {});
	     * // => { '1': ['a', 'c'], '2': ['b'] } (iteration order is not guaranteed)
	     */
	    function reduce(collection, iteratee, accumulator) {
	      var func = isArray(collection) ? arrayReduce : baseReduce,
	          initAccum = arguments.length < 3;

	      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEach);
	    }

	    /**
	     * This method is like `_.reduce` except that it iterates over elements of
	     * `collection` from right to left.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @param {*} [accumulator] The initial value.
	     * @returns {*} Returns the accumulated value.
	     * @see _.reduce
	     * @example
	     *
	     * var array = [[0, 1], [2, 3], [4, 5]];
	     *
	     * _.reduceRight(array, function(flattened, other) {
	     *   return flattened.concat(other);
	     * }, []);
	     * // => [4, 5, 2, 3, 0, 1]
	     */
	    function reduceRight(collection, iteratee, accumulator) {
	      var func = isArray(collection) ? arrayReduceRight : baseReduce,
	          initAccum = arguments.length < 3;

	      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEachRight);
	    }

	    /**
	     * The opposite of `_.filter`; this method returns the elements of `collection`
	     * that `predicate` does **not** return truthy for.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the new filtered array.
	     * @see _.filter
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney', 'age': 36, 'active': false },
	     *   { 'user': 'fred',   'age': 40, 'active': true }
	     * ];
	     *
	     * _.reject(users, function(o) { return !o.active; });
	     * // => objects for ['fred']
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.reject(users, { 'age': 40, 'active': true });
	     * // => objects for ['barney']
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.reject(users, ['active', false]);
	     * // => objects for ['fred']
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.reject(users, 'active');
	     * // => objects for ['barney']
	     */
	    function reject(collection, predicate) {
	      var func = isArray(collection) ? arrayFilter : baseFilter;
	      return func(collection, negate(getIteratee(predicate, 3)));
	    }

	    /**
	     * Gets a random element from `collection`.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to sample.
	     * @returns {*} Returns the random element.
	     * @example
	     *
	     * _.sample([1, 2, 3, 4]);
	     * // => 2
	     */
	    function sample(collection) {
	      var func = isArray(collection) ? arraySample : baseSample;
	      return func(collection);
	    }

	    /**
	     * Gets `n` random elements at unique keys from `collection` up to the
	     * size of `collection`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to sample.
	     * @param {number} [n=1] The number of elements to sample.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Array} Returns the random elements.
	     * @example
	     *
	     * _.sampleSize([1, 2, 3], 2);
	     * // => [3, 1]
	     *
	     * _.sampleSize([1, 2, 3], 4);
	     * // => [2, 3, 1]
	     */
	    function sampleSize(collection, n, guard) {
	      if ((guard ? isIterateeCall(collection, n, guard) : n === undefined$1)) {
	        n = 1;
	      } else {
	        n = toInteger(n);
	      }
	      var func = isArray(collection) ? arraySampleSize : baseSampleSize;
	      return func(collection, n);
	    }

	    /**
	     * Creates an array of shuffled values, using a version of the
	     * [Fisher-Yates shuffle](https://en.wikipedia.org/wiki/Fisher-Yates_shuffle).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to shuffle.
	     * @returns {Array} Returns the new shuffled array.
	     * @example
	     *
	     * _.shuffle([1, 2, 3, 4]);
	     * // => [4, 1, 3, 2]
	     */
	    function shuffle(collection) {
	      var func = isArray(collection) ? arrayShuffle : baseShuffle;
	      return func(collection);
	    }

	    /**
	     * Gets the size of `collection` by returning its length for array-like
	     * values or the number of own enumerable string keyed properties for objects.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object|string} collection The collection to inspect.
	     * @returns {number} Returns the collection size.
	     * @example
	     *
	     * _.size([1, 2, 3]);
	     * // => 3
	     *
	     * _.size({ 'a': 1, 'b': 2 });
	     * // => 2
	     *
	     * _.size('pebbles');
	     * // => 7
	     */
	    function size(collection) {
	      if (collection == null) {
	        return 0;
	      }
	      if (isArrayLike(collection)) {
	        return isString(collection) ? stringSize(collection) : collection.length;
	      }
	      var tag = getTag(collection);
	      if (tag == mapTag || tag == setTag) {
	        return collection.size;
	      }
	      return baseKeys(collection).length;
	    }

	    /**
	     * Checks if `predicate` returns truthy for **any** element of `collection`.
	     * Iteration is stopped once `predicate` returns truthy. The predicate is
	     * invoked with three arguments: (value, index|key, collection).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {boolean} Returns `true` if any element passes the predicate check,
	     *  else `false`.
	     * @example
	     *
	     * _.some([null, 0, 'yes', false], Boolean);
	     * // => true
	     *
	     * var users = [
	     *   { 'user': 'barney', 'active': true },
	     *   { 'user': 'fred',   'active': false }
	     * ];
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.some(users, { 'user': 'barney', 'active': false });
	     * // => false
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.some(users, ['active', false]);
	     * // => true
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.some(users, 'active');
	     * // => true
	     */
	    function some(collection, predicate, guard) {
	      var func = isArray(collection) ? arraySome : baseSome;
	      if (guard && isIterateeCall(collection, predicate, guard)) {
	        predicate = undefined$1;
	      }
	      return func(collection, getIteratee(predicate, 3));
	    }

	    /**
	     * Creates an array of elements, sorted in ascending order by the results of
	     * running each element in a collection thru each iteratee. This method
	     * performs a stable sort, that is, it preserves the original sort order of
	     * equal elements. The iteratees are invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Collection
	     * @param {Array|Object} collection The collection to iterate over.
	     * @param {...(Function|Function[])} [iteratees=[_.identity]]
	     *  The iteratees to sort by.
	     * @returns {Array} Returns the new sorted array.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'fred',   'age': 48 },
	     *   { 'user': 'barney', 'age': 36 },
	     *   { 'user': 'fred',   'age': 30 },
	     *   { 'user': 'barney', 'age': 34 }
	     * ];
	     *
	     * _.sortBy(users, [function(o) { return o.user; }]);
	     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 30]]
	     *
	     * _.sortBy(users, ['user', 'age']);
	     * // => objects for [['barney', 34], ['barney', 36], ['fred', 30], ['fred', 48]]
	     */
	    var sortBy = baseRest(function(collection, iteratees) {
	      if (collection == null) {
	        return [];
	      }
	      var length = iteratees.length;
	      if (length > 1 && isIterateeCall(collection, iteratees[0], iteratees[1])) {
	        iteratees = [];
	      } else if (length > 2 && isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
	        iteratees = [iteratees[0]];
	      }
	      return baseOrderBy(collection, baseFlatten(iteratees, 1), []);
	    });

	    /*------------------------------------------------------------------------*/

	    /**
	     * Gets the timestamp of the number of milliseconds that have elapsed since
	     * the Unix epoch (1 January 1970 00:00:00 UTC).
	     *
	     * @static
	     * @memberOf _
	     * @since 2.4.0
	     * @category Date
	     * @returns {number} Returns the timestamp.
	     * @example
	     *
	     * _.defer(function(stamp) {
	     *   console.log(_.now() - stamp);
	     * }, _.now());
	     * // => Logs the number of milliseconds it took for the deferred invocation.
	     */
	    var now = ctxNow || function() {
	      return root.Date.now();
	    };

	    /*------------------------------------------------------------------------*/

	    /**
	     * The opposite of `_.before`; this method creates a function that invokes
	     * `func` once it's called `n` or more times.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {number} n The number of calls before `func` is invoked.
	     * @param {Function} func The function to restrict.
	     * @returns {Function} Returns the new restricted function.
	     * @example
	     *
	     * var saves = ['profile', 'settings'];
	     *
	     * var done = _.after(saves.length, function() {
	     *   console.log('done saving!');
	     * });
	     *
	     * _.forEach(saves, function(type) {
	     *   asyncSave({ 'type': type, 'complete': done });
	     * });
	     * // => Logs 'done saving!' after the two async saves have completed.
	     */
	    function after(n, func) {
	      if (typeof func != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      n = toInteger(n);
	      return function() {
	        if (--n < 1) {
	          return func.apply(this, arguments);
	        }
	      };
	    }

	    /**
	     * Creates a function that invokes `func`, with up to `n` arguments,
	     * ignoring any additional arguments.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Function
	     * @param {Function} func The function to cap arguments for.
	     * @param {number} [n=func.length] The arity cap.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Function} Returns the new capped function.
	     * @example
	     *
	     * _.map(['6', '8', '10'], _.ary(parseInt, 1));
	     * // => [6, 8, 10]
	     */
	    function ary(func, n, guard) {
	      n = guard ? undefined$1 : n;
	      n = (func && n == null) ? func.length : n;
	      return createWrap(func, WRAP_ARY_FLAG, undefined$1, undefined$1, undefined$1, undefined$1, n);
	    }

	    /**
	     * Creates a function that invokes `func`, with the `this` binding and arguments
	     * of the created function, while it's called less than `n` times. Subsequent
	     * calls to the created function return the result of the last `func` invocation.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Function
	     * @param {number} n The number of calls at which `func` is no longer invoked.
	     * @param {Function} func The function to restrict.
	     * @returns {Function} Returns the new restricted function.
	     * @example
	     *
	     * jQuery(element).on('click', _.before(5, addContactToList));
	     * // => Allows adding up to 4 contacts to the list.
	     */
	    function before(n, func) {
	      var result;
	      if (typeof func != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      n = toInteger(n);
	      return function() {
	        if (--n > 0) {
	          result = func.apply(this, arguments);
	        }
	        if (n <= 1) {
	          func = undefined$1;
	        }
	        return result;
	      };
	    }

	    /**
	     * Creates a function that invokes `func` with the `this` binding of `thisArg`
	     * and `partials` prepended to the arguments it receives.
	     *
	     * The `_.bind.placeholder` value, which defaults to `_` in monolithic builds,
	     * may be used as a placeholder for partially applied arguments.
	     *
	     * **Note:** Unlike native `Function#bind`, this method doesn't set the "length"
	     * property of bound functions.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {Function} func The function to bind.
	     * @param {*} thisArg The `this` binding of `func`.
	     * @param {...*} [partials] The arguments to be partially applied.
	     * @returns {Function} Returns the new bound function.
	     * @example
	     *
	     * function greet(greeting, punctuation) {
	     *   return greeting + ' ' + this.user + punctuation;
	     * }
	     *
	     * var object = { 'user': 'fred' };
	     *
	     * var bound = _.bind(greet, object, 'hi');
	     * bound('!');
	     * // => 'hi fred!'
	     *
	     * // Bound with placeholders.
	     * var bound = _.bind(greet, object, _, '!');
	     * bound('hi');
	     * // => 'hi fred!'
	     */
	    var bind = baseRest(function(func, thisArg, partials) {
	      var bitmask = WRAP_BIND_FLAG;
	      if (partials.length) {
	        var holders = replaceHolders(partials, getHolder(bind));
	        bitmask |= WRAP_PARTIAL_FLAG;
	      }
	      return createWrap(func, bitmask, thisArg, partials, holders);
	    });

	    /**
	     * Creates a function that invokes the method at `object[key]` with `partials`
	     * prepended to the arguments it receives.
	     *
	     * This method differs from `_.bind` by allowing bound functions to reference
	     * methods that may be redefined or don't yet exist. See
	     * [Peter Michaux's article](http://peter.michaux.ca/articles/lazy-function-definition-pattern)
	     * for more details.
	     *
	     * The `_.bindKey.placeholder` value, which defaults to `_` in monolithic
	     * builds, may be used as a placeholder for partially applied arguments.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.10.0
	     * @category Function
	     * @param {Object} object The object to invoke the method on.
	     * @param {string} key The key of the method.
	     * @param {...*} [partials] The arguments to be partially applied.
	     * @returns {Function} Returns the new bound function.
	     * @example
	     *
	     * var object = {
	     *   'user': 'fred',
	     *   'greet': function(greeting, punctuation) {
	     *     return greeting + ' ' + this.user + punctuation;
	     *   }
	     * };
	     *
	     * var bound = _.bindKey(object, 'greet', 'hi');
	     * bound('!');
	     * // => 'hi fred!'
	     *
	     * object.greet = function(greeting, punctuation) {
	     *   return greeting + 'ya ' + this.user + punctuation;
	     * };
	     *
	     * bound('!');
	     * // => 'hiya fred!'
	     *
	     * // Bound with placeholders.
	     * var bound = _.bindKey(object, 'greet', _, '!');
	     * bound('hi');
	     * // => 'hiya fred!'
	     */
	    var bindKey = baseRest(function(object, key, partials) {
	      var bitmask = WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG;
	      if (partials.length) {
	        var holders = replaceHolders(partials, getHolder(bindKey));
	        bitmask |= WRAP_PARTIAL_FLAG;
	      }
	      return createWrap(key, bitmask, object, partials, holders);
	    });

	    /**
	     * Creates a function that accepts arguments of `func` and either invokes
	     * `func` returning its result, if at least `arity` number of arguments have
	     * been provided, or returns a function that accepts the remaining `func`
	     * arguments, and so on. The arity of `func` may be specified if `func.length`
	     * is not sufficient.
	     *
	     * The `_.curry.placeholder` value, which defaults to `_` in monolithic builds,
	     * may be used as a placeholder for provided arguments.
	     *
	     * **Note:** This method doesn't set the "length" property of curried functions.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Function
	     * @param {Function} func The function to curry.
	     * @param {number} [arity=func.length] The arity of `func`.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Function} Returns the new curried function.
	     * @example
	     *
	     * var abc = function(a, b, c) {
	     *   return [a, b, c];
	     * };
	     *
	     * var curried = _.curry(abc);
	     *
	     * curried(1)(2)(3);
	     * // => [1, 2, 3]
	     *
	     * curried(1, 2)(3);
	     * // => [1, 2, 3]
	     *
	     * curried(1, 2, 3);
	     * // => [1, 2, 3]
	     *
	     * // Curried with placeholders.
	     * curried(1)(_, 3)(2);
	     * // => [1, 2, 3]
	     */
	    function curry(func, arity, guard) {
	      arity = guard ? undefined$1 : arity;
	      var result = createWrap(func, WRAP_CURRY_FLAG, undefined$1, undefined$1, undefined$1, undefined$1, undefined$1, arity);
	      result.placeholder = curry.placeholder;
	      return result;
	    }

	    /**
	     * This method is like `_.curry` except that arguments are applied to `func`
	     * in the manner of `_.partialRight` instead of `_.partial`.
	     *
	     * The `_.curryRight.placeholder` value, which defaults to `_` in monolithic
	     * builds, may be used as a placeholder for provided arguments.
	     *
	     * **Note:** This method doesn't set the "length" property of curried functions.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Function
	     * @param {Function} func The function to curry.
	     * @param {number} [arity=func.length] The arity of `func`.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Function} Returns the new curried function.
	     * @example
	     *
	     * var abc = function(a, b, c) {
	     *   return [a, b, c];
	     * };
	     *
	     * var curried = _.curryRight(abc);
	     *
	     * curried(3)(2)(1);
	     * // => [1, 2, 3]
	     *
	     * curried(2, 3)(1);
	     * // => [1, 2, 3]
	     *
	     * curried(1, 2, 3);
	     * // => [1, 2, 3]
	     *
	     * // Curried with placeholders.
	     * curried(3)(1, _)(2);
	     * // => [1, 2, 3]
	     */
	    function curryRight(func, arity, guard) {
	      arity = guard ? undefined$1 : arity;
	      var result = createWrap(func, WRAP_CURRY_RIGHT_FLAG, undefined$1, undefined$1, undefined$1, undefined$1, undefined$1, arity);
	      result.placeholder = curryRight.placeholder;
	      return result;
	    }

	    /**
	     * Creates a debounced function that delays invoking `func` until after `wait`
	     * milliseconds have elapsed since the last time the debounced function was
	     * invoked. The debounced function comes with a `cancel` method to cancel
	     * delayed `func` invocations and a `flush` method to immediately invoke them.
	     * Provide `options` to indicate whether `func` should be invoked on the
	     * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
	     * with the last arguments provided to the debounced function. Subsequent
	     * calls to the debounced function return the result of the last `func`
	     * invocation.
	     *
	     * **Note:** If `leading` and `trailing` options are `true`, `func` is
	     * invoked on the trailing edge of the timeout only if the debounced function
	     * is invoked more than once during the `wait` timeout.
	     *
	     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
	     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
	     *
	     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
	     * for details over the differences between `_.debounce` and `_.throttle`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {Function} func The function to debounce.
	     * @param {number} [wait=0] The number of milliseconds to delay.
	     * @param {Object} [options={}] The options object.
	     * @param {boolean} [options.leading=false]
	     *  Specify invoking on the leading edge of the timeout.
	     * @param {number} [options.maxWait]
	     *  The maximum time `func` is allowed to be delayed before it's invoked.
	     * @param {boolean} [options.trailing=true]
	     *  Specify invoking on the trailing edge of the timeout.
	     * @returns {Function} Returns the new debounced function.
	     * @example
	     *
	     * // Avoid costly calculations while the window size is in flux.
	     * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
	     *
	     * // Invoke `sendMail` when clicked, debouncing subsequent calls.
	     * jQuery(element).on('click', _.debounce(sendMail, 300, {
	     *   'leading': true,
	     *   'trailing': false
	     * }));
	     *
	     * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
	     * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
	     * var source = new EventSource('/stream');
	     * jQuery(source).on('message', debounced);
	     *
	     * // Cancel the trailing debounced invocation.
	     * jQuery(window).on('popstate', debounced.cancel);
	     */
	    function debounce(func, wait, options) {
	      var lastArgs,
	          lastThis,
	          maxWait,
	          result,
	          timerId,
	          lastCallTime,
	          lastInvokeTime = 0,
	          leading = false,
	          maxing = false,
	          trailing = true;

	      if (typeof func != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      wait = toNumber(wait) || 0;
	      if (isObject(options)) {
	        leading = !!options.leading;
	        maxing = 'maxWait' in options;
	        maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
	        trailing = 'trailing' in options ? !!options.trailing : trailing;
	      }

	      function invokeFunc(time) {
	        var args = lastArgs,
	            thisArg = lastThis;

	        lastArgs = lastThis = undefined$1;
	        lastInvokeTime = time;
	        result = func.apply(thisArg, args);
	        return result;
	      }

	      function leadingEdge(time) {
	        // Reset any `maxWait` timer.
	        lastInvokeTime = time;
	        // Start the timer for the trailing edge.
	        timerId = setTimeout(timerExpired, wait);
	        // Invoke the leading edge.
	        return leading ? invokeFunc(time) : result;
	      }

	      function remainingWait(time) {
	        var timeSinceLastCall = time - lastCallTime,
	            timeSinceLastInvoke = time - lastInvokeTime,
	            timeWaiting = wait - timeSinceLastCall;

	        return maxing
	          ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
	          : timeWaiting;
	      }

	      function shouldInvoke(time) {
	        var timeSinceLastCall = time - lastCallTime,
	            timeSinceLastInvoke = time - lastInvokeTime;

	        // Either this is the first call, activity has stopped and we're at the
	        // trailing edge, the system time has gone backwards and we're treating
	        // it as the trailing edge, or we've hit the `maxWait` limit.
	        return (lastCallTime === undefined$1 || (timeSinceLastCall >= wait) ||
	          (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
	      }

	      function timerExpired() {
	        var time = now();
	        if (shouldInvoke(time)) {
	          return trailingEdge(time);
	        }
	        // Restart the timer.
	        timerId = setTimeout(timerExpired, remainingWait(time));
	      }

	      function trailingEdge(time) {
	        timerId = undefined$1;

	        // Only invoke if we have `lastArgs` which means `func` has been
	        // debounced at least once.
	        if (trailing && lastArgs) {
	          return invokeFunc(time);
	        }
	        lastArgs = lastThis = undefined$1;
	        return result;
	      }

	      function cancel() {
	        if (timerId !== undefined$1) {
	          clearTimeout(timerId);
	        }
	        lastInvokeTime = 0;
	        lastArgs = lastCallTime = lastThis = timerId = undefined$1;
	      }

	      function flush() {
	        return timerId === undefined$1 ? result : trailingEdge(now());
	      }

	      function debounced() {
	        var time = now(),
	            isInvoking = shouldInvoke(time);

	        lastArgs = arguments;
	        lastThis = this;
	        lastCallTime = time;

	        if (isInvoking) {
	          if (timerId === undefined$1) {
	            return leadingEdge(lastCallTime);
	          }
	          if (maxing) {
	            // Handle invocations in a tight loop.
	            clearTimeout(timerId);
	            timerId = setTimeout(timerExpired, wait);
	            return invokeFunc(lastCallTime);
	          }
	        }
	        if (timerId === undefined$1) {
	          timerId = setTimeout(timerExpired, wait);
	        }
	        return result;
	      }
	      debounced.cancel = cancel;
	      debounced.flush = flush;
	      return debounced;
	    }

	    /**
	     * Defers invoking the `func` until the current call stack has cleared. Any
	     * additional arguments are provided to `func` when it's invoked.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {Function} func The function to defer.
	     * @param {...*} [args] The arguments to invoke `func` with.
	     * @returns {number} Returns the timer id.
	     * @example
	     *
	     * _.defer(function(text) {
	     *   console.log(text);
	     * }, 'deferred');
	     * // => Logs 'deferred' after one millisecond.
	     */
	    var defer = baseRest(function(func, args) {
	      return baseDelay(func, 1, args);
	    });

	    /**
	     * Invokes `func` after `wait` milliseconds. Any additional arguments are
	     * provided to `func` when it's invoked.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {Function} func The function to delay.
	     * @param {number} wait The number of milliseconds to delay invocation.
	     * @param {...*} [args] The arguments to invoke `func` with.
	     * @returns {number} Returns the timer id.
	     * @example
	     *
	     * _.delay(function(text) {
	     *   console.log(text);
	     * }, 1000, 'later');
	     * // => Logs 'later' after one second.
	     */
	    var delay = baseRest(function(func, wait, args) {
	      return baseDelay(func, toNumber(wait) || 0, args);
	    });

	    /**
	     * Creates a function that invokes `func` with arguments reversed.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Function
	     * @param {Function} func The function to flip arguments for.
	     * @returns {Function} Returns the new flipped function.
	     * @example
	     *
	     * var flipped = _.flip(function() {
	     *   return _.toArray(arguments);
	     * });
	     *
	     * flipped('a', 'b', 'c', 'd');
	     * // => ['d', 'c', 'b', 'a']
	     */
	    function flip(func) {
	      return createWrap(func, WRAP_FLIP_FLAG);
	    }

	    /**
	     * Creates a function that memoizes the result of `func`. If `resolver` is
	     * provided, it determines the cache key for storing the result based on the
	     * arguments provided to the memoized function. By default, the first argument
	     * provided to the memoized function is used as the map cache key. The `func`
	     * is invoked with the `this` binding of the memoized function.
	     *
	     * **Note:** The cache is exposed as the `cache` property on the memoized
	     * function. Its creation may be customized by replacing the `_.memoize.Cache`
	     * constructor with one whose instances implement the
	     * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
	     * method interface of `clear`, `delete`, `get`, `has`, and `set`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {Function} func The function to have its output memoized.
	     * @param {Function} [resolver] The function to resolve the cache key.
	     * @returns {Function} Returns the new memoized function.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': 2 };
	     * var other = { 'c': 3, 'd': 4 };
	     *
	     * var values = _.memoize(_.values);
	     * values(object);
	     * // => [1, 2]
	     *
	     * values(other);
	     * // => [3, 4]
	     *
	     * object.a = 2;
	     * values(object);
	     * // => [1, 2]
	     *
	     * // Modify the result cache.
	     * values.cache.set(object, ['a', 'b']);
	     * values(object);
	     * // => ['a', 'b']
	     *
	     * // Replace `_.memoize.Cache`.
	     * _.memoize.Cache = WeakMap;
	     */
	    function memoize(func, resolver) {
	      if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      var memoized = function() {
	        var args = arguments,
	            key = resolver ? resolver.apply(this, args) : args[0],
	            cache = memoized.cache;

	        if (cache.has(key)) {
	          return cache.get(key);
	        }
	        var result = func.apply(this, args);
	        memoized.cache = cache.set(key, result) || cache;
	        return result;
	      };
	      memoized.cache = new (memoize.Cache || MapCache);
	      return memoized;
	    }

	    // Expose `MapCache`.
	    memoize.Cache = MapCache;

	    /**
	     * Creates a function that negates the result of the predicate `func`. The
	     * `func` predicate is invoked with the `this` binding and arguments of the
	     * created function.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Function
	     * @param {Function} predicate The predicate to negate.
	     * @returns {Function} Returns the new negated function.
	     * @example
	     *
	     * function isEven(n) {
	     *   return n % 2 == 0;
	     * }
	     *
	     * _.filter([1, 2, 3, 4, 5, 6], _.negate(isEven));
	     * // => [1, 3, 5]
	     */
	    function negate(predicate) {
	      if (typeof predicate != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      return function() {
	        var args = arguments;
	        switch (args.length) {
	          case 0: return !predicate.call(this);
	          case 1: return !predicate.call(this, args[0]);
	          case 2: return !predicate.call(this, args[0], args[1]);
	          case 3: return !predicate.call(this, args[0], args[1], args[2]);
	        }
	        return !predicate.apply(this, args);
	      };
	    }

	    /**
	     * Creates a function that is restricted to invoking `func` once. Repeat calls
	     * to the function return the value of the first invocation. The `func` is
	     * invoked with the `this` binding and arguments of the created function.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {Function} func The function to restrict.
	     * @returns {Function} Returns the new restricted function.
	     * @example
	     *
	     * var initialize = _.once(createApplication);
	     * initialize();
	     * initialize();
	     * // => `createApplication` is invoked once
	     */
	    function once(func) {
	      return before(2, func);
	    }

	    /**
	     * Creates a function that invokes `func` with its arguments transformed.
	     *
	     * @static
	     * @since 4.0.0
	     * @memberOf _
	     * @category Function
	     * @param {Function} func The function to wrap.
	     * @param {...(Function|Function[])} [transforms=[_.identity]]
	     *  The argument transforms.
	     * @returns {Function} Returns the new function.
	     * @example
	     *
	     * function doubled(n) {
	     *   return n * 2;
	     * }
	     *
	     * function square(n) {
	     *   return n * n;
	     * }
	     *
	     * var func = _.overArgs(function(x, y) {
	     *   return [x, y];
	     * }, [square, doubled]);
	     *
	     * func(9, 3);
	     * // => [81, 6]
	     *
	     * func(10, 5);
	     * // => [100, 10]
	     */
	    var overArgs = castRest(function(func, transforms) {
	      transforms = (transforms.length == 1 && isArray(transforms[0]))
	        ? arrayMap(transforms[0], baseUnary(getIteratee()))
	        : arrayMap(baseFlatten(transforms, 1), baseUnary(getIteratee()));

	      var funcsLength = transforms.length;
	      return baseRest(function(args) {
	        var index = -1,
	            length = nativeMin(args.length, funcsLength);

	        while (++index < length) {
	          args[index] = transforms[index].call(this, args[index]);
	        }
	        return apply(func, this, args);
	      });
	    });

	    /**
	     * Creates a function that invokes `func` with `partials` prepended to the
	     * arguments it receives. This method is like `_.bind` except it does **not**
	     * alter the `this` binding.
	     *
	     * The `_.partial.placeholder` value, which defaults to `_` in monolithic
	     * builds, may be used as a placeholder for partially applied arguments.
	     *
	     * **Note:** This method doesn't set the "length" property of partially
	     * applied functions.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.2.0
	     * @category Function
	     * @param {Function} func The function to partially apply arguments to.
	     * @param {...*} [partials] The arguments to be partially applied.
	     * @returns {Function} Returns the new partially applied function.
	     * @example
	     *
	     * function greet(greeting, name) {
	     *   return greeting + ' ' + name;
	     * }
	     *
	     * var sayHelloTo = _.partial(greet, 'hello');
	     * sayHelloTo('fred');
	     * // => 'hello fred'
	     *
	     * // Partially applied with placeholders.
	     * var greetFred = _.partial(greet, _, 'fred');
	     * greetFred('hi');
	     * // => 'hi fred'
	     */
	    var partial = baseRest(function(func, partials) {
	      var holders = replaceHolders(partials, getHolder(partial));
	      return createWrap(func, WRAP_PARTIAL_FLAG, undefined$1, partials, holders);
	    });

	    /**
	     * This method is like `_.partial` except that partially applied arguments
	     * are appended to the arguments it receives.
	     *
	     * The `_.partialRight.placeholder` value, which defaults to `_` in monolithic
	     * builds, may be used as a placeholder for partially applied arguments.
	     *
	     * **Note:** This method doesn't set the "length" property of partially
	     * applied functions.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.0.0
	     * @category Function
	     * @param {Function} func The function to partially apply arguments to.
	     * @param {...*} [partials] The arguments to be partially applied.
	     * @returns {Function} Returns the new partially applied function.
	     * @example
	     *
	     * function greet(greeting, name) {
	     *   return greeting + ' ' + name;
	     * }
	     *
	     * var greetFred = _.partialRight(greet, 'fred');
	     * greetFred('hi');
	     * // => 'hi fred'
	     *
	     * // Partially applied with placeholders.
	     * var sayHelloTo = _.partialRight(greet, 'hello', _);
	     * sayHelloTo('fred');
	     * // => 'hello fred'
	     */
	    var partialRight = baseRest(function(func, partials) {
	      var holders = replaceHolders(partials, getHolder(partialRight));
	      return createWrap(func, WRAP_PARTIAL_RIGHT_FLAG, undefined$1, partials, holders);
	    });

	    /**
	     * Creates a function that invokes `func` with arguments arranged according
	     * to the specified `indexes` where the argument value at the first index is
	     * provided as the first argument, the argument value at the second index is
	     * provided as the second argument, and so on.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Function
	     * @param {Function} func The function to rearrange arguments for.
	     * @param {...(number|number[])} indexes The arranged argument indexes.
	     * @returns {Function} Returns the new function.
	     * @example
	     *
	     * var rearged = _.rearg(function(a, b, c) {
	     *   return [a, b, c];
	     * }, [2, 0, 1]);
	     *
	     * rearged('b', 'c', 'a')
	     * // => ['a', 'b', 'c']
	     */
	    var rearg = flatRest(function(func, indexes) {
	      return createWrap(func, WRAP_REARG_FLAG, undefined$1, undefined$1, undefined$1, indexes);
	    });

	    /**
	     * Creates a function that invokes `func` with the `this` binding of the
	     * created function and arguments from `start` and beyond provided as
	     * an array.
	     *
	     * **Note:** This method is based on the
	     * [rest parameter](https://mdn.io/rest_parameters).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Function
	     * @param {Function} func The function to apply a rest parameter to.
	     * @param {number} [start=func.length-1] The start position of the rest parameter.
	     * @returns {Function} Returns the new function.
	     * @example
	     *
	     * var say = _.rest(function(what, names) {
	     *   return what + ' ' + _.initial(names).join(', ') +
	     *     (_.size(names) > 1 ? ', & ' : '') + _.last(names);
	     * });
	     *
	     * say('hello', 'fred', 'barney', 'pebbles');
	     * // => 'hello fred, barney, & pebbles'
	     */
	    function rest(func, start) {
	      if (typeof func != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      start = start === undefined$1 ? start : toInteger(start);
	      return baseRest(func, start);
	    }

	    /**
	     * Creates a function that invokes `func` with the `this` binding of the
	     * create function and an array of arguments much like
	     * [`Function#apply`](http://www.ecma-international.org/ecma-262/7.0/#sec-function.prototype.apply).
	     *
	     * **Note:** This method is based on the
	     * [spread operator](https://mdn.io/spread_operator).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.2.0
	     * @category Function
	     * @param {Function} func The function to spread arguments over.
	     * @param {number} [start=0] The start position of the spread.
	     * @returns {Function} Returns the new function.
	     * @example
	     *
	     * var say = _.spread(function(who, what) {
	     *   return who + ' says ' + what;
	     * });
	     *
	     * say(['fred', 'hello']);
	     * // => 'fred says hello'
	     *
	     * var numbers = Promise.all([
	     *   Promise.resolve(40),
	     *   Promise.resolve(36)
	     * ]);
	     *
	     * numbers.then(_.spread(function(x, y) {
	     *   return x + y;
	     * }));
	     * // => a Promise of 76
	     */
	    function spread(func, start) {
	      if (typeof func != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      start = start == null ? 0 : nativeMax(toInteger(start), 0);
	      return baseRest(function(args) {
	        var array = args[start],
	            otherArgs = castSlice(args, 0, start);

	        if (array) {
	          arrayPush(otherArgs, array);
	        }
	        return apply(func, this, otherArgs);
	      });
	    }

	    /**
	     * Creates a throttled function that only invokes `func` at most once per
	     * every `wait` milliseconds. The throttled function comes with a `cancel`
	     * method to cancel delayed `func` invocations and a `flush` method to
	     * immediately invoke them. Provide `options` to indicate whether `func`
	     * should be invoked on the leading and/or trailing edge of the `wait`
	     * timeout. The `func` is invoked with the last arguments provided to the
	     * throttled function. Subsequent calls to the throttled function return the
	     * result of the last `func` invocation.
	     *
	     * **Note:** If `leading` and `trailing` options are `true`, `func` is
	     * invoked on the trailing edge of the timeout only if the throttled function
	     * is invoked more than once during the `wait` timeout.
	     *
	     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
	     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
	     *
	     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
	     * for details over the differences between `_.throttle` and `_.debounce`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {Function} func The function to throttle.
	     * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
	     * @param {Object} [options={}] The options object.
	     * @param {boolean} [options.leading=true]
	     *  Specify invoking on the leading edge of the timeout.
	     * @param {boolean} [options.trailing=true]
	     *  Specify invoking on the trailing edge of the timeout.
	     * @returns {Function} Returns the new throttled function.
	     * @example
	     *
	     * // Avoid excessively updating the position while scrolling.
	     * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
	     *
	     * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
	     * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
	     * jQuery(element).on('click', throttled);
	     *
	     * // Cancel the trailing throttled invocation.
	     * jQuery(window).on('popstate', throttled.cancel);
	     */
	    function throttle(func, wait, options) {
	      var leading = true,
	          trailing = true;

	      if (typeof func != 'function') {
	        throw new TypeError(FUNC_ERROR_TEXT);
	      }
	      if (isObject(options)) {
	        leading = 'leading' in options ? !!options.leading : leading;
	        trailing = 'trailing' in options ? !!options.trailing : trailing;
	      }
	      return debounce(func, wait, {
	        'leading': leading,
	        'maxWait': wait,
	        'trailing': trailing
	      });
	    }

	    /**
	     * Creates a function that accepts up to one argument, ignoring any
	     * additional arguments.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Function
	     * @param {Function} func The function to cap arguments for.
	     * @returns {Function} Returns the new capped function.
	     * @example
	     *
	     * _.map(['6', '8', '10'], _.unary(parseInt));
	     * // => [6, 8, 10]
	     */
	    function unary(func) {
	      return ary(func, 1);
	    }

	    /**
	     * Creates a function that provides `value` to `wrapper` as its first
	     * argument. Any additional arguments provided to the function are appended
	     * to those provided to the `wrapper`. The wrapper is invoked with the `this`
	     * binding of the created function.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Function
	     * @param {*} value The value to wrap.
	     * @param {Function} [wrapper=identity] The wrapper function.
	     * @returns {Function} Returns the new function.
	     * @example
	     *
	     * var p = _.wrap(_.escape, function(func, text) {
	     *   return '<p>' + func(text) + '</p>';
	     * });
	     *
	     * p('fred, barney, & pebbles');
	     * // => '<p>fred, barney, &amp; pebbles</p>'
	     */
	    function wrap(value, wrapper) {
	      return partial(castFunction(wrapper), value);
	    }

	    /*------------------------------------------------------------------------*/

	    /**
	     * Casts `value` as an array if it's not one.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.4.0
	     * @category Lang
	     * @param {*} value The value to inspect.
	     * @returns {Array} Returns the cast array.
	     * @example
	     *
	     * _.castArray(1);
	     * // => [1]
	     *
	     * _.castArray({ 'a': 1 });
	     * // => [{ 'a': 1 }]
	     *
	     * _.castArray('abc');
	     * // => ['abc']
	     *
	     * _.castArray(null);
	     * // => [null]
	     *
	     * _.castArray(undefined);
	     * // => [undefined]
	     *
	     * _.castArray();
	     * // => []
	     *
	     * var array = [1, 2, 3];
	     * console.log(_.castArray(array) === array);
	     * // => true
	     */
	    function castArray() {
	      if (!arguments.length) {
	        return [];
	      }
	      var value = arguments[0];
	      return isArray(value) ? value : [value];
	    }

	    /**
	     * Creates a shallow clone of `value`.
	     *
	     * **Note:** This method is loosely based on the
	     * [structured clone algorithm](https://mdn.io/Structured_clone_algorithm)
	     * and supports cloning arrays, array buffers, booleans, date objects, maps,
	     * numbers, `Object` objects, regexes, sets, strings, symbols, and typed
	     * arrays. The own enumerable properties of `arguments` objects are cloned
	     * as plain objects. An empty object is returned for uncloneable values such
	     * as error objects, functions, DOM nodes, and WeakMaps.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to clone.
	     * @returns {*} Returns the cloned value.
	     * @see _.cloneDeep
	     * @example
	     *
	     * var objects = [{ 'a': 1 }, { 'b': 2 }];
	     *
	     * var shallow = _.clone(objects);
	     * console.log(shallow[0] === objects[0]);
	     * // => true
	     */
	    function clone(value) {
	      return baseClone(value, CLONE_SYMBOLS_FLAG);
	    }

	    /**
	     * This method is like `_.clone` except that it accepts `customizer` which
	     * is invoked to produce the cloned value. If `customizer` returns `undefined`,
	     * cloning is handled by the method instead. The `customizer` is invoked with
	     * up to four arguments; (value [, index|key, object, stack]).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to clone.
	     * @param {Function} [customizer] The function to customize cloning.
	     * @returns {*} Returns the cloned value.
	     * @see _.cloneDeepWith
	     * @example
	     *
	     * function customizer(value) {
	     *   if (_.isElement(value)) {
	     *     return value.cloneNode(false);
	     *   }
	     * }
	     *
	     * var el = _.cloneWith(document.body, customizer);
	     *
	     * console.log(el === document.body);
	     * // => false
	     * console.log(el.nodeName);
	     * // => 'BODY'
	     * console.log(el.childNodes.length);
	     * // => 0
	     */
	    function cloneWith(value, customizer) {
	      customizer = typeof customizer == 'function' ? customizer : undefined$1;
	      return baseClone(value, CLONE_SYMBOLS_FLAG, customizer);
	    }

	    /**
	     * This method is like `_.clone` except that it recursively clones `value`.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.0.0
	     * @category Lang
	     * @param {*} value The value to recursively clone.
	     * @returns {*} Returns the deep cloned value.
	     * @see _.clone
	     * @example
	     *
	     * var objects = [{ 'a': 1 }, { 'b': 2 }];
	     *
	     * var deep = _.cloneDeep(objects);
	     * console.log(deep[0] === objects[0]);
	     * // => false
	     */
	    function cloneDeep(value) {
	      return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG);
	    }

	    /**
	     * This method is like `_.cloneWith` except that it recursively clones `value`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to recursively clone.
	     * @param {Function} [customizer] The function to customize cloning.
	     * @returns {*} Returns the deep cloned value.
	     * @see _.cloneWith
	     * @example
	     *
	     * function customizer(value) {
	     *   if (_.isElement(value)) {
	     *     return value.cloneNode(true);
	     *   }
	     * }
	     *
	     * var el = _.cloneDeepWith(document.body, customizer);
	     *
	     * console.log(el === document.body);
	     * // => false
	     * console.log(el.nodeName);
	     * // => 'BODY'
	     * console.log(el.childNodes.length);
	     * // => 20
	     */
	    function cloneDeepWith(value, customizer) {
	      customizer = typeof customizer == 'function' ? customizer : undefined$1;
	      return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG, customizer);
	    }

	    /**
	     * Checks if `object` conforms to `source` by invoking the predicate
	     * properties of `source` with the corresponding property values of `object`.
	     *
	     * **Note:** This method is equivalent to `_.conforms` when `source` is
	     * partially applied.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.14.0
	     * @category Lang
	     * @param {Object} object The object to inspect.
	     * @param {Object} source The object of property predicates to conform to.
	     * @returns {boolean} Returns `true` if `object` conforms, else `false`.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': 2 };
	     *
	     * _.conformsTo(object, { 'b': function(n) { return n > 1; } });
	     * // => true
	     *
	     * _.conformsTo(object, { 'b': function(n) { return n > 2; } });
	     * // => false
	     */
	    function conformsTo(object, source) {
	      return source == null || baseConformsTo(object, source, keys(source));
	    }

	    /**
	     * Performs a
	     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
	     * comparison between two values to determine if they are equivalent.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
	     * @example
	     *
	     * var object = { 'a': 1 };
	     * var other = { 'a': 1 };
	     *
	     * _.eq(object, object);
	     * // => true
	     *
	     * _.eq(object, other);
	     * // => false
	     *
	     * _.eq('a', 'a');
	     * // => true
	     *
	     * _.eq('a', Object('a'));
	     * // => false
	     *
	     * _.eq(NaN, NaN);
	     * // => true
	     */
	    function eq(value, other) {
	      return value === other || (value !== value && other !== other);
	    }

	    /**
	     * Checks if `value` is greater than `other`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.9.0
	     * @category Lang
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {boolean} Returns `true` if `value` is greater than `other`,
	     *  else `false`.
	     * @see _.lt
	     * @example
	     *
	     * _.gt(3, 1);
	     * // => true
	     *
	     * _.gt(3, 3);
	     * // => false
	     *
	     * _.gt(1, 3);
	     * // => false
	     */
	    var gt = createRelationalOperation(baseGt);

	    /**
	     * Checks if `value` is greater than or equal to `other`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.9.0
	     * @category Lang
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {boolean} Returns `true` if `value` is greater than or equal to
	     *  `other`, else `false`.
	     * @see _.lte
	     * @example
	     *
	     * _.gte(3, 1);
	     * // => true
	     *
	     * _.gte(3, 3);
	     * // => true
	     *
	     * _.gte(1, 3);
	     * // => false
	     */
	    var gte = createRelationalOperation(function(value, other) {
	      return value >= other;
	    });

	    /**
	     * Checks if `value` is likely an `arguments` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
	     *  else `false`.
	     * @example
	     *
	     * _.isArguments(function() { return arguments; }());
	     * // => true
	     *
	     * _.isArguments([1, 2, 3]);
	     * // => false
	     */
	    var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
	      return isObjectLike(value) && hasOwnProperty.call(value, 'callee') &&
	        !propertyIsEnumerable.call(value, 'callee');
	    };

	    /**
	     * Checks if `value` is classified as an `Array` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an array, else `false`.
	     * @example
	     *
	     * _.isArray([1, 2, 3]);
	     * // => true
	     *
	     * _.isArray(document.body.children);
	     * // => false
	     *
	     * _.isArray('abc');
	     * // => false
	     *
	     * _.isArray(_.noop);
	     * // => false
	     */
	    var isArray = Array.isArray;

	    /**
	     * Checks if `value` is classified as an `ArrayBuffer` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.3.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an array buffer, else `false`.
	     * @example
	     *
	     * _.isArrayBuffer(new ArrayBuffer(2));
	     * // => true
	     *
	     * _.isArrayBuffer(new Array(2));
	     * // => false
	     */
	    var isArrayBuffer = nodeIsArrayBuffer ? baseUnary(nodeIsArrayBuffer) : baseIsArrayBuffer;

	    /**
	     * Checks if `value` is array-like. A value is considered array-like if it's
	     * not a function and has a `value.length` that's an integer greater than or
	     * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
	     * @example
	     *
	     * _.isArrayLike([1, 2, 3]);
	     * // => true
	     *
	     * _.isArrayLike(document.body.children);
	     * // => true
	     *
	     * _.isArrayLike('abc');
	     * // => true
	     *
	     * _.isArrayLike(_.noop);
	     * // => false
	     */
	    function isArrayLike(value) {
	      return value != null && isLength(value.length) && !isFunction(value);
	    }

	    /**
	     * This method is like `_.isArrayLike` except that it also checks if `value`
	     * is an object.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an array-like object,
	     *  else `false`.
	     * @example
	     *
	     * _.isArrayLikeObject([1, 2, 3]);
	     * // => true
	     *
	     * _.isArrayLikeObject(document.body.children);
	     * // => true
	     *
	     * _.isArrayLikeObject('abc');
	     * // => false
	     *
	     * _.isArrayLikeObject(_.noop);
	     * // => false
	     */
	    function isArrayLikeObject(value) {
	      return isObjectLike(value) && isArrayLike(value);
	    }

	    /**
	     * Checks if `value` is classified as a boolean primitive or object.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a boolean, else `false`.
	     * @example
	     *
	     * _.isBoolean(false);
	     * // => true
	     *
	     * _.isBoolean(null);
	     * // => false
	     */
	    function isBoolean(value) {
	      return value === true || value === false ||
	        (isObjectLike(value) && baseGetTag(value) == boolTag);
	    }

	    /**
	     * Checks if `value` is a buffer.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.3.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
	     * @example
	     *
	     * _.isBuffer(new Buffer(2));
	     * // => true
	     *
	     * _.isBuffer(new Uint8Array(2));
	     * // => false
	     */
	    var isBuffer = nativeIsBuffer || stubFalse;

	    /**
	     * Checks if `value` is classified as a `Date` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a date object, else `false`.
	     * @example
	     *
	     * _.isDate(new Date);
	     * // => true
	     *
	     * _.isDate('Mon April 23 2012');
	     * // => false
	     */
	    var isDate = nodeIsDate ? baseUnary(nodeIsDate) : baseIsDate;

	    /**
	     * Checks if `value` is likely a DOM element.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a DOM element, else `false`.
	     * @example
	     *
	     * _.isElement(document.body);
	     * // => true
	     *
	     * _.isElement('<body>');
	     * // => false
	     */
	    function isElement(value) {
	      return isObjectLike(value) && value.nodeType === 1 && !isPlainObject(value);
	    }

	    /**
	     * Checks if `value` is an empty object, collection, map, or set.
	     *
	     * Objects are considered empty if they have no own enumerable string keyed
	     * properties.
	     *
	     * Array-like values such as `arguments` objects, arrays, buffers, strings, or
	     * jQuery-like collections are considered empty if they have a `length` of `0`.
	     * Similarly, maps and sets are considered empty if they have a `size` of `0`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is empty, else `false`.
	     * @example
	     *
	     * _.isEmpty(null);
	     * // => true
	     *
	     * _.isEmpty(true);
	     * // => true
	     *
	     * _.isEmpty(1);
	     * // => true
	     *
	     * _.isEmpty([1, 2, 3]);
	     * // => false
	     *
	     * _.isEmpty({ 'a': 1 });
	     * // => false
	     */
	    function isEmpty(value) {
	      if (value == null) {
	        return true;
	      }
	      if (isArrayLike(value) &&
	          (isArray(value) || typeof value == 'string' || typeof value.splice == 'function' ||
	            isBuffer(value) || isTypedArray(value) || isArguments(value))) {
	        return !value.length;
	      }
	      var tag = getTag(value);
	      if (tag == mapTag || tag == setTag) {
	        return !value.size;
	      }
	      if (isPrototype(value)) {
	        return !baseKeys(value).length;
	      }
	      for (var key in value) {
	        if (hasOwnProperty.call(value, key)) {
	          return false;
	        }
	      }
	      return true;
	    }

	    /**
	     * Performs a deep comparison between two values to determine if they are
	     * equivalent.
	     *
	     * **Note:** This method supports comparing arrays, array buffers, booleans,
	     * date objects, error objects, maps, numbers, `Object` objects, regexes,
	     * sets, strings, symbols, and typed arrays. `Object` objects are compared
	     * by their own, not inherited, enumerable properties. Functions and DOM
	     * nodes are compared by strict equality, i.e. `===`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
	     * @example
	     *
	     * var object = { 'a': 1 };
	     * var other = { 'a': 1 };
	     *
	     * _.isEqual(object, other);
	     * // => true
	     *
	     * object === other;
	     * // => false
	     */
	    function isEqual(value, other) {
	      return baseIsEqual(value, other);
	    }

	    /**
	     * This method is like `_.isEqual` except that it accepts `customizer` which
	     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
	     * are handled by the method instead. The `customizer` is invoked with up to
	     * six arguments: (objValue, othValue [, index|key, object, other, stack]).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @param {Function} [customizer] The function to customize comparisons.
	     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
	     * @example
	     *
	     * function isGreeting(value) {
	     *   return /^h(?:i|ello)$/.test(value);
	     * }
	     *
	     * function customizer(objValue, othValue) {
	     *   if (isGreeting(objValue) && isGreeting(othValue)) {
	     *     return true;
	     *   }
	     * }
	     *
	     * var array = ['hello', 'goodbye'];
	     * var other = ['hi', 'goodbye'];
	     *
	     * _.isEqualWith(array, other, customizer);
	     * // => true
	     */
	    function isEqualWith(value, other, customizer) {
	      customizer = typeof customizer == 'function' ? customizer : undefined$1;
	      var result = customizer ? customizer(value, other) : undefined$1;
	      return result === undefined$1 ? baseIsEqual(value, other, undefined$1, customizer) : !!result;
	    }

	    /**
	     * Checks if `value` is an `Error`, `EvalError`, `RangeError`, `ReferenceError`,
	     * `SyntaxError`, `TypeError`, or `URIError` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an error object, else `false`.
	     * @example
	     *
	     * _.isError(new Error);
	     * // => true
	     *
	     * _.isError(Error);
	     * // => false
	     */
	    function isError(value) {
	      if (!isObjectLike(value)) {
	        return false;
	      }
	      var tag = baseGetTag(value);
	      return tag == errorTag || tag == domExcTag ||
	        (typeof value.message == 'string' && typeof value.name == 'string' && !isPlainObject(value));
	    }

	    /**
	     * Checks if `value` is a finite primitive number.
	     *
	     * **Note:** This method is based on
	     * [`Number.isFinite`](https://mdn.io/Number/isFinite).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a finite number, else `false`.
	     * @example
	     *
	     * _.isFinite(3);
	     * // => true
	     *
	     * _.isFinite(Number.MIN_VALUE);
	     * // => true
	     *
	     * _.isFinite(Infinity);
	     * // => false
	     *
	     * _.isFinite('3');
	     * // => false
	     */
	    function isFinite(value) {
	      return typeof value == 'number' && nativeIsFinite(value);
	    }

	    /**
	     * Checks if `value` is classified as a `Function` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a function, else `false`.
	     * @example
	     *
	     * _.isFunction(_);
	     * // => true
	     *
	     * _.isFunction(/abc/);
	     * // => false
	     */
	    function isFunction(value) {
	      if (!isObject(value)) {
	        return false;
	      }
	      // The use of `Object#toString` avoids issues with the `typeof` operator
	      // in Safari 9 which returns 'object' for typed arrays and other constructors.
	      var tag = baseGetTag(value);
	      return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
	    }

	    /**
	     * Checks if `value` is an integer.
	     *
	     * **Note:** This method is based on
	     * [`Number.isInteger`](https://mdn.io/Number/isInteger).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an integer, else `false`.
	     * @example
	     *
	     * _.isInteger(3);
	     * // => true
	     *
	     * _.isInteger(Number.MIN_VALUE);
	     * // => false
	     *
	     * _.isInteger(Infinity);
	     * // => false
	     *
	     * _.isInteger('3');
	     * // => false
	     */
	    function isInteger(value) {
	      return typeof value == 'number' && value == toInteger(value);
	    }

	    /**
	     * Checks if `value` is a valid array-like length.
	     *
	     * **Note:** This method is loosely based on
	     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
	     * @example
	     *
	     * _.isLength(3);
	     * // => true
	     *
	     * _.isLength(Number.MIN_VALUE);
	     * // => false
	     *
	     * _.isLength(Infinity);
	     * // => false
	     *
	     * _.isLength('3');
	     * // => false
	     */
	    function isLength(value) {
	      return typeof value == 'number' &&
	        value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
	    }

	    /**
	     * Checks if `value` is the
	     * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
	     * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is an object, else `false`.
	     * @example
	     *
	     * _.isObject({});
	     * // => true
	     *
	     * _.isObject([1, 2, 3]);
	     * // => true
	     *
	     * _.isObject(_.noop);
	     * // => true
	     *
	     * _.isObject(null);
	     * // => false
	     */
	    function isObject(value) {
	      var type = typeof value;
	      return value != null && (type == 'object' || type == 'function');
	    }

	    /**
	     * Checks if `value` is object-like. A value is object-like if it's not `null`
	     * and has a `typeof` result of "object".
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
	     * @example
	     *
	     * _.isObjectLike({});
	     * // => true
	     *
	     * _.isObjectLike([1, 2, 3]);
	     * // => true
	     *
	     * _.isObjectLike(_.noop);
	     * // => false
	     *
	     * _.isObjectLike(null);
	     * // => false
	     */
	    function isObjectLike(value) {
	      return value != null && typeof value == 'object';
	    }

	    /**
	     * Checks if `value` is classified as a `Map` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.3.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a map, else `false`.
	     * @example
	     *
	     * _.isMap(new Map);
	     * // => true
	     *
	     * _.isMap(new WeakMap);
	     * // => false
	     */
	    var isMap = nodeIsMap ? baseUnary(nodeIsMap) : baseIsMap;

	    /**
	     * Performs a partial deep comparison between `object` and `source` to
	     * determine if `object` contains equivalent property values.
	     *
	     * **Note:** This method is equivalent to `_.matches` when `source` is
	     * partially applied.
	     *
	     * Partial comparisons will match empty array and empty object `source`
	     * values against any array or object value, respectively. See `_.isEqual`
	     * for a list of supported value comparisons.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Lang
	     * @param {Object} object The object to inspect.
	     * @param {Object} source The object of property values to match.
	     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': 2 };
	     *
	     * _.isMatch(object, { 'b': 2 });
	     * // => true
	     *
	     * _.isMatch(object, { 'b': 1 });
	     * // => false
	     */
	    function isMatch(object, source) {
	      return object === source || baseIsMatch(object, source, getMatchData(source));
	    }

	    /**
	     * This method is like `_.isMatch` except that it accepts `customizer` which
	     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
	     * are handled by the method instead. The `customizer` is invoked with five
	     * arguments: (objValue, srcValue, index|key, object, source).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {Object} object The object to inspect.
	     * @param {Object} source The object of property values to match.
	     * @param {Function} [customizer] The function to customize comparisons.
	     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
	     * @example
	     *
	     * function isGreeting(value) {
	     *   return /^h(?:i|ello)$/.test(value);
	     * }
	     *
	     * function customizer(objValue, srcValue) {
	     *   if (isGreeting(objValue) && isGreeting(srcValue)) {
	     *     return true;
	     *   }
	     * }
	     *
	     * var object = { 'greeting': 'hello' };
	     * var source = { 'greeting': 'hi' };
	     *
	     * _.isMatchWith(object, source, customizer);
	     * // => true
	     */
	    function isMatchWith(object, source, customizer) {
	      customizer = typeof customizer == 'function' ? customizer : undefined$1;
	      return baseIsMatch(object, source, getMatchData(source), customizer);
	    }

	    /**
	     * Checks if `value` is `NaN`.
	     *
	     * **Note:** This method is based on
	     * [`Number.isNaN`](https://mdn.io/Number/isNaN) and is not the same as
	     * global [`isNaN`](https://mdn.io/isNaN) which returns `true` for
	     * `undefined` and other non-number values.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
	     * @example
	     *
	     * _.isNaN(NaN);
	     * // => true
	     *
	     * _.isNaN(new Number(NaN));
	     * // => true
	     *
	     * isNaN(undefined);
	     * // => true
	     *
	     * _.isNaN(undefined);
	     * // => false
	     */
	    function isNaN(value) {
	      // An `NaN` primitive is the only value that is not equal to itself.
	      // Perform the `toStringTag` check first to avoid errors with some
	      // ActiveX objects in IE.
	      return isNumber(value) && value != +value;
	    }

	    /**
	     * Checks if `value` is a pristine native function.
	     *
	     * **Note:** This method can't reliably detect native functions in the presence
	     * of the core-js package because core-js circumvents this kind of detection.
	     * Despite multiple requests, the core-js maintainer has made it clear: any
	     * attempt to fix the detection will be obstructed. As a result, we're left
	     * with little choice but to throw an error. Unfortunately, this also affects
	     * packages, like [babel-polyfill](https://www.npmjs.com/package/babel-polyfill),
	     * which rely on core-js.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a native function,
	     *  else `false`.
	     * @example
	     *
	     * _.isNative(Array.prototype.push);
	     * // => true
	     *
	     * _.isNative(_);
	     * // => false
	     */
	    function isNative(value) {
	      if (isMaskable(value)) {
	        throw new Error(CORE_ERROR_TEXT);
	      }
	      return baseIsNative(value);
	    }

	    /**
	     * Checks if `value` is `null`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is `null`, else `false`.
	     * @example
	     *
	     * _.isNull(null);
	     * // => true
	     *
	     * _.isNull(void 0);
	     * // => false
	     */
	    function isNull(value) {
	      return value === null;
	    }

	    /**
	     * Checks if `value` is `null` or `undefined`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
	     * @example
	     *
	     * _.isNil(null);
	     * // => true
	     *
	     * _.isNil(void 0);
	     * // => true
	     *
	     * _.isNil(NaN);
	     * // => false
	     */
	    function isNil(value) {
	      return value == null;
	    }

	    /**
	     * Checks if `value` is classified as a `Number` primitive or object.
	     *
	     * **Note:** To exclude `Infinity`, `-Infinity`, and `NaN`, which are
	     * classified as numbers, use the `_.isFinite` method.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a number, else `false`.
	     * @example
	     *
	     * _.isNumber(3);
	     * // => true
	     *
	     * _.isNumber(Number.MIN_VALUE);
	     * // => true
	     *
	     * _.isNumber(Infinity);
	     * // => true
	     *
	     * _.isNumber('3');
	     * // => false
	     */
	    function isNumber(value) {
	      return typeof value == 'number' ||
	        (isObjectLike(value) && baseGetTag(value) == numberTag);
	    }

	    /**
	     * Checks if `value` is a plain object, that is, an object created by the
	     * `Object` constructor or one with a `[[Prototype]]` of `null`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.8.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     * }
	     *
	     * _.isPlainObject(new Foo);
	     * // => false
	     *
	     * _.isPlainObject([1, 2, 3]);
	     * // => false
	     *
	     * _.isPlainObject({ 'x': 0, 'y': 0 });
	     * // => true
	     *
	     * _.isPlainObject(Object.create(null));
	     * // => true
	     */
	    function isPlainObject(value) {
	      if (!isObjectLike(value) || baseGetTag(value) != objectTag) {
	        return false;
	      }
	      var proto = getPrototype(value);
	      if (proto === null) {
	        return true;
	      }
	      var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
	      return typeof Ctor == 'function' && Ctor instanceof Ctor &&
	        funcToString.call(Ctor) == objectCtorString;
	    }

	    /**
	     * Checks if `value` is classified as a `RegExp` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.1.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
	     * @example
	     *
	     * _.isRegExp(/abc/);
	     * // => true
	     *
	     * _.isRegExp('/abc/');
	     * // => false
	     */
	    var isRegExp = nodeIsRegExp ? baseUnary(nodeIsRegExp) : baseIsRegExp;

	    /**
	     * Checks if `value` is a safe integer. An integer is safe if it's an IEEE-754
	     * double precision number which isn't the result of a rounded unsafe integer.
	     *
	     * **Note:** This method is based on
	     * [`Number.isSafeInteger`](https://mdn.io/Number/isSafeInteger).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a safe integer, else `false`.
	     * @example
	     *
	     * _.isSafeInteger(3);
	     * // => true
	     *
	     * _.isSafeInteger(Number.MIN_VALUE);
	     * // => false
	     *
	     * _.isSafeInteger(Infinity);
	     * // => false
	     *
	     * _.isSafeInteger('3');
	     * // => false
	     */
	    function isSafeInteger(value) {
	      return isInteger(value) && value >= -MAX_SAFE_INTEGER && value <= MAX_SAFE_INTEGER;
	    }

	    /**
	     * Checks if `value` is classified as a `Set` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.3.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a set, else `false`.
	     * @example
	     *
	     * _.isSet(new Set);
	     * // => true
	     *
	     * _.isSet(new WeakSet);
	     * // => false
	     */
	    var isSet = nodeIsSet ? baseUnary(nodeIsSet) : baseIsSet;

	    /**
	     * Checks if `value` is classified as a `String` primitive or object.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a string, else `false`.
	     * @example
	     *
	     * _.isString('abc');
	     * // => true
	     *
	     * _.isString(1);
	     * // => false
	     */
	    function isString(value) {
	      return typeof value == 'string' ||
	        (!isArray(value) && isObjectLike(value) && baseGetTag(value) == stringTag);
	    }

	    /**
	     * Checks if `value` is classified as a `Symbol` primitive or object.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
	     * @example
	     *
	     * _.isSymbol(Symbol.iterator);
	     * // => true
	     *
	     * _.isSymbol('abc');
	     * // => false
	     */
	    function isSymbol(value) {
	      return typeof value == 'symbol' ||
	        (isObjectLike(value) && baseGetTag(value) == symbolTag);
	    }

	    /**
	     * Checks if `value` is classified as a typed array.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
	     * @example
	     *
	     * _.isTypedArray(new Uint8Array);
	     * // => true
	     *
	     * _.isTypedArray([]);
	     * // => false
	     */
	    var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

	    /**
	     * Checks if `value` is `undefined`.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is `undefined`, else `false`.
	     * @example
	     *
	     * _.isUndefined(void 0);
	     * // => true
	     *
	     * _.isUndefined(null);
	     * // => false
	     */
	    function isUndefined(value) {
	      return value === undefined$1;
	    }

	    /**
	     * Checks if `value` is classified as a `WeakMap` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.3.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a weak map, else `false`.
	     * @example
	     *
	     * _.isWeakMap(new WeakMap);
	     * // => true
	     *
	     * _.isWeakMap(new Map);
	     * // => false
	     */
	    function isWeakMap(value) {
	      return isObjectLike(value) && getTag(value) == weakMapTag;
	    }

	    /**
	     * Checks if `value` is classified as a `WeakSet` object.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.3.0
	     * @category Lang
	     * @param {*} value The value to check.
	     * @returns {boolean} Returns `true` if `value` is a weak set, else `false`.
	     * @example
	     *
	     * _.isWeakSet(new WeakSet);
	     * // => true
	     *
	     * _.isWeakSet(new Set);
	     * // => false
	     */
	    function isWeakSet(value) {
	      return isObjectLike(value) && baseGetTag(value) == weakSetTag;
	    }

	    /**
	     * Checks if `value` is less than `other`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.9.0
	     * @category Lang
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {boolean} Returns `true` if `value` is less than `other`,
	     *  else `false`.
	     * @see _.gt
	     * @example
	     *
	     * _.lt(1, 3);
	     * // => true
	     *
	     * _.lt(3, 3);
	     * // => false
	     *
	     * _.lt(3, 1);
	     * // => false
	     */
	    var lt = createRelationalOperation(baseLt);

	    /**
	     * Checks if `value` is less than or equal to `other`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.9.0
	     * @category Lang
	     * @param {*} value The value to compare.
	     * @param {*} other The other value to compare.
	     * @returns {boolean} Returns `true` if `value` is less than or equal to
	     *  `other`, else `false`.
	     * @see _.gte
	     * @example
	     *
	     * _.lte(1, 3);
	     * // => true
	     *
	     * _.lte(3, 3);
	     * // => true
	     *
	     * _.lte(3, 1);
	     * // => false
	     */
	    var lte = createRelationalOperation(function(value, other) {
	      return value <= other;
	    });

	    /**
	     * Converts `value` to an array.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Lang
	     * @param {*} value The value to convert.
	     * @returns {Array} Returns the converted array.
	     * @example
	     *
	     * _.toArray({ 'a': 1, 'b': 2 });
	     * // => [1, 2]
	     *
	     * _.toArray('abc');
	     * // => ['a', 'b', 'c']
	     *
	     * _.toArray(1);
	     * // => []
	     *
	     * _.toArray(null);
	     * // => []
	     */
	    function toArray(value) {
	      if (!value) {
	        return [];
	      }
	      if (isArrayLike(value)) {
	        return isString(value) ? stringToArray(value) : copyArray(value);
	      }
	      if (symIterator && value[symIterator]) {
	        return iteratorToArray(value[symIterator]());
	      }
	      var tag = getTag(value),
	          func = tag == mapTag ? mapToArray : (tag == setTag ? setToArray : values);

	      return func(value);
	    }

	    /**
	     * Converts `value` to a finite number.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.12.0
	     * @category Lang
	     * @param {*} value The value to convert.
	     * @returns {number} Returns the converted number.
	     * @example
	     *
	     * _.toFinite(3.2);
	     * // => 3.2
	     *
	     * _.toFinite(Number.MIN_VALUE);
	     * // => 5e-324
	     *
	     * _.toFinite(Infinity);
	     * // => 1.7976931348623157e+308
	     *
	     * _.toFinite('3.2');
	     * // => 3.2
	     */
	    function toFinite(value) {
	      if (!value) {
	        return value === 0 ? value : 0;
	      }
	      value = toNumber(value);
	      if (value === INFINITY || value === -INFINITY) {
	        var sign = (value < 0 ? -1 : 1);
	        return sign * MAX_INTEGER;
	      }
	      return value === value ? value : 0;
	    }

	    /**
	     * Converts `value` to an integer.
	     *
	     * **Note:** This method is loosely based on
	     * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to convert.
	     * @returns {number} Returns the converted integer.
	     * @example
	     *
	     * _.toInteger(3.2);
	     * // => 3
	     *
	     * _.toInteger(Number.MIN_VALUE);
	     * // => 0
	     *
	     * _.toInteger(Infinity);
	     * // => 1.7976931348623157e+308
	     *
	     * _.toInteger('3.2');
	     * // => 3
	     */
	    function toInteger(value) {
	      var result = toFinite(value),
	          remainder = result % 1;

	      return result === result ? (remainder ? result - remainder : result) : 0;
	    }

	    /**
	     * Converts `value` to an integer suitable for use as the length of an
	     * array-like object.
	     *
	     * **Note:** This method is based on
	     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to convert.
	     * @returns {number} Returns the converted integer.
	     * @example
	     *
	     * _.toLength(3.2);
	     * // => 3
	     *
	     * _.toLength(Number.MIN_VALUE);
	     * // => 0
	     *
	     * _.toLength(Infinity);
	     * // => 4294967295
	     *
	     * _.toLength('3.2');
	     * // => 3
	     */
	    function toLength(value) {
	      return value ? baseClamp(toInteger(value), 0, MAX_ARRAY_LENGTH) : 0;
	    }

	    /**
	     * Converts `value` to a number.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to process.
	     * @returns {number} Returns the number.
	     * @example
	     *
	     * _.toNumber(3.2);
	     * // => 3.2
	     *
	     * _.toNumber(Number.MIN_VALUE);
	     * // => 5e-324
	     *
	     * _.toNumber(Infinity);
	     * // => Infinity
	     *
	     * _.toNumber('3.2');
	     * // => 3.2
	     */
	    function toNumber(value) {
	      if (typeof value == 'number') {
	        return value;
	      }
	      if (isSymbol(value)) {
	        return NAN;
	      }
	      if (isObject(value)) {
	        var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
	        value = isObject(other) ? (other + '') : other;
	      }
	      if (typeof value != 'string') {
	        return value === 0 ? value : +value;
	      }
	      value = baseTrim(value);
	      var isBinary = reIsBinary.test(value);
	      return (isBinary || reIsOctal.test(value))
	        ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
	        : (reIsBadHex.test(value) ? NAN : +value);
	    }

	    /**
	     * Converts `value` to a plain object flattening inherited enumerable string
	     * keyed properties of `value` to own properties of the plain object.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Lang
	     * @param {*} value The value to convert.
	     * @returns {Object} Returns the converted plain object.
	     * @example
	     *
	     * function Foo() {
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.assign({ 'a': 1 }, new Foo);
	     * // => { 'a': 1, 'b': 2 }
	     *
	     * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
	     * // => { 'a': 1, 'b': 2, 'c': 3 }
	     */
	    function toPlainObject(value) {
	      return copyObject(value, keysIn(value));
	    }

	    /**
	     * Converts `value` to a safe integer. A safe integer can be compared and
	     * represented correctly.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to convert.
	     * @returns {number} Returns the converted integer.
	     * @example
	     *
	     * _.toSafeInteger(3.2);
	     * // => 3
	     *
	     * _.toSafeInteger(Number.MIN_VALUE);
	     * // => 0
	     *
	     * _.toSafeInteger(Infinity);
	     * // => 9007199254740991
	     *
	     * _.toSafeInteger('3.2');
	     * // => 3
	     */
	    function toSafeInteger(value) {
	      return value
	        ? baseClamp(toInteger(value), -MAX_SAFE_INTEGER, MAX_SAFE_INTEGER)
	        : (value === 0 ? value : 0);
	    }

	    /**
	     * Converts `value` to a string. An empty string is returned for `null`
	     * and `undefined` values. The sign of `-0` is preserved.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Lang
	     * @param {*} value The value to convert.
	     * @returns {string} Returns the converted string.
	     * @example
	     *
	     * _.toString(null);
	     * // => ''
	     *
	     * _.toString(-0);
	     * // => '-0'
	     *
	     * _.toString([1, 2, 3]);
	     * // => '1,2,3'
	     */
	    function toString(value) {
	      return value == null ? '' : baseToString(value);
	    }

	    /*------------------------------------------------------------------------*/

	    /**
	     * Assigns own enumerable string keyed properties of source objects to the
	     * destination object. Source objects are applied from left to right.
	     * Subsequent sources overwrite property assignments of previous sources.
	     *
	     * **Note:** This method mutates `object` and is loosely based on
	     * [`Object.assign`](https://mdn.io/Object/assign).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.10.0
	     * @category Object
	     * @param {Object} object The destination object.
	     * @param {...Object} [sources] The source objects.
	     * @returns {Object} Returns `object`.
	     * @see _.assignIn
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     * }
	     *
	     * function Bar() {
	     *   this.c = 3;
	     * }
	     *
	     * Foo.prototype.b = 2;
	     * Bar.prototype.d = 4;
	     *
	     * _.assign({ 'a': 0 }, new Foo, new Bar);
	     * // => { 'a': 1, 'c': 3 }
	     */
	    var assign = createAssigner(function(object, source) {
	      if (isPrototype(source) || isArrayLike(source)) {
	        copyObject(source, keys(source), object);
	        return;
	      }
	      for (var key in source) {
	        if (hasOwnProperty.call(source, key)) {
	          assignValue(object, key, source[key]);
	        }
	      }
	    });

	    /**
	     * This method is like `_.assign` except that it iterates over own and
	     * inherited source properties.
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @alias extend
	     * @category Object
	     * @param {Object} object The destination object.
	     * @param {...Object} [sources] The source objects.
	     * @returns {Object} Returns `object`.
	     * @see _.assign
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     * }
	     *
	     * function Bar() {
	     *   this.c = 3;
	     * }
	     *
	     * Foo.prototype.b = 2;
	     * Bar.prototype.d = 4;
	     *
	     * _.assignIn({ 'a': 0 }, new Foo, new Bar);
	     * // => { 'a': 1, 'b': 2, 'c': 3, 'd': 4 }
	     */
	    var assignIn = createAssigner(function(object, source) {
	      copyObject(source, keysIn(source), object);
	    });

	    /**
	     * This method is like `_.assignIn` except that it accepts `customizer`
	     * which is invoked to produce the assigned values. If `customizer` returns
	     * `undefined`, assignment is handled by the method instead. The `customizer`
	     * is invoked with five arguments: (objValue, srcValue, key, object, source).
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @alias extendWith
	     * @category Object
	     * @param {Object} object The destination object.
	     * @param {...Object} sources The source objects.
	     * @param {Function} [customizer] The function to customize assigned values.
	     * @returns {Object} Returns `object`.
	     * @see _.assignWith
	     * @example
	     *
	     * function customizer(objValue, srcValue) {
	     *   return _.isUndefined(objValue) ? srcValue : objValue;
	     * }
	     *
	     * var defaults = _.partialRight(_.assignInWith, customizer);
	     *
	     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
	     * // => { 'a': 1, 'b': 2 }
	     */
	    var assignInWith = createAssigner(function(object, source, srcIndex, customizer) {
	      copyObject(source, keysIn(source), object, customizer);
	    });

	    /**
	     * This method is like `_.assign` except that it accepts `customizer`
	     * which is invoked to produce the assigned values. If `customizer` returns
	     * `undefined`, assignment is handled by the method instead. The `customizer`
	     * is invoked with five arguments: (objValue, srcValue, key, object, source).
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The destination object.
	     * @param {...Object} sources The source objects.
	     * @param {Function} [customizer] The function to customize assigned values.
	     * @returns {Object} Returns `object`.
	     * @see _.assignInWith
	     * @example
	     *
	     * function customizer(objValue, srcValue) {
	     *   return _.isUndefined(objValue) ? srcValue : objValue;
	     * }
	     *
	     * var defaults = _.partialRight(_.assignWith, customizer);
	     *
	     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
	     * // => { 'a': 1, 'b': 2 }
	     */
	    var assignWith = createAssigner(function(object, source, srcIndex, customizer) {
	      copyObject(source, keys(source), object, customizer);
	    });

	    /**
	     * Creates an array of values corresponding to `paths` of `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.0.0
	     * @category Object
	     * @param {Object} object The object to iterate over.
	     * @param {...(string|string[])} [paths] The property paths to pick.
	     * @returns {Array} Returns the picked values.
	     * @example
	     *
	     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
	     *
	     * _.at(object, ['a[0].b.c', 'a[1]']);
	     * // => [3, 4]
	     */
	    var at = flatRest(baseAt);

	    /**
	     * Creates an object that inherits from the `prototype` object. If a
	     * `properties` object is given, its own enumerable string keyed properties
	     * are assigned to the created object.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.3.0
	     * @category Object
	     * @param {Object} prototype The object to inherit from.
	     * @param {Object} [properties] The properties to assign to the object.
	     * @returns {Object} Returns the new object.
	     * @example
	     *
	     * function Shape() {
	     *   this.x = 0;
	     *   this.y = 0;
	     * }
	     *
	     * function Circle() {
	     *   Shape.call(this);
	     * }
	     *
	     * Circle.prototype = _.create(Shape.prototype, {
	     *   'constructor': Circle
	     * });
	     *
	     * var circle = new Circle;
	     * circle instanceof Circle;
	     * // => true
	     *
	     * circle instanceof Shape;
	     * // => true
	     */
	    function create(prototype, properties) {
	      var result = baseCreate(prototype);
	      return properties == null ? result : baseAssign(result, properties);
	    }

	    /**
	     * Assigns own and inherited enumerable string keyed properties of source
	     * objects to the destination object for all destination properties that
	     * resolve to `undefined`. Source objects are applied from left to right.
	     * Once a property is set, additional values of the same property are ignored.
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Object
	     * @param {Object} object The destination object.
	     * @param {...Object} [sources] The source objects.
	     * @returns {Object} Returns `object`.
	     * @see _.defaultsDeep
	     * @example
	     *
	     * _.defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
	     * // => { 'a': 1, 'b': 2 }
	     */
	    var defaults = baseRest(function(object, sources) {
	      object = Object(object);

	      var index = -1;
	      var length = sources.length;
	      var guard = length > 2 ? sources[2] : undefined$1;

	      if (guard && isIterateeCall(sources[0], sources[1], guard)) {
	        length = 1;
	      }

	      while (++index < length) {
	        var source = sources[index];
	        var props = keysIn(source);
	        var propsIndex = -1;
	        var propsLength = props.length;

	        while (++propsIndex < propsLength) {
	          var key = props[propsIndex];
	          var value = object[key];

	          if (value === undefined$1 ||
	              (eq(value, objectProto[key]) && !hasOwnProperty.call(object, key))) {
	            object[key] = source[key];
	          }
	        }
	      }

	      return object;
	    });

	    /**
	     * This method is like `_.defaults` except that it recursively assigns
	     * default properties.
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.10.0
	     * @category Object
	     * @param {Object} object The destination object.
	     * @param {...Object} [sources] The source objects.
	     * @returns {Object} Returns `object`.
	     * @see _.defaults
	     * @example
	     *
	     * _.defaultsDeep({ 'a': { 'b': 2 } }, { 'a': { 'b': 1, 'c': 3 } });
	     * // => { 'a': { 'b': 2, 'c': 3 } }
	     */
	    var defaultsDeep = baseRest(function(args) {
	      args.push(undefined$1, customDefaultsMerge);
	      return apply(mergeWith, undefined$1, args);
	    });

	    /**
	     * This method is like `_.find` except that it returns the key of the first
	     * element `predicate` returns truthy for instead of the element itself.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.1.0
	     * @category Object
	     * @param {Object} object The object to inspect.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {string|undefined} Returns the key of the matched element,
	     *  else `undefined`.
	     * @example
	     *
	     * var users = {
	     *   'barney':  { 'age': 36, 'active': true },
	     *   'fred':    { 'age': 40, 'active': false },
	     *   'pebbles': { 'age': 1,  'active': true }
	     * };
	     *
	     * _.findKey(users, function(o) { return o.age < 40; });
	     * // => 'barney' (iteration order is not guaranteed)
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.findKey(users, { 'age': 1, 'active': true });
	     * // => 'pebbles'
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.findKey(users, ['active', false]);
	     * // => 'fred'
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.findKey(users, 'active');
	     * // => 'barney'
	     */
	    function findKey(object, predicate) {
	      return baseFindKey(object, getIteratee(predicate, 3), baseForOwn);
	    }

	    /**
	     * This method is like `_.findKey` except that it iterates over elements of
	     * a collection in the opposite order.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Object
	     * @param {Object} object The object to inspect.
	     * @param {Function} [predicate=_.identity] The function invoked per iteration.
	     * @returns {string|undefined} Returns the key of the matched element,
	     *  else `undefined`.
	     * @example
	     *
	     * var users = {
	     *   'barney':  { 'age': 36, 'active': true },
	     *   'fred':    { 'age': 40, 'active': false },
	     *   'pebbles': { 'age': 1,  'active': true }
	     * };
	     *
	     * _.findLastKey(users, function(o) { return o.age < 40; });
	     * // => returns 'pebbles' assuming `_.findKey` returns 'barney'
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.findLastKey(users, { 'age': 36, 'active': true });
	     * // => 'barney'
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.findLastKey(users, ['active', false]);
	     * // => 'fred'
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.findLastKey(users, 'active');
	     * // => 'pebbles'
	     */
	    function findLastKey(object, predicate) {
	      return baseFindKey(object, getIteratee(predicate, 3), baseForOwnRight);
	    }

	    /**
	     * Iterates over own and inherited enumerable string keyed properties of an
	     * object and invokes `iteratee` for each property. The iteratee is invoked
	     * with three arguments: (value, key, object). Iteratee functions may exit
	     * iteration early by explicitly returning `false`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.3.0
	     * @category Object
	     * @param {Object} object The object to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Object} Returns `object`.
	     * @see _.forInRight
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.forIn(new Foo, function(value, key) {
	     *   console.log(key);
	     * });
	     * // => Logs 'a', 'b', then 'c' (iteration order is not guaranteed).
	     */
	    function forIn(object, iteratee) {
	      return object == null
	        ? object
	        : baseFor(object, getIteratee(iteratee, 3), keysIn);
	    }

	    /**
	     * This method is like `_.forIn` except that it iterates over properties of
	     * `object` in the opposite order.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Object
	     * @param {Object} object The object to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Object} Returns `object`.
	     * @see _.forIn
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.forInRight(new Foo, function(value, key) {
	     *   console.log(key);
	     * });
	     * // => Logs 'c', 'b', then 'a' assuming `_.forIn` logs 'a', 'b', then 'c'.
	     */
	    function forInRight(object, iteratee) {
	      return object == null
	        ? object
	        : baseForRight(object, getIteratee(iteratee, 3), keysIn);
	    }

	    /**
	     * Iterates over own enumerable string keyed properties of an object and
	     * invokes `iteratee` for each property. The iteratee is invoked with three
	     * arguments: (value, key, object). Iteratee functions may exit iteration
	     * early by explicitly returning `false`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.3.0
	     * @category Object
	     * @param {Object} object The object to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Object} Returns `object`.
	     * @see _.forOwnRight
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.forOwn(new Foo, function(value, key) {
	     *   console.log(key);
	     * });
	     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
	     */
	    function forOwn(object, iteratee) {
	      return object && baseForOwn(object, getIteratee(iteratee, 3));
	    }

	    /**
	     * This method is like `_.forOwn` except that it iterates over properties of
	     * `object` in the opposite order.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.0.0
	     * @category Object
	     * @param {Object} object The object to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Object} Returns `object`.
	     * @see _.forOwn
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.forOwnRight(new Foo, function(value, key) {
	     *   console.log(key);
	     * });
	     * // => Logs 'b' then 'a' assuming `_.forOwn` logs 'a' then 'b'.
	     */
	    function forOwnRight(object, iteratee) {
	      return object && baseForOwnRight(object, getIteratee(iteratee, 3));
	    }

	    /**
	     * Creates an array of function property names from own enumerable properties
	     * of `object`.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Object
	     * @param {Object} object The object to inspect.
	     * @returns {Array} Returns the function names.
	     * @see _.functionsIn
	     * @example
	     *
	     * function Foo() {
	     *   this.a = _.constant('a');
	     *   this.b = _.constant('b');
	     * }
	     *
	     * Foo.prototype.c = _.constant('c');
	     *
	     * _.functions(new Foo);
	     * // => ['a', 'b']
	     */
	    function functions(object) {
	      return object == null ? [] : baseFunctions(object, keys(object));
	    }

	    /**
	     * Creates an array of function property names from own and inherited
	     * enumerable properties of `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The object to inspect.
	     * @returns {Array} Returns the function names.
	     * @see _.functions
	     * @example
	     *
	     * function Foo() {
	     *   this.a = _.constant('a');
	     *   this.b = _.constant('b');
	     * }
	     *
	     * Foo.prototype.c = _.constant('c');
	     *
	     * _.functionsIn(new Foo);
	     * // => ['a', 'b', 'c']
	     */
	    function functionsIn(object) {
	      return object == null ? [] : baseFunctions(object, keysIn(object));
	    }

	    /**
	     * Gets the value at `path` of `object`. If the resolved value is
	     * `undefined`, the `defaultValue` is returned in its place.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.7.0
	     * @category Object
	     * @param {Object} object The object to query.
	     * @param {Array|string} path The path of the property to get.
	     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
	     * @returns {*} Returns the resolved value.
	     * @example
	     *
	     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
	     *
	     * _.get(object, 'a[0].b.c');
	     * // => 3
	     *
	     * _.get(object, ['a', '0', 'b', 'c']);
	     * // => 3
	     *
	     * _.get(object, 'a.b.c', 'default');
	     * // => 'default'
	     */
	    function get(object, path, defaultValue) {
	      var result = object == null ? undefined$1 : baseGet(object, path);
	      return result === undefined$1 ? defaultValue : result;
	    }

	    /**
	     * Checks if `path` is a direct property of `object`.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Object
	     * @param {Object} object The object to query.
	     * @param {Array|string} path The path to check.
	     * @returns {boolean} Returns `true` if `path` exists, else `false`.
	     * @example
	     *
	     * var object = { 'a': { 'b': 2 } };
	     * var other = _.create({ 'a': _.create({ 'b': 2 }) });
	     *
	     * _.has(object, 'a');
	     * // => true
	     *
	     * _.has(object, 'a.b');
	     * // => true
	     *
	     * _.has(object, ['a', 'b']);
	     * // => true
	     *
	     * _.has(other, 'a');
	     * // => false
	     */
	    function has(object, path) {
	      return object != null && hasPath(object, path, baseHas);
	    }

	    /**
	     * Checks if `path` is a direct or inherited property of `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The object to query.
	     * @param {Array|string} path The path to check.
	     * @returns {boolean} Returns `true` if `path` exists, else `false`.
	     * @example
	     *
	     * var object = _.create({ 'a': _.create({ 'b': 2 }) });
	     *
	     * _.hasIn(object, 'a');
	     * // => true
	     *
	     * _.hasIn(object, 'a.b');
	     * // => true
	     *
	     * _.hasIn(object, ['a', 'b']);
	     * // => true
	     *
	     * _.hasIn(object, 'b');
	     * // => false
	     */
	    function hasIn(object, path) {
	      return object != null && hasPath(object, path, baseHasIn);
	    }

	    /**
	     * Creates an object composed of the inverted keys and values of `object`.
	     * If `object` contains duplicate values, subsequent values overwrite
	     * property assignments of previous values.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.7.0
	     * @category Object
	     * @param {Object} object The object to invert.
	     * @returns {Object} Returns the new inverted object.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': 2, 'c': 1 };
	     *
	     * _.invert(object);
	     * // => { '1': 'c', '2': 'b' }
	     */
	    var invert = createInverter(function(result, value, key) {
	      if (value != null &&
	          typeof value.toString != 'function') {
	        value = nativeObjectToString.call(value);
	      }

	      result[value] = key;
	    }, constant(identity));

	    /**
	     * This method is like `_.invert` except that the inverted object is generated
	     * from the results of running each element of `object` thru `iteratee`. The
	     * corresponding inverted value of each inverted key is an array of keys
	     * responsible for generating the inverted value. The iteratee is invoked
	     * with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.1.0
	     * @category Object
	     * @param {Object} object The object to invert.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {Object} Returns the new inverted object.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': 2, 'c': 1 };
	     *
	     * _.invertBy(object);
	     * // => { '1': ['a', 'c'], '2': ['b'] }
	     *
	     * _.invertBy(object, function(value) {
	     *   return 'group' + value;
	     * });
	     * // => { 'group1': ['a', 'c'], 'group2': ['b'] }
	     */
	    var invertBy = createInverter(function(result, value, key) {
	      if (value != null &&
	          typeof value.toString != 'function') {
	        value = nativeObjectToString.call(value);
	      }

	      if (hasOwnProperty.call(result, value)) {
	        result[value].push(key);
	      } else {
	        result[value] = [key];
	      }
	    }, getIteratee);

	    /**
	     * Invokes the method at `path` of `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The object to query.
	     * @param {Array|string} path The path of the method to invoke.
	     * @param {...*} [args] The arguments to invoke the method with.
	     * @returns {*} Returns the result of the invoked method.
	     * @example
	     *
	     * var object = { 'a': [{ 'b': { 'c': [1, 2, 3, 4] } }] };
	     *
	     * _.invoke(object, 'a[0].b.c.slice', 1, 3);
	     * // => [2, 3]
	     */
	    var invoke = baseRest(baseInvoke);

	    /**
	     * Creates an array of the own enumerable property names of `object`.
	     *
	     * **Note:** Non-object values are coerced to objects. See the
	     * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
	     * for more details.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Object
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property names.
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.keys(new Foo);
	     * // => ['a', 'b'] (iteration order is not guaranteed)
	     *
	     * _.keys('hi');
	     * // => ['0', '1']
	     */
	    function keys(object) {
	      return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
	    }

	    /**
	     * Creates an array of the own and inherited enumerable property names of `object`.
	     *
	     * **Note:** Non-object values are coerced to objects.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Object
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property names.
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.keysIn(new Foo);
	     * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
	     */
	    function keysIn(object) {
	      return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
	    }

	    /**
	     * The opposite of `_.mapValues`; this method creates an object with the
	     * same values as `object` and keys generated by running each own enumerable
	     * string keyed property of `object` thru `iteratee`. The iteratee is invoked
	     * with three arguments: (value, key, object).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.8.0
	     * @category Object
	     * @param {Object} object The object to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Object} Returns the new mapped object.
	     * @see _.mapValues
	     * @example
	     *
	     * _.mapKeys({ 'a': 1, 'b': 2 }, function(value, key) {
	     *   return key + value;
	     * });
	     * // => { 'a1': 1, 'b2': 2 }
	     */
	    function mapKeys(object, iteratee) {
	      var result = {};
	      iteratee = getIteratee(iteratee, 3);

	      baseForOwn(object, function(value, key, object) {
	        baseAssignValue(result, iteratee(value, key, object), value);
	      });
	      return result;
	    }

	    /**
	     * Creates an object with the same keys as `object` and values generated
	     * by running each own enumerable string keyed property of `object` thru
	     * `iteratee`. The iteratee is invoked with three arguments:
	     * (value, key, object).
	     *
	     * @static
	     * @memberOf _
	     * @since 2.4.0
	     * @category Object
	     * @param {Object} object The object to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Object} Returns the new mapped object.
	     * @see _.mapKeys
	     * @example
	     *
	     * var users = {
	     *   'fred':    { 'user': 'fred',    'age': 40 },
	     *   'pebbles': { 'user': 'pebbles', 'age': 1 }
	     * };
	     *
	     * _.mapValues(users, function(o) { return o.age; });
	     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.mapValues(users, 'age');
	     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
	     */
	    function mapValues(object, iteratee) {
	      var result = {};
	      iteratee = getIteratee(iteratee, 3);

	      baseForOwn(object, function(value, key, object) {
	        baseAssignValue(result, key, iteratee(value, key, object));
	      });
	      return result;
	    }

	    /**
	     * This method is like `_.assign` except that it recursively merges own and
	     * inherited enumerable string keyed properties of source objects into the
	     * destination object. Source properties that resolve to `undefined` are
	     * skipped if a destination value exists. Array and plain object properties
	     * are merged recursively. Other objects and value types are overridden by
	     * assignment. Source objects are applied from left to right. Subsequent
	     * sources overwrite property assignments of previous sources.
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.5.0
	     * @category Object
	     * @param {Object} object The destination object.
	     * @param {...Object} [sources] The source objects.
	     * @returns {Object} Returns `object`.
	     * @example
	     *
	     * var object = {
	     *   'a': [{ 'b': 2 }, { 'd': 4 }]
	     * };
	     *
	     * var other = {
	     *   'a': [{ 'c': 3 }, { 'e': 5 }]
	     * };
	     *
	     * _.merge(object, other);
	     * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
	     */
	    var merge = createAssigner(function(object, source, srcIndex) {
	      baseMerge(object, source, srcIndex);
	    });

	    /**
	     * This method is like `_.merge` except that it accepts `customizer` which
	     * is invoked to produce the merged values of the destination and source
	     * properties. If `customizer` returns `undefined`, merging is handled by the
	     * method instead. The `customizer` is invoked with six arguments:
	     * (objValue, srcValue, key, object, source, stack).
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The destination object.
	     * @param {...Object} sources The source objects.
	     * @param {Function} customizer The function to customize assigned values.
	     * @returns {Object} Returns `object`.
	     * @example
	     *
	     * function customizer(objValue, srcValue) {
	     *   if (_.isArray(objValue)) {
	     *     return objValue.concat(srcValue);
	     *   }
	     * }
	     *
	     * var object = { 'a': [1], 'b': [2] };
	     * var other = { 'a': [3], 'b': [4] };
	     *
	     * _.mergeWith(object, other, customizer);
	     * // => { 'a': [1, 3], 'b': [2, 4] }
	     */
	    var mergeWith = createAssigner(function(object, source, srcIndex, customizer) {
	      baseMerge(object, source, srcIndex, customizer);
	    });

	    /**
	     * The opposite of `_.pick`; this method creates an object composed of the
	     * own and inherited enumerable property paths of `object` that are not omitted.
	     *
	     * **Note:** This method is considerably slower than `_.pick`.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Object
	     * @param {Object} object The source object.
	     * @param {...(string|string[])} [paths] The property paths to omit.
	     * @returns {Object} Returns the new object.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': '2', 'c': 3 };
	     *
	     * _.omit(object, ['a', 'c']);
	     * // => { 'b': '2' }
	     */
	    var omit = flatRest(function(object, paths) {
	      var result = {};
	      if (object == null) {
	        return result;
	      }
	      var isDeep = false;
	      paths = arrayMap(paths, function(path) {
	        path = castPath(path, object);
	        isDeep || (isDeep = path.length > 1);
	        return path;
	      });
	      copyObject(object, getAllKeysIn(object), result);
	      if (isDeep) {
	        result = baseClone(result, CLONE_DEEP_FLAG | CLONE_FLAT_FLAG | CLONE_SYMBOLS_FLAG, customOmitClone);
	      }
	      var length = paths.length;
	      while (length--) {
	        baseUnset(result, paths[length]);
	      }
	      return result;
	    });

	    /**
	     * The opposite of `_.pickBy`; this method creates an object composed of
	     * the own and inherited enumerable string keyed properties of `object` that
	     * `predicate` doesn't return truthy for. The predicate is invoked with two
	     * arguments: (value, key).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The source object.
	     * @param {Function} [predicate=_.identity] The function invoked per property.
	     * @returns {Object} Returns the new object.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': '2', 'c': 3 };
	     *
	     * _.omitBy(object, _.isNumber);
	     * // => { 'b': '2' }
	     */
	    function omitBy(object, predicate) {
	      return pickBy(object, negate(getIteratee(predicate)));
	    }

	    /**
	     * Creates an object composed of the picked `object` properties.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Object
	     * @param {Object} object The source object.
	     * @param {...(string|string[])} [paths] The property paths to pick.
	     * @returns {Object} Returns the new object.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': '2', 'c': 3 };
	     *
	     * _.pick(object, ['a', 'c']);
	     * // => { 'a': 1, 'c': 3 }
	     */
	    var pick = flatRest(function(object, paths) {
	      return object == null ? {} : basePick(object, paths);
	    });

	    /**
	     * Creates an object composed of the `object` properties `predicate` returns
	     * truthy for. The predicate is invoked with two arguments: (value, key).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The source object.
	     * @param {Function} [predicate=_.identity] The function invoked per property.
	     * @returns {Object} Returns the new object.
	     * @example
	     *
	     * var object = { 'a': 1, 'b': '2', 'c': 3 };
	     *
	     * _.pickBy(object, _.isNumber);
	     * // => { 'a': 1, 'c': 3 }
	     */
	    function pickBy(object, predicate) {
	      if (object == null) {
	        return {};
	      }
	      var props = arrayMap(getAllKeysIn(object), function(prop) {
	        return [prop];
	      });
	      predicate = getIteratee(predicate);
	      return basePickBy(object, props, function(value, path) {
	        return predicate(value, path[0]);
	      });
	    }

	    /**
	     * This method is like `_.get` except that if the resolved value is a
	     * function it's invoked with the `this` binding of its parent object and
	     * its result is returned.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Object
	     * @param {Object} object The object to query.
	     * @param {Array|string} path The path of the property to resolve.
	     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
	     * @returns {*} Returns the resolved value.
	     * @example
	     *
	     * var object = { 'a': [{ 'b': { 'c1': 3, 'c2': _.constant(4) } }] };
	     *
	     * _.result(object, 'a[0].b.c1');
	     * // => 3
	     *
	     * _.result(object, 'a[0].b.c2');
	     * // => 4
	     *
	     * _.result(object, 'a[0].b.c3', 'default');
	     * // => 'default'
	     *
	     * _.result(object, 'a[0].b.c3', _.constant('default'));
	     * // => 'default'
	     */
	    function result(object, path, defaultValue) {
	      path = castPath(path, object);

	      var index = -1,
	          length = path.length;

	      // Ensure the loop is entered when path is empty.
	      if (!length) {
	        length = 1;
	        object = undefined$1;
	      }
	      while (++index < length) {
	        var value = object == null ? undefined$1 : object[toKey(path[index])];
	        if (value === undefined$1) {
	          index = length;
	          value = defaultValue;
	        }
	        object = isFunction(value) ? value.call(object) : value;
	      }
	      return object;
	    }

	    /**
	     * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
	     * it's created. Arrays are created for missing index properties while objects
	     * are created for all other missing properties. Use `_.setWith` to customize
	     * `path` creation.
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.7.0
	     * @category Object
	     * @param {Object} object The object to modify.
	     * @param {Array|string} path The path of the property to set.
	     * @param {*} value The value to set.
	     * @returns {Object} Returns `object`.
	     * @example
	     *
	     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
	     *
	     * _.set(object, 'a[0].b.c', 4);
	     * console.log(object.a[0].b.c);
	     * // => 4
	     *
	     * _.set(object, ['x', '0', 'y', 'z'], 5);
	     * console.log(object.x[0].y.z);
	     * // => 5
	     */
	    function set(object, path, value) {
	      return object == null ? object : baseSet(object, path, value);
	    }

	    /**
	     * This method is like `_.set` except that it accepts `customizer` which is
	     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
	     * path creation is handled by the method instead. The `customizer` is invoked
	     * with three arguments: (nsValue, key, nsObject).
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The object to modify.
	     * @param {Array|string} path The path of the property to set.
	     * @param {*} value The value to set.
	     * @param {Function} [customizer] The function to customize assigned values.
	     * @returns {Object} Returns `object`.
	     * @example
	     *
	     * var object = {};
	     *
	     * _.setWith(object, '[0][1]', 'a', Object);
	     * // => { '0': { '1': 'a' } }
	     */
	    function setWith(object, path, value, customizer) {
	      customizer = typeof customizer == 'function' ? customizer : undefined$1;
	      return object == null ? object : baseSet(object, path, value, customizer);
	    }

	    /**
	     * Creates an array of own enumerable string keyed-value pairs for `object`
	     * which can be consumed by `_.fromPairs`. If `object` is a map or set, its
	     * entries are returned.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @alias entries
	     * @category Object
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the key-value pairs.
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.toPairs(new Foo);
	     * // => [['a', 1], ['b', 2]] (iteration order is not guaranteed)
	     */
	    var toPairs = createToPairs(keys);

	    /**
	     * Creates an array of own and inherited enumerable string keyed-value pairs
	     * for `object` which can be consumed by `_.fromPairs`. If `object` is a map
	     * or set, its entries are returned.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @alias entriesIn
	     * @category Object
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the key-value pairs.
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.toPairsIn(new Foo);
	     * // => [['a', 1], ['b', 2], ['c', 3]] (iteration order is not guaranteed)
	     */
	    var toPairsIn = createToPairs(keysIn);

	    /**
	     * An alternative to `_.reduce`; this method transforms `object` to a new
	     * `accumulator` object which is the result of running each of its own
	     * enumerable string keyed properties thru `iteratee`, with each invocation
	     * potentially mutating the `accumulator` object. If `accumulator` is not
	     * provided, a new object with the same `[[Prototype]]` will be used. The
	     * iteratee is invoked with four arguments: (accumulator, value, key, object).
	     * Iteratee functions may exit iteration early by explicitly returning `false`.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.3.0
	     * @category Object
	     * @param {Object} object The object to iterate over.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @param {*} [accumulator] The custom accumulator value.
	     * @returns {*} Returns the accumulated value.
	     * @example
	     *
	     * _.transform([2, 3, 4], function(result, n) {
	     *   result.push(n *= n);
	     *   return n % 2 == 0;
	     * }, []);
	     * // => [4, 9]
	     *
	     * _.transform({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
	     *   (result[value] || (result[value] = [])).push(key);
	     * }, {});
	     * // => { '1': ['a', 'c'], '2': ['b'] }
	     */
	    function transform(object, iteratee, accumulator) {
	      var isArr = isArray(object),
	          isArrLike = isArr || isBuffer(object) || isTypedArray(object);

	      iteratee = getIteratee(iteratee, 4);
	      if (accumulator == null) {
	        var Ctor = object && object.constructor;
	        if (isArrLike) {
	          accumulator = isArr ? new Ctor : [];
	        }
	        else if (isObject(object)) {
	          accumulator = isFunction(Ctor) ? baseCreate(getPrototype(object)) : {};
	        }
	        else {
	          accumulator = {};
	        }
	      }
	      (isArrLike ? arrayEach : baseForOwn)(object, function(value, index, object) {
	        return iteratee(accumulator, value, index, object);
	      });
	      return accumulator;
	    }

	    /**
	     * Removes the property at `path` of `object`.
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Object
	     * @param {Object} object The object to modify.
	     * @param {Array|string} path The path of the property to unset.
	     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
	     * @example
	     *
	     * var object = { 'a': [{ 'b': { 'c': 7 } }] };
	     * _.unset(object, 'a[0].b.c');
	     * // => true
	     *
	     * console.log(object);
	     * // => { 'a': [{ 'b': {} }] };
	     *
	     * _.unset(object, ['a', '0', 'b', 'c']);
	     * // => true
	     *
	     * console.log(object);
	     * // => { 'a': [{ 'b': {} }] };
	     */
	    function unset(object, path) {
	      return object == null ? true : baseUnset(object, path);
	    }

	    /**
	     * This method is like `_.set` except that accepts `updater` to produce the
	     * value to set. Use `_.updateWith` to customize `path` creation. The `updater`
	     * is invoked with one argument: (value).
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.6.0
	     * @category Object
	     * @param {Object} object The object to modify.
	     * @param {Array|string} path The path of the property to set.
	     * @param {Function} updater The function to produce the updated value.
	     * @returns {Object} Returns `object`.
	     * @example
	     *
	     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
	     *
	     * _.update(object, 'a[0].b.c', function(n) { return n * n; });
	     * console.log(object.a[0].b.c);
	     * // => 9
	     *
	     * _.update(object, 'x[0].y.z', function(n) { return n ? n + 1 : 0; });
	     * console.log(object.x[0].y.z);
	     * // => 0
	     */
	    function update(object, path, updater) {
	      return object == null ? object : baseUpdate(object, path, castFunction(updater));
	    }

	    /**
	     * This method is like `_.update` except that it accepts `customizer` which is
	     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
	     * path creation is handled by the method instead. The `customizer` is invoked
	     * with three arguments: (nsValue, key, nsObject).
	     *
	     * **Note:** This method mutates `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.6.0
	     * @category Object
	     * @param {Object} object The object to modify.
	     * @param {Array|string} path The path of the property to set.
	     * @param {Function} updater The function to produce the updated value.
	     * @param {Function} [customizer] The function to customize assigned values.
	     * @returns {Object} Returns `object`.
	     * @example
	     *
	     * var object = {};
	     *
	     * _.updateWith(object, '[0][1]', _.constant('a'), Object);
	     * // => { '0': { '1': 'a' } }
	     */
	    function updateWith(object, path, updater, customizer) {
	      customizer = typeof customizer == 'function' ? customizer : undefined$1;
	      return object == null ? object : baseUpdate(object, path, castFunction(updater), customizer);
	    }

	    /**
	     * Creates an array of the own enumerable string keyed property values of `object`.
	     *
	     * **Note:** Non-object values are coerced to objects.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Object
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property values.
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.values(new Foo);
	     * // => [1, 2] (iteration order is not guaranteed)
	     *
	     * _.values('hi');
	     * // => ['h', 'i']
	     */
	    function values(object) {
	      return object == null ? [] : baseValues(object, keys(object));
	    }

	    /**
	     * Creates an array of the own and inherited enumerable string keyed property
	     * values of `object`.
	     *
	     * **Note:** Non-object values are coerced to objects.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Object
	     * @param {Object} object The object to query.
	     * @returns {Array} Returns the array of property values.
	     * @example
	     *
	     * function Foo() {
	     *   this.a = 1;
	     *   this.b = 2;
	     * }
	     *
	     * Foo.prototype.c = 3;
	     *
	     * _.valuesIn(new Foo);
	     * // => [1, 2, 3] (iteration order is not guaranteed)
	     */
	    function valuesIn(object) {
	      return object == null ? [] : baseValues(object, keysIn(object));
	    }

	    /*------------------------------------------------------------------------*/

	    /**
	     * Clamps `number` within the inclusive `lower` and `upper` bounds.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Number
	     * @param {number} number The number to clamp.
	     * @param {number} [lower] The lower bound.
	     * @param {number} upper The upper bound.
	     * @returns {number} Returns the clamped number.
	     * @example
	     *
	     * _.clamp(-10, -5, 5);
	     * // => -5
	     *
	     * _.clamp(10, -5, 5);
	     * // => 5
	     */
	    function clamp(number, lower, upper) {
	      if (upper === undefined$1) {
	        upper = lower;
	        lower = undefined$1;
	      }
	      if (upper !== undefined$1) {
	        upper = toNumber(upper);
	        upper = upper === upper ? upper : 0;
	      }
	      if (lower !== undefined$1) {
	        lower = toNumber(lower);
	        lower = lower === lower ? lower : 0;
	      }
	      return baseClamp(toNumber(number), lower, upper);
	    }

	    /**
	     * Checks if `n` is between `start` and up to, but not including, `end`. If
	     * `end` is not specified, it's set to `start` with `start` then set to `0`.
	     * If `start` is greater than `end` the params are swapped to support
	     * negative ranges.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.3.0
	     * @category Number
	     * @param {number} number The number to check.
	     * @param {number} [start=0] The start of the range.
	     * @param {number} end The end of the range.
	     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
	     * @see _.range, _.rangeRight
	     * @example
	     *
	     * _.inRange(3, 2, 4);
	     * // => true
	     *
	     * _.inRange(4, 8);
	     * // => true
	     *
	     * _.inRange(4, 2);
	     * // => false
	     *
	     * _.inRange(2, 2);
	     * // => false
	     *
	     * _.inRange(1.2, 2);
	     * // => true
	     *
	     * _.inRange(5.2, 4);
	     * // => false
	     *
	     * _.inRange(-3, -2, -6);
	     * // => true
	     */
	    function inRange(number, start, end) {
	      start = toFinite(start);
	      if (end === undefined$1) {
	        end = start;
	        start = 0;
	      } else {
	        end = toFinite(end);
	      }
	      number = toNumber(number);
	      return baseInRange(number, start, end);
	    }

	    /**
	     * Produces a random number between the inclusive `lower` and `upper` bounds.
	     * If only one argument is provided a number between `0` and the given number
	     * is returned. If `floating` is `true`, or either `lower` or `upper` are
	     * floats, a floating-point number is returned instead of an integer.
	     *
	     * **Note:** JavaScript follows the IEEE-754 standard for resolving
	     * floating-point values which can produce unexpected results.
	     *
	     * @static
	     * @memberOf _
	     * @since 0.7.0
	     * @category Number
	     * @param {number} [lower=0] The lower bound.
	     * @param {number} [upper=1] The upper bound.
	     * @param {boolean} [floating] Specify returning a floating-point number.
	     * @returns {number} Returns the random number.
	     * @example
	     *
	     * _.random(0, 5);
	     * // => an integer between 0 and 5
	     *
	     * _.random(5);
	     * // => also an integer between 0 and 5
	     *
	     * _.random(5, true);
	     * // => a floating-point number between 0 and 5
	     *
	     * _.random(1.2, 5.2);
	     * // => a floating-point number between 1.2 and 5.2
	     */
	    function random(lower, upper, floating) {
	      if (floating && typeof floating != 'boolean' && isIterateeCall(lower, upper, floating)) {
	        upper = floating = undefined$1;
	      }
	      if (floating === undefined$1) {
	        if (typeof upper == 'boolean') {
	          floating = upper;
	          upper = undefined$1;
	        }
	        else if (typeof lower == 'boolean') {
	          floating = lower;
	          lower = undefined$1;
	        }
	      }
	      if (lower === undefined$1 && upper === undefined$1) {
	        lower = 0;
	        upper = 1;
	      }
	      else {
	        lower = toFinite(lower);
	        if (upper === undefined$1) {
	          upper = lower;
	          lower = 0;
	        } else {
	          upper = toFinite(upper);
	        }
	      }
	      if (lower > upper) {
	        var temp = lower;
	        lower = upper;
	        upper = temp;
	      }
	      if (floating || lower % 1 || upper % 1) {
	        var rand = nativeRandom();
	        return nativeMin(lower + (rand * (upper - lower + freeParseFloat('1e-' + ((rand + '').length - 1)))), upper);
	      }
	      return baseRandom(lower, upper);
	    }

	    /*------------------------------------------------------------------------*/

	    /**
	     * Converts `string` to [camel case](https://en.wikipedia.org/wiki/CamelCase).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the camel cased string.
	     * @example
	     *
	     * _.camelCase('Foo Bar');
	     * // => 'fooBar'
	     *
	     * _.camelCase('--foo-bar--');
	     * // => 'fooBar'
	     *
	     * _.camelCase('__FOO_BAR__');
	     * // => 'fooBar'
	     */
	    var camelCase = createCompounder(function(result, word, index) {
	      word = word.toLowerCase();
	      return result + (index ? capitalize(word) : word);
	    });

	    /**
	     * Converts the first character of `string` to upper case and the remaining
	     * to lower case.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to capitalize.
	     * @returns {string} Returns the capitalized string.
	     * @example
	     *
	     * _.capitalize('FRED');
	     * // => 'Fred'
	     */
	    function capitalize(string) {
	      return upperFirst(toString(string).toLowerCase());
	    }

	    /**
	     * Deburrs `string` by converting
	     * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
	     * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
	     * letters to basic Latin letters and removing
	     * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to deburr.
	     * @returns {string} Returns the deburred string.
	     * @example
	     *
	     * _.deburr('déjà vu');
	     * // => 'deja vu'
	     */
	    function deburr(string) {
	      string = toString(string);
	      return string && string.replace(reLatin, deburrLetter).replace(reComboMark, '');
	    }

	    /**
	     * Checks if `string` ends with the given target string.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to inspect.
	     * @param {string} [target] The string to search for.
	     * @param {number} [position=string.length] The position to search up to.
	     * @returns {boolean} Returns `true` if `string` ends with `target`,
	     *  else `false`.
	     * @example
	     *
	     * _.endsWith('abc', 'c');
	     * // => true
	     *
	     * _.endsWith('abc', 'b');
	     * // => false
	     *
	     * _.endsWith('abc', 'b', 2);
	     * // => true
	     */
	    function endsWith(string, target, position) {
	      string = toString(string);
	      target = baseToString(target);

	      var length = string.length;
	      position = position === undefined$1
	        ? length
	        : baseClamp(toInteger(position), 0, length);

	      var end = position;
	      position -= target.length;
	      return position >= 0 && string.slice(position, end) == target;
	    }

	    /**
	     * Converts the characters "&", "<", ">", '"', and "'" in `string` to their
	     * corresponding HTML entities.
	     *
	     * **Note:** No other characters are escaped. To escape additional
	     * characters use a third-party library like [_he_](https://mths.be/he).
	     *
	     * Though the ">" character is escaped for symmetry, characters like
	     * ">" and "/" don't need escaping in HTML and have no special meaning
	     * unless they're part of a tag or unquoted attribute value. See
	     * [Mathias Bynens's article](https://mathiasbynens.be/notes/ambiguous-ampersands)
	     * (under "semi-related fun fact") for more details.
	     *
	     * When working with HTML you should always
	     * [quote attribute values](http://wonko.com/post/html-escaping) to reduce
	     * XSS vectors.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category String
	     * @param {string} [string=''] The string to escape.
	     * @returns {string} Returns the escaped string.
	     * @example
	     *
	     * _.escape('fred, barney, & pebbles');
	     * // => 'fred, barney, &amp; pebbles'
	     */
	    function escape(string) {
	      string = toString(string);
	      return (string && reHasUnescapedHtml.test(string))
	        ? string.replace(reUnescapedHtml, escapeHtmlChar)
	        : string;
	    }

	    /**
	     * Escapes the `RegExp` special characters "^", "$", "\", ".", "*", "+",
	     * "?", "(", ")", "[", "]", "{", "}", and "|" in `string`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to escape.
	     * @returns {string} Returns the escaped string.
	     * @example
	     *
	     * _.escapeRegExp('[lodash](https://lodash.com/)');
	     * // => '\[lodash\]\(https://lodash\.com/\)'
	     */
	    function escapeRegExp(string) {
	      string = toString(string);
	      return (string && reHasRegExpChar.test(string))
	        ? string.replace(reRegExpChar, '\\$&')
	        : string;
	    }

	    /**
	     * Converts `string` to
	     * [kebab case](https://en.wikipedia.org/wiki/Letter_case#Special_case_styles).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the kebab cased string.
	     * @example
	     *
	     * _.kebabCase('Foo Bar');
	     * // => 'foo-bar'
	     *
	     * _.kebabCase('fooBar');
	     * // => 'foo-bar'
	     *
	     * _.kebabCase('__FOO_BAR__');
	     * // => 'foo-bar'
	     */
	    var kebabCase = createCompounder(function(result, word, index) {
	      return result + (index ? '-' : '') + word.toLowerCase();
	    });

	    /**
	     * Converts `string`, as space separated words, to lower case.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the lower cased string.
	     * @example
	     *
	     * _.lowerCase('--Foo-Bar--');
	     * // => 'foo bar'
	     *
	     * _.lowerCase('fooBar');
	     * // => 'foo bar'
	     *
	     * _.lowerCase('__FOO_BAR__');
	     * // => 'foo bar'
	     */
	    var lowerCase = createCompounder(function(result, word, index) {
	      return result + (index ? ' ' : '') + word.toLowerCase();
	    });

	    /**
	     * Converts the first character of `string` to lower case.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the converted string.
	     * @example
	     *
	     * _.lowerFirst('Fred');
	     * // => 'fred'
	     *
	     * _.lowerFirst('FRED');
	     * // => 'fRED'
	     */
	    var lowerFirst = createCaseFirst('toLowerCase');

	    /**
	     * Pads `string` on the left and right sides if it's shorter than `length`.
	     * Padding characters are truncated if they can't be evenly divided by `length`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to pad.
	     * @param {number} [length=0] The padding length.
	     * @param {string} [chars=' '] The string used as padding.
	     * @returns {string} Returns the padded string.
	     * @example
	     *
	     * _.pad('abc', 8);
	     * // => '  abc   '
	     *
	     * _.pad('abc', 8, '_-');
	     * // => '_-abc_-_'
	     *
	     * _.pad('abc', 3);
	     * // => 'abc'
	     */
	    function pad(string, length, chars) {
	      string = toString(string);
	      length = toInteger(length);

	      var strLength = length ? stringSize(string) : 0;
	      if (!length || strLength >= length) {
	        return string;
	      }
	      var mid = (length - strLength) / 2;
	      return (
	        createPadding(nativeFloor(mid), chars) +
	        string +
	        createPadding(nativeCeil(mid), chars)
	      );
	    }

	    /**
	     * Pads `string` on the right side if it's shorter than `length`. Padding
	     * characters are truncated if they exceed `length`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to pad.
	     * @param {number} [length=0] The padding length.
	     * @param {string} [chars=' '] The string used as padding.
	     * @returns {string} Returns the padded string.
	     * @example
	     *
	     * _.padEnd('abc', 6);
	     * // => 'abc   '
	     *
	     * _.padEnd('abc', 6, '_-');
	     * // => 'abc_-_'
	     *
	     * _.padEnd('abc', 3);
	     * // => 'abc'
	     */
	    function padEnd(string, length, chars) {
	      string = toString(string);
	      length = toInteger(length);

	      var strLength = length ? stringSize(string) : 0;
	      return (length && strLength < length)
	        ? (string + createPadding(length - strLength, chars))
	        : string;
	    }

	    /**
	     * Pads `string` on the left side if it's shorter than `length`. Padding
	     * characters are truncated if they exceed `length`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to pad.
	     * @param {number} [length=0] The padding length.
	     * @param {string} [chars=' '] The string used as padding.
	     * @returns {string} Returns the padded string.
	     * @example
	     *
	     * _.padStart('abc', 6);
	     * // => '   abc'
	     *
	     * _.padStart('abc', 6, '_-');
	     * // => '_-_abc'
	     *
	     * _.padStart('abc', 3);
	     * // => 'abc'
	     */
	    function padStart(string, length, chars) {
	      string = toString(string);
	      length = toInteger(length);

	      var strLength = length ? stringSize(string) : 0;
	      return (length && strLength < length)
	        ? (createPadding(length - strLength, chars) + string)
	        : string;
	    }

	    /**
	     * Converts `string` to an integer of the specified radix. If `radix` is
	     * `undefined` or `0`, a `radix` of `10` is used unless `value` is a
	     * hexadecimal, in which case a `radix` of `16` is used.
	     *
	     * **Note:** This method aligns with the
	     * [ES5 implementation](https://es5.github.io/#x15.1.2.2) of `parseInt`.
	     *
	     * @static
	     * @memberOf _
	     * @since 1.1.0
	     * @category String
	     * @param {string} string The string to convert.
	     * @param {number} [radix=10] The radix to interpret `value` by.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {number} Returns the converted integer.
	     * @example
	     *
	     * _.parseInt('08');
	     * // => 8
	     *
	     * _.map(['6', '08', '10'], _.parseInt);
	     * // => [6, 8, 10]
	     */
	    function parseInt(string, radix, guard) {
	      if (guard || radix == null) {
	        radix = 0;
	      } else if (radix) {
	        radix = +radix;
	      }
	      return nativeParseInt(toString(string).replace(reTrimStart, ''), radix || 0);
	    }

	    /**
	     * Repeats the given string `n` times.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to repeat.
	     * @param {number} [n=1] The number of times to repeat the string.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {string} Returns the repeated string.
	     * @example
	     *
	     * _.repeat('*', 3);
	     * // => '***'
	     *
	     * _.repeat('abc', 2);
	     * // => 'abcabc'
	     *
	     * _.repeat('abc', 0);
	     * // => ''
	     */
	    function repeat(string, n, guard) {
	      if ((guard ? isIterateeCall(string, n, guard) : n === undefined$1)) {
	        n = 1;
	      } else {
	        n = toInteger(n);
	      }
	      return baseRepeat(toString(string), n);
	    }

	    /**
	     * Replaces matches for `pattern` in `string` with `replacement`.
	     *
	     * **Note:** This method is based on
	     * [`String#replace`](https://mdn.io/String/replace).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to modify.
	     * @param {RegExp|string} pattern The pattern to replace.
	     * @param {Function|string} replacement The match replacement.
	     * @returns {string} Returns the modified string.
	     * @example
	     *
	     * _.replace('Hi Fred', 'Fred', 'Barney');
	     * // => 'Hi Barney'
	     */
	    function replace() {
	      var args = arguments,
	          string = toString(args[0]);

	      return args.length < 3 ? string : string.replace(args[1], args[2]);
	    }

	    /**
	     * Converts `string` to
	     * [snake case](https://en.wikipedia.org/wiki/Snake_case).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the snake cased string.
	     * @example
	     *
	     * _.snakeCase('Foo Bar');
	     * // => 'foo_bar'
	     *
	     * _.snakeCase('fooBar');
	     * // => 'foo_bar'
	     *
	     * _.snakeCase('--FOO-BAR--');
	     * // => 'foo_bar'
	     */
	    var snakeCase = createCompounder(function(result, word, index) {
	      return result + (index ? '_' : '') + word.toLowerCase();
	    });

	    /**
	     * Splits `string` by `separator`.
	     *
	     * **Note:** This method is based on
	     * [`String#split`](https://mdn.io/String/split).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to split.
	     * @param {RegExp|string} separator The separator pattern to split by.
	     * @param {number} [limit] The length to truncate results to.
	     * @returns {Array} Returns the string segments.
	     * @example
	     *
	     * _.split('a-b-c', '-', 2);
	     * // => ['a', 'b']
	     */
	    function split(string, separator, limit) {
	      if (limit && typeof limit != 'number' && isIterateeCall(string, separator, limit)) {
	        separator = limit = undefined$1;
	      }
	      limit = limit === undefined$1 ? MAX_ARRAY_LENGTH : limit >>> 0;
	      if (!limit) {
	        return [];
	      }
	      string = toString(string);
	      if (string && (
	            typeof separator == 'string' ||
	            (separator != null && !isRegExp(separator))
	          )) {
	        separator = baseToString(separator);
	        if (!separator && hasUnicode(string)) {
	          return castSlice(stringToArray(string), 0, limit);
	        }
	      }
	      return string.split(separator, limit);
	    }

	    /**
	     * Converts `string` to
	     * [start case](https://en.wikipedia.org/wiki/Letter_case#Stylistic_or_specialised_usage).
	     *
	     * @static
	     * @memberOf _
	     * @since 3.1.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the start cased string.
	     * @example
	     *
	     * _.startCase('--foo-bar--');
	     * // => 'Foo Bar'
	     *
	     * _.startCase('fooBar');
	     * // => 'Foo Bar'
	     *
	     * _.startCase('__FOO_BAR__');
	     * // => 'FOO BAR'
	     */
	    var startCase = createCompounder(function(result, word, index) {
	      return result + (index ? ' ' : '') + upperFirst(word);
	    });

	    /**
	     * Checks if `string` starts with the given target string.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to inspect.
	     * @param {string} [target] The string to search for.
	     * @param {number} [position=0] The position to search from.
	     * @returns {boolean} Returns `true` if `string` starts with `target`,
	     *  else `false`.
	     * @example
	     *
	     * _.startsWith('abc', 'a');
	     * // => true
	     *
	     * _.startsWith('abc', 'b');
	     * // => false
	     *
	     * _.startsWith('abc', 'b', 1);
	     * // => true
	     */
	    function startsWith(string, target, position) {
	      string = toString(string);
	      position = position == null
	        ? 0
	        : baseClamp(toInteger(position), 0, string.length);

	      target = baseToString(target);
	      return string.slice(position, position + target.length) == target;
	    }

	    /**
	     * Creates a compiled template function that can interpolate data properties
	     * in "interpolate" delimiters, HTML-escape interpolated data properties in
	     * "escape" delimiters, and execute JavaScript in "evaluate" delimiters. Data
	     * properties may be accessed as free variables in the template. If a setting
	     * object is given, it takes precedence over `_.templateSettings` values.
	     *
	     * **Note:** In the development build `_.template` utilizes
	     * [sourceURLs](http://www.html5rocks.com/en/tutorials/developertools/sourcemaps/#toc-sourceurl)
	     * for easier debugging.
	     *
	     * For more information on precompiling templates see
	     * [lodash's custom builds documentation](https://lodash.com/custom-builds).
	     *
	     * For more information on Chrome extension sandboxes see
	     * [Chrome's extensions documentation](https://developer.chrome.com/extensions/sandboxingEval).
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category String
	     * @param {string} [string=''] The template string.
	     * @param {Object} [options={}] The options object.
	     * @param {RegExp} [options.escape=_.templateSettings.escape]
	     *  The HTML "escape" delimiter.
	     * @param {RegExp} [options.evaluate=_.templateSettings.evaluate]
	     *  The "evaluate" delimiter.
	     * @param {Object} [options.imports=_.templateSettings.imports]
	     *  An object to import into the template as free variables.
	     * @param {RegExp} [options.interpolate=_.templateSettings.interpolate]
	     *  The "interpolate" delimiter.
	     * @param {string} [options.sourceURL='lodash.templateSources[n]']
	     *  The sourceURL of the compiled template.
	     * @param {string} [options.variable='obj']
	     *  The data object variable name.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Function} Returns the compiled template function.
	     * @example
	     *
	     * // Use the "interpolate" delimiter to create a compiled template.
	     * var compiled = _.template('hello <%= user %>!');
	     * compiled({ 'user': 'fred' });
	     * // => 'hello fred!'
	     *
	     * // Use the HTML "escape" delimiter to escape data property values.
	     * var compiled = _.template('<b><%- value %></b>');
	     * compiled({ 'value': '<script>' });
	     * // => '<b>&lt;script&gt;</b>'
	     *
	     * // Use the "evaluate" delimiter to execute JavaScript and generate HTML.
	     * var compiled = _.template('<% _.forEach(users, function(user) { %><li><%- user %></li><% }); %>');
	     * compiled({ 'users': ['fred', 'barney'] });
	     * // => '<li>fred</li><li>barney</li>'
	     *
	     * // Use the internal `print` function in "evaluate" delimiters.
	     * var compiled = _.template('<% print("hello " + user); %>!');
	     * compiled({ 'user': 'barney' });
	     * // => 'hello barney!'
	     *
	     * // Use the ES template literal delimiter as an "interpolate" delimiter.
	     * // Disable support by replacing the "interpolate" delimiter.
	     * var compiled = _.template('hello ${ user }!');
	     * compiled({ 'user': 'pebbles' });
	     * // => 'hello pebbles!'
	     *
	     * // Use backslashes to treat delimiters as plain text.
	     * var compiled = _.template('<%= "\\<%- value %\\>" %>');
	     * compiled({ 'value': 'ignored' });
	     * // => '<%- value %>'
	     *
	     * // Use the `imports` option to import `jQuery` as `jq`.
	     * var text = '<% jq.each(users, function(user) { %><li><%- user %></li><% }); %>';
	     * var compiled = _.template(text, { 'imports': { 'jq': jQuery } });
	     * compiled({ 'users': ['fred', 'barney'] });
	     * // => '<li>fred</li><li>barney</li>'
	     *
	     * // Use the `sourceURL` option to specify a custom sourceURL for the template.
	     * var compiled = _.template('hello <%= user %>!', { 'sourceURL': '/basic/greeting.jst' });
	     * compiled(data);
	     * // => Find the source of "greeting.jst" under the Sources tab or Resources panel of the web inspector.
	     *
	     * // Use the `variable` option to ensure a with-statement isn't used in the compiled template.
	     * var compiled = _.template('hi <%= data.user %>!', { 'variable': 'data' });
	     * compiled.source;
	     * // => function(data) {
	     * //   var __t, __p = '';
	     * //   __p += 'hi ' + ((__t = ( data.user )) == null ? '' : __t) + '!';
	     * //   return __p;
	     * // }
	     *
	     * // Use custom template delimiters.
	     * _.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
	     * var compiled = _.template('hello {{ user }}!');
	     * compiled({ 'user': 'mustache' });
	     * // => 'hello mustache!'
	     *
	     * // Use the `source` property to inline compiled templates for meaningful
	     * // line numbers in error messages and stack traces.
	     * fs.writeFileSync(path.join(process.cwd(), 'jst.js'), '\
	     *   var JST = {\
	     *     "main": ' + _.template(mainText).source + '\
	     *   };\
	     * ');
	     */
	    function template(string, options, guard) {
	      // Based on John Resig's `tmpl` implementation
	      // (http://ejohn.org/blog/javascript-micro-templating/)
	      // and Laura Doktorova's doT.js (https://github.com/olado/doT).
	      var settings = lodash.templateSettings;

	      if (guard && isIterateeCall(string, options, guard)) {
	        options = undefined$1;
	      }
	      string = toString(string);
	      options = assignInWith({}, options, settings, customDefaultsAssignIn);

	      var imports = assignInWith({}, options.imports, settings.imports, customDefaultsAssignIn),
	          importsKeys = keys(imports),
	          importsValues = baseValues(imports, importsKeys);

	      var isEscaping,
	          isEvaluating,
	          index = 0,
	          interpolate = options.interpolate || reNoMatch,
	          source = "__p += '";

	      // Compile the regexp to match each delimiter.
	      var reDelimiters = RegExp(
	        (options.escape || reNoMatch).source + '|' +
	        interpolate.source + '|' +
	        (interpolate === reInterpolate ? reEsTemplate : reNoMatch).source + '|' +
	        (options.evaluate || reNoMatch).source + '|$'
	      , 'g');

	      // Use a sourceURL for easier debugging.
	      // The sourceURL gets injected into the source that's eval-ed, so be careful
	      // to normalize all kinds of whitespace, so e.g. newlines (and unicode versions of it) can't sneak in
	      // and escape the comment, thus injecting code that gets evaled.
	      var sourceURL = '//# sourceURL=' +
	        (hasOwnProperty.call(options, 'sourceURL')
	          ? (options.sourceURL + '').replace(/\s/g, ' ')
	          : ('lodash.templateSources[' + (++templateCounter) + ']')
	        ) + '\n';

	      string.replace(reDelimiters, function(match, escapeValue, interpolateValue, esTemplateValue, evaluateValue, offset) {
	        interpolateValue || (interpolateValue = esTemplateValue);

	        // Escape characters that can't be included in string literals.
	        source += string.slice(index, offset).replace(reUnescapedString, escapeStringChar);

	        // Replace delimiters with snippets.
	        if (escapeValue) {
	          isEscaping = true;
	          source += "' +\n__e(" + escapeValue + ") +\n'";
	        }
	        if (evaluateValue) {
	          isEvaluating = true;
	          source += "';\n" + evaluateValue + ";\n__p += '";
	        }
	        if (interpolateValue) {
	          source += "' +\n((__t = (" + interpolateValue + ")) == null ? '' : __t) +\n'";
	        }
	        index = offset + match.length;

	        // The JS engine embedded in Adobe products needs `match` returned in
	        // order to produce the correct `offset` value.
	        return match;
	      });

	      source += "';\n";

	      // If `variable` is not specified wrap a with-statement around the generated
	      // code to add the data object to the top of the scope chain.
	      var variable = hasOwnProperty.call(options, 'variable') && options.variable;
	      if (!variable) {
	        source = 'with (obj) {\n' + source + '\n}\n';
	      }
	      // Throw an error if a forbidden character was found in `variable`, to prevent
	      // potential command injection attacks.
	      else if (reForbiddenIdentifierChars.test(variable)) {
	        throw new Error(INVALID_TEMPL_VAR_ERROR_TEXT);
	      }

	      // Cleanup code by stripping empty strings.
	      source = (isEvaluating ? source.replace(reEmptyStringLeading, '') : source)
	        .replace(reEmptyStringMiddle, '$1')
	        .replace(reEmptyStringTrailing, '$1;');

	      // Frame code as the function body.
	      source = 'function(' + (variable || 'obj') + ') {\n' +
	        (variable
	          ? ''
	          : 'obj || (obj = {});\n'
	        ) +
	        "var __t, __p = ''" +
	        (isEscaping
	           ? ', __e = _.escape'
	           : ''
	        ) +
	        (isEvaluating
	          ? ', __j = Array.prototype.join;\n' +
	            "function print() { __p += __j.call(arguments, '') }\n"
	          : ';\n'
	        ) +
	        source +
	        'return __p\n}';

	      var result = attempt(function() {
	        return Function(importsKeys, sourceURL + 'return ' + source)
	          .apply(undefined$1, importsValues);
	      });

	      // Provide the compiled function's source by its `toString` method or
	      // the `source` property as a convenience for inlining compiled templates.
	      result.source = source;
	      if (isError(result)) {
	        throw result;
	      }
	      return result;
	    }

	    /**
	     * Converts `string`, as a whole, to lower case just like
	     * [String#toLowerCase](https://mdn.io/toLowerCase).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the lower cased string.
	     * @example
	     *
	     * _.toLower('--Foo-Bar--');
	     * // => '--foo-bar--'
	     *
	     * _.toLower('fooBar');
	     * // => 'foobar'
	     *
	     * _.toLower('__FOO_BAR__');
	     * // => '__foo_bar__'
	     */
	    function toLower(value) {
	      return toString(value).toLowerCase();
	    }

	    /**
	     * Converts `string`, as a whole, to upper case just like
	     * [String#toUpperCase](https://mdn.io/toUpperCase).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the upper cased string.
	     * @example
	     *
	     * _.toUpper('--foo-bar--');
	     * // => '--FOO-BAR--'
	     *
	     * _.toUpper('fooBar');
	     * // => 'FOOBAR'
	     *
	     * _.toUpper('__foo_bar__');
	     * // => '__FOO_BAR__'
	     */
	    function toUpper(value) {
	      return toString(value).toUpperCase();
	    }

	    /**
	     * Removes leading and trailing whitespace or specified characters from `string`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to trim.
	     * @param {string} [chars=whitespace] The characters to trim.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {string} Returns the trimmed string.
	     * @example
	     *
	     * _.trim('  abc  ');
	     * // => 'abc'
	     *
	     * _.trim('-_-abc-_-', '_-');
	     * // => 'abc'
	     *
	     * _.map(['  foo  ', '  bar  '], _.trim);
	     * // => ['foo', 'bar']
	     */
	    function trim(string, chars, guard) {
	      string = toString(string);
	      if (string && (guard || chars === undefined$1)) {
	        return baseTrim(string);
	      }
	      if (!string || !(chars = baseToString(chars))) {
	        return string;
	      }
	      var strSymbols = stringToArray(string),
	          chrSymbols = stringToArray(chars),
	          start = charsStartIndex(strSymbols, chrSymbols),
	          end = charsEndIndex(strSymbols, chrSymbols) + 1;

	      return castSlice(strSymbols, start, end).join('');
	    }

	    /**
	     * Removes trailing whitespace or specified characters from `string`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to trim.
	     * @param {string} [chars=whitespace] The characters to trim.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {string} Returns the trimmed string.
	     * @example
	     *
	     * _.trimEnd('  abc  ');
	     * // => '  abc'
	     *
	     * _.trimEnd('-_-abc-_-', '_-');
	     * // => '-_-abc'
	     */
	    function trimEnd(string, chars, guard) {
	      string = toString(string);
	      if (string && (guard || chars === undefined$1)) {
	        return string.slice(0, trimmedEndIndex(string) + 1);
	      }
	      if (!string || !(chars = baseToString(chars))) {
	        return string;
	      }
	      var strSymbols = stringToArray(string),
	          end = charsEndIndex(strSymbols, stringToArray(chars)) + 1;

	      return castSlice(strSymbols, 0, end).join('');
	    }

	    /**
	     * Removes leading whitespace or specified characters from `string`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to trim.
	     * @param {string} [chars=whitespace] The characters to trim.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {string} Returns the trimmed string.
	     * @example
	     *
	     * _.trimStart('  abc  ');
	     * // => 'abc  '
	     *
	     * _.trimStart('-_-abc-_-', '_-');
	     * // => 'abc-_-'
	     */
	    function trimStart(string, chars, guard) {
	      string = toString(string);
	      if (string && (guard || chars === undefined$1)) {
	        return string.replace(reTrimStart, '');
	      }
	      if (!string || !(chars = baseToString(chars))) {
	        return string;
	      }
	      var strSymbols = stringToArray(string),
	          start = charsStartIndex(strSymbols, stringToArray(chars));

	      return castSlice(strSymbols, start).join('');
	    }

	    /**
	     * Truncates `string` if it's longer than the given maximum string length.
	     * The last characters of the truncated string are replaced with the omission
	     * string which defaults to "...".
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to truncate.
	     * @param {Object} [options={}] The options object.
	     * @param {number} [options.length=30] The maximum string length.
	     * @param {string} [options.omission='...'] The string to indicate text is omitted.
	     * @param {RegExp|string} [options.separator] The separator pattern to truncate to.
	     * @returns {string} Returns the truncated string.
	     * @example
	     *
	     * _.truncate('hi-diddly-ho there, neighborino');
	     * // => 'hi-diddly-ho there, neighbo...'
	     *
	     * _.truncate('hi-diddly-ho there, neighborino', {
	     *   'length': 24,
	     *   'separator': ' '
	     * });
	     * // => 'hi-diddly-ho there,...'
	     *
	     * _.truncate('hi-diddly-ho there, neighborino', {
	     *   'length': 24,
	     *   'separator': /,? +/
	     * });
	     * // => 'hi-diddly-ho there...'
	     *
	     * _.truncate('hi-diddly-ho there, neighborino', {
	     *   'omission': ' [...]'
	     * });
	     * // => 'hi-diddly-ho there, neig [...]'
	     */
	    function truncate(string, options) {
	      var length = DEFAULT_TRUNC_LENGTH,
	          omission = DEFAULT_TRUNC_OMISSION;

	      if (isObject(options)) {
	        var separator = 'separator' in options ? options.separator : separator;
	        length = 'length' in options ? toInteger(options.length) : length;
	        omission = 'omission' in options ? baseToString(options.omission) : omission;
	      }
	      string = toString(string);

	      var strLength = string.length;
	      if (hasUnicode(string)) {
	        var strSymbols = stringToArray(string);
	        strLength = strSymbols.length;
	      }
	      if (length >= strLength) {
	        return string;
	      }
	      var end = length - stringSize(omission);
	      if (end < 1) {
	        return omission;
	      }
	      var result = strSymbols
	        ? castSlice(strSymbols, 0, end).join('')
	        : string.slice(0, end);

	      if (separator === undefined$1) {
	        return result + omission;
	      }
	      if (strSymbols) {
	        end += (result.length - end);
	      }
	      if (isRegExp(separator)) {
	        if (string.slice(end).search(separator)) {
	          var match,
	              substring = result;

	          if (!separator.global) {
	            separator = RegExp(separator.source, toString(reFlags.exec(separator)) + 'g');
	          }
	          separator.lastIndex = 0;
	          while ((match = separator.exec(substring))) {
	            var newEnd = match.index;
	          }
	          result = result.slice(0, newEnd === undefined$1 ? end : newEnd);
	        }
	      } else if (string.indexOf(baseToString(separator), end) != end) {
	        var index = result.lastIndexOf(separator);
	        if (index > -1) {
	          result = result.slice(0, index);
	        }
	      }
	      return result + omission;
	    }

	    /**
	     * The inverse of `_.escape`; this method converts the HTML entities
	     * `&amp;`, `&lt;`, `&gt;`, `&quot;`, and `&#39;` in `string` to
	     * their corresponding characters.
	     *
	     * **Note:** No other HTML entities are unescaped. To unescape additional
	     * HTML entities use a third-party library like [_he_](https://mths.be/he).
	     *
	     * @static
	     * @memberOf _
	     * @since 0.6.0
	     * @category String
	     * @param {string} [string=''] The string to unescape.
	     * @returns {string} Returns the unescaped string.
	     * @example
	     *
	     * _.unescape('fred, barney, &amp; pebbles');
	     * // => 'fred, barney, & pebbles'
	     */
	    function unescape(string) {
	      string = toString(string);
	      return (string && reHasEscapedHtml.test(string))
	        ? string.replace(reEscapedHtml, unescapeHtmlChar)
	        : string;
	    }

	    /**
	     * Converts `string`, as space separated words, to upper case.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the upper cased string.
	     * @example
	     *
	     * _.upperCase('--foo-bar');
	     * // => 'FOO BAR'
	     *
	     * _.upperCase('fooBar');
	     * // => 'FOO BAR'
	     *
	     * _.upperCase('__foo_bar__');
	     * // => 'FOO BAR'
	     */
	    var upperCase = createCompounder(function(result, word, index) {
	      return result + (index ? ' ' : '') + word.toUpperCase();
	    });

	    /**
	     * Converts the first character of `string` to upper case.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category String
	     * @param {string} [string=''] The string to convert.
	     * @returns {string} Returns the converted string.
	     * @example
	     *
	     * _.upperFirst('fred');
	     * // => 'Fred'
	     *
	     * _.upperFirst('FRED');
	     * // => 'FRED'
	     */
	    var upperFirst = createCaseFirst('toUpperCase');

	    /**
	     * Splits `string` into an array of its words.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category String
	     * @param {string} [string=''] The string to inspect.
	     * @param {RegExp|string} [pattern] The pattern to match words.
	     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	     * @returns {Array} Returns the words of `string`.
	     * @example
	     *
	     * _.words('fred, barney, & pebbles');
	     * // => ['fred', 'barney', 'pebbles']
	     *
	     * _.words('fred, barney, & pebbles', /[^, ]+/g);
	     * // => ['fred', 'barney', '&', 'pebbles']
	     */
	    function words(string, pattern, guard) {
	      string = toString(string);
	      pattern = guard ? undefined$1 : pattern;

	      if (pattern === undefined$1) {
	        return hasUnicodeWord(string) ? unicodeWords(string) : asciiWords(string);
	      }
	      return string.match(pattern) || [];
	    }

	    /*------------------------------------------------------------------------*/

	    /**
	     * Attempts to invoke `func`, returning either the result or the caught error
	     * object. Any additional arguments are provided to `func` when it's invoked.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Util
	     * @param {Function} func The function to attempt.
	     * @param {...*} [args] The arguments to invoke `func` with.
	     * @returns {*} Returns the `func` result or error object.
	     * @example
	     *
	     * // Avoid throwing errors for invalid selectors.
	     * var elements = _.attempt(function(selector) {
	     *   return document.querySelectorAll(selector);
	     * }, '>_>');
	     *
	     * if (_.isError(elements)) {
	     *   elements = [];
	     * }
	     */
	    var attempt = baseRest(function(func, args) {
	      try {
	        return apply(func, undefined$1, args);
	      } catch (e) {
	        return isError(e) ? e : new Error(e);
	      }
	    });

	    /**
	     * Binds methods of an object to the object itself, overwriting the existing
	     * method.
	     *
	     * **Note:** This method doesn't set the "length" property of bound functions.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Util
	     * @param {Object} object The object to bind and assign the bound methods to.
	     * @param {...(string|string[])} methodNames The object method names to bind.
	     * @returns {Object} Returns `object`.
	     * @example
	     *
	     * var view = {
	     *   'label': 'docs',
	     *   'click': function() {
	     *     console.log('clicked ' + this.label);
	     *   }
	     * };
	     *
	     * _.bindAll(view, ['click']);
	     * jQuery(element).on('click', view.click);
	     * // => Logs 'clicked docs' when clicked.
	     */
	    var bindAll = flatRest(function(object, methodNames) {
	      arrayEach(methodNames, function(key) {
	        key = toKey(key);
	        baseAssignValue(object, key, bind(object[key], object));
	      });
	      return object;
	    });

	    /**
	     * Creates a function that iterates over `pairs` and invokes the corresponding
	     * function of the first predicate to return truthy. The predicate-function
	     * pairs are invoked with the `this` binding and arguments of the created
	     * function.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Util
	     * @param {Array} pairs The predicate-function pairs.
	     * @returns {Function} Returns the new composite function.
	     * @example
	     *
	     * var func = _.cond([
	     *   [_.matches({ 'a': 1 }),           _.constant('matches A')],
	     *   [_.conforms({ 'b': _.isNumber }), _.constant('matches B')],
	     *   [_.stubTrue,                      _.constant('no match')]
	     * ]);
	     *
	     * func({ 'a': 1, 'b': 2 });
	     * // => 'matches A'
	     *
	     * func({ 'a': 0, 'b': 1 });
	     * // => 'matches B'
	     *
	     * func({ 'a': '1', 'b': '2' });
	     * // => 'no match'
	     */
	    function cond(pairs) {
	      var length = pairs == null ? 0 : pairs.length,
	          toIteratee = getIteratee();

	      pairs = !length ? [] : arrayMap(pairs, function(pair) {
	        if (typeof pair[1] != 'function') {
	          throw new TypeError(FUNC_ERROR_TEXT);
	        }
	        return [toIteratee(pair[0]), pair[1]];
	      });

	      return baseRest(function(args) {
	        var index = -1;
	        while (++index < length) {
	          var pair = pairs[index];
	          if (apply(pair[0], this, args)) {
	            return apply(pair[1], this, args);
	          }
	        }
	      });
	    }

	    /**
	     * Creates a function that invokes the predicate properties of `source` with
	     * the corresponding property values of a given object, returning `true` if
	     * all predicates return truthy, else `false`.
	     *
	     * **Note:** The created function is equivalent to `_.conformsTo` with
	     * `source` partially applied.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Util
	     * @param {Object} source The object of property predicates to conform to.
	     * @returns {Function} Returns the new spec function.
	     * @example
	     *
	     * var objects = [
	     *   { 'a': 2, 'b': 1 },
	     *   { 'a': 1, 'b': 2 }
	     * ];
	     *
	     * _.filter(objects, _.conforms({ 'b': function(n) { return n > 1; } }));
	     * // => [{ 'a': 1, 'b': 2 }]
	     */
	    function conforms(source) {
	      return baseConforms(baseClone(source, CLONE_DEEP_FLAG));
	    }

	    /**
	     * Creates a function that returns `value`.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.4.0
	     * @category Util
	     * @param {*} value The value to return from the new function.
	     * @returns {Function} Returns the new constant function.
	     * @example
	     *
	     * var objects = _.times(2, _.constant({ 'a': 1 }));
	     *
	     * console.log(objects);
	     * // => [{ 'a': 1 }, { 'a': 1 }]
	     *
	     * console.log(objects[0] === objects[1]);
	     * // => true
	     */
	    function constant(value) {
	      return function() {
	        return value;
	      };
	    }

	    /**
	     * Checks `value` to determine whether a default value should be returned in
	     * its place. The `defaultValue` is returned if `value` is `NaN`, `null`,
	     * or `undefined`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.14.0
	     * @category Util
	     * @param {*} value The value to check.
	     * @param {*} defaultValue The default value.
	     * @returns {*} Returns the resolved value.
	     * @example
	     *
	     * _.defaultTo(1, 10);
	     * // => 1
	     *
	     * _.defaultTo(undefined, 10);
	     * // => 10
	     */
	    function defaultTo(value, defaultValue) {
	      return (value == null || value !== value) ? defaultValue : value;
	    }

	    /**
	     * Creates a function that returns the result of invoking the given functions
	     * with the `this` binding of the created function, where each successive
	     * invocation is supplied the return value of the previous.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Util
	     * @param {...(Function|Function[])} [funcs] The functions to invoke.
	     * @returns {Function} Returns the new composite function.
	     * @see _.flowRight
	     * @example
	     *
	     * function square(n) {
	     *   return n * n;
	     * }
	     *
	     * var addSquare = _.flow([_.add, square]);
	     * addSquare(1, 2);
	     * // => 9
	     */
	    var flow = createFlow();

	    /**
	     * This method is like `_.flow` except that it creates a function that
	     * invokes the given functions from right to left.
	     *
	     * @static
	     * @since 3.0.0
	     * @memberOf _
	     * @category Util
	     * @param {...(Function|Function[])} [funcs] The functions to invoke.
	     * @returns {Function} Returns the new composite function.
	     * @see _.flow
	     * @example
	     *
	     * function square(n) {
	     *   return n * n;
	     * }
	     *
	     * var addSquare = _.flowRight([square, _.add]);
	     * addSquare(1, 2);
	     * // => 9
	     */
	    var flowRight = createFlow(true);

	    /**
	     * This method returns the first argument it receives.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Util
	     * @param {*} value Any value.
	     * @returns {*} Returns `value`.
	     * @example
	     *
	     * var object = { 'a': 1 };
	     *
	     * console.log(_.identity(object) === object);
	     * // => true
	     */
	    function identity(value) {
	      return value;
	    }

	    /**
	     * Creates a function that invokes `func` with the arguments of the created
	     * function. If `func` is a property name, the created function returns the
	     * property value for a given element. If `func` is an array or object, the
	     * created function returns `true` for elements that contain the equivalent
	     * source properties, otherwise it returns `false`.
	     *
	     * @static
	     * @since 4.0.0
	     * @memberOf _
	     * @category Util
	     * @param {*} [func=_.identity] The value to convert to a callback.
	     * @returns {Function} Returns the callback.
	     * @example
	     *
	     * var users = [
	     *   { 'user': 'barney', 'age': 36, 'active': true },
	     *   { 'user': 'fred',   'age': 40, 'active': false }
	     * ];
	     *
	     * // The `_.matches` iteratee shorthand.
	     * _.filter(users, _.iteratee({ 'user': 'barney', 'active': true }));
	     * // => [{ 'user': 'barney', 'age': 36, 'active': true }]
	     *
	     * // The `_.matchesProperty` iteratee shorthand.
	     * _.filter(users, _.iteratee(['user', 'fred']));
	     * // => [{ 'user': 'fred', 'age': 40 }]
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.map(users, _.iteratee('user'));
	     * // => ['barney', 'fred']
	     *
	     * // Create custom iteratee shorthands.
	     * _.iteratee = _.wrap(_.iteratee, function(iteratee, func) {
	     *   return !_.isRegExp(func) ? iteratee(func) : function(string) {
	     *     return func.test(string);
	     *   };
	     * });
	     *
	     * _.filter(['abc', 'def'], /ef/);
	     * // => ['def']
	     */
	    function iteratee(func) {
	      return baseIteratee(typeof func == 'function' ? func : baseClone(func, CLONE_DEEP_FLAG));
	    }

	    /**
	     * Creates a function that performs a partial deep comparison between a given
	     * object and `source`, returning `true` if the given object has equivalent
	     * property values, else `false`.
	     *
	     * **Note:** The created function is equivalent to `_.isMatch` with `source`
	     * partially applied.
	     *
	     * Partial comparisons will match empty array and empty object `source`
	     * values against any array or object value, respectively. See `_.isEqual`
	     * for a list of supported value comparisons.
	     *
	     * **Note:** Multiple values can be checked by combining several matchers
	     * using `_.overSome`
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Util
	     * @param {Object} source The object of property values to match.
	     * @returns {Function} Returns the new spec function.
	     * @example
	     *
	     * var objects = [
	     *   { 'a': 1, 'b': 2, 'c': 3 },
	     *   { 'a': 4, 'b': 5, 'c': 6 }
	     * ];
	     *
	     * _.filter(objects, _.matches({ 'a': 4, 'c': 6 }));
	     * // => [{ 'a': 4, 'b': 5, 'c': 6 }]
	     *
	     * // Checking for several possible values
	     * _.filter(objects, _.overSome([_.matches({ 'a': 1 }), _.matches({ 'a': 4 })]));
	     * // => [{ 'a': 1, 'b': 2, 'c': 3 }, { 'a': 4, 'b': 5, 'c': 6 }]
	     */
	    function matches(source) {
	      return baseMatches(baseClone(source, CLONE_DEEP_FLAG));
	    }

	    /**
	     * Creates a function that performs a partial deep comparison between the
	     * value at `path` of a given object to `srcValue`, returning `true` if the
	     * object value is equivalent, else `false`.
	     *
	     * **Note:** Partial comparisons will match empty array and empty object
	     * `srcValue` values against any array or object value, respectively. See
	     * `_.isEqual` for a list of supported value comparisons.
	     *
	     * **Note:** Multiple values can be checked by combining several matchers
	     * using `_.overSome`
	     *
	     * @static
	     * @memberOf _
	     * @since 3.2.0
	     * @category Util
	     * @param {Array|string} path The path of the property to get.
	     * @param {*} srcValue The value to match.
	     * @returns {Function} Returns the new spec function.
	     * @example
	     *
	     * var objects = [
	     *   { 'a': 1, 'b': 2, 'c': 3 },
	     *   { 'a': 4, 'b': 5, 'c': 6 }
	     * ];
	     *
	     * _.find(objects, _.matchesProperty('a', 4));
	     * // => { 'a': 4, 'b': 5, 'c': 6 }
	     *
	     * // Checking for several possible values
	     * _.filter(objects, _.overSome([_.matchesProperty('a', 1), _.matchesProperty('a', 4)]));
	     * // => [{ 'a': 1, 'b': 2, 'c': 3 }, { 'a': 4, 'b': 5, 'c': 6 }]
	     */
	    function matchesProperty(path, srcValue) {
	      return baseMatchesProperty(path, baseClone(srcValue, CLONE_DEEP_FLAG));
	    }

	    /**
	     * Creates a function that invokes the method at `path` of a given object.
	     * Any additional arguments are provided to the invoked method.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.7.0
	     * @category Util
	     * @param {Array|string} path The path of the method to invoke.
	     * @param {...*} [args] The arguments to invoke the method with.
	     * @returns {Function} Returns the new invoker function.
	     * @example
	     *
	     * var objects = [
	     *   { 'a': { 'b': _.constant(2) } },
	     *   { 'a': { 'b': _.constant(1) } }
	     * ];
	     *
	     * _.map(objects, _.method('a.b'));
	     * // => [2, 1]
	     *
	     * _.map(objects, _.method(['a', 'b']));
	     * // => [2, 1]
	     */
	    var method = baseRest(function(path, args) {
	      return function(object) {
	        return baseInvoke(object, path, args);
	      };
	    });

	    /**
	     * The opposite of `_.method`; this method creates a function that invokes
	     * the method at a given path of `object`. Any additional arguments are
	     * provided to the invoked method.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.7.0
	     * @category Util
	     * @param {Object} object The object to query.
	     * @param {...*} [args] The arguments to invoke the method with.
	     * @returns {Function} Returns the new invoker function.
	     * @example
	     *
	     * var array = _.times(3, _.constant),
	     *     object = { 'a': array, 'b': array, 'c': array };
	     *
	     * _.map(['a[2]', 'c[0]'], _.methodOf(object));
	     * // => [2, 0]
	     *
	     * _.map([['a', '2'], ['c', '0']], _.methodOf(object));
	     * // => [2, 0]
	     */
	    var methodOf = baseRest(function(object, args) {
	      return function(path) {
	        return baseInvoke(object, path, args);
	      };
	    });

	    /**
	     * Adds all own enumerable string keyed function properties of a source
	     * object to the destination object. If `object` is a function, then methods
	     * are added to its prototype as well.
	     *
	     * **Note:** Use `_.runInContext` to create a pristine `lodash` function to
	     * avoid conflicts caused by modifying the original.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Util
	     * @param {Function|Object} [object=lodash] The destination object.
	     * @param {Object} source The object of functions to add.
	     * @param {Object} [options={}] The options object.
	     * @param {boolean} [options.chain=true] Specify whether mixins are chainable.
	     * @returns {Function|Object} Returns `object`.
	     * @example
	     *
	     * function vowels(string) {
	     *   return _.filter(string, function(v) {
	     *     return /[aeiou]/i.test(v);
	     *   });
	     * }
	     *
	     * _.mixin({ 'vowels': vowels });
	     * _.vowels('fred');
	     * // => ['e']
	     *
	     * _('fred').vowels().value();
	     * // => ['e']
	     *
	     * _.mixin({ 'vowels': vowels }, { 'chain': false });
	     * _('fred').vowels();
	     * // => ['e']
	     */
	    function mixin(object, source, options) {
	      var props = keys(source),
	          methodNames = baseFunctions(source, props);

	      if (options == null &&
	          !(isObject(source) && (methodNames.length || !props.length))) {
	        options = source;
	        source = object;
	        object = this;
	        methodNames = baseFunctions(source, keys(source));
	      }
	      var chain = !(isObject(options) && 'chain' in options) || !!options.chain,
	          isFunc = isFunction(object);

	      arrayEach(methodNames, function(methodName) {
	        var func = source[methodName];
	        object[methodName] = func;
	        if (isFunc) {
	          object.prototype[methodName] = function() {
	            var chainAll = this.__chain__;
	            if (chain || chainAll) {
	              var result = object(this.__wrapped__),
	                  actions = result.__actions__ = copyArray(this.__actions__);

	              actions.push({ 'func': func, 'args': arguments, 'thisArg': object });
	              result.__chain__ = chainAll;
	              return result;
	            }
	            return func.apply(object, arrayPush([this.value()], arguments));
	          };
	        }
	      });

	      return object;
	    }

	    /**
	     * Reverts the `_` variable to its previous value and returns a reference to
	     * the `lodash` function.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Util
	     * @returns {Function} Returns the `lodash` function.
	     * @example
	     *
	     * var lodash = _.noConflict();
	     */
	    function noConflict() {
	      if (root._ === this) {
	        root._ = oldDash;
	      }
	      return this;
	    }

	    /**
	     * This method returns `undefined`.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.3.0
	     * @category Util
	     * @example
	     *
	     * _.times(2, _.noop);
	     * // => [undefined, undefined]
	     */
	    function noop() {
	      // No operation performed.
	    }

	    /**
	     * Creates a function that gets the argument at index `n`. If `n` is negative,
	     * the nth argument from the end is returned.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Util
	     * @param {number} [n=0] The index of the argument to return.
	     * @returns {Function} Returns the new pass-thru function.
	     * @example
	     *
	     * var func = _.nthArg(1);
	     * func('a', 'b', 'c', 'd');
	     * // => 'b'
	     *
	     * var func = _.nthArg(-2);
	     * func('a', 'b', 'c', 'd');
	     * // => 'c'
	     */
	    function nthArg(n) {
	      n = toInteger(n);
	      return baseRest(function(args) {
	        return baseNth(args, n);
	      });
	    }

	    /**
	     * Creates a function that invokes `iteratees` with the arguments it receives
	     * and returns their results.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Util
	     * @param {...(Function|Function[])} [iteratees=[_.identity]]
	     *  The iteratees to invoke.
	     * @returns {Function} Returns the new function.
	     * @example
	     *
	     * var func = _.over([Math.max, Math.min]);
	     *
	     * func(1, 2, 3, 4);
	     * // => [4, 1]
	     */
	    var over = createOver(arrayMap);

	    /**
	     * Creates a function that checks if **all** of the `predicates` return
	     * truthy when invoked with the arguments it receives.
	     *
	     * Following shorthands are possible for providing predicates.
	     * Pass an `Object` and it will be used as an parameter for `_.matches` to create the predicate.
	     * Pass an `Array` of parameters for `_.matchesProperty` and the predicate will be created using them.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Util
	     * @param {...(Function|Function[])} [predicates=[_.identity]]
	     *  The predicates to check.
	     * @returns {Function} Returns the new function.
	     * @example
	     *
	     * var func = _.overEvery([Boolean, isFinite]);
	     *
	     * func('1');
	     * // => true
	     *
	     * func(null);
	     * // => false
	     *
	     * func(NaN);
	     * // => false
	     */
	    var overEvery = createOver(arrayEvery);

	    /**
	     * Creates a function that checks if **any** of the `predicates` return
	     * truthy when invoked with the arguments it receives.
	     *
	     * Following shorthands are possible for providing predicates.
	     * Pass an `Object` and it will be used as an parameter for `_.matches` to create the predicate.
	     * Pass an `Array` of parameters for `_.matchesProperty` and the predicate will be created using them.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Util
	     * @param {...(Function|Function[])} [predicates=[_.identity]]
	     *  The predicates to check.
	     * @returns {Function} Returns the new function.
	     * @example
	     *
	     * var func = _.overSome([Boolean, isFinite]);
	     *
	     * func('1');
	     * // => true
	     *
	     * func(null);
	     * // => true
	     *
	     * func(NaN);
	     * // => false
	     *
	     * var matchesFunc = _.overSome([{ 'a': 1 }, { 'a': 2 }])
	     * var matchesPropertyFunc = _.overSome([['a', 1], ['a', 2]])
	     */
	    var overSome = createOver(arraySome);

	    /**
	     * Creates a function that returns the value at `path` of a given object.
	     *
	     * @static
	     * @memberOf _
	     * @since 2.4.0
	     * @category Util
	     * @param {Array|string} path The path of the property to get.
	     * @returns {Function} Returns the new accessor function.
	     * @example
	     *
	     * var objects = [
	     *   { 'a': { 'b': 2 } },
	     *   { 'a': { 'b': 1 } }
	     * ];
	     *
	     * _.map(objects, _.property('a.b'));
	     * // => [2, 1]
	     *
	     * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
	     * // => [1, 2]
	     */
	    function property(path) {
	      return isKey(path) ? baseProperty(toKey(path)) : basePropertyDeep(path);
	    }

	    /**
	     * The opposite of `_.property`; this method creates a function that returns
	     * the value at a given path of `object`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.0.0
	     * @category Util
	     * @param {Object} object The object to query.
	     * @returns {Function} Returns the new accessor function.
	     * @example
	     *
	     * var array = [0, 1, 2],
	     *     object = { 'a': array, 'b': array, 'c': array };
	     *
	     * _.map(['a[2]', 'c[0]'], _.propertyOf(object));
	     * // => [2, 0]
	     *
	     * _.map([['a', '2'], ['c', '0']], _.propertyOf(object));
	     * // => [2, 0]
	     */
	    function propertyOf(object) {
	      return function(path) {
	        return object == null ? undefined$1 : baseGet(object, path);
	      };
	    }

	    /**
	     * Creates an array of numbers (positive and/or negative) progressing from
	     * `start` up to, but not including, `end`. A step of `-1` is used if a negative
	     * `start` is specified without an `end` or `step`. If `end` is not specified,
	     * it's set to `start` with `start` then set to `0`.
	     *
	     * **Note:** JavaScript follows the IEEE-754 standard for resolving
	     * floating-point values which can produce unexpected results.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Util
	     * @param {number} [start=0] The start of the range.
	     * @param {number} end The end of the range.
	     * @param {number} [step=1] The value to increment or decrement by.
	     * @returns {Array} Returns the range of numbers.
	     * @see _.inRange, _.rangeRight
	     * @example
	     *
	     * _.range(4);
	     * // => [0, 1, 2, 3]
	     *
	     * _.range(-4);
	     * // => [0, -1, -2, -3]
	     *
	     * _.range(1, 5);
	     * // => [1, 2, 3, 4]
	     *
	     * _.range(0, 20, 5);
	     * // => [0, 5, 10, 15]
	     *
	     * _.range(0, -4, -1);
	     * // => [0, -1, -2, -3]
	     *
	     * _.range(1, 4, 0);
	     * // => [1, 1, 1]
	     *
	     * _.range(0);
	     * // => []
	     */
	    var range = createRange();

	    /**
	     * This method is like `_.range` except that it populates values in
	     * descending order.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Util
	     * @param {number} [start=0] The start of the range.
	     * @param {number} end The end of the range.
	     * @param {number} [step=1] The value to increment or decrement by.
	     * @returns {Array} Returns the range of numbers.
	     * @see _.inRange, _.range
	     * @example
	     *
	     * _.rangeRight(4);
	     * // => [3, 2, 1, 0]
	     *
	     * _.rangeRight(-4);
	     * // => [-3, -2, -1, 0]
	     *
	     * _.rangeRight(1, 5);
	     * // => [4, 3, 2, 1]
	     *
	     * _.rangeRight(0, 20, 5);
	     * // => [15, 10, 5, 0]
	     *
	     * _.rangeRight(0, -4, -1);
	     * // => [-3, -2, -1, 0]
	     *
	     * _.rangeRight(1, 4, 0);
	     * // => [1, 1, 1]
	     *
	     * _.rangeRight(0);
	     * // => []
	     */
	    var rangeRight = createRange(true);

	    /**
	     * This method returns a new empty array.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.13.0
	     * @category Util
	     * @returns {Array} Returns the new empty array.
	     * @example
	     *
	     * var arrays = _.times(2, _.stubArray);
	     *
	     * console.log(arrays);
	     * // => [[], []]
	     *
	     * console.log(arrays[0] === arrays[1]);
	     * // => false
	     */
	    function stubArray() {
	      return [];
	    }

	    /**
	     * This method returns `false`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.13.0
	     * @category Util
	     * @returns {boolean} Returns `false`.
	     * @example
	     *
	     * _.times(2, _.stubFalse);
	     * // => [false, false]
	     */
	    function stubFalse() {
	      return false;
	    }

	    /**
	     * This method returns a new empty object.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.13.0
	     * @category Util
	     * @returns {Object} Returns the new empty object.
	     * @example
	     *
	     * var objects = _.times(2, _.stubObject);
	     *
	     * console.log(objects);
	     * // => [{}, {}]
	     *
	     * console.log(objects[0] === objects[1]);
	     * // => false
	     */
	    function stubObject() {
	      return {};
	    }

	    /**
	     * This method returns an empty string.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.13.0
	     * @category Util
	     * @returns {string} Returns the empty string.
	     * @example
	     *
	     * _.times(2, _.stubString);
	     * // => ['', '']
	     */
	    function stubString() {
	      return '';
	    }

	    /**
	     * This method returns `true`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.13.0
	     * @category Util
	     * @returns {boolean} Returns `true`.
	     * @example
	     *
	     * _.times(2, _.stubTrue);
	     * // => [true, true]
	     */
	    function stubTrue() {
	      return true;
	    }

	    /**
	     * Invokes the iteratee `n` times, returning an array of the results of
	     * each invocation. The iteratee is invoked with one argument; (index).
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Util
	     * @param {number} n The number of times to invoke `iteratee`.
	     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
	     * @returns {Array} Returns the array of results.
	     * @example
	     *
	     * _.times(3, String);
	     * // => ['0', '1', '2']
	     *
	     *  _.times(4, _.constant(0));
	     * // => [0, 0, 0, 0]
	     */
	    function times(n, iteratee) {
	      n = toInteger(n);
	      if (n < 1 || n > MAX_SAFE_INTEGER) {
	        return [];
	      }
	      var index = MAX_ARRAY_LENGTH,
	          length = nativeMin(n, MAX_ARRAY_LENGTH);

	      iteratee = getIteratee(iteratee);
	      n -= MAX_ARRAY_LENGTH;

	      var result = baseTimes(length, iteratee);
	      while (++index < n) {
	        iteratee(index);
	      }
	      return result;
	    }

	    /**
	     * Converts `value` to a property path array.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Util
	     * @param {*} value The value to convert.
	     * @returns {Array} Returns the new property path array.
	     * @example
	     *
	     * _.toPath('a.b.c');
	     * // => ['a', 'b', 'c']
	     *
	     * _.toPath('a[0].b.c');
	     * // => ['a', '0', 'b', 'c']
	     */
	    function toPath(value) {
	      if (isArray(value)) {
	        return arrayMap(value, toKey);
	      }
	      return isSymbol(value) ? [value] : copyArray(stringToPath(toString(value)));
	    }

	    /**
	     * Generates a unique ID. If `prefix` is given, the ID is appended to it.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Util
	     * @param {string} [prefix=''] The value to prefix the ID with.
	     * @returns {string} Returns the unique ID.
	     * @example
	     *
	     * _.uniqueId('contact_');
	     * // => 'contact_104'
	     *
	     * _.uniqueId();
	     * // => '105'
	     */
	    function uniqueId(prefix) {
	      var id = ++idCounter;
	      return toString(prefix) + id;
	    }

	    /*------------------------------------------------------------------------*/

	    /**
	     * Adds two numbers.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.4.0
	     * @category Math
	     * @param {number} augend The first number in an addition.
	     * @param {number} addend The second number in an addition.
	     * @returns {number} Returns the total.
	     * @example
	     *
	     * _.add(6, 4);
	     * // => 10
	     */
	    var add = createMathOperation(function(augend, addend) {
	      return augend + addend;
	    }, 0);

	    /**
	     * Computes `number` rounded up to `precision`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.10.0
	     * @category Math
	     * @param {number} number The number to round up.
	     * @param {number} [precision=0] The precision to round up to.
	     * @returns {number} Returns the rounded up number.
	     * @example
	     *
	     * _.ceil(4.006);
	     * // => 5
	     *
	     * _.ceil(6.004, 2);
	     * // => 6.01
	     *
	     * _.ceil(6040, -2);
	     * // => 6100
	     */
	    var ceil = createRound('ceil');

	    /**
	     * Divide two numbers.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.7.0
	     * @category Math
	     * @param {number} dividend The first number in a division.
	     * @param {number} divisor The second number in a division.
	     * @returns {number} Returns the quotient.
	     * @example
	     *
	     * _.divide(6, 4);
	     * // => 1.5
	     */
	    var divide = createMathOperation(function(dividend, divisor) {
	      return dividend / divisor;
	    }, 1);

	    /**
	     * Computes `number` rounded down to `precision`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.10.0
	     * @category Math
	     * @param {number} number The number to round down.
	     * @param {number} [precision=0] The precision to round down to.
	     * @returns {number} Returns the rounded down number.
	     * @example
	     *
	     * _.floor(4.006);
	     * // => 4
	     *
	     * _.floor(0.046, 2);
	     * // => 0.04
	     *
	     * _.floor(4060, -2);
	     * // => 4000
	     */
	    var floor = createRound('floor');

	    /**
	     * Computes the maximum value of `array`. If `array` is empty or falsey,
	     * `undefined` is returned.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Math
	     * @param {Array} array The array to iterate over.
	     * @returns {*} Returns the maximum value.
	     * @example
	     *
	     * _.max([4, 2, 8, 6]);
	     * // => 8
	     *
	     * _.max([]);
	     * // => undefined
	     */
	    function max(array) {
	      return (array && array.length)
	        ? baseExtremum(array, identity, baseGt)
	        : undefined$1;
	    }

	    /**
	     * This method is like `_.max` except that it accepts `iteratee` which is
	     * invoked for each element in `array` to generate the criterion by which
	     * the value is ranked. The iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Math
	     * @param {Array} array The array to iterate over.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {*} Returns the maximum value.
	     * @example
	     *
	     * var objects = [{ 'n': 1 }, { 'n': 2 }];
	     *
	     * _.maxBy(objects, function(o) { return o.n; });
	     * // => { 'n': 2 }
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.maxBy(objects, 'n');
	     * // => { 'n': 2 }
	     */
	    function maxBy(array, iteratee) {
	      return (array && array.length)
	        ? baseExtremum(array, getIteratee(iteratee, 2), baseGt)
	        : undefined$1;
	    }

	    /**
	     * Computes the mean of the values in `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Math
	     * @param {Array} array The array to iterate over.
	     * @returns {number} Returns the mean.
	     * @example
	     *
	     * _.mean([4, 2, 8, 6]);
	     * // => 5
	     */
	    function mean(array) {
	      return baseMean(array, identity);
	    }

	    /**
	     * This method is like `_.mean` except that it accepts `iteratee` which is
	     * invoked for each element in `array` to generate the value to be averaged.
	     * The iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.7.0
	     * @category Math
	     * @param {Array} array The array to iterate over.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {number} Returns the mean.
	     * @example
	     *
	     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
	     *
	     * _.meanBy(objects, function(o) { return o.n; });
	     * // => 5
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.meanBy(objects, 'n');
	     * // => 5
	     */
	    function meanBy(array, iteratee) {
	      return baseMean(array, getIteratee(iteratee, 2));
	    }

	    /**
	     * Computes the minimum value of `array`. If `array` is empty or falsey,
	     * `undefined` is returned.
	     *
	     * @static
	     * @since 0.1.0
	     * @memberOf _
	     * @category Math
	     * @param {Array} array The array to iterate over.
	     * @returns {*} Returns the minimum value.
	     * @example
	     *
	     * _.min([4, 2, 8, 6]);
	     * // => 2
	     *
	     * _.min([]);
	     * // => undefined
	     */
	    function min(array) {
	      return (array && array.length)
	        ? baseExtremum(array, identity, baseLt)
	        : undefined$1;
	    }

	    /**
	     * This method is like `_.min` except that it accepts `iteratee` which is
	     * invoked for each element in `array` to generate the criterion by which
	     * the value is ranked. The iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Math
	     * @param {Array} array The array to iterate over.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {*} Returns the minimum value.
	     * @example
	     *
	     * var objects = [{ 'n': 1 }, { 'n': 2 }];
	     *
	     * _.minBy(objects, function(o) { return o.n; });
	     * // => { 'n': 1 }
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.minBy(objects, 'n');
	     * // => { 'n': 1 }
	     */
	    function minBy(array, iteratee) {
	      return (array && array.length)
	        ? baseExtremum(array, getIteratee(iteratee, 2), baseLt)
	        : undefined$1;
	    }

	    /**
	     * Multiply two numbers.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.7.0
	     * @category Math
	     * @param {number} multiplier The first number in a multiplication.
	     * @param {number} multiplicand The second number in a multiplication.
	     * @returns {number} Returns the product.
	     * @example
	     *
	     * _.multiply(6, 4);
	     * // => 24
	     */
	    var multiply = createMathOperation(function(multiplier, multiplicand) {
	      return multiplier * multiplicand;
	    }, 1);

	    /**
	     * Computes `number` rounded to `precision`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.10.0
	     * @category Math
	     * @param {number} number The number to round.
	     * @param {number} [precision=0] The precision to round to.
	     * @returns {number} Returns the rounded number.
	     * @example
	     *
	     * _.round(4.006);
	     * // => 4
	     *
	     * _.round(4.006, 2);
	     * // => 4.01
	     *
	     * _.round(4060, -2);
	     * // => 4100
	     */
	    var round = createRound('round');

	    /**
	     * Subtract two numbers.
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Math
	     * @param {number} minuend The first number in a subtraction.
	     * @param {number} subtrahend The second number in a subtraction.
	     * @returns {number} Returns the difference.
	     * @example
	     *
	     * _.subtract(6, 4);
	     * // => 2
	     */
	    var subtract = createMathOperation(function(minuend, subtrahend) {
	      return minuend - subtrahend;
	    }, 0);

	    /**
	     * Computes the sum of the values in `array`.
	     *
	     * @static
	     * @memberOf _
	     * @since 3.4.0
	     * @category Math
	     * @param {Array} array The array to iterate over.
	     * @returns {number} Returns the sum.
	     * @example
	     *
	     * _.sum([4, 2, 8, 6]);
	     * // => 20
	     */
	    function sum(array) {
	      return (array && array.length)
	        ? baseSum(array, identity)
	        : 0;
	    }

	    /**
	     * This method is like `_.sum` except that it accepts `iteratee` which is
	     * invoked for each element in `array` to generate the value to be summed.
	     * The iteratee is invoked with one argument: (value).
	     *
	     * @static
	     * @memberOf _
	     * @since 4.0.0
	     * @category Math
	     * @param {Array} array The array to iterate over.
	     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
	     * @returns {number} Returns the sum.
	     * @example
	     *
	     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
	     *
	     * _.sumBy(objects, function(o) { return o.n; });
	     * // => 20
	     *
	     * // The `_.property` iteratee shorthand.
	     * _.sumBy(objects, 'n');
	     * // => 20
	     */
	    function sumBy(array, iteratee) {
	      return (array && array.length)
	        ? baseSum(array, getIteratee(iteratee, 2))
	        : 0;
	    }

	    /*------------------------------------------------------------------------*/

	    // Add methods that return wrapped values in chain sequences.
	    lodash.after = after;
	    lodash.ary = ary;
	    lodash.assign = assign;
	    lodash.assignIn = assignIn;
	    lodash.assignInWith = assignInWith;
	    lodash.assignWith = assignWith;
	    lodash.at = at;
	    lodash.before = before;
	    lodash.bind = bind;
	    lodash.bindAll = bindAll;
	    lodash.bindKey = bindKey;
	    lodash.castArray = castArray;
	    lodash.chain = chain;
	    lodash.chunk = chunk;
	    lodash.compact = compact;
	    lodash.concat = concat;
	    lodash.cond = cond;
	    lodash.conforms = conforms;
	    lodash.constant = constant;
	    lodash.countBy = countBy;
	    lodash.create = create;
	    lodash.curry = curry;
	    lodash.curryRight = curryRight;
	    lodash.debounce = debounce;
	    lodash.defaults = defaults;
	    lodash.defaultsDeep = defaultsDeep;
	    lodash.defer = defer;
	    lodash.delay = delay;
	    lodash.difference = difference;
	    lodash.differenceBy = differenceBy;
	    lodash.differenceWith = differenceWith;
	    lodash.drop = drop;
	    lodash.dropRight = dropRight;
	    lodash.dropRightWhile = dropRightWhile;
	    lodash.dropWhile = dropWhile;
	    lodash.fill = fill;
	    lodash.filter = filter;
	    lodash.flatMap = flatMap;
	    lodash.flatMapDeep = flatMapDeep;
	    lodash.flatMapDepth = flatMapDepth;
	    lodash.flatten = flatten;
	    lodash.flattenDeep = flattenDeep;
	    lodash.flattenDepth = flattenDepth;
	    lodash.flip = flip;
	    lodash.flow = flow;
	    lodash.flowRight = flowRight;
	    lodash.fromPairs = fromPairs;
	    lodash.functions = functions;
	    lodash.functionsIn = functionsIn;
	    lodash.groupBy = groupBy;
	    lodash.initial = initial;
	    lodash.intersection = intersection;
	    lodash.intersectionBy = intersectionBy;
	    lodash.intersectionWith = intersectionWith;
	    lodash.invert = invert;
	    lodash.invertBy = invertBy;
	    lodash.invokeMap = invokeMap;
	    lodash.iteratee = iteratee;
	    lodash.keyBy = keyBy;
	    lodash.keys = keys;
	    lodash.keysIn = keysIn;
	    lodash.map = map;
	    lodash.mapKeys = mapKeys;
	    lodash.mapValues = mapValues;
	    lodash.matches = matches;
	    lodash.matchesProperty = matchesProperty;
	    lodash.memoize = memoize;
	    lodash.merge = merge;
	    lodash.mergeWith = mergeWith;
	    lodash.method = method;
	    lodash.methodOf = methodOf;
	    lodash.mixin = mixin;
	    lodash.negate = negate;
	    lodash.nthArg = nthArg;
	    lodash.omit = omit;
	    lodash.omitBy = omitBy;
	    lodash.once = once;
	    lodash.orderBy = orderBy;
	    lodash.over = over;
	    lodash.overArgs = overArgs;
	    lodash.overEvery = overEvery;
	    lodash.overSome = overSome;
	    lodash.partial = partial;
	    lodash.partialRight = partialRight;
	    lodash.partition = partition;
	    lodash.pick = pick;
	    lodash.pickBy = pickBy;
	    lodash.property = property;
	    lodash.propertyOf = propertyOf;
	    lodash.pull = pull;
	    lodash.pullAll = pullAll;
	    lodash.pullAllBy = pullAllBy;
	    lodash.pullAllWith = pullAllWith;
	    lodash.pullAt = pullAt;
	    lodash.range = range;
	    lodash.rangeRight = rangeRight;
	    lodash.rearg = rearg;
	    lodash.reject = reject;
	    lodash.remove = remove;
	    lodash.rest = rest;
	    lodash.reverse = reverse;
	    lodash.sampleSize = sampleSize;
	    lodash.set = set;
	    lodash.setWith = setWith;
	    lodash.shuffle = shuffle;
	    lodash.slice = slice;
	    lodash.sortBy = sortBy;
	    lodash.sortedUniq = sortedUniq;
	    lodash.sortedUniqBy = sortedUniqBy;
	    lodash.split = split;
	    lodash.spread = spread;
	    lodash.tail = tail;
	    lodash.take = take;
	    lodash.takeRight = takeRight;
	    lodash.takeRightWhile = takeRightWhile;
	    lodash.takeWhile = takeWhile;
	    lodash.tap = tap;
	    lodash.throttle = throttle;
	    lodash.thru = thru;
	    lodash.toArray = toArray;
	    lodash.toPairs = toPairs;
	    lodash.toPairsIn = toPairsIn;
	    lodash.toPath = toPath;
	    lodash.toPlainObject = toPlainObject;
	    lodash.transform = transform;
	    lodash.unary = unary;
	    lodash.union = union;
	    lodash.unionBy = unionBy;
	    lodash.unionWith = unionWith;
	    lodash.uniq = uniq;
	    lodash.uniqBy = uniqBy;
	    lodash.uniqWith = uniqWith;
	    lodash.unset = unset;
	    lodash.unzip = unzip;
	    lodash.unzipWith = unzipWith;
	    lodash.update = update;
	    lodash.updateWith = updateWith;
	    lodash.values = values;
	    lodash.valuesIn = valuesIn;
	    lodash.without = without;
	    lodash.words = words;
	    lodash.wrap = wrap;
	    lodash.xor = xor;
	    lodash.xorBy = xorBy;
	    lodash.xorWith = xorWith;
	    lodash.zip = zip;
	    lodash.zipObject = zipObject;
	    lodash.zipObjectDeep = zipObjectDeep;
	    lodash.zipWith = zipWith;

	    // Add aliases.
	    lodash.entries = toPairs;
	    lodash.entriesIn = toPairsIn;
	    lodash.extend = assignIn;
	    lodash.extendWith = assignInWith;

	    // Add methods to `lodash.prototype`.
	    mixin(lodash, lodash);

	    /*------------------------------------------------------------------------*/

	    // Add methods that return unwrapped values in chain sequences.
	    lodash.add = add;
	    lodash.attempt = attempt;
	    lodash.camelCase = camelCase;
	    lodash.capitalize = capitalize;
	    lodash.ceil = ceil;
	    lodash.clamp = clamp;
	    lodash.clone = clone;
	    lodash.cloneDeep = cloneDeep;
	    lodash.cloneDeepWith = cloneDeepWith;
	    lodash.cloneWith = cloneWith;
	    lodash.conformsTo = conformsTo;
	    lodash.deburr = deburr;
	    lodash.defaultTo = defaultTo;
	    lodash.divide = divide;
	    lodash.endsWith = endsWith;
	    lodash.eq = eq;
	    lodash.escape = escape;
	    lodash.escapeRegExp = escapeRegExp;
	    lodash.every = every;
	    lodash.find = find;
	    lodash.findIndex = findIndex;
	    lodash.findKey = findKey;
	    lodash.findLast = findLast;
	    lodash.findLastIndex = findLastIndex;
	    lodash.findLastKey = findLastKey;
	    lodash.floor = floor;
	    lodash.forEach = forEach;
	    lodash.forEachRight = forEachRight;
	    lodash.forIn = forIn;
	    lodash.forInRight = forInRight;
	    lodash.forOwn = forOwn;
	    lodash.forOwnRight = forOwnRight;
	    lodash.get = get;
	    lodash.gt = gt;
	    lodash.gte = gte;
	    lodash.has = has;
	    lodash.hasIn = hasIn;
	    lodash.head = head;
	    lodash.identity = identity;
	    lodash.includes = includes;
	    lodash.indexOf = indexOf;
	    lodash.inRange = inRange;
	    lodash.invoke = invoke;
	    lodash.isArguments = isArguments;
	    lodash.isArray = isArray;
	    lodash.isArrayBuffer = isArrayBuffer;
	    lodash.isArrayLike = isArrayLike;
	    lodash.isArrayLikeObject = isArrayLikeObject;
	    lodash.isBoolean = isBoolean;
	    lodash.isBuffer = isBuffer;
	    lodash.isDate = isDate;
	    lodash.isElement = isElement;
	    lodash.isEmpty = isEmpty;
	    lodash.isEqual = isEqual;
	    lodash.isEqualWith = isEqualWith;
	    lodash.isError = isError;
	    lodash.isFinite = isFinite;
	    lodash.isFunction = isFunction;
	    lodash.isInteger = isInteger;
	    lodash.isLength = isLength;
	    lodash.isMap = isMap;
	    lodash.isMatch = isMatch;
	    lodash.isMatchWith = isMatchWith;
	    lodash.isNaN = isNaN;
	    lodash.isNative = isNative;
	    lodash.isNil = isNil;
	    lodash.isNull = isNull;
	    lodash.isNumber = isNumber;
	    lodash.isObject = isObject;
	    lodash.isObjectLike = isObjectLike;
	    lodash.isPlainObject = isPlainObject;
	    lodash.isRegExp = isRegExp;
	    lodash.isSafeInteger = isSafeInteger;
	    lodash.isSet = isSet;
	    lodash.isString = isString;
	    lodash.isSymbol = isSymbol;
	    lodash.isTypedArray = isTypedArray;
	    lodash.isUndefined = isUndefined;
	    lodash.isWeakMap = isWeakMap;
	    lodash.isWeakSet = isWeakSet;
	    lodash.join = join;
	    lodash.kebabCase = kebabCase;
	    lodash.last = last;
	    lodash.lastIndexOf = lastIndexOf;
	    lodash.lowerCase = lowerCase;
	    lodash.lowerFirst = lowerFirst;
	    lodash.lt = lt;
	    lodash.lte = lte;
	    lodash.max = max;
	    lodash.maxBy = maxBy;
	    lodash.mean = mean;
	    lodash.meanBy = meanBy;
	    lodash.min = min;
	    lodash.minBy = minBy;
	    lodash.stubArray = stubArray;
	    lodash.stubFalse = stubFalse;
	    lodash.stubObject = stubObject;
	    lodash.stubString = stubString;
	    lodash.stubTrue = stubTrue;
	    lodash.multiply = multiply;
	    lodash.nth = nth;
	    lodash.noConflict = noConflict;
	    lodash.noop = noop;
	    lodash.now = now;
	    lodash.pad = pad;
	    lodash.padEnd = padEnd;
	    lodash.padStart = padStart;
	    lodash.parseInt = parseInt;
	    lodash.random = random;
	    lodash.reduce = reduce;
	    lodash.reduceRight = reduceRight;
	    lodash.repeat = repeat;
	    lodash.replace = replace;
	    lodash.result = result;
	    lodash.round = round;
	    lodash.runInContext = runInContext;
	    lodash.sample = sample;
	    lodash.size = size;
	    lodash.snakeCase = snakeCase;
	    lodash.some = some;
	    lodash.sortedIndex = sortedIndex;
	    lodash.sortedIndexBy = sortedIndexBy;
	    lodash.sortedIndexOf = sortedIndexOf;
	    lodash.sortedLastIndex = sortedLastIndex;
	    lodash.sortedLastIndexBy = sortedLastIndexBy;
	    lodash.sortedLastIndexOf = sortedLastIndexOf;
	    lodash.startCase = startCase;
	    lodash.startsWith = startsWith;
	    lodash.subtract = subtract;
	    lodash.sum = sum;
	    lodash.sumBy = sumBy;
	    lodash.template = template;
	    lodash.times = times;
	    lodash.toFinite = toFinite;
	    lodash.toInteger = toInteger;
	    lodash.toLength = toLength;
	    lodash.toLower = toLower;
	    lodash.toNumber = toNumber;
	    lodash.toSafeInteger = toSafeInteger;
	    lodash.toString = toString;
	    lodash.toUpper = toUpper;
	    lodash.trim = trim;
	    lodash.trimEnd = trimEnd;
	    lodash.trimStart = trimStart;
	    lodash.truncate = truncate;
	    lodash.unescape = unescape;
	    lodash.uniqueId = uniqueId;
	    lodash.upperCase = upperCase;
	    lodash.upperFirst = upperFirst;

	    // Add aliases.
	    lodash.each = forEach;
	    lodash.eachRight = forEachRight;
	    lodash.first = head;

	    mixin(lodash, (function() {
	      var source = {};
	      baseForOwn(lodash, function(func, methodName) {
	        if (!hasOwnProperty.call(lodash.prototype, methodName)) {
	          source[methodName] = func;
	        }
	      });
	      return source;
	    }()), { 'chain': false });

	    /*------------------------------------------------------------------------*/

	    /**
	     * The semantic version number.
	     *
	     * @static
	     * @memberOf _
	     * @type {string}
	     */
	    lodash.VERSION = VERSION;

	    // Assign default placeholders.
	    arrayEach(['bind', 'bindKey', 'curry', 'curryRight', 'partial', 'partialRight'], function(methodName) {
	      lodash[methodName].placeholder = lodash;
	    });

	    // Add `LazyWrapper` methods for `_.drop` and `_.take` variants.
	    arrayEach(['drop', 'take'], function(methodName, index) {
	      LazyWrapper.prototype[methodName] = function(n) {
	        n = n === undefined$1 ? 1 : nativeMax(toInteger(n), 0);

	        var result = (this.__filtered__ && !index)
	          ? new LazyWrapper(this)
	          : this.clone();

	        if (result.__filtered__) {
	          result.__takeCount__ = nativeMin(n, result.__takeCount__);
	        } else {
	          result.__views__.push({
	            'size': nativeMin(n, MAX_ARRAY_LENGTH),
	            'type': methodName + (result.__dir__ < 0 ? 'Right' : '')
	          });
	        }
	        return result;
	      };

	      LazyWrapper.prototype[methodName + 'Right'] = function(n) {
	        return this.reverse()[methodName](n).reverse();
	      };
	    });

	    // Add `LazyWrapper` methods that accept an `iteratee` value.
	    arrayEach(['filter', 'map', 'takeWhile'], function(methodName, index) {
	      var type = index + 1,
	          isFilter = type == LAZY_FILTER_FLAG || type == LAZY_WHILE_FLAG;

	      LazyWrapper.prototype[methodName] = function(iteratee) {
	        var result = this.clone();
	        result.__iteratees__.push({
	          'iteratee': getIteratee(iteratee, 3),
	          'type': type
	        });
	        result.__filtered__ = result.__filtered__ || isFilter;
	        return result;
	      };
	    });

	    // Add `LazyWrapper` methods for `_.head` and `_.last`.
	    arrayEach(['head', 'last'], function(methodName, index) {
	      var takeName = 'take' + (index ? 'Right' : '');

	      LazyWrapper.prototype[methodName] = function() {
	        return this[takeName](1).value()[0];
	      };
	    });

	    // Add `LazyWrapper` methods for `_.initial` and `_.tail`.
	    arrayEach(['initial', 'tail'], function(methodName, index) {
	      var dropName = 'drop' + (index ? '' : 'Right');

	      LazyWrapper.prototype[methodName] = function() {
	        return this.__filtered__ ? new LazyWrapper(this) : this[dropName](1);
	      };
	    });

	    LazyWrapper.prototype.compact = function() {
	      return this.filter(identity);
	    };

	    LazyWrapper.prototype.find = function(predicate) {
	      return this.filter(predicate).head();
	    };

	    LazyWrapper.prototype.findLast = function(predicate) {
	      return this.reverse().find(predicate);
	    };

	    LazyWrapper.prototype.invokeMap = baseRest(function(path, args) {
	      if (typeof path == 'function') {
	        return new LazyWrapper(this);
	      }
	      return this.map(function(value) {
	        return baseInvoke(value, path, args);
	      });
	    });

	    LazyWrapper.prototype.reject = function(predicate) {
	      return this.filter(negate(getIteratee(predicate)));
	    };

	    LazyWrapper.prototype.slice = function(start, end) {
	      start = toInteger(start);

	      var result = this;
	      if (result.__filtered__ && (start > 0 || end < 0)) {
	        return new LazyWrapper(result);
	      }
	      if (start < 0) {
	        result = result.takeRight(-start);
	      } else if (start) {
	        result = result.drop(start);
	      }
	      if (end !== undefined$1) {
	        end = toInteger(end);
	        result = end < 0 ? result.dropRight(-end) : result.take(end - start);
	      }
	      return result;
	    };

	    LazyWrapper.prototype.takeRightWhile = function(predicate) {
	      return this.reverse().takeWhile(predicate).reverse();
	    };

	    LazyWrapper.prototype.toArray = function() {
	      return this.take(MAX_ARRAY_LENGTH);
	    };

	    // Add `LazyWrapper` methods to `lodash.prototype`.
	    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
	      var checkIteratee = /^(?:filter|find|map|reject)|While$/.test(methodName),
	          isTaker = /^(?:head|last)$/.test(methodName),
	          lodashFunc = lodash[isTaker ? ('take' + (methodName == 'last' ? 'Right' : '')) : methodName],
	          retUnwrapped = isTaker || /^find/.test(methodName);

	      if (!lodashFunc) {
	        return;
	      }
	      lodash.prototype[methodName] = function() {
	        var value = this.__wrapped__,
	            args = isTaker ? [1] : arguments,
	            isLazy = value instanceof LazyWrapper,
	            iteratee = args[0],
	            useLazy = isLazy || isArray(value);

	        var interceptor = function(value) {
	          var result = lodashFunc.apply(lodash, arrayPush([value], args));
	          return (isTaker && chainAll) ? result[0] : result;
	        };

	        if (useLazy && checkIteratee && typeof iteratee == 'function' && iteratee.length != 1) {
	          // Avoid lazy use if the iteratee has a "length" value other than `1`.
	          isLazy = useLazy = false;
	        }
	        var chainAll = this.__chain__,
	            isHybrid = !!this.__actions__.length,
	            isUnwrapped = retUnwrapped && !chainAll,
	            onlyLazy = isLazy && !isHybrid;

	        if (!retUnwrapped && useLazy) {
	          value = onlyLazy ? value : new LazyWrapper(this);
	          var result = func.apply(value, args);
	          result.__actions__.push({ 'func': thru, 'args': [interceptor], 'thisArg': undefined$1 });
	          return new LodashWrapper(result, chainAll);
	        }
	        if (isUnwrapped && onlyLazy) {
	          return func.apply(this, args);
	        }
	        result = this.thru(interceptor);
	        return isUnwrapped ? (isTaker ? result.value()[0] : result.value()) : result;
	      };
	    });

	    // Add `Array` methods to `lodash.prototype`.
	    arrayEach(['pop', 'push', 'shift', 'sort', 'splice', 'unshift'], function(methodName) {
	      var func = arrayProto[methodName],
	          chainName = /^(?:push|sort|unshift)$/.test(methodName) ? 'tap' : 'thru',
	          retUnwrapped = /^(?:pop|shift)$/.test(methodName);

	      lodash.prototype[methodName] = function() {
	        var args = arguments;
	        if (retUnwrapped && !this.__chain__) {
	          var value = this.value();
	          return func.apply(isArray(value) ? value : [], args);
	        }
	        return this[chainName](function(value) {
	          return func.apply(isArray(value) ? value : [], args);
	        });
	      };
	    });

	    // Map minified method names to their real names.
	    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
	      var lodashFunc = lodash[methodName];
	      if (lodashFunc) {
	        var key = lodashFunc.name + '';
	        if (!hasOwnProperty.call(realNames, key)) {
	          realNames[key] = [];
	        }
	        realNames[key].push({ 'name': methodName, 'func': lodashFunc });
	      }
	    });

	    realNames[createHybrid(undefined$1, WRAP_BIND_KEY_FLAG).name] = [{
	      'name': 'wrapper',
	      'func': undefined$1
	    }];

	    // Add methods to `LazyWrapper`.
	    LazyWrapper.prototype.clone = lazyClone;
	    LazyWrapper.prototype.reverse = lazyReverse;
	    LazyWrapper.prototype.value = lazyValue;

	    // Add chain sequence methods to the `lodash` wrapper.
	    lodash.prototype.at = wrapperAt;
	    lodash.prototype.chain = wrapperChain;
	    lodash.prototype.commit = wrapperCommit;
	    lodash.prototype.next = wrapperNext;
	    lodash.prototype.plant = wrapperPlant;
	    lodash.prototype.reverse = wrapperReverse;
	    lodash.prototype.toJSON = lodash.prototype.valueOf = lodash.prototype.value = wrapperValue;

	    // Add lazy aliases.
	    lodash.prototype.first = lodash.prototype.head;

	    if (symIterator) {
	      lodash.prototype[symIterator] = wrapperToIterator;
	    }
	    return lodash;
	  });

	  /*--------------------------------------------------------------------------*/

	  // Export lodash.
	  var _ = runInContext();

	  // Some AMD build optimizers, like r.js, check for condition patterns like:
	  if (freeModule) {
	    // Export for Node.js.
	    (freeModule.exports = _)._ = _;
	    // Export for CommonJS support.
	    freeExports._ = _;
	  }
	  else {
	    // Export to the global object.
	    root._ = _;
	  }
	}.call(commonjsGlobal)); 
} (lodash, lodash.exports));

var lodashExports = lodash.exports;

const { requiredField: requiredField$6, isDate: isDate$2, nam, namExpirationDate, postalCode, } = useFormRules();
// eslint-disable-next-line @typescript-eslint/no-var-requires
let JsBarcode = require('jsbarcode');
// eslint-disable-next-line @typescript-eslint/no-var-requires
let toUpper = require('lodash/toUpper');
let TitleAndPatient$1 = class TitleAndPatient extends Vue {
    constructor() {
        super(...arguments);
        this.showNamBarCode = false;
    }
    get i18n() {
        return this.$i18n.t('as6242024.titleAndPatient');
    }
    get hasPatientSectionAccess() {
        return globalDataVuex().getFormAppData.hasPatientSectionAccess;
    }
    get vuetifyBreakpoint() {
        return globalDataVuex().getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    get getMotherInformation() {
        if (lodashExports.get(this.item, 'motherName')) {
            return this.item.motherName;
        }
        else if (lodashExports.get(this.item, 'unknownMother')) {
            return this.$i18n.t('as6242024.titleAndPatient.unknownMother');
        }
        return '';
    }
    get getFatherInformation() {
        if (lodashExports.get(this.item, 'fatherName')) {
            return this.item.fatherName;
        }
        else if (lodashExports.get(this.item, 'unknownFather')) {
            return this.$i18n.t('as6242024.titleAndPatient.unknownFather');
        }
        return '';
    }
    mounted() {
        if (this.item.nam) {
            this.setNamBarCode();
        }
        if (!this.item.age && this.item.birthday) {
            this.getAge();
        }
    }
    created() {
        this.item.motherName = this.getMotherInformation;
        this.item.fatherName = this.getFatherInformation;
    }
    getAge() {
        let patientBirthday = this.item.birthday;
        if (patientBirthday.length === 10) {
            patientBirthday = patientBirthday.replace(/-/g, '');
        }
        if (patientBirthday.length === 8) {
            let d = patientBirthday.match(/^(\d{4})(\d{2})(\d{2})$/);
            if (d) {
                patientBirthday = d[1] + '-' + d[2] + '-' + d[3];
                let today = new Date();
                let birthDate = new Date(patientBirthday);
                let age = today.getFullYear() - birthDate.getFullYear();
                let m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
                if (!isNaN(age) && age >= 0) {
                    this.item = {
                        ...this.item,
                        age: age,
                    };
                }
            }
        }
    }
    setNamBarCode() {
        const nam = this.item.nam;
        if (nam.length === 12) {
            JsBarcode('#code128', nam, {
                format: 'CODE128',
                width: 2,
                height: 35,
            });
            this.item.namCodeBar = this.namBarCodeToBase64();
            this.showNamBarCode = true;
        }
        else {
            this.item.namCodeBar = '';
            this.showNamBarCode = false;
        }
    }
    namBarCodeToBase64() {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, this.item.nam, { format: 'CODE128' });
        return canvas.toDataURL('image/png');
    }
    toUpper(postalCode) {
        this.item.postalCode = toUpper(postalCode);
    }
    validateRequiredField(v) {
        return requiredField$6(v) || this.$i18n.t('as6242024.base.errors.requiredField');
    }
    validateDate(v) {
        return isDate$2(v) || this.$i18n.t('as6242024.base.errors.invalidDate');
    }
    validateNam(v) {
        return nam(v) || this.$i18n.t('as6242024.base.errors.invalidNam');
    }
    validateNamExpirationDate(v) {
        return namExpirationDate(v) || this.$i18n.t('as6242024.base.errors.invalidDate');
    }
    validatePostalCode(v) {
        return postalCode(v) || this.$i18n.t('as6242024.base.errors.invalidPostalCode');
    }
};
__decorate([
    Prop()
], TitleAndPatient$1.prototype, "data", void 0);
__decorate([
    Prop()
], TitleAndPatient$1.prototype, "establishmentId", void 0);
__decorate([
    Prop()
], TitleAndPatient$1.prototype, "appointmentNumber", void 0);
__decorate([
    PropSync('data', { type: Object })
], TitleAndPatient$1.prototype, "item", void 0);
TitleAndPatient$1 = __decorate([
    Component({})
], TitleAndPatient$1);
var script$n = TitleAndPatient$1;

/* script */
            const __vue_script__$n = script$n;
            
/* template */
var __vue_render__$n = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        { attrs: { sm6: "", xs12: "" } },
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "", "fill-height": "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs6: "" } },
                [
                  _c("v-img", {
                    staticClass: "logoAS624",
                    attrs: {
                      src:
                        "https://static.trimoz.com/clicsante/images/forms/as624/logo1-AS-624.png",
                      alt: _vm.i18n.logo
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs6: "" } },
                [
                  _c("v-img", {
                    staticClass: "barcodeAS624",
                    attrs: { src: _vm.i18n.barcodeUrl, alt: _vm.i18n.barCode }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  directives: [
                    {
                      name: "show",
                      rawName: "v-show",
                      value: _vm.showNamBarCode,
                      expression: "showNamBarCode"
                    }
                  ],
                  attrs: { xs12: "" }
                },
                [_c("svg", { attrs: { id: "code128" } })]
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-flex",
        { attrs: { sm6: "", xs12: "" } },
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs7: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.fileNo,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "30"
                    },
                    model: {
                      value: _vm.item.fileNo,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "fileNo", $$v);
                      },
                      expression: "item.fileNo"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c("v-flex", {
                staticClass: "border-bottom",
                attrs: { xs1: "" }
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-left", attrs: { xs4: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.siPmiId,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "15"
                    },
                    model: {
                      value: _vm.item.siPmiId,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "siPmiId", $$v);
                      },
                      expression: "item.siPmiId"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.name,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50",
                      rules: [
                        !_vm.appliedRequiredField || _vm.validateRequiredField
                      ]
                    },
                    model: {
                      value: _vm.item.name,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "name", $$v);
                      },
                      expression: "item.name"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.motherName,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50",
                      rules: [
                        !_vm.appliedRequiredField || _vm.validateRequiredField
                      ],
                      "test-value": _vm.item.motherName
                    },
                    model: {
                      value: _vm.item.motherName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "motherName", $$v);
                      },
                      expression: "item.motherName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.fatherName,
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "50",
                      "test-value": _vm.item.fatherName
                    },
                    model: {
                      value: _vm.item.fatherName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "fatherName", $$v);
                      },
                      expression: "item.fatherName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs4: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.birthday,
                      placeholder: _vm.i18n.placeholder.birthday,
                      "hide-details": "",
                      mask: "####-##-##",
                      "return-masked-value": "",
                      box: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      maxlength: "10",
                      rules: [
                        !_vm.appliedRequiredField || _vm.validateRequiredField,
                        _vm.validateDate
                      ]
                    },
                    on: { input: _vm.getAge },
                    model: {
                      value: _vm.item.birthday,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "birthday", $$v);
                      },
                      expression: "item.birthday"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c("v-flex", {
                staticClass: "border-bottom",
                attrs: { xs4: "" }
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "text-xs-center border-left border-bottom",
                  attrs: { xs4: "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: "",
                        "aria-label": _vm.i18n.sex,
                        "hide-details": "",
                        disabled: !_vm.hasPatientSectionAccess
                      },
                      model: {
                        value: _vm.item.sex,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "sex", $$v);
                        },
                        expression: "item.sex"
                      }
                    },
                    [
                      _c("v-radio", {
                        key: "M",
                        attrs: { label: "M", value: "M" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        key: "F",
                        attrs: { label: "F", value: "F" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.nam,
                      mask: "AAAA #### ##N#",
                      "hide-details": "",
                      placeholder: "",
                      disabled: !_vm.hasPatientSectionAccess,
                      box: "",
                      rules: [_vm.validateNam]
                    },
                    on: { input: _vm.setNamBarCode },
                    model: {
                      value: _vm.item.nam,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "nam", $$v);
                      },
                      expression: "item.nam"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.expiryDate,
                      placeholder: _vm.i18n.placeholder.expiryDate,
                      disabled: !_vm.hasPatientSectionAccess,
                      "hide-details": "",
                      mask: "####-##",
                      box: "",
                      rules: [_vm.validateNamExpirationDate]
                    },
                    model: {
                      value: _vm.item.expiryDate,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "expiryDate", $$v);
                      },
                      expression: "item.expiryDate"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.address,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: _vm.i18n.placeholder.address,
                      "hide-details": "",
                      box: "",
                      maxlength: "50"
                    },
                    model: {
                      value: _vm.item.address,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "address", $$v);
                      },
                      expression: "item.address"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-right",
                  attrs: { xs7: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.city,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: "Québec",
                      "hide-details": "",
                      box: "",
                      maxlength: "35"
                    },
                    model: {
                      value: _vm.item.city,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "city", $$v);
                      },
                      expression: "item.city"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs5: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.postalCode,
                      disabled: !_vm.hasPatientSectionAccess,
                      placeholder: "A1A1A1",
                      "hide-details": "",
                      box: "",
                      maxlength: "6",
                      rules: [_vm.validatePostalCode]
                    },
                    on: {
                      input: function($event) {
                        return _vm.toUpper(_vm.item.postalCode)
                      }
                    },
                    model: {
                      value: _vm.item.postalCode,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "postalCode", $$v);
                      },
                      expression: "item.postalCode"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$n = [];
__vue_render__$n._withStripped = true;

  /* style */
  const __vue_inject_styles__$n = function (inject) {
    if (!inject) return
    inject("data-v-33017786_0", { source: "\n.logoAS624[data-v-33017786] {\n  width: 200px;\n}\n.barcodeAS624[data-v-33017786] {\n  margin-top: 25px;\n  width: 175px;\n}\n", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/TitleAndPatient/TitleAndPatient.vue"],"names":[],"mappings":";AA+WA;EACA,YAAA;AACA;AAEA;EACA,gBAAA;EACA,YAAA;AACA","file":"TitleAndPatient.vue","sourcesContent":["<template>\n  <v-layout row wrap>\n\n    <!-- LOGO & CODEBAR -->\n    <v-flex sm6 xs12 >\n      <v-layout row wrap fill-height>\n        <v-flex xs6>\n          <v-img\n            src=\"https://static.trimoz.com/clicsante/images/forms/as624/logo1-AS-624.png\"\n            :alt=\"i18n.logo\"\n            class=\"logoAS624\"/>\n        </v-flex>\n        <v-flex xs6>\n          <v-img\n              :src=\"i18n.barcodeUrl\"\n              :alt=\"i18n.barCode\"\n              class=\"barcodeAS624\"\n          />\n        </v-flex>\n        <v-flex xs12 v-show=\"showNamBarCode\">\n          <svg id=\"code128\"></svg>\n        </v-flex>\n      </v-layout>\n    </v-flex>\n\n    <v-flex sm6 xs12>\n\n      <!-- USER'S INFORMATION -->\n      <v-layout row wrap class=\"border-all\">\n        <v-flex xs7>\n          <v-text-field\n            v-model=\"item.fileNo\"\n            :label=\"i18n.fileNo\"\n            placeholder=\"\"\n            hide-details\n            box\n            :disabled=\"!hasPatientSectionAccess\"\n            maxlength=\"30\"\n          />\n        </v-flex>\n        <v-flex xs1 class=\"border-bottom\">\n        </v-flex>\n        <v-flex xs4 class=\"border-left\">\n          <v-text-field\n            v-model=\"item.siPmiId\"\n            :label=\"i18n.siPmiId\"\n            placeholder=\"\"\n            hide-details\n            box\n            maxlength=\"15\"\n          />\n        </v-flex>\n        <v-flex xs12>\n          <v-text-field\n              v-model=\"item.name\"\n              :label=\"'* ' + i18n.name\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n              :rules=\"[\n                !appliedRequiredField || validateRequiredField\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs12>\n          <v-text-field\n              :label=\"'* ' + i18n.motherName\"\n              v-model=\"item.motherName\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n              :rules=\"[\n                !appliedRequiredField || validateRequiredField\n              ]\"\n              :test-value=\"item.motherName\"\n          />\n        </v-flex>\n        <v-flex xs12>\n          <v-text-field\n              :label=\"i18n.fatherName\"\n              v-model=\"item.fatherName\"\n              placeholder=\"\"\n              hide-details\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"50\"\n              :test-value=\"item.fatherName\"\n          />\n        </v-flex>\n        <v-flex xs4 class=\"\">\n          <v-text-field\n              v-model=\"item.birthday\"\n              :label=\"'* ' + i18n.birthday\"\n              :placeholder=\"i18n.placeholder.birthday\"\n              hide-details\n              mask=\"####-##-##\"\n              return-masked-value\n              box\n              :disabled=\"!hasPatientSectionAccess\"\n              maxlength=\"10\"\n              @input=\"getAge\"\n              :rules=\"[\n                !appliedRequiredField || validateRequiredField,\n                validateDate\n              ]\"\n          />\n        </v-flex>\n        <v-flex xs4 class=\"border-bottom\">\n        </v-flex>\n        <v-flex xs4 class=\"text-xs-center border-left border-bottom\">\n          <v-radio-group\n              row\n              :aria-label=\"i18n.sex\"\n              v-model=\"item.sex\"\n              hide-details\n              :disabled=\"!hasPatientSectionAccess\"\n          >\n            <v-radio\n                key=\"M\"\n                label=\"M\"\n                value=\"M\"\n            />\n            <v-radio\n                key=\"F\"\n                label=\"F\"\n                value=\"F\"\n            />\n          </v-radio-group>\n        </v-flex>\n\n        <v-flex xs7 :class=\"vuetifyBreakpoint.smAndDown ? '' : 'border-right'\">\n          <v-text-field\n              :label=\"i18n.nam\"\n              mask=\"AAAA #### ##N#\"\n              v-model=\"item.nam\"\n              hide-details\n              placeholder=\"\"\n              :disabled=\"!hasPatientSectionAccess\"\n              @input=\"setNamBarCode\"\n              box\n              :rules=\"[validateNam]\"\n          />\n        </v-flex>\n        <v-flex xs5>\n          <v-text-field\n              :label=\"i18n.expiryDate\"\n              v-model=\"item.expiryDate\"\n              :placeholder=\"i18n.placeholder.expiryDate\"\n              :disabled=\"!hasPatientSectionAccess\"\n              hide-details\n              mask=\"####-##\"\n              box\n              :rules=\"[validateNamExpirationDate]\"\n          />\n        </v-flex>\n\n        <v-flex xs12>\n          <v-text-field\n              :label=\"i18n.address\"\n              v-model=\"item.address\"\n              :disabled=\"!hasPatientSectionAccess\"\n              :placeholder=\"i18n.placeholder.address\"\n              hide-details\n              box\n              maxlength=\"50\"\n          />\n        </v-flex>\n\n        <v-flex xs7 :class=\"vuetifyBreakpoint.smAndDown ? '' : 'border-right'\">\n          <v-text-field\n              :label=\"i18n.city\"\n              v-model=\"item.city\"\n              :disabled=\"!hasPatientSectionAccess\"\n              placeholder=\"Québec\"\n              hide-details\n              box\n              maxlength=\"35\"\n          />\n        </v-flex>\n        <v-flex xs5>\n          <v-text-field\n              :label=\"i18n.postalCode\"\n              v-model=\"item.postalCode\"\n              :disabled=\"!hasPatientSectionAccess\"\n              placeholder=\"A1A1A1\"\n              hide-details\n              box\n              maxlength=\"6\"\n              @input=\"toUpper(item.postalCode)\"\n              :rules=\"[validatePostalCode]\"\n          />\n        </v-flex>\n      </v-layout>\n    </v-flex>\n  </v-layout>\n</template>\n\n<script lang=\"ts\">\nimport { Component, Prop, PropSync, Vue } from 'vue-property-decorator';\nimport { globalDataVuex } from '@/store';\nimport { LocaleMessages, TranslateResult } from 'vue-i18n';\nimport { As6242024FormData } from '@/form/components/as624-2024/interfaces/as6242024-form.data';\nimport { PropType } from 'vue';\nimport { useFormRules } from '@/utils/formRules';\nimport { get } from 'lodash';\n\nconst {\n  requiredField,\n  isDate,\n  nam,\n  namExpirationDate,\n  postalCode,\n} = useFormRules();\n\n// eslint-disable-next-line @typescript-eslint/no-var-requires\nlet JsBarcode = require('jsbarcode');\n// eslint-disable-next-line @typescript-eslint/no-var-requires\nlet toUpper = require('lodash/toUpper');\n\n@Component({})\nexport default class TitleAndPatient extends Vue {\n  @Prop() readonly data!: As6242024FormData;\n  @Prop() readonly establishmentId!: number;\n  @Prop() readonly appointmentNumber!: string;\n  @PropSync('data', { type: Object as PropType<As6242024FormData> }) item!: As6242024FormData;\n\n  showNamBarCode = false;\n\n  get i18n(): LocaleMessages {\n    return this.$i18n.t('as6242024.titleAndPatient') as LocaleMessages;\n  }\n\n  get hasPatientSectionAccess() {\n    return globalDataVuex().getFormAppData.hasPatientSectionAccess;\n  }\n\n  get vuetifyBreakpoint() {\n    return globalDataVuex().getFormAppData.vuetifyBreakpoint;\n  }\n\n  get appliedRequiredField() {\n    return globalDataVuex().getFormAppData.appliedRequiredField;\n  }\n\n  get getMotherInformation(): string {\n    if (get(this.item, 'motherName')) {\n      return this.item.motherName;\n    }\n    else if (get(this.item, 'unknownMother')) {\n      return this.$i18n.t('as6242024.titleAndPatient.unknownMother') as string;\n    }\n    return '';\n  }\n\n  get getFatherInformation(): string {\n    if (get(this.item, 'fatherName')) {\n      return this.item.fatherName;\n    }\n    else if (get(this.item, 'unknownFather')) {\n      return this.$i18n.t('as6242024.titleAndPatient.unknownFather') as string;\n    }\n    return '';\n  }\n\n  mounted() {\n    if (this.item.nam) {\n      this.setNamBarCode();\n    }\n\n    if (!this.item.age && this.item.birthday) {\n      this.getAge();\n    }\n  }\n  \n  created() {\n    this.item.motherName = this.getMotherInformation;\n    this.item.fatherName = this.getFatherInformation;\n  }\n\n  getAge() {\n    let patientBirthday: string = this.item.birthday as string;\n\n    if (patientBirthday.length === 10) {\n      patientBirthday = patientBirthday.replace(/-/g, '');\n    }\n\n    if (patientBirthday.length === 8) {\n      let d = patientBirthday.match(/^(\\d{4})(\\d{2})(\\d{2})$/);\n      if (d) {\n        patientBirthday = d[1] + '-' + d[2] + '-' + d[3];\n\n        let today = new Date();\n        let birthDate = new Date(patientBirthday);\n        let age = today.getFullYear() - birthDate.getFullYear();\n        let m = today.getMonth() - birthDate.getMonth();\n\n        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {\n          age--;\n        }\n\n        if (!isNaN(age) && age >= 0) {\n          this.item = {\n            ...this.item,\n            age: age,\n          };\n        }\n      }\n    }\n  }\n\n  setNamBarCode() {\n    const nam: string = this.item.nam as string;\n\n    if (nam.length === 12) {\n      JsBarcode('#code128', nam, {\n        format: 'CODE128',\n        width: 2,\n        height: 35,\n      });\n\n      this.item.namCodeBar = this.namBarCodeToBase64();\n      this.showNamBarCode = true;\n    }\n    else {\n      this.item.namCodeBar = '';\n      this.showNamBarCode = false;\n    }\n  }\n\n  namBarCodeToBase64(){\n    const canvas = document.createElement('canvas');\n    JsBarcode(canvas, this.item.nam, { format: 'CODE128' });\n    return canvas.toDataURL('image/png');\n  }\n\n  toUpper(postalCode: string) {\n    this.item.postalCode = toUpper(postalCode);\n  }\n\n  public validateRequiredField(v: string): boolean | TranslateResult {\n    return requiredField(v) || this.$i18n.t('as6242024.base.errors.requiredField');\n  }\n\n  public validateDate(v: string): boolean | TranslateResult {\n    return isDate(v) || this.$i18n.t('as6242024.base.errors.invalidDate');\n  }\n\n  public validateNam(v: string): boolean | TranslateResult {\n    return nam(v) || this.$i18n.t('as6242024.base.errors.invalidNam');\n  }\n\n  public validateNamExpirationDate(v: string): boolean | TranslateResult {\n    return namExpirationDate(v) || this.$i18n.t('as6242024.base.errors.invalidDate');\n  }\n\n  public validatePostalCode(v: string): boolean | TranslateResult {\n    return postalCode(v) || this.$i18n.t('as6242024.base.errors.invalidPostalCode');\n  }\n\n}\n</script>\n\n<style scoped>\n  .logoAS624 {\n    width: 200px;\n  }\n\n  .barcodeAS624 {\n    margin-top: 25px;\n    width: 175px;\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$n = "data-v-33017786";
  /* module identifier */
  const __vue_module_identifier__$8 = undefined;
  /* functional template */
  const __vue_is_functional_template__$n = false;
  /* component normalizer */
  function __vue_normalize__$n(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/TitleAndPatient/TitleAndPatient.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$8() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$8.styles || (__vue_create_injector__$8.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var TitleAndPatient = __vue_normalize__$n(
    { render: __vue_render__$n, staticRenderFns: __vue_staticRenderFns__$n },
    __vue_inject_styles__$n,
    __vue_script__$n,
    __vue_scope_id__$n,
    __vue_is_functional_template__$n,
    __vue_module_identifier__$8,
    __vue_create_injector__$8);

var PhoneTypeEnum;
(function (PhoneTypeEnum) {
    PhoneTypeEnum["CELL"] = "cell";
    PhoneTypeEnum["HOME"] = "home";
})(PhoneTypeEnum || (PhoneTypeEnum = {}));
var MonitoringTimeEnum;
(function (MonitoringTimeEnum) {
    MonitoringTimeEnum["FIFTEEN_MIN"] = "monitoringTime1";
    MonitoringTimeEnum["THIRTY_MIN"] = "monitoringTime2";
})(MonitoringTimeEnum || (MonitoringTimeEnum = {}));
var PersonLegalRepresentativeEnum;
(function (PersonLegalRepresentativeEnum) {
    PersonLegalRepresentativeEnum["PARENTAL_AUTHORITY"] = "parentalAuthority";
    PersonLegalRepresentativeEnum["PATIENT"] = "patient";
    PersonLegalRepresentativeEnum["MANDATARY"] = "mandatary";
    PersonLegalRepresentativeEnum["GUARDIAN"] = "guardian";
    PersonLegalRepresentativeEnum["CURATOR"] = "curator";
    PersonLegalRepresentativeEnum["PUBLIC_CURATOR"] = "publicCurator";
    PersonLegalRepresentativeEnum["SPOUSE"] = "spouse";
    PersonLegalRepresentativeEnum["CLOSE_RELATIVE"] = "closeRelative";
    PersonLegalRepresentativeEnum["PERSON_INTEREST"] = "personInterest";
    PersonLegalRepresentativeEnum["SPECIAL_INTEREST"] = "specialInterest";
})(PersonLegalRepresentativeEnum || (PersonLegalRepresentativeEnum = {}));
var AdministrationReasonInfluenzaEnum;
(function (AdministrationReasonInfluenzaEnum) {
    AdministrationReasonInfluenzaEnum["RESIDENT_CHSLD"] = "Resident in a CHSLD";
    AdministrationReasonInfluenzaEnum["RESIDENT_RPA"] = "Resident in a RPA";
    AdministrationReasonInfluenzaEnum["PREGNANT_WOMAN"] = "Pregnant woman";
    AdministrationReasonInfluenzaEnum["HEALTH_WORKER"] = "Health worker";
    AdministrationReasonInfluenzaEnum["CHRONIC_ILLNESS"] = "Chronic illness";
    AdministrationReasonInfluenzaEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonInfluenzaEnum || (AdministrationReasonInfluenzaEnum = {}));
var AdministrationReasonShinglesEnum;
(function (AdministrationReasonShinglesEnum) {
    AdministrationReasonShinglesEnum["IMMUNOCOMPROMISED"] = "Immunocompromised";
    AdministrationReasonShinglesEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonShinglesEnum || (AdministrationReasonShinglesEnum = {}));
var AdministrationReasonDcatEnum;
(function (AdministrationReasonDcatEnum) {
    AdministrationReasonDcatEnum["PREGNANT_WOMAN"] = "Pregnant woman";
    AdministrationReasonDcatEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonDcatEnum || (AdministrationReasonDcatEnum = {}));
var AdministrationReasonCovidEnum;
(function (AdministrationReasonCovidEnum) {
    AdministrationReasonCovidEnum["RESIDENT_CHSLD"] = "Resident in a CHSLD";
    AdministrationReasonCovidEnum["RESIDENT_RPA"] = "Resident in a RPA";
    AdministrationReasonCovidEnum["PREGNANT_WOMAN"] = "Pregnant woman";
    AdministrationReasonCovidEnum["HEALTH_WORKER"] = "Health worker";
    AdministrationReasonCovidEnum["CHRONIC_ILLNESS"] = "Chronic illness";
    AdministrationReasonCovidEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonCovidEnum || (AdministrationReasonCovidEnum = {}));
var AdministrationReasonVrsEnum;
(function (AdministrationReasonVrsEnum) {
    AdministrationReasonVrsEnum["RESIDENT_CHSLD"] = "Resident in a CHSLD";
    AdministrationReasonVrsEnum["RESIDENT_RPA"] = "Resident in a RPA";
    AdministrationReasonVrsEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonVrsEnum || (AdministrationReasonVrsEnum = {}));
var AdministrationReasonMpoxEnum;
(function (AdministrationReasonMpoxEnum) {
    AdministrationReasonMpoxEnum["PRE_EXPOSURE_IMMUNOCOMPROMISED"] = "Pre-exposure - immunocompromised";
    AdministrationReasonMpoxEnum["PRE_EXPOSURE_NON_IMMUNOCOMPROMISED"] = "Pre-exposure - non-immunocompromised";
    AdministrationReasonMpoxEnum["POST_EXPOSURE_IMMUNOCOMPROMISED"] = "Post-exposure - immunocompromised";
    AdministrationReasonMpoxEnum["POST_EXPOSURE_NON_IMMUNOCOMPROMISED"] = "Post-exposure - non-immunocompromised";
    AdministrationReasonMpoxEnum["OTHERS_REASONS"] = "Other reasons";
})(AdministrationReasonMpoxEnum || (AdministrationReasonMpoxEnum = {}));
var ProfessionEnum;
(function (ProfessionEnum) {
    ProfessionEnum["NURSE"] = "nurse";
    ProfessionEnum["PHYSICIAN"] = "physician";
    ProfessionEnum["INHALOTHERAPIST"] = "inhalotherapist";
    ProfessionEnum["MIDWIFE"] = "midwife";
    ProfessionEnum["PHARMACIST"] = "pharmacist";
    ProfessionEnum["OTHER"] = "other";
})(ProfessionEnum || (ProfessionEnum = {}));
var InjectionSiteEnum;
(function (InjectionSiteEnum) {
    InjectionSiteEnum["RIGHT_ARM"] = "rightArm";
    InjectionSiteEnum["LEFT_ARM"] = "leftArm";
    InjectionSiteEnum["RIGHT_THIGH"] = "rightThigh";
    InjectionSiteEnum["LEFT_THIGH"] = "leftThigh";
    InjectionSiteEnum["RIGHT_NOSTRIL"] = "rightNostril";
    InjectionSiteEnum["LEFT_NOSTRIL"] = "leftNostril";
})(InjectionSiteEnum || (InjectionSiteEnum = {}));
var ClosedQuestionsEnum;
(function (ClosedQuestionsEnum) {
    ClosedQuestionsEnum[ClosedQuestionsEnum["NO"] = 0] = "NO";
    ClosedQuestionsEnum[ClosedQuestionsEnum["YES"] = 1] = "YES";
    ClosedQuestionsEnum[ClosedQuestionsEnum["NA"] = -1] = "NA";
})(ClosedQuestionsEnum || (ClosedQuestionsEnum = {}));
var SexEnum;
(function (SexEnum) {
    SexEnum["M"] = "M";
    SexEnum["F"] = "F";
})(SexEnum || (SexEnum = {}));
var VaccineCategoryIdsEnum;
(function (VaccineCategoryIdsEnum) {
    VaccineCategoryIdsEnum["ZONA"] = "zona";
    VaccineCategoryIdsEnum["FLU"] = "flu";
    VaccineCategoryIdsEnum["COVID"] = "covid";
    VaccineCategoryIdsEnum["MPOX"] = "mpox";
    VaccineCategoryIdsEnum["DCAT"] = "dcat";
    VaccineCategoryIdsEnum["HEPATITIS"] = "hepatitis";
    VaccineCategoryIdsEnum["MENINGITIS"] = "meningitis";
    VaccineCategoryIdsEnum["PNEUMOCOCCAL"] = "pneumococcal";
    VaccineCategoryIdsEnum["RABIES"] = "rabies";
    VaccineCategoryIdsEnum["ROTAVIRUS"] = "rotavirus";
    VaccineCategoryIdsEnum["RRO"] = "rro";
    VaccineCategoryIdsEnum["TUBERCULOSIS"] = "tuberculosis";
    VaccineCategoryIdsEnum["VPH"] = "vph";
    VaccineCategoryIdsEnum["TRAVEL"] = "travel";
    VaccineCategoryIdsEnum["VRS"] = "vrs";
    VaccineCategoryIdsEnum["VRS_BABY"] = "vrs_baby";
    VaccineCategoryIdsEnum["TYPHOID"] = "typhoid";
    VaccineCategoryIdsEnum["AVIAN_INFLUENZA"] = "avian_influenza";
})(VaccineCategoryIdsEnum || (VaccineCategoryIdsEnum = {}));
var AdministrationEnum;
(function (AdministrationEnum) {
    AdministrationEnum["INTRAMUSCULAR"] = "intramuscular";
    AdministrationEnum["SUBCUTANEOUS"] = "subcutaneous";
    AdministrationEnum["ORAL"] = "oral";
    AdministrationEnum["INTRANASAL"] = "intranasal";
    AdministrationEnum["INTRADERMIC"] = "intradermic";
})(AdministrationEnum || (AdministrationEnum = {}));
var VaccineIdDcatEnum;
(function (VaccineIdDcatEnum) {
    VaccineIdDcatEnum["ACT_HIB"] = "actHib";
    VaccineIdDcatEnum["ADACEL"] = "adacel";
    VaccineIdDcatEnum["ADACEL_POLIO"] = "adacelPolio";
    VaccineIdDcatEnum["BOOSTRIX"] = "boostrix";
    VaccineIdDcatEnum["BOOSTRIX_POLIO"] = "boostrixPolio";
    VaccineIdDcatEnum["HIBERIX"] = "hiberix";
    VaccineIdDcatEnum["IMOVAX_POLIO"] = "imovaxPolio";
    VaccineIdDcatEnum["INFANRIX_HEXA"] = "infanrixHexa";
    VaccineIdDcatEnum["INFANRIX_IPV_HIB"] = "infanrixIPVHib";
    VaccineIdDcatEnum["PEDIACEL"] = "pediacel";
    VaccineIdDcatEnum["TD"] = "td";
    VaccineIdDcatEnum["PENTACEL"] = "pentacel";
})(VaccineIdDcatEnum || (VaccineIdDcatEnum = {}));
var ProfessionalRoleEnum;
(function (ProfessionalRoleEnum) {
    ProfessionalRoleEnum[ProfessionalRoleEnum["FORM_FILLED"] = 1] = "FORM_FILLED";
    ProfessionalRoleEnum[ProfessionalRoleEnum["OBTAINED_CONSENT"] = 2] = "OBTAINED_CONSENT";
})(ProfessionalRoleEnum || (ProfessionalRoleEnum = {}));
const DCatVaccinesRequiredForAdministrativeReason = [
    'adacel',
    'adacelPolio',
    'boostrix',
    'boostrixPolio',
    'infanrixHexa',
    'infanrixIPVHib',
    'pediacel',
    'pentacel',
];

const { regInd, requiredField: requiredField$5, shortTelephone, email, } = useFormRules();
let GeneralInformation$1 = class GeneralInformation extends Vue {
    get i18n() {
        return this.$i18n.t('as6242024.generalInformation');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasGeneralSectionAccess() {
        return this.globalData.getFormAppData.hasGeneralSectionAccess;
    }
    validateRequiredField(v) {
        return requiredField$5(v) || this.$i18n.t('as6242024.base.errors.requiredField');
    }
    validateRegInd(v) {
        return regInd(v) || this.$i18n.t('as6242024.base.errors.invalidRegInd');
    }
    validateShortTelephone(v) {
        return shortTelephone(v) || this.$i18n.t('as6242024.base.errors.phone2short');
    }
    validateEmail(v) {
        return email(v) || this.$i18n.t('as6242024.base.errors.invalidEmail');
    }
};
__decorate([
    Prop()
], GeneralInformation$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], GeneralInformation$1.prototype, "item", void 0);
GeneralInformation$1 = __decorate([
    Component({
        computed: {
            PersonLegalRepresentativeEnum() {
                return PersonLegalRepresentativeEnum;
            },
        },
    })
], GeneralInformation$1);
var script$m = GeneralInformation$1;

/* script */
            const __vue_script__$m = script$m;
            
/* template */
var __vue_render__$m = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { staticClass: "section-title", attrs: { xs12: "" } },
                [_c("span", [_vm._v(_vm._s(_vm.i18n.title))])]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "section-subtitle", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n          " +
                      _vm._s(_vm.i18n.user14YearsOrOlder) +
                      "\n        "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { md1: "", sm2: "", xs2: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.regInd,
                      "aria-label": _vm.i18n.regIndAriaLabel,
                      disabled: !_vm.hasGeneralSectionAccess,
                      mask: "( ### )",
                      "hide-details": "",
                      box: "",
                      rules: [
                        !_vm.appliedRequiredField || _vm.validateRequiredField,
                        _vm.validateRegInd
                      ],
                      maxlength: "3"
                    },
                    model: {
                      value: _vm.item.regInd,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "regInd", $$v);
                      },
                      expression: "item.regInd"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { md3: "", sm6: "", xs6: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: "* " + _vm.i18n.phone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      mask: "### - ####",
                      "hide-details": "",
                      box: "",
                      rules: [
                        !_vm.appliedRequiredField || _vm.validateRequiredField,
                        _vm.validateShortTelephone
                      ]
                    },
                    model: {
                      value: _vm.item.shortPhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "shortPhone", $$v);
                      },
                      expression: "item.shortPhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "border-bottom user-phone",
                  attrs: { md2: "", xs4: "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        "hide-details": "",
                        disabled: !_vm.hasGeneralSectionAccess
                      },
                      model: {
                        value: _vm.item.phoneType,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "phoneType", $$v);
                        },
                        expression: "item.phoneType"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.cell, value: "cell" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.home, value: "home" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-left",
                  attrs: { md6: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.email,
                      disabled: !_vm.hasGeneralSectionAccess,
                      type: "email",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "48",
                      rules: [_vm.validateEmail]
                    },
                    model: {
                      value: _vm.item.email,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "email", $$v);
                      },
                      expression: "item.email"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "section-subtitle", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n          " + _vm._s(_vm.i18n.userUnder14) + "\n        "
                  )
                ]
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "" : "border-right",
                  attrs: { xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.authorizedPerson,
                      disabled: !_vm.hasGeneralSectionAccess,
                      "hide-details": "",
                      box: "",
                      maxlength: "70"
                    },
                    model: {
                      value: _vm.item.authorizedPersonName,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonName", $$v);
                      },
                      expression: "item.authorizedPersonName"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "border-bottom pl-1 pb-2", attrs: { xs12: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        row: !_vm.vuetifyBreakpoint.xsOnly,
                        disabled: !_vm.hasGeneralSectionAccess,
                        "hide-details": ""
                      },
                      model: {
                        value: _vm.item.authorizedPersonLegalRepresentative,
                        callback: function($$v) {
                          _vm.$set(
                            _vm.item,
                            "authorizedPersonLegalRepresentative",
                            $$v
                          );
                        },
                        expression: "item.authorizedPersonLegalRepresentative"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.parentalAuthority,
                          value:
                            _vm.PersonLegalRepresentativeEnum.PARENTAL_AUTHORITY
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.spouse,
                          value: _vm.PersonLegalRepresentativeEnum.SPOUSE
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.mandatary,
                          value: _vm.PersonLegalRepresentativeEnum.MANDATARY
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.personInterest,
                          value:
                            _vm.PersonLegalRepresentativeEnum.PERSON_INTEREST
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.closeRelative,
                          value:
                            _vm.PersonLegalRepresentativeEnum.CLOSE_RELATIVE
                        }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: {
                          label: _vm.i18n.legalRepresentative.guardian,
                          value: _vm.PersonLegalRepresentativeEnum.GUARDIAN
                        }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { md1: "", sm2: "", xs2: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.regInd,
                      disabled: !_vm.hasGeneralSectionAccess,
                      "aria-label": _vm.i18n.authorizedPersonRegInd,
                      mask: "( ### )",
                      "hide-details": "",
                      box: "",
                      maxlength: "3",
                      rules: [_vm.validateRegInd]
                    },
                    model: {
                      value: _vm.item.authorizedPersonRegInd,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonRegInd", $$v);
                      },
                      expression: "item.authorizedPersonRegInd"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { md3: "", sm6: "", xs6: "" } },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.phone,
                      disabled: !_vm.hasGeneralSectionAccess,
                      mask: "### - ####",
                      "hide-details": "",
                      box: "",
                      rules: [_vm.validateShortTelephone]
                    },
                    model: {
                      value: _vm.item.authorizedPersonShortPhone,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonShortPhone", $$v);
                      },
                      expression: "item.authorizedPersonShortPhone"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  staticClass: "border-bottom user-phone",
                  attrs: { md2: "", xs4: "" }
                },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        disabled: !_vm.hasGeneralSectionAccess,
                        "hide-details": ""
                      },
                      model: {
                        value: _vm.item.authorizedPersonPhoneType,
                        callback: function($$v) {
                          _vm.$set(_vm.item, "authorizedPersonPhoneType", $$v);
                        },
                        expression: "item.authorizedPersonPhoneType"
                      }
                    },
                    [
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.cell, value: "cell" }
                      }),
                      _vm._v(" "),
                      _c("v-radio", {
                        attrs: { label: _vm.i18n.home, value: "work" }
                      })
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                {
                  class: _vm.vuetifyBreakpoint.smAndDown ? "" : "border-left",
                  attrs: { md6: "", xs12: "" }
                },
                [
                  _c("v-text-field", {
                    attrs: {
                      label: _vm.i18n.email,
                      disabled: !_vm.hasGeneralSectionAccess,
                      type: "email",
                      "hide-details": "",
                      box: "",
                      maxlength: "48",
                      rules: [_vm.validateEmail]
                    },
                    model: {
                      value: _vm.item.authorizedPersonEmail,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "authorizedPersonEmail", $$v);
                      },
                      expression: "item.authorizedPersonEmail"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$m = [];
__vue_render__$m._withStripped = true;

  /* style */
  const __vue_inject_styles__$m = function (inject) {
    if (!inject) return
    inject("data-v-72132af9_0", { source: "\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n", map: {"version":3,"sources":[],"names":[],"mappings":"","file":"GeneralInformation.vue"}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$m = "data-v-72132af9";
  /* module identifier */
  const __vue_module_identifier__$7 = undefined;
  /* functional template */
  const __vue_is_functional_template__$m = false;
  /* component normalizer */
  function __vue_normalize__$m(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/GeneralInformation/GeneralInformation.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$7() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$7.styles || (__vue_create_injector__$7.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var GeneralInformation = __vue_normalize__$m(
    { render: __vue_render__$m, staticRenderFns: __vue_staticRenderFns__$m },
    __vue_inject_styles__$m,
    __vue_script__$m,
    __vue_scope_id__$m,
    __vue_is_functional_template__$m,
    __vue_module_identifier__$7,
    __vue_create_injector__$7);

const { requiredField: requiredField$4 } = useFormRules();
let PreImmunization$1 = class PreImmunization extends Vue {
    constructor() {
        super(...arguments);
        // Number of questions
        this.questions = 8;
    }
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('as6242024.preImmunization');
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasPreImmunisationSectionAccess() {
        return this.globalData.getFormAppData.hasPreImmunisationSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    validateRequiredField(v) {
        return requiredField$4(v) || this.$i18n.t('as6242024.base.errors.requiredField');
    }
};
__decorate([
    Prop()
], PreImmunization$1.prototype, "data", void 0);
__decorate([
    Prop()
], PreImmunization$1.prototype, "modePharma", void 0);
__decorate([
    Prop()
], PreImmunization$1.prototype, "modeMsss", void 0);
__decorate([
    PropSync('data', { type: Object })
], PreImmunization$1.prototype, "item", void 0);
PreImmunization$1 = __decorate([
    Component({
        mixins: [FormRules],
    })
], PreImmunization$1);
var script$l = PreImmunization$1;

/* script */
            const __vue_script__$l = script$l;
            
/* template */
var __vue_render__$l = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    { staticClass: "px-2 py-0 mt-2 border-all", attrs: { fluid: "" } },
    [
      _c(
        "v-layout",
        [
          _c(
            "v-flex",
            { staticClass: "black px-1 py-0", attrs: { xs12: "" } },
            [
              _c("span", { staticClass: "section-title white--text" }, [
                _vm._v(_vm._s("* " + _vm.i18n.title))
              ])
            ]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass:
                " text-xs-center section-subtitle border-right px-2 grey--text text--lighten-2",
              attrs: { shrink: "" }
            },
            [_vm._v("0.")]
          ),
          _vm._v(" "),
          _c("v-flex", {
            staticClass: "hidden-xs-only section-subtitle border-right",
            attrs: { md6: "", xs5: "" },
            domProps: { innerHTML: _vm._s(_vm.i18n.header.elements) }
          }),
          _vm._v(" "),
          _c(
            "v-flex",
            {
              staticClass: "hidden-xs-only border-right section-subtitle",
              attrs: { shrink: "" }
            },
            [
              _c("span", { staticClass: "header-question" }, [
                _vm._v(_vm._s(_vm.i18n.header.yes))
              ]),
              _vm._v(" "),
              _c("span", { staticClass: "header-question" }, [
                _vm._v(_vm._s(_vm.i18n.header.no))
              ]),
              _vm._v(" "),
              _c("span", {
                staticClass: "header-question header-NA",
                domProps: { innerHTML: _vm._s(_vm.i18n.header.NA) }
              })
            ]
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            {
              staticClass: "hidden-xs-only section-subtitle",
              attrs: { grow: "" }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.header.details) + "\n    ")]
          )
        ],
        1
      ),
      _vm._v(" "),
      _vm._l(_vm.questions, function(i, index) {
        return _c(
          "v-layout",
          { key: index, attrs: { row: "" } },
          [
            _c(
              "v-flex",
              {
                key: "col1-" + index,
                staticClass:
                  "text-xs-center font-weight-bold border-bottom border-right px-2",
                attrs: { shrink: "" }
              },
              [_vm._v("\n      " + _vm._s(i) + ".\n    ")]
            ),
            _vm._v(" "),
            _c(
              "v-flex",
              {
                key: "col2-" + index,
                class: _vm.vuetifyBreakpoint.xsOnly
                  ? "ml-4 mb-1"
                  : "border-bottom border-right px-1",
                attrs: { md6: "", sm5: "", xs12: "", "pr-3": "" }
              },
              [
                _c("strong", [_vm._v(_vm._s(_vm.i18n["title" + i]))]),
                _vm._v(" "),
                _vm.i18n["title" + i] ? _c("br") : _vm._e(),
                _vm._v(" "),
                _c("span", {
                  domProps: { innerHTML: _vm._s(_vm.i18n["question" + i]) }
                })
              ]
            ),
            _vm._v(" "),
            _c(
              "v-flex",
              {
                key: "col3-" + index,
                staticClass: "font-weight-bold",
                class: _vm.vuetifyBreakpoint.xsOnly
                  ? "ml-4 mb-1"
                  : "border-bottom border-right px-1",
                attrs: { shrink: "" }
              },
              [
                _c(
                  "v-radio-group",
                  {
                    attrs: {
                      row: "",
                      disabled: !_vm.hasPreImmunisationSectionAccess,
                      rules: [
                        function(v) {
                          return (
                            !_vm.appliedRequiredField ||
                            v === 0 ||
                            v === 1 ||
                            v === -1 ||
                            _vm.validateRequiredField(v)
                          )
                        }
                      ]
                    },
                    model: {
                      value: _vm.item["preImmunization" + i],
                      callback: function($$v) {
                        _vm.$set(_vm.item, "preImmunization" + i, $$v);
                      },
                      expression: "item['preImmunization' + i]"
                    }
                  },
                  [
                    _c("v-radio", {
                      attrs: {
                        "aria-label": _vm.i18n.header.yes + " question " + i,
                        label: _vm.vuetifyBreakpoint.xsOnly
                          ? _vm.i18n.header.yes
                          : "",
                        value: 1
                      }
                    }),
                    _vm._v(" "),
                    _c("v-radio", {
                      attrs: {
                        "aria-label": _vm.i18n.header.no + " question " + i,
                        label: _vm.vuetifyBreakpoint.xsOnly
                          ? _vm.i18n.header.no
                          : "",
                        value: 0
                      }
                    }),
                    _vm._v(" "),
                    _c("v-radio", {
                      attrs: {
                        "aria-label": _vm.i18n.header.NA + " question " + i,
                        label: _vm.vuetifyBreakpoint.xsOnly
                          ? _vm.i18n.header.NA
                          : "",
                        value: -1
                      }
                    })
                  ],
                  1
                )
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "v-flex",
              {
                key: "col4-" + index,
                staticClass: "border-bottom",
                attrs: { grow: "" }
              },
              [
                _c("v-text-field", {
                  class: _vm.vuetifyBreakpoint.xsOnly ? "ml-4" : "",
                  attrs: {
                    label: _vm.i18n.precision,
                    "aria-label": _vm.i18n.precision + " question " + i,
                    disabled: !_vm.hasPreImmunisationSectionAccess,
                    "hide-details": "",
                    box: "",
                    maxlength: "54"
                  },
                  model: {
                    value: _vm.item["preImmunizationPrecision" + i],
                    callback: function($$v) {
                      _vm.$set(_vm.item, "preImmunizationPrecision" + i, $$v);
                    },
                    expression: "item['preImmunizationPrecision' + i]"
                  }
                })
              ],
              1
            )
          ],
          1
        )
      }),
      _vm._v(" "),
      _c(
        "v-layout",
        [
          _c(
            "v-flex",
            { staticClass: "px-1 align-content-center", attrs: { xs6: "" } },
            [
              _vm._v(
                "\n      " + _vm._s(_vm.i18n.monitoringTime.label) + "\n    "
              )
            ]
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "ml-4", attrs: { xs6: "" } },
            [
              _c(
                "v-radio-group",
                {
                  attrs: { "hide-details": "", row: "" },
                  model: {
                    value: _vm.item["monitoringTime"],
                    callback: function($$v) {
                      _vm.$set(_vm.item, "monitoringTime", $$v);
                    },
                    expression: "item['monitoringTime']"
                  }
                },
                [
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.monitoringTime.time1,
                      value: "monitoringTime1"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.monitoringTime.time2,
                      value: "monitoringTime2"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        [
          _c(
            "v-flex",
            { staticClass: "mt-0", attrs: { xs12: "" } },
            [
              _c("v-text-field", {
                attrs: {
                  label: _vm.i18n.monitoringTime.precision,
                  "hide-details": "",
                  box: "",
                  maxlength: "102"
                },
                model: {
                  value: _vm.item["monitoringTimePrecision"],
                  callback: function($$v) {
                    _vm.$set(_vm.item, "monitoringTimePrecision", $$v);
                  },
                  expression: "item['monitoringTimePrecision']"
                }
              })
            ],
            1
          )
        ],
        1
      )
    ],
    2
  )
};
var __vue_staticRenderFns__$l = [];
__vue_render__$l._withStripped = true;

  /* style */
  const __vue_inject_styles__$l = undefined;
  /* scoped */
  const __vue_scope_id__$l = undefined;
  /* functional template */
  const __vue_is_functional_template__$l = false;
  /* component normalizer */
  function __vue_normalize__$l(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/PreImmunization/PreImmunization.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var PreImmunization = __vue_normalize__$l(
    { render: __vue_render__$l, staticRenderFns: __vue_staticRenderFns__$l },
    __vue_inject_styles__$l,
    __vue_script__$l,
    __vue_scope_id__$l,
    __vue_is_functional_template__$l);

const { requiredField: requiredField$3 } = useFormRules();
let AdministrativeReason$1 = class AdministrativeReason extends Vue {
    get i18n() {
        return this.$i18n.t('as6242024.administration');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasReasonSectionAccess() {
        return this.globalData.getFormAppData.hasReasonSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get isReasonsRequired() {
        let result = {
            'zona': false,
            'flu': false,
            'covid': false,
            'mpox': false,
            'dcat': false,
            'hepatitis': false,
            'meningitis': false,
            'pneumococcal': false,
            'rabies': false,
            'rotavirus': false,
            'rro': false,
            'tuberculosis': false,
            'vph': false,
            'travel': false,
            'vrs': false,
            'vrs_baby': false,
            'typhoid': false,
            'avian_influenza': false,
        };
        const vaccineCategoryIds = Object.values(VaccineCategoryIdsEnum);
        for (let i = 1; i <= this.numberOfLineToDisplay; i++) {
            const prop = this.item[`vaccineDetailsCategory${i}`];
            const vaccineName = this.item[`vaccineDetailsName${i}`];
            if (vaccineCategoryIds.includes(prop)) {
                result[prop] = !((prop === VaccineCategoryIdsEnum.DCAT && !DCatVaccinesRequiredForAdministrativeReason.includes(vaccineName)) || prop === VaccineCategoryIdsEnum.AVIAN_INFLUENZA);
            }
        }
        return result;
    }
    //   Validation
    validateRequiredField(v) {
        return requiredField$3(v) || this.$i18n.t('as6242024.base.errors.requiredField');
    }
};
__decorate([
    Prop()
], AdministrativeReason$1.prototype, "data", void 0);
__decorate([
    Prop()
], AdministrativeReason$1.prototype, "numberOfLineToDisplay", void 0);
__decorate([
    PropSync('data', { type: Object })
], AdministrativeReason$1.prototype, "item", void 0);
AdministrativeReason$1 = __decorate([
    Component({
        computed: {
            // ENUMS
            VaccineCategoryIdsEnum() {
                return VaccineCategoryIdsEnum;
            },
            AdministrationReasonInfluenzaEnum() {
                return AdministrationReasonInfluenzaEnum;
            },
            AdministrationReasonMpoxEnum() {
                return AdministrationReasonMpoxEnum;
            },
            AdministrationReasonCovidEnum() {
                return AdministrationReasonCovidEnum;
            },
            AdministrationReasonDcatEnum() {
                return AdministrationReasonDcatEnum;
            },
            AdministrationReasonShinglesEnum() {
                return AdministrationReasonShinglesEnum;
            },
            AdministrationReasonVrsEnum() {
                return AdministrationReasonVrsEnum;
            },
            // Arrays
            AdministrationReasonInfluenzaArray() {
                return Object.keys(AdministrationReasonInfluenzaEnum);
            },
            AdministrationReasonMpoxArray() {
                return Object.keys(AdministrationReasonMpoxEnum);
            },
            AdministrationReasonCovidArray() {
                return Object.keys(AdministrationReasonCovidEnum);
            },
            AdministrationReasonShinglesArray() {
                return Object.keys(AdministrationReasonShinglesEnum);
            },
            AdministrationReasonDcatArray() {
                return Object.keys(AdministrationReasonDcatEnum);
            },
            AdministrationReasonVrsArray() {
                return Object.keys(AdministrationReasonVrsEnum);
            },
        },
    })
], AdministrativeReason$1);
var script$k = AdministrativeReason$1;

/* script */
            const __vue_script__$k = script$k;
            
/* template */
var __vue_render__$k = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    { staticClass: "px-2 py-0 mt-2 border-all", attrs: { fluid: "" } },
    [
      _c(
        "v-layout",
        [
          _c("v-flex", { staticClass: "section-title", attrs: { xs12: "" } }, [
            _c("span", [_vm._v(_vm._s(_vm.i18n.title))])
          ])
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "", wrap: "", "fill-height": "" } },
        [
          _c(
            "v-flex",
            { staticClass: "pa-0 border-bottom" },
            [
              _c(
                "v-layout",
                { attrs: { "fill-height": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "vertical-text section-subtitle text-xs-center border-right px-0",
                      attrs: { shrink: "" }
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.reason.influenza.title) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "text-xs-left border-left px-2",
                      attrs: { grow: "" }
                    },
                    _vm._l(_vm.AdministrationReasonInfluenzaArray, function(
                      reason,
                      index
                    ) {
                      return _c("v-checkbox", {
                        key: reason,
                        attrs: {
                          label: _vm.i18n.reason.influenza["" + (index + 1)],
                          "aria-label":
                            "administration-reason-influenza-" +
                            _vm.AdministrationReasonInfluenzaEnum[reason],
                          value:
                            "" + _vm.AdministrationReasonInfluenzaEnum[reason],
                          disabled: !_vm.hasReasonSectionAccess,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.isReasonsRequired[
                                _vm.VaccineCategoryIdsEnum.FLU
                              ] ||
                              _vm.validateRequiredField
                          ]
                        },
                        model: {
                          value: _vm.item.administrationReasonInfluenza,
                          callback: function($$v) {
                            _vm.$set(
                              _vm.item,
                              "administrationReasonInfluenza",
                              $$v
                            );
                          },
                          expression: "item.administrationReasonInfluenza"
                        }
                      })
                    }),
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "pa-0 border-left" },
            [
              _c(
                "v-flex",
                { staticClass: "pa-0 border-bottom" },
                [
                  _c(
                    "v-layout",
                    { attrs: { "fill-height": "" } },
                    [
                      _c(
                        "v-flex",
                        {
                          staticClass:
                            "vertical-text section-subtitle text-none text-xs-center px-0",
                          attrs: { shrink: "" }
                        },
                        [
                          _vm._v(
                            "\n              " +
                              _vm._s(_vm.i18n.reason.dcat.title) +
                              "\n            "
                          )
                        ]
                      ),
                      _vm._v(" "),
                      _c(
                        "v-flex",
                        {
                          staticClass: "text-xs-left border-left px-2",
                          attrs: { grow: "" }
                        },
                        _vm._l(_vm.AdministrationReasonDcatArray, function(
                          reason,
                          index
                        ) {
                          return _c("v-checkbox", {
                            key: reason,
                            attrs: {
                              label: _vm.i18n.reason.dcat["" + (index + 1)],
                              "aria-label":
                                "administration-reason-dcat-" +
                                _vm.AdministrationReasonDcatEnum[reason],
                              value:
                                "" + _vm.AdministrationReasonDcatEnum[reason],
                              disabled: !_vm.hasReasonSectionAccess,
                              "hide-details": "",
                              rules: [
                                !_vm.appliedRequiredField ||
                                  !_vm.isReasonsRequired[
                                    _vm.VaccineCategoryIdsEnum.DCAT
                                  ] ||
                                  _vm.validateRequiredField
                              ]
                            },
                            model: {
                              value: _vm.item.administrationReasonDcat,
                              callback: function($$v) {
                                _vm.$set(
                                  _vm.item,
                                  "administrationReasonDcat",
                                  $$v
                                );
                              },
                              expression: "item.administrationReasonDcat"
                            }
                          })
                        }),
                        1
                      )
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "pa-0 border-bottom" },
                [
                  _c(
                    "v-layout",
                    { attrs: { "fill-height": "" } },
                    [
                      _c(
                        "v-flex",
                        {
                          staticClass:
                            "vertical-text section-subtitle text-xs-center px-0",
                          attrs: { shrink: "" }
                        },
                        [
                          _vm._v(
                            "\n              " +
                              _vm._s(_vm.i18n.reason.shingles.title) +
                              "\n            "
                          )
                        ]
                      ),
                      _vm._v(" "),
                      _c(
                        "v-flex",
                        {
                          staticClass: "text-xs-left border-left px-2",
                          attrs: { grow: "" }
                        },
                        _vm._l(_vm.AdministrationReasonShinglesArray, function(
                          reason,
                          index
                        ) {
                          return _c("v-checkbox", {
                            key: reason,
                            attrs: {
                              label: _vm.i18n.reason.shingles["" + (index + 1)],
                              "aria-label":
                                "administration-reason-shingles-" +
                                _vm.AdministrationReasonShinglesEnum[reason],
                              value:
                                "" +
                                _vm.AdministrationReasonShinglesEnum[reason],
                              disabled: !_vm.hasReasonSectionAccess,
                              "hide-details": "",
                              rules: [
                                !_vm.appliedRequiredField ||
                                  !_vm.isReasonsRequired[
                                    _vm.VaccineCategoryIdsEnum.ZONA
                                  ] ||
                                  _vm.validateRequiredField
                              ]
                            },
                            model: {
                              value: _vm.item.administrationReasonShingles,
                              callback: function($$v) {
                                _vm.$set(
                                  _vm.item,
                                  "administrationReasonShingles",
                                  $$v
                                );
                              },
                              expression: "item.administrationReasonShingles"
                            }
                          })
                        }),
                        1
                      )
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "pa-0 border-bottom" },
                [
                  _c(
                    "v-layout",
                    { attrs: { "fill-height": "" } },
                    [
                      _c(
                        "v-flex",
                        {
                          staticClass:
                            "vertical-text section-subtitle text-none text-xs-center px-0",
                          attrs: { shrink: "" }
                        },
                        [
                          _vm._v(
                            "\n              " +
                              _vm._s(_vm.i18n.reason.vrs.title) +
                              "\n            "
                          )
                        ]
                      ),
                      _vm._v(" "),
                      _c(
                        "v-flex",
                        {
                          staticClass: "text-xs-left border-left px-2",
                          attrs: { grow: "" }
                        },
                        _vm._l(_vm.AdministrationReasonVrsArray, function(
                          reason,
                          index
                        ) {
                          return _c("v-checkbox", {
                            key: reason,
                            attrs: {
                              label: _vm.i18n.reason.vrs["" + (index + 1)],
                              "aria-label":
                                "administration-reason-vrs-" +
                                _vm.AdministrationReasonVrsEnum[reason],
                              value:
                                "" + _vm.AdministrationReasonVrsEnum[reason],
                              disabled: !_vm.hasReasonSectionAccess,
                              "hide-details": "",
                              rules: [
                                !_vm.appliedRequiredField ||
                                  !_vm.isReasonsRequired[
                                    _vm.VaccineCategoryIdsEnum.VRS
                                  ] ||
                                  _vm.validateRequiredField
                              ]
                            },
                            model: {
                              value: _vm.item.administrationReasonVrs,
                              callback: function($$v) {
                                _vm.$set(
                                  _vm.item,
                                  "administrationReasonVrs",
                                  $$v
                                );
                              },
                              expression: "item.administrationReasonVrs"
                            }
                          })
                        }),
                        1
                      )
                    ],
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "pa-0 border-bottom" },
            [
              _c(
                "v-layout",
                { attrs: { "fill-height": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "vertical-text section-subtitle text-xs-center border-right px-0",
                      attrs: { shrink: "" }
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.reason.covid.title) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "text-xs-left border-left px-2",
                      attrs: { grow: "" }
                    },
                    _vm._l(_vm.AdministrationReasonCovidArray, function(
                      reason,
                      index
                    ) {
                      return _c("v-checkbox", {
                        key: reason,
                        attrs: {
                          label: _vm.i18n.reason.covid["" + (index + 1)],
                          "aria-label":
                            "administration-reason-covid-" +
                            _vm.AdministrationReasonCovidEnum[reason],
                          value: "" + _vm.AdministrationReasonCovidEnum[reason],
                          disabled: !_vm.hasReasonSectionAccess,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.isReasonsRequired[
                                _vm.VaccineCategoryIdsEnum.COVID
                              ] ||
                              _vm.validateRequiredField
                          ]
                        },
                        model: {
                          value: _vm.item.administrationReasonCovid,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "administrationReasonCovid", $$v);
                          },
                          expression: "item.administrationReasonCovid"
                        }
                      })
                    }),
                    1
                  )
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "pa-0 border-bottom" },
            [
              _c(
                "v-layout",
                { attrs: { "fill-height": "" } },
                [
                  _c(
                    "v-flex",
                    {
                      staticClass:
                        "vertical-text section-subtitle text-xs-center border-right px-0",
                      attrs: { shrink: "" }
                    },
                    [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.i18n.reason.mpox.title) +
                          "\n          "
                      )
                    ]
                  ),
                  _vm._v(" "),
                  _c(
                    "v-flex",
                    {
                      staticClass: "text-xs-left border-right border-left px-2",
                      attrs: { grow: "" }
                    },
                    _vm._l(_vm.AdministrationReasonMpoxArray, function(
                      reason,
                      index
                    ) {
                      return _c("v-checkbox", {
                        key: reason,
                        attrs: {
                          label: _vm.i18n.reason.mpox["" + (index + 1)],
                          "aria-label":
                            "administration-reason-mpox-" +
                            _vm.AdministrationReasonMpoxEnum[reason],
                          value: "" + _vm.AdministrationReasonMpoxEnum[reason],
                          disabled: !_vm.hasReasonSectionAccess,
                          "hide-details": "",
                          rules: [
                            !_vm.appliedRequiredField ||
                              !_vm.isReasonsRequired[
                                _vm.VaccineCategoryIdsEnum.MPOX
                              ] ||
                              _vm.validateRequiredField
                          ]
                        },
                        model: {
                          value: _vm.item.administrationReasonMpox,
                          callback: function($$v) {
                            _vm.$set(_vm.item, "administrationReasonMpox", $$v);
                          },
                          expression: "item.administrationReasonMpox"
                        }
                      })
                    }),
                    1
                  )
                ],
                1
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$k = [];
__vue_render__$k._withStripped = true;

  /* style */
  const __vue_inject_styles__$k = undefined;
  /* scoped */
  const __vue_scope_id__$k = undefined;
  /* functional template */
  const __vue_is_functional_template__$k = false;
  /* component normalizer */
  function __vue_normalize__$k(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/AdministrativeReason/AdministrativeReason.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var AdministrativeReason = __vue_normalize__$k(
    { render: __vue_render__$k, staticRenderFns: __vue_staticRenderFns__$k },
    __vue_inject_styles__$k,
    __vue_script__$k,
    __vue_scope_id__$k,
    __vue_is_functional_template__$k);

let VaccineCategoryPrefillInput$1 = class VaccineCategoryPrefillInput extends Vue {
    /**
     * - If user begin to fill vaccine information then the field become required.
     **/
    get userAddVaccineInformationRules() {
        //@ts-ignore
        return !this.haveUserAddedVaccineInformation || this.requiredField();
    }
};
__decorate([
    Prop()
], VaccineCategoryPrefillInput$1.prototype, "index", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$1.prototype, "value", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$1.prototype, "item", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$1.prototype, "vaccineCategoryList", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$1.prototype, "appliedRequiredField", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$1.prototype, "haveUserAddedVaccineInformation", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$1.prototype, "disabled", void 0);
__decorate([
    Prop()
], VaccineCategoryPrefillInput$1.prototype, "ariaLabel", void 0);
VaccineCategoryPrefillInput$1 = __decorate([
    Component({
        mixins: [FormRules],
    })
], VaccineCategoryPrefillInput$1);
var script$j = VaccineCategoryPrefillInput$1;

/* script */
            const __vue_script__$j = script$j;
            
/* template */
var __vue_render__$j = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("v-autocomplete", {
    attrs: {
      "hide-details": "",
      "aria-label": _vm.ariaLabel,
      dense: "",
      items: _vm.vaccineCategoryList,
      "item-text": "name",
      "item-value": "id",
      disabled: _vm.disabled,
      box: "",
      rules: [!_vm.appliedRequiredField || _vm.userAddVaccineInformationRules]
    },
    on: {
      change: function($event) {
        return _vm.$emit("change", $event)
      }
    },
    model: {
      value: _vm.item["vaccineDetailsCategory" + _vm.index],
      callback: function($$v) {
        _vm.$set(_vm.item, "vaccineDetailsCategory" + _vm.index, $$v);
      },
      expression: "item[`vaccineDetailsCategory${index}`]"
    }
  })
};
var __vue_staticRenderFns__$j = [];
__vue_render__$j._withStripped = true;

  /* style */
  const __vue_inject_styles__$j = undefined;
  /* scoped */
  const __vue_scope_id__$j = undefined;
  /* functional template */
  const __vue_is_functional_template__$j = false;
  /* component normalizer */
  function __vue_normalize__$j(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/VaccineDetails/VaccineCategoryPrefillInput.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var VaccineCategoryPrefillInput = __vue_normalize__$j(
    { render: __vue_render__$j, staticRenderFns: __vue_staticRenderFns__$j },
    __vue_inject_styles__$j,
    __vue_script__$j,
    __vue_scope_id__$j,
    __vue_is_functional_template__$j);

const as6242024French = {
    as6242024: {
        general: {
            title: 'FORMULAIRE D\'IMMUNISATION',
            save: 'Enregistrer',
            markFormAsTransmitted: 'Marquer comme transmis',
            markFormAsNotTransmitted: 'Marquer comme non transmis',
            exportToPdf: 'Imprimer en pdf',
        },
        footer: {
            save: 'Enregistrer',
            saveAndComplete: 'Enregistrer et Terminer',
            markFormAsTransmitted: 'Marquer comme transmis',
            markFormAsNotTransmitted: 'Marquer comme non transmis',
            close: 'Fermer',
        },
        titleAndPatient: {
            pageTitle: 'FORMULAIRE D\'IMMUNISATION',
            logo: 'logo',
            barCode: 'Code barre',
            barcodeUrl: 'https://static.trimoz.com/clicsante/images/forms/as624/barcode-AS-624-fr-1.png',
            fileNo: 'Numéro de dossier',
            siPmiId: 'N° ID SI-PMI',
            name: 'Nom et prénom de l’usager',
            motherName: 'Nom et prénom de la mère',
            fatherName: 'Nom et prénom du père (optionnel)',
            unknownMother: 'Inconnue',
            unknownFather: 'Inconnu',
            birthday: 'Date de naissance',
            birthday1: 'Date de',
            birthday2: 'naissance',
            year: 'Année',
            month: 'Mois',
            day: 'Jour',
            sex: 'Sexe',
            gender: 'Sexe',
            nam: 'N° d’assurance maladie',
            expiryDate: 'Expiration',
            address: 'Adresse (N°, rue)',
            city: 'Ville',
            postalCode: 'Code postal',
            placeholder: {
                birthday: 'AAAA-MM-JJ',
                address: 'N°, rue',
                expiryDate: 'AAAA-MM',
            },
        },
        generalInformation: {
            title: 'INFORMATIONS GÉNÉRALES',
            user14YearsOrOlder: 'Usager apte de 14 ans ou plus',
            userUnder14: 'Usager de moins de 14 ans ou usager majeur inapte',
            regInd: 'Ind. rég.',
            regIndAriaLabel: 'Indicatif régional',
            phone: 'Nº de téléphone',
            cell: 'Cellulaire',
            home: 'Résidence',
            email: 'Si vous consentez à être joint par courriel, veuillez l’inscrire :',
            authorizedPerson: 'Personne autorisée selon sa déclaration (nom, prénom) :',
            authorizedPersonRegInd: 'Indicateur régional de la personne autorisée',
            legalRepresentative: {
                mandatary: 'Mandataire',
                guardian: 'Tuteur',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                closeRelative: 'Proche parent du majeur inapte',
                personInterest: 'Personne qui démontre pour le majeur un intérêt particulier',
                parentalAuthority: 'Autorité parentale',
            },
        },
        preImmunization: {
            title: 'QUESTIONNAIRE PRÉ IMMUNISATION',
            header: {
                elements: 'ÉLÉMENTS À ANALYSER',
                yes: 'OUI',
                no: 'NON',
                NA: 'SANS OBJET',
                details: 'PRÉCISIONS',
            },
            title1: 'Problèmes de santé',
            question1: 'L’usager a-t-il une condition de santé qui nécessite un suivi médical ou une prise régulière de médicaments et/ou un changement dans son état de santé?',
            title2: 'Immunodépression',
            question2: 'L’usager présente-t-il un affaiblissement du système immunitaire en raison d’une maladie (ex. : leucémie) ou d’un médicament pris actuellement (ex. : chimiothérapie)?',
            title3: 'Réactions antérieures',
            question3: 'L’usager a-t-il déjà eu une réaction allergique grave qui a nécessité des soins médicaux d’urgence? \n' +
                ' <div style="margin-left: 20px;">ou</div> a-t-il déjà eu, après l’administration d’un produit immunisant, une réaction assez grave qui a nécessité une consultation médicale?',
            title4: 'Troubles de la coagulation',
            question4: 'L’usager présente-t-il des troubles de la coagulation nécessitant un suivi médical ou une prise d’anticoagulants?',
            title5: 'Produit immunisant ou sanguin',
            question5: 'L’usager a-t-il reçu une transfusion sanguine ou une injection d’immunoglobulines au cours des 11 derniers mois? <br/> L’usager a-t-il reçu un vaccin vivant atténué dans les 4 dernières semaines?',
            title6: 'Grossesse',
            question6: 'Si l’usager est une femme, est-elle présentement enceinte?',
            title7: '',
            question7: 'Si l’usager est un enfant âgé de moins de six mois, la mère a-t-elle pris, pendant la grossesse, un agent biologique pour le traitement d’une maladie auto-immune (ex. : maladie de Crohn, polyarthrite rhumatoïde, lupus ou psoriasis)?',
            title8: '',
            question8: 'Précaution(s)/contre-indication(s) selon le Protocole d’immunisation du Québec (PIQ) pour le/les produits immunisants à administrer.',
            precision: 'Précision',
            monitoringTime: {
                label: 'Temps de surveillance recommandé après l’administration :',
                time1: '15 minutes ou',
                time2: '30 minutes',
                precision: 'Précisez au besoin :',
            },
        },
        administration: {
            title: 'RAISON D’ADMINISTRATION SI REQUISE',
            reason: {
                shingles: {
                    title: 'ZONA',
                    1: 'Immunodéprimé',
                    2: 'Autres raisons',
                },
                influenza: {
                    title: 'INFLUENZA',
                    1: 'Résident en CHSLD',
                    2: 'Résident en RPA',
                    3: 'Femme enceinte',
                    4: 'Travailleur de la santé',
                    5: 'Maladie chronique',
                    6: 'Autres raisons',
                },
                dcat: {
                    title: 'dcaT',
                    1: 'Femme enceinte (coqueluche)',
                    2: 'Autres raisons',
                },
                covid: {
                    title: 'COVID-19',
                    1: 'Résident en CHSLD',
                    2: 'Résident en RPA',
                    3: 'Femme enceinte',
                    4: 'Travailleur de la santé',
                    5: 'Maladie chronique',
                    6: 'Autres raisons',
                },
                mpox: {
                    title: 'MPOX',
                    1: 'Préexposition - immunodéprimé',
                    2: 'Préexposition - non immunodéprimé',
                    3: 'Postexposition - immunodéprimé',
                    4: 'Postexposition - non immunodéprimé',
                    5: 'Autres raisons',
                },
                vrs: {
                    title: 'VRS (adultes)',
                    1: 'Résident en CHSLD',
                    2: 'Résident en RPA',
                    3: 'Autres raisons',
                },
            },
        },
        consentAndDecision: {
            title: 'CONSENTEMENT',
            consentCheckbox1: 'Les renseignements sur la ou les maladies et sur le ou les produits immunisants, incluant les réactions possibles et la conduite à tenir après l’administration de ceux-ci, ont été donnés à l’usager ou à son représentant légal.',
            consentCheckbox2: 'Consent à l’administration du ou des produits immunisants suivants, à la suite de l’analyse du professionnel habilité :',
            consentCheckbox2precision: '',
            consentCheckbox2precisionAriaLabel: 'Précision des produits consentis',
            consentCheckbox3: 'Refuse le ou les produits immunisants suivants :',
            consentCheckbox3precision: '',
            consentCheckbox3precisionAriaLabel: 'Précision des produits non consentis',
            consentCheckbox4: 'Ne s’applique pas',
            recommendation: 'BUREAU DE SANTÉ :',
            healthOfficeOrientation: 'L’administration du ou des produits immunisants découle d’une recommandation du bureau de santé :',
            healthOfficeConsent: 'Je consens à ce que mes informations soient acheminées au bureau de santé',
            consentsRefuses: {
                title: 'CONSENTEMENT/REFUS OBTENU AUPRÈS DE :',
                patient: 'Usager',
                parentalAuthority: 'Autorité parentale',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                mandatary: 'Mandataire',
                specialInterest: 'Personne qui démontre pour le majeur inapte un intérêt particulier',
                closeRelative: 'Proche parent du majeur inapte',
                guardian: 'Tuteur',
            },
        },
        professionalInformation: {
            titleForm: 'INFORMATIONS SUR LE PROFESSIONNEL HABILITÉ QUI A ANALYSÉ LE QUESTIONNAIRE PRÉ-IMMUNISATION',
            titleConsent: 'INFORMATIONS SUR LE PROFESSIONNEL QUI OBTIENT LE CONSENTEMENT :',
            clearMyInformations: 'Retirer mes informations',
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            name: 'Nom',
            profession: {
                title: 'PROFESSION',
                nurse: 'Infirmière',
                physician: 'Médecin',
                inhalotherapist: 'Inhalothérapeute',
                midwife: 'Sage‐femme',
                pharmacist: 'Pharmacien',
                other: 'Autre',
            },
            licenceNumber: 'N° de permis :',
            professionalSignature: 'Signature :',
            date: 'Date',
            year: 'Année',
            month: 'Mois',
            day: 'Jour',
        },
        phoneConsent: {
            witnessName: 'Nom du témoin si le consentement est obtenu par téléphone :',
        },
        vaccineDetails: {
            title: 'DÉTAILS DES PRODUITS IMMUNISANTS ADMINISTRÉS',
            getMyInformations: 'Ajouter l\'injecteur ',
            clearMyInformations: 'Retirer l\'injecteur ',
            completeMyProfile: 'Compléter mon profil',
            category: 'Catégorie de vaccin',
            date: 'Date (année, mois, jour)',
            dateSubText: '(année, mois, jour)',
            hour: 'Heure (00:00)',
            name: 'Nom du produit immunisant',
            batchNumber: 'N° de lot',
            dose: 'Quantité / unité',
            administration: 'Voie d’administration',
            injectionSite: 'Site d’administration',
            vaccinePlace: 'LIEU DE DISPENSATION DE SERVICE (LDS):',
            siPmi: 'Saisie SI-PMI',
            initials: 'Initiales',
            placeholder: {
                date: 'AAAA-MM-JJ',
            },
            injectionSiteType: {
                leftArm: 'Bras gauche',
                rightArm: 'Bras droit',
                leftThigh: 'Cuisse gauche',
                rightThigh: 'Cuisse droite',
                other: 'Autre',
                otherAriaLabel: 'Autre lieu d\'injection',
            },
            administrationType: {
                intramuscular: 'Intra-musculaire',
                intranasal: 'Intra-nasal',
                intradermic: 'Intra-dermique',
                subcutaneous: 'Sous-cutanée',
                oral: 'Orale',
            },
            professionInformation: {
                title: 'INFORMATIONS SUR L’INTERVENANT QUI A ADMINISTRÉ LE OU LES PRODUITS IMMUNISANTS :',
                name: 'Nom :',
                profession: 'Profession (précisez) :',
                signature: 'Signature :',
                licenceNumber: 'N° de permis :',
                initials: 'Initiales :',
            },
            zona: {
                shingrix: 'Shingrix',
            },
            flu: {
                afluria: 'Afluria',
                fluad: 'Fluad',
                fluadPediatric: 'Fluad pédiatrique',
                flucelvax: 'Flucelvax quad',
                flulaval: 'Flulaval tetra',
                fluMist: 'FluMist quadrivalent',
                fluzoneQuadrivalent: 'Fluzone quadrivalent',
                fluzoneDose: 'Fluzone HD quadrivalent',
                influvacTetra: 'Influvac tetra',
                supemtek: 'Supemtek',
                other: 'Autre',
            },
            covid: {
                'Moderna': 'Spikevax (Moderna)',
                'Pfizer-BioNTech': 'Comirnaty (Pfizer-BioNTech)',
            },
            dcat: {
                actHib: 'Act-Hib',
                adacel: 'Adacel',
                adacelPolio: 'Adacel-Polio',
                boostrix: 'Boostrix',
                boostrixPolio: 'Boostrix-Polio',
                imovaxPolio: 'Imovax Polio',
                infanrixHexa: 'Infanrix hexa',
                infanrixIPVHib: 'Infanrix-IPV/Hib',
                pediacel: 'Pediacel',
                td: 'Td Adsorbées',
                pentacel: 'Pentacel',
            },
            mpox: {
                imvamune: 'Imvamune',
            },
            hepatitis: {
                avaxim: 'Avaxim',
                engerixB: 'Engerix-B',
                havrix: 'Havrix',
                recombivaxHb: 'Recombivax HB',
                twinrix: 'Twinrix',
                vaqta: 'Vaqta',
                prehevbrio: 'PREHEVBRIO',
            },
            meningitis: {
                bexsero: 'Bexsero',
                menactra: 'Menactra',
                menjugate: 'Menjugate',
                menveo: 'Menveo',
                neisvacC: 'Neisvac-C',
                nimenrix: 'Nimenrix',
                trumenba: 'Trumenba',
            },
            pneumococcal: {
                pneumovax23: 'Pneumovax 23',
                prevnar13: 'Prevnar 13',
                prevnar20: 'Prevnar 20',
                synflorix: 'Synflorix',
                vaxneuvance: 'Vaxneuvance',
                capvaxine: 'Capvaxine',
            },
            rabies: {
                imovaxRage: 'Imovax Rage',
                rabAvert: 'RabAvert',
            },
            rotavirus: {
                rotarix: 'Rotarix',
                rotateq: 'RotaTeq',
            },
            rro: {
                mmrII: 'M-M-R II',
                priorix: 'Priorix',
                priorixTetra: 'Priorix Tetra',
                proQuad: 'ProQuad',
                varilrix: 'Varilrix',
                varivaxIII: 'Varivax III',
            },
            tuberculosis: {
                bcg: 'BCG lyophilisé avec glutamate',
                tubersol: 'Tubersol',
            },
            vph: {
                cervarix: 'Cervarix',
                gardasil9: 'Gardasil 9',
            },
            travel: {
                dukoral: 'Dukoral',
                ixiaro: 'Ixiaro',
                yfVax: 'Yf Vax',
                ixchiq: 'Ixchiq',
                vaxchora: 'Vaxchora',
            },
            vrs: {
                arexvy: 'Arexvy',
                abrysvo: 'Abrysvo',
            },
            vrs_baby: {
                nirsevimab: 'Nirsevimab',
            },
            typhoid: {
                typhimVi: 'Typhim Vi',
                vivotif: 'Vivotif',
            },
            avian_influenza: {
                arepanrix: 'Arepanrix H5N1',
            },
            vaccineCategories: {
                zona: 'Zona',
                flu: 'Influenza',
                covid: 'COVID-19',
                mpox: 'MPOX',
                dcat: 'Diphtérie, coqueluche, tétanos, hépatite B, poliomyélite et H. influenzæ de type b',
                hepatitis: 'Hépatites',
                meningitis: 'Méningocoque',
                pneumococcal: 'Pneumocoque',
                rabies: 'Rage',
                rotavirus: 'Rotavirus',
                rro: 'Rougeole, rubéole, oreillons et varicelle',
                tuberculosis: 'Tuberculose',
                vph: 'Virus du papillome humain',
                travel: 'Voyageurs',
                vrs: 'Virus Respiratoire Syncytial (VRS)',
                vrs_baby: 'Virus Respiratoire Syncytial (VRS) Bébé',
                typhoid: 'Vaccin contre la typhoïde',
                avian_influenza: 'Grippe aviaire A (H5N1)',
            },
            vaccineCategoriesPdf: {
                zona: 'Zona',
                flu: 'Influenza',
                covid: 'COVID-19',
                mpox: 'MPOX',
                dcat: 'Dcat',
                hepatitis: 'Hépatites',
                meningitis: 'Méningocoque',
                pneumococcal: 'Pneumocoque',
                rabies: 'Rage',
                rotavirus: 'Rotavirus',
                rro: 'Rougeole, rubéole, oreillons et varicelle',
                tuberculosis: 'Tuberculose',
                vph: 'Virus du papillome humain',
                travel: 'Voyageurs',
                vrs: 'Virus Respiratoire Syncytial (VRS)',
                vrs_baby: 'Virus Respiratoire Syncytial (VRS) Bébé',
                typhoid: 'Vaccin contre la typhoïde',
                avian_influenza: 'Grippe aviaire A (H5N1)',
            },
            professionList: {
                nurse: 'Infirmière',
                physician: 'Médecin',
                inhalotherapist: 'Inhalothérapeute',
                midwife: 'Sage‐femme',
                pharmacist: 'Pharmacien',
                other: 'Autre',
            },
        },
        oldVaccineDetails: {
            flu: {
                agriflu: 'Agriflu',
                fluviral: 'Fluviral',
                influvac: 'Influvac',
            },
            covid: {
                'Spikevax-XBB-1-5': 'Spikevax XBB.1.5',
                'Comirnaty-XBB15-6m-4y': 'Comirnaty XBB.1.5 - 6 mois-4 ans',
                'Comirnaty-XBB15-5-11': 'Comirnaty XBB.1.5 - 5-11 ans',
                'Comirnaty-XBB15-12': 'Comirnaty XBB.1.5 - 12 ans +',
                'Spikevax-Bivalent-BA4-BA5': 'Spikevax Bivalent BA4-BA5 (Moderna)',
                'Moderna-Bivalent': 'Spikevax Bivalent BA1 (Moderna)',
                'Moderna-Kids': 'Spikevax (Moderna) 6 mois - 5 ans',
                'Pfizer-BioNTech-Kids': 'Comirnaty (Pfizer-BioNTech) 5-11 ans',
                'Covishield': 'Covishield',
                'Nuvaxovid': 'Nuvaxovid (Novavax)',
                'Novavax': 'Nuvaxovid (Novavax)',
                'AstraZeneca': 'Vaxzevria (AstraZeneca)',
                'Comirnaty-Bivalent-BA4-BA5': 'Comirnaty Bivalent BA4-BA5',
                'Pfizer-BioNTech-Kids-6m-4y': 'Comirnaty (Pfizer-BioNTech) 6 mois - 4 ans',
                'Comirnaty-Bivalent-BA4-BA5-Kids': 'Comirnaty Bivalent BA4-BA5 5-11 ans',
                'Jcovden': 'Jcovden',
                'Covifenz': 'Covifenz',
            },
            dcat: {
                hiberix: 'Hiberix',
            },
        },
        notes: {
            notes: 'Notes',
            requiredField: '* Indique un champ requis',
        },
        headerPage: {
            patientName: 'Nom et prénom de l\'usager',
            recordNumber: 'N° de dossier',
            siPmiId: 'N° ID SI-PMI',
        },
        footerPage: {
            title: 'FORMULAIRE D\'IMMUNISATION',
            formRevision: 'AS-624 DT9225 (rév. 2025-03)',
            patientFile: 'DOSSIER DE L\'USAGER',
        },
        error: {
            title: 'Le formulaire contient des erreurs',
            message: 'Des champs requis sont manquants ou contiennent des erreurs...',
        },
        warning: {
            title: 'Êtes-vous certain de vouloir modifier le contenu?',
            text: 'Le formulaire est terminé et signé par un Vaccinateur. Une fois le changement appliqué, vous devrez signer et assumer la responsabilité de l\'entièreté du document.',
            question: 'Désirez-vous poursuivre les modifications?',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Annuler',
            no: 'Non',
            yes: 'Oui',
        },
        base: {
            errors: {
                requiredField: 'Le champ est requis',
                requiredNumberField: 'La valeur doit être un nombre',
                invalidDate: 'Cette date est invalide',
                invalidTime: 'Cette heure est invalide',
                invalidNam: 'Le numéro d\'assurance maladie est invalide',
                invalidMonth: 'Ce mois est invalide',
                invalidPostalCode: 'Le code postal est invalide',
                invalidRegInd: 'L\'indicatif régional est invalide',
                phone2short: 'Le téléphone est incorrect',
                invalidEmail: 'Le format du courriel est invalide',
                iclscNumber: 'Le format du numéro I-CLSC est invalide',
            },
        },
    },
};

/**
 * Transform underscore string into sentence (start with an Uppercase, white space instead of underscore)
 * @param str
 * @returns
 */
function inSentence(str) {
    let i, frags = str.split('_'); // eslint-disable-line
    for (i = 0; i < frags.length; i++) {
        frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
    }
    return frags.join('');
}
function forceA11yNarratorSmallPause(text) {
    // Add a trailing period to force the narrator to pause
    return text + '.';
}
function setFocusForAccessibility(elementId, timeOut = 500, useBlurAll = true) {
    if (useBlurAll) {
        blurAll();
    }
    if (timeOut === null) {
        focusElement(elementId);
        return;
    }
    setTimeout(() => {
        focusElement(elementId);
    }, timeOut);
}
function focusElement(elementId) {
    const element = document.getElementById(elementId);
    if (element === null) {
        return;
    }
    if (element.getAttribute('tabindex') === '-1') {
        element.setAttribute('tabindex', 0);
    }
    if (element) {
        element.addEventListener('focus', (event) => { event.preventDefault(); });
        element.focus();
    }
}
/**
* Unfocus all.
* ref.: https://stackoverflow.com/a/29237391
*/
function blurAll() {
    const tmp = document.createElement('input');
    document.body.appendChild(tmp);
    tmp.focus();
    document.body.removeChild(tmp);
}
/*
 * Reference: https://developer.mozilla.org/en-US/docs/Glossary/Deep_copy
 */
function deepCopy(element) {
    return JSON.parse(JSON.stringify(element));
}
/*
 * Reference: https://stackoverflow.com/a/40610459
 */
function getObjectDiff(obj1, obj2) {
    const diff = Object.keys(obj1).reduce((result, key) => {
        if (!Object.prototype.hasOwnProperty.call(obj2, key)) {
            result.push(key);
        }
        else if (lodashExports.isEqual(obj1[key], obj2[key])) {
            const resultKeyIndex = result.indexOf(key);
            result.splice(resultKeyIndex, 1);
        }
        return result;
    }, Object.keys(obj2));
    return diff;
}

var useVaccineDetails = (categoryExclusionList) => {
    /**
     * Withdraw excluded vaccine categories if it was not already selected.
     * @param vaccineCategories List of selectable vaccine categories.
     * @param selectedVaccineCategories List of vaccine categories already selected.
     * @returns List of selectable vaccine categories without excluded vaccine categories.
     */
    function withdrawExcludedVaccineCategory(vaccineCategories, selectedVaccineCategories) {
        const excludedCategoriesSet = new Set(categoryExclusionList);
        const selectedVaccineCategoriesSet = new Set(selectedVaccineCategories);
        return lodashExports.filter(vaccineCategories, cat => !excludedCategoriesSet.has(cat.id) || selectedVaccineCategoriesSet.has(cat.id));
    }
    return {
        withdrawExcludedVaccineCategory,
    };
};

const { requiredField: requiredField$2, requiredNumberField, isDate: isDate$1 } = useFormRules();
const fields = [
    'vaccineDetailsCategory',
    'vaccineDetailsDate',
    'vaccineDetailsTime',
    'vaccineDetailsName',
    'vaccineDetailsBatchNumber',
    'vaccineDetailsDose',
    'vaccineDetailsAdministration',
    'vaccineDetailsInjectionSite',
    'vaccineDetailsOther',
    'vaccineDetailsInitials',
];
let itemBuffer = null;
let VaccineDetails$1 = class VaccineDetails extends Vue {
    constructor() {
        super(...arguments);
        this.AdministrationEnum = AdministrationEnum;
        this.InjectionSiteEnum = InjectionSiteEnum;
        this.maximumVaccineCategoryList = 10;
        this.vaccineCategoryList = [];
        this.immunizationProviderProfessionList = [];
    }
    otherAdministrationSiteEntered(index) {
        return !lodashExports.isEmpty(this.item[`vaccineDetailsOther${index}`]);
    }
    administrationSiteEntered(index) {
        return !lodashExports.isEmpty(this.item[`vaccineDetailsInjectionSite${index}`]);
    }
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('as6242024.vaccineDetails');
    }
    get prefillFields() {
        return this.globalData.getFormAppData.prefillFields;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasDetailSectionAccess() {
        return this.globalData.getFormAppData.hasDetailSectionAccess;
    }
    get hasDetailSectionVaccineTypeAccess() {
        return this.globalData.getFormAppData.hasDetailSectionVaccineTypeAccess;
    }
    get showInjectorBtn() {
        return this.globalData.getFormAppData.showInjectorBtn ||
            this.globalData.getFormAppData.showVaccinatorBtn;
    }
    get isProfileComplete() {
        return this.globalData.getFormAppData.isProfileComplete;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get informationExistInVaccineRows() {
        let result = [];
        for (let i = 1; i <= this.numberOfLineToDisplay; i++) {
            result[i] = (this.hasDataInRow(i));
        }
        return result;
    }
    get hasVaccinatorSectionAccess() {
        return globalDataVuex().getFormAppData.hasVaccinatorSectionAccess;
    }
    get numberOfInjectorLinesToDisplay() {
        return 2;
    }
    get isCoAdmin() {
        return COADMIN_FLU_COVID_SERVICES_2024.includes(this.serviceTemplate) ||
            COADMIN_FLU_COVID_SERVICES.includes(this.serviceTemplate);
    }
    get myInformationsIndex() {
        for (let i = 1; i <= this.numberOfInjectorLinesToDisplay; i++) {
            if (this.globalData.getFormAppData.isProfileComplete === this.item['immunizationProviderSignature' + i]) {
                return i;
            }
        }
        return null;
    }
    get getVaccineDetailsCategories() {
        let categories = [];
        for (let i = 1; i <= this.maximumVaccineCategoryList; i++) {
            categories.push(this.item[`vaccineDetailsCategory${i}`]);
        }
        return categories;
    }
    created() {
        if (this.status === 'new') {
            let vaccineCategory1 = '';
            if (SHINGLES_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.ZONA;
            }
            else if (FLU_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.FLU;
            }
            else if (COVID_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.COVID;
            }
            else if (DCAT_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.DCAT;
            }
            else if (MPOX_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.MPOX;
            }
            else if (HEPATITIS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.HEPATITIS;
            }
            else if (MENINGITIS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.MENINGITIS;
            }
            else if (PNEUMOCOCCAL_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.PNEUMOCOCCAL;
            }
            else if (RABIES_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.RABIES;
            }
            else if (ROTAVIRUS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.ROTAVIRUS;
            }
            else if (RRO_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.RRO;
            }
            else if (TUBERCULOSIS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.TUBERCULOSIS;
            }
            else if (VPH_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.VPH;
            }
            else if (VRS_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.VRS;
            }
            else if (VRS_BABY_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.VRS_BABY;
            }
            else if (TYPHOID_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.TYPHOID;
            }
            else if (TRAVEL_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.TRAVEL;
            }
            else if (AVIAN_INFLUENZA_SERVICES.includes(this.serviceTemplate)) {
                vaccineCategory1 = VaccineCategoryIdsEnum.AVIAN_INFLUENZA;
            }
            else if (this.isCoAdmin) {
                vaccineCategory1 = VaccineCategoryIdsEnum.COVID;
                this.item.vaccineDetailsCategory2 = VaccineCategoryIdsEnum.FLU;
                this.populateWithPrefillFields(VaccineCategoryIdsEnum.FLU, 2);
                if (!this.item.vaccineDetailsTime2) {
                    this.item.vaccineDetailsTime2 = this.item.vaccineDetailsTime1;
                }
                if (!this.item.vaccineDetailsDate2) {
                    this.item.vaccineDetailsDate2 = this.item.vaccineDetailsDate1;
                }
            }
            // prefill the first row with the prefill fields data
            this.item.vaccineDetailsCategory1 = vaccineCategory1;
            this.populateWithPrefillFields(vaccineCategory1, 1);
        }
        this.vaccineCategoryList = [{ id: null, name: '' }];
        for (const value in VaccineCategoryIdsEnum) {
            this.vaccineCategoryList.push({
                id: lodashExports.toLower(value), name: this.i18n.vaccineCategories[lodashExports.toLower(value)],
            });
        }
        const { withdrawExcludedVaccineCategory } = useVaccineDetails(this.categoryExclusionList);
        this.vaccineCategoryList = withdrawExcludedVaccineCategory(this.vaccineCategoryList, this.getVaccineDetailsCategories);
        this.immunizationProviderProfessionList = [];
        for (const value in ProfessionEnum) {
            this.immunizationProviderProfessionList.push({
                id: lodashExports.toLower(value), name: this.i18n.professionList[lodashExports.toLower(value)],
            });
        }
        itemBuffer = deepCopy(this.item);
    }
    onItemUpdate(newElement) {
        if (lodashExports.isEqual(itemBuffer, newElement) || this.isAllowedModificationWithoutClearingMyInfo(getObjectDiff(newElement, itemBuffer))) {
            return;
        }
        this.clearAllMyVaccinatorInfo();
        itemBuffer = deepCopy(newElement);
    }
    isAllowedModificationWithoutClearingMyInfo(modifiedElements) {
        return !lodashExports.find(fields, f => {
            let elementFound = false;
            for (let i = 1; i < this.numberOfLineToDisplay; i++) {
                elementFound = elementFound ? elementFound : lodashExports.indexOf(modifiedElements, f + i) !== -1;
            }
            return elementFound;
        });
    }
    insertMyVaccinatorInfo(id) {
        this.globalData.emitEvent({ event: 'insert-my-vaccinator-info', data: { id } });
    }
    openResourceForm() {
        this.globalData.emitEvent({ event: 'open-resource-form', data: true });
    }
    /**
     * - If user select a vaccine category then field become required.
     **/
    selectVaccineCategoryRules(index) {
        return !this.shouldCheckValidation || !this.item[`vaccineDetailsCategory${index}`] || !this.appliedRequiredField || this.validateRequiredField;
    }
    /**
     * - If user select a vaccine category and entered batch numbers or dose then field become required.
     *
     * See « Hypothèses » in this ticket https://gitlab.com/groups/trimoz/development/teams/-/epics/1165.
     * - H2. Patient refuse injection if « No de lot et Quantité » are empty.
     **/
    patientRefuseInjectionRules(index) {
        return !this.item[`vaccineDetailsCategory${index}`] || (!this.item[`vaccineDetailsBatchNumber${index}`] && !this.item[`vaccineDetailsDose${index}`]);
    }
    /**
     * - If user select a vaccine category, a date, a time, a vaccine name, a batch number and a dose then field become required.
     *
     * See « Conditions d'acception » in this ticket https://gitlab.com/groups/trimoz/development/teams/-/epics/1165.
     * - CoS2. « Site d'administration » fields required if « Date, Heure, Nom du produit, No de lot et Quantité » have values.
     * - CoS3. « initiales » follow the same rule than « CoS2 ».
     *
     * Note:
     * If « Date, Heure, Nom du produit, No de lot et Quantité » fields have values, it means that the patient accepted an injection.
     *
     **/
    patientAcceptInjectionRules(index) {
        return !(this.item[`vaccineDetailsCategory${index}`] && this.item[`vaccineDetailsDate${index}`] && this.item[`vaccineDetailsTime${index}`] && this.item[`vaccineDetailsName${index}`] && this.item[`vaccineDetailsBatchNumber${index}`] && this.item[`vaccineDetailsDose${index}`]) || !this.appliedRequiredField || this.validateRequiredField;
    }
    hasDataInRow(index) {
        let result = false;
        fields.forEach(field => {
            if (this.item[`${field}${index}`]) {
                result = true;
            }
        });
        return result;
    }
    isMyInformations(index) {
        return this.globalData.getFormAppData.isProfileComplete === this.item['immunizationProviderSignature' + index];
    }
    clearAllMyVaccinatorInfo() {
        let i = this.myInformationsIndex;
        while (i !== null) {
            this.clearMyVaccinatorInfo(i);
            i = this.myInformationsIndex;
        }
    }
    clearMyVaccinatorInfo(index) {
        this.item[`immunizationProviderSignature${index}`] = '';
        this.item[`immunizationProviderInitials${index}`] = '';
        this.item[`immunizationProviderName${index}`] = '';
        this.item[`immunizationProviderProfession${index}`] = null;
        this.item[`immunizationProviderLicenceNumber${index}`] = '';
    }
    populateWithPrefillFields(value, index) {
        for (let i = 1; i <= this.maximumVaccineCategoryList; i++) {
            if (this.prefillFields[`vaccineDetailsCategory${i}`] === value) {
                fields.forEach(field => {
                    if (this.prefillFields[`${field}${i}`] && !this.item[`${field}${index}`]) {
                        this.item[`${field}${index}`] = this.prefillFields[`${field}${i}`];
                    }
                });
            }
        }
        if (lodashExports.isEmpty(value)) {
            fields.forEach(field => {
                this.item[`${field}${index}`] = null;
            });
        }
    }
    setVaccineTypeList(index) {
        const result = [];
        const vaccineCategory = this.item[`vaccineDetailsCategory${index}`];
        if (vaccineCategory) {
            // @ts-ignore
            Object.entries(as6242024French.as6242024.vaccineDetails[vaccineCategory]).forEach(([key]) => result.push({ id: key, name: `as6242024.vaccineDetails.${vaccineCategory}.${key}` }));
            // Add old vaccine if the form is using it (meaning it's an old form)
            const selectedVaccine = this.item[`vaccineDetailsName${index}`];
            if (selectedVaccine) {
                // @ts-ignore
                const oldVaccineTypes = Object.entries(as6242024French.as6242024.oldVaccineDetails[vaccineCategory] || []).map(([key]) => key);
                const selectedOldVaccine = oldVaccineTypes.find((vaccine) => vaccine === selectedVaccine);
                if (selectedOldVaccine) {
                    result.unshift({
                        id: selectedOldVaccine,
                        name: `as6242024.oldVaccineDetails.${vaccineCategory}.${selectedOldVaccine}`,
                    });
                }
            }
        }
        return result;
    }
    validateRequiredField(v) {
        return requiredField$2(v) || this.$i18n.t('as6242024.base.errors.requiredField');
    }
    validateRequiredNumberField(v) {
        return requiredNumberField(v) || this.$i18n.t('as6242024.base.errors.requiredNumberField');
    }
    validateDate(v) {
        return !v || isDate$1(v) || this.$i18n.t('as6242024.base.errors.invalidDate');
    }
};
__decorate([
    Prop()
], VaccineDetails$1.prototype, "data", void 0);
__decorate([
    Prop()
], VaccineDetails$1.prototype, "serviceTemplate", void 0);
__decorate([
    Prop()
], VaccineDetails$1.prototype, "status", void 0);
__decorate([
    Prop()
], VaccineDetails$1.prototype, "shouldCheckValidation", void 0);
__decorate([
    Prop()
], VaccineDetails$1.prototype, "numberOfLineToDisplay", void 0);
__decorate([
    Prop()
], VaccineDetails$1.prototype, "categoryExclusionList", void 0);
__decorate([
    PropSync('data', { type: Object })
], VaccineDetails$1.prototype, "item", void 0);
__decorate([
    Watch('item', { deep: true })
], VaccineDetails$1.prototype, "onItemUpdate", null);
VaccineDetails$1 = __decorate([
    Component({
        mixins: [FormRules],
        components: {
            VaccineCategoryPrefillInput,
        },
    })
], VaccineDetails$1);
var script$i = VaccineDetails$1;

/* script */
            const __vue_script__$i = script$i;
            
/* template */
var __vue_render__$i = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { staticClass: "border-all", attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { staticClass: "section-title", attrs: { xs12: "" } },
                [_c("span", [_vm._v(" " + _vm._s(_vm.i18n.title) + " ")])]
              ),
              _vm._v(" "),
              _c("v-flex", { attrs: { xs12: "" } }, [
                _c(
                  "div",
                  { attrs: { id: "vaccine-details-as624" } },
                  [
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-category-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.category) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-date-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.date) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-time-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.hour) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-name-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.name) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-batch-number-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.batchNumber) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-dose-header px-1 body-1 border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.dose) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-administration-header subheading justify-center border-right border-bottom font-weight-bold"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administration) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-injection-header px-1 subheading justify-center border-bottom border-right font-weight-bold"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSite) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-intramuscular-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.intramuscular) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-subcutaneous-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.subcutaneous) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-oral-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.oral) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-intranasal-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.intranasal) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-intradermic-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.administrationType.intradermic) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-leftArm-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.leftArm) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-rightArm-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.rightArm) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-leftThigh-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.leftThigh) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-rightThigh-header px-1 caption justify-center text-xs-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.rightThigh) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-other-header px-1 caption justify-center border-right border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.injectionSiteType.other) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "header vaccine-initials-header px-1 body-1 border-bottom"
                      },
                      [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.i18n.initials) +
                            "\n          "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _vm._l(_vm.numberOfLineToDisplay, function(index) {
                      return [
                        _c(
                          "div",
                          {
                            key: "vaccine-category-value" + index,
                            staticClass:
                              "vaccine-category-value px-2 py-1 border-right border-bottom",
                            attrs: { "data-testid": "vaccine-category-inputs" }
                          },
                          [
                            _c("vaccine-category-prefill-input", {
                              attrs: {
                                index: index,
                                item: _vm.item,
                                ariaLabel: _vm.i18n.category,
                                value:
                                  _vm.item["vaccineDetailsCategory" + index],
                                vaccineCategoryList: _vm.vaccineCategoryList,
                                appliedRequiredField: _vm.appliedRequiredField,
                                haveUserAddedVaccineInformation:
                                  _vm.informationExistInVaccineRows[index],
                                disabled: !_vm.hasDetailSectionVaccineTypeAccess
                              },
                              on: {
                                change: function($event) {
                                  return _vm.populateWithPrefillFields(
                                    $event,
                                    index
                                  )
                                }
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-date-value" + index,
                            staticClass:
                              "vaccine-date-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": _vm.i18n.date,
                                placeholder: _vm.i18n.placeholder.date,
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "10",
                                mask: "####-##-##",
                                "return-masked-value": "",
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.selectVaccineCategoryRules(index)
                                ]
                              },
                              model: {
                                value: _vm.item["vaccineDetailsDate" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsDate" + index,
                                    $$v
                                  );
                                },
                                expression: "item[`vaccineDetailsDate${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-time-value" + index,
                            staticClass:
                              " vaccine-time-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": _vm.i18n.hour,
                                disabled: !_vm.hasDetailSectionAccess,
                                mask: "##:##",
                                "return-masked-value": "",
                                box: "",
                                maxlength: "10",
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.selectVaccineCategoryRules(index)
                                ]
                              },
                              model: {
                                value: _vm.item["vaccineDetailsTime" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsTime" + index,
                                    $$v
                                  );
                                },
                                expression: "item[`vaccineDetailsTime${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-name-value" + index,
                            staticClass:
                              "vaccine-name-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-autocomplete", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": _vm.i18n.name,
                                dense: "",
                                items: _vm.setVaccineTypeList(index),
                                "item-text": function(i) {
                                  return _vm.$i18n.t(i.name)
                                },
                                "item-value": "id",
                                disabled: !_vm.hasDetailSectionVaccineTypeAccess,
                                box: "",
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.selectVaccineCategoryRules(index)
                                ],
                                "test-value":
                                  _vm.item["vaccineDetailsName" + index]
                              },
                              model: {
                                value: _vm.item["vaccineDetailsName" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsName" + index,
                                    $$v
                                  );
                                },
                                expression: "item[`vaccineDetailsName${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-batch-number" + index,
                            staticClass:
                              "vaccine-batch-number-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "aria-label": _vm.i18n.batchNumber,
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "20",
                                rules:
                                  [
                                    function(v) {
                                      return (
                                        !_vm.hasDetailSectionAccess ||
                                        _vm.patientRefuseInjectionRules(
                                          index
                                        ) ||
                                        _vm.validateRequiredField(v)
                                      )
                                    }
                                  ] || !_vm.appliedRequiredField
                              },
                              model: {
                                value:
                                  _vm.item["vaccineDetailsBatchNumber" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsBatchNumber" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsBatchNumber${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-dose-value" + index,
                            staticClass:
                              "vaccine-dose-value px-2 py-1 border-right border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "aria-label": _vm.i18n.dose,
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "20",
                                rules: [
                                  function(v) {
                                    return (
                                      !_vm.hasDetailSectionAccess ||
                                      _vm.patientRefuseInjectionRules(index)
                                    )
                                  }
                                ]
                              },
                              model: {
                                value: _vm.item["vaccineDetailsDose" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsDose" + index,
                                    $$v
                                  );
                                },
                                expression: "item[`vaccineDetailsDose${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-intramuscular-value" + index,
                            staticClass:
                              "vaccine-intramuscular-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                disabled: !_vm.hasDetailSectionAccess,
                                "aria-label":
                                  _vm.i18n.administrationType.intramuscular,
                                value: _vm.AdministrationEnum.INTRAMUSCULAR,
                                "hide-details": "",
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-subcutaneous-value" + index,
                            staticClass:
                              "vaccine-subcutaneous-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                disabled: !_vm.hasDetailSectionAccess,
                                value: _vm.AdministrationEnum.SUBCUTANEOUS,
                                "aria-label":
                                  _vm.i18n.administrationType.subcutaneous,
                                "hide-details": "",
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-oral-value" + index,
                            staticClass:
                              "vaccine-oral-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                disabled: !_vm.hasDetailSectionAccess,
                                value: _vm.AdministrationEnum.ORAL,
                                "aria-label": _vm.i18n.administrationType.oral,
                                "hide-details": "",
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-intranasal-value" + index,
                            staticClass:
                              "vaccine-intranasal-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                disabled: !_vm.hasDetailSectionAccess,
                                value: _vm.AdministrationEnum.INTRANASAL,
                                "aria-label":
                                  _vm.i18n.administrationType.intranasal,
                                "hide-details": "",
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-intradermic-value" + index,
                            staticClass:
                              "vaccine-intradermic-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                disabled: !_vm.hasDetailSectionAccess,
                                value: _vm.AdministrationEnum.INTRADERMIC,
                                "aria-label":
                                  _vm.i18n.administrationType.intradermic,
                                "hide-details": "",
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsAdministration" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsAdministration" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsAdministration${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-leftArm-value" + index,
                            staticClass:
                              "vaccine-leftArm-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.InjectionSiteEnum.LEFT_ARM,
                                "aria-label":
                                  _vm.i18n.injectionSiteType.leftArm,
                                "hide-details": "",
                                disabled:
                                  !_vm.hasDetailSectionAccess ||
                                  _vm.otherAdministrationSiteEntered(index),
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.otherAdministrationSiteEntered(index) ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsInjectionSite" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInjectionSite" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInjectionSite${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-rightArm-value" + index,
                            staticClass:
                              "vaccine-rightArm-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.InjectionSiteEnum.RIGHT_ARM,
                                "aria-label":
                                  _vm.i18n.injectionSiteType.rightArm,
                                "hide-details": "",
                                disabled:
                                  !_vm.hasDetailSectionAccess ||
                                  _vm.otherAdministrationSiteEntered(index),
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.otherAdministrationSiteEntered(index) ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsInjectionSite" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInjectionSite" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInjectionSite${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-leftThigh-value" + index,
                            staticClass:
                              "vaccine-leftThigh-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.InjectionSiteEnum.LEFT_THIGH,
                                "aria-label":
                                  _vm.i18n.injectionSiteType.leftThigh,
                                "hide-details": "",
                                disabled:
                                  !_vm.hasDetailSectionAccess ||
                                  _vm.otherAdministrationSiteEntered(index),
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.otherAdministrationSiteEntered(index) ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsInjectionSite" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInjectionSite" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInjectionSite${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-rightThigh-value" + index,
                            staticClass:
                              "vaccine-rightThigh-value px-2 pt-4 border-right border-bottom"
                          },
                          [
                            _c("v-checkbox", {
                              attrs: {
                                value: _vm.InjectionSiteEnum.RIGHT_THIGH,
                                "aria-label":
                                  _vm.i18n.injectionSiteType.rightThigh,
                                "hide-details": "",
                                disabled:
                                  !_vm.hasDetailSectionAccess ||
                                  _vm.otherAdministrationSiteEntered(index),
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.otherAdministrationSiteEntered(index) ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item[
                                    "vaccineDetailsInjectionSite" + index
                                  ],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInjectionSite" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInjectionSite${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-other-value" + index,
                            staticClass:
                              "vaccine-other-value px-2 py-1 border-bottom border-right"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label":
                                  _vm.i18n.injectionSiteType.otherAriaLabel,
                                box: "",
                                maxlength: "24",
                                disabled:
                                  !_vm.hasDetailSectionAccess ||
                                  _vm.administrationSiteEntered(index),
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.administrationSiteEntered(index) ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value: _vm.item["vaccineDetailsOther" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsOther" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsOther${index}`]"
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "vaccine-initials-value" + index,
                            staticClass:
                              "vaccine-initials-value px-2 py-1 border-bottom"
                          },
                          [
                            _c("v-text-field", {
                              attrs: {
                                "hide-details": "",
                                "aria-label": _vm.i18n.initials,
                                box: "",
                                maxlength: "8",
                                disabled: !_vm.hasDetailSectionAccess,
                                rules: [
                                  !_vm.hasDetailSectionAccess ||
                                    _vm.patientAcceptInjectionRules(index)
                                ]
                              },
                              model: {
                                value:
                                  _vm.item["vaccineDetailsInitials" + index],
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "vaccineDetailsInitials" + index,
                                    $$v
                                  );
                                },
                                expression:
                                  "item[`vaccineDetailsInitials${index}`]"
                              }
                            })
                          ],
                          1
                        )
                      ]
                    }),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "vaccine-place px-2 py-3 subheading border-bottom"
                      },
                      [
                        _c(
                          "v-flex",
                          [
                            _c("v-text-field", {
                              attrs: {
                                label: _vm.i18n.vaccinePlace,
                                "hide-details": "",
                                disabled: !_vm.hasDetailSectionAccess,
                                box: "",
                                maxlength: "50"
                              },
                              model: {
                                value: _vm.item.immunizationProviderSite,
                                callback: function($$v) {
                                  _vm.$set(
                                    _vm.item,
                                    "immunizationProviderSite",
                                    $$v
                                  );
                                },
                                expression: "item.immunizationProviderSite"
                              }
                            })
                          ],
                          1
                        )
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "injector-section px-2 border-bottom font-weight-bold grey lighten-2 "
                      },
                      [
                        _c(
                          "v-layout",
                          { attrs: { row: "" } },
                          [
                            _c("v-flex", { staticClass: "py-2" }, [
                              _vm._v(
                                _vm._s(_vm.i18n.professionInformation.title)
                              )
                            ]),
                            _vm._v(" "),
                            _vm.showInjectorBtn
                              ? _c(
                                  "v-flex",
                                  { staticClass: "md4 xs12 text-xs-right" },
                                  [
                                    _vm._l(
                                      _vm.numberOfInjectorLinesToDisplay,
                                      function(index) {
                                        return [
                                          _c(
                                            "span",
                                            {
                                              key:
                                                "immunization-provider-btn" +
                                                index
                                            },
                                            [
                                              !_vm.item[
                                                "immunizationProviderSignature" +
                                                  index
                                              ]
                                                ? [
                                                    _vm.isProfileComplete
                                                      ? _c(
                                                          "v-btn",
                                                          {
                                                            staticClass:
                                                              "primary",
                                                            attrs: {
                                                              "aria-label":
                                                                "insert-evaluator-info",
                                                              dark: "",
                                                              flat: "",
                                                              small: "",
                                                              disabled:
                                                                _vm.lockForm
                                                            },
                                                            on: {
                                                              click: function() {
                                                                _vm.insertMyVaccinatorInfo(
                                                                  index
                                                                );
                                                              }
                                                            }
                                                          },
                                                          [
                                                            _vm._v(
                                                              "\n                        " +
                                                                _vm._s(
                                                                  _vm.i18n
                                                                    .getMyInformations +
                                                                    "#" +
                                                                    index
                                                                ) +
                                                                "\n                      "
                                                            )
                                                          ]
                                                        )
                                                      : _c(
                                                          "v-btn",
                                                          {
                                                            staticClass:
                                                              "primary",
                                                            attrs: {
                                                              "aria-label":
                                                                "complete-profile",
                                                              disabled:
                                                                _vm.lockForm,
                                                              dark: "",
                                                              flat: "",
                                                              small: ""
                                                            },
                                                            on: {
                                                              click:
                                                                _vm.openResourceForm
                                                            }
                                                          },
                                                          [
                                                            _vm._v(
                                                              "\n                        " +
                                                                _vm._s(
                                                                  _vm.i18n
                                                                    .completeMyProfile
                                                                ) +
                                                                "\n                      "
                                                            )
                                                          ]
                                                        )
                                                  ]
                                                : [
                                                    _vm.isMyInformations(index)
                                                      ? _c(
                                                          "v-btn",
                                                          {
                                                            staticClass:
                                                              "primary",
                                                            attrs: {
                                                              disabled:
                                                                _vm.lockForm,
                                                              "aria-label":
                                                                "insert-evaluator-info",
                                                              dark: "",
                                                              flat: "",
                                                              small: ""
                                                            },
                                                            on: {
                                                              click: function() {
                                                                _vm.clearMyVaccinatorInfo(
                                                                  index
                                                                );
                                                              }
                                                            }
                                                          },
                                                          [
                                                            _vm._v(
                                                              "\n                        " +
                                                                _vm._s(
                                                                  _vm.i18n
                                                                    .clearMyInformations +
                                                                    "#" +
                                                                    index
                                                                ) +
                                                                "\n                      "
                                                            )
                                                          ]
                                                        )
                                                      : _vm._e()
                                                  ]
                                            ],
                                            2
                                          )
                                        ]
                                      }
                                    )
                                  ],
                                  2
                                )
                              : _vm._e()
                          ],
                          1
                        )
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _vm._l(_vm.numberOfInjectorLinesToDisplay, function(index) {
                      return [
                        _c(
                          "div",
                          {
                            key: "immunization-provider-name" + index,
                            staticClass:
                              "injector-name px-2 py-3 border-right border-bottom"
                          },
                          [
                            _c(
                              "v-flex",
                              { staticClass: "shrink mr-3 align-self-center" },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(
                                      _vm.i18n.professionInformation.name
                                    ) +
                                    "\n              "
                                )
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "v-flex",
                              [
                                _c("v-text-field", {
                                  attrs: {
                                    "hide-details": "",
                                    "aria-label": "immunization-provider-name",
                                    disabled: !_vm.hasDetailSectionAccess,
                                    box: "",
                                    maxlength: "50",
                                    rules: [
                                      !(
                                        _vm.appliedRequiredField && index == 1
                                      ) ||
                                        !_vm.hasVaccinatorSectionAccess ||
                                        _vm.validateRequiredField
                                    ]
                                  },
                                  model: {
                                    value:
                                      _vm.item[
                                        "immunizationProviderName" + index
                                      ],
                                    callback: function($$v) {
                                      _vm.$set(
                                        _vm.item,
                                        "immunizationProviderName" + index,
                                        $$v
                                      );
                                    },
                                    expression:
                                      "item[`immunizationProviderName${index}`]"
                                  }
                                })
                              ],
                              1
                            )
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "immunization-provider-initials" + index,
                            staticClass:
                              "injector-initials px-2 py-3 border-right border-bottom"
                          },
                          [
                            _c(
                              "v-flex",
                              { staticClass: "shrink mr-3 align-self-center" },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(
                                      _vm.i18n.professionInformation.initials
                                    ) +
                                    "\n              "
                                )
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "v-flex",
                              [
                                _c("v-text-field", {
                                  attrs: {
                                    "hide-details": "",
                                    "aria-label":
                                      "immunization-provider-initials",
                                    disabled: !_vm.hasDetailSectionAccess,
                                    box: "",
                                    maxlength: "8",
                                    rules: [
                                      !(
                                        _vm.appliedRequiredField && index == 1
                                      ) ||
                                        !_vm.hasVaccinatorSectionAccess ||
                                        _vm.validateRequiredField
                                    ]
                                  },
                                  model: {
                                    value:
                                      _vm.item[
                                        "immunizationProviderInitials" + index
                                      ],
                                    callback: function($$v) {
                                      _vm.$set(
                                        _vm.item,
                                        "immunizationProviderInitials" + index,
                                        $$v
                                      );
                                    },
                                    expression:
                                      "item[`immunizationProviderInitials${index}`]"
                                  }
                                })
                              ],
                              1
                            )
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "immunization-provider-signature" + index,
                            staticClass:
                              "injector-signature px-2 py-3 border-right border-bottom"
                          },
                          [
                            !_vm.item["immunizationProviderSignature" + index]
                              ? _c(
                                  "v-flex",
                                  [
                                    _c("v-text-field", {
                                      staticClass: "imm-provider-signature",
                                      attrs: {
                                        "hide-details": "",
                                        "aria-label":
                                          "immunization-provider-signature",
                                        label:
                                          _vm.i18n.professionInformation
                                            .signature,
                                        disabled: "",
                                        box: "",
                                        rules: [
                                          !(
                                            _vm.appliedRequiredField &&
                                            index == 1
                                          ) ||
                                            !_vm.hasVaccinatorSectionAccess ||
                                            _vm.validateRequiredField
                                        ]
                                      }
                                    })
                                  ],
                                  1
                                )
                              : [
                                  _c(
                                    "v-flex",
                                    {
                                      staticClass:
                                        "shrink mr-3 align-self-center"
                                    },
                                    [
                                      _vm._v(
                                        "\n                  " +
                                          _vm._s(
                                            _vm.i18n.professionInformation
                                              .signature
                                          ) +
                                          "\n                "
                                      )
                                    ]
                                  ),
                                  _vm._v(" "),
                                  _c(
                                    "v-flex",
                                    {
                                      staticClass:
                                        "align-self-center imm-provider-img"
                                    },
                                    [
                                      _c("img", {
                                        attrs: {
                                          src:
                                            _vm.item[
                                              "immunizationProviderSignature" +
                                                index
                                            ],
                                          alt: "Signature"
                                        }
                                      })
                                    ]
                                  )
                                ]
                          ],
                          2
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "immunization-provider-profession" + index,
                            staticClass:
                              "injector-profession px-2 py-3 border-right border-bottom"
                          },
                          [
                            _c(
                              "v-flex",
                              { staticClass: "shrink mr-3 align-self-center" },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(
                                      _vm.i18n.professionInformation.profession
                                    ) +
                                    "\n              "
                                )
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "v-flex",
                              [
                                _c("v-autocomplete", {
                                  attrs: {
                                    "hide-details": "",
                                    "aria-label":
                                      "immunization-provider-profession",
                                    disabled: !_vm.hasDetailSectionAccess,
                                    dense: "",
                                    items:
                                      _vm.immunizationProviderProfessionList,
                                    "item-text": "name",
                                    "item-value": "id",
                                    box: "",
                                    rules: [
                                      !(
                                        _vm.appliedRequiredField && index == 1
                                      ) ||
                                        !_vm.hasVaccinatorSectionAccess ||
                                        _vm.validateRequiredField
                                    ]
                                  },
                                  model: {
                                    value:
                                      _vm.item[
                                        "immunizationProviderProfession" + index
                                      ],
                                    callback: function($$v) {
                                      _vm.$set(
                                        _vm.item,
                                        "immunizationProviderProfession" +
                                          index,
                                        $$v
                                      );
                                    },
                                    expression:
                                      "item[`immunizationProviderProfession${index}`]"
                                  }
                                })
                              ],
                              1
                            )
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          {
                            key: "immunization-provider-licence-number" + index,
                            staticClass:
                              "injector-licence px-2 py-3 border-bottom"
                          },
                          [
                            _c(
                              "v-flex",
                              { staticClass: "shrink mr-3 align-self-center" },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(
                                      _vm.i18n.professionInformation
                                        .licenceNumber
                                    ) +
                                    "\n              "
                                )
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "v-flex",
                              [
                                _c("v-text-field", {
                                  attrs: {
                                    "hide-details": "",
                                    "aria-label":
                                      "immunization-provider-licence-number",
                                    disabled: !_vm.hasDetailSectionAccess,
                                    box: "",
                                    maxlength: "25",
                                    rules: [
                                      !(
                                        _vm.appliedRequiredField && index == 1
                                      ) ||
                                        !_vm.hasVaccinatorSectionAccess ||
                                        _vm.validateRequiredField
                                    ]
                                  },
                                  model: {
                                    value:
                                      _vm.item[
                                        "immunizationProviderLicenceNumber" +
                                          index
                                      ],
                                    callback: function($$v) {
                                      _vm.$set(
                                        _vm.item,
                                        "immunizationProviderLicenceNumber" +
                                          index,
                                        $$v
                                      );
                                    },
                                    expression:
                                      "item[`immunizationProviderLicenceNumber${index}`]"
                                  }
                                })
                              ],
                              1
                            )
                          ],
                          1
                        )
                      ]
                    })
                  ],
                  2
                )
              ])
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$i = [];
__vue_render__$i._withStripped = true;

  /* style */
  const __vue_inject_styles__$i = function (inject) {
    if (!inject) return
    inject("data-v-62232bea_0", { source: "#vaccine-details-as624[data-v-62232bea] {\n  display: grid;\n  grid-template-columns: 11% 7% 7% 11% 7% 7% repeat(9, 4%) 8% 6%;\n  grid-template-rows: 50px auto;\n}\n#vaccine-details-as624 .header[data-v-62232bea] {\n    display: flex;\n    align-items: center;\n}\n#vaccine-details-as624 .vaccine-category-header[data-v-62232bea] {\n    grid-area: 1 / 1 / 3 / 2;\n}\n#vaccine-details-as624 .vaccine-date-header[data-v-62232bea] {\n    grid-area: 1 / 2 / 3 / 3;\n}\n#vaccine-details-as624 .vaccine-time-header[data-v-62232bea] {\n    grid-area: 1 / 3 / 3 / 4;\n}\n#vaccine-details-as624 .vaccine-name-header[data-v-62232bea] {\n    grid-area: 1 / 4 / 3 / 5;\n}\n#vaccine-details-as624 .vaccine-batch-number-header[data-v-62232bea] {\n    grid-area: 1 / 5 / 3 / 6;\n}\n#vaccine-details-as624 .vaccine-dose-header[data-v-62232bea] {\n    grid-area: 1 / 6 / 3 / 7;\n}\n#vaccine-details-as624 .vaccine-administration-header[data-v-62232bea] {\n    grid-area: 1 / 7 / 2 / 12;\n    border-right-width: 3px !important;\n}\n#vaccine-details-as624 .vaccine-injection-header[data-v-62232bea] {\n    grid-area: 1 / 12 / 2 / 17;\n}\n#vaccine-details-as624 .vaccine-initials-header[data-v-62232bea] {\n    grid-area: 1 / 17 / 3 / 18;\n}\n#vaccine-details-as624 .vaccine-intramuscular-header[data-v-62232bea] {\n    grid-area: 2 / 7 / 3 / 8;\n}\n#vaccine-details-as624 .vaccine-subcutaneous-header[data-v-62232bea] {\n    grid-area: 2 / 8 / 3 / 9;\n}\n#vaccine-details-as624 .vaccine-oral-header[data-v-62232bea] {\n    grid-area: 2 / 9 / 3 / 10;\n}\n#vaccine-details-as624 .vaccine-intranasal-header[data-v-62232bea] {\n    grid-area: 2 / 10 / 3 / 11;\n}\n#vaccine-details-as624 .vaccine-intradermic-header[data-v-62232bea] {\n    grid-area: 2 / 11 / 3 / 12;\n    border-right-width: 3px !important;\n}\n#vaccine-details-as624 .vaccine-leftArm-header[data-v-62232bea] {\n    grid-area: 2 / 12 / 3 / 13;\n}\n#vaccine-details-as624 .vaccine-rightArm-header[data-v-62232bea] {\n    grid-area: 2 / 13 / 3 / 14;\n}\n#vaccine-details-as624 .vaccine-leftThigh-header[data-v-62232bea] {\n    grid-area: 2 / 14 / 3 / 15;\n}\n#vaccine-details-as624 .vaccine-rightThigh-header[data-v-62232bea] {\n    grid-area: 2 / 15 / 3 / 16;\n}\n#vaccine-details-as624 .vaccine-other-header[data-v-62232bea] {\n    grid-area: 2 / 16 / 3 / 17;\n}\n#vaccine-details-as624 .vaccine-intramuscular-value[data-v-62232bea] {\n    grid-area: auto / 7 / auto / 8;\n}\n#vaccine-details-as624 .vaccine-subcutaneous-value[data-v-62232bea] {\n    grid-area: auto / 8 / auto / 9;\n}\n#vaccine-details-as624 .vaccine-oral-value[data-v-62232bea] {\n    grid-area: auto / 9 / auto / 10;\n}\n#vaccine-details-as624 .vaccine-intranasal-value[data-v-62232bea] {\n    grid-area: auto / 10 / auto / 11;\n}\n#vaccine-details-as624 .vaccine-intradermic-value[data-v-62232bea] {\n    grid-area: auto / 11 / auto / 12;\n    border-right-width: 3px !important;\n}\n#vaccine-details-as624 .vaccine-leftArm-value[data-v-62232bea] {\n    grid-area: auto / 12 / auto / 13;\n}\n#vaccine-details-as624 .vaccine-rightArm-value[data-v-62232bea] {\n    grid-area: auto / 13 / auto / 14;\n}\n#vaccine-details-as624 .vaccine-leftThigh-value[data-v-62232bea] {\n    grid-area: auto / 14 / auto / 15;\n}\n#vaccine-details-as624 .vaccine-rightThigh-value[data-v-62232bea] {\n    grid-area: auto / 15 / auto / 16;\n}\n#vaccine-details-as624 .vaccine-other-value[data-v-62232bea] {\n    grid-area: auto / 16 / auto / 17;\n}\n#vaccine-details-as624 .vaccine-initials-value[data-v-62232bea] {\n    grid-area: auto / 17 / auto / 18;\n}\n#vaccine-details-as624 .vaccine-place[data-v-62232bea] {\n    grid-area: auto / 1 / auto / 13;\n}\n#vaccine-details-as624 .vaccine-siPmi[data-v-62232bea] {\n    grid-area: auto / 13 / auto / 18;\n}\n#vaccine-details-as624 .injector-section[data-v-62232bea] {\n    grid-area: auto / 1 / auto / 18;\n}\n#vaccine-details-as624 .injector-name[data-v-62232bea] {\n    grid-area: auto / 1 / auto / 4;\n}\n#vaccine-details-as624 .injector-initials[data-v-62232bea] {\n    grid-area: auto / 4 / auto / 5;\n}\n#vaccine-details-as624 .injector-signature[data-v-62232bea] {\n    grid-area: auto / 5 / auto / 10;\n}\n#vaccine-details-as624 .injector-profession[data-v-62232bea] {\n    grid-area: auto / 10 / auto / 14;\n}\n#vaccine-details-as624 .injector-licence[data-v-62232bea] {\n    grid-area: auto / 14 / auto / 18;\n}\n.imm-provider-signature[data-v-62232bea] {\n  position: relative;\n  top: 24px;\n}\n.imm-provider-signature[data-v-62232bea] .v-label {\n    top: -22px;\n}\n.imm-provider-img[data-v-62232bea] {\n  text-align: center;\n  top: 15px;\n  position: relative;\n}\n\n/*# sourceMappingURL=VaccineDetails.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/VaccineDetails/VaccineDetails.vue","VaccineDetails.vue"],"names":[],"mappings":"AAgvBA;EACA,aAAA;EACA,8DAAA;EACA,6BAAA;AAAA;AAHA;IAMA,aAAA;IACA,mBAAA;AAAA;AAPA;IAWA,wBAAA;AAAA;AAXA;IAeA,wBAAA;AAAA;AAfA;IAmBA,wBAAA;AAAA;AAnBA;IAuBA,wBAAA;AAAA;AAvBA;IA2BA,wBAAA;AAAA;AA3BA;IA+BA,wBAAA;AAAA;AA/BA;IAmCA,yBAAA;IACA,kCAAA;AAAA;AApCA;IAwCA,0BAAA;AAAA;AAxCA;IA4CA,0BAAA;AAAA;AA5CA;IAgDA,wBAAA;AAAA;AAhDA;IAoDA,wBAAA;AAAA;AApDA;IAwDA,yBAAA;AAAA;AAxDA;IA4DA,0BAAA;AAAA;AA5DA;IAgEA,0BAAA;IACA,kCAAA;AAAA;AAjEA;IAqEA,0BAAA;AAAA;AArEA;IAyEA,0BAAA;AAAA;AAzEA;IA6EA,0BAAA;AAAA;AA7EA;IAiFA,0BAAA;AAAA;AAjFA;IAqFA,0BAAA;AAAA;AArFA;IAyFA,8BAAA;AAAA;AAzFA;IA6FA,8BAAA;AAAA;AA7FA;IAiGA,+BAAA;AAAA;AAjGA;IAqGA,gCAAA;AAAA;AArGA;IAyGA,gCAAA;IACA,kCAAA;AAAA;AA1GA;IA8GA,gCAAA;AAAA;AA9GA;IAkHA,gCAAA;AAAA;AAlHA;IAsHA,gCAAA;AAAA;AAtHA;IA0HA,gCAAA;AAAA;AA1HA;IA8HA,gCAAA;AAAA;AA9HA;IAkIA,gCAAA;AAAA;AAlIA;IAsIA,+BAAA;AAAA;AAtIA;IA0IA,gCAAA;AAAA;AA1IA;IA8IA,+BAAA;AAAA;AA9IA;IAkJA,8BAAA;AAAA;AAlJA;IAsJA,8BAAA;AAAA;AAtJA;IA0JA,+BAAA;AAAA;AA1JA;IA8JA,gCAAA;AAAA;AA9JA;IAkKA,gCAAA;AAAA;AAIA;EACA,kBAAA;EACA,SAAA;AAAA;AAFA;IAKA,UAAA;AAAA;AAIA;EACA,kBAAA;EACA,SAAA;EACA,kBACA;AAAA;;ACj0BA,6CAA6C","file":"VaccineDetails.vue","sourcesContent":[null,"#vaccine-details-as624 {\n  display: grid;\n  grid-template-columns: 11% 7% 7% 11% 7% 7% repeat(9, 4%) 8% 6%;\n  grid-template-rows: 50px auto; }\n  #vaccine-details-as624 .header {\n    display: flex;\n    align-items: center; }\n  #vaccine-details-as624 .vaccine-category-header {\n    grid-area: 1 / 1 / 3 / 2; }\n  #vaccine-details-as624 .vaccine-date-header {\n    grid-area: 1 / 2 / 3 / 3; }\n  #vaccine-details-as624 .vaccine-time-header {\n    grid-area: 1 / 3 / 3 / 4; }\n  #vaccine-details-as624 .vaccine-name-header {\n    grid-area: 1 / 4 / 3 / 5; }\n  #vaccine-details-as624 .vaccine-batch-number-header {\n    grid-area: 1 / 5 / 3 / 6; }\n  #vaccine-details-as624 .vaccine-dose-header {\n    grid-area: 1 / 6 / 3 / 7; }\n  #vaccine-details-as624 .vaccine-administration-header {\n    grid-area: 1 / 7 / 2 / 12;\n    border-right-width: 3px !important; }\n  #vaccine-details-as624 .vaccine-injection-header {\n    grid-area: 1 / 12 / 2 / 17; }\n  #vaccine-details-as624 .vaccine-initials-header {\n    grid-area: 1 / 17 / 3 / 18; }\n  #vaccine-details-as624 .vaccine-intramuscular-header {\n    grid-area: 2 / 7 / 3 / 8; }\n  #vaccine-details-as624 .vaccine-subcutaneous-header {\n    grid-area: 2 / 8 / 3 / 9; }\n  #vaccine-details-as624 .vaccine-oral-header {\n    grid-area: 2 / 9 / 3 / 10; }\n  #vaccine-details-as624 .vaccine-intranasal-header {\n    grid-area: 2 / 10 / 3 / 11; }\n  #vaccine-details-as624 .vaccine-intradermic-header {\n    grid-area: 2 / 11 / 3 / 12;\n    border-right-width: 3px !important; }\n  #vaccine-details-as624 .vaccine-leftArm-header {\n    grid-area: 2 / 12 / 3 / 13; }\n  #vaccine-details-as624 .vaccine-rightArm-header {\n    grid-area: 2 / 13 / 3 / 14; }\n  #vaccine-details-as624 .vaccine-leftThigh-header {\n    grid-area: 2 / 14 / 3 / 15; }\n  #vaccine-details-as624 .vaccine-rightThigh-header {\n    grid-area: 2 / 15 / 3 / 16; }\n  #vaccine-details-as624 .vaccine-other-header {\n    grid-area: 2 / 16 / 3 / 17; }\n  #vaccine-details-as624 .vaccine-intramuscular-value {\n    grid-area: auto / 7 / auto / 8; }\n  #vaccine-details-as624 .vaccine-subcutaneous-value {\n    grid-area: auto / 8 / auto / 9; }\n  #vaccine-details-as624 .vaccine-oral-value {\n    grid-area: auto / 9 / auto / 10; }\n  #vaccine-details-as624 .vaccine-intranasal-value {\n    grid-area: auto / 10 / auto / 11; }\n  #vaccine-details-as624 .vaccine-intradermic-value {\n    grid-area: auto / 11 / auto / 12;\n    border-right-width: 3px !important; }\n  #vaccine-details-as624 .vaccine-leftArm-value {\n    grid-area: auto / 12 / auto / 13; }\n  #vaccine-details-as624 .vaccine-rightArm-value {\n    grid-area: auto / 13 / auto / 14; }\n  #vaccine-details-as624 .vaccine-leftThigh-value {\n    grid-area: auto / 14 / auto / 15; }\n  #vaccine-details-as624 .vaccine-rightThigh-value {\n    grid-area: auto / 15 / auto / 16; }\n  #vaccine-details-as624 .vaccine-other-value {\n    grid-area: auto / 16 / auto / 17; }\n  #vaccine-details-as624 .vaccine-initials-value {\n    grid-area: auto / 17 / auto / 18; }\n  #vaccine-details-as624 .vaccine-place {\n    grid-area: auto / 1 / auto / 13; }\n  #vaccine-details-as624 .vaccine-siPmi {\n    grid-area: auto / 13 / auto / 18; }\n  #vaccine-details-as624 .injector-section {\n    grid-area: auto / 1 / auto / 18; }\n  #vaccine-details-as624 .injector-name {\n    grid-area: auto / 1 / auto / 4; }\n  #vaccine-details-as624 .injector-initials {\n    grid-area: auto / 4 / auto / 5; }\n  #vaccine-details-as624 .injector-signature {\n    grid-area: auto / 5 / auto / 10; }\n  #vaccine-details-as624 .injector-profession {\n    grid-area: auto / 10 / auto / 14; }\n  #vaccine-details-as624 .injector-licence {\n    grid-area: auto / 14 / auto / 18; }\n\n.imm-provider-signature::v-deep {\n  position: relative;\n  top: 24px; }\n  .imm-provider-signature::v-deep .v-label {\n    top: -22px; }\n\n.imm-provider-img::v-deep {\n  text-align: center;\n  top: 15px;\n  position: relative; }\n\n/*# sourceMappingURL=VaccineDetails.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$i = "data-v-62232bea";
  /* module identifier */
  const __vue_module_identifier__$6 = undefined;
  /* functional template */
  const __vue_is_functional_template__$i = false;
  /* component normalizer */
  function __vue_normalize__$i(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/VaccineDetails/VaccineDetails.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$6() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$6.styles || (__vue_create_injector__$6.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var VaccineDetails = __vue_normalize__$i(
    { render: __vue_render__$i, staticRenderFns: __vue_staticRenderFns__$i },
    __vue_inject_styles__$i,
    __vue_script__$i,
    __vue_scope_id__$i,
    __vue_is_functional_template__$i,
    __vue_module_identifier__$6,
    __vue_create_injector__$6);

/**
 * Element types used for Data Collection.
 */
var FormElementType;
(function (FormElementType) {
    FormElementType[FormElementType["MULTIPLE_CHOICE_CHECK_BOX"] = 1] = "MULTIPLE_CHOICE_CHECK_BOX";
    FormElementType[FormElementType["MULTIPLE_CHOICE_CHECK_BOX_WITH_TEXT"] = 2] = "MULTIPLE_CHOICE_CHECK_BOX_WITH_TEXT";
    FormElementType[FormElementType["SIMPLE_CHOICE_RADIO"] = 3] = "SIMPLE_CHOICE_RADIO";
    FormElementType[FormElementType["YES_NO_BUTTON"] = 4] = "YES_NO_BUTTON";
    FormElementType[FormElementType["FREE_SPACE"] = 5] = "FREE_SPACE";
    FormElementType[FormElementType["YES_NO_BUTTON_WITH_TEXT"] = 6] = "YES_NO_BUTTON_WITH_TEXT";
    FormElementType[FormElementType["SHORT_ANSWER_TEXT"] = 7] = "SHORT_ANSWER_TEXT";
    FormElementType[FormElementType["LONG_ANSWER_TEXT_AREA"] = 8] = "LONG_ANSWER_TEXT_AREA";
    FormElementType[FormElementType["DATE_PAST"] = 9] = "DATE_PAST";
    FormElementType[FormElementType["SECTION"] = 10] = "SECTION";
})(FormElementType || (FormElementType = {}));
/**
 * All form element component names mapped to form element type.
 */
const FormElementComponents = Object.keys(FormElementType).map((k) => {
    /**
     * Object.keys include strangely keys values so we will ignore this.
     */
    if (parseInt(k)) {
        return undefined;
    }
    const currentType = Object.values(FormElementType).indexOf(k) + 1;
    return { type: currentType, component: lodashExports.upperFirst(lodashExports.camelCase(inSentence(lodashExports.lowerCase(k)))) };
}).filter(v => v === null || v === void 0 ? void 0 : v.component);
[
    FormElementType.MULTIPLE_CHOICE_CHECK_BOX,
    FormElementType.MULTIPLE_CHOICE_CHECK_BOX_WITH_TEXT,
];
[
    FormElementType.SIMPLE_CHOICE_RADIO,
    FormElementType.YES_NO_BUTTON_WITH_TEXT,
    FormElementType.YES_NO_BUTTON,
];
[
    FormElementType.MULTIPLE_CHOICE_CHECK_BOX_WITH_TEXT,
    FormElementType.YES_NO_BUTTON_WITH_TEXT,
];
[
    FormElementType.MULTIPLE_CHOICE_CHECK_BOX,
    FormElementType.MULTIPLE_CHOICE_CHECK_BOX_WITH_TEXT,
    FormElementType.SIMPLE_CHOICE_RADIO,
    FormElementType.YES_NO_BUTTON,
    FormElementType.YES_NO_BUTTON_WITH_TEXT,
];
const MAX_NOTES_LENGTH = 875;

let Notes$1 = class Notes extends Vue {
    get i18n() {
        return this.$i18n.t('as6242024.notes');
    }
    get appliedRequiredField() {
        return globalDataVuex().getFormAppData.appliedRequiredField;
    }
    get hasNotesSectionAccess() {
        return globalDataVuex().getFormAppData.hasNotesSectionAccess;
    }
    get MAX_NOTES_LENGTH() { return MAX_NOTES_LENGTH; }
};
__decorate([
    Prop()
], Notes$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], Notes$1.prototype, "item", void 0);
Notes$1 = __decorate([
    Component({
        mixins: [FormRules],
    })
], Notes$1);
var script$h = Notes$1;

/* script */
            const __vue_script__$h = script$h;
            
/* template */
var __vue_render__$h = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { "mt-2": "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-textarea", {
                    attrs: {
                      disabled: !_vm.hasNotesSectionAccess,
                      "hide-details": "",
                      box: "",
                      label: _vm.i18n.notes,
                      maxlength: _vm.MAX_NOTES_LENGTH
                    },
                    model: {
                      value: _vm.item.notes,
                      callback: function($$v) {
                        _vm.$set(_vm.item, "notes", $$v);
                      },
                      expression: "item.notes"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { staticClass: "font-weight-medium", attrs: { xs12: "" } },
                [
                  _vm._v(
                    "\n        " + _vm._s(_vm.i18n.requiredField) + "\n        "
                  ),
                  _c("br")
                ]
              )
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$h = [];
__vue_render__$h._withStripped = true;

  /* style */
  const __vue_inject_styles__$h = undefined;
  /* scoped */
  const __vue_scope_id__$h = undefined;
  /* functional template */
  const __vue_is_functional_template__$h = false;
  /* component normalizer */
  function __vue_normalize__$h(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/Notes/Notes.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var Notes = __vue_normalize__$h(
    { render: __vue_render__$h, staticRenderFns: __vue_staticRenderFns__$h },
    __vue_inject_styles__$h,
    __vue_script__$h,
    __vue_scope_id__$h,
    __vue_is_functional_template__$h);

const { requiredField: requiredField$1 } = useFormRules();
let ProfessionalInformation$1 = class ProfessionalInformation extends Vue {
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('as6242024.professionalInformation');
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get showEvaluatorBtn() {
        return this.globalData.getFormAppData.showEvaluatorBtn;
    }
    get isProfileComplete() {
        return this.globalData.getFormAppData.isProfileComplete;
    }
    get hasProfessionalSectionAccess() {
        return this.globalData.getFormAppData.hasProfessionalSectionAccess;
    }
    get hasPhoneConsentProfessionalProfession() {
        return !!this.item.phoneConsentProfessionalProfession;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    get title() {
        return this.$i18n.t(`as6242024.${this.titleKey}`);
    }
    get professionalSignature() {
        return lodashExports.get(this.item, `professionalSignature${this.professionalRole}`);
    }
    get professionalProfession() {
        return lodashExports.get(this.item, `professionalProfession${this.professionalRole}`);
    }
    openResourceForm() {
        this.globalData.emitEvent({ event: 'open-resource-form', data: true });
    }
    insertMyEvaluatorInfo() {
        this.globalData.emitEvent({ event: `insert-my-evaluator-info${this.professionalRole}`, data: { exclude: ['other'] } });
    }
    clearMyEvaluatorInfo() {
        this.item[`professionalSignature${this.professionalRole}`] = '';
        this.item[`professionalLicenceNumber${this.professionalRole}`] = '';
        this.item[`professionalProfession${this.professionalRole}`] = null;
        this.item[`professionalName${this.professionalRole}`] = '';
        this.item[`professionalLicenceNumber${this.professionalRole}`] = '';
        this.item[`professionalDate${this.professionalRole}`] = null;
    }
    validateRequiredField(v) {
        return requiredField$1(v) || this.$i18n.t('as6242024.base.errors.requiredField');
    }
};
__decorate([
    Prop()
], ProfessionalInformation$1.prototype, "data", void 0);
__decorate([
    Prop()
], ProfessionalInformation$1.prototype, "professionalRole", void 0);
__decorate([
    Prop()
], ProfessionalInformation$1.prototype, "titleKey", void 0);
__decorate([
    PropSync('data', { type: Object })
], ProfessionalInformation$1.prototype, "item", void 0);
ProfessionalInformation$1 = __decorate([
    Component({})
], ProfessionalInformation$1);
var script$g = ProfessionalInformation$1;

/* script */
            const __vue_script__$g = script$g;
            
/* template */
var __vue_render__$g = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    {
      staticClass: "px-2 py-0 border-right border-left",
      attrs: { id: "professionalInformation", fluid: "" }
    },
    [
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { staticClass: "section-subtitle", attrs: { xs8: "" } },
            [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]
          ),
          _vm._v(" "),
          _vm.showEvaluatorBtn
            ? _c(
                "v-flex",
                {
                  staticClass:
                    "grey lighten-2 border-bottom py-0 text-xs-right",
                  attrs: { md4: "", xs12: "" }
                },
                [
                  !_vm.professionalSignature
                    ? [
                        _vm.isProfileComplete
                          ? _c(
                              "v-btn",
                              {
                                staticClass: "primary text-center",
                                attrs: {
                                  "aria-label": "insert-evaluator-info",
                                  dark: "",
                                  flat: "",
                                  small: "",
                                  disabled: _vm.lockForm
                                },
                                on: { click: _vm.insertMyEvaluatorInfo }
                              },
                              [
                                _vm._v(
                                  _vm._s(_vm.i18n.getMyInformations) +
                                    "\n        "
                                )
                              ]
                            )
                          : _c(
                              "v-btn",
                              {
                                staticClass: "primary text-center",
                                attrs: {
                                  "aria-label": "complete-profile",
                                  disabled: _vm.lockForm,
                                  dark: "",
                                  flat: "",
                                  small: ""
                                },
                                on: { click: _vm.openResourceForm }
                              },
                              [
                                _vm._v(
                                  _vm._s(_vm.i18n.completeMyProfile) +
                                    "\n        "
                                )
                              ]
                            )
                      ]
                    : [
                        _c(
                          "v-btn",
                          {
                            staticClass: "primary text-center",
                            attrs: {
                              "aria-label": "insert-evaluator-info",
                              disabled: _vm.lockForm,
                              dark: "",
                              flat: "",
                              small: ""
                            },
                            on: { click: _vm.clearMyEvaluatorInfo }
                          },
                          [
                            _vm._v(
                              _vm._s(_vm.i18n.clearMyInformations) +
                                "\n        "
                            )
                          ]
                        )
                      ]
                ],
                2
              )
            : _c("v-flex", {
                staticClass: "grey lighten-2 border-bottom py-0",
                class: _vm.vuetifyBreakpoint.smAndDown
                  ? "text-xs-left"
                  : "text-xs-right",
                attrs: { md4: "", xs12: "" }
              })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass: "border-bottom grey lighten-2",
              attrs: { xs12: "" }
            },
            [
              _c(
                "v-radio-group",
                {
                  attrs: {
                    "aria-label": "professional-profession",
                    row: !_vm.vuetifyBreakpoint.xsOnly,
                    disabled: !_vm.hasProfessionalSectionAccess,
                    "hide-details": "",
                    rules: [
                      _vm.hasPhoneConsentProfessionalProfession ||
                        !_vm.appliedRequiredField ||
                        _vm.validateRequiredField
                    ]
                  },
                  model: {
                    value:
                      _vm.item["professionalProfession" + _vm.professionalRole],
                    callback: function($$v) {
                      _vm.$set(
                        _vm.item,
                        "professionalProfession" + _vm.professionalRole,
                        $$v
                      );
                    },
                    expression:
                      "item[`professionalProfession${professionalRole}`]"
                  }
                },
                [
                  _c("v-radio", {
                    attrs: { label: _vm.i18n.profession.nurse, value: "nurse" }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.physician,
                      value: "physician"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.inhalotherapist,
                      value: "inhalotherapist"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.midwife,
                      value: "midwife"
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.profession.pharmacist,
                      value: "pharmacist"
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { staticClass: "pb-0", attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { staticClass: "border-bottom border-right", attrs: { xs5: "" } },
            [
              _c("v-text-field", {
                attrs: {
                  "aria-label": "professional-name",
                  disabled: !_vm.hasProfessionalSectionAccess,
                  label: _vm.i18n.name,
                  placeholder: "",
                  "hide-details": "",
                  box: "",
                  maxlength: "50",
                  rules: [
                    !_vm.professionalProfession ||
                      !_vm.appliedRequiredField ||
                      _vm.validateRequiredField
                  ]
                },
                model: {
                  value: _vm.item["professionalName" + _vm.professionalRole],
                  callback: function($$v) {
                    _vm.$set(
                      _vm.item,
                      "professionalName" + _vm.professionalRole,
                      $$v
                    );
                  },
                  expression: "item[`professionalName${professionalRole}`]"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "border-bottom border-right", attrs: { xs5: "" } },
            [
              !_vm.professionalSignature
                ? _c("v-text-field", {
                    attrs: {
                      "aria-label": "professional-signature",
                      label: _vm.i18n.professionalSignature,
                      disabled: "",
                      placeholder: "",
                      "hide-details": "",
                      box: "",
                      maxlength: "100",
                      rules: [
                        !_vm.professionalProfession ||
                          !_vm.appliedRequiredField ||
                          _vm.validateRequiredField
                      ]
                    }
                  })
                : [
                    _c(
                      "v-layout",
                      { attrs: { row: "", wrap: "" } },
                      [
                        _c("v-flex", { attrs: { xs12: "", "pl-2": "" } }, [
                          _c("span", { staticClass: "caption" }, [
                            _vm._v(_vm._s(_vm.i18n.professionalSignature))
                          ])
                        ]),
                        _vm._v(" "),
                        _c(
                          "v-flex",
                          {
                            staticStyle: {
                              "margin-top": "-8px",
                              "margin-bottom": "-8px"
                            }
                          },
                          [
                            _c("img", {
                              attrs: {
                                src: _vm.professionalSignature,
                                alt: "Signature"
                              }
                            })
                          ]
                        )
                      ],
                      1
                    )
                  ]
            ],
            2
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "border-bottom border-right", attrs: { xs2: "" } },
            [
              _c("v-text-field", {
                attrs: {
                  "aria-label": "professional-licence-number",
                  label: _vm.i18n.licenceNumber,
                  disabled: !_vm.hasProfessionalSectionAccess,
                  placeholder: "",
                  "hide-details": "",
                  box: "",
                  maxlength: "25",
                  rules: [
                    !_vm.professionalProfession ||
                      !_vm.appliedRequiredField ||
                      _vm.validateRequiredField
                  ]
                },
                model: {
                  value:
                    _vm.item[
                      "professionalLicenceNumber" + _vm.professionalRole
                    ],
                  callback: function($$v) {
                    _vm.$set(
                      _vm.item,
                      "professionalLicenceNumber" + _vm.professionalRole,
                      $$v
                    );
                  },
                  expression:
                    "item[`professionalLicenceNumber${professionalRole}`]"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "v-flex",
            { staticClass: "border-bottom border-right" },
            [
              _c("v-text-field", {
                attrs: {
                  label: "* " + _vm.i18n.date,
                  "hide-details": "",
                  mask: "####-##-##",
                  box: "",
                  disabled: !_vm.hasProfessionalSectionAccess,
                  maxlength: "10",
                  rules: [
                    !_vm.professionalProfession ||
                      !_vm.appliedRequiredField ||
                      _vm.validateRequiredField
                  ]
                },
                model: {
                  value: _vm.item["professionalDate" + _vm.professionalRole],
                  callback: function($$v) {
                    _vm.$set(
                      _vm.item,
                      "professionalDate" + _vm.professionalRole,
                      $$v
                    );
                  },
                  expression: "item[`professionalDate${professionalRole}`]"
                }
              })
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$g = [];
__vue_render__$g._withStripped = true;

  /* style */
  const __vue_inject_styles__$g = undefined;
  /* scoped */
  const __vue_scope_id__$g = undefined;
  /* functional template */
  const __vue_is_functional_template__$g = false;
  /* component normalizer */
  function __vue_normalize__$g(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/ProfessionalInformation/ProfessionalInformation.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var ProfessionalInformation = __vue_normalize__$g(
    { render: __vue_render__$g, staticRenderFns: __vue_staticRenderFns__$g },
    __vue_inject_styles__$g,
    __vue_script__$g,
    __vue_scope_id__$g,
    __vue_is_functional_template__$g);

const { requiredField } = useFormRules();
let ConsentAndDecision$1 = class ConsentAndDecision extends Vue {
    get i18n() {
        return this.$i18n.t('as6242024.consentAndDecision');
    }
    get globalData() {
        return globalDataVuex();
    }
    get hasConsentSectionAccess() {
        return this.globalData.getFormAppData.hasConsentSectionAccess;
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    validateRequiredField(v) {
        return requiredField(v) || this.$i18n.t('as6242024.base.errors.requiredField');
    }
};
__decorate([
    Prop()
], ConsentAndDecision$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], ConsentAndDecision$1.prototype, "item", void 0);
ConsentAndDecision$1 = __decorate([
    Component({
        components: { ProfessionalInformation },
        computed: {
            PersonLegalRepresentativeEnum() {
                return PersonLegalRepresentativeEnum;
            },
        },
    })
], ConsentAndDecision$1);
var script$f = ConsentAndDecision$1;

/* script */
            const __vue_script__$f = script$f;
            
/* template */
var __vue_render__$f = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    { staticClass: "px-2 py-0 mt-2 border-all", attrs: { fluid: "" } },
    [
      _c(
        "v-layout",
        { attrs: { row: "", wrap: "" } },
        [
          _c("v-flex", { staticClass: "section-title", attrs: { xs12: "" } }, [
            _c("span", [_vm._v(_vm._s(_vm.i18n.title))])
          ]),
          _vm._v(" "),
          _c(
            "v-flex",
            { attrs: { xs12: "", "pb-2": "" } },
            [
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.consentCheckbox1,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField || _vm.validateRequiredField
                  ]
                },
                model: {
                  value: _vm.item.consentCheckbox1,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentCheckbox1", $$v);
                  },
                  expression: "item.consentCheckbox1"
                }
              }),
              _vm._v(" "),
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.consentCheckbox2,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField ||
                      _vm.item.consentCheckbox3 ||
                      _vm.validateRequiredField
                  ]
                },
                model: {
                  value: _vm.item.consentCheckbox2,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentCheckbox2", $$v);
                  },
                  expression: "item.consentCheckbox2"
                }
              }),
              _vm._v(" "),
              _c("v-text-field", {
                staticClass: "pa-0 ma-0 ml-4 mb-1 consent-textfield",
                attrs: {
                  label: _vm.i18n.consentCheckbox2precision,
                  "aria-label": _vm.i18n.consentCheckbox2precisionAriaLabel,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": "",
                  maxlength: "105"
                },
                model: {
                  value: _vm.item.consentCheckbox2precision,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentCheckbox2precision", $$v);
                  },
                  expression: "item.consentCheckbox2precision"
                }
              }),
              _vm._v(" "),
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.consentCheckbox3,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": "",
                  rules: [
                    !_vm.appliedRequiredField ||
                      _vm.item.consentCheckbox2 ||
                      _vm.validateRequiredField
                  ]
                },
                model: {
                  value: _vm.item.consentCheckbox3,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentCheckbox3", $$v);
                  },
                  expression: "item.consentCheckbox3"
                }
              }),
              _vm._v(" "),
              _c("v-text-field", {
                staticClass: "pa-0 ma-0 ml-4 mb-1 consent-textfield",
                attrs: {
                  label: _vm.i18n.consentCheckbox3precision,
                  "aria-label": _vm.i18n.consentCheckbox3precisionAriaLabel,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": "",
                  maxlength: "105"
                },
                model: {
                  value: _vm.item.consentCheckbox3precision,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentCheckbox3precision", $$v);
                  },
                  expression: "item.consentCheckbox3precision"
                }
              }),
              _vm._v(" "),
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.consentCheckbox4,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": ""
                },
                model: {
                  value: _vm.item.consentCheckbox4,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "consentCheckbox4", $$v);
                  },
                  expression: "item.consentCheckbox4"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { staticClass: "border-top section-subtitle", attrs: { xs12: "" } },
            [
              _vm._v(
                "\n        " +
                  _vm._s("* " + _vm.i18n.consentsRefuses.title) +
                  "\n      "
              )
            ]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { attrs: { grow: "", "pa-1": "", "pl-2": "" } },
            [
              _c(
                "v-radio-group",
                {
                  attrs: {
                    row: !_vm.vuetifyBreakpoint.xsOnly,
                    disabled: !_vm.hasConsentSectionAccess,
                    "hide-details": "",
                    "aria-label": _vm.i18n.consentsRefuses.title,
                    rules: [
                      !_vm.appliedRequiredField || _vm.validateRequiredField
                    ]
                  },
                  model: {
                    value: _vm.item.consentRefuseObtainedFrom,
                    callback: function($$v) {
                      _vm.$set(_vm.item, "consentRefuseObtainedFrom", $$v);
                    },
                    expression: "item.consentRefuseObtainedFrom"
                  }
                },
                [
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.patient,
                      value: _vm.PersonLegalRepresentativeEnum.PATIENT
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.parentalAuthority,
                      value:
                        _vm.PersonLegalRepresentativeEnum.PARENTAL_AUTHORITY
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.spouse,
                      value: _vm.PersonLegalRepresentativeEnum.SPOUSE
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.mandatary,
                      value: _vm.PersonLegalRepresentativeEnum.MANDATARY
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.specialInterest,
                      value: _vm.PersonLegalRepresentativeEnum.SPECIAL_INTEREST
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.closeRelative,
                      value: _vm.PersonLegalRepresentativeEnum.CLOSE_RELATIVE
                    }
                  }),
                  _vm._v(" "),
                  _c("v-radio", {
                    attrs: {
                      label: _vm.i18n.consentsRefuses.guardian,
                      value: _vm.PersonLegalRepresentativeEnum.GUARDIAN
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { staticClass: "border-top section-subtitle", attrs: { xs12: "" } },
            [
              _vm._v(
                "\n        " + _vm._s(_vm.i18n.recommendation) + "\n      "
              )
            ]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            {
              staticClass: "checkbox-group-title-1",
              attrs: { xs12: "", "pl-1": "", "pb-2": "", "pt-1": "" }
            },
            [
              _vm._v(
                "\n        " +
                  _vm._s(_vm.i18n.healthOfficeOrientation) +
                  "\n      "
              )
            ]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { attrs: { grow: "", "pa-1": "", "pl-4": "" } },
            [
              _c("v-checkbox", {
                attrs: {
                  label: _vm.i18n.healthOfficeConsent,
                  disabled: !_vm.hasConsentSectionAccess,
                  "hide-details": ""
                },
                model: {
                  value: _vm.item.healthOfficeConsent,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "healthOfficeConsent", $$v);
                  },
                  expression: "item.healthOfficeConsent"
                }
              })
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$f = [];
__vue_render__$f._withStripped = true;

  /* style */
  const __vue_inject_styles__$f = undefined;
  /* scoped */
  const __vue_scope_id__$f = undefined;
  /* functional template */
  const __vue_is_functional_template__$f = false;
  /* component normalizer */
  function __vue_normalize__$f(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/ConsentAndDecision/ConsentAndDecision.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var ConsentAndDecision = __vue_normalize__$f(
    { render: __vue_render__$f, staticRenderFns: __vue_staticRenderFns__$f },
    __vue_inject_styles__$f,
    __vue_script__$f,
    __vue_scope_id__$f,
    __vue_is_functional_template__$f);

let PhoneConsent$1 = class PhoneConsent extends Vue {
    get globalData() {
        return globalDataVuex();
    }
    get i18n() {
        return this.$i18n.t('as6242024.phoneConsent');
    }
    get appliedRequiredField() {
        return this.globalData.getFormAppData.appliedRequiredField;
    }
    get hasProfessionalSectionAccess() {
        return this.globalData.getFormAppData.hasProfessionalSectionAccess;
    }
};
__decorate([
    Prop()
], PhoneConsent$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], PhoneConsent$1.prototype, "item", void 0);
PhoneConsent$1 = __decorate([
    Component({})
], PhoneConsent$1);
var script$e = PhoneConsent$1;

/* script */
            const __vue_script__$e = script$e;
            
/* template */
var __vue_render__$e = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-container",
    {
      staticClass: "px-2 pt-0 pb-2 mb-2 border-right border-left border-bottom",
      attrs: { fluid: "" }
    },
    [
      _c(
        "v-layout",
        { attrs: { row: "" } },
        [
          _c(
            "v-flex",
            { staticClass: "border-bottom", attrs: { xs12: "" } },
            [
              _c("v-text-field", {
                attrs: {
                  label: _vm.i18n.witnessName,
                  disabled: !_vm.hasProfessionalSectionAccess,
                  "hide-details": "",
                  box: "",
                  maxlength: "100"
                },
                model: {
                  value: _vm.item.phoneConsentWitness,
                  callback: function($$v) {
                    _vm.$set(_vm.item, "phoneConsentWitness", $$v);
                  },
                  expression: "item.phoneConsentWitness"
                }
              })
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$e = [];
__vue_render__$e._withStripped = true;

  /* style */
  const __vue_inject_styles__$e = undefined;
  /* scoped */
  const __vue_scope_id__$e = undefined;
  /* functional template */
  const __vue_is_functional_template__$e = false;
  /* component normalizer */
  function __vue_normalize__$e(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/PhoneConsent/PhoneConsent.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var PhoneConsent = __vue_normalize__$e(
    { render: __vue_render__$e, staticRenderFns: __vue_staticRenderFns__$e },
    __vue_inject_styles__$e,
    __vue_script__$e,
    __vue_scope_id__$e,
    __vue_is_functional_template__$e);

let FooterToolbarActions$1 = class FooterToolbarActions extends Vue {
    constructor() {
        super(...arguments);
        this.buttonSaveClicked = 'saveTemporaryForm';
    }
    get i18n() {
        return this.$i18n.t('as6242024.footer');
    }
    get globalData() {
        return globalDataVuex();
    }
    get vuetifyBreakpoint() {
        return this.globalData.getFormAppData.vuetifyBreakpoint;
    }
    get hasTransmitAccess() {
        return this.globalData.getFormAppData.hasTransmitAccess;
    }
    get hasExportAccess() {
        return this.globalData.getFormAppData.hasExportAccess;
    }
    get hasCompleteFormButtonAccess() {
        return this.globalData.getFormAppData.hasCompleteFormButtonAccess;
    }
    get lockForm() {
        return this.globalData.getFormAppData.lockForm;
    }
    get saveLoading() {
        return this.globalData.getFormAppData.saveLoading;
    }
    get isTransmit() {
        let status = this.item.status;
        return status.includes('_transmitted');
    }
    submitTransmitForm(oldStatus) {
        let newStatus;
        if (this.isTransmit) {
            newStatus = oldStatus.replace('_transmitted', '');
        }
        else {
            newStatus = oldStatus + '_transmitted';
        }
        this.globalData.emitEvent({ event: 'submit-transmit-form', data: newStatus });
    }
    exportToPdf() {
        this.globalData.emitEvent({ event: 'export-to-pdf', data: true });
    }
    saveTemporaryForm() {
        this.buttonSaveClicked = 'saveTemporaryForm';
        this.globalData.emitEvent({ event: 'save-temporary-form', data: true });
    }
    completeButtonDispatch() {
        this.buttonSaveClicked = 'completeButton';
        this.globalData.emitEvent({ event: 'complete-button-dispatch', data: true });
    }
    closeModalForm() {
        this.globalData.emitEvent({ event: 'close', data: true });
    }
};
__decorate([
    Prop()
], FooterToolbarActions$1.prototype, "data", void 0);
__decorate([
    PropSync('data', { type: Object })
], FooterToolbarActions$1.prototype, "item", void 0);
FooterToolbarActions$1 = __decorate([
    Component
], FooterToolbarActions$1);
var script$d = FooterToolbarActions$1;

/* script */
            const __vue_script__$d = script$d;
            
/* template */
var __vue_render__$d = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        {
          staticClass: "text-xs-center",
          class: this.$vuetify.breakpoint.smAndDown ? "text-xs-left" : "",
          attrs: { xs12: "", "pr-2": "", "pb-2": "" }
        },
        [
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: {
                dark: "",
                flat: "",
                disabled: _vm.lockForm || _vm.saveLoading,
                "aria-label": _vm.i18n.save,
                loading:
                  _vm.saveLoading &&
                  _vm.buttonSaveClicked == "saveTemporaryForm"
              },
              on: { click: _vm.saveTemporaryForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.save) + "\n    ")]
          ),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: {
                dark: "",
                flat: "",
                disabled:
                  !_vm.hasCompleteFormButtonAccess ||
                  _vm.lockForm ||
                  _vm.saveLoading,
                "aria-label": _vm.i18n.saveAndComplete,
                loading:
                  _vm.saveLoading && _vm.buttonSaveClicked == "completeButton"
              },
              on: { click: _vm.completeButtonDispatch }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.saveAndComplete) + "\n    ")]
          ),
          _vm._v(" "),
          _vm.hasTransmitAccess
            ? _c(
                "v-btn",
                {
                  staticClass: "primary text-center",
                  attrs: { dark: "", flat: "" },
                  on: {
                    click: function($event) {
                      return _vm.submitTransmitForm(_vm.item.status)
                    }
                  }
                },
                [
                  !_vm.isTransmit
                    ? _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsTransmitted))
                      ])
                    : _c("span", [
                        _vm._v(_vm._s(_vm.i18n.markFormAsNotTransmitted))
                      ])
                ]
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "v-btn",
            {
              staticClass: "primary text-center",
              attrs: { dark: "", flat: "" },
              on: { click: _vm.closeModalForm }
            },
            [_vm._v("\n      " + _vm._s(_vm.i18n.close) + "\n    ")]
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$d = [];
__vue_render__$d._withStripped = true;

  /* style */
  const __vue_inject_styles__$d = undefined;
  /* scoped */
  const __vue_scope_id__$d = undefined;
  /* functional template */
  const __vue_is_functional_template__$d = false;
  /* component normalizer */
  function __vue_normalize__$d(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/form-parts/FooterToolbarActions/FooterToolbarActions.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var FooterToolbarActions = __vue_normalize__$d(
    { render: __vue_render__$d, staticRenderFns: __vue_staticRenderFns__$d },
    __vue_inject_styles__$d,
    __vue_script__$d,
    __vue_scope_id__$d,
    __vue_is_functional_template__$d);

const hasSomeProviderChampFilled = (vaccinationData) => {
    return !!(vaccinationData.immunizationProviderLicenceNumber1 ||
        vaccinationData.immunizationProviderName1 ||
        vaccinationData.immunizationProviderProfession1 ||
        vaccinationData.immunizationProviderSignature1);
};
const hasFilledAllProviderChamps = (vaccinationData) => {
    return !!(vaccinationData.immunizationProviderLicenceNumber1 &&
        vaccinationData.immunizationProviderName1 &&
        vaccinationData.immunizationProviderProfession1 &&
        vaccinationData.immunizationProviderSignature1);
};

let As6242024 = class As6242024 extends Vue {
    constructor() {
        super(...arguments);
        // Dialogs
        this.showFormErrorDialog = false;
        this.showFormEditWarningDialog = false;
        this.establishmentId = this.data.establishmentId;
        this.appointmentNumber = this.data['appointment.appointmentNumber'];
        this.serviceTemplate = this.data['appointment.serviceTemplate'];
        this.status = this.data.status;
    }
    /**
     * Vue Hook - Created
     */
    created() {
        // Set the data in the vuex store
        globalDataVuex().setFormAppData(this.appData);
    }
    /**
     * Context:
     * If user is an evaluator, it will fill only some portion of the form and will not do the injection.
     * Since it need to be able to save the form (state « evaluation complete »), we want to skip all required vaccine details information.
     *
     **/
    get shouldValidateVaccineDetailsSection() {
        const data = this.item.data;
        return hasSomeProviderChampFilled(data);
    }
    /**
     * Retrieve the app data for this form
     */
    get getAppData() {
        return globalDataVuex().getFormAppData;
    }
    get getLastEmittedEvent() {
        return globalDataVuex().getLastEmittedEvent;
    }
    get numberOfLineToDisplay() {
        const item = this.item.data;
        for (let i = 4; i > 0; i--) {
            if (item[`vaccineDetailsCategory${i}`]) {
                return i + 1;
            }
        }
        return 1;
    }
    get getHasFilledAllProviderChamps() {
        return hasFilledAllProviderChamps(this.item.data);
    }
    async onEventEmit(event) {
        switch (event.event) {
            case 'complete-button-dispatch':
                await this.$emit('update-applied-required-field', true);
                if (this.validateForm(true)) {
                    await this.$emit(event.event, this.getHasFilledAllProviderChamps);
                }
                break;
            case 'export-to-pdf':
                await this.$emit(event.event, event.data);
                break;
            case 'save-temporary-form':
                await this.$emit('update-applied-required-field', false);
                if (this.validateForm(false)) {
                    await this.$emit(event.event, event.data);
                }
                break;
            default:
                await this.$emit(event.event, event.data);
        }
    }
    onAppDataUpdate() {
        globalDataVuex().setFormAppData(this.appData);
    }
    setAllowEditForm(value) {
        this.$emit('allow-edit-form', value);
    }
    validateForm(showFormErrorDialog) {
        let isValid = (this.as624Form && this.as624Form.validate());
        if (!isValid) {
            this.showFormErrorDialog = showFormErrorDialog;
            return false;
        }
        return true;
    }
    displayWarningIfLocked() {
        if (!globalDataVuex().getFormAppData.hasReadOnly && globalDataVuex().getFormAppData.lockForm) {
            this.showFormEditWarningDialog = true;
        }
    }
};
__decorate([
    Ref()
], As6242024.prototype, "as624Form", void 0);
__decorate([
    Ref()
], As6242024.prototype, "immunizationProviderForm", void 0);
__decorate([
    Prop()
], As6242024.prototype, "data", void 0);
__decorate([
    Prop()
], As6242024.prototype, "appData", void 0);
__decorate([
    Prop()
], As6242024.prototype, "categoryExclusionList", void 0);
__decorate([
    PropSync('data', { type: Object })
], As6242024.prototype, "item", void 0);
__decorate([
    Watch('getLastEmittedEvent')
], As6242024.prototype, "onEventEmit", null);
__decorate([
    Watch('appData')
], As6242024.prototype, "onAppDataUpdate", null);
As6242024 = __decorate([
    Component({
        computed: {
            ProfessionalRoleEnum() {
                return ProfessionalRoleEnum;
            },
        },
        components: {
            DialogFormError, DialogFormEditWarning,
            FooterToolbarActions,
            TitleAndPatient,
            HeaderToolbar,
            GeneralInformation,
            PreImmunization,
            AdministrativeReason,
            ConsentAndDecision,
            ProfessionalInformation,
            VaccineDetails,
            PhoneConsent,
            Notes,
        },
    })
], As6242024);
var script$c = As6242024;

/* script */
            const __vue_script__$c = script$c;
            
/* template */
var __vue_render__$c = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "v-layout",
    { attrs: { row: "", wrap: "" } },
    [
      _c(
        "v-flex",
        [
          _c(
            "v-card",
            { attrs: { tile: "" } },
            [
              _c("header-toolbar", {
                attrs: { data: _vm.item },
                on: {
                  "update:data": function($event) {
                    _vm.item = $event;
                  }
                }
              }),
              _vm._v(" "),
              !_vm.getAppData.vuetifyBreakpoint.xsOnly
                ? _c(
                    "v-card-text",
                    { on: { click: _vm.displayWarningIfLocked } },
                    [
                      _c(
                        "v-form",
                        {
                          ref: "as624Form",
                          attrs: { id: "as-624-form", "lazy-validation": "" }
                        },
                        [
                          _c("title-and-patient", {
                            attrs: {
                              data: _vm.item.data,
                              establishmentId: _vm.establishmentId,
                              appointmentNumber: _vm.appointmentNumber
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("general-information", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("pre-immunization", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("administrative-reason", {
                            attrs: {
                              data: _vm.item.data,
                              numberOfLineToDisplay: _vm.numberOfLineToDisplay
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("professional-information", {
                            attrs: {
                              data: _vm.item.data,
                              titleKey: "professionalInformation.titleForm",
                              professionalRole:
                                _vm.ProfessionalRoleEnum.FORM_FILLED
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("consent-and-decision", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("professional-information", {
                            attrs: {
                              data: _vm.item.data,
                              titleKey: "professionalInformation.titleConsent",
                              professionalRole:
                                _vm.ProfessionalRoleEnum.OBTAINED_CONSENT
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("phone-consent", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("vaccine-details", {
                            attrs: {
                              data: _vm.item.data,
                              serviceTemplate: +_vm.serviceTemplate,
                              status: _vm.status.toString(),
                              "should-check-validation":
                                _vm.shouldValidateVaccineDetailsSection,
                              categoryExclusionList: _vm.categoryExclusionList,
                              numberOfLineToDisplay: _vm.numberOfLineToDisplay
                            },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          }),
                          _vm._v(" "),
                          _c("notes", {
                            attrs: { data: _vm.item.data },
                            on: {
                              "update:data": function($event) {
                                return _vm.$set(_vm.item, "data", $event)
                              }
                            }
                          })
                        ],
                        1
                      )
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              _c(
                "v-card-actions",
                [
                  _c("footer-toolbar-actions", {
                    attrs: { data: _vm.item },
                    on: {
                      "update:data": function($event) {
                        _vm.item = $event;
                      }
                    }
                  })
                ],
                1
              )
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c("dialog-form-error", {
        model: {
          value: _vm.showFormErrorDialog,
          callback: function($$v) {
            _vm.showFormErrorDialog = $$v;
          },
          expression: "showFormErrorDialog"
        }
      }),
      _vm._v(" "),
      _c("dialog-form-edit-warning", {
        on: { "allow-edit-form": _vm.setAllowEditForm },
        model: {
          value: _vm.showFormEditWarningDialog,
          callback: function($$v) {
            _vm.showFormEditWarningDialog = $$v;
          },
          expression: "showFormEditWarningDialog"
        }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$c = [];
__vue_render__$c._withStripped = true;

  /* style */
  const __vue_inject_styles__$c = function (inject) {
    if (!inject) return
    inject("data-v-effd2da6_0", { source: "#as-624-form .border-all,\n#immunization-provider-form .border-all {\n  border: 1px black solid;\n}\n#as-624-form .v-input__slot,\n#immunization-provider-form .v-input__slot {\n  background: none;\n}\n#as-624-form .border-top,\n#immunization-provider-form .border-top {\n  border-top: 1px solid #000000 !important;\n}\n#as-624-form .border-bottom,\n#immunization-provider-form .border-bottom {\n  border-bottom: 1px solid #000000 !important;\n}\n#as-624-form .border-left,\n#immunization-provider-form .border-left {\n  border-left: 1px solid #000000 !important;\n}\n#as-624-form .border-right,\n#immunization-provider-form .border-right {\n  border-right: 1px solid #000000 !important;\n}\n#as-624-form .header-question,\n#immunization-provider-form .header-question {\n  width: 38px;\n  display: inline-flex;\n}\n#as-624-form .header-NA,\n#immunization-provider-form .header-NA {\n  width: 60px;\n  display: inline-flex;\n}\n#as-624-form .v-input--checkbox,\n#as-624-form .v-input--radio-group,\n#immunization-provider-form .v-input--checkbox,\n#immunization-provider-form .v-input--radio-group {\n  margin: 0;\n}\n#as-624-form .v-radio,\n#immunization-provider-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px;\n}\n#as-624-form .phone,\n#immunization-provider-form .phone {\n  float: left;\n  width: 50%;\n}\n#as-624-form .vaccinator-profession .v-radio,\n#as-624-form .professional-profession .v-radio,\n#immunization-provider-form .vaccinator-profession .v-radio,\n#immunization-provider-form .professional-profession .v-radio {\n  margin: 0 20px 0 0;\n}\n#as-624-form .user-phone .v-radio,\n#immunization-provider-form .user-phone .v-radio {\n  margin: 0;\n}\n#as-624-form .consent-textfield .v-input__control .v-input__slot,\n#immunization-provider-form .consent-textfield .v-input__control .v-input__slot {\n  height: 30px !important;\n  min-height: 30px !important;\n}\n#as-624-form .consent-textfield input,\n#immunization-provider-form .consent-textfield input {\n  margin-top: 0 !important;\n}\n#as-624-form .vertical-text,\n#immunization-provider-form .vertical-text {\n  writing-mode: tb-rl;\n  transform: rotate(-180deg);\n}\n#as-624-form .section-title,\n#immunization-provider-form .section-title {\n  background-color: black;\n  padding: 0 4px;\n  color: white;\n}\n#as-624-form .section-subtitle,\n#immunization-provider-form .section-subtitle {\n  text-transform: uppercase;\n  background-color: #e0e0e0;\n  padding-left: 4px;\n  padding-right: 4px;\n  font-weight: 700;\n  border-bottom: 0.8px solid black;\n}\n\n/*# sourceMappingURL=as6242024.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/as6242024.vue","as6242024.vue"],"names":[],"mappings":"AA8NA;;EAGA,uBAAA;AAAA;AAHA;;EAOA,gBAAA;AAAA;AAPA;;EAWA,wCAAA;AAAA;AAXA;;EAeA,2CAAA;AAAA;AAfA;;EAmBA,yCAAA;AAAA;AAnBA;;EAuBA,0CAAA;AAAA;AAvBA;;EA2BA,WAAA;EACA,oBAAA;AAAA;AA5BA;;EAgCA,WAAA;EACA,oBAAA;AAAA;AAjCA;;;;EAsCA,SAAA;AAAA;AAtCA;;EA0CA,kBAAA;EACA,eAAA;AAAA;AA3CA;;EA+CA,WAAA;EACA,UAAA;AAAA;AAhDA;;;;EAqDA,kBAAA;AAAA;AArDA;;EAyDA,SAAA;AAAA;AAzDA;;EA6DA,uBAAA;EACA,2BAAA;AAAA;AA9DA;;EAkEA,wBAAA;AAAA;AAlEA;;EAsEA,mBAAA;EACA,0BAAA;AAAA;AAvEA;;EA2EA,uBAAA;EACA,cAAA;EACA,YAAA;AAAA;AA7EA;;EAiFA,yBAAA;EACA,yBAAA;EACA,iBAAA;EACA,kBAAA;EACA,gBAAA;EACA,gCAAA;AAAA;;AC3NA,wCAAwC","file":"as6242024.vue","sourcesContent":[null,"#as-624-form .border-all,\n#immunization-provider-form .border-all {\n  border: 1px black solid; }\n\n#as-624-form .v-input__slot,\n#immunization-provider-form .v-input__slot {\n  background: none; }\n\n#as-624-form .border-top,\n#immunization-provider-form .border-top {\n  border-top: 1px solid #000000 !important; }\n\n#as-624-form .border-bottom,\n#immunization-provider-form .border-bottom {\n  border-bottom: 1px solid #000000 !important; }\n\n#as-624-form .border-left,\n#immunization-provider-form .border-left {\n  border-left: 1px solid #000000 !important; }\n\n#as-624-form .border-right,\n#immunization-provider-form .border-right {\n  border-right: 1px solid #000000 !important; }\n\n#as-624-form .header-question,\n#immunization-provider-form .header-question {\n  width: 38px;\n  display: inline-flex; }\n\n#as-624-form .header-NA,\n#immunization-provider-form .header-NA {\n  width: 60px;\n  display: inline-flex; }\n\n#as-624-form .v-input--checkbox,\n#as-624-form .v-input--radio-group,\n#immunization-provider-form .v-input--checkbox,\n#immunization-provider-form .v-input--radio-group {\n  margin: 0; }\n\n#as-624-form .v-radio,\n#immunization-provider-form .v-radio {\n  margin-bottom: 5px;\n  margin-top: 5px; }\n\n#as-624-form .phone,\n#immunization-provider-form .phone {\n  float: left;\n  width: 50%; }\n\n#as-624-form .vaccinator-profession .v-radio,\n#as-624-form .professional-profession .v-radio,\n#immunization-provider-form .vaccinator-profession .v-radio,\n#immunization-provider-form .professional-profession .v-radio {\n  margin: 0 20px 0 0; }\n\n#as-624-form .user-phone .v-radio,\n#immunization-provider-form .user-phone .v-radio {\n  margin: 0; }\n\n#as-624-form .consent-textfield .v-input__control .v-input__slot,\n#immunization-provider-form .consent-textfield .v-input__control .v-input__slot {\n  height: 30px !important;\n  min-height: 30px !important; }\n\n#as-624-form .consent-textfield input,\n#immunization-provider-form .consent-textfield input {\n  margin-top: 0 !important; }\n\n#as-624-form .vertical-text,\n#immunization-provider-form .vertical-text {\n  writing-mode: tb-rl;\n  transform: rotate(-180deg); }\n\n#as-624-form .section-title,\n#immunization-provider-form .section-title {\n  background-color: black;\n  padding: 0 4px;\n  color: white; }\n\n#as-624-form .section-subtitle,\n#immunization-provider-form .section-subtitle {\n  text-transform: uppercase;\n  background-color: #e0e0e0;\n  padding-left: 4px;\n  padding-right: 4px;\n  font-weight: 700;\n  border-bottom: 0.8px solid black; }\n\n/*# sourceMappingURL=as6242024.vue.map */"]}, media: undefined })
,inject("data-v-effd2da6_1", { source: "[data-v-effd2da6] .v-progress-circular__overlay {\n  stroke: white !important;\n}\n\n/*# sourceMappingURL=as6242024.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/as6242024.vue","as6242024.vue"],"names":[],"mappings":"AA0TA;EAEA,wBAAA;AAAA;;ACzTA,wCAAwC","file":"as6242024.vue","sourcesContent":[null,"::v-deep .v-progress-circular__overlay {\n  stroke: white !important; }\n\n/*# sourceMappingURL=as6242024.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$c = "data-v-effd2da6";
  /* module identifier */
  const __vue_module_identifier__$5 = undefined;
  /* functional template */
  const __vue_is_functional_template__$c = false;
  /* component normalizer */
  function __vue_normalize__$c(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/as624-2024/as6242024.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$5() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$5.styles || (__vue_create_injector__$5.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var as6242024 = __vue_normalize__$c(
    { render: __vue_render__$c, staticRenderFns: __vue_staticRenderFns__$c },
    __vue_inject_styles__$c,
    __vue_script__$c,
    __vue_scope_id__$c,
    __vue_is_functional_template__$c,
    __vue_module_identifier__$5,
    __vue_create_injector__$5);

// eslint-disable-next-line @typescript-eslint/no-var-requires
let upperCase = require('lodash/upperCase');
let Section$1 = class Section extends Vue {
    get title() {
        return upperCase(this.i18n(this.config.title));
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    get description() {
        return this.i18n(this.config.description);
    }
};
__decorate([
    Prop()
], Section$1.prototype, "config", void 0);
__decorate([
    Prop()
], Section$1.prototype, "configVersionNumber", void 0);
Section$1 = __decorate([
    Component({
        mixins: [VersionnedTranslation],
    })
], Section$1);
var script$b = Section$1;

/* script */
            const __vue_script__$b = script$b;
            
/* template */
var __vue_render__$b = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { attrs: { id: _vm.config.id } }, [
    _c(
      "h2",
      {
        staticClass: "section",
        attrs: { "aria-label": _vm.title, tabindex: "0" }
      },
      [_vm._v("\n      " + _vm._s(_vm.title) + "\n  ")]
    ),
    _vm._v(" "),
    _vm.description
      ? _c(
          "p",
          {
            staticClass: "description",
            attrs: { "aria-label": _vm.description, tabindex: "0" }
          },
          [_vm._v("\n    " + _vm._s(_vm.description) + "\n  ")]
        )
      : _vm._e()
  ])
};
var __vue_staticRenderFns__$b = [];
__vue_render__$b._withStripped = true;

  /* style */
  const __vue_inject_styles__$b = undefined;
  /* scoped */
  const __vue_scope_id__$b = undefined;
  /* functional template */
  const __vue_is_functional_template__$b = false;
  /* component normalizer */
  function __vue_normalize__$b(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/Section.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var Section = __vue_normalize__$b(
    { render: __vue_render__$b, staticRenderFns: __vue_staticRenderFns__$b },
    __vue_inject_styles__$b,
    __vue_script__$b,
    __vue_scope_id__$b,
    __vue_is_functional_template__$b);

let Alert$1 = class Alert extends Vue {
    data() {
        return {
            value: [],
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getAriaLabel(message) {
        return this.i18n('accessibility.alert') + ' ' + this.i18n(message);
    }
};
__decorate([
    Prop()
], Alert$1.prototype, "config", void 0);
__decorate([
    Prop()
], Alert$1.prototype, "configVersionNumber", void 0);
Alert$1 = __decorate([
    Component({
        mixins: [VersionnedTranslation],
    })
], Alert$1);
var script$a = Alert$1;

/* script */
            const __vue_script__$a = script$a;
            
/* template */
var __vue_render__$a = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    _vm._l(_vm.config.alert, function(alert) {
      return _c(
        "div",
        { key: alert.id, attrs: { id: _vm.config.id + "-alerts" } },
        [
          _c(
            "div",
            {
              staticClass: "alert",
              attrs: {
                id: _vm.config.id + "-alert-" + alert.id,
                "aria-label": _vm.getAriaLabel(alert.message),
                tabindex: "0"
              }
            },
            [
              _c("div", {
                domProps: { innerHTML: _vm._s(_vm.i18n(alert.message)) }
              })
            ]
          )
        ]
      )
    }),
    0
  )
};
var __vue_staticRenderFns__$a = [];
__vue_render__$a._withStripped = true;

  /* style */
  const __vue_inject_styles__$a = function (inject) {
    if (!inject) return
    inject("data-v-43e68a34_0", { source: ".alert[data-v-43e68a34] {\n  background-color: #DEF9FB;\n  border-radius: 10px;\n  padding: 8px 10px;\n  margin: 15px 0 0 0;\n  font-size: 16px;\n}\n\n/*# sourceMappingURL=Alert.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/Alert.vue","Alert.vue"],"names":[],"mappings":"AAgDA;EACA,yBAAA;EACA,mBAAA;EACA,iBAAA;EACA,kBAAA;EACA,eAAA;AAAA;;AC9CA,oCAAoC","file":"Alert.vue","sourcesContent":[null,".alert {\n  background-color: #DEF9FB;\n  border-radius: 10px;\n  padding: 8px 10px;\n  margin: 15px 0 0 0;\n  font-size: 16px; }\n\n/*# sourceMappingURL=Alert.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$a = "data-v-43e68a34";
  /* module identifier */
  const __vue_module_identifier__$4 = undefined;
  /* functional template */
  const __vue_is_functional_template__$a = false;
  /* component normalizer */
  function __vue_normalize__$a(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/Alert.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$4() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$4.styles || (__vue_create_injector__$4.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var Alert = __vue_normalize__$a(
    { render: __vue_render__$a, staticRenderFns: __vue_staticRenderFns__$a },
    __vue_inject_styles__$a,
    __vue_script__$a,
    __vue_scope_id__$a,
    __vue_is_functional_template__$a,
    __vue_module_identifier__$4,
    __vue_create_injector__$4);

let Header$1 = class Header extends Vue {
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    get description() {
        return this.i18n(this.config.description);
    }
};
__decorate([
    Prop()
], Header$1.prototype, "config", void 0);
__decorate([
    Prop()
], Header$1.prototype, "configVersionNumber", void 0);
Header$1 = __decorate([
    Component({
        mixins: [VersionnedTranslation],
    })
], Header$1);
var script$9 = Header$1;

/* script */
            const __vue_script__$9 = script$9;
            
/* template */
var __vue_render__$9 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "h3",
      {
        staticClass: "pt-2",
        attrs: { "aria-label": _vm.i18n(_vm.config.title), tabindex: "0" }
      },
      [
        _c("span", {
          domProps: { innerHTML: _vm._s(_vm.i18n(_vm.config.title)) }
        })
      ]
    ),
    _vm._v(" "),
    _vm.description
      ? _c("p", { attrs: { "aria-label": _vm.description, tabindex: "0" } }, [
          _vm._v("\n        " + _vm._s(_vm.description) + "\n    ")
        ])
      : _vm._e()
  ])
};
var __vue_staticRenderFns__$9 = [];
__vue_render__$9._withStripped = true;

  /* style */
  const __vue_inject_styles__$9 = undefined;
  /* scoped */
  const __vue_scope_id__$9 = undefined;
  /* functional template */
  const __vue_is_functional_template__$9 = false;
  /* component normalizer */
  function __vue_normalize__$9(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/Header.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var Header = __vue_normalize__$9(
    { render: __vue_render__$9, staticRenderFns: __vue_staticRenderFns__$9 },
    __vue_inject_styles__$9,
    __vue_script__$9,
    __vue_scope_id__$9,
    __vue_is_functional_template__$9);

const { isDate, pastDate } = useFormRules();
let DatePast$1 = class DatePast extends Vue {
    data() {
        return {
            value: null,
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getTextfieldAriaLabel(answer) {
        const text = this.i18n(answer.label) || this.i18n(this.config.description) || this.i18n(this.config.title);
        return this.i18n('accessibility.textfield') + '' + text;
    }
    isDate(value) {
        return isDate(value);
    }
    isPastDate(value) {
        return pastDate(value);
    }
    validateDateRules(v) {
        return this.isDate(v) || this.isPastDate(v);
    }
    validateDate(v) {
        return this.isDate(v) && this.isPastDate(v);
    }
    onValueUpdate() {
        let element = lodashExports.find(this.item, { name: this.config.name.split('-')[0] });
        const dateFormatted = this.$data.value.slice(0, 4) + '-' + this.$data.value.slice(4, 6) + '-' + this.$data.value.slice(6, 8);
        if (!element || (this.validateDate(dateFormatted) === 'Invalid Past Date')) {
            this.$data.value = null;
            return;
        }
        element.value = dateFormatted;
    }
};
__decorate([
    Prop()
], DatePast$1.prototype, "config", void 0);
__decorate([
    Prop()
], DatePast$1.prototype, "configVersionNumber", void 0);
__decorate([
    PropSync('data', { type: Array })
], DatePast$1.prototype, "item", void 0);
__decorate([
    Watch('value')
], DatePast$1.prototype, "onValueUpdate", null);
DatePast$1 = __decorate([
    Component({
        components: {
            Alert,
            Header,
        },
        mixins: [VersionnedTranslation],
    })
], DatePast$1);
var script$8 = DatePast$1;

/* script */
            const __vue_script__$8 = script$8;
            
/* template */
var __vue_render__$8 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { attrs: { "data-testid": "datePast", id: _vm.config.id } },
    [
      _c("Header", {
        attrs: {
          config: _vm.config,
          configVersionNumber: _vm.configVersionNumber
        }
      }),
      _vm._v(" "),
      _vm._l(_vm.config.answer, function(answer) {
        return _c(
          "span",
          { key: answer.id },
          [
            _c("v-text-field", {
              class: "e" + _vm.config.id + "-a" + answer.id,
              attrs: {
                "background-color": "#F5F5F5",
                id: answer.id,
                label: _vm.i18n(answer.label),
                "aria-label": _vm.getTextfieldAriaLabel(answer),
                placeholder:
                  _vm.$i18n.locale === "fr" ? "AAAA-MM-JJ" : "YYYY-MM-DD",
                mask: "####-##-##",
                rules: [_vm.validateDateRules],
                maxlength: "10"
              },
              model: {
                value: _vm.$data.value,
                callback: function($$v) {
                  _vm.$set(_vm.$data, "value", $$v);
                },
                expression: "$data.value"
              }
            })
          ],
          1
        )
      }),
      _vm._v(" "),
      _c("Alert", { attrs: { config: _vm.config } })
    ],
    2
  )
};
var __vue_staticRenderFns__$8 = [];
__vue_render__$8._withStripped = true;

  /* style */
  const __vue_inject_styles__$8 = undefined;
  /* scoped */
  const __vue_scope_id__$8 = undefined;
  /* functional template */
  const __vue_is_functional_template__$8 = false;
  /* component normalizer */
  function __vue_normalize__$8(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/Date/DatePast.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var DatePast = __vue_normalize__$8(
    { render: __vue_render__$8, staticRenderFns: __vue_staticRenderFns__$8 },
    __vue_inject_styles__$8,
    __vue_script__$8,
    __vue_scope_id__$8,
    __vue_is_functional_template__$8);

let LongAnswerTextArea$1 = class LongAnswerTextArea extends Vue {
    data() {
        return {
            value: null,
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getTextfieldAriaLabel(answer) {
        const text = this.i18n(answer.label) || this.i18n(this.config.description) || this.i18n(this.config.title);
        return this.i18n('accessibility.textfield') + '' + text;
    }
    onValueUpdate() {
        let element = lodashExports.find(this.item, { name: this.config.name.split('-')[0] });
        if (!element) {
            return;
        }
        element.value = this.$data.value;
    }
};
__decorate([
    Prop()
], LongAnswerTextArea$1.prototype, "config", void 0);
__decorate([
    Prop()
], LongAnswerTextArea$1.prototype, "configVersionNumber", void 0);
__decorate([
    PropSync('data', { type: Array })
], LongAnswerTextArea$1.prototype, "item", void 0);
__decorate([
    Watch('value')
], LongAnswerTextArea$1.prototype, "onValueUpdate", null);
LongAnswerTextArea$1 = __decorate([
    Component({
        components: {
            Header,
            Alert,
        },
        mixins: [VersionnedTranslation],
    })
], LongAnswerTextArea$1);
var script$7 = LongAnswerTextArea$1;

/* script */
            const __vue_script__$7 = script$7;
            
/* template */
var __vue_render__$7 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { attrs: { "data-testid": "longAnswerTextArea", id: _vm.config.id } },
    [
      _c("Header", {
        attrs: {
          config: _vm.config,
          configVersionNumber: _vm.configVersionNumber
        }
      }),
      _vm._v(" "),
      _vm._l(_vm.config.answer, function(answer) {
        return _c(
          "span",
          { key: answer.id },
          [
            _c("v-textarea", {
              class: "e" + _vm.config.id + "-a" + answer.id,
              attrs: {
                "background-color": "#F5F5F5",
                id: answer.id,
                label: _vm.i18n(answer.label),
                "aria-label": _vm.getTextfieldAriaLabel(answer),
                maxlength: "1000"
              },
              model: {
                value: _vm.$data.value,
                callback: function($$v) {
                  _vm.$set(_vm.$data, "value", $$v);
                },
                expression: "$data.value"
              }
            })
          ],
          1
        )
      }),
      _vm._v(" "),
      _c("Alert", {
        attrs: {
          config: _vm.config,
          configVersionNumber: _vm.configVersionNumber
        }
      })
    ],
    2
  )
};
var __vue_staticRenderFns__$7 = [];
__vue_render__$7._withStripped = true;

  /* style */
  const __vue_inject_styles__$7 = undefined;
  /* scoped */
  const __vue_scope_id__$7 = undefined;
  /* functional template */
  const __vue_is_functional_template__$7 = false;
  /* component normalizer */
  function __vue_normalize__$7(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/TextInput/LongAnswerTextArea.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var LongAnswerTextArea = __vue_normalize__$7(
    { render: __vue_render__$7, staticRenderFns: __vue_staticRenderFns__$7 },
    __vue_inject_styles__$7,
    __vue_script__$7,
    __vue_scope_id__$7,
    __vue_is_functional_template__$7);

let ShortAnswerText$1 = class ShortAnswerText extends Vue {
    data() {
        return {
            value: null,
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getTextfieldAriaLabel(answer) {
        const text = this.i18n(answer.label) || this.i18n(this.config.description) || this.i18n(this.config.title);
        return this.i18n('accessibility.textfield') + '' + text;
    }
    onValueUpdate() {
        let element = lodashExports.find(this.item, { name: this.config.name.split('-')[0] });
        if (!element) {
            return;
        }
        element.value = this.$data.value;
    }
};
__decorate([
    Prop()
], ShortAnswerText$1.prototype, "config", void 0);
__decorate([
    Prop()
], ShortAnswerText$1.prototype, "configVersionNumber", void 0);
__decorate([
    PropSync('data', { type: Array })
], ShortAnswerText$1.prototype, "item", void 0);
__decorate([
    Watch('value')
], ShortAnswerText$1.prototype, "onValueUpdate", null);
ShortAnswerText$1 = __decorate([
    Component({
        components: {
            Alert,
            Header,
        },
        mixins: [VersionnedTranslation],
    })
], ShortAnswerText$1);
var script$6 = ShortAnswerText$1;

/* script */
            const __vue_script__$6 = script$6;
            
/* template */
var __vue_render__$6 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { attrs: { "data-testid": "shortAnswerText", id: _vm.config.id } },
    [
      _c("Header", {
        attrs: {
          config: _vm.config,
          configVersionNumber: _vm.configVersionNumber
        }
      }),
      _vm._v(" "),
      _vm._l(_vm.config.answer, function(answer) {
        return _c(
          "span",
          { key: answer.id },
          [
            _c("v-text-field", {
              class: "e" + _vm.config.id + "-a" + answer.id,
              attrs: {
                "background-color": "#F5F5F5",
                id: answer.id,
                label: _vm.i18n(answer.label),
                "aria-label": _vm.getTextfieldAriaLabel(answer),
                maxlength: "255"
              },
              model: {
                value: _vm.$data.value,
                callback: function($$v) {
                  _vm.$set(_vm.$data, "value", $$v);
                },
                expression: "$data.value"
              }
            })
          ],
          1
        )
      }),
      _vm._v(" "),
      _c("Alert", {
        attrs: {
          config: _vm.config,
          configVersionNumber: _vm.configVersionNumber
        }
      })
    ],
    2
  )
};
var __vue_staticRenderFns__$6 = [];
__vue_render__$6._withStripped = true;

  /* style */
  const __vue_inject_styles__$6 = undefined;
  /* scoped */
  const __vue_scope_id__$6 = undefined;
  /* functional template */
  const __vue_is_functional_template__$6 = false;
  /* component normalizer */
  function __vue_normalize__$6(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/TextInput/ShortAnswerText.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var ShortAnswerText = __vue_normalize__$6(
    { render: __vue_render__$6, staticRenderFns: __vue_staticRenderFns__$6 },
    __vue_inject_styles__$6,
    __vue_script__$6,
    __vue_scope_id__$6,
    __vue_is_functional_template__$6);

let YesNoButtonWithText$1 = class YesNoButtonWithText extends Vue {
    data() {
        return {
            button: null,
            textfield: null,
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getTextfieldAriaLabel(answer) {
        const text = this.i18n(answer.label) || this.i18n(this.config.description) || this.i18n(this.config.title);
        return this.i18n('accessibility.textfield') + '' + text;
    }
    getAriaLabelForButton(value, label) {
        switch (value) {
            case 1:
                return forceA11yNarratorSmallPause(this.i18n(label) + ', ' + this.i18n('accessibility.yes.label'));
            case 2:
                return forceA11yNarratorSmallPause(this.i18n(label) + ', ' + this.i18n('accessibility.no.label'));
        }
        return '';
    }
    get buttonAnswers() {
        const answers = lodashExports.cloneDeep(this.config.answer);
        answers.pop();
        return answers;
    }
    get lastAnswer() {
        var _a;
        const answers = lodashExports.cloneDeep(this.config.answer);
        return (_a = answers.pop()) !== null && _a !== void 0 ? _a : { id: -1, label: '' };
    }
    onValueUpdateButton() {
        let elementButton = this.getFirstButton();
        if (!elementButton) {
            return;
        }
        elementButton.value = Number(this.$data.button);
        setFocusForAccessibility(`e${this.$props.config.id}-a${this.buttonAnswers[this.$data.button - 1].id}`, 500, false);
    }
    onValueUpdateTextfield() {
        let elementButton = this.getFirstButton();
        let elementTextfield = this.getTextfield();
        if (!elementButton || !elementTextfield) {
            return;
        }
        elementTextfield.value = this.$data.button === 1 ? this.$data.textfield : null;
    }
    getFirstButton() {
        return lodashExports.find(this.item, { name: this.config.name.split('-')[0] });
    }
    getTextfield() {
        return lodashExports.find(this.item, { name: this.config.name.split('-')[0] + '_Textfield' });
    }
};
__decorate([
    Prop()
], YesNoButtonWithText$1.prototype, "config", void 0);
__decorate([
    Prop()
], YesNoButtonWithText$1.prototype, "configVersionNumber", void 0);
__decorate([
    PropSync('data', { type: Array })
], YesNoButtonWithText$1.prototype, "item", void 0);
__decorate([
    Watch('button')
], YesNoButtonWithText$1.prototype, "onValueUpdateButton", null);
__decorate([
    Watch('textfield')
], YesNoButtonWithText$1.prototype, "onValueUpdateTextfield", null);
YesNoButtonWithText$1 = __decorate([
    Component({
        components: {
            Alert,
            Header,
        },
        mixins: [VersionnedTranslation],
    })
], YesNoButtonWithText$1);
var script$5 = YesNoButtonWithText$1;

/* script */
            const __vue_script__$5 = script$5;
            
/* template */
var __vue_render__$5 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { attrs: { "data-testid": "yesNoButtonWithText", id: _vm.config.id } },
    [
      _c(
        "v-container",
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("Header", {
                    attrs: {
                      config: _vm.config,
                      configVersionNumber: _vm.configVersionNumber
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c("v-flex", { attrs: { xs12: "", "mt-2": "" } }, [
                _c(
                  "p",
                  [
                    _c(
                      "v-btn-toggle",
                      {
                        attrs: { divided: "" },
                        model: {
                          value: _vm.$data.button,
                          callback: function($$v) {
                            _vm.$set(_vm.$data, "button", $$v);
                          },
                          expression: "$data.button"
                        }
                      },
                      _vm._l(_vm.buttonAnswers, function(answer) {
                        return _c(
                          "span",
                          { key: answer.id },
                          [
                            _c(
                              "v-btn",
                              {
                                class: "e" + _vm.config.id + "-a" + answer.id,
                                attrs: {
                                  id: "e" + _vm.config.id + "-a" + answer.id,
                                  value: answer.id,
                                  "aria-label": _vm.getAriaLabelForButton(
                                    _vm.$data.button,
                                    answer.label
                                  ),
                                  tabindex: "0"
                                }
                              },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(_vm.i18n(answer.label)) +
                                    "\n              "
                                )
                              ]
                            )
                          ],
                          1
                        )
                      }),
                      0
                    )
                  ],
                  1
                )
              ]),
              _vm._v(" "),
              _vm.$data.button === 1
                ? _c(
                    "v-flex",
                    { attrs: { xs12: "" } },
                    [
                      _c("v-text-field", {
                        class: "e" + _vm.config.id + "-a" + _vm.lastAnswer.id,
                        attrs: {
                          "background-color": "#F5F5F5",
                          id: _vm.lastAnswer.id,
                          label: _vm.i18n(_vm.lastAnswer.label),
                          "aria-label": _vm.getTextfieldAriaLabel(
                            _vm.lastAnswer
                          ),
                          maxlength: "255"
                        },
                        model: {
                          value: _vm.$data.textfield,
                          callback: function($$v) {
                            _vm.$set(_vm.$data, "textfield", $$v);
                          },
                          expression: "$data.textfield"
                        }
                      })
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              _vm.config.alert.length
                ? _c(
                    "v-flex",
                    { attrs: { xs12: "" } },
                    [
                      _c("Alert", {
                        attrs: {
                          config: _vm.config,
                          configVersionNumber: _vm.configVersionNumber
                        }
                      })
                    ],
                    1
                  )
                : _vm._e()
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$5 = [];
__vue_render__$5._withStripped = true;

  /* style */
  const __vue_inject_styles__$5 = function (inject) {
    if (!inject) return
    inject("data-v-b2fc0360_0", { source: "\n/*# sourceMappingURL=YesNoButtonWithText.vue.map */", map: {"version":3,"sources":["YesNoButtonWithText.vue"],"names":[],"mappings":";AACA,kDAAkD","file":"YesNoButtonWithText.vue"}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$5 = "data-v-b2fc0360";
  /* module identifier */
  const __vue_module_identifier__$3 = undefined;
  /* functional template */
  const __vue_is_functional_template__$5 = false;
  /* component normalizer */
  function __vue_normalize__$5(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/Button/YesNoButtonWithText.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$3() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$3.styles || (__vue_create_injector__$3.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var YesNoButtonWithText = __vue_normalize__$5(
    { render: __vue_render__$5, staticRenderFns: __vue_staticRenderFns__$5 },
    __vue_inject_styles__$5,
    __vue_script__$5,
    __vue_scope_id__$5,
    __vue_is_functional_template__$5,
    __vue_module_identifier__$3,
    __vue_create_injector__$3);

let YesNoButton$1 = class YesNoButton extends Vue {
    data() {
        return {
            value: null,
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getAriaLabelForButton(value, label, id) {
        // Add a trailing period to force the narrator to pause
        switch (value) {
            case 1:
                setFocusForAccessibility(id, 0, false);
                return forceA11yNarratorSmallPause(this.i18n(label) + ', ' + this.i18n('accessibility.yes.label'));
            case 2:
                setFocusForAccessibility(id, 500, false);
                return (this.i18n(label) + ', ' + this.i18n('accessibility.no.label'));
        }
        return '';
    }
    onValueUpdate() {
        let element = lodashExports.find(this.item, { name: this.config.name.split('-')[0] });
        if (!element) {
            return;
        }
        element.value = Number(this.$data.value);
    }
};
__decorate([
    Prop()
], YesNoButton$1.prototype, "config", void 0);
__decorate([
    Prop()
], YesNoButton$1.prototype, "configVersionNumber", void 0);
__decorate([
    PropSync('data', { type: Array })
], YesNoButton$1.prototype, "item", void 0);
__decorate([
    Watch('value')
], YesNoButton$1.prototype, "onValueUpdate", null);
YesNoButton$1 = __decorate([
    Component({
        components: {
            Alert,
            Header,
        },
        mixins: [VersionnedTranslation],
    })
], YesNoButton$1);
var script$4 = YesNoButton$1;

/* script */
            const __vue_script__$4 = script$4;
            
/* template */
var __vue_render__$4 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { attrs: { "data-testid": "yesNoButton", id: _vm.config.id } },
    [
      _c("Header", {
        attrs: {
          config: _vm.config,
          configVersionNumber: _vm.configVersionNumber
        }
      }),
      _vm._v(" "),
      _c(
        "p",
        { staticClass: "mt-2" },
        [
          _c(
            "v-btn-toggle",
            {
              attrs: { divided: "" },
              model: {
                value: _vm.$data.value,
                callback: function($$v) {
                  _vm.$set(_vm.$data, "value", $$v);
                },
                expression: "$data.value"
              }
            },
            _vm._l(_vm.config.answer, function(answer) {
              return _c(
                "span",
                { key: answer.id },
                [
                  _c(
                    "v-btn",
                    {
                      class: "e" + _vm.config.id + "-a" + answer.id,
                      attrs: {
                        id: "e" + _vm.config.id + "-a" + answer.id,
                        value: answer.id,
                        "aria-label": _vm.getAriaLabelForButton(
                          _vm.$data.value,
                          answer.label,
                          "e" + _vm.config.id + "-a" + answer.id
                        ),
                        tabindex: "0"
                      }
                    },
                    [
                      _vm._v(
                        "\n          " +
                          _vm._s(_vm.i18n(answer.label)) +
                          "\n        "
                      )
                    ]
                  )
                ],
                1
              )
            }),
            0
          )
        ],
        1
      ),
      _vm._v(" "),
      _c("Alert", {
        attrs: {
          config: _vm.config,
          configVersionNumber: _vm.configVersionNumber
        }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$4 = [];
__vue_render__$4._withStripped = true;

  /* style */
  const __vue_inject_styles__$4 = undefined;
  /* scoped */
  const __vue_scope_id__$4 = undefined;
  /* functional template */
  const __vue_is_functional_template__$4 = false;
  /* component normalizer */
  function __vue_normalize__$4(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/Button/YesNoButton.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var YesNoButton = __vue_normalize__$4(
    { render: __vue_render__$4, staticRenderFns: __vue_staticRenderFns__$4 },
    __vue_inject_styles__$4,
    __vue_script__$4,
    __vue_scope_id__$4,
    __vue_is_functional_template__$4);

let SimpleChoiceRadio$1 = class SimpleChoiceRadio extends Vue {
    data() {
        return {
            value: null,
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getAriaLabel(value, label, id) {
        const ariaLabel = value === id ? this.i18n('accessibility.checked') : this.i18n('accessibility.unchecked');
        return forceA11yNarratorSmallPause(this.i18n(label) + ', ' + ariaLabel);
    }
    onValueUpdate() {
        let element = lodashExports.find(this.item, { name: this.config.name.split('-')[0] });
        if (!element) {
            return;
        }
        element.value = this.$data.value;
    }
};
__decorate([
    Prop()
], SimpleChoiceRadio$1.prototype, "config", void 0);
__decorate([
    Prop()
], SimpleChoiceRadio$1.prototype, "configVersionNumber", void 0);
__decorate([
    PropSync('data', { type: Array })
], SimpleChoiceRadio$1.prototype, "item", void 0);
__decorate([
    Watch('value')
], SimpleChoiceRadio$1.prototype, "onValueUpdate", null);
SimpleChoiceRadio$1 = __decorate([
    Component({
        components: {
            Header,
            Alert,
        },
        mixins: [VersionnedTranslation],
    })
], SimpleChoiceRadio$1);
var script$3 = SimpleChoiceRadio$1;

/* script */
            const __vue_script__$3 = script$3;
            
/* template */
var __vue_render__$3 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { attrs: { "data-testid": "simpleChoiceRadio", id: _vm.config.id } },
    [
      _c(
        "v-container",
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("Header", {
                    attrs: {
                      config: _vm.config,
                      configVersionNumber: _vm.configVersionNumber
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c(
                    "v-radio-group",
                    {
                      attrs: {
                        "aria-label": _vm.i18n("accessibility.radioButton"),
                        tabindex: "0"
                      },
                      model: {
                        value: _vm.$data.value,
                        callback: function($$v) {
                          _vm.$set(_vm.$data, "value", $$v);
                        },
                        expression: "$data.value"
                      }
                    },
                    [
                      _c(
                        "v-layout",
                        { attrs: { row: "", wrap: "" } },
                        _vm._l(_vm.config.answer, function(answer, key) {
                          return _c(
                            "v-flex",
                            {
                              key: answer.id,
                              class: !_vm.$vuetify.breakpoint.smAndDown
                                ? "flex xs6"
                                : "flex xs12"
                            },
                            [
                              _c(
                                "span",
                                [
                                  _c("v-radio", {
                                    class:
                                      "e" + _vm.config.id + "-a" + answer.id,
                                    attrs: {
                                      id: answer.id,
                                      label: _vm.i18n(answer.label),
                                      "aria-label": _vm.getAriaLabel(
                                        _vm.$data.value,
                                        answer.label,
                                        answer.id
                                      ),
                                      "aria-checked":
                                        _vm.$data.value === answer.id
                                          ? "true"
                                          : "false",
                                      tabindex: key === 0 ? 0 : -1,
                                      value: answer.id
                                    }
                                  })
                                ],
                                1
                              )
                            ]
                          )
                        }),
                        1
                      )
                    ],
                    1
                  )
                ],
                1
              ),
              _vm._v(" "),
              _vm.config.alert.length
                ? _c(
                    "v-flex",
                    { attrs: { xs12: "" } },
                    [
                      _c("Alert", {
                        attrs: {
                          config: _vm.config,
                          configVersionNumber: _vm.configVersionNumber
                        }
                      })
                    ],
                    1
                  )
                : _vm._e()
            ],
            1
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$3 = [];
__vue_render__$3._withStripped = true;

  /* style */
  const __vue_inject_styles__$3 = undefined;
  /* scoped */
  const __vue_scope_id__$3 = undefined;
  /* functional template */
  const __vue_is_functional_template__$3 = false;
  /* component normalizer */
  function __vue_normalize__$3(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/SimpleChoice/SimpleChoiceRadio.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  

  
  var SimpleChoiceRadio = __vue_normalize__$3(
    { render: __vue_render__$3, staticRenderFns: __vue_staticRenderFns__$3 },
    __vue_inject_styles__$3,
    __vue_script__$3,
    __vue_scope_id__$3,
    __vue_is_functional_template__$3);

// eslint-disable-next-line @typescript-eslint/no-var-requires
let cloneDeep = require('lodash/cloneDeep');
let MultipleChoiceCheckBoxWithText$1 = class MultipleChoiceCheckBoxWithText extends Vue {
    data() {
        return {
            checkbox: [],
            textfield: null,
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getAriaLabel(value, label, id) {
        const ariaLabel = value[(id - 1)] ? this.i18n('accessibility.checked') : this.i18n('accessibility.unchecked');
        return forceA11yNarratorSmallPause(this.i18n(label) + ', ' + ariaLabel);
    }
    getTextfieldAriaLabel(label) {
        return this.i18n('accessibility.textfield') + ', ' + this.i18n(label);
    }
    get checkBoxAnswers() {
        const answers = cloneDeep(this.config.answer);
        answers.pop();
        return answers;
    }
    get lastAnswer() {
        var _a;
        const answers = cloneDeep(this.config.answer);
        return (_a = answers.pop()) !== null && _a !== void 0 ? _a : { id: -1, label: '' };
    }
    onValueUpdate() {
        let elementCheckBox = lodashExports.find(this.item, { name: this.config.name.split('-')[0] });
        let elementTextfield = lodashExports.find(this.item, { name: this.config.name.split('-')[0] + '_Textfield' });
        if (!elementCheckBox || !elementTextfield) {
            return;
        }
        elementCheckBox.value = this.$data.checkbox;
        elementTextfield.value = this.$data.textfield;
    }
};
__decorate([
    Prop()
], MultipleChoiceCheckBoxWithText$1.prototype, "config", void 0);
__decorate([
    Prop()
], MultipleChoiceCheckBoxWithText$1.prototype, "configVersionNumber", void 0);
__decorate([
    PropSync('data', { type: Array })
], MultipleChoiceCheckBoxWithText$1.prototype, "item", void 0);
__decorate([
    Watch('checkbox'),
    Watch('textfield')
], MultipleChoiceCheckBoxWithText$1.prototype, "onValueUpdate", null);
MultipleChoiceCheckBoxWithText$1 = __decorate([
    Component({
        components: {
            Header,
            Alert,
        },
        mixins: [VersionnedTranslation],
    })
], MultipleChoiceCheckBoxWithText$1);
var script$2 = MultipleChoiceCheckBoxWithText$1;

/* script */
            const __vue_script__$2 = script$2;
            
/* template */
var __vue_render__$2 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      attrs: {
        "data-testid": "multipleChoiceCheckBoxWithText",
        id: _vm.config.id
      }
    },
    [
      _c(
        "v-container",
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { staticClass: "details", attrs: { xs12: "" } },
                [
                  _c("Header", {
                    attrs: {
                      config: _vm.config,
                      configVersionNumber: _vm.configVersionNumber
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _vm._l(_vm.checkBoxAnswers, function(answer) {
                return _c(
                  "v-flex",
                  {
                    key: answer.id,
                    class: !_vm.$vuetify.breakpoint.smAndDown
                      ? "flex xs6"
                      : "flex xs12"
                  },
                  [
                    _c(
                      "span",
                      [
                        _c("v-checkbox", {
                          class: "e" + _vm.config.id + "-a" + answer.id,
                          attrs: {
                            id: answer.id,
                            label: _vm.i18n(answer.label),
                            "aria-label": _vm.getAriaLabel(
                              _vm.$data.checkbox,
                              answer.label,
                              answer.id
                            ),
                            value: answer.id
                          },
                          model: {
                            value: _vm.$data.checkbox,
                            callback: function($$v) {
                              _vm.$set(_vm.$data, "checkbox", $$v);
                            },
                            expression: "$data.checkbox"
                          }
                        })
                      ],
                      1
                    )
                  ]
                )
              }),
              _vm._v(" "),
              _c(
                "v-flex",
                { attrs: { xs12: "" } },
                [
                  _c("v-text-field", {
                    class: "e" + _vm.config.id + "-a" + _vm.lastAnswer.id,
                    attrs: {
                      "background-color": "#F5F5F5",
                      id: _vm.lastAnswer.id,
                      label: _vm.i18n(_vm.lastAnswer.label),
                      "aria-label": _vm.getTextfieldAriaLabel(
                        _vm.lastAnswer.label
                      ),
                      maxlength: "255"
                    },
                    model: {
                      value: _vm.$data.textfield,
                      callback: function($$v) {
                        _vm.$set(_vm.$data, "textfield", $$v);
                      },
                      expression: "$data.textfield"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _vm.config.alert.length
                ? _c(
                    "v-flex",
                    { attrs: { xs12: "" } },
                    [
                      _c("Alert", {
                        attrs: {
                          config: _vm.config,
                          configVersionNumber: _vm.configVersionNumber
                        }
                      })
                    ],
                    1
                  )
                : _vm._e()
            ],
            2
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$2 = [];
__vue_render__$2._withStripped = true;

  /* style */
  const __vue_inject_styles__$2 = function (inject) {
    if (!inject) return
    inject("data-v-7887acc2_0", { source: ".details[data-v-7887acc2] {\n  margin-bottom: 15px;\n}\n\n/*# sourceMappingURL=MultipleChoiceCheckBoxWithText.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/MultipleChoice/MultipleChoiceCheckBoxWithText.vue","MultipleChoiceCheckBoxWithText.vue"],"names":[],"mappings":"AAmHA;EACA,mBAAA;AAAA;;ACjHA,6DAA6D","file":"MultipleChoiceCheckBoxWithText.vue","sourcesContent":[null,".details {\n  margin-bottom: 15px; }\n\n/*# sourceMappingURL=MultipleChoiceCheckBoxWithText.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2 = "data-v-7887acc2";
  /* module identifier */
  const __vue_module_identifier__$2 = undefined;
  /* functional template */
  const __vue_is_functional_template__$2 = false;
  /* component normalizer */
  function __vue_normalize__$2(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/MultipleChoice/MultipleChoiceCheckBoxWithText.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$2() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$2.styles || (__vue_create_injector__$2.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var MultipleChoiceCheckBoxWithText = __vue_normalize__$2(
    { render: __vue_render__$2, staticRenderFns: __vue_staticRenderFns__$2 },
    __vue_inject_styles__$2,
    __vue_script__$2,
    __vue_scope_id__$2,
    __vue_is_functional_template__$2,
    __vue_module_identifier__$2,
    __vue_create_injector__$2);

let MultipleChoiceCheckBox$1 = class MultipleChoiceCheckBox extends Vue {
    data() {
        return {
            value: [],
        };
    }
    i18n(path) {
        return this.i18nVersionned(path, 'dataCollection', this.configVersionNumber);
    }
    getAriaLabel(value, label, id) {
        const ariaLabel = value[(id - 1)] ? this.i18n('accessibility.checked') : this.i18n('accessibility.unchecked');
        // Add a trailing period to force the narrator to pause
        return this.i18n(label) + ', ' + ariaLabel + '.';
    }
    onValueUpdate() {
        let element = lodashExports.find(this.item, { name: this.config.name.split('-')[0] });
        if (!element) {
            return;
        }
        element.value = this.$data.value;
    }
};
__decorate([
    Prop()
], MultipleChoiceCheckBox$1.prototype, "config", void 0);
__decorate([
    Prop()
], MultipleChoiceCheckBox$1.prototype, "configVersionNumber", void 0);
__decorate([
    PropSync('data', { type: Array })
], MultipleChoiceCheckBox$1.prototype, "item", void 0);
__decorate([
    Watch('value')
], MultipleChoiceCheckBox$1.prototype, "onValueUpdate", null);
MultipleChoiceCheckBox$1 = __decorate([
    Component({
        components: {
            Header,
            Alert,
        },
        mixins: [VersionnedTranslation],
    })
], MultipleChoiceCheckBox$1);
var script$1 = MultipleChoiceCheckBox$1;

/* script */
            const __vue_script__$1 = script$1;
            
/* template */
var __vue_render__$1 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { attrs: { "data-testid": "multipleChoiceCheckBox", id: _vm.config.id } },
    [
      _c(
        "v-container",
        [
          _c(
            "v-layout",
            { attrs: { row: "", wrap: "" } },
            [
              _c(
                "v-flex",
                { staticClass: "details", attrs: { xs12: "" } },
                [
                  _c("Header", {
                    attrs: {
                      config: _vm.config,
                      configVersionNumber: _vm.configVersionNumber
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _vm._l(_vm.config.answer, function(answer) {
                return _c(
                  "v-flex",
                  {
                    key: answer.id,
                    class: !_vm.$vuetify.breakpoint.smAndDown
                      ? "flex xs6"
                      : "flex xs12"
                  },
                  [
                    _c(
                      "span",
                      [
                        _c("v-checkbox", {
                          class: "e" + _vm.config.id + "-a" + answer.id,
                          attrs: {
                            id: String(answer.id),
                            label: _vm.i18n(answer.label),
                            "aria-label": _vm.getAriaLabel(
                              _vm.$data.value,
                              answer.label,
                              answer.id
                            ),
                            value: answer.id
                          },
                          model: {
                            value: _vm.$data.value,
                            callback: function($$v) {
                              _vm.$set(_vm.$data, "value", $$v);
                            },
                            expression: "$data.value"
                          }
                        })
                      ],
                      1
                    )
                  ]
                )
              }),
              _vm._v(" "),
              _vm.config.alert.length
                ? _c(
                    "v-flex",
                    { attrs: { xs12: "" } },
                    [
                      _c("Alert", {
                        attrs: {
                          config: _vm.config,
                          configVersionNumber: _vm.configVersionNumber
                        }
                      })
                    ],
                    1
                  )
                : _vm._e()
            ],
            2
          )
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$1 = [];
__vue_render__$1._withStripped = true;

  /* style */
  const __vue_inject_styles__$1 = function (inject) {
    if (!inject) return
    inject("data-v-3bf2ec3c_0", { source: ".details[data-v-3bf2ec3c] {\n  margin-bottom: 15px;\n}\n\n/*# sourceMappingURL=MultipleChoiceCheckBox.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/MultipleChoice/MultipleChoiceCheckBox.vue","MultipleChoiceCheckBox.vue"],"names":[],"mappings":"AAiFA;EACA,mBAAA;AAAA;;AC/EA,qDAAqD","file":"MultipleChoiceCheckBox.vue","sourcesContent":[null,".details {\n  margin-bottom: 15px; }\n\n/*# sourceMappingURL=MultipleChoiceCheckBox.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1 = "data-v-3bf2ec3c";
  /* module identifier */
  const __vue_module_identifier__$1 = undefined;
  /* functional template */
  const __vue_is_functional_template__$1 = false;
  /* component normalizer */
  function __vue_normalize__$1(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/element/MultipleChoice/MultipleChoiceCheckBox.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__$1() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__$1.styles || (__vue_create_injector__$1.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var MultipleChoiceCheckBox = __vue_normalize__$1(
    { render: __vue_render__$1, staticRenderFns: __vue_staticRenderFns__$1 },
    __vue_inject_styles__$1,
    __vue_script__$1,
    __vue_scope_id__$1,
    __vue_is_functional_template__$1,
    __vue_module_identifier__$1,
    __vue_create_injector__$1);

let DataCollection = class DataCollection extends Vue {
    get sections() {
        return this.formConfig.config.section;
    }
    get layout() {
        return this.formConfig.config.layout;
    }
    get validateButtonClass() {
        const className = 'validateButton';
        if (this.isSubmitting) {
            return (this.vuetifyBreakpoint.smAndDown ? `mobileButton ` : `largeButton `) + className + ' btn-submit-disabled';
        }
        return (this.vuetifyBreakpoint.smAndDown ? `mobileButton ` : `largeButton `) + className;
    }
    get dataCollectionClass() {
        return this.vuetifyBreakpoint.smAndDown ? 'dataCollectionMobile' : 'dataCollectionDesktop';
    }
    i18n(path) {
        return this.$i18n.t(`dataCollection.${path}`);
    }
    /**
     * Update various config ids based on the current section and element context.
     * @param layout
     * @param section
     */
    contextualizedConfig(layout, section) {
        let buffer = lodashExports.cloneDeep(layout);
        return { ...buffer, id: `${buffer.name}-s${section.id}-e${buffer.id}` };
    }
    get getOrderedComponents() {
        return lodashExports.sortBy(this.layout, (element) => element.order);
    }
    getLayoutComponentsForSection(sectionId) {
        return this.getOrderedComponents.filter((item) => item.section === sectionId);
    }
    getComponent(type) {
        var _a;
        return (_a = FormElementComponents.find(c => (c === null || c === void 0 ? void 0 : c.type) === type)) === null || _a === void 0 ? void 0 : _a.component;
    }
    updateForm() {
        this.$emit('update-form');
    }
    isLastElement(name, sectionId) {
        const sections = lodashExports.cloneDeep(this.sections);
        const lastSection = sections.pop();
        if (!lastSection || lastSection.id !== sectionId) {
            return false;
        }
        const allOrderedComponents = lodashExports.cloneDeep(this.getLayoutComponentsForSection(sectionId));
        const lastElement = allOrderedComponents.pop();
        if (!lastElement) {
            return false;
        }
        return lastElement.name === name;
    }
};
__decorate([
    Prop()
], DataCollection.prototype, "data", void 0);
__decorate([
    Prop()
], DataCollection.prototype, "formConfig", void 0);
__decorate([
    Prop()
], DataCollection.prototype, "configVersionNumber", void 0);
__decorate([
    Prop()
], DataCollection.prototype, "vuetifyBreakpoint", void 0);
__decorate([
    Prop({ type: Boolean, required: true, default: false })
], DataCollection.prototype, "isSubmitting", void 0);
__decorate([
    PropSync('data', { type: Array })
], DataCollection.prototype, "item", void 0);
DataCollection = __decorate([
    Component({
        components: {
            MultipleChoiceCheckBox,
            MultipleChoiceCheckBoxWithText,
            SimpleChoiceRadio,
            YesNoButton,
            YesNoButtonWithText,
            ShortAnswerText,
            LongAnswerTextArea,
            DatePast,
            Section,
        },
    })
], DataCollection);
var script = DataCollection;

/* script */
            const __vue_script__ = script;
            
/* template */
var __vue_render__ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("v-layout", { attrs: { row: "", wrap: "" } }, [
    _c(
      "div",
      { attrs: { id: "dataCollection", tile: "" } },
      [
        _vm._l(_vm.sections, function(section) {
          return _c(
            "span",
            { key: section.id, class: _vm.dataCollectionClass },
            [
              _c(_vm.getComponent(section.type), {
                tag: "component",
                attrs: {
                  config: section,
                  configVersionNumber: _vm.configVersionNumber
                }
              }),
              _vm._v(" "),
              _vm._l(_vm.getLayoutComponentsForSection(section.id), function(
                element
              ) {
                return _c(
                  "span",
                  { key: element.id },
                  [
                    _c(_vm.getComponent(element.type), {
                      tag: "component",
                      attrs: {
                        config: _vm.contextualizedConfig(element, section),
                        configVersionNumber: _vm.configVersionNumber,
                        data: _vm.item
                      },
                      on: {
                        "update:data": function($event) {
                          _vm.item = $event;
                        }
                      }
                    }),
                    _vm._v(" "),
                    !_vm.isLastElement(element.name, section.id)
                      ? _c("v-divider")
                      : _vm._e()
                  ],
                  1
                )
              })
            ],
            2
          )
        }),
        _vm._v(" "),
        _c(
          "v-btn",
          { class: _vm.validateButtonClass, on: { click: _vm.updateForm } },
          [_vm._v("\n      " + _vm._s(_vm.i18n("actionButton")) + "\n    ")]
        )
      ],
      2
    )
  ])
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = function (inject) {
    if (!inject) return
    inject("data-v-2d54e78e_0", { source: "#dataCollection h2.section {\n  font-size: 20px;\n  text-transform: uppercase;\n  font-weight: bold;\n}\n#dataCollection h3 {\n  font-size: 18px;\n  font-weight: bold;\n}\n#dataCollection p {\n  font-size: 14px;\n  line-height: 25px;\n}\n#dataCollection hr {\n  margin: 22px 0 20px 0;\n}\n#dataCollection .v-text-field input,\n#dataCollection .v-text-field label {\n  font-size: 16px;\n  line-height: 55px;\n  margin: 8px 10px;\n}\n#dataCollection .v-text-field,\n#dataCollection .v-text-area {\n  border-radius: 10px;\n}\n#dataCollection .v-input--selection-controls .v-input__slot > .v-label,\n#dataCollection .v-input--selection-controls .v-radio > .v-label {\n  font-size: 16px;\n}\n#dataCollection .v-input--selection-controls .v-input__slot {\n  margin: 0;\n  padding: 0;\n}\n#dataCollection .v-input--selection-controls .v-radio {\n  margin: 15px 0 0 0;\n  padding: 0;\n}\n#dataCollection .v-input--selection-controls.v-input {\n  margin: 0;\n  padding: 0;\n}\n#dataCollection .v-input--switch .v-input--selection-controls__input {\n  margin: 18px 0 0 0;\n}\n#dataCollection .container {\n  padding: 0;\n  max-width: 100%;\n}\n#dataCollection .theme--light.v-text-field > .v-input__control > .v-input__slot:before {\n  border-color: transparent;\n}\n#dataCollection .theme--light.v-text-field:not(.v-input--has-state):hover > .v-input__control > .v-input__slot:before {\n  border-color: transparent;\n}\n#dataCollection .v-text-field > .v-input__control > .v-input__slot:after {\n  border-color: transparent;\n  background-color: transparent;\n}\n#dataCollection .theme--light.v-btn--active:before,\n#dataCollection .theme--light.v-btn--active:hover:before {\n  background-color: #00577d;\n  opacity: 1;\n}\n#dataCollection button.v-btn.v-item--active.v-btn--active {\n  color: white;\n}\n#dataCollection .v-text-field > .v-input__control > .v-input__slot {\n  margin: 0;\n}\n#dataCollection .validateButton.theme--light.v-btn.v-btn--has-bg {\n  background-color: #FCC987;\n  border-radius: 10px;\n  font-size: 20px;\n  margin: 40px auto 30px auto;\n  display: block;\n}\n#dataCollection .mobileButton {\n  width: auto;\n}\n#dataCollection .largeButton {\n  min-width: 355px;\n}\n#dataCollection .v-text-field label {\n  line-height: 20px !important;\n}\n#dataCollection .dataCollectionMobile p {\n  line-height: 120%;\n}\n#dataCollection .dataCollectionMobile .v-input--selection-controls .v-input__slot > .v-label,\n#dataCollection .dataCollectionMobile .v-input--selection-controls .v-radio > .v-label {\n  font-size: 14px;\n  line-height: 120%;\n}\n#dataCollection .dataCollectionMobile h2, #dataCollection .dataCollectionMobile h3 {\n  line-height: 120%;\n  padding: 0 0 8px 0;\n}\n\n/*# sourceMappingURL=data-collection.vue.map */", map: {"version":3,"sources":["/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/data-collection.vue","data-collection.vue"],"names":[],"mappings":"AA+HA;EAEA,eAAA;EACA,yBAAA;EACA,iBAAA;AAAA;AAJA;EAQA,eAAA;EACA,iBAAA;AAAA;AATA;EAaA,eAAA;EACA,iBAAA;AAAA;AAdA;EAkBA,qBAAA;AAAA;AAlBA;;EAuBA,eAAA;EACA,iBAAA;EACA,gBAAA;AAAA;AAzBA;;EA8BA,mBAAA;AAAA;AA9BA;;EAmCA,eAAA;AAAA;AAnCA;EAuCA,SAAA;EACA,UAAA;AAAA;AAxCA;EA4CA,kBAAA;EACA,UAAA;AAAA;AA7CA;EAiDA,SAAA;EACA,UAAA;AAAA;AAlDA;EAsDA,kBAAA;AAAA;AAtDA;EA0DA,UAAA;EACA,eAAA;AAAA;AA3DA;EA+DA,yBAAA;AAAA;AA/DA;EAmEA,yBAAA;AAAA;AAnEA;EAuEA,yBAAA;EACA,6BAAA;AAAA;AAxEA;;EA6EA,yBAAA;EACA,UAAA;AAAA;AA9EA;EAkFA,YAAA;AAAA;AAlFA;EAsFA,SAAA;AAAA;AAtFA;EAyFA,yBAAA;EACA,mBAAA;EACA,eAAA;EACA,2BAAA;EACA,cAAA;AAAA;AA7FA;EAgGA,WAAA;AAAA;AAhGA;EAmGA,gBAAA;AAAA;AAnGA;EAsGA,4BAAA;AAAA;AAtGA;EA2GA,iBAAA;AAAA;AA3GA;;EAgHA,eAAA;EACA,iBAAA;AAAA;AAjHA;EAqHA,iBAAA;EACA,kBAAA;AAAA;;ACnJA,8CAA8C","file":"data-collection.vue","sourcesContent":[null,"#dataCollection h2.section {\n  font-size: 20px;\n  text-transform: uppercase;\n  font-weight: bold; }\n\n#dataCollection h3 {\n  font-size: 18px;\n  font-weight: bold; }\n\n#dataCollection p {\n  font-size: 14px;\n  line-height: 25px; }\n\n#dataCollection hr {\n  margin: 22px 0 20px 0; }\n\n#dataCollection .v-text-field input,\n#dataCollection .v-text-field label {\n  font-size: 16px;\n  line-height: 55px;\n  margin: 8px 10px; }\n\n#dataCollection .v-text-field,\n#dataCollection .v-text-area {\n  border-radius: 10px; }\n\n#dataCollection .v-input--selection-controls .v-input__slot > .v-label,\n#dataCollection .v-input--selection-controls .v-radio > .v-label {\n  font-size: 16px; }\n\n#dataCollection .v-input--selection-controls .v-input__slot {\n  margin: 0;\n  padding: 0; }\n\n#dataCollection .v-input--selection-controls .v-radio {\n  margin: 15px 0 0 0;\n  padding: 0; }\n\n#dataCollection .v-input--selection-controls.v-input {\n  margin: 0;\n  padding: 0; }\n\n#dataCollection .v-input--switch .v-input--selection-controls__input {\n  margin: 18px 0 0 0; }\n\n#dataCollection .container {\n  padding: 0;\n  max-width: 100%; }\n\n#dataCollection .theme--light.v-text-field > .v-input__control > .v-input__slot:before {\n  border-color: transparent; }\n\n#dataCollection .theme--light.v-text-field:not(.v-input--has-state):hover > .v-input__control > .v-input__slot:before {\n  border-color: transparent; }\n\n#dataCollection .v-text-field > .v-input__control > .v-input__slot:after {\n  border-color: transparent;\n  background-color: transparent; }\n\n#dataCollection .theme--light.v-btn--active:before,\n#dataCollection .theme--light.v-btn--active:hover:before {\n  background-color: #00577d;\n  opacity: 1; }\n\n#dataCollection button.v-btn.v-item--active.v-btn--active {\n  color: white; }\n\n#dataCollection .v-text-field > .v-input__control > .v-input__slot {\n  margin: 0; }\n\n#dataCollection .validateButton.theme--light.v-btn.v-btn--has-bg {\n  background-color: #FCC987;\n  border-radius: 10px;\n  font-size: 20px;\n  margin: 40px auto 30px auto;\n  display: block; }\n\n#dataCollection .mobileButton {\n  width: auto; }\n\n#dataCollection .largeButton {\n  min-width: 355px; }\n\n#dataCollection .v-text-field label {\n  line-height: 20px !important; }\n\n#dataCollection .dataCollectionMobile p {\n  line-height: 120%; }\n\n#dataCollection .dataCollectionMobile .v-input--selection-controls .v-input__slot > .v-label,\n#dataCollection .dataCollectionMobile .v-input--selection-controls .v-radio > .v-label {\n  font-size: 14px;\n  line-height: 120%; }\n\n#dataCollection .dataCollectionMobile h2, #dataCollection .dataCollectionMobile h3 {\n  line-height: 120%;\n  padding: 0 0 8px 0; }\n\n/*# sourceMappingURL=data-collection.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier,
    createInjector, createInjectorSSR
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {};

    // For security concerns, we use only base name in production mode.
    component.__file = "/builds/cV5U5iT_k/0/trimoz/back-end/npm-packages/forms-library/src/form/components/data-collection/data-collection.vue";

    if (!component.render) {
      component.render = template.render;
      component.staticRenderFns = template.staticRenderFns;
      component._compiled = true;

      if (functional) component.functional = true;
    }

    component._scopeId = scope;

    {
      let hook;
      if (style) {
        hook = function(context) {
          style.call(this, createInjector(context));
        };
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render;
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context);
            return originalRender(h, context)
          };
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate;
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__() {
    const head = document.head || document.getElementsByTagName('head')[0];
    const styles = __vue_create_injector__.styles || (__vue_create_injector__.styles = {});
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id;
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined });

      if (!style.ids.includes(id)) {
        let code = css.source;
        let index = style.ids.length;

        style.ids.push(id);

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']');
        }

        if (!style.element) {
          const el = style.element = document.createElement('style');
          el.type = 'text/css';

          if (css.media) el.setAttribute('media', css.media);
          if (isOldIE) {
            el.setAttribute('data-group', group);
            el.setAttribute('data-next-index', '0');
          }

          head.appendChild(el);
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'));
          style.element.setAttribute('data-next-index', index + 1);
        }

        if (style.element.styleSheet) {
          style.parts.push(code);
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n');
        } else {
          const textNode = document.createTextNode(code);
          const nodes = style.element.childNodes;
          if (nodes[index]) style.element.removeChild(nodes[index]);
          if (nodes.length) style.element.insertBefore(textNode, nodes[index]);
          else style.element.appendChild(textNode);
        }
      }
    }
  }
  /* style inject SSR */
  

  
  var dataCollection = __vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    __vue_create_injector__);

const influenzaEnglish = {
    influenza: {
        general: {
            title: 'Vaccination against influenza and pneumococcal',
            notAvailable: 'The form is not available in mobile version',
            save: 'Save',
            saveAndComplete: 'Save And Complete',
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            hideCompleteForm: 'Hide completed forms',
            hideFormList: 'Display forms only after a search',
            hideFormListMessage: 'The forms are hidden. Please use the search tool to find the forms',
            requiredField: '* Indicates required field to complete the form',
            markFormAsTransmitted: 'Marked as transmitted',
            markFormAsNotTransmitted: 'Marked as not transmitted',
            exportToPdf: 'Print to pdf',
            close: 'Close',
        },
        titleAndPatient: {
            barcodeUrl: 'https://static.trimoz.com/clicsante/images/forms/ah650/barcode-AH-650-en.png',
            pageTitle: 'FLU AND PNEUMOCOCCUS<br/>VACCINATION',
            name: 'Patient’s first and last name',
            motherName: 'Mother’s first and last name',
            fatherName: 'Father’s first and last name (optional)',
            birthday: 'Date of birth',
            birthday1: 'Date of',
            birthday2: 'birth',
            age: 'Age',
            year: 'Year',
            month: 'Month',
            day: 'Day',
            sex: 'Sex',
            nam: 'Health insurance number',
            expiryDate: 'Expiry date',
            address: 'Address (number, street)',
            city: 'City',
            postalCode: 'Postal code',
            applicationNumber: 'ClicSanté App. number',
            appointmentNumber: 'Coupon number',
            placeholder: {
                birthday: 'YYYY-MM-DD',
                address: 'Number, street',
                expiryDate: 'YYYY-MM',
            },
        },
        generalInformation: {
            title: 'GENERAL INFORMATION',
            user14YearsOrOlder: 'Capable user 14 years of age or older',
            userUnder14: 'Patient under 14 years of age or adult incapable of giving consent',
            areaCode: 'Area code',
            phone: 'Home phone no',
            otherPhone: 'Other phone no.',
            cell: 'Cell',
            work: 'Work',
            email: 'Email address',
            authorizedPerson: 'Authorized person as they so declare: (last name, first name):',
            legalRepresentative: {
                mandatary: 'Mandatary',
                guardian: 'Guardian',
                curator: 'Curator',
                publicCurator: 'Public curator',
                spouse: 'Spouse (married, civil union, or common law)',
                closeRelative: 'Close relative',
                personInterest: 'Person showing a special interest in this adult',
                parentalAuthority: 'Parental authority',
            },
            placeholder: {
                authorizedPerson: 'Enter the first and last name',
            },
        },
        preImmunization: {
            title: 'PRE-IMMUNIZATION QUESTIONNAIRE*',
            header: {
                elements: 'TO BE CHECKED BY THE VACCINATOR',
                yes: 'YES',
                no: 'NO',
                NA: 'N/A',
                details: 'DETAILS',
            },
            title1: 'Health problems',
            question1: '(Has the patient experienced any recent changes in their health?\n' + 'Do they have asthma? Are they experiencing severe congestion/runny nose? Are they taking ASA or medications containing it?)',
            title2: 'Immunosuppression',
            question2: '(Is the patient taking any immunosuppressive medications?\n' + 'Are they immunocompromised or do they have an autoimmune disease?)',
            title3: 'Previous reactions',
            question3: '(Has the patient ever had a significant reaction to a vaccine or other product that required a visit to the hospital?)',
            title4: 'Pregnancy',
            question4: '(If the patient is a woman, is she pregnant?)',
            title5: 'Immunizing products',
            question5: '(Has the patient received a vaccine in the last month?)',
            title6: 'Contacts',
            question6: '(Is the user in close contact with a very severely immunocompromised person?)',
            precision: 'Precision',
            importantNote: '*For contraindications and precautions, please refer to the Inf injectable section and Inf intranasal section of the Protocole d’immunisation du Québec.',
        },
        administration: {
            title: 'ADMINISTRATION REASON (by priority order)',
            reason: {
                1: '07 - Influenza - Resident in a CHSLD',
                2: '08 - Influenza - Resident in a RPA',
                3: '09 - Influenza - Pregnant woman',
                4: '10 - Influenza - Health worker',
                5: '11 - Influenza - Chronic illness',
                6: '12 - Influenza - Others reasons',
                7: 'Out of program',
                other: 'Other reasons, specify',
                specify: 'specify: ',
            },
        },
        consentAndDecision: {
            title: 'CONSENT/DECISION',
            1: 'Information on the benefits and risks of vaccination, possible reactions and what to do after being vaccinated has been given to the patient or their legal representative.',
            2: 'The information in the sheet intended for the population targeted by the Protocole d’immunisation du Québec (PIQ) has been communicated to the patient or their legal representative.',
            3: 'The patient will be monitored for 15 minutes after they have been vaccinated.',
            decision: 'DECISION',
            patient: 'The patient or their legal representative:',
            patientConsentInfluenza: 'Consents to vaccination against influenza',
            patientRefuseInfluenza: 'Refuses vaccination against influenza',
            patientConsentPneumococcal: 'Consents to vaccination against pneumococcus',
            patientRefusePneumococcal: 'Refuses vaccination against pneumococcus',
            employee: 'In the case of an employee of a health institution:',
            employeeConsent: 'Consents to have this information forwarded to the health unit',
            consentsRefuses: {
                title: 'CONSENT/REFUSAL OBTAINED FROM:',
                patient: 'Patient',
                mandatary: 'Mandatary',
                guardian: 'Guardian',
                curator: 'Curator',
                publicCurator: 'Public Curator',
                closeRelative: 'Close relative',
                spouse: 'Spouse (married, civil union, or common law)',
                specialInterest: 'Person showing a special interest in the person',
                parentalAuthority: 'Parental authority',
            },
        },
        professionalInformation: {
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            title: 'INFORMATION ON THE PROFESSIONAL WHO OBTAINED CONSENT',
            name: 'Full name of the professional',
            profession: {
                title: 'PROFESSION',
                nurse: 'Nurse',
                physician: 'Physician',
                inhalotherapist: 'Respiratory therapist',
                midwife: 'Midwife',
                pharmacist: 'Pharmacist',
            },
            licenceNumber: 'Licence no.:',
            professionalSignature: 'Professional’s signature:',
            phoneConsent: {
                title: 'PHONE CONSENT',
                subtitle: '(Complete this section only if consent is obtained by phone.)',
                witnessName: 'Name of witness:',
                witnessDate: 'Date',
                witnessYear: 'Year',
                witnessMonth: 'Month',
                witnessDay: 'Day',
                professionalName: 'Signature of the professional who obtained phone consent:',
                professionalDate: 'Date',
                professionalYear: 'Year',
                professionalMonth: 'Month',
                professionalDay: 'Day',
            },
        },
        vaccineDetails: {
            vaccineInfluenza: 'InfluenzaPharma.vue',
            vaccinePneumococcal: 'Pneumococcal',
            influenza: {
                title: 'DETAILS OF VACCINATION - INFLUENZA',
                titleExport: 'DETAILS OF VACCINATION - INFLUENZA - PNEUMOCOCCAL',
                date: 'Date (year, month, day)',
                hour: 'Hour of vaccination',
                name: 'Vaccine Name',
                batchNumber: 'Batch number',
                dose: 'Dose / unit',
                administration: 'Route of administration',
                injectionSite: 'Injection Site',
                leftArm: 'Left arm',
                rightArm: 'Right arm',
                leftThigh: 'Left thigh',
                rightThigh: 'Right thigh',
                leftNostril: 'Right nostril',
                rightNostril: 'Left nostril',
                placeholder: {
                    date: 'YYYY-MM-DD',
                },
                type: {
                    afluria: 'Afluria',
                    agriflu: 'Agriflu',
                    fluad: 'Fluad',
                    fluadPediatric: 'Fluad pediatric',
                    flucelvax: 'Flucelvax quad',
                    flulaval: 'Flulaval tetra',
                    fluMist: 'FluMist quadrivalent',
                    fluviral: 'Fluviral',
                    fluzoneQuadrivalent: 'Fluzone quadrivalent',
                    fluzoneDose: 'Fluzone HD quadrivalent',
                    influvacTetra: 'Influvac tetra',
                    supemtek: 'Supemtek',
                    other: 'Other',
                },
                administrationType: {
                    intramuscular: 'Intramuscular',
                    intranasal: 'Intranasal',
                },
            },
            pneumococcal: {
                title: 'DETAILS OF VACCINATION - PNEUMOCOCCAL',
                date: 'Date of vaccination',
                hour: 'Hour of vaccination',
                name: 'Vaccine Name',
                batchNumber: 'Batch Number',
                dose: 'Dose / unit',
                administration: 'Route of administration',
                injectionSite: 'Injection Site',
                leftArm: 'Left arm',
                rightArm: 'Right arm',
                leftThigh: 'Left thigh',
                rightThigh: 'Right thigh',
                placeholder: {
                    date: 'YYYY-MM-DD',
                },
                type: {
                    pneumovax23: 'Pneumovax 23',
                    prevnar13: 'Prevnar 13',
                    prevnar20: 'Prevnar 20',
                    synflorix: 'Synflorix',
                    vaxneuvance: 'Vaxneuvance',
                    other: 'Other',
                },
                administrationType: {
                    intramuscular: 'Intramuscular',
                    intranasal: 'Intranasal',
                },
            },
        },
        immunization: {
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            provider: {
                title: 'INFORMATION ON IMMUNIZATION PROVIDER',
                name: 'Vaccinator’s full name',
                profession: {
                    title: 'Profession',
                    nurse: 'Nurse',
                    physician: 'Physician',
                    inhalotherapist: 'Respiratory therapist',
                    midwife: 'Midwife',
                    pharmacist: 'Pharmacist',
                },
                professionalOrder: 'Professional order',
                licenceNumber: 'Licence no',
                vaccinationSite: 'Vaccination site (LDS)',
                vaccinatorSignature: 'Vaccinator’s signature',
            },
            professionInformation: {
                title: 'INFORMATION ON THE PROFESSIONAL WHO ADMINISTERED THE VACCINE',
                subtitle: '(Only if different from vaccinator)',
                name: 'Full name of professional who administered the vaccine:',
                profession: {
                    title: 'Profession',
                    nurse: 'Nurse practitioner',
                    other: 'Other',
                    specify: 'specify:',
                },
                licenceNumber: 'Licence no',
            },
        },
        undesirableSideEffects: {
            effects: {
                title: 'Immediate Undesirable Side Effects and First Aid',
                time: 'Time',
                reactions: 'Type of reaction and description of interventions',
                firstAidOnly: 'First Aid Only :',
                responderName: 'Responder Name',
                responderLicenceNumber: 'License',
                responderDate: 'Date',
            },
            registryEntry: {
                title: 'Register Entry',
                sipmiNumber: 'SI-PMI Number',
                iclscNumber: 'I-CLSC Number',
                complete: 'Register SI-PMI / Registry entry done',
                name: 'Name',
                date: 'Date of registration',
            },
        },
        notes: {
            notes: 'Notes',
            requiredField: '* Indicates required field',
        },
        headerPage: {
            patientName: 'User’s last and first name ',
            recordNumber: 'Record no.',
        },
        footerPage: {
            title1: 'FLU AND PNEUMOCOCCUS',
            title2: 'VACCINATION',
            formRevision: 'AH-650 DT9603 (2021-10)',
            patientFile: 'PATIENT FILE',
        },
        error: {
            title: 'The form contains errors',
            message: 'Required fields are missing or contain errors...',
        },
        warning: {
            title: 'Are you sure you want to edit the content?',
            text: 'The form is completed and signed by a Vaccinator. Once the change is applied, you will need to sign and take responsibility for the entire document.',
            question: 'Do you want to continue with the changes ?',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Cancel',
            no: 'No',
            yes: 'Yes',
        },
        base: {
            errors: {
                requireField: 'Field is required',
                invalidDate: 'This date is invalid',
                invalidTime: 'This time is invalid',
                invalidNam: 'The Health Insurance Card​ is invalid',
                invalidMonth: 'This month is invalid',
                invalidPostalCode: 'Postal code is invalid',
                phone2short: 'The phone number is incorrect',
                invalidEmail: 'Invalid email format',
                iclscNumber: 'The I-CLSC number format is invalid',
            },
        },
    },
    memoryAid: {
        title: 'Checklist - Pharmacy',
        exportToPdf: 'Print to PDF',
        name: 'Patient’s last and first name:',
        nam: 'Health insurance number:',
        header: {
            elements: 'TO BE CHECKED BY THE VACCINATOR',
            yes: 'YES',
            no: 'NO',
            NA: 'N/A',
            details: 'DETAILS',
        },
        title1: 'I have validated with the patient their allergies, allergic reactions and intolerances.',
        question1: '',
        title2: 'I validated with the patient if he/she had a coagulation problem.',
        question2: '',
        title3: 'I validated with the patient if he/she had received a blood transfusion or immunoglobulin injection in the last year.',
        question3: '',
        title4: 'I validated if the patient is a health care worker.',
        question4: '',
        title5: 'I have validated with the patient if he/she has received the pneumococcal vaccine in the past (65 years and older).',
        question5: '',
        title6: 'I have validated with the patient if he/she has received the shingles vaccine in the past (50+ years).',
        question6: '',
        title7: 'I have validated the immunization history and schedule in the Immunization Registry to ensure that the vaccine is administered at the recommended time.',
        question7: '',
        title8: 'I have reminded the patient of possible side effects.',
        question8: '',
        title9: 'If the patient is less than 9 years old, I have validated whether the patient is receiving the flu vaccine for the first time.',
        question9: '',
        title10: 'If the patient is less than 9 years old, I have informed the parent or legal guardian of the next dose reminder for the child, if applicable.',
        question10: '',
        title11: 'If I have administered a pneumococcal vaccine, I have notified the patient of the follow-up for the 2nd dose, if applicable.',
        question11: '',
        title12: 'I have documented the patient record.',
        question12: '',
        title13: 'I have recorded the vaccines administered in the patient\'s Immunization Record and Immunization Booklet.',
        question13: '',
        precision: 'Precision',
        importantNote: '',
        warning: {
            title: 'Important message',
            text: 'Please note! The information indicated in the section "Checklist - Pharmacy" will not be saved.',
            question: 'To keep this information, print the checklist as a PDF. A new tab will open automatically.',
            btnPrint: 'Print checklist in PDF and save form',
            btnSave: 'Save form only',
            btnPrint1: 'Print the Checklist and the form',
            btnSave1: 'Print form only',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Cancel',
            no: 'No',
            yes: 'Yes',
        },
    },
};

const influenzaFrench = {
    influenza: {
        general: {
            title: 'VACCINATION CONTRE LA GRIPPE ET LE PNEUMOCOQUE',
            notAvailable: 'Le formulaire n\'est pas disponible en version mobile',
            save: 'Enregistrer',
            saveAndComplete: 'Enregistrer et Terminer',
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            hideCompleteForm: 'Masquer les formulaires complétés',
            hideFormList: 'Afficher les formulaires uniquement après une recherche',
            hideFormListMessage: 'Les formulaires sont masqués. Veuillez utiliser l\'outil de recherche pour faire apparaître les formulaires',
            requiredField: '* Indique un champ requis pour completer le formulaire',
            markFormAsTransmitted: 'Marquer comme transmis',
            markFormAsNotTransmitted: 'Marquer comme non transmis',
            exportToPdf: 'Imprimer en pdf',
            close: 'Fermer',
        },
        titleAndPatient: {
            barcodeUrl: 'https://static.trimoz.com/clicsante/images/forms/ah650/barcode-AH-650-fr.png',
            pageTitle: 'VACCINATION CONTRE LA GRIPPE<br/>ET LE PNEUMOCOQUE',
            name: 'Prénom et nom de l’usager',
            motherName: 'Prénom et nom de la mère',
            fatherName: 'Prénom et nom du père (optionnel)',
            birthday: 'Date de naissance',
            birthday1: 'Date de',
            birthday2: 'naissance',
            age: 'Âge',
            year: 'Année',
            month: 'Mois',
            day: 'Jour',
            sex: 'Sexe',
            nam: 'No d’assurance maladie',
            expiryDate: 'Expiration',
            address: 'Adresse (No, rue)',
            city: 'Ville',
            postalCode: 'Code postal',
            applicationNumber: 'ClicSanté No. application',
            appointmentNumber: 'No. de coupon',
            placeholder: {
                birthday: 'AAAA-MM-JJ',
                address: 'No, rue',
                expiryDate: 'AAAA-MM',
            },
        },
        generalInformation: {
            title: 'INFORMATIONS GÉNÉRALES',
            user14YearsOrOlder: 'Usager apte de 14 ans ou plus',
            userUnder14: 'Usager de moins de 14 ans ou usager majeur inapte',
            phone: 'Nº de téléphone résidence',
            otherPhone: 'Autre Nº téléphone',
            cell: 'Cellulaire',
            work: 'Travail',
            email: 'Adresse courriel',
            authorizedPerson: 'Personne autorisée selon sa déclaration',
            legalRepresentative: {
                mandatary: 'Mandataire',
                guardian: 'Tuteur',
                curator: 'Curateur',
                publicCurator: 'Curateur public',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                closeRelative: 'Proche parent',
                personInterest: 'Personne qui démontre pour le majeur un intérêt particulier',
                parentalAuthority: 'Autorité parentale',
            },
            placeholder: {
                authorizedPerson: 'Inscrire le prénom et le nom',
            },
        },
        preImmunization: {
            title: 'QUESTIONNAIRE PRÉ IMMUNISATION',
            header: {
                elements: 'ÉLÉMENTS À VALIDER PAR LE VACCINATEUR',
                yes: 'OUI',
                no: 'NON',
                NA: 'N/A',
                details: 'PRÉCISION',
            },
            title1: 'Problèmes de santé actuels',
            question1: '(L’usager a-t-il remarqué un changement récent dans son état de santé? Souffre-t-il d’asthme? L’usager présente-t-il des sécrétions nasales importantes? Prend-il de l’AAS ou un médicament qui en contient?)',
            title2: 'Immunodépression',
            question2: '(L’usager prend-t-il des médicaments immunodépresseurs? Est-il immunodéprimé ou atteint d’une maladie auto-immune?)',
            title3: 'Réactions antérieures',
            question3: '(L’usager a-t-il déjà fait une réaction importante suivant l’administration d’un vaccin ou d’un autre produit ayant nécessité une visite à l’hôpital?)',
            title4: 'Grossesse',
            question4: '(Si l’usager est une femme, est-elle présentement enceinte?)',
            title5: 'Produits immunisants',
            question5: '(L’usager a-t-il reçu un vaccin le dernier mois?)',
            title6: 'Contacts',
            question6: '(L’usager est-il en contact étroit avec une personne très gravement immunodéprimée?)',
            precision: 'Précision',
            importantNote: '* Pour connaître les contre-indications et les précautions, veuillez-vous référer à la section Vaccin grippe du Protocole d’immunisation du Québec.',
        },
        administration: {
            title: 'RAISON D’ADMINISTRATION (par ordre de priorité)',
            reason: {
                1: '07 - Influenza - Résident en CHSLD',
                2: '08 - Influenza - Résident en RPA',
                3: '09 - Influenza - Femme enceinte',
                4: '10 - Influenza - Travailleur de la santé',
                5: '11 - Influenza - Maladie chronique',
                6: '12 - Influenza - Autres raisons',
                7: 'Hors programme',
                other: 'Autres raisons, précisez',
                specify: 'précisez :',
            },
        },
        consentAndDecision: {
            title: 'CONSENTEMENT/DÉCISION',
            1: 'Les renseignements sur les avantages et les risques de la vaccination, sur les réactions possibles et sur la conduite à tenir post vaccination ont été donnés à la personne ou son représentant légal.',
            2: 'Les informations contenues dans la feuille d’information destinée à la population fournie avec le Protocole d’immunisation du Québec (PIQ) ont été communiquées à la personne ou son représentant légal.',
            3: 'Un temps de surveillance de 15 minutes sera observé après la vaccination.',
            decision: 'DÉCISION',
            patient: 'L’usager ou son représentant légal :',
            patientConsentInfluenza: 'Consent à la vaccination contre l’influenza',
            patientRefuseInfluenza: 'Refuse la vaccination contre l’influenza',
            patientConsentPneumococcal: 'Consent à la vaccination contre le pneumocoque',
            patientRefusePneumococcal: 'Refuse la vaccination contre le pneumocoque',
            employee: 'Dans le cas d’un employé d’un établissement de santé :',
            employeeConsent: 'Consent à ce que ces informations soient acheminées au bureau de santé',
            consentsRefuses: {
                title: 'CONSENTEMENT/REFUS OBTENU AUPRÈS DE :',
                patient: 'Usager',
                mandatary: 'Mandataire',
                guardian: 'Tuteur',
                curator: 'Curateur',
                publicCurator: 'Curateur public',
                closeRelative: 'Proche parent',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                specialInterest: 'Personne qui démontre pour le majeur un intérêt particulier',
                parentalAuthority: 'Autorité parentale',
            },
        },
        professionalInformation: {
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            title: 'INFORMATION SUR LE PROFESSIONNEL QUI OBTIENT LE CONSENTEMENT',
            name: 'Nom du professionnel',
            profession: {
                title: 'PROFESSION',
                nurse: 'Infirmière',
                physician: 'Médecin',
                inhalotherapist: 'Inhalothérapeute',
                midwife: 'Sage‐femme',
                pharmacist: 'Pharmacien',
            },
            licenceNumber: 'No de permis :',
            professionalSignature: 'Signature du professionnel :',
            phoneConsent: {
                title: 'CONSENTEMENT TÉLÉPHONIQUE',
                subtitle: '(Compléter cette section uniquement si le consentement est obtenu par téléphone)',
                witnessName: 'Nom du témoin :',
                witnessDate: 'Date',
                witnessYear: 'Année',
                witnessMonth: 'Mois',
                witnessDay: 'Jour',
                professionalName: 'Signature du professionnel qui obtient le consentement téléphonique :',
                professionalDate: 'Date',
                professionalYear: 'Année',
                professionalMonth: 'Mois',
                professionalDay: 'Jour',
            },
        },
        vaccineDetails: {
            vaccineInfluenza: 'Influenza',
            vaccinePneumococcal: 'Pneumocoque',
            influenza: {
                title: 'DÉTAIL DU VACCIN ADMINISTRÉ - INFLUENZA',
                titleExport: 'DÉTAIL DU VACCIN ADMINISTRÉ - INFLUENZA - PNEUMOCOQUE',
                date: 'Date de vaccination',
                hour: 'Heure de vaccination',
                name: 'Nom du vaccin',
                batchNumber: 'Numéro de lot',
                dose: 'Quantité / unité',
                administration: 'Voie d’administration',
                injectionSite: 'Site d’administration',
                leftArm: 'Bras gauche',
                rightArm: 'Bras droit',
                leftThigh: 'Cuisse gauche',
                rightThigh: 'Cuisse droite',
                leftNostril: 'Narine gauche',
                rightNostril: 'Narine droite',
                placeholder: {
                    date: 'AAAA-MM-JJ',
                },
                type: {
                    afluria: 'Afluria',
                    agriflu: 'Agriflu',
                    fluad: 'Fluad',
                    fluadPediatric: 'Fluad pédiatrique',
                    flucelvax: 'Flucelvax quad',
                    flulaval: 'Flulaval tetra',
                    fluMist: 'FluMist quadrivalent',
                    fluviral: 'Fluviral',
                    fluzoneQuadrivalent: 'Fluzone quadrivalent',
                    fluzoneDose: 'Fluzone HD quadrivalent',
                    influvacTetra: 'Influvac tetra',
                    supemtek: 'Supemtek',
                    other: 'Autre',
                },
                administrationType: {
                    intramuscular: 'Intramusculaire',
                    intranasal: 'Intranasal',
                },
            },
            pneumococcal: {
                title: 'DÉTAIL DU VACCIN ADMINISTRÉ - PNEUMOCOQUE',
                date: 'Date de vaccination',
                hour: 'Heure de vaccination',
                name: 'Nom du vaccin',
                batchNumber: 'Numéro de lot',
                dose: 'Quantité / unité',
                administration: 'Voie d’administration',
                injectionSite: 'Site d’administration',
                leftArm: 'Bras gauche',
                rightArm: 'Bras droit',
                leftThigh: 'Cuisse gauche',
                rightThigh: 'Cuisse droite',
                placeholder: {
                    date: 'AAAA-MM-JJ',
                },
                type: {
                    pneumovax23: 'Pneumovax 23',
                    prevnar13: 'Prevnar 13',
                    prevnar20: 'Prevnar 20',
                    synflorix: 'Synflorix',
                    vaxneuvance: 'Vaxneuvance',
                    other: 'Autre',
                },
                administrationType: {
                    intramuscular: 'Intramusculaire',
                    intranasal: 'Intranasal',
                },
            },
        },
        immunization: {
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            provider: {
                title: 'INFORMATION SUR LE VACCINATEUR',
                name: 'Nom du vaccinateur',
                profession: {
                    title: 'Profession',
                    nurse: 'Infirmière',
                    physician: 'Médecin',
                    inhalotherapist: 'Inhalothérapeute',
                    midwife: 'Sage‐femme',
                    pharmacist: 'Pharmacien',
                },
                professionalOrder: 'Ordre professionnel',
                licenceNumber: 'No de permis',
                vaccinationSite: 'Lieu de vaccination (LDS)',
                vaccinatorSignature: 'Signature du vaccinateur',
            },
            professionInformation: {
                title: 'INFORMATION SUR L’INTERVENANT QUI A ADMINISTRÉ LE VACCIN',
                subtitle: '(à compléter uniquement si différent du vaccinateur)',
                name: 'Nom de l’intervenant qui a administré le vaccin',
                profession: {
                    title: 'Profession',
                    nurse: 'Infirmière auxiliaire',
                    other: 'Autre',
                    specify: 'précisez',
                },
                licenceNumber: 'No de permis',
            },
        },
        undesirableSideEffects: {
            effects: {
                title: 'Effets indésirables immédiats et premiers soins',
                time: 'Heure',
                reactions: 'Type de réaction et description des interventions',
                firstAidOnly: 'Pour les premiers soins seulement :',
                responderName: 'Nom de l’intervenant',
                responderLicenceNumber: 'Permis',
                responderDate: 'Date',
            },
            registryEntry: {
                title: 'Saisie au registre',
                sipmiNumber: 'Numéro SI-PMI',
                iclscNumber: 'Numéro I-CLSC',
                complete: 'Saisie SI-PMI / Registre faite',
                name: 'Nom',
                date: 'Date de l\'inscription',
            },
        },
        notes: {
            notes: 'Notes',
            requiredField: '* Indique un champ requis',
        },
        headerPage: {
            patientName: 'Prénom et nom de l\'usager',
            recordNumber: 'NO de dossier',
        },
        footerPage: {
            title1: 'VACCINATION CONTRE LA GRIPPE',
            title2: 'ET LE PNEUMOCOQUE',
            formRevision: 'AH-650 DT9602 (2021-10)',
            patientFile: 'DOSSIER DE L\'USAGER',
        },
        error: {
            title: 'Le formulaire contient des erreurs',
            message: 'Des champs requis sont manquants ou contiennent des erreurs...',
        },
        warning: {
            title: 'Êtes-vous certain de vouloir modifier le contenu?',
            text: 'Le formulaire est terminé et signé par un Vaccinateur. Une fois le changement appliqué, vous devrez signer et assumer la responsabilité de l\'entièreté du document.',
            question: 'Désirez-vous poursuivre les modifications?',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Annuler',
            no: 'Non',
            yes: 'Oui',
        },
        base: {
            errors: {
                requireField: 'Le champ est requis',
                invalidDate: 'Cette date est invalide',
                invalidTime: 'Cette heure est invalide',
                invalidNam: 'Le numéro d\'assurance maladie est invalide',
                invalidMonth: 'Ce mois est invalide',
                invalidPostalCode: 'Le code postal est invalide',
                phone2short: 'Le téléphone est incorrect',
                invalidEmail: 'Le format du courriel est invalide',
                iclscNumber: 'Le format du numéro I-CLSC est invalide',
            },
        },
    },
    memoryAid: {
        title: 'AIDE-MÉMOIRE - PHARMACIE',
        exportToPdf: 'Imprimer en PDF',
        name: 'Prénom et nom de l’usager :',
        nam: 'No d’assurance maladie :',
        header: {
            elements: 'ÉLÉMENTS À VALIDER PAR LE VACCINATEUR',
            yes: 'OUI',
            no: 'NON',
            NA: 'N/A',
            details: 'PRÉCISION',
        },
        title1: 'J’ai validé auprès du patient ses allergies, réactions allergiques et intolérances.',
        question1: '',
        title2: 'J’ai validé auprès du patient s’il avait un problème de coagulation.',
        question2: '',
        title3: 'J’ai validé auprès du patient s’il avait reçu une transfusion sanguine ou une injection d’immunoglobulines au courant de la dernière année.',
        question3: '',
        title4: 'J’ai validé si le patient est un travailleur de la santé.',
        question4: '',
        title5: 'J’ai validé auprès du patient s’il a déjà reçu le vaccin contre le pneumocoque dans le passé (65 ans et +).',
        question5: '',
        title6: 'J’ai validé auprès du patient s’il a déjà reçu le vaccin contre le zona dans le passé (50 ans et +).',
        question6: '',
        title7: 'J’ai validé dans le Registre de vaccination les antécédents d’immunisation ainsi que le calendrier de vaccination pour m’assurer que le vaccin est administré en temps recommandé.',
        question7: '',
        title8: 'J’ai rappelé les effets secondaires possibles au patient.',
        question8: '',
        title9: 'Si le patient a moins de 9 ans, j’ai validé si le patient reçoit la première fois le vaccin contre la grippe.',
        question9: '',
        title10: 'Si le patient a moins de 9 ans, j’ai informé le parent ou tuteur légal du rappel de la prochaine dose pour l\'enfant, le cas échéant.',
        question10: '',
        title11: 'Si j’ai administré un vaccin contre le pneumocoque, j’ai avisé le patient du suivi pour la 2e dose, le cas échéant.',
        question11: '',
        title12: 'J’ai documenté le dossier‐patient.',
        question12: '',
        title13: 'J’ai inscrit les vaccins administrés au Registre de vaccination et au carnet de vaccination du patient.',
        question13: '',
        precision: 'Précision',
        importantNote: '',
        warning: {
            title: 'Message important',
            text: 'Attention ! Les informations indiquées dans la section "Aide-mémoire - pharmacie" ne seront pas enregistrées.',
            question: 'Pour conserver ces informations, imprimer l\'aide-mémoire en PDF. Un nouvel onglet s’ouvrira automatiquement.',
            btnPrint: 'Imprimer l\'aide-mémoire et enregistrer le formulaire',
            btnSave: 'Enregistrer le formulaire seulement',
            btnPrint1: 'Imprimer l\'aide mémoire et le formulaire',
            btnSave1: 'Imprimer le formulaire seulement',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Annuler',
            no: 'Non',
            yes: 'Oui',
        },
    },
};

const phxCovidFrench = {
    phxCovid: {
        general: {
            title: 'TEST ANTIGÉNIQUE DE DÉPISTAGE RAPIDE',
            notAvailable: 'Le formulaire n\'est pas disponible en version mobile',
            save: 'Enregistrer',
            saveAndComplete: 'Enregistrer et Terminer',
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            hideCompleteForm: 'Masquer les formulaires complétés',
            hideFormList: 'Afficher les formulaires uniquement après une recherche',
            hideFormListMessage: 'Les formulaires sont masqués. Veuillez utiliser l\'outil de recherche pour faire apparaître les formulaires',
            requiredField: '* Indique un champ requis pour completer le formulaire',
            markFormAsTransmitted: 'Marquer comme transmis',
            markFormAsNotTransmitted: 'Marquer comme non transmis',
            exportToPdf: 'Imprimer en pdf',
            close: 'Fermer',
        },
        screeningConsent: {
            title: 'IMPORTANT - Veuillez lire ce qui suit au patient avant de lui offre le service',
            hint: 'Avant de procéder au test antigénique de dépistage rapide, il est important de comprendre les points suivants :',
            listItem1: 'L’utilisation de cette méthode de dépistage chez les personnes ne présentant pas de symptômes est indiquée par le manufacturier, bien que pas approuvée par Santé Canada.',
            listItem2: 'Une personne ne présentant pas de symptômes et ne rencontrant pas les critères pour passer un test PCR, pourra décider de passer ce type de test afin de rencontrer une exigence relative à un voyage ou en guise de précaution avant d\'ajouter des gens dans son cercle de contacts (pour revoir les situations où il est plutôt recommandé de faire un test de dépistage dans un centre de dépistage du gouvernement, <a target="_blank" href="{consentDomain}">cliquer ici</a>).',
            listItem3: 'Le test de dépistage rapide des antigènes est différent du test PCR. Ce test de dépistage est moins sensible que le test PCR et fournit des résultats préliminaires en fonction de l’heure à laquelle il a été effectué.',
            listItem4: 'Un résultat négatif n’exclut pas une infection par la COVID-19.',
            listItem5: 'Si vous obtenez un résultat préliminaire positif à votre test de dépistage, vous devez vous isoler à domicile et suivre les instructions sur le <a target="_blank" href="{consentDomain}">site du gouvernement.</a>',
            decisionText: 'Décision de l’usager ou de son représentant légal :',
            decisionYes: 'Oui (consent à passer à l’évaluation pour le test de dépistage)',
            decisionNo: 'Non (NE PAS effectuer l’évaluation pour le test de dépistage)',
            reasonLabel: 'Si la réponse est « oui », veuillez indiquer la raison du dépistage:',
            reason1: 'Voyage*',
            reason2: 'En guise de précaution chez un personne asymptomatique',
            reason3: 'Autre:',
            reasonHint: '*Il est de la responsabilité du voyageur de vérifier les exigences en matières du type de test de dépistage de la COVID-19 avec les pays/états/provinces visités (incluant les escales) ainsi que la compagnie aérienne. Certaines exigences pour le transit sont applicables.',
        },
        acknowledgementConsent: {
            title: 'CONSENTEMENT, ACCUSÉ DE RÉCEPTION ET CONFIRMATION:',
            hint: 'Je consens à recevoir le test antigénique de dépistage rapide de la COVID-19 Panbio d’Abbott (le « test ») effectué par un professionnel de la santé employé d\'un pharmacien propriétaire (le « professionnel »). En donnant mon consentement, je confirme que :',
            listItem1: 'Le patient ou son mandataire a confirmé que les réponses qui seront fournies aux questions d’évaluation du patient sont véridiques au mieux de sa connaissance, et reconnaît que si l’une de ces réponses est fausse, il peut être tenu responsable de tout préjudice causé.',
            listItem2: 'Le patient ou son mandataire accepte que les réponses fournies aux questions d’évaluation du patient ci-dessous soient utilisées pour évaluer la nécessité d’un test de dépistage de la COVID-19; si l’on conclut qu’il n’y a pas lieu de faire passer un test de dépistage de la COVID-19, le patient ou son mandataire accepte cette recommandation.',
            listItem3: 'Aucune déclaration ni garantie n’est formulée quant au moment où le test de dépistage rapide des antigènes pour la COVID-19 est effectué. Aucune déclaration ou garantie n’est formulée stipulant qu’un résultat négatif au test de dépistage rapide des antigènes pour la COVID-19 entraînera l’admission du patient ou de son mandataire dans la juridiction où il souhaite se rendre. En outre, le patient ou son mandataire reconnaît et accepte qu\'un pharmacien propriétaire, Pharmaprix, ses sociétés affiliées, ses directeurs, ses dirigeants, ses employés, ou ses agents ne sauraient en aucun cas être tenus responsables des pertes entraînées par l’impossibilité du patient ou de son mandataire d’être admis, ou d’être admis tardivement, dans la juridiction où il a l’intention de se rendre.',
            listItem4: 'Le patient ou son mandataire a compris et accepté les limites et les risques associés au test rapide, notamment le fait que le test rapide est un test rapide de diagnostic in vitro pour la détection qualitative de l’antigène (Ag) du SRAS-CoV-2 dans des échantillons de prélèvement nasal humain provenant de personnes qui répondent aux critères cliniques ou épidémiologiques de la COVID-19; que le test rapide est destiné à être utilisé comme aide au diagnostic de l’infection par le SRAS-CoV-2; que le test rapide peut être utilisé dans tout environnement de laboratoire et hors laboratoire qui répond à certaines exigences; que le test rapide ne fournit que des résultats préliminaires; qu’un résultat négatif n’exclut pas l’infection par le SRAS-CoV-2 et ne peut être utilisé comme seule base pour les décisions concernant le traitement ou d’autres décisions de gestion; que les résultats négatifs doivent être combinés avec les observations cliniques, les antécédents du patient et les informations épidémiologiques; et que le test rapide n’est pas destiné à être utilisé comme test de dépistage du SRAS-CoV-2 chez les donneurs. Le patient ou son mandataire convient et reconnaît en outre que : (i) le test rapide n’a été testé et approuvé que pour les personnes symptomatiques; (ii) il n’y a aucune preuve que le test rapide est efficace pour les personnes asymptomatiques; et (iii) l’absence d’un résultat positif préliminaire au test rapide ne signifie pas l’absence du SRAS-CoV-2. Ainsi, le patient ou son mandataire comprend que le test rapide comporte certaines limites et peut ne pas être précis, efficace ou approprié dans toutes les circonstances. Le pharmacien propriétaire, Pharmaprix Inc. ou Loblaw Inc. ne garantissent ni ne représentent l’exactitude, l’efficacité ou la pertinence du test rapide. Par la présente, j’accepte et reconnais ces limites du test rapide et je renonce à toute réclamation future contre le pharmacien propriétaire, Pharmaprix Inc. ou Loblaw Inc. concernant l’exactitude, l’efficacité et la pertinence du test rapide.',
            listItem5: 'Le patient ou son mandataire a été informé de la nature, des risques matériels, des conséquences, des effets secondaires, des bienfaits attendus et des solutions de rechange au test rapide.',
            listItem6: 'Le patient ou son mandataire comprend qu’il aura l’occasion de poser des questions au sujet du dépistage ou du test et de recevoir des réponses à toutes ses questions, avant d’effectuer le test de dépistage rapide de la COVID-19.',
            consent1: 'Consent à recevoir le test de dépistage',
            refuse1: 'Refuse de recevoir le test de dépistage',
            consent2: 'Accepte les limitations et les risques associés au test',
            refuse2: 'Refuse les limitations et les risques associés au test',
            consentsObtainedFrom: {
                title: 'Consentement obtenu auprès de:',
                user: 'User',
                proxy: 'Mandataire',
                guardian: 'Tuteur',
                legalGuardian: 'Curateur',
                publicGuardian: 'Curateur public',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                closeRelative: 'Proche parent',
                specialInterest: 'Personne qui démontre pour le majeur un intérêt particulier',
                parentalAuthority: 'Autorité parentale',
            },
            legalRepresentative: {
                mandatary: 'Mandataire',
                guardian: 'Tuteur',
                curator: 'Curateur',
                publicCurator: 'Curateur public',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                closeRelative: 'Proche parent',
                personInterest: 'Personne qui démontre pour le majeur un intérêt particulier',
                parentalAuthority: 'Autorité parentale',
            },
        },
        titleAndPatient: {
            pageTitle: 'TEST ANTIGÉNIQUE DE DÉPISTAGE RAPIDE',
            name: 'Prénom et nom de l’usager',
            motherName: 'Prénom et nom de la mère',
            fatherName: 'Prénom et nom du père (optionnel)',
            birthday: 'Date de naissance',
            birthday1: 'Date de',
            birthday2: 'naissance',
            age: 'Âge',
            year: 'Année',
            month: 'Mois',
            day: 'Jour',
            sex: 'Sexe',
            namPrint: 'No d’assurance maladie',
            nam: 'No d’assurance maladie (si disponible)',
            expiryDate: 'Expiration',
            address: 'Adresse (No, rue)',
            city: 'Ville',
            postalCode: 'Code postal',
            applicationNumber: 'ClicSanté No. application',
            appointmentNumber: 'No. de coupon',
            placeholder: {
                birthday: 'AAAA-MM-JJ',
                address: 'No, rue',
                expiryDate: 'AAAA-MM',
            },
            email: 'e-mail',
            phoneNumber: 'Numéro de téléphone',
        },
        generalInformation: {
            title: 'RENSEIGNEMENTS SUR LE PATIENT',
            user14YearsOrOlder: 'Usager apte de 14 ans ou plus',
            userUnder14: 'Usager de moins de 14 ans ou usager majeur inapte',
            phone: 'Nº de téléphone résidence',
            otherPhone: 'Autre Nº téléphone',
            cell: 'Cellulaire',
            work: 'Travail',
            email: 'Adresse courriel',
            authorizedPerson: 'Personne autorisée selon sa déclaration',
            legalRepresentative: {
                mandatary: 'Mandataire',
                guardian: 'Tuteur',
                curator: 'Curateur',
                publicCurator: 'Curateur public',
                spouse: 'Conjoint (marié, union civile ou de fait)',
                closeRelative: 'Proche parent',
                personInterest: 'Personne qui démontre pour le majeur un intérêt particulier',
                parentalAuthority: 'Autorité parentale',
            },
            placeholder: {
                authorizedPerson: 'Inscrire le prénom et le nom',
            },
        },
        results: {
            title: 'RÉSULTATS ET RENSEIGNEMENTS DU TEST DE DÉPISTAGE DE LA COVID-19',
            typeOfCovidTest: 'Type de test de dépistage de la COVID-19 administré',
            lot: 'Lot',
            expiryDate: 'Date d\'expiration',
            dateOfSpecimenCollection: 'Date de la collecte du prélèvement',
            timeOfSpecimenCollection: 'Heure de la collecte du prélèvement',
            nameOfPharmacist: 'Nom du professionel',
            pharmacistLicenseNumber: 'Numéro de permis',
            pharmacistTitle: 'Titre',
            testResult: 'Résultat du test de dépistage de la COVID-19',
            testResult2: 'En cas de résultat non concluant, documentez le résultat du deuxième test',
            testResultNegative: 'Négatif',
            testResultPreliminaryPositive: 'Préliminaires positif',
            testResultInconclusive: 'Non concluant',
            placeholder: {
                date: 'AAAA-MM-JJ',
            },
            professionalTitle: {
                nurse: 'Infirmière',
                physician: 'Médecin',
                inhalotherapist: 'Inhalothérapeute',
                midwife: 'Sage‐femme',
                pharmacist: 'Pharmacien',
            },
            onlyCertificate: 'Imprimer le certificat',
        },
        preImmunization: {
            title: 'QUESTIONNAIRE PRÉ IMMUNISATION',
            header: {
                elements: 'ÉLÉMENTS À VALIDER PAR LE VACCINATEUR',
                yes: 'OUI',
                no: 'NON',
                NA: 'N/A',
                details: 'PRÉCISION',
            },
            title1: 'Problèmes de santé actuels',
            question1: '(L’usager a-t-il remarqué un changement récent dans son état de santé? Souffre-t-il d’asthme? L’usager présente-t-il des sécrétions nasales importantes? Prend-il de l’AAS ou un médicament qui en contient?)',
            title2: 'Immunodépression',
            question2: '(L’usager prend-t-il des médicaments immunodépresseurs? Est-il immunodéprimé ou atteint d’une maladie auto-immune?)',
            title3: 'Réactions antérieures',
            question3: '(L’usager a-t-il déjà fait une réaction importante suivant l’administration d’un vaccin ou d’un autre produit ayant nécessité une visite à l’hôpital?)',
            title4: 'Grossesse',
            question4: '(Si l’usager est une femme, est-elle présentement enceinte?)',
            title5: 'Produits immunisants',
            question5: '(L’usager a-t-il reçu un vaccin le dernier mois?)',
            title6: 'Contacts',
            question6: '(L’usager est-il en contact étroit avec une personne très gravement immunodéprimée?)',
            precision: 'Précision',
            importantNote: 'Si la réponse à l’une des questions ci-dessus est OUI, ne continuez pas. Veuillez aviser le patient de s’isoler et d’effectuer un autotest rapide sans certificat*.  En cas de refus catégorique d\'un patient asymptomatique de s’isoler et faire un autotest, vous pouvez appliquer votre jugement professionnel en évaluant la balance des inconvénients incluant les risques pour votre santé.\n* Suivre toutes les consignes sur la COVID disponibles au https://www.quebec.ca/sante/problemes-de-sante/a-z/coronavirus-2019. Si le résultat de l’autotest est négatif et qu’il ne s’agit pas d’une situation de contact, vous pouvez procéder avec le test avec certificat selon votre jugement professionnel.',
        },
        evaluationQuestions: {
            title: 'QUESTIONS D\'ÉVALUATION DU PATIENT',
            header: {
                elements: '',
                yes: 'OUI',
                no: 'NON',
                NA: 'N/A',
                details: 'PRÉCISION',
            },
            phxCovidExperiencingSymptomsQuestion1: 'Présentez-vous actuellement l’un ou l’autre des symptômes suivants ?',
            phxCovidExperiencingSymptomsQuestion2: '(Fièvre, Perte soudaine de l’odorat sans congestion nasale (avec ou sans perte de goût), Grande fatigue, Perte d’appétit importante, Douleurs musculaires généralisées, Mal de tête, toux (nouvelle ou aggravée), Essoufflement, Difficulté à respirer, Mal de gorge, Écoulement nasal associé à un autre symptôme de la COVID-19, Nausées, Vomissements, Diarrhée, Maux de ventre)',
            phxCovidReceivedPositiveExposureNotificationQuestion1: 'Au cours des 14 derniers jours, avez-vous une notification d’exposition par l’application Alerte COVID ou un avis d’un contact qui a testé positif?',
            phxCovidReceivedPositiveExposureNotificationQuestion2: 'Si vous avez déjà fait un test PCR et que vous avez obtenu un résultat négatif, sélectionnez « Non ».',
            phxCovidReceivedPositivePcrTestQuestion1: 'Avez-vous reçu un résultat positif au test PCR pour la COVID-19 au cours des 90 derniers jours?',
            phxCovidReceivedPositivePcrTestQuestion2: '',
            phxCovidTravelledAndAdvisedToQuarantineQuestion1: 'Au cours des 14 derniers jours, avez-vous voyagé à l’extérieur du Canada ET été avisé de vous mettre en quarantaine conformément aux <a target="_blank" href="https://voyage.gc.ca/voyage-covid">exigences fédérales en matière de quarantaine</a>?',
            phxCovidTravelledAndAdvisedToQuarantineQuestion2: '',
            phxCovidAskedToGetAScreeningTestQuestion1: 'Un médecin, un professionnel de la santé ou une autorité de santé publique vous a-t-il demandé de faire un test de dépistage ?',
            phxCovidExperiencingSymptomsYesHint: 'Oui (s\'isoler et effectuer un autotest rapide*)',
            phxCovidReceivedPositiveExposureNotificationYesHint: 'Oui (le patient doit s’isoler)',
            phxCovidReceivedPositivePcrTestYesHint: 'Oui (le patient peut rester positif après la fin de la contagion)',
            phxCovidTravelledAndAdvisedToQuarantineYesHint: 'Oui (s’isoler et effectuer un autotest rapide*)',
            phxCovidAskedToGetAScreeningTestYesHint: 'Oui (s’isoler et effectuer un autotest rapide*)',
            precision: 'Précision',
            importantNote: 'Si la réponse à l’une des questions ci-dessus est OUI, ne continuez pas. Veuillez aviser le patient de s’isoler et d’effectuer un autotest rapide sans certificat*.  En cas de refus catégorique d\'un patient asymptomatique de s’isoler et faire un autotest, vous pouvez appliquer votre jugement professionnel en évaluant la balance des inconvénients incluant les risques pour votre santé.',
            importantNote2: '* Suivre toutes les consignes sur la COVID disponibles au ',
            importantNoteUrl: 'https://www.quebec.ca/sante/problemes-de-sante/a-z/coronavirus-2019',
            importantNote3: 'Si le résultat de l’autotest est négatif et qu’il ne s’agit pas d’une situation de contact, vous pouvez procéder avec le test avec certificat selon votre jugement professionnel.',
        },
        professionalInformation: {
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            title: 'INFORMATION SUR LE PROFESSIONNEL QUI OBTIENT LE CONSENTEMENT',
            name: 'Nom du professionnel',
            profession: {
                title: 'PROFESSION',
                titlePrint: 'Profession',
                atp: 'Assistant technique en pharmacie',
                pharmacist: 'Pharmacien',
                nurse: 'Infirmière',
                other: 'Autre, spécifiez :',
            },
            licenceNumber: 'No de permis (si pharmacien):',
            professionalSignature: 'Signature du professionnel :',
            phoneConsent: {
                title: 'CONSENTEMENT TÉLÉPHONIQUE',
                subtitle: '(Compléter cette section uniquement si le consentement est obtenu par téléphone)',
                witnessName: 'Nom du témoin :',
                witnessDate: 'Date',
                witnessYear: 'Année',
                witnessMonth: 'Mois',
                witnessDay: 'Jour',
                professionalName: 'Signature du professionnel qui obtient le consentement téléphonique :',
                professionalDate: 'Date',
                professionalYear: 'Année',
                professionalMonth: 'Mois',
                professionalDay: 'Jour',
            },
        },
        immunization: {
            getMyInformations: 'Inscrire mes informations',
            completeMyProfile: 'Compléter mon profil',
            provider: {
                title: 'INFORMATION SUR LE VACCINATEUR',
                name: 'Nom du vaccinateur',
                profession: {
                    title: 'Profession',
                    nurse: 'Infirmière',
                    physician: 'Médecin',
                    inhalotherapist: 'Inhalothérapeute',
                    midwife: 'Sage‐femme',
                    pharmacist: 'Pharmacien',
                },
                professionalOrder: 'Ordre professionnel',
                licenceNumber: 'No de permis',
                vaccinationSite: 'Lieu de vaccination (LDS)',
                vaccinatorSignature: 'Signature du vaccinateur',
            },
            professionInformation: {
                title: 'INFORMATION SUR L’INTERVENANT QUI A ADMINISTRÉ LE VACCIN',
                subtitle: '(à compléter uniquement si différent du vaccinateur)',
                name: 'Nom de l’intervenant qui a administré le vaccin',
                profession: {
                    title: 'Profession',
                    nurse: 'Infirmière auxiliaire',
                    other: 'Autre',
                    specify: 'précisez',
                },
                licenceNumber: 'No de permis',
            },
        },
        undesirableSideEffects: {
            effects: {
                title: 'Effets indésirables immédiats et premiers soins',
                time: 'Heure',
                reactions: 'Type de réaction et description des interventions',
                firstAidOnly: 'Pour les premiers soins seulement :',
                responderName: 'Nom de l’intervenant',
                responderLicenceNumber: 'Permis',
                responderDate: 'Date',
            },
            registryEntry: {
                title: 'Saisie au registre',
                sipmiNumber: 'Numéro SI-PMI',
                iclscNumber: 'Numéro I-CLSC',
                complete: 'Saisie SI-PMI / Registre faite',
                name: 'Nom',
                date: 'Date de l\'inscription',
            },
        },
        notes: {
            notes: 'Notes',
            requiredField: '* Indique un champ requis',
        },
        headerPage: {
            patientName: 'Nom et prénom de l\'usager',
            recordNumber: 'NO de dossier',
        },
        footerPage: {
            title1: 'FORMULAIRE D’ÉVALUATION CONFIDENTIEL POUR',
            title2: 'LE TEST ANTIGÉNIQUE DE DÉPISTAGE RAPIDE',
            formRevision: 'Rev.04FEV2022',
            patientFile: 'DOSSIER DE L\'USAGER',
        },
        error: {
            title: 'Le formulaire contient des erreurs',
            message: 'Des champs requis sont manquants ou contiennent des erreurs...',
        },
        warning: {
            title: 'Êtes-vous certain de vouloir modifier le contenu?',
            text: 'Le formulaire est terminé et signé. Une fois le changement appliqué, vous devrez signer et assumer la responsabilité de l\'entièreté du document.',
            question: 'Désirez-vous poursuivre les modifications?',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Annuler',
            no: 'Non',
            yes: 'Oui',
        },
        base: {
            errors: {
                requireField: 'Le champ est requis',
                invalidDate: 'Cette date est invalide',
                invalidTime: 'Cette heure est invalide',
                invalidNam: 'Le numéro d\'assurance maladie est invalide',
                invalidMonth: 'Ce mois est invalide',
                invalidPostalCode: 'Le code postal est invalide',
                phone2short: 'Le téléphone est incorrect',
                invalidEmail: 'Le format du courriel est invalide',
                iclscNumber: 'Le format du numéro I-CLSC est invalide',
            },
        },
    },
    memoryAid: {
        title: 'AIDE-MÉMOIRE - PHARMACIE',
        exportToPdf: 'Imprimer en PDF',
        name: 'Nom et prénom de l’usager :',
        nam: 'No d’assurance maladie :',
        header: {
            elements: 'ÉLÉMENTS À VALIDER PAR LE VACCINATEUR',
            yes: 'OUI',
            no: 'NON',
            NA: 'N/A',
            details: 'PRÉCISION',
        },
        title1: 'J’ai validé auprès du patient ses allergies, réactions allergiques et intolérances.',
        question1: '',
        title2: 'J’ai validé auprès du patient s’il avait un problème de coagulation.',
        question2: '',
        title3: 'J’ai validé auprès du patient s’il avait reçu une transfusion sanguine ou une injection d’immunoglobulines au courant de la dernière année.',
        question3: '',
        title4: 'J’ai validé si le patient est un travailleur de la santé.',
        question4: '',
        title5: 'J’ai validé auprès du patient s’il a déjà reçu le vaccin contre le pneumocoque dans le passé (65 ans et +).',
        question5: '',
        title6: 'J’ai validé auprès du patient s’il a déjà reçu le vaccin contre le zona dans le passé (50 ans et +).',
        question6: '',
        title7: 'J’ai validé dans le Registre de vaccination les antécédents d’immunisation ainsi que le calendrier de vaccination pour m’assurer que le vaccin est administré en temps recommandé.',
        question7: '',
        title8: 'J’ai rappelé les effets secondaires possibles au patient.',
        question8: '',
        title9: 'Si le patient a moins de 9 ans, j’ai validé si le patient reçoit la première fois le vaccin contre la grippe.',
        question9: '',
        title10: 'Si le patient a moins de 9 ans, j’ai informé le parent ou tuteur légal du rappel de la prochaine dose pour l\'enfant, le cas échéant.',
        question10: '',
        title11: 'Si j’ai administré un vaccin contre le pneumocoque, j’ai avisé le patient du suivi pour la 2e dose, le cas échéant.',
        question11: '',
        title12: 'J’ai documenté le dossier‐patient.',
        question12: '',
        title13: 'J’ai inscrit les vaccins administrés au Registre de vaccination et au carnet de vaccination du patient.',
        question13: '',
        precision: 'Précision',
        importantNote: '',
        warning: {
            title: 'Message important',
            text: 'Attention ! Les informations indiquées dans la section "Aide-mémoire - pharmacie" ne seront pas enregistrées.',
            question: 'Pour conserver ces informations, imprimer l\'aide-mémoire en PDF. Un nouvel onglet s’ouvrira automatiquement.',
            btnPrint: 'Imprimer l\'aide-mémoire et enregistrer le formulaire',
            btnSave: 'Enregistrer le formulaire seulement',
            btnPrint1: 'Imprimer l\'aide mémoire et le formulaire',
            btnSave1: 'Imprimer le formulaire seulement',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Annuler',
            no: 'Non',
            yes: 'Oui',
        },
    },
};

const phxCovidEnglish = {
    phxCovid: {
        general: {
            title: 'RAPID ANTIGEN SCREENING TEST',
            notAvailable: 'The form is not available in mobile version',
            save: 'Save',
            saveAndComplete: 'Save And Complete',
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            hideCompleteForm: 'Hide completed forms',
            hideFormList: 'Display forms only after a search',
            hideFormListMessage: 'The forms are hidden. Please use the search tool to find the forms',
            requiredField: '* Indicates required field to complete the form',
            markFormAsTransmitted: 'Marked as transmitted',
            markFormAsNotTransmitted: 'Marked as not transmitted',
            exportToPdf: 'Print to pdf',
            close: 'Close',
        },
        screeningConsent: {
            title: 'IMPORTANT – Please read the following to the patient before offering the service.',
            hint: 'Before proceeding with the rapid antigen screening, it is important to understand the following points:',
            listItem1: 'The use of this screening method in symptom-free individuals is indicated by the manufacturer, although not approved by Health Canada.',
            listItem2: 'A person who is not symptomatic and does not meet the criteria for PCR testing may decide to undergo antigen screening to meet a travel requirement or as a precautionary measure before adding people to their circle of contacts (to review situations where testing at a government testing center is recommended, <a href="{consentDomain}" target="_blank">click here</a>).',
            listItem3: 'Rapid antigen screening is different from the PCR test. This screening test is less sensitive than the PCR test and provides preliminary results depending at the time it was performed.',
            listItem4: 'A negative result does not rule out a COVID-19 infection.',
            listItem5: 'If you get a preliminary positive result from your screening test, you should isolate yourself at home and follow instructions described on the <a href="{consentDomain}" target="_blank">government website</a>.',
            decisionText: 'Decision of the user or their legal representative:',
            decisionYes: 'Yes (agrees to proceed with the assessment for the screening test)',
            decisionNo: 'No (DO NOT carry out the screening assessment)',
            reasonLabel: 'If the answer is “yes,” please indicate the reason for the screening:',
            reason1: 'Travel*',
            reason2: 'As a precautionary measure in an asymptomatic individual',
            reason3: 'Other:',
            reasonHint: '*It is the traveller’s responsibility to check the requirements for the type of COVID-19 test with the country/state/province visited (including stopovers) and the airline. Some transit requirements apply.',
        },
        acknowledgementConsent: {
            title: 'Consent, Acknowledgment and Confirmation:',
            hint: 'I consent to receive the Abbott Panbio COVID-19 Rapid Antigen Screening (“the screening”) performed by a health care professional employed by a pharmacist-owner (the “Professional”). In providing my consent, I confirm that:',
            listItem1: 'The patient or their proxy has confirmed that the responses that will be provided to the patient assessment questions are true to the best of their knowledge, and acknowledges that if any of such responses are untrue, they may be held liable for any resulting harm that is caused.',
            listItem2: 'The patient or their proxy agrees that the answers provided to the patient assessment questions below may be used to assess the need for COVID-19 screening; if it is concluded that COVID-19 screening is not required, the patient or their proxy will accept this recommendation.',
            listItem3: 'No representation or warranty has been made as to the timing of the rapid antigen screening for COVID-19. No representation or warranty has been made stating that a negative result of the COVID-19 rapid antigen screening will result in the patient or their proxy gaining admission to the jurisdiction to which they intend to travel. Furthermore, the patient or their proxy acknowledges and agrees that a pharmacist-owner, Pharmaprix, its affiliates, directors, officers, employees, or agents shall in no event be liable for any loss resulting from the patient’s or their proxy’s inability to gain admission, or delayed admission, to the jurisdiction to which they intend to travel.',
            listItem4: 'The patient or their proxy has understood and accepted the limitations and risks associated with the rapid screening, including the fact that the rapid screening is a rapid in vitro diagnostic test for the qualitative detection of SARS-CoV-2 antigen (Ag) in human nasal swab specimens from individuals who meet the clinical or epidemiological criteria of COVID-19; that the rapid screening is intended to be used as an aid in the diagnosis of SARS-CoV-2 infection; that the rapid screening may be used in any laboratory and non-laboratory environment that meets certain requirements; that the rapid screening provides only preliminary results; that a negative result does not exclude SARS-CoV-2 infection and cannot be used as the sole basis for treatment or other management decisions; that negative results must be combined with clinical findings, patient history and epidemiological information; and that the rapid screening is not intended for use as a screening test for SARS-CoV-2 in donors. The patient or their proxy further agrees and acknowledges that: (i) the rapid screening has been tested and approved only for symptomatic individuals; (ii) there is no evidence that the rapid screening is effective for asymptomatic individuals; and (iii) the absence of a preliminary positive rapid screening result does not mean the absence of SARS-CoV-2. As such, the patient or their proxy understands that the rapid screening has certain limitations and may not be accurate, effective or appropriate in all circumstances. The pharmacist-owner, Pharmaprix Inc. or Loblaw Inc. does not guarantee or represent the accuracy, efficacy or suitability of the rapid screening. I hereby accept and acknowledge these limitations of the rapid screening and waive any future claims against the pharmacist-owner, Pharmaprix Inc. or Loblaw Inc. regarding the accuracy, efficacy and suitability of the rapid screening.',
            listItem5: 'The patient or their proxy has been informed of the nature, material risks, consequences, side effects, expected benefits and alternatives to the rapid screening.',
            listItem6: 'The patient or their proxy understands that they will have the opportunity to ask questions about the screening or test, and to have all questions answered, prior to receiving the COVID-19 rapid screening.',
            consent1: 'Consents to receive the screening test',
            refuse1: 'Refuses to receive the screening test',
            consent2: 'Accepts the limitations and risks associated with the test',
            refuse2: 'Refuses the limitations and risks associated with the test',
            consentsObtainedFrom: {
                title: 'CONSENT OBTAINED FROM:',
                user: 'User',
                proxy: 'Proxy',
                guardian: 'Guardian',
                legalGuardian: 'Legal guardian',
                publicGuardian: 'Public guardian',
                spouse: 'Spouse (married, civil union or common-law)',
                closeRelative: 'Close relative',
                specialInterest: 'Person showing a special interest in the person',
                parentalAuthority: 'Parental authority',
            },
            professionalInfo: {
                title: 'Information on the professional obtaining consent',
                name: 'Name of the professional:',
                license: 'License number (if pharmacist):',
                profession: 'Profession (pharmacist/pharmacy technician)',
                signature: 'Signature of professional:',
            },
            legalRepresentative: {
                mandatary: 'Proxy',
                guardian: 'Guardian',
                curator: 'Legal guardian',
                publicCurator: 'Public guardian',
                spouse: 'Spouse (married, civil union, or common law)',
                closeRelative: 'Close relative',
                personInterest: 'Person showing a special interest in this adult',
                parentalAuthority: 'Parental authority',
            },
        },
        titleAndPatient: {
            pageTitle: 'PRIVATE RAPID ANTIGEN SCREENING ASSESSMENT FORM',
            name: 'Patient’s first and last name',
            motherName: 'Mother’s first and last name',
            fatherName: 'Father’s first and last name (optional)',
            birthday: 'Date of birth',
            birthday1: 'Date of',
            birthday2: 'birth',
            age: 'Age',
            year: 'Year',
            month: 'Month',
            day: 'Day',
            sex: 'Sex',
            nam: 'Health insurance number (if available)',
            namPrint: 'Health insurance number',
            expiryDate: 'Expiry date',
            address: 'Address (number, street)',
            city: 'City',
            postalCode: 'Postal code',
            applicationNumber: 'ClicSanté App. number',
            appointmentNumber: 'Coupon number',
            placeholder: {
                birthday: 'YYYY-MM-DD',
                address: 'Number, street',
                expiryDate: 'YYYY-MM',
            },
            email: 'Email',
            phoneNumber: 'Phone number',
        },
        generalInformation: {
            title: 'PATIENT INFORMATION',
            user14YearsOrOlder: 'Capable user 14 years of age or older',
            userUnder14: 'Patient under 14 years of age or adult incapable of giving consent',
            areaCode: 'Area code',
            phone: 'Home phone no',
            otherPhone: 'Other phone no.',
            cell: 'Cell',
            work: 'Work',
            email: 'Email address',
            authorizedPerson: 'Authorized person as they so declare: (last name, first name):',
            legalRepresentative: {
                mandatary: 'Proxy',
                guardian: 'Guardian',
                curator: 'Legal guardian',
                publicCurator: 'Public guardian',
                spouse: 'Spouse (married, civil union, or common law)',
                closeRelative: 'Close relative',
                personInterest: 'Person showing a special interest in this adult',
                parentalAuthority: 'Parental authority',
            },
            placeholder: {
                authorizedPerson: 'Enter the first and last name',
            },
        },
        results: {
            title: 'COVID-19 TEST RESULTS AND INFORMATION',
            typeOfCovidTest: 'Type of COVID-19 test administered',
            lot: 'Lot',
            expiryDate: 'Expiry date',
            dateOfSpecimenCollection: 'Date of specimen collection',
            timeOfSpecimenCollection: 'Time of specimen collection',
            nameOfPharmacist: 'Name of the professional',
            pharmacistLicenseNumber: 'Licence number',
            pharmacistTitle: 'Title',
            testResult: 'COVID-19 test result',
            testResult2: 'If the result is inconclusive, document the result of the second test',
            testResultNegative: 'Negative',
            testResultPreliminaryPositive: 'Preliminary positive',
            testResultInconclusive: 'Inconclusive',
            placeholder: {
                date: 'DD-MM-YYYY',
            },
            professionalTitle: {
                nurse: 'Nurse',
                physician: 'Physician',
                inhalotherapist: 'Inhalotherapist',
                midwife: 'Midwife',
                pharmacist: 'Pharmacist',
            },
            onlyCertificate: 'Print certificate',
        },
        preImmunization: {
            title: 'PRE-IMMUNIZATION QUESTIONNAIRE*',
            header: {
                elements: 'TO BE CHECKED BY THE VACCINATOR',
                yes: 'YES',
                no: 'NO',
                NA: 'N/A',
                details: 'DETAILS',
            },
            title1: 'Health problems',
            question1: '(Has the patient experienced any recent changes in their health?\n' + 'Do they have asthma? Are they experiencing severe congestion/runny nose? Are they taking ASA or medications containing it?)',
            title2: 'Immunosuppression',
            question2: '(Is the patient taking any immunosuppressive medications?\n' + 'Are they immunocompromised or do they have an autoimmune disease?)',
            title3: 'Previous reactions',
            question3: '(Has the patient ever had a significant reaction to a vaccine or other product that required a visit to the hospital?)',
            title4: 'Pregnancy',
            question4: '(If the patient is a woman, is she pregnant?)',
            title5: 'Immunizing products',
            question5: '(Has the patient received a vaccine in the last month?)',
            title6: 'Contacts',
            question6: '(Is the user in close contact with a very severely immunocompromised person?)',
            precision: 'Precision',
            importantNote: '*For contraindications and precautions, please refer to the Inf injectable section and Inf intranasal section of the Protocole d’immunisation du Québec.',
        },
        evaluationQuestions: {
            title: 'PATIENT ASSESSMENT QUESTIONS',
            header: {
                elements: '',
                yes: 'YES',
                no: 'NO',
                NA: 'N/A',
                details: 'DETAILS',
            },
            phxCovidExperiencingSymptomsQuestion1: 'Are you currently experiencing any of the following symptoms?',
            phxCovidExperiencingSymptomsQuestion2: '(fever, sudden loss of smell without nasal congestion (with or without loss of taste), severe fatigue, severe loss of appetite, generalized muscle pain, headache, cough (new or worsened), shortness of breath or breathing difficulty , sore throat, Runny nose associated with another COVID-19 symptom, nausea or vomiting, diarrhea, stomach pain)',
            phxCovidReceivedPositiveExposureNotificationQuestion1: 'In the last 14 days, have you received an exposure notification through the COVID Alert application?',
            phxCovidReceivedPositiveExposureNotificationQuestion2: '( If you already went for a PCR test and got a negative result, select “No.” )',
            phxCovidReceivedPositivePcrTestQuestion1: 'Have you received a positive PCR test for COVID-19 in the last 90 days?',
            phxCovidReceivedPositivePcrTestQuestion2: '',
            phxCovidTravelledAndAdvisedToQuarantineQuestion1: 'In the last 14 days, have you travelled outside of Canada AND been advised to quarantine per the <a target="_blank" href="https://travel.gc.ca/travel-covid">federal quarantine requirements</a>?',
            phxCovidTravelledAndAdvisedToQuarantineQuestion2: '',
            phxCovidAskedToGetAScreeningTestQuestion1: 'Have you been asked to get a screening test by a doctor, health care provider, or public health unit?\n',
            phxCovidAskedToGetAScreeningTestQuestion2: '(Has the patient received a vaccine in the last month?)',
            phxCovidExperiencingSymptomsYesHint: 'Yes (advise to self-isolate do a rapid self-test )*',
            phxCovidReceivedPositiveExposureNotificationYesHint: 'Yes (refer to a testing centre)*',
            phxCovidReceivedPositivePcrTestYesHint: 'Yes (advise patient that test results may remain positive after contagious period)',
            phxCovidTravelledAndAdvisedToQuarantineYesHint: 'Yes (advise to self-isolate do a rapid self-test )*',
            phxCovidAskedToGetAScreeningTestYesHint: 'Yes (advise to self-isolate do a rapid self-test )*',
            precision: 'Precision',
            importantNote: '*For contraindications and precautions, please refer to the Inf injectable section and Inf intranasal section of the Protocole d’immunisation du Québec.',
        },
        professionalInformation: {
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            title: 'INFORMATION ON THE PROFESSIONAL WHO OBTAINED CONSENT',
            name: 'Full name of the professional',
            profession: {
                title: 'PROFESSION',
                titlePrint: 'Profession',
                atp: 'Pharmacy technical assistant',
                pharmacist: 'Pharmacist',
                nurse: 'Nurse',
                other: 'Other, specify :',
            },
            licenceNumber: 'Licence no.:',
            professionalSignature: 'Professional’s signature:',
            phoneConsent: {
                title: 'PHONE CONSENT',
                subtitle: '(Complete this section only if consent is obtained by phone.)',
                witnessName: 'Name of witness:',
                witnessDate: 'Date',
                witnessYear: 'Year',
                witnessMonth: 'Month',
                witnessDay: 'Day',
                professionalName: 'Signature of the professional who obtained phone consent:',
                professionalDate: 'Date',
                professionalYear: 'Year',
                professionalMonth: 'Month',
                professionalDay: 'Day',
            },
        },
        immunization: {
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            provider: {
                title: 'INFORMATION ON IMMUNIZATION PROVIDER',
                name: 'Vaccinator’s full name',
                profession: {
                    title: 'Profession',
                    nurse: 'Nurse',
                    physician: 'Physician',
                    inhalotherapist: 'Respiratory therapist',
                    midwife: 'Midwife',
                    pharmacist: 'Pharmacist',
                },
                professionalOrder: 'Professional order',
                licenceNumber: 'Licence no',
                vaccinationSite: 'Vaccination site (LDS)',
                vaccinatorSignature: 'Vaccinator’s signature',
            },
            professionInformation: {
                title: 'INFORMATION ON THE PROFESSIONAL WHO ADMINISTERED THE VACCINE',
                subtitle: '(Only if different from vaccinator)',
                name: 'Full name of professional who administered the vaccine:',
                profession: {
                    title: 'Profession',
                    nurse: 'Nurse practitioner',
                    other: 'Other',
                    specify: 'specify:',
                },
                licenceNumber: 'Licence no',
            },
        },
        undesirableSideEffects: {
            effects: {
                title: 'Immediate Undesirable Side Effects and First Aid',
                time: 'Time',
                reactions: 'Type of reaction and description of interventions',
                firstAidOnly: 'First Aid Only :',
                responderName: 'Responder Name',
                responderLicenceNumber: 'License',
                responderDate: 'Date',
            },
            registryEntry: {
                title: 'Register Entry',
                sipmiNumber: 'SI-PMI Number',
                iclscNumber: 'I-CLSC Number',
                complete: 'Register SI-PMI / Registry entry done',
                name: 'Name',
                date: 'Date of registration',
            },
        },
        notes: {
            notes: 'Notes',
            requiredField: '* Indicates required field',
        },
        headerPage: {
            patientName: 'User’s last and first name ',
            recordNumber: 'Record no.',
        },
        footerPage: {
            title1: 'PRIVATE RAPID ANTIGEN SCREENING',
            title2: 'ASSESSMENT FORM',
            formRevision: 'AH-650 DT9603 (2021-10)',
            patientFile: 'PATIENT FILE',
        },
        error: {
            title: 'The form contains errors',
            message: 'Required fields are missing or contain errors...',
        },
        warning: {
            title: 'Are you sure you want to edit the content?',
            text: 'The form is completed and signed. Once the change is applied, you will need to sign and take responsibility for the entire document.',
            question: 'Do you want to continue with the changes ?',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Cancel',
            no: 'No',
            yes: 'Yes',
        },
        base: {
            errors: {
                requireField: 'Field is required',
                invalidDate: 'This date is invalid',
                invalidTime: 'This time is invalid',
                invalidNam: 'The Health Insurance Card​ is invalid',
                invalidMonth: 'This month is invalid',
                invalidPostalCode: 'Postal code is invalid',
                phone2short: 'The phone number is incorrect',
                invalidEmail: 'Invalid email format',
                iclscNumber: 'The I-CLSC number format is invalid',
            },
        },
    },
    memoryAid: {
        title: 'Checklist - Pharmacy',
        exportToPdf: 'Print to PDF',
        name: 'Patient’s last and first name:',
        nam: 'Health insurance number:',
        header: {
            elements: 'TO BE CHECKED BY THE VACCINATOR',
            yes: 'YES',
            no: 'NO',
            NA: 'N/A',
            details: 'DETAILS',
        },
        title1: 'I have validated with the patient their allergies, allergic reactions and intolerances.',
        question1: '',
        title2: 'I validated with the patient if he/she had a coagulation problem.',
        question2: '',
        title3: 'I validated with the patient if he/she had received a blood transfusion or immunoglobulin injection in the last year.',
        question3: '',
        title4: 'I validated if the patient is a health care worker.',
        question4: '',
        title5: 'I have validated with the patient if he/she has received the pneumococcal vaccine in the past (65 years and older).',
        question5: '',
        title6: 'I have validated with the patient if he/she has received the shingles vaccine in the past (50+ years).',
        question6: '',
        title7: 'I have validated the immunization history and schedule in the Immunization Registry to ensure that the vaccine is administered at the recommended time.',
        question7: '',
        title8: 'I have reminded the patient of possible side effects.',
        question8: '',
        title9: 'If the patient is less than 9 years old, I have validated whether the patient is receiving the flu vaccine for the first time.',
        question9: '',
        title10: 'If the patient is less than 9 years old, I have informed the parent or legal guardian of the next dose reminder for the child, if applicable.',
        question10: '',
        title11: 'If I have administered a pneumococcal vaccine, I have notified the patient of the follow-up for the 2nd dose, if applicable.',
        question11: '',
        title12: 'I have documented the patient record.',
        question12: '',
        title13: 'I have recorded the vaccines administered in the patient\'s Immunization Record and Immunization Booklet.',
        question13: '',
        precision: 'Precision',
        importantNote: '',
        warning: {
            title: 'Important message',
            text: 'Please note! The information indicated in the section "Checklist - Pharmacy" will not be saved.',
            question: 'To keep this information, print the checklist as a PDF. A new tab will open automatically.',
            btnPrint: 'Print checklist in PDF and save form',
            btnSave: 'Save form only',
            btnPrint1: 'Print the Checklist and the form',
            btnSave1: 'Print form only',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Cancel',
            no: 'No',
            yes: 'Yes',
        },
    },
};

const as624English = {
    as624: {
        general: {
            title: 'IMMUNIZATION FORM',
            save: 'Save',
            markFormAsTransmitted: 'Marked as transmitted',
            markFormAsNotTransmitted: 'Marked as not transmitted',
            exportToPdf: 'Print to pdf',
        },
        footer: {
            save: 'Save',
            saveAndComplete: 'Save And Complete',
            markFormAsTransmitted: 'Marked as transmitted',
            markFormAsNotTransmitted: 'Marked as not transmitted',
            close: 'Close',
        },
        titleAndPatient: {
            pageTitle: 'IMMUNIZATION FORM',
            barcodeUrl: 'https://static.trimoz.com/clicsante/images/forms/as624/barcode-AS-624-en-1.png',
            fileNo: 'File number',
            name: 'Patient’s last and first name',
            motherName: 'Mother’s last and first name',
            fatherName: 'Father\'s last and first name (optional)',
            birthday: 'Date of birth',
            birthday1: 'Date of',
            birthday2: 'birth',
            year: 'Year',
            month: 'Month',
            day: 'Day',
            sex: 'Sex',
            gender: 'Gender',
            nam: 'Health insurance number',
            expiryDate: 'Expiry date',
            address: 'Address (number, street)',
            city: 'City',
            postalCode: 'Postal code',
            applicationNumber: 'ClicSanté App. number',
            appointmentNumber: 'Coupon number',
            placeholder: {
                birthday: 'YYYY-MM-DD',
                address: 'Number, street',
                expiryDate: 'YYYY-MM',
            },
        },
        generalInformation: {
            title: 'GENERAL INFORMATION',
            user14YearsOrOlder: 'Capable user 14 years of age or older',
            userUnder14: 'Patient under 14 years of age or adult incapable of giving consent',
            phone: 'Home phone no',
            otherPhone: 'Other phone no.',
            cell: 'Cell',
            work: 'Work',
            email: 'Email address',
            authorizedPerson: 'Authorized person as they so declare: (last name, first name):',
            legalRepresentative: {
                mandatary: 'Mandatary',
                guardian: 'Guardian',
                spouse: 'Spouse (married, civil union, or common law)',
                closeRelative: 'Close relative of the incapable adult',
                personInterest: 'Person showing a special interest in this adult',
                parentalAuthority: 'Parental authority',
            },
            placeholder: {
                authorizedPerson: 'Enter the last and first name',
            },
        },
        preImmunization: {
            title: 'PRE-IMMUNIZATION QUESTIONNAIRE*',
            header: {
                elements: 'ELEMENTS TO BE VALIDATED BY THE PROFESSIONAL<br/>AUTHORIZED TO CARRY OUT THE VACCINATION',
                yes: 'YES',
                no: 'NO',
                NA: 'N/A',
                details: 'DETAILS',
                export: {
                    element1: 'ELEMENTS TO BE VALIDATED BY THE PROFESSIONAL',
                    element2: 'AUTHORIZED TO CARRY OUT THE VACCINATION',
                },
            },
            title1: '* Health problems',
            question1: 'Do you currently have a change in your health status, from your usual status?',
            title2: '* Immunosuppression',
            question2: 'Do you have a weakened immune system due to a disease or a medication ?',
            title3: '* Previous reactions',
            question3: 'Do <b>any</b> of the following apply to you? \n' +
                '<div style="margin-left: 20px;">- You have had a severe allergic reaction that required emergency medical attention</div>' +
                '<div style="margin-left: 20px;">- You have had a fairly severe reaction to a vaccine that required medical attention</div>',
            title4: '* Coagulation Disorders',
            question4: 'Do you have any bleeding disorders that require medical attention or anticoagulants?',
            title5: '* Immunizing or blood product',
            question5: 'Have you received a blood transfusion or immunoglobulin injection in the last 11 months?',
            title6: '* Pregnancy',
            question6: '<b>If you are a woman</b>, are you pregnant? ',
            title7: '',
            question7: '* If the patient is an infant less than 6 months of age, did the mother take a biologic agent (e.g., Enbrel, Remicade, Humira) for the treatment of an autoimmune disease (e.g., Crohn\'s disease, rheumatoid arthritis, lupus, or psoriasis) during pregnancy?',
            precision: 'Precision',
            importantNote: 'Légende: N/A: Not applicable',
        },
        administration: {
            title: 'ADMINISTRATION REASON',
            reason: {
                shingles: {
                    title: 'Shingles',
                    1: 'Immunocompromised',
                    2: 'Others reasons',
                },
                influenza: {
                    title: 'Influenza',
                    1: 'Resident in a CHSLD',
                    2: 'Resident in a RPA',
                    3: 'Pregnant woman',
                    4: 'Health worker',
                    5: 'Chronic illness',
                    6: 'Others reasons',
                },
                dcat: {
                    title: 'dcaT',
                    1: 'Pregnant woman (whooping cough)',
                    2: 'Others reasons',
                },
                covid: {
                    title: 'COVID-19',
                    1: 'Resident in a CHSLD',
                    2: 'Resident in a RPA',
                    3: 'Pregnant woman',
                    4: 'Health worker',
                    5: 'Chronic illness',
                    6: 'Others reasons',
                },
                mpox: {
                    title: 'Mpox',
                    1: 'Pre-exposure - immunocompromised',
                    2: 'Pre-exposure - non-immunocompromised',
                    3: 'Post-exposure - immunocompromised',
                    4: 'Post-exposure - non-immunocompromised',
                    5: 'Other reasons',
                },
            },
        },
        consentAndDecision: {
            title: 'CONSENT/DECISION',
            decision1: 'Information about the benefits and risks of vaccination, possible reactions and what to do after vaccination has been given to the user or his/her legal representative.',
            decision2: 'The patient will be monitored for 15 minutes after they have been vaccinated.',
            decision3: 'The patient will be monitored for 30 minutes after they have been vaccinated.',
            decision: 'DECISION',
            patient: 'The patient or their legal representative:',
            decisionConsentVaccination: 'Consent to vaccination (add comment if necessary):',
            decisionConsentVaccinationNote: '',
            decisionRefuseVaccination: 'Refuse the following vaccine(s)',
            decisionRefuseVaccinationNote: '',
            decisionCounterIndication: 'Has the following contraindication:',
            decisionCounterIndicationNote: '',
            decisionNotApplicable: 'Does not apply',
            recommendation: 'VACCINATION IS BASED ON A RECOMMENDATION FROM THE BUREAU DE SANTÉ',
            employee: 'In the case of an employee of a health institution:',
            employeeConsent: 'Consents to have this information forwarded to the health unit',
            consentsRefuses: {
                title: 'CONSENT/REFUSAL OBTAINED FROM:',
                patient: 'Patient',
                parentalAuthority: 'Parental authority',
                spouse: 'Spouse (married, civil union, or common law)',
                mandatary: 'Mandatary',
                specialInterest: 'Person showing a special interest in the person',
                closeRelative: 'Close relative',
                guardian: 'Guardian',
            },
        },
        professionalInformation: {
            title: 'INFORMATION ON THE PROFESSIONAL WHO OBTAINED CONSENT',
            clearMyInformations: 'Clear my information',
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            name: 'Full name of the professional',
            profession: {
                title: 'PROFESSION',
                nurse: 'Nurse',
                physician: 'Physician',
                inhalotherapist: 'Respiratory therapist',
                midwife: 'Midwife',
                pharmacist: 'Pharmacist',
            },
            licenceNumber: 'Licence no.:',
            professionalSignature: 'Professional’s signature:',
        },
        phoneConsent: {
            title: 'PHONE CONSENT',
            subtitle: '(Complete this section only if consent is obtained by phone.)',
            clearMyInformations: 'Clear my information',
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            professionalName: 'Signature of the professional',
            professionalDate: 'Date',
            witnessName: 'Name of witness:',
            witnessDate: 'Date',
            profession: {
                title: 'Profession',
                nurse: 'Nurse',
                physician: 'Physician',
                inhalotherapist: 'Respiratory therapist',
                midwife: 'Midwife',
                pharmacist: 'Pharmacist',
            },
        },
        vaccineDetails: {
            title: 'DETAILS OF IMMUNIZING PRODUCTS ADMINISTERED',
            getMyInformations: 'Enter my information',
            clearMyInformations: 'Clear my information',
            completeMyProfile: 'Complete my profile',
            category: 'Vaccine Category',
            date: 'Vaccination Date',
            dateSubText: '(year, month, day)',
            hour: 'Vaccination Hour (00:00)',
            name: 'Immunizing Product (vaccine)',
            batchNumber: 'Batch Number',
            dose: 'Quantity / Unit',
            administration: 'Route of administration',
            injectionSite: 'Injection Site',
            vaccinePlace: 'Vaccination site (LDS):',
            placeholder: {
                date: 'AAAA-MM-DD',
            },
            injectionSiteType: {
                leftArm: 'Left arm',
                rightArm: 'Right arm',
                leftThigh: 'Left thigh',
                rightThigh: 'Right thigh',
                other: 'Other',
            },
            administrationType: {
                intramuscular: 'Intramuscular',
                intranasal: 'Intranasal',
                intradermic: 'Intradermal',
                subcutaneous: 'Subcutaneous',
                oral: 'Oral',
            },
            professionInformation: {
                title: 'INFORMATION ON WHO ADMINISTERED THE VACCINE',
                name: 'Name:',
                profession: 'Profession :',
                signature: 'Signature :',
                licenceNumber: 'Licence no.:',
            },
            zona: {
                shingrix: 'Shingrix',
            },
            flu: {
                afluria: 'Afluria',
                fluad: 'Fluad',
                fluadPediatric: 'Fluad pédiatrique',
                flucelvax: 'Flucelvax quad',
                flulaval: 'Flulaval tetra',
                fluMist: 'FluMist quadrivalent',
                fluzoneQuadrivalent: 'Fluzone quadrivalent',
                fluzoneDose: 'Fluzone HD quadrivalent',
                influvacTetra: 'Influvac tetra',
                supemtek: 'Supemtek',
                other: 'Autre',
            },
            covid: {
                'Moderna': 'Spikevax (Moderna)',
                'Pfizer-BioNTech': 'Comirnaty (Pfizer-BioNTech)',
            },
            dcat: {
                actHib: 'Act-Hib',
                adacel: 'Adacel',
                adacelPolio: 'Adacel-Polio',
                boostrix: 'Boostrix',
                boostrixPolio: 'Boostrix-Polio',
                imovaxPolio: 'Imovax Polio',
                infanrixHexa: 'Infanrix hexa',
                infanrixIPVHib: 'Infanrix-IPV/Hib',
                pediacel: 'Pediacel',
                td: 'Td Adsorbées',
                pentacel: 'Pentacel',
            },
            mpox: {
                imvamune: 'Imvamune',
            },
            hepatitis: {
                avaxim: 'Avaxim',
                engerixB: 'Engerix-B',
                havrix: 'Havrix',
                recombivaxHb: 'Recombivax HB',
                twinrix: 'Twinrix',
                vaqta: 'Vaqta',
                prehevbrio: 'PREHEVBRIO',
            },
            meningitis: {
                bexsero: 'Bexsero',
                menactra: 'Menactra',
                menjugate: 'Menjugate',
                menveo: 'Menveo',
                neisvacC: 'Neisvac-C',
                nimenrix: 'Nimenrix',
                trumenba: 'Trumenba',
            },
            pneumococcal: {
                pneumovax23: 'Pneumovax 23',
                prevnar13: 'Prevnar 13',
                prevnar20: 'Prevnar 20',
                synflorix: 'Synflorix',
                vaxneuvance: 'Vaxneuvance',
            },
            rabies: {
                imovaxRage: 'Imovax Rage',
                rabAvert: 'RabAvert',
            },
            rotavirus: {
                rotarix: 'Rotarix',
                rotateq: 'RotaTeq',
            },
            rro: {
                mmrII: 'M-M-R II',
                priorix: 'Priorix',
                priorixTetra: 'Priorix Tetra',
                proQuad: 'ProQuad',
                varilrix: 'Varilrix',
                varivaxIII: 'Varivax III',
            },
            tuberculosis: {
                bcg: 'BCG lyophilisé avec glutamate',
                tubersol: 'Tubersol',
            },
            vph: {
                cervarix: 'Cervarix',
                gardasil9: 'Gardasil 9',
            },
            travel: {
                dukoral: 'Dukoral',
                ixiaro: 'Ixiaro',
                yfVax: 'Yf Vax',
                vaxchora: 'Vaxchora',
            },
            vrs: {
                arexvy: 'Arexvy',
                abrysvo: 'Abrysvo',
            },
            vrs_baby: {
                nirsevimab: 'Nirsevimab',
            },
            typhoid: {
                typhimVi: 'Typhim Vi',
                vivotif: 'Vivotif',
            },
            avian_influenza: {
                arepanrix: 'Arepanrix H5N1',
            },
            vaccineCategories: {
                zona: 'Zona',
                flu: 'Influenza',
                covid: 'COVID-19',
                mpox: 'MPOX',
                dcat: 'dcaT',
                hepatitis: 'Hepatitis',
                pneumococcal: 'Pneumococcal',
                meningitis: 'Meningitis',
                rabies: 'Rabies',
                rotavirus: 'Rotavirus',
                rro: 'Measles, mumps, rubella and chickenpox',
                tuberculosis: 'Tuberculosis',
                vph: 'Human papillomavirus',
                travel: 'Travellers',
                vrs: 'Respiratory Syncytial Virus (RSV)',
                vrs_baby: 'Respiratory Syncytial Virus (RSV) Baby',
                typhoid: 'Typhoid vaccine',
                avian_influenza: 'Avian influenza A (H5N1)',
            },
            vaccineCategoriesPdf: {
                zona: 'Zona',
                flu: 'Influenza',
                covid: 'COVID-19',
                mpox: 'MPOX',
                dcat: 'Dcat',
                hepatitis: 'Hepatitis',
                pneumococcal: 'Pneumococcal',
                meningitis: 'Meningitis',
                rabies: 'Rabies',
                rotavirus: 'Rotavirus',
                rro: 'Rougeole, rubéole, oreillons et varicelle',
                tuberculosis: 'Tuberculosis',
                vph: 'Human papillomavirus',
                travel: 'Travellers',
                vrs: 'Respiratory Syncytial Virus (RSV)',
                vrs_baby: 'Respiratory Syncytial Virus (RSV) Baby',
                typhoid: 'Typhoid vaccine',
                avian_influenza: 'Avian influenza A (H5N1)',
            },
            professionList: {
                nurse: 'Nurse',
                physician: 'Physician',
                inhalotherapist: 'Respiratory therapist',
                midwife: 'Midwife',
                pharmacist: 'Pharmacist',
                other: 'Other',
            },
        },
        oldVaccineDetails: {
            flu: {
                agriflu: 'Agriflu',
                fluviral: 'Fluviral',
                influvac: 'Influvac',
            },
            covid: {
                'Spikevax-XBB-1-5': 'Spikevax XBB.1.5',
                'Comirnaty-XBB15-6m-4y': 'Comirnaty XBB.1.5 - 6 months-4 years',
                'Comirnaty-XBB15-5-11': 'Comirnaty XBB.1.5 - 5-11 years old',
                'Comirnaty-XBB15-12': 'Comirnaty XBB.1.5 - 12 years +',
                'Spikevax-Bivalent-BA4-BA5': 'Spikevax Bivalent BA4-BA5 (Moderna)',
                'Moderna-Bivalent': 'Spikevax Bivalent BA1 (Moderna)',
                'Moderna-Kids': 'Spikevax (Moderna) 6 months - 5 years',
                'Pfizer-BioNTech-Kids': 'Comirnaty (Pfizer-BioNTech) 5-11 years old',
                'Covishield': 'Covishield',
                'Nuvaxovid': 'Nuvaxovid (Novavax)',
                'Novavax': 'Nuvaxovid (Novavax)',
                'AstraZeneca': 'Vaxzevria (AstraZeneca)',
                'Comirnaty-Bivalent-BA4-BA5': 'Comirnaty Bivalent BA4-BA5',
                'Pfizer-BioNTech-Kids-6m-4y': 'Comirnaty (Pfizer-BioNTech) 6 months - 4 years',
                'Comirnaty-Bivalent-BA4-BA5-Kids': 'Comirnaty Bivalent BA4-BA5  5-11 years old',
                'Jcovden': 'Jcovden',
                'Covifenz': 'Covifenz',
            },
            dcat: {
                hiberix: 'Hiberix',
            },
        },
        notes: {
            notes: 'Notes',
            requiredField: '* Indicates required field',
        },
        headerPage: {
            patientName: 'User’s last and first name ',
            recordNumber: 'Record no.',
        },
        footerPage: {
            title1: 'IMMUNIZATION FORM',
            title2: 'VACCINATION',
            formRevision: 'AS-624 DT9225 (rev. 2023-03)',
            patientFile: 'PATIENT FILE',
        },
        error: {
            title: 'The form contains errors',
            message: 'Required fields are missing or contain errors...',
        },
        warning: {
            title: 'Are you sure you want to edit the content?',
            text: 'The form is completed and signed by a Vaccinator. Once the change is applied, you will need to sign and take responsibility for the entire document.',
            question: 'Do you want to continue with the changes ?',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Cancel',
            no: 'No',
            yes: 'Yes',
        },
        base: {
            errors: {
                requireField: 'Field is required',
                invalidDate: 'This date is invalid',
                invalidTime: 'This time is invalid',
                invalidNam: 'The Health Insurance Card is invalid',
                invalidMonth: 'This month is invalid',
                invalidPostalCode: 'Postal code is invalid',
                phone2short: 'The phone number is incorrect',
                invalidEmail: 'Invalid email format',
                iclscNumber: 'The I-CLSC number format is invalid',
            },
        },
    },
};

const as6242024English = {
    as6242024: {
        general: {
            title: 'IMMUNIZATION FORM',
            save: 'Save',
            markFormAsTransmitted: 'Marked as transmitted',
            markFormAsNotTransmitted: 'Marked as not transmitted',
            exportToPdf: 'Print to pdf',
        },
        footer: {
            save: 'Save',
            saveAndComplete: 'Save And Complete',
            markFormAsTransmitted: 'Marked as transmitted',
            markFormAsNotTransmitted: 'Marked as not transmitted',
            close: 'Close',
        },
        titleAndPatient: {
            pageTitle: 'IMMUNIZATION FORM',
            logo: 'logo',
            barCode: 'Barcode',
            barcodeUrl: 'https://static.trimoz.com/clicsante/images/forms/as624/barcode-AS-624-en-1.png',
            fileNo: 'File number',
            siPmiId: 'N° ID SI-PMI',
            name: 'Patient’s last and first name',
            motherName: 'Mother’s last and first name',
            fatherName: 'Father\'s last and first name (optional)',
            unknownMother: 'Unknown',
            unknownFather: 'Unknown',
            birthday: 'Date of birth',
            birthday1: 'Date of',
            birthday2: 'birth',
            year: 'Year',
            month: 'Month',
            day: 'Day',
            sex: 'Sex',
            gender: 'Gender',
            nam: 'Health insurance number',
            expiryDate: 'Expiry date',
            address: 'Address (number, street)',
            city: 'City',
            postalCode: 'Postal code',
            placeholder: {
                birthday: 'YYYY-MM-DD',
                address: 'Number, street',
                expiryDate: 'YYYY-MM',
            },
        },
        generalInformation: {
            title: 'GENERAL INFORMATION',
            user14YearsOrOlder: 'Capable user 14 years of age or older',
            userUnder14: 'Patient under 14 years of age or adult incapable of giving consent',
            regInd: 'Reg. ind.',
            regIndAriaLabel: 'Regional indicator',
            phone: 'Phone no',
            cell: 'Cell',
            home: 'Home',
            email: 'If you censent to be contacted by email, please enter it here:',
            authorizedPerson: 'Authorized person as they so declare (last name, first name):',
            authorizedPersonRegInd: 'Authorizes person\'s regional indicator',
            legalRepresentative: {
                mandatary: 'Mandatary',
                guardian: 'Guardian',
                spouse: 'Spouse (married, civil union, or common law)',
                closeRelative: 'Close relative of the incapable adult',
                personInterest: 'Person showing a special interest in this adult',
                parentalAuthority: 'Parental authority',
            },
        },
        preImmunization: {
            title: 'PRE-IMMUNIZATION QUESTIONNAIRE*',
            header: {
                elements: 'ELEMENTS TO ANALYZE',
                yes: 'YES',
                no: 'NO',
                NA: 'N/A',
                details: 'DETAILS',
            },
            title1: 'Health problems',
            question1: 'Does the user have a health condition that requires medical monitoring or regular medication and/or a change in their health?',
            title2: 'Immunosuppression',
            question2: 'Does the user have a weakened immune system due to an illness (e.g. leukemia) or a medication currently being taken (e.g. chemotherapy)?',
            title3: 'Previous reactions',
            question3: 'Has the user ever had an allergic reaction that required emergency medical attention?\n' +
                ' <div style="margin-left: 20px;">or</div> Has the user ever had after the administration of an immunizing product, a reaction serious enough to require medical consultation?\n',
            title4: 'Coagulation Disorders',
            question4: 'Does the user have coagulation disorders requiring medical monitoring or taking anticoagulants?',
            title5: 'Immunizing or blood product',
            question5: 'Has the user received a blood transfusion or immunoglobulin injection in the last 11 months? <br/>Has the user received a live attenuated vaccine in the last 4 weeks?',
            title6: 'Pregnancy',
            question6: 'If the user is a woman, is she currently pregnant',
            title7: '',
            question7: 'If the user is a child under six months old, did the mother take, during pregnancy, a biological agent for the treatment of an autoimmune disease (eg: Crohn\'s disease, rheumatoid arthritis, lupus or psoriasis)?',
            title8: '',
            question8: 'Precaution(s)/contraindication(s) according to the Quebec Immunization Protocol (PIQ) for the immunizing product(s) to be administered.\n',
            precision: 'Precision',
            monitoringTime: {
                label: 'Recommended monitoring time after vaccination:',
                time1: '15 minutes or',
                time2: '30 minutes',
                precision: 'Specify if needed',
            },
        },
        administration: {
            title: 'ADMINISTRATION REASON IF REQUIRED',
            reason: {
                shingles: {
                    title: 'SHINGLES',
                    1: 'Immunocompromised',
                    2: 'Other reasons',
                },
                influenza: {
                    title: 'INFLUENZA',
                    1: 'Resident in a CHSLD',
                    2: 'Resident in a RPA',
                    3: 'Pregnant woman',
                    4: 'Health worker',
                    5: 'Chronic illness',
                    6: 'Other reasons',
                },
                dcat: {
                    title: 'dcaT',
                    1: 'Pregnant woman (whooping cough)',
                    2: 'Other reasons',
                },
                covid: {
                    title: 'COVID-19',
                    1: 'Resident in a CHSLD',
                    2: 'Resident in a RPA',
                    3: 'Pregnant woman',
                    4: 'Health worker',
                    5: 'Chronic illness',
                    6: 'Other reasons',
                },
                mpox: {
                    title: 'MPOX',
                    1: 'Pre-exposure - immunocompromised',
                    2: 'Pre-exposure - non-immunocompromised',
                    3: 'Post-exposure - immunocompromised',
                    4: 'Post-exposure - non-immunocompromised',
                    5: 'Other reasons',
                },
                vrs: {
                    title: 'RSV (adults)',
                    1: 'Resident in a CHSLD',
                    2: 'Resident in a RPA',
                    3: 'Other reasons',
                },
            },
        },
        consentAndDecision: {
            title: 'CONSENT',
            consentCheckbox1: 'Information on the disease(s) and on the immunizing product(s), including possible reactions and what to do after administration, were given to the user or his/her legal representative administration, have been given to the user or his/her legal representative.',
            consentCheckbox2: 'Consent to the administration of the following immunizing product(s), following analysis by the authorized professional',
            consentCheckbox2precision: '',
            consentCheckbox2precisionAriaLabel: 'Specify product consented',
            consentCheckbox3: 'Refuse the following immunizing product(s):',
            consentCheckbox3precision: '',
            consentCheckbox3precisionAriaLabel: 'Specify product not consented',
            consentCheckbox4: 'Does not apply',
            recommendation: 'HEALTH OFFICE:',
            healthOfficeOrientation: 'The administration of the immunizing product(s) follows a recommendation from the health office:',
            healthOfficeConsent: 'I consent to my information being forwarded to the health office',
            consentsRefuses: {
                title: 'CONSENT/REFUSAL OBTAINED FROM:',
                patient: 'Patient',
                parentalAuthority: 'Parental authority',
                spouse: 'Spouse (married, civil union, or common law)',
                mandatary: 'Mandatary',
                specialInterest: 'Person showing a special interest in the person',
                closeRelative: 'Close relative',
                guardian: 'Guardian',
            },
        },
        professionalInformation: {
            titleForm: 'INFORMATION ON THE AUTHORIZED PROFESSIONAL WHO ANALYZED THE PRE-IMMUNIZATION FORM',
            titleConsent: 'INFORMATION ON THE PROFESSIONAL WHO OBTAINED CONSENT:',
            clearMyInformations: 'Clear my information',
            getMyInformations: 'Enter my information',
            completeMyProfile: 'Complete my profile',
            name: 'Full name',
            profession: {
                title: 'PROFESSION',
                nurse: 'Nurse',
                physician: 'Physician',
                inhalotherapist: 'Respiratory therapist',
                midwife: 'Midwife',
                pharmacist: 'Pharmacist',
                other: 'Other',
            },
            licenceNumber: 'Licence n°.:',
            professionalSignature: 'Professional’s signature:',
            date: 'Date',
            year: 'Year',
            month: 'Month',
            day: 'Day',
        },
        phoneConsent: {
            witnessName: 'Name of witness if consent is obtained by telephone:',
        },
        vaccineDetails: {
            title: 'DETAILS OF IMMUNIZING PRODUCTS ADMINISTERED',
            getMyInformations: 'Enter injector ',
            clearMyInformations: 'Clear injector ',
            completeMyProfile: 'Complete my profile',
            category: 'Vaccine Category',
            date: 'Date (year, month, day)',
            dateSubText: '(year, month, day)',
            hour: 'Hour (00:00)',
            name: 'Immunizing Product',
            batchNumber: 'Batch Number',
            dose: 'Quantity / Unit',
            administration: 'Route of administration',
            injectionSite: 'Injection Site',
            vaccinePlace: 'PLACE OF SERVICE (LDS):',
            initials: 'Initials',
            siPmi: 'SI-PMI entry',
            placeholder: {
                date: 'AAAA-MM-DD',
            },
            injectionSiteType: {
                leftArm: 'Left arm',
                rightArm: 'Right arm',
                leftThigh: 'Left thigh',
                rightThigh: 'Right thigh',
                other: 'Other',
                otherAriaLabel: 'Autre injection site',
            },
            administrationType: {
                intramuscular: 'Intramuscular',
                intranasal: 'Intranasal',
                intradermic: 'Intradermal',
                subcutaneous: 'Subcutaneous',
                oral: 'Oral',
            },
            professionInformation: {
                title: 'INFORMATION ON THE PERSON WHO ADMINISTERED THE IMMUNIZING PRODUCT(S) :',
                name: 'Name:',
                profession: 'Profession (specify):',
                signature: 'Signature:',
                licenceNumber: 'Licence no.:',
                initials: 'Initials:',
            },
            zona: {
                shingrix: 'Shingrix',
            },
            flu: {
                afluria: 'Afluria',
                fluad: 'Fluad',
                fluadPediatric: 'Fluad pédiatrique',
                flucelvax: 'Flucelvax quad',
                flulaval: 'Flulaval tetra',
                fluMist: 'FluMist quadrivalent',
                fluzoneQuadrivalent: 'Fluzone quadrivalent',
                fluzoneDose: 'Fluzone HD quadrivalent',
                influvacTetra: 'Influvac tetra',
                supemtek: 'Supemtek',
                other: 'Autre',
            },
            covid: {
                'Moderna': 'Spikevax (Moderna)',
                'Pfizer-BioNTech': 'Comirnaty (Pfizer-BioNTech)',
            },
            dcat: {
                actHib: 'Act-Hib',
                adacel: 'Adacel',
                adacelPolio: 'Adacel-Polio',
                boostrix: 'Boostrix',
                boostrixPolio: 'Boostrix-Polio',
                imovaxPolio: 'Imovax Polio',
                infanrixHexa: 'Infanrix hexa',
                infanrixIPVHib: 'Infanrix-IPV/Hib',
                pediacel: 'Pediacel',
                td: 'Td Adsorbées',
                pentacel: 'Pentacel',
            },
            mpox: {
                imvamune: 'Imvamune',
            },
            hepatitis: {
                avaxim: 'Avaxim',
                engerixB: 'Engerix-B',
                havrix: 'Havrix',
                recombivaxHb: 'Recombivax HB',
                twinrix: 'Twinrix',
                vaqta: 'Vaqta',
                prehevbrio: 'PREHEVBRIO',
            },
            meningitis: {
                bexsero: 'Bexsero',
                menactra: 'Menactra',
                menjugate: 'Menjugate',
                menveo: 'Menveo',
                neisvacC: 'Neisvac-C',
                nimenrix: 'Nimenrix',
                trumenba: 'Trumenba',
            },
            pneumococcal: {
                pneumovax23: 'Pneumovax 23',
                prevnar13: 'Prevnar 13',
                prevnar20: 'Prevnar 20',
                synflorix: 'Synflorix',
                vaxneuvance: 'Vaxneuvance',
                capvaxine: 'Capvaxine',
            },
            rabies: {
                imovaxRage: 'Imovax Rage',
                rabAvert: 'RabAvert',
            },
            rotavirus: {
                rotarix: 'Rotarix',
                rotateq: 'RotaTeq',
            },
            rro: {
                mmrII: 'M-M-R II',
                priorix: 'Priorix',
                priorixTetra: 'Priorix Tetra',
                proQuad: 'ProQuad',
                varilrix: 'Varilrix',
                varivaxIII: 'Varivax III',
            },
            tuberculosis: {
                bcg: 'BCG lyophilisé avec glutamate',
                tubersol: 'Tubersol',
            },
            vph: {
                cervarix: 'Cervarix',
                gardasil9: 'Gardasil 9',
            },
            travel: {
                dukoral: 'Dukoral',
                ixiaro: 'Ixiaro',
                yfVax: 'Yf Vax',
                ixchiq: 'Ixchiq',
                vaxchora: 'Vaxchora',
            },
            vrs: {
                arexvy: 'Arexvy',
                abrysvo: 'Abrysvo',
            },
            vrs_baby: {
                nirsevimab: 'Nirsevimab',
            },
            typhoid: {
                typhimVi: 'Typhim Vi',
                vivotif: 'Vivotif',
            },
            avian_influenza: {
                arepanrix: 'Arepanrix H5N1',
            },
            vaccineCategories: {
                zona: 'Zona',
                flu: 'Influenza',
                covid: 'COVID-19',
                mpox: 'MPOX',
                dcat: 'dcaT',
                hepatitis: 'Hepatitis',
                pneumococcal: 'Pneumococcal',
                meningitis: 'Meningitis',
                rabies: 'Rabies',
                rotavirus: 'Rotavirus',
                rro: 'Measles, mumps, rubella and chickenpox',
                tuberculosis: 'Tuberculosis',
                vph: 'Human papillomavirus',
                travel: 'Travellers',
                vrs: 'Respiratory Syncytial Virus (RSV)',
                vrs_baby: 'Respiratory Syncytial Virus (RSV) Baby',
                typhoid: 'Typhoid vaccine',
                avian_influenza: 'Avian influenza A (H5N1)',
            },
            vaccineCategoriesPdf: {
                zona: 'Zona',
                flu: 'Influenza',
                covid: 'COVID-19',
                mpox: 'MPOX',
                dcat: 'Dcat',
                hepatitis: 'Hepatitis',
                pneumococcal: 'Pneumococcal',
                meningitis: 'Meningitis',
                rabies: 'Rabies',
                rotavirus: 'Rotavirus',
                rro: 'Rougeole, rubéole, oreillons et varicelle',
                tuberculosis: 'Tuberculosis',
                vph: 'Human papillomavirus',
                travel: 'Travellers',
                vrs: 'Respiratory Syncytial Virus (RSV)',
                vrs_baby: 'Respiratory Syncytial Virus (RSV) Baby',
                typhoid: 'Typhoid vaccine',
                avian_influenza: 'Avian influenza A (H5N1)',
            },
            professionList: {
                nurse: 'Nurse',
                physician: 'Physician',
                inhalotherapist: 'Respiratory therapist',
                midwife: 'Midwife',
                pharmacist: 'Pharmacist',
                other: 'Other',
            },
        },
        oldVaccineDetails: {
            flu: {
                agriflu: 'Agriflu',
                fluviral: 'Fluviral',
                influvac: 'Influvac',
            },
            covid: {
                'Spikevax-XBB-1-5': 'Spikevax XBB.1.5',
                'Comirnaty-XBB15-6m-4y': 'Comirnaty XBB.1.5 - 6 months-4 years',
                'Comirnaty-XBB15-5-11': 'Comirnaty XBB.1.5 - 5-11 years old',
                'Comirnaty-XBB15-12': 'Comirnaty XBB.1.5 - 12 years +',
                'Spikevax-Bivalent-BA4-BA5': 'Spikevax Bivalent BA4-BA5 (Moderna)',
                'Moderna-Bivalent': 'Spikevax Bivalent BA1 (Moderna)',
                'Moderna-Kids': 'Spikevax (Moderna) 6 months - 5 years',
                'Pfizer-BioNTech-Kids': 'Comirnaty (Pfizer-BioNTech) 5-11 years old',
                'Covishield': 'Covishield',
                'Nuvaxovid': 'Nuvaxovid (Novavax)',
                'Novavax': 'Nuvaxovid (Novavax)',
                'AstraZeneca': 'Vaxzevria (AstraZeneca)',
                'Comirnaty-Bivalent-BA4-BA5': 'Comirnaty Bivalent BA4-BA5',
                'Pfizer-BioNTech-Kids-6m-4y': 'Comirnaty (Pfizer-BioNTech) 6 months - 4 years',
                'Comirnaty-Bivalent-BA4-BA5-Kids': 'Comirnaty Bivalent BA4-BA5  5-11 years old',
                'Jcovden': 'Jcovden',
                'Covifenz': 'Covifenz',
            },
            dcat: {
                hiberix: 'Hiberix',
            },
        },
        notes: {
            notes: 'Notes',
            requiredField: '* Indicates required field',
        },
        headerPage: {
            patientName: 'User’s last and first name ',
            recordNumber: 'Record no.',
            siPmiId: 'N° ID SI-PMI',
        },
        footerPage: {
            title: 'IMMUNIZATION FORM',
            formRevision: 'AS-624 DT9225 (2025-03)',
            patientFile: 'PATIENT FILE',
        },
        error: {
            title: 'The form contains errors',
            message: 'Required fields are missing or contain errors...',
        },
        warning: {
            title: 'Are you sure you want to edit the content?',
            text: 'The form is completed and signed by a Vaccinator. Once the change is applied, you will need to sign and take responsibility for the entire document.',
            question: 'Do you want to continue with the changes ?',
        },
        actions: {
            ok: 'Ok',
            cancel: 'Cancel',
            no: 'No',
            yes: 'Yes',
        },
        base: {
            errors: {
                requiredField: 'Field is required',
                requiredNumberField: 'Value must be a number',
                invalidDate: 'This date is invalid',
                invalidTime: 'This time is invalid',
                invalidNam: 'The Health Insurance Card is invalid',
                invalidMonth: 'This month is invalid',
                invalidPostalCode: 'Postal code is invalid',
                invalidRegInd: 'Regional indicator est invalide',
                phone2short: 'The phone number is incorrect',
                invalidEmail: 'Invalid email format',
                iclscNumber: 'The I-CLSC number format is invalid',
            },
        },
    },
};

var dataCollectionBaseEnglish = {
    actionButton: 'Validate',
    form: {
        element: {
            type: {
                multipleChoiceCheckBox: {
                    title: 'Mocked - Multiple Choice Check Box Title',
                    description: 'Mocked - Multiple choice check box description.',
                    answer: {
                        mocked1: {
                            label: 'Mocked - Multiple choice check box answer 1',
                        },
                        mocked2: {
                            label: 'Mocked - Multiple choice check box answer 2',
                        },
                        mocked3: {
                            label: 'Mocked - Multiple choice check box answer 3',
                        },
                    },
                    alert: {
                        mocked1: {
                            message: 'Mocked - Alert for Multiple choice check box ',
                        },
                    },
                },
                multipleChoiceCheckBoxWithText: {
                    title: 'Mocked - Multiple Choice Check Box With Text',
                    description: 'Mocked - Multiple choice check box with text description.',
                    answer: {
                        mocked1: {
                            label: 'Mocked - Multiple choice check box with text answer 1',
                        },
                        mocked2: {
                            label: 'Mocked - Multiple choice check box with text answer 2',
                        },
                        mocked3: {
                            label: 'Mocked - Multiple choice check box with text answer 3',
                        },
                    },
                    alert: {
                        mocked1: {
                            message: 'Mocked - Alert for Multiple choice check box with text',
                        },
                        mocked2: {
                            message: 'Mocked 2 - Alert for Multiple choice check box with text',
                        },
                    },
                },
                simpleChoiceRadio: {
                    title: 'Mocked - Simple Choice Radio',
                    description: 'Mocked - Simple choice radio description.',
                    answer: {
                        mocked1: {
                            label: 'Mocked - Simple choice radio answer 1',
                        },
                        mocked2: {
                            label: 'Mocked - Simple choice radio answer 2',
                        },
                        mocked3: {
                            label: 'Mocked - Simple choice radio answer 3',
                        },
                    },
                },
                yesNoButton: {
                    title: 'Mocked - Yes No Button',
                    description: 'Mocked - Yes no button description.',
                    answer: {
                        mocked1: {
                            label: 'Yes',
                        },
                        mocked2: {
                            label: 'No',
                        },
                    },
                },
                YesNoButtonWithText: {
                    title: 'Mocked - Yes No Button With Text',
                    description: 'Mocked - Yes No Button With Text description.',
                    answer: {
                        mocked1: {
                            label: 'Yes',
                        },
                        mocked2: {
                            label: 'No',
                        },
                        mocked3: {
                            label: 'Mocked - Please precise here',
                        },
                    },
                },
                shortAnswerText: {
                    title: 'Mocked - Short Answer Text',
                    description: 'Mocked - Short answer text description.',
                    answer: {
                        mocked1: {
                            label: 'Mocked - Short answer text answer label.',
                        },
                    },
                },
                longAnswerTextArea: {
                    title: 'Mocked - Long Answer Text Area',
                    description: 'Mocked - Long answer text area description.',
                    answer: {
                        mocked1: {
                            label: 'Mocked - Long answer text area answer label.',
                        },
                    },
                },
                datePast: {
                    title: 'Mocked - Date Past',
                    description: 'Mocked - Date past description.',
                    answer: {
                        mocked1: {
                            label: 'Mocked - Date past answer label.',
                        },
                    },
                },
            },
            section: {
                mocked1: {
                    title: 'Mocked 1 - Section',
                    description: 'Mocked 1 - Section description.',
                },
                mocked2: {
                    title: 'Mocked 2 - Section',
                    description: 'Mocked 2 - Section description.',
                },
            },
        },
    },
    export: {
        title: 'Data collection form',
        appointmentBox: {
            firstName: 'First Name',
            lastName: 'Last Name',
            nam: 'Nam',
            service: 'Appointment type',
            appointmentDate: 'Appointment Date',
            birthday: 'Birthday',
        },
        header: {
            name: 'Name:',
            nam: 'NAM:',
            appointmentDate: 'Appointment Date:',
            birthday: 'Birthday:',
            na: 'N/A',
            completeOn: 'Completed on:',
        },
        footer: {
            page: 'Page ',
        },
    },
    accessibility: {
        checked: 'Option selected',
        unchecked: 'Option not selected',
        alert: 'Important message: ',
        yes: {
            label: 'The "Yes" button is selected',
        },
        no: {
            label: 'The "No" button is selected',
        },
        textfield: 'Text field, ',
        radioButton: 'Choosing with radio buttons: Use the keyboard\'s navigation arrows (up arrow and down arrow) to select the path.',
    },
};

var dataCollectionBaseFrench = {
    actionButton: 'Valider',
    form: {
        element: {
            type: {
                multipleChoiceCheckBox: {
                    title: 'FR - Mocked - Multiple Choice Check Box Title',
                    description: 'FR - Mocked - Multiple choice check box description.',
                    answer: {
                        mocked1: {
                            label: 'FR - Mocked - Multiple choice check box answer 1',
                        },
                        mocked2: {
                            label: 'FR - Mocked - Multiple choice check box answer 2',
                        },
                        mocked3: {
                            label: 'FR - Mocked - Multiple choice check box answer 3',
                        },
                    },
                    alert: {
                        mocked1: {
                            message: 'FR - Mocked - Alert for Multiple choice check box ',
                        },
                    },
                },
                multipleChoiceCheckBoxWithText: {
                    title: 'FR - Mocked - Multiple Choice Check Box With Text',
                    description: 'FR - Mocked - Multiple choice check box with text description.',
                    answer: {
                        mocked1: {
                            label: 'FR - Mocked - Multiple choice check box with text answer 1',
                        },
                        mocked2: {
                            label: 'FR - Mocked - Multiple choice check box with text answer 2',
                        },
                        mocked3: {
                            label: 'FR - Mocked - Multiple choice check box with text answer 3',
                        },
                    },
                    alert: {
                        mocked1: {
                            message: 'FR - Mocked - Alert for Multiple choice check box with text',
                        },
                        mocked2: {
                            message: 'FR - Mocked 2 - Alert for Multiple choice check box with text',
                        },
                    },
                },
                simpleChoiceRadio: {
                    title: 'FR - Mocked - Simple Choice Radio',
                    description: 'FR - Mocked - Simple choice radio description.',
                    answer: {
                        mocked1: {
                            label: 'FR - Mocked - Simple choice radio answer 1',
                        },
                        mocked2: {
                            label: 'FR - Mocked - Simple choice radio answer 2',
                        },
                        mocked3: {
                            label: 'FR - Mocked - Simple choice radio answer 3',
                        },
                    },
                },
                yesNoButton: {
                    title: 'FR - Mocked - Yes No Button',
                    description: 'FR - Mocked - Yes no button description.',
                    answer: {
                        mocked1: {
                            label: 'FR - Yes',
                        },
                        mocked2: {
                            label: 'FR - No',
                        },
                    },
                },
                YesNoButtonWithText: {
                    title: 'FR - Mocked - Yes No Button With Text',
                    description: 'FR - Mocked - Yes No Button With Text description.',
                    answer: {
                        mocked1: {
                            label: 'Oui',
                        },
                        mocked2: {
                            label: 'Non',
                        },
                        mocked3: {
                            label: 'FR - Mocked - Please precise here',
                        },
                        mocked4: {
                            label: '',
                        },
                    },
                },
                shortAnswerText: {
                    title: 'FR - Mocked - Short Answer Text',
                    description: 'FR - Mocked - Short answer text description.',
                    answer: {
                        mocked1: {
                            label: 'FR - Mocked - Short answer text answer label.',
                        },
                        mocked2: {
                            label: '',
                        },
                    },
                },
                longAnswerTextArea: {
                    title: 'FR - Mocked - Long Answer Text Area',
                    description: 'FR - Mocked - Long answer text area description.',
                    answer: {
                        mocked1: {
                            label: 'FR - Mocked - Long answer text area answer label.',
                        },
                        mocked2: {
                            label: '',
                        },
                    },
                },
                datePast: {
                    title: 'FR - Mocked - Date Past',
                    description: 'FR - Mocked - Date past description.',
                    answer: {
                        mocked1: {
                            label: 'FR - Mocked - Date past answer label.',
                        },
                        mocked2: {
                            label: '',
                        },
                    },
                },
            },
            section: {
                mocked1: {
                    title: 'FR - Mocked 1 - Section',
                    description: 'FR - Mocked 1 - Section description.',
                },
                mocked2: {
                    title: 'FR - Mocked 2 - Section',
                    description: 'FR - Mocked 2 - Section description.',
                },
            },
        },
    },
    export: {
        title: 'Formulaire de collecte d\'informations personnelles',
        appointmentBox: {
            firstName: 'Prénom',
            lastName: 'Nom',
            nam: 'NAM',
            service: 'Type de rendez-vous',
            appointmentDate: 'Date du rendez-vous',
        },
        header: {
            name: 'Nom :',
            nam: 'NAM :',
            appointmentDate: 'Date de rendez-vous :',
            birthday: 'Date de naissance :',
            na: 'S.O.',
            completeOn: 'Complété le :',
        },
        footer: {
            page: 'Page ',
        },
    },
    accessibility: {
        checked: 'Option sélectionnée',
        unchecked: 'Option non sélectionnée',
        alert: 'Message important: ',
        yes: {
            label: 'Le bouton "Oui" est sélectionné',
        },
        no: {
            label: 'Le bouton "Non" est sélectionné',
        },
        textfield: 'Champ de texte, ',
        radioButton: 'Choix avec des boutons radio : Le parcours doit être fait avec les flèches de navigation du clavier (flèche haut et flèche bas).',
    },
};

var v2$X = {
    consultationAvecUnPharmacien: {
        section: {
            section_1: {
                title: 'YOUR HEALTH ',
                description: '',
            },
            section_2: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            longAnswerTextArea_1: {
                title: 'What is the reason for consultation?',
                description: 'Please provide as much information as possible',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'If you are consulting about your treatments and medications, please use the right service: <br/>- Medication adjustment <br/>- Medication review <br/>- Prescription renewal and extension',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'What are your symptoms?',
                description: 'For example, nasal congestion, yellow nasal secretions, coughing, lower back pain, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'How much do these symptoms bother you?',
                description: 'For example, the pain prevents me from sleeping, I cough all the time, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_5: {
                title: 'What makes your symptoms worse or better?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_6: {
                title: 'Have you tried any treatment or tried any other measure to improve your situation?',
                description: 'For example, I put ice, I did a nasal irrigation with hydrasens, and so on',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you have flu-like symptoms?',
                description: 'Fever over 38°C, sudden cough, sore throat, muscle aches, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_10: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_11: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var consultationAvecUnPharmacienEnglish = {
    consultationAvecUnPharmacien: {
        v2: {
            ...v2$X.consultationAvecUnPharmacien,
        },
        section: {
            section_1: {
                title: 'YOUR HEALTH ',
                description: '',
            },
            section_2: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Reason for consultation',
                description: 'Please indicate the main reasons for your consultation',
                answer: {
                    answer_1: {
                        label: 'For my medication',
                    },
                    answer_2: {
                        label: 'For a prescription renewal',
                    },
                    answer_3: {
                        label: 'For questions about my medication',
                    },
                    answer_4: {
                        label: 'For a follow-up after a previous consultation',
                    },
                    answer_5: {
                        label: 'I have new symptoms',
                    },
                    answer_6: {
                        label: 'I have a known health problem and would like some advice',
                    },
                    answer_7: {
                        label: 'Other, please specify.',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Do you have flu-like symptoms?',
                description: 'Fever over 38°C, sudden cough, sore throat, muscle aches, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications ?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$W = {
    consultationAvecUnPharmacien: {
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            longAnswerTextArea_1: {
                title: 'Quel est le motif de consultation?',
                description: 'Fournissez le plus d\'informations possible',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Si votre raison de consultation concerne vos traitements et vos médicaments, veuillez consulter en utilisant le bon service: <br/>- Ajustement de médicaments <br/>- Révision de la médication <br/>- Prolongation et renouvellement d\'ordonnance',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Quels sont vos symptômes?',
                description: 'Par exemple, congestion nasale, sécrétions nasale jaune, toux, douleur dans le bas du dos, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Depuis quand avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'À quel point ces symptômes vous gênent?',
                description: 'Par exemple, la douleur m\'empêche de dormir, je tousse tout le temps, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_5: {
                title: 'Qu\'est-ce qui aggravent ou diminuent vos symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_6: {
                title: 'Avez-vous essayé un traitement ou tout autre moyen pour améliorer la situation?',
                description: 'Par exemple, j\'ai mis de la glace, j\'ai fait une irrigation nasale avec de l\'hydrasens, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Avez-vous des symptômes ressemblant à la grippe?',
                description: 'Fièvre de plus de 38 °C, toux soudaine, mal de gorge, douleurs musculaires, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_10: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous.',
                    },
                },
            },
            simpleChoiceRadio_11: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var consultationAvecUnPharmacienFrench = {
    consultationAvecUnPharmacien: {
        v2: {
            ...v2$W.consultationAvecUnPharmacien,
        },
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Raison de la consultation',
                description: 'Veuillez sélectionnez la raison principale pour laquelle vous consultez  :',
                answer: {
                    answer_1: {
                        label: 'Pour mes médicaments',
                    },
                    answer_2: {
                        label: 'Pour un renouvellement',
                    },
                    answer_3: {
                        label: 'Pour des questions sur mes médicaments',
                    },
                    answer_4: {
                        label: 'Pour un suivi suite à une consultation',
                    },
                    answer_5: {
                        label: 'J\'ai des nouveaux symptômes',
                    },
                    answer_6: {
                        label: 'J\'ai un problème de santé connu et j\'aimerais avoir des conseils',
                    },
                    answer_7: {
                        label: 'Autres, veuillez préciser',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Présentez-vous des symptômes d\'allure grippales ? ',
                description: 'Fièvre de plus de 38 °C, toux soudaine, mal de gorge, douleurs musculaires, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$V = {
    emergencyOralContraception: {
        section: {
            section_1: {
                title: 'YOUR HEALTH ',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            simpleChoiceRadio_1: {
                title: 'What is the reason for consultation?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Unprotected sex (no condom or contraceptive)',
                    },
                    answer_2: {
                        label: 'Incorrect use of a contraceptive method (forgetting or delaying a pill, removing the ring for more than three hours, etc.)',
                    },
                    answer_3: {
                        label: 'Contraception failure (condom breakage, etc.)',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'What is the date and time of the last unprotected intercourse?',
                description: 'An approximative time will suffice',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Unprotected or partially protected sex can also mean having a sexually transmitted infection (STI). If you think you need a screening test, you can consult the STI and sexual health tab on the ClicSante home page.',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you had other at-risk sexual relations in the last 7 days?',
                description: 'If so, please indicate the date',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Is your menstrual cycle regular?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'How many days does your period last? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_6: {
                title: 'When was the 1st day of your last period?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Was it different?',
                description: 'For instance, lighter than usual, heavier than usual, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Have you done a urine pregnancy test?',
                description: 'If so, please indicate the date and the result (positive or negative)',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Do you think you might be pregnant or are you having pregnancy symptoms?',
                description: 'For example, nausea, sore breasts or absence of last period or else',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Emergency contraception will prevents pregnancy when taken within 5 days of sexual intercourse. You may not be eligible if you do not meet these conditions.',
                    },
                },
            },
            shortAnswerText_10: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_11: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_12: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_13: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Are you using any method of contraception?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_17: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var emergencyOralContraceptionEnglish = {
    emergencyOralContraception: {
        v2: {
            ...v2$V.emergencyOralContraception,
        },
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            simpleChoiceRadio_1: {
                title: 'Please indicate why you need this service:',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Unprotected sex (no condom or contraceptive)',
                    },
                    answer_2: {
                        label: ' Incorrect use of a contraceptive method (Forgetting or delaying a pill, removing the ring for more than three hours, etc.)',
                    },
                    answer_3: {
                        label: 'Contraception failure (Condom breakage, etc.)',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Please indicate the date and time of the last unprotected sexual intercourse:',
                description: 'An approximative time will be enough.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Unprotected or partially protected sex can also mean having a sexually transmitted infection (STI). If you think you need a screening test, you can consult the STI and sexual health tab on the ClicSante home page.',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'When was the 1st day of your last period?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Was it different?',
                description: 'For instance, lighter than usual, heavier than usual, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Have you done a urine pregnancy test?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Do you believe you\'re already pregnant?',
                description: 'Nausea, sore breasts or absence of last period or else',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Emergency contraception will prevents pregnancy when taken within 5 days of sexual intercourse. You may not be eligible if you do not meet these conditions.',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'Weight',
                description: 'What is your weight?',
                answer: {
                    answer_1: {
                        label: 'Please specify (kg or pounds)',
                    },
                },
            },
            shortAnswerText_8: {
                title: 'Height',
                description: 'What is your height?',
                answer: {
                    answer_1: {
                        label: 'Please specify (m or inches)',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Contraceptive',
                description: 'Are you currently using contraception?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Known health problems',
                description: 'Examples: Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
        },
    },
};

var v2$U = {
    emergencyOralContraception: {
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            simpleChoiceRadio_1: {
                title: 'Quel est le motif de consultation?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Relation non protégée (sans condom et aucune contraception)',
                    },
                    answer_2: {
                        label: 'Mauvaise utilisation de la contraception (oubli ou retard dans la prise d\'un comprimé, retrait de l\'anneau pendant plus de trois heures, etc.)',
                    },
                    answer_3: {
                        label: 'Échec de la méthode de contraception (bris du condom, etc.)',
                    },
                    answer_4: {
                        label: 'Aucun de ces choix',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Quelle est la date et l\'heure de la dernière relation sexuelle non protégée?',
                description: 'Il suffit d\'inscrire une heure approximative',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Une relation sexuelle non protégée ou partiellement protégée peut aussi signifier infection transmissible sexuellement. Si vous pensez avoir besoin d\'un dépistage, vous pouvez consulter la catégorie ITSS et Santé sexuelle sur la page d\'accueil de ClicSanté.',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous eu d\'autres relations sexuelles à risque dans les 7 derniers jours?',
                description: 'Si oui, indiquez la date',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Votre cycle menstruel est-il regulier?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'Combien de jours vos menstruations durent-elles? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_6: {
                title: 'Quelle est la date du 1er jour de vos dernières menstruations?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Étaient-elles différentes? ',
                description: 'Plus abondante, moins abondante, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Avez-vous fait un test de grossesse urinaire?',
                description: 'Si oui, veuillez indiquer la date et le résultat (positif ou négatif)',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Pensez-vous être enceinte ou avez-vous des symptômes de grossesse?',
                description: 'Par exemple, nausées, mal aux seins ou absence des dernières menstruations, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'La contraception orale d\'urgence prévient une grossesse lorsqu\'elle est prit dans un délai maximal de 5 jours après la relation sexuelle à risque. En dehors de ces conditions, il est possible que vous ne soyez pas admissible.',
                    },
                },
            },
            shortAnswerText_10: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_11: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_12: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_13: {
                title: 'Est-ce que votre vaccination est à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Utilisez-vous une méthode de contraception?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez laquelle ici',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquelles ici',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Allergies et réactions',
                description: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquelles ici',
                    },
                },
            },
            yesNoButtonWithText_17: {
                title: 'Problèmes de santé connus',
                description: 'Y a-t-il des problèmes de santé connu comme le diabète, hypertension artérielle, insuffisance rénale, etc?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquelles ici',
                    },
                },
            },
        },
    },
};

var emergencyOralContraceptionFrench = {
    emergencyOralContraception: {
        v2: {
            ...v2$U.emergencyOralContraception,
        },
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            simpleChoiceRadio_1: {
                title: 'Indiquez ce qui vous pousse à avoir recours à ce service :',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Relation non protégée (Sans condom et aucune contraception)',
                    },
                    answer_2: {
                        label: 'Mauvaise utilisation de la contraception (Oublie ou retard dans la prise d\'un comprimé, retrait de l\'anneau pendant plus de trois heures, etc.)',
                    },
                    answer_3: {
                        label: 'Échec de la méthode de contraception (Bris du condom, etc.)',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Pouvez-vous inscrire la date et l\'heure de la dernière relation sexuelle non protégée :',
                description: 'Il suffit d\'inscrire une heure approximative.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Une relation sexuelle non protégée ou partiellement protégée peut aussi signifier infection transmissible sexuellement. Si vous pensez avoir besoin d\'un dépistage, vous pouvez consulter la catégorie ITSS et Santé sexuelle sur la page d\'accueil de ClicSanté',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Quelle est la date du 1er jour de vos dernières menstruations ?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Étaient-elles différentes ?',
                description: 'Plus abondante, moins abondante, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Avez-vous fait un test de grossesse urinaire ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Pensez-vous être déjà enceinte ?',
                description: 'Nausée, mal aux seins ou absence des dernières menstruations, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'La contraception orale d\'urgence prévient une grossesse lorsqu\'elle est prit dans un délai maximal de 5 jours après la relation sexuelle à risque. En dehors de ces conditions, il est possible que vous ne soyez pas admissible.',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'Quel est votre poids ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Spécifiez (kg ou lbs)',
                    },
                },
            },
            shortAnswerText_8: {
                title: 'Quelle est votre taille ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Spécifiez (m ou pieds et pouces)',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Contraception',
                description: 'Utilisez-vous actuellement une méthode de contraception ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser laquelle ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$T = {
    hormonalContraception: {
        section: {
            section_1: {
                title: 'YOUR HEALTH ',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Do any of these situations apply to you?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Allergy to a component of the contraceptive',
                    },
                    answer_2: {
                        label: 'Currently pregnant or having given birth less than 6 weeks ago',
                    },
                    answer_3: {
                        label: 'Have migraines with aura',
                    },
                    answer_4: {
                        label: 'Migraines with auras or causing neurological disorders',
                    },
                    answer_5: {
                        label: 'Being 35 years old or more and smoking or having any kind of migraine',
                    },
                    answer_6: {
                        label: 'High blood pressure',
                    },
                    answer_7: {
                        label: 'Uncontrolled diabetes',
                    },
                    answer_8: {
                        label: 'Lupus erythematosus',
                    },
                    answer_9: {
                        label: 'Generalized skin disorders',
                    },
                    answer_10: {
                        label: 'Structural abnormality of the uterus or genitals',
                    },
                    answer_11: {
                        label: 'No known health problems',
                    },
                    answer_12: {
                        label: 'Other, specify here',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_2: {
                title: 'What health problems do you have or have you ever had?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Blood clots (stroke, infarction, venous thrombosis, etc.)',
                    },
                    answer_2: {
                        label: 'Breast cancer',
                    },
                    answer_3: {
                        label: 'Liver or pancreas problems',
                    },
                    answer_4: {
                        label: 'Major surgery requiring immobilization for more than 72 hours',
                    },
                    answer_5: {
                        label: 'None of the above',
                    },
                    answer_6: {
                        label: 'Other, specify here',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_5: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_6: {
                title: 'Have you ever used hormonal contraception?',
                description: 'Select all that apply and specify which and how you tolerated this method (side effects, etc.)',
                answer: {
                    answer_1: {
                        label: 'Pill',
                    },
                    answer_2: {
                        label: 'Vaginal ring',
                    },
                    answer_3: {
                        label: 'Patch',
                    },
                    answer_4: {
                        label: 'Injections',
                    },
                    answer_5: {
                        label: 'IUD',
                    },
                    answer_6: {
                        label: 'Implant',
                    },
                    answer_7: {
                        label: 'Condom',
                    },
                    answer_8: {
                        label: 'No contraception method',
                    },
                    answer_9: {
                        label: 'Other, specify here',
                    },
                },
            },
            multipleChoiceCheckBox_7: {
                title: 'Contraceptive preference',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Oral contraceptive pill',
                    },
                    answer_2: {
                        label: 'Vaginal ring',
                    },
                    answer_3: {
                        label: 'Contraceptive patch',
                    },
                    answer_4: {
                        label: 'Injectable contraception',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Pharmacists cannot prescribe IUDs or contraceptive implants. Instead, please consult a sexual health nurse at your CLSC or a physician.',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Will you be able to follow the instructions for the chosen method?',
                description: 'For example, will you be able to take a pill every day at the same time without exception?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'We suggest that you consult the website itsaplan.ca before your appointment to learn more about the different contraceptive methods available.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var hormonalContraceptionEnglish = {
    hormonalContraception: {
        v2: {
            ...v2$T.hormonalContraception,
        },
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Your health condition',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Allergy to a component of the contraceptive',
                    },
                    answer_2: {
                        label: 'Currently pregnant or having given birth less than 6 weeks ago',
                    },
                    answer_3: {
                        label: 'Have migraines with aura',
                    },
                    answer_4: {
                        label: 'Migraines with auras or causing neurological disorders',
                    },
                    answer_5: {
                        label: 'Being 35 years old or more and smoking or having any kind of migraine',
                    },
                    answer_6: {
                        label: 'High blood pressure',
                    },
                    answer_7: {
                        label: 'Uncontrolled diabetes',
                    },
                    answer_8: {
                        label: 'Lupus erythematosus',
                    },
                    answer_9: {
                        label: 'Generalized skin disorders',
                    },
                    answer_10: {
                        label: 'Structural abnormality of the uterus or genitals',
                    },
                    answer_11: {
                        label: 'Other, specify here:',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_2: {
                title: 'Past medical history',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Blood clots (stroke, infarction, venous thrombosis, etc.)',
                    },
                    answer_2: {
                        label: 'Breast cancer',
                    },
                    answer_3: {
                        label: 'Liver or pancreas problems',
                    },
                    answer_4: {
                        label: 'Major surgery requiring immobilization for more than 72 hours',
                    },
                    answer_5: {
                        label: 'Other, specify here:',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Weight',
                description: 'What is your weight?',
                answer: {
                    answer_1: {
                        label: 'Please specify (kg or pounds)',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Height',
                description: 'What is your height?',
                answer: {
                    answer_1: {
                        label: 'Please specify (m or inches)',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Smoking',
                description: 'Are you a smoker?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBox_6: {
                title: 'Contraceptive preference',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Oral contraceptive pill',
                    },
                    answer_2: {
                        label: 'Vaginal ring',
                    },
                    answer_3: {
                        label: 'Contraceptive patch',
                    },
                    answer_4: {
                        label: 'Injectable contraception',
                    },
                    answer_5: {
                        label: 'IUD',
                    },
                    answer_6: {
                        label: 'Contraceptive implant',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'We suggest that you consult the website itsaplan.ca before your appointment to learn more about the different contraceptive methods available.',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Known health problems',
                description: 'Examples: Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
        },
    },
};

var v2$S = {
    hormonalContraception: {
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Allergie aux ingrédients du contraceptif',
                    },
                    answer_2: {
                        label: 'Être enceinte ou avoir accouché il y a moins de 6 semaines',
                    },
                    answer_3: {
                        label: 'Avoir des migraines avec aura',
                    },
                    answer_4: {
                        label: 'Avoir des migraines avec des atteintes neurologiques',
                    },
                    answer_5: {
                        label: 'Avoir 35 ans et plus, être fumeuse ou avoir tout type de migraine',
                    },
                    answer_6: {
                        label: 'Hypertension artérielle',
                    },
                    answer_7: {
                        label: 'Diabète avec complications',
                    },
                    answer_8: {
                        label: 'Lupus érythémateux',
                    },
                    answer_9: {
                        label: 'Troubles généralisés de la peau',
                    },
                    answer_10: {
                        label: 'Anomalie structurelle au niveau de l\'utérus ou des organes génitaux',
                    },
                    answer_11: {
                        label: 'Aucun problème de santé connu',
                    },
                    answer_12: {
                        label: 'Autres, précisez ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_2: {
                title: 'Quels problèmes de santé avez-vous ou avez-vous déjà eu? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Caillots (AVC, infarctus, thrombose veineuse, etc.)',
                    },
                    answer_2: {
                        label: 'Cancer du sein',
                    },
                    answer_3: {
                        label: 'Problèmes au foie ou au pancréas',
                    },
                    answer_4: {
                        label: 'Chirurgie majeure avec une immobilisation de plus de 72 h',
                    },
                    answer_5: {
                        label: 'Aucun antécédent personnel',
                    },
                    answer_6: {
                        label: 'Autres, précisez ici',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_5: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_6: {
                title: 'Avez-vous déjà utilisé un moyen de contraception hormonale?',
                description: 'Précisez la méthode et comment vous l\'avez toléré (les effets secondaires, etc.) Sélectionnez tout ce qui s\'applique',
                answer: {
                    answer_1: {
                        label: 'Pilule',
                    },
                    answer_2: {
                        label: 'Anneau',
                    },
                    answer_3: {
                        label: 'Timbre',
                    },
                    answer_4: {
                        label: 'Injection',
                    },
                    answer_5: {
                        label: 'Stérilet',
                    },
                    answer_6: {
                        label: 'Implant',
                    },
                    answer_7: {
                        label: 'Condom',
                    },
                    answer_8: {
                        label: 'Aucune méthode de contraception',
                    },
                    answer_9: {
                        label: 'Autre, précisez ici',
                    },
                },
            },
            multipleChoiceCheckBox_7: {
                title: 'Préférence du moyen de contraception',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Pilule',
                    },
                    answer_2: {
                        label: 'Anneau',
                    },
                    answer_3: {
                        label: 'Timbre',
                    },
                    answer_4: {
                        label: 'Injection',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Le pharmacien ou la pharmacienne ne peut pas prescrire le stérilet ou l\'implant contraceptif. Veuillez plutôt consulter en santé sexuelle.',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Croyez-vous être en mesure de respecter les indications de la méthode préférée?',
                description: 'Par exemple, pourrez-vous prendre un comprimé tous les jours à la même heure sans exception? ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avant votre rendez-vous, nous vous suggérons de consulter le site internet caseplanifie.ca pour en savoir plus sur les différentes méthodes contraceptives.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var hormonalContraceptionFrench = {
    hormonalContraception: {
        v2: {
            ...v2$S.hormonalContraception,
        },
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Votre état de santé',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Allergie aux ingrédients du contraceptif',
                    },
                    answer_2: {
                        label: 'Être enceinte ou avoir accouché il y a moins de 6 semaines',
                    },
                    answer_3: {
                        label: 'Avoir des migraines avec aura',
                    },
                    answer_4: {
                        label: 'Avoir des migraines avec des atteintes neurologiques',
                    },
                    answer_5: {
                        label: 'Avoir 35 ans et plus, être fumeuse ou avoir tout type de migraine',
                    },
                    answer_6: {
                        label: 'Hypertension artérielle',
                    },
                    answer_7: {
                        label: 'Diabète avec complications',
                    },
                    answer_8: {
                        label: 'Lupus érythémateux',
                    },
                    answer_9: {
                        label: 'Troubles généralisés de la peau',
                    },
                    answer_10: {
                        label: 'Anomalie structurelle au niveau de l\'utérus ou des organes génitaux',
                    },
                    answer_11: {
                        label: 'Autres, précisez ici :',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_2: {
                title: 'Antécédents personnels',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Caillots (AVC, infarctus, thrombose veineuse, etc.)',
                    },
                    answer_2: {
                        label: 'Cancer du sein',
                    },
                    answer_3: {
                        label: 'Problèmes au foie ou au pancréas',
                    },
                    answer_4: {
                        label: 'Chirurgie majeure avec une immobilisation de plus de 72 h',
                    },
                    answer_5: {
                        label: 'Autres, précisez ici :',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Poids',
                description: 'Quel est votre poids ?',
                answer: {
                    answer_1: {
                        label: 'Spécifiez (kg ou lbs)',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Taille',
                description: 'Quelle est votre taille ?',
                answer: {
                    answer_1: {
                        label: 'Spécifiez (m ou pieds et pouces)',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Tabagisme',
                description: 'Est-ce que vous fumez ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBox_6: {
                title: 'Préférence du moyen de contraception',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Pilule',
                    },
                    answer_2: {
                        label: 'Anneau',
                    },
                    answer_3: {
                        label: 'Timbre',
                    },
                    answer_4: {
                        label: 'Injection',
                    },
                    answer_5: {
                        label: 'Stérilet',
                    },
                    answer_6: {
                        label: 'Implant',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avant votre rendez-vous, nous vous suggérons de consulter le site internet caseplanifie.ca pour en savoir plus sur les différentes méthodes contraceptives.',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$R = {
    menstrualCramps: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Do you have any of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Pain in the form of cramps that occur at the time of menstruation',
                    },
                    answer_2: {
                        label: 'Pain that lasts 2 to 3 days',
                    },
                    answer_3: {
                        label: 'Fatigue',
                    },
                    answer_4: {
                        label: 'Nausea and/or vomiting',
                    },
                    answer_5: {
                        label: 'Diarrhea',
                    },
                    answer_6: {
                        label: 'Back pain',
                    },
                    answer_7: {
                        label: 'Thigh pain',
                    },
                    answer_8: {
                        label: 'Headache',
                    },
                    answer_9: {
                        label: 'Dizziness',
                    },
                    answer_10: {
                        label: 'Other, specify here',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'For how long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_4: {
                title: 'Do you have any of these health conditions that might explain these pains? ',
                description: 'For example, a diagnosed gynecological disorder. If so, please specify which one',
                answer: {
                    answer_1: {
                        label: 'Endometriosis',
                    },
                    answer_2: {
                        label: 'Recent insertion of an IUD (less than 6 months)',
                    },
                    answer_3: {
                        label: 'Recent childbirth',
                    },
                    answer_4: {
                        label: 'Polycystic ovary syndrome',
                    },
                    answer_5: {
                        label: 'None of the above',
                    },
                    answer_6: {
                        label: 'Other',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            simpleChoiceRadio_7: {
                title: 'In the last three months, what has been your situation?',
                description: 'Select the one that applies',
                answer: {
                    answer_1: {
                        label: 'Stable partner',
                    },
                    answer_2: {
                        label: 'A new partner',
                    },
                    answer_3: {
                        label: 'Multiple partners',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Having a new or multiple partner can also mean having a sexually transmitted infection. In the STI and Sexual Health tab on the ClicSanté home page, you can book a STI screening appointment.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_9: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            multipleChoiceCheckBox_10: {
                title: 'Would you like hormonal contraception?',
                description: 'Some hormonal contraceptives reduce menstrual pain. Indicate at your appointment if this option interests you',
                answer: {
                    answer_1: {
                        label: 'Pill',
                    },
                    answer_2: {
                        label: 'Ring',
                    },
                    answer_3: {
                        label: 'Patch',
                    },
                    answer_4: {
                        label: 'Injections',
                    },
                    answer_5: {
                        label: 'IUD',
                    },
                    answer_6: {
                        label: 'Implant',
                    },
                    answer_7: {
                        label: 'No hormonal contraception',
                    },
                    answer_8: {
                        label: 'No, it\'s not needed',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Before your appointment, we suggest you visit the çaseplanifie.ca website to learn more about the different contraceptive methods available. Please note that the pharmacists can\'t prescribe IUDs or contraceptive implants.',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var menstrualCrampsEnglish = {
    menstrualCramps: {
        v2: {
            ...v2$R.menstrualCramps,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'General',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Pain in the form of cramps that occur at the time of menstruation',
                    },
                    answer_2: {
                        label: 'Pain that lasts 2 to 3 days',
                    },
                    answer_3: {
                        label: 'Fatigue',
                    },
                    answer_4: {
                        label: 'Nausea and/or vomiting',
                    },
                    answer_5: {
                        label: 'Diarrhea',
                    },
                    answer_6: {
                        label: 'Back pain',
                    },
                    answer_7: {
                        label: 'Thigh pain',
                    },
                    answer_8: {
                        label: 'Headache',
                    },
                    answer_9: {
                        label: 'Dizziness',
                    },
                    answer_10: {
                        label: 'Other, specify here:',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Sexual activity',
                description: 'Please indicate your situation in the last 3 months. ',
                answer: {
                    answer_1: {
                        label: 'Stable partner',
                    },
                    answer_2: {
                        label: 'A new partner',
                    },
                    answer_3: {
                        label: 'Multiple partners',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Having a new or multiple partner can also mean having a sexually transmitted infection. In the STI and Sexual Health tab on the ClicSanté home page, you can book a STI screening appointment.',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Would you like hormonal contraception?',
                description: 'Some hormonal contraceptives reduce menstrual pain. Tell your pharmacist if you are interested in this option.',
                answer: {
                    answer_1: {
                        label: 'Pill',
                    },
                    answer_2: {
                        label: 'Ring',
                    },
                    answer_3: {
                        label: 'Patch',
                    },
                    answer_4: {
                        label: 'Injections',
                    },
                    answer_5: {
                        label: 'IUD',
                    },
                    answer_6: {
                        label: 'Implant',
                    },
                    answer_7: {
                        label: 'I\'m already taking one',
                    },
                    answer_8: {
                        label: 'I don\'t want any hormonal contraceptive',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Before your appointment, we suggest you visit the çaseplanifie.ca website to learn more about the different contraceptive methods available.',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Medications et natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$Q = {
    menstrualCramps: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Douleur sous forme de crampes qui surviennent au moment des menstruations',
                    },
                    answer_2: {
                        label: 'Douleur qui dure 2 à 3 jours',
                    },
                    answer_3: {
                        label: 'Fatigue',
                    },
                    answer_4: {
                        label: 'Nausées et/ou vomissements',
                    },
                    answer_5: {
                        label: 'Diarrhée',
                    },
                    answer_6: {
                        label: 'Douleur au dos',
                    },
                    answer_7: {
                        label: 'Douleurs aux cuisses',
                    },
                    answer_8: {
                        label: 'Migraine',
                    },
                    answer_9: {
                        label: 'Étourdissement',
                    },
                    answer_10: {
                        label: 'Autre, précisez ici',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de temps durent ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, précisez les méthodes ou les produits utilisés',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_4: {
                title: 'Avez-vous une condition de santé susceptible d\'expliquer ces douleurs parmi les suivantes? ',
                description: 'Par exemple, un trouble gynécologique diagnostiqué. Si oui, spécifiez lequel',
                answer: {
                    answer_1: {
                        label: 'Endométriose',
                    },
                    answer_2: {
                        label: 'Insertion récente d\'un stérilet (moins de 6 mois)',
                    },
                    answer_3: {
                        label: 'Accouchement récent',
                    },
                    answer_4: {
                        label: 'Syndrome des ovaires polykystiques',
                    },
                    answer_5: {
                        label: 'Aucune de ces conditions de santé',
                    },
                    answer_6: {
                        label: 'Autres',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            simpleChoiceRadio_7: {
                title: 'Dans les trois derniers mois, quelle était votre situation?',
                description: 'Sélectionnez celle qui s\'applique',
                answer: {
                    answer_1: {
                        label: 'Partenaire stable',
                    },
                    answer_2: {
                        label: 'Un nouveau partenaire',
                    },
                    answer_3: {
                        label: 'Partenaires multiples',
                    },
                    answer_4: {
                        label: 'Aucun de ces choix',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avoir un nouveau ou de multiples de partenaires peut aussi signifier infection transmissible sexuellement. Dans l\'onglet ITSS et Santé sexuelle sur la page d\'accueil de ClicSanté, il est possible de prendre un rendez-vous Dépistage ITSS.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles, leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_9: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            multipleChoiceCheckBox_10: {
                title: 'Aimeriez-vous avoir une contraception hormonale? ',
                description: 'Certains contraceptifs hormonaux diminuent les douleurs menstruelles. Indiquez lors de votre rendez-vous si cette option vous intéresse',
                answer: {
                    answer_1: {
                        label: 'Pilule',
                    },
                    answer_2: {
                        label: 'Anneau',
                    },
                    answer_3: {
                        label: 'Timbre',
                    },
                    answer_4: {
                        label: 'Injection',
                    },
                    answer_5: {
                        label: 'Stérilet',
                    },
                    answer_6: {
                        label: 'Implant',
                    },
                    answer_7: {
                        label: 'Préférence pour contraception non hormonale',
                    },
                    answer_8: {
                        label: 'Non, ce n\'est pas nécessaire',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avant votre rendez-vous, nous vous suggérons de consulter le site internet çaseplanifie.ca pour en savoir plus sur les différentes méthodes contraceptives. À noter que les stérilets et les implants contraceptifs ne peuvent être prescrits en pharmacie.',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquelles ici',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var menstrualCrampsFrench = {
    menstrualCramps: {
        v2: {
            ...v2$Q.menstrualCramps,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Généraux',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Douleur sous forme de crampes qui surviennent au moment des menstruations',
                    },
                    answer_2: {
                        label: 'Douleur qui dure 2 à 3 jours',
                    },
                    answer_3: {
                        label: 'Fatigue',
                    },
                    answer_4: {
                        label: 'Nausées et/ou vomissements',
                    },
                    answer_5: {
                        label: 'Diarrhée',
                    },
                    answer_6: {
                        label: 'Douleur au dos',
                    },
                    answer_7: {
                        label: 'Douleurs aux cuisses',
                    },
                    answer_8: {
                        label: 'Mal de tête',
                    },
                    answer_9: {
                        label: 'Étourdissement',
                    },
                    answer_10: {
                        label: 'Autre, précisez ici :',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Activité sexuelle',
                description: 'Veuillez indiquer votre situation dans les 3 derniers mois. ',
                answer: {
                    answer_1: {
                        label: 'Partenaire stable',
                    },
                    answer_2: {
                        label: 'Un nouveau partenaire',
                    },
                    answer_3: {
                        label: 'Partenaires multiples',
                    },
                    answer_4: {
                        label: 'Aucun de ces choix',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avoir un nouveau ou de multiples partenaires peut aussi signifier infection transmissible sexuellement. Dans l’onglet « ITSS et Santé sexuelle » sur la page d’accueil de ClicSanté, il est possible de prendre un rendez-vous pour un dépistage ITSS.',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Aimeriez-vous avoir une contraception hormonale ? ',
                description: 'Certains contraceptifs hormonaux diminuent les douleurs menstruelles. Spécifiez à votre pharmacien.ne si cette option vous intéresse.',
                answer: {
                    answer_1: {
                        label: 'Pilule',
                    },
                    answer_2: {
                        label: 'Anneau',
                    },
                    answer_3: {
                        label: 'Timbre',
                    },
                    answer_4: {
                        label: 'Injection',
                    },
                    answer_5: {
                        label: 'Stérilet',
                    },
                    answer_6: {
                        label: 'Implant',
                    },
                    answer_7: {
                        label: 'J\'en prend déjà une',
                    },
                    answer_8: {
                        label: 'Aucune',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avant votre rendez-vous, nous vous suggérons de consulter le site internet çaseplanifie.ca pour en savoir plus sur les différentes méthodes contraceptives.',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$P = {
    prophylaxisForAcuteMountainSickness: {
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATION ABOUT YOUR TRIP',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'What is the expected ascent?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Stay of more than 24h at high altitude (> 2000 m) without exceeding 4000 m',
                    },
                    answer_2: {
                        label: 'It will be possible to evacuate quickly',
                    },
                    answer_3: {
                        label: 'The ascent will comply with current safety standards',
                    },
                    answer_4: {
                        label: 'I don\'t know',
                    },
                },
            },
            longAnswerTextArea_2: {
                title: 'What is the expected rate of ascent?',
                description: 'Specify predicted ascent speed including altitude information while sleeping',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Which country and region will you be traveling to?',
                description: 'Specify the region you are visiting',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Caution: Some regions are more at risk than others, especially when it comes to isolation and the complexity of evacuation. During your appointment, you may be referred to a medical clinic if this is safer.',
                    },
                },
            },
            longAnswerTextArea_4: {
                title: 'What is your travel itinerary?',
                description: 'Please indicate the different places you will be visiting during your trip, and how long you plan on staying at each location',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'How long will your trip last?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Have you ever stayed at high altitudes in the past?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Have you ever suffered from acute mountain sickness?',
                description: 'If so, specify the symptoms',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBox_8: {
                title: 'Do you have one of these conditions?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Severe COPD',
                    },
                    answer_2: {
                        label: 'Heart disease with uncontrolled angina',
                    },
                    answer_3: {
                        label: 'Heart failure',
                    },
                    answer_4: {
                        label: 'Pulmonary hypertension',
                    },
                    answer_5: {
                        label: 'Congenital heart disease',
                    },
                    answer_6: {
                        label: 'Pulmonary vascular anomalies',
                    },
                    answer_7: {
                        label: 'Sickle-cell anemia with seizures',
                    },
                    answer_8: {
                        label: 'None of these conditions',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Caution: It is not recommended to climb at high altitude with any of these health conditions. Please discuss with your health care professional for more information.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Are you pregnant? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Caution: It is not recommended to climb at high altitude with this health condition. Please discuss with your health care professional for more information.',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Do you take a hormonal contraceptive?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Hormonal IUD',
                    },
                    answer_2: {
                        label: 'Pills with progesterone only (Micronor)',
                    },
                    answer_3: {
                        label: 'All other contraceptive pills',
                    },
                    answer_4: {
                        label: 'Vaginal ring',
                    },
                    answer_5: {
                        label: 'Implant',
                    },
                    answer_6: {
                        label: 'Depo-Provera injection',
                    },
                    answer_7: {
                        label: 'Patch (Evra)',
                    },
                    answer_8: {
                        label: 'No hormonal contraceptive',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Do you suffer from migraines with aura?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBox_12: {
                title: 'Do you have one of these conditions?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Obesity',
                    },
                    answer_2: {
                        label: 'Mild COPD',
                    },
                    answer_3: {
                        label: 'Controlled asthma',
                    },
                    answer_4: {
                        label: 'Controlled hypertension',
                    },
                    answer_5: {
                        label: 'Coronary bypass without angina',
                    },
                    answer_6: {
                        label: 'Stable anemia',
                    },
                    answer_7: {
                        label: 'Migraine',
                    },
                    answer_8: {
                        label: 'Stabilized seizure disorder on medication',
                    },
                    answer_9: {
                        label: 'Controlled diabetes',
                    },
                    answer_10: {
                        label: 'Laser corneal surgery',
                    },
                    answer_11: {
                        label: 'Stabilized psychiatric illness',
                    },
                    answer_12: {
                        label: 'Cancer',
                    },
                    answer_13: {
                        label: 'Inflammatory diseases',
                    },
                    answer_14: {
                        label: 'None of the above',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_13: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_14: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_17: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var prophylaxisForAcuteMountainSicknessEnglish = {
    prophylaxisForAcuteMountainSickness: {
        v2: {
            ...v2$P.prophylaxisForAcuteMountainSickness,
        },
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATION ABOUT YOUR TRIP',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Ascent information',
                description: 'Select all that apply ',
                answer: {
                    answer_1: {
                        label: 'Stay of more than 24h at high altitude (> 2000 m) without exceeding 4000 m.',
                    },
                    answer_2: {
                        label: 'It will be possible to evacuate quickly',
                    },
                    answer_3: {
                        label: 'The ascent will comply with current safety standards.',
                    },
                },
            },
            longAnswerTextArea_2: {
                title: 'Ascent speed',
                description: 'Specify predicted ascent speed including altitude information while sleeping.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Visited region',
                description: 'Specify the region you are visiting.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Caution: Some regions are more at risk than others, especially when it comes to isolation and the complexity of evacuation. Your pharmacist may refer you to a physician if he or she deems it safer to do so.',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Previous climbing',
                description: 'Have you spent time at high altitudes in the past?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Past history of acute mountain sickness',
                description: 'Have you ever suffered from acute mountain sickness? If so, please describe the symptoms you experienced',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which symptoms here',
                    },
                },
            },
            multipleChoiceCheckBox_6: {
                title: 'Do you have one of these conditions?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Severe COPD',
                    },
                    answer_2: {
                        label: 'Heart disease with uncontrolled angina',
                    },
                    answer_3: {
                        label: 'Heart failure',
                    },
                    answer_4: {
                        label: 'Pulmonary hypertension',
                    },
                    answer_5: {
                        label: 'Congenital heart disease',
                    },
                    answer_6: {
                        label: 'Pulmonary vascular anomalies',
                    },
                    answer_7: {
                        label: 'Sickle-cell anemia with seizures',
                    },
                    answer_8: {
                        label: 'None of these conditions',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Caution: It is not recommended to climb at high altitude with any of these health conditions. Please discuss with your health care professional for more information.',
                    },
                },
            },
            simpleChoiceRadio_7: {
                title: 'Are you pregnant? ',
                description: 'Please select the one that best suits your situation',
                answer: {
                    answer_1: {
                        label: 'Low-risk pregnancy',
                    },
                    answer_2: {
                        label: 'High-risk pregnancy',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Caution: It is not recommended to climb at high altitude with this health condition. Please discuss with your health care professional for more information.',
                    },
                },
            },
            simpleChoiceRadio_8: {
                title: 'Are you taking a hormonal contraceptive?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Hormonal IUD',
                    },
                    answer_2: {
                        label: 'Pills with progesterone only (Micronor)',
                    },
                    answer_3: {
                        label: 'All other contraceptive pills',
                    },
                    answer_4: {
                        label: 'Vaginal ring',
                    },
                    answer_5: {
                        label: 'Implant',
                    },
                    answer_6: {
                        label: 'Depo-Provera injection',
                    },
                    answer_7: {
                        label: 'Patch (Evra)',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Do you suffer from migraines with aura?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBox_10: {
                title: 'Do you have one of these conditions?',
                description: 'Select all that apply.',
                answer: {
                    answer_1: {
                        label: 'Obesity',
                    },
                    answer_2: {
                        label: 'Mild COPD',
                    },
                    answer_3: {
                        label: 'Controlled asthma',
                    },
                    answer_4: {
                        label: 'Controlled hypertension',
                    },
                    answer_5: {
                        label: 'Coronary bypass without angina',
                    },
                    answer_6: {
                        label: 'Stable anemia',
                    },
                    answer_7: {
                        label: 'Migraine',
                    },
                    answer_8: {
                        label: 'Stabilized seizure disorder on medication',
                    },
                    answer_9: {
                        label: 'Compensated diabetes',
                    },
                    answer_10: {
                        label: 'Laser corneal surgery',
                    },
                    answer_11: {
                        label: 'Taking oral contraceptives',
                    },
                    answer_12: {
                        label: 'Low-risk pregnancy',
                    },
                    answer_13: {
                        label: 'Stabilized psychiatric illness',
                    },
                    answer_14: {
                        label: 'Cancer',
                    },
                    answer_15: {
                        label: 'Inflammatory diseases',
                    },
                    answer_16: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Smoking',
                description: 'Are you a smoker?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$O = {
    prophylaxisForAcuteMountainSickness: {
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES SUR VOTRE VOYAGE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Quelle est l\'ascension prévue?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Séjour prévu de plus de 24h en haute altitude (> 2000 m) sans dépasser 4000 m',
                    },
                    answer_2: {
                        label: 'Il sera possible d\'évacuer rapidement',
                    },
                    answer_3: {
                        label: 'L\'ascension se fera selon les normes de sécurité en vigueur',
                    },
                    answer_4: {
                        label: 'Je ne connais pas l\'information',
                    },
                },
            },
            longAnswerTextArea_2: {
                title: 'Quelle est la vitesse d\'ascension prévue?',
                description: 'Spécifiez la vitesse prévue d\'ascension incluant les informations sur l\'altitude pendant le sommeil ou indiquez si vous ne savez pas',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Dans quel pays et région voyagerez-vous?',
                description: 'Spécifiez le pays ainsi que la région visitée',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention: Certaines régions sont plus à risque que d\'autres notamment pour ce qui est de l\'isolement et de la difficulté à être évacué. Lors de votre rendez-vous, vous pourriez être référé vers une clinique médicale si cela est plus sécuritaire.',
                    },
                },
            },
            longAnswerTextArea_4: {
                title: 'Quel est votre itinéraire de voyage?',
                description: 'Veuillez indiquer les différents endroits visités durant votre voyage ainsi que la durée prévue à chaque endroit',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'Quelle est la durée totale de votre voyage?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Avez-vous déjà séjourné dans les hautes altitudes dans le passé?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Avez-vous déjà souffert du mal aigü des montagnes?',
                description: 'Si oui, spécifiez les symptômes',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBox_8: {
                title: 'Avez-vous l\'une de ces conditions?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'MPOC sévère',
                    },
                    answer_2: {
                        label: 'Maladie du coeur avec une angine non contrôlée',
                    },
                    answer_3: {
                        label: 'Insuffisance cardiaque',
                    },
                    answer_4: {
                        label: 'Hypertension pulmonaire',
                    },
                    answer_5: {
                        label: 'Maladie cardiaque congénitale',
                    },
                    answer_6: {
                        label: 'Anomalies vasculaires pulmonaires',
                    },
                    answer_7: {
                        label: 'Anémie falciformes avec crises',
                    },
                    answer_8: {
                        label: 'Aucune de ces conditions',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention: Il est non recommandé de faire de l\'ascension en haute altitude en présence de l\'une de ces conditions de santé. Discutez-en, au besoin, lors de votre rendez-vous.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention: Il est non recommandé de faire de l\'ascension en haute altitude si votre grossesse est à risque élevé de complications. Veuillez en discuter lors de votre rendez-vous.',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Prenez-vous un contraceptif hormonal?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Stérilet hormonal',
                    },
                    answer_2: {
                        label: 'Pilule avec progestérone uniquement (Micronor)',
                    },
                    answer_3: {
                        label: 'Toutes autres pilules contraceptives',
                    },
                    answer_4: {
                        label: 'Anneau vaginal',
                    },
                    answer_5: {
                        label: 'Implant',
                    },
                    answer_6: {
                        label: 'Injection de Dépo-Provera',
                    },
                    answer_7: {
                        label: 'Timbre (Evra)',
                    },
                    answer_8: {
                        label: 'Aucune contraception hormonale',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Avez-vous des migraines avec aura?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBox_12: {
                title: 'Quelles conditions avez-vous parmi les suivantes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Obésité',
                    },
                    answer_2: {
                        label: 'MPOC légère',
                    },
                    answer_3: {
                        label: 'Asthme contrôlé',
                    },
                    answer_4: {
                        label: 'Hypertension artérielle contrôlée',
                    },
                    answer_5: {
                        label: 'Pontage coronarien sans angine',
                    },
                    answer_6: {
                        label: 'Anémie stable',
                    },
                    answer_7: {
                        label: 'Migraine',
                    },
                    answer_8: {
                        label: 'Maladie convulsive sous médication et stabilisée',
                    },
                    answer_9: {
                        label: 'Diabète stable et bien traité',
                    },
                    answer_10: {
                        label: 'Chirurgie de la cornée au laser',
                    },
                    answer_11: {
                        label: 'Maladies psychiatriques stabilisées',
                    },
                    answer_12: {
                        label: 'Cancer',
                    },
                    answer_13: {
                        label: 'Maladies inflammatoires',
                    },
                    answer_14: {
                        label: 'Aucune de ces conditions de santé',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_13: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_14: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_17: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var prophylaxisForAcuteMountainSicknessFrench = {
    prophylaxisForAcuteMountainSickness: {
        v2: {
            ...v2$O.prophylaxisForAcuteMountainSickness,
        },
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES SUR VOTRE VOYAGE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Informations sur l\'ascension',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Séjour prévu de plus de 24h en haute altitude (> 2000 m) sans dépasser 4000 m.',
                    },
                    answer_2: {
                        label: 'Il sera possible d\'évacuer rapidement',
                    },
                    answer_3: {
                        label: 'L\'ascension se fera selon les normes de sécurité en vigueur.',
                    },
                },
            },
            longAnswerTextArea_2: {
                title: 'Vitesse d\'ascension',
                description: 'Spécifiez la vitesse prévue d\'ascension incluant les informations sur l\'altidude pendant le sommeil.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Région visitée',
                description: 'Spécifiez la région visitée.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention : Certaines régions sont plus à risque que d\'autres notamment pour ce qui est de l\'isolement et de la difficulté à être évacué. Votre pharmacien se réserve le droit de vous référer vers un médecin s\'il juge cela plus sécuritaire.',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Ascension antérieure',
                description: 'Avez-vous déjà séjourné dans les hautes altitudes dans le passé ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Antécédent de mal aigü des montagnes',
                description: 'Avez-vous déjà souffert du mal aigü des montagnes ? Si oui, précisez quels symptômes vous avez ressenti',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser les symptômes ici',
                    },
                },
            },
            multipleChoiceCheckBox_6: {
                title: 'Avez-vous une de ces conditions ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'MPOC sévère',
                    },
                    answer_2: {
                        label: 'Maladie du coeur avec une angine non contrôlée',
                    },
                    answer_3: {
                        label: 'Insuffisance cardiaque',
                    },
                    answer_4: {
                        label: 'Hypertension pulmonaire',
                    },
                    answer_5: {
                        label: 'Maladie cardiaque congénitale',
                    },
                    answer_6: {
                        label: 'Anomalies vasculaires pulmonaires',
                    },
                    answer_7: {
                        label: 'Anémie falciformes avec crises',
                    },
                    answer_8: {
                        label: 'Aucune de ces conditions de santé',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention : Il est non recommandée de faire de l\'ascension en haute altitude en présence de l\'une de ces conditions de santé. Discutez-en, au besoin, avec votre professionnel de la santé',
                    },
                },
            },
            simpleChoiceRadio_7: {
                title: 'Êtes-vous enceinte ? ',
                description: 'Veuillez sélectionner ce qui correspond le mieux à votre situation',
                answer: {
                    answer_1: {
                        label: 'Grossesse à bas risque',
                    },
                    answer_2: {
                        label: 'Grossesse à risque élevé',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention : Il est non recommandée de faire de l\'ascension en haute altitude en présence de l\'une de ces conditions de santé. Veuillez en discuter avec votre professionnel de la santé.',
                    },
                },
            },
            simpleChoiceRadio_8: {
                title: 'Prenez-vous un contraceptif hormonal ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Stérilet hormonal',
                    },
                    answer_2: {
                        label: 'Pilule avec progestérone uniquement (Micronor)',
                    },
                    answer_3: {
                        label: 'Toutes autres pilules contraceptives',
                    },
                    answer_4: {
                        label: 'Anneau vaginal',
                    },
                    answer_5: {
                        label: 'Implant',
                    },
                    answer_6: {
                        label: 'Injection de Dépo-Provera',
                    },
                    answer_7: {
                        label: 'Timbre (Evra)',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Avez-vous des migraines avec aura ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBox_10: {
                title: 'Avez-vous une de ces conditions ?',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer et précisez dans la section réservée à cet effet.',
                answer: {
                    answer_1: {
                        label: 'Obésité',
                    },
                    answer_2: {
                        label: 'MPOC légère',
                    },
                    answer_3: {
                        label: 'Asthme contrôlé',
                    },
                    answer_4: {
                        label: 'Hypertension artérielle contrôlée',
                    },
                    answer_5: {
                        label: 'Pontage coronarien sans angine',
                    },
                    answer_6: {
                        label: 'Anémie stable',
                    },
                    answer_7: {
                        label: 'Migraine',
                    },
                    answer_8: {
                        label: 'Maladie convulsive sous médication et stabilisée',
                    },
                    answer_9: {
                        label: 'Diabète compensé',
                    },
                    answer_10: {
                        label: 'Chirurgie de la cornée au laser',
                    },
                    answer_11: {
                        label: 'Prise de contraceptifs oraux',
                    },
                    answer_12: {
                        label: 'Grossesse à bas risque',
                    },
                    answer_13: {
                        label: 'Maladies psychiatriques stabilisées',
                    },
                    answer_14: {
                        label: 'Cancer',
                    },
                    answer_15: {
                        label: 'Maladies inflammatoires',
                    },
                    answer_16: {
                        label: 'Aucune de ces conditions',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Tabagisme',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$N = {
    vaginitisAndBacterialVaginosis: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'YOUR HEALTH ',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Do you have any of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Whitish, cottage cheese-like vaginal secretions',
                    },
                    answer_2: {
                        label: 'Itchiness',
                    },
                    answer_3: {
                        label: 'No particular odor',
                    },
                    answer_4: {
                        label: 'Slight burning or tingling sensation in the vulva or vaginal area',
                    },
                    answer_5: {
                        label: 'Discomfort during sexual relations',
                    },
                    answer_6: {
                        label: 'None of the above',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            longAnswerTextArea_6: {
                title: 'What feminine care products do you use?',
                description: 'For example, the type of menstruation product, feminine hygiene product, etc. ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Some feminine hygiene products are more irritating than others. For some women, changing products or discontinuing use of certain products is enough to prevent recurrence of vaginitis. ',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_8: {
                title: 'In the last three months, what has been your situation?',
                description: 'Select the one that applies',
                answer: {
                    answer_1: {
                        label: 'Stable partner',
                    },
                    answer_2: {
                        label: 'A new partner',
                    },
                    answer_3: {
                        label: 'Multiple partners',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Having a new or multiple partner can also mean having a sexually transmitted infection. In the STI and Sexual Health tab on the ClicSanté home page, you can book a STI screening appointment.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var vaginitisAndBacterialVaginosisEnglish = {
    vaginitisAndBacterialVaginosis: {
        v2: {
            ...v2$N.vaginitisAndBacterialVaginosis,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_3: {
                title: 'YOUR HEALTH ',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'General',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Whitish, cottage cheese-like vaginal secretions',
                    },
                    answer_2: {
                        label: 'Itchiness',
                    },
                    answer_3: {
                        label: 'No particular odor',
                    },
                    answer_4: {
                        label: 'Slight burning or tingling sensation in the vulva or vaginal area',
                    },
                    answer_5: {
                        label: 'Discomfort during sexual relations',
                    },
                    answer_6: {
                        label: 'Other, specify here',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Sexual activity',
                description: 'Please indicate your situation in the last 3 months. ',
                answer: {
                    answer_1: {
                        label: 'Stable partner',
                    },
                    answer_2: {
                        label: 'A new partner',
                    },
                    answer_3: {
                        label: 'Multiple partners',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Having a new or multiple partner can also mean having a sexually transmitted infection. In the STI and Sexual Health tab on the ClicSanté home page, you can book a STI screening appointment.',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$M = {
    vaginitisAndBacterialVaginosis: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Sécrétions vaginales blanchâtres de type « fromage cottage »',
                    },
                    answer_2: {
                        label: 'Démangeaisons',
                    },
                    answer_3: {
                        label: 'Aucune d’odeur particulière',
                    },
                    answer_4: {
                        label: 'Légère sensation de brûlure ou de picotements au niveau de la vulve ou du vagin',
                    },
                    answer_5: {
                        label: 'Inconfort lors des relations sexuelles',
                    },
                    answer_6: {
                        label: 'Aucun de ces symptômes',
                    },
                    answer_7: {
                        label: 'Autre, précisez ici',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, précisez les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            longAnswerTextArea_6: {
                title: 'Quels produits d\'hygiènes féminines utilisez-vous? ',
                description: 'Par exemple, le type de produit pour les menstruations, nettoyant intime, etc. ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Certains produits hygiéniques féminins sont plus irritants que d\'autres. Dans certains cas, il suffit de changer de produit ou de cesser l\'utilisation de certains produits pour éviter les récidives des vaginites. ',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_8: {
                title: 'Dans les trois derniers mois, quelle a été votre situation?',
                description: 'Sélectionnez celle qui s\'applique',
                answer: {
                    answer_1: {
                        label: 'Partenaire stable',
                    },
                    answer_2: {
                        label: 'Un nouveau partenaire',
                    },
                    answer_3: {
                        label: 'Partenaires multiples',
                    },
                    answer_4: {
                        label: 'Aucun de ces choix',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avoir un nouveau ou de multiples partenaires peut aussi signifier infection transmissible sexuellement.Dans l\'onglet ITSS et Santé sexuelle sur la page d\'accueil de ClicSanté, il est possible de prendre un rendez-vous Dépistage ITSS.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var vaginitisAndBacterialVaginosisFrench = {
    vaginitisAndBacterialVaginosis: {
        v2: {
            ...v2$M.vaginitisAndBacterialVaginosis,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Généraux',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Sécrétions vaginales blanchâtres de type « fromage cottage »',
                    },
                    answer_2: {
                        label: 'Démangeaisons',
                    },
                    answer_3: {
                        label: 'Aucune odeur particulière',
                    },
                    answer_4: {
                        label: 'Légère sensation de brûlure ou de picotements au niveau de la vulve ou du vagin',
                    },
                    answer_5: {
                        label: 'Inconfort lors des relations sexuelles',
                    },
                    answer_6: {
                        label: 'Autre, précisez ici',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Activité sexuelle',
                description: 'Veuillez indiquer votre situation dans les 3 derniers mois. ',
                answer: {
                    answer_1: {
                        label: 'Partenaire stable',
                    },
                    answer_2: {
                        label: 'Un nouveau partenaire',
                    },
                    answer_3: {
                        label: 'Partenaires multiples',
                    },
                    answer_4: {
                        label: 'Aucun de ces choix',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avoir un nouveau ou de multiples partenaires peut aussi signifier infection transmissible sexuellement. Dans l’onglet « ITSS et Santé sexuelle » sur la page d’accueil de ClicSanté, il est possible de prendre un rendez-vous pour le dépistage d’une ITSS.',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$L = {
    smokingCessation: {
        section: {
            section_1: {
                title: 'YOUR SMOKING HABITS',
                description: '',
            },
            section_2: {
                title: 'YOUR DETERMINATION TO QUIT SMOKING',
                description: '',
            },
            section_3: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Have you used any tobacco products in the last 30 days? ',
                description: 'If so, please specify which ones (e.g. e-cigarette, cigarette, etc.)',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'For how many years have you been smoking?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'When do you smoke your first cigarette of the day? ',
                description: 'For example, I smoke my first cigarette less than 30 minutes after waking up',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'When do you smoke the most?',
                description: 'For example, I smoke more in the evening when I come home from work. I smoke more when I see friends',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Do you get up at night to smoke?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            shortAnswerText_6: {
                title: 'How many cigarettes do you smoke a day?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Have you ever tried to quit smoking with the help of a nicotine substitute?',
                description: 'If so, please indicate if it was within the last year (less than 12 months)',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'What nicotine substitutes have you already used?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Gum',
                    },
                    answer_2: {
                        label: 'Patch',
                    },
                    answer_3: {
                        label: 'Lozenge',
                    },
                    answer_4: {
                        label: 'Swallowable tablet',
                    },
                    answer_5: {
                        label: 'Tablet to be melted',
                    },
                    answer_6: {
                        label: 'Inhaler',
                    },
                    answer_7: {
                        label: 'No product',
                    },
                    answer_8: {
                        label: 'Other, specify here',
                    },
                },
            },
            shortAnswerText_9: {
                title: 'How did you tolerate these treatments?',
                description: 'For example, did you experience any side effects? Were you able to follow the treatment instructions?',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you use other products such as cannabis, alcohol or other substances?',
                description: 'If so, please specify which one(s) and indicate the quantity and frequency',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Do you feel ready to quit smoking?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'The Québec sans tabac website is packed with information and offers personalized support.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_12: {
                title: 'What motivates you most to quit smoking? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'My health',
                    },
                    answer_2: {
                        label: 'My finances (saving money)',
                    },
                    answer_3: {
                        label: 'My life partner',
                    },
                    answer_4: {
                        label: 'My entourage',
                    },
                    answer_5: {
                        label: 'My family (children, grandchildren, etc)',
                    },
                    answer_6: {
                        label: 'Other, please specify',
                    },
                },
            },
            longAnswerTextArea_13: {
                title: 'Do you have a goal you\'d like to achieve that will help you quit? ',
                description: 'For example, I\'m planning to go on a trip with the money I\'ve saved, I want to run a 5 km race in 6 months, and so on',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Setting a precise, measurable objective helps measure progress. This helps you persevere in changing this habit, despite the difficulties it involves.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_14: {
                title: 'Do you have a preference for one of these nicotine substitutes?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Gum',
                    },
                    answer_2: {
                        label: 'Patch',
                    },
                    answer_3: {
                        label: 'Lozenge',
                    },
                    answer_4: {
                        label: 'Swallowable tablet',
                    },
                    answer_5: {
                        label: 'Tablet to be melted',
                    },
                    answer_6: {
                        label: 'Inhaler',
                    },
                    answer_7: {
                        label: 'No product',
                    },
                    answer_8: {
                        label: 'Other, specify here',
                    },
                },
            },
            multipleChoiceCheckBox_15: {
                title: 'Do any of these situations apply to you?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Unstable angina in the last 2 weeks',
                    },
                    answer_2: {
                        label: 'Infarction within the last 2 weeks',
                    },
                    answer_3: {
                        label: 'Known severe arrhythmia',
                    },
                    answer_4: {
                        label: 'Stroke within the last 2 weeks',
                    },
                    answer_5: {
                        label: 'Generalized skin problem',
                    },
                    answer_6: {
                        label: 'Allergy to diachylons or adhesives',
                    },
                    answer_7: {
                        label: 'Partial or coronal dentures',
                    },
                    answer_8: {
                        label: 'COPD',
                    },
                    answer_9: {
                        label: 'Asthma',
                    },
                    answer_10: {
                        label: 'Menthol sensitivity',
                    },
                    answer_11: {
                        label: 'Seizure disorders',
                    },
                    answer_12: {
                        label: 'Bulimia or anorexia',
                    },
                    answer_13: {
                        label: 'Withdrawal from alcohol, benzodiazepines or other sedatives',
                    },
                    answer_14: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_17: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            simpleChoiceRadio_18: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_19: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_20: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_21: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var smokingCessationEnglish = {
    smokingCessation: {
        v2: {
            ...v2$L.smokingCessation,
        },
        section: {
            section_1: {
                title: 'YOUR SMOKING HABITS',
                description: '',
            },
            section_2: {
                title: 'YOUR DETERMINATION TO QUIT SMOKING',
                description: '',
            },
            section_3: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Have you used any tobacco products in the last 30 days? ',
                description: 'If yes, which smoking product did you use? E.g. vaporizer, cigarette, gum, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'For how many years have you been smoking?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'When do you smoke your first cigarette of the day? ',
                description: 'For example, I smoke my first cigarette less than 30 minutes after waking up.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'When do you smoke the most?',
                description: 'For example, I smoke more in the evening when I get home from work. I smoke more when I see friends, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'How many cigarettes do you smoke a day?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Have you ever tried quitting smoking?',
                description: 'If so, please specify which treatments you have already tried.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Are you using another product apart from cigarettes?',
                description: 'If so, please specify which ones. For example: vape, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Do you feel ready to quit smoking?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
                alert: {
                    alert_1: {
                        message: '<a href="https://www.tobaccofreequebec.ca/iquitnow" target="_blank">The Tobacco-Free Quebec website</a> is packed with information and offers personalized support. ',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'What motivates you most to quit smoking? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'My health',
                    },
                    answer_2: {
                        label: 'My finances (saving money)',
                    },
                    answer_3: {
                        label: 'My life partner',
                    },
                    answer_4: {
                        label: 'My entourage',
                    },
                    answer_5: {
                        label: 'My family (children, grandchildren, etc)',
                    },
                    answer_6: {
                        label: 'Other, please specify',
                    },
                },
            },
            longAnswerTextArea_10: {
                title: 'Do you have a goal you\'d like to achieve that will help you quit? ',
                description: 'For example, I\'m planning to go on a trip with the money I\'ve saved, I want to run a 5 km race in 6 months, and so on.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Setting a precise, measurable objective helps measure progress. This helps you persevere in changing this habit, despite the difficulties it involves.',
                    },
                },
            },
            multipleChoiceCheckBox_11: {
                title: 'Do any of these situations apply to you?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Unstable angina in the last 2 weeks',
                    },
                    answer_2: {
                        label: 'Infarction within the last 2 weeks',
                    },
                    answer_3: {
                        label: 'Known severe arrhythmia',
                    },
                    answer_4: {
                        label: 'Stroke within the last 2 weeks',
                    },
                    answer_5: {
                        label: 'Generalized skin problem',
                    },
                    answer_6: {
                        label: 'Allergy to diachylons or adhesives',
                    },
                    answer_7: {
                        label: 'Partial or coronal dentures',
                    },
                    answer_8: {
                        label: 'COPD',
                    },
                    answer_9: {
                        label: 'Asthma',
                    },
                    answer_10: {
                        label: 'Menthol sensitivity',
                    },
                    answer_11: {
                        label: 'Seizure disorders',
                    },
                    answer_12: {
                        label: 'Bulimia or anorexia',
                    },
                    answer_13: {
                        label: 'Withdrawal from alcohol, benzodiazepines or other sedatives',
                    },
                    answer_14: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButton_12: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_13: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$K = {
    smokingCessation: {
        section: {
            section_1: {
                title: 'VOS HABITUDES DE CONSOMMATION DU TABAC',
                description: '',
            },
            section_2: {
                title: 'VOTRE DÉTERMINATION À CESSER DE FUMER',
                description: '',
            },
            section_3: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Avez-vous consommé un ou des produits tabagiques dans les 30 derniers jours? ',
                description: 'Si oui, spécifiez lesquels (ex. vapoteuse, cigarette, etc.)',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien d\'années fumez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'À quel moment fumez-vous votre première cigarette de la journée? ',
                description: 'Par exemple, je fume la première cigarette moins de 30 minutes après mon réveil',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Dans quel contexte fumez-vous le plus?',
                description: 'Par exemple, je fume plus le soir en rentrant du travail. Je fume plus lorsque je vois des amis',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Vous levez-vous la nuit pour fumer?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            shortAnswerText_6: {
                title: 'Combien de cigarettes fumez-vous par jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Avez-vous déjà essayer d\'arrêter de fumer avec l\'aide d\'un substitut de la nicotine?',
                description: 'Si oui, est-ce que c\'était dans la dernière année (moins de 12 mois)?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Quels substituts de la nicotine avez-vous déjà utilisé?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Gomme',
                    },
                    answer_2: {
                        label: 'Timbre (patch)',
                    },
                    answer_3: {
                        label: 'Pastille',
                    },
                    answer_4: {
                        label: 'Comprimé à avaler',
                    },
                    answer_5: {
                        label: 'Comprimé à faire fondre',
                    },
                    answer_6: {
                        label: 'Inhalateur',
                    },
                    answer_7: {
                        label: 'Aucun produit',
                    },
                    answer_8: {
                        label: 'Autre, précisez ici',
                    },
                },
            },
            shortAnswerText_9: {
                title: 'Comment avez-vous toléré ces traitements?',
                description: 'Par exemple, avez-vous eu des effets secondaires? Étiez-vous capable de suivre les indications du traitements?',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Consommez-vous d\'autres produits tel que du cannabis, de l\'acool ou d\'autres substances?',
                description: 'Si oui, spécifiez lequel ou lesquels ainsi que d\'indiquer la quantité et la fréquence',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Vous sentez-vous prêt à arrêter de fumer?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Le site internet Québec sans tabac regorge d\'informations en plus d\'offrir du soutien personnalisé.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_12: {
                title: 'Qu\'est-ce qui vous motive le plus à arrêter de fumer? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Ma santé',
                    },
                    answer_2: {
                        label: 'Mes finances (l\'économie d\'argent)',
                    },
                    answer_3: {
                        label: 'Mon partenaire de vie',
                    },
                    answer_4: {
                        label: 'Mon entourage',
                    },
                    answer_5: {
                        label: 'Ma famille (enfants, petits-enfants, etc)',
                    },
                    answer_6: {
                        label: 'Autres, spécifiez',
                    },
                },
            },
            longAnswerTextArea_13: {
                title: 'Avez-vous un objectif que vous désirez atteindre qui vous aidera à persister dans votre cessation tabagique? ',
                description: 'Par exemple, je planifie partir en voyage avec l\'argent économisé, je désire courir un 5 km dans 6 mois, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Ce fixer un objectif précis et mesurable, permet de mesurer les progrès réalisés. Cela contribue à persévérer dans le changement malgré la difficulté que cela occasionne.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_14: {
                title: 'Avez-vous une préférence pour un de ces substituts de la nicotine?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Gomme',
                    },
                    answer_2: {
                        label: 'Timbre (patch)',
                    },
                    answer_3: {
                        label: 'Pastille',
                    },
                    answer_4: {
                        label: 'Comprimé à avaler',
                    },
                    answer_5: {
                        label: 'Comprimé à faire fondre',
                    },
                    answer_6: {
                        label: 'Inhalateur',
                    },
                    answer_7: {
                        label: 'Aucun produit',
                    },
                    answer_8: {
                        label: 'Autre, précisez ici',
                    },
                },
            },
            multipleChoiceCheckBox_15: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Angine instable dans les 2 dernières semaines',
                    },
                    answer_2: {
                        label: 'Infarctus dans les 2 dernières semaines',
                    },
                    answer_3: {
                        label: 'Arythmie sévère connue',
                    },
                    answer_4: {
                        label: 'AVC dans les 2 dernières semaines',
                    },
                    answer_5: {
                        label: 'Problème de peau généralisée',
                    },
                    answer_6: {
                        label: 'Allergie aux diachylons ou aux adhésifs',
                    },
                    answer_7: {
                        label: 'Dentier, partiel ou couronne',
                    },
                    answer_8: {
                        label: 'MPOC',
                    },
                    answer_9: {
                        label: 'Asthme',
                    },
                    answer_10: {
                        label: 'Sensibilité au menthol',
                    },
                    answer_11: {
                        label: 'Troubles convulsivafs',
                    },
                    answer_12: {
                        label: 'Boulimie ou anorexie',
                    },
                    answer_13: {
                        label: 'Sevrage d\'alcool, de benzodiazépine ou d\'autres sédatifs',
                    },
                    answer_14: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_17: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            simpleChoiceRadio_18: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_19: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_20: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_21: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var smokingCessationFrench = {
    smokingCessation: {
        v2: {
            ...v2$K.smokingCessation,
        },
        section: {
            section_1: {
                title: 'VOS HABITUDES DE CONSOMMATION DU TABAC',
                description: '',
            },
            section_2: {
                title: 'VOTRE DÉTERMINATION À CESSER DE FUMER',
                description: '',
            },
            section_3: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Avez-vous consommé un produit tabagique dans les 30 derniers jours ? ',
                description: 'Si oui, quel produit tabagique avez-vous consommé ? Ex. vapoteuse, cigarette, gomme, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien d\'années fumez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'À quel moment fumez-vous votre première cigarette du jour ? ',
                description: 'Par exemple, je fume ma première cigarette moins de 30 minutes après mon réveil.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Dans quel contexte fumez-vous le plus ?',
                description: 'Par exemple, je fume plus le soir en rentrant du travail. Je fume plus lorsque je vois des amis. ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'Combien de cigarettes fumez-vous par jour ?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Avez-vous déjà essayé d\'arrêter de fumer ?',
                description: 'Si oui, spécifiez les traitements que vous avez déjà essayés.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Est-ce que vous utilisez un autre produit que la cigarette ?',
                description: 'Si oui, spécifiez lesquels. Par exemple, vapoteuse, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Vous sentez-vous prêt à arrêter de fumer ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
                alert: {
                    alert_1: {
                        message: '<a href="https://www.quebecsanstabac.ca/je-minforme" target="_blank">Le site internet Québec sans tabac</a> regorge d\'informations en plus d\'offrir du soutien personnalisé.',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Qu\'est-ce qui vous motive le plus à arrêter de fumer ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Ma santé',
                    },
                    answer_2: {
                        label: 'Mes finances (l\'économie d\'argent)',
                    },
                    answer_3: {
                        label: 'Mon partenaire de vie',
                    },
                    answer_4: {
                        label: 'Mon entourage',
                    },
                    answer_5: {
                        label: 'Ma famille (enfants, petits-enfants, etc)',
                    },
                    answer_6: {
                        label: 'Autres, spécifiez',
                    },
                },
            },
            longAnswerTextArea_10: {
                title: 'Avez-vous un objectif que vous désirez atteindre qui vous aidera à persister dans votre cessation tabagique ? ',
                description: 'Par exemple, je planifie partir en voyage avec l\'argent économisé, je désire courir un 5 km dans 6 mois, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Se fixer un objectif précis et mesurable permet de mesurer les progrès réalisés. Cela contribue à persévérer dans le changement malgré la difficulté que cela occasionne.',
                    },
                },
            },
            multipleChoiceCheckBox_11: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Angine instable dans les 2 dernières semaines',
                    },
                    answer_2: {
                        label: 'Infarctus dans les 2 dernières semaines',
                    },
                    answer_3: {
                        label: 'Arythmie sévère connue',
                    },
                    answer_4: {
                        label: 'AVC dans les 2 dernières semaines',
                    },
                    answer_5: {
                        label: 'Problème de peau généralisé',
                    },
                    answer_6: {
                        label: 'Allergie aux diachylons ou aux adhésifs',
                    },
                    answer_7: {
                        label: 'Dentier, partiel ou couronne',
                    },
                    answer_8: {
                        label: 'MPOC',
                    },
                    answer_9: {
                        label: 'Asthme',
                    },
                    answer_10: {
                        label: 'Sensibilité au menthol',
                    },
                    answer_11: {
                        label: 'Troubles convulsifs',
                    },
                    answer_12: {
                        label: 'Boulimie ou anorexie',
                    },
                    answer_13: {
                        label: 'Sevrage d\'alcool, de benzodiazépine ou d\'autres sédatifs',
                    },
                    answer_14: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            yesNoButton_12: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_13: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$J = {
    preventionOfTravelersDiarrheaAndCholera: {
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATION ABOUT YOUR TRIP',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Does one or more of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Travel outside the tourist circuit',
                    },
                    answer_2: {
                        label: 'Long stay in the country visited',
                    },
                    answer_3: {
                        label: 'Travel during the rainy season',
                    },
                    answer_4: {
                        label: 'Suffering from achlorhydia',
                    },
                    answer_5: {
                        label: 'HIV positive',
                    },
                    answer_6: {
                        label: 'Immunosuppressed',
                    },
                    answer_7: {
                        label: 'Have an active cancer',
                    },
                    answer_8: {
                        label: 'Less than 3 months since a heart attack',
                    },
                    answer_9: {
                        label: 'Have unstable angina',
                    },
                    answer_10: {
                        label: 'Pregnancy in 3rd trimester',
                    },
                    answer_11: {
                        label: 'Multiple pregnancies',
                    },
                    answer_12: {
                        label: 'Uncontrolled chronic disease: diabetes, hypertension, etc.',
                    },
                    answer_13: {
                        label: 'Suffering from a serious chronic disease such as heart or kidney failure',
                    },
                    answer_14: {
                        label: 'Taking anticoagulants for less than 6 months',
                    },
                    answer_15: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Please note: Some conditions require medical advice. You may be referred to a medical clinic.',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Are you under 18 years old?',
                description: 'If yes, please indicate the traveler\'s weight in kg',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Which country and region will you be traveling to?',
                description: 'Specify the country and region visited',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Some simple gestures reduce the risk of contracting traveler\'s diarrhea: good hand hygiene, drinking or using only bottled water and well-cooked and boiled food. Ask your health care professional for more information.',
                    },
                },
            },
            longAnswerTextArea_4: {
                title: 'Your travel itinerary',
                description: 'Indicate the different places visited during your trip as well as the expected duration at each place',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'How long will your trip last?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            simpleChoiceRadio_6: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Are you pregnant?',
                description: 'If so, please indicate the number of weeks',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var diarrheaPreventionEnglish = {
    preventionOfTravelersDiarrheaAndCholera: {
        v2: {
            ...v2$J.preventionOfTravelersDiarrheaAndCholera,
        },
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATION ABOUT YOUR TRIP',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Does one or more of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Travel outside the tourist circuit',
                    },
                    answer_2: {
                        label: 'Long stay in the country visited',
                    },
                    answer_3: {
                        label: 'Travel during the rainy season',
                    },
                    answer_4: {
                        label: 'Suffering from achlorhydia',
                    },
                    answer_5: {
                        label: 'HIV positive',
                    },
                    answer_6: {
                        label: 'Immunosuppressed.',
                    },
                    answer_7: {
                        label: 'Having an active cancer',
                    },
                    answer_8: {
                        label: 'Less than 3 months since a heart attack',
                    },
                    answer_9: {
                        label: 'Having unstable angina',
                    },
                    answer_10: {
                        label: 'Pregnancy in 3rd trimester',
                    },
                    answer_11: {
                        label: 'Multiple pregnancies',
                    },
                    answer_12: {
                        label: 'Uncontrolled chronic disease: diabetes, hypertension, etc.',
                    },
                    answer_13: {
                        label: 'Suffering from a serious chronic disease such as heart or kidney failure',
                    },
                    answer_14: {
                        label: 'Taking anticoagulants for less than 6 months',
                    },
                    answer_15: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Warning: Some of these conditions require medical advice. Your pharmacist may refer you to a doctor if necessary.',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Are you under 18 years old?',
                description: 'If so, please indicate the traveler\'s weight in kg',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_3: {
                title: 'What region are you traveling to?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Southeast Asia',
                    },
                    answer_2: {
                        label: 'India',
                    },
                    answer_3: {
                        label: 'Mexico',
                    },
                    answer_4: {
                        label: 'Haiti',
                    },
                    answer_5: {
                        label: 'Dominican Republic',
                    },
                    answer_6: {
                        label: 'Middle East',
                    },
                    answer_7: {
                        label: 'Africa',
                    },
                    answer_8: {
                        label: 'Central America',
                    },
                    answer_9: {
                        label: 'South America',
                    },
                    answer_10: {
                        label: 'Other regions, please specify:',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Some simple gestures such as practicing good hand hygiene, drinking or using only bottled water and well-cooked and boiled food reduce the risk of contracting traveler\'s diarrhea. Ask your health care professional for more information.',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Are you pregnant?',
                description: 'If so, please indicate the number of weeks.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$I = {
    preventionOfTravelersDiarrheaAndCholera: {
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES SUR VOTRE VOYAGE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Est-ce que l\'une ou plusieurs de ces situations s\'appliquent à vous? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Voyage hors des circuits touristiques',
                    },
                    answer_2: {
                        label: 'Long séjour dans le pays visité',
                    },
                    answer_3: {
                        label: 'Voyage pendant la saison des pluies',
                    },
                    answer_4: {
                        label: 'Souffrir d\'achlorhydie',
                    },
                    answer_5: {
                        label: 'VIH',
                    },
                    answer_6: {
                        label: 'Être immunodéprimé ou immunodéprimée',
                    },
                    answer_7: {
                        label: 'Avoir un cancer actif',
                    },
                    answer_8: {
                        label: 'Moins de 3 mois depuis un infarctus',
                    },
                    answer_9: {
                        label: 'Avoir de l\'angine instable',
                    },
                    answer_10: {
                        label: 'Grossesse au 3e trimestre',
                    },
                    answer_11: {
                        label: 'Grossesse multiple',
                    },
                    answer_12: {
                        label: 'Maladie chronique non controlée: diabète, hypertension, etc.',
                    },
                    answer_13: {
                        label: 'Souffrir d\'une maladie chronique grave: insuffisance cardiaque ou rénale',
                    },
                    answer_14: {
                        label: 'Prendre des anticoagulants depuis moins de 6 mois',
                    },
                    answer_15: {
                        label: 'Aucune de ces situations',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention: Certaines conditions nécessitent un avis médical. Il est possible que l\'on vous réfère vers une clinique médicale.',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Avez-vous moins de 18 ans?',
                description: 'Si oui, indiquez le poids en kg du voyageur',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Dans quel pays et région voyagerez-vous?',
                description: 'Spécifiez le pays ainsi que la région visitée',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Certains gestes simple réduisent le risque de contracter la diarrhée du voyageur: avoir un bon hygiène des mains, ne boire ou n\'utiliser que de l\'eau embouteillé et des aliments bien cuits et bouilli. Demandez à votre professionnel de la santé afin d\'obtenir plus d\'informations.',
                    },
                },
            },
            longAnswerTextArea_4: {
                title: 'Itinéraire de votre voyage',
                description: 'Indiquez les différents endroits visités durant votre voyage ainsi que la durée prévue à chaque endroit',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'Quelle est la durée totale de votre voyage?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            simpleChoiceRadio_6: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Êtes-vous enceinte? ',
                description: 'Si oui, indiquez le nombre de semaines de grossesse',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var diarrheaPreventionFrench = {
    preventionOfTravelersDiarrheaAndCholera: {
        v2: {
            ...v2$I.preventionOfTravelersDiarrheaAndCholera,
        },
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES SUR VOTRE VOYAGE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Est-ce que une ou plusieurs de ces situations s\'appliquent à vous ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Voyage hors des circuits touristiques',
                    },
                    answer_2: {
                        label: 'Long séjour dans le pays visité',
                    },
                    answer_3: {
                        label: 'Voyage pendant la saison des pluies',
                    },
                    answer_4: {
                        label: 'Souffrir d\'achlorhydie',
                    },
                    answer_5: {
                        label: 'VIH',
                    },
                    answer_6: {
                        label: 'Être immunodéprimé.',
                    },
                    answer_7: {
                        label: 'Avoir un cancer actif',
                    },
                    answer_8: {
                        label: 'Moins de 3 mois depuis un infarctus',
                    },
                    answer_9: {
                        label: 'Avoir de l\'angine instable',
                    },
                    answer_10: {
                        label: 'Grossesse au 3e trimestre',
                    },
                    answer_11: {
                        label: 'Grossesse multiple',
                    },
                    answer_12: {
                        label: 'Maladie chronique non contrôlée : diabète, hypertension, etc.',
                    },
                    answer_13: {
                        label: 'Souffrir d\'une maladie chronique grave : insuffisance cardiaque ou rénale',
                    },
                    answer_14: {
                        label: 'Prendre des anticoagulants depuis moins de 6 mois',
                    },
                    answer_15: {
                        label: 'Aucune de ces situations',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention : Certaines conditions nécessitent un avis médical. Votre pharmacien se réserve le droit de vous référer vers un médecin s\'il juge cela nécessaire.',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Avez-vous moins de 18 ans ?',
                description: 'Si oui, indiquez le poids en kg du voyageur',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_3: {
                title: 'Dans quelle région voyagez-vous ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Asie du Sud-Est',
                    },
                    answer_2: {
                        label: 'Inde',
                    },
                    answer_3: {
                        label: 'Mexique',
                    },
                    answer_4: {
                        label: 'Haïti',
                    },
                    answer_5: {
                        label: 'République Dominicaine',
                    },
                    answer_6: {
                        label: 'Moyen-Orient',
                    },
                    answer_7: {
                        label: 'Afrique',
                    },
                    answer_8: {
                        label: 'Amérique centrale',
                    },
                    answer_9: {
                        label: 'Amérique du Sud',
                    },
                    answer_10: {
                        label: 'Autres régions, précisez :',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Certains gestes simples réduisent le risque de contracter la diarrhée du voyageur : avoir une bonne hygiène des mains, ne boire ou n\'utiliser que de l\'eau embouteillée et des aliments bien cuits et bouillis. Demandez à votre professionnel de la santé afin d\'obtenir plus d\'informations.',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Êtes-vous enceinte ? ',
                description: 'Si oui, indiquez le nombre de semaines.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$H = {
    eczemaAtopicDermatitis: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH ',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Itching, sometimes intense, that can cause scratches (type of wound)',
                    },
                    answer_2: {
                        label: 'Skin breaking out and redness',
                    },
                    answer_3: {
                        label: 'Small pimples with clear fluid',
                    },
                    answer_4: {
                        label: 'No eczema patches',
                    },
                    answer_5: {
                        label: 'Slight itching',
                    },
                    answer_6: {
                        label: 'Dry skin all over the body',
                    },
                    answer_7: {
                        label: 'Irregular redness with flaking skin',
                    },
                    answer_8: {
                        label: 'None of these symptoms',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Contact with a toxic plant or a chemical product',
                    },
                    answer_2: {
                        label: 'Recently started a new medication',
                    },
                    answer_3: {
                        label: 'Potential burn in affected area',
                    },
                    answer_4: {
                        label: 'Injury in affected area',
                    },
                    answer_5: {
                        label: 'Lesions present on more than 30% of the body',
                    },
                    answer_6: {
                        label: 'New lesion on face',
                    },
                    answer_7: {
                        label: 'Blistering in affected area',
                    },
                    answer_8: {
                        label: 'Recurrent lesions',
                    },
                    answer_9: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'For how long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are your symptoms the same as in a previous episode? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            simpleChoiceRadio_6: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var eczemaEnglish = {
    eczemaAtopicDermatitis: {
        v2: {
            ...v2$H.eczemaAtopicDermatitis,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH ',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Itching, sometimes intense, causing scratches ( type of wound)',
                    },
                    answer_2: {
                        label: 'Skin breaking out and redness',
                    },
                    answer_3: {
                        label: 'Small pimple with clear fluid',
                    },
                    answer_4: {
                        label: 'No eczema patches',
                    },
                    answer_5: {
                        label: 'Slight itching',
                    },
                    answer_6: {
                        label: 'Dry skin all over the body',
                    },
                    answer_7: {
                        label: 'Irregular redness with flaking skin',
                    },
                    answer_8: {
                        label: 'None of these symptoms',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Contact with a toxic plant or a chemical product',
                    },
                    answer_2: {
                        label: 'Recently started a new medication',
                    },
                    answer_3: {
                        label: 'Potential burn in affected area',
                    },
                    answer_4: {
                        label: 'Injury in affected area',
                    },
                    answer_5: {
                        label: 'Lesions present on more than 30% of the body',
                    },
                    answer_6: {
                        label: 'New lesion on face',
                    },
                    answer_7: {
                        label: 'Blistering in affected area',
                    },
                    answer_8: {
                        label: 'Recurrent lesions',
                    },
                    answer_9: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Did you use any products in the past week(s) to relieve your symptoms? ',
                description: 'If so, please indicate which ones and for how long.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications ?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$G = {
    eczemaAtopicDermatitis: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Présentez-vous certains de ces symptômes? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Démangeaisons parfois intense pouvant causé des écorchures (type de plaie)',
                    },
                    answer_2: {
                        label: 'Gerçure et rougeur de la peau',
                    },
                    answer_3: {
                        label: 'Petits boutons avec un écoulement clair',
                    },
                    answer_4: {
                        label: 'Aucune plaque d\'eczéma',
                    },
                    answer_5: {
                        label: 'Légère démangeaisons',
                    },
                    answer_6: {
                        label: 'Sécheresse de la peau partout sur le corps',
                    },
                    answer_7: {
                        label: 'Rougeurs irrégulières avec la peau qui à tendance à écailler',
                    },
                    answer_8: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Possible contact avec une plante ou un produit chimique',
                    },
                    answer_2: {
                        label: 'Nouveau médicament débuté récemment',
                    },
                    answer_3: {
                        label: 'Possible brûlure dans la zone atteinte',
                    },
                    answer_4: {
                        label: 'Blessure dans la zone',
                    },
                    answer_5: {
                        label: 'Lésions sur plus de 30% du corps',
                    },
                    answer_6: {
                        label: 'Nouvelle lésion au niveau du visage',
                    },
                    answer_7: {
                        label: 'Présence d\'ampoule au niveau de la zone',
                    },
                    answer_8: {
                        label: 'Lésions récurrentes',
                    },
                    answer_9: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Est-ce que vos symptômes sont les mêmes que lors d\'un épisode précédent? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, précisez les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            simpleChoiceRadio_6: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var eczemaFrench = {
    eczemaAtopicDermatitis: {
        v2: {
            ...v2$G.eczemaAtopicDermatitis,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Présentez-vous certains de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Démangeaison parfois intense pouvant causer des écorchures (type de plaie)',
                    },
                    answer_2: {
                        label: 'Gerçure et rougeur de la peau',
                    },
                    answer_3: {
                        label: 'Petit bouton avec un écoulement clair',
                    },
                    answer_4: {
                        label: 'Aucune plaque d\'eczéma',
                    },
                    answer_5: {
                        label: 'Légère démangeaison',
                    },
                    answer_6: {
                        label: 'Sécheresse de la peau partout sur le corps',
                    },
                    answer_7: {
                        label: 'Rougeur irrégulière avec la peau qui a tendance à écailler',
                    },
                    answer_8: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Est-ce qu\'une de ces situation s\'applique à vous ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Possible contact avec une plante ou un produit chimique',
                    },
                    answer_2: {
                        label: 'Nouveau médicament débuté récemment',
                    },
                    answer_3: {
                        label: 'Possible brûlure dans la zone atteinte',
                    },
                    answer_4: {
                        label: 'Blessure dans la zone',
                    },
                    answer_5: {
                        label: 'Lésions sur plus de 30% du corps',
                    },
                    answer_6: {
                        label: 'Nouvelle lésion au niveau du visage',
                    },
                    answer_7: {
                        label: 'Présence d\'ampoule au niveau de la zone',
                    },
                    answer_8: {
                        label: 'Lésions récurrentes',
                    },
                    answer_9: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous utilisé un ou des produits dans la ou les dernières semaines pour soulager vos symptômes ? ',
                description: 'Si oui, veuillez indiquer lesquels et depuis quand.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$F = {
    mouthUlcers: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH ',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Deep, numerous (more than 5) and painful lesions',
                    },
                    answer_2: {
                        label: 'More than 1 cm in diameter',
                    },
                    answer_3: {
                        label: 'HIV positive',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'These symptoms are associated with a major form of the disorder, and healing may take several weeks.',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Have you ever had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Do you have one or more of these conditions?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Taking opioids (morphine, dilaudid, fentanyl, etc.)',
                    },
                    answer_2: {
                        label: 'Anti-inflammatory drugs (Naprosyn, etc.)',
                    },
                    answer_3: {
                        label: 'Taking a beta-blocker (metoprolol, bisoprolol, proranolol or atenolol)',
                    },
                    answer_4: {
                        label: 'Use of drugs known to be cytotoxic (chemotherapy, etc.)',
                    },
                    answer_5: {
                        label: 'Oral or sexual contact with a person having the same symptoms',
                    },
                    answer_6: {
                        label: 'Orthodontic appliances',
                    },
                    answer_7: {
                        label: 'Dental prostheses',
                    },
                    answer_8: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Warning: Some conditions require medical advice. During your appointment, you may be referred to a medical clinic if necessary.',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var mouthUlcersEnglish = {
    mouthUlcers: {
        v2: {
            ...v2$F.mouthUlcers,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have one or more of these conditions?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Taking opioids,',
                    },
                    answer_2: {
                        label: 'Anti-inflammatory drugs,',
                    },
                    answer_3: {
                        label: 'Taking beta-blockers,',
                    },
                    answer_4: {
                        label: 'Use of drugs known to be cytotoxic.',
                    },
                    answer_5: {
                        label: 'Oral or sexual contact with a person with the same symptoms.',
                    },
                    answer_6: {
                        label: 'Orthodontic appliances',
                    },
                    answer_7: {
                        label: 'Dental prostheses',
                    },
                    answer_8: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Warning: Some conditions may require medical advice. If deemed necessary, your pharmacist may refer you to a doctor.',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Small, round or oval, painful lesions',
                    },
                    answer_2: {
                        label: 'Less than 1 cm in diameter',
                    },
                    answer_3: {
                        label: 'Located in the mucosa of the lips, cheeks, tongue, palate or under the tongue',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'These symptoms are associated with a minor form of the disorder. Healing occurs spontaneously in most cases within 7 to 10 days.',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Deep, numerous (more than 5) and painful lesions',
                    },
                    answer_2: {
                        label: 'More than 1 cm in diameter',
                    },
                    answer_3: {
                        label: 'HIV positive',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'These symptoms are associated with a major form of the disorder, and healing may take several weeks.',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are your symptoms similar to those of a previous episode? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day ',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Alcoholic beverages',
                description: 'Do you drink alcoholic beverages? If so, please specify quantity.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$E = {
    mouthUlcers: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Plusieurs lésions profondes (+ de 5) et douloureuses',
                    },
                    answer_2: {
                        label: 'Diamètre de plus de 1 cm',
                    },
                    answer_3: {
                        label: 'Atteint du VIH',
                    },
                    answer_4: {
                        label: 'Aucun de ces symptômes',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Ces symptômes sont associés à une forme majeure du trouble et la guérison peut prendre plusieurs semaines.',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Avez-vous déjà eu ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Avez-vous l\'une ou plusieurs de ces conditions?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Prise d\'opioïdes (morphine, dilaudid, fentanyl, etc)',
                    },
                    answer_2: {
                        label: 'Prise d\'anti-inflammatoire (Naprosyn, etc.)',
                    },
                    answer_3: {
                        label: 'Prise d\'un bêta-bloqueur (metoprolol, bisoprolol, proranolol ou atenolol)',
                    },
                    answer_4: {
                        label: 'Prise d\'un médicament connu pour être cytotoxique (chimiothérapie, etc.)',
                    },
                    answer_5: {
                        label: 'Contact oral ou sexuel avec une personne ayant les mêmes symptômes',
                    },
                    answer_6: {
                        label: 'Appareils orthodontiques',
                    },
                    answer_7: {
                        label: 'Prothèses dentaires',
                    },
                    answer_8: {
                        label: 'Aucune de ces conditions',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention: Certaines conditions nécessitent un avis médical. Lors de votre rendez-vous, vous pourriez être redirigé vers une clinique médicale si cela est nécessaire.',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var mouthUlcersFrench = {
    mouthUlcers: {
        v2: {
            ...v2$E.mouthUlcers,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous une ou plusieurs de ces conditions ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Prise d\'opioïdes,',
                    },
                    answer_2: {
                        label: 'Prise d\'anti-inflammatoires,',
                    },
                    answer_3: {
                        label: 'Prise de bêta-bloqueurs,',
                    },
                    answer_4: {
                        label: 'Prise d\'un médicament connu pour être cytotoxique.',
                    },
                    answer_5: {
                        label: 'Contact oral ou sexuel avec une personne ayant les mêmes symptômes.',
                    },
                    answer_6: {
                        label: 'Appareils orthodontiques',
                    },
                    answer_7: {
                        label: 'Prothèses dentaires',
                    },
                    answer_8: {
                        label: 'Aucune de ces conditions',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Attention : Certaines conditions nécessitent un avis médical. Votre pharmacien se réserve le droit de vous référer vers un médecin s\'il juge cela nécessaire.',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Avez-vous un ou plusieurs de ces symptômes ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Lésions petites, rondes ou ovales et douloureuses',
                    },
                    answer_2: {
                        label: 'Diamètre de moins de 1 cm',
                    },
                    answer_3: {
                        label: 'Situées dans la muqueuse des lèvres, des joues, de la langue, du palais ou sous la langue',
                    },
                    answer_4: {
                        label: 'Aucun de ces symptômes',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Ces symptômes sont associés à une forme mineure du trouble et la guérison est la plupart du temps spontanée en 7 à 10 jours.',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Avez-vous un ou plusieurs de ces symptômes ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Lésion profonde, nombreuse (+ de 5) et douloureuse',
                    },
                    answer_2: {
                        label: 'Diamètre de plus de 1 cm',
                    },
                    answer_3: {
                        label: 'Atteint du VIH',
                    },
                    answer_4: {
                        label: 'Aucun de ces symptômes',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Ces symptômes sont associés à une forme majeure du trouble et la guérison peut prendre plusieurs semaines.',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Est-ce que vos symptômes sont les mêmes que lors d\'un épisode précédent ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Tabagisme',
                description: 'Si oui, pouvez-vous inscrire le nombre de cigarettes par jour ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Boissons alcoolisées',
                description: 'Consommez-vous des boissons alcoolisées ? Si oui, spécifiez la quantité.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$D = {
    oralThrush: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Whitish deposits in the mouth (tongue, palate, etc.)',
                    },
                    answer_2: {
                        label: 'Pain on swallowing',
                    },
                    answer_3: {
                        label: 'Sensitivity and blurred sensation in the mouth',
                    },
                    answer_4: {
                        label: 'Loss of taste',
                    },
                    answer_5: {
                        label: 'Slight bleeding when plaque is scraped off',
                    },
                    answer_6: {
                        label: 'Swelling of the gums',
                    },
                    answer_7: {
                        label: 'None of these symptoms',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Difficulty swallowing',
                    },
                    answer_2: {
                        label: 'Pain in the mouth',
                    },
                    answer_3: {
                        label: 'Irritation or cracks that may extend beyond the lips (chin, etc.)',
                    },
                    answer_4: {
                        label: 'Lesions around the eyes or in the genital area',
                    },
                    answer_5: {
                        label: 'None of these symptoms',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Have you had these symptoms in the past? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBox_6: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Past history of dry mouth',
                    },
                    answer_2: {
                        label: 'Diabetes',
                    },
                    answer_3: {
                        label: 'Immunocompromised (HIV, cancer, etc.)',
                    },
                    answer_4: {
                        label: 'Wearing dentures',
                    },
                    answer_5: {
                        label: 'Use of inhaled corticosteroids (Pulmicort, Flovent, etc.)',
                    },
                    answer_6: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var oralThrushEnglish = {
    oralThrush: {
        v2: {
            ...v2$D.oralThrush,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Whitish coating in the mouth (tongue, palate, etc.) that detaches if scratched',
                    },
                    answer_2: {
                        label: 'Whitish coating in the mouth (tongue, palate, etc.) that does not detach if scratched',
                    },
                    answer_3: {
                        label: 'Difficulty swallowing',
                    },
                    answer_4: {
                        label: 'Pain when swallowing',
                    },
                    answer_5: {
                        label: 'Sensitivity and pain in the mouth',
                    },
                    answer_6: {
                        label: 'Blurred sensation in the mouth',
                    },
                    answer_7: {
                        label: 'Loss of taste',
                    },
                    answer_8: {
                        label: 'Irritation or cracks that may extend beyond the lips (chin, etc.)',
                    },
                    answer_9: {
                        label: 'Lesions around the eyes or in the genital area',
                    },
                    answer_10: {
                        label: 'Slight bleeding when plaque is scraped off',
                    },
                    answer_11: {
                        label: 'Swollen gums',
                    },
                    answer_12: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Have you had these symptoms in the past? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Past history of dry mouth',
                    },
                    answer_2: {
                        label: 'Diabetes',
                    },
                    answer_3: {
                        label: 'Immunocompromised (HIV, cancer, etc.)',
                    },
                    answer_4: {
                        label: 'Wearing dentures',
                    },
                    answer_5: {
                        label: 'Use of inhaled corticosteroids (Pulmicort, Flovent, etc.)',
                    },
                    answer_6: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day ',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$C = {
    oralThrush: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Dépôt blanchâtre dans la bouche (langue, palais, etc.)',
                    },
                    answer_2: {
                        label: 'Douleur à la déglutition',
                    },
                    answer_3: {
                        label: 'Sensibilité et sensation floue dans la bouche',
                    },
                    answer_4: {
                        label: 'Perte de goût',
                    },
                    answer_5: {
                        label: 'Présence d\'un faible saignement lorsque la plaque est grattée',
                    },
                    answer_6: {
                        label: 'Enflure des gencives',
                    },
                    answer_7: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Difficulté à avaler',
                    },
                    answer_2: {
                        label: 'Douleur dans la bouche',
                    },
                    answer_3: {
                        label: 'Irritation ou fissures pouvant s\'étendre au-delà des lèvres (menton, etc.)',
                    },
                    answer_4: {
                        label: 'Lésions autour des yeux ou dans le région génitale',
                    },
                    answer_5: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Avez-vous déjà eu ces symptômes dans le passé? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBox_6: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Antécédent de sécheresse buccale',
                    },
                    answer_2: {
                        label: 'Diabète',
                    },
                    answer_3: {
                        label: 'Immunodéprimé (VIH, cancer, etc.)',
                    },
                    answer_4: {
                        label: 'Port d\'une prothèse dentaire',
                    },
                    answer_5: {
                        label: 'Utilisation de corticostéroîdes en inhalation (Pulmicort, Flovent, etc.)',
                    },
                    answer_6: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var oralThrushFrench = {
    oralThrush: {
        v2: {
            ...v2$C.oralThrush,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Dépôt blanchâtre dans la bouche (langue, palais, etc.) qui se détache si gratté',
                    },
                    answer_2: {
                        label: 'Dépôt blanchâtre dans la bouche (langue, palais, etc.) qui ne se détache pas si gratté',
                    },
                    answer_3: {
                        label: 'Difficulté à avaler',
                    },
                    answer_4: {
                        label: 'Douleur à la déglutition',
                    },
                    answer_5: {
                        label: 'Sensibilité et douleur dans la bouche',
                    },
                    answer_6: {
                        label: 'Sensation floue dans la bouche',
                    },
                    answer_7: {
                        label: 'Perte de goût',
                    },
                    answer_8: {
                        label: 'Irritation ou fissures pouvant s\'étendre au-delà des lèvres (menton, etc.)',
                    },
                    answer_9: {
                        label: 'Lésions autour des yeux ou dans le région génitale',
                    },
                    answer_10: {
                        label: 'Présence d\'un faible saignement lorsque la plaque est grattée',
                    },
                    answer_11: {
                        label: 'Enflure des gencives',
                    },
                    answer_12: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Avez-vous déjà eu ces symptômes dans le passé ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Antécédent de sécheresse buccale',
                    },
                    answer_2: {
                        label: 'Diabète',
                    },
                    answer_3: {
                        label: 'Immunodéprimé (VIH, cancer, etc.)',
                    },
                    answer_4: {
                        label: 'Port d\'une prothèse dentaire',
                    },
                    answer_5: {
                        label: 'Utilisation de corticostéroïdes en inhalation (Pulmicort, Flovent, etc.)',
                    },
                    answer_6: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Tabagisme',
                description: 'Si oui, inscrire le nombre de cigarettes par jour ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$B = {
    medicationAdjustment: {
        section: {
            section_1: {
                title: 'YOUR DRUG PROFILE',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'What is the reason for consultation?',
                description: 'Select the main reason(s) for which you are consulting',
                answer: {
                    answer_1: {
                        label: 'Management of side effects',
                    },
                    answer_2: {
                        label: 'Reduced efficiency of medication',
                    },
                    answer_3: {
                        label: 'Attempt to stop treatment',
                    },
                    answer_4: {
                        label: 'Attempt to reduce treatment dose',
                    },
                    answer_5: {
                        label: 'Onset of new symptoms',
                    },
                    answer_6: {
                        label: 'My medication requires regular follow-up for dose adjustment (e.g. coumadin, synthroid, etc.)',
                    },
                    answer_7: {
                        label: 'Other, please specify',
                    },
                },
            },
            longAnswerTextArea_2: {
                title: 'Can you provide more details about your situation?',
                description: 'Please provide as much information as possible',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Which drug do you think needs adjustment?',
                description: 'Please write the name of the drug, the dose you are taking and the schedule (e.g. norvasc 5 mg every day)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_4: {
                title: 'Why do you take this treatment? ',
                description: 'Please provide as much information as possible',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_8: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var medicationAdjustmentEnglish = {
    medicationAdjustment: {
        v2: {
            ...v2$B.medicationAdjustment,
        },
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR DRUG PROFILE',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            yesNoButton_1: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_3: {
                title: 'Reason for consultation',
                description: 'Please select the main reason for your visit:',
                answer: {
                    answer_1: {
                        label: 'Management of side effects',
                    },
                    answer_2: {
                        label: 'Reduced efficiency of medication',
                    },
                    answer_3: {
                        label: 'Attempt to stop treatment',
                    },
                    answer_4: {
                        label: 'Attempt to reduce treatment dose',
                    },
                    answer_5: {
                        label: 'I have new symptoms',
                    },
                    answer_6: {
                        label: 'My medication requires regular follow-up for dose adjustment (e.g. coumadin, synthroid, etc.)',
                    },
                    answer_7: {
                        label: 'Other, please specify',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Which medication requires adjustment? ',
                description: 'Please write the name of the drug, the dose you are taking and the schedule (e.g. norvasc 5 mg every day).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_5: {
                title: 'Can you provide more details about your situation?',
                description: 'Please provide as much information as possible to help your pharmacist understand your situation.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_6: {
                title: 'Weight ',
                description: 'What is your weight? Please specify (kg or pounds). ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'Height',
                description: 'What is your height? Please specify (m or feet).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$A = {
    medicationAdjustment: {
        section: {
            section_1: {
                title: 'VOTRE PROFIL PHARMACEUTIQUE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Quelle est la raison de la consultation?',
                description: 'Sélectionnez la ou les raisons principales pour laquelle vous consultez',
                answer: {
                    answer_1: {
                        label: 'Gestion des effets secondaires',
                    },
                    answer_2: {
                        label: 'Diminution de l\'efficacité du médicament',
                    },
                    answer_3: {
                        label: 'Tentative d\'arrêter le traitement',
                    },
                    answer_4: {
                        label: 'Tentative de diminuer la dose du traitement',
                    },
                    answer_5: {
                        label: 'Apparition de nouveaux symptômes',
                    },
                    answer_6: {
                        label: 'Mon médicament nécessite un suivi régulier pour un ajustement de dose (ex: coumadin, synthroïd, etc)',
                    },
                    answer_7: {
                        label: 'Autres, veuillez préciser',
                    },
                },
            },
            longAnswerTextArea_2: {
                title: 'Pouvez-vous décrire davantage votre situation?',
                description: 'Fournissez le plus d\'informations possible',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Quel médicament, selon vous, nécessite un ajustement? ',
                description: 'Veuillez écrire le nom du médicament, la dose que vous prenez ainsi que la fréquence (ex: norvasc 5 mg chaque jour)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_4: {
                title: 'Pour quelles raisons prenez-vous ce médicament? ',
                description: 'Fournissez le plus d\'informations possible',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_8: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var medicationAdjustmentFrench = {
    medicationAdjustment: {
        v2: {
            ...v2$A.medicationAdjustment,
        },
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOTRE PROFIL PHARMACEUTIQUE',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            yesNoButton_1: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_3: {
                title: 'Raison de la consultation',
                description: 'Veuillez sélectionnez la ou les raisons principales pour laquelle vous consultez :',
                answer: {
                    answer_1: {
                        label: 'Gestion des effets secondaires',
                    },
                    answer_2: {
                        label: 'Diminution de l\'efficacité du médicament',
                    },
                    answer_3: {
                        label: 'Tentative d\'arrêter le traitement',
                    },
                    answer_4: {
                        label: 'Tentative de diminuer la dose du traitement',
                    },
                    answer_5: {
                        label: 'J\'ai des nouveaux symptômes',
                    },
                    answer_6: {
                        label: 'Mon médicament nécessite un suivi régulier pour un ajustement de dose (ex: coumadin, synthroïd, etc)',
                    },
                    answer_7: {
                        label: 'Autres, veuillez préciser',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Quel médicament nécessite un ajustement ? ',
                description: 'Veuillez écrire le nom du médicament, la dose que vous prenez ainsi que la fréquence (Ex: norvasc 5 mg chaque jour)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_5: {
                title: 'Pouvez-vous décrire davantage votre situation ?',
                description: 'Veuillez fournir le plus d\'informations pouvant aider votre pharmacien à bien comprendre votre situation.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_6: {
                title: 'Taille',
                description: 'Quelle est votre taille ? Spécifiez (m ou pieds).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'Poids',
                description: 'Quel est votre poids ? Spécifiez (kg ou lbs).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Tabagisme',
                description: 'Si oui, pouvez-vous inscrire le nombre de cigarettes par jour.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$z = {
    urinaryTractInfection: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Do you have any of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Pain or burning sensation on urination',
                    },
                    answer_2: {
                        label: 'Frequent need to urinate',
                    },
                    answer_3: {
                        label: 'Urine is foul-smelling and cloudy',
                    },
                    answer_4: {
                        label: 'Pain in the lower abdomen',
                    },
                    answer_5: {
                        label: 'Difficulty urinating',
                    },
                    answer_6: {
                        label: 'Other, specify here',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do you have any of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Vulvar or vaginal itchiness or changes in discharge',
                    },
                    answer_2: {
                        label: 'Lesions or vesicles (small white bunches of blisters)',
                    },
                    answer_3: {
                        label: 'Presence of blood in the urine',
                    },
                    answer_4: {
                        label: 'None of these symptoms',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Your symptoms could be caused by a sexually transmitted infection (STI). In the STI and sexual health tab on the ClicSanté home page, you can book an appointment for STI screening.',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Do you have one of these conditions?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Anomaly of the kidneys or urinary tract (bladder, urethra, etc.)',
                    },
                    answer_2: {
                        label: 'Urinary tract infection in the last 30 days',
                    },
                    answer_3: {
                        label: 'Immunosuppressed',
                    },
                    answer_4: {
                        label: 'Kidney or urinary tract anomaly (bladder, urethra, etc.)',
                    },
                    answer_5: {
                        label: 'None of these conditions',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'For how long have you had these symptoms? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatment. To ensure your safety, please share this information with your health-care professional. ',
                    },
                },
            },
            simpleChoiceRadio_8: {
                title: 'In the last three months, what has been your situation?',
                description: 'Select the one that applies',
                answer: {
                    answer_1: {
                        label: 'Stable partner',
                    },
                    answer_2: {
                        label: 'A new partner',
                    },
                    answer_3: {
                        label: 'Multiple partners',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Having a new or multiple partner can also mean having a sexually transmitted infection. In the STI and Sexual Health tab on the ClicSanté home page, you can book a STI screening appointment.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var urinaryTractInfectionEnglish = {
    urinaryTractInfection: {
        v2: {
            ...v2$z.urinaryTractInfection,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'General',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Pain or burning sensation on urination',
                    },
                    answer_2: {
                        label: 'Frequent need to urinate',
                    },
                    answer_3: {
                        label: 'Urine is foul-smelling and cloudy',
                    },
                    answer_4: {
                        label: 'Pain in the lower abdomen',
                    },
                    answer_5: {
                        label: 'Difficulty urinating',
                    },
                    answer_6: {
                        label: 'Other, specify here:',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: '',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Vulvar or vaginal itchiness or changes in discharge',
                    },
                    answer_2: {
                        label: 'Lesions or vesicles (small white bunches of blisters)',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Your symptoms could be caused by a sexually transmitted infection (STI).  In the STI and sexual health tab on the ClicSanté home page, you can book an appointment for STI screening.',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Have you been treated for this problem :',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'In the last 6 months',
                    },
                    answer_2: {
                        label: 'More than twice in the last 12 months',
                    },
                    answer_3: {
                        label: 'None of these situations',
                    },
                },
            },
            multipleChoiceCheckBox_4: {
                title: 'Do you have one of these conditions?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Menopause',
                    },
                    answer_2: {
                        label: 'Anomaly of the kidneys or urinary tract (bladder, urethra, etc.).',
                    },
                    answer_3: {
                        label: 'Urinary tract infection in the last 30 days',
                    },
                    answer_4: {
                        label: 'Immunosuppressed',
                    },
                    answer_5: {
                        label: 'None of these conditions',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Based on your answers, we recommend that you consult a physician, a nurse practitioner or an emergency room within the next few hours.',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBox_7: {
                title: 'Sexual activity',
                description: 'Please indicate your situation in the last 3 months. ',
                answer: {
                    answer_1: {
                        label: 'Stable partner',
                    },
                    answer_2: {
                        label: 'A new partner',
                    },
                    answer_3: {
                        label: 'Multiple partners',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Having a new or multiple partner can also mean having a sexually transmitted infection. In the STI and Sexual Health tab on the ClicSanté home page, you can book a STI screening appointment.',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$y = {
    urinaryTractInfection: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Douleur ou brûlure à la miction',
                    },
                    answer_2: {
                        label: 'Envie fréquente d\'uriner',
                    },
                    answer_3: {
                        label: 'Urine est malodorante et trouble',
                    },
                    answer_4: {
                        label: 'Douleur dans le bas du ventre',
                    },
                    answer_5: {
                        label: 'Difficulté à uriner',
                    },
                    answer_6: {
                        label: 'Autre, précisez ici',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Démangeaisons ou écoulements vulvaires ou vaginaux',
                    },
                    answer_2: {
                        label: 'Lésions ou des vésicules (petits boutons blancs en grappe)',
                    },
                    answer_3: {
                        label: 'Présence de sang dans les urines',
                    },
                    answer_4: {
                        label: 'Aucun de ces symptômes',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Vos symptômes peuvent être causés par un infection transmissible sexuellement et par le sang (ITSS). Dans l\'onglet ITSS et Santé sexuelle sur la page d\'accueil de ClicSanté, il est possible de prendre un rendez-vous Dépistage ITSS avec symptômes.',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Avez-vous l\'une de ces conditions? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Anomalie au niveau des reins ou de l\'appareil urinaire (vessie, urètre, etc.)',
                    },
                    answer_2: {
                        label: 'Infection urinaire au cours des 30 derniers jours',
                    },
                    answer_3: {
                        label: 'Immunodéprimée',
                    },
                    answer_4: {
                        label: 'Anomalie au niveau des reins ou de l\'appareil urinaire (vessie, urètre, etc.)',
                    },
                    answer_5: {
                        label: 'Aucune de ces conditions',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Depuis combien de temps avez-vous ces symptômes? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_8: {
                title: 'Dans les trois derniers mois, quelle a été votre situation?',
                description: 'Sélectionnez celle qui s\'applique',
                answer: {
                    answer_1: {
                        label: 'Partenaire stable',
                    },
                    answer_2: {
                        label: 'Un nouveau partenaire',
                    },
                    answer_3: {
                        label: 'Partenaires multiples',
                    },
                    answer_4: {
                        label: 'Aucun de ces choix',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avoir un nouveau ou de multiples de partenaire peut aussi signifier infection transmissible sexuellement. Dans l\'onglet ITSS et Santé sexuelle sur la page d\'accueil de ClicSanté, il est possible de prendre un rendez-vous Dépistage ITSS.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var urinaryTractInfectionFrench = {
    urinaryTractInfection: {
        v2: {
            ...v2$y.urinaryTractInfection,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Généraux',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Douleur ou brûlure à la miction',
                    },
                    answer_2: {
                        label: 'Envie fréquent d\'uriner',
                    },
                    answer_3: {
                        label: 'Urine est malodorante et trouble',
                    },
                    answer_4: {
                        label: 'Douleur dans le bas du ventre.',
                    },
                    answer_5: {
                        label: 'Difficulté à uriner',
                    },
                    answer_6: {
                        label: 'Autre, précisez ici :',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: '',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Démangeaisons ou écoulements vulvaires ou vaginaux',
                    },
                    answer_2: {
                        label: 'Lésions ou des vésicules (petits boutons blancs en grappe)',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Vos symptômes peuvent être causés par une infection transmissible sexuellement et par le sang (ITSS). Dans l\'onglet ITSS et Santé sexuelle sur la page d\'accueil de ClicSanté, il est possible de prendre un rendez-vous Dépistage ITSS avec symptôme.',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Avez-vous été traité pour ce problème :  ',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Dans les 6 derniers mois',
                    },
                    answer_2: {
                        label: '2 fois ou plus dans les 12 derniers mois',
                    },
                    answer_3: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            multipleChoiceCheckBox_4: {
                title: 'Avez-vous l\'une de ces conditions ? ',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Ménopause',
                    },
                    answer_2: {
                        label: 'Anomalie au niveau des reins ou de l\'appareil urinaire (vessie, urètre,etc).',
                    },
                    answer_3: {
                        label: 'Infection urinaire au cours des 30 derniers jours',
                    },
                    answer_4: {
                        label: 'Immunodéprimée',
                    },
                    answer_5: {
                        label: 'Aucune de ces conditions',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Selon vos réponses nous vous invitons à consulter dans les prochaines heures un médecin, une Infirmière praticienne spécialisée ou vous dirigez vers l\'urgence.',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBox_7: {
                title: 'Activité sexuelle',
                description: 'Veuillez indiquer votre situation dans les 3 derniers mois. ',
                answer: {
                    answer_1: {
                        label: 'Partenaire stable',
                    },
                    answer_2: {
                        label: 'Un nouveau partenaire',
                    },
                    answer_3: {
                        label: 'Partenaires multiples',
                    },
                    answer_4: {
                        label: 'Aucun de ces choix',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Avoir un nouveau ou de multiples partenaires peut aussi signifier infection transmissible sexuellement. Dans l\'onglet ITSS et Santé sexuelle sur la page d\'accueil de ClicSanté, il est possible de prendre un rendez-vous Dépistage ITSS.',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$x = {
    medicationReview: {
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR DRUG PROFILE',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Have you ever had your medication reviewed? ',
                description: 'If so, please specify the approximate date',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Since the review, have you had any new symptoms?',
                description: 'If so, please indicate which ones',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Since the review, have you had any new health conditions?',
                description: 'If so, please specify. (e.g. hypothyroidism, diabetes, nail fungus, etc.)',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'When was your last blood test? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Do you have any recent results of: <br/>- Blood pressure <br/>- Capillary blood glucose <br/>- Other results that are relevant to the monitoring of your treatment',
                description: 'If so, please provide this data',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you planning to become pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you have a family doctor?',
                description: 'If so, please provide their full name',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have any concerns about the medication you are taking? ',
                description: 'If so, please provide as much information as possible to help your pharmacist understand your concerns',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Are you concerned about your health? ',
                description: 'If so, please provide as much information as possible to help your pharmacist understand your concerns',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you experience side effects from your medication?',
                description: 'If so, please list these side effects',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_13: {
                title: 'Do you sometimes have trouble swallowing your pills? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_14: {
                title: 'Do you sometimes forget or confuse your medications? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            shortAnswerText_15: {
                title: 'Do you have the impression that your medication is helping your condition? ',
                description: 'Detail your impressions',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_16: {
                title: 'What are your expectations or needs in regard to this consultation? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'I need information about the medications I\'m taking',
                    },
                    answer_2: {
                        label: 'I would like to better understand each medication',
                    },
                    answer_3: {
                        label: 'I would like to reduce certain doses of medication',
                    },
                    answer_4: {
                        label: 'I\'d like to discuss the side effects of medications',
                    },
                    answer_5: {
                        label: 'I\'d like help to avoid forgetting or mixing up my medication',
                    },
                    answer_6: {
                        label: 'Other needs or expectations, please specify',
                    },
                },
            },
            shortAnswerText_17: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_18: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_19: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_20: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_21: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_22: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_23: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var medicationReviewEnglish = {
    medicationReview: {
        v2: {
            ...v2$x.medicationReview,
        },
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR DRUG PROFILE',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Have you ever had your medication reviewed? ',
                description: 'If so, please specify the approximate date.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Have you had any new symptoms since?',
                description: 'If yes, please indicate which ones.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Do you have any new health issues since ?',
                description: 'If yes, please specify (e.g. hypothyroidism, diabetes, nail fungus, etc.)',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Do you have a family doctor?',
                description: 'If yes, please provide his or her full name.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Do you have any concerns about the medication you are taking? ',
                description: 'If so, please provide as much information as possible to help your pharmacist understand your concerns.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Are you concerned about your health? ',
                description: 'If so, please provide as much information as possible to help your pharmacist understand your concerns.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you experience side effects from your medication?',
                description: 'If yes, please list these side effects.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_10: {
                title: 'Do you sometimes have trouble swallowing your pills? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Do you sometimes forget or confuse your medications? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_12: {
                title: 'What are your expectations or needs in regard to this consultation? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'I need information about the medications I\'m taking',
                    },
                    answer_2: {
                        label: 'I would like to better understand each medication',
                    },
                    answer_3: {
                        label: 'I would like to reduce certain doses of medication',
                    },
                    answer_4: {
                        label: 'I\'d like to discuss the side effects of medications',
                    },
                    answer_5: {
                        label: 'I\'d like help to avoid forgetting or mixing up my medication',
                    },
                    answer_6: {
                        label: 'Other needs or expectations, please specify',
                    },
                },
            },
            shortAnswerText_13: {
                title: 'Height',
                description: 'What is your height? Please specify (m or feet).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_14: {
                title: 'Weight ',
                description: 'What is your weight? Please specify (kg or pounds). ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_17: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_18: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$w = {
    medicationReview: {
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOTRE PROFIL PHARMACEUTIQUE',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Avez-vous déjà fait une révision de votre médication? ',
                description: 'Si oui, spécifiez la date approximative de celle-ci',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Depuis la révision, avez-vous de nouveaux symptômes? ',
                description: 'Si oui, veuillez inscrire lesquels',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Depuis la révision, avez-vous une ou des nouvelles conditions de santé?',
                description: 'Si oui, inscrivez lesquels. (Ex: hypothyroïdie, diabète, mycose des ongles, etc.)',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'À quand remonte votre dernière prise de sang (bilan sanguin)? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Avez-vous des résultats récents de: <br/>- Pression artérielle <br/>- Glycémie capillaire <br/>- Autres résultats pertinents pour le suivi de votre traitement',
                description: 'Si oui, fournissez ces données',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Avez-vous un projet de grossesse?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Avez-vous un médecin de famille?',
                description: 'Si oui, inscrivez son nom complet',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Avez-vous une préoccupation en lien avec les médicaments que vous prenez? ',
                description: 'Si oui, veuillez fournir le plus d\'informations pouvant aider à bien comprendre vos préoccupations',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Avez-vous une préoccupation en lien avec votre santé? ',
                description: 'Si oui, veuillez fournir le plus d\'informations pouvant aider à bien comprendre vos préoccupations',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Avez-vous l\'impression d\'avoir des effets secondaires de votre médication?',
                description: 'Si oui, inscrivez ces effets secondaires',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_13: {
                title: 'Avez-vous parfois de la difficulté à avaler vos médicaments? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_14: {
                title: 'Vous arrive-t-il d\'oublier ou de mélanger vos médications? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            shortAnswerText_15: {
                title: 'Avez-vous l\'impression que vos médicaments traitent votre condition? ',
                description: 'Détaillez vos impressions',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_16: {
                title: 'Quelles sont vos attentes ou vos besoins en lien avec cette consultation? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'J\'ai besoin d\'informations sur les médicaments que je prends',
                    },
                    answer_2: {
                        label: 'J\'aimerais mieux comprendre chaque médicament',
                    },
                    answer_3: {
                        label: 'J\'aimerais diminuer certaines doses de médicaments',
                    },
                    answer_4: {
                        label: 'J\'aimerais discuter des effets secondaires des médicaments',
                    },
                    answer_5: {
                        label: 'J\'aimerais avoir de l\'aide pour éviter un oubli ou un mélange de mes médicaments',
                    },
                    answer_6: {
                        label: 'Autres attentes ou besoins, veuillez spécifier',
                    },
                },
            },
            shortAnswerText_17: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_18: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_19: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_20: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_21: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_22: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_23: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var medicationReviewFrench = {
    medicationReview: {
        v2: {
            ...v2$w.medicationReview,
        },
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOTRE PROFIL PHARMACEUTIQUE',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Avez-vous déjà fait une révision de votre médication ? ',
                description: 'Si oui, veuillez spécifier la date approximative de celle-ci.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Depuis, avez-vous de nouveaux symptômes ? ',
                description: 'Si oui, veuillez inscrire lesquels.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Depuis, avez-vous une ou des nouvelles conditions de santé ?',
                description: 'Si oui, veuillez inscrire lesquels. (Ex: hypothyroïdie, diabète, mycose des ongles, etc.)',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Avez-vous un médecin de famille ?',
                description: 'Si oui, veuillez inscrire son nom complet.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Avez-vous une préoccupation en lien avec les médicaments que vous prenez ? ',
                description: 'Si oui, veuillez fournir le plus d\'informations pouvant aider votre pharmacien à bien comprendre vos préoccupations.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Avez-vous une préoccupation en lien avec votre santé ? ',
                description: 'Si oui, veuillez fournir le plus d\'informations pouvant aider votre pharmacien à bien comprendre vos préoccupations.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Avez-vous l\'impression d\'avoir des effets secondaires de votre médication ?',
                description: 'Si oui, veuillez inscrire ces effets secondaires.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_10: {
                title: 'Avez-vous parfois de la difficulté à avaler vos médicaments ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Vous arrive-t-il d\'oublier ou de mélanger vos médications ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_12: {
                title: 'Quelles sont vos attentes ou vos besoins en lien avec cette consultation ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'J\'ai besoin d\'informations sur les médicaments que je prends',
                    },
                    answer_2: {
                        label: 'J\'aimerais mieux comprendre chaque médicament',
                    },
                    answer_3: {
                        label: 'J\'aimerais diminuer certaines doses de médicaments',
                    },
                    answer_4: {
                        label: 'J\'aimerais discuter des effets secondaires des médicaments',
                    },
                    answer_5: {
                        label: 'J\'aimerais avoir de l\'aide pour éviter que j\'oublie ou mélange mes médicaments',
                    },
                    answer_6: {
                        label: 'Autres attentes ou besoins, veuillez spécifier',
                    },
                },
            },
            shortAnswerText_13: {
                title: 'Taille',
                description: 'Quelle est votre taille ? Spécifiez (m ou pieds).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_14: {
                title: 'Poids',
                description: 'Quel est votre poids ? Spécifiez (kg ou lbs).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Tabagisme',
                description: 'Si oui, pouvez-vous inscrire le nombre de cigarettes par jour.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_17: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_18: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$v = {
    prescriptionRenewalAndExtension: {
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR DRUG PROFILE',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'Which medication needs to be  extended? ',
                description: 'Write the name of the medicine, the dose you are taking and the frequency (e.g. norvasc 5 mg every day)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Has your prescription ever been extended?',
                description: 'If so, specify the number of times and the duration of the renewal. For example, 1 time for a duration of 4 months',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How many or for how long can your prescription be extended? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            simpleChoiceRadio_4: {
                title: 'Are you planning to become pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Not applicable',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Do you have a family doctor?',
                description: 'If so, please provide their full name',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you have any concerns about the medication you are taking? ',
                description: 'Please provide as much information as possible',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Are you concerned about your health? ',
                description: 'Please provide as much information as possible',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you experience side effects from your medication?',
                description: 'If so, please list these side effects',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Do you sometimes forget or confuse your medications? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'Do you feel like your medication helps your condition? ',
                description: 'Detail your impressions',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_13: {
                title: 'When was your last blood test? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Do you have any recent results of: <br/>- Blood pressure <br/>- Capillary blood glucose <br/>- Other results that are relevant to the monitoring of your treatment',
                description: 'If so, please provide this data',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_15: {
                title: 'What are your expectations or needs in regard to this consultation? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'I need to extend my prescription',
                    },
                    answer_2: {
                        label: 'I need to renew my prescription',
                    },
                    answer_3: {
                        label: 'Other needs or expectations, please specify',
                    },
                },
            },
            shortAnswerText_16: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_17: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_18: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_19: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_20: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_21: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_22: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var prescriptionRenewalAndExtensionEnglish = {
    prescriptionRenewalAndExtension: {
        v2: {
            ...v2$v.prescriptionRenewalAndExtension,
        },
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR DRUG PROFILE',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Have you ever renewed or extended this prescription? ',
                description: 'If so, please specify the number of times.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Which medication needs to be renewed or extended? ',
                description: 'Please write the name of the drug, the dose you are taking and the schedule (e.g. norvasc 5 mg every day).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Do you have a family doctor?',
                description: 'If yes, please provide his or her full name.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Do you have any concerns about the medication you are taking? ',
                description: 'If so, please provide as much information as possible to help your pharmacist understand your concerns.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Are you concerned about your health? ',
                description: 'If so, please provide as much information as possible to help your pharmacist understand your concerns.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you experience side effects from your medication?',
                description: 'If yes, please list these side effects',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Do you sometimes forget or confuse your medications? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_10: {
                title: 'What are your expectations or needs in regard to this consultation? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'I need to extend my prescription',
                    },
                    answer_2: {
                        label: 'I need to renew my prescription',
                    },
                    answer_3: {
                        label: 'Other needs or expectations, please specify',
                    },
                },
            },
            shortAnswerText_11: {
                title: 'Weight ',
                description: 'What is your weight? Please specify (kg or pounds). ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'Height',
                description: 'What is your height? Please specify (m or feet).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$u = {
    prescriptionRenewalAndExtension: {
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOTRE PROFIL PHARMACEUTIQUE',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'Quel médicament nécessite une prolongation? ',
                description: 'Écrivez le nom du médicament, la dose que vous prenez ainsi que la fréquence (ex: norvasc 5 mg chaque jour)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Est-ce que votre prescription a déjà été prolongée? ',
                description: 'Si oui, spécifiez le nombre de fois et la durée du renouvellement. Par exemple, 1 fois pour une durée de 4 mois',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Quel est le nombre ou la durée de prolongation initiale de votre ordonnance? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            simpleChoiceRadio_4: {
                title: 'Avez-vous un projet de grossesse? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne s\'applique pas',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Êtes-vous suivi par un ou une médecin de famille?',
                description: 'Si oui, inscrivez son nom complet',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Avez-vous une préoccupation en lien avec les médicaments que vous prenez? ',
                description: 'Fournissez le plus d\'informations possible',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Avez-vous une préoccupation en lien avec votre santé? ',
                description: 'Fournissez le plus d\'informations possible',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Avez-vous l\'impression d\'avoir des effets secondaires de votre médication?',
                description: 'Si oui, inscrivez ces effets secondaires',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Vous arrive-t-il d\'oublier ou de mélanger vos médications? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'Avez-vous l\'impression que vos médicaments traitent votre condition? ',
                description: 'Détaillez vos impressions',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_13: {
                title: 'À quand remonte votre dernière prise de sang (bilan sanguin)? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Avez-vous des résultats récent de: <br/>- Pression artérielle <br/>- Glycémie capillaire <br/>- Autres résultats pertinents pour le suivi de votre traitement',
                description: 'Si oui, fournissez ces données',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_15: {
                title: 'Quelles sont vos attentes ou vos besoins en lien avec cette consultation? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'J\'ai besoin de prolonger mon ordonnance',
                    },
                    answer_2: {
                        label: 'J\'ai besoin de renouveler mon ordonnance',
                    },
                    answer_3: {
                        label: 'Autres attentes ou besoins, veuillez spécifier',
                    },
                },
            },
            shortAnswerText_16: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_17: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_18: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_19: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_20: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_21: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_22: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var prescriptionRenewalAndExtensionFrench = {
    prescriptionRenewalAndExtension: {
        v2: {
            ...v2$u.prescriptionRenewalAndExtension,
        },
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOTRE PROFIL PHARMACEUTIQUE',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Avez-vous déjà renouvellé ou prolongé cette ordonnance ? ',
                description: 'Si oui, veuillez spécifier le nombre de fois.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Quel médicament nécessite un renouvellement ou une prolongation ? ',
                description: 'Veuillez écrire le nom du médicament, la dose que vous prenez ainsi que la fréquence (ex. : norvasc 5 mg chaque jour)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Avez-vous un médecin de famille ?',
                description: 'Si oui, veuillez inscrire son nom complet.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Avez-vous une préoccupation en lien avec les médicaments que vous prenez ? ',
                description: 'Si oui, veuillez fournir le plus d\'informations pouvant aider votre pharmacien à bien comprendre vos préoccupations.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Avez-vous une préoccupation en lien avec votre santé ? ',
                description: 'Si oui, veuillez fournir le plus d\'informations pouvant aider votre pharmacien à bien comprendre vos préoccupations.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Avez-vous l\'impression d\'avoir des effets secondaires de votre médication ?',
                description: 'Si oui, veuillez inscrire ces effets secondaires',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Vous arrive-t-il d\'oublier ou de mélanger vos médications ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_10: {
                title: 'Quelles sont vos attentes ou vos besoins en lien avec cette consultation ? ',
                description: 'Sélectionnez tout ce qui s’applique.',
                answer: {
                    answer_1: {
                        label: 'J\'ai besoin de prolonger mon ordonnance',
                    },
                    answer_2: {
                        label: 'J\'ai besoin de renouveller mon ordonnance',
                    },
                    answer_3: {
                        label: 'Autres attentes ou besoins, veuillez spécifier',
                    },
                },
            },
            shortAnswerText_11: {
                title: 'Taille',
                description: 'Quelle est votre taille ? Spécifiez (m ou pieds).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'Poids',
                description: 'Quel est votre poids ? Spécifiez (kg ou lbs).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Tabagisme',
                description: 'Si oui,  inscrire le nombre de cigarettes par jour ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$t = {
    nauseaAndVomitingRelatedToPregnancy: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'What symptoms do you have? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Gastroesophageal reflux',
                    },
                    answer_2: {
                        label: 'Vomiting at specific times of the day',
                    },
                    answer_3: {
                        label: 'Morning sickness or nausea at specific times of the day',
                    },
                    answer_4: {
                        label: 'Epigastric pain (heartburn)',
                    },
                    answer_5: {
                        label: 'None of these symptoms',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'For how long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How many weeks pregnant are you?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'At what time of the day do you experience nausea and/or vomiting? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Do you already have a prescribed treatment?',
                description: 'If so, please indicate which one and specify whether it is effective or not',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, indicate the methods or products used and specify whether they are effective or not',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Are you currently taking multivitamins?',
                description: 'If so, which ones?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Medications and natural products',
                description: 'Are you taking any medications or natural products on a regular basis? If so, please indicate which ones and for what reasons you are taking them',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            shortAnswerText_11: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_13: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var nauseaAndVomitingPregnancyEnglish = {
    nauseaAndVomitingRelatedToPregnancy: {
        v2: {
            ...v2$t.nauseaAndVomitingRelatedToPregnancy,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Severe and persistent nausea and vomiting accompanied by weight loss and dehydration.',
                    },
                    answer_2: {
                        label: 'Severe dehydration: dark urine, dizziness when changing position, dry mouth, fruity breath, etc.).',
                    },
                    answer_3: {
                        label: 'Fever over 38.5°C',
                    },
                    answer_4: {
                        label: 'Fever between 38 and 38.4°C persisting for more than 24 hours',
                    },
                    answer_5: {
                        label: 'Severe migraine',
                    },
                    answer_6: {
                        label: 'Vaginal bleeding',
                    },
                    answer_7: {
                        label: 'Presence of blood in vomit (bright red or coffee ground black)',
                    },
                    answer_8: {
                        label: 'Gastroesophageal reflux disease',
                    },
                    answer_9: {
                        label: 'Epigastric pain (heartburn)',
                    },
                    answer_10: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Weight ',
                description: 'What is your weight? Please specify (kg or pounds). ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Height',
                description: 'What is your height? Please specify (m or feet).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day ',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Know health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$s = {
    nauseaAndVomitingRelatedToPregnancy: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Quels symptômes avez-vous?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Des reflux gastro-oesophagien',
                    },
                    answer_2: {
                        label: 'Des vomissements à des moments précis dans la journée',
                    },
                    answer_3: {
                        label: 'Des nausées matinales ou à des moments précis dans la journée',
                    },
                    answer_4: {
                        label: 'Une douleur épigastrique (brûlure d\'estomac)',
                    },
                    answer_5: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'À combien de semaines de grossesse êtes-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'À quel moment de la journée avez-vous des nausées et/ou des vomissements? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Avez-vous déjà un traitement de prescrit?',
                description: 'Si oui, précisez lequel et s\'il est efficace ou non',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, indiquez les méthodes ou les produits utilisés et spécifiez s\'ils sont efficaces ou non',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Prenez-vous actuellement des multivitamines?',
                description: 'Si oui, lesquelles?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière? Si oui, lesquelles et expliquez pourquoi vous prenez ce traitement',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            shortAnswerText_11: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_13: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var nauseaAndVomitingPregnancyFrench = {
    nauseaAndVomitingRelatedToPregnancy: {
        v2: {
            ...v2$s.nauseaAndVomitingRelatedToPregnancy,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Présentez-vous certains de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui s’applique.',
                answer: {
                    answer_1: {
                        label: 'Nausées et vomissements sévères et persistants accompagnés d\'une perte de poids et de déshydratation',
                    },
                    answer_2: {
                        label: 'Déshydratation sévère : urine foncée, étourdissement au changement de position, bouche sèche, haleine fruitée, etc.)',
                    },
                    answer_3: {
                        label: 'Fièvre de plus de 38,5 °C',
                    },
                    answer_4: {
                        label: 'Fièvre entre 38 et 38,4 °C qui persiste depuis plus de 24h',
                    },
                    answer_5: {
                        label: 'Migraine sévère',
                    },
                    answer_6: {
                        label: 'Saignements vaginaux',
                    },
                    answer_7: {
                        label: 'Des reflux gastro-oesophagien',
                    },
                    answer_8: {
                        label: 'Une douleur épigastrique (brûlure d\'estomac)',
                    },
                    answer_9: {
                        label: 'Présence de sang dans le vomi (rouge vif ou noir)',
                    },
                    answer_10: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Taille',
                description: 'Quelle est votre taille ? Spécifiez (m ou pieds).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Poids',
                description: 'Quel est votre poids ? Spécifiez (kg ou lbs).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Tabagisme',
                description: 'Si oui, inscrire le nombre de cigarettes par jour ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$r = {
    vitaminSupplementDuringPregnancy: {
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Are you pregnant or are you planning to become pregnant in the next three months? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Is this your first pregnancy?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you ever taken vitamin supplements? ',
                description: 'If so, please indicate which ones and how you reacted to them',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Do you take any medications or natural products on a regular basis?',
                description: 'If so, please indicate which ones and for what reasons you are taking them',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify here',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Do any of these situations apply to you? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Neural tube defect in a previous pregnancy in both partners',
                    },
                    answer_2: {
                        label: 'History of neural tube defect',
                    },
                    answer_3: {
                        label: 'Male partner with a personal history of neural tube defect',
                    },
                    answer_4: {
                        label: 'None of these situations',
                    },
                },
            },
            simpleChoiceRadio_6: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_8: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var vitaminSupplementDuringPregnancyEnglish = {
    vitaminSupplementDuringPregnancy: {
        v2: {
            ...v2$r.vitaminSupplementDuringPregnancy,
        },
        section: {
            section_1: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Are you pregnant? ',
                description: 'If yes, please indicate the number of weeks of pregnancy.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Are you breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that aply',
                answer: {
                    answer_1: {
                        label: 'Personal history of neural tube defect',
                    },
                    answer_2: {
                        label: 'History of pregnancy affected by neural tube defect',
                    },
                    answer_3: {
                        label: 'Male partner with personal history of neural tube defect',
                    },
                    answer_4: {
                        label: 'Male partner with history of fetus affected by neural tube defect',
                    },
                    answer_5: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Height',
                description: 'What is your height? Please specify (m or feet).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'Weight ',
                description: 'What is your weight? Please specify (kg or pounds). ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day ',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Alcoholic beverages',
                description: 'Do you drink alcoholic beverages? If so, please specify quantity.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$q = {
    vitaminSupplementDuringPregnancy: {
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Êtes-vous enceinte ou planifiez-vous une grossesse dans les trois prochains mois? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Est-ce que c\'est votre première grossesse?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous déjà pris des suppléments vitaminiques? ',
                description: 'Si oui, veuillez indiquer lesquels et comment vous avez réagit ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière? ',
                description: 'Si oui, indiquez lesquelles et la raison pour laquelle vous prenez ce traitement',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez ici',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Est-ce que l\'une de ces situations s\'applique à votre situation? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Anomalie du tube neural lors d\'une grossesse précédente chez les deux partenaires',
                    },
                    answer_2: {
                        label: 'Antécédent d\'anomalie du tube neural',
                    },
                    answer_3: {
                        label: 'Partenaire masculin présentant des antécédents personnels d\'anomalie du tube neural',
                    },
                    answer_4: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            simpleChoiceRadio_6: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_8: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var vitaminSupplementDuringPregnancyFrench = {
    vitaminSupplementDuringPregnancy: {
        v2: {
            ...v2$q.vitaminSupplementDuringPregnancy,
        },
        section: {
            section_1: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            yesNoButtonWithText_1: {
                title: 'Êtes-vous enceinte ? ',
                description: 'Si oui, veuillez indiquer le nombre de semaine de grossesse.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Est-ce que l\'une de ces situations s\'appliquent à votre situation ? ',
                description: 'Sélectionnez tout ce qui s’applique.',
                answer: {
                    answer_1: {
                        label: 'Antécédents personnels d\'anomalies du tube neural',
                    },
                    answer_2: {
                        label: 'Antécédents de grossesse affectée par une anomalie du tube neural',
                    },
                    answer_3: {
                        label: 'Partenaire masculin présentant des antécédents personnels d\'anomalies du tube neural',
                    },
                    answer_4: {
                        label: 'Partenaire masculin présentant des antécédents de foetus affecté par une anomalie du tube neural',
                    },
                    answer_5: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            shortAnswerText_4: {
                title: 'Taille',
                description: 'Quelle est votre taille ? Spécifiez (m ou pieds).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'Poids',
                description: 'Quel est votre poids ? Spécifiez (kg ou lbs).',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Tabagisme',
                description: 'Si oui, inscrire le nombre de cigarettes par jour.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Boissons alcoolisées',
                description: 'Consommez-vous des boissons alcoolisées ? Si oui, spécifiez la quantité.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$p = {
    diaperRash: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'What are the symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Plaques and redness in areas in contact with the diaper',
                    },
                    answer_2: {
                        label: 'Lesions that may become shiny and erosive',
                    },
                    answer_3: {
                        label: 'Recent episode of diarrhea',
                    },
                    answer_4: {
                        label: 'Recent antibiotic treatment',
                    },
                    answer_5: {
                        label: 'None of these symptoms',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_4: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var diaperRashEnglish = {
    diaperRash: {
        v2: {
            ...v2$p.diaperRash,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have one or more of these conditions?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Plaques and redness in areas in contact with the diaper',
                    },
                    answer_2: {
                        label: 'Lesions that may become shiny and erosive',
                    },
                    answer_3: {
                        label: 'Recent episode of diarrhea',
                    },
                    answer_4: {
                        label: 'Recent antibiotic treatment',
                    },
                    answer_5: {
                        label: 'None of these symptoms',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'How long have you noticed the redness? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_3: {
                title: 'Have you already tried some products?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Zinc oxide barrier cream after each diaper change',
                    },
                    answer_2: {
                        label: 'Other products, specify',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$o = {
    diaperRash: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Quels sont les symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Plaques et rougeurs dans les zones en contact avec la couche',
                    },
                    answer_2: {
                        label: 'Lésions qui peuvent devenir luisantes et érosives',
                    },
                    answer_3: {
                        label: 'Épisode de diarrhée récent',
                    },
                    answer_4: {
                        label: 'Prise récente d\'antibiotique',
                    },
                    answer_5: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de temps ces symptômes sont présents?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous essayé de soulager les symptômes?',
                description: 'Si oui, précisez les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_4: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var diaperRashFrench = {
    diaperRash: {
        v2: {
            ...v2$o.diaperRash,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous une ou plusieurs de ces conditions ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Plaques et rougeurs dans les zones en contact avec la couche',
                    },
                    answer_2: {
                        label: 'Lésions qui peuvent devenir luisantes et érosives',
                    },
                    answer_3: {
                        label: 'Épisode de diarrhée récent',
                    },
                    answer_4: {
                        label: 'Prise récente d\'antibiotique',
                    },
                    answer_5: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de jours avez-vous remarqué la rougeur ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_3: {
                title: 'Avez-vous déjà essayé certains produits ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Crème barrière à base d\'oxyde de zinc après chaque changement de couche',
                    },
                    answer_2: {
                        label: 'Autres produits, spécifiez',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels de façon régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$n = {
    allergicRhinitis: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Nasal congestion',
                    },
                    answer_2: {
                        label: 'Exposure to a chemical agent',
                    },
                    answer_3: {
                        label: 'Exposure to a pollutant',
                    },
                    answer_4: {
                        label: 'Clear nasal discharge',
                    },
                    answer_5: {
                        label: 'Headache',
                    },
                    answer_6: {
                        label: 'Intense and sometimes paroxysmal sneezing',
                    },
                    answer_7: {
                        label: 'Itching in the nose and throat',
                    },
                    answer_8: {
                        label: 'None of these symptoms',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Colored nasal discharge',
                    },
                    answer_2: {
                        label: 'Pain in the face or teeth',
                    },
                    answer_3: {
                        label: 'Fever',
                    },
                    answer_4: {
                        label: 'Recurrent nosebleeds',
                    },
                    answer_5: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            simpleChoiceRadio_8: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var allergicRhinitisEnglish = {
    allergicRhinitis: {
        v2: {
            ...v2$n.allergicRhinitis,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Nasal congestion',
                    },
                    answer_2: {
                        label: 'Clear nasal discharge',
                    },
                    answer_3: {
                        label: 'Colored nasal discharge',
                    },
                    answer_4: {
                        label: 'Pain in the face or teeth',
                    },
                    answer_5: {
                        label: 'Fever',
                    },
                    answer_6: {
                        label: 'Headache',
                    },
                    answer_7: {
                        label: 'Recurrent nosebleeds',
                    },
                    answer_8: {
                        label: 'Intense sneezing, sometimes in fits and starts',
                    },
                    answer_9: {
                        label: 'Itchy nose and throat',
                    },
                    answer_10: {
                        label: 'None of these symptoms',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Are you taking an oral contraceptive? ',
                description: 'If so, please specify which one',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Medications and natural products',
                description: 'Do you take any medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$m = {
    allergicRhinitis: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Congestion nasale',
                    },
                    answer_2: {
                        label: 'Exposition à un agent chimique',
                    },
                    answer_3: {
                        label: 'Exposition à un polluant',
                    },
                    answer_4: {
                        label: 'Écoulement nasal clair',
                    },
                    answer_5: {
                        label: 'Mal de tête',
                    },
                    answer_6: {
                        label: 'Éternuements intenses et parfois en quinte',
                    },
                    answer_7: {
                        label: 'Démangeaison au niveau du nez et de la gorge',
                    },
                    answer_8: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Écoulement nasal coloré',
                    },
                    answer_2: {
                        label: 'Présence de douleur au niveau du visage ou des dents',
                    },
                    answer_3: {
                        label: 'Fièvre',
                    },
                    answer_4: {
                        label: 'Présence récurrente de saignements de nez',
                    },
                    answer_5: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            simpleChoiceRadio_8: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var allergicRhinitisFrench = {
    allergicRhinitis: {
        v2: {
            ...v2$m.allergicRhinitis,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Congestion nasale',
                    },
                    answer_2: {
                        label: 'Écoulement nasal clair',
                    },
                    answer_3: {
                        label: 'Écoulement nasal coloré',
                    },
                    answer_4: {
                        label: 'Présence de douleur au niveau du visage ou des dents',
                    },
                    answer_5: {
                        label: 'Fièvre',
                    },
                    answer_6: {
                        label: 'Mal de tête',
                    },
                    answer_7: {
                        label: 'Présence récurrente de saignements de nez',
                    },
                    answer_8: {
                        label: 'Éternuement intense et parfois en quinte',
                    },
                    answer_9: {
                        label: 'Démangeaison au niveau du nez et de la gorge',
                    },
                    answer_10: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Prenez-vous un contraceptif oral ? ',
                description: 'Si oui, veuillez spécifier lequel',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels de façon régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$l = {
    hemorrhoids: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Itching, irritation, discomfort, burning and swelling of the anus',
                    },
                    answer_2: {
                        label: 'Presence of constipation or diarrhea',
                    },
                    answer_3: {
                        label: 'Presence of light-red blood on toilet paper or in the bowl during defecation',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Have been in contact with children who may have intestinal worms',
                    },
                    answer_2: {
                        label: 'Black stools',
                    },
                    answer_3: {
                        label: 'Fecal incontinence',
                    },
                    answer_4: {
                        label: 'Fever',
                    },
                    answer_5: {
                        label: 'Abdominal cramps',
                    },
                    answer_6: {
                        label: 'Bleeding for more than 7 days',
                    },
                    answer_7: {
                        label: 'Heavy, persistent or recurrent rectal bleeding',
                    },
                    answer_8: {
                        label: 'Rectal prolapse',
                    },
                    answer_9: {
                        label: 'Severe, sharp pain on defecation',
                    },
                    answer_10: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var hemorrhoidsEnglish = {
    hemorrhoids: {
        v2: {
            ...v2$l.hemorrhoids,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Itching, irritation, discomfort, burning and swelling of the anus.',
                    },
                    answer_2: {
                        label: 'Presence of constipation or diarrhea',
                    },
                    answer_3: {
                        label: 'Presence of light-red blood on toilet paper or in the bowl during defecation',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Have been in contact with children who may have intestinal worms',
                    },
                    answer_2: {
                        label: 'Black stools',
                    },
                    answer_3: {
                        label: 'Fecal incontinence',
                    },
                    answer_4: {
                        label: 'Fever',
                    },
                    answer_5: {
                        label: 'Abdominal cramps',
                    },
                    answer_6: {
                        label: 'Bleeding for more than 7 days',
                    },
                    answer_7: {
                        label: 'Heavy, persistent or recurrent rectal bleeding',
                    },
                    answer_8: {
                        label: 'Rectal prolapse',
                    },
                    answer_9: {
                        label: 'Severe, sharp pain on defecation',
                    },
                    answer_10: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day ',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Alcoholic beverages',
                description: 'Do you drink alcoholic beverages? If yes, please specify the quantity.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$k = {
    hemorrhoids: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Démangeaisons, irritations, inconfort, sensation de brûlure et enflure au niveau de l\'anus',
                    },
                    answer_2: {
                        label: 'Présence de constipation ou diarrhée',
                    },
                    answer_3: {
                        label: 'Présence de sang rouge clair sur le papier hygiénique ou dans la cuvette lors de la défécation',
                    },
                    answer_4: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Avez été en contact avec des enfants ayant possiblement des vers intestinaux',
                    },
                    answer_2: {
                        label: 'Selles noires',
                    },
                    answer_3: {
                        label: 'Incontinence fécale',
                    },
                    answer_4: {
                        label: 'Fièvre',
                    },
                    answer_5: {
                        label: 'Crampes abdominales',
                    },
                    answer_6: {
                        label: 'Présence de saignements depuis plus de 7 jours',
                    },
                    answer_7: {
                        label: 'Saignement rectal abondant, qui persiste ou est récurrent',
                    },
                    answer_8: {
                        label: 'Descente d\'organe au niveau rectal',
                    },
                    answer_9: {
                        label: 'Douleur importante et vive à la défécation',
                    },
                    answer_10: {
                        label: 'Aucune de ces conditions',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var hemorrhoidsFrench = {
    hemorrhoids: {
        v2: {
            ...v2$k.hemorrhoids,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes ?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Démangeaisons, irritations, inconfort, sensation de brûlure et enflure au niveau de l\'anus',
                    },
                    answer_2: {
                        label: 'Présence de constipation ou diarrhée',
                    },
                    answer_3: {
                        label: 'Présence de sang rouge clair sur le papier hygiénique ou dans la cuvette lors de la défécation',
                    },
                    answer_4: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Avez été en contact avec des enfants ayant possiblement des vers intestinaux',
                    },
                    answer_2: {
                        label: 'Selles noires',
                    },
                    answer_3: {
                        label: 'Incontinence fécale',
                    },
                    answer_4: {
                        label: 'Fièvre',
                    },
                    answer_5: {
                        label: 'Crampes abdominales',
                    },
                    answer_6: {
                        label: 'Présence de saignements depuis plus de 7 jours',
                    },
                    answer_7: {
                        label: 'Saignement rectal abondant, qui persiste ou est récurrent',
                    },
                    answer_8: {
                        label: 'Prolapsus rectal',
                    },
                    answer_9: {
                        label: 'Douleur importante et vive à la défécation',
                    },
                    answer_10: {
                        label: 'Aucune de ces conditions',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Tabagisme',
                description: 'Si oui, pouvez-vous indiquer le nombre de cigarettes par jour ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Boissons alcoolisées',
                description: 'Consommez-vous des boissons alcoolisées ? Si oui, spécifiez la quantité.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels de façon régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$j = {
    shingleTreatmentAndCare: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have one or more of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Pain in the area of lesions',
                    },
                    answer_2: {
                        label: 'Tingling sensation in the affected area',
                    },
                    answer_3: {
                        label: 'Burning or itching sensation',
                    },
                    answer_4: {
                        label: 'Abnormal sensitivity to touch',
                    },
                    answer_5: {
                        label: 'Change in sensitivity in the affected area',
                    },
                    answer_6: {
                        label: 'Lesions forming an even shape with a linear pattern',
                    },
                    answer_7: {
                        label: 'Lesions measuring 2 to 3 mm',
                    },
                    answer_8: {
                        label: 'Diffuse or bright redness in the affected area',
                    },
                    answer_9: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'At your appointment, the appropriate treatment may be prescribed for you and you will be given a referral form that you will need to present at your next appointment. However, it is advisable to consult a medical clinic within 72 hours of starting treatment.',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Lesions apparent for less than 72 hours',
                    },
                    answer_2: {
                        label: 'Lesions apparent for more than 72 hours',
                    },
                    answer_3: {
                        label: 'Patients aged 50 and over',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Can you specify how long you\'ve had symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            simpleChoiceRadio_6: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var shingleTreatmentAndCareEnglish = {
    shingleTreatmentAndCare: {
        v2: {
            ...v2$j.shingleTreatmentAndCare,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have one or more of these symptoms? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Pain in the area of lesions.',
                    },
                    answer_2: {
                        label: 'Tingling sensation in the affected area.',
                    },
                    answer_3: {
                        label: 'Burning or itching sensation.',
                    },
                    answer_4: {
                        label: 'Abnormal sensitivity to touch.',
                    },
                    answer_5: {
                        label: 'Change in sensitivity in the affected area.',
                    },
                    answer_6: {
                        label: 'Lesions forming an even shape with a linear pattern.',
                    },
                    answer_7: {
                        label: 'Lesions measuring 2 to 3 mm.',
                    },
                    answer_8: {
                        label: 'Diffuse or bright redness in the affected area.',
                    },
                    answer_9: {
                        label: 'None of the above.',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Your pharmacist can prescribe the right treatment. However, it\'s best to see a doctor or NP within 72 hours of starting treatment. Your pharmacist will give you a reference form to present to the doctor or NP (nurse practitioner). ',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Lesions visible for less than 72 hours.',
                    },
                    answer_2: {
                        label: 'Lesions apparent for more than 72 hours.',
                    },
                    answer_3: {
                        label: 'Patients aged 50 and over.',
                    },
                    answer_4: {
                        label: 'None of the above.',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Do you suffer from kidney failure? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$i = {
    shingleTreatmentAndCare: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Douleur dans la zone des lésions',
                    },
                    answer_2: {
                        label: 'Sensation de fourmillements dans la zone atteinte',
                    },
                    answer_3: {
                        label: 'Sensation de brûlure ou démangeaison',
                    },
                    answer_4: {
                        label: 'Une sensibilité anormale au toucher',
                    },
                    answer_5: {
                        label: 'Changement dans la sensibilité dans la zone atteinte',
                    },
                    answer_6: {
                        label: 'Lésions formant une forme égale et à tendance linéaire',
                    },
                    answer_7: {
                        label: 'Lésions mesurant de 2 à 3 mm',
                    },
                    answer_8: {
                        label: 'Rougeur diffuse ou vive dans la zone atteinte',
                    },
                    answer_9: {
                        label: 'Aucun de ces symptômes',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Lors de votre rendez-vous, le traitement approprié pourra vous être prescrit et on vous remettra un formulaire de référence que vous devrez présenter lors du prochain rendez-vous. Cependant, il est conseillé de consulter en clinique médicale dans les 72h suivant le début du traitement. ',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Avez-vous une ou plusieurs de ces situations? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Lésions apparentes depuis moins de 72 heures',
                    },
                    answer_2: {
                        label: 'Lésions apparentes depuis plus de 72 heures',
                    },
                    answer_3: {
                        label: 'Personne âgé de 50 ans et plus',
                    },
                    answer_4: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Pouvez-vous préciser depuis combien de temps vous avez des symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            simpleChoiceRadio_6: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var shingleTreatmentAndCareFrench = {
    shingleTreatmentAndCare: {
        v2: {
            ...v2$i.shingleTreatmentAndCare,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Douleur dans la zone des lésions.',
                    },
                    answer_2: {
                        label: 'Sensation de fourmillements dans la zone atteinte.',
                    },
                    answer_3: {
                        label: 'Sensation de brûlure ou démangeaison.',
                    },
                    answer_4: {
                        label: 'Une sensibilité anormale au toucher.',
                    },
                    answer_5: {
                        label: 'Changement dans la sensibilité dans la zone atteinte.',
                    },
                    answer_6: {
                        label: 'Lésion formant une forme égale et à tendance linéaire.',
                    },
                    answer_7: {
                        label: 'Lésions mesurant de 2 à 3 mm.',
                    },
                    answer_8: {
                        label: 'Rougeur diffuse ou vive dans la zone atteinte.',
                    },
                    answer_9: {
                        label: 'Aucun de ces symptômes.',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Votre pharmacien peut vous prescrire le traitement approprié. Cependant, il est conseillé de consulter un médecin ou une IPS dans les 72h suivant le début du traitement. Votre pharmacien vous remettra un formulaire de référence que vous devrez présenter au médecin ou à l\'IPS (infirmière praticienne spécialisée). ',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Avez-vous une ou plusieurs de ces situations? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Lésions apparentes depuis moins de 72 heures.',
                    },
                    answer_2: {
                        label: 'Lésions apparentes depuis plus de 72 heures.',
                    },
                    answer_3: {
                        label: 'Patients âgés de 50 ans et plus.',
                    },
                    answer_4: {
                        label: 'Aucune de ces situations.',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Souffrez-vous d\'insuffisance rénale ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels de façon régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$h = {
    acneProblems: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Localized lesions on face, shoulders, back and trunk',
                    },
                    answer_2: {
                        label: 'Presence of open comedones (blackheads), closed comedones (whiteheads) and papules',
                    },
                    answer_3: {
                        label: 'Absence of pustules or cysts',
                    },
                    answer_4: {
                        label: 'Presence of pustules or cysts',
                    },
                    answer_5: {
                        label: 'None of the above',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Sometimes acne can worsen at the beginning of treatment. Various creams can also temporarily change the appearance of lesions. Lesions typically start to improve after four (4) weeks.',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_6: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var acneProblemsEnglish = {
    acneProblems: {
        v2: {
            ...v2$h.acneProblems,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Localized lesions on face, shoulders, back and trunk.',
                    },
                    answer_2: {
                        label: 'Presence of open comedones (blackheads), closed comedones (whiteheads) and papules.',
                    },
                    answer_3: {
                        label: 'Absence of pustules or cysts.',
                    },
                    answer_4: {
                        label: 'Presence of pustules or cysts.',
                    },
                    answer_5: {
                        label: 'None of the above.',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Sometimes acne can worsen at the beginning of treatment. Various creams can also temporarily change the appearance of lesions. Lesions typically start to improve after four (4) weeks.',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Have you tried any treatments to relieve your symptoms?',
                description: 'If so, please specify which ones',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Smoking',
                description: 'If yes, please enter the number of cigarettes per day ',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Alcoholic beverages',
                description: 'Do you drink alcoholic beverages? If yes, please specify quantity.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$g = {
    acneProblems: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Lésions localisées au niveau du visage, des épaules, du dos et du tronc',
                    },
                    answer_2: {
                        label: 'Présence de comédons ouverts (points noirs), fermés (points blancs) et de papules',
                    },
                    answer_3: {
                        label: 'Absence de pustules ou de kystes',
                    },
                    answer_4: {
                        label: 'Présence de pustules ou de kystes',
                    },
                    answer_5: {
                        label: 'Aucun de ces symptômes',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Il arrive parfois que l\'acné s\'aggrave au début du traitement. Les différentes crèmes peuvent également modifier temporairement l\'apparence des lésions. Les lésions commencent à s\'améliorer habituellement au bout de quatre (4) semaines.',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_6: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var acneProblemsFrench = {
    acneProblems: {
        v2: {
            ...v2$g.acneProblems,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous un ou plusieurs de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Lésions localisées au niveau du visage, des épaules, du dos et du tronc.',
                    },
                    answer_2: {
                        label: 'Présence de comédons ouverts (points noirs), fermés (points blancs) et de papules.',
                    },
                    answer_3: {
                        label: 'Absence de pustules ou de kystes.',
                    },
                    answer_4: {
                        label: 'Présence de pustules ou de kystes.',
                    },
                    answer_5: {
                        label: 'Aucun de ces symptômes.',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Il arrive parfois que l\'acné s\'aggrave au début du traitement. Les différentes crèmes peuvent également modifier temporairement l\'apparence des lésions. Les lésions commencent à s\'améliorer habituellement au bout de quatre (4) semaines.',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Avez-vous déjà essayé des traitements pour soulager vos symptômes ?',
                description: 'Si oui, veuillez spécifier lesquels',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Tabagisme',
                description: 'Si oui, pouvez inscrire le nombre de cigarettes par jour ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Boissons alcoolisées',
                description: 'Consommez-vous des boissons alcoolisées ? Si oui, spécifiez la quantité.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels de façon régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$f = {
    allergicConjunctivitis: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Clear watery eyes',
                    },
                    answer_2: {
                        label: 'Itchy eyes',
                    },
                    answer_3: {
                        label: 'Symptoms present in both eyes',
                    },
                    answer_4: {
                        label: 'Symptoms present in only one eye',
                    },
                    answer_5: {
                        label: 'Slight sensation of burning or irritation',
                    },
                    answer_6: {
                        label: 'Nasal symptoms sometimes present (nasal congestion, etc.)',
                    },
                    answer_7: {
                        label: 'Recurrent or seasonal symptoms',
                    },
                    answer_8: {
                        label: 'Sensitivity or pain in the eye',
                    },
                    answer_9: {
                        label: 'None of these symptoms',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Thick, coloured secretions',
                    },
                    answer_2: {
                        label: 'Fever',
                    },
                    answer_3: {
                        label: 'Change in vision',
                    },
                    answer_4: {
                        label: 'Possibility of a foreign body in the eye',
                    },
                    answer_5: {
                        label: 'Severe headaches',
                    },
                    answer_6: {
                        label: 'Nausea and vomiting',
                    },
                    answer_7: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Have you had these symptoms in the past? ',
                description: 'If so, indicate when and what treatment worked',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_7: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var allergicConjunctivitisEnglish = {
    allergicConjunctivitis: {
        v2: {
            ...v2$f.allergicConjunctivitis,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Clear tearing',
                    },
                    answer_2: {
                        label: 'Itchy eyes',
                    },
                    answer_3: {
                        label: 'Symptoms present in both eyes',
                    },
                    answer_4: {
                        label: 'Symptoms present in only one eye',
                    },
                    answer_5: {
                        label: 'Slight sensation of burning or irritation',
                    },
                    answer_6: {
                        label: 'Nasal symptoms sometimes present (nasal congestion, etc.)',
                    },
                    answer_7: {
                        label: 'Recurrent or seasonal symptoms',
                    },
                    answer_8: {
                        label: 'Thick, colored secretions',
                    },
                    answer_9: {
                        label: 'Fever',
                    },
                    answer_10: {
                        label: 'Change in vision',
                    },
                    answer_11: {
                        label: 'Sensitivity or pain in the eye',
                    },
                    answer_12: {
                        label: 'Possibility of a foreign body in the eye',
                    },
                    answer_13: {
                        label: 'Severe headache',
                    },
                    answer_14: {
                        label: 'Nausea and vomiting',
                    },
                    answer_15: {
                        label: 'None of these symptoms',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Do you mainly have nasal symptoms?',
                description: 'For example, nasal congestion, clear nasal secretions, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$e = {
    allergicConjunctivitis: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Larmoiement clair',
                    },
                    answer_2: {
                        label: 'Démangeaisons au niveau des yeux',
                    },
                    answer_3: {
                        label: 'Symptômes présents dans les deux yeux',
                    },
                    answer_4: {
                        label: 'Symptômes présents dans un seul oeil',
                    },
                    answer_5: {
                        label: 'Légère sensation de brûlure ou d\'irritation',
                    },
                    answer_6: {
                        label: 'Symptômes nasale parfois présent (congestion nasale, etc.)',
                    },
                    answer_7: {
                        label: 'Symptômes récurrents ou saisonniers',
                    },
                    answer_8: {
                        label: 'Sensibilité ou douleur au niveau de l\'oeil',
                    },
                    answer_9: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Sécrétions épaisses et colorées',
                    },
                    answer_2: {
                        label: 'Fièvre',
                    },
                    answer_3: {
                        label: 'Changement dans la vision',
                    },
                    answer_4: {
                        label: 'Possibilité d\'avoir un corps étranger dans l\'oeil',
                    },
                    answer_5: {
                        label: 'Maux de tête sévères',
                    },
                    answer_6: {
                        label: 'Nausées et vomissements',
                    },
                    answer_7: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Avez-vous déjà eu ces symptômes dans le passé? ',
                description: 'Si oui, indiquez quand et quel traitement avait fonctionné',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_7: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var allergicConjunctivitisFrench = {
    allergicConjunctivitis: {
        v2: {
            ...v2$e.allergicConjunctivitis,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Larmoiement clair',
                    },
                    answer_2: {
                        label: 'Démangeaison au niveau des yeux',
                    },
                    answer_3: {
                        label: 'Symptômes présents dans les deux yeux',
                    },
                    answer_4: {
                        label: 'Symptômes présents dans un seul oeil',
                    },
                    answer_5: {
                        label: 'Légère sensation de brûlure ou d\'irritation',
                    },
                    answer_6: {
                        label: 'Symptômes nasaux parfois présents (congestion nasale, etc.)',
                    },
                    answer_7: {
                        label: 'Symptômes récurrents ou saisonniers',
                    },
                    answer_8: {
                        label: 'Sécrétions épaisses et colorées',
                    },
                    answer_9: {
                        label: 'Fièvre',
                    },
                    answer_10: {
                        label: 'Changement dans la vision',
                    },
                    answer_11: {
                        label: 'Sensibilité ou douleur au niveau de l\'oeil',
                    },
                    answer_12: {
                        label: 'Possibilité d\'avoir un corps étranger dans l\'oeil',
                    },
                    answer_13: {
                        label: 'Maux de tête sévères',
                    },
                    answer_14: {
                        label: 'Nausées et vomissements',
                    },
                    answer_15: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            yesNoButton_2: {
                title: 'Avez-vous principalement des symptômes nasaux ? ',
                description: 'Par exemple, de la congestion nasale, des sécrétions nasales claires, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$d = {
    coldSores: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Lesions around the mouth',
                    },
                    answer_2: {
                        label: 'Lesions around the eyes, mucous membranes of the mouth or throat',
                    },
                    answer_3: {
                        label: 'Burning and/or tingling sensation before lesions appear',
                    },
                    answer_4: {
                        label: 'Occurs after an episode of fever',
                    },
                    answer_5: {
                        label: 'Occurs after exposure to the sun',
                    },
                    answer_6: {
                        label: 'Accumulation of painful lesions (vesicles) that release a clear liquid when ruptured',
                    },
                    answer_7: {
                        label: 'Scab formation',
                    },
                    answer_8: {
                        label: 'Lesions present for more than 14 days',
                    },
                    answer_9: {
                        label: 'Presence of pus',
                    },
                    answer_10: {
                        label: '6 or more episodes in the last 12 months',
                    },
                    answer_11: {
                        label: 'Fever, chills, general malaise',
                    },
                    answer_12: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Have the lesions been present for more than 72 hours (3 days)?',
                description: 'Indicate for how long',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_6: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var coldSoresEnglish = {
    coldSores: {
        v2: {
            ...v2$d.coldSores,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Lesions around the mouth',
                    },
                    answer_2: {
                        label: 'Lesions around the eyes, mucous membranes of the mouth or throat',
                    },
                    answer_3: {
                        label: 'Burning and/or tingling sensation before lesions appear',
                    },
                    answer_4: {
                        label: 'Occurs after an episode of fever',
                    },
                    answer_5: {
                        label: 'Occurs after exposure to the sun',
                    },
                    answer_6: {
                        label: 'Accumulation of painful lesions (vesicles) that release a clear liquid when ruptured',
                    },
                    answer_7: {
                        label: 'Scab formation',
                    },
                    answer_8: {
                        label: 'Lesions present for more than 14 days',
                    },
                    answer_9: {
                        label: 'Presence of pus',
                    },
                    answer_10: {
                        label: '6 or more episodes in the last 12 months',
                    },
                    answer_11: {
                        label: 'Fever, chills, general malaise',
                    },
                    answer_12: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Have the lesions been present for more than 72 hours (3 days)?',
                description: 'If so, please indicate for how long',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Do you suffer from kidney failure?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Do you take corticosteroids?',
                description: 'If so, please provide the name of the drug.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$c = {
    coldSores: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Lésions situées au pourtour de la bouche',
                    },
                    answer_2: {
                        label: 'Lésions situées au niveau des yeux, des muqueuses de la bouche ou de la gorge',
                    },
                    answer_3: {
                        label: 'Sensation de brûlure et/ou de picotement avant l\'apparition de lésions',
                    },
                    answer_4: {
                        label: 'Survient après un épisode de fièvre',
                    },
                    answer_5: {
                        label: 'Survient après une exposition au soleil',
                    },
                    answer_6: {
                        label: 'Accumulation de lésions (vésicules) douloureuses qui libèrent un liquide clair lorsqu\'elles se rompent',
                    },
                    answer_7: {
                        label: 'Formation de croûtes après quelques jours',
                    },
                    answer_8: {
                        label: 'Lésions présentes depuis plus de 14 jours',
                    },
                    answer_9: {
                        label: 'Présence de pus',
                    },
                    answer_10: {
                        label: '6 épisodes ou plus dans les 12 derniers mois',
                    },
                    answer_11: {
                        label: 'Fièvre, frisson, malaise généralisé',
                    },
                    answer_12: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Est-ce que la ou les lésions sont apparues depuis plus de 72 heures (3 jours)?',
                description: 'Indiquez depuis combien de temps',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_6: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var coldSoresFrench = {
    coldSores: {
        v2: {
            ...v2$c.coldSores,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Lésions situées au pourtour de la bouche',
                    },
                    answer_2: {
                        label: 'Lésions situées au niveau des yeux, des muqueuses de la bouche ou de la gorge',
                    },
                    answer_3: {
                        label: 'Sensation de brûlure et/ou de picotement avant l\'apparition de lésions',
                    },
                    answer_4: {
                        label: 'Survient après un épisode de fièvre',
                    },
                    answer_5: {
                        label: 'Survient après une exposition au soleil',
                    },
                    answer_6: {
                        label: 'Accumulation de lésions (vésicules) douloureuses qui libèrent un liquide clair lorsqu\'elles se rompent',
                    },
                    answer_7: {
                        label: 'Formation d\'une croûte après quelques jours',
                    },
                    answer_8: {
                        label: 'Lésions présentes depuis plus de 14 jours',
                    },
                    answer_9: {
                        label: 'Présence de pus',
                    },
                    answer_10: {
                        label: '6 épisodes ou plus dans les 12 derniers mois',
                    },
                    answer_11: {
                        label: 'Fièvre, frissons, malaise généralisé',
                    },
                    answer_12: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Est-ce que la ou les lésions sont apparues depuis plus de 72 heures (3 jours) ?',
                description: 'Si oui, veuillez indiquer depuis combien de temps',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Souffrez-vous d\'insuffisance rénale ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Prenez-vous des corticostéroïdes ?',
                description: 'Si oui, veuillez inscrire le nom du médicament.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$b = {
    headLice: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do any of these situations apply to you? 	',
                description: '',
                answer: {
                    answer_1: {
                        label: 'I have seen one or more nits in my hair',
                    },
                    answer_2: {
                        label: 'I have seen one or more lice in my hair',
                    },
                    answer_3: {
                        label: 'There has been close contact with a confirmed case of pediculosis (family or elsewhere)',
                    },
                    answer_4: {
                        label: 'There is an outbreak of head lice in the environment (school, daycare, etc.)',
                    },
                    answer_5: {
                        label: 'Significant itching of the scalp with no apparent cause for more than a week',
                    },
                    answer_6: {
                        label: 'None of these situations',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you experienced the same condition in the past month? ',
                description: 'If so, please specify the date and type of treatment',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here',
                    },
                },
            },
        },
    },
};

var headLiceEnglsih = {
    headLice: {
        v2: {
            ...v2$b.headLice,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'How old is the person affected?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Have you experienced the same condition in the past month? ',
                description: 'If so, please specify both date and type of treatment',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Are you allergic to chrysanthemums or ragweed?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$a = {
    headLice: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'J\'ai vu une ou des lentes dans les cheveux',
                    },
                    answer_2: {
                        label: 'J\'ai vu un ou des poux dans les cheveux',
                    },
                    answer_3: {
                        label: 'Il y a eu un contact étroit avec un cas confirmé de pédiculose (famille ou ailleurs)',
                    },
                    answer_4: {
                        label: 'Il y a une éclosion de poux de tête dans le milieu fréquenté (école, garderie, etc.)',
                    },
                    answer_5: {
                        label: 'Présence de démangeaisons importantes du cuir chevelu sans cause apparante depuis plus d\'une semaine',
                    },
                    answer_6: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous traité la même condition dans le dernier mois? ',
                description: 'Si oui, spécifiez la date et le type de traitement',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var headLiceFrench = {
    headLice: {
        v2: {
            ...v2$a.headLice,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'Quel âge a la personne atteinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Avez-vous traité la même condition dans le dernier mois ? ',
                description: 'Si oui, veuillez spécifier la date et le type de traitement',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Avez-vous une allergie aux chrysanthèmes ou à l\'herbe à poux ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$9 = {
    cutaneousCandidiasis: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Plaques diffuses d\'un rouge vif au niveau des plis du corps (aisselle, entre cuisse, etc.) ou dans des zones humides',
                    },
                    answer_2: {
                        label: 'Lésions a bordures irrégulières',
                    },
                    answer_3: {
                        label: 'Démangeaisons',
                    },
                    answer_4: {
                        label: 'Lésions situées en-dehors de la zone humide, irritées et des plis',
                    },
                    answer_5: {
                        label: 'Présence de pus',
                    },
                    answer_6: {
                        label: 'Présence de petites boutons blanchâtres ou d\'une rougeur diffuse',
                    },
                    answer_7: {
                        label: 'Malaise plus général',
                    },
                    answer_8: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, veuillez préciser les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBox_4: {
                title: 'Avez-vous une ou plusieurs de ces conditions?',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Incontinence urinaire ou fécale',
                    },
                    answer_2: {
                        label: 'Immobilité ou impossibilité de changer de position sans assistance',
                    },
                    answer_3: {
                        label: 'Diabète',
                    },
                    answer_4: {
                        label: 'Immunosuppression',
                    },
                    answer_5: {
                        label: 'Obésité',
                    },
                    answer_6: {
                        label: 'Sudation excessive',
                    },
                    answer_7: {
                        label: 'Aucune de ces conditions',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var cutaneousCandidiasisFrench = {
    cutaneousCandidiasis: {
        v2: {
            ...v2$9.cutaneousCandidiasis,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Plaques diffuses rouge vif au niveau des plis du corps (aisselle, entre cuisse, etc.) ou dans des zones humides',
                    },
                    answer_2: {
                        label: 'Bordure irrégulière',
                    },
                    answer_3: {
                        label: 'Démangeaison',
                    },
                    answer_4: {
                        label: 'Lésions situées en-dehors de la zone humide, irritée et des plis',
                    },
                    answer_5: {
                        label: 'Présence de pus',
                    },
                    answer_6: {
                        label: 'Présence de petits boutons blanchâtres ou d\'une rougeur diffuse',
                    },
                    answer_7: {
                        label: 'Malaise plus général',
                    },
                    answer_8: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Avez-vous une ou plusieurs de ces conditions ?',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Incontinence urinaire ou fécale',
                    },
                    answer_2: {
                        label: 'Diabète',
                    },
                    answer_3: {
                        label: 'VIH',
                    },
                    answer_4: {
                        label: 'Obésité',
                    },
                    answer_5: {
                        label: 'Sudation excessive',
                    },
                    answer_6: {
                        label: 'Aucune de ces conditions',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$8 = {
    cutaneousCandidiasis: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Bright red diffuse patches in body folds (armpit, between thighs, etc.) or in damp areas',
                    },
                    answer_2: {
                        label: 'Lesions with irregular borders',
                    },
                    answer_3: {
                        label: 'Itching',
                    },
                    answer_4: {
                        label: 'Lesions located outside moist, irritated areas and folds',
                    },
                    answer_5: {
                        label: 'Presence of pus',
                    },
                    answer_6: {
                        label: 'Presence of small whitish pimples or diffuse redness',
                    },
                    answer_7: {
                        label: 'General discomfort',
                    },
                    answer_8: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_3: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBox_4: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Urinary or fecal incontinence',
                    },
                    answer_2: {
                        label: 'Immobility or inability to change position without assistance',
                    },
                    answer_3: {
                        label: 'Diabetes',
                    },
                    answer_4: {
                        label: 'Immunosuppression',
                    },
                    answer_5: {
                        label: 'Obesity',
                    },
                    answer_6: {
                        label: 'Excessive sweating',
                    },
                    answer_7: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var cutaneousCandidiasisEnglish = {
    cutaneousCandidiasis: {
        v2: {
            ...v2$8.cutaneousCandidiasis,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Bright red diffuse patches in body folds (armpit, between thighs, etc.) or in damp areas',
                    },
                    answer_2: {
                        label: 'Irregular borders',
                    },
                    answer_3: {
                        label: 'Itching',
                    },
                    answer_4: {
                        label: 'Lesions located outside moist, irritated areas and folds',
                    },
                    answer_5: {
                        label: 'Presence of pus',
                    },
                    answer_6: {
                        label: 'Presence of small whitish pimples or diffuse redness',
                    },
                    answer_7: {
                        label: 'General discomfort',
                    },
                    answer_8: {
                        label: 'None of the above',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Urinary or fecal incontinence',
                    },
                    answer_2: {
                        label: 'Diabetes',
                    },
                    answer_3: {
                        label: 'HIV',
                    },
                    answer_4: {
                        label: 'Obesity',
                    },
                    answer_5: {
                        label: 'Excessive sweating',
                    },
                    answer_6: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$7 = {
    heartburnAndGastroesophagealReflux: {
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Acid regurgitation',
                    },
                    answer_2: {
                        label: 'Epigastric pain',
                    },
                    answer_3: {
                        label: 'Heartburn',
                    },
                    answer_4: {
                        label: 'Nausea',
                    },
                    answer_5: {
                        label: 'Belching',
                    },
                    answer_6: {
                        label: 'Abdominal distension and bloating',
                    },
                    answer_7: {
                        label: 'Abnormal digestive sensation',
                    },
                    answer_8: {
                        label: 'Slow digestion',
                    },
                    answer_9: {
                        label: 'Feeling of early satiety',
                    },
                    answer_10: {
                        label: 'None of the above',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Presence of blood in regurgitates',
                    },
                    answer_2: {
                        label: 'Black stools',
                    },
                    answer_3: {
                        label: 'Rapid, unexplained weight loss',
                    },
                    answer_4: {
                        label: 'Difficulty or pain swallowing',
                    },
                    answer_5: {
                        label: 'Coughing or difficulty breathing',
                    },
                    answer_6: {
                        label: 'Severe, unexplained abdominal pain',
                    },
                    answer_7: {
                        label: 'None of these symptoms',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How long have you had these symptoms?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Have you tried to relieve your symptoms?',
                description: 'If so, please specify the methods or products used',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Do any of these situations apply to you? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Family history of stomach or esophageal cancer',
                    },
                    answer_2: {
                        label: 'Personal history of gastric ulcer',
                    },
                    answer_3: {
                        label: 'Personal history of H. pylori',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_7: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Do you consume caffeinated beverages?',
                description: 'If so, specify daily amount',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Eating habits and lifestyle contribute to acid reflux and heartburn. Ask about them during your appointment, we will be able to advise you or refer you if necessary.',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var heartburnEnglish = {
    heartburnAndGastroesophagealReflux: {
        v2: {
            ...v2$7.heartburnAndGastroesophagealReflux,
        },
        section: {
            section_1: {
                title: 'YOUR SYMPTOMS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Do you have any of these symptoms?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Acid regurgitation',
                    },
                    answer_2: {
                        label: 'Epigastric pain',
                    },
                    answer_3: {
                        label: 'Heartburn',
                    },
                    answer_4: {
                        label: 'Nausea',
                    },
                    answer_5: {
                        label: 'Belching',
                    },
                    answer_6: {
                        label: 'Abdominal distension and bloating',
                    },
                    answer_7: {
                        label: 'Abnormal digestive sensation',
                    },
                    answer_8: {
                        label: 'Slow digestion',
                    },
                    answer_9: {
                        label: 'Presence of blood in regurgitates',
                    },
                    answer_10: {
                        label: 'Black stools',
                    },
                    answer_11: {
                        label: 'Rapid, unexplained weight loss',
                    },
                    answer_12: {
                        label: 'Difficulty or pain when swallowing',
                    },
                    answer_13: {
                        label: 'Coughing or difficulty breathing',
                    },
                    answer_14: {
                        label: 'Severe, unexplained abdominal pain',
                    },
                    answer_15: {
                        label: 'Feeling full too soon',
                    },
                    answer_16: {
                        label: 'None of these symptoms',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Do any of these situations apply to you? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Over 60 years of age and first episode',
                    },
                    answer_2: {
                        label: 'Family history of stomach or esophageal cancer',
                    },
                    answer_3: {
                        label: 'Personal history of gastric ulcer',
                    },
                    answer_4: {
                        label: 'Personal history of H. pylori',
                    },
                    answer_5: {
                        label: 'Under 18 years of age',
                    },
                    answer_6: {
                        label: 'Regular use of anti-inflammatory drugs (e.g. aspirin, ibuprofen)',
                    },
                    answer_7: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Caffeine',
                description: 'Do you consume caffeinated beverages? If yes, specify daily amount',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Smoking',
                description: 'Do you smoke? If yes, please enter the number of cigarettes per day ',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Alcoholic beverages',
                description: 'Do you drink alcoholic beverages? If so, please specify quantity.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$6 = {
    heartburnAndGastroesophagealReflux: {
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Régurgitations acides',
                    },
                    answer_2: {
                        label: 'Douleurs épigastriques',
                    },
                    answer_3: {
                        label: 'Brûlures d\'estomac',
                    },
                    answer_4: {
                        label: 'Nausées',
                    },
                    answer_5: {
                        label: 'Éructations',
                    },
                    answer_6: {
                        label: 'Ballonnements et distension abdominale',
                    },
                    answer_7: {
                        label: 'Sensations anormales au niveau de la digestion',
                    },
                    answer_8: {
                        label: 'Sensation de digestion lente',
                    },
                    answer_9: {
                        label: 'Avoir le sentiment de satiété précoce',
                    },
                    answer_10: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Présence de sang dans les régurgits',
                    },
                    answer_2: {
                        label: 'Selles noires',
                    },
                    answer_3: {
                        label: 'Perte de poids rapide et inexpliquée',
                    },
                    answer_4: {
                        label: 'Difficulté ou douleur à la déglutition',
                    },
                    answer_5: {
                        label: 'Toux ou difficulté à respirer',
                    },
                    answer_6: {
                        label: 'Douleurs abdominales sévères et inexpliquées',
                    },
                    answer_7: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Depuis combien de temps avez-vous ces symptômes?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Avez-vous essayé de soulager vos symptômes?',
                description: 'Si oui, précisez les méthodes ou les produits utilisés ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Est-ce que l\'une de ces situations s\'applique à vous? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Histoire familiale de cancer de l\'estomac ou de l\'oesophage',
                    },
                    answer_2: {
                        label: 'Histoire personnelle d\'ulcère gastrique',
                    },
                    answer_3: {
                        label: 'Histoire personnelle de H. pylori',
                    },
                    answer_4: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_7: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Consommez-vous des boissons caféinés?',
                description: 'Si oui, spécifiez la quantité quotidienne',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Les habitudes alimentaires ainsi que les habitudes de vie contribuent aux troubles de reflux gastriques et aux brûlures d\'estomac. Informez-vous lors de votre rendez-vous, on saura vous conseillez ou vous référez au besoin. ',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var heartburnFrench = {
    heartburnAndGastroesophagealReflux: {
        v2: {
            ...v2$6.heartburnAndGastroesophagealReflux,
        },
        section: {
            section_1: {
                title: 'VOS SYMPTÔMES',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBox_1: {
                title: 'Avez-vous l\'un ou plusieurs de ces symptômes ? ',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Régurgitations acides',
                    },
                    answer_2: {
                        label: 'Douleurs épigastriques',
                    },
                    answer_3: {
                        label: 'Brûlures d\'estomac',
                    },
                    answer_4: {
                        label: 'Nausées',
                    },
                    answer_5: {
                        label: 'Éructations',
                    },
                    answer_6: {
                        label: 'Ballonnements et distension abdominale',
                    },
                    answer_7: {
                        label: 'Sensation anormale au niveau de la digestion',
                    },
                    answer_8: {
                        label: 'Sensation de digestion lente',
                    },
                    answer_9: {
                        label: 'Présence de sang dans les régurgits',
                    },
                    answer_10: {
                        label: 'Selles noires',
                    },
                    answer_11: {
                        label: 'Perte de poids rapide et inexpliquée',
                    },
                    answer_12: {
                        label: 'Difficulté ou douleur à la déglutition',
                    },
                    answer_13: {
                        label: 'Toux ou difficulté à respirer',
                    },
                    answer_14: {
                        label: 'Douleur abdominale sévère et inexpliquée',
                    },
                    answer_15: {
                        label: 'Avoir le sentiment de satiété précoce',
                    },
                    answer_16: {
                        label: 'Aucun de ces symptômes',
                    },
                },
            },
            multipleChoiceCheckBox_2: {
                title: 'Est-ce que l\'une ou plusieurs de ces situations s\'appliquent à vous ? ',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Avoir plus de 60 ans et premier épisode',
                    },
                    answer_2: {
                        label: 'Histoire familiale de cancer de l\'estomac ou de l\'oesophage',
                    },
                    answer_3: {
                        label: 'Histoire personnelle d\'ulcère gastrique',
                    },
                    answer_4: {
                        label: 'Histoire personnelle de H. pylori',
                    },
                    answer_5: {
                        label: 'Avoir moins de 18 ans',
                    },
                    answer_6: {
                        label: 'Prendre des anti-inflammatoires (Ex : aspirine, ibuprofen) régulièrement',
                    },
                    answer_7: {
                        label: 'Aucune de ces situations',
                    },
                },
            },
            yesNoButton_3: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_4: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Tabagisme',
                description: 'Est-ce que vous fumez? Si oui, inscrivez le nombre de cigarettes par jour ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Caféine',
                description: 'Consommez-vous des breuvages caféinés? Si oui, spécifiez la quantité quotidienne',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Boissons alcoolisées',
                description: 'Consommez-vous des boissons alcoolisées ? Si oui, spécifiez la quantité par jour ou par semaine.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_9: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$5 = {
    postExposureProphylaxis: {
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATIONS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'What kind of exposure have you had? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Sexual intercourse with penetration',
                    },
                    answer_2: {
                        label: 'Sharing of sexual objects involving penetration',
                    },
                    answer_3: {
                        label: 'Oral sexual contact with ejaculation',
                    },
                    answer_4: {
                        label: 'Any other sexual contact',
                    },
                    answer_5: {
                        label: 'Sharing injection equipment',
                    },
                    answer_6: {
                        label: 'Human bite',
                    },
                    answer_7: {
                        label: 'Getting stung or cut with a potentially contaminated tool/instrument',
                    },
                    answer_8: {
                        label: 'Other type of exposure, please specify',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_2: {
                title: 'What type of biological fluid have you been exposed to?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Blood or liquid containing blood',
                    },
                    answer_2: {
                        label: 'Semen',
                    },
                    answer_3: {
                        label: 'Vaginal secretions',
                    },
                    answer_4: {
                        label: 'Breast milk',
                    },
                    answer_5: {
                        label: 'Other liquids, please specify',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'What is the date and time of exposure to biological fluids? ',
                description: 'An approximative time will be enough',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBox_4: {
                title: 'Which group(s) do you belong to?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Men who have sex with men',
                    },
                    answer_2: {
                        label: 'Injecting drug user',
                    },
                    answer_3: {
                        label: 'Originating from a country where HIV is endemic',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Do you know the HIV status of your partner(s)?',
                description: 'For instance, HIV+, unknown, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBox_6: {
                title: 'In the last 3 months, have you had any signs of infection?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Fever',
                    },
                    answer_2: {
                        label: 'Nocturnal diaphoresis',
                    },
                    answer_3: {
                        label: 'Swollen lymph nodes',
                    },
                    answer_4: {
                        label: 'None of these signs of infection',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Have you had an STI screening in the last 6 weeks?',
                description: 'If so, which one? Specify the result (negative or positive) and the treatment received if applicable',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Have you heard about the PrEP before? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you have regular STI screening tests?',
                description: 'If so, please indicate frequency',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_13: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var postExposureProphylaxisEnglish = {
    postExposureProphylaxis: {
        v2: {
            ...v2$5.postExposureProphylaxis,
        },
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATIONS',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'What is the date and time of exposure to biological fluids? ',
                description: 'An approximative time will be enough. ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_2: {
                title: 'What type of biological fluid have you been exposed to?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Blood or liquid containing blood',
                    },
                    answer_2: {
                        label: 'Semen',
                    },
                    answer_3: {
                        label: 'Vaginal secretions',
                    },
                    answer_4: {
                        label: 'Breast milk',
                    },
                    answer_5: {
                        label: 'Other liquids, please specify',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_3: {
                title: 'What kind of exposure have you had? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Sexual intercourse with penetration',
                    },
                    answer_2: {
                        label: 'Sharing of sexual objects involving penetration',
                    },
                    answer_3: {
                        label: 'Oral sexual contact with ejaculation',
                    },
                    answer_4: {
                        label: 'Any other sexual contact',
                    },
                    answer_5: {
                        label: 'Sharing injection equipment',
                    },
                    answer_6: {
                        label: 'Human bite with or without blood in the wound',
                    },
                    answer_7: {
                        label: 'Human bite with or without blood in mouth',
                    },
                    answer_8: {
                        label: 'Getting stung or cut with a potentially contaminated tool/instrument',
                    },
                    answer_9: {
                        label: 'Other type of exposure, please specify',
                    },
                },
            },
            multipleChoiceCheckBox_4: {
                title: 'Do you consider yourself to be part of a group at risk?',
                description: 'Select all that applies',
                answer: {
                    answer_1: {
                        label: 'Men who have sex with men',
                    },
                    answer_2: {
                        label: 'Injecting drug user',
                    },
                    answer_3: {
                        label: 'Originating from a country where HIV is endemic',
                    },
                    answer_4: {
                        label: 'None of the above',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'In the last 3 months, have you had any signs of infection?',
                description: 'Select all that applies',
                answer: {
                    answer_1: {
                        label: 'Fever',
                    },
                    answer_2: {
                        label: 'Nocturnal diaphoresis',
                    },
                    answer_3: {
                        label: 'Swollen lymph nodes',
                    },
                    answer_4: {
                        label: 'None of these signs of infection',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Have you had an STI screening in the last 6 weeks?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_7: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Have you heard about the HIV pre-exposure prophylaxis treatment taken in anticipation of an unsafe sexual contact? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Do you have regular STI screening tests?',
                description: 'If so, please indicate frequency',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Medications and natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Known health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clots, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
        },
    },
};

var v2$4 = {
    postExposureProphylaxis: {
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES ',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            multipleChoiceCheckBoxWithText_1: {
                title: 'Quel type d\'exposition avez-vous eu? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Contact sexuel avec pénétration',
                    },
                    answer_2: {
                        label: 'Partage d\'objets sexuels avec pénétration',
                    },
                    answer_3: {
                        label: 'Contact sexuel oral avec éjaculation',
                    },
                    answer_4: {
                        label: 'Tout autre contact sexuel',
                    },
                    answer_5: {
                        label: 'Partage de matériel d\'injection',
                    },
                    answer_6: {
                        label: 'Morsure humaine',
                    },
                    answer_7: {
                        label: 'Piqûre ou coupure avec un outil/instrument potentiellement contaminé',
                    },
                    answer_8: {
                        label: 'Autre type d\'exposition, veuillez préciser',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_2: {
                title: 'À quel type de liquide biologique avez-vous été exposé?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Sang ou liquide contenant du sang',
                    },
                    answer_2: {
                        label: 'Sperme',
                    },
                    answer_3: {
                        label: 'Sécrétions vaginales',
                    },
                    answer_4: {
                        label: 'Lait maternel',
                    },
                    answer_5: {
                        label: 'Autres liquides, veuillez préciser',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Quelle est la date et l\'heure de l\'exposition à des liquides biologiques? ',
                description: 'Il suffit d\'inscrire une heure approximative',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBox_4: {
                title: 'À quel(s) groupe(s) appartenez-vous?',
                description: 'Sélectionnez le groupe',
                answer: {
                    answer_1: {
                        label: 'Homme ayant des relations sexuelles avec des hommes',
                    },
                    answer_2: {
                        label: 'Utilisateur de drogues injectables',
                    },
                    answer_3: {
                        label: 'Originaire d\'un pays où le VIH est endémique',
                    },
                    answer_4: {
                        label: 'Aucun de ces groupes',
                    },
                },
            },
            yesNoButtonWithText_5: {
                title: 'Connaissez-vous le statut VIH du ou des partenaire(s)?',
                description: 'Par exemple, VIH +, inconnu, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBox_6: {
                title: 'Au courant des 3 derniers mois, avez-vous eu des signes d\'infection?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Fièvre',
                    },
                    answer_2: {
                        label: 'Diaphorèse nocturne',
                    },
                    answer_3: {
                        label: 'Enflure des ganglions',
                    },
                    answer_4: {
                        label: 'Aucun de ces signes d\'infections',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Avez-vous eu un dépistage ITSS dans les 6 dernières semaines?',
                description: 'Si oui, lequel? Spécifiez le résultat (négatif ou positif) et le traitement reçu si applicable',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Est-ce que votre vaccination est à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Connaissez-vous la PrEP? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Faites-vous des tests de dépitage ITSS régulièrement?',
                description: 'Si oui, indiquez la fréquence',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_13: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var postExposureProphylaxisFrench = {
    postExposureProphylaxis: {
        v2: {
            ...v2$4.postExposureProphylaxis,
        },
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES ',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'Quelle est la date et l\'heure de l\'exposition à des liquides biologiques ? ',
                description: 'Il suffit d\'inscrire une heure approximative.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_2: {
                title: 'À quel type de liquide biologique avez-vous été exposé ?',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Sang ou liquide contenant du sang',
                    },
                    answer_2: {
                        label: 'Sperme',
                    },
                    answer_3: {
                        label: 'Sécrétions vaginales',
                    },
                    answer_4: {
                        label: 'Lait maternel',
                    },
                    answer_5: {
                        label: 'Autre liquides, veuillez préciser',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_3: {
                title: 'Quel type d\'exposition avez-vous eu ? ',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Contact sexuel avec pénétration',
                    },
                    answer_2: {
                        label: 'Partage d\'objets sexuels avec pénétration',
                    },
                    answer_3: {
                        label: 'Contact sexuel oral avec éjaculation',
                    },
                    answer_4: {
                        label: 'Tout autre contact sexuel',
                    },
                    answer_5: {
                        label: 'Partage de matériel d\'injection',
                    },
                    answer_6: {
                        label: 'Morsure humaine avec ou sans présence de sang dans la plaie',
                    },
                    answer_7: {
                        label: 'Morsure humaine avec ou sans présence de sang dans la bouche',
                    },
                    answer_8: {
                        label: 'Piqûre ou coupure avec un outil/instrument potentiellement contaminé',
                    },
                    answer_9: {
                        label: 'Autre type d\'exposition, veuillez préciser',
                    },
                },
            },
            multipleChoiceCheckBox_4: {
                title: 'Considérez-vous faire parti d\'un groupe à risque ?',
                description: 'Si oui, veuillez sélectionner le groupe',
                answer: {
                    answer_1: {
                        label: 'Homme ayant des relations sexuelles avec des hommes',
                    },
                    answer_2: {
                        label: 'Utilisateur de drogues injectables',
                    },
                    answer_3: {
                        label: 'Originaire d\'un pays où le VIH est endémique',
                    },
                    answer_4: {
                        label: 'Aucun de ces groupes',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Au courant des 3 derniers mois, avez-vous eu des signes d\'infection ?',
                description: 'Sélectionnez tout ce qui pourrait s’appliquer',
                answer: {
                    answer_1: {
                        label: 'Fièvre',
                    },
                    answer_2: {
                        label: 'Diaphorèse nocturne',
                    },
                    answer_3: {
                        label: 'Enflure des ganglions',
                    },
                    answer_4: {
                        label: 'Aucun de ces signes d\'infection',
                    },
                },
            },
            yesNoButton_6: {
                title: 'Avez-vous eu un dépistage ITSS dans les 6 dernières semaines ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_7: {
                title: 'Êtes-vous enceinte ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_8: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Connaissez-vous le traitement préventif du VIH pris en prévision d\'un risque accru de contact sexuel à risque ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Faites-vous des tests de dépitage ITSS régulièrement ?',
                description: 'Si oui, veuillez indiquer la fréquence',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillots, etc',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$3 = {
    malariaProphylaxis: {
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATION ABOUT YOUR TRIP',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'Which country and region will you be traveling to?',
                description: 'Specify country and region',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'The risk of contracting malaria is only present in certain regions. Depending on your answer, you may not need prophylactic (preventative) treatment for malaria. Your appointment may be cancelled.',
                    },
                },
            },
            longAnswerTextArea_2: {
                title: 'What is your travel itinerary?',
                description: 'Please indicate the different places you will be visiting during your trip, and how long you plan on staying at each location',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'How long will your trip last?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Are you taking anticoagulants? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please specify which one',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Are you allergic to any of the drugs prescribed to prevent malaria? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Chloroquine',
                    },
                    answer_2: {
                        label: 'Mefloquine',
                    },
                    answer_3: {
                        label: 'Hydroxychloroquine',
                    },
                    answer_4: {
                        label: 'Atovaquone/proguanil',
                    },
                    answer_5: {
                        label: 'None of the above',
                    },
                },
            },
            shortAnswerText_6: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Specify which ones here ',
                    },
                },
            },
        },
    },
};

var malariaProphylaxisEnglish = {
    malariaProphylaxis: {
        v2: {
            ...v2$3.malariaProphylaxis,
        },
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATION ABOUT YOUR TRIP',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'What region are you travelling to? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'The risk of contracting malaria is only present in some regions. Depending on your answer, you may not need malaria prophylaxis. Your pharmacist reserves the right to cancel your appointment.',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Are you taking anticoagulants? ',
                description: 'If so, please specify which one.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Are you allergic to any of the drugs prescribed to prevent malaria? ',
                description: 'Select all that apply.',
                answer: {
                    answer_1: {
                        label: 'Chloroquine',
                    },
                    answer_2: {
                        label: 'Mefloquine',
                    },
                    answer_3: {
                        label: 'Hydroxychloroquine',
                    },
                    answer_4: {
                        label: 'Atovaquone/proguanil',
                    },
                    answer_5: {
                        label: 'None of the above',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Are you pregnant?',
                description: 'If so, please indicate the number of weeks of pregnancy.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Were you breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Medications et natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Know health problems',
                description: 'Examples : Diabetes, high blood pressure, past history of blood clot, etc',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
        },
    },
};

var v2$2 = {
    malariaProphylaxis: {
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES SUR VOTRE VOYAGE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'Dans quel pays et région voyagerez-vous?',
                description: 'Spécifiez le pays et la région',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Le risque de contracter le paludisme (malaria) n\'est présent que dans certaines régions. Selon votre réponse, il est possible que vous n\'ayez pas besoin d\'un traitement prophylactique (préventif) de la malaria. Votre rendez-vous peut être annulé.',
                    },
                },
            },
            longAnswerTextArea_2: {
                title: 'Quel est votre itinéraire de voyage?',
                description: 'Spécifiez votre itinéraire et la durée à chaque endroit visité',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Quelle est la durée totale de votre voyage?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Prenez-vous des anticoagulants? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, spécifiez lequel',
                    },
                },
            },
            multipleChoiceCheckBox_5: {
                title: 'Avez-vous une allergie à un des médicaments prescrit en prévention du paludisme? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Chloroquine',
                    },
                    answer_2: {
                        label: 'Méfloquine',
                    },
                    answer_3: {
                        label: 'Hydroxychloroquine',
                    },
                    answer_4: {
                        label: 'Atovaquone/proguanil',
                    },
                    answer_5: {
                        label: 'Aucun de ces médicaments',
                    },
                },
            },
            shortAnswerText_6: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_7: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, indiquez le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_9: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            simpleChoiceRadio_10: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_11: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_12: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var malariaProphylaxisFrench = {
    malariaProphylaxis: {
        v2: {
            ...v2$2.malariaProphylaxis,
        },
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES SUR VOTRE VOYAGE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'Dans quelle région est votre voyage ? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Le risque de contracter le paludisme (malaria) n\'est présent que dans certaines régions. Selon votre réponse, il est possible que vous n\'ayez pas besoin d\'un traitement prophylactique (préventif) de la malaria. Votre pharmacien se réserve le droit d\'annuler votre rendez-vous.',
                    },
                },
            },
            yesNoButtonWithText_2: {
                title: 'Prenez-vous des anticoagulants ? ',
                description: 'Si oui, veuillez spécifier lequel.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBox_3: {
                title: 'Avez-vous une allergie à un des médicaments prescrit en prévention du paludisme ? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Chloroquine',
                    },
                    answer_2: {
                        label: 'Méfloquine',
                    },
                    answer_3: {
                        label: 'Hydroxychloroquine',
                    },
                    answer_4: {
                        label: 'Atovaquone/proguanil',
                    },
                    answer_5: {
                        label: 'Aucun des médicaments',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Êtes-vous enceinte ? ',
                description: 'Si oui, veuillez indiquer le nombre de semaines de grossesse.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            yesNoButton_5: {
                title: 'Allaitez-vous ?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            yesNoButtonWithText_6: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_7: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou faites-vous des réactions à des médicaments ?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_8: {
                title: 'Problèmes de santé connus',
                description: 'Exemples : Diabète, hypertension artérielle, historique de caillot, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

var v2$1 = {
    travelHealthConsultation: {
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATION ABOUT YOUR TRIP',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'What is your departure date?',
                description: 'Expected date of departure from Canada',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'What is your return date?',
                description: 'Expected date of return from Canada',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Who will you be travelling with? ',
                description: 'For example, family members, friends or alone',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_4: {
                title: 'Indicate the countries where you will spend more than one day. Specify the number of days for each country. (Country 1, City 1, Number of days)',
                description: 'For each country, please specify the region and/or the city',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_5: {
                title: 'Are other members of the same family on the same trip?',
                description: 'If yes: indicate surname, first name, date of birth, weight, height, health problems. If dates differ, please fill in another form. ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_6: {
                title: 'Which rural (villages, countryside, forest excursion, farm, etc.) or urban (cities) areas will you visit?',
                description: 'Specify',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'What is the purpose of your trip? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'All-inclusive personal vacation without leaving the tourist area (with or without children)',
                    },
                    answer_2: {
                        label: 'Business',
                    },
                    answer_3: {
                        label: 'Cruise ship',
                    },
                    answer_4: {
                        label: 'Studies (long term)',
                    },
                    answer_5: {
                        label: 'Humanitarian aid',
                    },
                    answer_6: {
                        label: 'Visit family/friends',
                    },
                    answer_7: {
                        label: 'Organized group trip',
                    },
                    answer_8: {
                        label: 'Personal vacation without children',
                    },
                    answer_9: {
                        label: 'Personal vacation with children',
                    },
                    answer_10: {
                        label: 'All-inclusive or organized trip without excursion',
                    },
                    answer_11: {
                        label: 'All-inclusive or organized trip with excursion',
                    },
                    answer_12: {
                        label: 'Climbing, mountain climbing or high altitude activity (&gt;2000m)',
                    },
                    answer_13: {
                        label: 'Cruise',
                    },
                    answer_14: {
                        label: 'Scuba diving',
                    },
                    answer_15: {
                        label: 'Safari or contact with animals',
                    },
                    answer_16: {
                        label: 'Off the tourist circuit (example: backpacking, precise itinerary unknown)',
                    },
                    answer_17: {
                        label: 'Unorganized tourist circuits (example: visit to large cities and monuments)',
                    },
                    answer_18: {
                        label: 'Other, specify',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Where will you sleep?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Hostel',
                    },
                    answer_2: {
                        label: 'Hotel',
                    },
                    answer_3: {
                        label: 'Cruise ship',
                    },
                    answer_4: {
                        label: 'Apartment/condo (Airbnb style)',
                    },
                    answer_5: {
                        label: 'B&B',
                    },
                    answer_6: {
                        label: 'People\'s home (family and/or friends)',
                    },
                    answer_7: {
                        label: 'Resort',
                    },
                    answer_8: {
                        label: 'Other, please specify',
                    },
                },
            },
            longAnswerTextArea_9: {
                title: 'Are you planning any excursions or activities outside the tourist circuit? ',
                description: 'Name all those that apply (e.g. visit to a farm, hike in the forest, etc.)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Are you pregnant?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'If so, please indicate the number of weeks of pregnancy',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'What is your weight?',
                description: 'Specify kg or pounds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_13: {
                title: 'What is your height?',
                description: 'Specify m or feet',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Do you have a weakened immune system due to a medical condition (autoimmune (autoimmune disease, leukemia) or medication (e.g. coristone, methotrexate), celiac disease, ulcerative colitis, Crohn\'s disease, hepatitis, cirrhosis, spleen removal). spleen removal).',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            simpleChoiceRadio_15: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Have you ever had an abnormal reaction to a vaccine?',
                description: 'If so, please provide details of the reaction',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_17: {
                title: 'Do you use any of these substances?',
                description: 'If so, please specify which one and the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_18: {
                title: 'Do you take medications or natural products on a regular basis?',
                description: 'Specify which pharmacy has your file',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_19: {
                title: 'Do you have allergies or do you have unusual reactions to medications?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_20: {
                title: 'Have you ever had surgery?',
                description: 'If so, please specify',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_21: {
                title: 'Do you have health problems?',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
        },
    },
};

var travelHealthConsultationEnglish = {
    travelHealthConsultation: {
        v2: {
            ...v2$1.travelHealthConsultation,
        },
        section: {
            section_1: {
                title: 'IMPORTANT INFORMATION ABOUT YOUR TRIP',
                description: '',
            },
            section_2: {
                title: 'YOUR HEALTH',
                description: '',
            },
            section_3: {
                title: 'YOUR LIFESTYLE ',
                description: '',
            },
            section_4: {
                title: 'ADDITIONAL INFORMATION',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'When are you leaving?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'When will you be back?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_3: {
                title: 'Which countries are you visiting and how long are you planning on staying there?',
                description: 'For each country, please specify the region and/or the city',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Will you be spending a few days in other countries during your trip? ',
                description: 'Please specify the country and the city',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'Are you traveling in a urban or rural area?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_6: {
                title: 'What is the purpose of your trip? ',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'All-inclusive personal vacation without leaving the tourist zone (with or without children)',
                    },
                    answer_2: {
                        label: 'Business',
                    },
                    answer_3: {
                        label: 'Cruise ship',
                    },
                    answer_4: {
                        label: 'Study (long term)',
                    },
                    answer_5: {
                        label: 'Humanitarian aid',
                    },
                    answer_6: {
                        label: 'Visiting family/friends',
                    },
                    answer_7: {
                        label: 'Group travel',
                    },
                    answer_8: {
                        label: 'Personal vacation without children',
                    },
                    answer_9: {
                        label: 'Personal vacation with children',
                    },
                    answer_10: {
                        label: 'Other, please specify',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'What kind of environment will you be sleeping in?',
                description: 'Select all that apply',
                answer: {
                    answer_1: {
                        label: 'Hostel',
                    },
                    answer_2: {
                        label: 'Hotel',
                    },
                    answer_3: {
                        label: 'Cruise ship',
                    },
                    answer_4: {
                        label: 'Apartment/condo (Airbnb style)',
                    },
                    answer_5: {
                        label: 'B&B',
                    },
                    answer_6: {
                        label: 'People\'s home (family and/or friends)',
                    },
                    answer_7: {
                        label: 'Resort',
                    },
                    answer_8: {
                        label: 'Other, please specify',
                    },
                },
            },
            shortAnswerText_8: {
                title: 'Are you planning any excursions or activities outside the tourist circuit? ',
                description: 'For example, take a hike in the rainforest, spend a day on a farm, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'If you are a woman, are you pregnant?',
                description: 'If so, please indicate the number of weeks of pregnancy',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Not applicable',
                    },
                    answer_4: {
                        label: 'Number of weeks of pregnancy',
                    },
                },
            },
            multipleChoiceCheckBox_10: {
                title: 'If you are a woman, are you currently breastfeeding?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Not applicable',
                    },
                },
            },
            shortAnswerText_11: {
                title: 'Weight ',
                description: 'What is your weight? Please specify (kg or pounds)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'Height',
                description: 'What is your height? Please specify (m or feet)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'State of health',
                description: 'Has your health changed in the last few months or have you had any changes in your medication (dose, frequency, etc.)?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            multipleChoiceCheckBox_14: {
                title: 'Are your vaccines up to date?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Unknown',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Have you ever had an abnormal reaction to a vaccine?',
                description: 'If so, please provide details of the reaction',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify here ',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_16: {
                title: 'Smoking, vaping, cannabis, recreational drugs, alcohol',
                description: 'Do you use any of these substances? If so, please specify which one as well as the frequency and quantity (e.g. cigarettes, 6 times a day)',
                answer: {
                    answer_1: {
                        label: 'Smoking',
                    },
                    answer_2: {
                        label: 'Vaping',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Recreational drugs',
                    },
                    answer_5: {
                        label: 'Alcohol consumption',
                    },
                    answer_6: {
                        label: 'No consumption',
                    },
                    answer_7: {
                        label: 'Other, specify here',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Even occasional use of certain drugs can affect the effectiveness of treatments. To ensure your safety, it is important to communicate this information during your appointment.',
                    },
                },
            },
            yesNoButtonWithText_17: {
                title: 'Medications et natural products',
                description: 'Do you take medications or natural products on a regular basis?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_18: {
                title: 'Allergies and reactions',
                description: 'Do you have allergies or do you have unusual reactions to medications?',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_19: {
                title: 'Surgical history',
                description: 'Have you ever had surgery? If so, please specify',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
            yesNoButtonWithText_20: {
                title: 'Known health problems',
                description: 'Examples: Diabetes, high blood pressure, kidney failure, etc.',
                answer: {
                    answer_1: {
                        label: 'Yes',
                    },
                    answer_2: {
                        label: 'No',
                    },
                    answer_3: {
                        label: 'Please specify which ones here ',
                    },
                },
            },
        },
    },
};

var v2 = {
    travelHealthConsultation: {
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES SUR VOTRE VOYAGE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'Quelle est votre date de départ?',
                description: 'Date prévue de départ du Canada',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'Quelle est votre date de retour?',
                description: 'Date prévue de retour au Canada',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_3: {
                title: 'Avec qui voyagerez-vous? ',
                description: 'Par exemple, des membres de la famille, des amis ou seul',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_4: {
                title: 'Indiquez les pays où vous passerez plus d\'un jour. Préciser le nombre de jours pour chaque pays. (Pays 1, Ville 1, Nombre de jour)',
                description: 'Pour chaque pays, spécifiez la région et/ou la ville',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_5: {
                title: 'Est-ce que d’autres personnes de la même famille font partie du même voyage ?',
                description: 'Si oui: indiquez nom, prénom, date de naissance, poids, taille problèmes de santé. Si des dates diffèrent, remplissez un autre formulaire. ',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_6: {
                title: 'Quelles régions rurales (villages, campagne, excursion en forêt, ferme, etc) ou urbaines (villes) visiterez-vous?',
                description: 'Précisez',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Quel est le but de votre voyage? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Vacances personnelles de type tout inclus sans sortie de la zone touristique (avec ou sans enfant)',
                    },
                    answer_2: {
                        label: 'Affaires',
                    },
                    answer_3: {
                        label: 'Bateau de croisière',
                    },
                    answer_4: {
                        label: 'Études (longue durée)',
                    },
                    answer_5: {
                        label: 'Aide humanitaire',
                    },
                    answer_6: {
                        label: 'Visite famille/amis',
                    },
                    answer_7: {
                        label: 'Voyage organisé en groupe',
                    },
                    answer_8: {
                        label: 'Vacances personnelles sans enfant',
                    },
                    answer_9: {
                        label: 'Vacances personnelles avec enfant',
                    },
                    answer_10: {
                        label: 'Tout inclus ou voyage organisé sans excursion',
                    },
                    answer_11: {
                        label: 'Tout inclus ou voyage organisé avec excursion',
                    },
                    answer_12: {
                        label: 'Escalade, ascension d’une montagne ou activité en haute altitude (plus de 2000m)',
                    },
                    answer_13: {
                        label: 'Croisière',
                    },
                    answer_14: {
                        label: 'Plongée sous marine',
                    },
                    answer_15: {
                        label: 'Safari ou contact avec des animaux',
                    },
                    answer_16: {
                        label: 'Hors des circuits touristiques (exemple : voyage en sac à dos, itinéraire précis inconnu)',
                    },
                    answer_17: {
                        label: 'Circuits touristiques non organisés (ex : visite de grandes villes et monuments)',
                    },
                    answer_18: {
                        label: 'Autres, précisez',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_8: {
                title: 'Où dormirez-vous?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Auberge de jeunesse',
                    },
                    answer_2: {
                        label: 'Hôtel',
                    },
                    answer_3: {
                        label: 'Bateau de croisière',
                    },
                    answer_4: {
                        label: 'Appartement/condo (style Airbnb)',
                    },
                    answer_5: {
                        label: 'Auberge/B&B',
                    },
                    answer_6: {
                        label: 'Chez des gens (famille ou/et amis)',
                    },
                    answer_7: {
                        label: 'Centre de villégiature',
                    },
                    answer_8: {
                        label: 'Autres, précisez',
                    },
                },
            },
            longAnswerTextArea_9: {
                title: 'Quels types d’excursion ou activités prévoyez-vous effectuer?',
                description: 'Nommer toutes celles qui s’appliquent (ex : visite d’une ferme, randonnée en forêt, etc)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_10: {
                title: 'Êtes-vous enceinte? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Si oui, veuillez indiquer le nombre de semaines de grossesse',
                    },
                },
            },
            yesNoButton_11: {
                title: 'Allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'Quel est votre poids?',
                description: 'Spécifiez kg ou lbs',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_13: {
                title: 'Quelle est votre taille?',
                description: 'Spécifiez m ou pieds',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_14: {
                title: 'Présentez-vous une diminution de votre système immunitaire en raison d’un problème de santé (maladie auto-immune, leucémie) ou médicament pris (ex : coristone, methotrexate), maladie céliaque, Colite ulcéreuse, maladie de Crohn, hépatite, cirrhose, ablation de la rate).',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            simpleChoiceRadio_15: {
                title: 'Votre vaccination est-elle à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_16: {
                title: 'Avez-vous déjà eu une réaction à un vaccin?',
                description: 'Si oui, décrivez la réaction',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_17: {
                title: 'Consommez-vous une ou plusieurs de ces substances?',
                description: 'Si oui, spécifiez lesquelles leurs fréquences et quantités (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_18: {
                title: 'Des médicaments ou des produits naturels sont-ils pris de façon régulière?',
                description: 'Précisez à quelle pharmacie se trouve votre dossier',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_19: {
                title: 'Des allergies ou des réactions à certains médicaments sont-elles connues? ',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_20: {
                title: 'Avez-vous déjà eu une ou des chirurgies? ',
                description: 'Si oui, lesquelles?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_21: {
                title: 'Y a-t-il des problèmes de santé connu?',
                description: 'Par exemple, du diabète, de l\'hypertension artérielle, de l\'insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Précisez lesquels ici',
                    },
                },
            },
        },
    },
};

var travelHealthConsultationFrench = {
    travelHealthConsultation: {
        v2: {
            ...v2.travelHealthConsultation,
        },
        section: {
            section_1: {
                title: 'INFORMATIONS IMPORTANTES SUR VOTRE VOYAGE',
                description: '',
            },
            section_2: {
                title: 'VOTRE CONDITION DE SANTÉ',
                description: '',
            },
            section_3: {
                title: 'VOS HABITUDES DE VIE',
                description: '',
            },
            section_4: {
                title: 'INFORMATIONS SUPPLÉMENTAIRES',
                description: '',
            },
        },
        element: {
            shortAnswerText_1: {
                title: 'À quelle date partez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_2: {
                title: 'À quelle date revenez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            longAnswerTextArea_3: {
                title: 'Quels pays visitez-vous et combien de temps prévoyez-vous rester dans chacun de ces pays? ',
                description: 'Pour chaque pays, veuillez spécifier la région et/ou la ville',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_4: {
                title: 'Prévoyez-vous des escales de quelques jours dans d\'autres pays lors de votre voyage? ',
                description: 'Veuillez spécifier la région et/ou la ville',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Veuillez préciser ici',
                    },
                },
            },
            shortAnswerText_5: {
                title: 'Voyagez-vous en milieu urbain ou en milieu rural? ',
                description: '',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_6: {
                title: 'Quel est le but de votre voyage? ',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Vacances personnelles de type tout inclus sans sortie de la zone touristique (avec ou sans enfant)',
                    },
                    answer_2: {
                        label: 'Affaires',
                    },
                    answer_3: {
                        label: 'Bateau de croisière',
                    },
                    answer_4: {
                        label: 'Études (longue durée)',
                    },
                    answer_5: {
                        label: 'Aide humanitaire',
                    },
                    answer_6: {
                        label: 'Visite famille/amis',
                    },
                    answer_7: {
                        label: 'Voyage organisé en groupe',
                    },
                    answer_8: {
                        label: 'Vacances personnelles sans enfant',
                    },
                    answer_9: {
                        label: 'Vacances personnelles avec enfant',
                    },
                    answer_10: {
                        label: 'Autres, précisez',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_7: {
                title: 'Dans quel type d\'environnement dormirez-vous?',
                description: 'Sélectionnez tout ce qui s’applique',
                answer: {
                    answer_1: {
                        label: 'Auberge jeunesse',
                    },
                    answer_2: {
                        label: 'Hôtel',
                    },
                    answer_3: {
                        label: 'Bateau de croisière',
                    },
                    answer_4: {
                        label: 'Appartement/condo (style Airbnb)',
                    },
                    answer_5: {
                        label: 'Auberge/B&B',
                    },
                    answer_6: {
                        label: 'Chez des gens (famille ou/et amis)',
                    },
                    answer_7: {
                        label: 'Centre de villégiature',
                    },
                    answer_8: {
                        label: 'Autres, précisez',
                    },
                },
            },
            shortAnswerText_8: {
                title: 'Avez-vous prévu faire des excursions ou des activités en-dehors des circuits touristiques ? ',
                description: 'Par exemple, faire une randonnée en forêt tropicale, passer une journée sur une ferme, etc.',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_9: {
                title: 'Si vous êtes une femme, êtes-vous enceinte? ',
                description: 'Si oui, veuillez indiquer le nombre de semaines de grossesse',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne s\'applique pas',
                    },
                    answer_4: {
                        label: 'Nombre de semaines de grossesse',
                    },
                },
            },
            multipleChoiceCheckBox_10: {
                title: 'Si vous êtes une femme, allaitez-vous?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne s\'applique pas',
                    },
                },
            },
            shortAnswerText_11: {
                title: 'Quel est votre poids?',
                description: 'Quel est votre poids? Spécifiez (kg ou lbs)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            shortAnswerText_12: {
                title: 'Quel est votre taille?',
                description: 'Quel est votre taille? Spécifiez (m ou pieds)',
                answer: {
                    answer_1: {
                        label: '',
                    },
                },
            },
            yesNoButtonWithText_13: {
                title: 'État de santé',
                description: 'Y a-t-il eu des changements dans votre état de santé dans les derniers mois ou avez vous eu des changements dans vos médicaments (dose, fréquence, etc.)? ',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            multipleChoiceCheckBox_14: {
                title: 'Est-ce que votre vaccination est à jour?',
                description: '',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: 'Ne sais pas',
                    },
                },
            },
            yesNoButtonWithText_15: {
                title: 'Avez-vous déjà eu une réaction anormale à un vaccin? ',
                description: 'Si oui, veuillez détailler la réaction',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser ici',
                    },
                },
            },
            multipleChoiceCheckBoxWithText_16: {
                title: 'Tabagisme, vapotage, cannabis, drogues récréatives, alcool',
                description: 'Consommez-vous une ou plusieurs de ces substances? Si oui, veuillez spécifier lesquelles ainsi que la fréquence et quantité (exemple: cigarette, 6 fois par jour)',
                answer: {
                    answer_1: {
                        label: 'Tabagisme',
                    },
                    answer_2: {
                        label: 'Vapotage',
                    },
                    answer_3: {
                        label: 'Cannabis',
                    },
                    answer_4: {
                        label: 'Drogues récréatives',
                    },
                    answer_5: {
                        label: 'Alcool',
                    },
                    answer_6: {
                        label: 'Aucune consommation',
                    },
                    answer_7: {
                        label: 'Autres, précisez ici',
                    },
                },
                alert: {
                    alert_1: {
                        message: 'Même une consommation occasionnelle de certaines drogues peut influencer l\'efficacité des traitements. Pour assurer votre sécurité, il est important de communiquer cette information lors de votre rendez-vous. ',
                    },
                },
            },
            yesNoButtonWithText_17: {
                title: 'Médicaments et produits naturels',
                description: 'Prenez-vous des médicaments ou des produits naturels sur une base régulière?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_18: {
                title: 'Allergies et réactions',
                description: 'Avez-vous des allergies ou des réactions à certains médicaments?',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_19: {
                title: 'Antécédents chirurgicaux',
                description: 'Avez-vous déjà subit une ou des chirurgies? Si oui, veuillez indiquer lesquelles',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
            yesNoButtonWithText_20: {
                title: 'Problèmes de santé connus',
                description: 'Exemples: Diabète, hypertension artérielle, insuffisance rénale, etc.',
                answer: {
                    answer_1: {
                        label: 'Oui',
                    },
                    answer_2: {
                        label: 'Non',
                    },
                    answer_3: {
                        label: ' Veuillez préciser lesquels ici',
                    },
                },
            },
        },
    },
};

/**
 * Merge locales into an existing i18n module
 * @param i18n {VueI18n} Parent application vue-i18n instance
 */
function mergeLocales(i18n) {
    i18n.mergeLocaleMessage('en', { ...influenzaEnglish, ...phxCovidEnglish, ...as624English, ...as6242024English,
        dataCollection: {
            ...dataCollectionBaseEnglish,
            ...consultationAvecUnPharmacienEnglish,
            ...emergencyOralContraceptionEnglish,
            ...hormonalContraceptionEnglish,
            ...menstrualCrampsEnglish,
            ...prophylaxisForAcuteMountainSicknessEnglish,
            ...vaginitisAndBacterialVaginosisEnglish,
            ...smokingCessationEnglish,
            ...diarrheaPreventionEnglish,
            ...eczemaEnglish,
            ...mouthUlcersEnglish,
            ...oralThrushEnglish,
            ...medicationAdjustmentEnglish,
            ...urinaryTractInfectionEnglish,
            ...medicationReviewEnglish,
            ...prescriptionRenewalAndExtensionEnglish,
            ...nauseaAndVomitingPregnancyEnglish,
            ...vitaminSupplementDuringPregnancyEnglish,
            ...diaperRashEnglish,
            ...allergicRhinitisEnglish,
            ...hemorrhoidsEnglish,
            ...shingleTreatmentAndCareEnglish,
            ...acneProblemsEnglish,
            ...allergicConjunctivitisEnglish,
            ...coldSoresEnglish,
            ...headLiceEnglsih,
            ...cutaneousCandidiasisEnglish,
            ...heartburnEnglish,
            ...postExposureProphylaxisEnglish,
            ...malariaProphylaxisEnglish,
            ...travelHealthConsultationEnglish,
        },
    });
    i18n.mergeLocaleMessage('fr', { ...influenzaFrench, ...phxCovidFrench, ...as624French, ...as6242024French,
        dataCollection: {
            ...dataCollectionBaseFrench,
            ...consultationAvecUnPharmacienFrench,
            ...emergencyOralContraceptionFrench,
            ...hormonalContraceptionFrench,
            ...menstrualCrampsFrench,
            ...prophylaxisForAcuteMountainSicknessFrench,
            ...vaginitisAndBacterialVaginosisFrench,
            ...smokingCessationFrench,
            ...diarrheaPreventionFrench,
            ...eczemaFrench,
            ...mouthUlcersFrench,
            ...oralThrushFrench,
            ...medicationAdjustmentFrench,
            ...urinaryTractInfectionFrench,
            ...medicationReviewFrench,
            ...prescriptionRenewalAndExtensionFrench,
            ...nauseaAndVomitingPregnancyFrench,
            ...vitaminSupplementDuringPregnancyFrench,
            ...diaperRashFrench,
            ...allergicRhinitisFrench,
            ...hemorrhoidsFrench,
            ...shingleTreatmentAndCareFrench,
            ...acneProblemsFrench,
            ...allergicConjunctivitisFrench,
            ...coldSoresFrench,
            ...headLiceFrench,
            ...cutaneousCandidiasisFrench,
            ...heartburnFrench,
            ...postExposureProphylaxisFrench,
            ...malariaProphylaxisFrench,
            ...travelHealthConsultationFrench,
        },
    });
}
const i18n = {
    locales: [
        {
            code: 'en',
            iso: 'en-CA',
            name: 'English',
        },
        {
            code: 'fr',
            iso: 'fr-CA',
            name: 'Français',
        },
    ],
    defaultLocale: 'fr',
    vueI18n: {
        fallbackLocale: 'en',
        messages: {
            en: {
                ...influenzaEnglish,
                ...phxCovidEnglish,
                ...as624English,
                ...as6242024English,
                dataCollection: {
                    ...dataCollectionBaseEnglish,
                    ...consultationAvecUnPharmacienEnglish,
                    ...emergencyOralContraceptionEnglish,
                    ...hormonalContraceptionEnglish,
                    ...malariaProphylaxisEnglish,
                    ...menstrualCrampsEnglish,
                    ...prophylaxisForAcuteMountainSicknessEnglish,
                    ...vaginitisAndBacterialVaginosisEnglish,
                    ...smokingCessationEnglish,
                    ...diarrheaPreventionEnglish,
                    ...eczemaEnglish,
                    ...mouthUlcersEnglish,
                    ...oralThrushEnglish,
                    ...medicationAdjustmentEnglish,
                    ...urinaryTractInfectionEnglish,
                    ...medicationReviewEnglish,
                    ...prescriptionRenewalAndExtensionEnglish,
                    ...nauseaAndVomitingPregnancyEnglish,
                    ...vitaminSupplementDuringPregnancyEnglish,
                    ...diaperRashEnglish,
                    ...allergicRhinitisEnglish,
                    ...hemorrhoidsEnglish,
                    ...shingleTreatmentAndCareEnglish,
                    ...acneProblemsEnglish,
                    ...allergicConjunctivitisEnglish,
                    ...coldSoresEnglish,
                    ...headLiceEnglsih,
                    ...cutaneousCandidiasisEnglish,
                    ...heartburnEnglish,
                    ...postExposureProphylaxisEnglish,
                    ...malariaProphylaxisEnglish,
                    ...travelHealthConsultationEnglish,
                },
            },
            fr: {
                ...influenzaFrench,
                ...phxCovidFrench,
                ...as624French,
                ...as6242024French,
                dataCollection: {
                    ...dataCollectionBaseFrench,
                    ...consultationAvecUnPharmacienFrench,
                    ...emergencyOralContraceptionFrench,
                    ...hormonalContraceptionFrench,
                    ...malariaProphylaxisFrench,
                    ...menstrualCrampsFrench,
                    ...prophylaxisForAcuteMountainSicknessFrench,
                    ...vaginitisAndBacterialVaginosisFrench,
                    ...smokingCessationFrench,
                    ...diarrheaPreventionFrench,
                    ...eczemaFrench,
                    ...mouthUlcersFrench,
                    ...oralThrushFrench,
                    ...medicationAdjustmentFrench,
                    ...urinaryTractInfectionFrench,
                    ...medicationReviewFrench,
                    ...prescriptionRenewalAndExtensionFrench,
                    ...nauseaAndVomitingPregnancyFrench,
                    ...vitaminSupplementDuringPregnancyFrench,
                    ...diaperRashFrench,
                    ...allergicRhinitisFrench,
                    ...hemorrhoidsFrench,
                    ...shingleTreatmentAndCareFrench,
                    ...acneProblemsFrench,
                    ...allergicConjunctivitisFrench,
                    ...coldSoresFrench,
                    ...headLiceFrench,
                    ...cutaneousCandidiasisFrench,
                    ...heartburnFrench,
                    ...postExposureProphylaxisFrench,
                    ...malariaProphylaxisFrench,
                    ...travelHealthConsultationFrench,
                },
            },
        },
    },
};

export { GlobalDataModule, ah650, as624, as6242024, dataCollection, i18n, memoryAid, mergeLocales, phx, registerModule };
//# sourceMappingURL=index.esm.js.map
